// DispatchDeck - Main Application Entry
// Firebase-powered fleet management SaaS

// TASK-10076 — FIRST import, so window.__ddChunk exists before any module runs a
// lazy import(). A stale tab whose lazy chunk was removed by a deploy reloads onto
// the new build (or asks) instead of dead-ending. Static on purpose: a lazy copy
// of this would be lost by the same deploy. install() is wired below with the
// StaleTabGuard install. The ONE importer.
import './utils/chunkReload.js?v=2'; // TASK-10108 bad cached chunk heal
import {
  auth,
  db,
  functions,
  httpsCallable,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInAnonymously,
  signOut,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signInWithCredential,
  linkWithCredential,
  EmailAuthProvider,
  deleteUser,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  getDocs,
  deleteDoc,
  addDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  Timestamp,
  increment,
  storage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
  arrayUnion,
  arrayRemove,
  limit
} from './firebase-config.js?v=1868';

// 🔴 TASK-9279 — the ONLY write form that removes a key from a Firestore MAP.
// `merge:true` merges maps key-by-key: it ADDS and OVERWRITES the keys present in
// the payload and PRESERVES every key absent from it, so writing a locally-trimmed
// copy of `settings.accessPins` can never revoke a PIN. Re-measured against real
// Firestore 2026-09-22 on a throwaway doc: set({accessPins:{1111}},{merge:true})
// over {1111,2222} read back {1111,2222}; update(FieldPath('settings','accessPins',
// '2222'), deleteField()) read back {1111} with the sibling `settings` keys intact.
//
// FieldPath rather than the dotted string `settings.accessPins.${pin}`: the dotted
// form re-splits on '.', so a PIN key containing a dot would delete a nested path
// that does not exist — a silent no-op, i.e. THIS EXACT BUG again, with no error.
// Every live key is 4-6 digits today (censused 2026-09-22: 46 PINs, 39 tenants, 0
// non-numeric), but a revocation path must not depend on that holding.
//
// Imported straight from the pinned gstatic URL rather than added to
// firebase-config.js — 35 modules import that file at ?v=1868, so a new export
// there forces a 35-file buster sweep for two symbols. The URL string is IDENTICAL
// to the one firebase-config.js uses ⇒ same module instance, so this is NOT the
// ?v= cross-import trap of Pitfall #1. Precedent: ap.js, bi.js, billing.js,
// clock.js, dispatch.js all pull runTransaction in exactly this way.
// `runTransaction` joins them for saveAccessPin (COD round 2 ❌, three lanes
// converging): the guards there are read-then-write, and the ADD / EDIT-IN-PLACE
// branch writes its PIN key unconditionally — so a delete committing between the
// read and the write PUT THE REVOKED CODE BACK. Only reachable now that deletes
// are real, and it is this task's own bug class in a new costume, so it is closed
// here rather than deferred. See the transaction in saveAccessPin for the rest.
import { deleteField, FieldPath, runTransaction } from 'https://www.gstatic.com/firebasejs/10.8.0/firebase-firestore.js';

// TASK-9206 FOLLOW-UP — the ONE calendar-day-offset rule, on window.ClockDay.
// GENERATED by scripts/build-shared.cjs (already in the hosting:app predeploy)
// from the canonical functions/src/shared/clockDayOffset.js, because functions
// and hosting are separate deploys and no single file can be loaded by both.
// MUST come before sharedFormat.js: Fmt.shiftSpan / Fmt.shiftCells call
// window.ClockDay at CALL time, and this is the module that defines it.
import './shared/clock-day-offset.js?v=1512001';

// TASK-9153 — the attachment-limits FAQ quotes the live caps instead of typing
// them, so a cap change can't leave the Help Center / Ask AI answer stale.
// Same ?v= as every other planLimits importer (Pitfall #1).
import { STARTER_LIMITS } from './config/planLimits.js?v=1763';
// TASK-9812 — ONE Self-Serve price table (monthly + annual), generated from
// functions/src/shared/planPricing.js by scripts/build-shared.cjs.
import { PLAN_PRICING } from './shared/plan-pricing.js?v=9812.1';
// TASK-9644 — the Help FAQ, GENERATED by scripts/build-shared.cjs from the canonical
// functions/src/shared/helpFaq.js (the in-app Help bot reads the same file server-side).
// Only app.js imports it, so a named import is safe (no second ?v= instance).
// rel2089 perf budget: help-faq.js (~88 KB gzip) is loaded LAZILY by ensureFaqData() below,
// still imported ONLY by app.js (Pitfall #1). 2089-on-2088.6: ?v=9822004 = rel2089 lazy FAQ + 2088.6 FAQs (TASK-9716 · 9703 · 9728 · 9879 · 9710 · 9692 · 9753 · 9876 · 9778 · 9705) + TASK-9727 PPE FAQ + TASK-9750 tentative-day FAQ (?v=9822005) + TASK-9433 +50-vehicle FAQ copy (?v=9822433).
// TASK-9402 — public (PIN/link) timesheet photos: iPhone HEIC → JPEG at pick
// time, same converter as every other photo screen; Save waits it out.
import { prepareUploadFile, photoErrorMessage } from './utils/uploadPrep.js?v=1';
import { createPhotoPrepTracker, settlePhotoPrepBeforeSave } from './utils/heicConvert.js?v=3';
// TASK-9178 — the ONE W-2 / 1099 worker-classification rule, on window.WorkerClass.
// GENERATED by scripts/build-shared.cjs from functions/src/shared/workerClassification.js
// (the QBO employee sync reads the same file server-side). Every view that asks
// "is this person a W-2 employee or a 1099 contractor?" calls
// window.WorkerClass.resolve(person) at CALL time — never the raw fields.
import './shared/worker-classification.js?v=1522201';
// TASK-9493 — the ONE split-receipt helper, on window.CostSplit. GENERATED by
// scripts/build-shared.cjs from functions/src/shared/costAllocationSplit.js (the
// three expense callables validate with the same file). Every reader that
// attributes a costEntry to a job / vehicle / customer / allocation loops over
// window.CostSplit.expandCostEntry(c) at CALL time — never a cross-?v= import.
import './shared/cost-allocation-split.js?v=2'; // integ/2129: TASK-10162 S4 split shares

// TASK-862 — shared escaping + format helpers on window.* (escapeHtml/escAttr/Fmt).
// MUST be imported before the view modules below: those views are STATICALLY
// imported (they evaluate before app.js's body) and reference these helpers via
// lazy delegating wrappers, so the window.* assignments must already be in place.
import './sharedFormat.js?v=1511301';
// TASK-9390 — window.JobTypes (managed job-type list + the ONE label resolver
// every view reads). Side-effect module, same load-order contract as above.
import './utils/jobTypes.js?v=10276006'; // TASK-10006 S2 rentalDirection per job type
// P0 2026-10-06 — window.BillableCustomer: the ONE "vendors/subcontractors are
// not customers" rule (GENERATED by scripts/build-shared.cjs from
// functions/src/shared/billableCustomer.js). MUST load before pickerGroups.js
// below — its customer builder filters through it. Read at CALL time.
import './shared/billable-customer.js?v=9684001';
// TASK-10188 — window.FieldTicketSettings: the ONE close-out Field Ticket
// Settings rule for a customer with several Active Price Books (GENERATED by
// scripts/build-shared.cjs from functions/src/shared/fieldTicketSettings.js).
// clock.js + dispatch.js read it at CALL time.
import './shared/field-ticket-settings.js?v=10190001'; // TASK-10190 +pickLineItemsContract/toJobDayStamp
// TASK-9657 S0 — window.SafetyHold: the ONE "is this unit on a safety hold on
// day X?" rule (GENERATED by scripts/build-shared.cjs from
// functions/src/shared/safetyHold.js; the connector's create_job / assign_crew
// warning reads the same file). dispatch.js + maintenance.js read it at CALL time.
import './shared/safety-hold.js?v=2'; // wo-legacy-followups: isLegacyOrderShape + resolveLegacy (an imported order's row number never holds a unit)
// TASK-9886 — window.DvirCoverage: a DVIR is owed per truck + DRIVER + day
// (GENERATED by scripts/build-shared.cjs from functions/src/shared/dvirCoverage.js;
// dvirAccountabilityCheck requires the same file). dispatch.js reads it at CALL time.
import './shared/dvir-coverage.js?v=1';
// TASK-9763 — window.CrewPairing: "who's in what" — dispatch pairs crew to units
// per day; clock-in preselects only YOUR unit (GENERATED by scripts/build-shared.cjs
// from functions/src/shared/crewPairing.js). jobboard.js / clock.js / dispatch.js
// read it at CALL time; absent = today's behaviour (every row checked).
import './shared/crew-pairing.js?v=4'; // integ/2128 addendum 4 (TASK-9797 S1+S2 shipped 3; TASK-10193 S2 changed the file too) · TASK-9763 S3 — seniority / crew order (orderView, orderNext, orderPrune) · TASK-10193 S2 crewPairPrune keeps riderTruckIds / coDriverTruckIds
// TASK-10015 — window.RentalLedgerMath: the ONE rental-count math (job ledger
// entry + move, planned pickups, holding from days, the recount). GENERATED by
// scripts/build-shared.cjs from functions/src/shared/rentalLedgerMath.js (the
// pickup settle + recount callables run the same file). dispatch.js + roster.js
// read it at CALL time. MUST load before the dispatch view.
import './shared/rental-ledger-math.js?v=5'; // TASK-9263 rlmProjectQtyUsed · integ/2113 rlmLeavePlan + rlmStockChange (shared with deleteJobLedgered, TASK-10010) · TASK-10006 rlmProjectRentalsOut + rlmTxDelta · TASK-10006 S3 rlmDayPickedUp / rlmAnnotatePickedUp / rlmJobPickupStatus (stock moves on a real pickup)
// wo-legacy-orders — window.LegacyWorkOrder: imported (old-app) Open/Closed/Done
// work orders read in the current shape + resolved to a unit by NAME (GENERATED
// by scripts/build-shared.cjs from functions/src/shared/legacyWorkOrder.js; the
// partner maintenance summary runs the same file). maintenance.js reads it at CALL time.
import './shared/legacy-work-order.js?v=1';
// TASK-10269 — window.Moneyish: the ONE typed cost/hours reader ("$1,250.00" → 1250;
// GENERATED by scripts/build-shared.cjs from functions/src/shared/moneyish.js, which the
// emailed maintenance summary runs). maintenance.js reads it at CALL time (and hands it to its lazy History module).
import './shared/moneyish.js?v=1';
// TASK-9862 — window.Locates811: the ONE set of 811 locate rules (site grouping,
// good-through dates, status, the expired-locate hard stop, renew plan).
// GENERATED by scripts/build-shared.cjs from functions/src/shared/locates811.js
// (read811Ticket runs the same file). dispatch.js / projects.js / jobboard.js
// read it at CALL time.
import './shared/locates-811.js?v=101670003'; // integ/2094 pre-ship renewal covered only by a good+clear old ticket · TASK-9920 strict dig-start time (fwd 2090.1) · TASK-9887 month-name dates + customer-prefix strip
import './shared/locates-811-search.js?v=98874'; // TASK-9887 Locates page search (window.Locates811Search)
// TASK-9839 — window.TimeOffBalance: the ONE time-off balance + milestone grants
// (GENERATED by scripts/build-shared.cjs from functions/src/shared/timeOffBalance.js;
// the approval text and the daily grant audit run the same file). hr.js +
// dispatch.js (driver time-off modal) read it at CALL time.
import './shared/time-off-balance.js?v=9838001';
// TASK-4720 follow-up — window.TimeOffOutbox: offline time-off requests / withdraws
// wait on the phone and send when signal returns (dispatch.js driver modal).
import './timeoff-outbox.js?v=4720001';
import './shared/pm-status.js?v=2'; // TASK-9659 — window.PmStatus, the ONE PM brain (maintenance.js + pretrip.js read it at load / call time)
// TASK-9452 — window.PpeReport: the ONE PPE report (GENERATED by
// scripts/build-shared.cjs from functions/src/shared/ppeReport.js; the Claude
// connector's get_ppe_report and Ask DispatchDeck's getPpeReport run the same
// file server-side). hr.js (Safety Equipment → Manage → Reports) reads it at CALL time.
import './shared/ppe-report.js?v=9728004'; // TASK-9263 dispatch ledger rows read newQty - previousQty · TASK-9728 serialized PPE (serialAssetsForType, serialCustody) · TASK-9722 stock ledger read-back (strip, alert flags, History paging) · TASK-9719 voided rows out + returnedQty + itemTotals · TASK-9723 reorderQty (par / fill-to)
// TASK-9489 — window.EquipmentRounding: the ONE "does this equipment line round
// up to the half hour?" decision (company default + per-line Always / Never /
// Company default). GENERATED by scripts/build-shared.cjs from
// functions/src/shared/equipmentRounding.js; the server enrichment runs the same
// file. clock.js + billing.js read it at CALL time.
import './shared/equipment-rounding.js?v=1';
// TASK-9440 — window.FuelSurcharge: the ONE fuel surcharge calculation (settings
// resolve, base, whole-cent amount, words, ticket state, line plan). GENERATED by
// scripts/build-shared.cjs from functions/src/shared/fuelSurcharge.js; the server
// line pass runs the same file. billing.js reads it at CALL time.
import './shared/fuel-surcharge.js?v=1';
// TASK-9548 — window.PickerGroups: the ONE grouped-<select> builder (internal
// customers under "Internal — not billed", then "Customers" A–Z). Same
// side-effect load-order contract as above; views read it at CALL time.
import './utils/customerSource.js?v=9694601'; // TASK-9694 S6a — window.CustomerSourceCore: crew screens read customerDirectory (name + flags), not customers. MUST load before any view.
import './utils/driverAcademy.js?v=2'; // integ/2122 repointed at the TASK-10092 episodes · TASK-10093 driver Get help: Academy training videos (window.DriverAcademy) — fills the Driver Code screen slot + dispatch.js Help sheet
import './utils/pickerGroups.js?v=10030001'; // slice 2 + P0 2026-10-06: customer builder drops vendors · TASK-9548 slice 3 keepId · decisions-1010 billing/sales pickers drop Shop/Office · TASK-10030 isInactiveCustomer + excludeInactive
// clock-double-tap — one open shift per person: clock-in guard + the "which shift
// are you closing?" chooser. Side-effect module (window.ClockShiftGuard), read by
// clock.js / jobboard.js / dispatch.js / PersonalActions at CALL time (Pitfall #1).
// TASK-9971 (clock-dst-all) — window.TenantLocalTime: the ONE place a typed clock time becomes
// an instant (COMPANY zone, repeated fall-back hour asked, gap refused). Loaded
// BEFORE clockShiftGuard (which delegates to it) and every view (Pitfall #1).
// TASK-9878 — window.PayPeriodHours: the ONE pay-period hours rule + the company
// workweek (GENERATED by scripts/build-shared.cjs from functions/src/shared/payPeriodHours.js;
// the Claude connector's get_payroll_hours runs the same file). tenantLocalTime.js's week
// helpers and clock.js (pay-period grid, workweek OT, Payroll Prep) read it at CALL time.
import './shared/pay-period-hours.js?v=10034001'; // TASK-10034 lastDayYmd + onPayroll (NLE on payroll through the last-day period)
import './utils/tenantLocalTime.js?v=6'; // TASK-9878 week helpers delegate to window.PayPeriodHours · TASK-1507 company workweek helpers (weekKey / weekRange / periodBoundsMs)
import './utils/clockShiftGuard.js?v=6'; // TASK-9971 DST late close asks which 1:xx · 2088.6: TASK-9713 driver self clock-in window
// TASK-9658 S1 — window.RecordLinks: parse/build record deep links
// (#/maintenance/vehicle/{id}, #/maintenance/task/{id}, #/pretrip/dvir/{id})
// and the one-shot pendingRecordFocus drain. Side-effect module; the router
// below and maintenance.js / pretrip.js read it at CALL time (Pitfall #1).
import './utils/recordLinks.js?v=3'; // TASK-9587 dispatch/claude-job route · TASK-9862 dispatch/locate
// TASK-9653 S1 — window.OpenLinks: "open DispatchDeck at this screen" hints
// (#/hr?dd_open=hr_document_upload&folder={id}) and the generic one-shot
// AppState.pendingFocus queue. Same side-effect load-order contract as above;
// the router below and hr.js read it at CALL time (Pitfall #1).
import './utils/openLinks.js?v=9671003'; // TASK-9653 S1 (new file) · TASK-9671 S2 ticket + customer hints · S3 invoice + price book hints
// TASK-9700 — window.ProjectStatusFilter: the ONE Projects status filter (chip
// words, order, default 'active', predicate) + customer grouping shared by
// Dispatch -> Projects and AR/AP -> Projects. Same side-effect load-order
// contract as above; both views read it at CALL time.
import './utils/projectStatusFilter.js?v=9700';
// TASK-9855 slice 1 — window.StorageFile: in-app views open uploaded files
// through the Storage SDK on their PATH (getBlob → object URL, storage.rules
// decide) instead of the stored permanent token URL. Side-effect module, read
// at CALL time (Pitfall #1); init() + the driver door are wired below.
// integ/2123 storageFile.js 10142123 also carries: TASK-10086-N vehicle docs by storage path
import './utils/storageFile.js?v=10142123'; // TASK-9978 external links: main-frame nav only in the native app; Storage-host refused
// Same pinned gstatic URL as firebase-config.js ⇒ same module instance (not the
// ?v= trap) — the runTransaction precedent above.
import { getBlob as _sfGetBlob, getStorage as _sfGetStorage, getMetadata as _sfGetMetadata } from 'https://www.gstatic.com/firebasejs/10.8.0/firebase-storage.js';

// TASK-345 Slice L REVERTED 2026-05-28 — restored static imports for all view
// modules per COD V1 unanimous verdict (Architect ❌ / Skeptic 🔴 / UX ❌ /
// Code Reviewer ⚠️ / Independent ⚠️). Lazy-load via dynamic import() was
// fragile on iOS WebView cache / weak cell — chunk fetches hung forever
// (10s timeout band-aid still left users facing a 21s spinner-then-CTA).
// Drivers in the field cannot tolerate that. Eating the ~5.8MB initial JS
// payload back to guarantee reliability. trailguide + demoSeeder S3 lazy
// helpers (below) stay — those are optional addons, not driver-critical.
// TASK-10015 roster.js 10015001: owner Recount from live jobs (Roster › Assets)
// integ/2122 roster.js 10139389 also carries: TASK-9891 S2 link-length copy; TASK-9389 S1 Can manage logins switch; TASK-9986 S1 Billing settings switch
// integ/2123 roster.js 10142123 also carries: TASK-10039 per-person Edit Ticket grant; TASK-10086-D escJsAttr; TASK-10086-N/P vehicle + cab docs by path
// integ/2124 roster.js 10172124 also carries: TASK-10138 Vehicles/Trailers search matches VIN (branch 10142138); TASK-9891 follow-up send confirms show the real link length (branch 10139390)
// TASK-10027 (on2125, with TASK-10025) busters: roster 10172710, dispatch 10272710, hr 99999991, account 10140027 (setupQuestionnaire 10027001), reminders 10027002 (rosterPinSetup 9941302) — muted / no-contact company send notice. The integ/2114 reminder-toast fold is re-applied (TASK-10025 rides).
// TASK-10027 (on2124) busters: roster 10172710, dispatch 10272710, hr 99999991, account 10125727 (setupQuestionnaire 10027001), reminders 10027002 (rosterPinSetup 9941302) — muted / no-contact company send notice. The integ/2114 reminder-toast fold waits for TASK-10025.
import { initRoster } from './views/roster.js?v=11041295'; // TASK-9688 AR / AP tag text names the QuickBooks paid sync exceptions email · TASK-10211 BREAK IN SERVICE chip on Inactive drivers + recall-date hint (rosterPersonDeactivate.js 10211002) · integ/2131 10370094: TASK-10094 denied-login copy (its own 10294094) over main's 10360211 · TASK-10211 Laid off label + Recall (lazy rosterPersonDeactivate.js) + Laid off / Texted STOP filters (above 10212's 10350212) · TASK-10183 regular days off (lazy rotationDays.js) (integ/2129 addendum) · TASK-10139 S4 rig editor UX (rosterPermitGeometry.js 10302129) + TASK-10172-A lazy Deactivate with Sold / Retired + date (rosterUnitDeactivate.js 10302129) (integ/2129) · integ/2127: TASK-9985 badge labels + 10139 geometry gap + 4851 D + 9911 M3 + 10025 · TASK-9880 rented-in Billed to the customer (pass-through / markup / own rate, setting on) ·TASK-10007 star an asset onto the dispatch board strip (settings.pinnedAssetIds, Roster module or owner) · TASK-4692 bulk Text setup codes (Dispatch module) + new-hire setup-code toast + setup-code replace-confirm only when a code was issued · TASK-9433 soft vehicle-cap warning · TASK-9716 Update from photo button (Vehicles / Trailers) · TASK-9715 vehicle spec ranges (window.VehicleSpecRanges) + audit viewer trailer/Claude rows · TASK-9728 gas monitor Who has it (getSerialPpeCustody) · TASK-9879 rented-in call-off date + idle/cycle/no-return-date hints and badge · TASK-9710 per-driver Add/Edit Job switch · TASK-9753 canInviteUsers export for Settings → Add team member · TASK-9844 Skills (company list, person skills, required per type) · TASK-9869 rented-in section (vendor, vendor unit, return-by; rate + contract owner/billing only, Read terms from contract) · TASK-9778 Receipts & Expenses module in the staff-login picker · TASK-9813/9814 IRP cab card + registration upload beside its expiry, Doesn't-expire, soft prompt, No reg. expiry badge · TASK-9722 tracked item Count now / History buttons + strip in the edit form + Inventory / Supplies notify tag · TASK-9720 stock-tracked items read-only count, Receive/Adjust via recordSupplyStock, no yard transfer + Safety Gear ↔ PPE link prompts · TASK-9267 Permit Readiness quick fill (type L/W/H/registered weight per unit, stale-write guard, lower-needs-confirm) · TASK-9677 partner save allowlist + merged SPH check · TASK-9662 COD: refused partner edits not cascaded/audited · TASK-9662 partner roster termination via partner callables + last-day bounds · TASK-4876 S2 Permit Readiness button on Vehicles/Trailers headers + smartassist-names-2 make/model/engine/manufacturer/inspection-location identifier mode + nicknames noFuzzy + TASK-4834 identifier fields off the name helper (VIN/plate/policy/serial; units exact-dup only) + Assets list shows part-less docs (no orderBy partNumber) + vehicle exam reports load (tenant + no index) · TASK-4800 S3 Assets On Site (stranded equipment, oldest first) + View on map · TASK-9548 slice 2 labeled yard/type picker groups · HOTFIX stored-XSS escaping (job/ticket text) · TASK-9509 asset cards (b: fixed 2x2 stat grid): Owned/Total label, branch-scoped banner line, chip wrap, individual Out — {job} + TASK-1550 person + vehicle reminders + TASK-4710 'projects' in admin preset + module grid + TASK-4873 Driver Code panel setup-code branch + TASK-4810 rental needs-recount banner/card line + per-item out cap in branch rollups + TASK-9179 role-conflict: CDL-holding managers who drive no longer flagged + TASK-4840 invite delivery: no text-only, preferred channel preselected, honest resend toast + TASK-9402 profile photo pickers take any image + HEIC + TASK-932 founder-gate sweep · P0 vendors-not-customers (order link + customer list) · TASK-9548 s3 grouped customer pickers · decisions-1010 billing/sales pickers drop Shop/Office · TASK-9696 driver types (rel2087) · 2088.6: TASK-9731 trailer tire size · TASK-9828 S2 one Inactive path (Edit toggle → Deactivate confirm, mute), shared future-only cascade skips cancelled + counts rules denials, PERSON_TERMINATED reasonLength · rel2089 integrated (9828 S2 + 9778 + 9575d + 9723 + 9806) · TASK-9894 view & upload only: Roster writes disabled + said why · integ/2095 TASK-9686 titled person sections (Skills + per-driver job edit re-placed) · integ/2095 TASK-9929 Transfer Yard works (transferredByUid + already-there guard) · TASK-9814 follow-up "Doesn't expire" clears+disables the reg date · TASK-9935 Doesn't-expire audit + heavy-unit confirm · TASK-9929 FU yardTransfers server clock + current yard disabled · integ/2098 9999963 (9724 supply cost switch + 9935 regNoExpiry confirm/audit + 9929 FU yard transfer, over 2097 9999958) · TASK-9652 S2 (Customer documents tag / badge counts pending uploads / rejected list + canned reasons) · fix/1018 Customer documents & requests tag (change requests share CUSTOMER_DOCS) · TASK-9725 Bill to customer setting on supply items · TASK-9724 S2 Track supply cost switch in Settings → Company · TASK-9725 S2 UI Billing section on a supply item (Item Code picker, Track-stock hint) · TASK-9682 Also goes by (person nicknames, Roster module or owner) · TASK-9941 F S3 Driver PINs (no-private-PIN chip/filter, server-side Send setup codes, owner tablet cutover date; lazy rosterPinSetup.js) replaces TASK-4692 Text setup codes · TASK-9823 Calls module in the staff-login picker (9999994) · TASK-4874 follow-up Driver Code panel warns on codes code-only sign-in can't find · integ/2108 9999999 (above 2107 9999995 / 9823 9999994 / orphans 9999998) · TASK-9994 Locates (811) staff-login grant hidden while 811 is off · TASK-10034 No Longer Employed: what is still open before Save + payroll reminder + revoke-on-save · HOTFIX stale Smart Assist banner cleared on edit open
import { initPretrip } from './views/pretrip.js?v=11020208'; // TASK-10208 DVIR export + a PDF of each inspection (dvirExportUi.js ?v=2) · TASK-10208 print body lazy (utils/dvirPrint.js ?v=1) · TASK-10208 DVIR letterhead + unit checks OK/FAILED + Export · TASK-10135 Maintenance → Fleet tools → Unit Checklists · TASK-10135 unit checklist polish (inspection-templates.js buster) · TASK-10024 (jsa.js buster: template self sign-in) · TASK-10024 (jsa.js buster: crew Sign on a filed JSA) · TASK-9996 (jsa.js buster: hospital search) · TASK-10011 (jsa.js buster: Start JSA from the office + first-open waits for jobs/templates) · TASK-9766 DVIR-delete cascade writes MAINT_TASK_STATUS · integ/2091 TASK-9559 equipment picker grouped + search · TASK-9898 follow-up suspect-reading createdAt · TASK-9659 DVIR meter plausibility confirm (window.PmStatus) · TASK-9741 unit checklists per type/unit + blank PDF · TASK-9714 (jsa.js buster) · TASK-9658 S1 #/pretrip/dvir/{id} deep link drain · TASK-9554 inspection GPS at submit + Submitted near line · TASK-9548 slice 2 (jsa.js buster: labeled job picker groups) · HOTFIX stored-XSS escaping (job/ticket text) · TASK-9511 (jsa.js buster) · TASK-9507 PIN driver preselected + locked on the DVIR, prefill no longer trips the dirty guard, equipment yard label + id match + TASK-9405 Open defects tab = one closure predicate (mechanic + repairs done + next driver) + TASK-9397 COD DVIR Save aborts if the form changed while a photo converted + TASK-932 founder-gate sweep · P0 jsa.js buster · integ/2095 TASK-9691 DVIR repair sign-off · TASK-9694 S6a (jsa.js buster) · integ/2103 9995212 (S6a jsa.js buster)
import { initMaintenance } from './views/maintenance.js?v=102398550'; // TASK-9855 S1 WO + shop receipts store the path, not a token URL (maintCosts 1139855; above 10234's 102340001) · TASK-10269 follow-up #3: WO list Cost filter/sort/badge, WO detail + print labor line, History CSV read "$1,250.00" (maintVehicleInfo 1102692) · TASK-10269 follow-up: "$1,250.00" costs count (window.Moneyish; maintVehicleInfo 1102691) · TASK-10269 panel tiles open History (maintVehicleInfo 1102690) · TASK-10206 WO costs totals-first (maintCosts 1120206; above 10208's 10350208 + 10171 item 3's 1017103) · TASK-10205 Smart Scan takes PDFs (maintCosts 1110205) · TASK-10135 Fleet tools → Unit Checklists · integ/2125: TASK-10146 maintCitations/maintSourceDvir 1027146 + WO vehicle-change audit · TASK-9934 registration no-expiry wins over a stale date · TASK-9913 "It's right" stamps server time + uid · wo-legacy-followups: driver PIN list reads imported orders through window.LegacyWorkOrder (same tab + logged-by rule as normal orders) · wo-legacy-orders: imported Open/Closed/Done work orders show in the right tab on the right vehicle (window.LegacyWorkOrder) · integ/2091 (9897 + 9899 + wo-legacy-orders + 9898 7-day review) · TASK-9899 open-WO card counts on the shared status vocabulary (window.MaintStatus from bi.js), trailer hold attention, History lists Closed/Done · TASK-9897 Work Orders filter bar (search, vehicle multi-select, type, priority, sort; More: dates/people/cost; phone bottom sheet; chips + hidden-critical line) · TASK-9898 S3-S5 tap-a-card Vehicle panel (2 buttons per card, trailer History fixed), Fleet tools menu + count tiles, local-day History filter, tenant-timezone DVIR cutoff, one shop costEntry per WO · TASK-9898 S1+S2 readings strip (mistyped-only leaves Needs Attention, attention ranked by severity) + each fact once (one fleet default-interval line, no DVIR header icon) · TASK-9766 Start Work keeps the modal open + Has open task filter + status-change audit rows + possible-repeat DVIR warning · TASK-9706 DVIR sign-off write-back no longer fails quietly · TASK-9659 PM math from window.PmStatus + suspect-reading fix + default-interval notes · TASK-9657 Approvals badge in the header · TASK-9657 S0 hold day is LOCAL + window.SafetyHold · TASK-9658 S1 #/maintenance/vehicle|task/{id} deep link drain · TASK-4884 Inspections tab (window.FacilityInspections) · TASK-9554 source-DVIR Submitted near line · TASK-9548 slice 2 shop-cost vehicle picker Trucks/Trailers groups · HOTFIX stored-XSS escaping (job/ticket text) · TASK-1550 vehicle reminders in Vehicle Info · TASK-9405 DVIR history Resolved uses window.dvirDefectClosure + TASK-932 founder-gate sweep · 2088.6: TASK-9775 driver receipt on WO completion · 2088.6: TASK-9731 trailer tire size · integ/2095 TASK-9691 DVIR sign-off · TASK-9814 follow-up reg "Doesn't expire" wins over a stale date
// TASK-10015 dispatch.js 10015001: rental counts jive (shared window.RentalLedgerMath, later-day pickups wait for their day, qty edits move stock, reversal rows)
// integ/2122 clock.js 10124720 also carries: TASK-4720 time-off requester-only + outbox
// integ/2123 clock.js 10142123 also carries: TASK-10006 S3b findOrCreateTicket stamps createdByUid (driver + clock tickets)
import { initClock } from './views/clock.js?v=10350250'; // perf/app-coldload-slim2: Payroll CSV export body moved to the lazy views/clockPayrollCsv.js · perf/app-coldload-slim: Workforce Reports Print body moved to the lazy views/clockWorkforceReport.js · TASK-1377(a) [integ/2129 addendum 10302130: + TASK-10026 sent ticket closed to drivers once everyone is clocked out; above 2128's 10302128 and the branch's 10300026] · view-only My Ticket for a Clock-only worker (integ/2127) · TASK-9797 S2 driver 'your slot' (integ/2128) · TASK-10182 workDateEnd server-owned + price-book hint only for a biller · TASK-10190 clock-out Field Ticket Settings from the job's Price Book (integ/2128) · TASK-4869 small green text -> --status-success-emphasis (dispatcher: pick the final value) · TASK-10121 Time Off grouped by employee (timeOffGroup.js 10121001; dispatcher: pick the final value) · TASK-9989 item 1 ticket gateway on for every company (fallback logged) · integ/2118: 10118001 = fresh above integ/2117 10117002 and integ/2116 10116001 (TASK-10030 merged-customer clock-in filter) · TASK-9989 Leave Unbilled on a billed day is recorded for billing · integ/2117: 10117002 = fresh above 10034 10034001 / 10040b 10040101 / main 10036001 · TASK-10040 day over 24h + Shop past midnight review flags · TASK-1507 overtime weeks in the COMPANY zone + workweek start (payRates 1507020) · TASK-9988 S1 openEntryForClockOut (Billing's clock-them-out-first) · TASK-9878 S1 (re-port on 2110) pay-period grid + company-workweek OT + Payroll Prep hours read window.PayPeriodHours (shared with get_payroll_hours) · TASK-9971 typed clock times in the COMPANY zone (fall-back hour asked, gap refused) · TASK-9646 S2 ticket totals computed server-side (client sends lines only) · TASK-9170 gauge mark · TASK-9763 S1 clock-in preselects only YOUR paired unit · TASK-9884 MAINT_TASK_STATUS audit rows labelled · TASK-9715 audit rows · TASK-9443 slice 3 Payroll exported chip on Workforce day headers · simple-mode equipment tie OFF (TASK-9781) · TASK-9752 fuel-card PIN view rows in the audit log · TASK-9440 crew Switch Job review hides the fuel surcharge · TASK-9443 slice 2 manual-entry payroll warning + After payroll badge · TASK-9694 polish shift-note placeholder follows requireShiftNotes · TASK-9666/9667 field-ticket items carry Show at $0 + invoice-hide request · integ/b-hr-billing re-review: unknown pay-period lock state still warns (payrollRunLock.js 9443002) · TASK-9443 pay-period soft lock: warn before editing/deleting a shift in an already-exported period + TASK-9220 sidebar PERSONAL clock hands off to the Clock view (one clock) · TASK-9603 own-only time-off listener for non-full readers · TASK-9534 license wording in identity toasts · TASK-9359 (TASK-9459 whole-day flat hold) identity off the broadcast people doc + stuck-shift-morning a shift left open on an earlier day: clock-in asks for the finish time, closes it (closedLate), then clocks in + Workforce flags name closed-late shifts + clock-double-tap one open shift per person (clock-in guard + lock claim, Clock Out asks which shift, Close newest shift) + TASK-9579 Clock sheet says when no truck was found for the shift's day + two open shifts flagged on Currently Clocked In / Hours grid / status + clock-out acts on the newest + TASK-9548 slice 2 job pickers Today/In progress/Recent + fix-autofill-jobday clock-out equipment fills the shift's own day or nothing + TASK-9548 grouped customer pickers + TASK-9471 Audit Log shows job PO old -> new + TASK-9451 claim-review moved-day guard + open-shift reason + vehicleReimb buster + TASK-9439 approval before payroll + TASK-9411 back to the time clock after a ticket + TASK-4713 sent-stamp write is member-only (PIN drivers never write it) + TASK-9402 HEIC receipts/tickets + TASK-9363 Tier 4 ticket guard + TASK-9390 jobTypeCode + TASK-9388 + TASK-932 founder-gate sweep + TASK-1572 truck ask at clock-in + Ready completeness gate + TASK-1483 vehicle reimbursement · P0 vendors-not-customers (adjustment + reports pickers) · TASK-9693 shift notes no longer copied onto the shared ticket · equipment half-hour rounding company default + per-line choice (TASK-9489) · TASK-9665 consumables (rel2087) · 2088.6: TASK-9713 driver self clock-in window (15 min / 24h flagged) · TASK-9858 PIN count via window.accessPinMap · TASK-9836 field-ticket items for driver links come rate-free from getFieldTicketItems · rel2089 integrated (9804 + 9816-9819 + 9836) + TASK-9816-9819 harvest smalls (selfedit OFF, Time Clock) · TASK-9689 s3 Pay Settings 'How is overtime figured with two rates?' link → HR assistant · integ/2098 9995010 (9689 S3 Pay Settings link merged over 2097 9995009) · TASK-9763 S2 different truck at clock-in · TASK-9694 S6a crew customer reads → customerDirectory · TASK-9941 F S2 Clock tablet PIN checked on the server (verifyClockPersonCode), unverified punches marked for review, tablet private-PIN setup · TASK-9646 S4a no jobNotes copy on new tickets + Switch Job review amounts from driverTicketPrices · TASK-9527 Pay Settings save = owner or Workforce module (canEditPaySettings) · integ/2108.1: TASK-9983 Workforce pay fork on an invoiced shift (pay-only save, reason, place/job pickers, wrong-entry guard) · integ/2109 merge of 2108.1 over 2109 clock 9996680 / 2108.1 9998310 (9998320) · TASK-10036 invoice-notes merge: coworker line kept unless confirmed + who/surface stamp · TASK-10034 Payroll Prep/CSV keep NLE people through their last-day period + hours-after-last-day flag + final-check delete warning · TASK-1507 overtime weeks in the COMPANY zone + workweek start (payRates 1507020) · TASK-10040b merge of 10040 (10040001) over 2115 (10036001) = 10040101
// integ/2122 jobboard.js 10130116 also carries: fix/receipt-label-wrap (driver Record Expense receipt hint under the label); TASK-10116 stop Open-in-Maps icon + http(s)-only stop links
import { initJobBoard } from './views/jobboard.js?v=1105010267'; // TASK-9264 · TASK-10267 sent lock only when the server locked it · [integ/2129 addendum 10302130: + TASK-10026 sent-ticket driver lock] · driver Materials Used on the Build Ticket (a claim on his own clock entry; UI is the lazy jbMaterialsUsed.js?v=1, integ/2128 perf) (integ/2128 addendum 10302129, above 10302128 and 9264's 10299264) · TASK-9797 S2 driver 'your slot H2-Night' · TASK-9694 S6 crew Updates feed (lazy jobScreenFiles.js 9694601 + jobUpdates.js 96946) · TASK-10190 follow-ups (driverFieldTicketSettings read-time pads) · primeSelf pair (integ/2128): clock actions wait for an unconfirmed own mode · TASK-9694 driver job screen groups Docs by isJobDocEntry (jobScreenFiles 9694503; S5c held out of 2127) · TASK-10141 Paid with: charge account + TASK-10145 (stacked) ·  TASK-10106 S1b driver job details show rental asks + the office's answer · TASK-9694 S4 switch from job B's screen (sheet bound to entry A, A's own note, lazy jobScreenSwitch.js) · TASK-10024 Open to sign for office + lands on the viewer's line · integ/2113: 10024001 = the highest of TASK-10024 10024001 / integ 10017002 · integ/2113: fresh value above main's served 10017001 (TASK-10014 set 10014001 on an older base) · TASK-10014 fast-phone clock-out retries on the phone clock · expense lost-answer v2 (Finish sending replays the same id + attaches the photo server-side) · integ/2094 pre-ship 811 renewal find · TASK-10017 attach adopts the server ticket on a rules refusaler · TASK-10011 Start JSA goes through JSAModule.startForJob · TASK-9971 board time edit / forgot clock-out / ticket rows in the COMPANY zone · TASK-9646 S2 ticket totals computed server-side (client sends lines only) · integ/2094 pre-ship 811 renewal finder · TASK-9763 S1 clock-in preselects only YOUR paired unit + "Your truck" line · integ/2092 early-stop row says "Not active until" · TASK-9920 811 dig-start safety (fwd 2090.1) · TASK-9710 per-driver Add/Edit Job gate · integ/2091 TASK-9898 follow-up suspect-reading createdAt · 2088.5: TASK-9865 crew preselect · TASK-9867 fix-waiting chip · TASK-9868 ticket doc type + uploader · TASK-9862 driver locate card · TASK-9767 driver sees project 811 tickets (2088.2) · TASK-9740 crew-scoped BOL filing + refusal shown to the driver + PDF via getJobDocumentPdf · TASK-9794/9795 clock review flags + driver time-edit lock (merge rel2086) · TASK-9740 BOL behind settings.jobDocumentsEnabled (rel2087) · TASK-9791 driver ticket shows the fuel surcharge row when prices are on · simple-mode equipment tie OFF at clock-in (TASK-9781) · TASK-9719 voided PPE issue drops out of the sign-off queue · steve-lines-gps Line Info merge baseline advances after each save + GPS Grab fresh fix · TASK-9714 job-screen bottom padding + scroll-padding clear the sticky bar · TASK-9694 polish job-screen cards + pills + clock-in feedback + tap-time clockIn · TASK-9712 Switch Job stamps clock-out GPS on A + clock-in GPS on B + TASK-9711 classic Build Ticket placards ellipsize at 390px + TASK-9694 job-screen placards ellipsize at 390px + TASK-9694 driver job screen = the ticket (switch settings.ticketOnJobScreen) + TASK-9563 driver project strip via driverProjectInfo + TASK-9561 Add Job header button + stuck-shift-morning board + standalone clock-in close a shift left open on an earlier day + TASK-9593 own-cards-only expense picker + Other/not listed · clock-double-tap one open shift per person (board clock-in guard + lock claim, close-out asks which shift) + TASK-9592 truck/trailer Cost Allocation hides the Vehicle field + sends its vehicle · TASK-9579 close-out sheet says when no truck was found for the shift's day · fix-autofill-jobday clock-out equipment fills the shift's own day or nothing · fix-1999-followups Personal Actions tile label re-reads the open shift after clock-in / clock-out · TASK-9576 Personal Actions clock-out loads its own context when the Job Board was never opened · HOTFIX stored-XSS escaping + TASK-9556 shared jbRunSwitchJob for both Switch Job buttons · TASK-9548 grouped customer pickers · TASK-9511 Cancel on a job's JSA returns to the job · TASK-9493 split-receipt lines in Record Expense · release 1.15.1973: TASK-1483 Personal Vehicle section light variant on the clock-out sheet + equipment Start/End boxes fit 390px · TASK-4713 sent-stamp write is member-only (PIN drivers never write it) + TASK-9402 HEIC receipts/tickets/expenses/Smart Scan/stamp + TASK-9399 customer filter sheet + TASK-932 founder-gate sweep + TASK-1572 truck ask at clock-in + Ready completeness gate + TASK-1483 vehicle reimbursement · TASK-9693 jbSaveTicket no longer writes ticket.shiftNotes · TASK-4882 safety outline (rel2087) · TASK-9740 driver BOL (rel2087) · TASK-9742 safety sign-in lists attached meeting docs (rel2087) · TASK-9859 811 expiry badge on the driver's ticket rows · TASK-9767 driver sees project 811 tickets (2088.2) · 2088.6: TASK-9650 project doc refs on jobs · TASK-9713 clock-in window · TASK-9721 optional Job on the PPE self-log · TASK-9707 job modal lists the day's truck/trailer/equipment DVIRs + Inspections-list defect count · TASK-9809 BOL harvest 10/13 (relay, swapped-in driver, hazmat) · TASK-9750 tentative/half-day labels · TASK-9763 S2 different truck at clock-in · TASK-9694 S6a crew customer reads → customerDirectory · integ/2103 9995211 (S6a) · TASK-10036 asks before removing a crew note line (before the ack timer)
// TASK-10013 dispatch.js 10013001 (integ/2113 ships the higher 10015763) — Add Job: new project takes the matching stop's pin, Saved Locations search focused + this customer's/recent first + Last used, load calculator names assets with no weight / trucks with no capacity
// TASK-10010 dispatch.js 10010001 (integ/2113 ships the higher 10015763) — delete job: busy state + one at a time, Remove just this day, server rental give-back, Restore on the toast + ⋮ Recently deleted (lazy jobDeleteUx.js?v=10010001)
// integ/2122 dispatch.js 10132079 also carries: TASK-10116 http(s)-only stop links + Open-in-Maps icon (branch 10126116), TASK-10079 permit-cost decisions via decidePermitCost, TASK-10093 driver Help sheet Academy videos, TASK-4720 driver time-off outbox, TASK-9703 S1 driver Point of Contact, TASK-10067c consumable counts via adjustAssetQuantity
// integ/2123 dispatch.js 10142123 also carries: TASK-10006 S3 return-job prompt + pickup overdue / Mark picked up + S3b Undo (branch 10015765); TASK-4810 #2 pre-ledger rental reversal row in the same transaction; TASK-10106 S2 driver Add Job shows Type (company list; driverSaveJob checks it) (branch 10140106); TASK-10106 S4 driver Add Job Saved Locations picker (read-only, driverSavedLocations) (branch 10144106); TASK-10126 fill job title from project name (company setting, default off) (branch 10132126)
// integ/2124 dispatch.js 10272124 also carries: TASK-9862 follow-up locate flag = red icon + red right edge (branch 10272124); TASK-10100 Notify Crew copy; TASK-4800 S4 Maps "Estimated locations" layer (hollow markers, last record / home yard, never confirmed; lazy views/mapEstimated.js) (branch 10164800); TASK-10106 S1a/S1b (branch 10146106)
// integ/2126 busters (v1.15.2126.0): dispatch.js 10276006 = TASK-10106 S5 (10275106) + TASK-10006 S2 (10276006) + TASK-10027 (10272710) + TASK-10112 · jobboard.js 10179694 = TASK-9694 S5/S5b (lazy jobScreenFiles.js 9694502) · roster.js 10172710 = TASK-10027 + TASK-10098 + TASK-10094 + TASK-9730 · hr.js 99999991 = TASK-10027 + TASK-9581 · features.js 10195275 = TASK-9527(5) · compliance.js 10282126 = TASK-10154 (10154001) · account.js / notes.js / ap.js / billing.js / maintenance.js / pretrip.js 10282126 (TASK-10027, 10112, 10145 r2/r3 folds, 9730, 10146 follow-up, 9581 jsa.js) · help-faq.js 101552126 (10106 S5, 10006 S2, 10098, 10112 FAQ).
import { initDispatch } from './views/dispatch.js?v=1105310248'; // TASK-4789 S1 drawn map layers + S2 attach to a job stop (lazy mapDrawLayers.js: routes, red lines, areas, no-go) · TASK-10248 New Job form: one chip / checkbox / inline-button look (1105310248, above 1105210278) · TASK-10301 personalized text drops the footer (1105110301) · TASK-10299 Notify Crew opens personalized when Personalized Schedule is on (1105010299, above every READY value 1104996296) · TASK-10295 Destinations not Stops (1104610295, above 10288 1104510288) · TASK-10288 move a job day safely (1104510288, above 10291 1104410291) · TASK-10291 Edit Job no longer shows the previous job's mileage · perf/app-coldload-slim2: Export Jobs CSV body moved to the lazy views/dispatchExportJobs.js · TASK-10224 first office photo ticks Include photos in Driver's job view + hidden-photo hint (103310224, above 10213 B4's 103310213) · integ/2129 one value 101952186 (addendum; 101952185 before it) above 2128's 101952130 and every pick (10184 101952184, 4579 10294579, 4789 10284789, 10168 10281168) · TASK-10184 time-off warning on assign (Assign Crew + Add/Edit Job) · TASK-10183 regular-day-off warning + rotation days (lazy rotationDays.js) · TASK-4786 A2 map opens on Today · TASK-1581 S3 Add to project on the Similar-job warning · TASK-4579 Smart Job Builder's AI-picked customer runs onCustomerChange · TASK-9811 S2 main + extra POs (job-po-read.js 9811003) · TASK-10168 811 upload review = one plain table (lazy views/p811ReviewTable.js?v=10168002) (integ/2129) · TASK-9631 S3 saved named maps (lazy mapViews.js) · TASK-10193 S2 Assign Crew "Who's in what" seats (assignPairing.js 6) (integ/2128 addendum 101952130: one value above 10195's 101952128, 10193's 101932128 and 9631 S3's 10289631) · TASK-10195 board filter row mobile (chip strip + Load older line) · TASK-9797 S2 propose_job_slots duplicate-slot warning · TASK-9694 S6 read-only office notes + crew Updates (lazy jobUpdates.js) · TASK-10182 propagateJobTitle ticket half only for a biller · TASK-10180 office Locates list (lazy p811LocatesList.js 10180001) · TASK-10167 S2 811 fail-open (lazy p811Reads.js 10167002) · TASK-9797 item 1 named slots (debugScroll/debugWheel removed) · TASK-10190 field-ticket settings by Price Book + TASK-10181 Add Job 811 nudge (lazy p811JobNudge.js) (integ/2128) · TASK-9694 S5c stale Edit Job saves keep driver notes/pics/Docs · TASK-10106 S5 driver PO on blank + photos/permits · TASK-10006 S2 job type rentals out/back · TASK-10027 muted-tenant crew-text refusal · TASK-10106 S1b driver asks for rentals, office Confirm/Decline (deploys only via Assign Crew save) · TASK-10106 S1a driver Add Job standard equipment (driverSaveJob classifies every asset; rentals/stock refused) · TASK-10106 S4 driver Add Job Saved Locations picker (read-only, driverSavedLocations) · TASK-10106 S2 driver Add Job shows Type (company list; driverSaveJob checks it) · integ/2118: fresh above TASK-10050 10050001 (2117) and integ/2113.2 10031764 (TASK-10030 new-job picker hides Inactive/merged + clock-in / Switch Job case-blind + delete guard) · TASK-10007 pinned asset strip (owned / out / available) + starred-first Add Job picker · TASK-10008 tap a calendar day → Add Job on it · TASK-10009 job type under the title + quickview Edit Job opens the full form · TASK-10011 Start JSA on Job Details + quickview · TASK-9943 day UX: Remove Day shows Checking… + records refused/cancelled removes, Edit Job → Remove Day list, crew editor Cancel offers to remove a just-added day, Add Day asks on a job idle > 7 days (Start new job = Copy to New Job), JOB_DAY_ADD row (lazy jobDayUx.js?v=9997943) · TASK-4803 S3b builder customer hints (usual PO/well/AFE, repeat job + copy, PO in title; lazy jobBuilderCustomerHints.js) · TASK-9763 S3 crew seniority ("first on, last off": jobDay.crewOrder + Dispatch Settings → Crew seniority) ·TASK-9934 registration no-expiry wins over a stale date · TASK-9763 S1 "Who's in what" per-unit pairing in Assign Crew + driver card line · 1017 Ticket-Only keeps shop/yard hours (integ/2096: 9995138 was taken twice) · wo-legacy-followups: board safety-hold target for an imported critical order by NAME (window.LegacyWorkOrder, whole fleet), never its old row number · integ/2091 trailguide.js buster + TASK-9569 job-card reminder bell · TASK-9905 regenerate driver link via server callable (revokes link members) · TASK-4692 schedule-text setup-code wording true for a new hire with nothing sent + pin-helper label "Setup code" (not "sent") for a pending new hire · TASK-9884 crew role tag getTypeLabel args (slug, kind) · 2088.5: TASK-9628 maps polish · TASK-9842 mass text status · TASK-9844 missing-skill warning · rel2086 dispatchui-1012 (harvest q11/q15, TASK-9648 811 permit ids) · TASK-9467 S1 job delete via deleteJobLedgered (server-only outside demo) · Lanes multi-job days: count badge + tap lists the day's jobs + name lists the week · TASK-9587 Claude job request opens pre-filled in the Job Builder (amber assumed values, create-only save) · TASK-9540 saved-place categories + shapes on Maps, one Manage places (Add Job + Maps) · TASK-9541 permanent stop ids · TASK-9598 Copy to New Job titled "Copy of J-#### — New Job" + copied-from banner w/ Clear crew & equipment + switch-job tie rule follows clock-out mode (TASK-9781) · TASK-9720 consumable deduct skips stock-tracked items · TASK-9657 Approvals badge in the header · steve-bluejay-lines-gps crew picker doc-id wins over unit, saved-location pins, share-link address geocode · project-job-click openJobDetailsById (Projects job rows open details, any date) · TASK-9645 B/C Invoiced/Paid chips + Edit Job invoice lock · TASK-9712 Switch Ticket passes its jobs for the switch geofence · TASK-9657 S0 board holds via window.SafetyHold + local today · TASK-9631 F1-F4 map fixes (saved-location gate, keep pin on edit, no name-match delete, keep user map view) + TASK-4803 S3a builder usual-crew suggestions + TASK-9202/9517 Dispatch Settings crew-text menu copy · TASK-9627 whole-job Delete removes EVERY day (Firestore read by jobId, not the 90-day board window) + TASK-9236 job details: labeled Remove Day on each day row + always-on hint + TASK-9563 driver PIN job form: project picker (customer-switch safe), stops (server-geocoded), crew + vehicles/trailers, stale-pin drop, credential toast + TASK-9606 job form Project picker filtered to the picked customer + TASK-9597 Crew text layout setting for Notify Crew + TASK-9599 job form offers to add the job's customer to a project with no customer + honest conflict toast + TASK-1387 archived-ticket grey dot on dispatch calendars + smartassist-names-2 job well name + location noFuzzy + TASK-4834 job-vendor-number off SmartAssistant + trim on save + TASK-4800 S3 planEquipmentOnSite + openEquipmentMap hand-off + TASK-9359 identity off the broadcast people doc + stuck-shift-morning driver PIN clock-in closes a shift left open on an earlier day + TASK-4665 Notify Crew SMS capacity reads twilio.* only + clock-double-tap driver PIN clock-in guard + clock-out asks which shift + TASK-9516 editor-name alert setting + edit stamp + TASK-9588 No ticket emails checkbox + TASK-9579 Switch Ticket says when no truck was found for the shift's day + names a second open shift + TASK-9546 PDF under Add Photo files into Load Permit Files; permit section always open + TASK-9532 Notify Crew keeps unsent draft + fix-autofill-jobday Switch Ticket closes only the shift's own day's equipment + fix-1999-followups Switch Ticket reads the open shift by personId + loads its own customers/jobs off the Dispatch view · HOTFIX stored-XSS escaping + TASK-9556 Switch Job modal finalizes the old shift via the switchJob op (old labor line closed) + TASK-9548 grouped customer pickers + TASK-9508 Maps equipment paint order/labelled counts/list header/loading + TASK-9510 asset picker yard labels + typed qty ticks the row + TASK-1550 job reminders + TASK-4803 builder past-stop pre-fill + TASK-4695 (+ TASK-4800 Equipment-on-site layer) Add Job modal: Project picker + collapsed groups + TASK-4800 Maps Equipment-on-site layer + deployed-equipment list + TASK-4873 no computed code in texts while a setup code is pending + TASK-9464 waypoint-log edit dedupe + TASK-9175 Maps key/layers/project filter + TASK-4810 over-deploy warning + needs-recount pill/preview + Edit Job rental notice + merge 1976: TASK-9430 string CDL restrictions + TASK-9446 Draft Price Book hint + TASK-9407 default start date + TASK-9421 saved-location customer filter + TASK-9439 trailguide buster + TASK-9163 Job Builder refuses lookup questions + TASK-4808 job Notes auto-grow + TASK-9397 COD exam report always saves + TASK-9278 job number is the 8th job-card title token (move/untick); month chip shows it alone + TASK-932 founder-gate sweep + TASK-1483 vehicle reimbursement · P0 vendors-not-customers (job customer dropdown, Smart Job Builder match, saved-location picker) · rate picks keep Company default, three-way (TASK-9489) · TASK-9740 driver BOL (rel2087) · TASK-9779 811-only login sees only the 811 window (rel2087) · TASK-9740 signed documents open via getJobDocumentPdf (no stored URL) · TASK-9859 811 window: bulk upload + drag-drop, customer-first job list, ticket customer from the job, expiry dates/badges/warnings, good-for-N-days setting · 811 project dropdown · 2088.6: TASK-9734 map route coverage · TASK-9650 project doc library on jobs · TASK-9713 clock-in window · TASK-9710 driverJobEdit field-path save · TASK-9703 driver Add Another Day / Copy to New Job / own Delete · TASK-9737 Claude job request start time prefill · TASK-9624 Add Job: fill from a work order / rate con / BOL · TASK-9887 Locates (+ row menu hidden until placed; potential jobs/projects bank, directions skeleton): site card actions + Attach to project, clickable summary tiles, ⋮ menu kept in the viewport, stronger search · rel2089: + TASK-9809 BOL harvest, TASK-9816-9819 harvest smalls · TASK-9750 tentative / half-day job days · TASK-9894 view & upload only: Dispatch settings Save disabled + said why · TASK-4794 date-sync never fills a hole inside the old range · integ/2094 TASK-9886 DVIR warning per truck + driver + day (window.DvirCoverage) · integ/2095 TASK-9651 Projects module gate · integ/2098 9995142 (TASK-9769 customer text requests — changed without a bump) · TASK-9934 registration no-expiry wins over a stale date · TASK-9652 S2 (Customer documents tag / badge counts pending uploads / rejected list + canned reasons) · fix/2096-preship Assign Crew pairing snapshot before any await, bound to its day · TASK-9497 server stop geocode suggestions ("Use this location" in Not plotted) + query= links + place pin before viewport + stop provenance kept on edit · TASK-9763 S2 driverTruckSwap setting · integ/2102 9996900 (9497 9995497 + 4803 S3b 9996803 + 9763 S2 9995165 merged) · integ/2106 9997980 (notes golden orb: trailguide buster inside dispatch.js moved; 9997957 seen on another branch) · TASK-4794 follow-up ticket-locked kept day counts as a surviving day (integ/2108 9998080, above 2107 9998050 / orphans 9998071) · TASK-9994 everything 811 gone while 811 is off (Locates window, 811-only screen, job form leftovers, settings) · TASK-9994 811 tree (customer > project > site) + sort (Soonest / Newest / A-Z) + Show 811 warnings (default off) + 811 Locates settings section · TASK-9994 p1 811 side never makes a job: Job picker (active/upcoming + ended 30 days, search, + Add as potential job / project) in Site name's spot, Start a job here only with Dispatch write access (else Add as potential) · TASK-9838 driver time-off: Unpaid only without a plan + unpaid-taken line · TASK-10006 Create return job (Job Details strip + button, return Copy, On Site project spill) · TASK-9763 S4 team pairing (assignPairing.js 3) · integ/2113: 10015763 = the highest of TASK-9763 S4 10015763 / TASK-10006 10015006 / TASK-10013 10013001 / TASK-10010 10010001 · integ/2113.1: fresh value above integ/2113 10015763 — TASK-10031 job form Project list from price books + Site picker · TASK-10050 List view header toggles (show vehicles / crew on every job; trailguide + help-faq busters) · TASK-4696 project defaults pre-fill (Well name / AFE / Vendor / Job type + Inherited from project hint) · TASK-4789 S1 drawn map layers (lazy mapDrawLayers.js: routes, red lines, areas, no-go) · TASK-4803 S2 Smart Job Builder conversation (proposal card: Use this / Revise / Start over; jobBuilderConversation.js?v=1), fresh above 10118501 · integ/2118: fresh above TASK-10050 10050001 (2117) and integ/2113.2 10031764 (TASK-10030 new-job picker hides Inactive/merged + clock-in / Switch Job case-blind + delete guard) · TASK-10007 pinned asset strip (owned / out / available) + starred-first Add Job picker · TASK-10008 tap a calendar day → Add Job on it · TASK-10009 job type under the title + quickview Edit Job opens the full form · TASK-10011 Start JSA on Job Details + quickview · TASK-9943 day UX: Remove Day shows Checking… + records refused/cancelled removes, Edit Job → Remove Day list, crew editor Cancel offers to remove a just-added day, Add Day asks on a job idle > 7 days (Start new job = Copy to New Job), JOB_DAY_ADD row (lazy jobDayUx.js?v=9997943) · TASK-4803 S3b builder customer hints (usual PO/well/AFE, repeat job + copy, PO in title; lazy jobBuilderCustomerHints.js) · TASK-9763 S3 crew seniority ("first on, last off": jobDay.crewOrder + Dispatch Settings → Crew seniority) ·TASK-9934 registration no-expiry wins over a stale date · TASK-9763 S1 "Who's in what" per-unit pairing in Assign Crew + driver card line · 1017 Ticket-Only keeps shop/yard hours (integ/2096: 9995138 was taken twice) · wo-legacy-followups: board safety-hold target for an imported critical order by NAME (window.LegacyWorkOrder, whole fleet), never its old row number · integ/2091 trailguide.js buster + TASK-9569 job-card reminder bell · TASK-9905 regenerate driver link via server callable (revokes link members) · TASK-4692 schedule-text setup-code wording true for a new hire with nothing sent + pin-helper label "Setup code" (not "sent") for a pending new hire · TASK-9884 crew role tag getTypeLabel args (slug, kind) · 2088.5: TASK-9628 maps polish · TASK-9842 mass text status · TASK-9844 missing-skill warning · rel2086 dispatchui-1012 (harvest q11/q15, TASK-9648 811 permit ids) · TASK-9467 S1 job delete via deleteJobLedgered (server-only outside demo) · Lanes multi-job days: count badge + tap lists the day's jobs + name lists the week · TASK-9587 Claude job request opens pre-filled in the Job Builder (amber assumed values, create-only save) · TASK-9540 saved-place categories + shapes on Maps, one Manage places (Add Job + Maps) · TASK-9541 permanent stop ids · TASK-9598 Copy to New Job titled "Copy of J-#### — New Job" + copied-from banner w/ Clear crew & equipment + switch-job tie rule follows clock-out mode (TASK-9781) · TASK-9720 consumable deduct skips stock-tracked items · TASK-9657 Approvals badge in the header · steve-bluejay-lines-gps crew picker doc-id wins over unit, saved-location pins, share-link address geocode · project-job-click openJobDetailsById (Projects job rows open details, any date) · TASK-9645 B/C Invoiced/Paid chips + Edit Job invoice lock · TASK-9712 Switch Ticket passes its jobs for the switch geofence · TASK-9657 S0 board holds via window.SafetyHold + local today · TASK-9631 F1-F4 map fixes (saved-location gate, keep pin on edit, no name-match delete, keep user map view) + TASK-4803 S3a builder usual-crew suggestions + TASK-9202/9517 Dispatch Settings crew-text menu copy · TASK-9627 whole-job Delete removes EVERY day (Firestore read by jobId, not the 90-day board window) + TASK-9236 job details: labeled Remove Day on each day row + always-on hint + TASK-9563 driver PIN job form: project picker (customer-switch safe), stops (server-geocoded), crew + vehicles/trailers, stale-pin drop, credential toast + TASK-9606 job form Project picker filtered to the picked customer + TASK-9597 Crew text layout setting for Notify Crew + TASK-9599 job form offers to add the job's customer to a project with no customer + honest conflict toast + TASK-1387 archived-ticket grey dot on dispatch calendars + smartassist-names-2 job well name + location noFuzzy + TASK-4834 job-vendor-number off SmartAssistant + trim on save + TASK-4800 S3 planEquipmentOnSite + openEquipmentMap hand-off + TASK-9359 identity off the broadcast people doc + stuck-shift-morning driver PIN clock-in closes a shift left open on an earlier day + TASK-4665 Notify Crew SMS capacity reads twilio.* only + clock-double-tap driver PIN clock-in guard + clock-out asks which shift + TASK-9516 editor-name alert setting + edit stamp + TASK-9588 No ticket emails checkbox + TASK-9579 Switch Ticket says when no truck was found for the shift's day + names a second open shift + TASK-9546 PDF under Add Photo files into Load Permit Files; permit section always open + TASK-9532 Notify Crew keeps unsent draft + fix-autofill-jobday Switch Ticket closes only the shift's own day's equipment + fix-1999-followups Switch Ticket reads the open shift by personId + loads its own customers/jobs off the Dispatch view · HOTFIX stored-XSS escaping + TASK-9556 Switch Job modal finalizes the old shift via the switchJob op (old labor line closed) + TASK-9548 grouped customer pickers + TASK-9508 Maps equipment paint order/labelled counts/list header/loading + TASK-9510 asset picker yard labels + typed qty ticks the row + TASK-1550 job reminders + TASK-4803 builder past-stop pre-fill + TASK-4695 (+ TASK-4800 Equipment-on-site layer) Add Job modal: Project picker + collapsed groups + TASK-4800 Maps Equipment-on-site layer + deployed-equipment list + TASK-4873 no computed code in texts while a setup code is pending + TASK-9464 waypoint-log edit dedupe + TASK-9175 Maps key/layers/project filter + TASK-4810 over-deploy warning + needs-recount pill/preview + Edit Job rental notice + merge 1976: TASK-9430 string CDL restrictions + TASK-9446 Draft Price Book hint + TASK-9407 default start date + TASK-9421 saved-location customer filter + TASK-9439 trailguide buster + TASK-9163 Job Builder refuses lookup questions + TASK-4808 job Notes auto-grow + TASK-9397 COD exam report always saves + TASK-9278 job number is the 8th job-card title token (move/untick); month chip shows it alone + TASK-932 founder-gate sweep + TASK-1483 vehicle reimbursement · P0 vendors-not-customers (job customer dropdown, Smart Job Builder match, saved-location picker) · rate picks keep Company default, three-way (TASK-9489) · TASK-9740 driver BOL (rel2087) · TASK-9779 811-only login sees only the 811 window (rel2087) · TASK-9740 signed documents open via getJobDocumentPdf (no stored URL) · TASK-9859 811 window: bulk upload + drag-drop, customer-first job list, ticket customer from the job, expiry dates/badges/warnings, good-for-N-days setting · 811 project dropdown · 2088.6: TASK-9734 map route coverage · TASK-9650 project doc library on jobs · TASK-9713 clock-in window · TASK-9710 driverJobEdit field-path save · TASK-9703 driver Add Another Day / Copy to New Job / own Delete · TASK-9737 Claude job request start time prefill · TASK-9624 Add Job: fill from a work order / rate con / BOL · TASK-9887 Locates (+ row menu hidden until placed; potential jobs/projects bank, directions skeleton): site card actions + Attach to project, clickable summary tiles, ⋮ menu kept in the viewport, stronger search · rel2089: + TASK-9809 BOL harvest, TASK-9816-9819 harvest smalls · TASK-9750 tentative / half-day job days · TASK-9894 view & upload only: Dispatch settings Save disabled + said why · TASK-4794 date-sync never fills a hole inside the old range · integ/2094 TASK-9886 DVIR warning per truck + driver + day (window.DvirCoverage) · integ/2095 TASK-9651 Projects module gate · integ/2098 9995142 (TASK-9769 customer text requests — changed without a bump) · TASK-9934 registration no-expiry wins over a stale date · TASK-9652 S2 (Customer documents tag / badge counts pending uploads / rejected list + canned reasons) · fix/2096-preship Assign Crew pairing snapshot before any await, bound to its day · TASK-9497 server stop geocode suggestions ("Use this location" in Not plotted) + query= links + place pin before viewport + stop provenance kept on edit · TASK-9763 S2 driverTruckSwap setting · integ/2102 9996900 (9497 9995497 + 4803 S3b 9996803 + 9763 S2 9995165 merged) · integ/2106 9997980 (notes golden orb: trailguide buster inside dispatch.js moved; 9997957 seen on another branch) · TASK-4794 follow-up ticket-locked kept day counts as a surviving day (integ/2108 9998080, above 2107 9998050 / orphans 9998071) · TASK-9994 everything 811 gone while 811 is off (Locates window, 811-only screen, job form leftovers, settings) · TASK-9994 811 tree (customer > project > site) + sort (Soonest / Newest / A-Z) + Show 811 warnings (default off) + 811 Locates settings section · TASK-9994 p1 811 side never makes a job: Job picker (active/upcoming + ended 30 days, search, + Add as potential job / project) in Site name's spot, Start a job here only with Dispatch write access (else Add as potential) · TASK-9838 driver time-off: Unpaid only without a plan + unpaid-taken line · TASK-10006 Create return job (Job Details strip + button, return Copy, On Site project spill) · TASK-9763 S4 team pairing (assignPairing.js 3) · integ/2113: 10015763 = the highest of TASK-9763 S4 10015763 / TASK-10006 10015006 / TASK-10013 10013001 / TASK-10010 10010001 · integ/2113.1: fresh value above integ/2113 10015763 — TASK-10031 job form Project list from price books + Site picker · TASK-10050 List view header toggles (show vehicles / crew on every job; trailguide + help-faq busters) · TASK-9501 Stops wording (fresh above 10118001) · TASK-4789 S1 drawn map layers (lazy mapDrawLayers.js: routes, red lines, areas, no-go) · TASK-9631 S2 + Pin on the map: add / move (Undo) / delete saved places (lazy mapPins.js?v=1) · integ/2118: fresh above TASK-10050 10050001 (2117) and integ/2113.2 10031764 (TASK-10030 new-job picker hides Inactive/merged + clock-in / Switch Job case-blind + delete guard) · TASK-10007 pinned asset strip (owned / out / available) + starred-first Add Job picker · TASK-10008 tap a calendar day → Add Job on it · TASK-10009 job type under the title + quickview Edit Job opens the full form · TASK-10011 Start JSA on Job Details + quickview · TASK-9943 day UX: Remove Day shows Checking… + records refused/cancelled removes, Edit Job → Remove Day list, crew editor Cancel offers to remove a just-added day, Add Day asks on a job idle > 7 days (Start new job = Copy to New Job), JOB_DAY_ADD row (lazy jobDayUx.js?v=9997943) · TASK-4803 S3b builder customer hints (usual PO/well/AFE, repeat job + copy, PO in title; lazy jobBuilderCustomerHints.js) · TASK-9763 S3 crew seniority ("first on, last off": jobDay.crewOrder + Dispatch Settings → Crew seniority) ·TASK-9934 registration no-expiry wins over a stale date · TASK-9763 S1 "Who's in what" per-unit pairing in Assign Crew + driver card line · 1017 Ticket-Only keeps shop/yard hours (integ/2096: 9995138 was taken twice) · wo-legacy-followups: board safety-hold target for an imported critical order by NAME (window.LegacyWorkOrder, whole fleet), never its old row number · integ/2091 trailguide.js buster + TASK-9569 job-card reminder bell · TASK-9905 regenerate driver link via server callable (revokes link members) · TASK-4692 schedule-text setup-code wording true for a new hire with nothing sent + pin-helper label "Setup code" (not "sent") for a pending new hire · TASK-9884 crew role tag getTypeLabel args (slug, kind) · 2088.5: TASK-9628 maps polish · TASK-9842 mass text status · TASK-9844 missing-skill warning · rel2086 dispatchui-1012 (harvest q11/q15, TASK-9648 811 permit ids) · TASK-9467 S1 job delete via deleteJobLedgered (server-only outside demo) · Lanes multi-job days: count badge + tap lists the day's jobs + name lists the week · TASK-9587 Claude job request opens pre-filled in the Job Builder (amber assumed values, create-only save) · TASK-9540 saved-place categories + shapes on Maps, one Manage places (Add Job + Maps) · TASK-9541 permanent stop ids · TASK-9598 Copy to New Job titled "Copy of J-#### — New Job" + copied-from banner w/ Clear crew & equipment + switch-job tie rule follows clock-out mode (TASK-9781) · TASK-9720 consumable deduct skips stock-tracked items · TASK-9657 Approvals badge in the header · steve-bluejay-lines-gps crew picker doc-id wins over unit, saved-location pins, share-link address geocode · project-job-click openJobDetailsById (Projects job rows open details, any date) · TASK-9645 B/C Invoiced/Paid chips + Edit Job invoice lock · TASK-9712 Switch Ticket passes its jobs for the switch geofence · TASK-9657 S0 board holds via window.SafetyHold + local today · TASK-9631 F1-F4 map fixes (saved-location gate, keep pin on edit, no name-match delete, keep user map view) + TASK-4803 S3a builder usual-crew suggestions + TASK-9202/9517 Dispatch Settings crew-text menu copy · TASK-9627 whole-job Delete removes EVERY day (Firestore read by jobId, not the 90-day board window) + TASK-9236 job details: labeled Remove Day on each day row + always-on hint + TASK-9563 driver PIN job form: project picker (customer-switch safe), stops (server-geocoded), crew + vehicles/trailers, stale-pin drop, credential toast + TASK-9606 job form Project picker filtered to the picked customer + TASK-9597 Crew text layout setting for Notify Crew + TASK-9599 job form offers to add the job's customer to a project with no customer + honest conflict toast + TASK-1387 archived-ticket grey dot on dispatch calendars + smartassist-names-2 job well name + location noFuzzy + TASK-4834 job-vendor-number off SmartAssistant + trim on save + TASK-4800 S3 planEquipmentOnSite + openEquipmentMap hand-off + TASK-9359 identity off the broadcast people doc + stuck-shift-morning driver PIN clock-in closes a shift left open on an earlier day + TASK-4665 Notify Crew SMS capacity reads twilio.* only + clock-double-tap driver PIN clock-in guard + clock-out asks which shift + TASK-9516 editor-name alert setting + edit stamp + TASK-9588 No ticket emails checkbox + TASK-9579 Switch Ticket says when no truck was found for the shift's day + names a second open shift + TASK-9546 PDF under Add Photo files into Load Permit Files; permit section always open + TASK-9532 Notify Crew keeps unsent draft + fix-autofill-jobday Switch Ticket closes only the shift's own day's equipment + fix-1999-followups Switch Ticket reads the open shift by personId + loads its own customers/jobs off the Dispatch view · HOTFIX stored-XSS escaping + TASK-9556 Switch Job modal finalizes the old shift via the switchJob op (old labor line closed) + TASK-9548 grouped customer pickers + TASK-9508 Maps equipment paint order/labelled counts/list header/loading + TASK-9510 asset picker yard labels + typed qty ticks the row + TASK-1550 job reminders + TASK-4803 builder past-stop pre-fill + TASK-4695 (+ TASK-4800 Equipment-on-site layer) Add Job modal: Project picker + collapsed groups + TASK-4800 Maps Equipment-on-site layer + deployed-equipment list + TASK-4873 no computed code in texts while a setup code is pending + TASK-9464 waypoint-log edit dedupe + TASK-9175 Maps key/layers/project filter + TASK-4810 over-deploy warning + needs-recount pill/preview + Edit Job rental notice + merge 1976: TASK-9430 string CDL restrictions + TASK-9446 Draft Price Book hint + TASK-9407 default start date + TASK-9421 saved-location customer filter + TASK-9439 trailguide buster + TASK-9163 Job Builder refuses lookup questions + TASK-4808 job Notes auto-grow + TASK-9397 COD exam report always saves + TASK-9278 job number is the 8th job-card title token (move/untick); month chip shows it alone + TASK-932 founder-gate sweep + TASK-1483 vehicle reimbursement · P0 vendors-not-customers (job customer dropdown, Smart Job Builder match, saved-location picker) · rate picks keep Company default, three-way (TASK-9489) · TASK-9740 driver BOL (rel2087) · TASK-9779 811-only login sees only the 811 window (rel2087) · TASK-9740 signed documents open via getJobDocumentPdf (no stored URL) · TASK-9859 811 window: bulk upload + drag-drop, customer-first job list, ticket customer from the job, expiry dates/badges/warnings, good-for-N-days setting · 811 project dropdown · 2088.6: TASK-9734 map route coverage · TASK-9650 project doc library on jobs · TASK-9713 clock-in window · TASK-9710 driverJobEdit field-path save · TASK-9703 driver Add Another Day / Copy to New Job / own Delete · TASK-9737 Claude job request start time prefill · TASK-9624 Add Job: fill from a work order / rate con / BOL · TASK-9887 Locates (+ row menu hidden until placed; potential jobs/projects bank, directions skeleton): site card actions + Attach to project, clickable summary tiles, ⋮ menu kept in the viewport, stronger search · rel2089: + TASK-9809 BOL harvest, TASK-9816-9819 harvest smalls · TASK-9750 tentative / half-day job days · TASK-9894 view & upload only: Dispatch settings Save disabled + said why · TASK-4794 date-sync never fills a hole inside the old range · integ/2094 TASK-9886 DVIR warning per truck + driver + day (window.DvirCoverage) · integ/2095 TASK-9651 Projects module gate · integ/2098 9995142 (TASK-9769 customer text requests — changed without a bump) · TASK-9934 registration no-expiry wins over a stale date · TASK-9652 S2 (Customer documents tag / badge counts pending uploads / rejected list + canned reasons) · fix/2096-preship Assign Crew pairing snapshot before any await, bound to its day · TASK-9497 server stop geocode suggestions ("Use this location" in Not plotted) + query= links + place pin before viewport + stop provenance kept on edit · TASK-9763 S2 driverTruckSwap setting · integ/2102 9996900 (9497 9995497 + 4803 S3b 9996803 + 9763 S2 9995165 merged) · integ/2106 9997980 (notes golden orb: trailguide buster inside dispatch.js moved; 9997957 seen on another branch) · TASK-4794 follow-up ticket-locked kept day counts as a surviving day (integ/2108 9998080, above 2107 9998050 / orphans 9998071) · TASK-9994 everything 811 gone while 811 is off (Locates window, 811-only screen, job form leftovers, settings) · TASK-9994 811 tree (customer > project > site) + sort (Soonest / Newest / A-Z) + Show 811 warnings (default off) + 811 Locates settings section · TASK-9994 p1 811 side never makes a job: Job picker (active/upcoming + ended 30 days, search, + Add as potential job / project) in Site name's spot, Start a job here only with Dispatch write access (else Add as potential) · TASK-9838 driver time-off: Unpaid only without a plan + unpaid-taken line · TASK-10006 Create return job (Job Details strip + button, return Copy, On Site project spill) · TASK-9763 S4 team pairing (assignPairing.js 3) · integ/2113: 10015763 = the highest of TASK-9763 S4 10015763 / TASK-10006 10015006 / TASK-10013 10013001 / TASK-10010 10010001 · integ/2113.1: fresh value above integ/2113 10015763 — TASK-10031 job form Project list from price books + Site picker · TASK-10050 List view header toggles (show vehicles / crew on every job; trailguide + help-faq busters) · TASK-4696 project defaults pre-fill (Well name / AFE / Vendor / Job type + Inherited from project hint) · HOTFIX stale Smart Assist banner cleared on job form open · TASK-4786 A4 map customer filter · TASK-10278 S1 locates ticket rows
// integ/2122 billing.js 10132122 also carries: TASK-10122 P0 Mark paid/Unpaid revenue-row mirror sends only rules-allowlisted keys (branch 10122001); TASK-10088 accounting-only ticket notes; TASK-9671 S2 ticket openAt drain; TASK-9986 S1 Billing settings switch
// integ/2123 billing.js 10142123 also carries: TASK-10039 Roster grant / ticket edit copy; TASK-9671 S3 Claude invoice link drain (branch 10133671)
import { initBilling } from './views/billing.js?v=11090295'; // TASK-9417 QuickBooks paid sync modal · TASK-10264 Tickets toolbar wraps left + 44px touch floor (11050264, above main 11040257) · TASK-10257 B AR/AP search adds job number / PO / project (11040257, above main 10350260, 10026 11032026, 10242 10360242, 9789 10359789, 10258 10350258) · integ/2131 10340123: TASK-10123 QuickBooks setup entry (qboSetup 10123001) over TASK-9264 10339264 · TASK-10197 wizard XSS (lazy billingOnboardingWizard.js 2, fresh above integ/2129 10329302) · integ/2129 addendum: one value above the headroom slice's 10329301, 10134's 10304135 and 10183's 10183001 (lazy billingOnboardingWizard.js 1, billingReviewUi.js 10304135) · QuickBooks paid sync modal · perf/app-headroom-on2129 onboarding wizard -> lazy billingOnboardingWizard.js (fresh above 10304134) · TASK-10134 follow-up (billingReviewUi / billingBoardOverlay 10304134) (integ/2129) · TASK-9811 S2 main + extra POs (job-po-read.js 9811003) (integ/2129) · TASK-9264 office half: crew-reported materials at Review (lazy materialsReview.js) · TASK-9672 Price Book follow-ups (item code picker, lazy priceBookItemCode.js 9672002) (integ/2128 addendum 10299265, above 10296186 / 9672's 10296187 / 9264's 10299264) · TASK-9694 S6 Dispatch Notes block with collapsed Updates · TASK-10186 No clock entry chip + TASK-10185 dispatch notes on tickets (10296186, above 10294767) · integ/2127 billing.js reconciled once: TASK-4767 S2 invoice payments (10294767, fresh above 10282126) + 10164-B + 1406 + 9646 S3b + 9694 Doc-not-permit + 10107 + 1429 + 4852 + 9724 S3 · TASK-9989 UNINVOICE audit row + left-unbilled pay-change sentence · TASK-4673 decideLineRounding prefers the server roundingProvenance stamp (10118673, above integ/2118 10118001) ·integ/2118: fresh above integ/2117 10115101 (TASK-10040b) and integ/2116 10116001 (TASK-10030 merged-customer refusal) · TASK-10044 ticket footer: Grand Total in the decision row, no footer Close, quieter tools (10118044, above integ/2118 10118001) · integ/2118: fresh above integ/2117 10115101 (TASK-10040b) and integ/2116 10116001 (TASK-10030 merged-customer refusal) · integ/2115: fresh value above TASK-10041 10041001 and TASK-10049 10049001 (qboSetup.js 10049001); Review notes save is transactional + merges a concurrent note · integ/2113: fresh value above TASK-9988 S2 10009988 (archive goes through the server op) and TASK-10004 S2 10004107 (qboSetup.js 10004103) · integ/2112: fresh value (9988 S1 + 10004 S1 both changed billing.js; main served 9998311) · TASK-9988 S1 open clock-in stops invoice/billed-elsewhere/archive · TASK-10004 S1 9997104 (qboSetup.js 10004003) · TASK-9704 driver-prices toggle copy (display only) · 2088.5: TASK-9872 one revenue row per ticket · TASK-9810 ticket into project · TASK-9866 sister-job hours · TASK-9868 ticket doc type + uploader · TASK-9654 QuickBooks invoice links + aging filter · TASK-9440 fuel surcharge tax = accountant's call label · TASK-9792 qboSetup 1099 suggestion copy · TASK-9467 S1 job delete via deleteJobLedgered · TASK-9760 f/u 2 shift notes only from bound clock entries · TASK-9720 ticket deduct skips stock-tracked items · TASK-9440 fuel surcharge (flat %, off by default) · TASK-9443 slice 2 Review time-sync payroll warning · TASK-9694 polish neutral shift-note preview · TASK-9611 S2 QuickBooks setup Account map step (qboSetup.js 9611202) · TASK-9645 B/C Billing-only View invoice · TASK-9611 S3 Customers step (qboSetup.js 9611003) · TASK-9611 S1 QuickBooks setup checklist + Vendors step (merged with 9666/9667) · TASK-9666/9667 Price Book Show at $0 + Needs price + Deactivate label + per-item invoice hide (setting) · TASK-9626 Tickets list depth (gray well, flat white rows, plain meta) · TASK-9610 Tickets tree project folders + drag a job into/out of a project + Move to project button · TASK-9608 ticket review header collapses to a summary + Change, internal-time chip, customer-change cancel reverts the dropdown · TASK-9609 ticket job picker grouped by project + type-to-filter · TASK-9554 Capture GPS on inspections toggle · TASK-1387 shared calendar ticket-status helper + TASK-1412 billing Gmail status reloads after a dead-grant send + smartassist-names-2 ticket well name noFuzzy, well number + location identifier + TASK-4834 ticket-vendor-number off SmartAssistant + TASK-9588 per-customer No ticket emails (Unsent quiet, Paper ticket) + TASK-9503/9538 + TASK-9567 Ticket Only mode label + HOTFIX stored-XSS escaping + TASK-9548 grouped customer pickers + TASK-9414 Project filter on Tickets + Add jobs to a project (jobs stay separate) + TASK-4845 customer map (Line Information publish/preview) + TASK-1572 not-Ready invoice warning (warn, never block) + TASK-9436 permit markup % (Price Book + ticket) (merge 1973: 9436 also changed billing.js) + TASK-9437 print shows the customer approval (merge 1973: 1972 + 9434-37 both changed billing.js) + TASK-4757 Send me a copy + TASK-9411 ticket close returns to the time clock + TASK-9412 ticket time edits update the clock entry + TASK-9173 control-auto · TASK-4832 slip allocator self-heal + honest save-failure copy · TASK-9340 clear buttons target their own input · two-band Tickets toolbar + On shift badge · TASK-9393 one customer per grouped invoice · TASK-9390 Type header + code-based move + TASK-932 founder-gate sweep + TASK-1572 truck ask at clock-in + Ready completeness gate + TASK-1483 vehicle reimbursement · P0 vendors-not-customers (every billing customer picker/matcher) + ticket job picker FLAT (no optgroups — Lacey could not click a job), filter box below · TASK-9693 billed-elsewhere reads shift notes from clock entries · Billing Settings rounding switch + Price Book Always/Never/Company default (TASK-9489) · TASK-9744 Projects Open lands on the project in Dispatch (was Coming Soon) · TASK-9751 Billing Settings Tickets & pricing visible in Simple mode (only ticket-mode rows hide) · TASK-9755 ticket crew clocked-but-missing banner + Add back, audited Review line removals, job-change crew note · TASK-9760 Review crew banner trusts a clock entry ticketId only when bound to the ticket (same job or customer) · TASK-9548 s3 grouped customer pickers · decisions-1010 billing/sales pickers drop Shop/Office · TASK-9665 consumables (rel2087) · TASK-9779 openers refuse an 811-only login (rel2087) · 2088.6: TASK-9756/9757 QuickBooks read-only switch (qboSetup.js buster) · TASK-9735 Save stays on the ticket · TASK-9709 thousands separators on ticket-list totals · TASK-9705 Required Fields gate names where it is set · 2088.5: TASK-9810 ticket into project · TASK-9857 document/signature img src + href escaped · rel2089: + TASK-9700 Projects chips/default/grouping shared with Dispatch · TASK-9567 retire dead Piece Rate clock-out toggle · integ/2095 TASK-9651 + TASK-9537 QuickBooks push once-only · 1017 ticket clock mode ensures an internal-time Shop customer · fix/2096-preship turning a ticket clock mode on needs the Shop customer first (blocks the save) · TASK-9725 billed PPE lines never deduct stock · TASK-9725 S2 UI PPE roll-up chip (Review + ticket view) + Supplies billed to customers needs-a-look card (Tickets) · TASK-9763 S2 ran vs planned note · TASK-9942 revenue row written server-side (demo keeps the browser writer) · integ/2103 9995600 · TASK-9646 S4a Dispatch Notes read the job's own note, no jobNotes copy · integ/2105 9996461 (S4a's 9996460 was shared with clock.js) · TASK-9527 wizard build-tickets locked in Simple mode · TASK-9527 billing wizard build-tickets audit row · TASK-9527 module switches: driverContactDirectory→dispatch, driverDocs/enableJsaTemplates→hr (leaf), enableCitations→compliance · TASK-9945 f/u Paid · Review badge (overpay flag) · integ/2106 9996700 (2105 9996461; 9527 9996527/9996528/9996541; 2106 draft 9996606/9996607; gap left for hotfix 9735) · TASK-9971 review / line time edits in the COMPANY zone · TASK-4875 labor billing opt-in switch + Review crew-rate lock · TASK-9973 held-overpay badge (integ/2108 9997980, above 9971 9997030 / 4875 9997300 / 9973 9997973) · hotfix/billed-elsewhere-1020 Switch to Billed elsewhere + Unarchive & mark Billed elsewhere (9998020) · integ/2108.1: TASK-9983 Pay changed after invoicing banner + Review banner + Dismiss (9998311) · TASK-10036 Review save names what it removes + no blind notes write + view-only banner + delete needs a reason when the ticket has hours + notes-edit stamp · TASK-10041 dispatched-no-ticket review · TASK-10040b Billing → Settings missed clock-out alert hours read the server's resolveClockOutAlertHours (never saved = 24/24, not 12) · TASK-9501 Stops wording (fresh above 10118001) · TASK-10004 S3 owner payroll answer (qboSetup.js 10004303, above 10119993) · TASK-9417 QuickBooks paid sync modal · TASK-9989 UNINVOICE audit row + left-unbilled pay-change sentence · TASK-4673 decideLineRounding prefers the server roundingProvenance stamp (10118673, above integ/2118 10118001) ·integ/2118: fresh above integ/2117 10115101 (TASK-10040b) and integ/2116 10116001 (TASK-10030 merged-customer refusal) · TASK-10044 ticket footer: Grand Total in the decision row, no footer Close, quieter tools (10118044, above integ/2118 10118001) · integ/2118: fresh above integ/2117 10115101 (TASK-10040b) and integ/2116 10116001 (TASK-10030 merged-customer refusal) · integ/2115: fresh value above TASK-10041 10041001 and TASK-10049 10049001 (qboSetup.js 10049001); Review notes save is transactional + merges a concurrent note · integ/2113: fresh value above TASK-9988 S2 10009988 (archive goes through the server op) and TASK-10004 S2 10004107 (qboSetup.js 10004103) · integ/2112: fresh value (9988 S1 + 10004 S1 both changed billing.js; main served 9998311) · TASK-9988 S1 open clock-in stops invoice/billed-elsewhere/archive · TASK-10004 S1 9997104 (qboSetup.js 10004003) · TASK-9704 driver-prices toggle copy (display only) · 2088.5: TASK-9872 one revenue row per ticket · TASK-9810 ticket into project · TASK-9866 sister-job hours · TASK-9868 ticket doc type + uploader · TASK-9654 QuickBooks invoice links + aging filter · TASK-9440 fuel surcharge tax = accountant's call label · TASK-9792 qboSetup 1099 suggestion copy · TASK-9467 S1 job delete via deleteJobLedgered · TASK-9760 f/u 2 shift notes only from bound clock entries · TASK-9720 ticket deduct skips stock-tracked items · TASK-9440 fuel surcharge (flat %, off by default) · TASK-9443 slice 2 Review time-sync payroll warning · TASK-9694 polish neutral shift-note preview · TASK-9611 S2 QuickBooks setup Account map step (qboSetup.js 9611202) · TASK-9645 B/C Billing-only View invoice · TASK-9611 S3 Customers step (qboSetup.js 9611003) · TASK-9611 S1 QuickBooks setup checklist + Vendors step (merged with 9666/9667) · TASK-9666/9667 Price Book Show at $0 + Needs price + Deactivate label + per-item invoice hide (setting) · TASK-9626 Tickets list depth (gray well, flat white rows, plain meta) · TASK-9610 Tickets tree project folders + drag a job into/out of a project + Move to project button · TASK-9608 ticket review header collapses to a summary + Change, internal-time chip, customer-change cancel reverts the dropdown · TASK-9609 ticket job picker grouped by project + type-to-filter · TASK-9554 Capture GPS on inspections toggle · TASK-1387 shared calendar ticket-status helper + TASK-1412 billing Gmail status reloads after a dead-grant send + smartassist-names-2 ticket well name noFuzzy, well number + location identifier + TASK-4834 ticket-vendor-number off SmartAssistant + TASK-9588 per-customer No ticket emails (Unsent quiet, Paper ticket) + TASK-9503/9538 + TASK-9567 Ticket Only mode label + HOTFIX stored-XSS escaping + TASK-9548 grouped customer pickers + TASK-9414 Project filter on Tickets + Add jobs to a project (jobs stay separate) + TASK-4845 customer map (Line Information publish/preview) + TASK-1572 not-Ready invoice warning (warn, never block) + TASK-9436 permit markup % (Price Book + ticket) (merge 1973: 9436 also changed billing.js) + TASK-9437 print shows the customer approval (merge 1973: 1972 + 9434-37 both changed billing.js) + TASK-4757 Send me a copy + TASK-9411 ticket close returns to the time clock + TASK-9412 ticket time edits update the clock entry + TASK-9173 control-auto · TASK-4832 slip allocator self-heal + honest save-failure copy · TASK-9340 clear buttons target their own input · two-band Tickets toolbar + On shift badge · TASK-9393 one customer per grouped invoice · TASK-9390 Type header + code-based move + TASK-932 founder-gate sweep + TASK-1572 truck ask at clock-in + Ready completeness gate + TASK-1483 vehicle reimbursement · P0 vendors-not-customers (every billing customer picker/matcher) + ticket job picker FLAT (no optgroups — Lacey could not click a job), filter box below · TASK-9693 billed-elsewhere reads shift notes from clock entries · Billing Settings rounding switch + Price Book Always/Never/Company default (TASK-9489) · TASK-9744 Projects Open lands on the project in Dispatch (was Coming Soon) · TASK-9751 Billing Settings Tickets & pricing visible in Simple mode (only ticket-mode rows hide) · TASK-9755 ticket crew clocked-but-missing banner + Add back, audited Review line removals, job-change crew note · TASK-9760 Review crew banner trusts a clock entry ticketId only when bound to the ticket (same job or customer) · TASK-9548 s3 grouped customer pickers · decisions-1010 billing/sales pickers drop Shop/Office · TASK-9665 consumables (rel2087) · TASK-9779 openers refuse an 811-only login (rel2087) · 2088.6: TASK-9756/9757 QuickBooks read-only switch (qboSetup.js buster) · TASK-9735 Save stays on the ticket · TASK-9709 thousands separators on ticket-list totals · TASK-9705 Required Fields gate names where it is set · 2088.5: TASK-9810 ticket into project · TASK-9857 document/signature img src + href escaped · rel2089: + TASK-9700 Projects chips/default/grouping shared with Dispatch · TASK-9567 retire dead Piece Rate clock-out toggle · integ/2095 TASK-9651 + TASK-9537 QuickBooks push once-only · 1017 ticket clock mode ensures an internal-time Shop customer · fix/2096-preship turning a ticket clock mode on needs the Shop customer first (blocks the save) · TASK-9725 billed PPE lines never deduct stock · TASK-9725 S2 UI PPE roll-up chip (Review + ticket view) + Supplies billed to customers needs-a-look card (Tickets) · TASK-9763 S2 ran vs planned note · TASK-9942 revenue row written server-side (demo keeps the browser writer) · integ/2103 9995600 · TASK-9646 S4a Dispatch Notes read the job's own note, no jobNotes copy · integ/2105 9996461 (S4a's 9996460 was shared with clock.js) · TASK-9527 wizard build-tickets locked in Simple mode · TASK-9527 billing wizard build-tickets audit row · TASK-9527 module switches: driverContactDirectory→dispatch, driverDocs/enableJsaTemplates→hr (leaf), enableCitations→compliance · TASK-9945 f/u Paid · Review badge (overpay flag) · integ/2106 9996700 (2105 9996461; 9527 9996527/9996528/9996541; 2106 draft 9996606/9996607; gap left for hotfix 9735) · TASK-9971 review / line time edits in the COMPANY zone · TASK-4875 labor billing opt-in switch + Review crew-rate lock · TASK-9973 held-overpay badge (integ/2108 9997980, above 9971 9997030 / 4875 9997300 / 9973 9997973) · hotfix/billed-elsewhere-1020 Switch to Billed elsewhere + Unarchive & mark Billed elsewhere (9998020) · integ/2108.1: TASK-9983 Pay changed after invoicing banner + Review banner + Dismiss (9998311) · TASK-10036 Review save names what it removes + no blind notes write + view-only banner + delete needs a reason when the ticket has hours + notes-edit stamp · TASK-10041 dispatched-no-ticket review · TASK-10040b Billing → Settings missed clock-out alert hours read the server's resolveClockOutAlertHours (never saved = 24/24, not 12) · TASK-9501 Stops wording (fresh above 10118001) · TASK-9417 QuickBooks paid sync modal · TASK-10048 qboSetup.js 10060048 (QuickBooks jobs to sort) · TASK-10060 qboSetup.js 10060001 (Customers step redesign) · integ/2118: fresh above integ/2117 10115101 (TASK-10040b) and integ/2116 10116001 (TASK-10030 merged-customer refusal) · integ/2115: fresh value above TASK-10041 10041001 and TASK-10049 10049001 (qboSetup.js 10049001); Review notes save is transactional + merges a concurrent note · integ/2113: fresh value above TASK-9988 S2 10009988 (archive goes through the server op) and TASK-10004 S2 10004107 (qboSetup.js 10004103) · integ/2112: fresh value (9988 S1 + 10004 S1 both changed billing.js; main served 9998311) · TASK-9988 S1 open clock-in stops invoice/billed-elsewhere/archive · TASK-10004 S1 9997104 (qboSetup.js 10004003) · TASK-9704 driver-prices toggle copy (display only) · 2088.5: TASK-9872 one revenue row per ticket · TASK-9810 ticket into project · TASK-9866 sister-job hours · TASK-9868 ticket doc type + uploader · TASK-9654 QuickBooks invoice links + aging filter · TASK-9440 fuel surcharge tax = accountant's call label · TASK-9792 qboSetup 1099 suggestion copy · TASK-9467 S1 job delete via deleteJobLedgered · TASK-9760 f/u 2 shift notes only from bound clock entries · TASK-9720 ticket deduct skips stock-tracked items · TASK-9440 fuel surcharge (flat %, off by default) · TASK-9443 slice 2 Review time-sync payroll warning · TASK-9694 polish neutral shift-note preview · TASK-9611 S2 QuickBooks setup Account map step (qboSetup.js 9611202) · TASK-9645 B/C Billing-only View invoice · TASK-9611 S3 Customers step (qboSetup.js 9611003) · TASK-9611 S1 QuickBooks setup checklist + Vendors step (merged with 9666/9667) · TASK-9666/9667 Price Book Show at $0 + Needs price + Deactivate label + per-item invoice hide (setting) · TASK-9626 Tickets list depth (gray well, flat white rows, plain meta) · TASK-9610 Tickets tree project folders + drag a job into/out of a project + Move to project button · TASK-9608 ticket review header collapses to a summary + Change, internal-time chip, customer-change cancel reverts the dropdown · TASK-9609 ticket job picker grouped by project + type-to-filter · TASK-9554 Capture GPS on inspections toggle · TASK-1387 shared calendar ticket-status helper + TASK-1412 billing Gmail status reloads after a dead-grant send + smartassist-names-2 ticket well name noFuzzy, well number + location identifier + TASK-4834 ticket-vendor-number off SmartAssistant + TASK-9588 per-customer No ticket emails (Unsent quiet, Paper ticket) + TASK-9503/9538 + TASK-9567 Ticket Only mode label + HOTFIX stored-XSS escaping + TASK-9548 grouped customer pickers + TASK-9414 Project filter on Tickets + Add jobs to a project (jobs stay separate) + TASK-4845 customer map (Line Information publish/preview) + TASK-1572 not-Ready invoice warning (warn, never block) + TASK-9436 permit markup % (Price Book + ticket) (merge 1973: 9436 also changed billing.js) + TASK-9437 print shows the customer approval (merge 1973: 1972 + 9434-37 both changed billing.js) + TASK-4757 Send me a copy + TASK-9411 ticket close returns to the time clock + TASK-9412 ticket time edits update the clock entry + TASK-9173 control-auto · TASK-4832 slip allocator self-heal + honest save-failure copy · TASK-9340 clear buttons target their own input · two-band Tickets toolbar + On shift badge · TASK-9393 one customer per grouped invoice · TASK-9390 Type header + code-based move + TASK-932 founder-gate sweep + TASK-1572 truck ask at clock-in + Ready completeness gate + TASK-1483 vehicle reimbursement · P0 vendors-not-customers (every billing customer picker/matcher) + ticket job picker FLAT (no optgroups — Lacey could not click a job), filter box below · TASK-9693 billed-elsewhere reads shift notes from clock entries · Billing Settings rounding switch + Price Book Always/Never/Company default (TASK-9489) · TASK-9744 Projects Open lands on the project in Dispatch (was Coming Soon) · TASK-9751 Billing Settings Tickets & pricing visible in Simple mode (only ticket-mode rows hide) · TASK-9755 ticket crew clocked-but-missing banner + Add back, audited Review line removals, job-change crew note · TASK-9760 Review crew banner trusts a clock entry ticketId only when bound to the ticket (same job or customer) · TASK-9548 s3 grouped customer pickers · decisions-1010 billing/sales pickers drop Shop/Office · TASK-9665 consumables (rel2087) · TASK-9779 openers refuse an 811-only login (rel2087) · 2088.6: TASK-9756/9757 QuickBooks read-only switch (qboSetup.js buster) · TASK-9735 Save stays on the ticket · TASK-9709 thousands separators on ticket-list totals · TASK-9705 Required Fields gate names where it is set · 2088.5: TASK-9810 ticket into project · TASK-9857 document/signature img src + href escaped · rel2089: + TASK-9700 Projects chips/default/grouping shared with Dispatch · TASK-9567 retire dead Piece Rate clock-out toggle · integ/2095 TASK-9651 + TASK-9537 QuickBooks push once-only · 1017 ticket clock mode ensures an internal-time Shop customer · fix/2096-preship turning a ticket clock mode on needs the Shop customer first (blocks the save) · TASK-9725 billed PPE lines never deduct stock · TASK-9725 S2 UI PPE roll-up chip (Review + ticket view) + Supplies billed to customers needs-a-look card (Tickets) · TASK-9763 S2 ran vs planned note · TASK-9942 revenue row written server-side (demo keeps the browser writer) · integ/2103 9995600 · TASK-9646 S4a Dispatch Notes read the job's own note, no jobNotes copy · integ/2105 9996461 (S4a's 9996460 was shared with clock.js) · TASK-9527 wizard build-tickets locked in Simple mode · TASK-9527 billing wizard build-tickets audit row · TASK-9527 module switches: driverContactDirectory→dispatch, driverDocs/enableJsaTemplates→hr (leaf), enableCitations→compliance · TASK-9945 f/u Paid · Review badge (overpay flag) · integ/2106 9996700 (2105 9996461; 9527 9996527/9996528/9996541; 2106 draft 9996606/9996607; gap left for hotfix 9735) · TASK-9971 review / line time edits in the COMPANY zone · TASK-4875 labor billing opt-in switch + Review crew-rate lock · TASK-9973 held-overpay badge (integ/2108 9997980, above 9971 9997030 / 4875 9997300 / 9973 9997973) · hotfix/billed-elsewhere-1020 Switch to Billed elsewhere + Unarchive & mark Billed elsewhere (9998020) · integ/2108.1: TASK-9983 Pay changed after invoicing banner + Review banner + Dismiss (9998311) · TASK-10036 Review save names what it removes + no blind notes write + view-only banner + delete needs a reason when the ticket has hours + notes-edit stamp · TASK-10041 dispatched-no-ticket review · TASK-10040b Billing → Settings missed clock-out alert hours read the server's resolveClockOutAlertHours (never saved = 24/24, not 12) · TASK-10134 Meeting / Shop Day unbillable in weekly cleanup + billing-only jobs (billingReviewUi / billingOnlyJobsUi / billingBoardOverlay 10134001) · TASK-4767 S2 + one pay path on integ/2125 (fresh above 10134001): Invoices chip + lazy invoicePayments.js, Mark paid only off an invoice record, uninvoice clears invoice-set Paid
import { initTimeOff } from './views/timeoff.js?v=1524720'; // TASK-4720 requester-only changes, withdraw + resubmit
// integ/2122 hr.js 99999984 also carries: TASK-9891 S2 link-length copy
// integ/2123 hr.js 99999985 also carries: TASK-1496 S5 background kit off the hire path, S6 medical detail via peopleDq, S11 offer letter to peopleComp (lazy hrOfferLetter.js)
// integ/2124 hr.js 99999990 (8-digit, task9938) also carries: TASK-10142 memo recipient list stays stable + review before send (branch 100142001); TASK-10121 Time Off grouped by employee (branch 99999986); TASK-1496 S7 MVR via peopleDq; TASK-4753 CDLIS suggest-and-confirm (branch 99999982); TASK-9891 follow-up link-length copy (branch 99999985); TASK-9455 Enter SSN on an application, office only; partner sees it greyed "Carrier office only" (branch 99999988)
import { initHR } from './views/hr.js?v=102600142'; // TASK-10142 Driver Memos one click from the HR landing · TASK-10270 S1 PEI email guard + summary rows open the employer (hrDqRecords 9999980) · TASK-10244 plain-language background-check words (lazy bgCheckLabels.js 10244001, hrBgCheck 9999986) · TASK-10259 hrDqRecords 9999979 (remembered PEI employer address) · TASK-10033 consent cap: picker offers only packages within the applicant's consent (lazy BG module 9999980) · TASK-10175 ORDERS #24 TCPA packet text window copy (hrSignPackets.js v2) · TASK-10211 'More than one card?' hint under the card slots (above 10210's 100110310) · TASK-9742 safety docs picker hint line + TASK-1605 no screening package = stop + Pick a package link (integ/2129: 2128 holds 99999999, the 8-digit ceiling, so hr.js goes to 9 digits; task9938 now asks for a value above it) · TASK-10178 + TASK-10179 offer-letter owner in-transaction + rehire carry, hrOfferLetter.js v3 (integ/2128) · TASK-10142 memo picker first-load repaint + integ/2127 (TASK-9985 badge labels, 4573 training audit, 10142 items 1/2/5, 10033, 9581, 10163 hrApproveRoster 9999993) + tenant-change people reset · TASK-9976 SSN-on-file hint reads ssnMasked (ssnHash moved server-only) · TASK-9980 memo double-send guard: Send locked while in flight, progress + Done/duplicate states, 150s callable timeout, resendOf (dispatcher: pick the final value) · TASK-9250 crown-1019 lane: license-states line + CDLIS applicant line, TASK-9761 C duplicate banner, TASK-9444 3b reviewer print (dispatcher: pick the final value) · TASK-9938 bundle headroom: Checkr/BG+MVR viewers/adjudication, reviews, Approve to Roster, citation/PEI/med/intrastate/MVR pull moved to lazy hrBgCheck/hrReviews/hrApproveRoster/hrDqRecords (9999971-4) · TASK-9912 training delete writes before removing the file + module-denied toast · TASK-9903 med-card upload link text sent by the server (integ/2094) · TASK-9888 docs share link minted server-side · integ/2091 TASK-9570 TRIR/DART on HR → Safety (safety-rates.js 9570001) + Reset link · TASK-9145 D&A receipt bulk send tile · TASK-9884 position label getTypeLabel args (slug, kind) · TASK-4692 HR card hides the name+phone code for private-PIN / setup-pending people · TASK-9883 default PPE list seeded once (server check + fixed ids) · TASK-9728 Issue Equipment serial picker (assetId) · TASK-9721 optional Job on Log Equipment + Issue Equipment · TASK-9655 signed SPH verification prints no DRAFT · TASK-9689 Smart Search answers general wage-and-hour questions (cited) without a document · TASK-1649 request copy names the real payer (hrBgPaidByPartner) · TASK-9722 Count now + mismatch strip + item History (paged, CSV/print injection-safe) + alerts banner + optional return signature · TASK-9745 f/u timeline shows the job title · TASK-9720 PPE void (never delete) + Return qty/condition + Track stock / Inventory tab (safetyInventory retired) · TASK-9752 fuel-card PIN Show via the audited revealFuelCardPin callable · TASK-9452 PPE Reports tab · TASK-9690 SPH draft progress feedback (merged with 9303+9677) · TASK-9677 disciplinary last-day bound + CDLIS toast + personSections buster (merged with 9303) · TASK-9303 Hiring hides the licence-back row for non-CDL applicants (never asked) · TASK-9662 personSections buster · TASK-9531 integ/b-hr-billing COD: profile SSN eye via the audited revealPersonSsn callable · TASK-9531 Employee Profile: every identifier in one place, SSN + DL masked with an eye · TASK-9438 CDLIS card + eye reveal · TASK-9268 HR Smart Search notice · TASK-9444 slice 2 + fast_completion hidden · TASK-4884 fire-ext → Inspections · TASK-9299 De-Clutter · TASK-4884 fire-ext section points at Maintenance → Inspections · TASK-9299 De-Clutter: shorter CDLIS + Still-open copy, Manual chips removed · TASK-9602 employee Time Off tab history + Directory days off · TASK-9601 Time Off list: submitted + notice, via, full reason, Also off, summary strip, filters · TASK-9530 Email Application Link + email lock · TASK-9143 DER-missing refuse-with-override on the incumbent D&A notice · TASK-9456 review schedule setting + next-step reminders + honest review wording · TASK-4625 hire derives cdlLicensed from CDL class · TASK-9359 identity off the broadcast people doc + TASK-9583 fold duplicate no-SSN drafts on Hiring + TASK-9593 Add card on person page Cards section · TASK-9535 time-off by employee + TASK-9503/9538 + TASK-9577 Canadian applicants: province + country on address/licence, 'No U.S. SSN (applicant declared)' · fix-1999-followups onboarding-slot Remove deletes its Documents row through deleteHrPersonDocument (refusal shown) · HOTFIX stored-XSS escaping (job/ticket text) · TASK-1458 partner category uploads: 'other' DQ category + category label / partner uploader on the row · TASK-9512 Safety Roster Matrix shows the company name · TASK-9295 View MVR report on the MVR row + Checkr PDF button follows modules · TASK-9166 record a CDLIS report obtained outside Checkr (source + attestation, view file) · TASK-1320 break-in-service work history · TASK-9476 med-card setting hint for admins on Hiring uploads · TASK-1403 final COD: ND exemption button gated like the callable (modules, not roles) · TASK-1550 person reminders · TASK-1403 Intrastate only (ND med card) · TASK-9476 med card N/A for Non-Drivers · TASK-4748 Clearinghouse Log Query drawn only for a session that can save it (clearinghouseLogMode) · TASK-9428 legacy SSN reveal + merge 1976: TASK-9149 no Save offered on a past day nobody signed + TASK-9426 employer address + TASK-9429 MVR no-doc + TASK-9430 print headers + TASK-4791 applicant activity log + TASK-9167 employer-response button + TASK-9449 partner uploads via uploadPersonComplianceDoc + Clearinghouse via clearinghouse.logQuery + Application Questions button (merge 1974: 9449 + 4791 both changed hr.js) · TASK-9448 med card per job + TASK-9423/9424/9425 applicant flags on Hiring + driver multi-flag w/ clear reasons + archived read-only · TASK-4725 job openings: shared Copy Apply Link picker + Add Candidate datalist + "Applied for" + TASK-9402 HEIC + TASK-9173 control-auto + TASK-932 founder-gate sweep + TASK-1483 vehicle reimbursement + TASK-9422 partner saves write only changed keys (merge 1973: 9422 + 9423-25 + 9448 all changed hr.js) · TASK-9419 archived applicant read-only remainder · TASK-4882 safety outline (rel2087) · TASK-9699 licence photo (rel2087) · TASK-9742 safety meeting templates + attached company documents (rel2087) · TASK-9858 accessPinMap · TASK-9850 CMV needs-answers tile · TASK-9806 CMV checks · TASK-9697 Clearinghouse/CDLIS card reads PROOF ON FILE (display only) · rel2089 integrated (9804 + 9806 + 9697 + 9698) · TASK-9689 s3 openDocAssistant({ask}) · integ/2098 99999152 (9689 S3 and 2097 both shipped 99999151 — collision made unique) · TASK-9941 F S3 HR directory no longer prints the name + phone code · integ/2111 hr.js escJsAttr sweep (fix/hr-escjsattr-1021)
import { initCRM } from './views/crm.js?v=47090212'; // TASK-10212 vendor page: units rented from it (lazy crmVendorRentals.js 1) · TASK-9672 item code picker on contract lines (lazy priceBookItemCode.js 9672002: integ/2128 moved the line-form item-code gate + rate-audit log into it) (47082129, above integ/2128 47082128) · TASK-9797 item 1 crmContractChecklist -> ops-reports 10297971 (integ/2128) · TASK-10141 vendor Add to charge accounts + chip ·  TASK-10138 Back to Contracts on the contract form + back to the list after an edit-save · TASK-9671 S3 Claude Price Book link drain · TASK-9671 S2 Claude customer link drain · TASK-4705 one people list on the opportunity (window.ProjectPeople, portal.assignees derived) · TASK-9880 crmContractChecklist buster (ops-reports 10119880) · TASK-10030 merge dialog 390px fix (counts write vs preview fingerprint) · TASK-10030 Merge into… + delete guard (integ/2113.2: fresh value above 2113.1's served 9988761) · TASK-9797 item 3 contract checklist (crmContractChecklist.js 9797301, ops-reports 9797764 — integ/2113.1 one value across importers) · TASK-9793 AR/AP → AP opens the CRM vendor edit form (openVendorEditor, vendorOnly) · TASK-9792 QuickBooks 1099 suggestion on the vendor record (Apply / Not 1099) · TASK-9736 Active Projects row opens the project (Dispatch → Projects) · TASK-9666 Price Book Show at $0 + Needs price · TASK-9622 From rate sheet (Price Book) + Deal Desk rate-sheet button + dd:price-book-updated refresh · TASK-9268 slice 5 Deal Desk transcript notice · smartassist-names-2 addresses identifier mode (keeps ND/NE/APT/ZIP) + TASK-9588 No ticket emails checkbox + HOTFIX stored-XSS escaping (job/ticket text) · TASK-9446 Price Book save invalidates dispatch cache + open-contract deep link + TASK-1412 unconfirmed sends + TASK-9402 + TASK-9436 permit/misc kinds kept on save · P0 vendors-not-customers (dashboard counts, opportunity picker, pipeline, same-kind contract copy) · TASK-9548 s3 grouped customer pickers · decisions-1010 billing/sales pickers drop Shop/Office · TASK-9621 S0 (rel2087) · TASK-9779 openers refuse an 811-only login (rel2087) · 2088.6: TASK-9739 add project from CRM · TASK-9705 Required Fields help text says it blocks Ready + invoicing · TASK-9623 Deal Desk + customer Documents: file a contract / COI (window.DocIntake), key-terms line · integ/2095 TASK-9651 Projects gate · TASK-9823 recorded call button + saved-audio player + owner retention (9982301) · integ/2108 9988760 (above 9823 9982301 / 9988753 on another branch)
// crm-brochure.js (window.CRMBrochure) is lazy — see _ensureCrmBrochure below (integ/2095 perf budget).
// fleetPhotoIntake.js (window.FleetPhotoIntake) + rateSheetIntake.js (window.RateSheetIntake) are lazy — see _lazyIntake below (integ/2096 perf budget).
// integ/2122 bi.js 10129701 also carries: TASK-9701 S1 shared P&L statement (lazy pnl-statement.js)
// integ/2123 bi.js 10142123 also carries: TASK-9917 owner-draw expiry notice
import { initBI } from './views/bi.js?v=10350150'; // TASK-10150 demo sample Books decisions (booksDecisions.js 10150002) · integ/2131 10350123: TASK-10123 Books hint + qboBooks 10123001 over maintCosts 10342131 · TASK-9701 fu loan months inside the period (pnl-statement.js 10319701) · TASK-9952 refund matches the statement by refundDate (reconciliation.js 10312129, above the branch's 10169952) · TASK-10169 S1 month close per account (reconciliation.js 10312129) (integ/2129: one value above 10169's 10312128 and 10134's 10304134) · TASK-10134 follow-up: a Meeting / Shop Day ticket is a soft note (biReports / dispatched-no-ticket 10304134) (integ/2129: one value above this release's 10302129) · TASK-10162 S4 split one statement line + TASK-10172-A sold units keep past periods, Equipment & Loan Payments section (reconciliation.js / pnl-statement.js / biPnlPrint.js 10302129) (integ/2129) · TASK-10174 loan map off unit docs (vehicleLoans home, dual read) · TASK-10171 work-order shop line counted once (pnl-statement statementCosts; biPlRankings 10302128) · TASK-10186 biReports -> dispatched-no-ticket 10296186 · TASK-10170 reconciliation.js 10302128 (card-payment guard) · TASK-9797 item 1 Truck Breakout slots (biReports 10302128, ops-reports 10297971) (integ/2128) · integ/2127: TASK-10162 follow-ups (reconciliation.js 10162001) + TASK-10130 S3 BI + TASK-10150 Books decisions + TASK-9701 S2 · TASK-9701 S2 printable P&L + prior-period column (biPnlPrint.js lazy) · TASK-10145 1099 Paid YTD + spend by vendor count a vendor statement once; reconciliation.js gross (TASK-10141 notice) ·  integ/2120: 10120003 (10119881 is iftaMilesByState's; task9582 S1) · TASK-9878 S2 labor-cost rate + Customer/Job/Project drill-down from window.JobCost (shared/job-cost.js, imported by bi.js) · TASK-9880 Rental Register billed vs cost by project (biReports 10119880) · TASK-9979 BI OT weeks = payroll workweek (TenantLocalTime.weekKey) · TASK-10004 S1 qboBooks.js 10004004 · TASK-9726 follow-up Supplies by job adds Used on jobs / tickets (biReports 9797746, biSupplies 9726046) · TASK-9726 BI Reports 'Supplies by job' (lazy biReports → biSupplies) + P&L by-project names every project incl. completed + Forecast moved to lazy biPlForecast.js · TASK-9917 s4 P&L Charges list + Personal for imports · TASK-9917 s2 personal-charge repayments in GL / IIF / bank feed + Due from Employees in chart of accounts · TASK-9917 personal charges off the P&L (owner draws + owed by employees) · TASK-9899 exposes window.MaintStatus (the isMaintComplete twin) · TASK-9170 reconciliation.js buster · integ/2090 qboVariance.js 1976461 (QuickBooks connect path) · TASK-9884 vehicle type badge getTypeLabel args (slug, kind) · TASK-9872 revenue rows judged by shared/revenue-entry-canon.js · TASK-9797 Reports tab (biReports.js lazy + shared/ops-reports.js) · TASK-9480 Books tab QuickBooks expense tie-out (qboBooks.js lazy import) · TASK-9590 Balance Sheet use-before-declare crash + TASK-9242 Overview revenue basis toggle · TASK-9592 per-truck cost falls back to the Cost Allocation refId · TASK-9548 slice 2 cost-entry vehicle picker Trucks/Trailers/Assets groups · TASK-9503/9538 card holders + Unclaimed in Debt Accounts · HOTFIX stored-XSS escaping + TASK-9482b dedupe vendor rule + "different charge" override (reconciliation.js buster) · TASK-9482 statement re-upload dedupe (reconciliation.js buster) · TASK-9512 Overview loading state + P&L drilldown names · TASK-9493 split-aware cost attribution · TASK-9490 per-vehicle revenue excludes deleted tickets + assetId-only + TASK-9210 Books tab (qboVariance.js lazy import) + TASK-4810 needs-recount badge + COD 1976: reconciliation.js dynamic-import buster 1520124 -> 1976451 (9442/9451 changes never bumped it) + TASK-9451 free-this-credit repair (reconciliation.js buster) + TASK-9442 one-claim-per-credit (reconciliation.js buster) + TASK-9413 per-card reconciliation (reconciliation.js buster) + TASK-9173 control-auto + release 1.15.1971 integration COD + TASK-932 founder-gate sweep · TASK-4860 Fleet Utilization: vehicles used + vehicle-days side by side · 2088.6: TASK-9756/9757 QuickBooks check tab (qboBooks.js buster, map edits move to setup) · TASK-9624 Draft bills out of AP aging · integ/2103 9995750 (TASK-9942 revenue-entry-canon buster) · TASK-9969 QB Books S1 qboBooks.js 9961001 (tie-out never green when incomplete) · TASK-9969 S2 Financial Health QuickBooks row (qboBooks.js 9969203) · TASK-9947 refund-aware isRecognizedCost + reimbursement net of refunds + reconciliation.js buster (integ/2108 9995970, above S2 9995969 / 9947 9947002) · TASK-9763 S4 orPairDay read-time filter (biReports 9797764, ops-reports 9797763) · TASK-9797 item 3 per-contract checklist: biReports 9797765, ops-reports 9797764 (integ/2113.1 fresh values) · TASK-9878 follow-up BI Crew Member + Vehicle drill-down from window.JobCost.crewTotals/vehicleTotals/rank (job-cost.js 9878302) · TASK-9878 S2 labor-cost rate + Customer/Job/Project drill-down from window.JobCost (shared/job-cost.js, imported by bi.js) · TASK-9880 Rental Register billed vs cost by project (biReports 10119880) · TASK-9979 BI OT weeks = payroll workweek (TenantLocalTime.weekKey) · TASK-10004 S1 qboBooks.js 10004004 · TASK-9726 follow-up Supplies by job adds Used on jobs / tickets (biReports 9797746, biSupplies 9726046) · TASK-9726 BI Reports 'Supplies by job' (lazy biReports → biSupplies) + P&L by-project names every project incl. completed + Forecast moved to lazy biPlForecast.js · TASK-9917 s4 P&L Charges list + Personal for imports · TASK-9917 s2 personal-charge repayments in GL / IIF / bank feed + Due from Employees in chart of accounts · TASK-9917 personal charges off the P&L (owner draws + owed by employees) · TASK-9899 exposes window.MaintStatus (the isMaintComplete twin) · TASK-9170 reconciliation.js buster · integ/2090 qboVariance.js 1976461 (QuickBooks connect path) · TASK-9884 vehicle type badge getTypeLabel args (slug, kind) · TASK-9872 revenue rows judged by shared/revenue-entry-canon.js · TASK-9797 Reports tab (biReports.js lazy + shared/ops-reports.js) · TASK-9480 Books tab QuickBooks expense tie-out (qboBooks.js lazy import) · TASK-9590 Balance Sheet use-before-declare crash + TASK-9242 Overview revenue basis toggle · TASK-9592 per-truck cost falls back to the Cost Allocation refId · TASK-9548 slice 2 cost-entry vehicle picker Trucks/Trailers/Assets groups · TASK-9503/9538 card holders + Unclaimed in Debt Accounts · HOTFIX stored-XSS escaping + TASK-9482b dedupe vendor rule + "different charge" override (reconciliation.js buster) · TASK-9482 statement re-upload dedupe (reconciliation.js buster) · TASK-9512 Overview loading state + P&L drilldown names · TASK-9493 split-aware cost attribution · TASK-9490 per-vehicle revenue excludes deleted tickets + assetId-only + TASK-9210 Books tab (qboVariance.js lazy import) + TASK-4810 needs-recount badge + COD 1976: reconciliation.js dynamic-import buster 1520124 -> 1976451 (9442/9451 changes never bumped it) + TASK-9451 free-this-credit repair (reconciliation.js buster) + TASK-9442 one-claim-per-credit (reconciliation.js buster) + TASK-9413 per-card reconciliation (reconciliation.js buster) + TASK-9173 control-auto + release 1.15.1971 integration COD + TASK-932 founder-gate sweep · TASK-4860 Fleet Utilization: vehicles used + vehicle-days side by side · 2088.6: TASK-9756/9757 QuickBooks check tab (qboBooks.js buster, map edits move to setup) · TASK-9624 Draft bills out of AP aging · integ/2103 9995750 (TASK-9942 revenue-entry-canon buster) · TASK-9969 QB Books S1 qboBooks.js 9961001 (tie-out never green when incomplete) · TASK-9969 S2 Financial Health QuickBooks row (qboBooks.js 9969203) · TASK-9947 refund-aware isRecognizedCost + reimbursement net of refunds + reconciliation.js buster (integ/2108 9995970, above S2 9995969 / 9947 9947002) · TASK-9763 S4 orPairDay read-time filter (biReports 9797764, ops-reports 9797763) · TASK-9797 item 3 per-contract checklist: biReports 9797765, ops-reports 9797764 (integ/2113.1 fresh values)
// integ/2122 compliance.js 10129993 also carries: TASK-9993 permit measurements hook
// integ/2123 compliance.js 10142123 also carries: TASK-10086-O IFTA fuel gates; TASK-10086-P cab docs by path; TASK-1496 S6 medical detail via peopleDq
// integ/2124 compliance.js 10172124 also carries: TASK-1496 S7 MVR detail via peopleDq
import { initCompliance } from './views/compliance.js?v=160710395'; // TASK-10216 MVR review banner: View MVR report / no-report note + upload, review records where the MVR was read (review modal lazy complianceMvrReview.js 1) · TASK-1605 partner-routed Request MVR row says pick a package (integ/2129: one value above 1605's 160522127) · TASK-10139 S4 rosterPermitGeometry.js 10302129 + TASK-9993 scale-measured vs estimated + booster share confirmed by text + TASK-10028 / 9349 / 9348 route gate + route check from the queue item + TASK-9348 per-state miles (permitQueue.js 100302129, above 9348's 100289348) (integ/2129: one value above the 10028 branch's 100282127) · TASK-9797 item 1 iftaMilesByState -> ops-reports 10297971 (integ/2128) · TASK-9571 Company-tab FMCSA safety picture host (lazy carrierSafety.js 9571001) · integ/2125: above TASK-10149 10172125 and TASK-10133 10143133 · TASK-10133 permitQueue.js 10133001 (route check off chip) · TASK-10149 advisor chat tab height + layout · TASK-9880 iftaMilesByState buster (ops-reports 10119880) · TASK-9582 IFTA Miles by state host (lazy iftaMilesByState.js 9999980) · TASK-9934 registration no-expiry wins over a stale date · TASK-9912 training delete writes before removing the file + module-denied toast · TASK-9903 med-card upload link text sent by the server (integ/2094) · TASK-9351 permitQueue.js 9351001 (Through Claude source chip) · TASK-9145 D&A receipt bulk send banner + single send texts via server · TASK-9706 open DVIR defects = dvirDefectClosure + unit label · TASK-9813/9814 IRP + no-reg-expiry · TASK-9719 voided PPE out of signals · TASK-9438 CDLIS row on the Compliance → People driver card · TASK-9143 DER-missing refuse-with-override on the D&A notice · TASK-9359 identity off the broadcast people doc + TASK-9533 person-detail header tint neutral for a separated person · HOTFIX stored-XSS escaping + TASK-1458 DQ browser fetches 'other' (partner "Other: <label>" uploads) · TASK-9295 View MVR report on the MVR row · TASK-1320 break-in-service work history · TASK-1403 intrastate N/A note · TASK-4748 Log Query gated on window.HRModule.clearinghouseLogMode · COD 1976: MVR row counts the bg-doc Checkr MVR + TASK-9429 MVR no-doc row + TASK-9449 partner uploads + Clearinghouse through the server paths · TASK-9422 partner saves write only changed keys + TASK-9424 driver multi-flag + clear reasons (merge 1973: both changed compliance.js) · TASK-9696 driver types (rel2087) · 2088.6: TASK-9709 MVR-review prompt closes on leaving Compliance · TASK-9349 Permit queue button (permitQueue.js) · rel2089 integrated (9349 + 9806 + 9698; permitQueue.js lazy) · TASK-9935 Doesn't-expire heavy-unit confirm · TASK-9936 IFTA Needs state tag + other-quarter jumps · integ/2098 9999155 (9935 regNoExpiry confirm + 9936 IFTA Needs state, over 2097 9999151) · TASK-9763 S4 ops-reports buster (iftaMilesByState 10001764, ops-reports 9797763) · integ/2113.1: iftaMilesByState 10001766 (ops-reports 9797764, TASK-9797 item 3)
import { initDotAdvisor } from './views/dotAdvisor.js?v=1529268';
// perf headroom: Permit Pete is lazy (window.PermitPeteModule stub, _lazyGlobal below) — TASK-10133 route-check setting help line · TASK-9624 start from an issued permit · TASK-10001 saved machines (+dispatch read)
// integ/2123 ap.js 10142123 also carries: TASK-9917 owner-draw expiry notice
import { initAP, initReceiptsView } from './views/ap.js?v=99971625'; // TASK-9589 S1 residue: AP bill card QuickBooks button reads the live answer (integ/2129 addendum: the branch's 99969589, above 99959729) · TASK-10172-A Equipment Purchase / Loan Payment AP categories (integ/2129) · TASK-10170 receipts.js 10302128 (late receipt attaches) (integ/2128) · TASK-10145 link crew charge receipts to a vendor statement bill (TASK-10141 charge label) ·  TASK-9947 refunds on driver/staff expenses, approve/reject refunds, net payout, refund approval rule · TASK-9931 expense approval refusals shown plainly (bulk: N not approved + why) · TASK-9917 s2 Record repayment / Undo + repaid per person · TASK-9917 Personal button + Personal Charges pill · TASK-9774 receipt search · TASK-9773 receipt match suggestions on integ/2088.5 · TASK-9776 In QuickBooks? check on receipt inbox · TASK-9778 Receipts & Expenses view (receipts-only login: inbox + expense review, no AP bills / vendors / bank) · TASK-9772 receipts.js buster (receipt reader v2 confirm UI) · TASK-9793 Edit vendor from AP (vendor group pencil + bill-form picker) via window.CRMModule.openVendorEditor · TASK-9592-FU Re-allocate expense (audited callable) + TASK-9593 card-not-assigned flag on driver expense rows · TASK-9567 expense-settings hint path + TASK-9493 split lines on expense rows · TASK-9771 Upload receipts (rel2087) · 2088.6: TASK-9775 receipts.js buster (driver WO receipt) · TASK-9778 approve-txn (Correct payment details on paid reimbursements; decisions send expectedStatus / expectedCostAllocationId; already-decided toasts) · TASK-9624 From a bill (draft bill, approve first) + Draft badge · integ/2093: imports receipts.js 9776014 (TASK-9770 follow-ups) · integ/2095 TASK-9537 bill push once-only · TASK-9917 s4 Personal dialog moved to lazy repay dialog (part-of-it split), extra personal-charge subscription, employee-view wording · integ/2108 9995024 (COD r2: rejected refunds never block a bill total change) · TASK-9724 S3 Receive into stock on approved expenses + saved bill lines (lazy apStockIn.js 9724303) + Supplies / PPE default AP category (ap 99959727)
// TASK-10013 projects.js?v=10013001 — a geocode timeout is finished in the background (no false 'couldn't be mapped'), the warning says how to fix it
// integ/2123 projects.js 47060003 also carries: TASK-10120 lazy Edit Project / Change Order / Templates
import { initProjects } from './views/projects.js?v=47060295'; // TASK-9700 status chips + grouping from shared window.ProjectStatusFilter · TASK-9652 customer uploads review on the project (Review uploads) · 1017 office change orders on the customer portal (source 'office' + modal hint) · TASK-9552 By Customer = one packed grid (no collapsible stack), typed 'lat, lng' stored as the pin with no geocode, status word only when not active, same rows on every card · TASK-9649 project Docs 'From jobs' list · TASK-9862 site-name offer + transactional move · project-job-click: job rows/Overview/Timeline are buttons → openJobDetailsById · TASK-9674 Overview 'Field totals' card (admin-picked ticket data points summed across the project, shared ticket load) + TASK-9675 Docs tab shows ticket photos (job · date · driver) + thumbnails + URL-only files open by derived path + TASK-9647 Docs tab links open (fullUrl, safe URLs only, grid CSS) + TASK-9605 Projects list: one marker per card, red only when work ran past the end date, grey Wrapped + Mark complete + TASK-9599 link gate adopts the customer into a customer-less project in the same transaction + TASK-9558 card job counts repaint when they land + TASK-9506 overdue = after the end of the last local day (tenant tz) + TASK-4710 hours stat shows -- when clock read is denied + TASK-4842 project geocode + portal Map toggle + Projects grouped by customer (Lacey) + TASK-9289 r5.1 every unlink chunk re-checks the delete-lock token (resume-after-takeover) · P0 vendors-not-customers (create/edit project customer) · TASK-9548 s3 grouped customer pickers · TASK-9767 811 tickets on a project (Docs tab), merged into TASK-9859 · 811 project dropdown (setPermitProject) · 2088.6: TASK-9650 project document library · TASK-9887 potential project offered on create · integ/2093: TASK-9549 project settings + Internal/Customer notes, TASK-9918/9921 change orders · integ/2095 TASK-9651 Projects module · TASK-9652 S2 (Customer documents tag / badge counts pending uploads / rejected list + canned reasons) · fix/1018 nobody-notified warning covers change requests (Changes tab + Edit Project) · TASK-9994 Docs 811 rows/chip + 811 waiting offer only while 811 is on · TASK-10003 re-mounting Dispatch keeps the live project list · TASK-10006 Out on this project strip + Create return job · TASK-4705 People on this job card · TASK-4706 crew step grant on the Projects People card (fresh above integ/2119 10119001)
import { initAccount } from './views/account.js?v=11032492'; // TASK-10249 S2 claudeConnector v30 (typed approval cards) · TASK-10249 claudeConnector v29 (requests on #/approvals) · TASK-10194 FU 11019402 claudeConnector v28 (History, phone cards, <name> (Owner)) · integ/2131 TASK-10110 claudeConnector v27 Approve selected · TASK-10194 S2 claudeConnector v26 (year, real expiry, Replaced) (integ/2129) · TASK-9566 claudeConnector v25 Disconnect all (integ/2129) · TASK-10132 S2+S3 claudeConnector v24 key expiry on the Connect Claude card (integ/2128) · TASK-10140 10140001 (fresh above 10125713) claudeConnector v21 send_memo preview label · merge 2122+TASK-10119 (10119 account.js Add credits copy, on main, not live) · TASK-10111 10125711 (fresh above 10125659) claudeConnector v20 approve-link reasons · TASK-1659 10125659 plan card says "Paid for by <partner>" when the compliance partner pays (fresh above 10002001 / branch 9995437) · TASK-9970 AI app switches (claudeConnector v17) · TASK-10002 Sign-in & security card (changePassword.js lazy chunk) · TASK-9768 pre-ship CDL line (setupQuestionnaire.js 9768004) · TASK-9170 claudeConnector/partnerAiConsent busters · 1015: SMS auto-recharge default cap 4 · TASK-9433 Vehicles section (+50 for $25/mo) · 2088.6: TASK-9777 biller questions (setupQuestionnaire.js buster) · TASK-9768 customer setup link card · TASK-9683 s2 per-person rules link · TASK-9683 second approver · TASK-9587 Review in Job Builder · TASK-9657 approve/reject refreshes the Approvals badge + TASK-9629 Claude requests approve feedback + sec-mcp-mint-reauth Connect Claude re-auth prompt + TASK-9465-E Connect Claude card (sign-in path behind flag) + TASK-9471 connector copy-token honest toast + TASK-9466 token name field + TASK-9269 Connect Claude card + TASK-4665-S3 SMS pack buy/top-up for every billable owner + TASK-9432 plan/billing controls + TASK-9473 one Plan card + one SMS card · TASK-9572 notify_crew preview + second-approver copy (claudeConnector v14) · rel2089 integrated + TASK-9805/9808/9820 partner 811 + AI consent · integ/2098 9995436 Connect Claude card (claudeConnector.js) lazy-loaded off the cold bundle (perf budget, not raised) · integ/2108 9995440 (9970 branch went down to 9970001; 2107 was 9995436, 9995437 seen elsewhere)
import { initPayroll } from './views/payroll.js?v=10119952'; // TASK-9952 refund corrections panel (lazy payrollRefundCorrections.js) · integ/2117: 10117002 = fresh above 10034 10034001 / 10040 10040001 · TASK-10040 over-24h-in-one-day warning · TASK-9443 slice 2 export-not-recorded (no Workforce) told up front · integ/b-hr-billing re-review: kept corrections stay listed until marked handled, whatever their age · integ/b-hr-billing COD: earlier-period changes carried only after an explicit 'Mark as handled' · TASK-9443 pay-period soft lock: export stamps the period, Prep lists changes after payroll + TASK-9451 (TASK-9459 dayHeld reason) clock-out-still-open reason + TASK-9439 approval before payroll + TASK-850B Gusto Embedded + TASK-1169 Payroll Prep + TASK-1211 sandbox kill switch · TASK-9804 Paychex Flex hours file · TASK-10034 Final check / hours after the last day warnings + LAST DAY badge · TASK-9443 slice 2 export-not-recorded (no Workforce) told up front
import { initSmartAssistant } from './smartAssistant.js?v=11041104'; // smartassist-names-2 noFuzzy option + identifier whitespace-only fix (also class fields) + TASK-4834 identifier option (exact-duplicate only) · HOTFIX stale-suggestion guard (data-sa-from) + clearAll
// TASK-1550 — registers window.Reminders (person / vehicle / job reminders:
// banner + text). The ONE importer; roster.js / dispatch.js call window.Reminders.
import './reminders.js?v=10282127'; // TASK-10018 shared office notice (one per text DD could not act on: full message, Make this a job, Done-for-all) · 1017 reminder note personal-info warning · TASK-9569 reminder bells on Roster/Dispatch cards, per-login Show reminder bells (integ/2091)
import './approvalsBadge.js?v=4'; // TASK-10249 #/approvals + old-link forward · TASK-9803 S1 approve deep link · TASK-9683 s2 waiting-on-you nudge + owner expired clock-out alert · TASK-9657 — Approvals N on Dispatch / Maintenance / Account nav (window.ApprovalsBadge)
// perf headroom: the approval rules box is lazy (window.ClaudeApprovalRules stub, _lazyGlobal below) — TASK-9683 s2 — Claude approval rules box in Company Settings (window.ClaudeApprovalRules)
import './notes.js?v=10282127'; // TASK-9619 — Team Notes: the golden orb by the logo + @mention sharing (window.TeamNotes). The ONE importer.
// TASK-4884 — registers window.FacilityInspections (Maintenance → Inspections:
// shop extinguishers / eyewash / first-aid / spill kits). The ONE importer;
// maintenance.js calls window.FacilityInspections.mount at call time.
// perf headroom: lazy since perf/app-coldload-headroom (window.FacilityInspections stub, _lazyGlobal below).

// trailguide + demoSeeder lazy-imported on demand (TASK-345 S3) — saves ~182KB on cold load
// for driver-PIN sessions which never trigger either. Native ES module import()
// already de-dupes by specifier, so no extra cache layer needed. We DO wrap a single
// retry-on-chunk-error so a mid-import network blip doesn't permanently break the
// trailguide UX. Every wrapper .catches its own rejection so fire-and-forget callers
// don't trip unhandled-promise warnings.
// HOTFIX 2026-05-28 — added 10-second timeout. Previous version awaited
// `import(...)` with no timeout, so a hung chunk fetch (iOS WebView cache
// state, edge-CDN issue, slow cell) left the user stuck on "Loading view…"
// forever. Now if neither the initial import nor the retry completes within
// 10s each, the function rejects with a recognizable error that the caller's
// catch block surfaces via _renderLazyViewError's reload CTA.
// TASK-866 — takes a THUNK `() => import('./x.js?v=…')` (not a string specifier) so
// the import() specifier is a LITERAL. Under esbuild bundling that lets the bundler
// statically resolve + DEDUPE trailguide/demoSeeder into the graph (the ?v= is
// stripped by the build resolver), collapsing them to ONE module instance shared
// with dispatch.js's own literal import — instead of a 2nd runtime instance holding
// separate module-local tour state. Works identically served-raw (the thunk just
// runs import() at call time) so rollback to /src needs no code change.
async function _lazyImportWithRetry(importThunk, label) {
  const _importWithTimeout = (phase) => Promise.race([
    importThunk(),
    new Promise((_, reject) => {
      // TASK-10076 (COD r2): while utils/chunkReload.js is reloading or has its
      // "DispatchDeck was updated" question open, the timeout waits — otherwise a
      // question left open 10 s let this reject and the screen's toast show under it.
      const fire = () => (window.ChunkReload?.busy?.()
        ? setTimeout(fire, 1000)
        : reject(new Error(`Lazy import ${phase} for ${label} timed out after 10s`)));
      setTimeout(fire, 10000);
    })
  ]);
  try {
    return await _importWithTimeout('initial');
  } catch (err) {
    // A stale-tab chunk failure the user already answered (Not now): retrying the
    // removed chunk cannot work and would only ask again.
    if (err && err.ddStaleChunk) throw err;
    console.warn(`[Lazy] Import failed once for ${label}, retrying in 1s:`, err?.message || err);
    await new Promise(r => setTimeout(r, 1000));
    return _importWithTimeout('retry');
  }
}
// IMPORTANT: when bumping app.js cache-buster, grep for './trailguide.js?v=' and
 // './demoSeeder.js?v=47069797' across public/src/ and update lockstep — same Pitfall #1
// duplicate-instance rule applies to dynamic import() specifiers.
function _ensureTrailguide() {
  return _lazyImportWithRetry(() => __ddChunkWrap(import('./trailguide.js?v=10329295')), 'trailguide');
}
// TASK-9623 / TASK-9624 — window.DocIntake loads on first use (perf budget:
// the modal is not part of the cold load). This stub answers canOpen() for the
// button gates and forwards open() to the real module, which replaces it.
// DOC_INTAKE_SURFACE_MODULES = the modules behind each surface's kinds
// (views/docIntake.js FAMILIES/SURFACES; tests/task9624 pins them equal).
const DOC_INTAKE_SURFACE_MODULES = {
  ask_dd: ['crm', 'roster', 'billing'], deal_desk: ['crm'], customer: ['crm'], ap: ['billing'],
  job_builder: ['dispatch'], permit_pete: ['dispatch'],
};
function _ensureDocIntake() {
  return _lazyImportWithRetry(() => __ddChunkWrap(import('./views/docIntake.js?v=10119001')), 'docIntake');
}
if (!window.DocIntake) {
  window.DocIntake = {
    canOpen(surface) {
      const mods = DOC_INTAKE_SURFACE_MODULES[surface] || [];
      const ma = (window.AppState || {}).memberAccess;
      if (ma == null) return mods.length > 0;
      if (ma.synthetic || (ma.status && ma.status !== 'active') || ma.viewUploadOnly || ma.readOnly) return false;
      return mods.some((m) => (ma.modules || []).includes(m));
    },
    open(opts) {
      _ensureDocIntake().then(() => window.DocIntake.open(opts)).catch(() => window.showToast?.('Could not open that. Try again.', 'error'));
    },
    close() {},
  };
}
function _ensureDemoSeeder() {
  return _lazyImportWithRetry(() => __ddChunkWrap(import('./demoSeeder.js?v=47069797')), 'demoSeeder');
}
// integ/2095 perf budget — the CRM brochure builder (window.CRMBrochure) is off the
// cold load. It still loads on every session right after app.js runs (no
// condition), so its own auto-init (tenant known + not a driver PIN) runs exactly
// as it did as a static import. crm.js already guards every use with
// window.CRMBrochure?.; this stub only covers a click that beats the chunk, and is
// replaced when crm-brochure.js assigns window.CRMBrochure.
function _ensureCrmBrochure() {
  return _lazyImportWithRetry(() => __ddChunkWrap(import('./views/crm-brochure.js?v=1511148')), 'crmBrochure');
}
if (!window.CRMBrochure) {
  const _brochureOpen = (name) => (...args) => {
    _ensureCrmBrochure()
      .then(() => window.CRMBrochure[name](...args))
      .catch(() => window.showToast?.('Could not open the brochure builder. Try again.', 'error'));
  };
  window.CRMBrochure = {
    openBuilder: _brochureOpen('openBuilder'),
    openFromCustomer: _brochureOpen('openFromCustomer'),
    openFromRateSheet: _brochureOpen('openFromRateSheet'),
  };
}
setTimeout(() => {
  _ensureCrmBrochure().then(() => {
    // COD fold: CRM → Marketing rendered before the chunk landed shows crm.js's
    // fallback (no renderList yet) — repaint that list once, with loaded brochures.
    const B = window.CRMBrochure;
    if (!document.getElementById('crm-brochure-list') || typeof B?.loadBrochures !== 'function') return;
    B.loadBrochures().then(() => {
      const el = document.getElementById('crm-brochure-list');
      if (el && typeof B.renderList === 'function') el.innerHTML = B.renderList();
    }).catch(() => {});
  }).catch((err) => console.warn('[Lazy] crm-brochure failed:', err?.message || err));
}, 0);
// TASK-9375 S5 — ORGANIZATION (start). docs/design/org-layer-9375.md §4/§15.
// views/organization.js (and all its CSS) is OFF the cold load: fetched only
// when getMyOrg returns a row (then it mounts the "Organization" nav entry) or
// when Settings shows the "Part of <org>" card (tenantData.orgId set). Org
// access is not a tenant module, so the view is an overlay from its own nav
// entry, not a routed view. The server guard runs on every call; nothing here
// is authority (the chunk owns the org-callable allow-list). Kept tiny: perf budget.
const _orgMod = () => _lazyImportWithRetry(() => __ddChunkWrap(import('./views/organization.js?v=3')), 'organization').then(() => window.OrganizationModule); // TASK-9502 R3 region pills + editor (organization.js v=3)
window.OrganizationView = {
  open: (o) => _orgMod().then((M) => M.open(o)).catch(() => window.showToast?.('Could not open Organization.', 'error')),
  // No orgId on this tenant (every non-branch, demo included) → no chunk.
  paintBranchCard: () => (window.AppState?.tenantData?.orgId ? _orgMod().then((M) => M.paintBranchCard()).catch(() => {}) : Promise.resolve()),
};
let _orgSeq = 0;
onAuthStateChanged(auth, (u) => {
  const seq = ++_orgSeq;
  window.OrganizationModule?.unmountNav();
  // After the app state settles; anonymous / driver / demo / public sessions never ask.
  if (u && !u.isAnonymous && u.email) setTimeout(function go(n) {
    const as = window.AppState || {};
    if (seq !== _orgSeq || as.isDemo || as.driverModePin || as.driverMode || as.isPublicAccess) return;
    if (!as.user) { if (n < 40) setTimeout(go, 500, n + 1); return; }
    httpsCallable(functions, 'getMyOrg')({}).then((r) => { // null = not an org member: no nav, no chunk
      const org = r && r.data;
      if (seq === _orgSeq && org?.orgId) return _orgMod().then((M) => { if (seq === _orgSeq) M.mountNav(org); });
    }).catch(() => {});
  }, 1500, 0);
});
// TASK-9375 S5 — ORGANIZATION (end)
// integ/2096 perf budget — the two photo/sheet intake modals are off the cold load.
// Both modules only assign window.X = { open, close } at load (no auto-init), and
// every caller goes through window.X.open(...) at click time, so this stub loads
// the chunk on first open and is replaced when the module assigns window.X.
// Was: import './views/fleetPhotoIntake.js?v=10119001' (TASK-9716) + import './views/rateSheetIntake.js?v=10119001' (TASK-9768 2b / TASK-9622).
function _lazyIntake(globalName, thunk, label, failMsg) {
  if (window[globalName]) return;
  const load = () => _lazyImportWithRetry(thunk, label);
  window[globalName] = {
    open: (...args) => load()
      .then(() => { const M = window[globalName]; if (M && M.__lazyStub) throw new Error(label + ' did not register'); return M.open(...args); })
      .catch(() => window.showToast?.(failMsg, 'error')),
    close: () => {},
    __lazyStub: true,
  };
}
_lazyIntake('FleetPhotoIntake', () => __ddChunkWrap(import('./views/fleetPhotoIntake.js?v=10119001')), 'fleetPhotoIntake', 'Could not open the whiteboard photo reader. Try again.');
_lazyIntake('RateSheetIntake', () => __ddChunkWrap(import('./views/rateSheetIntake.js?v=10119001')), 'rateSheetIntake', 'Could not open the rate sheet reader. Try again.');
// TASK-9527 — Features (every company feature switch on one screen) is off the cold
// load: Settings → Features calls window.FeaturesPage.open(); the stub loads the chunk.
// TASK-10090 S1: the switch list is the shared registry (shared/company-features.js
// attaches window.CompanyFeatures); it loads first, inside the same lazy chunk.
_lazyIntake('FeaturesPage', () => __ddChunkWrap(import('./shared/company-features.js?v=10339264')).then(() => __ddChunkWrap(import('./views/features.js?v=10319527'))), 'features', 'Could not open Features. Try again.'); // TASK-10090 S1 shared registry · TASK-9956 S2 web search switch · TASK-9527 (5) module holders reach Features; scoped links (10195275, above TASK-9986 S1 10129986)
// perf/app-coldload-headroom (app cold-load budget, not raised) — window.* modules that
// only paint inside a sub-tab or a Settings box are off the cold load: Compliance →
// Permit Pete, Maintenance → Inspections (shop equipment) and the Claude approval
// rules box in Company Settings (each specifier appears once, in its thunk). None
// is on the driver path, and every caller already reaches them only through
// window.X?.method(...) at call time — so the stub loads the chunk on the first call and
// forwards; the module then replaces the stub (same as _lazyIntake). Routed views stay
// static (TASK-345 Slice L revert). `hosts` = methods whose first argument is the DOM
// host they paint into: a call whose host left the page, or that a teardown cancelled,
// while the chunk loaded is dropped. `cancels` = teardown methods: before the chunk has
// loaded nothing is mounted, so they only cancel a paint still waiting on the chunk.
// `hostOf` = { method: () => token-or-null } for a host method with no DOM argument: the
// call is stale once the token differs from its call-time value (or is null), and a newer
// call of the same method supersedes an older one.
function _lazyGlobal(globalName, thunk, label, methods, { hosts = [], hostOf = {}, cancels = [], failMsg = '' } = {}) {
  if (window[globalName]) return;
  let gen = 0;
  const seqs = {};
  const stub = { __lazyStub: true };
  for (const name of methods) {
    if (cancels.includes(name)) { stub[name] = () => { gen++; }; continue; }
    stub[name] = (...args) => {
      const at = gen;
      const seq = seqs[name] = (seqs[name] || 0) + 1;
      const own = hostOf[name];
      const tok = own ? own() : undefined;
      // A paint nobody is waiting for any more: its host left the page, a teardown ran,
      // or (self-hosted) a newer call of the same method superseded it.
      const stale = () => {
        if (!hosts.includes(name)) return false;
        if (at !== gen) return true;
        if (!own) return !!(args[0] && args[0].isConnected === false);
        return seq !== seqs[name] || !tok || own() !== tok;
      };
      return _lazyImportWithRetry(thunk, label).then(() => {
        const M = window[globalName];
        if (!M || M.__lazyStub) throw new Error(label + ' did not register');
        if (stale()) return undefined;
        return M[name](...args);
      }).catch((err) => {
        console.warn(`[Lazy] ${label}.${name} failed:`, err?.message || err);
        if (failMsg && !stale()) window.showToast?.(failMsg, 'error'); // COD r1: no toast for a screen already left
      });
    };
  }
  window[globalName] = stub;
}
_lazyGlobal('PermitPeteModule', () => __ddChunkWrap(import('./views/permitPete.js?v=10304876')), 'permitPete',
  ['init', 'selectSuggestion', 'openReview', 'closeReview', 'submit'],
  { hosts: ['init'], failMsg: 'Could not open Permit Pete. Try again.' });
_lazyGlobal('FacilityInspections', () => __ddChunkWrap(import('./facilityInspections.js?v=4884002')), 'facilityInspections',
  ['mount', 'unmount', 'closeFacilityModal'],
  { hosts: ['mount'], cancels: ['unmount', 'closeFacilityModal'], failMsg: 'Could not load Inspections. Try again.' });
_lazyGlobal('ClaudeApprovalRules', () => __ddChunkWrap(import('./claudeApprovalRules.js?v=3')), 'claudeApprovalRules',
  ['mount', 'mountSettings', 'openInSettings'], { hosts: ['mount', 'mountSettings'], failMsg: 'Could not load the Claude approval rules. Try again.',
    hostOf: { mountSettings: () => _settingsSession } }); // COD r1: never silent; Edge: closed / reopened Settings = stale
// One token per Settings open (closeSettingsModal nulls it) — a deferred paint or toast
// from an earlier open is dropped. Not modal visibility: mountSettings runs pre-unhide.
var _settingsSession = null;
// Thin wrappers preserving the original named-import API used throughout this file.
// PIN-driver and non-demo paths never trigger these, so the bytes stay off the
// wire on cold load for the worst-perf surface. All wrappers swallow their own
// rejections (logged at warn) so fire-and-forget callers in setTimeout don't
// generate unhandled promise rejection noise.
async function initTrailguide(...args) {
  try { const m = await _ensureTrailguide(); return m.initTrailguide(...args); }
  catch (err) { console.warn('[Lazy] initTrailguide failed:', err?.message); }
}
async function resetTrailguide(...args) {
  try { const m = await _ensureTrailguide(); return m.resetTrailguide(...args); }
  catch (err) { console.warn('[Lazy] resetTrailguide failed:', err?.message); }
}
async function initOnboardingHints(...args) {
  try { const m = await _ensureTrailguide(); return m.initOnboardingHints(...args); }
  catch (err) { console.warn('[Lazy] initOnboardingHints failed:', err?.message); }
}
async function showOnboardingHint(...args) {
  try { const m = await _ensureTrailguide(); return m.showOnboardingHint(...args); }
  catch (err) { console.warn('[Lazy] showOnboardingHint failed:', err?.message); }
}
async function seedDemoData(...args) {
  // demoSeeder consumer (L4810) DOES await — let errors propagate to caller's .catch
  const m = await _ensureDemoSeeder();
  return m.seedDemoData(...args);
}

// TASK-345 Slice L REVERTED 2026-05-28 — _ensureView / _ensureCrmBrochure /
// _ensureSmartAssistant / _preloadCommonViews removed. View modules are now
// statically imported at the top of this file so they're available
// synchronously. window._preloadCommonViews still exposed as a no-op for any
// callers that referenced it (cheap shim, removed in a follow-up cleanup).
window._preloadCommonViews = function _preloadCommonViewsNoop() { /* Slice L reverted */ };
import './lib/telemetry.js?v=15263';  // registers window.Telemetry
import './lib/action-breadcrumbs.js?v=15979';  // registers window.ActionLog (TASK-398 breadcrumb ring buffer)
import {
  createAccessRequest as oshaCreateAccessRequest,
  listAccessLog as oshaListAccessLog,
  markFulfilled as oshaMarkFulfilled,
  markDenied as oshaMarkDenied,
  voidEntry as oshaVoidEntry,
  getSlaStatus as oshaGetSlaStatus
} from './utils/oshaRecordsHelpers.js?v=4';

// ============================================
// TASK-855 — GATED DEBUG LOGGING (ddLog / ddWarn)
// Sensitive console output (customer rates, PO#, pay rates, emails/PII) must NOT
// emit to the prod console — these run on shared yard kiosks / tablets where a
// customer or driver could read the console. ddLog/ddWarn are no-ops unless debug
// is explicitly enabled, then pass through to console.log/console.warn.
//
// Enable via any of: localStorage 'dd_debug' = '1' · URL ?dddebug=1 (also persists
// to localStorage) · window.DD_DEBUG = true (flip live in the console).
// The flag is re-checked on every call (reads window.DD_DEBUG) so it can be toggled
// at runtime without a reload.
//
// Exposed on window.* (NOT an ES export) on purpose — view modules call
// window.ddLog(...) so there is no cross-module import that would collide with a
// ?v= cache-buster (Critical Pitfall #1: foo.js?v=100 and foo.js?v=200 are two
// separate ES module instances).
// ============================================
(function initDdDebugFlag() {
  let enabled = false;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('dddebug') === '1') {
      enabled = true;
      try { localStorage.setItem('dd_debug', '1'); } catch (_) { /* private mode */ }
    }
    if (localStorage.getItem('dd_debug') === '1') enabled = true;
  } catch (_) { /* localStorage blocked — stay off (safe default) */ }
  if (window.DD_DEBUG !== true) window.DD_DEBUG = enabled;
})();
function ddLog(...args) { if (window.DD_DEBUG) console.log(...args); }
function ddWarn(...args) { if (window.DD_DEBUG) console.warn(...args); }
window.ddLog = ddLog;
window.ddWarn = ddWarn;

// TASK-9855 slice 1 — wire window.StorageFile to the SDK, and register the
// driver vehicle-doc door: storage.rules deny driver sessions truck-docs /
// trailer-docs, so a denied driver read of a doc the Job Board shows goes to
// getDriverVehicleDocBytes (job-day-scoped, server-side) instead of the URL.
try {
  // identity + reset (COD): object URLs are cached per signed-in uid and all of
  // them are revoked on every auth change, so a shared tablet's next user can
  // never be handed a file the previous user's rules allowed.
  window.StorageFile.init({ storage, ref, getBlob: _sfGetBlob, getStorage: _sfGetStorage, getMetadata: _sfGetMetadata, getDownloadURL, identity: () => (auth.currentUser && auth.currentUser.uid) || '' });
  onAuthStateChanged(auth, () => { try { window.StorageFile.reset(); } catch (_) { /* never break auth */ } });
  window.StorageFile.registerDoor('driverVehicle', async ({ path, args }) => {
    const res = await httpsCallable(functions, 'getDriverVehicleDocBytes')({ vtype: args.vtype, vid: args.vid, storagePath: path });
    const d = (res && res.data) || {};
    if (!(d.ok && d.base64)) throw new Error('No document data returned');
    const bin = atob(d.base64);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    return new Blob([arr], { type: d.contentType || 'application/octet-stream' });
  }, {
    // Fix 2026-10-19: the iOS app cannot open a blob:, so there the same callable
    // (same checks) returns a 5-minute signed link that StorageFile.open hands to
    // Safari. Used ONLY by the iOS-shell branch of StorageFile.open.
    link: async ({ path, args }) => {
      const res = await httpsCallable(functions, 'getDriverVehicleDocBytes')({ vtype: args.vtype, vid: args.vid, storagePath: path, mode: 'link' });
      const d = (res && res.data) || {};
      if (!(d.ok && d.link && typeof d.url === 'string')) throw new Error('No document link returned');
      return d.url;
    },
  });
} catch (e) { console.error('[StorageFile] init failed', e); }

// ============================================
// ORIGINAL URL — snapshot BEFORE anything can modify window.location
// Version checkers, replaceState, redirects can strip query params.
// This is the ground truth for ?access=, ?timeoff=, etc.
// ============================================
const ORIGINAL_URL_PARAMS = new URLSearchParams(window.location.search);
const ORIGINAL_HASH = window.location.hash;

// ============================================
// GLOBAL MODAL SCROLL LOCK
// Automatically locks body scroll when any .modal is visible
// Prevents background content from scrolling on iOS
// ============================================
(function initModalScrollLock() {
  function checkModals() {
    const visibleModals = document.querySelectorAll('.modal:not(.hidden)');
    const isLocked = document.body.classList.contains('modal-open');
    if (visibleModals.length > 0 && !isLocked) {
      document.body.classList.add('modal-open');
      document.documentElement.classList.add('modal-open');
    } else if (visibleModals.length === 0 && isLocked) {
      document.body.classList.remove('modal-open');
      document.documentElement.classList.remove('modal-open');
    }
    // When multiple modals are stacked, only the topmost should be interactive
    if (visibleModals.length > 1) {
      const sorted = [...visibleModals].sort((a, b) => {
        const zA = parseInt(getComputedStyle(a).zIndex) || 0;
        const zB = parseInt(getComputedStyle(b).zIndex) || 0;
        return zA - zB;
      });
      // Disable interaction on all but the topmost
      sorted.forEach((m, i) => {
        if (i < sorted.length - 1) {
          m.style.pointerEvents = 'none';
        } else {
          m.style.pointerEvents = '';
        }
      });
    } else {
      // Single modal or none — restore pointer events
      visibleModals.forEach(m => { m.style.pointerEvents = ''; });
    }
  }
  const observer = new MutationObserver(checkModals);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class', 'style'],
    subtree: true,
    childList: true
  });
})();

// ============================================
// GLOBAL INPUT LENGTH ENFORCER
// Auto-caps text inputs at 255 chars, textareas at 2000 chars.
// Respects existing maxlength attrs. Use data-maxlen="N" to override.
// 🔴 TASK-9301: this clip is SILENT. Any free-text field whose words must reach us
// whole (assistant chats, feedback) sets data-maxlen above its server cap and
// refuses over-cap text at send time with a plain message instead.
// ============================================
(function initInputLengthEnforcer() {
  document.addEventListener('input', function(e) {
    const el = e.target;
    if (el.hasAttribute('maxlength')) return; // native maxlength already set
    const tag = el.tagName;
    let limit = 0;
    if (tag === 'INPUT' && el.type === 'text') {
      limit = parseInt(el.dataset.maxlen, 10) || 255;
    } else if (tag === 'TEXTAREA') {
      limit = parseInt(el.dataset.maxlen, 10) || 2000;
    }
    if (limit && el.value.length > limit) {
      el.value = el.value.slice(0, limit);
    }
  }, true); // capture phase — fires before any module handlers
})();

/** Clamp a string to maxLen chars. Returns '' for falsy input. */
function clampText(str, maxLen) {
  if (!str) return '';
  return typeof str === 'string' && str.length > maxLen ? str.slice(0, maxLen) : str;
}
window.clampText = clampText;

// ============================================
// TWA DETECTION — cache on first load because referrer clears after navigation
// Used to decide whether to open same-origin pages (like /marketing/academy.html)
// in the current TWA WebView (counts toward Play Console session time) vs
// Chrome Custom Tabs (does NOT count). Desktop users always get _blank.
// ============================================
(function detectTWA() {
  try {
    if (sessionStorage.getItem('isTWA') === '1') return;
    if (document.referrer && document.referrer.startsWith('android-app://')) {
      sessionStorage.setItem('isTWA', '1');
    }
  } catch (e) { /* sessionStorage can throw in private mode */ }
})();
function isTWA() {
  try { return sessionStorage.getItem('isTWA') === '1'; } catch (e) { return false; }
}
function academyTarget() { return isTWA() ? '_self' : '_blank'; }
window.isTWA = isTWA;
window.academyTarget = academyTarget;

// ============================================
// ADDRESS AUTOCOMPLETE — shared helper for geocodeAddress suggestions
// Usage: attachAddressAutocomplete(inputEl) — adds suggestion dropdown below any text input
// ============================================
let _addrACDebounce = null;
function attachAddressAutocomplete(inputEl) {
  if (!inputEl || inputEl.dataset.acAttached) return;
  inputEl.dataset.acAttached = '1';
  let _justSelected = false;
  let _debounce = null;

  const sugBox = document.createElement('div');
  sugBox.className = 'addr-ac-suggestions';
  sugBox.style.cssText = 'display:none;position:fixed;z-index:99999;background:#fff;border:1px solid var(--border-light);border-radius:0 0 8px 8px;box-shadow:var(--shadow-lg);max-height:240px;overflow-y:auto;';
  document.body.appendChild(sugBox);

  function positionSugBox() {
    const rect = inputEl.getBoundingClientRect();
    sugBox.style.top = rect.bottom + 'px';
    sugBox.style.left = rect.left + 'px';
    sugBox.style.width = rect.width + 'px';
  }

  inputEl.addEventListener('input', () => {
    if (_justSelected) { _justSelected = false; return; }
    clearTimeout(_debounce);
    const q = inputEl.value.trim();
    if (q.length < 3) { sugBox.style.display = 'none'; sugBox.innerHTML = ''; return; }
    // Skip autocomplete for coordinates and map URLs (pure numbers OK — Google Places handles them)
    if (/^-?\d{1,3}\.\d+[,\s]+-?\d{1,3}\.\d+$/.test(q) || q.startsWith('http')) {
      sugBox.style.display = 'none'; sugBox.innerHTML = ''; return;
    }
    _debounce = setTimeout(async () => {
      try {
        // Maps endpoints now require a Firebase ID token (safety-audit M3).
        const idToken = await auth.currentUser?.getIdToken();
        if (!idToken) { sugBox.style.display = 'none'; sugBox.innerHTML = ''; return; }
        const resp = await fetch('https://us-central1-dispatch-deck-be88c.cloudfunctions.net/placesAutocomplete', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
          body: JSON.stringify({ input: q })
        });
        const data = await resp.json();
        if (data.predictions && data.predictions.length > 0) {
          positionSugBox();
          sugBox.style.display = '';
          sugBox.innerHTML = data.predictions.map((p, i) =>
            `<div class="addr-ac-item${i === 0 ? ' addr-ac-first' : ''}" data-addr="${(p.fullText || '').replace(/"/g, '&quot;')}">
              <strong>${p.mainText || ''}</strong> <span style="color:var(--text-secondary);">${p.secondaryText || ''}</span>
            </div>`
          ).join('');
          sugBox.querySelectorAll('.addr-ac-item').forEach(el => {
            el.addEventListener('mousedown', (e) => {
              e.preventDefault();
              _justSelected = true;
              inputEl.value = el.dataset.addr;
              sugBox.style.display = 'none';
              sugBox.innerHTML = '';
              inputEl.dispatchEvent(new Event('change', { bubbles: true }));
            });
          });
        } else { sugBox.style.display = 'none'; sugBox.innerHTML = ''; }
      } catch { sugBox.style.display = 'none'; sugBox.innerHTML = ''; }
    }, 300);
  });

  inputEl.addEventListener('blur', () => {
    setTimeout(() => { sugBox.style.display = 'none'; }, 200);
  });

  document.addEventListener('click', function closeSug(e) {
    if (!sugBox.contains(e.target) && e.target !== inputEl) {
      sugBox.style.display = 'none';
      sugBox.innerHTML = '';
    }
    if (!document.body.contains(inputEl)) {
      document.removeEventListener('click', closeSug);
      sugBox.remove();
    }
  });
}
window.attachAddressAutocomplete = attachAddressAutocomplete;

// ============================================
// DEBUG LOGGING - Logs errors to Firestore for remote debugging
// Logs to BOTH tenant-specific and central collections for AI agent monitoring
// See CLAUDE.md for how to read these logs
// ============================================
// TASK-4840 (C) — max signed-out error records per page load (see logError).
let _preAuthLogBudget = 20;
async function logError(error, context = {}) {
  try {
    const tenant = window.AppState?.tenant || 'unknown';
    if (tenant === 'demo') return; // Don't log demo errors

    // TASK-620: systemic WRITE-error enrichment. When the error is a Firestore
    // permission-denied (FirebaseError code or the generic message), enrich the
    // breadcrumb with uid + coarse route + code so an "insufficient permissions"
    // report names WHO was denied WHERE — instead of forcing static rule-
    // archaeology. PII-safe: uid + op + coarse route ONLY, never doc data /
    // field values / query filter values. Fully guarded — must never throw or
    // break error logging itself.
    try {
      const _code = error?.code || '';
      const _msg = error?.message || (typeof error === 'string' ? error : '') || '';
      if (_code === 'permission-denied' || /missing or insufficient permissions/i.test(_msg)) {
        if (context.permissionDenied === undefined) context.permissionDenied = true;
        if (context.errCode === undefined && _code) context.errCode = _code;
        if (context.uid === undefined) {
          context.uid = window.AppState?.user?.uid || auth?.currentUser?.uid || 'anon';
        }
        if (context.route === undefined) {
          // Coarse route only — strip query params + segments beyond the first
          // (e.g. #/hr/candidates/{id} -> #/hr) so entity IDs never leak.
          const h = (typeof location !== 'undefined' && location.hash) || '';
          const raw = h.split('?')[0].split('/').slice(0, 2).join('/');
          // SECURITY (COD Adversary): location.hash is attacker-controllable and
          // this value is PERSISTED to the central errorLogs collection (super-
          // admin visible). Allowlist to a safe route charset so a hash like
          // #/<img onerror=...> can't store HTML; reject anything else as 'invalid'.
          context.route = /^#?\/?[a-z0-9_-]*$/i.test(raw) ? raw : 'invalid';
        }
      }
    } catch (_) { /* enrichment is best-effort — never break error logging */ }

    // TASK-398: flush the action breadcrumb trail so a crash carries the
    // sequence of steps that led to it. Fire-and-forget, swallows its own
    // errors — must never interfere with error logging itself. We tag only the
    // error's coarse NAME (e.g. 'TypeError'), never its raw message — the full
    // message + stack already live on the parallel debugLogs/errorLogs doc, and
    // the breadcrumb trail is intentionally structural-only / no free-text.
    try { window.ActionLog?.flush?.('error', { errorName: error?.name || 'Error' }); } catch (_) {}

    const userId = window.AppState?.user?.uid || 'anonymous';
    // SECURITY (H4, 2026-06-25): don't park the raw account email in the central
    // errorLogs collection — userId (uid) already carries identity. displayName is
    // safe human-readable; fall back to 'Unknown', NOT email.
    const userName = window.AppState?.user?.displayName || 'Unknown';

    // SECURITY (H4, 2026-06-25): driver / customer-portal / project-portal / sign /
    // accept-invite / verify pages carry SECRET bearer tokens in the URL query string
    // (?access=TOKEN, ?token=…). Persisting the full href into the queryable
    // debugLogs/errorLogs collections parks a replayable credential at rest. Strip the
    // query + hash — keep only origin+path, which is all that's useful for debugging.
    let safeUrl;
    try { const u = new URL(window.location.href); safeUrl = u.origin + u.pathname; }
    catch (_) { safeUrl = String(window.location.href).split('?')[0].split('#')[0]; }

    // TASK-4840 (C) — SIGNED OUT (login / signup / forgot-password screens).
    // Both Firestore writes below require auth (debugLogs: canReadTenant,
    // errorLogs: isAuth), so every error on these screens used to die as
    // "Failed to log error: Missing or insufficient permissions" — the auth
    // failures most worth having were the ones never recorded. The rules stay
    // closed; a rate-limited, size-capped public callable takes the record
    // instead (functions/src/index.js logPreAuthClientError). Per-page budget
    // so one broken page can't hammer it.
    if (!auth?.currentUser) {
      if (_preAuthLogBudget <= 0) return;
      _preAuthLogBudget--;
      const _code = (typeof error?.code === 'string') ? error.code : null;
      await httpsCallable(functions, 'logPreAuthClientError')({
        message: String(error?.message || String(error)).substring(0, 2000),
        stack: error?.stack ? String(error.stack).substring(0, 4000) : null,
        url: safeUrl,
        userAgent: navigator.userAgent.substring(0, 200),
        errCode: _code,
        context: { type: context.type || 'manual', module: context.module || null },
      });
      return;
    }

    const errorDoc = {
      type: 'error',
      message: error?.message || String(error),
      stack: error?.stack || null,
      url: safeUrl,
      userAgent: navigator.userAgent.substring(0, 200),
      context: context,
      timestamp: Timestamp.now()
    };

    // TASK-4840 (C) — the two writes are INDEPENDENT. They used to be chained,
    // so a signed-in user with no tenant yet (tenant === 'unknown': a Google
    // sign-in with no users/{uid} doc — exactly the pending-invite state) failed
    // the tenant write on canReadTenant and never reached the central write,
    // which only needs isAuth and would have succeeded.
    if (tenant && tenant !== 'unknown') {
      // Log to tenant-specific collection (existing behavior)
      try {
        await addDoc(collection(db, 'tenants', tenant, 'debugLogs'), errorDoc);
      } catch (e) {
        console.warn('[DebugLog] Tenant log write failed:', e?.code || e?.message || e);
      }
    }

    // ALSO log to central collection for super admin AI agent monitoring
    await addDoc(collection(db, 'errorLogs'), {
      ...errorDoc,
      tenantId: tenant,
      userId: userId,
      userName: userName,
      severity: categorizeError(error),
      resolved: false
    });
  } catch (e) {
    // Silent fail - don't break app if logging fails
    console.warn('[DebugLog] Failed to log error:', e);
  }
}

// Categorize error severity for AI agent prioritization
function categorizeError(error) {
  const msg = (error?.message || String(error)).toLowerCase();

  // Critical - auth, payment, data loss
  if (msg.includes('auth') || msg.includes('permission') || msg.includes('stripe') ||
      msg.includes('payment') || msg.includes('firebase') || msg.includes('firestore')) {
    return 'critical';
  }

  // High - core functionality broken
  if (msg.includes('cannot read') || msg.includes('undefined') || msg.includes('null') ||
      msg.includes('failed to fetch') || msg.includes('network')) {
    return 'high';
  }

  // Medium - feature issues
  if (msg.includes('dvir') || msg.includes('dispatch') || msg.includes('roster') ||
      msg.includes('maintenance') || msg.includes('pdf')) {
    return 'medium';
  }

  // Low - cosmetic or minor
  return 'low';
}

// Global error handlers
window.onerror = function(message, source, lineno, colno, error) {
  logError(error || { message, stack: `${source}:${lineno}:${colno}` }, { type: 'uncaught' });
  return false; // Let default handler run too
};

window.onunhandledrejection = function(event) {
  logError(event.reason, { type: 'unhandledRejection' });
};

// Export for manual logging from other modules
window.logError = logError;

// Intercept console.error to auto-log module errors to Firestore
// Catches all [Billing], [Dispatch], [Roster], etc. prefixed errors without modifying every catch block
const _origConsoleError = console.error;
const _modulePattern = /^\[(\w+)\]/;
let _lastLoggedError = '';
let _lastLoggedTime = 0;
console.error = function(...args) {
  _origConsoleError.apply(console, args);
  try {
    const first = typeof args[0] === 'string' ? args[0] : '';
    const match = first.match(_modulePattern);
    if (match) {
      const module = match[1];
      const errObj = args.find(a => a instanceof Error);
      const message = errObj?.message || args.slice(1).map(a => typeof a === 'string' ? a : a?.message || '').join(' ').substring(0, 500);
      // Dedupe — don't log the same error twice within 5 seconds
      const key = module + message;
      const now = Date.now();
      if (key !== _lastLoggedError || now - _lastLoggedTime > 5000) {
        _lastLoggedError = key;
        _lastLoggedTime = now;
        // TASK-10108 — with an Error object the row used to keep only its message,
        // so "[HR] hrBgCheck load failed:" (WHICH screen) was lost. Keep the label
        // and the error's name (TypeError / ReferenceError …) beside it. Only a
        // label shaped like "[HR] someName failed:" / "[HR] someName load failed:"
        // is kept — one code identifier, nothing free-text, so a name or number
        // interpolated into a label never lands in errorLogs (COD).
        const _labelOk = /^\[\w+\] [A-Za-z_$][\w$.]{0,60}(?: load)? failed:?$/.test(first);
        logError(errObj || { message: first + ' ' + message }, errObj
          ? { type: 'module-error', module, ...(_labelOk ? { label: first } : {}), errName: String(errObj.name || '').substring(0, 60) }
          : { type: 'module-error', module });
      }
    }
  } catch (e) { /* never break console.error */ }
};

// ============================================
// SAFE UPLOAD — wraps uploadBytes with error logging
// Re-throws so callers can handle; logs context to errorLogs on failure
// ============================================
window.safeUpload = async function(storageRef, data, metadata, context = {}) {
  try {
    await uploadBytes(storageRef, data, metadata);
    const url = await getDownloadURL(storageRef);
    return url;
  } catch (err) {
    // If unauthorized, try refreshing claims and retry once
    if (err.code === 'storage/unauthorized' && auth.currentUser && !context._retried) {
      console.warn('[Upload] Unauthorized — refreshing token and retrying...');
      try {
        await auth.currentUser.getIdToken(true);
        return await window.safeUpload(storageRef, data, metadata, { ...context, _retried: true });
      } catch (retryErr) {
        console.error('[Upload] Retry also failed:', retryErr);
      }
    }
    const fileName = storageRef?.fullPath?.split('/').pop() || 'unknown';
    console.error(`[Upload] Failed: ${fileName}`, err);
    logError(err, {
      module: context.module || 'unknown',
      operation: 'file-upload',
      fileName,
      storagePath: storageRef?.fullPath,
      sizeBytes: data?.size || data?.byteLength || 0,
      ...context
    });
    throw err;
  }
};

// ============================================
// TASK-1479 — Firestore THROWS on an `undefined` field value (this SDK is not
// configured with ignoreUndefinedProperties), and both audit writers below catch
// and log rather than surface. So one conditionally-undefined key silently
// deleted an entire audit action, forever, on every tenant.
//
// It was not hypothetical: on the founder tenant, across 7,824 auditLog rows,
// UPDATE_PERSON and UPDATE_TRUCK had ZERO — the only two roster actions whose
// payloads carry conditional undefined (`oldName: changed ? old : undefined`,
// `terminated: data.terminated`) — while CREATE_PERSON / ASSET_DEACTIVATE /
// TRUCK_OWNERSHIP_CHANGED, which coerce to booleans, wrote fine. Person and
// vehicle EDITS were unauditable, which is how a driver termination ended up
// with no trail at all.
//
// Arrays: Firestore rejects an undefined ELEMENT too, and mapping alone doesn't
// remove it — undefined entries are dropped. An undefined element carries no
// information, and substituting null would silently invent a value in an audit
// record.
//
// TASK-1495 (2026-08-21) — the nine call sites that used to addDoc straight into
// tenants/{tid}/auditLog and bypass both shared writers are GONE: 3 in this file,
// 3 in billing.js (ticket_hours_override / ticket_edit / ticket_undo), 2 in hr.js,
// 1 in compliance.js. All now go through window.logAudit, so all are covered by
// this strip. tests/task1495-audit-writer-routing.test.mjs fails the build if a
// new direct writer appears — the allowance list is empty on purpose.
//
// logOperation below is the OTHER shared writer and keeps its own direct addDoc;
// it is a wrapper with a different payload shape, not a bypass.
function _stripUndefinedForFirestore(v) {
  if (Array.isArray(v)) {
    return v.filter((x) => x !== undefined).map(_stripUndefinedForFirestore);
  }
  if (v && typeof v === 'object' && !(v instanceof Date) && typeof v.toDate !== 'function') {
    const out = {};
    for (const [k, val] of Object.entries(v)) {
      if (val === undefined) continue;
      out[k] = _stripUndefinedForFirestore(val);
    }
    return out;
  }
  return v;
}

// OPERATION TRACKING — thin wrapper on logAudit for success/fail + duration
// Usage: window.logOperation('dispatch', 'JOB_CREATE', { status: 'success', jobId, durationMs })
// ============================================
window.logOperation = async function(module, operation, details = {}) {
  try {
    const tenant = window.AppState?.tenant;
    if (!tenant || tenant === 'demo') return;
    const entry = {
      category: module,
      action: operation,
      timestamp: Timestamp.now(),
      user: window.AppState?.user?.email || window.AppState?.driverPersonName || 'unknown',
      status: details.status || 'success',
      durationMs: details.durationMs || null,
      errorMessage: details.error?.message || details.errorMessage || null
    };
    // Spread remaining details (jobId, photoCount, etc.) but strip internal fields
    const { status, durationMs, error, errorMessage, ...rest } = details;
    // TASK-1479 — same undefined-throw hazard as logAudit; this helper writes to
    // the SAME auditLog collection, so it needs the same guard.
    Object.assign(entry, _stripUndefinedForFirestore(rest));
    await addDoc(collection(db, 'tenants', tenant, 'auditLog'), entry);
  } catch (err) {
    console.warn('[Operation] Log failed:', err.message);
  }
};

// ============================================
// EMAIL LOGGING - Track all outgoing emails for Overlord visibility
// Logs to central _emailLogs collection for AI agent monitoring
// ============================================
async function logEmailEvent(eventData) {
  try {
    const logEntry = {
      ...eventData,
      timestamp: Timestamp.now(),
      userId: window.AppState?.user?.uid || null,
      userEmail: window.AppState?.user?.email || eventData.recipient,
      environment: window.location.hostname.includes('localhost') ? 'dev' : 'prod'
    };

    // Log to central collection for Overlord/admin visibility
    await addDoc(collection(db, '_emailLogs'), logEntry);
    console.log('[EmailLog] Logged:', eventData.type, eventData.status);
  } catch (e) {
    // Silent fail - don't break app if logging fails
    console.warn('[EmailLog] Failed to log email event:', e);
  }
}

// Export for use from other modules
window.logEmailEvent = logEmailEvent;

// ============================================
// APP STATE
// ============================================
window.AppState = {
  user: null,
  tenant: null,
  currentView: 'roster',
  isDemo: false,
  viewCleanup: null,
  driverMode: false,
  signupInProgress: false
};

// TASK-9653 S1 — park an open hint from the ORIGINAL hash now, before any
// navigation can rewrite it (doNavigateToView sets the bare `#/view`, and
// sign-in / OAuth-return code may replace the hash before the router's first
// signed-in pass). The router re-parks on every hashchange that carries one
// and drops it for a view this session cannot open; the view drains it.
try { window.OpenLinks?.parkFromHash?.(ORIGINAL_HASH, window.AppState); } catch (_) {}

// Detect iOS Capacitor app (for App Store compliance — hide Stripe, show Apple IAP notice)
window.isIOSApp = !!(window.Capacitor?.isNativePlatform?.() || (window.Capacitor && /iphone|ipad|ipod/i.test(navigator.userAgent)));
window.isNativeApp = !!window.Capacitor?.isNativePlatform?.();
// TASK-10111 — window.NativeLinkOpen: in the iOS app a tapped DispatchDeck link (approveAt /
// reviewAt / record links) arrives as Capacitor's appUrlOpen event, never as a navigation; it
// hands the link's #/ hash to the router. Native shell only, and lazy, so browsers never
// download it (perf budget). Runs after every static import, so approvalsBadge.js's
// hashchange listener (it parks the approve link's id) is already up; the shell keeps the
// cold-launch event until this listener takes it.
if (window.isNativeApp) __ddChunkWrap(import('./utils/nativeLinkOpen.js?v=1')).catch((e) => console.warn('[NativeLinks] load failed:', e?.message || e)); // TASK-10111 (new file)

// Store badges on the sign-in screen are for browser visitors. Inside the iOS app a
// Google Play badge is an App Review rejection (2.3.10 — no other-platform references),
// and inside either app "download the app" is noise.
if (window.isIOSApp || window.isNativeApp || isTWA()) {
  try { document.getElementById('auth-store-badges')?.style.setProperty('display', 'none'); } catch (_) {}
}

// Capture UTM + referrer on page load.
// First-touch semantics: paid UTM params always win (overwrite on any landing with UTM);
// organic/direct attribution is written only when no prior entry exists, so internal
// navigation never clobbers the original source.
(function() {
  try {
    const params = new URLSearchParams(window.location.search);
    const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid', 'fbclid'];
    const utm = {};
    utmKeys.forEach(k => { if (params.get(k)) utm[k] = params.get(k); });
    const hasUtm = Object.keys(utm).length > 0;

    const existingRaw = localStorage.getItem('dd_utm');
    let existing = null;
    try { existing = existingRaw ? JSON.parse(existingRaw) : null; } catch (_) {}

    // First-touch preservation within 90 days. Expire stale attribution so a
    // 2024 visit doesn't get credit for a 2026 signup.
    const NINETY_DAYS_MS = 90 * 24 * 60 * 60 * 1000;
    const existingFresh = existing && existing.capturedAt &&
      (Date.now() - new Date(existing.capturedAt).getTime()) < NINETY_DAYS_MS;
    if (!hasUtm && existingFresh) return;

    let referrerHost = 'direct';
    try {
      if (document.referrer) {
        const u = new URL(document.referrer);
        if (u.protocol === 'http:' || u.protocol === 'https:') {
          referrerHost = u.hostname;
        }
      }
    } catch (_) {}

    const record = {
      ...utm,
      landingPage: window.location.pathname,
      referrer: referrerHost,
      capturedAt: new Date().toISOString(),
    };
    localStorage.setItem('dd_utm', JSON.stringify(record));
  } catch (_) { /* localStorage unavailable (private browsing, quota) — skip */ }
})();

// TASK-9183 — first-touch AND last-touch, beside the legacy dd_utm record above
// (which a later paid click overwrites, so it is neither). Same storage, same
// 90-day TTL, no new tracker. landing.html runs the identical capture.
//   dd_touch_first — written once, the first visit we ever saw
//   dd_touch_last  — rewritten on every real arrival: UTM / click id / partner
//                    code on the URL, or a referrer from outside our own hosts.
//                    Internal navigation and bare reloads never count.
// Path only for landingPage (a query string can carry an email or a token).
// The server re-cleans all of it (functions/src/utils/signupAttribution.js).
function captureSignupTouch() {
  try {
    const TTL = 90 * 24 * 60 * 60 * 1000;
    const params = new URLSearchParams(window.location.search);
    const t = {};
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid', 'fbclid']
      .forEach((k) => { const v = params.get(k); if (v) t[k] = v.slice(0, 300); });
    const ref = params.get('ref') || params.get('partner');
    if (ref && /^[A-Za-z0-9_-]{1,40}$/.test(ref)) t.ref = ref;
    let host = 'direct';
    try {
      if (document.referrer) {
        const u = new URL(document.referrer);
        if (u.protocol === 'http:' || u.protocol === 'https:') host = u.hostname.toLowerCase();
      }
    } catch (_) {}
    const own = host === window.location.hostname.toLowerCase()
      || /(^|\.)(dispatchdeck\.app|web\.app|firebaseapp\.com)$/.test(host)
      || /^(accounts\.google\.com|appleid\.apple\.com|login\.microsoftonline\.com|checkout\.stripe\.com|billing\.stripe\.com)$/.test(host);
    const external = host !== 'direct' && !own;
    const isTouch = Object.keys(t).length > 0 || external;
    const rec = { ...t, landingPage: window.location.pathname, referrer: external ? host : 'direct', capturedAt: new Date().toISOString() };
    const fresh = (k) => {
      try {
        const v = JSON.parse(localStorage.getItem(k) || 'null');
        return v && v.capturedAt && (Date.now() - new Date(v.capturedAt).getTime()) < TTL ? v : null;
      } catch (_) { return null; }
    };
    if (!fresh('dd_touch_first')) localStorage.setItem('dd_touch_first', JSON.stringify(rec));
    if (isTouch || !fresh('dd_touch_last')) localStorage.setItem('dd_touch_last', JSON.stringify(rec));
  } catch (_) { /* storage blocked — attribution is best-effort */ }
}
captureSignupTouch();

function getSignupTouches() {
  const TTL = 90 * 24 * 60 * 60 * 1000;
  const read = (k) => {
    try {
      const v = JSON.parse(localStorage.getItem(k) || 'null');
      return v && v.capturedAt && (Date.now() - new Date(v.capturedAt).getTime()) < TTL ? v : null;
    } catch (_) { return null; }
  };
  return { firstTouch: read('dd_touch_first'), lastTouch: read('dd_touch_last') };
}

// TASK-9183 — the optional signup phone. Same rule the landing lead form uses
// (US/Canada, 10 digits after an optional leading 1). Empty is fine.
function isValidSignupPhone(raw) {
  let d = String(raw || '').replace(/\D/g, '');
  if (d.length === 11 && d[0] === '1') d = d.slice(1);
  return d.length === 10 && d[0] !== '0' && d[0] !== '1';
}

// Fire-and-forget: the server writes _signupDetails/{tenantId} (source, touches,
// phone). Never blocks or fails a signup.
function recordSignupDetails(tenantId, { phone = '', entry = 'signup' } = {}) {
  try {
    const { firstTouch, lastTouch } = getSignupTouches();
    httpsCallable(functions, 'recordSignupDetails')({ tenantId, phone, entry, firstTouch, lastTouch })
      .catch((err) => console.warn('[Auth] Signup details not saved (non-blocking):', err?.message || err));
  } catch (err) {
    console.warn('[Auth] Could not invoke recordSignupDetails (non-blocking):', err?.message || err);
  }
}

function getUtmData() {
  try {
    const raw = localStorage.getItem('dd_utm');
    if (!raw) return null;
    const data = JSON.parse(raw);
    // TTL check — drop stale first-touch beyond 90 days.
    if (data?.capturedAt) {
      const age = Date.now() - new Date(data.capturedAt).getTime();
      if (age > 90 * 24 * 60 * 60 * 1000) return null;
    }
    return data || null;
  } catch (e) { return null; }
}

// Filter test/internal accounts from GA4 conversion events
const TEST_EMAIL_PATTERNS = [/@test-dispatchdeck\.com$/i, /^christopherkjorstad@gmail\.com$/i, /^ckjorstad@trippintrucking\.com$/i, /^chris@bluecollar\.dev$/i, /^john@company\.com$/i];
function isTestEmail(email) {
  return !email || TEST_EMAIL_PATTERNS.some(p => p.test(email));
}

// ============================================
// FEATURE FLAGS
// ============================================
function isVehicleInventoryEnabled() {
  return true;
}
window.isVehicleInventoryEnabled = isVehicleInventoryEnabled;

// ============================================
// VERSION CHECKING - Auto-update for users
// ============================================
const APP_VERSION_KEY = 'dispatchdeck_version';
let versionCheckInterval = null;

// TASK-9121 — "Later" snooze for the update banner. In-memory ONLY: a real
// reload (the thing the banner asks for) clears it, and 15 min is deliberately
// shorter than a shift so the TASK-1193/TASK-1197 stale-bundle counterweight
// still holds — checkForAppUpdate re-fires every 5 min plus on focus.
let _updateSnoozedUntil = 0;
const UPDATE_SNOOZE_MS = 15 * 60 * 1000;

// TASK-9130 — the OFFICE dismiss is not a snooze. Chris, 2026-09-10: "if they do
// it later, the banner comes later? I dont want the banner stuck covering up
// utilities." A 15-minute re-show of a full card is the same interruption again,
// so on the office side "Dismiss" means gone for the session and the standing
// affordance moves to .sidebar-version, which covers nothing. Drivers KEEP the
// 15-minute snooze: PIN mode has no sidebar to fall back to, and TASK-1193 /
// TASK-1197 exist because a driver rode a stale bundle across a whole shift.
let _updateDismissedThisSession = false;

// 2026-09-23 (TASK-9327 follow-up — "tickets still aren't saving") — the version
// THIS TAB IS RUNNING. checkForAppUpdate used to compare version.json against
// localStorage, and localStorage is SHARED BY EVERY TAB on the origin. So the
// moment any tab on the machine applied an update (applyUpdate writes the key),
// every OTHER tab still running the old bundle read "cached === server" and went
// silent — forever. That is how a billing tab loaded before the TASK-9327 fix
// kept writing typed 0-hour lines WITHOUT the hoursManual stamp all afternoon on
// 2026-09-23, re-arming the very self-heal the fix exists to stop, while
// office@ on the fixed bundle stamped correctly. The reverse was wrong too: a
// FRESH tab running current code showed "Update available" whenever the shared
// key was stale.
//
// The truth is the bundle's own cache-buster: scripts/build-frontend.cjs syncs
// index.html's /dist/app.min.js?v= to version.json on every hosting build
// (TASK-1198, and it runs as a hosting predeploy), so the ?v= on the <script>
// that loaded this page IS the running version. Read once; a tab cannot change
// bundles without reloading. null (source mode, a page with no buster) falls
// back to the old localStorage comparison, unchanged.
let _runningBundleVersion;
function getRunningBundleVersion() {
  if (_runningBundleVersion !== undefined) return _runningBundleVersion;
  _runningBundleVersion = null;
  try {
    for (const sc of Array.from(document.scripts || [])) {
      const src = sc.getAttribute('src') || '';
      if (!/\/(dist\/app\.min|src\/app)\.js\?/.test(src)) continue;
      const v = new URL(src, window.location.href).searchParams.get('v');
      if (v && /^\d+(\.\d+)+$/.test(v)) { _runningBundleVersion = v; break; }
    }
  } catch (_) { _runningBundleVersion = null; }
  return _runningBundleVersion;
}

// ============================================
// UNSAVED-WORK TRACKING (TASK-9121)
// ============================================
// WHY: the update banner reloads the app. Mike lost a half-built job to it.
// A confirm that fires on EVERY refresh trains people to dismiss it and then
// protects nobody ([[lesson_a_guard_that_cries_wolf_gets_repaired_not_fixed]]),
// so we only interrupt when there is genuinely something to lose.
//
// The spec's first shape was "is a modal with form fields open?". That is both
// too wide (an untouched prefilled Edit form has nothing to lose) and too
// narrow (it misses every inline, non-modal form — and after the z-index fix
// below the banner is UNCLICKABLE while a modal is open, so a modal-only test
// would have had almost no live path at all). So we track what the user
// actually EDITED instead, wherever it lives.
//
// COVERED: any input/textarea/select/contenteditable the user typed into or
// changed, modal or not, plus a stroke drawn on a signature canvas — counted
// only while that element is still on screen and still holds the edit.
// NOT COVERED: state that never touches a DOM control (drag-reorder, map pins),
// and a click-without-drag on a chart canvas is deliberately not a "stroke".
//
// >>> TASK-9121-UNSAVED-WORK (extracted verbatim by tests/update-banner-unsaved-work.unit.cjs) >>>
const _dirtyFields = new Set();
let _unsavedTrackingStarted = false;

function _isTrackedField(el) {
  if (!el || !el.tagName) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') {
    const type = String(el.type || '').toLowerCase();
    // A search/filter box is chrome, not work — reloading it loses a keystroke.
    if (type === 'search' || type === 'hidden') return false;
  } else if (!el.isContentEditable) {
    return false;
  }
  const hint = `${el.id || ''} ${el.name || ''} ${typeof el.className === 'string' ? el.className : ''}`.toLowerCase();
  // A checkbox/radio's OWN name is data, not a role: #oil-filter-replaced (the
  // fluids form) is real work that happens to say "filter". Those two are
  // exempted only by sitting in a filter-named container (below), never by
  // their own id.
  const ownType = String(el.type || '').toLowerCase();
  if (ownType !== 'checkbox' && ownType !== 'radio' && /search|filter|lookup|typeahead/.test(hint)) return false;
  // MEASURED, not guessed (2026-09-10, live app, all 11 nav views): every
  // visible select/checkbox sitting OUTSIDE a modal was page chrome that acts
  // immediately — #sort-fleet, #clock-person, #billing-timeoff-toggle, the
  // compliance row status pickers. Counting those as "unsaved work" is how a
  // guard starts crying wolf. Inside a .modal or a <form> the same control is
  // real form state, so it counts there.
  const t = String(el.type || '').toLowerCase();
  if (el.tagName === 'SELECT' || t === 'checkbox' || t === 'radio') {
    if (typeof el.closest !== 'function') return true;
    const scope = el.closest('.modal, form');
    if (!scope) return false;
    // TASK-9332 — the word "filter" is often on the WRAPPER, not the control:
    // Inventory > History is <div class="inventory-history-filter"><select
    // id="inventory-history-type">, and changing it re-renders the list, so
    // there is nothing to lose. A select/checkbox/radio inside a filter-named
    // container acts immediately → chrome. Only when that container sits
    // STRICTLY inside the modal/form: a modal or form that is itself named
    // "filter…" never blanket-exempts its fields, and typed text (qtys, notes)
    // is never exempted by a wrapper — only by its own search/filter hint.
    const wrap = el.closest('[id*="filter"], [class*="filter"]');
    if (wrap && wrap !== scope && typeof scope.contains === 'function' && scope.contains(wrap)) return false;
  }
  return true;
}

// Still on screen? A closed modal is display:none, so its fields measure 0 —
// which is exactly the "user threw the form away themselves" case.
function _isOnScreen(el) {
  if (!el || el.isConnected === false) return false;
  if ((el.offsetHeight || 0) > 0 || (el.offsetWidth || 0) > 0) return true;
  // TASK-9753 — a field in a Settings section you switched away from is hidden,
  // not gone: the one Save Settings still writes it, so its edit still counts.
  // Only the section switch is looked through; a field hidden for its own
  // reason (an inline display:none group) stays off screen, as before.
  try {
    const sec = typeof el.closest === 'function' ? el.closest('#settings-modal .settings-section') : null;
    // Only while Settings is open — a closed Settings modal hides everything.
    if (sec && !document.getElementById('settings-modal')?.classList.contains('hidden')) {
      for (let n = el; n && n !== sec; n = n.parentElement) {
        if (n.style && n.style.display === 'none') return false;
        if (n.hidden) return false;
      }
      return true;
    }
  } catch (_) {}
  return false;
}

// Typed-then-cleared is not work. Checkbox/radio compare against their default
// so an untouched prefilled form stays quiet.
function _stillHoldsWork(el) {
  if (el.tagName === 'INPUT') {
    const type = String(el.type || '').toLowerCase();
    if (type === 'checkbox' || type === 'radio') return el.checked !== el.defaultChecked;
    if (type === 'file') return !!(el.files && el.files.length);
    return String(el.value == null ? '' : el.value).trim() !== '';
  }
  if (el.tagName === 'TEXTAREA') return String(el.value == null ? '' : el.value).trim() !== '';
  // select / contenteditable / canvas — the edit event itself is the evidence.
  return true;
}

function _noteUserEdit(e) {
  const el = e && e.target;
  if (_isTrackedField(el)) _dirtyFields.add(el);
}

function _markCanvasDirty(canvas) {
  if (canvas && canvas.tagName === 'CANVAS') _dirtyFields.add(canvas);
}

// A stroke = pointerdown/touchstart on a canvas FOLLOWED by movement. Signature
// pads qualify; a plain click on a chart does not. Both event families are
// wired because several pads preventDefault() on touchstart.
function _noteCanvasPointerDown(e) {
  const canvas = e && e.target;
  if (!canvas || canvas.tagName !== 'CANVAS' || typeof canvas.addEventListener !== 'function') return;
  const mark = () => _markCanvasDirty(canvas);
  canvas.addEventListener('pointermove', mark, { once: true });
  canvas.addEventListener('touchmove', mark, { once: true });
}

// TASK-9129/9138 — `root` scopes the SAME detector to one subtree so the modal
// close guard can ask "is there unsaved work in THIS modal", without a second
// dirty-field set to drift out of sync. Omitted = app-wide, which is exactly
// what the update banner (TASK-9121) still asks for.
function hasUnsavedWork(root) {
  let found = false;
  for (const el of Array.from(_dirtyFields)) {
    if (el.isConnected === false) { _dirtyFields.delete(el); continue; }
    if (root && typeof root.contains === 'function' && !root.contains(el)) continue;
    if (!_isOnScreen(el)) continue;
    if (!_stillHoldsWork(el)) continue;
    found = true;
  }
  return found;
}

function startUnsavedWorkTracking() {
  if (_unsavedTrackingStarted || typeof document === 'undefined') return;
  _unsavedTrackingStarted = true;
  // Capture phase: a view that stopPropagation()s its own input events can't
  // blind the guard.
  document.addEventListener('input', _noteUserEdit, true);
  document.addEventListener('change', _noteUserEdit, true);
  document.addEventListener('pointerdown', _noteCanvasPointerDown, true);
  document.addEventListener('touchstart', _noteCanvasPointerDown, true);
}
// <<< TASK-9121-UNSAVED-WORK <<<

// ============================================
// MODAL CLOSE GUARD (TASK-9129 + TASK-9138)
// ============================================
// WHY: closing a modal IS the data-loss door. Chris, on the shipped 9121 banner:
// "Drop the banner below modals — what if they close the modal?" Measured on
// this tree 2026-09-10:
//     44 backdrop handlers  (`if (e.target === modal)`)  +3 written inline in crm.js
//    276 pure close-intent inline onclicks (the X and the Cancel button)
// Every one of them threw the form away with no prompt and no undo. #modal-job —
// the Add Job form Mike lost — has NO backdrop handler at all, so its only exit
// was one of the 276.
//
// 🔴 THE CHOKEPOINT IS THE CLICK, NOT closeModal(). closeModal() is also called
// after a SUCCESSFUL save (dispatch.js :10550 :11022 :15260 :22527). Guarding the
// function would ask a dispatcher whether to discard a job already written to
// Firestore. So this guards ONE user gesture — a trusted click whose handler does
// nothing but close — in ONE capture-phase document listener. Zero view files are
// touched, and a modal added tomorrow is born covered.
//
// WHAT COUNTS AS A CLOSE:
//  - backdrop: e.target IS the .modal, and that modal is actually backdrop-
//    closable (see the probe below). A backdrop click on a modal that ignores it
//    (#modal-job) must NOT nag — nothing was going to be lost.
//  - X / Cancel: the nearest onclick-bearing ancestor inside the modal, whose
//    handler body is close calls and NOTHING else. `saveTicket()` never matches;
//    `closeModal('billing-day');openReviewTicket(id)` never matches either — a
//    compound onclick is a navigation, and the destination decides.
//
// NOT COVERED (measured, stated rather than hidden): Escape is not wired to any
// data modal in this app — only #feature-upgrade-modal (no fields) and the
// showConfirm/showPrompt dialogs themselves — so there is no Escape hole to
// close. Close buttons wired by addEventListener instead of an onclick attribute
// (trailguide.js, smartAssistant.js) are help overlays with no form state.

// >>> TASK-9129-CLOSE-GUARD (extracted verbatim by tests/modal-close-guard.unit.cjs) >>>
// A modal is backdrop-closable if something listens for a click on it. Two ways
// that is written in this tree — `modal.onclick = ...` (22 sites + 3 inline in
// crm.js, readable directly) and `modal.addEventListener('click', ...)` (20
// sites, invisible from the DOM). The shim below is the only way to see the
// second kind; it tags and always calls through.
const _backdropCloseCapable = typeof WeakSet === 'function' ? new WeakSet() : null;

// 🔴 COD Code Reviewer, caught before ship: a click listener is not the same as a
// close. `jobboard.js:9153` registers a DELIBERATE no-op backdrop handler on the
// driver clock-out sheet — `if (e.target === modal) { /* don't close on backdrop
// click */ }`. Tagging that would offer a driver "Discard?" on a tap that discards
// nothing, which is the exact cry-wolf failure this guard exists to avoid. So we
// look at whether the handler DOES anything: strip the parameter list and the
// control keywords, then ask if a call survives.
// Measured against the SHIPPED artifact, not guessed — in public/dist/app.min.js
// that no-op is `R=>{R.target}` (no call at all) while the real ones are
// `o=>{o.target===n&&n.remove()}` / `n=>{if(n.target===t)...}`. So the test holds
// on both the source and the minified bundle prod actually serves.
function _listenerDoesSomething(listener) {
  let text;
  try { text = String(listener); } catch (_) { return true; }   // unreadable → assume real
  const body = text
    .replace(/^\s*(?:async\s+)?(?:function\s*[\w$]*\s*)?\([^)]*\)\s*(?:=>)?/, '')
    .replace(/^\s*[\w$]+\s*=>/, '')
    .replace(/\b(?:if|for|while|switch|catch|return|typeof|new|do|else)\s*\(/g, '(');
  return /[\w$)\]]\s*\(/.test(body);
}

(function installBackdropCloseProbe() {
  if (typeof Element === 'undefined' || !_backdropCloseCapable) return;
  const proto = Element.prototype;
  const orig = proto.addEventListener;
  if (typeof orig !== 'function' || orig.__ddCloseGuardShim) return;
  function addEventListener(type, listener, options) {
    if (type === 'click') {
      try {
        if (this && this.classList && this.classList.contains('modal') && _listenerDoesSomething(listener)) {
          _backdropCloseCapable.add(this);
        }
      } catch (_) { /* tagging must never break a listener registration */ }
    }
    return orig.apply(this, arguments);
  }
  addEventListener.__ddCloseGuardShim = true;
  proto.addEventListener = addEventListener;
})();

// The guard's own dialogs. #confirm-modal is z-index 9999 so it paints above the
// modal it is asking about, but it is still a .modal and must never guard itself.
// 🔴 COD Blast-Radius, caught before ship: two billing modals ALREADY guard
// themselves. closeLineItemsModal() (billing.js:12229) and closeReviewModal()
// (billing.js:27911) each open their own confirm() off their own dirty flag, which
// this guard cannot see. Without the exemption the user gets asked twice — ours,
// then, on Discard, theirs — because the replayed click is untrusted and sails
// past us straight into their check. A modal that owns its guard keeps it.
// (Both of those are NATIVE confirm(), a pre-existing pitfall-6 violation that
// blocks browser automation. Not this lane's to fix — worth its own row.)
// 'auth-modal' — the sign-in screen is a .modal that can never be closed, but
// roster/billing/timeoff/pretrip/maintenance.js each tag EVERY .modal with a
// backdrop listener on init, so after visiting one of them and logging out, a
// click on the grey area around a filled-in sign-in card asked "Discard your
// changes?" about a login form (Chris, 2026-09-23 screenshot).
// 'modal-mass-text' — TASK-9532: Notify Crew keeps an edited message as a draft
// (per user + tenant + day range, dispatch.js saveMassTextDraftNow) and offers it
// back on reopen, so "Closing will lose them" would be a lie for that modal. The
// text is the only typed work in it; the toggles act immediately.
const _CLOSE_GUARD_EXEMPT_IDS = [
  'confirm-modal', 'prompt-modal', 'feature-upgrade-modal',
  'modal-billing-lineitems', 'modal-review-ticket', 'auth-modal',
  'modal-mass-text',
];

// TWO shapes count as a close call, and the split is deliberate:
//  (a) …close/hide/dismiss…Modal(anything)  — 276 sites. The name says modal, so
//      arguments are fine: closeModal('job') is the house pattern.
//  (b) closeSomething()  with NO arguments  — the ~60 panels whose closer never
//      says "Modal": closeLoanEdit(), closeDetail(), closeJobDetails(),
//      closeDqPacketViewer(), closeInvoiceBuilder()… 🔴 COD Code Reviewer found
//      (b) missing: bi.js's loan editor X calls closeLoanEdit(), so typed loan
//      changes vanished with no confirm.
// The no-argument rule is what keeps (b) safe. Every close-ish handler in this
// tree that DOES something other than close takes an argument — dismissJsa(id),
// dismissPermitFlag(ticketId), closeStaleClockEntry(entryId), dismissDvirFlag(id)
// all write data, and all are excluded. `close[A-Z]` also excludes bare
// `window.close()`, which closes the browser tab and is nobody's Cancel button.
const _CLOSE_CALL_RE = /[A-Za-z0-9_.$]*(?:(?:close|hide|dismiss)[A-Za-z0-9_]*Modal\s*\([^()]*\)|close[A-Z][A-Za-z0-9_]*\s*\(\s*\))\s*;?/g;
const _CLOSE_CALL_PROBE = /(?:(?:close|hide|dismiss)[A-Za-z0-9_]*Modal\s*\(|close[A-Z][A-Za-z0-9_]*\s*\(\s*\))/;

// "Body is close calls and nothing else." Whitespace and stray semicolons only.
function _isPureCloseIntent(source) {
  if (!source || typeof source !== 'string') return false;
  if (!_CLOSE_CALL_PROBE.test(source)) return false;
  _CLOSE_CALL_RE.lastIndex = 0;
  return source.replace(_CLOSE_CALL_RE, '').replace(/[\s;]/g, '') === '';
}

// Returns { modal, replayEl } when this click is a user-initiated close, else null.
function _findModalCloseIntent(target) {
  if (!target || typeof target.closest !== 'function') return null;
  const modal = target.classList && target.classList.contains('modal') ? target : target.closest('.modal');
  if (!modal || modal.classList.contains('hidden')) return null;
  if (_CLOSE_GUARD_EXEMPT_IDS.indexOf(modal.id) !== -1) return null;

  if (target === modal) {
    const closable = (typeof modal.onclick === 'function') ||
      (_backdropCloseCapable && _backdropCloseCapable.has(modal));
    return closable ? { modal, replayEl: modal } : null;
  }

  for (let el = target; el && el !== modal; el = el.parentElement) {
    if (typeof el.getAttribute !== 'function') continue;
    const onclick = el.getAttribute('onclick');
    if (!onclick) continue;
    // First handler on the way up decides. If it does anything besides close,
    // this is a save or a navigation and the guard stays out of it.
    return _isPureCloseIntent(onclick) ? { modal, replayEl: el } : null;
  }
  return null;
}

// 🔴 FOUND IN THE BROWSER, DRIVER LANE, not in review. #confirm-modal is z-index
// 9999 (styles.css:20581) which beats a plain `.modal` at 1000 — but the driver
// expense sheet sets an INLINE `z-index:10000` (jobboard.js:10683), so the guard's
// dialog opened *behind* it. The driver sees the sheet do nothing on Cancel, taps
// again, and is trapped: worse than the data loss we came to fix. styles.css
// already carries a comment about this exact arms race (:3573).
// Scoped ON PURPOSE to the guard rather than raised globally in showConfirm(): the
// singleton is shared by ~59 call sites and this needs zero blast radius on them.
// (The same latent trap exists for any OTHER confirm raised from inside a
// z-index-10000 driver sheet — e.g. the delete-photo confirm. Worth its own row.)
//
// TASK-10076 — with NO modal, lift above the highest layer on screen: any open
// .modal or any visible child of <body> (the inline-z overlays most views append),
// minus the confirm itself and the toast stack (toasts outrank everything on
// purpose). The "DispatchDeck was updated" question opens when a lazy screen fails
// to load, often AFTER that screen's own dialog is up (driver Record Expense,
// inline z-index 10000): it painted under it and the driver never saw it.
function _liftConfirmAbove(modal) {
  const noop = () => {};
  if (typeof document === 'undefined' || typeof window === 'undefined') return noop;
  const dlg = document.getElementById('confirm-modal');
  if (!dlg) return noop;
  const z = (el, shown) => {
    try { const cs = window.getComputedStyle(el); return shown && cs.display == 'none' ? 0 : parseInt(cs.zIndex, 10) || 0; } catch (_) { return 0; }
  };
  const target = modal ? z(modal) : Math.max(0, ...[...document.querySelectorAll('.modal:not(.hidden),body>*')]
    .map((el) => (el == dlg || el.id == 'toast-container' ? 0 : z(el, 1))));
  if (z(dlg) > target) return noop;          // already on top — leave it alone
  const prev = dlg.style.zIndex;
  dlg.style.zIndex = String(target + 1);
  return () => { dlg.style.zIndex = prev; };
}

// TASK-10076 — showConfirm above whatever is open, for the update questions.
// Lift one microtask AFTER showConfirm: if it superseded a pending confirm, that
// one's restore (queued when it resolved) runs first and cannot drop this lift.
function _confirmOnTop(...a) {
  const p = showConfirm(...a);
  return Promise.resolve().then(() => p.finally(_liftConfirmAbove()));
}

// 🔴 FOUND IN THE BROWSER, not in review — and it is the difference between a
// guard and a nag. A modal is HIDDEN, not destroyed, so the very same
// <input id="job-title"> comes back the next time Edit Job opens. It was still in
// _dirtyFields from the job the user discarded ten seconds ago, and it now holds a
// PREFILLED value nobody typed — so the guard asked "Discard your changes?" on an
// untouched edit form. That is exactly the cry-wolf shape TASK-9121 measured and
// rejected, arriving through element reuse.
//
// Pruning inside hasUnsavedWork() is too late: by the time it runs again the modal
// is open and the field is visible. It has to happen while the modal is still
// closed — so it rides the guard's own capture listener, which already sees EVERY
// click. Whatever the user clicks to reopen a modal is processed here first. Cost
// is one pass over a set that normally holds 0-5 elements.
//
// Only fields inside a CLOSED .modal are dropped. An inline non-modal form keeps
// its edits (TASK-9121 covers those deliberately), and a field merely off screen
// inside an OPEN modal — a collapsed section, an inactive tab — is left in the set.
function _pruneClosedModalFields() {
  if (typeof _dirtyFields === 'undefined' || !_dirtyFields.size) return;
  for (const el of Array.from(_dirtyFields)) {
    if (el.isConnected === false) { _dirtyFields.delete(el); continue; }
    const owner = typeof el.closest === 'function' ? el.closest('.modal') : null;
    if (owner && owner.classList && owner.classList.contains('hidden')) _dirtyFields.delete(el);
  }
}

let _closeGuardPending = false;

function _onModalCloseGuardClick(e) {
  if (_closeGuardPending) return;
  try { _pruneClosedModalFields(); } catch (_) {}
  // A replayed / programmatic .click() is untrusted, so the Discard path below
  // passes straight through, and so does every post-save close a view fires.
  if (e.isTrusted === false) return;
  let hit = null;
  try { hit = _findModalCloseIntent(e.target); } catch (_) { return; }
  if (!hit) return;
  if (!hasUnsavedWork(hit.modal)) return;   // nothing typed → close instantly, no nag

  e.preventDefault();
  e.stopPropagation();
  _closeGuardPending = true;
  const restoreZ = _liftConfirmAbove(hit.modal);
  const done = (discard) => {
    _closeGuardPending = false;
    try { restoreZ(); } catch (_) {}
    if (discard) {
      try { hit.replayEl.click(); } catch (_) { /* modal already gone */ }
      // Deterministic half of the prune above: the modal just closed, so drop what
      // the user discarded NOW rather than waiting for their next click. Without
      // this, reopening the form programmatically (a deep link, a follow-on action)
      // would still resurrect the discarded field.
      try { _pruneClosedModalFields(); } catch (_) {}
    }
  };
  const ask = typeof window !== 'undefined' && typeof window.showConfirm === 'function'
    ? window.showConfirm : (typeof showConfirm === 'function' ? showConfirm : null);
  if (!ask) { done(true); return; }       // never trap the user behind a missing dialog
  ask('Discard your changes?', 'You have unsaved changes in this form. Closing will lose them.', {
    confirmText: 'Discard',
    cancelText: 'Keep editing',
    danger: true,
    safeDefault: true   // Keep editing is the solid primary — the safe answer wins the eye
  }).then(done).catch(() => { _closeGuardPending = false; });
}

function startModalCloseGuard() {
  if (typeof document === 'undefined') return;
  if (startModalCloseGuard._started) return;
  startModalCloseGuard._started = true;
  // The guard is only as good as the detector feeding it, and a PIN driver never
  // walks the office boot path — so make sure edit tracking is on either way.
  startUnsavedWorkTracking();
  // Capture at document: this runs before the modal's own backdrop listener and
  // before the button's inline onclick, which is the only place both can be
  // stopped from one line.
  document.addEventListener('click', _onModalCloseGuardClick, true);
  // An honest "is this bundle the one with the guard" flag. Without it a browser
  // proof can go green against a stale /dist and mean nothing.
  try { window.__ddCloseGuardInstalled = true; } catch (_) {}
}

// <<< TASK-9129-CLOSE-GUARD <<<

// Started at load, not after auth: the shim above has to be in place before the
// first modal registers its backdrop listener, and driver PIN mode opens modals
// without ever running the office boot path.
startModalCloseGuard();

// TASK-10035 — an outdated tab must refresh before money or pay actions. The
// guard (utils/staleTabGuard.js) stops a click on a money/pay control while THIS
// tab's bundle is older than the served version.json, and offers Refresh, which
// goes through requestAppUpdate so typed-but-unsaved work is asked about first.
// Installed at load for the same reason as the close guard: one capture listener,
// no view file touched. It only acts for a signed-in office session.
//
// The running version also goes on window.APP_VERSION, which telemetry's
// session_start / user_action rows already read and which nothing ever set (the
// row's note: appVersion was null on every session, so nobody could tell who was
// outdated).
//
// Loaded as its own chunk (perf budget, scripts/perf-budgets.cjs) but kicked off
// HERE, at page load, not at first use: after a deploy the old chunk file is gone,
// so a stale tab that waited until its first money click to fetch it would 404 and
// run unguarded — the exact tab this exists for.
try { window.APP_VERSION = getRunningBundleVersion() || null; } catch (_) {}
// Both "DispatchDeck was updated" questions (this money guard and the lazy-chunk
// recovery below) can open while another dialog is up, so their confirm goes
// through _confirmOnTop (TASK-10076).
_lazyImportWithRetry(() => __ddChunkWrap(import('./utils/staleTabGuard.js?v=1')), 'staleTabGuard').then(() => {
  window.StaleTabGuard?.install({
    getRunningVersion: getRunningBundleVersion,
    hasUnsavedWork: () => hasUnsavedWork(),
    requestAppUpdate: (version) => requestAppUpdate(version, document.getElementById('update-banner')),
    showConfirm: _confirmOnTop,
    showToast: (...a) => window.showToast?.(...a),
    markSidebarUpdateReady: (version) => { if (version && !document.getElementById('update-banner')) markSidebarUpdateReady(version); },
  });
}).catch((err) => { console.warn('[StaleTabGuard] load failed:', err?.message); });
// TASK-10076 — the lazy-chunk recovery (utils/chunkReload.js) gets the same
// running-version / unsaved-work / reload doors as the money guard above.
try {
  window.ChunkReload?.install({
    getRunningVersion: getRunningBundleVersion,
    hasUnsavedWork: () => hasUnsavedWork(),
    // An open dialog can hold work the dirty-field tracker cannot see (a signature
    // pad, a ticket being built) — ask instead of reloading (COD Domain).
    isModalOpen: () => !!document.querySelector('.modal:not(.hidden)'),
    isDriverSession: () => !!(window.AppState?.driverModePin || window.AppState?.driverMode || window.AppState?.isPublicAccess),
    reload: (version) => requestAppUpdate(version, document.getElementById('update-banner')),
    showConfirm: _confirmOnTop,
    showToast: (...a) => window.showToast?.(...a),
  });
} catch (err) { console.warn('[ChunkReload] install failed:', err?.message); }

async function checkForAppUpdate() {
  // Don't check or show banner on login screen
  if (!window.AppState?.user) return;
  try {
    // Fetch version.json with cache-busting
    const response = await fetch(`/version.json?t=${Date.now()}`, {
      cache: 'no-store'
    });
    if (!response.ok) return;

    const serverVersion = await response.json();
    // TASK-10035 — keep the money-action guard's view of the served version warm.
    try { window.StaleTabGuard?.noteServedVersion(serverVersion?.version); } catch (_) {}
    // COD 2026-09-23 (Code Reviewer): a throwing storage accessor must not stop the per-tab bundle comparison below.
    let cachedVersion = null;
    try { cachedVersion = localStorage.getItem(APP_VERSION_KEY); } catch (_) {}
    const runningVersion = getRunningBundleVersion();

    console.log('[Version] Server:', serverVersion.version, '| Running:', runningVersion, '| Cached:', cachedVersion);

    // TASK-9130 — put the RUNNING version in the sidebar footer (it was hardcoded
    // "v0.2.17" in index.html and nothing ever wrote to it). First visit has no
    // cached version yet, so the server's is the one this tab just loaded.
    renderSidebarVersion(runningVersion || cachedVersion || serverVersion.version);

    // 2026-09-23 — compare THIS TAB's bundle, not the shared localStorage key
    // (see getRunningBundleVersion). Keep the key in step so the fallback path
    // and any older tab's comparison read the newest version anyone has loaded.
    if (runningVersion && serverVersion.version) {
      if (runningVersion !== serverVersion.version) {
        console.log('[Version] Update available:', serverVersion.version, '(this tab runs', runningVersion + ')');
        showUpdateBanner(serverVersion.version);
      } else if (cachedVersion !== runningVersion) {
        try { localStorage.setItem(APP_VERSION_KEY, runningVersion); } catch (_) {}
      }
      return;
    }

    if (!cachedVersion) {
      // First visit - store current version
      localStorage.setItem(APP_VERSION_KEY, serverVersion.version);
      console.log('[Version] First visit, stored version:', serverVersion.version);
      return;
    }

    if (cachedVersion !== serverVersion.version) {
      console.log('[Version] Update available:', serverVersion.version);
      showUpdateBanner(serverVersion.version);
    }
  } catch (err) {
    // Silent fail - don't break app if version check fails
    console.log('[Version] Check failed:', err.message);
  }
}

function showUpdateBanner(version) {
  // Don't show duplicate banners
  if (document.getElementById('update-banner')) return;
  // Don't show on login screen — wait until user is authenticated and sidebar visible
  if (!window.AppState?.user) return;
  // TASK-9121 — "Later" snooze. Checked before the driver hand-off below so it
  // covers both banners even when this is the entry point.
  if (Date.now() < _updateSnoozedUntil) return;

  // TASK-1197 — driver sessions (PIN / driver-link / public access) have NO
  // sidebar (showSidebar force-hides it), so the sidebar gate below made this
  // an office-only banner and drivers rode stale bundles indefinitely
  // (TASK-1193 incident: fix shipped 10 PM, driver's phone still ran the old
  // bundle next morning). In driver mode render a tap-to-refresh banner pinned
  // above the driver bottom-nav instead of bailing.
  if (window.AppState?.driverModePin || window.AppState?.driverMode || window.AppState?.isPublicAccess) {
    showDriverUpdateBanner(version);
    return;
  }

  const sidebar = document.getElementById('app-sidebar');
  if (!sidebar || sidebar.classList.contains('hidden')) return;

  // TASK-9130 — dismissed means dismissed for this session on the office side.
  // The update is never silently dropped: it lives on in the sidebar footer.
  if (_updateDismissedThisSession) { markSidebarUpdateReady(version); return; }

  const banner = document.createElement('div');
  banner.id = 'update-banner';
  // TASK-9130 — the strip used to be `position:fixed; top:0; left:0; right:0`
  // with NO compensating content offset, so it sat ON TOP of the header. That is
  // almost certainly how Mike "clicked the update banner": he aimed at something
  // in the top strip and the banner was over it. Playwright reproduced it
  // verbatim — `<div id="update-banner"> intercepts pointer events` on Add Job.
  // Rather than reserve space at the top and reflow the whole app, the office
  // banner is now the same compact bottom-pinned card the driver has always had.
  // Geometry (bottom-centre desktop, bottom-left-of-the-FAB-column on phones)
  // lives in styles.css under #update-banner.update-banner-office so it can react
  // to viewport + safe-area; only the paint stays inline.
  banner.className = 'update-banner-office';
  banner.style.cssText = `
    position: fixed;
    background: linear-gradient(135deg, #1e40af, #3B82F6);
    color: white;
    padding: 12px 14px;
    font-family: system-ui, -apple-system, sans-serif;
    font-size: 14px;
    z-index: 950;
    box-shadow: 0 10px 30px rgba(15,23,42,0.30);
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 10px;
    cursor: pointer;
    transition: background 0.2s;
  `;
  // TASK-9121 — 950, not 99999. .modal is z-index 1000 (styles.css) so the strip
  // now sits BEHIND an open Add/Edit Job form and its backdrop: visible but
  // unclickable, which is the state the driver banner has always had. Still
  // above app chrome (sidebar/header 100-200, .locked-preview-overlay 895/897,
  // .order-management-overlay 900) and below #confirm-modal (9999), so our own
  // "lose your work?" dialog draws cleanly over it.
  banner.onmouseenter = () => banner.style.background = 'linear-gradient(135deg, #1e3a8a, #2563eb)';
  banner.onmouseleave = () => banner.style.background = 'linear-gradient(135deg, #1e40af, #3B82F6)';
  banner.innerHTML = `
    <span class="material-icons update-shimmer" style="font-size:20px;flex:0 0 auto;">system_update</span>
    <span class="update-shimmer" style="display:inline-flex;flex-direction:column;line-height:1.3;text-align:left;flex:1 1 auto;min-width:0;">
      <span><strong>Update available</strong> — tap to refresh</span>
      <span style="font-size:12px;">Save your work first — this reloads the app</span>
    </span>
    <button type="button" id="update-banner-later"
      style="flex:0 0 auto;margin-left:8px;min-width:44px;min-height:44px;padding:0 12px;border:1px solid rgba(255,255,255,0.55);border-radius:8px;background:transparent;color:#fff;font-size:13px;font-weight:600;cursor:pointer;">Dismiss</button>
  `;
  banner.onclick = () => requestAppUpdate(version, banner);
  const laterBtn = banner.querySelector('#update-banner-later');
  if (laterBtn) {
    laterBtn.onclick = (e) => {
      e.stopPropagation();
      dismissOfficeUpdateBanner(banner, version);
    };
  }
  document.body.appendChild(banner);
}

// TASK-9130 — office dismiss. Not a snooze: the card does not come back this
// session. The update is handed to .sidebar-version instead, which is already
// part of the chrome and covers nothing, so an update can never be silently
// dropped just because someone cleared their screen.
function dismissOfficeUpdateBanner(banner, version) {
  _updateDismissedThisSession = true;
  if (banner) banner.remove();
  markSidebarUpdateReady(version);
  showToast('Update parked in the sidebar — click "Update ready" when you are at a good stopping point.', 'info');
}

// TASK-9130 — the standing, non-covering update affordance. .sidebar-version sat
// in index.html hardcoded to "v0.2.17" with no JS writing to it (prod is on
// 1.15.19xx), so the one line support always asks for was a lie AND the slot was
// dead weight. It now shows the running version, and becomes the update button
// once the office card is dismissed.
function markSidebarUpdateReady(version) {
  const el = document.querySelector('.sidebar-version');
  if (!el) return;
  el.classList.add('is-update-ready');
  el.setAttribute('role', 'button');
  el.setAttribute('tabindex', '0');
  el.setAttribute('title', `Update to ${version} — this reloads the app`);
  el.textContent = 'Update ready — refresh';
  el.onclick = () => requestAppUpdate(version, el);
  el.onkeydown = (e) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      requestAppUpdate(version, el);
    }
  };
}

function clearSidebarUpdateReady() {
  const el = document.querySelector('.sidebar-version');
  if (!el) return;
  el.classList.remove('is-update-ready');
  el.removeAttribute('role');
  el.removeAttribute('tabindex');
  el.removeAttribute('title');
  el.onclick = null;
  el.onkeydown = null;
  // Disarming has to put the TEXT back too, not just the handlers. Without this the
  // slot keeps reading "Update ready — refresh" after a logout — dead words that do
  // nothing — until the next successful version.json fetch happens to overwrite it,
  // and that fetch is silently skipped on the login screen (checkForAppUpdate bails
  // when there is no AppState.user) and on any network hiccup. Fail closed: fall back
  // to the version this tab is running.
  renderSidebarVersion(getRunningBundleVersion() || localStorage.getItem(APP_VERSION_KEY));
}

// The plain state of that slot: whatever bundle this tab is actually running.
function renderSidebarVersion(version) {
  const el = document.querySelector('.sidebar-version');
  if (!el || !version) return;
  // The update affordance owns the slot once it is armed — don't overwrite it.
  if (el.classList.contains('is-update-ready')) return;
  el.textContent = `v${String(version).replace(/^v/i, '')}`;
}

// TASK-9121 — the banner's only affordance used to be the destructive one:
// "get this strip off my screen" and "throw away what I'm working on" were the
// same click, which is the likeliest thing that actually happened to Mike.
function snoozeUpdateBanner(banner) {
  _updateSnoozedUntil = Date.now() + UPDATE_SNOOZE_MS;
  if (banner) banner.remove();
}

// TASK-9121 — single gate in front of applyUpdate for BOTH banners. Only
// interrupts when the user has genuinely edited something that is still on
// screen; otherwise the banner behaves exactly as it always did.
async function requestAppUpdate(version, banner) {
  if (hasUnsavedWork()) {
    // showConfirm lives in this same bundle, so it is always defined — no
    // native window.confirm fallback, which would block browser automation
    // (CLAUDE.md pitfall #6) and is dead code besides.
    const ok = await showConfirm(
      'Refresh now and lose your unsaved work?',
      'Refreshing reloads the app and closes what you have open. Anything you typed but have not saved yet will be gone. Save first, or keep working — the update will still be here.',
      {
        confirmText: 'Refresh anyway',
        cancelText: 'Keep working',
        danger: true,
        safeDefault: true,
        icon: 'warning',
      }
    );
    // Cancel (or a superseded dialog, which force-resolves false per TASK-1342)
    // leaves the banner untouched and reading "Update available".
    // TASK-10076: false/true tells utils/chunkReload.js whether a reload is coming.
    if (!ok) return false;
  }
  // The "Updating..." swap happens ONLY once we're committed. Doing it before
  // the confirm stranded a cancelled banner on "Updating..." forever.
  if (banner) {
    banner.innerHTML = '<span class="material-icons" style="font-size:20px;animation:spin 1s linear infinite;">refresh</span> <span>Updating&hellip;</span>';
  }
  applyUpdate(version);
  return true;
}

// TASK-1197 — driver-mode update affordance (PIN / driver-link / public access).
// Tap-to-refresh ONLY — no auto-reload, ever. Repercussion guard (the TASK-429
// uid-roll comments show mid-flow reloads are hazardous): a reload can only be
// triggered by the driver's own tap, and we don't even RENDER the banner while
// a modal is open (clock-out form / ticket builder / signature pads all live in
// `.modal` elements toggled via `.hidden` — same open-modal check as the
// onboarding-hint and partner-provision gates) or while the driver Actions
// sheet is up. Deferral is safe: startVersionChecking re-invokes
// checkForAppUpdate every 5 minutes plus on focus/visibilitychange, so the
// banner appears at the next check after the flow closes.
function showDriverUpdateBanner(version) {
  // TASK-9121 — honour "Later" here too: checkForAppUpdate re-fires every 5 min
  // plus on focus/visibilitychange, so without this the snooze does nothing.
  if (Date.now() < _updateSnoozedUntil) return;
  if (document.querySelector('.modal:not(.hidden)')) return;
  const actionsSheet = document.getElementById('driver-actions-sheet');
  if (actionsSheet && !actionsSheet.classList.contains('hidden')) return;

  // Pin above whichever bottom nav this driver surface renders:
  // #driver-bottom-nav (PIN mode, TASK-587 shell chrome or board-view copy) or
  // the generic #mobile-nav (driver-link / public access). offsetHeight already
  // includes the nav's safe-area-inset-bottom padding.
  const nav = document.getElementById('driver-bottom-nav') || document.getElementById('mobile-nav');
  const navVisible = nav && !nav.classList.contains('hidden') && nav.offsetHeight > 0;
  const bottom = navVisible ? `${nav.offsetHeight + 8}px` : 'calc(72px + env(safe-area-inset-bottom))';

  const banner = document.createElement('div');
  banner.id = 'update-banner';
  banner.setAttribute('role', 'button');
  banner.style.cssText = `
    position: fixed;
    left: 12px;
    right: 12px;
    bottom: ${bottom};
    background: linear-gradient(135deg, #1e40af, #3B82F6);
    color: #fff;
    padding: 14px 18px;
    min-height: 48px;
    border-radius: 12px;
    font-family: system-ui, -apple-system, sans-serif;
    font-size: 14px;
    z-index: 150;
    box-shadow: 0 4px 16px rgba(0,0,0,0.25);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    cursor: pointer;
  `;
  banner.innerHTML = `
    <span class="material-icons update-shimmer" style="font-size:20px;">system_update</span>
    <span class="update-shimmer" style="display:inline-flex;flex-direction:column;line-height:1.3;text-align:left;flex:1;">
      <span><strong>Update available</strong> — tap to refresh</span>
      <span style="font-size:12px;">Save your work first — this reloads the app</span>
    </span>
    <button type="button" id="update-banner-later"
      style="min-width:44px;min-height:44px;padding:0 12px;border:1px solid rgba(255,255,255,0.55);border-radius:8px;background:transparent;color:#fff;font-size:13px;font-weight:600;cursor:pointer;">Later</button>
  `;
  banner.onclick = () => requestAppUpdate(version, banner);
  const laterBtn = banner.querySelector('#update-banner-later');
  if (laterBtn) {
    laterBtn.onclick = (e) => {
      e.stopPropagation();
      snoozeUpdateBanner(banner);
    };
  }
  document.body.appendChild(banner);
}

async function applyUpdate(version) {
  console.log('[Version] Applying update to:', version);
  // TASK-10076 (COD Code Reviewer): blocked storage threw here and the reload
  // below never ran — the tab sat on "Updating…" (and a lazy screen waited forever).
  try { localStorage.setItem(APP_VERSION_KEY, version); } catch (_) { /* reload anyway */ }

  // Clear Cache API entries to bust stale JS/CSS
  if (window.caches && caches.keys) {
    try {
      const names = await caches.keys();
      await Promise.all(names.map(n => caches.delete(n)));
    } catch (e) { /* ignore */ }
  }

  // Also tell service worker to skip waiting if there's a new one
  if (navigator.serviceWorker?.controller) {
    navigator.serviceWorker.controller.postMessage('skipWaiting');
  }

  setTimeout(() => {
    const params = new URLSearchParams(window.location.search);
    params.set('_v', version);
    window.location.replace(window.location.pathname + '?' + params.toString() + window.location.hash);
  }, 500);
}


// Named handlers so they can be removed on logout
function _onVisibilityChangeVersion() { if (document.visibilityState === 'visible') checkForAppUpdate(); }
function _onFocusVersion() { checkForAppUpdate(); }

function startVersionChecking() {
  // Clean up cache-bust param from URL after reload (keeps address bar clean)
  // IMPORTANT: preserve other query params (access tokens, timeoff, etc.)
  const url = new URL(window.location);
  if (url.searchParams.has('_v')) {
    url.searchParams.delete('_v');
    const remaining = url.searchParams.toString();
    window.history.replaceState(null, '', url.pathname + (remaining ? '?' + remaining : '') + url.hash);
  }

  // TASK-9121 — start watching for edits before the first banner can appear.
  startUnsavedWorkTracking();

  // Check immediately on load
  checkForAppUpdate();

  // Check every 5 minutes while app is running
  versionCheckInterval = setInterval(checkForAppUpdate, 5 * 60 * 1000);

  // Also check when user returns to tab (catches updates while away)
  document.addEventListener('visibilitychange', _onVisibilityChangeVersion);

  // Check when window regains focus
  window.addEventListener('focus', _onFocusVersion);
}

function stopVersionChecking() {
  if (versionCheckInterval) { clearInterval(versionCheckInterval); versionCheckInterval = null; }
  document.removeEventListener('visibilitychange', _onVisibilityChangeVersion);
  window.removeEventListener('focus', _onFocusVersion);
  window._versionCheckStarted = false;
  // TASK-9121 — a logout must not carry a snooze or a stale dirty-field set
  // into the next session. The capture listeners stay attached (they are
  // idempotent-guarded and cheap); only the state resets.
  _updateSnoozedUntil = 0;
  _dirtyFields.clear();
  // TASK-9130 — same for the session dismiss and the sidebar affordance it armed:
  // the next user to sign in on this tab must not inherit either.
  _updateDismissedThisSession = false;
  clearSidebarUpdateReady();
  const banner = document.getElementById('update-banner');
  if (banner) banner.remove();
}

// ============================================
// EMAIL-VERIFICATION RE-CHECK (TASK-682)
// ============================================
// An already-open session that verifies in another tab/device never re-checks,
// so the verify-email banner persists. Re-check on focus/visibilitychange: reload
// the user's Firebase Auth record and, if now verified, auto-remove the banner +
// durably stamp the tenant doc via the syncMyEmailVerified server callable
// (the direct client write is now blocked by the firestore.rules denylist).
// Named handlers mirror _onVisibilityChangeVersion/_onFocusVersion so they can be
// removed on logout. Re-check is in flight guarded against overlapping runs.
let _verifyRecheckInFlight = false;

async function recheckEmailVerification() {
  // Only meaningful while the banner is up for a real signed-in (non-PIN) user.
  if (_verifyRecheckInFlight) return;
  const user = window.AppState.user;
  const tenant = window.AppState.tenant;
  if (!user || !tenant) return;
  if (!document.getElementById('verification-reminder-banner')) return;
  if (window.AppState.driverModePin || window.AppState.isDemo) return;

  _verifyRecheckInFlight = true;
  try {
    try { await user.reload(); } catch (_) { /* ignore reload errors */ }
    // Firebase emailVerified can lag right after the click — short delayed retries.
    let verified = user.emailVerified || window.AppState.tenantData?.emailVerified === true;
    for (let attempt = 0; attempt < 2 && !verified; attempt++) {
      await new Promise(r => setTimeout(r, 1500));
      try { await user.reload(); } catch (_) { /* ignore */ }
      verified = user.emailVerified || window.AppState.tenantData?.emailVerified === true;
    }
    if (!verified) return;

    // Verified — clear the banner immediately, then durably stamp the tenant doc.
    dismissVerificationBanner();
    window.AppState.showVerificationBanner = false;
    if (window.AppState.tenantData) window.AppState.tenantData.emailVerified = true;

    try {
      const syncVerified = httpsCallable(functions, 'syncMyEmailVerified');
      const res = await syncVerified({ tenantId: tenant });
      if (res?.data?.verified) {
        console.log('[Auth] Email verification confirmed on recheck (server)');
        try {
          await logEmailEvent({
            type: 'verification_confirmed',
            recipient: user.email,
            tenantId: tenant,
            status: 'verified',
            provider: 'firebase-auth'
          });
        } catch (_) { /* non-fatal */ }
      }
    } catch (e) {
      console.warn('[Auth] Could not persist verification on recheck:', e);
    }
  } finally {
    _verifyRecheckInFlight = false;
  }
}
// Exposed so the banner's "I've verified — re-check" button can call it.
window.recheckEmailVerification = recheckEmailVerification;

// Named handlers so they can be removed on logout / signed-out.
function _onVisibilityChangeVerify() { if (document.visibilityState === 'visible') recheckEmailVerification(); }
function _onFocusVerify() { recheckEmailVerification(); }

function startEmailVerifyRecheck() {
  if (window._verifyRecheckStarted) return;
  window._verifyRecheckStarted = true;
  document.addEventListener('visibilitychange', _onVisibilityChangeVerify);
  window.addEventListener('focus', _onFocusVerify);
}

function stopEmailVerifyRecheck() {
  document.removeEventListener('visibilitychange', _onVisibilityChangeVerify);
  window.removeEventListener('focus', _onFocusVerify);
  window._verifyRecheckStarted = false;
}

// Version checking starts after auth (see onAuthStateChanged)
// Don't show update banners on the login screen

// ============================================
// DOM ELEMENTS
// ============================================
const DOM = {
  sidebar: document.getElementById('app-sidebar'),
  mainContent: document.getElementById('main-content'),
  loadingScreen: document.getElementById('loading-screen'),
  authModal: document.getElementById('auth-modal'),
  loginForm: document.getElementById('login-form'),
  signupForm: document.getElementById('signup-form'),
  authError: document.getElementById('auth-error'),
  toastContainer: document.getElementById('toast-container'),
  navItems: document.querySelectorAll('.nav-item:not(.nav-logout)'),
  mobileNav: document.getElementById('mobile-nav'),
  mobileMoreMenu: document.getElementById('mobile-more-menu'),
  mobileNavItems: document.querySelectorAll('.mobile-nav-item[data-view]')
};

// ============================================
// INITIALIZATION
// ============================================
// Helper: sign in for public pages without replacing a real auth session
async function ensurePublicAuth() {
  // 1. Wait for Firebase Auth to finish restoring any persisted session from IndexedDB.
  //    Without this, signInAnonymously() can create a user that gets OVERRIDDEN when
  //    the persisted session restoration completes moments later, causing auth.currentUser
  //    to flip to null or a different user mid-operation → permission-denied on Firestore.
  await new Promise((resolve) => {
    const unsub = onAuthStateChanged(auth, () => { unsub(); resolve(); });
  });
  console.log('[PublicAuth] Auth state settled, currentUser:', auth.currentUser?.uid || 'null', auth.currentUser?.isAnonymous ? '(anon)' : '');

  // 2. If a real user is already signed in, reuse them
  if (auth.currentUser && !auth.currentUser.isAnonymous) {
    console.log('[PublicAuth] Reusing existing session:', auth.currentUser.uid);
    window.AppState.user = auth.currentUser;
    return auth.currentUser;
  }

  // 3. If an anonymous user exists with a valid token, reuse them
  if (auth.currentUser && auth.currentUser.isAnonymous) {
    try {
      await auth.currentUser.getIdToken(true);
      console.log('[PublicAuth] Reusing anonymous session:', auth.currentUser.uid);
      window.AppState.user = auth.currentUser;
      return auth.currentUser;
    } catch (e) {
      console.warn('[PublicAuth] Stale anonymous session, creating fresh one');
      try { await auth.signOut(); } catch (_) { /* ignore */ }
    }
  }

  // 4. Fresh anonymous sign-in
  const cred = await signInAnonymously(auth);
  // Wait for auth state to propagate (ensures Firestore picks up the new token)
  await new Promise((resolve) => {
    const unsub = onAuthStateChanged(auth, (user) => {
      if (user) { unsub(); resolve(); }
    });
  });
  console.log('[PublicAuth] Fresh anonymous sign-in settled:', auth.currentUser?.uid);
  window.AppState.user = auth.currentUser;
  return auth.currentUser;
}

/**
 * TASK-429 — auth helper for the DRIVER-PIN flow specifically.
 *
 * Like ensurePublicAuth(), it awaits persisted-session restoration FIRST (so a
 * raw signInAnonymously() can't override currentUser moments later and trigger
 * the permission-denied flood) and reuses an existing ANONYMOUS session with a
 * refreshed token. It DIFFERS from ensurePublicAuth() in one critical way: it
 * NEVER reuses a non-anonymous (real-account) user. Driver-PIN login calls
 * linkDriverSession() right after, which force-overwrites the signed-in uid's
 * custom claims with { tenantId: <driverTenant>, role: 'driver' }. Reusing a
 * real account's uid there would clobber that account's claims and break its
 * Storage access cross-tenant (COD Code Reviewer ❌, 2026-06-03). So if a real
 * user is signed in, we sign them out and mint a FRESH anonymous user for the
 * driver session instead.
 */
async function ensureDriverAnonAuth() {
  // 1. Wait for Firebase Auth to finish restoring any persisted session.
  await new Promise((resolve) => {
    const unsub = onAuthStateChanged(auth, () => { unsub(); resolve(); });
  });

  // 2. A real (non-anonymous) account is signed in — do NOT reuse it (would let
  //    linkDriverSession overwrite its claims). Sign out, then fall through to
  //    a fresh anonymous sign-in.
  if (auth.currentUser && !auth.currentUser.isAnonymous) {
    console.log('[DriverAuth] Real account present — signing out before driver anon sign-in to protect its claims');
    try { await auth.signOut(); } catch (_) { /* ignore */ }
  }

  // 3. Reuse an existing anonymous user (token-refreshed) — this is the churn fix.
  if (auth.currentUser && auth.currentUser.isAnonymous) {
    try {
      await auth.currentUser.getIdToken(true);
      console.log('[DriverAuth] Reusing anonymous session:', auth.currentUser.uid);
      window.AppState.user = auth.currentUser;
      return auth.currentUser;
    } catch (e) {
      console.warn('[DriverAuth] Stale anonymous session, creating fresh one');
      try { await auth.signOut(); } catch (_) { /* ignore */ }
    }
  }

  // 4. Fresh anonymous sign-in, settle before returning.
  await signInAnonymously(auth);
  await new Promise((resolve) => {
    const unsub = onAuthStateChanged(auth, (user) => {
      if (user) { unsub(); resolve(); }
    });
  });
  console.log('[DriverAuth] Fresh anonymous sign-in settled:', auth.currentUser?.uid);
  window.AppState.user = auth.currentUser;
  return auth.currentUser;
}

async function init() {
  try { window.__ddBootMark?.('app:init'); } catch (_) {} // boot-watchdog telemetry step
  console.log('[DispatchDeck] Initializing... v1.14.82');
  console.log('[DispatchDeck] Original URL:', window.location.href);

  // TASK-745 — wire the hold-to-dim sidebar module. Consumed via
  // window.SidebarCustomize (NOT an ES import) to avoid the ?v=
  // duplicate-instance trap (Pitfall #1).
  // TASK-1224 — the dim is now EPHEMERAL (memory-only), so there is nothing to
  // persist and no Firestore injection: it clears on refresh/logout/tenant
  // switch and is explicitly reset on device lock/unlock (toggleDeviceLock).
  // Only `mode` (to disable the gesture) and `tenantId` are still needed.
  if (window.SidebarCustomize) {
    window.SidebarCustomize.init({
      getContext: () => {
        const as = window.AppState || {};
        let mode = 'app';
        if (as.isDemo || as.tenant === 'demo') mode = 'demo';
        else if (as.driverMode || as.driverModePin) mode = 'driver';
        else if (as.isPublicAccess || as.accessToken) mode = 'public';
        return { tenantId: as.tenant || null, mode };
      },
      // Nav re-render is handled by applyHideState directly. TASK-1290 hangs the
      // locked-module PREVIEW off the same signal, so dimming / un-dimming /
      // resetDim() on the view you're standing on takes effect immediately.
      onChange: () => { try { syncLockedModulePreview(); } catch (_) {} },
      toast: (msg, type) => { try { showToast(msg, type); } catch (_) {} },
    });
  }
  console.log('[DispatchDeck] Original params:', ORIGINAL_URL_PARAMS.toString());
  console.log('[DispatchDeck] Current search:', window.location.search);

  // Use ORIGINAL_URL_PARAMS — immune to URL manipulation by version checkers
  const urlParams = ORIGINAL_URL_PARAMS;
  if (urlParams.get('view') === 'driver') {
    window.AppState.driverMode = true;
    window.AppState.currentView = 'dispatch';
    console.log('[DispatchDeck] Driver mode activated via URL');
  }

  // Check for public docs token (company documents page)
  const docsToken = urlParams.get('docs');
  if (docsToken) {
    console.log('[DispatchDeck] Public docs token found, loading docs page...');
    window.AppState.publicTokenPage = true;
    await loadPublicDocsPage(docsToken);
    return; // Public token page — never fall through to login
  }

  // Check for order request token (customer order page)
  const orderToken = urlParams.get('order');
  if (orderToken) {
    console.log('[DispatchDeck] Order request token found, loading order page...');
    window.AppState.publicTokenPage = true;
    await loadOrderRequestPage(orderToken);
    return; // Public token page — never fall through to login
  }

  // Check for public invoice view token
  const invoiceToken = urlParams.get('invoice');
  if (invoiceToken) {
    console.log('[DispatchDeck] Invoice view token found, loading public invoice...');
    window.AppState.publicTokenPage = true;
    await loadPublicInvoicePage(invoiceToken);
    return; // Public token page — never fall through to login
  }

  // Check for signature token (delivery signature page)
  const signToken = urlParams.get('sign');
  if (signToken) {
    console.log('[DispatchDeck] Signature token found, loading signature page...');
    window.AppState.publicTokenPage = true;
    await loadSignaturePage(signToken);
    return; // Public token page — never fall through to login
  }

  // Check for ticket builder token (public ticket submission)
  const ticketToken = urlParams.get('ticket');
  if (ticketToken) {
    console.log('[DispatchDeck] Ticket builder token found, loading ticket page...');
    window.AppState.publicTokenPage = true;
    await loadPublicTicketBuilder(ticketToken);
    return; // Public token page — never fall through to login
  }

  // Check for time-off request token (employee request form)
  const timeoffToken = urlParams.get('timeoff');
  if (timeoffToken) {
    console.log('[DispatchDeck] Time-off request token found, loading form...');
    window.AppState.publicTokenPage = true;
    await loadTimeOffRequestPage(timeoffToken);
    return; // Public token page — never fall through to login
  }

  // Check for time-off approval token (manager approval page)
  const approveToken = urlParams.get('approve');
  if (approveToken) {
    console.log('[DispatchDeck] Time-off approval token found, loading page...');
    window.AppState.publicTokenPage = true;
    await loadTimeOffManagePage(approveToken);
    return; // Public token page — never fall through to login
  }

  // Check for public access token (driver link)
  const accessToken = urlParams.get('access');
  if (accessToken) {
    console.log('[DispatchDeck] Access token found, validating...');
    try { window.__ddBootMark?.('app:access-token'); } catch (_) {} // boot-watchdog telemetry step
    const tokenValid = await validateAccessToken(accessToken);
    if (tokenValid) {
      // Token is valid - skip normal auth, go straight to driver mode
      return;
    }
    // Token invalid/expired - stop here, don't show login (driver can't use login anyway)
    return;
  }

  // Check for existing driver PIN session (page refresh)
  try { window.__ddBootMark?.('app:pin-session-check'); } catch (_) {} // boot-watchdog telemetry step
  const driverPinSessionValid = await checkDriverPinSession();
  if (driverPinSessionValid) {
    console.log('[DispatchDeck] Driver PIN session restored');
    hideLoading();
    hideAuthModal();
    showSidebar();
    setupEventListeners();
    setupRouting();
    // TASK-429 (Fix B1) — register the auth-state listener BEFORE returning,
    // mirroring the demo path (above). Without this, a PIN-session restore at
    // startup left NO onAuthStateChanged listener installed (the main one at
    // the bottom of init() is never reached because of this early return). A
    // later signInWithEmailAndPassword then changed auth state with nothing
    // driving handleAuthStateChange — so login only proceeded via the 5s
    // safety-net setTimeout in handleLogin (the reported "5s delay on every
    // email/password login when a driver-PIN session is cached"). The uid-roll
    // re-gate (Fix #2) in handleAuthStateChange also depends on this listener
    // being live so an anon-uid roll during the PIN session is observed.
    //
    // onAuthStateChanged fires IMMEDIATELY on registration for the already-
    // restored anon user (same uid, no roll). The restore block above has
    // already mounted the jobboard view, so that first fire would otherwise
    // re-run the PIN branch and re-navigate('jobboard') WHILE the jobboard
    // onSnapshot listeners are still wiring — re-introducing the propagation-
    // window flood this task fixes (COD: Skeptic 🟡 + both Code Reviewers).
    // Suppress exactly that one immediate fire with a one-shot flag the handler
    // consumes; a LATER genuine auth change (real login, uid roll, sign-out)
    // still flows through normally.
    window.AppState._pinRestoreSkipInitialAuthFire = true;
    onAuthStateChanged(auth, handleAuthStateChange);
    // 2026-05-28 — land restored PIN sessions on /jobboard to match fresh PIN
    // login at submitDriverPin (was inconsistent: fresh login → jobboard,
    // restore → dispatch, which caused the dispatch.js heavy-subs slow path
    // to fire on every page refresh; COD Code Reviewer V1 catch).
    // TASK-9658 S1 — EXCEPT when the driver tapped a record link: setupRouting()
    // above already mounted that record's module through navigateToView (driver
    // PIN gate included). Re-navigating to jobboard here would clear the focus
    // and strand them on the board.
    if (!window.RecordLinks?.hasPendingFocus?.()) navigateToView('jobboard');
    return;
  }

  // Check for email verification token
  const verifyToken = urlParams.get('verify');
  if (verifyToken) {
    console.log('[DispatchDeck] Email verification token found, validating...');
    await handleEmailVerificationToken(verifyToken);
  }

  // TASK-685 (S3): the verifyEmailToken function now verifies SERVER-SIDE on the
  // email click and 302-redirects back here with ?verified=1 (success) or
  // ?verify_failed=<reason> (failure). Surface a friendly result + drop the user
  // at the login modal — verification already happened on the backend.
  const verifiedFlag = urlParams.get('verified');
  const verifyFailed = urlParams.get('verify_failed');
  if (verifiedFlag === '1' || verifyFailed) {
    const url = new URL(window.location);
    url.searchParams.delete('verified');
    url.searchParams.delete('verify_failed');
    window.history.replaceState({}, document.title, url.toString());
    if (verifiedFlag === '1') {
      showToast('Email verified successfully! You can now log in.', 'success');
    } else {
      const msg = (verifyFailed === 'expired')
        ? 'That verification link has expired. Sign in and request a new one.'
        : (verifyFailed === 'invalid' || verifyFailed === 'missing')
          ? 'That verification link is no longer valid. Sign in and request a new one.'
          : 'We could not verify your email. Sign in and request a new link.';
      showToast(msg, 'error');
    }
    showAuthModal();
  }

  // Check for password reset token
  const resetToken = urlParams.get('reset');
  if (resetToken) {
    console.log('[DispatchDeck] Password reset token found, showing reset form...');
    showPasswordResetModal(resetToken);
    // Don't return - let auth state handler continue normally
  }

  // Check for ?demo=true (auto-start demo from landing page)
  if (urlParams.get('demo') === 'true') {
    console.log('[DispatchDeck] Auto-starting demo from landing page link');
    window.history.replaceState({}, document.title, window.location.pathname);
    // Flag demo BEFORE auth listener so stale-session check doesn't kill us
    window.AppState.pendingDemo = true;
    markDemoHeartbeat(); // …and before any sibling tab sees the anon broadcast
    // Hide auth modal and show loading screen immediately — no login flash
    const authModal = document.getElementById('auth-modal');
    if (authModal) authModal.classList.add('hidden');
    const loadEl = document.getElementById('loading-screen');
    const loadMsg = loadEl?.querySelector('p');
    if (loadMsg) loadMsg.textContent = 'Loading demo...';
    showLoading();
    // If user is already logged in, sign them out first so demo starts clean
    if (auth.currentUser) {
      console.log('[Auth] Signing out existing user before demo start');
      await auth.signOut();
    }
    // Set up auth, then auto-trigger demo
    onAuthStateChanged(auth, handleAuthStateChange);
    setupEventListeners();
    setupRouting();
    setTimeout(() => handleDemoLogin(), 300);
    return;
  }

  // Check for ?signup=true (auto-show signup form from landing page CTAs)
  //
  // TASK-4775 — this DOM manipulation alone never worked. It unhid #signup-step-1
  // before the auth listener was registered, and then the very next signed-out
  // onAuthStateChanged fire called showAuthModal(), whose "reset to Sign In tab"
  // block re-hid it. Every paid/SEO CTA that carries ?signup=true (landing.html x4,
  // feature.html x3, the three comparison pages x2 each, how-we-use-ai.html) landed
  // the buyer on a Sign In box for an account they do not have — while the CTA's own
  // gtag/fbq('track','Lead') had already fired.
  //
  // The fix is an explicit ONE-SHOT intent flag that showAuthModal() consumes, so the
  // URL param and the auth listener stop racing: whichever runs last, the signed-out
  // visitor ends on the signup form. The flag is deliberately NOT sticky —
  // handleAuthStateChange clears it the moment a real session resolves, so an
  // already-signed-in customer who clicks a marketing link goes to their board and a
  // later logout in the same tab still lands on Sign In, not signup.
  if (urlParams.get('signup') === 'true') {
    window.history.replaceState({}, document.title, window.location.pathname);
    window.AppState.pendingSignupIntent = true;
    // Paint it now as well, so there is no Sign In flash in the window before the
    // auth listener fires. showAuthModal() is what makes it STICK.
    const loginForm = document.getElementById('login-form');
    const signupForm = document.getElementById('signup-form');
    const signupStep1 = document.getElementById('signup-step-1');
    if (loginForm) loginForm.classList.add('hidden');
    if (signupForm) signupForm.classList.add('hidden');
    if (signupStep1) signupStep1.classList.remove('hidden');
    // GA4: Track signup intent. `method` splits the two emitters of this event —
    // without it the URL-param arrivals and the in-app "Start a free trial" link were
    // indistinguishable in GA4, which is why nobody could see this break in the funnel.
    if (typeof gtag === 'function') gtag('event', 'signup_started', { event_category: 'engagement', method: 'url_param' });
  }

  // Check for Stripe checkout result
  const checkoutResult = urlParams.get('checkout');
  if (checkoutResult === 'success') {
    const plan = urlParams.get('plan') || 'pro';
    // Clear the URL params
    window.history.replaceState({}, document.title, window.location.pathname);
    // Show success message after a short delay (let auth settle)
    setTimeout(() => {
      // TASK-9812 — base / base_annual / pro_plus are all "DispatchDeck".
      const _welcomeName = /^(base|base_annual|pro_plus|pro)$/.test(plan) ? 'DispatchDeck' : (plan.charAt(0).toUpperCase() + plan.slice(1));
      showToast(`Welcome to ${_welcomeName}! Your subscription is now active${plan === 'base_annual' ? ' for the year' : ''}.`, 'success');
      // GA4: Track subscription start with attribution (skip test accounts)
      const _utmPurchase = getUtmData();
      const _userEmail = auth.currentUser?.email || '';
      if (typeof gtag === 'function' && !isTestEmail(_userEmail)) gtag('event', 'purchase', {
        currency: 'USD', value: PRICING[plan] || 49, items: [{ item_name: plan }],
        campaign_source: _utmPurchase?.utm_source || undefined,
        campaign_medium: _utmPurchase?.utm_medium || undefined,
        campaign_name: _utmPurchase?.utm_campaign || undefined,
        gclid: _utmPurchase?.gclid || undefined
      });
      if (typeof fbq !== 'undefined' && !isTestEmail(_userEmail)) fbq('track', 'Purchase', {value: PRICING[plan] || 49, currency: 'USD'});
      if (typeof window.lintrk !== 'undefined' && !isTestEmail(_userEmail)) window.lintrk('track', { conversion_id: 25160540 });
    }, 1500);
  } else if (checkoutResult === 'cancelled') {
    window.history.replaceState({}, document.title, window.location.pathname);
    setTimeout(() => {
      showToast('Checkout cancelled. No charges were made.', 'info');
    }, 1500);
  }

  // Check for Stripe Connect return
  const stripeReturn = urlParams.get('stripe') || new URLSearchParams(window.location.hash.split('?')[1] || '').get('stripe');
  if (stripeReturn === 'complete') {
    // Clean up URL
    window.history.replaceState({}, document.title, window.location.pathname + window.location.hash.split('?')[0]);
    setTimeout(async () => {
      try {
        const checkStatus = httpsCallable(functions, 'checkStripeConnectStatus');
        const res = await checkStatus({});
        if (res.data?.complete) {
          showToast('Stripe account connected! You can now accept online payments.', 'success');
        } else {
          showToast('Stripe setup is not yet complete. Open Settings to continue.', 'info');
        }
      } catch (err) {
        console.error('[StripeConnect] Status check error:', err);
      }
    }, 2000);
  } else if (stripeReturn === 'refresh') {
    window.history.replaceState({}, document.title, window.location.pathname + window.location.hash.split('?')[0]);
    setTimeout(() => {
      showToast('Stripe session expired. Open Settings to try again.', 'info');
    }, 1500);
  }

  // QBO OAuth return
  const qboReturn = urlParams.get('qbo') || new URLSearchParams(window.location.hash.split('?')[1] || '').get('qbo');
  if (qboReturn === 'complete') {
    window.history.replaceState({}, document.title, window.location.pathname + window.location.hash.split('?')[0]);
    // TASK-781 — stash the QBO return so it's handled AFTER auth + tenant load
    // (drained in handleAuthStateChange). Running qboCheckStatus / navigateToView
    // here in init() races auth (callable needs context.auth) and tenant state
    // (navigateToView would hit a PIN gate before settings load). The old 2s
    // setTimeout was a fragile proxy for "wait for auth"; we now drive off the
    // real post-load signal + the qboCheckStatus resolve.
    window._pendingQboReturn = { status: 'complete' };
  } else if (qboReturn === 'error') {
    window.history.replaceState({}, document.title, window.location.pathname + window.location.hash.split('?')[0]);
    // Map the server-supplied reason code to a friendly message. Do NOT echo the
    // raw param verbatim — `reason` is URL-hash-controlled (a crafted #/...?qbo=
    // error&reason=... link could inject arbitrary text into the branded modal,
    // a phishing primitive). Unknown codes fall back to a generic message.
    const rawReason = urlParams.get('reason') || new URLSearchParams(window.location.hash.split('?')[1] || '').get('reason') || 'unknown';
    const QBO_ERROR_MESSAGES = {
      access_denied: 'You declined the QuickBooks authorization.',
      missing_params: 'QuickBooks returned an incomplete response.',
      invalid_state: 'The QuickBooks session was invalid or expired.',
      state_mismatch: 'The QuickBooks session could not be verified.',
      token_exchange_failed: 'We could not complete the QuickBooks token exchange.',
      // TASK-9589 — single-use / bound OAuth state + owner-only realm change.
      state_expired: 'That QuickBooks connect link expired (15 minutes). Start again.',
      state_unbound: 'The QuickBooks session could not be verified.',
      state_check_failed: 'The QuickBooks session could not be verified.',
      no_access: 'Ask the account owner to connect QuickBooks.',
      callback_failed: 'QuickBooks could not finish connecting. Please try again.',
      owner_only_realm_change: 'Only the account owner can connect a different QuickBooks company than the one on file.',
      realm_in_use: 'That QuickBooks company is already connected to another DispatchDeck account.',
      disconnected_meanwhile: 'QuickBooks was disconnected while you were connecting. Start again if you still want it.',
      changed_meanwhile: 'The QuickBooks connection changed while you were connecting. Check the status and try again.',
    };
    const reason = QBO_ERROR_MESSAGES[rawReason] || 'An unexpected error occurred.';
    window._pendingQboReturn = { status: 'error', reason };
  }

  // Plaid return (if using redirect mode instead of popup)
  const plaidReturn = urlParams.get('plaid') || new URLSearchParams(window.location.hash.split('?')[1] || '').get('plaid');
  if (plaidReturn === 'complete') {
    window.history.replaceState({}, document.title, window.location.pathname + window.location.hash.split('?')[0]);
    setTimeout(() => {
      showToast('Bank account linked!', 'success');
    }, 1500);
  }

  // Set up auth state listener
  try { window.__ddBootMark?.('app:auth-wait'); } catch (_) {} // boot-watchdog telemetry step
  onAuthStateChanged(auth, handleAuthStateChange);

  // Handle redirect result from iOS Google/Apple sign-in
  if (window.isIOSApp) {
    getRedirectResult(auth).then(result => {
      if (result?.user) {
        ddLog('[Auth] Redirect sign-in result:', result.user.uid, result.user.email);
        // onAuthStateChanged will handle the rest
      }
    }).catch(err => {
      if (err.code !== 'auth/popup-closed-by-user') {
        console.warn('[Auth] Redirect result error:', err.message);
      }
    });
  }

  // Set up event listeners
  setupEventListeners();

  // Set up routing
  setupRouting();
}

// ============================================
// PUBLIC ACCESS TOKEN (Driver Link)
// ============================================
async function validateAccessToken(token) {
  try {
    // Auth FIRST — Firestore security rules require it
    window.AppState.isPublicAccess = true;
    await ensurePublicAuth();

    // Look up token in Firestore (retry once on network/cold-start failure)
    let tokenDoc;
    try {
      tokenDoc = await getDoc(doc(db, 'accessTokens', token));
    } catch (readErr) {
      console.warn('[Access] Token read failed, retrying in 1s...', readErr.message);
      await new Promise(r => setTimeout(r, 1000));
      tokenDoc = await getDoc(doc(db, 'accessTokens', token));
    }

    if (!tokenDoc.exists()) {
      console.log('[Access] Token not found');
      showAccessError('Ask the company that sent it for a fresh one.');
      return false;
    }

    const tokenData = tokenDoc.data();

    // Check expiration (null = never expires)
    if (tokenData.expiresAt !== null) {
      const now = new Date();
      const expiresAt = tokenData.expiresAt?.toDate?.() || new Date(tokenData.expiresAt);

      if (now > expiresAt) {
        console.log('[Access] Token expired');
        showAccessError('This link has expired. Please ask your dispatcher for a new one.');
        return false;
      }
    }

    // Token is valid!
    console.log('[Access] Token valid for tenant:', tokenData.tenantId);

    // Portal tokens get their own page (project board) — PIN required
    if (tokenData.type === 'portal') {
      console.log('[Access] Portal token — checking PIN');
      // Check if PIN already validated this session
      if (sessionStorage.getItem(`portal_pin_${token}`) === 'ok') {
        await loadPortalProjectBoard(token, tokenData);
      } else {
        await showPortalPinGate(token, tokenData);
      }
      return true;
    }

    // Grant membership via Cloud Function (ensures member doc exists for this UID)
    try {
      const grantMembership = httpsCallable(functions, 'grantAccessTokenMembership');
      const result = await grantMembership({ token });
      console.log('[Access] Membership confirmed:', result.data);
    } catch (memberErr) {
      console.warn('[Access] Membership grant attempt 1 failed, retrying...', memberErr.message);
      await new Promise(r => setTimeout(r, 1000));
      try {
        const grantMembership = httpsCallable(functions, 'grantAccessTokenMembership');
        const result = await grantMembership({ token });
        console.log('[Access] Membership confirmed (retry):', result.data);
      } catch (retryErr) {
        console.error('[Access] Membership grant failed:', retryErr);
        // TASK-9381 — a DELIBERATE server refusal (the TASK-9360 grant gate:
        // wrong type, retired job-restricted shape, revoked, consumed) used to
        // fall through to an empty board with no explanation. Show the server's
        // own message and stop. Transient failures (internal/unavailable) still
        // fall through exactly as before so a cold start never strands a driver.
        // FunctionsError.code is 'functions/<code>' in the modular SDK; strip the
        // prefix so the check does not depend on it.
        const refusalCode = String((retryErr && retryErr.code) || '').replace(/^functions\//, '');
        if (['permission-denied', 'failed-precondition'].includes(refusalCode)) {
          showAccessError(retryErr.message || 'This link does not open the job board. Ask your office for a new one.');
          return false;
        }
      }
    }

    window.AppState.tenant = tokenData.tenantId;
    window.AppState.driverMode = true;
    window.AppState.isPublicAccess = true;
    window.AppState.accessToken = token;
    // H3 READ-ONLY — every ?access= link is a view-only board; the PIN is the
    // only write credential. TASK-10097: legacy links with no readOnly flag too
    // (the server mints them a read-only member). The server is the real
    // enforcement; this drives the UI (hide clock/ticket/expense, PIN hint).
    window.AppState.accessTokenReadOnly = true;

    // Store token-granted modules (default: dispatch/pretrip for driver links)
    window.AppState.tokenModules = tokenData.modules || ['dispatch', 'pretrip'];

    // Load tenant settings for all access tokens (needed for allowDriverTimeEdit, clockOutMode, etc.)
    try {
      const tenantDoc = await getDoc(doc(db, 'tenants', tokenData.tenantId));
      if (tenantDoc.exists()) {
        window.AppState.settings = tenantDoc.data().settings || {};
        window.AppState.tenantData = tenantDoc.data();
        console.log('[Access] Loaded tenant settings for access token');
      }
      if (typeof subscribeTenantSettings === 'function') subscribeTenantSettings(tokenData.tenantId);
    } catch (settingsErr) {
      console.error('[Access] Failed to load tenant settings:', settingsErr);
    }

    // PIN-gated tokens require PIN entry before accessing modules
    if (tokenData.pinGated) {
      window.AppState.pinGated = true;
    }

    // TASK-9381 — `tokenData.restrictedToJobId` is no longer read here. The
    // job-restricted link was a client filter over a full-tenant member; the
    // shape is retired and grantAccessTokenMembership refuses it (surfaced above).

    // Store the token's default view for routing (used by handleHashChange)
    if (tokenData.defaultView) {
      window.AppState.defaultView = tokenData.defaultView;
    }

    // Check if a specific view was requested via hash (e.g., #/pretrip)
    const requestedView = window.location.hash.slice(2).split('?')[0].split('/')[0] || tokenData.defaultView || 'dispatch';
    const validViews = ['dispatch', 'pretrip', 'roster', 'maintenance', 'clock', 'billing', 'timeoff', 'jobboard', 'crm'];
    window.AppState.currentView = validViews.includes(requestedView) ? requestedView : 'dispatch';

    // Add driver-mode class to body for CSS targeting
    document.body.classList.add('driver-mode');

    // CRITICAL: Ensure scroll is not locked in driver mode
    document.body.classList.remove('modal-open');
    document.documentElement.classList.remove('modal-open');
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.documentElement.style.overflow = '';

    // Hide loading and auth modal
    hideLoading();
    hideAuthModal();
    showSidebar();

    // Set up event listeners and routing
    // setupRouting() calls handleHashChange() which triggers navigateToView()
    setupEventListeners();
    setupRouting();

    return true;
  } catch (error) {
    console.error('[Access] Error validating token:', error);
    showAccessError('Check your signal and try again.');
    return false;
  }
}

function showAccessError(message) {
  hideLoading();

  // Detect in-app browsers (Instagram, TikTok, Facebook, Snapchat, LinkedIn, etc.)
  const ua = navigator.userAgent || '';
  const isInApp = /FBAN|FBAV|Instagram|Snapchat|TikTok|LinkedIn|Twitter|Line\//i.test(ua);
  const currentUrl = window.location.href;

  const inAppHint = isInApp ? `
    <div style="margin-top:16px;padding:16px;background:var(--bg-elevated);border-radius:12px;max-width:400px;">
      <p style="margin:0 0 8px 0;color:var(--brand-blue);font-weight:600;font-size:0.95rem;">
        <span class="material-icons" aria-hidden="true" style="font-size:18px;vertical-align:middle;margin-right:4px;">info</span>
        Open in your browser instead
      </p>
      <p style="margin:0 0 12px 0;color:var(--text-secondary);font-size:0.85rem;line-height:1.5;">
        This link may not work in app browsers. Tap the menu (⋯) and choose <strong>"Open in Safari"</strong> or <strong>"Open in Chrome"</strong>.
      </p>
      <button onclick="navigator.clipboard?.writeText(${window.escAttr(JSON.stringify(String(currentUrl)))});this.textContent='Copied!'" class="btn btn-secondary" style="font-size:0.85rem;padding:8px 16px;">
        <span class="material-icons" aria-hidden="true" style="font-size:16px;vertical-align:middle;margin-right:4px;">content_copy</span> Copy Link
      </button>
    </div>
  ` : '';

  // TASK-9900 — the heading used to read "Link Expired or Invalid" even for a
  // network blip ("Unable to load ... try again"), telling a driver their good
  // link was dead. A retry-able failure now says so and offers Try again; a
  // dead link says it in plain words. Every caller's message is static copy
  // except the TASK-9381 server refusal, so escape it regardless.
  const msg = String(message || '');
  const isRetry = /try again/i.test(msg);
  const heading = isRetry ? "We couldn't load this page"
    : /expired/i.test(msg) ? 'This link has expired'
    : "This link isn't valid anymore";
  const escMsg = msg.replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));
  const action = isRetry
    ? `<button type="button" onclick="location.reload()" class="btn btn-primary" style="margin-top:16px;min-height:44px;">Try again</button>`
    : `<a href="/" class="btn btn-primary" style="text-decoration:none;margin-top:16px;">
          <span class="material-icons" aria-hidden="true">home</span> Go to Login
        </a>`;

  const mainContent = document.getElementById('main-content');
  if (mainContent) {
    mainContent.innerHTML = `
      <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:60vh;padding:20px;text-align:center;">
        <span class="material-icons" aria-hidden="true" style="font-size:64px;color:var(--status-danger);margin-bottom:16px;">${isRetry ? 'wifi_off' : 'link_off'}</span>
        <h2 style="margin:0 0 8px 0;color:var(--text-primary);">${heading}</h2>
        <p style="margin:0 0 24px 0;color:var(--text-secondary);max-width:400px;">${escMsg}</p>
        ${inAppHint}
        ${action}
      </div>
    `;
  }
}

// ============================================
// PORTAL PROJECT BOARD (Customer-facing project status)
// ============================================
function renderPortalNotes(notes) {
  if (!notes || notes.length === 0) {
    return '<div style="text-align:center;color:var(--text-secondary);font-size:0.8125rem;padding:var(--spacing-sm);">No feedback yet. Be the first to leave a note!</div>';
  }
  const escHTML = (s) => s ? String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;') : '';
  return notes.map(n => {
    const dt = n.createdAt?.toDate ? n.createdAt.toDate() : (n.createdAt ? new Date(n.createdAt) : null);
    const dateStr = dt ? dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
    const typeLabel = n.authorType === 'customer' ? 'Customer' : n.authorType === 'crew' ? 'Crew' : 'Admin';
    const typeColor = n.authorType === 'customer' ? '#10B981' : n.authorType === 'crew' ? '#3B82F6' : '#8B5CF6';
    return `
      <div style="padding:10px 12px;background:var(--bg-secondary);border-radius:var(--radius-sm);margin-bottom:8px;">
        <div style="display:flex;align-items:center;gap:6px;margin-bottom:4px;">
          <span style="font-size:0.7rem;font-weight:600;color:${typeColor};background:${typeColor}15;padding:1px 6px;border-radius:4px;">${typeLabel}</span>
          <span style="font-size:0.75rem;color:var(--text-secondary);">${escHTML(n.author || 'Unknown')} · ${dateStr}</span>
        </div>
        <div style="font-size:0.875rem;color:var(--text-primary);line-height:1.5;">${escHTML(n.text)}</div>
      </div>`;
  }).join('');
}

// TASK-9856 -- typed link pages do not read the tenant doc (it carries
// settings.accessPins); their header comes from getLinkPageTenant (names +
// https logo). Returns a DocumentSnapshot-shaped object so callers keep their
// `exists()` / `data()` reads.
async function _linkTenantDoc(token) {
  const res = await httpsCallable(functions, 'getLinkPageTenant')({ token });
  const t = (res && res.data && res.data.tenant) || {};
  return { exists: () => true, data: () => t };
}

async function showPortalPinGate(token, tokenData) {
  // Hide standard UI
  const header = document.getElementById('app-header');
  const sidebar = document.getElementById('app-sidebar');
  const mobileNav = document.getElementById('mobile-nav');
  if (header) header.style.display = 'none';
  if (sidebar) sidebar.style.display = 'none';
  if (mobileNav) mobileNav.style.display = 'none';
  document.body.classList.add('portal-mode');

  // Load company branding
  let companyName = 'Project Board';
  let companyLogo = '';
  try {
    const tenantDoc = await _linkTenantDoc(token);
    if (tenantDoc.exists()) {
      const td = tenantDoc.data();
      companyName = td.companyName || td.name || 'Project Board';
      companyLogo = td.companyLogoUrl || td.logoUrl || '';
    }
  } catch (e) {}

  const mainContent = document.getElementById('main-content');
  if (!mainContent) return;

  // TASK-1422 — companyName/companyLogo come from the TENANT doc, which a tenant
  // admin controls, and this page is PUBLIC. Unescaped they were a stored-XSS
  // sink firing for anyone who opened that tenant's portal.
  // escapeHtml (textContent->innerHTML) escapes <>& but NOT quotes, so it is
  // correct for TEXT and wrong for an ATTRIBUTE — window.escAttr (sharedFormat.js,
  // imported at the top of this file) escapes the full &<>"' set. Using the wrong
  // one here would look fixed and still allow a `"` breakout into onerror=.
  const safeLogo = /^https:\/\//i.test(String(companyLogo || '')) ? companyLogo : '';
  mainContent.innerHTML = `
    <div class="portal-page" style="display:flex;align-items:center;justify-content:center;min-height:80vh;">
      <div class="portal-card" style="max-width:360px;width:100%;text-align:center;">
        <div style="margin-bottom:var(--spacing-lg);">
          ${safeLogo ? `<img src="${window.escAttr(safeLogo)}" alt="" style="height:48px;margin-bottom:12px;">` : ''}
          <h2 style="font-size:1.25rem;font-weight:700;margin:0 0 4px 0;">${escapeHtml(companyName)}</h2>
          <div style="font-size:0.875rem;color:var(--text-secondary);">Enter your PIN to view this project</div>
        </div>
        <div id="portal-pin-error" style="display:none;color:#EF4444;font-size:0.8125rem;margin-bottom:12px;"></div>
        <input type="tel" id="portal-pin-input" maxlength="4" placeholder="4-digit PIN"
          style="width:100%;text-align:center;font-size:1.5rem;letter-spacing:8px;padding:12px;border:2px solid var(--border-light);border-radius:var(--radius-md);font-weight:700;"
          onkeydown="if(event.key==='Enter')window._submitPortalPin()">
        <button id="portal-pin-btn" class="btn btn-primary" onclick="window._submitPortalPin()" style="width:100%;margin-top:12px;padding:12px;">
          <span class="material-icons" style="font-size:18px;vertical-align:middle;">lock_open</span> Access Project
        </button>
      </div>
    </div>
  `;

  hideLoading();
  hideAuthModal();
  document.getElementById('portal-pin-input')?.focus();

  window._submitPortalPin = async () => {
    const pinInput = document.getElementById('portal-pin-input');
    const errorEl = document.getElementById('portal-pin-error');
    const btn = document.getElementById('portal-pin-btn');
    const pin = pinInput?.value?.trim();

    if (!pin || pin.length !== 4) {
      errorEl.textContent = 'Please enter a 4-digit PIN';
      errorEl.style.display = 'block';
      return;
    }

    btn.disabled = true;
    btn.innerHTML = '<span class="btn-spinner"></span> Verifying...';

    try {
      const validatePin = httpsCallable(functions, 'portalValidatePin');
      const result = await validatePin({ token, pin });
      if (result.data.ok) {
        sessionStorage.setItem(`portal_pin_${token}`, 'ok');
        await loadPortalProjectBoard(token, tokenData);
      } else {
        errorEl.textContent = result.data.error || 'Incorrect PIN';
        errorEl.style.display = 'block';
        btn.disabled = false;
        btn.innerHTML = '<span class="material-icons" style="font-size:18px;vertical-align:middle;">lock_open</span> Access Project';
        pinInput.value = '';
        pinInput.focus();
      }
    } catch (err) {
      console.error('[Portal] PIN validation error:', err);
      errorEl.textContent = 'Unable to verify PIN. Please try again.';
      errorEl.style.display = 'block';
      btn.disabled = false;
      btn.innerHTML = '<span class="material-icons" style="font-size:18px;vertical-align:middle;">lock_open</span> Access Project';
    }
  };
}

async function loadPortalProjectBoard(token, tokenData) {
  const escHTML = (s) => s ? String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;') : '';
  const formatDollars = (n) => (n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  try {
    // Grant membership
    try {
      const grantMembership = httpsCallable(functions, 'grantAccessTokenMembership');
      await grantMembership({ token });
    } catch (e) { console.warn('[Portal] Membership grant:', e.message); }

    window.AppState.tenant = tokenData.tenantId;

    // Hide standard UI
    const header = document.getElementById('app-header');
    const sidebar = document.getElementById('app-sidebar');
    const mobileNav = document.getElementById('mobile-nav');
    if (header) header.style.display = 'none';
    if (sidebar) sidebar.style.display = 'none';
    if (mobileNav) mobileNav.style.display = 'none';
    document.body.classList.add('portal-mode');

    // Load tenant info for branding
    let companyName = 'Project Board';
    let companyLogo = '';
    try {
      const tenantDoc = await _linkTenantDoc(token);
      if (tenantDoc.exists()) {
        const td = tenantDoc.data();
        companyName = td.companyName || td.name || 'Project Board';
        companyLogo = td.companyLogoUrl || td.logoUrl || '';
      }
    } catch (e) { console.warn('[Portal] Tenant load:', e.message); }

    // Load opportunity data
    const oppPath = `tenants/${tokenData.tenantId}/customers/${tokenData.customerId}/opportunities/${tokenData.opportunityId}`;
    const oppDoc = await getDoc(doc(db, oppPath));
    if (!oppDoc.exists()) {
      showAccessError('This project board is no longer available.');
      return;
    }
    const opp = oppDoc.data();

    // Load customer name
    let customerName = opp.customerName || '';
    if (!customerName && tokenData.customerId) {
      try {
        const custDoc = await getDoc(doc(db, `tenants/${tokenData.tenantId}/customers/${tokenData.customerId}`));
        if (custDoc.exists()) customerName = custDoc.data().name || '';
      } catch (e) {}
    }

    // Build portal UI
    const mainContent = document.getElementById('main-content');
    if (!mainContent) return;

    const portal = opp.portal || {};
    const trackerSteps = portal.trackerSteps || [];
    const bidLines = opp.bidLines || [];
    const bidTotal = opp.bidTotal || bidLines.reduce((s, l) => s + (l.total || 0), 0);

    // Find current step (first non-completed)
    const currentStepIdx = trackerSteps.findIndex(s => !s.completed);

    // Build tracker HTML
    let trackerHTML = '';
    if (portal.trackerEnabled && trackerSteps.length > 0) {
      trackerHTML = `
        <div class="portal-section">
          <h2 class="portal-section-title"><span class="material-icons">timeline</span> Progress</h2>
          <div class="pizza-tracker">
            ${trackerSteps.map((step, i) => {
              const isCompleted = step.completed;
              const isCurrent = i === currentStepIdx;
              const stageClass = isCompleted ? 'completed' : (isCurrent ? 'current' : '');
              const connectorHTML = i < trackerSteps.length - 1
                ? `<div class="tracker-connector ${isCompleted ? 'completed' : ''}"></div>`
                : '';
              return `
                <div class="tracker-stage ${stageClass}">
                  <div class="tracker-dot-large"></div>
                  <div class="tracker-label">${escHTML(step.name)}</div>
                  ${isCompleted && step.completedAt ? `<div style="font-size:0.6875rem;color:var(--text-secondary);">${step.completedAt?.toDate ? step.completedAt.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}</div>` : ''}
                </div>
                ${connectorHTML}
              `;
            }).join('')}
          </div>
        </div>
      `;
    }

    // Build bid summary HTML
    let bidHTML = '';
    if (bidLines.length > 0) {
      bidHTML = `
        <div class="portal-section">
          <h2 class="portal-section-title"><span class="material-icons">request_quote</span> Bid Summary</h2>
          <div class="portal-bid-table">
            <div class="portal-bid-header">
              <span style="flex:2;">Item</span>
              <span style="flex:1;text-align:center;">Rate</span>
              <span style="flex:1;text-align:center;">Qty</span>
              <span style="flex:1;text-align:right;">Total</span>
            </div>
            ${bidLines.map(line => `
              <div class="portal-bid-line">
                <div style="flex:2;">
                  <div style="font-weight:500;color:var(--text-primary);">${escHTML(line.lineItemName || 'Item')}</div>
                </div>
                <div style="flex:1;text-align:center;color:var(--text-secondary);">$${(line.rate || 0).toFixed(2)}/${line.rateType || 'hr'}</div>
                <div style="flex:1;text-align:center;">${line.qty || 0}</div>
                <div style="flex:1;text-align:right;font-weight:600;">$${formatDollars(line.total || 0)}</div>
              </div>
            `).join('')}
            <div class="portal-bid-total">
              <span>Total</span>
              <span>$${formatDollars(bidTotal)}</span>
            </div>
          </div>
        </div>
      `;
    }

    // Assets
    let assetsHTML = '';
    if (opp.assets?.length > 0) {
      assetsHTML = `
        <div class="portal-section">
          <h2 class="portal-section-title"><span class="material-icons">local_shipping</span> Equipment</h2>
          <div style="display:flex;flex-wrap:wrap;gap:8px;">
            ${opp.assets.map(a => `
              <div class="portal-asset-chip">
                <span class="material-icons" style="font-size:16px;">${a.assetType === 'truck' ? 'local_shipping' : a.assetType === 'trailer' ? 'rv_hookup' : 'build'}</span>
                ${escHTML(a.assetUnit || a.assetName || 'Asset')}
              </div>
            `).join('')}
          </div>
        </div>
      `;
    }

    // Portal Orders section
    let ordersHTML = '';
    let portalAssets = [];
    if (portal.ordersEnabled) {
      // TASK-10032 -- through the getLinkPageAssets allow-list (the server
      // applies ordersEnabled, orderCategories and showPricing); a portal link
      // can no longer read asset docs. Sorted here, as before.
      try {
        const assetsRes = await httpsCallable(functions, 'getLinkPageAssets')({ token });
        portalAssets = _sortAssetsByPartNo((assetsRes.data && assetsRes.data.assets) || []);
      } catch (err) {
        console.warn('[Portal] Assets load:', err.message);
        window.showToast?.('Could not load the items you can order. Please reload the page.', 'error');
      }

      // Load existing orders for this customer
      let existingOrders = [];
      try {
        const ordersSnap = await getDocs(
          query(
            collection(db, 'tenants', tokenData.tenantId, 'orders'),
            where('customerId', '==', tokenData.customerId),
            orderBy('createdAt', 'desc')
          )
        );
        existingOrders = ordersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      } catch (err) {
        console.warn('[Portal] Orders load:', err.message);
      }

      const orderStatusColors = { pending: '#F59E0B', approved: '#3B82F6', prep: '#06B6D4', shipping: '#8B5CF6', delivered: '#10B981', cancelled: '#EF4444' };
      const orderStatusLabels = { pending: 'Pending', approved: 'Approved', prep: 'Preparing', shipping: 'In Transit', delivered: 'Delivered', cancelled: 'Cancelled' };

      const existingOrdersHTML = existingOrders.length > 0 ? `
        <div style="margin-bottom:16px;">
          <h3 style="font-size:0.875rem;font-weight:600;color:var(--text-secondary);margin-bottom:8px;">Recent Orders</h3>
          ${existingOrders.slice(0, 5).map(o => {
            const sc = orderStatusColors[o.status] || '#94A3B8';
            const sl = orderStatusLabels[o.status] || o.status;
            const date = o.createdAt?.toDate ? o.createdAt.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
            const itemCount = (o.items || []).length;
            return `
              <div style="display:flex;justify-content:space-between;align-items:center;padding:10px 12px;background:var(--bg-secondary);border-radius:var(--radius-sm);margin-bottom:6px;">
                <div>
                  <div style="font-size:0.8125rem;font-weight:500;">${itemCount} item${itemCount !== 1 ? 's' : ''}${o.poNumber ? ` · PO: ${escHTML(o.poNumber)}` : ''}</div>
                  <div style="font-size:0.75rem;color:var(--text-secondary);">${date}${o.location ? ` · ${escHTML(o.location)}` : ''}</div>
                </div>
                <span style="font-size:0.6875rem;font-weight:600;padding:3px 8px;border-radius:10px;background:${sc}15;color:${sc};border:1px solid ${sc}30;">${sl}</span>
              </div>
            `;
          }).join('')}
        </div>
      ` : '';

      const assetItemsHTML = portalAssets.length > 0
        ? portalAssets.map(asset => {
            const available = asset.trackingMode === 'quantity'
              ? `${portalDisplayQty(asset.quantity)} available`
              : '1 avail';
            const priceHTML = portal.showPricing && asset.price
              ? `<span style="font-size:0.75rem;color:var(--text-secondary);">$${(asset.price || 0).toFixed(2)}</span>`
              : '';
            return `
              <div class="portal-order-item" data-asset-id="${window.escAttr(asset.id)}">
                <input type="checkbox" id="portal-asset-${window.escAttr(asset.id)}" onchange="window._togglePortalAsset(${window.escAttr(JSON.stringify(String(asset.id)))})">
                <div style="flex:1;min-width:0;">
                  <div style="font-size:0.8125rem;font-weight:500;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escHTML(asset.partNumber)} - ${escHTML(asset.name)}</div>
                  <div style="font-size:0.75rem;color:var(--text-secondary);">${escHTML(asset.category ? (window.getTypeLabel?.(asset.category, 'assetCategories') || asset.category) : 'Other')} · ${available}</div>
                </div>
                ${priceHTML}
                <input type="number" class="portal-order-qty" id="portal-qty-${window.escAttr(asset.id)}"
                  min="1" max="${asset.trackingMode === 'quantity' ? (portalDisplayQty(asset.quantity) || 1) : 1}"
                  value="1" disabled style="width:56px;padding:4px 6px;border:1px solid var(--border-light);border-radius:var(--radius-sm);font-size:0.8125rem;text-align:center;">
              </div>
            `;
          }).join('')
        : '<p style="font-size:0.875rem;color:var(--text-secondary);text-align:center;padding:16px 0;">No items available for order at this time.</p>';

      ordersHTML = `
        <div class="portal-section">
          <h2 class="portal-section-title"><span class="material-icons">shopping_cart</span> Request Equipment</h2>
          ${existingOrdersHTML}
          <div id="portal-order-form">
            <div style="display:flex;flex-direction:column;gap:4px;margin-bottom:16px;">
              ${assetItemsHTML}
            </div>
            ${portalAssets.length > 0 ? `
              <div style="display:flex;flex-direction:column;gap:10px;margin-bottom:12px;">
                <div style="display:flex;gap:8px;">
                  <input type="text" id="portal-order-po" placeholder="PO Number (optional)"
                    style="flex:1;padding:8px 10px;border:1px solid var(--border-light);border-radius:var(--radius-sm);font-size:0.8125rem;font-family:inherit;">
                  <input type="date" id="portal-order-date" required
                    style="flex:1;padding:8px 10px;border:1px solid var(--border-light);border-radius:var(--radius-sm);font-size:0.8125rem;font-family:inherit;">
                </div>
                <input type="text" id="portal-order-location" placeholder="Delivery location *" required
                  style="width:100%;padding:8px 10px;border:1px solid var(--border-light);border-radius:var(--radius-sm);font-size:0.8125rem;box-sizing:border-box;font-family:inherit;">
                <textarea id="portal-order-notes" placeholder="Notes (gate codes, instructions...)" rows="2"
                  style="width:100%;padding:8px 10px;border:1px solid var(--border-light);border-radius:var(--radius-sm);font-size:0.8125rem;resize:vertical;box-sizing:border-box;font-family:inherit;"></textarea>
              </div>
              <button id="portal-order-btn" onclick="window._submitPortalOrder()" class="btn btn-primary" style="width:100%;padding:10px;">
                <span class="material-icons" style="font-size:16px;">send</span> Submit Order Request
              </button>
            ` : ''}
          </div>
        </div>
      `;
    }

    // People assigned
    let peopleHTML = '';
    const assignedPeople = opp.assignedPeople || [];
    const assignedRoles = opp.assignedRoles || [];
    if (assignedPeople.length > 0 || assignedRoles.length > 0) {
      peopleHTML = `
        <div class="portal-section">
          <h2 class="portal-section-title"><span class="material-icons">group</span> Team</h2>
          <div style="display:flex;flex-wrap:wrap;gap:8px;">
            ${assignedPeople.map(p => `
              <div class="portal-person-chip">
                <span class="material-icons" style="font-size:16px;">person</span>
                ${escHTML(p.personName)}${p.role ? ` <span style="color:var(--text-secondary);font-size:0.75rem;">(${escHTML(p.role)})</span>` : ''}
              </div>
            `).join('')}
            ${assignedRoles.map(r => `
              <div class="portal-person-chip" style="border-style:dashed;">
                <span class="material-icons" style="font-size:16px;color:#F59E0B;">badge</span>
                ${escHTML(r.roleName)} <span style="color:var(--text-secondary);font-size:0.75rem;">(TBD)</span>
              </div>
            `).join('')}
          </div>
        </div>
      `;
    }

    // Stage info
    const stageColors = { initiating: '#94A3B8', qualifying: '#F59E0B', prospecting: '#3B82F6', negotiating: '#06B6D4', closing: '#10B981', on_hold: '#F97316', won: '#8B5CF6', lost: '#EF4444' };
    const stageLabels = { initiating: 'Planning', qualifying: 'Evaluating', prospecting: 'In Progress', negotiating: 'Finalizing', closing: 'Wrapping Up', on_hold: 'On Hold', won: 'Complete', lost: 'Cancelled' };
    const stageColor = stageColors[opp.stage] || '#94A3B8';
    const stageLabel = stageLabels[opp.stage] || opp.stage;

    // Dates
    const startDate = opp.dispatchStartDate || '';
    const endDate = opp.dispatchEndDate || '';
    const dateRange = startDate ? (endDate && endDate !== startDate ? `${startDate} → ${endDate}` : startDate) : '';

    // Linked project portal
    let linkedProjectHTML = '';
    if (opp.projectId) {
      try {
        // TASK-9856 -- display fields from the server; a portal link can't read
        // the project doc (TASK-4820: portalPin, estimatedRevenue).
        const projRes = await httpsCallable(functions, 'getPortalLinkedProject')({ token });
        const proj = projRes && projRes.data && projRes.data.project;
        if (proj && proj.portalUrl) {
          linkedProjectHTML = `
            <div class="portal-section">
              <h2 class="portal-section-title"><span class="material-icons">engineering</span> Active Project</h2>
              <div style="display:flex;align-items:center;justify-content:space-between;padding:12px;background:rgba(59,130,246,0.05);border:1px solid rgba(59,130,246,0.15);border-radius:var(--radius-sm);">
                <div>
                  <div style="font-weight:600;color:var(--text-primary);">${escHTML(proj.name)}</div>
                  <div style="font-size:0.8rem;color:var(--text-secondary);">${escHTML(proj.status || '')}${proj.location ? ' · ' + escHTML(proj.location) : ''}</div>
                </div>
                <a href="${/^https:\/\//i.test(String(proj.portalUrl || '')) ? escHTML(proj.portalUrl) : '#'}" class="btn btn-primary btn-sm" target="_blank" rel="noopener noreferrer" style="text-decoration:none;">
                  <span class="material-icons" style="font-size:14px;">open_in_new</span> View Progress
                </a>
              </div>
            </div>`;
        }
      } catch (e) { /* linked project load failure is non-blocking */ }
    }

    mainContent.innerHTML = `
      <div class="portal-page">
        <div class="portal-header">
          ${/^https:\/\//i.test(String(companyLogo || '')) ? `<img src="${escHTML(companyLogo)}" alt="" class="portal-logo">` : ''}
          <div class="portal-header-text">
            <h1 class="portal-company">${escHTML(companyName)}</h1>
            <div class="portal-subtitle">Project Board</div>
          </div>
        </div>

        <div class="portal-card portal-hero">
          <div style="display:flex;justify-content:space-between;align-items:flex-start;flex-wrap:wrap;gap:12px;">
            <div>
              <h2 class="portal-project-title">${escHTML(opp.title)}</h2>
              ${customerName ? `<div style="font-size:0.875rem;color:var(--text-secondary);">${escHTML(customerName)}</div>` : ''}
              ${dateRange ? `<div style="font-size:0.8125rem;color:var(--text-secondary);margin-top:4px;"><span class="material-icons" style="font-size:14px;vertical-align:middle;">event</span> ${dateRange}</div>` : ''}
            </div>
            <div class="portal-status-badge" style="background:${stageColor}15;color:${stageColor};border:1px solid ${stageColor}30;">
              ${stageLabel}
            </div>
          </div>
          ${opp.notes ? `<div style="margin-top:12px;font-size:0.875rem;color:var(--text-secondary);line-height:1.5;">${escHTML(opp.notes)}</div>` : ''}
        </div>

        ${linkedProjectHTML}
        ${trackerHTML}
        ${bidHTML}
        ${assetsHTML}
        ${peopleHTML}
        ${ordersHTML}

        <div class="portal-section">
          <h2 class="portal-section-title"><span class="material-icons">rate_review</span> Project Feedback</h2>
          <div id="portal-notes-list">
            ${renderPortalNotes(portal.notes || [])}
          </div>
          <div style="margin-top:12px;">
            <textarea id="portal-note-input" maxlength="500" rows="2" placeholder="Leave feedback or a note about this project..."
              style="width:100%;padding:10px;border:1px solid var(--border-light);border-radius:var(--radius-sm);font-size:0.875rem;resize:vertical;box-sizing:border-box;font-family:inherit;"></textarea>
            <div style="display:flex;justify-content:space-between;align-items:center;margin-top:6px;">
              <span id="portal-note-chars" style="font-size:0.75rem;color:var(--text-secondary);">0/500</span>
              <button id="portal-note-btn" class="btn btn-primary btn-sm" onclick="window._submitPortalNote()" style="padding:8px 16px;">
                <span class="material-icons" style="font-size:16px;">send</span> Send
              </button>
            </div>
          </div>
        </div>

        <div class="portal-footer">
          <div style="font-size:0.75rem;color:var(--text-secondary);">Powered by <a href="https://dispatchdeck.app" target="_blank" style="color:var(--brand-blue);text-decoration:none;font-weight:600;">DispatchDeck</a></div>
        </div>
      </div>
    `;

    // Wire up char counter
    const noteInput = document.getElementById('portal-note-input');
    if (noteInput) {
      noteInput.addEventListener('input', () => {
        const chars = document.getElementById('portal-note-chars');
        if (chars) chars.textContent = `${noteInput.value.length}/500`;
      });
    }

    // Note submission
    window._submitPortalNote = async () => {
      const input = document.getElementById('portal-note-input');
      const btn = document.getElementById('portal-note-btn');
      const text = input?.value?.trim();
      if (!text) return;

      btn.disabled = true;
      btn.innerHTML = '<span class="btn-spinner"></span>';
      try {
        const submitNote = httpsCallable(functions, 'portalSubmitNote');
        await submitNote({ token, text, authorName: 'Customer', authorType: 'customer' });
        input.value = '';
        const chars = document.getElementById('portal-note-chars');
        if (chars) chars.textContent = '0/500';
        // Note will appear via onSnapshot
      } catch (err) {
        console.error('[Portal] Note submit error:', err);
        window.showToast?.('Failed to send feedback', 'error');
      }
      btn.disabled = false;
      btn.innerHTML = '<span class="material-icons" style="font-size:16px;">send</span> Send';
    };

    // Portal order toggle & submit
    window._togglePortalAsset = (assetId) => {
      const cb = document.getElementById(`portal-asset-${assetId}`);
      const qty = document.getElementById(`portal-qty-${assetId}`);
      const item = cb?.closest('.portal-order-item');
      if (cb && qty && item) {
        qty.disabled = !cb.checked;
        item.classList.toggle('selected', cb.checked);
        if (cb.checked) qty.focus();
      }
    };

    window._submitPortalOrder = async () => {
      const btn = document.getElementById('portal-order-btn');
      const location = document.getElementById('portal-order-location')?.value?.trim();
      const dateVal = document.getElementById('portal-order-date')?.value;

      // Collect selected items
      const items = [];
      document.querySelectorAll('.portal-order-item').forEach(item => {
        const assetId = item.dataset.assetId;
        const cb = document.getElementById(`portal-asset-${assetId}`);
        const qtyInput = document.getElementById(`portal-qty-${assetId}`);
        if (cb?.checked) {
          const asset = portalAssets.find(a => a.id === assetId);
          items.push({
            assetId,
            partNumber: asset?.partNumber || '',
            name: asset?.name || '',
            quantity: parseInt(qtyInput?.value) || 1
          });
        }
      });

      if (items.length === 0) {
        window.showToast?.('Please select at least one item', 'error');
        return;
      }
      if (!location) {
        window.showToast?.('Delivery location is required', 'error');
        return;
      }
      if (!dateVal) {
        window.showToast?.('Requested date is required', 'error');
        return;
      }

      btn.disabled = true;
      btn.innerHTML = '<span class="btn-spinner"></span> Submitting...';

      try {
        const orderData = {
          customerId: tokenData.customerId || null,
          customerName: customerName || '',
          opportunityId: tokenData.opportunityId || null,
          poNumber: document.getElementById('portal-order-po')?.value?.trim() || '',
          requestedDate: Timestamp.fromDate(new Date(dateVal + 'T12:00:00')),
          location: location,
          notes: document.getElementById('portal-order-notes')?.value?.trim() || null,
          items: items,
          status: 'pending',
          statusHistory: [{
            status: 'pending',
            timestamp: Timestamp.now(),
            updatedBy: 'customer'
          }],
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
          createdVia: 'customer_portal',
          requestToken: token
        };

        await addDoc(collection(db, 'tenants', tokenData.tenantId, 'orders'), orderData);

        // Show success inline
        const formEl = document.getElementById('portal-order-form');
        if (formEl) {
          formEl.innerHTML = `
            <div style="text-align:center;padding:20px 0;">
              <span class="material-icons" style="font-size:48px;color:#10B981;">check_circle</span>
              <h3 style="margin:8px 0 4px;font-size:1rem;font-weight:600;">Order Submitted!</h3>
              <p style="font-size:0.8125rem;color:var(--text-secondary);">Your order request has been sent for review.</p>
              <button onclick="location.reload()" class="btn btn-secondary" style="margin-top:12px;padding:8px 16px;font-size:0.8125rem;">
                <span class="material-icons" style="font-size:14px;">add</span> Place Another Order
              </button>
            </div>
          `;
        }
      } catch (err) {
        console.error('[Portal] Order submit error:', err);
        window.showToast?.('Failed to submit order. Please try again.', 'error');
        btn.disabled = false;
        btn.innerHTML = '<span class="material-icons" style="font-size:16px;">send</span> Submit Order Request';
      }
    };

    // Set default order date to tomorrow
    const orderDateInput = document.getElementById('portal-order-date');
    if (orderDateInput) {
      const tmrw = new Date();
      tmrw.setDate(tmrw.getDate() + 1);
      orderDateInput.value = tmrw.toISOString().split('T')[0];
      orderDateInput.min = new Date().toISOString().split('T')[0];
    }

    hideLoading();
    hideAuthModal();

    // Listen for real-time updates
    onSnapshot(doc(db, oppPath), (snap) => {
      if (snap.exists()) {
        const updated = snap.data();
        const updatedPortal = updated.portal || {};
        const updatedSteps = updatedPortal.trackerSteps || [];

        // Update tracker if it exists
        const trackerEl = mainContent.querySelector('.pizza-tracker');
        if (trackerEl && updatedSteps.length > 0) {
          const curIdx = updatedSteps.findIndex(s => !s.completed);
          trackerEl.innerHTML = updatedSteps.map((step, i) => {
            const isCompleted = step.completed;
            const isCurrent = i === curIdx;
            const stageClass = isCompleted ? 'completed' : (isCurrent ? 'current' : '');
            const conn = i < updatedSteps.length - 1
              ? `<div class="tracker-connector ${isCompleted ? 'completed' : ''}"></div>`
              : '';
            return `
              <div class="tracker-stage ${stageClass}">
                <div class="tracker-dot-large"></div>
                <div class="tracker-label">${escHTML(step.name)}</div>
                ${isCompleted && step.completedAt ? `<div style="font-size:0.6875rem;color:var(--text-secondary);">${step.completedAt?.toDate ? step.completedAt.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}</div>` : ''}
              </div>
              ${conn}
            `;
          }).join('');
        }

        // Update status badge
        const badge = mainContent.querySelector('.portal-status-badge');
        if (badge) {
          const sc = stageColors[updated.stage] || '#94A3B8';
          const sl = stageLabels[updated.stage] || updated.stage;
          badge.style.background = `${sc}15`;
          badge.style.color = sc;
          badge.style.borderColor = `${sc}30`;
          badge.textContent = sl;
        }

        // Update notes
        const notesList = document.getElementById('portal-notes-list');
        if (notesList) {
          notesList.innerHTML = renderPortalNotes(updatedPortal.notes || []);
        }
      }
    });

  } catch (err) {
    console.error('[Portal] Error loading project board:', err);
    showAccessError('Unable to load project board. Please try again.');
  }
}

// ============================================
// ORDER REQUEST PAGE (Customer Order Link)
// ============================================
async function loadOrderRequestPage(token) {
  try {
    // Set public access flag and auth BEFORE any Firestore reads
    window.AppState.isPublicAccess = true;
    try {
      await ensurePublicAuth();
    } catch (authErr) {
      console.error('[Order] Auth error:', authErr);
      window.AppState.isPublicAccess = false;
      showAccessError('Unable to load order page. Please try again.');
      return false;
    }

    // Look up token in Firestore
    const tokenDoc = await getDoc(doc(db, 'accessTokens', token));

    if (!tokenDoc.exists()) {
      console.log('[Order] Token not found');
      showAccessError('This order request link is invalid or has been revoked.');
      return false;
    }

    const tokenData = tokenDoc.data();

    // Verify this is an order_request token
    if (tokenData.type !== 'order_request') {
      console.log('[Order] Invalid token type:', tokenData.type);
      showAccessError('This link is not valid for ordering.');
      return false;
    }

    // Check expiration (null = never expires)
    if (tokenData.expiresAt !== null) {
      const now = new Date();
      const expiresAt = tokenData.expiresAt?.toDate?.() || new Date(tokenData.expiresAt);

      if (now > expiresAt) {
        console.log('[Order] Token expired');
        showAccessError('This order request link has expired. Please contact us for a new one.');
        return false;
      }
    }

    // Token is valid! Set up order request mode
    console.log('[Order] Token valid for tenant:', tokenData.tenantId);

    // Grant membership via Cloud Function
    try {
      const grantMembership = httpsCallable(functions, 'grantAccessTokenMembership');
      const result = await grantMembership({ token });
      console.log('[Order] Membership granted:', result.data);
    } catch (memberErr) {
      console.error('[Order] Failed to grant membership:', memberErr);
    }

    window.AppState.tenant = tokenData.tenantId;
    window.AppState.orderRequestToken = token;
    window.AppState.orderRequestCustomerId = tokenData.customerId || null;

    // Hide standard UI elements and set up order request mode
    const header = document.getElementById('app-header');
    const sidebar = document.getElementById('app-sidebar');
    const mobileNav = document.getElementById('mobile-nav');

    if (header) header.style.display = 'none';
    if (sidebar) sidebar.style.display = 'none';
    if (mobileNav) mobileNav.style.display = 'none';

    // Add body class for order request styling (enables scroll)
    document.body.classList.add('order-request-mode');

    // Load tenant data for company name
    let companyName = 'Equipment Request';
    try {
      const tenantDoc = await _linkTenantDoc(token);
      if (tenantDoc.exists()) {
        companyName = tenantDoc.data().companyName || 'Equipment Request';
      }
    } catch (err) {
      console.warn('[Order] Could not load tenant name:', err);
    }

    // Load customer name if customerId is set
    let customerName = '';
    if (tokenData.customerId) {
      try {
        const customerDoc = await getDoc(doc(db, 'tenants', tokenData.tenantId, 'customers', tokenData.customerId));
        if (customerDoc.exists()) {
          customerName = customerDoc.data().name || '';
        }
      } catch (err) {
        console.warn('[Order] Could not load customer name:', err);
      }
    }

    // Load available assets -- TASK-10032: through the getLinkPageAssets
    // allow-list; an order link can no longer read asset docs.
    let assets = [];
    try {
      const assetsRes = await httpsCallable(functions, 'getLinkPageAssets')({ token });
      assets = _sortAssetsByPartNo((assetsRes.data && assetsRes.data.assets) || []);
    } catch (err) {
      console.error('[Order] Error loading assets:', err);
      window.showToast?.('Could not load the items you can order. Please reload the page.', 'error');
    }

    // Render the order request page
    hideLoading();
    renderOrderRequestPage(companyName, customerName, assets, tokenData.customerId, token);

    return true;
  } catch (error) {
    console.error('[Order] Error loading order request page:', error);
    showAccessError('Unable to load order page. Please try again.');
    return false;
  }
}

// Asset order for the customer portal / order-request pages without a
// Firestore orderBy (which drops docs missing partNumber). Same order as the
// old orderBy for docs with a part number; part-less docs follow, by name.
// Mirrors sortRosterAssets in roster.js (not imported — Pitfall #1).
function _sortAssetsByPartNo(list) {
  const pn = (a) => (a && a.partNumber !== null && a.partNumber !== undefined) ? String(a.partNumber).trim() : '';
  const cmp = (x, y) => (x < y ? -1 : x > y ? 1 : 0);
  return (list || []).slice().sort((a, b) => {
    const pa = pn(a), pb = pn(b);
    if (!pa !== !pb) return pa ? -1 : 1;
    if (pa) { const c = cmp(pa, pb); if (c) return c; }
    const n = String(a?.name || '').toLowerCase().localeCompare(String(b?.name || '').toLowerCase());
    return n || cmp(String(a?.id || ''), String(b?.id || ''));
  });
}

// TASK-9318 customer-facing on-hand count: never show a negative (or non-numeric) count, clamp to 0.
function portalDisplayQty(q) {
  const n = Number(q);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function renderOrderRequestPage(companyName, customerName, assets, customerId, token) {
  const mainContent = document.getElementById('main-content');
  if (!mainContent) return;

  const assetItemsHtml = assets.length > 0
    ? assets.map(asset => {
        const available = asset.trackingMode === 'quantity'
          ? `${portalDisplayQty(asset.quantity)} available`
          : (asset.serialNumber ? `SN: ${escapeHtml(asset.serialNumber)}` : '1 available');
        return `
          <div class="asset-selection-item" data-asset-id="${window.escAttr(asset.id)}">
            <input type="checkbox" id="asset-${window.escAttr(asset.id)}" onchange="window.toggleAssetSelection(${window.escAttr(JSON.stringify(String(asset.id)))})">
            <div class="asset-selection-info">
              <div class="asset-selection-name">${escapeHtml(asset.partNumber)} - ${escapeHtml(asset.name)}</div>
              <div class="asset-selection-detail">${getCategoryLabel(asset.category)}</div>
            </div>
            <input type="number" class="form-control asset-selection-qty" id="qty-${window.escAttr(asset.id)}"
              min="1" max="${asset.trackingMode === 'quantity' ? (portalDisplayQty(asset.quantity) || 1) : 1}"
              value="1" disabled placeholder="Qty">
            <div class="asset-selection-available">${available}</div>
          </div>
        `;
      }).join('')
    : '<p class="text-muted">No assets available for order at this time.</p>';

  mainContent.innerHTML = `
    <div class="order-request-page">
      <div class="order-request-header">
        <h1><span class="material-icons">local_shipping</span> ${escapeHtml(companyName)}</h1>
        <p>Request equipment for delivery</p>
      </div>

      <form id="order-request-form" novalidate onsubmit="window.submitOrderRequest(event)">
        <!-- TASK-1422 — token/customerId are URL-supplied and land in an ATTRIBUTE,
             so they need escAttr (full &<>"' set), not escapeHtml, which leaves
             quotes intact and would allow a breakout out of value="...". -->
        <input type="hidden" id="order-token" value="${window.escAttr(token)}">
        <input type="hidden" id="order-customer-id" value="${window.escAttr(customerId || '')}">

        <div class="order-request-section">
          <h2><span class="material-icons">inventory_2</span> Select Items</h2>
          <div class="asset-selection-list" id="asset-selection-list">
            ${assetItemsHtml}
          </div>
        </div>

        <div class="order-request-section">
          <h2><span class="material-icons">local_shipping</span> Delivery Details</h2>
          <div class="order-request-form">
            ${!customerId ? `
              <div class="form-group">
                <label for="order-customer-name">Company/Customer Name *</label>
                <input type="text" id="order-customer-name" class="form-control" required
                  placeholder="ABC Oil & Gas" value="${window.escAttr(customerName)}">
              </div>
            ` : `
              <div class="form-group">
                <label>Customer</label>
                <div class="form-control-static">${escapeHtml(customerName)}</div>
              </div>
            `}
            <div class="form-row">
              <div class="form-group">
                <label for="order-po-number">PO Number</label>
                <input type="text" id="order-po-number" class="form-control" placeholder="PO-2026-001">
              </div>
              <div class="form-group">
                <label for="order-requested-date">Requested Date *</label>
                <input type="date" id="order-requested-date" class="form-control" required>
              </div>
            </div>
            <div class="form-group">
              <label for="order-requested-time">Requested Time</label>
              <input type="time" id="order-requested-time" class="form-control" placeholder="2:00 PM">
            </div>
            <div class="form-group">
              <label for="order-location">Delivery Location *</label>
              <input type="text" id="order-location" class="form-control" required
                placeholder="123 Well Site Rd, Midland TX">
            </div>
            <div class="form-group">
              <label for="order-maps-url">Google Maps URL (optional)</label>
              <input type="url" id="order-maps-url" class="form-control"
                placeholder="https://maps.google.com/...">
            </div>
            <div class="form-group">
              <label for="order-notes">Notes (gate codes, instructions, etc.)</label>
              <textarea id="order-notes" class="form-control" rows="3"
                placeholder="Gate code: 1234, call when arriving..."></textarea>
            </div>
          </div>
        </div>

        <div class="form-actions" style="justify-content: center; margin-top: var(--spacing-lg);">
          <button type="submit" class="btn btn-primary btn-lg" id="btn-submit-order" ${assets.length === 0 ? 'disabled' : ''}>
            <span class="material-icons">send</span> Submit Order Request
          </button>
        </div>
      </form>
    </div>
  `;

  // Set default date to tomorrow
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const dateInput = document.getElementById('order-requested-date');
  if (dateInput) {
    dateInput.value = tomorrow.toISOString().split('T')[0];
    dateInput.min = new Date().toISOString().split('T')[0]; // Can't select past dates
  }
}

function getCategoryLabel(category) {
  if (window.getTypeLabel) return window.getTypeLabel(category, 'assetCategories') || category;
  const labels = {
    drill_pipe: 'Drill Pipe',
    equipment: 'Equipment',
    tool: 'Tool',
    other: 'Other'
  };
  return labels[category] || category || 'Other';
}

window.toggleAssetSelection = function(assetId) {
  const checkbox = document.getElementById(`asset-${assetId}`);
  const qtyInput = document.getElementById(`qty-${assetId}`);
  const item = checkbox?.closest('.asset-selection-item');

  if (checkbox && qtyInput && item) {
    qtyInput.disabled = !checkbox.checked;
    item.classList.toggle('selected', checkbox.checked);
    if (checkbox.checked) {
      qtyInput.focus();
    }
  }
};

window.submitOrderRequest = async function(e) {
  e.preventDefault();
  if (!window.validateRequiredFields(e.target)) return;

  const btn = document.getElementById('btn-submit-order');
  btn.disabled = true;
  btn.innerHTML = '<span class="material-icons rotating">sync</span> Submitting...';

  try {
    const token = document.getElementById('order-token').value;
    const tenantId = window.AppState.tenant;
    const customerId = document.getElementById('order-customer-id').value;

    // Collect selected items
    const items = [];
    document.querySelectorAll('.asset-selection-item').forEach(item => {
      const assetId = item.dataset.assetId;
      const checkbox = document.getElementById(`asset-${assetId}`);
      const qtyInput = document.getElementById(`qty-${assetId}`);

      if (checkbox?.checked) {
        const qty = parseInt(qtyInput?.value) || 1;
        const nameEl = item.querySelector('.asset-selection-name');
        const nameParts = nameEl?.textContent?.split(' - ') || ['', ''];
        items.push({
          assetId,
          partNumber: nameParts[0]?.trim() || '',
          name: nameParts[1]?.trim() || '',
          quantity: qty
        });
      }
    });

    if (items.length === 0) {
      showToast('Please select at least one item', 'error');
      btn.disabled = false;
      btn.innerHTML = '<span class="material-icons">send</span> Submit Order Request';
      return;
    }

    // Get customer name
    let customerName = '';
    const customerNameInput = document.getElementById('order-customer-name');
    if (customerNameInput) {
      customerName = customerNameInput.value.trim();
    } else {
      // Get from hidden field or token data
      try {
        const tokenDoc = await getDoc(doc(db, 'accessTokens', token));
        if (tokenDoc.exists() && tokenDoc.data().customerId) {
          const customerDoc = await getDoc(doc(db, 'tenants', tenantId, 'customers', tokenDoc.data().customerId));
          if (customerDoc.exists()) {
            customerName = customerDoc.data().name || '';
          }
        }
      } catch (err) {
        console.warn('[Order] Could not get customer name:', err);
      }
    }

    // Build order data
    const orderData = {
      customerId: customerId || null,
      customerName: customerName,
      poNumber: document.getElementById('order-po-number').value.trim(),
      requestedDate: Timestamp.fromDate(new Date(document.getElementById('order-requested-date').value)),
      requestedTime: document.getElementById('order-requested-time').value || null,
      location: document.getElementById('order-location').value.trim(),
      googleMapsUrl: document.getElementById('order-maps-url').value.trim() || null,
      notes: document.getElementById('order-notes').value.trim() || null,
      items: items,
      status: 'pending',
      statusHistory: [{
        status: 'pending',
        timestamp: Timestamp.now(),
        updatedBy: 'customer'
      }],
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      createdVia: 'customer_link',
      requestToken: token
    };

    // Save to Firestore
    const orderRef = await addDoc(collection(db, 'tenants', tenantId, 'orders'), orderData);
    console.log('[Order] Order created:', orderRef.id);

    // Show success page
    showOrderRequestSuccess(customerName);

  } catch (error) {
    console.error('[Order] Error submitting order:', error);
    showToast('Error submitting order. Please try again.', 'error');
    btn.disabled = false;
    btn.innerHTML = '<span class="material-icons">send</span> Submit Order Request';
  }
};

function showOrderRequestSuccess(customerName) {
  const mainContent = document.getElementById('main-content');
  if (!mainContent) return;

  mainContent.innerHTML = `
    <div class="order-request-page">
      <div class="order-request-success">
        <span class="material-icons success-icon">check_circle</span>
        <h2>Order Request Submitted!</h2>
        <p>Thank you${customerName ? ', ' + escapeHtml(customerName) : ''}. Your order request has been received.</p>
        <p style="margin-top: var(--spacing-lg);">You will be contacted when your order status changes.</p>
        <div style="margin-top: var(--spacing-xl);">
          <p class="text-muted">You can close this page now.</p>
        </div>
      </div>
    </div>
  `;
}

// ============================================
// TIME-OFF REQUEST PAGE (Public Employee Link)
// ============================================
async function loadTimeOffRequestPage(token) {
  try {
    // Set public access flag and auth BEFORE any Firestore reads
    window.AppState.isPublicAccess = true;
    try {
      await ensurePublicAuth();
    } catch (authErr) {
      console.error('[TimeOff] Auth error:', authErr);
      window.AppState.isPublicAccess = false;
      showAccessError('Unable to load time-off request page. Please try again.');
      return false;
    }

    // Look up token in Firestore
    const tokenDoc = await getDoc(doc(db, 'accessTokens', token));

    if (!tokenDoc.exists()) {
      console.log('[TimeOff] Token not found');
      showAccessError('This time-off request link is invalid or has been revoked.');
      return false;
    }

    const tokenData = tokenDoc.data();

    // Verify this is a timeoff_request token
    if (tokenData.type !== 'timeoff_request') {
      console.log('[TimeOff] Invalid token type:', tokenData.type);
      showAccessError('This link is not valid for time-off requests.');
      return false;
    }

    // Check expiration (null = never expires)
    if (tokenData.expiresAt !== null) {
      const now = new Date();
      const expiresAt = tokenData.expiresAt?.toDate?.() || new Date(tokenData.expiresAt);
      if (now > expiresAt) {
        console.log('[TimeOff] Token expired');
        showAccessError('This time-off request link has expired. Please contact your manager for a new one.');
        return false;
      }
    }

    // Token is valid! Grant membership
    console.log('[TimeOff] Token valid for tenant:', tokenData.tenantId);
    try {
      const grantMembership = httpsCallable(functions, 'grantAccessTokenMembership');
      const result = await grantMembership({ token });
      console.log('[TimeOff] Membership granted:', result.data);
    } catch (memberErr) {
      console.error('[TimeOff] Failed to grant membership:', memberErr);
    }

    window.AppState.tenant = tokenData.tenantId;
    window.AppState.timeOffRequestToken = token;

    // Hide standard UI elements
    const header = document.getElementById('app-header');
    const sidebar = document.getElementById('app-sidebar');
    const mobileNav = document.getElementById('mobile-nav');

    if (header) header.style.display = 'none';
    if (sidebar) sidebar.style.display = 'none';
    if (mobileNav) mobileNav.style.display = 'none';

    // Add body class for styling (reuses order-request-mode scroll behavior)
    document.body.classList.add('timeoff-request-mode');

    // Load tenant data for company name
    let companyName = 'Time Off Request';
    try {
      const tenantDoc = await _linkTenantDoc(token);
      if (tenantDoc.exists()) {
        companyName = tenantDoc.data().companyName || 'Time Off Request';
      }
    } catch (err) {
      console.warn('[TimeOff] Could not load tenant name:', err);
    }

    // Load active people for employee dropdown
    let people = [];
    try {
      // TASK-9856 -- id + name (+ policy keys) from the server, never whole people docs.
      const res = await httpsCallable(functions, 'getLinkPagePeople')({ token });
      people = (res && res.data && res.data.people) || [];
    } catch (err) {
      console.error('[TimeOff] Error loading people:', err);
    }

    // Load time-off policy for balance display
    let policy = null;
    let policyLoadFailed = false; // TASK-9838 COD r2 — a failed read is not "no plan"
    try {
      const policyDoc = await getDoc(doc(db, 'tenants', tokenData.tenantId, 'hrTimeOffPolicy', 'config'));
      if (policyDoc.exists()) policy = policyDoc.data();
    } catch (err) { policyLoadFailed = true; console.warn('[TimeOff] No policy found'); }

    // Load time-off requests for balance calculation + My Requests section
    let timeOffRequests = [];
    try {
      const toSnap = await getDocs(query(
        collection(db, 'tenants', tokenData.tenantId, 'timeOff'),
        where('status', 'in', ['approved', 'pending'])
      ));
      timeOffRequests = toSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (err) { console.warn('[TimeOff] Could not load time-off requests'); }

    // Store for balance check function + My Requests
    window._timeOffPageData = { people, policy, policyLoadFailed, timeOffRequests, tenantId: tokenData.tenantId };

    // Render the time-off request page
    hideLoading();
    renderTimeOffRequestPage(companyName, people, policy);

    return true;
  } catch (error) {
    console.error('[TimeOff] Error loading time-off request page:', error);
    showAccessError('Unable to load time-off request page. Please try again.');
    return false;
  }
}

function renderTimeOffRequestPage(companyName, people, policy) {
  const mainContent = document.getElementById('main-content');
  if (!mainContent) return;

  // Default dates: start = tomorrow, end = tomorrow
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const defaultDate = tomorrow.toISOString().split('T')[0];

  const peopleOptions = people.length > 0
    ? people.map(p => `<option value="${window.escAttr(p.id)}">${escapeHtml(p.name)}</option>`).join('')
    : '<option value="" disabled>No employees found</option>';

  // Only show balance section if a policy exists
  const balanceSectionHtml = policy?.levels?.length ? `
        <div class="order-request-section" id="timeoff-balance-section">
          <h2><span class="material-icons">event_available</span> Your Time-Off Balance</h2>
          <div id="timeoff-balance-locked">
            <p style="font-size:0.85rem;color:var(--text-secondary);margin-bottom:var(--spacing-sm);">
              Enter your driver PIN to view your remaining time off (your private PIN, or your name + phone code if you haven't set one)
            </p>
            <div style="display:flex;gap:var(--spacing-sm);align-items:center;">
              <input type="password" id="timeoff-balance-pin" class="form-control" autocomplete="off"
                placeholder="Your PIN" maxlength="8"
                style="max-width:140px;text-transform:uppercase;letter-spacing:1px;">
              <button type="button" class="btn btn-primary"
                onclick="window.checkTimeOffBalancePin()"
                style="padding:8px 20px;font-size:0.9rem;">
                <span class="material-icons">visibility</span> View My Balance
              </button>
            </div>
          </div>
          <div id="timeoff-balance-result" style="display:none;"></div>
        </div>` : '';

  mainContent.innerHTML = `
    <div class="order-request-page">
      <div class="order-request-header">
        <h1><span class="material-icons">event_busy</span> ${escapeHtml(companyName)}</h1>
        <p>Request Time Off</p>
      </div>

      <form id="timeoff-request-form" novalidate onsubmit="window.submitTimeOffPublicRequest(event)">

        <div class="order-request-section">
          <h2><span class="material-icons">person</span> Who are you?</h2>
          <div class="form-group">
            <label for="timeoff-person">Select your name *</label>
            <select id="timeoff-person" class="form-control" required
              onchange="window.resetTimeOffBalance();window.applyPublicTimeOffTypeOptions()">
              <option value="">-- Select --</option>
              ${peopleOptions}
            </select>
          </div>
        </div>

        ${balanceSectionHtml}

        <div class="order-request-section">
          <h2><span class="material-icons">date_range</span> Time Off Details</h2>
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: var(--spacing-md);">
            <div class="form-group">
              <label for="timeoff-start">Start Date *</label>
              <input type="date" id="timeoff-start" class="form-control" required value="${defaultDate}"
                onchange="var _e=document.getElementById('timeoff-end');if(_e&&this.value&&_e.value<this.value)_e.value=this.value;window.updatePublicTimeOffHours()">
            </div>
            <div class="form-group">
              <label for="timeoff-end">End Date *</label>
              <input type="date" id="timeoff-end" class="form-control" required value="${defaultDate}"
                onchange="window.updatePublicTimeOffHours()">
            </div>
          </div>
          <div class="form-group">
            <label for="timeoff-hours">Hours *</label>
            <div style="display:flex;align-items:center;gap:var(--spacing-sm);">
              <input type="number" id="timeoff-hours" class="form-control" min="1" max="2000" value="8" required
                style="max-width:100px;">
              <span id="timeoff-hours-hint" style="font-size:0.8rem;color:var(--text-secondary);">1 day = 8 hrs</span>
            </div>
          </div>
          <div class="form-group">
            <label for="timeoff-type">Type *</label>
            <select id="timeoff-type" class="form-control" required onchange="document.getElementById('public-bereavement-sub-group').style.display = this.value === 'bereavement' ? '' : 'none';">
              <!-- TASK-9838: built per person by applyPublicTimeOffTypeOptions — Unpaid only without a plan -->
              <option value="unpaid">Unpaid</option>
            </select>
            <div id="timeoff-type-hint" style="display:none;margin-top:6px;font-size:0.8rem;color:var(--text-secondary);"></div>
          </div>
          <div id="public-bereavement-sub-group" style="display:none;">
            <div class="form-group">
              <label for="public-bereavement-level">Relationship Level *</label>
              <select id="public-bereavement-level" class="form-control" onchange="window.autofillBereavementHoursPublic()">
                <option value="">Select relationship...</option>
                <option value="level1">Level 1 — Spouse, child, parent, grandchild</option>
                <option value="level2">Level 2 — Sibling, in-laws</option>
                <option value="level3">Level 3 — Household member</option>
                <option value="level4">Level 4 — Grandparent, grandparent-in-law</option>
                <option value="level5">Level 5 — Loss of pregnancy</option>
                <option value="level6">Level 6 — Aunt, uncle, cousin, niece, nephew</option>
                <option value="level7">Level 7 — Distant relative, close friend</option>
              </select>
            </div>
            <div class="form-group">
              <label style="display:flex;align-items:center;gap:var(--spacing-sm);cursor:pointer;">
                <input type="checkbox" id="public-bereavement-travel" onchange="window.autofillBereavementHoursPublic()">
                <span style="font-size:0.85rem;">Travel days (funeral 250+ miles away)</span>
              </label>
            </div>
          </div>
          <div class="form-group">
            <label for="timeoff-reason">Reason/Notes (optional)</label>
            <textarea id="timeoff-reason" class="form-control" rows="3" placeholder="Any details you'd like to share..."></textarea>
          </div>
        </div>

        <div style="text-align: center; margin-top: var(--spacing-lg);">
          <button type="submit" id="btn-submit-timeoff" class="btn btn-primary" style="min-width: 200px; padding: var(--spacing-sm) var(--spacing-lg);">
            <span class="material-icons">send</span> Submit Request
          </button>
        </div>
      </form>

      <div id="timeoff-my-requests" style="display:none;">
        <div class="order-request-section">
          <h2><span class="material-icons">history</span> My Requests</h2>
          <p style="font-size:0.8rem;color:var(--text-secondary);margin-bottom:var(--spacing-md);">Your recent time-off requests. Need to cancel or change an approved request? Use the button below.</p>
          <div id="timeoff-my-requests-list"></div>
        </div>
      </div>
    </div>
  `;
}

// >>> TASK-9838 no time-off plan = Unpaid only (public time-off link page) — BEGIN
// The person picked must have a Time-Off Level that exists in the company plan
// for the paid types to show (window.TimeOffBalance.hasPlan — the same rule as
// the office form and the driver modal). No plan / no level / no person picked
// = Unpaid only. No Bereavement without a plan (Chris 10/13).
const PUBLIC_TIMEOFF_ALL_TYPES = [
  ['unpaid', 'Unpaid'], ['vacation', 'Vacation'], ['sick', 'Sick Leave'], ['personal', 'Personal Day'],
  ['bereavement', 'Bereavement'], ['parental_leave', 'Parental Leave'], ['jury_duty', 'Jury Duty'], ['other', 'Other']
];
function _publicTimeOffHasPlan(personId) {
  const data = window._timeOffPageData;
  const person = personId && data && Array.isArray(data.people) ? data.people.find(p => p.id === personId) : null;
  const policy = data ? data.policy : null;
  const TOB = window.TimeOffBalance;
  if (TOB && typeof TOB.hasPlan === 'function') return TOB.hasPlan(person, policy);
  const levelId = person && person.hrTimeOffLevel;
  return !!levelId && !!policy && Array.isArray(policy.levels) && policy.levels.some(l => l && l.id === levelId);
}
// Options for a type select. `keepType` (an edited request's own type) stays
// selectable when no longer offered, so saving an edit never rewrites it.
function _publicTimeOffTypeOptions(hasPlan, keepType) {
  const opts = (hasPlan ? PUBLIC_TIMEOFF_ALL_TYPES : PUBLIC_TIMEOFF_ALL_TYPES.filter(([v]) => v === 'unpaid')).slice();
  if (keepType && !opts.some(([v]) => v === keepType)) {
    const known = PUBLIC_TIMEOFF_ALL_TYPES.find(([v]) => v === keepType);
    opts.push([keepType, `${known ? known[1] : keepType} (no longer offered)`]);
  }
  return opts;
}
window.applyPublicTimeOffTypeOptions = function() {
  const sel = document.getElementById('timeoff-type');
  if (!sel) return;
  const personId = document.getElementById('timeoff-person')?.value || '';
  const hasPlan = _publicTimeOffHasPlan(personId);
  const opts = _publicTimeOffTypeOptions(hasPlan, null);
  const wanted = sel.value || 'unpaid';
  sel.innerHTML = opts.map(([v, l]) => `<option value="${window.escAttr(v)}">${escapeHtml(l)}</option>`).join('');
  sel.value = opts.some(([v]) => v === wanted) ? wanted : 'unpaid';
  const ber = document.getElementById('public-bereavement-sub-group');
  if (ber) ber.style.display = sel.value === 'bereavement' ? '' : 'none';
  const hint = document.getElementById('timeoff-type-hint');
  if (hint) {
    const noPlan = !!personId && !hasPlan;
    const loadFailed = !!(window._timeOffPageData && window._timeOffPageData.policyLoadFailed);
    hint.textContent = !noPlan ? ''
      : loadFailed ? "Couldn't load your company's time-off plan, so only Unpaid shows. Reload the page to try again."
        : 'No time-off plan on your profile, so requests are Unpaid.';
    hint.style.display = noPlan ? '' : 'none';
  }
};
// <<< TASK-9838 — END

window.submitTimeOffPublicRequest = async function(e) {
  e.preventDefault();
  if (!window.validateRequiredFields(e.target)) return;

  const btn = document.getElementById('btn-submit-timeoff');
  btn.disabled = true;
  btn.innerHTML = '<span class="material-icons rotating">sync</span> Submitting...';

  try {
    const tenantId = window.AppState.tenant;
    const personId = document.getElementById('timeoff-person').value;
    const startDate = document.getElementById('timeoff-start').value;
    const endDate = document.getElementById('timeoff-end').value;
    const hours = parseInt(document.getElementById('timeoff-hours')?.value) || 8;
    const type = document.getElementById('timeoff-type').value || 'unpaid';
    const reason = document.getElementById('timeoff-reason').value.trim();

    if (!personId) {
      showToast('Please select your name', 'error');
      btn.disabled = false;
      btn.innerHTML = '<span class="material-icons">send</span> Submit Request';
      return;
    }

    if (!startDate || !endDate) {
      showToast('Please select start and end dates', 'error');
      btn.disabled = false;
      btn.innerHTML = '<span class="material-icons">send</span> Submit Request';
      return;
    }

    if (new Date(endDate) < new Date(startDate)) {
      showToast('End date cannot be before start date', 'error');
      btn.disabled = false;
      btn.innerHTML = '<span class="material-icons">send</span> Submit Request';
      return;
    }

    // Get the person's name from the dropdown
    const personSelect = document.getElementById('timeoff-person');
    const personName = personSelect.options[personSelect.selectedIndex].text;

    // Submit via Cloud Function (bypasses Firestore rules for anonymous users)
    const token = window.AppState.timeOffRequestToken;
    const submitRequest = httpsCallable(functions, 'submitTimeOffRequest');
    const requestPayload = {
      token,
      personId,
      personName,
      startDate: startDate + 'T12:00:00Z',
      endDate: endDate + 'T12:00:00Z',
      hours,
      type,
      reason
    };

    if (type === 'bereavement') {
      const bereavementLevel = document.getElementById('public-bereavement-level')?.value || '';
      const bereavementTravel = document.getElementById('public-bereavement-travel')?.checked || false;
      if (bereavementLevel) requestPayload.bereavementLevel = bereavementLevel;
      if (bereavementTravel) requestPayload.bereavementTravel = true;
    }

    const result = await submitRequest(requestPayload);

    console.log('[TimeOff] Request created:', result.data?.requestId);

    // Show success page
    showTimeOffRequestSuccess(personName);

  } catch (error) {
    console.error('[TimeOff] Error submitting request:', error);
    showToast(timeOffErrorMessage(error, 'Error submitting request. Please try again.'), 'error');
    btn.disabled = false;
    btn.innerHTML = '<span class="material-icons">send</span> Submit Request';
  }
};

// Auto-fill bereavement hours from policy on public form
window.autofillBereavementHoursPublic = function() {
  const level = document.getElementById('public-bereavement-level')?.value;
  const travel = document.getElementById('public-bereavement-travel')?.checked;
  const hoursInput = document.getElementById('timeoff-hours');
  const hint = document.getElementById('timeoff-hours-hint');
  if (!level || !hoursInput) return;

  const pageData = window._timeOffPageData;
  if (!pageData?.policy?.levels?.length || !pageData?.people?.length) return;

  const personId = document.getElementById('timeoff-person')?.value;
  const person = personId ? pageData.people.find(p => p.id === personId) : null;
  if (!person?.hrTimeOffLevel || !person?.hrEmploymentType) return;

  const policyLevel = pageData.policy.levels.find(l => l.id === person.hrTimeOffLevel);
  if (!policyLevel) return;
  const allowances = policyLevel[person.hrEmploymentType] || {};
  const bObj = (typeof allowances.bereavement === 'object' && allowances.bereavement !== null)
    ? allowances.bereavement : null;
  if (!bObj) return;

  const bUnit = pageData.policy.bereavementUnit || 'days';
  let val = bObj[level] || 0;
  if (travel && bObj.travelDays) val += bObj.travelDays;
  const hrs = bUnit === 'hours' ? val : val * 8;
  hoursInput.value = hrs;
  if (hint) {
    hint.textContent = bUnit === 'hours'
      ? `${val} hr${val !== 1 ? 's' : ''} (per policy)`
      : `${val} day${val !== 1 ? 's' : ''} = ${hrs} hrs (per policy)`;
  }
};

// Driver PIN formula (duplicated here since roster.js isn't loaded on public pages)
function calculateDriverPinPublic(name, phone) {
  if (!name || !phone) return null;
  const firstName = name.trim().split(/\s+/)[0] || '';
  const letters = firstName.substring(0, 2).toUpperCase();
  if (letters.length < 2) return null;
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 4) return null;
  return letters + digits.slice(-4);
}

// Time-off category labels (subset for public page)
const TIMEOFF_CATEGORY_LABELS = {
  vacation: 'Vacation', sick: 'Sick Leave', personal: 'Personal Day',
  unpaid: 'Unpaid', bereavement: 'Bereavement', parental_leave: 'Parental Leave',
  jury_duty: 'Jury Duty', other: 'Other'
};

// Bereavement tiers for public balance display
const BEREAVEMENT_TIERS_PUBLIC = [
  { id: 'level1', label: 'Level 1', desc: 'Spouse, child, parent, grandchild' },
  { id: 'level2', label: 'Level 2', desc: 'Sibling, in-laws' },
  { id: 'level3', label: 'Level 3', desc: 'Household member' },
  { id: 'level4', label: 'Level 4', desc: 'Grandparent, grandparent-in-law' },
  { id: 'level5', label: 'Level 5', desc: 'Loss of pregnancy' },
  { id: 'level6', label: 'Level 6', desc: 'Aunt, uncle, cousin, niece, nephew' },
  { id: 'level7', label: 'Level 7', desc: 'Distant relative, close friend' },
];

function sumBereavementDaysPublic(bObj) {
  if (typeof bObj === 'number') return bObj;
  if (typeof bObj !== 'object' || bObj === null) return 0;
  return BEREAVEMENT_TIERS_PUBLIC.reduce((s, t) => s + (bObj[t.id] || 0), 0);
}

window.updatePublicTimeOffHours = function() {
  const startVal = document.getElementById('timeoff-start')?.value;
  const endVal = document.getElementById('timeoff-end')?.value;
  const hoursInput = document.getElementById('timeoff-hours');
  const hint = document.getElementById('timeoff-hours-hint');
  if (!startVal || !endVal || !hoursInput) return;
  const start = new Date(startVal + 'T00:00:00');
  const end = new Date(endVal + 'T00:00:00');
  if (end < start) return;
  const days = Math.max(1, Math.round((end - start) / (24 * 60 * 60 * 1000)) + 1);
  hoursInput.value = days * 8;
  if (hint) hint.textContent = `${days} day${days !== 1 ? 's' : ''} = ${days * 8} hrs`;
};

window.resetTimeOffBalance = function() {
  const locked = document.getElementById('timeoff-balance-locked');
  const result = document.getElementById('timeoff-balance-result');
  const pinInput = document.getElementById('timeoff-balance-pin');
  if (locked) locked.style.display = '';
  if (result) { result.style.display = 'none'; result.innerHTML = ''; }
  if (pinInput) pinInput.value = '';
  // Also refresh My Requests section
  renderMyTimeOffRequests();
};

// TASK-1426 — a denial has to LAND somewhere the person understands. The
// time-off callables now reject an EXPIRED access token server-side, but all
// three catch blocks below showed a generic "Error submitting request. Please
// try again." — so someone on a dead link would retry forever and never learn
// the link is the problem. Surface the server's own sentence for the cases the
// user can act on; keep the generic fallback for genuine faults, which the user
// can do nothing about anyway.
function timeOffErrorMessage(err, fallback) {
  const code = String(err?.code || '');
  const msg = String(err?.message || '');
  if (code.includes('failed-precondition') || code.includes('not-found') || code.includes('permission-denied')
      || code.includes('invalid-argument') || code.includes('resource-exhausted')) {
    return msg || fallback;
  }
  if (/expired|link/i.test(msg)) return msg;
  return fallback;
}

function renderMyTimeOffRequests() {
  const container = document.getElementById('timeoff-my-requests');
  const list = document.getElementById('timeoff-my-requests-list');
  if (!container || !list) return;

  const personId = document.getElementById('timeoff-person')?.value;
  const data = window._timeOffPageData;
  if (!personId || !data?.timeOffRequests) {
    container.style.display = 'none';
    return;
  }

  const catLabels = { vacation: 'Vacation', sick: 'Sick Leave', personal: 'Personal', unpaid: 'Unpaid',
    bereavement: 'Bereavement', parental_leave: 'Parental Leave', jury_duty: 'Jury Duty', other: 'Other' };
  const statusColors = { approved: 'background:#DCFCE7;color:#166534;', pending: 'background:#FEF3C7;color:#92400E;', denied: 'background:#FEE2E2;color:#991B1B;' };

  const myReqs = data.timeOffRequests
    .filter(r => r.personId === personId)
    .sort((a, b) => {
      const aDate = a.startDate?.toDate?.() || new Date(a.startDate);
      const bDate = b.startDate?.toDate?.() || new Date(b.startDate);
      return bDate - aDate;
    })
    .slice(0, 10);

  if (myReqs.length === 0) {
    container.style.display = 'none';
    return;
  }

  container.style.display = '';
  const fmtDate = (d) => {
    const dt = d?.toDate?.() || new Date(d);
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  list.innerHTML = myReqs.map(r => {
    const start = fmtDate(r.startDate);
    const end = fmtDate(r.endDate);
    const dateRange = start === end ? start : `${start} — ${end}`;
    const isChangeReq = r.changeRequestFor;
    // Edit/Withdraw go through modifyTimeOffRequest, which only accepts the
    // session that filed the request (submittedByUid) — the server owns that
    // check; this just avoids offering buttons that would be refused. Any other
    // pending row (office-filed, another device, legacy) gets Request Change.
    const filedHere = !!r.submittedByUid && r.submittedByUid === auth.currentUser?.uid;
    let actionBtns = '';
    if (r.status === 'pending' && !isChangeReq && filedHere) {
      actionBtns = `<div style="display:flex;gap:6px;margin-top:6px;">
        <button class="btn btn-secondary btn-sm" onclick="window.showTimeOffEditForm('${r.id}')" style="font-size:0.75rem;padding:4px 10px;">
          <span class="material-icons" style="font-size:14px;">edit</span> Edit
        </button>
        <button class="btn btn-secondary btn-sm" onclick="window.withdrawTimeOffRequest('${r.id}')" style="font-size:0.75rem;padding:4px 10px;color:#dc2626;border-color:#fca5a5;">
          <span class="material-icons" style="font-size:14px;">close</span> Withdraw
        </button>
      </div>`;
    } else if (r.status === 'approved' || (r.status === 'pending' && !isChangeReq)) {
      actionBtns = `<button class="btn btn-secondary btn-sm" onclick="window.showTimeOffChangeForm('${r.id}')" style="font-size:0.75rem;padding:4px 10px;margin-top:6px;">
          <span class="material-icons" style="font-size:14px;">edit_note</span> Request Change
        </button>`;
    }
    return `
      <div style="border:1px solid var(--border-light);border-radius:8px;padding:12px;margin-bottom:8px;background:var(--bg-page);">
        <div style="display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:4px;">
          <span style="font-weight:600;font-size:0.85rem;">${escapeHtml(dateRange)}</span>
          <span style="font-size:0.7rem;padding:2px 8px;border-radius:4px;font-weight:600;${statusColors[r.status] || ''}">${isChangeReq ? 'Change ' : ''}${(r.status || 'pending').charAt(0).toUpperCase() + (r.status || 'pending').slice(1)}</span>
        </div>
        <div style="font-size:0.8rem;color:var(--text-secondary);margin-top:4px;">
          ${escapeHtml(catLabels[r.type] || r.type || 'Unpaid')} · ${escapeHtml(String(r.hours || 8))} hrs
          ${r.reason ? ' · ' + escapeHtml(r.reason.substring(0, 60)) + (r.reason.length > 60 ? '…' : '') : ''}
          ${isChangeReq ? '<br><span style="color:var(--brand-blue);font-weight:500;">Change request</span>' : ''}
        </div>
        ${actionBtns}
        <div id="timeoff-edit-form-${r.id}" style="display:none;"></div>
        <div id="timeoff-change-form-${r.id}" style="display:none;"></div>
      </div>`;
  }).join('');
}

window.showTimeOffChangeForm = function(requestId) {
  const formEl = document.getElementById(`timeoff-change-form-${requestId}`);
  if (!formEl) return;

  if (formEl.style.display !== 'none') {
    formEl.style.display = 'none';
    return;
  }

  formEl.style.display = '';
  formEl.innerHTML = `
    <div style="margin-top:8px;padding:10px;background:var(--bg-secondary);border-radius:6px;">
      <label style="font-size:0.8rem;font-weight:600;display:block;margin-bottom:4px;">What would you like to change?</label>
      <select id="timeoff-change-type-${requestId}" class="form-control" style="font-size:0.85rem;margin-bottom:8px;">
        <option value="modify">Change dates or hours</option>
        <option value="cancel">Cancel this time off</option>
      </select>
      <label style="font-size:0.8rem;font-weight:600;display:block;margin-bottom:4px;">Details</label>
      <textarea id="timeoff-change-reason-${requestId}" class="form-control" rows="2" placeholder="e.g., Need to move to next week instead..." style="font-size:0.85rem;margin-bottom:8px;"></textarea>
      <div style="display:flex;gap:8px;">
        <button class="btn btn-secondary btn-sm" onclick="document.getElementById('timeoff-change-form-${requestId}').style.display='none'" style="font-size:0.8rem;">Cancel</button>
        <button class="btn btn-primary btn-sm" onclick="window.submitTimeOffChangeRequest('${requestId}')" style="font-size:0.8rem;">
          <span class="material-icons" style="font-size:14px;">send</span> Submit
        </button>
      </div>
    </div>`;
};

window.submitTimeOffChangeRequest = async function(requestId) {
  const data = window._timeOffPageData;
  if (!data) return;

  const personId = document.getElementById('timeoff-person')?.value;
  const personSelect = document.getElementById('timeoff-person');
  const personName = personSelect?.options[personSelect.selectedIndex]?.text || '';
  const changeType = document.getElementById(`timeoff-change-type-${requestId}`)?.value || 'modify';
  const reason = document.getElementById(`timeoff-change-reason-${requestId}`)?.value?.trim() || '';

  if (!reason) {
    showToast('Please provide details about the change', 'error');
    return;
  }

  const originalReq = data.timeOffRequests.find(r => r.id === requestId);
  if (!originalReq) return;

  try {
    const token = window.AppState.timeOffRequestToken;
    const submitRequest = httpsCallable(functions, 'submitTimeOffRequest');
    const result = await submitRequest({
      token,
      personId,
      personName,
      startDate: originalReq.startDate?.toDate?.()?.toISOString() || originalReq.startDate,
      endDate: originalReq.endDate?.toDate?.()?.toISOString() || originalReq.endDate,
      hours: originalReq.hours || 8,
      type: originalReq.type || 'unpaid',
      reason: (changeType === 'cancel' ? '[CANCELLATION REQUEST] ' : '[CHANGE REQUEST] ') + reason,
      changeRequestFor: requestId,
      changeType
    });

    showToast('Change request submitted', 'success');
    // Hide the form
    const formEl = document.getElementById(`timeoff-change-form-${requestId}`);
    if (formEl) formEl.style.display = 'none';

    // Add to local data so it shows immediately
    data.timeOffRequests.push({
      id: result.data?.requestId || 'temp',
      personId,
      personName,
      startDate: originalReq.startDate,
      endDate: originalReq.endDate,
      hours: originalReq.hours,
      type: originalReq.type,
      reason: (changeType === 'cancel' ? '[CANCELLATION REQUEST] ' : '[CHANGE REQUEST] ') + reason,
      status: 'pending',
      changeRequestFor: requestId,
      changeType,
      createdAt: new Date()
    });
    renderMyTimeOffRequests();
  } catch (err) {
    console.error('[TimeOff] Change request error:', err);
    showToast(timeOffErrorMessage(err, 'Error submitting change request'), 'error');
  }
};

window.withdrawTimeOffRequest = async function(requestId) {
  if (!confirm('Withdraw this time-off request?')) return;

  const data = window._timeOffPageData;
  const personId = document.getElementById('timeoff-person')?.value;
  if (!data || !personId) return;

  try {
    const token = window.AppState.timeOffRequestToken;
    const modifyRequest = httpsCallable(functions, 'modifyTimeOffRequest');
    await modifyRequest({ token, requestId, personId, action: 'withdraw' });

    showToast('Request withdrawn', 'success');
    // Remove from local data
    data.timeOffRequests = data.timeOffRequests.filter(r => r.id !== requestId);
    renderMyTimeOffRequests();
  } catch (err) {
    console.error('[TimeOff] Withdraw error:', err);
    showToast(timeOffErrorMessage(err, 'Error withdrawing request'), 'error');
  }
};

window.showTimeOffEditForm = function(requestId) {
  const formEl = document.getElementById(`timeoff-edit-form-${requestId}`);
  if (!formEl) return;

  if (formEl.style.display !== 'none') {
    formEl.style.display = 'none';
    return;
  }

  const data = window._timeOffPageData;
  const req = data?.timeOffRequests?.find(r => r.id === requestId);
  if (!req) return;

  const startDate = req.startDate?.toDate?.() || new Date(req.startDate);
  const endDate = req.endDate?.toDate?.() || new Date(req.endDate);
  const startStr = startDate.toISOString().split('T')[0];
  const endStr = endDate.toISOString().split('T')[0];

  // TASK-9838 — same plan gate as the new-request form; the request's own type is kept.
  const typeOptions = _publicTimeOffTypeOptions(_publicTimeOffHasPlan(document.getElementById('timeoff-person')?.value || ''), req.type || 'unpaid')
    .map(([v,l]) => `<option value="${window.escAttr(v)}" ${req.type === v ? 'selected' : ''}>${escapeHtml(l)}</option>`).join('');

  formEl.style.display = '';
  formEl.innerHTML = `
    <div style="margin-top:8px;padding:12px;background:var(--bg-secondary);border-radius:6px;">
      <div style="display:flex;gap:8px;margin-bottom:8px;">
        <div style="flex:1;">
          <label style="font-size:0.75rem;font-weight:600;display:block;margin-bottom:2px;">Start Date</label>
          <input type="date" id="timeoff-edit-start-${requestId}" class="form-control" value="${startStr}" style="font-size:0.85rem;">
        </div>
        <div style="flex:1;">
          <label style="font-size:0.75rem;font-weight:600;display:block;margin-bottom:2px;">End Date</label>
          <input type="date" id="timeoff-edit-end-${requestId}" class="form-control" value="${endStr}" style="font-size:0.85rem;">
        </div>
      </div>
      <div style="display:flex;gap:8px;margin-bottom:8px;">
        <div style="flex:1;">
          <label style="font-size:0.75rem;font-weight:600;display:block;margin-bottom:2px;">Hours</label>
          <input type="number" id="timeoff-edit-hours-${requestId}" class="form-control" value="${req.hours || 8}" min="1" max="120" style="font-size:0.85rem;">
        </div>
        <div style="flex:1;">
          <label style="font-size:0.75rem;font-weight:600;display:block;margin-bottom:2px;">Type</label>
          <select id="timeoff-edit-type-${requestId}" class="form-control" style="font-size:0.85rem;">${typeOptions}</select>
        </div>
      </div>
      <div style="margin-bottom:8px;">
        <label style="font-size:0.75rem;font-weight:600;display:block;margin-bottom:2px;">Reason/Notes</label>
        <textarea id="timeoff-edit-reason-${requestId}" class="form-control" rows="2" style="font-size:0.85rem;">${escapeHtml(req.reason || '')}</textarea>
      </div>
      <div style="display:flex;gap:8px;">
        <button class="btn btn-secondary btn-sm" onclick="document.getElementById('timeoff-edit-form-${requestId}').style.display='none'" style="font-size:0.8rem;">Cancel</button>
        <button class="btn btn-primary btn-sm" id="timeoff-edit-save-${requestId}" onclick="window.saveTimeOffEdit('${requestId}')" style="font-size:0.8rem;">
          <span class="material-icons" style="font-size:14px;">save</span> Save
        </button>
      </div>
    </div>`;
};

window.saveTimeOffEdit = async function(requestId) {
  const data = window._timeOffPageData;
  const personId = document.getElementById('timeoff-person')?.value;
  if (!data || !personId) return;

  const startDate = document.getElementById(`timeoff-edit-start-${requestId}`)?.value;
  const endDate = document.getElementById(`timeoff-edit-end-${requestId}`)?.value;
  const hours = document.getElementById(`timeoff-edit-hours-${requestId}`)?.value;
  const type = document.getElementById(`timeoff-edit-type-${requestId}`)?.value;
  const reason = document.getElementById(`timeoff-edit-reason-${requestId}`)?.value?.trim();

  if (!startDate || !endDate) {
    showToast('Please select dates', 'error');
    return;
  }
  if (endDate < startDate) {
    showToast('End date cannot be before start date', 'error');
    return;
  }

  const btn = document.getElementById(`timeoff-edit-save-${requestId}`);
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Saving...'; }

  try {
    const token = window.AppState.timeOffRequestToken;
    const modifyRequest = httpsCallable(functions, 'modifyTimeOffRequest');
    await modifyRequest({
      token, requestId, personId, action: 'update',
      updates: { startDate: startDate + 'T12:00:00Z', endDate: endDate + 'T12:00:00Z', hours: parseInt(hours) || 8, type, reason }
    });

    showToast('Request updated', 'success');

    // Update local data
    const req = data.timeOffRequests.find(r => r.id === requestId);
    if (req) {
      req.startDate = new Date(startDate + 'T12:00:00Z');
      req.endDate = new Date(endDate + 'T12:00:00Z');
      req.hours = parseInt(hours) || 8;
      req.type = type;
      req.reason = reason;
    }
    renderMyTimeOffRequests();
  } catch (err) {
    console.error('[TimeOff] Edit error:', err);
    showToast(timeOffErrorMessage(err, 'Error updating request'), 'error');
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:14px;">save</span> Save'; }
  }
};

window.checkTimeOffBalancePin = async function() {
  const data = window._timeOffPageData;
  if (!data) return;

  const personId = document.getElementById('timeoff-person')?.value;
  if (!personId) { showToast('Please select your name first', 'error'); return; }

  const person = data.people.find(p => p.id === personId);
  if (!person) { showToast('Employee not found', 'error'); return; }

  const enteredPin = (document.getElementById('timeoff-balance-pin')?.value || '').trim().toUpperCase();
  if (!enteredPin) { showToast('Please enter your PIN', 'error'); return; }

  // TASK-1441 Phase 0b — this code deliberately does NOT read `person.driverPin`.
  //
  // This page is PUBLIC and token-addressed, and it loads the whole active roster
  // with a bulk getDocs that spreads `...doc.data()`. Anyone holding the emailed
  // link therefore receives every active employee's full person doc. That is the
  // WIDEST instance of the plaintext-PIN leak — wider than the driver-session one,
  // because the link gets forwarded.
  //
  // 🔴 BE PRECISE ABOUT WHAT IS AND IS NOT FIXED (COD Adversary 🔴, 2nd pass on
  // the integrated diff — an earlier version of this comment overstated it):
  //   • Dropping this read does NOT by itself remove the PIN from the payload. A
  //     client cannot self-redact a spread object.
  //   • Throughout the SOAK the parent `people` doc still carries `driverPin`, by
  //     design (rules do not delete data; readers merge vault-over-parent so a
  //     half-migrated roster still authenticates). During that window this page
  //     STILL leaks it, exactly as before — devtools on
  //     `window._timeOffPageData.people[i].driverPin` still works.
  //   • What actually closes it is `scripts/migrate-people-1441b.cjs --delete`,
  //     the LAST step of the migration, which strips the parent copies per tenant.
  //     Until every tenant has been deleted, treat this page as still leaking.
  //   • The durable fix for the page itself is a server-side field projection for
  //     these public token surfaces, so the whole person doc never reaches an
  //     anonymous client at all. Tracked as Phase 1 — the doc also carries DOB,
  //     home address and DL numbers, which this change does not move.
  //
  // 🔴 DO NOT "restore" a stored-PIN read here — it would re-couple this public
  // page to the credential after the delete step finally removes it.
  //
  // This gate was never a security control: it compared user input against a
  // value the browser already had, so anyone who could fail it could also read
  // the answer out of devtools. It stays as a soft speed bump on the computed
  // code. MEASURED COST (live census 2026-08-31): 21 people platform-wide have a
  // stored code that differs from their computed one — spread over 20 tenants,
  // none in trippin-trucking or show-account — and they must now use the
  // computed code here. Anyone with a custom PIN was ALREADY on this path, since
  // setDriverPin deletes the plaintext.
  // TASK-9856 -- the page no longer holds anyone's name + phone; the code is
  // checked server-side (checkLinkPagePin, rate-limited per link).
  let pinOk = false;
  let pinReason = null;
  try {
    const res = await httpsCallable(functions, 'checkLinkPagePin')({ token: window.AppState.timeOffRequestToken, pin: enteredPin, personId });
    pinOk = !!(res && res.data && res.data.ok);
    pinReason = (res && res.data && res.data.reason) || null;
  } catch (err) {
    showToast(timeOffErrorMessage(err, 'Could not check your code. Try again.'), 'error');
    return;
  }
  if (!pinOk) {
    // TASK-9941 F S2 — the server says why only when it tells the person what to do.
    const why = pinReason;
    showToast(why === 'use_private_pin' ? 'Use your private PIN, not the name + phone code.'
      : why === 'standard_code_off' ? 'Name + phone codes are turned off. Use your private PIN.'
      : 'Invalid PIN', 'error');
    return;
  }

  // PIN matched — compute and display balance
  const policy = data.policy;
  const locked = document.getElementById('timeoff-balance-locked');
  const result = document.getElementById('timeoff-balance-result');
  if (locked) locked.style.display = 'none';
  if (!result) return;

  if (!policy?.levels?.length || !person.hrTimeOffLevel || !person.hrEmploymentType) {
    result.style.display = '';
    result.innerHTML = '<p style="font-size:0.85rem;color:var(--text-secondary);">No time-off policy assigned to your profile. Contact your supervisor.</p>';
    return;
  }

  const level = policy.levels.find(l => l.id === person.hrTimeOffLevel);
  if (!level) {
    result.style.display = '';
    result.innerHTML = '<p style="font-size:0.85rem;color:var(--text-secondary);">Your assigned level was not found. Contact your dispatcher.</p>';
    return;
  }

  const empType = person.hrEmploymentType;
  const allowances = level[empType] || {};
  const currentYear = new Date().getFullYear();

  // Filter approved requests for this employee this year
  const approvedRequests = (data.timeOffRequests || []).filter(r => {
    if (r.personId !== personId && r.employeeId !== personId && r.employeeName !== person.name) return false;
    const start = r.startDate?.toDate?.() || new Date(r.startDate);
    return start.getFullYear() === currentYear;
  });

  // Count used hours per category (legacy requests without hours field: days × 8)
  const used = {};
  const bereavementUsedByTier = {};
  let bereavementTravelUsed = 0;
  approvedRequests.forEach(r => {
    const type = r.type || 'other';
    const hrs = r.hours ? r.hours : (() => {
      const start = r.startDate?.toDate?.() || new Date(r.startDate);
      const end = r.endDate?.toDate?.() || new Date(r.endDate);
      return Math.max(1, Math.round((end - start) / (24 * 60 * 60 * 1000)) + 1) * 8;
    })();
    used[type] = (used[type] || 0) + hrs;
    if (type === 'bereavement' && r.bereavementLevel) {
      bereavementUsedByTier[r.bereavementLevel] = (bereavementUsedByTier[r.bereavementLevel] || 0) + hrs;
      if (r.bereavementTravel) bereavementTravelUsed += hrs;
    }
  });

  // Build categories list
  const categoryIds = ['vacation', 'sick', 'personal', 'unpaid', 'bereavement', 'parental_leave', 'jury_duty', 'other'];
  const categories = categoryIds.filter(id => {
    if (id === 'unpaid' && empType === 'salary') return false;
    return true;
  });

  const isTieredBereavement = typeof allowances.bereavement === 'object' && allowances.bereavement !== null;

  let html = `<div style="font-size:0.85rem;color:var(--text-secondary);margin-bottom:var(--spacing-sm);">
    <strong>${escapeHtml(level.name)}</strong> &middot; ${empType === 'salary' ? 'Salary' : 'Hourly'} &middot; ${currentYear}
  </div>`;

  html += `<table style="width:100%;border-collapse:collapse;font-size:0.85rem;">
    <thead>
      <tr style="border-bottom:2px solid var(--border-light);text-align:left;">
        <th style="padding:6px 8px;">Category</th>
        <th style="padding:6px 8px;text-align:center;">Allowed</th>
        <th style="padding:6px 8px;text-align:center;">Used</th>
        <th style="padding:6px 8px;text-align:center;">Remaining</th>
      </tr>
    </thead>
    <tbody>`;

  categories.forEach(catId => {
    // Bereavement is per-event, not a pool — shown separately below
    if (catId === 'bereavement') return;

    const allowedDays = allowances[catId] || 0;
    const allowedHrs = allowedDays * 8;
    const usedHrs = used[catId] || 0;
    const remaining = allowedHrs - usedHrs;
    const color = remaining < 0 ? 'var(--danger-red)' : remaining === 0 ? 'var(--warning-amber)' : 'var(--text-primary)';

    html += `<tr style="border-bottom:1px solid var(--border-light);">
      <td style="padding:6px 8px;">${TIMEOFF_CATEGORY_LABELS[catId] || catId}</td>
      <td style="padding:6px 8px;text-align:center;">${allowedHrs} hrs</td>
      <td style="padding:6px 8px;text-align:center;">${usedHrs} hrs</td>
      <td style="padding:6px 8px;text-align:center;font-weight:600;color:${color};">${remaining} hrs</td>
    </tr>`;
  });

  html += '</tbody></table>';

  // Bereavement per-event reference section
  if (isTieredBereavement) {
    const bereavementEvents = approvedRequests.filter(r => r.type === 'bereavement');

    html += `<div style="margin-top:var(--spacing-md);border:1px solid var(--border-light);border-radius:var(--radius-md);background:var(--bg-secondary);padding:var(--spacing-sm) var(--spacing-md);">
      <div style="font-size:0.85rem;font-weight:600;margin-bottom:var(--spacing-sm);display:flex;align-items:center;gap:4px;">
        <span class="material-icons" style="font-size:1rem;">favorite</span> Bereavement Policy
        <span style="font-weight:400;color:var(--text-secondary);font-size:0.75rem;margin-left:auto;">resets per event</span>
      </div>
      <table style="width:100%;border-collapse:collapse;font-size:0.8rem;">
        <thead>
          <tr style="border-bottom:1px solid var(--border-light);text-align:left;">
            <th style="padding:4px 8px;">Relationship</th>
            <th style="padding:4px 8px;text-align:center;">Days/Event</th>
          </tr>
        </thead>
        <tbody>`;

    BEREAVEMENT_TIERS_PUBLIC.forEach(t => {
      const days = allowances.bereavement[t.id] || 0;
      if (days > 0) {
        html += `<tr style="border-bottom:1px solid var(--border-light);">
          <td style="padding:4px 8px;color:var(--text-secondary);">${t.label}: ${t.desc}</td>
          <td style="padding:4px 8px;text-align:center;font-weight:600;">${days} day${days !== 1 ? 's' : ''}</td>
        </tr>`;
      }
    });

    if (allowances.bereavement.travelDays) {
      html += `<tr style="border-bottom:1px solid var(--border-light);">
        <td style="padding:4px 8px;color:var(--text-secondary);">+ Travel Days (funeral 250+ miles away)</td>
        <td style="padding:4px 8px;text-align:center;font-weight:600;">${allowances.bereavement.travelDays} day${allowances.bereavement.travelDays !== 1 ? 's' : ''}</td>
      </tr>`;
    }

    html += '</tbody></table>';

    if (bereavementEvents.length > 0) {
      html += `<div style="margin-top:var(--spacing-sm);font-size:0.75rem;color:var(--text-secondary);">
        ${bereavementEvents.length} bereavement event${bereavementEvents.length !== 1 ? 's' : ''} approved this year
      </div>`;
    }

    html += '</div>';
  }

  // TASK-341 follow-up: removed `buildHolidaysHtml()` salaried-employee
  // fallthrough that rendered a hardcoded `PAID_HOLIDAYS_2026` federal-holiday
  // hint table. TASK-341 deliberately chose BLANK as the default for
  // `settings.companyHolidays`. Real configured holidays surface via HR
  // Settings + `window.AppHelpers.getCompanyHolidays()`.

  result.style.display = '';
  result.innerHTML = html;
};

function showTimeOffRequestSuccess(personName) {
  const mainContent = document.getElementById('main-content');
  if (!mainContent) return;

  mainContent.innerHTML = `
    <div class="order-request-page">
      <div class="order-request-success">
        <span class="material-icons success-icon">check_circle</span>
        <h2>Time Off Request Submitted!</h2>
        <p>Thank you${personName ? ', ' + escapeHtml(personName) : ''}. Your request has been submitted for review.</p>
        <p style="margin-top: var(--spacing-lg);">You'll be notified when it's approved or denied.</p>
        <div style="margin-top: var(--spacing-xl);">
          <p class="text-muted">You can close this page now.</p>
        </div>
      </div>
    </div>
  `;
}

// ============================================
// TIME-OFF MANAGEMENT PAGE (Manager Approval Link)
// ============================================
async function loadTimeOffManagePage(token) {
  try {
    // Set public access flag and auth BEFORE any Firestore reads
    window.AppState.isPublicAccess = true;
    try {
      await ensurePublicAuth();
    } catch (authErr) {
      console.error('[TimeOffManage] Auth error:', authErr);
      window.AppState.isPublicAccess = false;
      showAccessError('Unable to load approval page. Please try again.');
      return false;
    }

    const tokenDoc = await getDoc(doc(db, 'accessTokens', token));

    if (!tokenDoc.exists()) {
      showAccessError('This approval link is invalid or has been revoked.');
      return false;
    }

    const tokenData = tokenDoc.data();

    if (tokenData.type !== 'timeoff_manage') {
      showAccessError('This link is not valid for time-off management.');
      return false;
    }

    if (tokenData.expiresAt !== null) {
      const now = new Date();
      const expiresAt = tokenData.expiresAt?.toDate?.() || new Date(tokenData.expiresAt);
      if (now > expiresAt) {
        showAccessError('This approval link has expired.');
        return false;
      }
    }

    console.log('[TimeOffManage] Token valid for tenant:', tokenData.tenantId);

    // Grant membership
    try {
      const grantMembership = httpsCallable(functions, 'grantAccessTokenMembership');
      await grantMembership({ token });
    } catch (memberErr) {
      console.error('[TimeOffManage] Failed to grant membership:', memberErr);
    }

    window.AppState.tenant = tokenData.tenantId;
    window.AppState.timeOffManageToken = token;

    const header = document.getElementById('app-header');
    const sidebar = document.getElementById('app-sidebar');
    const mobileNav = document.getElementById('mobile-nav');
    if (header) header.style.display = 'none';
    if (sidebar) sidebar.style.display = 'none';
    if (mobileNav) mobileNav.style.display = 'none';

    document.body.classList.add('timeoff-request-mode');

    // Load tenant name (TASK-9856: the PIN is checked server-side)
    let companyName = 'Time Off';
    try {
      const tenantDoc = await _linkTenantDoc(token);
      if (tenantDoc.exists()) {
        companyName = tenantDoc.data().companyName || 'Time Off';
      }
      // TASK-9856 -- no people read: the approval PIN is checked server-side.
    } catch (err) {
      console.warn('[TimeOffManage] Could not load tenant data:', err);
    }

    hideLoading();

    // Check if PIN already remembered in localStorage
    const pinKey = `dispatchdeck_approve_pin_${tokenData.tenantId}`;
    const savedPin = localStorage.getItem(pinKey);
    let pinValid = false;

    let savedCheck = null;
    if (savedPin) {
      savedCheck = await validateApprovalPin(savedPin, token);
      pinValid = savedCheck.ok;
    }

    if (!pinValid) {
      // Clear a saved PIN only when the server said it is WRONG -- never on
      // "too many tries" or a network error (TASK-9856).
      if (savedPin && savedCheck && !savedCheck.exhausted && !savedCheck.message) localStorage.removeItem(pinKey);
      if (savedCheck && savedCheck.message) setTimeout(() => showToast(savedCheck.message, 'error'), 0);
      // Show PIN challenge
      renderApprovalPinChallenge(companyName, tokenData.tenantId, token, pinKey);
      return true;
    }

    // PIN valid — load and show approval page
    await loadAndShowApprovalPage(companyName, tokenData.tenantId, token);
    return true;
  } catch (error) {
    console.error('[TimeOffManage] Error:', error);
    showAccessError('Unable to load approval page. Please try again.');
    return false;
  }
}

// Validates a Driver Schedule PIN for approval page access
// Only accepts Driver PINs (XX1234 format) for people with admin or dispatcher role
// TASK-9856 -- server-side (checkLinkPagePin): settings access PIN with
// workforce / timeoff / dispatch, or an active admin / dispatcher's Driver
// Code. The page no longer loads people or settings.accessPins to compare.
// -> { ok, exhausted, message }. "exhausted" = this browser hit the wrong-PIN
// window (resource-exhausted): the PIN may be RIGHT, so a saved PIN is kept
// and the page says how long to wait.
async function validateApprovalPin(pin, token) {
  if (!pin) return { ok: false };
  try {
    const res = await httpsCallable(functions, 'checkLinkPagePin')({ token, pin: String(pin).trim() });
    return { ok: !!(res && res.data && res.data.ok) };
  } catch (err) {
    const code = String((err && err.code) || '').replace(/^functions\//, '');
    if (code === 'resource-exhausted') {
      const mins = err && err.details && err.details.retryAfterMin;
      return { ok: false, exhausted: true, message: mins ? `Too many tries. Wait ${mins} min and try again.` : 'Too many tries. Wait a few minutes and try again.' };
    }
    console.warn('[TimeOffManage] PIN check failed:', err && err.message);
    return { ok: false, message: 'Could not check your PIN. Try again.' };
  }
}

function renderApprovalPinChallenge(companyName, tenantId, token, pinKey) {
  const mainContent = document.getElementById('main-content');
  if (!mainContent) return;

  mainContent.innerHTML = `
    <div class="order-request-page" style="max-width:420px;">
      <div class="order-request-header">
        <h1><span class="material-icons">lock</span> ${escapeHtml(companyName)}</h1>
        <p>Enter your office access PIN to manage time-off requests</p>
      </div>

      <div class="order-request-section" style="text-align:center;">
        <div style="margin-bottom:var(--spacing-sm);font-size:0.8rem;color:var(--text-secondary);">
          Enter the office access PIN that unlocks Workforce, Time Off or Dispatch
        </div>
        <div style="margin-bottom:var(--spacing-md);">
          <input type="text" id="approve-pin-input" maxlength="10" placeholder="Access PIN" autocapitalize="characters" autocomplete="off"
            style="font-size:1.5rem;text-align:center;letter-spacing:0.3em;padding:var(--spacing-md);width:220px;border:2px solid var(--border-color);border-radius:var(--radius-md);color:var(--text-primary);background:var(--bg-elevated);"
            onkeydown="if(event.key==='Enter')document.getElementById('approve-pin-submit').click()">
        </div>
        <div id="approve-pin-error" style="color:var(--status-danger);font-size:0.85rem;margin-bottom:var(--spacing-md);display:none;">
          Invalid PIN. Please try again.
        </div>
        <button id="approve-pin-submit" class="btn btn-primary" style="padding:var(--spacing-sm) var(--spacing-xl);font-size:1rem;">
          <span class="material-icons" style="font-size:18px;">login</span> Enter
        </button>
      </div>
    </div>
  `;

  // Focus the input
  setTimeout(() => document.getElementById('approve-pin-input')?.focus(), 100);

  document.getElementById('approve-pin-submit').addEventListener('click', async () => {
    const pin = document.getElementById('approve-pin-input').value.trim();
    const errorEl = document.getElementById('approve-pin-error');

    if (!pin) {
      errorEl.style.display = 'block';
      errorEl.textContent = 'Please enter your PIN.';
      return;
    }

    const check = await validateApprovalPin(pin, token);
    if (!check.ok) {
      errorEl.style.display = 'block';
      errorEl.textContent = check.message || 'Invalid PIN. Please try again.';
      document.getElementById('approve-pin-input').value = '';
      document.getElementById('approve-pin-input').focus();
      return;
    }

    window.AppState.timeOffManagePin = pin;
    // PIN valid — save to localStorage (remembered for this tenant)
    localStorage.setItem(pinKey, pin);

    // Show loading state
    mainContent.innerHTML = `
      <div class="order-request-page" style="text-align:center;padding-top:30vh;">
        <div class="loading-spinner"></div>
        <p style="color:var(--text-secondary);margin-top:var(--spacing-md);">Loading requests...</p>
      </div>
    `;

    await loadAndShowApprovalPage(companyName, tenantId, token);
  });
}

async function loadAndShowApprovalPage(companyName, tenantId, token) {
  let requests = [];
  try {
    const snap = await getDocs(
      query(
        collection(db, 'tenants', tenantId, 'timeOff'),
        orderBy('createdAt', 'desc')
      )
    );
    requests = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.error('[TimeOffManage] Error loading requests:', err);
  }

  renderTimeOffManagePage(companyName, requests, token);
}

function renderTimeOffManagePage(companyName, requests, token) {
  const mainContent = document.getElementById('main-content');
  if (!mainContent) return;

  const formatDate = (d) => {
    if (!d) return 'N/A';
    const date = d.toDate ? d.toDate() : new Date(d);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  };

  const typeLabels = {
    unpaid: 'Unpaid', vacation: 'Vacation', sick: 'Sick Leave',
    personal: 'Personal Day', bereavement: 'Bereavement', parental_leave: 'Parental Leave',
    jury_duty: 'Jury Duty', other: 'Other'
  };

  const pending = requests.filter(r => r.status === 'pending');
  const resolved = requests.filter(r => r.status !== 'pending').slice(0, 20);

  const renderCard = (req, showActions) => {
    const statusColors = { approved: '#16a34a', denied: '#dc2626', pending: '#eab308' };
    const statusIcons = { approved: 'check_circle', denied: 'cancel', pending: 'schedule' };
    const color = statusColors[req.status] || '#6b7280';
    const icon = statusIcons[req.status] || 'help';

    return `
      <div style="padding: var(--spacing-md); margin-bottom: var(--spacing-sm); background: var(--bg-elevated); border: 1px solid var(--border-light); border-left: 4px solid ${color}; border-radius: var(--radius-md);">
        <div style="display: flex; align-items: flex-start; gap: var(--spacing-sm);">
          <span class="material-icons" style="color: ${color}; margin-top: 2px;">${icon}</span>
          <div style="flex: 1; min-width: 0;">
            <div style="font-weight: 600;">${escapeHtml(req.personName || 'Unknown')}</div>
            <div style="font-size: 0.85rem; color: var(--text-secondary);">
              ${formatDate(req.startDate)} — ${formatDate(req.endDate)}${req.hours ? ` (${req.hours} hrs)` : ''} &middot; ${typeLabels[req.type] || req.type || 'Time Off'}
            </div>
            ${req.reason ? `<div style="font-size: 0.85rem; color: var(--text-muted); margin-top: 4px;">${escapeHtml(req.reason)}</div>` : ''}
          </div>
          ${!showActions ? `
            <div style="display: flex; align-items: center; gap: var(--spacing-xs); flex-shrink: 0;">
              <span style="font-size: 0.8rem; font-weight: 600; color: ${color}; text-transform: uppercase;">${req.status}</span>
              <button class="btn btn-icon" style="padding: 4px; color: var(--text-muted);" onclick="window.manageTimeOff('${req.id}', 'delete', this)" title="Delete">
                <span class="material-icons" style="font-size: 16px;">delete</span>
              </button>
            </div>
          ` : ''}
        </div>
        ${showActions ? `
          <div style="display: flex; gap: var(--spacing-xs); margin-top: var(--spacing-sm); flex-wrap: wrap;">
            <button class="btn btn-primary" style="padding: 6px 12px; font-size: 0.85rem; flex: 1; min-width: 80px;" onclick="window.manageTimeOff('${req.id}', 'approve', this)">
              <span class="material-icons" style="font-size: 16px;">check</span> Approve
            </button>
            <button class="btn btn-secondary" style="padding: 6px 12px; font-size: 0.85rem; flex: 1; min-width: 70px;" onclick="window.manageTimeOff('${req.id}', 'deny', this)">
              <span class="material-icons" style="font-size: 16px;">close</span> Deny
            </button>
          </div>
          <!-- TASK-4720: no Delete on a PENDING request; the requester withdraws it, the office approves or rejects. -->
        ` : ''}
      </div>
    `;
  };

  const pendingHtml = pending.length > 0
    ? pending.map(r => renderCard(r, true)).join('')
    : '<p style="color: var(--text-muted); text-align: center; padding: var(--spacing-lg);">No pending requests</p>';

  const resolvedHtml = resolved.length > 0
    ? resolved.map(r => renderCard(r, false)).join('')
    : '<p style="color: var(--text-muted); text-align: center; padding: var(--spacing-sm);">No recent history</p>';

  mainContent.innerHTML = `
    <div class="order-request-page">
      <div class="order-request-header">
        <h1><span class="material-icons">event_busy</span> ${escapeHtml(companyName)}</h1>
        <p>Time Off Requests</p>
      </div>

      <div class="order-request-section">
        <h2><span class="material-icons" style="color: #eab308;">schedule</span> Pending Review (${pending.length})</h2>
        ${pendingHtml}
      </div>

      <div class="order-request-section">
        <h2><span class="material-icons">history</span> Recent History</h2>
        ${resolvedHtml}
      </div>
    </div>
  `;
}

window.manageTimeOff = async function(requestId, action, btnEl) {
  const token = window.AppState.timeOffManageToken;
  if (!token) {
    showToast('Session error — please refresh', 'error');
    return;
  }

  const card = btnEl.closest('[style*="border-left"]');
  const row = btnEl.closest('div');

  // Disable all buttons in the card
  card.querySelectorAll('button').forEach(b => { b.disabled = true; });

  const spinLabels = { approve: 'Approving...', deny: 'Denying...', delete: 'Deleting...' };
  btnEl.innerHTML = `<span class="material-icons rotating">sync</span> ${spinLabels[action] || '...'}`;

  try {
    const manage = httpsCallable(functions, 'manageTimeOffRequest');
    // TASK-9856 -- the server requires the approval PIN on every action.
    const pin = localStorage.getItem(`dispatchdeck_approve_pin_${window.AppState.tenant}`) || window.AppState.timeOffManagePin || '';
    const result = await manage({ token, requestId, action, pin });

    if (result.data?.ok) {
      if (action === 'delete') {
        card.style.transition = 'opacity 0.3s, max-height 0.3s';
        card.style.opacity = '0';
        card.style.maxHeight = '0';
        card.style.overflow = 'hidden';
        card.style.marginBottom = '0';
        card.style.padding = '0';
        setTimeout(() => card.remove(), 300);
        showToast('Request deleted', 'success');
      } else {
        const color = action === 'approve' ? '#16a34a' : '#dc2626';
        const icon = action === 'approve' ? 'check_circle' : 'cancel';
        const label = action === 'approve' ? 'APPROVED' : 'DENIED';

        card.style.borderLeftColor = color;
        card.querySelector('.material-icons').textContent = icon;
        card.querySelector('.material-icons').style.color = color;

        row.innerHTML = `
          <span style="font-size: 0.85rem; font-weight: 600; color: ${color}; text-transform: uppercase;">${label}</span>
          <button class="btn btn-icon" style="padding: 4px; color: var(--text-muted); margin-left: auto;" onclick="window.manageTimeOff('${requestId}', 'delete', this)" title="Delete">
            <span class="material-icons" style="font-size: 16px;">delete</span>
          </button>`;
        row.style.justifyContent = 'flex-start';
        row.style.alignItems = 'center';

        showToast(`Request ${label.toLowerCase()}`, 'success');
      }
    }
  } catch (error) {
    console.error('[TimeOffManage] Error:', error);
    // TASK-1426 — was dumping the raw error message straight to the user. Route
    // it through the same helper so ONE place decides what is safe to surface.
    showToast(timeOffErrorMessage(error, 'Something went wrong updating that request.'), 'error');
    card.querySelectorAll('button').forEach(b => { b.disabled = false; });
    btnEl.innerHTML = `<span class="material-icons" style="font-size: 16px;">delete</span>`;
  }
};

// ============================================
// PUBLIC INVOICE VIEW PAGE
// ============================================
async function loadPublicInvoicePage(token) {
  const esc = (s) => s ? String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;') : '';
  const fmtMoney = (n) => '$' + (n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  try {
    window.AppState.isPublicAccess = true;

    // Hide standard UI
    const header = document.getElementById('app-header');
    const sidebar = document.getElementById('app-sidebar');
    const mobileNav = document.getElementById('mobile-nav');
    const authModal = document.getElementById('auth-modal');
    if (header) header.style.display = 'none';
    if (sidebar) sidebar.style.display = 'none';
    if (mobileNav) mobileNav.style.display = 'none';
    if (authModal) authModal.classList.add('hidden');
    document.body.classList.add('portal-mode');

    await ensurePublicAuth();

    // Show loading
    const mainContent = document.getElementById('main-content');
    if (mainContent) {
      mainContent.innerHTML = `
        <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:60vh;padding:20px;text-align:center;">
          <div class="spinner" style="width:40px;height:40px;border:3px solid #e2e8f0;border-top:3px solid #3B82F6;border-radius:50%;animation:spin 1s linear infinite;margin-bottom:16px;"></div>
          <p style="color:#64748B;">Loading invoice...</p>
        </div>`;
    }
    hideLoading();

    // Call Cloud Function to get invoice data (no auth required — function validates token)
    const getInvoiceFn = httpsCallable(functions, 'getPublicInvoice', { timeout: 30000 });
    let result;
    try {
      result = await getInvoiceFn({ token });
    } catch (err) {
      console.error('[Invoice] Cloud Function error:', err);
      if (mainContent) {
        mainContent.innerHTML = `
          <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:60vh;padding:20px;text-align:center;">
            <span class="material-icons" style="font-size:64px;color:#DC2626;margin-bottom:16px;">receipt_long</span>
            <h2 style="margin:0 0 8px 0;color:#0F172A;">Invoice Not Found</h2>
            <p style="margin:0 0 24px 0;color:#64748B;max-width:400px;">This invoice link is invalid or has expired. Please contact the sender for a new link.</p>
          </div>`;
      }
      return;
    }

    const { ticket, lines, company, customer, job, settings, onlinePaymentsEnabled } = result.data;
    if (!ticket) {
      if (mainContent) {
        mainContent.innerHTML = `
          <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:60vh;padding:20px;text-align:center;">
            <span class="material-icons" style="font-size:64px;color:#DC2626;margin-bottom:16px;">receipt_long</span>
            <h2 style="margin:0 0 8px 0;color:#0F172A;">Invoice Not Found</h2>
            <p style="margin:0 0 24px 0;color:#64748B;max-width:400px;">This invoice link is invalid or has expired.</p>
          </div>`;
      }
      return;
    }

    // Sort lines
    const sortedLines = (lines || []).sort((a, b) => {
      if (a.type !== b.type) return (a.type || '').localeCompare(b.type || '');
      return 0;
    });

    // TASK-511 PART 2 — honor the customer's per-contract "hide these line types
    // on invoices" default on the CUSTOMER-FACING page. Mirrors the exact semantics
    // of billing.js isLineInvoiced()/excludedInvoiceAmount() (the in-app render) so
    // the customer-facing total == shown lines == in-app render. invoiceExcludeLineTypes
    // is resolved server-side from the customer's active contract (getPublicInvoice);
    // defaults to [] (nothing hidden) for customers without the field.
    const _exTypes = Array.isArray(result.data.invoiceExcludeLineTypes) ? result.data.invoiceExcludeLineTypes : [];
    const _isLineInvoiced = (l) => {
      if (!l) return false;
      if (l.includeOnInvoice === false) return false; // manual exclude
      if (l.includeOnInvoice === true) return true;    // manual include beats default
      return !_exTypes.includes(l.type);
    };
    const empLines = sortedLines.filter(l => l.type === 'EMPLOYEE' && _isLineInvoiced(l));
    const equipLines = sortedLines.filter(l => l.type === 'EQUIPMENT' && _isLineInvoiced(l));
    const downLines = sortedLines.filter(l => l.type === 'DOWNTIME' && _isLineInvoiced(l));
    const standbyLines = sortedLines.filter(l => l.type === 'STANDBY' && _isLineInvoiced(l));
    const permitLines = sortedLines.filter(l => l.type === 'PERMIT' && _isLineInvoiced(l));
    // FIELD_ITEM / MISC were previously counted in ticket.grandTotal but never
    // rendered on this customer-facing page (pre-existing gap). Render them now so
    // the displayed total exactly equals the sum of shown lines AND matches the
    // in-app billing.js render (which renders FIELD_ITEM + MISC tables too).
    const fieldItemLines = sortedLines.filter(l => l.type === 'FIELD_ITEM' && _isLineInvoiced(l));
    const miscLines = sortedLines.filter(l => l.type === 'MISC' && _isLineInvoiced(l));
    // TASK-9440 — the fuel surcharge is in ticket.grandTotal, so it is shown too
    // (the displayed total must equal the shown lines).
    const fuelLines = sortedLines.filter(l => l.type === 'FUEL_SURCHARGE' && _isLineInvoiced(l));
    // Sign-aware sum of EVERY excluded line (DOWNTIME deducts), subtracted from the
    // stored grandTotal so the displayed total exactly matches the rendered lines.
    const excludedAmount = sortedLines.reduce((s, l) => {
      if (_isLineInvoiced(l)) return s;
      const sign = l.type === 'DOWNTIME' ? -1 : 1;
      return s + sign * (l.lineAmount || 0);
    }, 0);
    // fix/stripe-invoice-amount-1019 — the server's amountDue is what Pay Now
    // charges (same helper as the checkout); show that. Older servers / an
    // underivable total fall back to the page's own math.
    const grandTotal = (typeof result.data.amountDue === 'number' && Number.isFinite(result.data.amountDue))
      ? result.data.amountDue
      : (ticket.grandTotal || sortedLines.reduce((s, l) => s + (l.type === 'DOWNTIME' ? -1 : 1) * (l.lineAmount || 0), 0)) - excludedAmount;

    const invoiceDate = ticket.date
      ? new Date(ticket.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
      : '';
    const workDate = ticket.workDate
      ? new Date(ticket.workDate + (ticket.workDate.includes('T') ? '' : 'T00:00:00')).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
        + (ticket.workDateEnd && ticket.workDateEnd !== ticket.workDate ? ' – ' + new Date(ticket.workDateEnd + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '')
      : invoiceDate;
    const dueDate = (() => { const d = new Date(); d.setDate(d.getDate() + 30); return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }); })();

    // Build line item tables
    function buildPublicLaborTable(lines) {
      if (lines.length === 0) return '';
      const sub = lines.reduce((s, l) => s + (l.lineAmount || 0), 0);
      const rows = lines.map(l => `
        <tr>
          <td style="padding:8px 10px;border-bottom:1px solid #E2E8F0;font-size:13px;">${esc(l.employeeName || l.name || '-')}</td>
          <td style="padding:8px 10px;border-bottom:1px solid #E2E8F0;text-align:right;font-size:13px;">${(l.hours || 0).toFixed(2)}</td>
          <td style="padding:8px 10px;border-bottom:1px solid #E2E8F0;text-align:right;font-size:13px;">${fmtMoney(l.rate)}</td>
          <td style="padding:8px 10px;border-bottom:1px solid #E2E8F0;text-align:right;font-size:13px;font-weight:600;">${fmtMoney(l.lineAmount)}</td>
        </tr>`).join('');
      return `
        <div style="font-size:13px;font-weight:700;color:#0F172A;margin:20px 0 6px;text-transform:uppercase;letter-spacing:0.5px;">Labor</div>
        <table style="width:100%;border-collapse:collapse;">
          <thead><tr style="background:#F1F5F9;">
            <th style="padding:8px 10px;text-align:left;font-size:12px;font-weight:600;color:#475569;">Name</th>
            <th style="padding:8px 10px;text-align:right;font-size:12px;font-weight:600;color:#475569;">Hours</th>
            <th style="padding:8px 10px;text-align:right;font-size:12px;font-weight:600;color:#475569;">Rate</th>
            <th style="padding:8px 10px;text-align:right;font-size:12px;font-weight:600;color:#475569;">Amount</th>
          </tr></thead>
          <tbody>${rows}
            <tr style="background:#F8FAFC;"><td colspan="3" style="padding:8px 10px;text-align:right;font-size:13px;font-weight:600;">Labor Subtotal</td><td style="padding:8px 10px;text-align:right;font-size:13px;font-weight:700;">${fmtMoney(sub)}</td></tr>
          </tbody>
        </table>`;
    }

    function buildPublicEquipTable(lines, title) {
      if (lines.length === 0) return '';
      const sub = lines.reduce((s, l) => s + (l.lineAmount || 0), 0);
      const rows = lines.map(l => `
        <tr>
          <td style="padding:8px 10px;border-bottom:1px solid #E2E8F0;font-size:13px;">${esc(l.assetName || l.truckName || l.name || '-')}</td>
          <td style="padding:8px 10px;border-bottom:1px solid #E2E8F0;font-size:13px;">${esc(l.fleetNumber || '-')}</td>
          <td style="padding:8px 10px;border-bottom:1px solid #E2E8F0;text-align:right;font-size:13px;">${(l.hours || l.quantity || 0).toFixed ? (l.hours || l.quantity || 0).toFixed(2) : (l.hours || l.quantity || 0)}</td>
          <td style="padding:8px 10px;border-bottom:1px solid #E2E8F0;text-align:right;font-size:13px;">${fmtMoney(l.rate)}</td>
          <td style="padding:8px 10px;border-bottom:1px solid #E2E8F0;text-align:right;font-size:13px;font-weight:600;">${fmtMoney(l.lineAmount)}</td>
        </tr>`).join('');
      return `
        <div style="font-size:13px;font-weight:700;color:#0F172A;margin:20px 0 6px;text-transform:uppercase;letter-spacing:0.5px;">${esc(title)}</div>
        <table style="width:100%;border-collapse:collapse;">
          <thead><tr style="background:#F1F5F9;">
            <th style="padding:8px 10px;text-align:left;font-size:12px;font-weight:600;color:#475569;">Asset</th>
            <th style="padding:8px 10px;text-align:left;font-size:12px;font-weight:600;color:#475569;">Fleet #</th>
            <th style="padding:8px 10px;text-align:right;font-size:12px;font-weight:600;color:#475569;">QTY</th>
            <th style="padding:8px 10px;text-align:right;font-size:12px;font-weight:600;color:#475569;">Rate</th>
            <th style="padding:8px 10px;text-align:right;font-size:12px;font-weight:600;color:#475569;">Amount</th>
          </tr></thead>
          <tbody>${rows}
            <tr style="background:#F8FAFC;"><td colspan="4" style="padding:8px 10px;text-align:right;font-size:13px;font-weight:600;">${esc(title)} Subtotal</td><td style="padding:8px 10px;text-align:right;font-size:13px;font-weight:700;">${fmtMoney(sub)}</td></tr>
          </tbody>
        </table>`;
    }

    function buildPublicDowntimeTable(lines) {
      if (lines.length === 0) return '';
      const sub = lines.reduce((s, l) => s + (l.lineAmount || 0), 0);
      const rows = lines.map(l => `
        <tr>
          <td style="padding:8px 10px;border-bottom:1px solid #E2E8F0;font-size:13px;">${esc(l.employeeName || l.name || '-')}</td>
          <td style="padding:8px 10px;border-bottom:1px solid #E2E8F0;text-align:right;font-size:13px;">${(l.hours || 0).toFixed(2)}</td>
          <td style="padding:8px 10px;border-bottom:1px solid #E2E8F0;text-align:right;font-size:13px;">${fmtMoney(l.rate)}</td>
          <td style="padding:8px 10px;border-bottom:1px solid #E2E8F0;text-align:right;font-size:13px;font-weight:600;color:#DC2626;">-${fmtMoney(l.lineAmount)}</td>
          <td style="padding:8px 10px;border-bottom:1px solid #E2E8F0;font-size:12px;color:#475569;">${esc(l.notes || '')}</td>
        </tr>`).join('');
      return `
        <div style="font-size:13px;font-weight:700;color:#0F172A;margin:20px 0 6px;text-transform:uppercase;letter-spacing:0.5px;">Downtime Deduction</div>
        <table style="width:100%;border-collapse:collapse;">
          <thead><tr style="background:#F1F5F9;">
            <th style="padding:8px 10px;text-align:left;font-size:12px;font-weight:600;color:#475569;">Person</th>
            <th style="padding:8px 10px;text-align:right;font-size:12px;font-weight:600;color:#475569;">Hours</th>
            <th style="padding:8px 10px;text-align:right;font-size:12px;font-weight:600;color:#475569;">Rate</th>
            <th style="padding:8px 10px;text-align:right;font-size:12px;font-weight:600;color:#475569;">Deduction</th>
            <th style="padding:8px 10px;text-align:left;font-size:12px;font-weight:600;color:#475569;">Notes</th>
          </tr></thead>
          <tbody>${rows}
            <tr style="background:#F8FAFC;"><td colspan="3" style="padding:8px 10px;text-align:right;font-size:13px;font-weight:600;">Downtime Deduction</td><td style="padding:8px 10px;text-align:right;font-size:13px;font-weight:700;color:#DC2626;">-${fmtMoney(sub)}</td><td></td></tr>
          </tbody>
        </table>`;
    }

    function buildPublicStandbyTable(lines) {
      if (lines.length === 0) return '';
      const sub = lines.reduce((s, l) => s + (l.lineAmount || 0), 0);
      const rows = lines.map(l => `
        <tr>
          <td style="padding:8px 10px;border-bottom:1px solid #E2E8F0;font-size:13px;">${esc(l.employeeName || l.name || '-')}</td>
          <td style="padding:8px 10px;border-bottom:1px solid #E2E8F0;text-align:right;font-size:13px;">${(l.hours || 0).toFixed(2)}</td>
          <td style="padding:8px 10px;border-bottom:1px solid #E2E8F0;text-align:right;font-size:13px;">${fmtMoney(l.rate)}</td>
          <td style="padding:8px 10px;border-bottom:1px solid #E2E8F0;text-align:right;font-size:13px;font-weight:600;">${fmtMoney(l.lineAmount)}</td>
          <td style="padding:8px 10px;border-bottom:1px solid #E2E8F0;font-size:12px;color:#475569;">${esc(l.notes || '')}</td>
        </tr>`).join('');
      return `
        <div style="font-size:13px;font-weight:700;color:#0F172A;margin:20px 0 6px;text-transform:uppercase;letter-spacing:0.5px;">Standby</div>
        <table style="width:100%;border-collapse:collapse;">
          <thead><tr style="background:#F1F5F9;">
            <th style="padding:8px 10px;text-align:left;font-size:12px;font-weight:600;color:#475569;">Person</th>
            <th style="padding:8px 10px;text-align:right;font-size:12px;font-weight:600;color:#475569;">Hours</th>
            <th style="padding:8px 10px;text-align:right;font-size:12px;font-weight:600;color:#475569;">Rate</th>
            <th style="padding:8px 10px;text-align:right;font-size:12px;font-weight:600;color:#475569;">Amount</th>
            <th style="padding:8px 10px;text-align:left;font-size:12px;font-weight:600;color:#475569;">Notes</th>
          </tr></thead>
          <tbody>${rows}
            <tr style="background:#F8FAFC;"><td colspan="3" style="padding:8px 10px;text-align:right;font-size:13px;font-weight:600;">Standby Subtotal</td><td style="padding:8px 10px;text-align:right;font-size:13px;font-weight:700;">${fmtMoney(sub)}</td><td></td></tr>
          </tbody>
        </table>`;
    }

    function buildPublicPermitTable(lines) {
      if (lines.length === 0) return '';
      const sub = lines.reduce((s, l) => s + (l.lineAmount || 0), 0);
      const rows = lines.map(l => `
        <tr>
          <td style="padding:8px 10px;border-bottom:1px solid #E2E8F0;font-size:13px;">${esc(l.description || '-')}</td>
          <td style="padding:8px 10px;border-bottom:1px solid #E2E8F0;text-align:right;font-size:13px;font-weight:600;">${fmtMoney(l.lineAmount)}</td>
        </tr>`).join('');
      return `
        <div style="font-size:13px;font-weight:700;color:#0F172A;margin:20px 0 6px;text-transform:uppercase;letter-spacing:0.5px;">Permits</div>
        <table style="width:100%;border-collapse:collapse;">
          <thead><tr style="background:#F1F5F9;">
            <th style="padding:8px 10px;text-align:left;font-size:12px;font-weight:600;color:#475569;">Description</th>
            <th style="padding:8px 10px;text-align:right;font-size:12px;font-weight:600;color:#475569;">Amount</th>
          </tr></thead>
          <tbody>${rows}
            <tr style="background:#F8FAFC;"><td style="padding:8px 10px;text-align:right;font-size:13px;font-weight:600;">Permit Subtotal</td><td style="padding:8px 10px;text-align:right;font-size:13px;font-weight:700;">${fmtMoney(sub)}</td></tr>
          </tbody>
        </table>`;
    }

    // TASK-511 PART 2 — Field Items + Misc tables (mirror in-app
    // buildFieldItemsTableHTML/buildMiscTableHTML: Description / Qty / Rate / Amount)
    function buildPublicDescQtyRateTable(lines, title, subtotalLabel) {
      if (lines.length === 0) return '';
      const sub = lines.reduce((s, l) => s + (l.lineAmount || 0), 0);
      const rows = lines.map(l => `
        <tr>
          <td style="padding:8px 10px;border-bottom:1px solid #E2E8F0;font-size:13px;">${esc(l.description || '-')}</td>
          <td style="padding:8px 10px;border-bottom:1px solid #E2E8F0;text-align:right;font-size:13px;">${l.quantity || 0}</td>
          <td style="padding:8px 10px;border-bottom:1px solid #E2E8F0;text-align:right;font-size:13px;">${fmtMoney(l.rate)}/${esc(l.rateUnit || 'ea')}</td>
          <td style="padding:8px 10px;border-bottom:1px solid #E2E8F0;text-align:right;font-size:13px;font-weight:600;">${fmtMoney(l.lineAmount)}</td>
        </tr>`).join('');
      return `
        <div style="font-size:13px;font-weight:700;color:#0F172A;margin:20px 0 6px;text-transform:uppercase;letter-spacing:0.5px;">${esc(title)}</div>
        <table style="width:100%;border-collapse:collapse;">
          <thead><tr style="background:#F1F5F9;">
            <th style="padding:8px 10px;text-align:left;font-size:12px;font-weight:600;color:#475569;">Description</th>
            <th style="padding:8px 10px;text-align:right;font-size:12px;font-weight:600;color:#475569;">Qty</th>
            <th style="padding:8px 10px;text-align:right;font-size:12px;font-weight:600;color:#475569;">Rate</th>
            <th style="padding:8px 10px;text-align:right;font-size:12px;font-weight:600;color:#475569;">Amount</th>
          </tr></thead>
          <tbody>${rows}
            <tr style="background:#F8FAFC;"><td colspan="3" style="padding:8px 10px;text-align:right;font-size:13px;font-weight:600;">${esc(subtotalLabel || (title + ' Subtotal'))}</td><td style="padding:8px 10px;text-align:right;font-size:13px;font-weight:700;">${fmtMoney(sub)}</td></tr>
          </tbody>
        </table>`;
    }

    // TASK-9440 — Fuel Surcharge: one row per line, its own words ("Fuel
    // Surcharge 12% of $1,450.00 equipment") and amount.
    function buildPublicFuelTable(lines) {
      if (lines.length === 0) return '';
      const sub = lines.reduce((s, l) => s + (l.lineAmount || 0), 0);
      const rows = lines.map(l => `
        <tr>
          <td style="padding:8px 10px;border-bottom:1px solid #E2E8F0;font-size:13px;">${esc(l.description || 'Fuel Surcharge')}</td>
          <td style="padding:8px 10px;border-bottom:1px solid #E2E8F0;text-align:right;font-size:13px;font-weight:600;">${fmtMoney(l.lineAmount)}</td>
        </tr>`).join('');
      return `
        <div style="font-size:13px;font-weight:700;color:#0F172A;margin:20px 0 6px;text-transform:uppercase;letter-spacing:0.5px;">Fuel Surcharge</div>
        <table style="width:100%;border-collapse:collapse;">
          <thead><tr style="background:#F1F5F9;">
            <th style="padding:8px 10px;text-align:left;font-size:12px;font-weight:600;color:#475569;">Description</th>
            <th style="padding:8px 10px;text-align:right;font-size:12px;font-weight:600;color:#475569;">Amount</th>
          </tr></thead>
          <tbody>${rows}
            <tr style="background:#F8FAFC;"><td style="padding:8px 10px;text-align:right;font-size:13px;font-weight:600;">Fuel Surcharge Subtotal</td><td style="padding:8px 10px;text-align:right;font-size:13px;font-weight:700;">${fmtMoney(sub)}</td></tr>
          </tbody>
        </table>`;
    }

    // Build work performed section
    const generalNotes = ticket.notes || ticket.workPerformed || '';
    const showNotes = ticket.showNotesOnInvoice !== false;
    let workPerformedHtml = '';
    if (generalNotes && showNotes) {
      workPerformedHtml = `
        <div style="font-size:13px;font-weight:700;color:#0F172A;margin:20px 0 6px;text-transform:uppercase;letter-spacing:0.5px;">Work Performed</div>
        <div style="color:#334155;font-size:13px;white-space:pre-wrap;margin-bottom:16px;">${esc(generalNotes)}</div>`;
    }

    // Equipment sub-tables by type
    const vehicleLines = equipLines.filter(l => l.assetType === 'truck');
    const trailerLines = equipLines.filter(l => l.assetType === 'trailer');
    const assetLines = equipLines.filter(l => !l.assetType || l.assetType === 'asset');

    const lineTablesHtml = [
      workPerformedHtml,
      buildPublicLaborTable(empLines),
      buildPublicEquipTable(vehicleLines, 'Vehicles'),
      buildPublicEquipTable(trailerLines, 'Trailers'),
      buildPublicEquipTable(assetLines, 'Assets'),
      buildPublicDowntimeTable(downLines),
      buildPublicStandbyTable(standbyLines),
      buildPublicPermitTable(permitLines),
      // TASK-9725 — billed PPE roll-up lines (sourceType 'safetyEquipment') in
      // their own "Consumables used on this job" table under Field Items. Display
      // only: grandTotal above never reads these tables.
      buildPublicDescQtyRateTable(fieldItemLines.filter(l => l.sourceType !== 'safetyEquipment'), 'Field Items'),
      buildPublicDescQtyRateTable(fieldItemLines.filter(l => l.sourceType === 'safetyEquipment'), 'Consumables used on this job', 'Consumables Subtotal'),
      buildPublicDescQtyRateTable(miscLines, 'Miscellaneous'),
      buildPublicFuelTable(fuelLines) // TASK-9440 — last, after the charges it is a % of
    ].filter(Boolean).join('');

    // Payment instructions
    const paymentInstructions = settings?.invoiceEmailTemplate?.paymentInstructions || '';
    const paymentHtml = paymentInstructions
      ? `<div style="margin-top:24px;padding:14px 16px;background:#FFFBEB;border:1px solid #FDE68A;border-radius:8px;">
          <div style="font-size:12px;font-weight:700;color:#92400E;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:6px;">Payment Instructions</div>
          <div style="font-size:13px;color:#78350F;line-height:1.5;white-space:pre-wrap;">${esc(paymentInstructions)}</div>
        </div>`
      : '';

    // Build the full page
    const logoHtml = company.logoUrl
      ? `<img src="${esc(company.logoUrl)}" alt="${esc(company.name)}" style="max-height:80px;max-width:260px;margin-bottom:8px;">`
      : '';

    if (mainContent) {
      mainContent.innerHTML = `
        <div style="max-width:800px;margin:0 auto;padding:24px 16px;">
          <!-- Print button — big and obvious -->
          <div style="text-align:center;margin-bottom:24px;padding:16px;background:#F0F9FF;border:1px solid #BAE6FD;border-radius:12px;" class="no-print">
            <button onclick="window.print()" style="padding:16px 48px;background:linear-gradient(135deg,#3B82F6,#06B6D4);color:#fff;border:none;border-radius:10px;font-size:17px;cursor:pointer;font-weight:700;letter-spacing:0.3px;box-shadow:0 2px 8px rgba(59,130,246,0.3);">
              <span class="material-icons" style="font-size:20px;vertical-align:middle;margin-right:6px;">print</span> Print / Download PDF
            </button>
            <div style="margin-top:8px;font-size:13px;color:#64748B;">Opens your print dialog — choose "Save as PDF" to download.</div>
          </div>

          <!-- Invoice Card -->
          <div style="background:#fff;border-radius:12px;box-shadow:0 2px 12px rgba(0,0,0,0.08);overflow:hidden;">
            <!-- Company Header -->
            <div style="padding:32px 32px 24px;border-bottom:1px solid #E2E8F0;">
              <div style="display:flex;justify-content:space-between;align-items:flex-start;flex-wrap:wrap;gap:16px;">
                <div style="display:flex;align-items:flex-start;gap:12px;">
                  ${logoHtml ? `<div>${logoHtml}</div>` : ''}
                  <div>
                    <div style="font-size:18px;font-weight:800;color:#0F172A;">${esc(company.name)}</div>
                    ${company.address ? `<div style="color:#475569;font-size:13px;margin-top:2px;">${esc(company.address)}</div>` : ''}
                    ${company.phone ? `<div style="color:#475569;font-size:13px;">${esc(company.phone)}</div>` : ''}
                    ${company.email ? `<div style="color:#475569;font-size:13px;">${esc(company.email)}</div>` : ''}
                  </div>
                </div>
                <div style="text-align:right;">
                  <div style="font-size:28px;font-weight:800;color:#3B82F6;letter-spacing:1px;">INVOICE</div>
                  ${ticket.invoiceNumber ? `<div style="font-size:16px;font-weight:600;color:#0F172A;margin-top:4px;">${esc(ticket.invoiceNumber)}</div>` : ''}
                  <div style="color:#475569;margin-top:4px;">Date: ${esc(invoiceDate)}</div>
                  ${ticket.ticketNo ? `<div style="color:#475569;">Ticket #${esc(String(ticket.ticketNo))}</div>` : ''}
                </div>
              </div>
            </div>

            <!-- Bill To + Job Details -->
            <div style="padding:24px 32px;">
              <div style="margin-bottom:24px;padding:16px;background:#F8FAFC;border-radius:6px;">
                <div style="font-size:11px;font-weight:700;color:#64748B;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:4px;">Bill To</div>
                <div style="font-size:16px;font-weight:700;color:#0F172A;">${esc(customer?.name || ticket.customerName || '-')}</div>
                ${customer?.address ? `<div style="color:#475569;margin-top:2px;">${esc(customer.address)}</div>` : ''}
                ${customer?.phone ? `<div style="color:#475569;">${esc(customer.phone)}</div>` : ''}
                ${customer?.email ? `<div style="color:#475569;">${esc(customer.email)}</div>` : ''}
              </div>

              ${(job || ticket.location || ticket.poNumber || ticket.vendorNumber || ticket.wellName) ? `
                <div style="margin-bottom:24px;display:grid;grid-template-columns:1fr 1fr;gap:8px 24px;font-size:13px;">
                  ${job?.title ? `<div><span style="color:#64748B;">Job:</span> <strong>${esc(job.title)}</strong></div>` : ''}
                  ${ticket.location || job?.location ? `<div><span style="color:#64748B;">Location:</span> ${esc(ticket.location || job?.location)}</div>` : ''}
                  <div><span style="color:#64748B;">Work Date:</span> ${esc(workDate)}</div>
                  ${ticket.poNumber ? `<div><span style="color:#64748B;">PO #:</span> ${esc(ticket.poNumber)}</div>` : ''}
                  ${ticket.vendorNumber ? `<div><span style="color:#64748B;">Vendor #:</span> ${esc(ticket.vendorNumber)}</div>` : ''}
                  ${ticket.wellName ? `<div><span style="color:#64748B;">Well:</span> ${esc(ticket.wellName)}${ticket.wellNumber ? ' #' + esc(ticket.wellNumber) : ''}</div>` : ''}
                </div>
              ` : ''}

              <!-- Line Item Tables -->
              ${lineTablesHtml}

              <!-- Tax + Grand Total -->
              ${ticket.taxEnabled ? `
                <div style="margin-top:32px;padding:16px;background:#F8FAFC;border-radius:6px;">
                  <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">
                    <span style="font-size:14px;color:#475569;">Subtotal</span>
                    <span style="font-size:16px;font-weight:600;color:#0F172A;">${fmtMoney(ticket.subtotal || (grandTotal - (ticket.taxAmount || 0)))}</span>
                  </div>
                  <div style="display:flex;justify-content:space-between;align-items:center;">
                    <span style="font-size:14px;color:#475569;">Tax (${(ticket.taxRate || 0).toFixed(2)}%)</span>
                    <span style="font-size:16px;font-weight:600;color:#0F172A;">${fmtMoney(ticket.taxAmount || 0)}</span>
                  </div>
                </div>` : ''}

              <div style="margin-top:${ticket.taxEnabled ? '8' : '32'}px;padding:16px;background:#0F172A;border-radius:6px;display:flex;justify-content:space-between;align-items:center;">
                <span style="font-size:18px;font-weight:700;color:#fff;">Grand Total</span>
                <span style="font-size:24px;font-weight:800;color:#fff;">${fmtMoney(grandTotal)}</span>
              </div>

              ${ticket.paid ? `
              <div style="margin-top:16px;padding:14px 16px;background:#ECFDF5;border:1px solid #6EE7B7;border-radius:8px;text-align:center;">
                <span class="material-icons" style="font-size:28px;color:#059669;vertical-align:middle;margin-right:6px;">check_circle</span>
                <span style="font-size:16px;font-weight:700;color:#065F46;">Payment Received</span>
                ${ticket.paidAt ? `<div style="font-size:12px;color:#047857;margin-top:4px;">Paid ${new Date(ticket.paidAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</div>` : ''}
              </div>` : ''}

              ${onlinePaymentsEnabled && !ticket.paid ? `
              <div style="margin-top:16px;text-align:center;" class="no-print">
                <button id="btn-pay-invoice" onclick="window.payInvoice('${esc(token)}')" style="padding:14px 40px;background:linear-gradient(135deg,#7C3AED,#6D28D9);color:#fff;border:none;border-radius:8px;font-size:16px;font-weight:700;cursor:pointer;letter-spacing:0.3px;box-shadow:0 2px 8px rgba(124,58,237,0.3);transition:transform 0.15s,box-shadow 0.15s;">
                  <span class="material-icons" style="font-size:18px;vertical-align:middle;margin-right:6px;">credit_card</span> Pay Now
                </button>
              </div>` : ''}

              ${ORIGINAL_URL_PARAMS.get('paid') === 'true' && !ticket.paid ? `
              <div style="margin-top:16px;padding:14px 16px;background:#EFF6FF;border:1px solid #93C5FD;border-radius:8px;text-align:center;">
                <span class="material-icons" style="font-size:24px;color:#2563EB;vertical-align:middle;margin-right:6px;">info</span>
                <span style="font-size:14px;font-weight:600;color:#1E40AF;">Payment is being processed. This page will update once confirmed.</span>
              </div>` : ''}

              ${paymentHtml}

              <div style="margin-top:12px;text-align:center;color:#64748B;font-size:11px;">
                Thank you for your business — <strong style="color:#475569;">${esc(company.name)}</strong>
              </div>

              <!-- Download / Print -->
              <div style="margin-top:20px;text-align:center;" class="no-print">
                <button onclick="window.print()" style="padding:12px 32px;background:#F1F5F9;color:#475569;border:1px solid #CBD5E1;border-radius:8px;font-size:14px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:6px;">
                  <span class="material-icons" style="font-size:18px;">download</span> Download PDF
                </button>
              </div>
            </div>
          </div>

          <!-- DD Footer -->
          <div style="text-align:center;margin-top:24px;padding:16px 0;">
            <a href="https://dispatchdeck.app" style="text-decoration:none;color:#94a3b8;font-size:11px;display:inline-flex;align-items:center;gap:4px;">
              <img src="/logo-light-tight-sm.png" alt="DispatchDeck" style="height:14px;opacity:0.5;">
              Powered by DispatchDeck
            </a>
          </div>
        </div>

        <style>
          @media print {
            .no-print { display: none !important; }
            body { background: #fff !important; }
          }
        </style>`;
    }

    // Set page title
    document.title = `Invoice ${ticket.invoiceNumber || ''} - ${company.name}`;

    // Wire up Pay Now handler
    window.payInvoice = async (invoiceToken) => {
      const btn = document.getElementById('btn-pay-invoice');
      if (btn) {
        btn.disabled = true;
        btn.innerHTML = '<span class="btn-spinner"></span> Redirecting...';
      }
      try {
        const createSession = httpsCallable(functions, 'createInvoiceCheckoutSession', { timeout: 30000 });
        const res = await createSession({ token: invoiceToken });
        if (res.data?.url) {
          window.location.href = res.data.url;
        } else {
          throw new Error('No checkout URL returned');
        }
      } catch (err) {
        console.error('[Invoice] Pay error:', err);
        if (btn) {
          btn.disabled = false;
          btn.innerHTML = '<span class="material-icons" style="font-size:18px;vertical-align:middle;margin-right:6px;">credit_card</span> Pay Now';
        }
        const msg = err?.message?.includes('already been paid') ? 'This invoice has already been paid.' : 'Unable to process payment. Please try again.';
        showToast(msg, 'error');
      }
    };

  } catch (error) {
    console.error('[Invoice] Error loading public invoice:', error);
    const mainContent = document.getElementById('main-content');
    if (mainContent) {
      mainContent.innerHTML = `
        <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:60vh;padding:20px;text-align:center;">
          <span class="material-icons" style="font-size:64px;color:#DC2626;margin-bottom:16px;">error_outline</span>
          <h2 style="margin:0 0 8px 0;color:#0F172A;">Something went wrong</h2>
          <p style="margin:0 0 24px 0;color:#64748B;max-width:400px;">We couldn't load this invoice. Please try again or contact the sender.</p>
        </div>`;
    }
  }
}

// ============================================
// PUBLIC COMPANY DOCS PAGE
// ============================================
async function loadPublicDocsPage(token) {
  const escHTML = (s) => s ? String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;') : '';
  try {
    window.AppState.isPublicAccess = true;
    await ensurePublicAuth();

    // TASK-9888 — ONE token-checked server call, no membership. The server
    // re-checks the link on every load (exists, company_docs, not reset, not
    // expired) and returns only the publiclyShared docs as display fields.
    // This page used to call the membership-grant callable, which made every
    // visitor a members doc of the company for good.
    let payload;
    try {
      payload = (await httpsCallable(functions, 'getPublicCompanyDocs')({ token })).data || {};
    } catch (e) {
      const code = e?.code || '';
      showAccessError(code === 'functions/resource-exhausted'
        ? 'Too many requests from this network. Wait a few minutes and try again.'
        : code === 'functions/not-found' || code === 'functions/invalid-argument'
          ? 'This link is invalid or has been removed.'
          : 'Something went wrong loading this page. Please try again.');
      return false;
    }
    if (payload.ok !== true) {
      // integ/2090: showAccessError escapes its message itself (TASK-9900) — no escHTML here (would double-escape).
      const who = String(payload.company?.name || '') || 'the company that sent it';
      showAccessError(`This link has expired or was reset — contact ${who} for a new one.`);
      return false;
    }

    // Hide standard UI
    const header = document.getElementById('app-header');
    const sidebar = document.getElementById('app-sidebar');
    const mobileNav = document.getElementById('mobile-nav');
    if (header) header.style.display = 'none';
    if (sidebar) sidebar.style.display = 'none';
    if (mobileNav) mobileNav.style.display = 'none';
    document.body.classList.add('portal-mode');

    // Tenant branding comes back with the docs (names + https logo only).
    const companyName = payload.company?.name || 'Company Documents';
    const companyLogo = payload.company?.logoUrl || '';

    // Validate URL is a safe https URL before injecting into HTML attributes.
    // Returns the escaped URL or '#' if unsafe (blocks javascript:, data:, malformed URLs).
    const safeUrl = (u) => {
      try {
        const parsed = new URL(u);
        if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return '#';
        return escHTML(parsed.toString());
      } catch { return '#'; }
    };
    // TASK-1223 — RAW variant for DOM *property* assignment (el.href = ...).
    // safeUrl() HTML-entity-escapes (& → &amp;), which is correct inside HTML
    // attribute markup (the parser decodes it) but WRONG on the .href property
    // (no decoding happens): the Storage token param became `amp;token=...`,
    // every driver "Download" 403'd, and Safari saved the JSON error body
    // renamed .pdf→.json. Same protocol validation, no escaping.
    const safeUrlRaw = (u) => {
      try {
        const parsed = new URL(u);
        if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return '#';
        return parsed.toString();
      } catch { return '#'; }
    };

    // Shared resources: already filtered, projected and sorted (oldest first) server-side.
    const resources = Array.isArray(payload.docs) ? payload.docs : [];

    // Build page
    const mainContent = document.getElementById('main-content');
    if (!mainContent) return;

    const logoHTML = companyLogo
      ? `<img src="${escHTML(companyLogo)}" alt="" style="width:48px;height:48px;border-radius:10px;object-fit:contain;background:#fff;border:1px solid var(--border-light);">`
      : `<div style="width:48px;height:48px;border-radius:10px;background:linear-gradient(135deg,var(--brand-blue),var(--accent-teal));display:flex;align-items:center;justify-content:center;"><span class="material-icons" style="font-size:24px;color:#fff;">business</span></div>`;

    // Build dropdown options
    const optionsHTML = resources.map((r, i) => {
      const isDoc = r.type === 'document';
      const sizeStr = r.sizeBytes ? ` (${(r.sizeBytes / 1024 / 1024).toFixed(1)} MB)` : '';
      const label = escHTML(r.title) + (isDoc ? sizeStr : '');
      return `<option value="${i}">${label}</option>`;
    }).join('');

    // Stash resources for the selector handler
    window._publicDocsResources = resources;

    if (resources.length === 0) {
      mainContent.innerHTML = `
        <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:60vh;padding:20px;text-align:center;">
          <span class="material-icons" style="font-size:48px;margin-bottom:8px;color:var(--border-light);">folder_open</span>
          <p style="margin:0;color:var(--text-secondary);">No documents have been shared yet.</p>
        </div>
      `;
    } else {
      mainContent.innerHTML = `
        <div style="display:flex;flex-direction:column;height:100vh;height:100dvh;">
          <!-- Header bar -->
          <div style="padding:12px 20px;border-bottom:1px solid var(--border-light);background:var(--bg-elevated);display:flex;align-items:center;gap:12px;flex-shrink:0;">
            ${logoHTML}
            <div style="min-width:0;flex:1;">
              <h1 style="margin:0;font-size:1.1rem;font-weight:700;color:var(--text-primary);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escHTML(companyName)}</h1>
              <p style="margin:2px 0 0;font-size:0.75rem;color:var(--text-secondary);">Business Documents</p>
            </div>
          </div>
          <!-- Doc selector -->
          <div style="padding:12px 20px;border-bottom:1px solid var(--border-light);background:var(--bg-secondary);display:flex;align-items:center;gap:10px;flex-shrink:0;">
            <select id="public-docs-selector" onchange="window._publicDocsSelect(this.value)"
              style="flex:1;padding:8px 12px;border:1px solid var(--border-light);border-radius:var(--radius-sm);font-size:0.875rem;background:var(--bg-elevated);color:var(--text-primary);max-width:500px;">
              <option value="" disabled selected>Select a document…</option>
              ${optionsHTML}
            </select>
            <button id="public-docs-find-btn" onclick="window._publicDocsToggleSearch()" style="display:none;padding:8px 10px;background:var(--bg-elevated);border:1px solid var(--border-light);border-radius:var(--radius-sm);cursor:pointer;color:var(--text-secondary);flex-shrink:0;" title="Find in document">
              <span class="material-icons" style="font-size:18px;vertical-align:middle;">search</span>
            </button>
            <a id="public-docs-download" href="#" target="_blank" style="display:none;padding:8px 14px;background:var(--brand-blue);color:#fff;border-radius:var(--radius-sm);text-decoration:none;font-size:0.8rem;font-weight:600;white-space:nowrap;flex-shrink:0;">
              <span class="material-icons" style="font-size:16px;vertical-align:middle;margin-right:2px;">download</span> Download
            </a>
          </div>
          <!-- Search bar (hidden by default) -->
          <div id="public-docs-search-bar" style="display:none;padding:8px 20px;border-bottom:1px solid var(--border-light);background:var(--bg-elevated);flex-shrink:0;">
            <div style="display:flex;align-items:center;gap:8px;max-width:500px;">
              <span class="material-icons" style="font-size:18px;color:var(--text-secondary);">search</span>
              <input type="text" id="public-docs-search-input" placeholder="Search in document…"
                style="flex:1;padding:6px 10px;border:1px solid var(--border-light);border-radius:var(--radius-sm);font-size:0.85rem;background:var(--bg-secondary);color:var(--text-primary);"
                onkeydown="if(event.key==='Enter'){window._publicDocsSearch(this.value);event.preventDefault()}"
                onkeyup="if(event.key==='Escape'){window._publicDocsToggleSearch()}">
              <button onclick="window._publicDocsSearch(document.getElementById('public-docs-search-input').value)" style="padding:6px 12px;background:var(--brand-blue);color:#fff;border:none;border-radius:var(--radius-sm);cursor:pointer;font-size:0.8rem;font-weight:600;">Find</button>
              <button onclick="window._publicDocsToggleSearch()" style="padding:6px;background:none;border:none;cursor:pointer;color:var(--text-secondary);">
                <span class="material-icons" style="font-size:18px;">close</span>
              </button>
            </div>
          </div>
          <!-- Viewer area -->
          <div id="public-docs-viewer" style="flex:1;overflow:auto;background:#f1f5f9;display:flex;align-items:center;justify-content:center;">
            <div style="text-align:center;padding:60px 20px;">
              ${companyLogo
                ? `<img src="${escHTML(companyLogo)}" alt="" style="width:80px;height:80px;border-radius:16px;object-fit:contain;background:#fff;border:1px solid var(--border-light);margin-bottom:20px;">`
                : `<div style="width:80px;height:80px;border-radius:16px;background:linear-gradient(135deg,var(--brand-blue),var(--accent-teal));display:flex;align-items:center;justify-content:center;margin:0 auto 20px;"><span class="material-icons" style="font-size:40px;color:#fff;">business</span></div>`
              }
              <h2 style="margin:0 0 8px;font-size:1.25rem;font-weight:700;color:var(--text-primary);">${escHTML(companyName)}</h2>
              <p style="margin:0;font-size:1rem;color:var(--text-secondary);">Select a document above to get started</p>
            </div>
          </div>
          <!-- Footer -->
          <div style="text-align:center;padding:8px;border-top:1px solid var(--border-light);background:var(--bg-elevated);flex-shrink:0;">
            <a href="https://dispatchdeck.app" target="_blank" style="font-size:0.7rem;color:var(--text-secondary);text-decoration:none;">Powered by DispatchDeck</a>
          </div>
        </div>
      `;

      // Auto-select first doc if only one
      if (resources.length === 1) {
        document.getElementById('public-docs-selector').value = '0';
      }
    }

    // Document viewer handler
    // Search toggle + handler
    window._publicDocsToggleSearch = function() {
      const bar = document.getElementById('public-docs-search-bar');
      if (!bar) return;
      const visible = bar.style.display !== 'none';
      bar.style.display = visible ? 'none' : 'flex';
      if (!visible) {
        const input = document.getElementById('public-docs-search-input');
        if (input) { input.value = ''; input.focus(); }
      }
    };

    window._publicDocsSearch = function(term) {
      if (!term || !term.trim()) return;
      const r = window._publicDocsCurrentResource;
      if (!r) return;
      const isPdf = r.mimeType?.includes('pdf') || r.isPdf;
      if (isPdf) {
        // Chrome/Edge PDF viewer supports #search=term
        const viewer = document.getElementById('public-docs-viewer');
        const isMobile = window.innerWidth < 768;
        if (isMobile) {
          viewer.innerHTML = `<div style="width:100%;height:100%;display:flex;flex-direction:column;">
            <iframe src="https://docs.google.com/gview?url=${encodeURIComponent(r.url)}&embedded=true" style="flex:1;border:none;width:100%;" allowfullscreen></iframe>
          </div>`;
        } else {
          viewer.innerHTML = `<iframe src="${safeUrl(r.url)}#search=${encodeURIComponent(term.trim())}" style="width:100%;height:100%;border:none;" allowfullscreen></iframe>`;
        }
      } else {
        // Non-PDF: use browser find
        try { window.find(term.trim()); } catch (e) { /* unsupported */ }
      }
    };

    window._publicDocsSelect = function(idx) {
      const r = window._publicDocsResources[parseInt(idx)];
      if (!r) return;
      window._publicDocsCurrentResource = r;
      const viewer = document.getElementById('public-docs-viewer');
      const dlBtn = document.getElementById('public-docs-download');
      const findBtn = document.getElementById('public-docs-find-btn');
      if (!viewer) return;

      // Show download button
      if (dlBtn) {
        dlBtn.href = safeUrlRaw(r.url);
        dlBtn.style.display = 'inline-flex';
      }

      const isDoc = r.type === 'document';
      const isPdf = r.mimeType?.includes('pdf') || r.isPdf;
      const isImage = r.mimeType?.startsWith('image/');
      const isLink = !isDoc;

      // Show find button for PDFs — but NOT on mobile, where we render
      // via Google Docs gview iframe which doesn't honor #search=. Exposing
      // the button there gives a silent no-op (caught by Chris 2026-05-21).
      // Proper fix is shipping PDF.js viewer for true cross-platform search.
      const isMobileViewport = window.innerWidth < 768;
      if (findBtn) findBtn.style.display = (isPdf && !isMobileViewport) ? 'inline-flex' : 'none';
      // Hide search bar when switching docs
      const searchBar = document.getElementById('public-docs-search-bar');
      if (searchBar) searchBar.style.display = 'none';

      if (isPdf) {
        // Embed PDF — use Google Docs viewer as fallback for mobile
        const isMobile = window.innerWidth < 768;
        if (isMobile) {
          viewer.innerHTML = `
            <div style="width:100%;height:100%;display:flex;flex-direction:column;">
              <iframe src="https://docs.google.com/gview?url=${encodeURIComponent(r.url)}&embedded=true" style="flex:1;border:none;width:100%;" allowfullscreen></iframe>
            </div>`;
        } else {
          viewer.innerHTML = `<iframe src="${safeUrl(r.url)}" style="width:100%;height:100%;border:none;" allowfullscreen></iframe>`;
        }
      } else if (isImage) {
        viewer.innerHTML = `
          <div style="padding:20px;max-width:100%;max-height:100%;overflow:auto;display:flex;align-items:center;justify-content:center;">
            <img src="${safeUrl(r.url)}" alt="${escHTML(r.title)}" style="max-width:100%;max-height:80vh;border-radius:8px;box-shadow:0 2px 12px rgba(0,0,0,0.1);">
          </div>`;
      } else if (isLink) {
        // External link — show info + open button
        viewer.innerHTML = `
          <div style="text-align:center;padding:40px 20px;">
            <span class="material-icons" style="font-size:48px;color:var(--brand-blue);margin-bottom:12px;">open_in_new</span>
            <h3 style="margin:0 0 8px;color:var(--text-primary);">${escHTML(r.title)}</h3>
            ${r.desc ? `<p style="margin:0 0 16px;color:var(--text-secondary);font-size:0.875rem;">${escHTML(r.desc)}</p>` : ''}
            <a href="${safeUrl(r.url)}" target="_blank" rel="noopener noreferrer" class="btn btn-primary" style="text-decoration:none;display:inline-flex;align-items:center;gap:6px;">
              <span class="material-icons" style="font-size:18px;">open_in_new</span> Open Link
            </a>
          </div>`;
        if (dlBtn) dlBtn.style.display = 'none';
      } else {
        // Other file types — show download prompt
        const sizeStr = r.sizeBytes ? `${(r.sizeBytes / 1024 / 1024).toFixed(1)} MB` : '';
        viewer.innerHTML = `
          <div style="text-align:center;padding:40px 20px;">
            <span class="material-icons" style="font-size:48px;color:#3B82F6;margin-bottom:12px;">description</span>
            <h3 style="margin:0 0 4px;color:var(--text-primary);">${escHTML(r.title)}</h3>
            <p style="margin:0 0 16px;color:var(--text-secondary);font-size:0.8rem;">${escHTML(r.fileName || '')}${sizeStr ? ' · ' + sizeStr : ''}</p>
            <a href="${safeUrl(r.url)}" target="_blank" rel="noopener noreferrer" class="btn btn-primary" style="text-decoration:none;display:inline-flex;align-items:center;gap:6px;">
              <span class="material-icons" style="font-size:18px;">download</span> Download
            </a>
          </div>`;
      }
    };

    // Auto-show first doc
    if (resources.length === 1) {
      window._publicDocsSelect('0');
    }
    hideLoading();
    return true;
  } catch (err) {
    console.error('[Docs] Error loading public docs page:', err);
    showAccessError('Check your signal and try again.');
    return false;
  }
}

// ============================================
// DELIVERY SIGNATURE PAGE
// ============================================
async function loadSignaturePage(token) {
  try {
    // Sign in anonymously if not already signed in
    if (!auth.currentUser) {
      await signInAnonymously(auth);
    }

    // Validate the token
    const tokenDoc = await getDoc(doc(db, 'accessTokens', token));
    if (!tokenDoc.exists()) {
      console.error('[Signature] Token not found');
      showToast('Invalid signature link', 'error');
      return false;
    }

    const tokenData = tokenDoc.data();

    // Check token type — delivery receipt OR partner document signature (TASK-888)
    if (tokenData.type !== 'delivery_signature' && tokenData.type !== 'partner_doc_signature') {
      console.error('[Signature] Invalid token type:', tokenData.type);
      return false;
    }

    // Check if already used (applies to both token types)
    if (tokenData.used) {
      renderSignatureAlreadyUsed();
      return true;
    }

    // Check expiry (if set) — applies to both token types
    if (tokenData.expiresAt) {
      const expiryDate = tokenData.expiresAt.toDate ? tokenData.expiresAt.toDate() : new Date(tokenData.expiresAt);
      if (new Date() > expiryDate) {
        console.error('[Signature] Token expired');
        showToast('This signature link has expired', 'error');
        return false;
      }
    }

    // TASK-888 — partner document e-signature flow. Everything the (anonymous)
    // signer needs lives on the PUBLIC accessTokens doc (title, originalPdfUrl,
    // signerName, tenantId); the signatureRequests record is partner-read-only by
    // rules, so we never touch it here. Completion goes through the
    // submitPartnerDocSignature callable (the token is the authorization).
    if (tokenData.type === 'partner_doc_signature') {
      window.AppState.signatureToken = token;
      renderPartnerDocSignaturePage(token, tokenData);
      return true;
    }

    // Store token for submission
    window.AppState.tenant = tokenData.tenantId;
    window.AppState.signatureToken = token;
    window.AppState.signatureOrderId = tokenData.orderId;

    // Load the order
    const orderDoc = await getDoc(doc(db, 'tenants', tokenData.tenantId, 'orders', tokenData.orderId));
    if (!orderDoc.exists()) {
      console.error('[Signature] Order not found');
      showToast('Order not found', 'error');
      return false;
    }

    const order = { id: orderDoc.id, ...orderDoc.data() };

    // Load tenant data for company name
    const tenantDoc = await getDoc(doc(db, 'tenants', tokenData.tenantId));
    const tenantData = tenantDoc.exists() ? tenantDoc.data() : {};

    // Render signature page
    renderSignaturePage(order, tenantData);
    return true;

  } catch (error) {
    console.error('[Signature] Error loading signature page:', error);
    showToast('Error loading signature page', 'error');
    return false;
  }
}

function renderSignaturePage(order, tenantData) {
  hideLoading();
  hideAuthModal();
  document.body.classList.add('signature-mode');

  const companyName = tenantData.name || 'DispatchDeck';
  const requestedDate = order.requestedDate?.toDate?.() || new Date(order.requestedDate);
  const dateStr = requestedDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  const deliveredAt = order.statusHistory?.find(h => h.status === 'delivered')?.timestamp;
  const deliveredStr = deliveredAt?.toDate?.()?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) || 'N/A';

  const mainContent = document.getElementById('main-content');
  mainContent.innerHTML = `
    <div class="signature-page">
      <div class="signature-header">
        <h1>${escapeHtml(companyName)}</h1>
        <h2>Delivery Receipt</h2>
      </div>

      <div class="signature-ticket">
        <div class="ticket-info-grid">
          <div class="ticket-info-item">
            <span class="ticket-label">Customer</span>
            <span class="ticket-value">${escapeHtml(order.customerName || 'N/A')}</span>
          </div>
          <div class="ticket-info-item">
            <span class="ticket-label">PO Number</span>
            <span class="ticket-value">${escapeHtml(order.poNumber || 'N/A')}</span>
          </div>
          <div class="ticket-info-item">
            <span class="ticket-label">Requested</span>
            <span class="ticket-value">${dateStr}</span>
          </div>
          <div class="ticket-info-item">
            <span class="ticket-label">Delivered</span>
            <span class="ticket-value">${deliveredStr}</span>
          </div>
          <div class="ticket-info-item full-width">
            <span class="ticket-label">Location</span>
            <span class="ticket-value">${escapeHtml(order.location || 'N/A')}</span>
          </div>
        </div>

        <div class="signature-items">
          <h3>Items Received</h3>
          <div class="items-list">
            ${(order.items || []).map(item => `
              <div class="item-row">
                <span class="item-qty">${item.quantity}x</span>
                <span class="item-desc">${escapeHtml(item.partNumber)} - ${escapeHtml(item.name)}</span>
              </div>
            `).join('')}
          </div>
        </div>

        <div class="signature-capture">
          <h3>Sign Below</h3>
          <p class="signature-instructions">By signing below, you confirm receipt of the items listed above.</p>

          <div class="form-group">
            <label for="signer-name">Your Name *</label>
            <input type="text" id="signer-name" class="form-control" placeholder="Enter your name" required>
          </div>

          <div class="signature-canvas-container">
            <canvas id="signature-canvas" width="400" height="200"></canvas>
            <button type="button" class="btn btn-ghost btn-sm" onclick="window.clearSignature()">
              <span class="material-icons">refresh</span> Clear
            </button>
          </div>

          <button type="button" class="btn btn-primary btn-lg" id="btn-submit-signature" onclick="window.submitSignature()">
            <span class="material-icons">check</span> Confirm Receipt
          </button>
        </div>
      </div>
    </div>
  `;

  // Initialize signature canvas
  initSignatureCanvas();
}

function renderSignatureAlreadyUsed() {
  hideLoading();
  hideAuthModal();
  document.body.classList.add('signature-mode');

  const mainContent = document.getElementById('main-content');
  mainContent.innerHTML = `
    <div class="signature-page">
      <div class="signature-success">
        <span class="material-icons success-icon">verified</span>
        <h2>Already Signed</h2>
        <p>This delivery receipt has already been signed.</p>
        <p class="text-muted" style="margin-top: var(--spacing-lg);">You can close this page.</p>
      </div>
    </div>
  `;
}

// Signature canvas state
let signatureCanvas = null;
let signatureCtx = null;
let isDrawing = false;
let lastX = 0;
let lastY = 0;

function initSignatureCanvas() {
  signatureCanvas = document.getElementById('signature-canvas');
  if (!signatureCanvas) return;

  // TASK-4534 — #signature-canvas renders at width:100%/max-400px (styles.css)
  // over a fixed 400x150 backing store, and the draw handlers use raw CSS-pixel
  // coords: on narrow phones the stroke landed offset from the finger, and on
  // high-DPI screens it was blurry. Back the store at rect x dpr and scale the
  // ctx so CSS-pixel drawing is exact (same fix as pretrip's pad).
  {
    const _rect = signatureCanvas.getBoundingClientRect();
    const _dpr = window.devicePixelRatio || 1;
    if (_rect.width > 2 && _rect.height > 2) {
      signatureCanvas.width = Math.round(_rect.width * _dpr);
      signatureCanvas.height = Math.round(_rect.height * _dpr);
      signatureCanvas.getContext('2d').scale(_dpr, _dpr);
    }
  }

  signatureCtx = signatureCanvas.getContext('2d');
  signatureCtx.strokeStyle = '#1E293B';
  signatureCtx.lineWidth = 2;
  signatureCtx.lineCap = 'round';
  signatureCtx.lineJoin = 'round';

  // Clear canvas with white background
  signatureCtx.fillStyle = '#FFFFFF';
  signatureCtx.fillRect(0, 0, signatureCanvas.width, signatureCanvas.height);

  // Mouse events
  signatureCanvas.addEventListener('mousedown', startDrawing);
  signatureCanvas.addEventListener('mousemove', draw);
  signatureCanvas.addEventListener('mouseup', stopDrawing);
  signatureCanvas.addEventListener('mouseout', stopDrawing);

  // Touch events
  signatureCanvas.addEventListener('touchstart', handleTouchStart);
  signatureCanvas.addEventListener('touchmove', handleTouchMove);
  signatureCanvas.addEventListener('touchend', stopDrawing);
}

function startDrawing(e) {
  isDrawing = true;
  const rect = signatureCanvas.getBoundingClientRect();
  lastX = e.clientX - rect.left;
  lastY = e.clientY - rect.top;
}

function draw(e) {
  if (!isDrawing) return;
  e.preventDefault();

  const rect = signatureCanvas.getBoundingClientRect();
  const x = e.clientX - rect.left;
  const y = e.clientY - rect.top;

  signatureCtx.beginPath();
  signatureCtx.moveTo(lastX, lastY);
  signatureCtx.lineTo(x, y);
  signatureCtx.stroke();

  lastX = x;
  lastY = y;
}

function handleTouchStart(e) {
  e.preventDefault();
  const touch = e.touches[0];
  const rect = signatureCanvas.getBoundingClientRect();
  isDrawing = true;
  lastX = touch.clientX - rect.left;
  lastY = touch.clientY - rect.top;
}

function handleTouchMove(e) {
  if (!isDrawing) return;
  e.preventDefault();

  const touch = e.touches[0];
  const rect = signatureCanvas.getBoundingClientRect();
  const x = touch.clientX - rect.left;
  const y = touch.clientY - rect.top;

  signatureCtx.beginPath();
  signatureCtx.moveTo(lastX, lastY);
  signatureCtx.lineTo(x, y);
  signatureCtx.stroke();

  lastX = x;
  lastY = y;
}

function stopDrawing() {
  isDrawing = false;
}

window.clearSignature = function() {
  if (!signatureCtx || !signatureCanvas) return;
  signatureCtx.fillStyle = '#FFFFFF';
  signatureCtx.fillRect(0, 0, signatureCanvas.width, signatureCanvas.height);
};

function isCanvasBlank() {
  if (!signatureCanvas) return true;
  const imageData = signatureCtx.getImageData(0, 0, signatureCanvas.width, signatureCanvas.height);
  const data = imageData.data;

  // Check if all pixels are white (255, 255, 255)
  for (let i = 0; i < data.length; i += 4) {
    if (data[i] !== 255 || data[i + 1] !== 255 || data[i + 2] !== 255) {
      return false;
    }
  }
  return true;
}

window.submitSignature = async function() {
  const signerName = document.getElementById('signer-name')?.value?.trim();
  if (!signerName) {
    showToast('Please enter your name', 'error');
    return;
  }

  if (isCanvasBlank()) {
    showToast('Please sign in the box above', 'error');
    return;
  }

  const btn = document.getElementById('btn-submit-signature');
  btn.disabled = true;
  btn.innerHTML = '<span class="material-icons rotating">sync</span> Saving...';

  try {
    const tenant = window.AppState.tenant;
    const orderId = window.AppState.signatureOrderId;
    const token = window.AppState.signatureToken;

    // Get signature as data URL
    const signatureDataUrl = signatureCanvas.toDataURL('image/png');

    // Convert data URL to blob for upload
    const response = await fetch(signatureDataUrl);
    const blob = await response.blob();

    // Upload to Firebase Storage
    const signaturePath = `signatures/${tenant}/${orderId}_${Date.now()}.png`;
    const signatureRef = ref(storage, signaturePath);
    await uploadBytes(signatureRef, blob);
    const signatureUrl = await getDownloadURL(signatureRef);

    // Update order with signature data
    await updateDoc(doc(db, 'tenants', tenant, 'orders', orderId), {
      signatureUrl: signatureUrl,
      signedBy: signerName,
      signedAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });

    // Mark token as used
    await updateDoc(doc(db, 'accessTokens', token), {
      used: true,
      usedAt: Timestamp.now()
    });

    // Show success
    showSignatureSuccess(signerName);

  } catch (error) {
    console.error('[Signature] Error submitting signature:', error);
    showToast('Error saving signature. Please try again.', 'error');
    btn.disabled = false;
    btn.innerHTML = '<span class="material-icons">check</span> Confirm Receipt';
  }
};

function showSignatureSuccess(signerName) {
  const mainContent = document.getElementById('main-content');
  mainContent.innerHTML = `
    <div class="signature-page">
      <div class="signature-success">
        <span class="material-icons success-icon">verified</span>
        <h2>Receipt Confirmed!</h2>
        <p>Thank you, ${escapeHtml(signerName)}. Your signature has been recorded.</p>
        <p class="text-muted" style="margin-top: var(--spacing-lg);">You can close this page now.</p>
      </div>
    </div>
  `;
}

// ============================================
// TASK-888 — Partner document e-signature page (arbitrary uploaded PDF).
// Mirrors renderSignaturePage's canvas UX (#signature-canvas + window.clearSignature
// + initSignatureCanvas + toDataURL capture) but for a partner-sent PDF. The PDF is
// download-only (target=_blank, never rendered inline — XSS surface). All token-
// derived strings are escaped; the prefilled name + PDF href are set via JS so a
// value/href can't break out of the attribute.
// ============================================
function renderPartnerDocSignaturePage(token, tokenData) {
  hideLoading();
  hideAuthModal();
  document.body.classList.add('signature-mode');

  const title = tokenData.title || 'Document';
  const pdfUrl = (typeof tokenData.originalPdfUrl === 'string' && /^https:\/\//i.test(tokenData.originalPdfUrl))
    ? tokenData.originalPdfUrl : '';

  const mainContent = document.getElementById('main-content');
  mainContent.innerHTML = `
    <div class="signature-page">
      <div class="signature-header">
        <h1>Signature Requested</h1>
        <h2>${escapeHtml(title)}</h2>
      </div>

      <div class="signature-ticket">
        <div class="signature-doc-review" style="text-align:center;margin-bottom:var(--spacing-lg);">
          ${pdfUrl
            ? `<a id="sig-doc-download" class="btn btn-secondary btn-lg" target="_blank" rel="noopener">
                 <span class="material-icons">picture_as_pdf</span> Download &amp; review the document
               </a>`
            : `<p class="text-muted">The document could not be loaded. Please contact the sender.</p>`}
          <p class="signature-instructions" style="margin-top: var(--spacing-md);">Open and review the document above, then sign below to confirm.</p>
        </div>

        <div class="signature-capture">
          <h3>Sign Below</h3>
          <p class="signature-instructions">By signing below, you confirm you have reviewed and agree to the document above.</p>

          <div class="form-group">
            <label for="signer-name">Your Name *</label>
            <input type="text" id="signer-name" class="form-control" placeholder="Enter your name" required>
          </div>

          <div class="signature-canvas-container">
            <canvas id="signature-canvas" width="400" height="200"></canvas>
            <button type="button" class="btn btn-ghost btn-sm" onclick="window.clearSignature()">
              <span class="material-icons">refresh</span> Clear
            </button>
          </div>

          <button type="button" class="btn btn-primary btn-lg" id="btn-submit-signature" onclick="window.submitPartnerDocSignature()">
            <span class="material-icons">check</span> Submit Signature
          </button>
        </div>
      </div>
    </div>
  `;

  // Set the PDF href + the prefilled name via JS (avoids attribute-injection — escapeHtml
  // doesn't encode quotes, and these are token-derived).
  if (pdfUrl) {
    const dl = document.getElementById('sig-doc-download');
    if (dl) dl.href = pdfUrl;
  }
  const nameInput = document.getElementById('signer-name');
  if (nameInput && tokenData.signerName) nameInput.value = String(tokenData.signerName);

  // Reuse the exact delivery-flow canvas init.
  initSignatureCanvas();
}

window.submitPartnerDocSignature = async function() {
  const signerName = document.getElementById('signer-name')?.value?.trim();
  if (!signerName) {
    showToast('Please enter your name', 'error');
    return;
  }
  if (isCanvasBlank()) {
    showToast('Please sign in the box above', 'error');
    return;
  }

  const btn = document.getElementById('btn-submit-signature');
  if (!btn || btn._sigInFlight) return;   // double-click / in-flight guard
  btn._sigInFlight = true;
  btn.disabled = true;
  btn.innerHTML = '<span class="material-icons rotating">sync</span> Saving...';

  try {
    const token = window.AppState.signatureToken;
    const signatureDataUrl = signatureCanvas.toDataURL('image/png');

    // Same unauthenticated callable pattern the rest of the signer page uses.
    const submitFn = httpsCallable(functions, 'submitPartnerDocSignature');
    await submitFn({ token, signerName, signatureDataUrl });

    showPartnerDocSignatureSuccess(signerName);
  } catch (error) {
    console.error('[Signature] partner doc submit error:', error);
    showToast((error && error.message) || 'Error saving signature. Please try again.', 'error');
    btn._sigInFlight = false;
    btn.disabled = false;
    btn.innerHTML = '<span class="material-icons">check</span> Submit Signature';
  }
};

function showPartnerDocSignatureSuccess(signerName) {
  const mainContent = document.getElementById('main-content');
  mainContent.innerHTML = `
    <div class="signature-page">
      <div class="signature-success">
        <span class="material-icons success-icon">verified</span>
        <h2>Signed — thank you!</h2>
        <p>Thank you, ${escapeHtml(signerName)}. Your signature has been recorded.</p>
        <p class="text-muted" style="margin-top: var(--spacing-lg);">You can close this page now.</p>
      </div>
    </div>
  `;
}

// ============================================
// AUTH STATE HANDLING
// ============================================
// TASK-9526 — SINGLE-FLIGHT sign-in. handleAuthStateChange is reached from
// onAuthStateChanged, from the email-login watchdog, and from explicit callers
// (social sign-in, signup, invite accept) that fire right after the listener
// already did. Two runs for the SAME uid used to execute CONCURRENTLY: the
// second one's loadUserTenant reset memberAccess to null mid-way through the
// first (an invited staffer then hit the owner "Create Your Primary PIN"
// wizard), and its trailing navigation remounted the current view ~13s after
// login (the Clock form lost the picked customer → "Please select a customer";
// reproduced 6/6 on WebKit, prod 1.15.1995).
//
// Contract (pinned by tests/auth-single-flight.unit.cjs):
//   - a call for a uid with NO run in flight starts one immediately (sync start,
//     same as before);
//   - a call for the SAME uid while a run is in flight does NOT start a second
//     concurrent run. It queues ONE trailing run that starts after the current
//     one settles (any number of such calls collapse into that one), so an
//     explicit caller that changed state (users doc migrated, invite accepted)
//     still gets a fresh pass — serialized, never overlapped;
//   - a trailing run is dropped if a DIFFERENT uid has started since (stale);
//   - sign-out (null user) and a different uid are never queued: unchanged.
//     Sign-out also drops any trailing run queued for the old uid;
//   - a run older than staleMs is treated as hung, so a new call may start
//     fresh rather than wait forever behind it.
// Self-contained on purpose (no app globals) so the unit test can lift it
// verbatim with js-slice and drive it in a vm.
function _createAuthSingleFlight(run, opts) {
  const staleMs = (opts && opts.staleMs) || 30000;
  const now = (opts && opts.now) || (() => Date.now());
  let flight = null; // { uid, startedAt, promise, trailing, trailingUser }

  function start(user) {
    const f = { uid: user.uid, startedAt: now(), promise: null, trailing: null, trailingUser: null };
    flight = f;
    let p;
    try { p = Promise.resolve(run(user)); } catch (err) { p = Promise.reject(err); }
    f.promise = p;
    const clear = () => { if (flight === f && !f.trailing) flight = null; };
    p.then(clear, clear);
    return p;
  }

  function invoke(user) {
    const uid = user && user.uid ? user.uid : null;
    if (!uid) {
      // Sign-out invalidates whatever was in flight, so a trailing run queued
      // for the old uid is dropped (`flight !== f`) instead of re-signing-in a
      // user who just left (COD Code Reviewer, TASK-9526).
      flight = null;
      return run(user);
    }
    if (flight && flight.uid === uid && (now() - flight.startedAt) < staleMs) {
      const f = flight;
      f.trailingUser = user;
      if (!f.trailing) {
        f.trailing = f.promise.then(() => {}, () => {}).then(() => {
          if (flight !== f) return undefined; // a different uid took over — stale
          flight = null;
          return start(f.trailingUser);
        });
      }
      return f.trailing;
    }
    return start(user);
  }

  invoke.inFlightFor = (uid) => !!(uid && flight && flight.uid === uid
    && (now() - flight.startedAt) < staleMs);
  return invoke;
}

// `var`, not let/const: handleAuthStateChange is a hoisted declaration that
// init code registers with onAuthStateChanged, and a TDZ binding read before
// this line evaluates would throw. Built lazily on first call.
var _authSingleFlight;
function _getAuthSingleFlight() {
  if (!_authSingleFlight) _authSingleFlight = _createAuthSingleFlight(_runAuthStateChange);
  return _authSingleFlight;
}
function handleAuthStateChange(user) {
  try { window.__ddBootMark?.(user ? 'auth:user' : 'auth:none'); } catch (_) {} // boot-watchdog telemetry step
  return _getAuthSingleFlight()(user);
}
function _authHandlerInFlightFor(uid) {
  return _getAuthSingleFlight().inFlightFor(uid);
}

// TASK-9526 — bounded wait for an in-progress member-access read to settle.
// Resolves true once memberAccessPending is false, false on timeout.
function _awaitMemberAccessSettled(timeoutMs) {
  return new Promise((resolve) => {
    const started = Date.now();
    const tick = () => {
      if (!window.AppState?.memberAccessPending) return resolve(true);
      if (Date.now() - started >= timeoutMs) return resolve(false);
      setTimeout(tick, 100);
    };
    tick();
  });
}

async function _runAuthStateChange(user) {
  console.log('[Auth] State changed:', user ? user.uid : 'signed out');

  // Skip if signup is in progress - signup will call us manually when done
  if (window.AppState.signupInProgress) {
    console.log('[Auth] Signup in progress, skipping auth state handling');
    return;
  }

  // Skip if we're in the middle of signing out (e.g. exiting driver PIN mode)
  if (window.AppState.signingOut) {
    console.log('[Auth] Sign-out in progress, skipping auth state handling');
    return;
  }

  // Skip if driver PIN login is in progress (submitDriverPin handles navigation)
  if (window.AppState.driverPinLoginInProgress) {
    console.log('[Auth] Driver PIN login in progress, skipping auth state handling');
    return;
  }

  // TASK-429 (Fix B1) — consume the one-shot flag set when the PIN-restore block
  // registered this listener. onAuthStateChanged fires immediately on register
  // for the already-restored anon user; the restore block already mounted the
  // view, so swallow exactly that first same-uid fire to avoid a redundant
  // remount during the listener-wiring window. Only suppress when it's truly the
  // expected restored-anon fire (PIN mode + same uid we restored); otherwise
  // clear the flag and fall through so no real later event is ever masked.
  if (window.AppState._pinRestoreSkipInitialAuthFire) {
    window.AppState._pinRestoreSkipInitialAuthFire = false;
    if (window.AppState.driverModePin === true && user && user.isAnonymous
        && user.uid === window.AppState.user?.uid) {
      console.log('[Auth] Swallowing initial PIN-restore auth fire (view already mounted)');
      return;
    }
  }

  // Skip if we're on a public token page (docs, order, invoice, clock, etc.)
  // These pages handle their own auth/anon flow — the main auth handler would clobber them
  if (window.AppState.publicTokenPage || window.AppState.isPublicAccess) {
    console.log('[Auth] Public token page active, skipping auth state handling');
    return;
  }

  // Skip if profile setup is already in progress (prevent re-entry from onAuthStateChanged)
  if (window.AppState._profileSetupActive) {
    console.log('[Auth] Profile setup in progress, skipping auth state handling');
    return;
  }

  // Skip if we're on a public access page (timeoff, order, signature, etc.)
  if (window.AppState.isPublicAccess) {
    console.log('[Auth] Public access page active, skipping auth state handling');
    return;
  }

  if (user) {
    // Wrap entire auth flow in try/catch — if anything throws, still complete login
    try {
    // TASK-429 — capture the PRIOR uid BEFORE we overwrite AppState.user, so the
    // PIN-driver branch below can detect an anonymous-uid ROLL (Fix #2) and the
    // real-login-over-stale-PIN case (Fix B2) before state is clobbered.
    const _prevUid = window.AppState.user?.uid || null;
    window.AppState.user = user;
    // TASK-10218 — account switch with no explicit logout: drop the previous account's
    // banners before this login paints its own further down.
    if (_prevUid && _prevUid !== user.uid) teardownAccountScopedChrome();

    // If an anonymous user is lingering from a previous public page visit,
    // sign them out so they get the login screen instead of demo mode
    // Use ORIGINAL_URL_PARAMS — immune to version-checker URL manipulation
    const hasPublicToken = ORIGINAL_URL_PARAMS.get('timeoff') || ORIGINAL_URL_PARAMS.get('approve') || ORIGINAL_URL_PARAMS.get('order') || ORIGINAL_URL_PARAMS.get('sign') || ORIGINAL_URL_PARAMS.get('access') || ORIGINAL_URL_PARAMS.get('docs');
    if (user.isAnonymous && !hasPublicToken && !window.AppState.driverModePin && !window.AppState.pendingDemo && !window.AppState.isDemo) {
      // Cross-tab demo guard (2026-08-17) — this anonymous user may belong to a
      // demo ANOTHER tab just started; Firebase broadcasts it to every tab on
      // the origin, and pendingDemo/isDemo above are per-tab in-memory state.
      // Signing out here killed that demo mid-seed. Show login in THIS tab
      // (the shared Firebase session really is anonymous now, so there is no
      // account session left to render) but leave the session itself alone.
      // Deliberately does NOT fall through: two tabs both entering demo mode
      // would race resetDemoData() + seedDemoData() against each other.
      if (isDemoActiveInAnotherTab()) {
        console.log('[Auth] Anonymous session belongs to a demo in another tab — showing login here, NOT signing out');
        window.AppState.user = null;
        hideLoading();
        hideSidebar();
        showAuthModal();
        return;
      }
      console.log('[Auth] Stale anonymous session detected, signing out to show login');
      window.AppState.signingOut = true;
      try { await auth.signOut(); } catch (e) { /* ignore */ }
      window.AppState.signingOut = false;
      window.AppState.user = null;
      hideLoading();
      hideSidebar();
      showAuthModal();
      return;
    }

    // TASK-4775 — a session survived to here, so this visitor is NOT a cold
    // prospect: drop any ?signup=true intent. Placed AFTER the stale-anonymous
    // branches above (those end at showAuthModal() + return, and a marketing CTA
    // clicked while a demo runs in another tab SHOULD still reach signup), and
    // before the tenant load, so a logout later in this same tab lands on Sign In.
    window.AppState.pendingSignupIntent = false;

    // TASK-429 Fix B2 — a REAL (non-anonymous) user just signed in while a
    // driver-PIN session is still flagged active (e.g. someone restored a PIN
    // session, then logged in with email/password on the same device). Without
    // this, isPinDriverMode below would be true and we'd take the PIN branch,
    // SKIPPING loadUserTenant → the real account would inherit the PIN tenant
    // context (cross-tenant risk). Clear the PIN session state WITHOUT forcing
    // a signout (same reset fields as exitDriverPinMode / handleLogout) and
    // fall through to the normal authenticated path so loadUserTenant runs.
    if (window.AppState.driverModePin === true && !user.isAnonymous) {
      console.log('[Auth] Real account signed in over a stale driver-PIN session — clearing PIN state, falling through to normal login');
      // Tear down any PIN-view subscriptions before the tenant context changes,
      // so an in-flight jobboard listener can't fire under the new (real) uid.
      if (window.AppState.viewCleanup) {
        try { window.AppState.viewCleanup(); } catch (_) {}
        window.AppState.viewCleanup = null;
      }
      // TASK-903 — shared PIN chrome + AppState residue teardown (token, driver*
      // fields, body.driver-mode-pin, #driver-pin-shell-chrome, stub nav).
      _teardownDriverPinChrome();
      // Clear the PIN tenant context so loadUserTenant resolves the REAL account's
      // tenant; KEEP the (real, non-anonymous) signed-in user.
      window.AppState.tenant = null;
      window.AppState.tenantData = null;
      window.AppState.settings = {};
      // Cross-tenant residue guards (mirror handleLogout / exitDriverPinMode).
      window.AppState.fleetTrucks = null;
      window.AppState.fleetTrailers = null;
      window.AppState.fleetPeople = null;

      window.ClockMode?.reset?.(); // TASK-9553
      try { window.DispatchModule?.resetTenantData?.(); } catch (_) {} // P0 1019 — board roster/fleet never outlives the tenant
      // (no signOut — the real user IS the session we want to keep)
    }

    // TASK-429 Fix #2 — the anonymous uid ROLLED while we're still in driver-PIN
    // mode (version-forced reload, token rotation, or a late persisted-session
    // restore). Already-mounted jobboard onSnapshot listeners would re-evaluate
    // under the new, NOT-YET-LINKED uid during its rule-propagation window →
    // permission-denied flood → dead listeners → "logged out". Survive it:
    // (a) tear down ALL view subscriptions FIRST, (b) re-link the NEW uid +
    // re-gate on driverSessions visibility, (c) THEN let the PIN branch below
    // remount. Listeners must never live through a propagation window under an
    // unlinked uid. (Skipped during the normal fresh-PIN login — that path
    // sets driverPinLoginInProgress, which already short-circuited above.)
    if (window.AppState.driverModePin === true && user.isAnonymous
        && _prevUid && _prevUid !== user.uid) {
      console.warn('[Auth] Driver-PIN anon uid rolled', _prevUid, '→', user.uid, '— re-linking + re-gating before remount');
      // (a) Tear down view subscriptions FIRST.
      if (window.AppState.viewCleanup) {
        try { window.AppState.viewCleanup(); } catch (_) {}
        window.AppState.viewCleanup = null;
      }
      // (b) Re-link the new uid to the existing driver token, then re-gate.
      try {
        const _tok = window.AppState.driverAccessToken;
        if (_tok) {
          const _relink = httpsCallable(functions, 'linkDriverSession');
          const _r = await _relink({ token: _tok });
          if (!_r?.data?.ok) {
            console.error('[Auth] Re-link after uid roll failed:', _r?.data?.error);
            // Can't recover the driver session — drop to PIN screen cleanly.
            await exitDriverPinMode();
            return;
          }
          // Refresh the cached ID token so the tenantId/role claims for the NEW
          // uid are present before any Storage upload (same as the login paths).
          try { await user.getIdToken(true); } catch (_) {}
          // Gate on rule-visibility of driverSessions/{newUid} before remount.
          const _vis = await _awaitDriverSessionVisible(window.AppState.tenant);
          if (!_vis.snap) {
            console.warn('[Auth] Re-linked session not rule-visible after retries — dropping to PIN screen');
            await exitDriverPinMode();
            return;
          }
        } else {
          console.warn('[Auth] uid rolled but no driverAccessToken to re-link — dropping to PIN screen');
          await exitDriverPinMode();
          return;
        }
      } catch (_relinkErr) {
        console.error('[Auth] Re-link/re-gate after uid roll threw:', _relinkErr?.message);
        await exitDriverPinMode();
        return;
      }
      // (c) Fall through — the PIN branch below remounts the view under the
      // now-linked, rule-visible new uid.
    }

    // Check if this is PIN-based driver access (anonymous but NOT demo)
    const isPinDriverMode = window.AppState.driverModePin === true;
    window.AppState.isDemo = user.isAnonymous && !isPinDriverMode;
    window.AppState.pendingDemo = false;

    // Load user's tenant (skip for PIN driver mode - tenant already set by submitDriverPin)
    if (!isPinDriverMode) {
      await loadUserTenant(user.uid);

      // Ensure custom claims have tenantId (for Storage rules tenant scoping)
      //
      // TASK-1658 — skipped for a partner inside a client account. setTenantClaims
      // derives the tenant SERVER-side from users/{uid} (verified owner/member) or by
      // scanning tenants.ownerId — it takes no client input and knows nothing about
      // ?client=, so a partner is neither, and the call can only ever no-op. Verified:
      // there is NO path by which the carrier's tenantId reaches the partner's Auth
      // token, which matters because the token IS shared across every tab on this
      // origin. Skipping it is purely to drop a guaranteed-useless round-trip that
      // cold-started slowly enough to trip the 5s "login modal still visible"
      // watchdog and re-run the whole auth handler.
      if (window.AppState.tenant && !user.isAnonymous && !isPartnerClientSession()) {
        try {
          const tokenResult = await user.getIdTokenResult();
          if (!tokenResult.claims.tenantId) {
            console.log('[Auth] Setting tenant claims...');
            const setClaimsFn = httpsCallable(functions, 'setTenantClaims');
            await setClaimsFn();
            await user.getIdToken(true); // Force refresh to pick up new claims
            console.log('[Auth] Tenant claims set successfully');
          }
        } catch (claimsErr) {
          console.warn('[Auth] Failed to set tenant claims (non-blocking):', claimsErr.message);
        }
      }

      // If tenant didn't load (orphan auth, incomplete social signup), stop here
      if (!window.AppState.tenant && !window.AppState.isDemo) {
        // signupInProgress means loadUserTenant detected a new social user and is showing
        // the company name modal — don't interfere
        if (window.AppState.signupInProgress) {
          console.log('[Auth] Social signup in progress, letting company name modal handle it');
          return;
        }
        console.log('[Auth] No tenant loaded — orphan auth');
        hideLoading();
        // TASK-545 follow-up: a compliance PARTNER who signs in at the MAIN app has no
        // users/{uid}/tenant (their identity lives in _users/{uid}). Route them to the
        // partner portal instead of dead-ending at the carrier login modal.
        // getComplianceManagerData succeeds ONLY for a complianceManager (throws
        // permission-denied otherwise), so a non-partner orphan falls straight through.
        // No redirect-loop risk: this is the main app; compliance-manager.html runs its
        // own auth and never loads app.js.
        // TASK-1658 — a VALID ?client= can never reach here: loadUserTenant
        // resolved a tenant, so `!window.AppState.tenant` above is false. This is
        // belt-and-braces so a future edit upstream cannot bounce a partner who
        // arrived with a legitimate client context straight back out of the app,
        // which would make the whole feature unreachable.
        if (isPartnerClientSession()) {
          console.warn('[Auth] Partner client context is live but no tenant loaded — not redirecting');
          showToast('Could not open that client account.', 'error');
          return;
        }
        try {
          await httpsCallable(functions, 'getComplianceManagerData')();
          console.log('[Auth] Orphan auth is a compliance partner — redirecting to /compliance-manager-v2.html');
          window.location.href = '/compliance-manager-v2.html';
          return;
        } catch (cmErr) { /* not a compliance partner — fall through to normal orphan handling */ }
        console.log('[Auth] Orphan auth, showing login');
        showToast('Account not found. Please sign in again.', 'error');
        try { await signOut(auth); } catch (e) { /* ignore */ }
        showAuthModal();
        return;
      }
    } else {
      // For PIN driver mode, load minimal tenant settings (needed for clockOutMode, billing config)
      console.log('[Auth] PIN driver mode - using preset tenant:', window.AppState.tenant);
      window.AppState.tenantData = {};
      window.AppState.settings = {};
      try {
        const _pinTenantDoc = await getDoc(doc(db, 'tenants', window.AppState.tenant));
        if (_pinTenantDoc.exists()) {
          window.AppState.settings = _pinTenantDoc.data().settings || {};
          console.log('[Auth] Loaded tenant settings for PIN driver mode');
          await primeSelfWithin(); // TASK-9553
        }
      } catch (_pinSettingsErr) {
        console.warn('[Auth] Failed to load PIN driver settings:', _pinSettingsErr.message);
      }
    }

    // For demo mode: reset old data first, then seed fresh — show progress
    if (window.AppState.isDemo) {
      // Keep the cross-tab heartbeat alive for as long as this demo runs, so a
      // sibling tab (or one opened later) never mistakes this anon session for
      // a stale one and signs it out from under us.
      startDemoHeartbeat();
      const _demoUid = user.uid;
      const loadEl = document.getElementById('loading-screen');
      const loadMsg = loadEl?.querySelector('p');
      if (loadMsg) loadMsg.textContent = 'Setting up your demo fleet...';
      showLoading();
      await resetDemoData().catch(err => console.error('[Demo] Reset error:', err));
      // Belt-and-suspenders: if ANYTHING dropped the session while we were
      // clearing (the cross-tab signout above was one cause, a token roll is
      // another), stop here. Continuing wrote a half-seeded demo and left the
      // app in the tenant-null zombie state the signed-out branch then has to
      // clean up. That branch owns the UI from here.
      if (auth.currentUser?.uid !== _demoUid) {
        console.warn('[Demo] Auth session changed mid-reset — abandoning demo setup');
        stopDemoHeartbeat();
        return;
      }
      if (loadMsg) loadMsg.textContent = 'Adding trucks, crew & jobs...';
      await seedDemoData();
      // Same ownership check again on the far side of the seed: another tab can
      // take the demo over mid-seed (it signs this session out first), and a
      // tab that keeps going from here would render a welcome banner and mount
      // views on a session that no longer exists.
      if (auth.currentUser?.uid !== _demoUid) {
        console.warn('[Demo] Auth session changed mid-seed — abandoning demo setup');
        stopDemoHeartbeat();
        return;
      }
      if (loadMsg) loadMsg.textContent = 'Almost ready...';
      // Custom demo welcome toast with Academy link (8s duration)
      const demoToast = document.createElement('div');
      demoToast.className = 'toast-banner success';
      demoToast.innerHTML = `Welcome to the demo! Explore freely. <a href="/marketing/academy.html" target="${academyTarget()}" style="color:#fff;text-decoration:underline;font-weight:600;margin-left:6px;">Watch Training Videos &rarr;</a>`;
      document.body.appendChild(demoToast);
      setTimeout(() => {
        demoToast.style.animation = 'slideDown 0.3s ease reverse';
        setTimeout(() => demoToast.remove(), 300);
      }, 8000);
    }

    // Check email verification for non-demo users (skip for PIN driver mode)
    // TASK-1658 — never for a partner inside a client account: this gate reads the
    // SIGNED-IN user's email verification and, on success, syncs it onto the TENANT
    // doc. A partner's verified email says nothing about the carrier's, and the
    // not-verified branch would hard-wall the partner behind the carrier owner's
    // unfinished signup.
    if (!window.AppState.isDemo && !isPinDriverMode && !isComplianceOnlyTenant() && !isPartnerClientSession()) {
      // Force reload to pick up emailVerified changes (clicking link doesn't update cached token)
      if (!user.emailVerified) {
        try { await user.reload(); } catch (e) { /* ignore reload errors */ }
      }
      // Trust tenant doc emailVerified flag (set by verification endpoint or admin)
      const tenantEmailVerified = window.AppState.tenantData?.emailVerified === true;
      if (user.emailVerified || tenantEmailVerified) {
        // User is verified - update tenant doc if not already marked.
        // TASK-682: the emailVerified field is now on the firestore.rules denylist
        // (TASK-685 S2b), so the old client updateDoc() fails with permission-denied.
        // Route through the syncMyEmailVerified server callable, which validates the
        // caller's OWN Firebase Auth email_verified server-side and writes via the
        // Admin SDK (bypasses the denylist).
        if (window.AppState.tenantData?.emailVerified === false) {
          try {
            const syncVerified = httpsCallable(functions, 'syncMyEmailVerified');
            const res = await syncVerified({ tenantId: window.AppState.tenant });
            if (res?.data?.verified) {
              window.AppState.tenantData.emailVerified = true;
              console.log('[Auth] Email verification confirmed and saved (server)');

              // Log for Overlord
              await logEmailEvent({
                type: 'verification_confirmed',
                recipient: user.email,
                tenantId: window.AppState.tenant,
                status: 'verified',
                provider: 'firebase-auth'
              });
            }
          } catch (e) {
            console.warn('[Auth] Could not update verification status:', e);
          }
        }
      } else {
        // Not verified - check grace period
        const deadline = window.AppState.tenantData?.verificationDeadline?.toDate?.() ||
                         window.AppState.tenantData?.verificationDeadline;
        const now = new Date();

        if (deadline && now > deadline) {
          // Grace period expired - block access
          hideLoading();
          hideAuthModal();
          showEmailVerificationPending();
          return;
        }

        // Within grace period - show warning banner but allow access
        window.AppState.showVerificationBanner = true;
      }
    }

    // Check trial status (skip for demo and PIN driver mode)
    // TASK-1658 — skipped for a partner inside a client account, same reasoning as
    // the driver-PIN skip at the navigation wall (~:7769): a partner does not own
    // this subscription. autoDowngradeExpiredTrial is admin-gated (it would 403
    // and drop through to the hard lockout), and the trial-expired screen is a
    // full-page subscribe wall on the CARRIER's billing — it would make the DQ
    // files unreachable to the person hired to maintain them. TASK-1659 owns the
    // partner-pays flow.
    if (!window.AppState.isDemo && !isPinDriverMode && !isPartnerClientSession()) {
      const trialStatus = getTrialStatus();
      console.log('[Auth] Trial status:', trialStatus);

      if (trialStatus.status === 'expired') {
        // Auto-downgrade to compliance-only mode instead of hard lockout.
        // Keeps the carrier on the platform (Roster / HR / Compliance) and
        // they can attach a card to run BG checks via the new metered flow.
        // Subscription upgrade CTAs remain prominent in the compliance-only
        // sidebar. Falls back to the legacy trial-expired screen if the
        // downgrade callable fails.
        try {
          const downgrade = httpsCallable(functions, 'autoDowngradeExpiredTrial');
          const res = await downgrade({ tenantId: window.AppState.tenant });
          if (res?.data?.ok) {
            // Refresh local tenantData so isComplianceOnlyTenant() returns true
            // and the rest of the boot path renders the compliance-only sidebar.
            const tenantDoc = await getDoc(doc(db, 'tenants', window.AppState.tenant));
            if (tenantDoc.exists()) {
              window.AppState.tenantData = tenantDoc.data();
            }
            // Stash a one-time banner reason so the UI can surface
            // "Your trial ended — you're on free Compliance now" with an
            // upgrade CTA.
            window.AppState.justAutoDowngraded = true;
            console.log('[Auth] Trial expired — auto-downgraded to compliance-only');
            // Continue boot
          } else {
            console.warn('[Auth] Auto-downgrade declined:', res?.data?.reason);
            hideLoading();
            hideAuthModal();
            hideEmailVerificationPending();
            showTrialExpiredScreen();
            return;
          }
        } catch (err) {
          console.error('[Auth] Auto-downgrade failed, falling back to lockout:', err);
          hideLoading();
          hideAuthModal();
          hideEmailVerificationPending();
          showTrialExpiredScreen();
          return;
        }
      }

      // Store trial info for banner
      window.AppState.trialStatus = trialStatus;
    }

    // Show app
    hideLoading();
    hideAuthModal();
    hideSidebar(); // Hide verification screen if it was showing
    hideEmailVerificationPending();
    hideTrialExpiredScreen();

    // Show verification reminder banner if needed (before PIN setup so it's visible during onboarding)
    if (window.AppState.showVerificationBanner) {
      showVerificationReminderBanner();
      // TASK-682 — re-check on focus/visibility so a verify-in-another-tab auto-clears it.
      startEmailVerifyRecheck();
    }

    // Show trial reminder banner if applicable (skip if verification banner is already showing)
    if (window.AppState.trialStatus?.status === 'trialing' && window.AppState.trialStatus?.daysRemaining <= 3 && !window.AppState.showVerificationBanner) {
      showTrialBanner(window.AppState.trialStatus.daysRemaining);
    }
    // Chris 2026-10-16 — declined annual renewal: days left + Update card (owner).
    if (window.AppState.trialStatus?.dunning === true && !window.AppState.showVerificationBanner) {
      showAnnualGraceBanner(annualGraceBannerState(window.AppState.tenantData, {
        tenantId: window.AppState.tenant, uid: window.AppState.user?.uid, nowMs: Date.now(),
      }));
    }
    // TASK-1649 slice 2b-2 — the partner moved check costs to us (owner / Billing).
    if (!window.AppState.showVerificationBanner) {
      showBgCostMovedBanner(bgCostMovedNoticeState(window.AppState.tenantData, {
        tenantId: window.AppState.tenant, nowMs: Date.now(), canManage: hrCanManageComplianceBilling(),
      }));
    }
    // TASK-1659 — the compliance partner stopped paying (or its card failed):
    // the owner sees the 30-day window and an Add card button.
    if (!window.AppState.showVerificationBanner && !window.AppState.isDemo && !isPinDriverMode) {
      showPartnerGraceBanner(partnerGraceBannerState(window.AppState.tenantData, {
        tenantId: window.AppState.tenant, uid: window.AppState.user?.uid, nowMs: Date.now(),
      }));
    }

    // CRM "tasks due today" banner. TASK-932 — was founder-tenant-only; CRM is a
    // per-tenant module, so it now follows the module grant like every other gate
    // (checkModuleAccess handles member modules, driver/public modes and PINs; the
    // compliance-only preview grant is excluded because that tenant has no CRM).
    if (checkModuleAccess('crm', true) === true && !isComplianceOnlyTenant()) {
      checkCrmTasksBanner();
    }

    // TASK-1550 — the signed-in user's own reminders (banner + My reminders).
    // Runs after memberAccess resolved above; Reminders gates itself on the
    // module that owns each record type and stays off for driver/link sessions.
    try { window.Reminders?.start(); } catch (e) { console.warn('[Reminders] start failed:', e?.message || e); }

    // TASK-9619 — Team Notes orb beside the logo. Gates itself on the `notes`
    // module (owner via ownerId); never driver/PIN/link sessions.
    try { window.TeamNotes?.start(); } catch (e) { console.warn('[TeamNotes] start failed:', e?.message || e); }

    // TASK-9657 — count of Claude requests waiting for THIS person's approval.
    // Gates itself (window.carrierAssistantEligible; never driver/PIN/link).
    try { window.ApprovalsBadge?.start(); } catch (e) { console.warn('[ApprovalsBadge] start failed:', e?.message || e); }

    // Check if PIN setup is required (non-demo users with no PINs, skip for PIN driver mode and compliance-only)
    // TASK-596 — a per-user staff MEMBER (TASK-319 login: staff/dispatcher/mechanic
    // with granted modules) must NOT be blocked by the owner-worded primary-PIN
    // setup wizard: they can't set the tenant's PIN and per-user logins don't use
    // the PIN system at all. TASK-1529 widened the exemption to ALL invited
    // members (any non-owner with modules[]) — only owners, legacy admins (no
    // modules field), and legacy single-login owners (memberAccess null) set
    // up the PIN.
    // memberAccess is resolved here (handleAuthStateChange awaits loadUserTenant
    // at :5025 before this gate; TASK-595's loading gate further guarantees it).
    //
    // TASK-9526 — …unless ANOTHER writer has a member read in flight right now
    // (the concurrent second sign-in run that single-flight now prevents was one;
    // a future one must not be able to wall an invited staffer either). Never
    // judge "is this an invited member?" off a half-loaded memberAccess: wait for
    // it (bounded), and if it still has not settled, skip the owner wizard for
    // this pass — an owner with no PINs simply gets it on the next sign-in.
    let _pinGateUnsettled = false;
    if (window.AppState.memberAccessPending) {
      _pinGateUnsettled = !(await _awaitMemberAccessSettled(8000));
      if (_pinGateUnsettled) console.warn('[Auth] Member access still loading at the PIN-setup gate — skipping the owner PIN wizard this pass');
    }
    const _ma = window.AppState.memberAccess;
    // TASK-1529 — the primary-PIN setup wizard is OWNER-worded ("create your
    // company PIN") and blocks the whole app. Any INVITED member — scoped
    // modules[] present, role !== 'owner' — should never hit it, admins
    // included: an outside bookkeeper/consultant invited with role 'admin'
    // can't meaningfully set the tenant's kiosk PINs and was walled on first
    // login. Owners and legacy admins (no modules field — the pre-TASK-319
    // de-facto operators) keep the wizard exactly as before.
    const _isInvitedMember = !!(_ma && _ma.role !== 'owner'
      && Array.isArray(_ma.modules) && _ma.modules.length > 0
      && !['revoked', 'inactive', 'pending'].includes(_ma.status));
    if (!window.AppState.isDemo && !isPinDriverMode && !isComplianceOnlyTenant() && !_isInvitedMember && !_pinGateUnsettled) {
      const accessPins = accessPinMap();
      if (Object.keys(accessPins).length === 0) {
        // No PINs configured - show required PIN setup
        showRequiredPinSetup();
        return; // Don't proceed until PIN is created
      }
    }

    showSidebar();

    // Hide PRO badges for founder account
    hideUpgradeBadges();

    // If owner profile setup is needed, skip ALL view rendering and hints — go straight to profile setup
    // TASK-1658 — a partner must never be walked through the CARRIER OWNER's
    // profile-setup wizard (it opens the owner's roster person record for edit).
    const needsProfileSetup = !window.AppState.isDemo && !isComplianceOnlyTenant() && !isPartnerClientSession() &&
      window.AppState.tenantData?.ownerProfileSetup === false;
    if (needsProfileSetup) {
      window.AppState._profileSetupActive = true;
      checkOwnerProfileSetup();
      return;
    }

    // Compliance-only tenants default to compliance view
    if (isComplianceOnlyTenant() && (!window.location.hash || window.location.hash === '#/' || window.location.hash === '#/undefined' || window.location.hash === '#/roster')) {
      window.AppState.currentView = 'compliance';
    }

    // One-time notification when a tenant just auto-downgraded from trial.
    // Tells the carrier why their sidebar shrank + offers the upgrade CTA.
    // Wording varies for partner-managed tenants — gives the partner's
    // relationship the credit instead of saying "free."
    if (window.AppState.justAutoDowngraded) {
      window.AppState.justAutoDowngraded = false;
      setTimeout(() => {
        try {
          const partnerName = (window.AppState?.tenantData?.compliancePartner || '').trim();
          const msg = partnerName
            ? `Trial ended — you're now on Compliance through ${partnerName}. Roster, HR, and Compliance stay open. Upgrade to unlock Dispatch, Maintenance, Billing, and BI.`
            : 'Trial ended — you\'re now on free Compliance. Roster, HR, and Compliance stay open. Upgrade to unlock Dispatch, Maintenance, Billing, and BI.';
          window.showToast?.(msg, 'info');
        } catch (_) {}
      }, 1200);
    }

    // Load current view
    // 2026-05-22 QA sweep F1: re-derive default view from memberAccess BEFORE
    // navigating. The pre-auth setupRouting() call fires handleHashChange()
    // when memberAccess is still undefined, setting currentView='roster'.
    // For a staff member without roster access, navigating to that stale
    // currentView lands on "No access - Roster" (the iOS TWA cold-start
    // screenshot Chris flagged 2026-05-22). If the explicit hash isn't a
    // granted module, override to the member's natural default.
    try {
      const _memberAccess = window.AppState?.memberAccess;
      const _isMember = _memberAccess && _memberAccess.status === 'active'
        && _memberAccess.role !== 'owner'
        && Array.isArray(_memberAccess.modules)
        && _memberAccess.modules.length > 0;
      if (_isMember) {
        // TASK-355 2026-05-22: My Today killed; jobboard is now the canonical
        // personId-scoped daily view for SPA members.
        // TASK-1097 — pretrip/dvir are force-granted so driver boards work, but
        // NOT for compliance-only tenants (no driver board there; Inspections is
        // noise in their view). Fail OPEN to the historical always-grant if the
        // signal is unavailable — never deny a paying tenant's member.
        const _complianceOnly = typeof isComplianceOnlyTenant === 'function' && isComplianceOnlyTenant();
        const _grantedNav = new Set(_complianceOnly
          ? [..._memberAccess.modules, 'account', 'approvals']
          : [..._memberAccess.modules, 'pretrip', 'dvir', 'account', 'approvals']);
        const _hashView = (window.location.hash.slice(2) || '').split('?')[0].split('/')[0];
        const _curView = window.AppState.currentView;
        // Use explicit hash if it's granted; otherwise pick the member default.
        if (_hashView && _grantedNav.has(_hashView)) {
          window.AppState.currentView = _hashView;
        } else if (!_grantedNav.has(_curView)) {
          // dispatch-first member default (see computeMemberDefaultView).
          window.AppState.currentView = computeMemberDefaultView(_memberAccess);
        }
      }
    } catch (_routeErr) { console.warn('[Auth] Member default-view re-derivation failed (non-fatal):', _routeErr.message); }
    navigateToView(window.AppState.currentView);
    // TASK-345 Slice L — after the user's first view loads, idle-warm the
    // common follow-up views so the next switchTab is instant.
    try { _preloadCommonViews(); } catch (e) { console.warn('[Lazy] preload trigger failed:', e?.message); }

    // TASK-781 — drain a pending QBO OAuth return (stashed in init()) now that
    // auth + tenant state are loaded. Land the user on Billing → QuickBooks (the
    // connected panel) + a success/error pop-out, instead of the default module
    // + a 2s-delayed toast.
    if (window._pendingQboReturn && !isPinDriverMode) {
      const _qbo = window._pendingQboReturn;
      window._pendingQboReturn = null;
      (async () => {
        // navigateToView dynamically imports the billing module, so
        // window.BillingModule.openBillingSettings may not exist the instant the
        // call returns — briefly poll for it (mirrors the ?hr= deep-link pattern,
        // but robust to import timing instead of a fixed delay).
        const _openBillingSettingsWhenReady = async () => {
          try { navigateToView('billing'); } catch (navErr) { console.warn('[QBO] nav to billing failed:', navErr); }
          for (let i = 0; i < 25; i++) { // ~5s max (25 × 200ms)
            if (typeof window.BillingModule?.openBillingSettings === 'function') {
              try { window.BillingModule.openBillingSettings(); } catch (e) { console.warn('[QBO] openBillingSettings failed:', e); }
              return;
            }
            await new Promise(r => setTimeout(r, 200));
          }
          console.warn('[QBO] BillingModule.openBillingSettings never became available — left on Billing view.');
        };
        try {
          if (_qbo.status === 'complete') {
            let connected = false;
            let companyName = '';
            try {
              const checkStatus = httpsCallable(functions, 'qboCheckStatus');
              const res = await checkStatus({});
              if (res.data?.connected) {
                connected = true;
                companyName = res.data.companyName || '';
                if (window.AppState?.tenantData) {
                  window.AppState.tenantData.qboConnected = true;
                  window.AppState.tenantData.qboStatus = 'connected';
                  window.AppState.tenantData.qboCompanyName = res.data.companyName;
                  window.AppState.tenantData.qboRealmId = res.data.realmId;
                }
              }
            } catch (statusErr) {
              console.warn('[QBO] status check failed (non-fatal):', statusErr?.message);
            }
            // Land on Billing → QuickBooks regardless so the user sees the panel
            // (connected state if confirmed, Connect button if status is unclear).
            await _openBillingSettingsWhenReady();
            if (connected) {
              showConfirm(
                'QuickBooks Connected',
                `Your QuickBooks Online account is connected and ready to sync.${companyName ? '\n' + companyName : ''}`,
                { icon: 'check_circle', iconColor: '#059669', hideCancel: true, confirmText: 'Done' }
              );
            } else {
              // Callback fired but we couldn't confirm the connection — don't
              // falsely claim success. Surface the Settings panel + a soft notice.
              showConfirm(
                'Finishing QuickBooks Setup',
                'We couldn\'t confirm the QuickBooks connection yet. Open QuickBooks settings to check the status or try reconnecting.',
                { icon: 'info', iconColor: '#2563EB', hideCancel: true, confirmText: 'OK' }
              );
            }
          } else if (_qbo.status === 'error') {
            await _openBillingSettingsWhenReady();
            showConfirm(
              'QuickBooks Connection Failed',
              `${_qbo.reason || 'An unexpected error occurred.'} Please try again from QuickBooks settings.`,
              { icon: 'error', iconColor: '#DC2626', hideCancel: true, confirmText: 'Close' }
            );
          }
        } catch (e) { console.warn('[QBO] post-OAuth handler error:', e); }
      })();
    }

    // Deep link: ?hr=<personId> — focus a specific employee in HR after data loads.
    // Used by the compliance partner portal "Open in HR" link so consultants can
    // jump straight from the compliance modal to the driver's HR doc vault.
    try {
      const hrParam = ORIGINAL_URL_PARAMS.get('hr');
      if (hrParam && !isPinDriverMode) {
        // Strip the param so it doesn't re-fire on every render
        const url = new URL(window.location);
        url.searchParams.delete('hr');
        const remaining = url.searchParams.toString();
        window.history.replaceState(null, '', url.pathname + (remaining ? '?' + remaining : '') + url.hash);
        setTimeout(() => {
          try {
            navigateToView('hr');
            setTimeout(() => {
              if (window.HRModule?.openEmployeeDetail) {
                window.HRModule.openEmployeeDetail(hrParam);
              }
            }, 600);
          } catch (e) { console.warn('[HR deep link] open failed:', e); }
        }, 800);
      }
    } catch (e) { console.warn('[HR deep link] handler error:', e); }

    // Setup wizard banner for new accounts missing key data
    if (!isPinDriverMode) {
      setTimeout(() => checkSetupWizard(), 1500);
    }

    // Initialize Trailguide contextual help (skip for PIN driver mode and compliance-only)
    if (!isPinDriverMode) {
      setTimeout(() => {
        initTrailguide();
        initSalesButton(); // Floating sales button for demo mode
        initDemoTrialCtas(); // Trial conversion CTAs for demo mode
        if (!isComplianceOnlyTenant()) initLockFab(); // Floating lock/unlock button for non-demo accounts
        if (!isComplianceOnlyTenant()) initLockSidebarLogo(); // TASK-763 — sidebar logo = desktop lock/unlock + status pill
        if (!isComplianceOnlyTenant()) initSparkles(); // Training sparkles
        if (!isComplianceOnlyTenant()) initChangedMarkers(); // TASK-1098 "what changed" nav markers
        if (!isComplianceOnlyTenant()) initSmartAssistant(); // Inline data quality warnings
        const t = window.AppState?.tenant;
        if (!isComplianceOnlyTenant()) {
          initOnboardingHints(t && t !== 'demo' ? (hintsSeen) => {
            updateDoc(doc(db, 'tenants', t), { onboardingHintsSeen: hintsSeen })
              .catch(err => console.warn('[Onboarding] Firestore persist error:', err));
          } : undefined);
        }
      }, 500);

      // PWA install banner (iOS + Android)
      setTimeout(() => initInstallBanner(), 2000);
    }
    } catch (authError) {
      // If auth flow throws, still try to complete login so user isn't stuck on login screen
      console.error('[Auth] Error during auth state handling:', authError);
      logError(authError, 'handleAuthStateChange', { uid: user.uid, tenant: window.AppState.tenant });
      try {
        hideLoading();
        hideAuthModal();
        showSidebar();
        navigateToView(window.AppState.currentView || 'dispatch');
        showToast('Signed in with errors — some features may not load. Try refreshing.', 'error');
      } catch (recoveryError) {
        console.error('[Auth] Recovery also failed:', recoveryError);
        showToast('Login error. Please refresh and try again.', 'error');
      }
    }
  } else {
    // TASK-905 Part A (flood fix, mode-agnostic) — tear down any surviving view
    // subscriptions (e.g. Pretrip + JSA onSnapshot listeners) BEFORE clearing
    // state, so they stop re-evaluating as the now-null user and spewing
    // permission-denied. Runs for EVERY sign-out (carrier logout too), not just
    // PIN mode.
    try { window.AppState.viewCleanup?.(); } catch (_) {}
    window.AppState.viewCleanup = null;

    // TASK-905 Part B (driver-PIN silent recovery) — an anon-session drop while a
    // driver is mid-shift (token rotation, network blip, version-forced reload)
    // fires onAuthStateChanged(null). The old path dumped them straight to the
    // carrier login screen ("kicked out"). Instead, if we're STILL in PIN mode
    // with a saved token and this is NOT a deliberate sign-out, silently
    // re-establish the driver session and remount the current view. We short-
    // circuit + return BEFORE showAuthModal so TASK-903's _teardownDriverPinChrome
    // does NOT run on a transient drop (the PIN chrome stays mounted).
    if (window.AppState.driverModePin === true && !window.AppState.signingOut) {
      let _savedPin = null;
      try { _savedPin = JSON.parse(localStorage.getItem('driverPinAccess') || 'null'); } catch (_) {}
      if (_savedPin && _savedPin.token) {
        const _res = await _recoverDriverPinSession(_savedPin);
        if (_res.recovered) {
          // Reconnected in place — never show login, never tear down PIN chrome.
          return;
        }
        // Recovery abandoned. If the token was only PRESERVED (transient failure),
        // fall through to a dedicated "Reconnecting failed — re-enter your PIN"
        // state on the Driver Code tab (NOT a silent carrier-login dump). If the
        // token was DEFINITIVELY invalid (and cleared), drop to the normal login.
        if (!_res.definitiveInvalid) {
          window.AppState._pinReconnectFailed = true;
          // Stash the preserved session — showAuthModal()'s _teardownDriverPinChrome
          // (TASK-903) clears localStorage.driverPinAccess, so we re-persist it after
          // the modal shows. Honors mitigation #3: a transient failure keeps the
          // token so closing + reopening the app auto-restores without re-typing.
          window.AppState._pinReconnectSaved = _savedPin;
        }
      }
    }

    // Same as handleLogout: this tab's demo (if any) is over, so release the
    // cross-tab heartbeat before isDemo is cleared.
    if (window.AppState.isDemo) stopDemoHeartbeat();
    window.AppState.user = null;
    window.AppState.tenant = null;
    window.AppState.isDemo = false;
    window.AppState.showVerificationBanner = false;
    // TASK-379 — clear fleet bridge so a subsequent tenant on a shared device
    // never reads the prior tenant's trucks/trailers before fresh snapshots land.
    window.AppState.fleetTrucks = null;
    window.AppState.fleetTrailers = null;
    window.AppState.fleetPeople = null;

    window.ClockMode?.reset?.(); // TASK-9553
    try { window.DispatchModule?.resetTenantData?.(); } catch (_) {} // P0 1019 — board roster/fleet never outlives the tenant

    // Remove verification banner if it exists
    dismissVerificationBanner();
    stopEmailVerifyRecheck(); // TASK-682 — drop verify-recheck handler when signed out

    // Clean up UI elements that shouldn't persist to login screen
    const salesFab = document.getElementById('sales-fab');
    if (salesFab) salesFab.remove();
    const lockFab = document.getElementById('lock-fab');
    if (lockFab) lockFab.remove();
    teardownLockSidebarLogo(); // TASK-763 — clear sidebar lock pill + reset logo art (no stale "Locked")
    dismissTrialBanner();
    teardownAccountScopedChrome(); // TASK-10218

    // Show auth modal
    hideLoading();
    hideSidebar();
    showAuthModal();

    // TASK-905 — dedicated reconnect-failed state. The driver has no carrier
    // login, so after an ABANDONED transient recovery land them on the Driver
    // Code tab with a clear message instead of the generic Sign In screen.
    if (window.AppState._pinReconnectFailed) {
      window.AppState._pinReconnectFailed = false;
      // Re-persist the preserved token (showAuthModal's _teardownDriverPinChrome
      // cleared it) — mitigation #3: a transient failure keeps the saved session
      // so a close + reopen auto-restores via checkDriverPinSession without
      // re-typing the code. The driver may still re-enter the PIN here to
      // reconnect immediately.
      try {
        if (window.AppState._pinReconnectSaved) {
          localStorage.setItem('driverPinAccess', JSON.stringify(window.AppState._pinReconnectSaved));
        }
      } catch (_) {}
      window.AppState._pinReconnectSaved = null;
      try {
        if (typeof showDriverPinForm === 'function') showDriverPinForm();
        showToast?.('Reconnecting failed — please re-enter your PIN to continue.', 'error');
      } catch (_) {}
    }
  }
}

async function loadUserTenant(userId) {
  // TASK-1528 — raise the TASK-595 loading gate for the ENTIRE tenant+member
  // resolution, not just the member getDoc below (~:6162). AppState.user is
  // already set by handleAuthStateChange (:5426), so a navigation firing while
  // the users-doc / tenant-doc reads here are still in flight used to fall
  // through checkModuleAccess's member branch (memberAccess not loaded yet)
  // into the PIN fallback ("settings not loaded → 'needs-pin'") and throw a
  // spurious #pin-challenge-modal over a signed-in scoped member's app — and
  // the modal survives even after access resolves. With the gate up from the
  // first await, those navigations defer (bounded 5s, navigateToView ~:7692)
  // and re-gate once access is authoritative. Cleared on EVERY exit path by
  // the function-level finally; demo / driver-PIN checks in checkModuleAccess
  // run BEFORE the gate, so those flows never defer.
  window.AppState.memberAccessPending = true;
  try { window.__ddBootMark?.('tenant:load'); } catch (_) {} // boot-watchdog telemetry step
  try {
    // TASK-1658 — a compliance PARTNER arriving at dispatchdeck.app/?client=<tenantId>.
    // Resolves ONLY when the signed-in uid is a verified complianceManager AND the
    // requested tenant is in its server-held managedTenants; anything else returns
    // null and the untouched single-tenant path below runs exactly as before.
    // No param → this is a single `URLSearchParams.get` and an immediate null, so
    // every carrier / member / driver session is unaffected.
    const _partnerCtx = await resolvePartnerClientContext();
    if (_partnerCtx && await loadPartnerClientTenant(_partnerCtx, userId)) return;

    // Get user profile to find their tenant
    const userDoc = await getDoc(doc(db, 'users', userId));
    try { window.__ddBootMark?.('tenant:user-doc'); } catch (_) {} // boot-watchdog telemetry step

    if (userDoc.exists()) {
      const userData = userDoc.data();
      ddLog('[Auth] User data:', { email: userData.email, tenantId: userData.tenantId });

      if (!userData.tenantId) {
        console.error('[Auth] User exists but has no tenantId - account may need repair');
        showToast('Account setup incomplete. Please contact support.', 'error');
        hideLoading();
        showAuthModal();
        return;
      }

      window.AppState.tenant = userData.tenantId;
      console.log('[Auth] Loaded tenant:', window.AppState.tenant);

      // Telemetry: per-load heartbeat so we can answer "did this user open the
      // app since X?" without grepping Twilio/Stripe/Sentry. See lib/telemetry.js.
      window.Telemetry?.logSessionStart();

      // Track last login on both tenant and user doc (non-blocking)
      updateDoc(doc(db, 'tenants', userData.tenantId), { lastLoginAt: new Date() }).catch(e => console.warn('[Auth] tenant lastLogin failed:', e.message));
      updateDoc(doc(db, 'users', userId), { lastLoginAt: new Date() }).catch(e => console.warn('[Auth] user lastLogin failed:', e.message));

      // Load tenant settings (including accessPins) for PIN verification
      // TASK-531 — does this uid ALSO have a compliance-partner identity? The
      // provisioner stamps users/{uid}.partnerOwnTenant=true when it creates the
      // partner's own comped sandbox. We can't read _users/{uid} from the client
      // (rules deny it), so this stamp is the client-visible signal that the uid
      // is a partner → reveal the "Switch to Advisor Portal" link regardless of
      // which tenant they're currently viewing.
      window.AppState.isCompliancePartner = userData.partnerOwnTenant === true;

      const tenantDoc = await getDoc(doc(db, 'tenants', userData.tenantId));
      if (tenantDoc.exists()) {
        const tenantData = tenantDoc.data();
        window.AppState.settings = tenantData.settings || {};
        window.AppState.tenantData = tenantData; // Store full tenant data for email verification check
        console.log('[Auth] Loaded tenant settings');
      }

      // TASK-531 — reveal the Advisor Portal switcher + the Partner Sandbox
      // banner now that tenantData + the partner flag are loaded.
      if (typeof applyPartnerSandboxUi === 'function') applyPartnerSandboxUi();
      // TASK-402 — show the "Ask DispatchDeck" FAB for owner/admin. Called again
      // below once memberAccess loads (role may downgrade eligibility).
      if (typeof initCarrierAssistant === 'function') initCarrierAssistant();
      // Subscribe to live updates so admin PIN changes show up without a restart
      if (typeof subscribeTenantSettings === 'function') subscribeTenantSettings(userData.tenantId);

      // TASK-319: Load this user's member doc for per-user module access.
      // members/{uid}.modules is the ABSOLUTE source of truth for signed-in
      // users; the PIN system below only applies to users with no member doc
      // (kiosk path). Owner/legacy-admin members see everything.
      //
      // TASK-366: Seed from a one-shot getDoc for fast first-paint, then hand
      // off to subscribeMemberAccess() (onSnapshot) so admin updates to the
      // user's modules via updateMemberAccess propagate live without reload.
      // TASK-595 — reset to a SAFE default (no member grant) BEFORE the read so
      // an error path can never leave a STALE prior-login memberAccess that
      // skips a PIN (COD Code Reviewer). The success/else branches overwrite it;
      // a thrown read defaults to the no-doc → PIN/owner path. Then mark loading
      // so checkModuleAccess defers rather than spuriously PIN-challenging an
      // entitled member while this resolves on a slow cold-load.
      window.AppState.memberAccess = null;
      window.AppState.memberAccessPending = true;
      try {
        const memberDoc = await getDoc(doc(db, 'tenants', userData.tenantId, 'members', userId));
        if (memberDoc.exists()) {
          const m = memberDoc.data() || {};
          window.AppState.memberAccess = {
            uid: userId,
            role: m.role || null,
            modules: Array.isArray(m.modules) ? m.modules : null,
            personId: m.personId || null,
            // TASK-1489 — TRUE only for an INTENTIONALLY personless member (an
            // outside compliance manager / bookkeeper invited with no employee
            // record). Read from the explicit stored flag, NOT derived from a
            // missing personId: an employee whose login was simply never linked
            // must keep today's "ask an admin to link your roster profile" hint,
            // while a deliberate outside user should never see it at all. Those
            // are different states and conflating them would silently change
            // behavior for every pre-existing unlinked member.
            personless: m.personless === true,
            status: m.status || 'active',
            // TASK-1098 — tenure signal for the changed-markers baseline.
            acceptedAtMs: (typeof m.acceptedAtMs === 'number') ? m.acceptedAtMs : null,
            // TASK-608 — "View & upload only" capability flag. Surfaced into
            // memberAccess so future module views can pre-disable edit/delete
            // buttons (UX nicety). ENFORCEMENT is firestore.rules-only via
            // isViewUploadOnly(); this flag is advisory in the client and must
            // never be the sole gate (a UI-only flag is bypassable). Absent ==
            // false == unrestricted.
            viewUploadOnly: m.viewUploadOnly === true,
            // TASK-1403 final COD — the callables' _usersDocTenantAuthorized refuses
            // a readOnly member; surfaced so client gates (hr.js _hrIntrastateGate)
            // can hide what the server will refuse. Advisory only.
            readOnly: m.readOnly === true,
            // TASK-9986 S1 — the owner's per-module "can change settings" switch
            // (server-written; read by canChangeModuleSettings). Advisory: rules +
            // callables enforce. Absent = none.
            settingsModules: Array.isArray(m.settingsModules) ? m.settingsModules.filter(x => typeof x === 'string') : [],
            // TASK-9389 S1 — the owner's "can manage logins" switch (server-written;
            // read by roster.js _canManageStaffLogins). Advisory: callables enforce.
            manageLogins: m.manageLogins === true,
            // Member display name (TASK-319 writes this on the member doc).
            // Used by jobboard.js getActiveJobBoardPersonName() as a fallback
            // when the SPA member's roster person doc lookup fails.
            displayName: m.displayName || null,
            // TASK-9369 — HOME BRANCH (a settings.yards slug), written by the
            // updateMemberAccess callable. It is a DEFAULT FILTER for Roster →
            // Assets and NOTHING else: never a read boundary, never a gate, and
            // no future code may treat it as one (Chris: any branch user can
            // look at other branches; TASK-9375). Rules have no field allowlist
            // on members/{uid}, so an admin CAN hand-write any string here —
            // accepted because it gates nothing. Mirrored in subscribeMemberAccess
            // (the second writer of this object — a field added only here is
            // dropped on the next snapshot). Not the same thing as
            // people/{id}.yardId (the person's assigned facility, hr.js).
            yardId: (typeof m.yardId === 'string' && m.yardId) ? m.yardId : null,
          };
          console.log('[Auth] Member access loaded:', {
            role: m.role,
            modulesCount: Array.isArray(m.modules) ? m.modules.length : 0,
          });
        } else {
          // No member doc = legacy single-login owner. Explicitly null out any
          // residual memberAccess from a prior account in this SPA session so
          // loadUserTenant is authoritative on every login path (not just the
          // logout button) — a stale sub-account module set must never leak
          // into the owner's nav. null → checkModuleAccess legacy/owner path.
          window.AppState.memberAccess = null;
          console.log('[Auth] No member doc for this user — falling back to legacy PIN access');
        }
      } catch (e) {
        console.warn('[Auth] Member doc load failed (non-fatal):', e.message);
      } finally {
        // TASK-595 — read resolved (doc / null / error). Clear the loading gate
        // so deferred navigations re-gate against the now-authoritative access.
        window.AppState.memberAccessPending = false;
      }
      // TASK-402 — re-evaluate the assistant FAB now that memberAccess is known.
      // The earlier call (above) ran before the member doc loaded; a non-owner
      // member (dispatcher/staff) must NOT see the FAB, so this hides it.
      if (typeof initCarrierAssistant === 'function') initCarrierAssistant();
      // TASK-366 — live-sync member access. Survives the rest of this signed-in
      // session; torn down on handleLogout.
      if (typeof subscribeMemberAccess === 'function') subscribeMemberAccess(userData.tenantId, userId);

      // TASK-745 → TASK-1224 — the hold-to-dim state is ephemeral (memory-only),
      // so there are no prefs to load here: this just starts the session with a
      // clean, undimmed sidebar.
      if (window.SidebarCustomize) window.SidebarCustomize.onTenantReady();

      // TASK-346 v1 polish — compute hasDirectReports once per session.
      // A person is a supervisor if at least one other person in the tenant
      // has reportsTo === thisPersonId. Cached on AppState so the sidebar
      // render does not re-query Firestore every time hideUpgradeBadges runs.
      // Fire-and-forget; the sidebar gate re-runs when this resolves.
      window.AppState.hasDirectReports = false;
      const _supervisorPersonId = window.AppState?.memberAccess?.personId || null;
      if (_supervisorPersonId) {
        try {
          const reportsQ = query(
            collection(db, 'tenants', userData.tenantId, 'people'),
            where('reportsTo', '==', _supervisorPersonId),
            limit(1)
          );
          const reportsSnap = await getDocs(reportsQ);
          window.AppState.hasDirectReports = !reportsSnap.empty;
          console.log('[Auth] hasDirectReports:', window.AppState.hasDirectReports, '(supervisorPersonId=', _supervisorPersonId, ')');
        } catch (e) {
          console.warn('[Auth] hasDirectReports lookup failed (non-fatal):', e.message);
        }
      }
    } else if (window.AppState.isDemo) {
      // Demo users get a special demo tenant
      window.AppState.tenant = 'demo';
      console.log('[Auth] Using demo tenant');
    } else {
      // No user doc for this UID — try finding by email (handles UID changes after account reset)
      // Wrapped in try/catch because Firestore rules deny cross-user queries; on rejection
      // we just fall through to the social-signup or orphan paths below.
      const email = auth.currentUser?.email;
      if (email) {
        try {
          const emailLookup = await getDocs(query(collection(db, 'users'), where('email', '==', email), limit(1)));
          if (!emailLookup.empty) {
            const existingUserData = emailLookup.docs[0].data();
            const oldUid = emailLookup.docs[0].id;
            console.log('[Auth] Found user doc by email under old UID', oldUid, '— migrating to', userId);
            // Migrate: create user doc under current UID, pointing to existing tenant
            await setDoc(doc(db, 'users', userId), {
              email: email,
              tenantId: existingUserData.tenantId,
              createdAt: existingUserData.createdAt || new Date()
            });
            // Also migrate members doc so Firestore rules recognize the new UID
            try {
              const oldMemberDoc = await getDoc(doc(db, 'tenants', existingUserData.tenantId, 'members', oldUid));
              if (oldMemberDoc.exists()) {
                await setDoc(doc(db, 'tenants', existingUserData.tenantId, 'members', userId), oldMemberDoc.data());
                console.log('[Auth] Migrated members doc from', oldUid, 'to', userId);
              }
            } catch (memberErr) {
              console.warn('[Auth] Could not migrate members doc:', memberErr.message);
            }
            // Retry load. `await` matters (TASK-1528): a bare `return promise`
            // would run this call's finally (clearing memberAccessPending)
            // BEFORE the retry finishes, reopening the spurious-PIN window
            // mid-retry. With await, the gate stays up until the retry's own
            // finally has cleared it.
            return await loadUserTenant(userId);
          }
        } catch (lookupErr) {
          console.warn('[Auth] Email lookup denied or failed (expected for new social signups):', lookupErr.code || lookupErr.message);
          // Fall through — this is the normal path for first-time social signups
        }
      }

      // Check if this is a social sign-in user who never completed company name
      const isSocialUser = auth.currentUser?.providerData?.some(p =>
        p.providerId === 'google.com' || p.providerId === 'apple.com' || p.providerId === 'microsoft.com'
      );
      if (isSocialUser) {
        // TASK-4840 (B) — no users/{uid} doc is ALSO the state of an invitee who
        // signed in with Google before accepting (Alyse, 2026-09-09). Check for a
        // waiting invite before offering to create a company.
        console.log('[Auth] Social user with no users doc — checking for a pending invite before the company name modal');
        hideLoading();
        showAuthModal();
        offerInviteOrCompanyModal(
          auth.currentUser,
          auth.currentUser.providerData[0]?.providerId?.replace('.com', '') || 'social'
        );
        return;
      }
      console.error('[Auth] User document not found for:', userId);
      showToast('Account not found. Logging out so you can sign up fresh.', 'error');
      // Log out the orphaned auth user so they can sign up again
      setTimeout(() => handleLogout(), 2000);
      return;
    }
  } catch (error) {
    console.error('[Auth] Error loading tenant:', error);
    showToast('Error loading account data', 'error');
  } finally {
    // TASK-1528 — every exit path (early returns: no tenantId / social signup /
    // orphan; the demo branch; a thrown read) lowers the gate so a deferred
    // navigation can never spin against a permanently-pending flag. The
    // member-read finally above (~:6214) already cleared it on the main path;
    // this is idempotent there.
    window.AppState.memberAccessPending = false;
  }
}

// ============================================
// AUTHENTICATION
// ============================================
async function handleLogin(e) {
  e.preventDefault();

  const email = document.getElementById('login-email').value;
  const password = document.getElementById('login-password').value;
  const btn = document.getElementById('btn-login');

  // Prevent double-submit
  if (btn.disabled) {
    console.log('[Auth] Login already in progress, ignoring');
    return;
  }

  try {
    hideAuthError();
    btn.disabled = true;
    btn.classList.add('btn-loading');
    btn.innerHTML = '<span class="btn-spinner"></span> Signing in...';
    // Chris-reported 2026-05-17: even with the button-spinner the visible
    // change can lag enough on mobile that users tap 2-3x. Drop a full-page
    // overlay so feedback is impossible to miss; auth-state change removes it.
    _showLoginOverlay();
    await signInWithEmailAndPassword(auth, email, password);
    _hideLoginOverlay();
    showToast('Welcome back!', 'success');
    // Button reset happens via auth state change -> successful login hides modal
    // Safety net: if the auth modal is STUCK, re-trigger the proper auth state
    // handler so the trial-expired/email-verification/PIN-setup flows all run
    // instead of being bypassed by a raw showSidebar() + navigateToView().
    _scheduleLoginStuckNudge(Date.now());
  } catch (error) {
    console.error('[Auth] Login error:', error);
    // TASK-4851 (C) — a credential failure gets three ways forward instead of a
    // guess at which half was wrong. See showLoginRecoveryOptions.
    if (_isAmbiguousCredentialError(error.code)) showLoginRecoveryOptions();
    else showAuthError(getAuthErrorMessage(error.code));
    _hideLoginOverlay();
  } finally {
    // Always reset button state
    btn.disabled = false;
    btn.classList.remove('btn-loading');
    btn.innerHTML = 'Sign In';
    // Success-path overlay is removed by auth-state-change → modal hides;
    // failure path already hid it in catch. This is a belt-and-suspenders
    // safety in case neither runs.
    setTimeout(_hideLoginOverlay, 6000);
  }
}

// TASK-9526 — the email-login safety net NUDGES a stuck UI; it never starts a
// second sign-in alongside one that is still working. The old version re-ran
// the whole handler at a flat 5s whenever the modal was still up — but a normal
// cold sign-in on WebKit takes ~7s (measured: member read 4s + hasDirectReports
// 2.5s), so it fired mid-run every time and the overlap wiped the Clock form and
// could wall invited staff behind the owner PIN wizard. Now: while a run for this
// uid is in flight, keep waiting (re-check every 5s). Only when NO run is in
// flight and the modal is still up (listener never fired, or a run bailed on a
// stale flag) does it re-run the handler. Past ~35s a still-"in-flight" run is
// treated as hung; single-flight's staleMs (30s) then lets the nudge start fresh.
const LOGIN_NUDGE_INTERVAL_MS = 5000;
const LOGIN_NUDGE_GIVE_UP_MS = 35000;
function _scheduleLoginStuckNudge(startedAt) {
  setTimeout(() => {
    if (!DOM.authModal || DOM.authModal.classList.contains('hidden')) return; // UI moved on
    const u = auth.currentUser;
    if (!u) return;
    if (_authHandlerInFlightFor(u.uid) && (Date.now() - startedAt) < LOGIN_NUDGE_GIVE_UP_MS) {
      _scheduleLoginStuckNudge(startedAt); // still working — wait, don't overlap it
      return;
    }
    console.warn('[Auth] Login modal still visible and no sign-in run in flight — re-running auth state handler');
    handleAuthStateChange(u);
  }, LOGIN_NUDGE_INTERVAL_MS);
}

function _showLoginOverlay() {
  if (document.getElementById('login-overlay')) return;
  const el = document.createElement('div');
  el.id = 'login-overlay';
  el.style.cssText = 'position:fixed;inset:0;z-index:99999;background:rgba(15,23,42,0.82);display:flex;align-items:center;justify-content:center;flex-direction:column;gap:18px;color:#fff;font-family:Inter,-apple-system,BlinkMacSystemFont,sans-serif;-webkit-backdrop-filter:blur(4px);backdrop-filter:blur(4px);';
  el.innerHTML = '<div style="width:48px;height:48px;border:4px solid rgba(255,255,255,0.25);border-top-color:#fff;border-radius:50%;animation:spin 0.8s linear infinite;"></div><div style="font-size:1.05rem;font-weight:600;letter-spacing:0.2px;">Signing you in…</div>';
  document.body.appendChild(el);
}

function _hideLoginOverlay() {
  document.getElementById('login-overlay')?.remove();
}

// Shared helper: creates a trial account (used by both auth modal and demo signup modal)
async function createTrialAccount({ company, firstName, lastName, email, password, hasSmsBlock = false, phone = '', entry = 'signup', rosterMatchOverride = false }) {
  // Set flag to prevent race condition with onAuthStateChanged
  window.AppState.signupInProgress = true;

  // Create auth user
  const userCredential = await createUserWithEmailAndPassword(auth, email, password);
  const user = userCredential.user;

  // Calculate trial end date (TASK-1108 — TRIAL_DAYS, canonical 30)
  const trialEndDate = new Date();
  trialEndDate.setDate(trialEndDate.getDate() + TRIAL_DAYS);

  // Create tenant with plan and subscription info
  const tenantId = generateId();
  company = clampText(company, 100);
  firstName = clampText(firstName, 100);
  lastName = clampText(lastName, 100);
  const ownerFullName = `${firstName} ${lastName}`;

  // TASK-767: these 4 writes MUST stay serial + awaited — do NOT batch them.
  // The Firestore rules for members/{uid} (get() on the tenant doc's ownerId)
  // and people/{id} (getMemberRole → reads the members doc) read COMMITTED
  // state, and there is no getAfter() anywhere in firestore.rules. A writeBatch
  // evaluates every write against pre-commit state, so the tenant/member docs
  // wouldn't exist yet → member + person creates get DENIED → signup breaks.
  // (COD real-diff Code Reviewer 🔴, 2026-06-23, confirmed against firestore.rules
  // :674 + :734 + getMemberRole.) The perf win comes from deferring the email
  // below, not from batching these ordering-dependent writes.
  await setDoc(doc(db, 'tenants', tenantId), {
    name: company,
    createdAt: new Date(),
    ownerId: user.uid,
    ownerName: ownerFullName,
    ownerEmail: email,
    attribution: getUtmData(),

    // Email verification (24-hour grace period, then required)
    emailVerified: false,
    verificationDeadline: new Date(Date.now() + 24 * 60 * 60 * 1000),

    // Trial email drip campaign — must be set for scheduled emails to fire
    trialStartedAt: new Date(),
    lastTrialEmail: -1,

    // Plan & subscription (ready for Stripe)
    plan: 'base',
    // TASK-4769 — write the ROOT key too. This create wrote the nested map only,
    // which is where every nested-only tenant doc came from (8 live on
    // 2026-10-12); verifyEmailToken backfilled root later, if the owner ever
    // clicked the link. The rules create arm admits root 'trialing'
    // (firestore.rules clause (2)); social signup already writes both.
    subscriptionStatus: 'trialing',
    planLimits: {
      vehicleTiers: 50,
      users: -1
    },
    addons: {
      aiPack: true,
      smsCredits: hasSmsBlock ? 500 : 0
    },
    subscription: {
      status: 'trialing',
      trialEnd: trialEndDate,
      monthlyPrice: 250,
      stripeCustomerId: null,
      stripeSubscriptionId: null,
      currentPeriodEnd: null
    },

    // SMS starter credit — TASK-1165: one-time $5 comp (500 segments) so a new
    // signup can TRY texting before buying. (Was 250; standardized to 500.) The
    // firestore create-rule caps a client-written balance at 500 — anything larger
    // must come from the pack-purchase webhook via Admin SDK (a paid SMS pack at
    // signup routes through real checkout, not a client-seeded balance).
    twilio: {
      creditBalance: 500,
      creditUsed: 0,
      messagesSent: 0,
      status: 'active',
      isPromo: !hasSmsBlock,
      promoType: hasSmsBlock ? null : 'starter'
    },
    hasSmsPack: true,

    // Usage tracking
    usage: {
      vehicles: 0,
      users: 1,
      smsRemaining: hasSmsBlock ? 500 : 0,
      smsLastPurchase: hasSmsBlock ? new Date() : null
    },

    // Onboarding state
    ownerProfileSetup: false,
    onboardingHintsSeen: [],

    // TASK-9861 — the server said this email/phone is on an active company
    // roster and the person chose "I'm starting my own trucking company".
    // Read by onTenantCreatedSignupReview (flag for Overlord review).
    ...(rosterMatchOverride === true ? { signupRosterMatchOverride: true } : {}),

    settings: {
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      weekStart: 'monday',
      dvirAutoPurge: true,
      dvirRetentionMonths: 6,
      photoAutoPurge: true,
      photoRetentionDays: 90,
      // TASK-9817 — new companies start with drivers NOT editing their own finished shifts (decision 2026-10-13). Absent already reads OFF everywhere; written explicitly so a future default flip cannot turn it on. Existing tenants keep what they stored.
      allowDriverTimeEdit: false,
      // TASK-10029 — new companies screen applicants first; the paid background check runs only when the office clicks Run (decision 2026-10-22). Absent still reads one_stage everywhere, so existing tenants keep what they have.
      applicationFlow: 'two_stage'
    }
  });

  // Add user as tenant member (rule reads the tenant doc above — must exist first)
  await setDoc(doc(db, 'tenants', tenantId, 'members', user.uid), {
    email: email,
    role: 'owner',
    status: 'active', // TASK-1423 — every new members doc carries its status
    joinedAt: new Date()
  });

  // Create user profile
  await setDoc(doc(db, 'users', user.uid), {
    email: email,
    tenantId: tenantId,
    createdAt: new Date()
  });

  // Add owner to roster as a Person (rule reads the members doc above — must exist first)
  await setDoc(doc(db, 'tenants', tenantId, 'people', generateId()), {
    name: ownerFullName,
    role: 'Admin',
    email: email,
    phone: '',
    status: 'active',
    smsOptIn: true,
    notifications: { sms: true, muted: false, preferredChannel: 'both', tags: ['all'] },
    createdAt: new Date()
  });

  // Set custom claims immediately so new user has tenant-scoped Storage access.
  // Kept AWAITED and AFTER the doc writes (TASK-767): setTenantClaims reads the
  // membership/user docs, and Storage tenant-scoping needs the force-refreshed
  // token, so this must finish before the user lands in-app. Not deferrable.
  try {
    const setClaimsFn = httpsCallable(functions, 'setTenantClaims');
    await setClaimsFn();
    await user.getIdToken(true); // Force refresh to pick up new claims
    console.log('[Auth] Tenant claims set for new user');
  } catch (claimsErr) {
    console.warn('[Auth] Failed to set claims for new user (non-blocking):', claimsErr.message);
  }

  // TASK-9183 — phone + where this signup came from, written by the server to
  // _signupDetails/{tenantId} (Overlord-only). The phone deliberately does NOT
  // go on the owner's Person above: tenants/{id}/people is broadcast to every
  // session, driver PINs included. Fire-and-forget.
  recordSignupDetails(tenantId, { phone, entry });

  // Send branded verification email via Cloud Function (SendGrid).
  // TASK-767: fire-and-forget — do NOT await. The user still gets the "check your
  // email" toast and the email sends in the background (mirrors seedRideAlongTemplate
  // below). The logEmailEvent is chained onto the send so the sent/failed audit
  // still fires and there's no unhandled-rejection leak. The email takes 1-3s and
  // was the main "Creating account…" stall.
  try {
    const sendVerify = httpsCallable(functions, 'sendVerificationEmail');
    sendVerify({ tenantId, email })
      .then(() => {
        console.log('[Auth] Verification email sent via SendGrid');
        return logEmailEvent({
          type: 'verification',
          recipient: email,
          tenantId: tenantId,
          status: 'sent',
          provider: 'sendgrid'
        });
      })
      .catch((verifyError) => {
        console.error('[Auth] Error sending verification email (non-blocking):', verifyError);
        return logEmailEvent({
          type: 'verification',
          recipient: email,
          tenantId: tenantId,
          status: 'failed',
          provider: 'sendgrid',
          error: verifyError.message
        }).catch(() => {});
      });
  } catch (verifyErr) {
    console.warn('[Auth] Could not invoke sendVerificationEmail (non-blocking):', verifyErr?.message || verifyErr);
  }

  // Seed HR Company Documents with the Ride-Along & Road Test Verification
  // template — fire-and-forget; never block signup if PDF gen has trouble.
  try {
    const seedRideAlong = httpsCallable(functions, 'seedRideAlongTemplate');
    seedRideAlong({ tenantId }).catch((err) => {
      console.warn('[Auth] Ride-along template seed failed (non-blocking):', err?.message || err);
    });
  } catch (seedErr) {
    console.warn('[Auth] Could not invoke seedRideAlongTemplate (non-blocking):', seedErr?.message || seedErr);
  }

  return { user, tenantId, hasSmsBlock };
}

// ============================================
// TASK-9861 — SIGNUP ROSTER CHECK
// ============================================
// Before an auth user or a tenant is created, ask the server whether this
// email / phone already belongs to an ACTIVE person on some company's roster
// (a driver who wandered into "Sign up"). The answer is one bit plus one
// generic sentence — never which company. Fails OPEN: any error or a slow
// reply lets the signup continue (the server-side onCreate review still flags
// a roster-matched tenant that got through).
const SIGNUP_ROSTER_CHECK_TIMEOUT_MS = 8000;
const SIGNUP_ROSTER_MATCH_MESSAGE = 'Looks like your company already uses DispatchDeck. Ask your office for your driver code, or for a staff login invite.';

async function checkSignupRoster({ email, phone = '' }) {
  try {
    const call = httpsCallable(functions, 'checkSignupRosterMatch')({ email, phone }).catch((err) => {
      console.warn('[Auth] Roster check unavailable (continuing):', err?.message || err);
      return null;
    });
    const timeout = new Promise((resolve) => setTimeout(() => resolve(null), SIGNUP_ROSTER_CHECK_TIMEOUT_MS));
    const res = await Promise.race([call, timeout]);
    const d = res && res.data;
    if (d && d.rosterMatch === true) {
      return { match: true, message: (typeof d.message === 'string' && d.message) ? d.message : SIGNUP_ROSTER_MATCH_MESSAGE };
    }
  } catch (err) {
    console.warn('[Auth] Roster check failed (continuing):', err?.message || err);
  }
  return { match: false, message: '' };
}

// The override is tied to the exact address that was checked: editing the
// email clears it, so "starting my own" can't carry over to a different one.
function setSignupRosterOverride(email) {
  window._signupRosterOverrideEmail = String(email || '').trim().toLowerCase();
}
function hasSignupRosterOverride(email) {
  return !!window._signupRosterOverrideEmail && window._signupRosterOverrideEmail === String(email || '').trim().toLowerCase();
}

function showSignupRosterMatch(message) {
  const panel = document.getElementById('signup-roster-match');
  const msg = document.getElementById('signup-roster-match-msg');
  if (msg) msg.textContent = message || SIGNUP_ROSTER_MATCH_MESSAGE;
  hideAuthError();
  if (panel) {
    panel.classList.remove('hidden');
    try { panel.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); } catch (_) { /* old browsers */ }
    // The likely right answer gets focus (COD r1 UX) — Enter goes to Driver Code.
    try { document.getElementById('signup-roster-goto-driver')?.focus({ preventScroll: true }); } catch (_) { /* ignore */ }
  }
  if (typeof gtag === 'function') gtag('event', 'signup_roster_match', { event_category: 'engagement' });
}
function hideSignupRosterMatch() {
  document.getElementById('signup-roster-match')?.classList.add('hidden');
}

async function handleSignup(e) {
  e.preventDefault();

  const company = document.getElementById('signup-company').value.trim();
  const firstName = document.getElementById('signup-firstname').value.trim();
  const lastName = document.getElementById('signup-lastname').value.trim();
  const email = document.getElementById('signup-email').value.trim();
  const password = document.getElementById('signup-password').value;
  const phone = (document.getElementById('signup-phone')?.value || '').trim();

  // Get add-on selections
  const hasSmsBlock = document.getElementById('signup-addon-sms')?.value === 'true';

  const btn = document.getElementById('btn-signup');

  // TASK-9183 — phone is optional, but a typo'd one is no use to anyone.
  if (phone && !isValidSignupPhone(phone)) {
    showAuthError('Enter a 10-digit US or Canadian phone number, or leave it blank.');
    document.getElementById('signup-phone')?.focus();
    return;
  }

  try {
    hideAuthError();
    btn.disabled = true;
    btn.classList.add('btn-loading');
    btn.innerHTML = '<span class="btn-spinner"></span> Creating account...';

    // TASK-9861 — already on a company's roster? Stop before anything is created.
    const rosterMatchOverride = hasSignupRosterOverride(email);
    if (!rosterMatchOverride) {
      const rc = await checkSignupRoster({ email, phone });
      if (rc.match) {
        showSignupRosterMatch(rc.message);
        btn.disabled = false;
        btn.classList.remove('btn-loading');
        btn.innerHTML = 'Start 30-Day Trial';
        return;
      }
    }
    hideSignupRosterMatch();

    const result = await createTrialAccount({ company, firstName, lastName, email, password, hasSmsBlock, phone, rosterMatchOverride });

    // TASK-767 refinement: users now land in-app faster (verification email is
    // fire-and-forget), so hold this reminder ~6s instead of the default 1.8s.
    showToast('Welcome! Check your email to verify your account. Check spam folder if not in inbox.', 'success', null, 6000);

    // GA4: Track signup completion with attribution (skip test accounts)
    const _utmSignup = getUtmData();
    if (typeof gtag === 'function' && !isTestEmail(email)) gtag('event', 'sign_up', {
      method: 'email', has_ai_pack: true, has_sms: hasSmsBlock,
      campaign_source: _utmSignup?.utm_source || undefined,
      campaign_medium: _utmSignup?.utm_medium || undefined,
      campaign_name: _utmSignup?.utm_campaign || undefined,
      campaign_content: _utmSignup?.utm_content || undefined,
      gclid: _utmSignup?.gclid || undefined
    });
    if (typeof fbq !== 'undefined' && !isTestEmail(email)) fbq('track', 'CompleteRegistration');
    if (typeof window.lintrk !== 'undefined' && !isTestEmail(email)) window.lintrk('track', { conversion_id: 25160532 });

    // Clear signup flag and manually trigger auth state handling
    window.AppState.signupInProgress = false;
    await handleAuthStateChange(result.user);

  } catch (error) {
    console.error('[Auth] Signup error:', error);
    window.AppState.signupInProgress = false;
    showAuthError(getAuthErrorMessage(error.code));
    btn.disabled = false;
    btn.classList.remove('btn-loading');
    btn.innerHTML = 'Start 30-Day Trial';
  }
}

// ============================================
// SOCIAL SIGN-IN (Google, Apple, Microsoft)
// ============================================
// ============================================
// NATIVE iOS SIGN-IN (Capacitor plugins)
// ============================================
function generateNonce(length = 32) {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const values = crypto.getRandomValues(new Uint8Array(length));
  return Array.from(values, v => chars[v % chars.length]).join('');
}

async function sha256(message) {
  const msgBuffer = new TextEncoder().encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
  return Array.from(new Uint8Array(hashBuffer), b => b.toString(16).padStart(2, '0')).join('');
}

async function nativeAppleSignIn() {
  const SignInWithApple = window.Capacitor?.Plugins?.SignInWithApple;
  if (!SignInWithApple) throw new Error('Apple Sign-In plugin not available');

  const rawNonce = generateNonce();
  const hashedNonce = await sha256(rawNonce);

  const result = await SignInWithApple.authorize({
    clientId: 'app.dispatchdeck.ios',
    redirectURI: 'https://dispatchdeck.app',
    scopes: 'email name',
    nonce: hashedNonce,
  });

  const provider = new OAuthProvider('apple.com');
  const credential = provider.credential({
    idToken: result.response.identityToken,
    rawNonce: rawNonce,
  });

  return signInWithCredential(auth, credential);
}

async function nativeGoogleSignIn() {
  const GoogleAuth = window.Capacitor?.Plugins?.GoogleAuth;
  if (!GoogleAuth) throw new Error('Google Auth plugin not available');

  // Initialize before first sign-in (safe to call multiple times)
  await GoogleAuth.initialize({
    clientId: '615372411262-gum9ohej8rl7713tis791u3lk4mgqtam.apps.googleusercontent.com',
    scopes: ['profile', 'email'],
  });

  const result = await GoogleAuth.signIn();
  const idToken = result?.authentication?.idToken;
  if (!idToken) throw new Error('No ID token returned from Google Sign-In');
  const credential = GoogleAuthProvider.credential(idToken);
  return signInWithCredential(auth, credential);
}

async function handleSocialSignIn(providerName) {
  // iOS native: try Capacitor plugins first, fall back to redirect
  if (window.isIOSApp && window.Capacitor?.Plugins) {
    // Check if native plugins are actually available (v1.3+ removed them)
    const hasNativeGoogle = !!window.Capacitor.Plugins.GoogleAuth;
    const hasNativeApple = !!window.Capacitor.Plugins.SignInWithApple;

    if ((providerName === 'google' && hasNativeGoogle) || (providerName === 'apple' && hasNativeApple)) {
      try {
        let userCredential;
        if (providerName === 'apple') {
          userCredential = await nativeAppleSignIn();
        } else if (providerName === 'google') {
          userCredential = await nativeGoogleSignIn();
        }
      // Success — handle as existing or new user (same as popup flow below)
      const user = userCredential.user;
      ddLog('[Auth] Native iOS sign-in success:', user.uid, user.email);
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        showToast('Welcome back!', 'success');
        await handleAuthStateChange(user);
        return;
      }
      // Check if email exists under different UID (wrapped — Firestore rules deny cross-user queries)
      if (user.email) {
        try {
          const existingByEmail = await getDocs(query(collection(db, 'users'), where('email', '==', user.email)));
          if (!existingByEmail.empty) {
            const existingData = existingByEmail.docs[0].data();
            await setDoc(doc(db, 'users', user.uid), { email: user.email, tenantId: existingData.tenantId, createdAt: existingData.createdAt || new Date() });
            try {
              const oldMemberDoc = await getDoc(doc(db, 'tenants', existingData.tenantId, 'members', existingByEmail.docs[0].id));
              if (oldMemberDoc.exists()) await setDoc(doc(db, 'tenants', existingData.tenantId, 'members', user.uid), oldMemberDoc.data());
            } catch (e) { console.warn('[Auth] Could not copy members doc:', e.message); }
            showToast('Welcome back!', 'success');
            await handleAuthStateChange(user);
            return;
          }
        } catch (lookupErr) {
          console.warn('[Auth] Email lookup denied or failed (expected for new social signups):', lookupErr.code || lookupErr.message);
          // Fall through to new user company modal
        }
      }
      // New user — pending invite first (TASK-4840 B), else company name modal
      await offerInviteOrCompanyModal(user, providerName);
      return;
    } catch (error) {
      if (error.message?.includes('cancel') || error.message?.includes('Cancel') || error.code === 1001) {
        console.log('[Auth] Native sign-in cancelled by user');
      } else {
        console.error('[Auth] Native iOS sign-in error:', error);
        showToast('Sign in failed. Please try again.', 'error');
      }
      return;
    }
    } else {
      // No native plugins — fall through to web redirect below
      console.log('[Auth] No native plugins available, using web redirect');
    }
  }
  console.log('[Auth] Social sign-in started:', providerName);

  // Build provider
  let provider;
  if (providerName === 'google') {
    provider = new GoogleAuthProvider();
  } else if (providerName === 'apple') {
    provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
  } else if (providerName === 'microsoft') {
    provider = new OAuthProvider('microsoft.com');
  } else {
    showToast('Unknown sign-in provider', 'error');
    return;
  }

  // Disable all social buttons during sign-in
  const socialBtns = document.querySelectorAll('.btn-social');
  socialBtns.forEach(btn => btn.disabled = true);

  try {
    hideAuthError();
    // Use redirect on iOS (popup blocked in WebView), popup everywhere else
    if (window.isIOSApp) {
      console.log('[Auth] Using signInWithRedirect for iOS WebView');
      await signInWithRedirect(auth, provider);
      return; // Page will reload — redirect result handled on next load
    }
    const result = await signInWithPopup(auth, provider);
    const user = result.user;
    ddLog('[Auth] Social sign-in success:', user.uid, user.email);

    // Check if user doc already exists (returning user)
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    if (userDoc.exists()) {
      // Existing user — explicitly run auth handler (onAuthStateChanged may have raced)
      console.log('[Auth] Existing social user, loading app');
      showToast('Welcome back!', 'success');
      await handleAuthStateChange(user);
      return;
    }

    // Fallback: check if another UID has this email (e.g., email/password account exists)
    // Wrapped in try/catch because Firestore rules deny cross-user queries; on rejection
    // we treat this as a brand new social user and continue to the company name modal.
    if (user.email) {
      try {
        const existingByEmail = await getDocs(query(collection(db, 'users'), where('email', '==', user.email)));
        if (!existingByEmail.empty) {
          const existingData = existingByEmail.docs[0].data();
          console.log('[Auth] Found existing user doc by email, migrating UID:', existingByEmail.docs[0].id, '→', user.uid);
          // Create user doc for the new social UID pointing to same tenant
          await setDoc(doc(db, 'users', user.uid), {
            email: user.email,
            tenantId: existingData.tenantId,
            createdAt: existingData.createdAt || new Date()
          });
          // Copy members doc so Firestore rules work
          try {
            const oldMemberDoc = await getDoc(doc(db, 'tenants', existingData.tenantId, 'members', existingByEmail.docs[0].id));
            if (oldMemberDoc.exists()) {
              await setDoc(doc(db, 'tenants', existingData.tenantId, 'members', user.uid), oldMemberDoc.data());
            }
          } catch (memberErr) {
            console.warn('[Auth] Could not copy members doc:', memberErr.message);
          }
          showToast('Welcome back!', 'success');
          await handleAuthStateChange(user);
          return;
        }
      } catch (lookupErr) {
        console.warn('[Auth] Email lookup denied or failed (expected for new social signups):', lookupErr.code || lookupErr.message);
        // Fall through — this is the normal path for first-time social signups
      }
    }

    // No home yet — a pending invite first (TASK-4840 B), else company name.
    console.log('[Auth] New social user, checking for a pending invite before the company name modal');
    await offerInviteOrCompanyModal(user, providerName);

  } catch (error) {
    console.error('[Auth] Social sign-in error:', error);

    if (error.code === 'auth/popup-closed-by-user') {
      // User cancelled — no error message needed
      console.log('[Auth] Social sign-in popup closed by user');
    } else if (error.code === 'auth/account-exists-with-different-credential') {
      // Extract pending credential and show link modal
      const pendingEmail = error.customData?.email || '';
      const pendingCredential = GoogleAuthProvider.credentialFromError(error);
      if (pendingEmail && pendingCredential) {
        window._pendingLinkEmail = pendingEmail;
        window._pendingLinkCredential = pendingCredential;
        // Show link account modal
        const linkModal = document.getElementById('modal-link-account');
        const linkSubtitle = document.getElementById('link-account-subtitle');
        const linkPwInput = document.getElementById('link-account-password');
        const linkError = document.getElementById('link-account-error');
        if (linkModal) {
          linkSubtitle.textContent = `You already have an account with ${pendingEmail}. Enter your password to link Google sign-in.`;
          linkPwInput.value = '';
          if (linkError) linkError.classList.add('hidden');
          linkModal.classList.remove('hidden');
          setTimeout(() => linkPwInput.focus(), 100);
        }
      } else {
        showAuthError('An account already exists with this email. Try signing in with your password instead.');
      }
    } else if (error.code === 'auth/admin-restricted-operation' || error.code === 'auth/operation-not-allowed') {
      showToast("This sign-in method isn't available yet. Try email/password.", 'error');
    } else {
      showAuthError(getAuthErrorMessage(error.code));
    }
  } finally {
    socialBtns.forEach(btn => btn.disabled = false);
  }
}

// Expose to window for onclick handlers in HTML
window.handleSocialSignIn = handleSocialSignIn;

// ============================================
// TASK-4840 (B) — PENDING INVITE BEFORE "CREATE A COMPANY"
// A signed-in social user with no users/{uid} doc is EITHER a genuine new
// signup OR an invitee who signed in before accepting (Alyse, 2026-09-09: one
// submit away from creating an empty tenant instead of joining trippin-trucking).
// This is the ONE choke point every "open the company-name modal" path goes
// through (loadUserTenant's incomplete-social branch, the native iOS sign-in,
// the web popup sign-in). It asks the server for an invite addressed to the
// caller's VERIFIED email (findMyPendingInvite — the server never trusts a
// client email) and, if one exists, offers Join first. "Start my own company"
// stays one tap away: self-serve signup must never be blocked, and someone can
// legitimately hold an invite AND want their own tenant — offer both, never
// auto-pick. A failed lookup falls back to the company modal, which carries a
// static "invited? use your email link" hint.
// ============================================
let _inviteOfferInFlight = null;
function offerInviteOrCompanyModal(user, providerName) {
  window.AppState.signupInProgress = true; // Prevent handleAuthStateChange race
  window._pendingSocialUser = user;
  window._pendingSocialProvider = providerName;
  // Two paths can reach here for one sign-in (the popup handler AND
  // onAuthStateChanged → loadUserTenant). One lookup, one modal.
  if (_inviteOfferInFlight && _inviteOfferInFlight.uid === user.uid) return _inviteOfferInFlight.promise;
  window._pendingInviteOffer = null;
  window._pendingInviteDeclined = false;
  const promise = (async () => {
    let invite = null;
    try {
      const res = await httpsCallable(functions, 'findMyPendingInvite')({});
      invite = res?.data?.invite || null;
    } catch (e) {
      console.warn('[Auth] Pending-invite lookup failed — showing company modal:', e?.code || e?.message);
    }
    // A different sign-in took over. Compare by uid, not object identity: the
    // popup result and auth.currentUser can be distinct objects for one user.
    if (window._pendingSocialUser?.uid !== user.uid) return;
    if (invite && invite.tenantId) {
      window._pendingInviteOffer = invite;
      showPendingInviteModal(invite);
    } else {
      showSocialCompanyModal();
    }
  })().finally(() => { _inviteOfferInFlight = null; });
  _inviteOfferInFlight = { uid: user.uid, promise };
  return promise;
}

function showSocialCompanyModal() {
  window._socialStartingOwnCompany = false; // TASK-9861 — never carried over
  document.getElementById('social-roster-match')?.classList.add('hidden');
  const socialCompanyInput = document.getElementById('social-company-name');
  if (socialCompanyInput) socialCompanyInput.value = '';
  document.getElementById('modal-social-company')?.classList.remove('hidden');
  setTimeout(() => socialCompanyInput?.focus(), 100);
}

function showPendingInviteModal(invite) {
  const modal = document.getElementById('modal-pending-invite');
  if (!modal) { showSocialCompanyModal(); return; }
  // textContent only — tenant/inviter names are admin-entered strings.
  const title = document.getElementById('pending-invite-title');
  const sub = document.getElementById('pending-invite-subtitle');
  const err = document.getElementById('pending-invite-error');
  const joinBtn = document.getElementById('btn-pending-invite-join');
  if (title) title.textContent = `Join ${invite.tenantName || 'your team'}`;
  if (sub) sub.textContent = `${invite.inviterName || 'Your team admin'} invited you to ${invite.tenantName || 'their team'} on DispatchDeck. You don't need to create a company.`;
  if (err) { err.textContent = ''; err.classList.add('hidden'); }
  if (joinBtn) { joinBtn.disabled = false; joinBtn.textContent = `Join ${invite.tenantName || 'your team'}`; }
  document.getElementById('modal-social-company')?.classList.add('hidden');
  modal.classList.remove('hidden');
}

async function acceptPendingInviteFromModal() {
  const invite = window._pendingInviteOffer;
  const user = window._pendingSocialUser;
  if (!invite || !user) return;
  const joinBtn = document.getElementById('btn-pending-invite-join');
  const err = document.getElementById('pending-invite-error');
  if (window._pendingInviteAccepting) return;
  const declineBtn = document.getElementById('btn-pending-invite-decline');
  try {
    // COD round 1 — while the join is in flight, decline/close are locked too,
    // so a tenant can't be created in parallel with an accept that is landing.
    window._pendingInviteAccepting = true;
    if (joinBtn) { joinBtn.disabled = true; joinBtn.innerHTML = '<span class="btn-spinner"></span> Joining...'; }
    if (declineBtn) declineBtn.disabled = true;
    if (err) err.classList.add('hidden');
    await httpsCallable(functions, 'acceptMyPendingInvite')({ tenantId: invite.tenantId });
    window._pendingInviteAccepting = false;
    document.getElementById('modal-pending-invite')?.classList.add('hidden');
    window._pendingInviteOffer = null;
    window._pendingInviteDeclined = false;
    window._pendingSocialUser = null;
    window._pendingSocialProvider = null;
    window.AppState.signupInProgress = false;
    showToast(`Welcome to ${invite.tenantName || 'your team'}!`, 'success');
    await handleAuthStateChange(user);
  } catch (e) {
    console.error('[Auth] Accept pending invite failed:', e);
    // COD round 2: the claim may have COMMITTED even though the response was
    // lost (network drop). If users/{uid} now exists, the join landed: load the
    // app instead of re-opening the path to creating a second tenant.
    try {
      const landed = await getDoc(doc(db, 'users', user.uid));
      if (landed.exists()) {
        document.getElementById('modal-pending-invite')?.classList.add('hidden');
        window._pendingInviteOffer = null;
        window._pendingSocialUser = null;
        window._pendingSocialProvider = null;
        window._pendingInviteAccepting = false;
        window.AppState.signupInProgress = false;
        await handleAuthStateChange(user);
        return;
      }
    } catch (_) { /* can't confirm — fall through to the retryable error state */ }
    // TASK-9441 — no client-side hold any more. A Join that commits LATE can
    // no longer race a company creation: createSocialSignupTenant and the
    // Join's claim both run in server transactions over users/{uid}, so
    // whichever lands first wins and the other refuses (or, for Create,
    // answers with the home that already exists). Retrying Join is safe — the
    // claim is transactional and a repeat by the same uid answers ok.
    window._pendingInviteAccepting = false;
    if (declineBtn) declineBtn.disabled = false;
    if (err) {
      err.textContent = e?.message || "Couldn't join right now. Open the invite link in your email instead.";
      err.classList.remove('hidden');
    }
    if (joinBtn) { joinBtn.disabled = false; joinBtn.textContent = `Join ${invite.tenantName || 'your team'}`; }
  }
}

function declinePendingInvite() {
  if (window._pendingInviteAccepting) return; // a join is landing — see acceptPendingInviteFromModal
  // An explicit choice — the only way past the invite to the company modal.
  window._pendingInviteDeclined = true;
  document.getElementById('modal-pending-invite')?.classList.add('hidden');
  showSocialCompanyModal();
}

function closePendingInviteModal() {
  if (window._pendingInviteAccepting) return; // a join is landing
  document.getElementById('modal-pending-invite')?.classList.add('hidden');
  // Same semantics as closeSocialCompanyModal: stop the signup, stay signed in.
  window.AppState.signupInProgress = false;
  window._pendingSocialUser = null;
  window._pendingSocialProvider = null;
  window._pendingInviteOffer = null;
  window._pendingInviteDeclined = false;
}

window.acceptPendingInviteFromModal = acceptPendingInviteFromModal;
window.declinePendingInvite = declinePendingInvite;
window.closePendingInviteModal = closePendingInviteModal;

async function completeSocialSignup(user, companyName) {
  // TASK-4840 (B) — a tenant is never requested while an invite offer is
  // showing and has not been explicitly declined (a stale Enter keypress, a
  // script, a future caller).
  if (window._pendingInviteAccepting) return; // a join is in flight — see acceptPendingInviteFromModal
  if (window._pendingInviteOffer && window._pendingInviteDeclined !== true) {
    showPendingInviteModal(window._pendingInviteOffer);
    return;
  }
  // Lock BEFORE the first await so a double click (or Enter + click) sends one
  // request. The server is the real guarantee (a second request for the same
  // uid answers with the tenant the first one created); this just keeps the
  // button honest. Every exit below releases the lock.
  if (window._socialSignupInFlight) return;
  window._socialSignupInFlight = true;
  ddLog('[Auth] Completing social signup for:', user.email, 'company:', companyName);

  const btn = document.getElementById('btn-social-company-submit');
  const resetBtn = () => { if (btn) { btn.disabled = false; btn.innerHTML = 'Get Started'; } };
  const finishToApp = async () => {
    document.getElementById('modal-social-company')?.classList.add('hidden');
    document.getElementById('modal-pending-invite')?.classList.add('hidden');
    window._pendingInviteOffer = null;
    window._pendingInviteDeclined = false;
    window._pendingSocialUser = null;
    window._pendingSocialProvider = null;
    window.AppState.signupInProgress = false;
    window._socialSignupInFlight = false;
    await handleAuthStateChange(user);
  };
  try {
    if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Creating account...'; }

    const providerName = window._pendingSocialProvider || 'social';
    const email = user.email || '';

    // TASK-9441 — the tenant, owner membership, users/{uid} profile and roster
    // Person are created by the server in ONE transaction (plus the day-0
    // welcome email and trial clock). firestore.rules no longer let a social
    // account create a tenant from the browser. The server refuses, and
    // creates nothing, if this uid already has a home, a team membership, or
    // an invite the user has not declined.
    const res = await httpsCallable(functions, 'createSocialSignupTenant')({
      companyName: clampText(companyName, 100),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      attribution: getUtmData(),
      // TASK-9183 — first/last touch; the server writes _signupDetails/{tenantId}.
      ...getSignupTouches(),
      declinedInvite: window._pendingInviteDeclined === true,
      // TASK-9861 — the explicit "I'm starting my own trucking company" choice,
      // only ever set by the roster-match panel's button.
      startingOwnCompany: window._socialStartingOwnCompany === true,
    });
    const result = res?.data || {};

    // The provider did NOT verify this email (possible with Microsoft/Apple):
    // the server created the email-signup shape (24h grace), so send the same
    // verification email createTrialAccount sends. Fire-and-forget. Also on a
    // retry whose first reply was lost (server says verificationPending).
    const needsVerification = result.created === true ? result.emailVerified === false : result.verificationPending === true;
    if (needsVerification && email) {
      try {
        httpsCallable(functions, 'sendVerificationEmail')({ tenantId: result.tenantId, email })
          .catch((verifyErr) => console.warn('[Auth] Verification email failed (non-blocking):', verifyErr?.message || verifyErr));
      } catch (verifyErr) {
        console.warn('[Auth] Could not invoke sendVerificationEmail (non-blocking):', verifyErr?.message || verifyErr);
      }
    }

    if (result.created !== true) {
      // Already set up: a second click, a retry after a lost reply, or a team
      // Join that landed first. Nothing was created; open that home.
      await finishToApp();
      return;
    }

    showToast('Welcome to DispatchDeck! Your 30-day trial has started.', 'success');
    if (typeof fbq !== 'undefined' && !isTestEmail(email)) fbq('track', 'StartTrial', {value: 0, currency: 'USD'});
    if (typeof window.lintrk !== 'undefined' && !isTestEmail(email)) window.lintrk('track', { conversion_id: 25160532 });

    // GA4: Track signup with attribution (skip test accounts)
    const _utmSocial = getUtmData();
    if (typeof gtag === 'function' && !isTestEmail(email)) gtag('event', 'sign_up', {
      method: providerName,
      campaign_source: _utmSocial?.utm_source || undefined,
      campaign_medium: _utmSocial?.utm_medium || undefined,
      campaign_name: _utmSocial?.utm_campaign || undefined,
      campaign_content: _utmSocial?.utm_content || undefined,
      gclid: _utmSocial?.gclid || undefined
    });
    if (typeof fbq !== 'undefined' && !isTestEmail(email)) fbq('track', 'CompleteRegistration');
    if (typeof window.lintrk !== 'undefined' && !isTestEmail(email)) window.lintrk('track', { conversion_id: 25160532 });

    await finishToApp();

  } catch (error) {
    window._socialSignupInFlight = false;
    // The server found an invite this user has not answered (e.g. the invite
    // lookup failed earlier and the company form was shown instead). Offer it.
    // TASK-9861 — this sign-in's email is on an active company roster. Show the
    // generic sentence + the deliberate "own company" way through; never a toast.
    if (error?.details?.reason === 'roster-match') {
      resetBtn();
      const panel = document.getElementById('social-roster-match');
      const msg = document.getElementById('social-roster-match-msg');
      if (msg) msg.textContent = error.message || SIGNUP_ROSTER_MATCH_MESSAGE;
      panel?.classList.remove('hidden');
      if (typeof gtag === 'function') gtag('event', 'signup_roster_match', { event_category: 'engagement', method: 'social' });
      return;
    }
    const invite = error?.details?.reason === 'invite-pending' ? error.details.invite : null;
    if (invite && invite.tenantId) {
      resetBtn();
      window._pendingInviteOffer = invite;
      window._pendingInviteDeclined = false;
      showPendingInviteModal(invite);
      return;
    }
    console.error('[Auth] Social signup error:', error);
    console.error('[Auth] Error details:', {
      code: error?.code,
      message: error?.message,
      name: error?.name,
      stack: error?.stack
    });
    // Server refusals carry a plain-English message; anything else shows the code.
    const refused = !!error?.details?.reason;
    const reason = refused ? error.message : (error?.code || error?.message || 'unknown error');
    showToast(refused ? reason : `Error creating account: ${reason}`, 'error');
    resetBtn();
  }
}

function closeSocialCompanyModal() {
  document.getElementById('modal-social-company').classList.add('hidden');
  window._socialStartingOwnCompany = false; // TASK-9861
  // Clear the signup flag so normal auth can proceed
  window.AppState.signupInProgress = false;
  window._pendingSocialUser = null;
  window._pendingSocialProvider = null;
  window._pendingInviteOffer = null; // TASK-4840 (B)
  window._pendingInviteDeclined = false;
}

window.closeSocialCompanyModal = closeSocialCompanyModal;

// ============================================
// LINK GOOGLE ACCOUNT TO EXISTING EMAIL/PASSWORD
// ============================================
function closeLinkAccountModal() {
  document.getElementById('modal-link-account').classList.add('hidden');
  window._pendingLinkEmail = null;
  window._pendingLinkCredential = null;
}
window.closeLinkAccountModal = closeLinkAccountModal;

async function handleLinkAccount() {
  const email = window._pendingLinkEmail;
  const pendingCredential = window._pendingLinkCredential;
  const password = document.getElementById('link-account-password').value;
  const linkError = document.getElementById('link-account-error');
  const btn = document.getElementById('btn-link-account');

  if (!password) {
    if (linkError) {
      linkError.textContent = 'Please enter your password.';
      linkError.classList.remove('hidden');
    }
    return;
  }

  if (!email || !pendingCredential) {
    showToast('Something went wrong. Please try again.', 'error');
    closeLinkAccountModal();
    return;
  }

  try {
    btn.disabled = true;
    btn.innerHTML = '<span class="btn-spinner"></span> Linking...';
    if (linkError) linkError.classList.add('hidden');

    // Sign in with existing email/password
    const userCredential = await signInWithEmailAndPassword(auth, email, password);

    // Link the pending Google credential
    await linkWithCredential(userCredential.user, pendingCredential);

    ddLog('[Auth] Google credential linked to existing account:', email);
    closeLinkAccountModal();
    showToast('Google account linked! You can now use either method.', 'success');
    // Auth state change fires naturally — user proceeds to app

  } catch (error) {
    console.error('[Auth] Link account error:', error);
    if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
      if (linkError) {
        linkError.textContent = 'Incorrect password. Please try again.';
        linkError.classList.remove('hidden');
      }
    } else if (error.code === 'auth/too-many-requests') {
      if (linkError) {
        linkError.textContent = 'Too many attempts. Please try again later.';
        linkError.classList.remove('hidden');
      }
    } else {
      showToast('Unable to link accounts. Please try again.', 'error');
      closeLinkAccountModal();
    }
  } finally {
    btn.disabled = false;
    btn.innerHTML = '<span class="material-icons" style="font-size:18px;">link</span> Link & Sign In';
  }
}

// ── Cross-tab demo heartbeat (2026-08-17) ─────────────────────────────────
// Firebase Auth persistence is shared by EVERY tab on this origin, so the
// anonymous user one tab mints for the demo is broadcast to all the others.
// `pendingDemo` / `isDemo` are per-tab, in-memory flags — so a second tab used
// to see that anon user, conclude "stale anonymous session," and sign it out
// (handleAuthStateChange, the hasPublicToken guard). That signout propagates
// straight back and kills the demo mid-seed: permission-denied on the reset,
// tenant null, and a dead "[PIN] No tenant ID" UI in the tab that clicked
// Try Demo. Reported by Chris 2026-08-17; reproduced with two tabs open.
//
// This localStorage heartbeat is the cross-tab signal that in-memory state
// can't be. It is deliberately TTL-based rather than a sticky flag: a demo
// tab that gets closed goes stale within a minute, so a later cold start
// still correctly treats a leftover anonymous session as stale and shows the
// login screen (the original guard's whole purpose).
const DEMO_HEARTBEAT_KEY = 'dispatchdeck_demo_heartbeat';
const DEMO_HEARTBEAT_INTERVAL_MS = 15000;
const DEMO_HEARTBEAT_STALE_MS = 45000;
let _demoHeartbeatTimer = null;

function markDemoHeartbeat() {
  try { localStorage.setItem(DEMO_HEARTBEAT_KEY, String(Date.now())); } catch (_) {}
}

function startDemoHeartbeat() {
  markDemoHeartbeat();
  if (_demoHeartbeatTimer) return;
  _demoHeartbeatTimer = setInterval(markDemoHeartbeat, DEMO_HEARTBEAT_INTERVAL_MS);
}

// Stops refreshing the stamp and lets it expire on its own — it deliberately
// does NOT remove the key. Deleting would reintroduce the exact race this
// whole mechanism exists to close: when a tab takes the demo over from
// another one, the losing tab's cleanup runs AFTER the winner has already
// stamped, so a delete there would unprotect a demo that just started. An
// expired stamp is equivalent and race-free.
function stopDemoHeartbeat() {
  if (_demoHeartbeatTimer) { clearInterval(_demoHeartbeatTimer); _demoHeartbeatTimer = null; }
}

// True when ANOTHER tab is running (or is milliseconds from running) a demo.
function isDemoActiveInAnotherTab() {
  try {
    const ts = parseInt(localStorage.getItem(DEMO_HEARTBEAT_KEY) || '0', 10);
    return !!ts && (Date.now() - ts) < DEMO_HEARTBEAT_STALE_MS;
  } catch (_) { return false; }
}

async function handleDemoLogin() {
  try {
    hideAuthError();

    // Disable button and show immediate feedback
    // TASK-4847 — class toggle, NOT innerHTML. Rewriting this button's markup
    // threw away the .btn-demo-label/.btn-demo-sub structure and reintroduced the
    // old inline-styled gradient look on the error path and on modal reopen. The
    // "Loading demo..." span lives in index.html; .is-loading swaps which span shows.
    const demoBtn = document.getElementById('btn-demo');
    if (demoBtn) {
      demoBtn.disabled = true;
      demoBtn.classList.add('is-loading');
    }

    // Show immediate toast - build excitement!
    showToast("Firin' up the engines... this is gonna be good!", 'info');

    // GA4: Track demo start with attribution
    const _utmDemo = getUtmData();
    if (typeof gtag === 'function') gtag('event', 'demo_started', {
      event_category: 'engagement',
      campaign_source: _utmDemo?.utm_source || undefined,
      campaign_medium: _utmDemo?.utm_medium || undefined,
      campaign_name: _utmDemo?.utm_campaign || undefined
    });
    if (typeof fbq !== 'undefined') fbq('track', 'Lead');

    // An anonymous session may already be alive in THIS tab — most often one
    // left here by a demo running in another tab (the guard in
    // handleAuthStateChange now keeps it instead of signing it out). Firebase
    // hands that same user straight back from signInAnonymously() and fires NO
    // auth-state change, so the handler that actually builds the demo would
    // never run and this button would spin forever. Drop it first to force a
    // real sign-in event. This does take the demo over from the other tab —
    // which is exactly what clicking Try Demo here asks for, and Firebase only
    // keeps one session per origin regardless.
    if (auth.currentUser?.isAnonymous && !window.AppState.isDemo && !window.AppState.driverModePin) {
      try { await auth.signOut(); } catch (_) {}
    }

    // Flag demo mode BEFORE sign-in so auth handler doesn't kill the anonymous session
    window.AppState.pendingDemo = true;
    // …and stamp the cross-tab heartbeat BEFORE sign-in too, so a sibling tab
    // that receives the broadcast anon user (~250ms later) already sees the
    // demo as live and leaves it alone.
    markDemoHeartbeat();

    await signInAnonymously(auth);

    // Note: Demo data reset and welcome banner are handled in handleAuthStateChange
    // to avoid race conditions with seeding
  } catch (error) {
    console.error('[Auth] Demo login error:', error);
    // Release the demo flags — a failed start must not leave `pendingDemo`
    // set, which disarms this tab's own stale-anon guard for the rest of the
    // page's life. The heartbeat stops refreshing here and expires within
    // DEMO_HEARTBEAT_STALE_MS; until then other tabs merely decline to
    // force-signout a leftover anon session, which is harmless.
    window.AppState.pendingDemo = false;
    stopDemoHeartbeat();
    showAuthError('Unable to start demo. Please try again.');

    // Re-enable button on error (TASK-4847: drop .is-loading, don't rebuild markup)
    const demoBtn = document.getElementById('btn-demo');
    if (demoBtn) {
      demoBtn.disabled = false;
      demoBtn.classList.remove('is-loading');
    }
  }
}

async function resetDemoData() {
  console.log('[Demo] Resetting demo data...');

  // Every collection the seeder recreates MUST be listed here — anything
  // seeded but never reset accumulates junk forever. TASK-1226: 'assets',
  // 'orders' and 'permits' were missing, so 50 stray "Test Drill Bit" docs
  // (left by E2E specs that log in through the demo button) piled up in the
  // demo fleet where prospects could see them.
  const demoCollections = [
    'people',
    'trucks',
    'trailers',
    'pretrips',
    'maintenance',
    'jobs',
    'jobDays',
    'clockEntries',
    'tickets',
    'timeOff',
    'customers',
    'metersLog',
    'vehicleHolds',
    'fluidsLog',
    'assets',
    'orders',
    'permits',
    'returnInspections',
    'revenueEntries',  // TASK-4700 v3 -- seeded (demoSeeder.js:2991), never reset; 4 were found live
    'notes',           // TASK-9619 -- seeded per demo session (ensureDemoNotes)
    'reminders'        // TASK-1550 -- seeded per demo session (ensureDemoReminders)
  ];

  // TASK-4700 v3 -- EVERY COLLECTION GETS ITS OWN try/catch, AND THE DELETES USE
  // allSettled, NOT all. This is the load-bearing part of the fix, so do not
  // "simplify" it back to one try/catch around the loop.
  //
  // What that shape did: a single refused delete rejected Promise.all, the one
  // outer catch swallowed it, and the loop NEVER RESUMED -- so every collection
  // after the failing one went uncleared. It was real, it was live, and it was
  // invisible: a DVIR retention rule refused two demo pretrips (collection 4 of
  // 18), which silently stranded the remaining 14 INCLUDING `tickets`. A
  // surviving INVOICED ticket then made the next seed's content rewrite bounce
  // off the TASK-1176 billing lock, and Clock & Billing failed on every run
  // thereafter. One denied delete, an entire demo tenant degrading one-way.
  //
  // This is TASK-1226's junk-accumulation bug with a different trigger: there
  // the collections were missing from the list, here they were in the list and
  // never reached. Isolating each collection means the worst case is one dirty
  // collection that we can SEE in the log, not a silent cascade.
  let failed = [];
  for (const collectionName of demoCollections) {
    try {
      const collRef = collection(db, 'tenants', 'demo', collectionName);
      const snapshot = await getDocs(collRef);

      // Deleting a parent does NOT delete its subcollections -- the lines would
      // outlive the ticket and orphan themselves (10 such rows were found live).
      // Clear children first so a refused parent can't strand them either.
      if (collectionName === 'tickets') {
        for (const t of snapshot.docs) {
          try {
            const lines = await getDocs(collection(db, 'tenants', 'demo', 'tickets', t.id, 'ticketLines'));
            await Promise.allSettled(lines.docs.map(l =>
              deleteDoc(doc(db, 'tenants', 'demo', 'tickets', t.id, 'ticketLines', l.id))
            ));
          } catch (_) { /* per-ticket; a bad child must not stop the sweep */ }
        }
      }

      const results = await Promise.allSettled(snapshot.docs.map(docSnap =>
        deleteDoc(doc(db, 'tenants', 'demo', collectionName, docSnap.id))
      ));
      const stuck = results.filter(r => r.status === 'rejected').length;
      if (stuck) {
        failed.push(`${collectionName}(${stuck})`);
        console.warn(`[Demo] ${stuck}/${snapshot.docs.length} docs in ${collectionName} refused deletion — continuing`);
      } else {
        console.log(`[Demo] Cleared ${snapshot.docs.length} docs from ${collectionName}`);
      }
    } catch (error) {
      failed.push(collectionName);
      console.error(`[Demo] Error clearing ${collectionName} (continuing):`, error);
    }
  }

  // Say so out loud. The old code's silence is what let this run for days.
  if (failed.length) {
    console.warn(`[Demo] Demo data reset INCOMPLETE — undeleted: ${failed.join(', ')}`);
  } else {
    console.log('[Demo] Demo data reset complete');
  }
}

async function handleLogout() {
  _clearPinCaches(null); // TASK-9858 — offline PIN hashes never outlive the login
  try {
    // Clean up Firestore subscriptions FIRST to prevent permission errors after signout
    if (window.AppState.viewCleanup) {
      window.AppState.viewCleanup();
      window.AppState.viewCleanup = null;
    }
    try { unsubscribeTenantSettings?.(); } catch(_){}
    // TASK-366 — tear down member-access onSnapshot to prevent permission errors after signout
    try { unsubscribeMemberAccess?.(); } catch(_){}
    // TASK-745 — tear down the sidebar-prefs onSnapshot + clear hide-state so the
    // next login can't inherit the prior account's hidden modules (Edge #5).
    try { window.SidebarCustomize?.onTenantSwitch(); } catch(_){}
    // TASK-1290 — the preview rides the dim, so it dies with it.
    try { removeLockedModulePreview(); } catch(_){}
    try { document.getElementById('btn-customize-sidebar')?.remove(); } catch(_){}
    try { document.getElementById('mobile-customize-sidebar-btn')?.remove(); } catch(_){}

    // Clean up UI elements before signing out
    const salesFab = document.getElementById('sales-fab');
    if (salesFab) salesFab.remove();
    const lockFab = document.getElementById('lock-fab');
    if (lockFab) lockFab.remove();
    teardownLockSidebarLogo(); // TASK-763 — clear sidebar lock pill + reset logo art (no stale "Locked")
    stopVersionChecking();
    stopEmailVerifyRecheck(); // TASK-682 — no dangling verify-recheck handler on a signed-out session
    dismissTrialBanner();
    teardownAccountScopedChrome(); // TASK-10218 — CRM / grace / verify / sandbox banners + starters

    // Clear driver PIN mode if active — TASK-903 shared teardown (token, driver*
    // fields, body.driver-mode-pin, #driver-pin-shell-chrome, stub nav).
    if (window.AppState.driverModePin) {
      _teardownDriverPinChrome();
    }

    // Reset app state
    // Drop the cross-tab demo heartbeat FIRST — only this tab's demo owns it,
    // and it must not outlive the session it protects (checked before isDemo
    // is cleared below).
    if (window.AppState.isDemo) stopDemoHeartbeat();
    window.AppState.user = null;
    window.AppState.tenant = null;
    window.AppState.isDemo = false;
    // TASK-379 — clear fleet bridge (cross-tenant residue guard).
    window.AppState.fleetTrucks = null;
    window.AppState.fleetTrailers = null;
    window.AppState.fleetPeople = null;

    window.ClockMode?.reset?.(); // TASK-9553
    try { window.DispatchModule?.resetTenantData?.(); } catch (_) {} // P0 1019 — board roster/fleet never outlives the tenant
    // Clear member-access residue so the NEXT login (especially a legacy
    // single-login owner with no member doc) can't inherit the prior account's
    // module set. Without this, logging out of a limited sub-account and back
    // into the owner showed the sub-account's restricted nav until a hard
    // refresh (the owner's no-member-doc path doesn't overwrite memberAccess).
    window.AppState.memberAccess = null;
    // TASK-1658 — drop the partner client context + its banner. This tab must not
    // carry one carrier's identity into the next login.
    window.AppState.partnerClientContext = null;
    window.AppState.partnerClientRefused = false;
    if (typeof removePartnerClientBanner === 'function') removePartnerClientBanner();
    // TASK-595 — also clear the loading gate; a logout mid-getDoc would otherwise
    // leave it true until finally{} settles, and a deferred _awaitMemberAccess
    // poll could re-enter navigateToView post-logout (COD Blast/Independent).
    window.AppState.memberAccessPending = false;
    window.AppState.hasDirectReports = false;
    // TASK-1348 — also clear the DOM residue that state reset can't reach: the
    // previous account's nav stamps live on the elements, and this tab never
    // reloads between logins. hideUpgradeBadges() resets it too; doing it here
    // as well means a logout interrupted mid-sequence still leaves a clean nav.
    resetCoreNavBaseline();
    setAccountNavVisible(false);

    await signOut(auth);

    // Explicitly show auth screen (don't rely on auth state listener)
    hideSidebar();
    hideTrialExpiredScreen();
    hideEmailVerificationPending();
    showAuthModal();

    showToast('Signed out successfully', 'success');
  } catch (error) {
    console.error('[Auth] Logout error:', error);
    showToast('Error signing out', 'error');
  }
}

// Account deletion — Apple App Store guideline 5.1.1
async function handleDeleteAccount() {
  const user = auth.currentUser;
  if (!user) { showToast('Not signed in', 'error'); return; }

  const tenantId = window.AppState?.tenant;
  if (!tenantId) { showToast('No account found', 'error'); return; }

  // Prevent founder account deletion
  const FOUNDER_PROTECT = ['trippin-trucking'];
  if (FOUNDER_PROTECT.includes(tenantId)) {
    showToast('Founder accounts cannot be deleted', 'error');
    return;
  }

  // Double confirm
  const email = user.email || 'this account';
  if (!confirm(`DELETE ACCOUNT?\n\nThis will permanently delete your account (${email}) and all company data — people, vehicles, jobs, billing, files and settings.\n\nDriver qualification files, drug testing, payroll and time records you are required to keep will be deleted too. If you need a copy, contact support@dispatchdeck.app before you delete.\n\nAs required by law, we keep two kinds of records after you delete: background-check records for 5 years (not the report itself) and billing records for compliance services for 7 years. No one using DispatchDeck can see them.\n\nThis cannot be undone.`)) return;
  if (!confirm(`Are you absolutely sure?\n\nAll dispatch data, people, vehicles, billing records, and settings will be permanently deleted, except the records the law requires us to keep.`)) return;

  const btn = document.getElementById('btn-delete-account');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Deleting...'; }

  try {
    showToast('Deleting account data...', 'info');

    // TASK-9930 — the whole delete runs on the SERVER (deleteAccountData):
    // jobs through the ledgered wipe (TASK-9467 S1), then every collection the
    // tenant actually has (listCollections — it never relies on a list of
    // names), its files, sign-in links, the owner login and the tenant doc,
    // with a ledger row before the first delete. The old client loop named
    // collections the app does not use (billingTickets, timeOffRequests,
    // notifications) and swallowed every failure, so real data stayed behind.
    // Owner-only (ownerId), sign-in within 5 minutes. Time-boxed server side,
    // so a big account takes a few calls; each call resumes the last one.
    if (window.AppState?.isDemo === true || tenantId === 'demo') {
      showToast('The demo account cannot be deleted', 'error');
      return;
    }
    const _purge = httpsCallable(functions, 'deleteAccountData', { timeout: 545000 });
    let _done = false;
    for (let _i = 0; !_done && _i < 30; _i++) {
      if (_i > 0) showToast('Still deleting — a big account takes a few minutes. Keep this page open.', 'info');
      const _r = await _purge({ tenantId });
      _done = _r?.data?.ok === true && _r?.data?.more !== true;
    }
    if (!_done) throw new Error('This account is too big to delete in one go. What was deleted stays deleted — press Delete Account again to finish, or contact support.');

    // The server deleted the login itself; drop this tab's session.
    try { await signOut(auth); } catch (_) { /* already gone */ }

    // Reset state and show login
    window.AppState.user = null;
    window.AppState.tenant = null;
    window.AppState.isDemo = false;

    hideSidebar();
    showAuthModal();
    showToast('Account deleted successfully', 'success');
  } catch (error) {
    console.error('[Auth] Delete account error:', error);
    if (error.code === 'auth/requires-recent-login') {
      showToast('For security, please sign out and sign back in, then try again', 'error');
    } else {
      showToast('Error deleting account: ' + error.message, 'error');
    }
  } finally {
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:16px;">delete_forever</span> Delete Account'; }
  }
}
window.handleDeleteAccount = handleDeleteAccount;
window.handleLogout = handleLogout;

// TASK-4851 (C) — the login form stops asserting a cause it does not know.
// auth/invalid-credential means "wrong password" OR "no account with that
// email" OR "this account signs in with Google" — Firebase merges them on
// purpose (email-enumeration protection) and we must not un-merge them. The old
// copy said "Incorrect password"; Alyse Hymer's account was Google-only. Now:
// one neutral sentence and the three routes that cover every cause. Nothing
// here looks the email up — the same panel renders whether or not the account
// or an invite exists.
const LOGIN_CREDENTIAL_FAIL_MSG = "That email and password didn't sign you in.";
const _AMBIGUOUS_CREDENTIAL_CODES = new Set([
  'auth/invalid-credential',
  'auth/invalid-login-credentials',
  'auth/wrong-password',
  'auth/user-not-found',
]);
function _isAmbiguousCredentialError(code) {
  return _AMBIGUOUS_CREDENTIAL_CODES.has(code);
}

function showLoginRecoveryOptions() {
  const box = DOM.authError;
  if (!box) { showToast(LOGIN_CREDENTIAL_FAIL_MSG, 'error'); return; }
  box.textContent = '';
  const msg = document.createElement('div');
  msg.textContent = LOGIN_CREDENTIAL_FAIL_MSG + ' Try one of these:';
  msg.className = 'login-recovery-msg';
  const row = document.createElement('div');
  row.id = 'login-recovery-options';
  row.style.cssText = 'display:flex;flex-wrap:wrap;gap:8px;';
  const mk = (tag, id, label) => {
    const el = document.createElement(tag);
    el.id = id;
    el.textContent = label;
    el.className = 'login-recovery-btn'; // brand pill, styles.css (matches Try Demo)
    if (tag === 'button') el.type = 'button';
    return el;
  };
  const forgot = mk('button', 'login-recovery-forgot', 'Forgot password');
  forgot.addEventListener('click', () => handlePasswordReset());
  const google = mk('button', 'login-recovery-google', 'Sign in with Google');
  google.addEventListener('click', () => handleSocialSignIn('google'));
  // Apple too, so an Apple-only account isn't steered to Google.
  const apple = mk('button', 'login-recovery-apple', 'Sign in with Apple');
  apple.addEventListener('click', () => handleSocialSignIn('apple'));
  // Plain link, no email attached: the accept page needs the token from the
  // invite link (or, later, a typed code — slice B). Never an invite lookup.
  const invited = mk('a', 'login-recovery-invited', 'I was invited');
  invited.href = '/accept-invite.html';
  row.append(forgot, google, apple, invited);
  box.append(msg, row);
  box.classList.remove('hidden');
  document.getElementById('auth-success')?.classList.add('hidden');
  showToast(LOGIN_CREDENTIAL_FAIL_MSG, 'error');
  // The panel sits below the whole form (under Try Demo on a phone). 'center',
  // not 'nearest': at the bottom edge the error toast covers the third route.
  try { box.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (_) {}
}

function getAuthErrorMessage(code) {
  const messages = {
    'auth/invalid-email': 'Please enter a valid email address.',
    'auth/user-disabled': 'This account has been disabled. Contact support for help.',
    // TASK-4851 (C) — ONE message for all four on purpose. Firebase's email-
    // enumeration protection merges "no such account" and "wrong password" into
    // auth/invalid-credential; a different message per code would hand the
    // distinction back wherever protection is off.
    'auth/user-not-found': LOGIN_CREDENTIAL_FAIL_MSG,
    'auth/wrong-password': LOGIN_CREDENTIAL_FAIL_MSG,
    'auth/invalid-credential': LOGIN_CREDENTIAL_FAIL_MSG,
    'auth/invalid-login-credentials': LOGIN_CREDENTIAL_FAIL_MSG,
    'auth/email-already-in-use': 'An account with this email already exists. If you signed up with Google, use "Continue with Google" to sign in.',
    'auth/weak-password': 'Password must be at least 6 characters.',
    'auth/too-many-requests': 'Too many failed attempts. Please wait a few minutes and try again.',
    'auth/network-request-failed': 'Network error. Check your connection and try again.',
    'auth/missing-password': 'Please enter your password.',
    'auth/internal-error': 'Sign-in temporarily unavailable. Please try again in a moment.',
    'auth/operation-not-allowed': 'This sign-in method is not enabled. Please try another method.',
    'auth/popup-blocked': 'Pop-up was blocked. Please allow pop-ups and try again.',
    'auth/cancelled-popup-request': 'Sign-in was cancelled. Please try again.',
    'auth/popup-closed-by-user': 'Sign-in was cancelled. Please try again.'
  };
  if (messages[code]) return messages[code];
  console.warn('[Auth] Unhandled error code:', code);
  return 'Unable to sign in. Please check your connection and try again.';
}

// Password Reset - uses custom branded email via SendGrid
let _lastPasswordResetAt = 0;
const PASSWORD_RESET_COOLDOWN_MS = 60 * 1000;

async function handlePasswordReset() {
  const email = document.getElementById('login-email').value.trim();

  if (!email) {
    showAuthError('Please enter your email address first.');
    return;
  }

  // Basic email format validation
  if (!email.includes('@') || !email.includes('.')) {
    showAuthError('Please enter a valid email address.');
    return;
  }

  // Client-side cooldown to prevent accidental double-clicks sending duplicate emails
  const elapsed = Date.now() - _lastPasswordResetAt;
  if (elapsed < PASSWORD_RESET_COOLDOWN_MS) {
    const secondsLeft = Math.ceil((PASSWORD_RESET_COOLDOWN_MS - elapsed) / 1000);
    showAuthSuccess(`Reset email just sent — check your inbox (and spam). You can try again in ${secondsLeft}s.`);
    return;
  }

  try {
    hideAuthError();
    _lastPasswordResetAt = Date.now();
    // Use our custom password reset function (branded SendGrid email)
    const sendReset = httpsCallable(functions, 'sendCustomPasswordReset');
    await sendReset({ email });
    // Always show the SAME message (never reveal whether the account exists).
    showAuthSuccess('If an account exists with that email, a reset link has been sent. Check your inbox and spam folder.');
  } catch (error) {
    _lastPasswordResetAt = 0;
    console.error('[Auth] Password reset error:', error);
    showAuthError('Unable to send reset email. Please try again.');
  }
}

// Expose to window for onclick
window.handlePasswordReset = handlePasswordReset;

// ============================================
// ROUTING
// ============================================
function setupRouting() {
  window.addEventListener('hashchange', handleHashChange);
  handleHashChange(); // Handle initial hash
}

// Single source of truth for a signed-in member's default landing view.
// (TASK-355 / Roger @ Split Rock 2026-06-26.) There are FOUR routing call
// sites — handleHashChange, the auth-observer re-derivation, the member-login
// path, and the no-access fallback — that each used to keep their own copy of
// this ternary. They drifted, so the dispatch-first fix below would only fire on
// some of them (a fresh login hits the member-login path, NOT handleHashChange).
// Centralize here and call from all four so the default can never disagree.
//
// Order of preference:
//   1. dispatch-capable members (dispatcher/admin who run the WHOLE schedule) →
//      the full Dispatch board. A new dispatcher with 0 jobs assigned to himself
//      landing on his personId-scoped (empty) Job Board read as "nothing on my
//      account" even though Dispatch had the full 100-job schedule.
//   2. personId-linked jobboard members (drivers / field staff) → their personal
//      daily Job Board (the canonical personId-scoped view; My Today killed).
//   3. otherwise → first granted module with a sidebar entry.
// Per-module access is still enforced downstream by checkModuleAccess(), so this
// only chooses WHERE to land, never WHAT a member may see.
// TASK-9779 — a staff login holding '811-permits' and NOT 'dispatch'. The 811
// window lives in the Dispatch view, so this login is let into that VIEW
// (checkModuleAccess) and dispatch.js renders ONLY the 811 window for it
// (is811OnlyMode). It grants no job access: firestore.rules keeps jobs on
// 'dispatch', and openJobDetailsById / the project openers refuse it. The
// signed-in twin of the PIN rule "811 rides dispatch" (pinGrantsModule).
// Owners and legacy no-modules admins are never 811-only.
// TASK-9805 (Chris 2026-10-13, harvest partner-811: "Yes") — a compliance-
// partner session (role 'compliancePartner' = PARTNER_MEMBER_ROLE, a literal
// here because this runs before that const in tests) now IS 811-only: its
// scope keeps '811-permits' and drops dispatch, so it gets the 811 window and
// nothing else of Dispatch. The job picker stays hidden for every 811-only
// login (show811Submission / renamePermit), which was COD r1's reason for the
// old exclusion. A partner may only LOOK (isPartner811ReadOnly below).
function isMember811Only(memberAccess) {
  if (!memberAccess || memberAccess.role === 'owner') return false;
  if (['revoked', 'inactive', 'pending'].includes(memberAccess.status)) return false;
  const mods = memberAccess.modules;
  return Array.isArray(mods) && mods.includes('811-permits') && !mods.includes('dispatch');
}
window.isMember811Only = () => isMember811Only(window.AppState?.memberAccess);
// TASK-9805 — the partner's 811 window is READ-ONLY, matching every other
// operational window a partner opens (firestore.rules TASK-1658 slice B: jobs,
// maintenance, DVIR … are read arms only; the carrier's members stay the only
// writers). /permits create/update need a members doc holding dispatch or
// 811-permits, which a partner never has, so a write would 403 anyway — this
// hides the upload / edit / delete controls so the window never offers one.
// The synthetic partner grant is the ONLY memberAccess with this role (it is
// never persisted as a members doc — buildPartnerMemberEquivalent).
function isPartner811ReadOnly(memberAccess) {
  return !!memberAccess && memberAccess.role === 'compliancePartner' && isMember811Only(memberAccess);
}
window.isPartner811ReadOnly = () => isPartner811ReadOnly(window.AppState?.memberAccess);

// TASK-9651 — Projects follows the 'projects' MODULE, not Dispatch. The ONE
// client answer to "may this login see projects?", mirroring firestore.rules
// projRead: demo, the owner (tenants.ownerId), or a live member whose modules
// LIST holds 'projects'. members.role is never read, so a legacy no-modules
// admin is NOT in (the rules refuse them too). Driver-PIN / link sessions never
// are (they read projects through driverProjectInfo). Every Projects surface —
// the Dispatch Projects button + view, the job form's Project picker, CRM's
// Active Projects card + links, Billing's Projects view, the 811 project field —
// asks this instead of computing its own.
function canAccessProjects() {
  const S = window.AppState || {};
  if (S.isDemo || S.tenant === 'demo') return true;
  if (S.driverModePin || S.driverMode || S.isPublicAccess || S.accessToken) return false;
  const uid = S.user?.uid || null;
  const ownerId = S.tenantData?.ownerId || null;
  if (uid && ownerId && uid === ownerId) return true;
  const m = S.memberAccess;
  if (!m || ['suspended', 'removed', 'revoked', 'inactive', 'pending'].includes(m.status)) return false;
  return Array.isArray(m.modules) && m.modules.includes('projects');
}
window.canAccessProjects = canAccessProjects;

function computeMemberDefaultView(memberAccess) {
  if (!memberAccess || !Array.isArray(memberAccess.modules) || memberAccess.modules.length === 0) return null;
  if (memberAccess.modules.includes('dispatch')) return 'dispatch';
  if (memberAccess.personId && memberAccess.modules.includes('jobboard')) return 'jobboard';
  // TASK-1489 — the `personId &&` guard above was being defeated one line down:
  // 'jobboard' also sits in the generic fallback whitelist, so a member with NO
  // personId still landed on the Job Board — which, with a falsy driverPersonId,
  // renders the ENTIRE tenant schedule (driverScheduleHelpers
  // jobDayVisibleToCurrentDriver returns true for everything). Drop jobboard from
  // the fallback for personId-less members so the guard actually holds. A LINKED
  // member is unaffected: line 2 above already claimed them.
  // TASK-9778 — 'receipts' (Receipts & Expenses) last: a receipts-only login lands on it.
  const fallback = ['jobboard', 'hr', 'maintenance', 'dispatch', 'crm', 'clock', 'billing', 'workforce', 'bi', 'compliance', 'receipts']
    .filter(m => m !== 'jobboard' || !!memberAccess.personId);
  // TASK-9779 — '811-permits' is not a view; its window opens in Dispatch.
  return memberAccess.modules.map(m => (m === '811-permits' ? 'dispatch' : m)).find(m => fallback.includes(m)) || memberAccess.modules[0];
}

// TASK-9131 — `ap` is a VIEW, not a MODULE.
//
// Accounts Payable is a SURFACE of the `billing` module, not an entitlement of
// its own. Three independent places already say so:
//   • doNavigateToView highlights the BILLING nav row for it
//     (`const navView = view === 'ap' ? 'billing' : view`).
//   • billing.js reaches the very same screen with switchBillingView('ap') from
//     the AR/AP pill row, and renders it through window.APModule.renderAPView.
//   • roster.js STAFF_LOGIN_MODULE_OPTIONS — the single source of truth for
//     what an admin can actually grant a staff login — has NO `ap` row. The
//     billing row is literally labelled "AR / AP Billing".
//
// So nothing in the product can ever put the string 'ap' into
// `members/{uid}.modules`, into a driver token's `tokenModules`, or into
// `settings.accessPins`. Gating the view by its own name therefore fails BOTH
// ways and both ways are silent:
//   • a scoped member who legitimately holds `billing` gets "No access — AR/AP",
//     because `memberAccess.modules.includes('ap')` is false forever; and
//   • a kiosk/shared-login tenant sails STRAIGHT PAST its billing PIN, because
//     'ap' is not in checkModuleAccess's `lockedModules` list and so falls all
//     the way through to the `return true` "allowed by default" tail — vendor
//     invoices, bank routing numbers and the check register behind no PIN.
// Gate the VIEW by the MODULE that owns it. Keep this map tiny and obvious; if
// it grows past a couple of entries the right answer is a real module, not
// another alias.
// TASK-10249: the Approvals page (#/approvals) has exactly Account's access.
const VIEW_MODULE_ALIASES = { ap: 'billing', approvals: 'account' };
function moduleForView(view) {
  return VIEW_MODULE_ALIASES[view] || view;
}

function handleHashChange() {
  // TASK-319 multi-user member — default to a granted view (not roster),
  // otherwise the SPA lands on no-access for members without roster perms.
  const memberAccess = window.AppState?.memberAccess;
  const isMember = memberAccess && memberAccess.status === 'active'
    && memberAccess.role !== 'owner'
    && Array.isArray(memberAccess.modules)
    && memberAccess.modules.length > 0;
  const memberDefault = isMember ? computeMemberDefaultView(memberAccess) : null;
  // Default to token's defaultView (if set), dispatch for driver mode, member's first granted, roster for normal mode
  const defaultView = window.AppState?.driverMode || window.AppState?.isPublicAccess
    ? (window.AppState?.defaultView || 'dispatch')
    : memberDefault
      || (isComplianceOnlyTenant() ? 'compliance' : 'roster');
  // Skip if hash was set programmatically by doNavigateToView
  if (window._skipHashChange) {
    window._skipHashChange = false;
    return;
  }

  // Strip any `?query` suffix before parsing the view — OAuth returns land on
  // hashes like `#/settings?qbo=complete`; without this strip the view name
  // becomes the literal `settings?qbo=complete`, matches no known view, and
  // falls back to the default module (roster/dispatch). (TASK-781)
  const hash = (window.location.hash.slice(2) || defaultView).split('?')[0]; // Remove #/ + ?query
  const segments = hash.split('/');
  const view = segments[0]; // First segment is the top-level view

  // TASK-9753 S2 — #/settings and #/settings/<section> (e.g. #/settings/team)
  // open the Settings modal on that section, over the current page. Office
  // sessions only; a ?query hash (Stripe / QuickBooks OAuth returns) keeps the
  // fallback below exactly as before.
  if (view === 'settings' && !window.location.hash.includes('?')
      && !window.AppState?.driverModePin && !window.AppState?.driverMode && !window.AppState?.isPublicAccess) {
    let section = '';
    try { section = decodeURIComponent(segments[1] || ''); } catch (_) { section = ''; }
    _routeSettingsDeepLink(section, defaultView);
    return;
  }

  // TASK-228 deep-link support — `#/hr/people/{id}` and `#/hr/candidates/{id}`
  // route to the HR view and open the employee/candidate detail modal once
  // its data loads. Used by checkrWebhook BG-complete emails so the carrier
  // admin lands one click away from the Compliance tab. hr.js drains
  // `window.AppState.pendingHrFocus` inside its onSnapshot callbacks.
  if (view === 'hr' && segments[1] && segments[2]) {
    const subType = segments[1];
    let subId = '';
    try {
      subId = decodeURIComponent(segments[2]);
    } catch (e) {
      // Malformed encoding — fall through to bare #/hr without focus.
      console.warn('[Router] hr deep-link decode failed', e);
    }
    if ((subType === 'people' || subType === 'candidates') && subId) {
      window.AppState.pendingHrFocus = { type: subType, id: subId };
      // 60s stale-clear so a never-matched id doesn't trap focus on subsequent
      // unrelated onSnapshot fires. Cleared on match too (hr.js drain).
      try { clearTimeout(window._pendingHrFocusTimer); } catch (_) {}
      window._pendingHrFocusTimer = setTimeout(() => {
        if (window.AppState && window.AppState.pendingHrFocus) {
          console.warn('[Router] pendingHrFocus expired (no match in 60s)', window.AppState.pendingHrFocus);
          window.AppState.pendingHrFocus = null;
        }
      }, 60000);
    }
  }

  // Build allowed views based on access mode
  let allowedViews;
  if (window.AppState?.driverModePin) {
    // Driver PIN mode — dispatch + pretrip + maintenance (if allowed) + jobboard (if clock access)
    allowedViews = ['dispatch', 'pretrip'];
    if (window.driverCanSeeMaintenance?.()) allowedViews.push('maintenance'); // TASK-774 Gap B — grant OR ≥1 assigned task
    if (window.driverHasClockAccess?.()) allowedViews.push('jobboard');
  } else if (window.AppState?.driverMode || window.AppState?.isPublicAccess) {
    // Public access mode — base views + any token-granted modules
    const baseViews = ['dispatch', 'jobboard', 'pretrip'];
    const tokenModules = window.AppState?.tokenModules || [];
    allowedViews = [...new Set([...baseViews, ...tokenModules])];
  } else if (window.AppState?.isDemo) {
    // Demo mode - all modules accessible for full demonstration
    // 'account' / 'ap' — see the notes on the full-access list below.
    allowedViews = ['dispatch', 'jobboard', 'pretrip', 'maintenance', 'roster', 'clock', 'billing', 'ap', 'timeoff', 'hr', 'crm', 'bi', 'compliance', 'dot-advisor', 'payroll', 'account', 'approvals', 'receipts'];
  } else {
    // Full access - includes HR and CRM for founder/VIP accounts.
    // TASK-355 2026-05-22: 'mytoday' removed; jobboard is now member-aware.
    // TASK-850B: 'payroll' — admin-only in-view + server-side; route resolves for all.
    // 'account' — Settings → "Manage Account & Billing" (index.html
    // #btn-account-management) sets location.hash='#/account', but 'account'
    // was never in this list, so handleHashChange fell through its final
    // else-if (driver/public only) and did NOTHING: the hash changed, no
    // navigation fired, and `case 'account'` (:11292 initAccount) was
    // unreachable dead code since the button shipped. Members are already
    // granted 'account' at :9503 — the router just never honoured it.
    // TASK-9131: 'ap' — the SAME bug, still live after 'account' was fixed.
    // `case 'ap': initAP(...)` and the ap→billing nav highlight both shipped,
    // but 'ap' was in ZERO of these four lists, so #/ap was reachable by
    // function call and unreachable by URL. It is listed HERE and only here
    // (plus demo): a driver-PIN session must never gain AP, and the
    // public/token branch derives its list from tokenModules, which can never
    // contain 'ap'. The real entitlement check still runs downstream —
    // navigateToView → checkModuleAccess → moduleForView('ap') === 'billing'.
    // TASK-9778: 'receipts' — the Receipts & Expenses view; checkModuleAccess
    // admits the receipts or billing module, and on the kiosk PIN path it
    // rides the Billing PIN (never the allowed-by-default tail).
    allowedViews = ['dispatch', 'jobboard', 'pretrip', 'maintenance', 'roster', 'clock', 'billing', 'ap', 'timeoff', 'hr', 'crm', 'bi', 'compliance', 'dot-advisor', 'payroll', 'account', 'approvals', 'receipts'];
  }

  // Workforce is a virtual view — route to clock module's workforce tab
  if (view === 'workforce') {
    window.AppState.currentView = 'workforce';
    if (window.AppState.user) {
      navigateToView(view);
    }
    return;
  }

  // TASK-9658 S1 — record deep links. Parked ONLY when this session's router
  // allows the view; navigateToView() below still runs the module-access gate
  // (member modules / kiosk PIN / driver PIN) and a denied view never mounts,
  // so the record is never drained. Any hash that is NOT a record link drops a
  // previously parked focus, so a stale one can't pop a modal later.
  const _recordRoute = window.RecordLinks?.parseRecordRoute?.(window.location.hash) || null;
  if (_recordRoute && allowedViews.includes(_recordRoute.view)) {
    window.AppState.pendingRecordFocus = { view: _recordRoute.view, type: _recordRoute.type, id: _recordRoute.id, at: Date.now() };
  } else {
    window.AppState.pendingRecordFocus = null;
    if (_recordRoute) console.warn(`[Router] record link for "${_recordRoute.view}" not routable in this session — ignored`);
  }

  // TASK-9653 S1 — open hints ride the hash QUERY (`#/hr?dd_open=…`), which the
  // view parse above strips. Parked only for a view this session's router
  // allows; navigateToView() below still runs the module gate, and the view
  // re-checks access when it drains. A hint parked earlier (the ORIGINAL hash,
  // at load) for a view this session cannot route is dropped here. A hash with
  // no hint leaves a parked one alone: doNavigateToView drops it on a move to
  // another view.
  try {
    const _openHint = window.OpenLinks?.parseOpenHint?.(window.location.hash) || null;
    if (_openHint && allowedViews.includes(_openHint.view)) window.OpenLinks.parkHint(_openHint, window.AppState);
    if (window.AppState.pendingFocus && !allowedViews.includes(window.AppState.pendingFocus.view)) window.AppState.pendingFocus = null;
  } catch (_) { window.AppState.pendingFocus = null; }

  if (allowedViews.includes(view)) {
    window.AppState.currentView = view;
    if (window.AppState.user) {
      navigateToView(view);
    }
  } else if (window.AppState?.driverModePin || window.AppState?.driverMode || window.AppState?.isPublicAccess) {
    // Invalid view in driver/public mode - redirect to dispatch
    const fallback = window.AppState?.defaultView || 'dispatch';
    window.AppState.currentView = fallback;
    if (window.AppState.user) {
      // TASK-9658 S1b — a record link this login can't open (focus already
      // dropped above) says so instead of silently landing somewhere else.
      if (_recordRoute && _recordRoute.view === view) {
        try { showToast("That link opens a record this login can't see.", 'info'); } catch (_) {}
      } else if (view === 'approvals') { // TASK-10249: old #/account?claude=requests links arrive here already forwarded (approvalsBadge.js)
        // TASK-10111 — an approveAt link opened in a driver session: say why it
        // did not open, instead of a silent drop onto the driver board.
        try { showToast('This approval link can’t open here: this device is signed in as a driver. Open it where you sign in to the office app.', 'info'); } catch (_) {}
      }
      navigateToView(fallback);
    }
  } else {
    // 🔴 TASK-9131 — THE CLASS FIX. Until now this `else` did not exist: the
    // chain ended at the driver/public branch above, so an OFFICE user whose
    // hash named a view missing from `allowedViews` fell off the END of this
    // function. The hash changed, no navigation fired, and the app silently
    // kept showing whatever it was already showing. It did not error. It lied.
    //
    // That is the shape of BOTH known instances: 'account' (fixed 2026-xx, see
    // the note on the full-access list) and 'ap' (fixed in this task). Someone
    // fixed the instance and not the class, so the next missing entry was
    // guaranteed to be silent too. From here a route we do not recognise
    // BOUNCES somewhere real and SAYS so.
    //
    // BLAST RADIUS, enumerated before shipping (every `location.hash =` and
    // every `#/…` literal in public/, excluding dist/ and worktrees, plus the
    // app deep-links emitted from functions/):
    //   • Every literal that resolves today (#/dispatch #/roster #/hr #/billing
    //     #/clock #/crm #/bi #/pretrip #/maintenance #/compliance #/workforce
    //     #/dot-advisor #/account) is in a list above — untouched.
    //   • `#/` and `href="#"` already fall back via `hash.slice(2) || defaultView`
    //     and hit the allowed branch — untouched. There is not a single bare
    //     in-page anchor (`href="#name"`) in public/index.html or public/src.
    //   • `#/settings…` — the Stripe/QBO OAuth returns and the SMS-credit email
    //     CTA. All COLD loads, where AppState.currentView already defaults to
    //     'roster' and the post-auth navigateToView(currentView) already lands
    //     there; the QBO drain then explicitly navigates to Billing. Same end
    //     state, minus the `#/undefined` currentView this used to leave behind.
    //   • `#bi` (views/ap.js, missing its slash → parses to view 'i') is the one
    //     live WARM writer of an unrecognised hash. Today that button does
    //     nothing at all; now it bounces visibly. Neither reaches BI — the typo
    //     is a separate row, and this fix is what makes it findable.
    console.warn(`[Router] Unrecognised view "${view}" — falling back to "${defaultView}". If this view exists in navigateToView, it is MISSING from allowedViews above.`);
    window.AppState.currentView = defaultView;
    if (window.AppState.user) {
      // Only toast once the app is actually up — the pre-auth setupRouting()
      // call runs with no user and must stay silent.
      try { showToast(`That page isn’t available — showing ${_prettyModuleLabel(defaultView)}.`, 'info'); } catch (_) {}
      navigateToView(defaultView);
    }
  }
}

// TASK-974 S2 — Spanish toggle for the DRIVER-facing surfaces. The i18n module
// (public/src/driver-i18n-es.js, classic script, exposes window.DriverI18n) is
// injected ONLY when a driver session is active (PIN mode / legacy driver link /
// ?access= token). Office/dispatcher sessions never even download the file, so
// the office app is byte-identical untouched. Idempotent + cheap flag check —
// called from navigateToView on every navigation (driver flags are set before
// the driver's first navigate on all three session paths).
function _maybeLoadDriverI18n() {
  const as = window.AppState || {};
  if (!(as.driverModePin || as.driverMode || as.isPublicAccess)) return;
  if (window.DriverI18n) {
    try { window.DriverI18n.syncActivation(); } catch (_) {}
    return;
  }
  if (window._driverI18nLoading) return;
  window._driverI18nLoading = true;
  // COD Code Reviewer — version checkers can strip/rewrite location.search, so
  // hand the module the PRESERVED ?lang= param (ORIGINAL_URL_PARAMS pattern,
  // same reason app.js:1093 uses it) instead of letting it re-parse the URL.
  try { window.__ddDriverLangParam = ORIGINAL_URL_PARAMS.get('lang'); } catch (_) {}
  const s = document.createElement('script');
  s.src = '/src/driver-i18n-es.js?v=10295'; // TASK-9694 S4 switch-sheet strings; TASK-9703 add-day / copy / delete strings; TASK-9710 per-driver job edit strings; TASK-9865 crew-line strings; TASK-9862 locate card strings; TASK-9648 811 ticket strings; TASK-9694 polish strings; TASK-9694 job-screen strings; TASK-9493 split strings; // classic script (not an ES module) — no dual-instance risk
  s.defer = true;
  document.head.appendChild(s);
}

function navigateToView(view) {
  // TASK-345 Slice L — async because each view module is dynamically imported.
  // Callers that fire-and-forget (setTimeout, onclick) don't need to await.
  console.log('[Router] Navigating to:', view);

  // TASK-974 S2 — activate the driver Spanish toggle for driver sessions only.
  _maybeLoadDriverI18n();

  // 2026-05-28 — PIN driver /dispatch redirect REVERTED. The redirect was
  // added to bypass dispatch's heavy subs but it stranded PIN drivers from
  // the entire driver UI: bottom-nav (Inspections/Clock/Exam/Actions) +
  // Actions sheet tiles (Vehicles, Switch Job, Time Off, Record Expense) —
  // all rendered inside dispatch.js renderDriverBoard. Without /dispatch
  // access, PIN drivers lost that whole surface. Reverting restores their
  // canonical UI. Perf concern addressed elsewhere: v1.15.913 cleanup-
  // before-subscribe killed the duplicate-listener storm that was the
  // actual root cause of "dispatch takes forever" (PIN restore +
  // hash-change combined to fire initDispatch twice without intermediate
  // cleanup, creating 20+ listeners and 14+ cascading renders).

  // Track demo engagement (3-click popup trigger)
  trackDemoEngagement();

  // Trial-expired hard wall — block all navigation, route to subscribe screen.
  // Skip for demo, public token pages, and driver PIN mode (drivers don't
  // own subscriptions; they shouldn't see a subscribe screen if their employer
  // lapsed — that's the owner's problem to resolve).
  // TASK-1658 — and skip for a partner inside a client account, for exactly the
  // driver reason above: they do not own the subscription, and walling them here
  // would lock the compliance files away from the person paid to keep them legal.
  if (!window.AppState?.isDemo && !window.AppState?.isPublicAccess && !window.AppState?.driverModePin && !isPartnerClientSession()) {
    const trialStatus = window.AppState?.trialStatus || getTrialStatus();
    if (trialStatus?.status === 'expired') {
      console.log('[Router] Trial expired — routing to subscribe screen instead of', view);
      window.AppState.pendingRecordFocus = null; // TASK-9658 S1 — never drained behind the wall
      window.AppState.pendingFocus = null; // TASK-9653 S1 — nor an open hint
      showTrialExpiredScreen();
      return;
    }
  }

  // Driver PIN mode — hard block everything except dispatch + pretrip + jobboard (if clock access)
  if (window.AppState?.driverModePin) {
    const driverAllowed = ['dispatch', 'pretrip'];
    if (window.driverCanSeeMaintenance?.()) driverAllowed.push('maintenance'); // TASK-774 Gap B
    if (window.driverHasClockAccess?.()) driverAllowed.push('jobboard');
    if (!driverAllowed.includes(view)) {
      console.log(`[Router] Driver PIN mode - blocking ${view}`);
      showNoAccessMessage(view);
      return;
    }
  }

  // Compliance-only tenants: let them browse all modules with a preview overlay.
  // Roster moved to visible-locked (2026-05-13) — clicking Roster now shows
  // the upgrade overlay rather than the live people list (carriers still add
  // people via HR → Hiring/Applicants → Add Candidate → promote, or via
  // John's compliance partner portal).
  // 🔴 TASK-1658 — same bypass as checkModuleAccess (COD Adversary/Blast). This
  // branch calls doNavigateToView(view) for ANY view and RENDERS the live data
  // before the upgrade overlay lands 300ms later, so on a complianceOnly carrier
  // a partner could route straight into Billing/CRM/Dispatch and read it. A
  // partner skips this branch and is routed by their own module scope instead.
  if (isComplianceOnlyTenant() && !isPartnerClientSession()) {
    const complianceUnlocked = ['compliance', 'hr'];
    if (!complianceUnlocked.includes(view)) {
      // TASK-9658 S1 — a locked preview module never opens a record: the
      // record modal would sit on top of the upgrade overlay.
      window.AppState.pendingRecordFocus = null;
      window.AppState.pendingFocus = null; // TASK-9653 S1 — same for an open hint
      // Load the view so they can see their data, then show the subscribe overlay
      doNavigateToView(view);
      setTimeout(() => showComplianceUpgradeOverlay(view), 300);
      return;
    }
    // Compliance + HR: full access, skip all other gates
    doNavigateToView(view);
    return;
  }

  // Single plan — all modules available to subscribers, demo, trial, founder, VIP
  // No per-module tier gating needed

  // Check module access (skip for demo, public access, and pretrip which is always open)
  // PIN-gated tokens still need PIN check despite being public access
  if (!window.AppState?.isDemo && (!window.AppState?.isPublicAccess || window.AppState?.pinGated) && view !== 'pretrip') {
    const hasAccess = checkModuleAccess(view);
    if (hasAccess === false) {
      // No access - show message
      showNoAccessMessage(view);
      return;
    }
    if (hasAccess === 'loading') {
      // TASK-595 — member access still resolving. Show the loading screen and
      // wait, then re-gate, instead of PIN-challenging an entitled signed-in
      // member. Bounded (5s): on a slow/hung read we STOP waiting, force-clear
      // the flag (so the re-gate can't re-enter 'loading' forever — COD Code
      // Reviewer), warn the user, and fall back to today's behavior.
      showLoading();
      const _started = Date.now();
      const _awaitMemberAccess = () => {
        if (!window.AppState?.memberAccessPending) {
          hideLoading();
          navigateToView(view);
        } else if (Date.now() - _started > 5000) {
          window.AppState.memberAccessPending = false;
          hideLoading();
          showToast('Still loading your access — check your connection', 'warning');
          navigateToView(view);
        } else {
          setTimeout(_awaitMemberAccess, 50);
        }
      };
      _awaitMemberAccess();
      return;
    }
    if (hasAccess === 'needs-pin') {
      // TASK-1528 — members/{uid}.modules is ABSOLUTE for signed-in scoped
      // members: they must NEVER see the PIN challenge (a kiosk/shared-device
      // surface they have no PIN for). 'needs-pin' legitimately reaches here
      // only for kiosk users (no member doc) and TASK-719 device-locked
      // owners / legacy admins (role 'owner', or 'admin' with NO modules —
      // both excluded below, so their deliberate PIN routing is untouched).
      // A scoped member (non-owner/admin-bypass, non-blocked, with a modules
      // array) landing here means a load-order race or future regression —
      // show the member-aware no-access message instead of a dead-end PIN
      // prompt. Belt-and-braces on top of the loadUserTenant-wide
      // memberAccessPending gate (~:6093).
      const _pinMa = window.AppState?.memberAccess;
      const _pinScopedMember = _pinMa
        && !['revoked', 'inactive', 'pending'].includes(_pinMa.status)
        && _pinMa.role !== 'owner'
        && Array.isArray(_pinMa.modules) && _pinMa.modules.length > 0;
      if (_pinScopedMember) {
        console.warn(`[Router] Scoped member reached 'needs-pin' for ${view} — suppressing PIN challenge (TASK-1528)`);
        showNoAccessMessage(view);
        return;
      }
      // Has role access but needs PIN.
      // TASK-9131 — label by the OWNING module, so #/ap on a PIN-gated tenant
      // says "Enter PIN to unlock AR / AP Billing" and not "…unlock Ap". The
      // PIN itself is already the billing PIN (moduleForView, above).
      // TASK-9135 — third argument is the ACCESS SCOPE, not the label: the
      // entered PIN must actually grant this module or submitPinChallenge
      // refuses it. Without it the challenge accepts any configured PIN for any
      // module and this callback navigates on it.
      showPinChallenge(view === 'ap' ? 'AR / AP Billing' : capitalizeFirst(view), (unlocked) => {
        if (unlocked) {
          doNavigateToView(view);
        } else {
          showNoAccessMessage(view);
        }
      }, moduleForView(view));
      return;
    }
  }

  // Single plan — no feature package gating

  doNavigateToView(view);
}

// Founding accounts - full access forever, no payment required
const FOUNDER_TENANTS = ['trippin-trucking'];

// VIP accounts - full access, legacy/exempt accounts
const VIP_TENANTS = ['ml71j5u3v1p1eucih', 'ml5i6vjiw6qnp3ph8', 'mls9heexfhihldja7', 'mlv7vojmq1d7jadhf', 'mly4vjfs4kjj4lu0x', 'mlwstjmtoyp7jyhv0', 'mmjk6kgvailry6sz8', 'mmfnt3968rcjj4ope', 'mmfo7b4no2fu0ypjl', 'mmhevn8d6m8uf6f2g', 'showcase-mmw5ce16', 'show-account', 'mnc4z8d70evle7pzu', 'mmwwgj1lkr941cytz', 'moj282kc4mozd4ijd']; // Nathan (Expertool), Shauna (S&F), Robert (Topknot), Larry (Aces admin), Larry (Aces personal), David (Vanassche - friend/bug tester), Chris test account, Josh Fox (Rad Pros), Kirk Kancilia (Kirk's Welding), Josh Erickson (Noble Trucking), Guy demo (christopherkjorstad@gmail.com), Show demo (show@dispatchdeck.app), Amber (North Point Transport), Apple Review test (christopherkjorstad@gmail.com), Guy Hancock (Split Rock Energy — real active customer, comped), David Moore (Busy Bee's Hot Oil Inc — comped, ship slip feature largely built for them)

function isComplianceOnlyTenant() {
  return window.AppState?.tenantData?.complianceOnly === true;
}
// Expose on window so cross-module callers (compliance.js, etc.) can gate
// features by tier without a direct ES import (which would create a
// duplicate module instance via the cache buster — see lessons.md).
window.isComplianceOnlyTenant = isComplianceOnlyTenant;

function setupComplianceOnlySidebar() {
  const unlockedViews = ['compliance', 'hr'];
  const visibleLocked = ['dispatch', 'maintenance', 'pretrip', 'billing', 'bi', 'roster'];
  const hiddenViews = ['clock', 'workforce', 'crm'];

  // Show all modules; compliance + hr are the live views. pretrip renders as
  // a visibleLocked upsell tease — NOT fully accessible (TASK-1097: the
  // pretrip/dvir force-grant no longer applies to compliance-only tenants).
  document.querySelectorAll('#main-nav .nav-item, .mobile-more-sheet .nav-item').forEach(el => {
    const view = el.dataset.view;
    if (!view) return;

    if (unlockedViews.includes(view)) {
      el.classList.remove('hidden', 'locked');
      el.querySelectorAll('.nav-upgrade-badge').forEach(b => { b.style.display = 'none'; });
      const lockIcon = el.querySelector('.lock-icon');
      if (lockIcon) lockIcon.remove();
    } else if (visibleLocked.includes(view)) {
      el.classList.remove('hidden');
      el.classList.add('locked');

      // Smart tease: show data counts on locked sidebar items
      const fmcsa = window.AppState?.tenantData?.fmcsaData || {};
      const pCount = window.AppState?.people?.length || fmcsa.totalDrivers || 0;
      const tCount = window.AppState?.trucks?.length || fmcsa.totalPowerUnits || 0;
      const teaseMap = {
        dispatch: pCount > 0 ? `${pCount} drivers` : '',
        roster: pCount > 0 ? `${pCount} people` : '',
        maintenance: tCount > 0 ? `${tCount} vehicles` : '',
        pretrip: tCount > 0 ? `${tCount} vehicles` : '',
        billing: pCount > 0 ? `${pCount} crew` : '',
        bi: pCount > 0 ? 'Analytics' : '',
      };
      const teaseText = teaseMap[view] || '';
      // Replace badge text with data-aware tease
      el.querySelectorAll('.nav-upgrade-badge').forEach(b => {
        b.textContent = teaseText || 'SUBSCRIBE';
        if (teaseText) {
          b.style.cssText = 'display:inline-block;font-size:0.6rem;padding:1px 6px;border-radius:4px;background:rgba(6,182,212,0.12);color:#06B6D4;font-weight:600;';
        } else {
          b.style.display = '';
        }
      });
      // Add lock icon if not present
      if (!el.querySelector('.lock-icon')) {
        const lockIcon = document.createElement('span');
        lockIcon.className = 'material-icons lock-icon';
        lockIcon.textContent = 'lock';
        el.appendChild(lockIcon);
      }
    } else if (hiddenViews.includes(view)) {
      el.classList.add('hidden');
    }
  });

  // Make compliance the default landing view
  const complianceNav = document.querySelector('#main-nav [data-view="compliance"]');
  if (complianceNav) {
    // Move compliance to top of nav
    const nav = complianceNav.parentNode;
    nav.insertBefore(complianceNav, nav.firstElementChild);
  }

  // Show compliance partner branding in sidebar if present
  const partner = (window.AppState?.tenantData?.compliancePartner || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  if (partner && !document.getElementById('compliance-partner-badge')) {
    const badge = document.createElement('div');
    badge.id = 'compliance-partner-badge';
    badge.style.cssText = `
      padding: 10px 16px; margin: 8px 12px; border-radius: 8px;
      background: rgba(6, 182, 212, 0.08); border: 1px solid rgba(6, 182, 212, 0.2);
      text-align: center; font-size: 0.7rem; color: #06B6D4;
      font-weight: 600; letter-spacing: 0.03em;
    `;
    badge.innerHTML = `<span class="material-icons" style="font-size: 14px; vertical-align: -2px; margin-right: 4px;">verified</span>Managed by ${partner}`;
    const sidebar = document.getElementById('app-sidebar');
    if (sidebar) {
      const nav = sidebar.querySelector('#main-nav');
      if (nav) nav.parentNode.insertBefore(badge, nav);
    }
  }

  // Hide Settings gear for compliance tenants (managed by advisor)
  const settingsBtn = document.getElementById('btn-settings');
  if (settingsBtn) settingsBtn.style.display = 'none';
  const mobileSettingsBtn = document.getElementById('mobile-settings-btn');
  if (mobileSettingsBtn) mobileSettingsBtn.style.display = 'none';
  // Account nav follows Settings here (plan/billing is advisor-managed).
  setAccountNavVisible(false);
}

// TASK-4769 (2026-10-12) — THE ONE SEMANTICS TABLE for subscription status.
// Server twin: SUBSCRIPTION_ACCESS_STATUSES in functions/src/index.js, beside
// _subscriptionStatusWrite(). Client and server cannot share a module (Pitfall
// #1 / functions packaging), so this exists exactly twice and
// tests/task4769-stripe-status-access-path.unit.cjs diffs the two. Change BOTH
// or neither.
//   subscribed — a paid (or founder-stamped) plan.
//   dunning    — card failing (past_due / unpaid). ACCESS IS KEPT: Chris
//                2026-10-01 (TASK-9543, "do what you recommend") — the app
//                treats these as live, Twilio is suspended by the webhook and
//                the owner sees the payment note. Before this table the access
//                gate knew only 'active'/'founder', so a declined card dropped a
//                carrier into the trial math and, past their trial dates, into
//                the compliance-only auto-downgrade mid-shift.
//   cancelled  — Stripe's 'canceled' is normalised to this by the resolver.
const SUBSCRIPTION_ACCESS_STATUSES = Object.freeze({
  subscribed: Object.freeze(['active', 'founder']),
  dunning: Object.freeze(['past_due', 'unpaid']),
  cancelled: Object.freeze(['cancelled']),
});

function isFounderAccount() {
  return FOUNDER_TENANTS.includes(window.AppState?.tenant);
}

function isVipAccount() {
  // TASK-531 — comped tenants (compliance-partner own sandboxes, VIP comps) are
  // VIP for billing/access purposes. The ~15 call sites + the billing gate
  // (getTrialStatus) rely on this, so reading comped here is what actually
  // exempts a comped tenant from the $250/mo gate client-side.
  if (window.AppState?.tenantData?.comped === true) return true;
  return VIP_TENANTS.includes(window.AppState?.tenant);
}

// TASK-9432 — ONE answer for "which plan-billing buttons does this viewer get",
// shared by the Settings billing row and the account view so they cannot drift.
// Pure (explicit args, no AppState reads) so the unit test can execute it.
// Mirrors the server: createCheckoutSession's _baseCheckoutRefusal (owner only,
// never comped, never already subscribed) and createBillingPortalSession
// (owner only, needs a Stripe customer — founder and VIP included, because a
// comped PLAN can still have cards and invoices on file). iOS gets neither
// (Apple 3.1.1), matching startCheckout's isIOSApp guard.
function getPlanBillingControls(td, { tenantId, uid, isIOS = false, isDemo = false } = {}) {
  const t = td || {};
  const isOwner = !!(t.ownerId && uid && t.ownerId === uid);
  // TASK-4769 — 'active' plus the dunning set from the ONE table, so "already
  // subscribed" here agrees with the access gate about a card-failing owner.
  const liveStatuses = ['active', ...SUBSCRIPTION_ACCESS_STATUSES.dunning];
  const isComped = FOUNDER_TENANTS.includes(tenantId) || VIP_TENANTS.includes(tenantId)
    || t.comped === true || t.vipAccess === true || t.isVip === true
    || t.subscriptionStatus === 'comped' || t.subscription?.status === 'comped';
  // hasBasePlan is never cleared on cancellation (the webhook writes
  // 'cancelled' but leaves it) — same carve-out as the server's
  // _baseCheckoutDocSaysCancelled, or a lapsed owner could never re-subscribe.
  const _cx = (v) => v === 'cancelled' || v === 'canceled';
  const docCancelled = _cx(t.subscriptionStatus) || _cx(t.subscription?.status) || _cx(t.plan);
  const isSubscribed = (t.hasBasePlan === true && !docCancelled)
    || liveStatuses.includes(t.subscriptionStatus) || liveStatuses.includes(t.subscription?.status);
  const blocked = isIOS || isDemo || !tenantId || tenantId === 'demo' || !isOwner;
  // TASK-1659 — the compliance partner pays this subscription and its payment
  // is in good standing: no Upgrade (the server refuses it too —
  // _baseCheckoutRefusal 'partner_pays'), and the plan card says who pays.
  // During a lapse the carrier gets Upgrade back: that is its 30-day path.
  const cov = partnerCoverageState(t, Date.now());
  const partnerPays = cov.kind === 'covered';
  // Chris 2026-10-21 — the partner takes over when this carrier's own plan
  // period ends (the carrier still pays until then). Shown on the plan card.
  const ps = t.partnerSeat || {};
  const switchAtMs = Number(ps.switchAtMs) || 0;
  const partnerSwitch = (ps.status === 'scheduled' && switchAtMs > Date.now()
    && String(t.complianceManagerUid || '').trim() === ps.partnerUid)
    ? { partnerName: cov.partnerName, atMs: switchAtMs } : null;
  return {
    isOwner,
    isComped,
    isSubscribed,
    partnerPays,
    partnerName: partnerPays ? cov.partnerName : null,
    partnerSwitch,
    showUpgrade: !blocked && !isComped && !isSubscribed && !partnerPays,
    // TASK-1361b — the Stripe customer id moved to the server-only
    // tenants/{tid}/secrets/billing doc; the tenant root carries only the
    // `hasStripeCustomer` boolean the server stamps beside it. The id itself is
    // still honoured until the backfill has emptied every tenant root of it.
    showManageBilling: !blocked && (t.hasStripeCustomer === true || !!t.stripeCustomerId),
  };
}
window.getPlanBillingControls = getPlanBillingControls;

// =============================================================================
// TASK-1659 — the compliance partner pays this carrier's subscription.
// =============================================================================
// Byte-for-byte twin of coverageState() in functions/src/services/partnerSeats.js
// (tests/task1659-partner-pays.unit.cjs runs both over one matrix). Pure.
//   covered — the partner pays and its payment is fine
//   grace   — the partner stopped paying (or its card failed): the carrier
//             keeps everything and has until graceEndsAtMs to add its own card
//   none    — nothing to say
function partnerCoverageState(td, nowMs) {
  const t = td || {};
  const s = t.partnerSeat || {};
  const _ms = (v) => {
    if (v == null) return null;
    if (typeof v === 'number') return Number.isFinite(v) ? v : null;
    if (typeof v.toMillis === 'function') return v.toMillis();
    if (typeof v.seconds === 'number') return v.seconds * 1000;
    const x = new Date(v).getTime();
    return Number.isFinite(x) ? x : null;
  };
  const DAY = 24 * 60 * 60 * 1000;
  const mgr = String(t.complianceManagerUid || '').trim();
  const active = t.subscriptionPayer === 'partner' && s.status === 'active' && !!mgr && s.partnerUid === mgr;
  const partnerName = String(t.compliancePartner || '').trim() || 'Your compliance partner';
  const graceEndsAtMs = _ms(s.graceEndsAtMs);
  const daysLeft = graceEndsAtMs ? Math.max(0, Math.ceil((graceEndsAtMs - nowMs) / DAY)) : null;
  if (active && !graceEndsAtMs) return { kind: 'covered', partnerName, graceEndsAtMs: null, daysLeft: null, reason: null };
  if (active && graceEndsAtMs) return { kind: 'grace', partnerName, graceEndsAtMs, daysLeft, reason: s.graceReason || 'partner_payment_failed' };
  if (s.status === 'ended' && graceEndsAtMs && graceEndsAtMs > nowMs && t.subscriptionPayer !== 'partner') {
    const st = String((t.subscription && t.subscription.status) || t.subscriptionStatus || '').toLowerCase();
    if (st === 'active' || st === 'past_due' || st === 'unpaid') return { kind: 'none', partnerName, graceEndsAtMs: null, daysLeft: null, reason: null };
    return { kind: 'grace', partnerName, graceEndsAtMs, daysLeft, reason: s.endReason || 'partner_opted_out' };
  }
  return { kind: 'none', partnerName, graceEndsAtMs: null, daysLeft: null, reason: null };
}
window.partnerCoverageState = partnerCoverageState;

// The carrier OWNER's notice while its 30-day window runs. Owner only: the
// owner is the one person who can start the subscription (createCheckoutSession
// is owner-only). Copy says plainly that nothing is lost. Pure.
function partnerGraceBannerState(td, { tenantId, uid, nowMs } = {}) {
  const t = td || {};
  const cov = partnerCoverageState(t, nowMs);
  if (cov.kind !== 'grace') return null;
  if (!getPlanBillingControls(t, { tenantId, uid }).isOwner) return null;
  const date = new Date(cov.graceEndsAtMs).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const lead = cov.reason === 'partner_payment_failed'
    ? `${cov.partnerName}'s payment for your DispatchDeck subscription didn't go through.`
    : cov.reason === 'partner_switch_failed'
      ? `Your own plan ended and ${cov.partnerName} was set to take over your DispatchDeck subscription, but its payment didn't go through.`
      : `${cov.partnerName} is no longer paying for your DispatchDeck subscription.`;
  return {
    daysLeft: cov.daysLeft,
    text: `${lead} Your account and all your records stay as they are. Add your own card by ${date} to keep full access ($250/mo).`,
  };
}

function showPartnerGraceBanner(state) {
  if (!state || document.getElementById('partner-grace-banner')) return;
  if (typeof isPartnerClientSession === 'function' && isPartnerClientSession()) return;
  // One bar at a time: this one replaces the generic "N days left in your
  // free trial" bar, which would be wrong here (it is not a free trial).
  document.getElementById('trial-reminder-banner')?.remove();
  const d = Number(state.daysLeft) || 0;
  const banner = document.createElement('div');
  banner.id = 'partner-grace-banner';
  banner.className = `trial-banner ${d <= 7 ? 'trial-urgent' : ''}`;
  banner.setAttribute('role', 'alert');
  const safe = (typeof escapeHtml === 'function') ? escapeHtml(state.text) : String(state.text).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  banner.innerHTML = `
    <div class="trial-banner-content">
      <span class="material-icons">credit_card</span>
      <span>${safe}</span>
      <button id="btn-partner-grace-subscribe" class="btn btn-sm btn-primary" onclick="startSubscription(this)">Add card</button>
      <button class="btn-icon" aria-label="Hide for now" onclick="dismissPartnerGraceBanner()">
        <span class="material-icons">close</span>
      </button>
    </div>
  `;
  document.body.appendChild(banner);
  document.body.classList.add('has-trial-banner');
}

// Session-only: it comes back on the next load until a card is on file.
function dismissPartnerGraceBanner() {
  const banner = document.getElementById('partner-grace-banner');
  if (banner) {
    banner.remove();
    document.body.classList.remove('has-trial-banner');
  }
}
window.dismissPartnerGraceBanner = dismissPartnerGraceBanner;

// =============================================================================
// TASK-9812 — Monthly / Annual billing interval for the Self-Serve plan.
// =============================================================================
// ONE piece of state (which interval the owner picked) and ONE renderer that
// every upgrade surface calls (feature-upgrade modal, trial-expired wall). The
// surfaces carry `data-plan-interval-ui` containers; syncPlanIntervalUi fills
// them from PLAN_PRICING so no price is hand-typed in HTML.
// Annual is OFFERED only when the launch switch is on AND the tenant is not
// partner-attributed (same two fields the server refuses on — the server is
// the boundary; this is cosmetic). Hidden, not disabled, for partner tenants.
let _billingInterval = 'month';

// Pure twin of the server's annualOfferedTo(): explicit args so the unit test runs it.
function annualOfferedTo(td, pricing = PLAN_PRICING) {
  const t = td || {};
  if (!pricing || pricing.annualEnabled !== true) return false;
  const managerUid = String(t.complianceManagerUid || '').trim();
  const partnerName = String(t.compliancePartner || '').trim();
  return !managerUid && !partnerName;
}
window.annualOfferedTo = annualOfferedTo;

// Which base plan does a Subscribe click send? Pure: (interval, offered) -> plan.
function basePlanFor(interval, offered) {
  return (offered && interval === 'year') ? 'base_annual' : 'base';
}
window.basePlanFor = basePlanFor;

function _annualOfferedNow() {
  return annualOfferedTo(window.AppState?.tenantData || null);
}

function selectedBasePlan() {
  return basePlanFor(_billingInterval, _annualOfferedNow());
}

function setBillingInterval(interval) {
  _billingInterval = interval === 'year' ? 'year' : 'month';
  syncPlanIntervalUi();
}
window.setBillingInterval = setBillingInterval;

// Display strings for one interval. Pure — the unit test checks the math.
function planIntervalDisplay(interval, pricing = PLAN_PRICING) {
  const annual = interval === 'year';
  return {
    amount: annual ? '$' + pricing.annualDollars.toLocaleString('en-US') : '$' + pricing.monthlyDollars.toLocaleString('en-US'),
    period: annual ? '/yr' : '/mo',
    label: annual ? pricing.annualLabel : pricing.monthlyLabel,
    cta: annual ? `Subscribe — ${pricing.annualLabel}` : `Subscribe — ${pricing.monthlyLabel}`,
    savings: annual ? pricing.annualSavingsLabel : '',
    finePrint: annual ? pricing.annualFinePrint : pricing.monthlyFinePrint,
  };
}
window.planIntervalDisplay = planIntervalDisplay;

// Fill every `[data-plan-interval-ui]` container on the page.
function syncPlanIntervalUi() {
  const offered = _annualOfferedNow();
  if (!offered) _billingInterval = 'month';
  const d = planIntervalDisplay(_billingInterval);
  document.querySelectorAll('[data-plan-interval-ui]').forEach((root) => {
    const amount = root.querySelector('[data-pi-amount]');
    const period = root.querySelector('[data-pi-period]');
    const savings = root.querySelector('[data-pi-savings]');
    const fine = root.querySelector('[data-pi-fine]');
    const toggle = root.querySelector('[data-pi-toggle]');
    const cta = root.querySelector('[data-pi-cta]');
    if (amount) amount.textContent = d.amount;
    if (period) period.textContent = d.period;
    if (savings) { savings.textContent = d.savings ? `— ${d.savings}` : ''; savings.style.display = d.savings ? '' : 'none'; }
    if (fine) fine.textContent = d.finePrint;
    if (cta) cta.textContent = d.cta;
    if (toggle) {
      toggle.style.display = offered ? '' : 'none';
      toggle.querySelectorAll('[data-pi-toggle-savings]').forEach((el) => { el.textContent = PLAN_PRICING.annualSavingsLabel; });
      toggle.querySelectorAll('[data-pi-interval]').forEach((btn) => {
        const on = btn.dataset.piInterval === _billingInterval;
        btn.classList.toggle('plan-interval-on', on);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }
  });
}
window.syncPlanIntervalUi = syncPlanIntervalUi;

// Signup step 1 shows "or $2,500/yr — save $500" once annual is live (no
// tenant yet, so only the launch switch gates it). Runs once at load.
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-pi-annual-hint]').forEach((el) => {
    if (PLAN_PRICING.annualEnabled === true) {
      el.textContent = `or ${PLAN_PRICING.annualLabel} — ${PLAN_PRICING.annualSavingsLabel}`;
      el.hidden = false;
    }
  });
});

// TASK-4665-S3 — ONE answer for "may this viewer buy an SMS pack, and is it a
// top-up". Shared by the Account view buttons, the SMS meter CTAs and the SMS
// upgrade modal so they cannot drift. Pure (explicit args) so the unit test runs it.
// Mirrors the server's _smsPackCheckoutRefusal: OWNER only (tenants.ownerId),
// never demo / show-account (server NON_BILLABLE_TENANTS). Founder, VIP, comped,
// trial and has-pack owners all may buy — a pack is usage, not the plan, and a
// repeat purchase is a top-up. iOS never (Apple 3.1.1; startCheckout refuses too).
const SMS_PACK_NON_BILLABLE_TENANTS = ['demo', 'show-account'];
function getSmsPackPurchaseControl(td, { tenantId, uid, isIOS = false, isDemo = false } = {}) {
  const t = td || {};
  const tw = t.twilio || {};
  const isOwner = !!(t.ownerId && uid && t.ownerId === uid);
  if (isIOS || isDemo || !tenantId || SMS_PACK_NON_BILLABLE_TENANTS.includes(tenantId) || !isOwner) {
    return { show: false, isTopUp: false, isOwner, label: '' };
  }
  // A suspended account can't text; the server refuses the checkout too.
  if (tw.status === 'suspended' || tw.status === 'abuse_suspended') {
    return { show: false, isTopUp: false, isOwner, label: '' };
  }
  const isPromo = tw.isPromo === true;
  // Already holding real credit (a bought pack, or a comped/VIP balance) → top-up.
  const isTopUp = !isPromo && (t.hasSmsPack === true || Number(tw.creditBalance) > 0);
  const label = isTopUp
    ? 'Top up — $25 (1,500 more SMS Messages)'
    : isPromo ? 'Upgrade — $25 Pack (1,500 SMS Messages)' : 'Get $25 Pack (1,500 SMS Messages)';
  return { show: true, isTopUp, isOwner, label };
}
window.getSmsPackPurchaseControl = getSmsPackPurchaseControl;
window.SMS_PACK_NON_BILLABLE_TENANTS = SMS_PACK_NON_BILLABLE_TENANTS; // TASK-9816 — account.js auto-recharge card
// The signed-in viewer's answer (AppState). Fail closed on missing state.
function _smsPackControlForViewer() {
  return getSmsPackPurchaseControl(window.AppState?.tenantData, {
    tenantId: window.AppState?.tenant, uid: window.AppState?.user?.uid,
    isIOS: !!window.isIOSApp, isDemo: !!window.AppState?.isDemo,
  });
}

// ============================================
// TASK-9433 — +50 vehicles for $25/mo (per block over the included 50)
// ============================================
// Mirrors functions/src/index.js VEHICLES_INCLUDED / VEHICLE_BLOCK_SIZE /
// VEHICLE_BLOCK_MAX — tests/task9433-vehicle-blocks.unit.cjs fails on drift.
// The block count itself is server-owned (tenant.vehicleAddon, written only by
// setVehicleBlocks + the Stripe webhook; firestore.rules denies client writes).
// The cap is SOFT: going over warns, it never blocks a save.
const VEHICLE_ALLOWANCE = Object.freeze({ included: 50, blockSize: 50, maxBlocks: 20, blockPrice: 25 });

// Pure. How many active vehicles may this tenant run? Comped (founder / VIP /
// comped flags — the ONE list in getPlanBillingControls) and demo are unlimited.
function getVehicleAllowance(td, { tenantId } = {}) {
  const t = td || {};
  const isComped = getPlanBillingControls(t, { tenantId }).isComped === true;
  const raw = Number(t.vehicleAddon?.blocks);
  // NOT clamped to maxBlocks (COD r2): a quantity set above the cap in Stripe is
  // what the owner pays for, and Remove must send that exact count.
  const blocks = Number.isInteger(raw) && raw > 0 ? raw : 0;
  return {
    unlimited: isComped || !tenantId || tenantId === 'demo',
    included: VEHICLE_ALLOWANCE.included,
    blocks,
    limit: VEHICLE_ALLOWANCE.included + blocks * VEHICLE_ALLOWANCE.blockSize,
  };
}
window.getVehicleAllowance = getVehicleAllowance;

// Pure. May this viewer add / remove blocks? Mirrors the server's
// _vehicleBlockPurchaseRefusal: OWNER only, never comped, never demo /
// show-account, needs an ACTIVE paid subscription (a trial has nothing to put
// the block on). iOS never (Apple 3.1.1). `note` explains a hidden button to the
// owner. Cosmetic — setVehicleBlocks is the boundary.
function getVehicleBlockControl(td, { tenantId, uid, isIOS = false, isDemo = false } = {}) {
  const t = td || {};
  const isOwner = !!(t.ownerId && uid && t.ownerId === uid);
  const allowance = getVehicleAllowance(t, { tenantId });
  const off = (note) => ({ canAdd: false, canRemove: false, isOwner, note });
  if (isIOS || isDemo || !tenantId || SMS_PACK_NON_BILLABLE_TENANTS.includes(tenantId) || !isOwner) return off('');
  if (allowance.unlimited) return off('');
  const st = t.subscription?.status || t.subscriptionStatus || null;
  if (st === 'past_due' || st === 'unpaid') return off('Update your card in Manage Billing to change vehicle blocks.');
  if (st !== 'active') return off('Subscribe first — vehicle blocks go on your monthly plan.');
  // TASK-9433 x TASK-9812 — the block price is monthly; setVehicleBlocks refuses
  // an add on a yearly plan (Stripe can't mix intervals). Removal stays possible.
  if (t.subscription?.billingInterval === 'year') {
    return {
      canAdd: false,
      canRemove: allowance.blocks > 0,
      isOwner,
      note: "Vehicle blocks are billed monthly and can't be added to an annual plan yet. Email support@dispatchdeck.app and we'll set it up.",
    };
  }
  return {
    canAdd: allowance.blocks < VEHICLE_ALLOWANCE.maxBlocks,
    canRemove: allowance.blocks > 0,
    isOwner,
    note: allowance.blocks >= VEHICLE_ALLOWANCE.maxBlocks ? 'Need more than this? Contact support@dispatchdeck.app.' : '',
  };
}
window.getVehicleBlockControl = getVehicleBlockControl;

// Active fleet = trucks + trailers not switched off (a doc with no `active`
// field is active — saveTruck/saveTrailer default it true). Returns null when
// it can't count (never a guessed 0).
async function countActiveVehicles(tenantId) {
  if (!tenantId) return null;
  try {
    const [trucks, trailers] = await Promise.all([
      getDocs(collection(db, 'tenants', tenantId, 'trucks')),
      getDocs(collection(db, 'tenants', tenantId, 'trailers')),
    ]);
    let n = 0;
    trucks.forEach((d) => { if (d.data()?.active !== false) n++; });
    trailers.forEach((d) => { if (d.data()?.active !== false) n++; });
    return n;
  } catch (e) {
    console.warn('[vehicle-blocks] could not count vehicles', e?.message);
    return null;
  }
}
window.countActiveVehicles = countActiveVehicles;

// Owner clicks +/- on the Account page. `delta` is +1 or -1. The server takes
// the ABSOLUTE target plus the count this screen showed (expectedBlocks) and
// refuses if Stripe holds anything else, so a stale screen or a second tab
// can't cut paid blocks or add a duplicate item.
let _vehicleBlocksBusy = false;
async function changeVehicleBlocks(delta) {
  if (_vehicleBlocksBusy) return;
  _vehicleBlocksBusy = true; // held through the confirm too — no second dialog/call
  try { await _changeVehicleBlocks(delta); } finally { _vehicleBlocksBusy = false; }
}
async function _changeVehicleBlocks(delta) {
  if (window.isIOSApp) { showToast('Manage your plan at dispatchdeck.app', 'info'); return; }
  const tenantId = window.AppState?.tenant;
  const td = window.AppState?.tenantData || {};
  const ctl = getVehicleBlockControl(td, {
    tenantId, uid: window.AppState?.user?.uid, isIOS: !!window.isIOSApp, isDemo: !!window.AppState?.isDemo,
  });
  const cur = getVehicleAllowance(td, { tenantId });
  const adding = delta > 0;
  if ((adding && !ctl.canAdd) || (!adding && !ctl.canRemove)) {
    if (ctl.note) showToast(ctl.note, 'info');
    return;
  }
  const target = cur.blocks + (adding ? 1 : -1);
  const newLimit = VEHICLE_ALLOWANCE.included + target * VEHICLE_ALLOWANCE.blockSize;
  const ok = await showConfirm(
    adding ? `Add 50 vehicles for $${VEHICLE_ALLOWANCE.blockPrice}/mo?` : 'Remove a 50-vehicle block?',
    adding
      ? `Your plan will cover ${newLimit} vehicles and go up $${VEHICLE_ALLOWANCE.blockPrice}/mo. The rest of this billing period is prorated onto your next invoice.`
      : `Your plan will cover ${newLimit} vehicles and go down $${VEHICLE_ALLOWANCE.blockPrice}/mo. The unused part of this period is credited on your next invoice.`,
    { confirmText: adding ? `Add — $${VEHICLE_ALLOWANCE.blockPrice}/mo` : 'Remove block', icon: 'local_shipping' }
  );
  if (!ok) return;
  try {
    const fn = httpsCallable(functions, 'setVehicleBlocks');
    const res = await fn({ tenantId, blocks: target, expectedBlocks: cur.blocks });
    const blocks = Number(res?.data?.blocks);
    // The viewer may have switched tenants while Stripe answered — never write
    // this tenant's count into another tenant's state.
    if (window.AppState?.tenant !== tenantId) return;
    if (window.AppState?.tenantData && Number.isInteger(blocks) && res?.data?.mirrored !== false) {
      window.AppState.tenantData.vehicleAddon = {
        ...(window.AppState.tenantData.vehicleAddon || {}),
        blocks, vehicleLimit: res.data.vehicleLimit,
      };
    }
    showToast(`Your plan now covers ${res?.data?.vehicleLimit ?? newLimit} vehicles.`, 'success');
    if (window.AppState?.currentView === 'account') initAccount(DOM.mainContent, tenantId);
  } catch (e) {
    console.error('[vehicle-blocks] change failed', e);
    showToast(e?.message || 'Could not change vehicle blocks. Please try again.', 'error', null, 8000);
    // COD r3 (UX) — a stale screen: the server just re-synced the count from
    // Stripe, so re-read it and redraw instead of asking for a page reload.
    if (String(e?.code || '').endsWith('aborted') && window.AppState?.tenant === tenantId) {
      try {
        const snap = await getDoc(doc(db, 'tenants', tenantId));
        if (window.AppState?.tenant === tenantId && window.AppState?.tenantData && snap.exists()) {
          window.AppState.tenantData.vehicleAddon = snap.data()?.vehicleAddon || null;
          if (window.AppState?.currentView === 'account') initAccount(DOM.mainContent, tenantId);
        }
      } catch (_) { /* the toast already told them to refresh */ }
    }
  }
}
window.changeVehicleBlocks = changeVehicleBlocks;

// Soft cap. Called by roster after a vehicle is added or switched back on.
// `activeCount` = active vehicles INCLUDING the one just saved. Warns once per
// crossing; never blocks the save.
function noteVehicleSoftCap(activeCount) {
  const tenantId = window.AppState?.tenant;
  const td = window.AppState?.tenantData || {};
  const a = getVehicleAllowance(td, { tenantId });
  if (a.unlimited || !Number.isFinite(activeCount) || activeCount <= a.limit) return false;
  const isOwner = !!(td.ownerId && td.ownerId === window.AppState?.user?.uid);
  const isAnnual = td.subscription?.billingInterval === 'year'; // TASK-9812 — blocks are monthly-only
  const msg = isOwner && isAnnual
    ? `You have ${activeCount} active vehicles and your plan covers ${a.limit}. Email support@dispatchdeck.app to add a 50-vehicle block to your annual plan.`
    : isOwner
    ? `You have ${activeCount} active vehicles and your plan covers ${a.limit}. Add 50 more for $${VEHICLE_ALLOWANCE.blockPrice}/mo under Account → Vehicles.`
    : `You have ${activeCount} active vehicles and your plan covers ${a.limit}. Ask the account owner to add a 50-vehicle block.`;
  // COD r2 (UX) — owners get a one-tap way to the Vehicles section.
  const action = isOwner ? { label: 'Open Account', callback: () => { location.hash = '#/account'; } } : null;
  showToast(msg, 'warning', action, 9000);
  return true;
}
window.noteVehicleSoftCap = noteVehicleSoftCap;

// TASK-531 — partner "Switch to DispatchDeck" surfaces + Partner Sandbox badge.
//   (a) Reveal the "Switch to Advisor Portal" links (sidebar + mobile-more) when
//       the signed-in uid also has a compliance-partner identity.
//   (b) Show a subtle "Partner Sandbox" banner when the current tenant IS the
//       partner's own comped sandbox, so they never confuse it with a client
//       tenant they were later invited into.
function applyPartnerSandboxUi() {
  try {
    const isPartner = window.AppState?.isCompliancePartner === true;
    const inSandbox = window.AppState?.tenantData?.partnerSandbox === true;

    // (a) Switcher links
    const sideBtn = document.getElementById('btn-switch-advisor');
    if (sideBtn) sideBtn.classList.toggle('hidden', !isPartner);
    const mobBtn = document.getElementById('mobile-switch-advisor-btn');
    if (mobBtn) mobBtn.classList.toggle('hidden', !isPartner);

    // (b) Sandbox banner — create once, idempotent.
    let banner = document.getElementById('partner-sandbox-banner');
    if (inSandbox) {
      if (!banner) {
        banner = document.createElement('div');
        banner.id = 'partner-sandbox-banner';
        banner.style.cssText = 'background:#0F172A;color:#fff;font-size:0.78rem;font-weight:600;letter-spacing:0.02em;text-align:center;padding:5px 12px;display:flex;align-items:center;justify-content:center;gap:6px;';
        banner.innerHTML = '<span class="material-icons" style="font-size:15px;vertical-align:middle;">science</span> Partner Sandbox — your own demo carrier (not a client account)';
        const mc = document.getElementById('main-content') || document.body;
        mc.insertBefore(banner, mc.firstChild);
      }
      banner.style.display = 'flex';
    } else if (banner) {
      banner.style.display = 'none';
    }
  } catch (e) {
    console.warn('[TASK-531] applyPartnerSandboxUi failed (non-fatal):', e.message);
  }
}
window.applyPartnerSandboxUi = applyPartnerSandboxUi;

// ===================================================================
// TASK-1658 — a compliance PARTNER working inside a managed carrier's
// real DispatchDeck app: dispatchdeck.app/?client=<tenantId>.
//
// 🔴 WHY THE TENANT LIVES IN THE URL AND NOWHERE ELSE.
// Firebase Auth is ONE session per ORIGIN, broadcast to every tab
// ([[lesson_firebase_auth_is_shared_across_tabs]]). If the selected carrier
// lived in localStorage / IndexedDB / a module global, opening carrier B in a
// second tab would silently repoint tab A — header still says Acme, data is
// now Beta. For a partner running nine carriers that is an MVR pulled on the
// wrong driver at the wrong carrier: a real FCRA event on someone who was
// never meant to be screened, with nothing to notice it by. The URL is the
// ONLY carrier selector, so two tabs are independent by construction.
// DO NOT move this into shared storage.
//
// 🔴 THE PARAM IS A SELECTOR, NOT AN AUTHORIZATION. Everything below is a
// client-side convenience: it decides which tenant to ASK for. firestore.rules
// re-verifies isComplianceManager on every single read, so a hand-typed
// foreign tenantId 403s at the data layer even if this code were bypassed.
// The membership check here exists so the human gets a clear refusal instead
// of an app full of permission errors — never as the security boundary.
//
// ⚠️ This deliberately reverses TASK-531 (partners get their own comped
// sandbox, never client-data impersonation). Chris un-locked it 2026-08-19.
// The distinction he is relying on: this is the partner's OWN uid with
// partner attribution — NOT a session minted as the carrier owner and NOT a
// shared credential. Audit rows still carry the partner's uid.
// ===================================================================

const PARTNER_CLIENT_PARAM = 'client';

// The partner's role string on the synthetic member-equivalent. Deliberately
// NOT 'owner' and NOT 'admin': both of those short-circuit checkModuleAccess to
// "everything allowed" and flip hrEffectiveAdmin() on, which would hand an
// external firm the carrier's admin UI (Settings, Team Access, SSN edit) —
// exactly the escalation the rules-layer deny list exists to prevent.
const PARTNER_MEMBER_ROLE = 'compliancePartner';

// Read the requested carrier off the URL. Shape-validated only (a Firestore
// doc id); authorization happens in resolvePartnerClientContext().
//
// Reads ORIGINAL_URL_PARAMS (~:213) — the snapshot taken before ANY code can
// touch window.location — not the live search string. Several boot paths call
// `replaceState(…, window.location.pathname)` and drop the whole query (the
// ?demo= and ?checkout= handlers do exactly that), and the version-checker
// reloads the page. Reading live search would mean a stripped param resolves to
// nothing and the partner silently lands in their OWN sandbox — the wrong-carrier
// failure this whole design exists to prevent. Same idiom the ?access= /
// ?timeoff= public tokens already use. Path-agnostic, so /?client= and
// /index.html?client= (the shape the portal button emits) both work.
function getRequestedPartnerClientId() {
  try {
    const raw = (typeof ORIGINAL_URL_PARAMS !== 'undefined' && ORIGINAL_URL_PARAMS.get(PARTNER_CLIENT_PARAM))
      || new URLSearchParams(window.location.search).get(PARTNER_CLIENT_PARAM);
    if (!raw) return null;
    const id = String(raw).trim();
    // Firestore doc-id shape. Rejects '..', '/', and anything that could be
    // path-injected into doc(db, 'tenants', id).
    if (!id || id.length > 1500 || !/^[A-Za-z0-9_-]+$/.test(id)) {
      console.warn('[Partner] ?client= is not a valid tenant id — ignoring');
      return null;
    }
    return id;
  } catch (_) { return null; }
}

// True while this TAB is a partner sitting inside a managed carrier's account.
// Read from AppState (per-tab, per-page-load, seeded from the URL) — never from
// any store that another tab could write.
function isPartnerClientSession() {
  return !!(window.AppState && window.AppState.partnerClientContext);
}
window.isPartnerClientSession = isPartnerClientSession;

// TASK-9422 — the carrier's Compliance/HR "edit details" forms write EVERY field
// on Save, null-on-empty. For a carrier member that is harmless; for a managing
// partner, firestore.rules' people-update arm is an allowlist on CHANGED keys,
// and absent→null counts as a change — so fixing a CDL expiry on a driver with
// no DOB on file 403'd on `dob`, a field the partner never touched and is
// deliberately not allowed to edit. In a partner session only, drop every key
// whose value is unchanged (undefined / '' / null are the same "empty"; an
// explicit boolean is always written). A real change to a non-allowlisted key still
// reaches the rules and is still refused — this narrows the write, it grants
// nothing. Mutates and returns `update`.
function partnerDropUnchangedKeys(update, before) {
  if (!isPartnerClientSession() || !update || typeof update !== 'object') return update;
  const prev = before || {};
  const norm = (v) => (v === undefined || v === '' ? null : v);
  for (const k of Object.keys(update)) {
    const a = norm(prev[k]);
    const b = norm(update[k]);
    // NOTE: absent vs false is NOT "unchanged" (COD round 3): hr.js renders an
    // absent inRandomPool on a CDL driver as CHECKED, so an explicit `false`
    // is a real decision and must be written.
    const same = (a === b)
      || (a !== null && b !== null && typeof a === 'object' && typeof b === 'object'
          && JSON.stringify(a) === JSON.stringify(b));
    if (same) delete update[k];
  }
  return update;
}
window.partnerDropUnchangedKeys = partnerDropUnchangedKeys;

// Put ?client=<id> back in the address bar if something stripped it. The URL is
// the ONLY carrier selector, so it is also what a manual refresh re-reads — a
// stripped param plus an F5 drops the partner into their own sandbox while their
// muscle memory still says "I am in Acme". One-shot and idempotent (only ever
// called right after a context resolves), so there is no rewrite loop. Preserves
// pathname, so /index.html stays /index.html, and preserves every other param.
function _ensurePartnerClientParamInUrl(tenantId) {
  try {
    if (!tenantId) return;
    const url = new URL(window.location.href);
    if (url.searchParams.get(PARTNER_CLIENT_PARAM) === tenantId) return;
    url.searchParams.set(PARTNER_CLIENT_PARAM, tenantId);
    window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);
  } catch (_) { /* non-fatal — the resolved context is already in AppState */ }
}

// Modules the synthetic member-equivalent grants. DERIVED FROM THE PARTNER ROLE
// — no tenant id, no person, no email appears anywhere in this function.
//
// The base set is the DOT record surfaces a compliance partner's job owns:
//   compliance   §391 DQ files, MVRs, Clearinghouse, drug & alcohol program
//   roster       the drivers those files belong to
//   maintenance  §396 vehicle maintenance + annual inspection files
//   pretrip/dvir §396.11 driver vehicle inspection reports
// Everything NOT listed (dispatch, billing, clock, crm, bi, ap, workforce,
// jobboard) is money / operations the partner has no role in and whose write
// callables are owner/admin-gated server-side — granting them would produce the
// "reads fine, 403s on save" experience that reads as a broken product.
function derivePartnerModuleScope() {
  // FULL CATALOG (Chris, 2026-08-19): "make it so all the modules are available,
  // for now — since TITS is his only client right now let's give him full access
  // so he can play." Widened from the original compliance-work-only scope
  // (compliance/roster/maintenance/pretrip/dvir/hr/hr-directory) to everything a
  // tenant admin holds, plus the two inspection keys that are partner-only.
  //
  // 🔴 THIS IS A UI SCOPE, NOT A GRANT. Read the standing invariant below before
  // assuming this hands a partner anything: there is no members/{uid} doc, so
  // firestore.rules hasModule() cannot see this list and it confers NOTHING
  // server-side. Widening it changes which nav entries render, not what the
  // server will accept. Anything the rules layer does not open to
  // isComplianceManager()/isPartnerElevated() still 403s on save — the same
  // known wart already documented for the SSN edit affordance. That is the
  // accepted trade for "let him play"; it is not a security change.
  //
  // Derived from the largest real module set in use (a tenant admin holds 13;
  // the admin preset is 15 since TASK-4710 added 'projects' and TASK-9619
  // 'notes'), unioned with the partner-only inspection keys. 'projects' and
  // 'notes' are deliberately omitted — see below.
  // 🔴 'notes' (TASK-9619): a partner has no members doc, so firestore.rules
  //    notesCanUse() and the manageNote gate refuse them regardless; notes are
  //    private between carrier staff, and a partner must never be mentionable.
  return [
    'compliance', 'roster', 'maintenance', 'pretrip', 'dvir',
    // Added for full-catalog access. Mirrors the 'admin' preset's module list
    // (public/src/views/roster.js — STAFF_LOGIN role presets), which is the
    // source of truth for "everything a tenant admin holds". Mirrored rather
    // than imported: cross-importing a ?v=-busted module creates a second
    // instance (Pitfall #1). If that preset gains a module, add it here too.
    //
    // 🔴 TWO DELIBERATE OMISSIONS from that admin list:
    //  - 'jobboard' is PERSON-SCOPED, and buildPartnerMemberEquivalent() sets
    //    personId:null on purpose (a partner has no employee record at the
    //    carrier — TASK-1489 'personless'). computeMemberDefaultView() already
    //    refuses to land a personless member on Job Board; granting the module
    //    would hand it a null identity to resolve. Omitted, not forgotten.
    //  - 'projects' (TASK-4710 re-decided; the old "dead key" reasoning was
    //    WRONG — firestore.rules read it in the clockEntries read arm, a
    //    tenant-wide clock grant; that clause is now removed). Still omitted,
    //    for real reasons: the module's power is the per-project role in
    //    projects/{id}.access, which counts only via rules hasModule(), and a
    //    partner has NO members doc — so it would confer nothing server-side
    //    (partner project READ already comes from isComplianceManager). And
    //    Projects has no top-level route; it renders inside Dispatch, which a
    //    partner is not granted. Re-add when Projects gets its own route AND
    //    partners get a members-doc-backed grant.
    // 🔴 VERIFIED IN PRODUCTION, signed in as the real partner (2026-08-19).
    // 'dispatch', 'billing', 'crm' and 'bi' were granted here and every one of
    // them rendered EMPTY, because the collections behind them — tickets,
    // customers, orders, revenue/AP entries — are deliberately NOT open to a
    // partner (that is the carrier's commercial relationship, not compliance
    // surface). Observed console, as the partner:
    //   [Dispatch] Customers/Tickets subscription error: Missing or insufficient permissions
    //   [Billing]  Error loading customers / tickets
    //   [CRM]      Customer / Tasks / Item codes subscription error
    //   [BI]       APInvoices / CostEntries / Customers / RevenueEntries / bankAccounts
    // A nav entry that always opens a broken view is worse than no nav entry —
    // it reads as "DispatchDeck is broken" rather than "this isn't shared".
    // Removed until the underlying reads are opened; re-add them in the SAME
    // commit that opens the collections, never before.
    //
    // 'clock' and 'workforce' are kept: they render, and lose only customer
    // labels. '811-permits' is kept (permits IS open to a partner — READ only;
    // TASK-9805 opens the 811 window for it, read-only, via isMember811Only).
    'clock', 'workforce', '811-permits',
    // HR is the employment application and the post-hire employee file — the
    // partner's actual work — and it is the module key hrCanManageModule() reads.
    'hr',
    // 🔴 'hr-directory' IS LOAD-BEARING, not a nice-to-have. It is the ONLY key
    // hrCanViewSecureTax() accepts for a non-admin, and that gate is what stands
    // between the partner and `peopleTax` — the SSN. You cannot order an MVR or a
    // PSP report without the SSN, which is the entire reason this feature exists.
    // Omitting it fails closed in the most useless possible way: the partner gets
    // in, sees everything, and cannot do the one job they came to do.
    //
    // ⚠️ KNOWN WART, deliberately accepted: hrCanViewSecureTax() is a single
    // view+EDIT gate, while the rules layer opened `peopleTax` READ only (the
    // create/update/delete arm stays bare isTenantAdmin — that split is
    // deliberate, it is the §391.21(b)(2) SSN vault). So a partner will see an
    // SSN edit affordance whose save 403s. Enforcement is correct and the data is
    // safe; only the affordance is wrong. Splitting hrCanViewSecureTax into
    // view-vs-edit is the real fix and belongs with whoever owns hr.js.
    'hr-directory',
  ];
}

// NOTE — the scope above is now the FULL catalog, not compliance-only. The
// paragraph below predates that and is kept because its REASONING still holds
// verbatim for 'hr': a partner's access is bounded by managedTenants and
// firestore.rules, never by the carrier's invoice. That argument is now simply
// applied to every module rather than to HR alone.
//
// NOTE on what is deliberately NOT conditioned on the carrier's HR add-on.
// An earlier draft gated 'hr' on hasAddOnPackage('hr'). That is wrong here: the
// add-on is the CARRIER's billing state, and it would have revoked the partner's
// SSN/DQ access on exactly one carrier shape — a paid carrier that declined HR —
// silently, and only for them. Every other shape already grants it (a trialing
// carrier returns true from hasAddOnPackage; a lapsed one auto-downgrades to
// complianceOnly where HR is unlocked free). A gate that fires on one rare shape
// and blocks a federally-required record is worse than no gate: the partner's
// access is bounded by managedTenants and firestore.rules, not by the carrier's
// invoice.

// The synthetic member-equivalent. A partner has no members/{uid} doc, so
// AppState.memberAccess would be null → checkModuleAccess falls past the member
// branch into the driver-PIN/kiosk fallback and throws a spurious
// #pin-challenge-modal over the partner's app (the TASK-1528 failure mode).
// Giving them a scoped-member shape makes the member branch authoritative and
// RETURN, so the PIN path is never reached — without touching that path for any
// real driver or kiosk user.
// 🔴 STANDING INVARIANT — NEVER PERSIST THIS OBJECT AS A REAL members/{uid} DOC.
// It exists ONLY on window.AppState and is never written to Firestore. That is
// what makes it safe: firestore.rules `hasModule()` (:348-352) requires
// `exists(tenants/{tid}/members/{uid})` and reads `.data.modules` off that doc, so
// the module list below is invisible to the rules layer and grants NOTHING
// server-side. COD's Domain lane asked whether `hr-directory` here reaches the
// `peopleTax` create/update/delete arm (:2086-2087, `hasModule(hr-directory) &&
// !isViewUploadOnly`) — i.e. WRITE and DELETE on another company's
// §391.21(b)(2) SSN vault. Verified in source: it does not, and cannot, because
// there is no member doc for hasModule() to find.
//
// Materialising this as a real member doc would flip that overnight AND hand the
// partner the `members` create arm — a persistent backdoor that survives
// reverting the rule, which is the one failure mode Chris said he cannot accept.
// If a partner ever needs a server-visible grant, it must come from
// isComplianceManager() in rules, never from a synthesised members doc.
function buildPartnerMemberEquivalent(uid, ctx) {
  return {
    uid,
    role: PARTNER_MEMBER_ROLE,
    modules: derivePartnerModuleScope(),
    // No employee record at this carrier — and that is permanent, not an unlinked
    // login. TASK-1489: `personless: true` is the flag for a deliberate OUTSIDE
    // user, which suppresses the "ask an admin to link your roster profile" hint
    // and keeps the personId-scoped surfaces (jobboard/My-*) closed.
    personId: null,
    personless: true,
    status: 'active',
    acceptedAtMs: null,
    viewUploadOnly: false,
    displayName: (ctx && ctx.partnerName) || null,
    // Provenance — so anything downstream can tell a synthetic grant from a real
    // members/{uid} doc without inferring it from the role string.
    synthetic: true,
    partnerClientTenantId: (ctx && ctx.tenantId) || null,
  };
}

// Resolve ?client= to a verified partner context, or null.
//
// Returns null (silently, and with ZERO extra work) for every session that does
// not carry the param — which is every normal carrier, member and driver
// session. That gating is deliberate: it is what makes this change a no-op for
// everyone who is not a partner following a partner link.
async function resolvePartnerClientContext() {
  const requested = getRequestedPartnerClientId();
  if (!requested) return null;

  // getComplianceManagerData is the EXISTING server gate (reuse, do not rewrite):
  // it reads _users/{uid} with the Admin SDK, throws permission-denied unless
  // role === 'complianceManager', and returns tenants[] built from the
  // server-held managedTenants — never from client input. _users is deny-all to
  // the client SDK, so this callable is the only way to learn either fact.
  let data = null;
  try {
    const res = await httpsCallable(functions, 'getComplianceManagerData')();
    data = (res && res.data) || null;
  } catch (e) {
    // Not a compliance manager, or the call failed. Either way NOTHING is
    // verified → ignore the param entirely and take the normal single-tenant
    // path. A carrier who pastes ?client= into their own URL lands in their own
    // account exactly as before.
    console.log('[Partner] ?client= present but caller is not a verified compliance partner — ignoring');
    return null;
  }

  const managed = Array.isArray(data && data.tenants) ? data.tenants : [];
  const match = managed.find(t => t && t.id === requested);
  if (!match) {
    // A real partner reaching for a carrier that is NOT in their managedTenants.
    // Refuse here for a clean message; firestore.rules refuses again on every
    // read regardless, so this branch is UX, not the boundary.
    console.warn('[Partner] ?client= is not in this partner\'s managed clients — refused');
    window.AppState.partnerClientRefused = true;
    // Strip the param from the address bar (COD Code Reviewer). We fall through to
    // the normal single-tenant path, and for a partner who HAS their own sandbox
    // that path boots a DIFFERENT tenant — leaving ?client=<carrier> sitting in the
    // URL would have the address bar naming a carrier this session is not in, with
    // no banner to contradict it. That is the same "named ≠ opened" confusion the
    // banner guard rejects, just relocated to the URL. Nothing may claim a carrier
    // that is not the one being rendered.
    try {
      const _u = new URL(window.location.href);
      _u.searchParams.delete(PARTNER_CLIENT_PARAM);
      window.history.replaceState({}, document.title, _u.pathname + _u.search + _u.hash);
    } catch (_) { /* non-fatal */ }
    try { showToast('You don\'t have access to that account.', 'error'); } catch (_) {}
    return null;
  }

  const profile = (data && data.partnerProfile) || {};
  return {
    tenantId: match.id,
    carrierName: match.companyName || match.id,
    partnerName: profile.companyName || data.userName || 'Compliance partner',
    partnerEmail: profile.email || '',
    firmUid: profile.firmUid || null,
  };
}

// Boot the app against a managed carrier. Returns true when the partner context
// is live; false means "could not — fall through to the normal path", and the
// function has already undone everything it touched.
async function loadPartnerClientTenant(ctx, userId) {
  window.AppState.partnerClientContext = ctx;
  window.AppState.tenant = ctx.tenantId;
  window.AppState.isCompliancePartner = true;

  // ⚠️ Set the synthetic member-equivalent BEFORE anything is awaited (COD AuthZ,
  // "weakest check"): hrEffectiveAdmin() returns TRUE when memberAccess is null —
  // that is the legacy single-login-owner path — so every tick this session spends
  // with a live partner context and a null memberAccess is a tick where the partner
  // reads as a FULL TENANT ADMIN. Nothing is mounted that early today, but the
  // window costs nothing to close and a future await inserted above would open it.
  // derivePartnerModuleScope() is role-derived and reads no tenant state, so it is
  // safe to build here, before the tenant doc loads.
  window.AppState.memberAccess = buildPartnerMemberEquivalent(userId, ctx);
  window.AppState.memberAccessPending = false;

  // Deliberately NOT written here (all three are carrier-owner signals that a
  // partner visit must not forge):
  //   tenants/{id}.lastLoginAt  — the portal surfaces this as carrier engagement
  //   users/{uid}.lastLoginAt   — the partner has no users/ doc at this carrier
  //   Telemetry.logSessionStart — "the carrier opened the app" is not true here
  let tenantData = null;
  try {
    const tenantDoc = await getDoc(doc(db, 'tenants', ctx.tenantId));
    if (!tenantDoc.exists()) throw new Error('tenant not found');
    tenantData = tenantDoc.data();
  } catch (e) {
    // firestore.rules refused the read (the rules half of TASK-1658 not landed,
    // or this partner's access was revoked between the callable and the read) —
    // OR the tenant is gone. Unwind cleanly rather than rendering a shell.
    console.warn('[Partner] Could not open client tenant:', e && e.message);
    window.AppState.partnerClientContext = null;
    window.AppState.tenant = null;
    // Unwind the synthetic grant too — a null memberAccess here is correct (this
    // returns false and the normal single-tenant path re-resolves it from scratch).
    window.AppState.memberAccess = null;
    window.AppState.memberAccessPending = true;
    try { showToast('Could not open that client account.', 'error'); } catch (_) {}
    return false;
  }

  window.AppState.tenantData = tenantData;
  window.AppState.settings = tenantData.settings || {};
  _ensurePartnerClientParamInUrl(ctx.tenantId);
  console.log('[Partner] Opened client account:', ctx.tenantId);

  // memberAccess was already built above, before the tenant read.
  window.AppState.hasDirectReports = false;

  // Land on Compliance. Mirrors the compliance-only-tenant default (~:5850) and
  // the same guard: only when there is no meaningful explicit hash, so a deep link
  // from the portal (#/roster, #/maintenance) is still honoured. Without this the
  // partner lands on Roster purely because 'roster' is AppState.currentView's
  // static default AND is in their scope, so the member default-view re-derivation
  // (~:5880) sees a granted view and leaves it alone.
  const _h = window.location.hash;
  if (!_h || _h === '#/' || _h === '#/undefined' || _h === '#/roster') {
    window.AppState.currentView = 'compliance';
  }

  if (typeof applyPartnerSandboxUi === 'function') applyPartnerSandboxUi();
  if (typeof subscribeTenantSettings === 'function') subscribeTenantSettings(ctx.tenantId);
  if (window.SidebarCustomize) window.SidebarCustomize.onTenantReady();

  // initCarrierAssistant() MUST be called — it is what HIDES the FAB. The FAB
  // ships in index.html and this function is the only thing that sets
  // display:none on it for an ineligible session; skipping the call left the
  // Ask-DispatchDeck assistant (which reads the whole carrier's data, and whose
  // callable is owner/admin-gated server-side) sitting on a partner's screen.
  // carrierAssistantEligible() returns false here — the synthetic grant carries
  // ma.synthetic, which carrierAssistantMemberModules() rejects outright
  // (TASK-4624: the partner's module list is a UI scope, not a grant, and the
  // server would resolve the PARTNER's own tenant) — so calling it hides.
  if (typeof initCarrierAssistant === 'function') initCarrierAssistant();

  // NOT subscribeMemberAccess(): there is no members/{uid} doc here, and its
  //   !snap.exists() branch nulls memberAccess — it would delete the synthetic
  //   grant a tick after we set it. Revocation still works: managedTenants is
  //   re-checked (server-side, uncached) on every load, and rules re-check on
  //   every read — so dropping the tenant from managedTenants closes everything.

  renderPartnerClientBanner();
  return true;
}

// -------------------------------------------------------------------
// The banner. Chris: "The URL protects the machine; the banner protects
// the human." It is what stops a mis-click from becoming an adverse-action
// letter on the wrong driver, so it is FIXED to the viewport (visible on every
// page, at every scroll position) rather than parked inside a view.
//
// Design (DESIGN.md — industrial, function over decoration): brand navy
// #0F172A with white text (16:1) and an amber #FBBF24 accent + rule, matching
// the TASK-531 partner-sandbox bar so a partner reads one consistent "you are
// in a partner context" language. Deliberately NOT the amber trial-banner
// treatment: those two must never be confused, and the trial banner is
// suppressed in this mode anyway (see showTrialBanner) so the two fixed
// top:0 bars can never stack.
//
// Carrier name goes in via textContent — tenant-controlled string, never
// interpolated into markup or an attribute.
// -------------------------------------------------------------------
function renderPartnerClientBanner() {
  try {
    const ctx = window.AppState && window.AppState.partnerClientContext;
    if (!ctx) { removePartnerClientBanner(); return; }

    // 🔴 The carrier NAMED and the carrier OPENED must come from one read.
    // ctx.tenantId and ctx.carrierName are both fields of the SAME entry of the
    // SAME getComplianceManagerData response, and AppState.tenant was assigned
    // from that same ctx.tenantId — so they cannot disagree unless something
    // repointed the tenant underneath us. If that ever happens, the one thing we
    // must not do is keep displaying a carrier name that is no longer the data
    // being rendered. Refuse to name anyone instead.
    if (window.AppState.tenant !== ctx.tenantId) {
      console.error('[Partner] Banner/tenant mismatch — refusing to name a carrier',
        { banner: ctx.tenantId, active: window.AppState.tenant });
      window.AppState.partnerClientContext = null;
      removePartnerClientBanner();
      try { showToast('Client account mismatch — reopen this client from the portal.', 'error'); } catch (_) {}
      return;
    }

    let bar = document.getElementById('partner-client-banner');
    if (!bar) {
      bar = document.createElement('div');
      bar.id = 'partner-client-banner';
      bar.setAttribute('role', 'status');
      bar.style.cssText = [
        'position:fixed', 'top:0', 'left:0', 'right:0', 'z-index:10000',
        'background:#0F172A', 'border-bottom:3px solid #F59E0B',
        'color:#fff', 'box-sizing:border-box',
        'display:flex', 'align-items:center', 'gap:8px',
        'padding:8px 12px',
        'font-size:0.875rem', 'font-weight:600', 'line-height:1.25',
      ].join(';') + ';';

      const icon = document.createElement('span');
      icon.className = 'material-icons';
      icon.style.cssText = 'font-size:18px;color:#FBBF24;flex:0 0 auto;';
      icon.textContent = 'verified_user';
      bar.appendChild(icon);

      const tag = document.createElement('span');
      tag.id = 'partner-client-banner-tag';
      tag.style.cssText = 'color:#FBBF24;letter-spacing:0.06em;text-transform:uppercase;font-size:0.72rem;font-weight:700;flex:0 0 auto;';
      bar.appendChild(tag);

      const name = document.createElement('span');
      name.id = 'partner-client-banner-name';
      // min-width:0 + ellipsis so a long carrier name shrinks instead of
      // pushing the Exit control off a 393px screen.
      name.style.cssText = 'flex:1 1 auto;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-weight:700;';
      bar.appendChild(name);

      const exit = document.createElement('button');
      exit.type = 'button';
      exit.id = 'partner-client-banner-exit';
      exit.textContent = 'Exit';
      exit.style.cssText = [
        'flex:0 0 auto', 'background:rgba(255,255,255,0.14)',
        'border:1px solid rgba(255,255,255,0.38)', 'color:#fff',
        'border-radius:6px', 'padding:6px 12px', 'min-height:32px',
        'font-size:0.75rem', 'font-weight:600', 'cursor:pointer',
      ].join(';') + ';';
      exit.addEventListener('click', () => { window.location.href = '/compliance-manager-v2.html'; });
      bar.appendChild(exit);

      document.body.appendChild(bar);
      window.addEventListener('resize', _onPartnerClientBannerResize);
    }

    // TASK-682 pattern — the full label + a long carrier name + the control
    // overflowed a 375-393px row, so shorten the tag on narrow phones.
    const narrow = !!(window.matchMedia && window.matchMedia('(max-width: 480px)').matches);
    const tagEl = document.getElementById('partner-client-banner-tag');
    // TASK-9422 — this said "View only" and stopped being true: a managing
    // partner can now send back / correct applications, move stages, log
    // Clearinghouse queries and fix CDL/med-card records here, and every one of
    // those saves is stamped with the partner's name in the carrier's audit trail.
    // Say whose name goes on the record instead. Billing, team logins and
    // account settings stay the carrier's. Dropped on narrow screens where the
    // carrier name needs the room.
    if (tagEl) tagEl.textContent = narrow ? 'Partner' : 'Partner access · changes logged in your name';
    // Prefer the LIVE tenant doc name. subscribeTenantSettings keeps
    // AppState.tenantData in sync from an onSnapshot pinned to THIS tenantId, and
    // the guard above already proved AppState.tenant === ctx.tenantId — so this is
    // the same carrier, just fresher. Without it a rename mid-session leaves the
    // banner naming one carrier while every view underneath names another, which
    // is the precise confusion the banner exists to prevent (COD Code Reviewer).
    const liveName = (window.AppState.tenantData && (window.AppState.tenantData.companyName || window.AppState.tenantData.name)) || '';
    const shownName = liveName || ctx.carrierName || ctx.tenantId;
    const nameEl = document.getElementById('partner-client-banner-name');
    if (nameEl) nameEl.textContent = shownName;
    bar.title = 'Partner access — you are working inside ' + shownName;

    _syncPartnerClientBannerOffset();
  } catch (e) {
    console.warn('[Partner] Banner render failed (non-fatal):', e && e.message);
  }
}
window.renderPartnerClientBanner = renderPartnerClientBanner;

// The tag copy is breakpoint-dependent (TASK-682 pattern), so a resize/rotate has
// to re-RENDER, not just re-measure — otherwise a phone rotated to landscape, or a
// desktop window narrowed and widened again, keeps the abbreviated label forever.
function _onPartnerClientBannerResize() { renderPartnerClientBanner(); }

// Reserve the banner's MEASURED height on both the scroll container and the
// fixed sidebar, so it never covers content or a nav control (the FAB-over-the-
// map-camera-control bug). Inline, because nothing else sets these inline and
// the trial/verification banners — the only other writers of #main-content
// padding-top — are suppressed in this mode.
function _syncPartnerClientBannerOffset() {
  const bar = document.getElementById('partner-client-banner');
  const mc = document.getElementById('main-content');
  const sb = document.getElementById('app-sidebar');
  if (!bar) {
    if (mc) mc.style.paddingTop = '';
    if (sb) sb.style.paddingTop = '';
    return;
  }
  const h = Math.ceil(bar.getBoundingClientRect().height) || 44;
  if (mc) mc.style.paddingTop = h + 'px';
  if (sb) sb.style.paddingTop = h + 'px';
}

function removePartnerClientBanner() {
  const bar = document.getElementById('partner-client-banner');
  if (bar) bar.remove();
  window.removeEventListener('resize', _onPartnerClientBannerResize);
  _syncPartnerClientBannerOffset();
}


// ===================================================================
// TASK-402 — "Ask DispatchDeck" carrier ops assistant (FAB + chat panel)
// -------------------------------------------------------------------
// Clones the proven compliance-manager assistant pattern, retargeted to the
// carrier's OWN tenant (server-side `askCarrierAssistant`). Distinct from the
// Help Center "Ask AI" (FAQ cowboy bot) — this is a NEW ops assistant grounded
// in live fleet data. Per-session, client-only history (never persisted — PII).
// FAB shows for the tenant owner / admin, or (TASK-4624) an active member
// holding at least one AI-relevant module (mirrors the server gate).
// ===================================================================
const carrierAssistantState = {
  history: [],
  open: false,
  pending: false,
  initialized: false,
};
const CARRIER_ASSISTANT_MAX_HISTORY = 20;
// TASK-9301 — client mirrors of the server input caps (functions/src/index.js
// CARRIER_AI_MAX_PROMPT_CHARS / ASSISTANT_INPUT_MAX_CHARS). Refuse BEFORE sending,
// keep the text in the box, say why. A unit tripwire asserts the numbers agree.
const CARRIER_ASSISTANT_MAX_PROMPT_CHARS = 5000;
const ASK_AI_MAX_QUESTION_CHARS = 5000;
function assistantTooLongMessage(len, max) {
  return `Your message is ${Number(len).toLocaleString('en-US')} characters — this assistant takes up to ${Number(max).toLocaleString('en-US')}. Nothing was sent. Shorten it or split it into two messages.`;
}
// TASK-9638 — EXACT copy of the server's _CARRIER_AI_TOOL_MODULES (functions/src/
// index.js): 'mod' = that module, { all: [...] } = every one, { any: [...] } =
// any one. COSMETIC here (which chips show); the server predicate is the gate.
// tests/task9638-carrier-gate-plumbing.unit.cjs fails if the two ever differ.
const CARRIER_ASSISTANT_TOOL_MODULES = {
  getTodaySchedule: 'dispatch',
  findPastJobLocations: 'dispatch',
  getWhoIsClockedIn: 'workforce',
  getPendingExpenses: { any: ['billing', 'receipts'] }, // TASK-9778
  getExpiringCompliance: 'compliance',
  getDriverComplianceStatus: 'compliance',
  auditHoursVsTickets: { all: ['billing', 'workforce'] },
  getPpeReport: { any: ['hr', 'workforce', 'compliance'] }, // TASK-9453
  getPmDue: 'maintenance', // TASK-9659
  // TASK-9638 items 2-4 — the six shared Claude-connector reads.
  findJobs: 'dispatch',
  findTickets: 'billing',
  getClockHistory: 'workforce',
  findPriceBook: { any: ['billing', 'crm'] },
  findContacts: 'crm',
  getRevenue: 'billing',
  checkAvailability: 'dispatch',
  getVehicleStatus: { any: ['maintenance', 'dispatch'] },
  getReadyToInvoice: 'billing',
  findInventory: { any: ['roster', 'hr'] }, // TASK-9727
  getInventoryLedger: { any: ['roster', 'hr'] }, // TASK-9727
  getConsumableUsage: { any: ['hr', 'workforce', 'compliance'] }, // TASK-9727
  getTextingReadiness: { any: ['dispatch', 'roster'] }, // TASK-9834 S2
  checkPerson: 'roster', // TASK-9834 S2 slice 2
  checkCompanySettings: { any: ['dispatch', 'roster', 'clock', 'workforce', 'billing'] }, // TASK-9834 S2 slice 2
  findDuplicateRecords: { any: ['roster', 'crm', 'billing'] }, // TASK-9834 S2 slice 2
};
// TASK-4624 / TASK-9638 — each suggestion chip names the TOOL that answers it; a
// module-scoped member only sees the chips their login can actually answer.
// TASK-9638 item 5 — PRIORITY ORDER: the list is filtered to the tools this
// login can answer, then the intro shows the first CARRIER_ASSISTANT_MAX_CHIPS —
// so each login gets its best 4 (owner: schedule, free tomorrow, trucks, bill).
const CARRIER_ASSISTANT_SUGGESTIONS = [
  { q: "What's on the schedule today?", tool: 'getTodaySchedule' },
  { q: "Who's free tomorrow?", tool: 'checkAvailability' },
  { q: "What's open on my trucks?", tool: 'getVehicleStatus' },
  { q: 'What can I bill?', tool: 'getReadyToInvoice' },
  { q: 'What jobs are on the board tomorrow?', tool: 'findJobs' },
  { q: "Who's clocked in right now?", tool: 'getWhoIsClockedIn' },
  { q: 'What was our revenue last month?', tool: 'getRevenue' },
  { q: 'Any expenses pending approval?', tool: 'getPendingExpenses' },
  { q: 'What tickets were written yesterday?', tool: 'findTickets' },
  { q: 'What documents are expiring in the next 30 days?', tool: 'getExpiringCompliance' },
  { q: 'Which trucks are due for PM?', tool: 'getPmDue' }, // TASK-9659
  { q: 'What PPE and supplies are low or out?', tool: 'findInventory' }, // TASK-9727 (COD UX: generic, not oilfield-only)
];
const CARRIER_ASSISTANT_MAX_CHIPS = 4;
// TASK-4624 — the modules that grant a non-admin member the assistant. Mirrors
// the server's _CARRIER_AI_MEMBER_MODULES exactly, so the FAB shows for exactly
// who the server will serve (this predicate is COSMETIC — the server gate in
// _resolveCarrierAssistantTenant is the boundary). TASK-9638 — + crm, hr; the
// drift test above pins this list to the server's, and both ship together.
// TASK-9672 / TASK-9657 (Chris 2026-10-12) — + maintenance, roster: a Maintenance-
// only or Roster-only login gets the assistant (and may connect Claude).
// TASK-9778 — + receipts (Receipts & Expenses login: pending-expenses chip only).
const CARRIER_ASSISTANT_MEMBER_MODULES = ['dispatch', 'workforce', 'billing', 'compliance', 'crm', 'hr', 'maintenance', 'roster', 'receipts'];

// TASK-9638 — same verdict as the server's _carrierAiToolAllowed(name, {modules}):
// unmapped tool = closed except recordProductFeedback (TASK-9778); malformed requirement = closed; mods exactly null = full
// access; anything else that is not an array = closed.
function carrierAssistantToolAllowed(tool, mods) {
  const req = Object.prototype.hasOwnProperty.call(CARRIER_ASSISTANT_TOOL_MODULES, tool) ? CARRIER_ASSISTANT_TOOL_MODULES[tool] : undefined;
  // TASK-9778 — deny-by-default, same as the server: the ONLY unmapped tool
  // admitted is recordProductFeedback.
  if (req === undefined) return tool === 'recordProductFeedback';
  const okList = v => Array.isArray(v) && v.every(m => typeof m === 'string' && m.length > 0);
  let all = [];
  let any = [];
  if (typeof req === 'string' && req.length > 0) {
    all = [req];
  } else if (req && typeof req === 'object' && !Array.isArray(req)
    && Object.keys(req).every(k => k === 'all' || k === 'any')
    && (req.all === undefined || okList(req.all)) && (req.any === undefined || okList(req.any))) {
    all = req.all || [];
    any = req.any || [];
  }
  if (all.length === 0 && any.length === 0) return false;
  if (mods === null) return true;
  if (!Array.isArray(mods)) return false;
  return all.every(m => mods.includes(m)) && (any.length === 0 || any.some(m => mods.includes(m)));
}

// TASK-4624 — null = full access (legacy owner or role owner|admin); otherwise
// the member's AI-relevant modules (possibly empty = ineligible). Role
// 'dispatcher' counts as holding 'dispatch' (mirrors the server, which mirrors
// requireDispatchAccess / firestore.rules isDispatcherOrHigher). A partner's
// synthetic member-equivalent (ma.synthetic) never qualifies — its module list
// is a UI scope, not a grant, and the server would resolve the PARTNER's own
// tenant, not the carrier being viewed.
function carrierAssistantMemberModules() {
  const ma = (window.AppState || {}).memberAccess;
  if (ma == null) return null; // legacy owner / no member doc = full access
  if (ma.synthetic) return [];
  if (ma.status && ma.status !== 'active') return [];
  // TASK-9638 — the server resolver never admits a read-only (?access= link)
  // member, whatever the role (TASK-9566 item 9); mirror it.
  if (ma.readOnly === true) return [];
  if (ma.role === 'owner' || ma.role === 'admin') return null;
  const mods = new Set(Array.isArray(ma.modules) ? ma.modules : []);
  if (ma.role === 'dispatcher') mods.add('dispatch');
  return CARRIER_ASSISTANT_MEMBER_MODULES.filter(m => mods.has(m));
}

// TASK-4624 — the suggestion chips this user's access can actually answer.
function carrierAssistantSuggestionList() {
  const mods = carrierAssistantMemberModules();
  const list = mods == null
    ? CARRIER_ASSISTANT_SUGGESTIONS
    : CARRIER_ASSISTANT_SUGGESTIONS.filter(s => carrierAssistantToolAllowed(s.tool, mods));
  return list.map(s => s.q);
}

// TASK-9622 — may this login change a price book (mirrors _mcpModuleAccess
// for billing|crm with write:true)? Owner / legacy single-login = yes.
// TASK-9716 — "Update from photo": owner, or a writable login holding Roster.
// Cosmetic only; proposeFleetPhotoChanges re-checks (_mcpModuleAccess roster write).
function carrierAssistantCanEditFleet() {
  const st = window.AppState || {};
  const ma = st.memberAccess;
  if (ma == null) return true;
  // The owner by ownerId (as _mcpModuleAccess decides), even with a members doc
  // that lists no modules (an owner members doc carries role 'owner' only).
  const ownerId = st.tenantData && st.tenantData.ownerId;
  if (ownerId && st.user && st.user.uid === ownerId) return true;
  if (ma.synthetic || (ma.status && ma.status !== 'active')) return false;
  if (ma.viewUploadOnly === true || ma.readOnly === true) return false;
  return Array.isArray(ma.modules) && ma.modules.includes('roster');
}

// TASK-9884 — the owner is identified by tenants/{tid}.ownerId FIRST, the way
// the server's _usersDocTenantAuthorized does: an owner who also has a members
// doc with no modules was losing the rate-sheet button. Nobody else is widened.
function carrierAssistantCanEditPriceBook() {
  const st = window.AppState || {};
  const uid = st.user?.uid || null;
  const ownerId = st.tenantData?.ownerId || null;
  if (uid && ownerId && uid === ownerId) return true;
  const ma = st.memberAccess;
  if (ma == null) return true;
  if (ma.synthetic || (ma.status && ma.status !== 'active')) return false;
  if (ma.viewUploadOnly === true || ma.readOnly === true) return false;
  const mods = Array.isArray(ma.modules) ? ma.modules : [];
  return mods.includes('billing') || mods.includes('crm');
}

// TASK-4624 BINDING 2 — Smart Filing (classify/fileCarrierUpload) stays
// owner/admin; the widened audience is read-only. Cosmetic gate for the
// attach/camera buttons — the server rejects a non-admin regardless.
function carrierAssistantCanSmartFile() {
  return carrierAssistantMemberModules() == null;
}

// Owner / admin, or (TASK-4624) an active non-partner member holding at least
// one AI-relevant module — never in demo or driver mode. memberAccess === null
// means a legacy single-login owner (full access). A member with ZERO relevant
// modules gets no FAB at all: never show a bot that can answer nothing.
// Mirrors the server (_resolveCarrierAssistantTenant with allowModuleMembers).
function carrierAssistantEligible() {
  const st = window.AppState || {};
  if (st.isDemo || st.tenant === 'demo') return false;
  if (st.driverMode || st.driverModePin || st.isPublicAccess) return false;
  if (!st.tenant) return false;
  // TASK-1024 — the Ask DispatchDeck assistant (and the Smart Filing paperclip/
  // camera buttons that live INSIDE it) are paid-tier Smart Tools. complianceOnly
  // (free-tier / trial-expired) carriers don't get them; askCarrierAssistant /
  // classifyCarrierUpload / fileCarrierUpload also reject server-side.
  if (isComplianceOnlyTenant()) return false;
  const ma = st.memberAccess;
  if (ma == null) return true; // legacy owner / no member doc = full access
  if (ma.status && ma.status !== 'active') return false;
  if (ma.readOnly === true) return false; // TASK-9638 — server refuses these
  if (ma.role === 'owner' || ma.role === 'admin') return true;
  const mods = carrierAssistantMemberModules();
  return Array.isArray(mods) && mods.length > 0;
}

// TASK-9657 — the Approvals badge (approvalsBadge.js) asks the same question
// before calling listMcpProposals, so it never calls for a login the server
// would refuse. Cosmetic, like the FAB gate; the server is the boundary.
window.carrierAssistantEligible = carrierAssistantEligible;

function carrierAssistantBodyEl() { return document.getElementById('dd-assistant-body'); }

function carrierAssistantFormatMessage(text) {
  // TASK-1523 — the model answers in markdown ("**Walt Norman**", "* bullet")
  // and this rendered the asterisks literally, prospect-facing (it's in both
  // money frames of the Ask-DispatchDeck ad).
  //
  // 🔴 SAFETY CONTRACT (2nd security-only COD 2026-08-12): escapeHtml() MUST
  // run over the WHOLE answer BEFORE any markdown regex, and nothing below may
  // interpolate into an ATTRIBUTE or href position. Tenant-controlled text
  // (person/vehicle/customer names) reaches this sink through the model's
  // answer; the pre-escape is the entire XSS argument. Fixed tag allowlist:
  // strong, em, code, ul, li, br — NO attributes, and deliberately NO link/
  // []() conversion (javascript: URLs are the one addition that reopens risk).
  let html = escapeHtml(text);
  // Inline code + bold + em — post-escape, so only literal * and ` match.
  html = html.replace(/`([^`\n]+)`/g, '<code>$1</code>');
  html = html.replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/(^|[\s>])\*([^*\n]+)\*(?=[\s.,;:!?)<]|$)/g, '$1<em>$2</em>');
  // Bullets BEFORE the newline→<br> step, or the list markup comes out invalid:
  // group consecutive "* item" / "- item" lines into one <ul>.
  html = html.replace(/(?:^|\n)((?:[*-] [^\n]*(?:\n|$))+)/g, (m, block) => {
    const items = block.trimEnd().split('\n')
      .map((l) => l.replace(/^[*-] /, ''))
      .map((l) => `<li>${l}</li>`)
      .join('');
    return `\n<ul>${items}</ul>\n`;
  });
  html = html.replace(/\n/g, '<br>');
  // A <ul> block already provides its own breaks — drop the <br> padding the
  // newline pass just added around it so lists don't double-space.
  html = html.replace(/<br><ul>/g, '<ul>').replace(/<\/ul><br>/g, '</ul>');
  return html;
}

function carrierAssistantAppendMessage(role, html, extraClass = '') {
  const body = carrierAssistantBodyEl();
  if (!body) return null;
  const wrap = document.createElement('div');
  wrap.className = `dd-assistant-msg dd-assistant-msg-${role}${extraClass ? ' ' + extraClass : ''}`;
  wrap.innerHTML = `<div class="dd-assistant-msg-bubble">${html}</div>`;
  body.appendChild(wrap);
  body.scrollTop = body.scrollHeight;
  return wrap;
}

function carrierAssistantAppendThinking() {
  const html = `<span class="dd-assistant-thinking-dots"><span></span><span></span><span></span></span>`;
  return carrierAssistantAppendMessage('thinking', html);
}

function carrierAssistantAutosize(el) {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = Math.min(el.scrollHeight, 120) + 'px';
}

// TASK-1419 — the opening brief. Chris: "on opening the AI, it would
// instinctively start talking about things that might be important."
// The signals are computed SERVER-SIDE and deterministically (no model call on
// open — free, instant, and it cannot invent an expired medical card). The
// static chips below are the fallback for a first-run/offline/error open.
const carrierAssistantBriefState = { loaded: false, signals: [], notes: '', learned: '', opens: 0, degraded: false, surfaceLabel: '' };

function carrierAssistantRenderIntro() {
  const body = carrierAssistantBodyEl();
  if (!body) return;
  // TASK-4624 — chips + the Smart Filing sentence match what this user's access
  // can actually do (a module-scoped member has no attach button to point at).
  const chips = carrierAssistantSuggestionList().slice(0, CARRIER_ASSISTANT_MAX_CHIPS).map(q =>
    `<button type="button" class="dd-assistant-chip" data-q="${window.escAttr(q)}">${escapeHtml(q)}</button>`
  ).join('');
  const filingLine = carrierAssistantCanSmartFile()
    ? ` You can also attach a med card, CDL, MVR, or receipt with the
      <span class="material-icons" style="font-size:14px;vertical-align:middle;">attach_file</span>
      button and I'll file it for you — <strong>nothing is saved until you confirm.</strong>`
    : '';
  // Full-access users get the classic enumeration; a module-scoped member gets
  // a scope-neutral line (their chips below already show what they can ask).
  const scopeLine = carrierAssistantMemberModules() == null
    ? `the schedule, jobs, tickets, who's clocked in, revenue,
      pending expenses, and documents that are expiring`
    : `the parts of the company your login covers`;
  body.innerHTML = `
    <div class="dd-assistant-intro">
      <strong>Hi — I'm Ask DispatchDeck.</strong> I can give you a fast read on what's
      happening in your company today: ${scopeLine}. I'm read-only when you ask questions — I summarize,
      I don't change anything.${filingLine}
      <div class="dd-assistant-chip-row">${chips}</div>
    </div>
  `;
  carrierAssistantBindChips(body);
}

function carrierAssistantBindChips(root) {
  root.querySelectorAll('.dd-assistant-chip').forEach(chip => {
    chip.addEventListener('click', () => {
      const input = document.getElementById('dd-assistant-input');
      if (input) {
        input.value = chip.getAttribute('data-q') || '';
        carrierAssistantAutosize(input);
        input.focus();
      }
    });
  });
}

const CARRIER_BRIEF_SEV_STYLE = {
  critical: { color: '#DC2626', bg: '#FEF2F2', border: '#FECACA' },
  warn: { color: '#B45309', bg: '#FFFBEB', border: '#FDE68A' },
  info: { color: '#1E40AF', bg: '#EFF6FF', border: '#BFDBFE' },
};

function carrierAssistantSignalCard(sig) {
  const st = CARRIER_BRIEF_SEV_STYLE[sig.severity] || CARRIER_BRIEF_SEV_STYLE.info;
  return `
    <div class="dd-brief-card" data-signal="${window.escAttr(sig.id)}"
         style="display:flex;gap:10px;align-items:flex-start;padding:10px 12px;margin:6px 0;border-radius:10px;
                background:${st.bg};border:1px solid ${st.border};cursor:pointer;">
      <span class="material-icons" style="font-size:18px;color:${st.color};flex-shrink:0;margin-top:1px;">${escapeHtml(sig.icon || 'info')}</span>
      <div style="flex:1;min-width:0;">
        <div style="font-size:0.84rem;font-weight:600;color:var(--text-primary);line-height:1.35;">${escapeHtml(sig.headline)}</div>
        ${sig.detail ? `<div style="font-size:0.76rem;color:var(--text-secondary);margin-top:2px;overflow:hidden;text-overflow:ellipsis;">${escapeHtml(sig.detail)}</div>` : ''}
      </div>
      <button type="button" class="dd-brief-dismiss" data-signal="${window.escAttr(sig.id)}" title="Stop raising this"
              style="background:none;border:none;cursor:pointer;color:var(--text-muted);padding:0 2px;line-height:1;flex-shrink:0;">
        <span class="material-icons" style="font-size:15px;">close</span>
      </button>
    </div>`;
}

function carrierAssistantRenderBrief() {
  const body = carrierAssistantBodyEl();
  if (!body) return;
  const { signals, notes, opens, degraded } = carrierAssistantBriefState;

  // COD caught this: an empty signal list means one of TWO different things, and
  // rendering them the same way is how you get a confident "nothing expired"
  // built on a query that failed. `degraded` distinguishes them.
  let cards;
  if (signals.length) {
    cards = signals.map(carrierAssistantSignalCard).join('');
  } else if (degraded) {
    cards = `<div style="padding:10px 12px;border-radius:10px;background:#FFFBEB;border:1px solid #FDE68A;
         font-size:0.84rem;color:var(--text-primary);">I couldn't finish checking everything just now, so I can't tell you it's all clear. Ask me again in a minute.</div>`;
  } else {
    cards = `<div style="padding:10px 12px;border-radius:10px;background:var(--bg-secondary);border:1px solid var(--border-light);
         font-size:0.84rem;color:var(--text-secondary);">Nothing needs your attention right now — board's clear, nothing expired, no expenses waiting.</div>`;
  }
  // Some signals came back but not all — flag the gap rather than letting a
  // partial brief read as a complete one.
  const partialNote = (signals.length && degraded)
    ? `<div style="font-size:0.74rem;color:#B45309;margin-top:2px;">Heads up — I couldn't check everything, so this may not be the full picture.</div>`
    : '';

  // Tailored to the screen they opened it from — but honest that the door
  // doesn't limit the room. Chris: "be honest about being able to do anything."
  const where = carrierAssistantBriefState.surfaceLabel;
  const lead = opens <= 1
    ? `<strong>Hi — I'm Ask DispatchDeck.</strong> Here's what stands out right now. I'll learn what you actually care about as we go.`
    : (where
      ? `<strong>You're in ${escapeHtml(where)} — here's what stands out.</strong> Ask me about anything else too, I'm the same everywhere.`
      : `<strong>Here's what stands out right now.</strong>`);

  body.innerHTML = `
    <div class="dd-assistant-intro">
      <div style="font-size:0.84rem;line-height:1.45;margin-bottom:4px;">${lead}</div>
      ${cards}
      ${partialNote}
      <div class="dd-assistant-chip-row">${
        carrierAssistantSuggestionList().slice(0, 2).map(q =>
          `<button type="button" class="dd-assistant-chip" data-q="${window.escAttr(q)}">${escapeHtml(q)}</button>`
        ).join('')
      }</div>
      <button type="button" id="dd-brief-profile-btn"
              style="background:none;border:none;cursor:pointer;padding:6px 0 0;font-size:0.74rem;color:var(--text-muted);
                     display:inline-flex;align-items:center;gap:4px;">
        <span class="material-icons" style="font-size:13px;">edit_note</span>
        ${notes && notes.trim() ? 'What I know about how you work' : 'Tell me how you work'}
      </button>
    </div>`;

  carrierAssistantBindChips(body);

  // Tapping a card asks the question it stands for — and that click is the
  // honest signal of what this person cares about.
  body.querySelectorAll('.dd-brief-card').forEach(card => {
    card.addEventListener('click', (ev) => {
      if (ev.target.closest('.dd-brief-dismiss')) return;
      const id = card.getAttribute('data-signal');
      const sig = carrierAssistantBriefState.signals.find(s => s.id === id);
      if (!sig) return;
      carrierAssistantRecordInteraction(id, 'click');
      const input = document.getElementById('dd-assistant-input');
      if (input) { input.value = sig.ask || sig.headline; carrierAssistantAutosize(input); }
      carrierAssistantSendMessage();
    });
  });

  body.querySelectorAll('.dd-brief-dismiss').forEach(btn => {
    btn.addEventListener('click', (ev) => {
      ev.stopPropagation();
      const id = btn.getAttribute('data-signal');
      carrierAssistantRecordInteraction(id, 'dismiss');
      carrierAssistantBriefState.signals = carrierAssistantBriefState.signals.filter(s => s.id !== id);
      carrierAssistantRenderBrief();
    });
  });

  document.getElementById('dd-brief-profile-btn')?.addEventListener('click', carrierAssistantRenderProfile);
}

// The profile IS a markdown file the user can read and edit — that's the point
// of it being a document rather than an opaque score. Chris: "would be great if
// it could build its own 'MD' file for its user."
function carrierAssistantRenderProfile() {
  const body = carrierAssistantBodyEl();
  if (!body) return;
  body.innerHTML = `
    <div class="dd-assistant-intro">
      <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:6px;">
        <strong style="font-size:0.88rem;">How you work</strong>
        <button type="button" id="dd-brief-back"
                style="background:none;border:none;cursor:pointer;font-size:0.76rem;color:var(--brand-blue);">Back</button>
      </div>
      <div style="font-size:0.78rem;color:var(--text-secondary);line-height:1.45;margin-bottom:8px;">
        Anything you write here I read before I say anything — what to raise first,
        what to skip, how blunt to be. It's yours; edit or clear it whenever.
      </div>
      ${carrierAssistantBriefState.learned ? `
      <details style="margin-bottom:10px;">
        <summary style="cursor:pointer;font-size:0.76rem;color:var(--text-secondary);">What I've picked up on my own</summary>
        <pre style="white-space:pre-wrap;font-family:inherit;font-size:0.76rem;line-height:1.5;color:var(--text-secondary);
                    background:var(--bg-secondary);border:1px solid var(--border-light);border-radius:8px;
                    padding:9px 11px;margin:6px 0 0;">${escapeHtml(carrierAssistantBriefState.learned)}</pre>
      </details>` : ''}
      <textarea id="dd-brief-notes" rows="8" placeholder="e.g. I look at billing first thing Friday. Don't bother me about expenses under $50. Keep it short."
        style="width:100%;box-sizing:border-box;padding:9px 11px;border:1px solid var(--border-light);border-radius:9px;
               font-family:inherit;font-size:0.82rem;line-height:1.5;resize:vertical;background:var(--bg-primary);
               color:var(--text-primary);">${escapeHtml(carrierAssistantBriefState.notes || '')}</textarea>
      <div style="display:flex;align-items:center;gap:8px;margin-top:8px;">
        <button type="button" id="dd-brief-save" class="btn btn-primary" style="font-size:0.8rem;padding:6px 14px;">Save</button>
        <span id="dd-brief-save-status" style="font-size:0.76rem;color:var(--text-muted);"></span>
      </div>
    </div>`;

  document.getElementById('dd-brief-back')?.addEventListener('click', carrierAssistantRenderBrief);
  document.getElementById('dd-brief-save')?.addEventListener('click', async () => {
    const ta = document.getElementById('dd-brief-notes');
    const status = document.getElementById('dd-brief-save-status');
    const btn = document.getElementById('dd-brief-save');
    if (!ta) return;
    btn.disabled = true;
    if (status) status.textContent = 'Saving…';
    try {
      await httpsCallable(functions, 'carrierAssistantProfile')({ action: 'saveNotes', notes: ta.value || '' });
      carrierAssistantBriefState.notes = ta.value || '';
      if (status) status.textContent = 'Saved.';
    } catch (err) {
      console.error('[ask-dd] profile save failed', err);
      if (status) status.textContent = err?.message || "Couldn't save that.";
    } finally {
      btn.disabled = false;
    }
  });
}

// Fire-and-forget: a failed counter must never interrupt what the user is doing.
function carrierAssistantRecordInteraction(signalId, kind) {
  if (!signalId) return;
  try {
    httpsCallable(functions, 'carrierAssistantProfile')({ action: 'interact', signalId, kind })
      .catch(err => console.warn('[ask-dd] interaction not recorded', err?.message));
  } catch (_) { /* non-fatal */ }
}

async function carrierAssistantLoadBrief() {
  // Only on a clean panel — never wipe an in-progress conversation.
  if (carrierAssistantState.history.length) return;
  try {
    // TASK-1419 — tell it which door we came through. It changes what the
    // assistant LEADS with, never what it can do.
    const res = await httpsCallable(functions, 'carrierAssistantBriefing')({
      module: window.AppState?.currentView || '',
    });
    const d = res?.data || {};
    carrierAssistantBriefState.surfaceLabel = typeof d.surfaceLabel === 'string' ? d.surfaceLabel : '';
    carrierAssistantBriefState.signals = Array.isArray(d.signals) ? d.signals : [];
    carrierAssistantBriefState.notes = typeof d.notes === 'string' ? d.notes : '';
    carrierAssistantBriefState.learned = typeof d.learned === 'string' ? d.learned : '';
    carrierAssistantBriefState.opens = Number(d.opens || 0);
    // Absent/malformed `degraded` is treated as DEGRADED on purpose: an old
    // cached client that doesn't understand the field must not render a
    // confident all-clear it has no basis for.
    carrierAssistantBriefState.degraded = d.degraded !== false;
    carrierAssistantBriefState.loaded = true;
    if (!carrierAssistantState.history.length) carrierAssistantRenderBrief();
  } catch (err) {
    // The static intro is already on screen — leave it. A brief that can't load
    // is a worse panel, not a broken one.
    console.warn('[ask-dd] brief unavailable', err?.message);
  }
}

async function carrierAssistantSendMessage() {
  if (carrierAssistantState.pending) return;
  const input = document.getElementById('dd-assistant-input');
  const sendBtn = document.getElementById('dd-assistant-send-btn');
  if (!input) return;
  const prompt = (input.value || '').trim();
  if (!prompt) return;
  if (prompt.length > CARRIER_ASSISTANT_MAX_PROMPT_CHARS) {   // TASK-9301
    carrierAssistantAppendMessage('ai', escapeHtml(assistantTooLongMessage(prompt.length, CARRIER_ASSISTANT_MAX_PROMPT_CHARS)), 'dd-assistant-msg-error');
    return;
  }

  carrierAssistantState.pending = true;
  if (sendBtn) sendBtn.disabled = true;

  carrierAssistantAppendMessage('user', escapeHtml(prompt));
  carrierAssistantState.history.push({ role: 'user', content: prompt });
  if (carrierAssistantState.history.length > CARRIER_ASSISTANT_MAX_HISTORY) {
    carrierAssistantState.history.splice(0, carrierAssistantState.history.length - CARRIER_ASSISTANT_MAX_HISTORY);
  }
  input.value = '';
  carrierAssistantAutosize(input);

  const thinkingEl = carrierAssistantAppendThinking();

  try {
    const callable = httpsCallable(functions, 'askCarrierAssistant');
    const res = await callable({
      prompt,
      conversationHistory: carrierAssistantState.history.slice(0, -1), // server adds current turn
      module: window.AppState?.currentView || '', // where it's standing — affects emphasis only
    });
    thinkingEl?.remove();
    const answer = res?.data?.answer || '';
    if (answer) {
      carrierAssistantState.history.push({ role: 'assistant', content: answer });
      if (carrierAssistantState.history.length > CARRIER_ASSISTANT_MAX_HISTORY) {
        carrierAssistantState.history.splice(0, carrierAssistantState.history.length - CARRIER_ASSISTANT_MAX_HISTORY);
      }
      carrierAssistantAppendMessage('ai', carrierAssistantFormatMessage(answer));
    } else {
      carrierAssistantAppendMessage('ai', 'No answer came back. Try rephrasing the question.', 'dd-assistant-msg-error');
    }
  } catch (err) {
    thinkingEl?.remove();
    console.error('[ask-dd] send failed', err);
    let msg = err?.message || 'Assistant error';
    if (/unauthenticated|sign-in/i.test(msg)) msg = "You're signed out. Refresh and sign in again.";
    else if (/permission/i.test(msg)) msg = "You don't have access to this assistant.";
    // TASK-9957 — the server's refusal text says whether it is the daily cap or a
    // temporary blip; show it rather than inventing "tomorrow" here.
    else if (/resource-exhausted/i.test(String(err?.code || '')) || /limit reached|resource-exhausted/i.test(msg)) msg = (err?.message && !/^(functions\/)?resource-exhausted$/i.test(String(err.message).trim())) ? err.message : 'AI limit reached or temporarily unavailable — try again later.';
    else if (/unavailable|temporarily/i.test(msg)) msg = 'AI is temporarily unavailable — try again shortly.';
    else msg = 'Something went wrong — try again.';
    carrierAssistantAppendMessage('ai', escapeHtml(msg), 'dd-assistant-msg-error');
    carrierAssistantState.history.pop(); // drop failed turn so retries work
  } finally {
    carrierAssistantState.pending = false;
    if (sendBtn) sendBtn.disabled = false;
    input?.focus();
  }
}

function carrierAssistantToggle() {
  const fab = document.getElementById('dd-assistant-fab');
  const panel = document.getElementById('dd-assistant-panel');
  if (!fab || !panel) return;
  carrierAssistantState.open = !carrierAssistantState.open;
  const glyph = fab.querySelector('.dd-assistant-fab-glyph');
  const closeIcon = fab.querySelector('.dd-assistant-fab-close');
  if (carrierAssistantState.open) {
    panel.classList.add('is-open');
    fab.classList.add('is-open');
    fab.setAttribute('aria-label', 'Close Ask DispatchDeck');
    if (glyph) glyph.style.display = 'none';
    if (closeIcon) closeIcon.style.display = '';
    document.getElementById('dd-assistant-input')?.focus();
    // TASK-1419 — lead with what's actually going on instead of "ask me
    // anything". Refetched per open (server caches 60s) so a brief can't go
    // stale in a panel left closed all afternoon.
    try { carrierAssistantLoadBrief(); } catch (_) { /* non-fatal */ }
    // TASK-982 — re-show any pending Smart Filing confirm cards on open.
    // TASK-4624 (COD) — non-admin members can't read the pending collection
    // (rules deny) and their Smart Filing UI is hidden; skip the fetch so the
    // panel open doesn't spray a permission error into the console.
    if (carrierAssistantCanSmartFile()) {
      try { ddSmartFilingLoadPending(); } catch (_) { /* non-fatal */ }
    }
  } else {
    panel.classList.remove('is-open');
    fab.classList.remove('is-open');
    ddAssistantFabLabel(fab); // TASK-9985 — keeps the waiting-cards count in the label
    if (glyph) glyph.style.display = '';
    if (closeIcon) closeIcon.style.display = 'none';
  }
}
window.__toggleCarrierAssistant = carrierAssistantToggle;

function carrierAssistantReset() {
  carrierAssistantState.history = [];
  carrierAssistantRenderIntro();
}
window.__resetCarrierAssistant = carrierAssistantReset;
window.__sendCarrierAssistantMessage = carrierAssistantSendMessage;

// ===================================================================
// TASK-9268 slice 2 — "your questions are saved" notice + AI history.
// -------------------------------------------------------------------
// Chris 2026-09-29: record AI questions, and ONLY the tenant owner may read
// other people's. The server is the gate (listAiTranscripts/getAiTranscript
// resolve ownerId server-side; firestore.rules deny every client verb on
// aiTranscripts). This UI only decides what to OFFER: the owner gets "Team AI
// history", everyone else "Your AI history". Both open the same modal.
// The notice renders BEFORE anyone types, on every eligible tenant load — a GM
// finding out afterwards that the owner can read his questions is the trust
// problem the row warned about.
// ===================================================================
const AI_TRANSCRIPT_RETENTION_DAYS = 90; // mirrors AI_TRANSCRIPT_TTL_DAYS in functions/src/index.js

function aiHistoryViewerIsOwner() {
  const st = window.AppState || {};
  if (st.driverMode || st.isPublicAccess || st.driverModePin) return false;
  if (st.isDemo || st.tenant === 'demo') return false;
  const uid = st.user?.uid || null;
  const ownerId = st.tenantData?.ownerId || null;
  return !!(uid && ownerId && uid === ownerId);
}

// Shared copy so every recording assistant says the same thing (Ask DispatchDeck,
// Help chat, Permit Pete, DOT Advisor, HR Smart Search, Deal Desk). Harvest
// dp-transcripts (Chris 2026-10-12): "make it small and to the point" — one short
// line, no preamble. Only the OWNER can read a teammate's chats (aiTranscripts
// reads are server-side, owner-gated), so it names the owner, not "admins".
function aiTranscriptNoticeText(isOwner = aiHistoryViewerIsOwner()) {
  // The demo tenant records nothing (askCarrierAssistant refuses demo; askAI
  // and askPermitPete skip it), so say nothing rather than claim a log that
  // does not exist (COD 2026-10-02, Code Reviewer).
  const st = window.AppState || {};
  if (st.isDemo || st.tenant === 'demo') return '';
  return isOwner
    ? `Chats here are saved for ${AI_TRANSCRIPT_RETENTION_DAYS} days. As the owner, you can review your team's.`
    : `Chats here are saved for ${AI_TRANSCRIPT_RETENTION_DAYS} days. Your account owner can review them.`;
}
window.aiTranscriptNoticeText = aiTranscriptNoticeText;

function carrierAssistantRenderDisclosure() {
  const panel = document.getElementById('dd-assistant-panel');
  const row = panel?.querySelector('.dd-assistant-input-row');
  if (!panel || !row) return;
  let el = document.getElementById('dd-assistant-disclosure');
  if (!el) {
    el = document.createElement('div');
    el.id = 'dd-assistant-disclosure';
    el.setAttribute('role', 'note');
    el.style.cssText = 'display:flex;align-items:flex-start;gap:6px;padding:6px 12px;font-size:0.72rem;line-height:1.4;'
      + 'color:var(--text-secondary);background:var(--bg-secondary);border-top:1px solid var(--border-light);';
    row.insertAdjacentElement('beforebegin', el);
  }
  const st = window.AppState || {};
  const isOwner = aiHistoryViewerIsOwner();
  const canReview = !(st.isDemo || st.tenant === 'demo');
  // Demo records nothing, so it shows no notice and no history button.
  el.style.display = canReview ? 'flex' : 'none';
  el.innerHTML = `
    <span class="material-icons" aria-hidden="true" style="font-size:14px;margin-top:1px;">history</span>
    <span>${escapeHtml(aiTranscriptNoticeText(isOwner))}${canReview ? `
      <button type="button" id="dd-assistant-history-btn"
        style="background:none;border:none;padding:0;cursor:pointer;font:inherit;color:var(--brand-blue);text-decoration:underline;">
        ${isOwner ? 'Team AI history' : 'Your AI history'}</button>` : ''}</span>`;
  el.querySelector('#dd-assistant-history-btn')?.addEventListener('click', () => { openAiHistoryModal(); });
}

const AI_HISTORY_SURFACE_LABEL = {
  askCarrierAssistant: 'Ask DispatchDeck',
  askAI: 'Help chat',
  askPermitPete: 'Permit Pete',
  askDotAdvisor: 'DOT Advisor',
  askHRDocuments: 'HR Smart Search',
  crmAssistant: 'Deal Desk',
};
// gen + key: every request remembers the generation and the account it was
// sent for; a response that comes back after an account switch, a reopen or a
// logout is DROPPED, never rendered (COD 2026-10-02 round 2, Code Reviewer).
const aiHistoryState = { rows: [], nextAfter: null, loading: false, isOwner: false, gen: 0, key: '' };
function aiHistoryAccountKey() {
  const st = window.AppState || {};
  return `${st.user?.uid || ''}|${st.tenant || ''}`;
}
function aiHistoryStale(gen, key) {
  return gen !== aiHistoryState.gen || key !== aiHistoryAccountKey();
}
function aiHistoryResetForAccountChange() {
  aiHistoryState.gen += 1;
  aiHistoryState.rows = [];
  aiHistoryState.nextAfter = null;
  aiHistoryState.loading = false;
  aiHistoryState.isOwner = false;
  aiHistoryState.key = '';
  const modal = document.getElementById('modal-ai-history');
  if (modal) { modal.classList.add('hidden'); modal.innerHTML = ''; }
}
window.aiHistoryResetForAccountChange = aiHistoryResetForAccountChange;

// Callable Timestamps arrive as {_seconds,_nanoseconds} (or seconds/nanoseconds).
function aiHistoryFormatTime(ts) {
  const s = ts && (ts._seconds ?? ts.seconds);
  if (!Number.isFinite(Number(s))) return '';
  try {
    return new Date(Number(s) * 1000).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch (_) { return ''; }
}

function aiHistoryModalBody() { return document.getElementById('ai-history-body'); }

async function openAiHistoryModal() {
  let modal = document.getElementById('modal-ai-history');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'modal-ai-history';
    modal.className = 'modal';
    // The Ask DispatchDeck panel sits at z-index 2199; open above it.
    modal.style.zIndex = '2300';
    document.body.appendChild(modal);
  }
  aiHistoryState.gen += 1; // orphan anything still in flight from a previous open
  aiHistoryState.loading = false;
  aiHistoryState.key = aiHistoryAccountKey();
  aiHistoryState.isOwner = aiHistoryViewerIsOwner();
  aiHistoryState.rows = [];
  aiHistoryState.nextAfter = null;
  const title = aiHistoryState.isOwner ? 'Team AI history' : 'Your AI history';
  modal.innerHTML = `
    <div class="modal-content modal-fullscreen" style="max-width:760px;">
      <div class="modal-header">
        <h3><span class="material-icons" style="vertical-align:middle;">history</span> ${title}</h3>
        <button class="btn-icon" type="button" id="ai-history-close" aria-label="Close"><span class="material-icons">close</span></button>
      </div>
      <div style="padding:8px var(--spacing-lg) 0;font-size:0.8rem;color:var(--text-secondary);line-height:1.45;">
        ${aiHistoryState.isOwner
          ? `Every question your team asked Ask DispatchDeck, the Help chat, Permit Pete, DOT Advisor, HR Smart Search and Deal Desk in the last ${AI_TRANSCRIPT_RETENTION_DAYS} days. Only you, the account owner, can see other people's. Opening someone's history is recorded in the audit log.`
          : `The questions you asked Ask DispatchDeck, the Help chat, Permit Pete, DOT Advisor, HR Smart Search and Deal Desk in the last ${AI_TRANSCRIPT_RETENTION_DAYS} days. Your account owner can also see these.`}
      </div>
      <div id="ai-history-body" style="max-height:72vh;overflow-y:auto;padding:var(--spacing-lg);"></div>
    </div>`;
  modal.classList.remove('hidden');
  modal.querySelector('#ai-history-close')?.addEventListener('click', () => modal.classList.add('hidden'));
  await aiHistoryLoadPage();
}
window.openAiHistoryModal = openAiHistoryModal;

async function aiHistoryLoadPage() {
  const body = aiHistoryModalBody();
  if (!body || aiHistoryState.loading) return;
  const gen = aiHistoryState.gen;
  const key = aiHistoryState.key;
  if (aiHistoryStale(gen, key)) return;
  aiHistoryState.loading = true;
  if (!aiHistoryState.rows.length) {
    body.innerHTML = `<div style="text-align:center;color:var(--text-secondary);padding:24px;"><span class="btn-spinner" style="margin-right:8px;"></span> Loading...</div>`;
  }
  try {
    const args = { limit: 50 };
    if (aiHistoryState.isOwner) args.scope = 'tenant';
    if (aiHistoryState.nextAfter) args.after = aiHistoryState.nextAfter;
    const res = await httpsCallable(functions, 'listAiTranscripts')(args);
    if (aiHistoryStale(gen, key)) return; // account changed / reopened while waiting
    const d = res?.data || {};
    aiHistoryState.rows = aiHistoryState.rows.concat(Array.isArray(d.rows) ? d.rows : []);
    aiHistoryState.nextAfter = d.nextAfter || null;
    aiHistoryRenderList();
  } catch (err) {
    console.error('[ai-history] list failed', err);
    if (aiHistoryStale(gen, key)) return;
    body.innerHTML = `<div style="color:var(--text-primary);padding:16px;">${escapeHtml(err?.message || "Couldn't load AI history.")}</div>`;
  } finally {
    if (gen === aiHistoryState.gen) aiHistoryState.loading = false;
  }
}

function aiHistoryRenderList() {
  const body = aiHistoryModalBody();
  if (!body) return;
  if (!aiHistoryState.rows.length) {
    body.innerHTML = `<div style="text-align:center;color:var(--text-secondary);padding:24px;">No questions saved yet.</div>`;
    return;
  }
  const items = aiHistoryState.rows.map((r) => {
    const surface = AI_HISTORY_SURFACE_LABEL[r.surface] || 'Assistant';
    const meta = [aiHistoryFormatTime(r.createdAt), aiHistoryState.isOwner ? (r.who || '') : '', surface]
      .filter(Boolean).map((x) => escapeHtml(x)).join(' · ');
    return `
      <button type="button" class="ai-history-row" data-id="${window.escAttr(r.id)}"
        style="display:block;width:100%;text-align:left;background:var(--bg-primary);border:1px solid var(--border-light);
               border-radius:8px;padding:10px 12px;margin-bottom:8px;cursor:pointer;color:var(--text-primary);">
        <div style="font-size:0.74rem;color:var(--text-secondary);margin-bottom:3px;">${meta}${r.errored ? ' · <span style="color:#B45309;">error</span>' : ''}</div>
        <div style="font-size:0.85rem;line-height:1.4;white-space:pre-wrap;word-break:break-word;">${escapeHtml(r.promptPreview || '')}${(r.promptChars || 0) > (r.promptPreview || '').length ? '…' : ''}</div>
      </button>`;
  }).join('');
  body.innerHTML = items + (aiHistoryState.nextAfter
    ? `<div style="text-align:center;margin-top:8px;"><button type="button" class="btn btn-secondary" id="ai-history-more">Load more</button></div>`
    : '');
  body.querySelectorAll('.ai-history-row').forEach((btn) => {
    btn.addEventListener('click', () => aiHistoryOpenTurn(btn.getAttribute('data-id')));
  });
  body.querySelector('#ai-history-more')?.addEventListener('click', () => aiHistoryLoadPage());
}

async function aiHistoryOpenTurn(id) {
  const body = aiHistoryModalBody();
  if (!body || !id) return;
  const gen = aiHistoryState.gen;
  const key = aiHistoryState.key;
  if (aiHistoryStale(gen, key)) return;
  body.innerHTML = `<div style="text-align:center;color:var(--text-secondary);padding:24px;"><span class="btn-spinner" style="margin-right:8px;"></span> Loading...</div>`;
  try {
    const res = await httpsCallable(functions, 'getAiTranscript')({ id });
    if (aiHistoryStale(gen, key)) return; // account changed / reopened while waiting
    const t = res?.data || {};
    const surface = AI_HISTORY_SURFACE_LABEL[t.surface] || 'Assistant';
    const tools = Array.isArray(t.toolCalls) && t.toolCalls.length
      ? t.toolCalls.map((c) => escapeHtml(c.name || '')).join(', ')
      : (Array.isArray(t.toolCallNames) && t.toolCallNames.length ? t.toolCallNames.map((n) => escapeHtml(n)).join(', ') : '');
    const meta = [aiHistoryFormatTime(t.createdAt), t.who || '', surface].filter(Boolean).map((x) => escapeHtml(x)).join(' · ');
    const block = (label, text) => `
      <div style="font-size:0.74rem;font-weight:600;color:var(--text-secondary);margin:12px 0 4px;">${label}</div>
      <div style="font-size:0.85rem;line-height:1.5;white-space:pre-wrap;word-break:break-word;background:var(--bg-secondary);
                  border:1px solid var(--border-light);border-radius:8px;padding:10px 12px;color:var(--text-primary);">${escapeHtml(text || '')}</div>`;
    body.innerHTML = `
      <button type="button" id="ai-history-back" style="background:none;border:none;cursor:pointer;color:var(--brand-blue);font-size:0.82rem;padding:0;">← Back</button>
      <div style="font-size:0.76rem;color:var(--text-secondary);margin-top:8px;">${meta}</div>
      ${block('Question', t.prompt)}
      ${(t.historyUserTurns || 0) > 0 ? `<div style="font-size:0.72rem;color:var(--text-secondary);margin-top:4px;">Part of a conversation (${Number(t.historyUserTurns) || 0} earlier question${t.historyUserTurns === 1 ? '' : 's'}).</div>` : ''}
      ${block('Answer', t.errored ? (t.answer || 'The assistant hit an error on this question.') : t.answer)}
      ${tools ? `<div style="font-size:0.74rem;color:var(--text-secondary);margin-top:10px;">Looked up: ${tools}</div>` : ''}
      <div style="font-size:0.7rem;color:var(--text-secondary);margin-top:10px;">Phone numbers, SSNs and keys are blanked out before saving. Deleted automatically after ${AI_TRANSCRIPT_RETENTION_DAYS} days.</div>`;
    body.querySelector('#ai-history-back')?.addEventListener('click', () => aiHistoryRenderList());
  } catch (err) {
    console.error('[ai-history] get failed', err);
    if (aiHistoryStale(gen, key)) return;
    body.innerHTML = `<button type="button" id="ai-history-back" style="background:none;border:none;cursor:pointer;color:var(--brand-blue);font-size:0.82rem;padding:0;">← Back</button>
      <div style="color:var(--text-primary);padding:16px 0;">${escapeHtml(err?.message || "Couldn't open that question.")}</div>`;
    body.querySelector('#ai-history-back')?.addEventListener('click', () => aiHistoryRenderList());
  }
}

// ===================================================================
// TASK-982 — Smart Filing (attach a doc → classify → confirm → file)
// -------------------------------------------------------------------
// Attach/photo a med card / CDL / MVR / receipt in the chat. The client
// uploads to the quarantine (tenants/{tid}/smartInbox/{uploadId}), calls
// classifyCarrierUpload (Gemini reads it), then renders an in-chat CONFIRM
// card. NOTHING is written to the canonical home until the user clicks
// "Confirm & File" → fileCarrierUpload. Every model/entity field is escHtml'd
// before it touches innerHTML (carrierAssistantAppendMessage uses innerHTML).
// ===================================================================
const CARRIER_SMART_FILING = {
  MAX_BYTES: 10 * 1024 * 1024,
  OK_MIME: ['image/png', 'image/jpeg', 'image/webp', 'application/pdf'],
  OK_EXT: /\.(png|jpe?g|webp|pdf)$/i,
  cards: {}, // uploadId -> stored classification (for the Change… toggle)
};

function ddSmartFilingUploadId() {
  try { if (window.crypto && crypto.randomUUID) return 'sf_' + crypto.randomUUID().replace(/-/g, ''); } catch (_) { /* noop */ }
  return 'sf_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 12);
}

function ddSmartFilingOpenIfClosed() {
  if (!carrierAssistantState.open) carrierAssistantToggle();
}

// Roster people for the driver picker. Prefer the live RosterState cache; fall
// back to a one-time Firestore read when Roster hasn't been opened this session.
async function ddSmartFilingGetPeople() {
  const cached = window.RosterState && Array.isArray(window.RosterState.people) ? window.RosterState.people : null;
  if (cached && cached.length) return cached.map(p => ({ id: p.id, name: p.name || '' }));
  const tenant = window.AppState && window.AppState.tenant;
  if (!tenant) return [];
  try {
    const snap = await getDocs(collection(db, 'tenants', tenant, 'people'));
    return snap.docs.map(d => ({ id: d.id, name: (d.data() || {}).name || '' }));
  } catch (_) { return []; }
}

function __ddSmartFilingPick(mode) {
  const id = mode === 'camera' ? 'dd-assistant-camera-input' : 'dd-assistant-file-input';
  const el = document.getElementById(id);
  if (el) { el.value = ''; el.click(); }
}
window.__ddSmartFilingPick = __ddSmartFilingPick;

async function ddSmartFilingHandleFile(file) {
  if (!file) return;
  ddSmartFilingOpenIfClosed();
  // Client MIME + extension check (defense in depth — server re-sniffs magic bytes).
  const type = file.type || '';
  const okMime = CARRIER_SMART_FILING.OK_MIME.includes(type) || type.startsWith('image/');
  const okExt = CARRIER_SMART_FILING.OK_EXT.test(file.name || '');
  if (!okMime || !okExt) {
    carrierAssistantAppendMessage('ai', escapeHtml('That file type isn’t supported. Attach a photo (PNG/JPG/WebP) or a PDF.'), 'dd-assistant-msg-error');
    return;
  }
  if (file.size > CARRIER_SMART_FILING.MAX_BYTES) {
    carrierAssistantAppendMessage('ai', escapeHtml('That file is over 10MB. Try a smaller photo or a single-page PDF.'), 'dd-assistant-msg-error');
    return;
  }
  const tenant = window.AppState && window.AppState.tenant;
  if (!tenant) return;

  const uploadId = ddSmartFilingUploadId();
  const safeName = (String(file.name || 'document').replace(/[^A-Za-z0-9._-]/g, '_').slice(0, 120)) || 'document';
  const path = `tenants/${tenant}/smartInbox/${uploadId}/${safeName}`;

  const thinkingEl = carrierAssistantAppendMessage('ai',
    `<span class="dd-assistant-thinking-dots"><span></span><span></span><span></span></span> Reading ${escapeHtml(safeName)}…`);

  try {
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file, { contentType: CARRIER_SMART_FILING.OK_MIME.includes(type) ? type : 'image/jpeg' });
    const callable = httpsCallable(functions, 'classifyCarrierUpload');
    const res = await callable({ uploadId });
    thinkingEl && thinkingEl.remove();
    ddSmartFilingRenderConfirmCard((res && res.data) || { uploadId });
    ddSmartFilingRefreshBadge();
  } catch (err) {
    thinkingEl && thinkingEl.remove();
    console.error('[smart-filing] classify failed', err);
    let msg = (err && err.message) || 'Upload failed';
    if (/permission/i.test(msg)) msg = 'You don’t have access to file documents here.';
    // TASK-9957 — show the server's refusal text (cap vs temporary); never invent "tomorrow".
    else if (/resource-exhausted/i.test(String(err?.code || '')) || /limit reached|resource-exhausted/i.test(msg)) msg = (err?.message && !/^(functions\/)?resource-exhausted$/i.test(String(err.message).trim())) ? err.message : 'AI limit reached or temporarily unavailable — try again later.';
    else if (/unavailable|temporarily/i.test(msg)) msg = 'Smart Tools is temporarily unavailable — try again shortly.';
    else msg = 'I couldn’t read that document. Try a clearer photo.';
    carrierAssistantAppendMessage('ai', escapeHtml(msg), 'dd-assistant-msg-error');
  }
}

// Render the in-chat confirm card. EVERY model/entity string is escHtml'd.
async function ddSmartFilingRenderConfirmCard(payload) {
  const body = carrierAssistantBodyEl();
  if (!body) return;
  const uploadId = payload && payload.uploadId;
  if (!uploadId) return;
  const cls = (payload && payload.classification) || {};
  CARRIER_SMART_FILING.cards[uploadId] = cls;

  const fileName = escapeHtml(payload.fileName || 'document');
  const family = cls.docFamily || '';
  const docType = cls.docType || '';
  const conf = Number(cls.confidence) || 0;
  const lowConf = conf < 0.6 || !family;

  const people = await ddSmartFilingGetPeople();
  const driverOpts = people
    .slice()
    .sort((a, b) => String(a.name).localeCompare(String(b.name)))
    .map(p => `<option value="${window.escAttr(p.id)}"${(cls.entity && cls.entity.personId === p.id) ? ' selected' : ''}>${escapeHtml(p.name || '(unnamed)')}</option>`)
    .join('');

  const docTypeOpts = [['medCard', 'Medical Card'], ['cdl', 'CDL'], ['mvr', 'MVR']]
    .map(([v, l]) => `<option value="${v}"${docType === v ? ' selected' : ''}>${escapeHtml(l)}</option>`).join('');
  const familyOpts = [['driver_credential', 'Driver credential (med card / CDL / MVR)'], ['receipt', 'Receipt / expense']]
    .map(([v, l]) => `<option value="${v}"${family === v ? ' selected' : ''}>${escapeHtml(l)}</option>`).join('');

  const proposedExpiry = window.escAttr(cls.expiryDate || '');
  const proposedAmount = (cls.amount != null && Number.isFinite(Number(cls.amount))) ? window.escAttr(String(cls.amount)) : '';
  const proposedVendor = window.escAttr(cls.vendor || '');

  // Summary line (escaped) — the human-readable "what I found".
  let summary;
  if (family === 'driver_credential') {
    const label = { medCard: 'Med card', cdl: 'CDL', mvr: 'MVR' }[docType] || 'Driver document';
    const who = (cls.entity && cls.entity.personName) ? escapeHtml(cls.entity.personName) : escapeHtml(cls.entityGuess || 'unknown driver');
    const when = cls.expiryDate ? escapeHtml(cls.expiryDate) : '—';
    summary = `${escapeHtml(label)} — ${who} — ${docType === 'mvr' ? 'pulled' : 'expires'} ${when}`;
  } else if (family === 'receipt') {
    const amt = (cls.amount != null) ? '$' + escapeHtml(String(cls.amount)) : '';
    summary = `Receipt${cls.vendor ? ' — ' + escapeHtml(cls.vendor) : ''}${amt ? ' — ' + amt : ''}`;
  } else {
    summary = 'I’m not sure what this is — pick a type below.';
  }

  const wrap = document.createElement('div');
  wrap.className = 'dd-assistant-msg dd-assistant-msg-ai dd-assistant-confirm-card';
  wrap.setAttribute('data-upload-id', uploadId);
  wrap.innerHTML = `
    <div class="dd-assistant-msg-bubble dd-sf-card">
      <div class="dd-sf-head"><span class="material-icons" style="font-size:16px;">description</span> <strong>Smart Filing</strong> <span class="dd-sf-file">${fileName}</span></div>
      <div class="dd-sf-summary">${summary}</div>
      <div class="dd-sf-fields">
        <label class="dd-sf-label">What is this?
          <select class="dd-sf-family" data-sf="family">${familyOpts}${family ? '' : '<option value="" selected>— choose —</option>'}</select>
        </label>
        <div class="dd-sf-cred" style="display:${family === 'receipt' ? 'none' : 'block'};">
          <label class="dd-sf-label">Type
            <select class="dd-sf-doctype" data-sf="docType">${docType ? '' : '<option value="" selected>— choose —</option>'}${docTypeOpts}</select>
          </label>
          <label class="dd-sf-label">Driver
            <select class="dd-sf-person" data-sf="personId"><option value="">— choose driver —</option>${driverOpts}</select>
          </label>
          <label class="dd-sf-label">Expiry / pull date
            <input type="date" class="dd-sf-expiry" data-sf="expiryDate" value="${proposedExpiry}" />
          </label>
        </div>
        <div class="dd-sf-receipt" style="display:${family === 'receipt' ? 'block' : 'none'};">
          <label class="dd-sf-label">Amount ($)
            <input type="number" step="0.01" min="0" class="dd-sf-amount" data-sf="amount" value="${proposedAmount}" placeholder="0.00" />
          </label>
          <label class="dd-sf-label">Vendor
            <input type="text" maxlength="120" class="dd-sf-vendor" data-sf="vendor" value="${proposedVendor}" placeholder="Merchant" />
          </label>
        </div>
      </div>
      <div class="dd-sf-actions">
        <button type="button" class="btn btn-primary dd-sf-confirm">Confirm &amp; File</button>
        <button type="button" class="btn btn-secondary dd-sf-dismiss">Dismiss</button>
      </div>
      <div class="dd-sf-note">Nothing is saved until you confirm.</div>
    </div>
  `;
  body.appendChild(wrap);
  body.scrollTop = body.scrollHeight;

  // Toggle field-sets when the family changes.
  const familySel = wrap.querySelector('.dd-sf-family');
  const credBox = wrap.querySelector('.dd-sf-cred');
  const receiptBox = wrap.querySelector('.dd-sf-receipt');
  if (familySel) {
    familySel.addEventListener('change', () => {
      const v = familySel.value;
      if (credBox) credBox.style.display = (v === 'driver_credential') ? 'block' : 'none';
      if (receiptBox) receiptBox.style.display = (v === 'receipt') ? 'block' : 'none';
    });
  }
  wrap.querySelector('.dd-sf-confirm').addEventListener('click', () => ddSmartFilingConfirm(uploadId, wrap));
  wrap.querySelector('.dd-sf-dismiss').addEventListener('click', () => { wrap.remove(); });
  if (lowConf) wrap.classList.add('dd-sf-lowconf');
}

async function ddSmartFilingConfirm(uploadId, wrap) {
  if (!wrap || wrap.getAttribute('data-filing') === '1') return;
  wrap.setAttribute('data-filing', '1'); // idempotency guard #1 — block double-click on the client
  const confirmBtn = wrap.querySelector('.dd-sf-confirm');
  if (confirmBtn) { confirmBtn.disabled = true; confirmBtn.textContent = 'Filing…'; }

  const get = (sel) => { const el = wrap.querySelector(sel); return el ? el.value : ''; };
  const family = get('.dd-sf-family');
  const args = { uploadId, docFamily: family };
  if (family === 'driver_credential') {
    args.docType = get('.dd-sf-doctype');
    args.personId = get('.dd-sf-person');
    args.expiryDate = get('.dd-sf-expiry') || null;
  } else if (family === 'receipt') {
    args.amount = parseFloat(get('.dd-sf-amount'));
    args.vendor = get('.dd-sf-vendor');
  }

  try {
    const callable = httpsCallable(functions, 'fileCarrierUpload');
    const res = await callable(args);
    const filed = (res && res.data) || {};
    let done;
    if (filed.kind === 'receipt') {
      done = `Filed a draft expense${filed.vendor ? ' for ' + escapeHtml(filed.vendor) : ''}${filed.amount != null ? ' ($' + escapeHtml(String(filed.amount)) + ')' : ''}. It’s waiting for approval in Billing.`;
    } else if (filed.kind === 'driver_credential') {
      const lbl = { medCard: 'Med card', cdl: 'CDL', mvr: 'MVR' }[filed.docType] || 'Document';
      done = `Filed ${escapeHtml(lbl)} for ${escapeHtml(filed.personName || 'the driver')}${filed.expiresAt ? ' (expires ' + escapeHtml(filed.expiresAt) + ')' : ''}.`;
    } else {
      done = 'Filed.';
    }
    wrap.remove();
    carrierAssistantAppendMessage('ai', `<span class="material-icons" style="font-size:14px;vertical-align:middle;color:#16a34a;">check_circle</span> ${done}`);
    delete CARRIER_SMART_FILING.cards[uploadId];
    ddSmartFilingRefreshBadge();
  } catch (err) {
    console.error('[smart-filing] file failed', err);
    wrap.setAttribute('data-filing', '0');
    if (confirmBtn) { confirmBtn.disabled = false; confirmBtn.innerHTML = 'Confirm &amp; File'; }
    let msg = (err && err.message) || 'Could not file that.';
    if (/pick a driver|pick a document|amount|enter the|date/i.test(msg)) { /* keep the specific message */ }
    else if (/permission/i.test(msg)) msg = 'You don’t have access to file this.';
    else if (/not found/i.test(msg)) msg = 'That upload expired — attach it again.';
    else msg = 'Could not file that — try again.';
    const errEl = wrap.querySelector('.dd-sf-note');
    if (errEl) { errEl.textContent = msg; errEl.style.color = '#dc2626'; }
  }
}

// TASK-9985 — ONE query for the badge count AND the cards the panel re-shows,
// so the number on the button is exactly the cards you see when you open it.
function ddSmartFilingPendingQuery(tenant) {
  return query(collection(db, 'tenants', tenant, 'smartInbox'), where('status', '==', 'pending_confirm'));
}

function ddSmartFilingBadgeLabel(n) {
  return n === 1
    ? '1 document is waiting for you to confirm where it gets filed'
    : `${n} documents are waiting for you to confirm where they get filed`;
}

// Closed-state label of the Ask DispatchDeck button: says what its badge counts.
function ddAssistantFabLabel(fab) {
  if (!fab) return;
  const n = Number(carrierAssistantState.pendingFilingCount) || 0;
  if (n > 0) {
    fab.setAttribute('aria-label', `Open Ask DispatchDeck. ${ddSmartFilingBadgeLabel(n)}.`);
    fab.title = `Ask DispatchDeck — ${ddSmartFilingBadgeLabel(n)}. Tap to see them.`;
  } else {
    fab.setAttribute('aria-label', 'Open Ask DispatchDeck');
    fab.title = 'Ask DispatchDeck';
  }
}

// Badge = count of pending confirm cards not yet filed. Also re-shows pending
// cards when the panel is (re)opened.
async function ddSmartFilingRefreshBadge() {
  const badge = document.getElementById('dd-assistant-badge');
  const tenant = window.AppState && window.AppState.tenant;
  if (!badge || !tenant) return;
  try {
    const snap = await getDocs(ddSmartFilingPendingQuery(tenant));
    if ((window.AppState && window.AppState.tenant) !== tenant) return null; // COD r1 — logged out / switched mid-read
    const n = snap.size;
    carrierAssistantState.pendingFilingCount = n;
    if (n > 0) {
      badge.textContent = String(n);
      badge.title = ddSmartFilingBadgeLabel(n);
      badge.style.display = 'flex';
    } else { badge.style.display = 'none'; badge.removeAttribute('title'); }
    const fab = document.getElementById('dd-assistant-fab');
    if (fab && !carrierAssistantState.open) ddAssistantFabLabel(fab);
    return snap;
  } catch (_) { return null; }
}

// Re-show pending confirm cards on panel open (deduped against cards already in the DOM).
async function ddSmartFilingLoadPending() {
  const body = carrierAssistantBodyEl();
  const tenant = window.AppState && window.AppState.tenant;
  if (!body || !tenant) return;
  let snap;
  try {
    snap = await getDocs(ddSmartFilingPendingQuery(tenant)); // TASK-9985 — same query as the badge
  } catch (_) { return; }
  if ((window.AppState && window.AppState.tenant) !== tenant) return; // TASK-9985 — logged out / switched mid-read
  snap.forEach((docSnap) => {
    const d = docSnap.data() || {};
    if (body.querySelector(`.dd-assistant-confirm-card[data-upload-id="${docSnap.id}"]`)) return; // already shown
    ddSmartFilingRenderConfirmCard({ uploadId: docSnap.id, fileName: d.fileName, classification: d.classification || {} });
  });
}
window.__ddSmartFilingRefreshBadge = ddSmartFilingRefreshBadge;

// Wire the file inputs + drag-drop once. Called from initCarrierAssistant.
function ddSmartFilingWireInputs() {
  const fileInput = document.getElementById('dd-assistant-file-input');
  const camInput = document.getElementById('dd-assistant-camera-input');
  if (fileInput && !fileInput.__wired) {
    fileInput.__wired = true;
    fileInput.addEventListener('change', () => { const f = fileInput.files && fileInput.files[0]; if (f) ddSmartFilingHandleFile(f); });
  }
  if (camInput && !camInput.__wired) {
    camInput.__wired = true;
    camInput.addEventListener('change', () => { const f = camInput.files && camInput.files[0]; if (f) ddSmartFilingHandleFile(f); });
  }
  const panel = document.getElementById('dd-assistant-panel');
  if (panel && !panel.__sfDrop) {
    panel.__sfDrop = true;
    panel.addEventListener('dragover', (e) => { e.preventDefault(); panel.classList.add('dd-sf-dragover'); });
    panel.addEventListener('dragleave', (e) => { if (e.target === panel) panel.classList.remove('dd-sf-dragover'); });
    panel.addEventListener('drop', (e) => {
      e.preventDefault();
      panel.classList.remove('dd-sf-dragover');
      const f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
      if (f) ddSmartFilingHandleFile(f);
    });
  }
}

// Show/hide the FAB based on eligibility; wire listeners once. Called from the
// post-auth UI setup (alongside applyPartnerSandboxUi) and idempotent.
function initCarrierAssistant() {
  // TASK-634: re-evaluate AI entry-point visibility on every tenant-load path
  // (this fn is already called at each one). Runs before the early-returns below
  // so it applies even for driver/ineligible sessions (harmless there).
  applySmartToolsVisibility();
  try {
    const fab = document.getElementById('dd-assistant-fab');
    if (!fab) return;
    // TASK-634: Smart Tools off also suppresses the assistant FAB (the CSS rule on
    // .ai-entry covers it, but the inline display set below would otherwise fight it).
    const smartToolsOff = window.AppState?.tenantData?.settings?.smartToolsEnabled === false;
    const eligible = carrierAssistantEligible() && !smartToolsOff;
    fab.style.display = eligible ? 'flex' : 'none';
    // Stack the lock/unlock FAB ABOVE the assistant FAB only when the assistant is
    // actually shown (both default to bottom-right 24/24 → the assistant would otherwise
    // sit on top of the lock button). CSS keys off this class so the lock stays in the
    // corner when the assistant is hidden (no floating gap).
    document.body.classList.toggle('has-assistant-fab', eligible);
    if (!eligible) {
      // Collapse the panel if a role change revoked access mid-session.
      if (carrierAssistantState.open) carrierAssistantToggle();
      return;
    }
    // TASK-982 — wire Smart Filing inputs + drag-drop + refresh the pending badge
    // on every eligible tenant-load (idempotent; runs before the init early-return).
    // TASK-4624 BINDING 2 — Smart Filing stays owner/admin (the callables WRITE
    // compliance docs/expenses and reject a non-admin server-side). Hide the
    // attach/camera buttons for a module-scoped member instead of leaving dead
    // buttons that toast a raw refusal. Cosmetic only; re-evaluated on every
    // tenant-load path, so a mid-session role change flips it both ways.
    const canSmartFile = carrierAssistantCanSmartFile();
    const attachBtn = document.getElementById('dd-assistant-attach-btn');
    const cameraBtn = document.getElementById('dd-assistant-camera-btn');
    if (attachBtn) attachBtn.style.display = canSmartFile ? '' : 'none';
    if (cameraBtn) cameraBtn.style.display = canSmartFile ? '' : 'none';
    // TASK-9622 — rate sheet → price book. Same audience as the price-book
    // write rule (owner, or billing/crm on a writable login); cosmetic only —
    // proposeRateSheetChanges re-checks on the server.
    const rsBtn = document.getElementById('dd-assistant-ratesheet-btn');
    if (rsBtn) rsBtn.style.display = carrierAssistantCanEditPriceBook() ? '' : 'none';
    const fpBtn = document.getElementById('dd-assistant-fleetphoto-btn');
    if (fpBtn) fpBtn.style.display = carrierAssistantCanEditFleet() ? '' : 'none';
    // TASK-9623 / TASK-9624 — file a contract, COI, vehicle doc or vendor bill.
    // Shown when this login can file at least one kind; cosmetic only —
    // readDocIntake / proposeDocIntake re-check the module on the server.
    const diBtn = document.getElementById('dd-assistant-docintake-btn');
    if (diBtn) diBtn.style.display = (window.DocIntake && window.DocIntake.canOpen('ask_dd')) ? '' : 'none';
    const inputForPh = document.getElementById('dd-assistant-input');
    if (inputForPh) inputForPh.placeholder = canSmartFile ? 'Ask, or attach a doc…' : 'Ask DispatchDeck...';
    if (canSmartFile) {
      try { ddSmartFilingWireInputs(); ddSmartFilingRefreshBadge(); } catch (_) { /* non-fatal */ }
    }
    // TASK-9268 — the "questions are saved" notice. Re-rendered on every
    // eligible tenant load (before the init early-return) so an account switch
    // in the same tab flips owner/member wording both ways.
    try { carrierAssistantRenderDisclosure(); } catch (_) { /* non-fatal */ }
    if (carrierAssistantState.initialized) return;
    carrierAssistantState.initialized = true;
    carrierAssistantRenderIntro();
    const input = document.getElementById('dd-assistant-input');
    if (input) {
      input.addEventListener('input', () => carrierAssistantAutosize(input));
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          carrierAssistantSendMessage();
        }
      });
    }
  } catch (e) {
    console.warn('[TASK-402] initCarrierAssistant failed (non-fatal):', e.message);
  }
}
window.initCarrierAssistant = initCarrierAssistant;

// TASK-634: When a tenant turns Smart Tools OFF (settings.smartToolsEnabled === false),
// the server gate (geminiGate.js) blocks every AI/model call with failed-precondition.
// Hide the AI entry-point UI entirely so a fleet manager who disabled AI doesn't click a
// dead button and get a red error toast — the feature is simply gone. Default ON: an absent
// or true flag shows everything as today. Body-class toggle + CSS rule (body.smart-tools-off
// .ai-entry { display:none !important }) keeps it to one switch + per-element tagging, and it
// re-applies live on the Settings toggle save (no reload). Driver-PIN/portal AI surfaces are
// already gated elsewhere; this only affects carrier-side .ai-entry elements.
function applySmartToolsVisibility() {
  try {
    const enabled = window.AppState?.tenantData?.settings?.smartToolsEnabled;
    // Off ONLY when explicitly false. Absent/true/anything-else => on (default ON).
    document.body.classList.toggle('smart-tools-off', enabled === false);
  } catch (e) {
    console.warn('[TASK-634] applySmartToolsVisibility failed (non-fatal):', e.message);
  }
}
window.applySmartToolsVisibility = applySmartToolsVisibility;

// TASK-1348 — nav baseline reset, so hideUpgradeBadges() is IDEMPOTENT across
// account switches in the same tab. The member branch below stamps .hidden on
// every non-granted nav row; handleLogout() resets AppState in place WITHOUT a
// page reload, so those stamps survived into the next login and an owner signing
// in after a restricted member lost Dispatch/Roster/Maintenance/CRM (and
// Settings) until a hard refresh. The owner path only ever un-hides
// billing/workforce/hr/bi/compliance — it assumes the core rows are still
// pristine from index.html. This restores them. Hardcoded list on purpose:
// never derive nav identity from user-influenced input.
const _CORE_NAV_VIEWS = ['dispatch', 'pretrip', 'maintenance', 'roster', 'crm'];
function resetCoreNavBaseline() {
  document.querySelectorAll('#main-nav [data-view], #mobile-nav [data-view], .mobile-more-sheet [data-view]').forEach(el => {
    if (!_CORE_NAV_VIEWS.includes(el.getAttribute('data-view'))) return;
    // .locked too — otherwise a compliance-only → full-account switch leaves
    // the core rows locked (setupComplianceOnlySidebar stamps them locked).
    el.classList.remove('hidden', 'locked');
  });
  // TASK-9779 — undo the 811-only "811 Permits" relabel of the Dispatch row
  // (hideUpgradeBadges): logout -> another login does not reload the page.
  document.querySelectorAll('[data-view="dispatch"] [data-label811-orig]').forEach(lbl => {
    lbl.textContent = lbl.dataset.label811Orig;
  });
  // The member branch hides both Settings entries and nothing brings them back.
  // Two different hide mechanisms are in play: the member branch adds .hidden,
  // setupComplianceOnlySidebar() sets inline style.display='none' (:7710) —
  // clear BOTH, or a compliance-only → owner switch still loses Settings
  // (COD Code Reviewer catch).
  ['btn-settings', 'mobile-settings-btn'].forEach(id => {
    const el = document.getElementById(id);
    if (!el) return;
    el.classList.remove('hidden');
    if (el.style.display === 'none') el.style.display = '';
  });
}
window.resetCoreNavBaseline = resetCoreNavBaseline;

// Account nav entry (sidebar footer #btn-account + More-sheet #mobile-account-btn).
// Both default to .hidden in index.html and are revealed ONLY from
// hideUpgradeBadges() — owner/legacy-admin path right after resetCoreNavBaseline,
// member path explicitly (the footer sits outside #main-nav, so the member
// data-view sweep never reaches it). Logout and driver-PIN / public sessions hide
// them again — a logout does not reload the page, and the next login in this tab
// may be a driver PIN whose More sheet must not offer Account. Deliberately NOT
// in resetCoreNavBaseline(): that also runs on logout, where it would re-show it.
const _ACCOUNT_NAV_IDS = ['btn-account', 'mobile-account-btn'];
function setAccountNavVisible(show) {
  _ACCOUNT_NAV_IDS.forEach(id => {
    const el = document.getElementById(id);
    if (!el) return;
    el.classList.toggle('hidden', !show);
    el.style.display = show ? '' : 'none';
  });
}

// Hide upgrade badges and configure nav based on actual subscription tier
// Tiers: Dispatch (base) → Pro (clockbilling+crm) → Pro+ (hr+bi)
function hideUpgradeBadges() {
  // Driver-side sessions (PIN login, public access tokens) get a restricted
  // sidebar — never reveal admin-only modules (HR, BI, Compliance) to a driver.
  // The nav items default to .hidden in index.html; skipping this function
  // leaves them hidden. (DOT Advisor lives inside Compliance now.)
  if (window.AppState?.driverModePin ||
      window.AppState?.driverMode ||
      window.AppState?.isPublicAccess) {
    setAccountNavVisible(false);
    return;
  }

  // TASK-319 multi-user member gate — members/{uid}.modules is ABSOLUTE
  // source of truth. Skip tenant-PIN gating entirely; show ONLY granted
  // modules. Owners + legacy admins (no modules field) fall through to
  // the standard PIN/add-on logic below for full access.
  const memberAccess = window.AppState?.memberAccess;
  if (memberAccess && memberAccess.status === 'active'
      && memberAccess.role !== 'owner'
      && Array.isArray(memberAccess.modules)
      && memberAccess.modules.length > 0) {
    const granted = new Set(memberAccess.modules);
    // TASK-9779 — an 811-only login's window lives in the Dispatch view: show
    // that row, labelled "811 Permits" (restored for any other login).
    const only811 = isMember811Only(memberAccess);
    if (only811) granted.add('dispatch');
    document.querySelectorAll('#main-nav [data-view="dispatch"] .nav-label, #mobile-nav [data-view="dispatch"] .mobile-nav-label, .mobile-more-sheet [data-view="dispatch"] .mobile-nav-label').forEach(lbl => {
      if (!lbl.dataset.label811Orig) lbl.dataset.label811Orig = lbl.textContent;
      // TASK-9994 — the Locates name only while the company has 811 on.
      const on811 = window.AppState?.settings?.enable811 === true || window.AppState?.isDemo === true;
      lbl.textContent = only811 && on811 ? (lbl.classList.contains('nav-label') ? 'LOCATES (811)' : 'Locates (811)') : lbl.dataset.label811Orig; // TASK-9862 rename
    });
    // Pretrip + DVIR are alwaysAccessible per checkModuleAccess; mirror in sidebar.
    // TASK-1097 — EXCEPT compliance-only tenants: the driver-board force-grant
    // doesn't apply there (their sidebar is Compliance-only). Fail OPEN to the
    // historical always-grant if the signal is unavailable.
    if (!(typeof isComplianceOnlyTenant === 'function' && isComplianceOnlyTenant())) {
      granted.add('pretrip');
      granted.add('dvir');
    }
    // TASK-355 2026-05-22: My Today killed; jobboard is now member-aware.
    // Account settings always available to members.
    granted.add('account');
    // Three nav surfaces: #main-nav (desktop sidebar), #mobile-nav (mobile
    // bottom-nav on iOS TWA + responsive mobile), .mobile-more-sheet (overflow
    // sheet). All three need the same member-gate. Test Chris on iOS cold-load
    // hit "No access - Dispatch" because the bottom-nav's Dispatch tile wasn't
    // filtered — added #mobile-nav to the selector 2026-05-22.
    document.querySelectorAll('#main-nav [data-view], #mobile-nav [data-view], .mobile-more-sheet [data-view]').forEach(el => {
      const view = el.getAttribute('data-view');
      if (granted.has(view)) {
        el.classList.remove('hidden', 'locked');
        // Hide upgrade badge child element — present on HR/BI/Compliance even
        // when granted (Test Chris on mobile More-sheet showed UPGRADE badge
        // on HR despite his memberAccess.modules including 'hr'). Badge is
        // a child span, not affected by the .hidden class above.
        el.querySelectorAll('.nav-upgrade-badge').forEach(b => { b.style.display = 'none'; });
      } else {
        el.classList.add('hidden');
      }
    });
    // TASK-319 security: Settings modal exposes admin-only config (Team Access,
    // Access PINs, Subscription, Integrations, Danger Zone). Non-owner members
    // should NOT see it. Hide BOTH desktop + mobile entries (Test Chris on iOS
    // showed Settings in mobile More-sheet despite the desktop fix). Owners +
    // legacy admins fall through to the standard sidebar render.
    // TASK-9389 (admin backfill lane) — EXCEPT a member the owner gave a switch
    // ("Can manage logins" and/or "Can change <module> settings"): they get the
    // Settings entry, and openSettingsModal shows them only what the switch
    // covers (memberSettingsScope). Never on a compliance-only tenant.
    let _mScope = null;
    try { _mScope = typeof window.memberSettingsScope === 'function' ? window.memberSettingsScope() : null; } catch (_) { _mScope = null; }
    const _showMemberSettings = !!(_mScope && _mScope.any) && !(typeof isComplianceOnlyTenant === 'function' && isComplianceOnlyTenant());
    const btnSettings = document.getElementById('btn-settings');
    if (btnSettings) btnSettings.classList.toggle('hidden', !_showMemberSettings);
    const mobileSettings = document.getElementById('mobile-settings-btn');
    if (mobileSettings) mobileSettings.classList.toggle('hidden', !_showMemberSettings);
    // ...but Account (its own nav entry) stays reachable: 'account' is granted
    // above, and account.js hides the owner-only plan/billing/SMS sections for a
    // non-owner. Connect Claude is the part a member needs. NOT on a
    // compliance-only tenant: this branch returns before the compliance
    // sidebar, and there Account is advisor-managed like Settings (COD r2).
    setAccountNavVisible(!(typeof isComplianceOnlyTenant === 'function' && isComplianceOnlyTenant()));
    // TASK-9220 (D) — the PERSONAL section used to be populated only at the END
    // of this function, after this branch's `return`, so every member with
    // explicit modules got no PERSONAL section at all. Populate it here, AFTER
    // the Settings hiding above (that return stays — it is the scoped-member
    // nav gate). Not on a compliance-only tenant: that sidebar is Compliance-only.
    if (!(typeof isComplianceOnlyTenant === 'function' && isComplianceOnlyTenant())) {
      populatePersonalActionsSection();
    }
    return;
  }

  // TASK-1348 — clear any nav stamps left by a PREVIOUS account in this tab
  // before the owner/legacy-admin render below (which only un-hides the
  // add-on rows). Safe ahead of the compliance-only branch: that path
  // re-stamps these same rows synchronously in the same tick.
  resetCoreNavBaseline();
  // Demo hides it too (initDemoTrialCtas, which runs later on a timer) — never
  // reveal it there even briefly (COD r2). Compliance-only re-hides below.
  setAccountNavVisible(!window.AppState?.isDemo);

  // Compliance-only tenants: special sidebar config
  if (isComplianceOnlyTenant()) {
    setupComplianceOnlySidebar();
    return;
  }

  const hasClockBilling = hasAddOnPackage('clockbilling');
  const hasCrm = hasAddOnPackage('crm');
  const hasHr = hasAddOnPackage('hr');

  // Auto-expand primary PIN to include purchased add-on modules
  const accessPins = accessPinMap();
  const primaryPinKey = Object.keys(accessPins).find(k => accessPins[k].isPrimary);
  if (primaryPinKey) {
    const primaryPin = accessPins[primaryPinKey];
    const currentModules = new Set(primaryPin.modules || []);
    let expanded = false;
    if (hasClockBilling) {
      ['clock', 'billing', 'workforce', 'hr'].forEach(m => { if (!currentModules.has(m)) { currentModules.add(m); expanded = true; } });
    }
    if (hasCrm) {
      if (!currentModules.has('crm')) { currentModules.add('crm'); expanded = true; }
    }
    if (hasHr) {
      ['bi', 'compliance'].forEach(m => { if (!currentModules.has(m)) { currentModules.add(m); expanded = true; } });
    }
    // TASK-815 (Chris 2026-06-25) — REVERSED. `hr-directory` is now the REAL
    // grant for SSN/W-4/I-9 (peopleTax). `hr` alone must NOT auto-add
    // hr-directory: granting plain HR = NO SSN (the SAFE DEFAULT). hr-directory
    // is a DELIBERATE, separate grant. This reverses the old auto-ride that
    // silently escalated every HR grant to SSN visibility (the COD-flagged
    // dangerous default). Direction now: hr-directory REQUIRES hr (enforced in
    // the PIN modal + roster staff-login grid), but hr never forces directory.
    if (expanded) {
      primaryPin.modules = [...currentModules];
      // Persist to Firestore (non-blocking)
      //
      // 🔴 TASK-9279 (COD round 3, Blast-Radius lane) — this WRITE CAN RESURRECT A
      // REVOKED PIN, and only since revocation started working. `primaryPinKey`
      // comes from `window.AppState.settings` a few lines up, which is a cached
      // copy. If the Primary PIN was RENAMED or DELETED between that read and this
      // write, a dotted-path `updateDoc` CREATES the missing key rather than
      // failing: `settings.accessPins.<oldKey>` reappears holding `{ modules: [...] }`
      // with no `isPrimary` and no `label`. submitPinChallenge looks up
      // `accessPins[pin]`, finds that object, and pinGrantsModule reads its
      // `modules` — so the revoked code authenticates again with the primary's
      // whole module set. Before this task the primary key could never disappear
      // (that WAS the bug), so this could only ever update a key that existed.
      // A transaction re-reads and simply does nothing if the key is gone or is no
      // longer the primary. `expanded` is only true until the add-on modules have
      // been persisted once, so this is rare, not a per-render cost.
      const tenant = window.AppState?.tenant;
      if (tenant) {
        const _tref = doc(db, 'tenants', tenant);
        // 🔴 COD round 4 (Code Reviewer) — re-derive the UNION from the LIVE module
        // list inside the transaction rather than writing the cached array built
        // from AppState. Writing the cached copy would REVERT a module another
        // admin removed in the window, and one of these is `hr-directory` — the
        // deliberate SSN / peopleTax grant (TASK-815). Silently re-granting that
        // is the same class of "a stale write undoes a deliberate change" this
        // task exists to stop. Adding only what is missing is idempotent.
        const _addOns = [];
        if (hasClockBilling) _addOns.push('clock', 'billing', 'workforce', 'hr');
        if (hasCrm) _addOns.push('crm');
        if (hasHr) _addOns.push('bi', 'compliance');
        runTransaction(db, async (tx) => {
          const snap = await tx.get(_tref);
          const _ls = (snap.exists() && snap.data()?.settings) || {};
          const live = accessPinMap(_ls); // TASK-9858 — meta (ids) or the legacy map
          const _pinField = live === _ls.accessPinMeta ? 'accessPinMeta' : 'accessPins';
          if (!live[primaryPinKey] || live[primaryPinKey].isPrimary !== true) return;
          const liveModules = Array.isArray(live[primaryPinKey].modules) ? live[primaryPinKey].modules : [];
          const merged = [...new Set([...liveModules, ..._addOns])];
          if (merged.length === liveModules.length) return; // nothing to add
          tx.update(_tref, new FieldPath('settings', _pinField, primaryPinKey, 'modules'), merged);
        }).catch(e => console.warn('[Settings] PIN module sync failed:', e.message));
      }
      // Update localStorage if this PIN is stored
      try {
        const stored = localStorage.getItem(`dispatchdeck_pin_${window.AppState?.tenant}`);
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed.pin === primaryPinKey) {
            parsed.modules = primaryPin.modules;
            localStorage.setItem(`dispatchdeck_pin_${window.AppState?.tenant}`, JSON.stringify(parsed));
          }
        }
      } catch (e) { /* ignore */ }
    }
  }

  // Always hide Clock nav item — it's accessed via Billing tab, not a separate module
  document.querySelectorAll('#main-nav [data-view="clock"], .mobile-more-sheet [data-view="clock"]').forEach(el => {
    el.classList.add('hidden');
  });

  // Helper: lock or unlock a nav item
  function setNavLocked(view, locked) {
    document.querySelectorAll(`[data-view="${view}"]`).forEach(el => {
      if (locked) {
        el.classList.add('locked');
        el.querySelectorAll('.nav-upgrade-badge').forEach(badge => { badge.style.display = ''; });
      } else {
        el.classList.remove('locked');
        el.querySelectorAll('.nav-upgrade-badge').forEach(badge => { badge.style.display = 'none'; });
      }
    });
  }

  // Single plan — unlock all modules for everyone
  ['billing', 'workforce'].forEach(view => {
    document.querySelectorAll(`[data-view="${view}"]`).forEach(el => { el.classList.remove('hidden'); });
    setNavLocked(view, false);
  });
  document.querySelectorAll(`#main-nav [data-view="billing"] .nav-label`).forEach(el => { el.textContent = 'AR / AP'; });
  document.querySelectorAll(`.mobile-more-sheet [data-view="billing"] .mobile-more-label`).forEach(el => { el.textContent = 'AR / AP'; });

  setNavLocked('crm', false);

  setNavLocked('hr', false);
  document.querySelectorAll(`#main-nav [data-view="hr"], .mobile-more-sheet [data-view="hr"]`).forEach(el => {
    el.classList.remove('hidden');
  });

  setNavLocked('bi', false);
  document.querySelectorAll(`#main-nav [data-view="bi"], .mobile-more-sheet [data-view="bi"]`).forEach(el => {
    el.classList.remove('hidden');
  });

  setNavLocked('compliance', false);
  document.querySelectorAll(`#main-nav [data-view="compliance"], .mobile-more-sheet [data-view="compliance"]`).forEach(el => {
    el.classList.remove('hidden');
  });

  // TASK-1066 — DD Payroll is no longer a standalone nav item; it lives as a
  // Workforce sub-tab (clock.js), gated on PayrollModule.isPayrollAdmin. Nothing
  // to reveal here. The #/payroll route still resolves for deep links.

  // DOT Advisor moved into the Compliance module as a tab — no standalone nav
  // entry to reveal anymore. The #/dot-advisor route still resolves so external
  // deep-links (marketing pages, FAQ, partner portal) keep working.

  // Founder, Demo, or Trial: also remove all remaining badges + coming-soon attributes
  const trialCheck = getTrialStatus();
  if (isFounderAccount() || window.AppState?.isDemo || trialCheck.status === 'trialing') {
    document.querySelectorAll('.nav-upgrade-badge').forEach(badge => { badge.style.display = 'none'; });
    document.querySelectorAll('[data-coming-soon]').forEach(el => { el.removeAttribute('data-coming-soon'); });
    // Demo + Trial: unlock all nav items so users can explore everything
    if (window.AppState?.isDemo || trialCheck.status === 'trialing') {
      ['billing', 'workforce', 'crm', 'hr', 'bi', 'compliance'].forEach(view => setNavLocked(view, false));
    }
  }

  // TASK-346 reframe (2026-05-21) — the dedicated "My Crew" sidebar entry +
  // sub-view were killed. Supervisor employee-notes now surface as a button
  // under HR module Actions (window.HRModule.openAddEmployeeNoteModal). The
  // sidebar-gate / openMyCrewFromNav helpers were removed with the entries.
  // AppState.hasDirectReports remains computed in loadUserTenant() above so
  // both the HR Actions gate AND Worker R's driver-portal Team-note tile
  // (TASK-347 Slice 3) can read it.

  // TASK-348 — populate the PERSONAL actions sidebar mini-section.
  populatePersonalActionsSection();

  // TASK-355 2026-05-22: applyMyTodayNavGate() and the My Today sidebar entry
  // were killed per unanimous COD verdict. Job Board (jobboard.js, member-aware)
  // is now the canonical personId-scoped daily view for SPA-signed-in members.

  initComingSoonNav();
}

// ============================================
// TASK-348 — Personal Actions sidebar mini-section
// ============================================
// Surface: sidebar PERSONAL group above the sidebar-footer (COD-locked Option D,
// unanimous 5-of-5 in cod-reviews/2026-05-21T21-45_task-348-spa-admin-personal-actions-surface-decision-co.md).
// Mirror in #mobile-more-menu.
// Empty state: when memberAccess.personId is null, renders a single hint linking
// to Settings -> Team Access (E2, 4-of-5 — Skeptic dissented for silent hide;
// UX+CodeReviewer+Independent prefer actionable fix path).
//
// Per-action wrapper rules (Architect locked):
//   1) NEVER mutate AppState.driverPersonId to "trick" the driver module.
//   2) Pass personId/personName as explicit args into refactored handlers.
//   3) No static cross-module imports (cache-buster Pitfall #1) -- only
//      window.JobBoardModule.* / window.DispatchModule.* / window.ClockModule.*.
//   4) Validate memberAccess.personId !== null before invoking any handler.
function populatePersonalActionsSection() {
  const section = document.getElementById('personal-actions-section');
  const body = document.getElementById('personal-actions-body');
  const sectionMobile = document.getElementById('personal-actions-section-mobile');
  const bodyMobile = document.getElementById('personal-actions-body-mobile');
  if (!section || !body || !sectionMobile || !bodyMobile) return;

  // Driver-PIN / public-access sessions get TASK-347's driver-portal bottom nav
  // for the same actions; the SPA sidebar isn't rendered in PIN mode anyway,
  // but keep this guard for safety.
  if (window.AppState?.driverModePin || window.AppState?.driverMode || window.AppState?.isPublicAccess) {
    section.classList.add('hidden');
    sectionMobile.classList.add('hidden');
    return;
  }

  // Only render when the user has a member doc (TASK-319 signed-in identity).
  // Legacy single-login owner accounts also get the section (memberAccess may
  // be missing if they predate per-user logins, but they're always personId-less
  // in that case -- show the empty-state hint).
  const memberAccess = window.AppState?.memberAccess;
  const signedInUid = window.AppState?.user?.uid;
  if (!signedInUid) {
    section.classList.add('hidden');
    sectionMobile.classList.add('hidden');
    return;
  }

  const personId = memberAccess?.personId || null;
  const personName = (memberAccess && memberAccess.personId)
    ? (window.AppState?.tenantData?.ownerName || window.AppState?.user?.displayName || '')
    : '';

  // Empty state: only the staff/dispatcher branch is shown — they need
  // their admin to link their roster profile to get personal actions.
  // Owners/admins (including legacy single-login accounts with no memberAccess)
  // get the section hidden entirely; they don't need this surface (they can
  // self-serve clock/expense from dispatch directly).
  if (!personId) {
    const role = memberAccess?.role;
    const isOwnerOrAdmin = !memberAccess || role === 'owner' || role === 'admin';
    // TASK-1489 — an INTENTIONALLY personless member (outside compliance manager,
    // bookkeeper, agent) has no roster profile BY DESIGN. "Ask your admin to link
    // your roster profile" is wrong for them — there is nothing to link, and the
    // hint reads as a broken account. Hide the section entirely, same as an owner.
    // An employee whose login merely was never linked (personless !== true) still
    // gets the actionable hint below, unchanged.
    if (isOwnerOrAdmin || memberAccess?.personless === true) {
      section.classList.add('hidden');
      sectionMobile.classList.add('hidden');
      return;
    }
    body.innerHTML = `<div class="personal-actions-hint">Ask your admin to link your roster profile in Settings so personal actions show up here.</div>`;
    bodyMobile.innerHTML = `<div class="personal-actions-hint-mobile">Ask your admin to link your roster profile in Settings so personal actions show up here.</div>`;
    section.classList.remove('hidden');
    sectionMobile.classList.remove('hidden');
    _applyPersonalActionsCollapsed();
    return;
  }

  // Personal Actions tile menu. Each tile:
  //   - data-action: handler name on window.PersonalActions.*
  //   - feature-flag gates apply where settings disable underlying features.
  const driverExpensesEnabled = window.AppState?.settings?.driverExpenses?.enabled !== false;
  const safetyMeetingEnabled = window.AppState?.settings?.safetyMeetingSignInEnabled === true;

  // Resolved personName from roster lookup if available (for friendly Time Off
  // pre-fill). Falls back to user.displayName / email-prefix.
  const resolvedName = _resolvePersonalActionName(personId);

  // UX taste flag (COD): distinct icon for Personal Clock vs the Clock module
  // nav-item (which uses 'schedule'). Use 'login'/'logout' for Clock In/Out.
  // TASK-9220 — the Clock tile IS the Clock view (one clock), so it shows only
  // when this login can open that view. A member whose modules leave out
  // `clock` gets no tile rather than a tile that lands on "No access". Before
  // TASK-9220 such members never saw this section at all (see hideUpgradeBadges).
  const tiles = [];
  if (checkModuleAccess('clock', true) !== false) {
    // TASK-9818 — the tile is "Time Clock" (decision 2026-10-13): it opens the Clock
    // screen, it does not clock you in. Internal id 'clock' / togglePersonalClock unchanged.
    tiles.push({ id: 'clock', icon: 'login', label: 'TIME CLOCK', labelMobile: 'Time Clock', action: 'togglePersonalClock' });
  }
  tiles.push(
    { id: 'switch', icon: 'swap_horiz', label: 'SWITCH TICKET', labelMobile: 'Switch Ticket', action: 'switchPersonalTicket' },
    { id: 'pretrip', icon: 'checklist', label: 'PRE-TRIP / DVIR', labelMobile: 'Pre-trip / DVIR', action: 'goPersonalPretrip' },
  );
  if (driverExpensesEnabled) {
    tiles.push({ id: 'expense', icon: 'receipt', label: 'RECORD EXPENSE', labelMobile: 'Record Expense', action: 'openPersonalExpense' });
  }
  tiles.push({ id: 'timeoff', icon: 'event_busy', label: 'TIME OFF', labelMobile: 'Time Off', action: 'openPersonalTimeOff' });
  if (safetyMeetingEnabled) {
    tiles.push({ id: 'jsa', icon: 'how_to_reg', label: 'SAFETY SIGN-IN', labelMobile: 'Safety Sign-In', action: 'openPersonalSafetyMeeting' });
  }

  body.innerHTML = tiles.map(t => `
    <a href="#" class="nav-item nav-item-personal" data-personal-action="${t.id}"
       onclick="event.preventDefault();window.PersonalActions?.${t.action}?.()">
      <span class="material-icons">${t.icon}</span>
      <span class="nav-label">${t.label}</span>
    </a>
  `).join('');

  bodyMobile.innerHTML = tiles.map(t => `
    <button class="mobile-more-item" data-personal-action="${t.id}"
       onclick="document.getElementById('mobile-more-menu')?.classList.add('hidden');window.PersonalActions?.${t.action}?.()">
      <span class="material-icons">${t.icon}</span>
      <span class="mobile-more-label">${t.labelMobile}</span>
    </button>
  `).join('');

  section.classList.remove('hidden');
  sectionMobile.classList.remove('hidden');
  _applyPersonalActionsCollapsed();

  // Keep the Clock In/Out tile label fresh based on the user's open clockEntry.
  _refreshPersonalClockLabel();

  // Stash resolved name for handlers that need it (Time Off pre-fill).
  window.AppState._personalActionsName = resolvedName;
}

// TASK-348 — sidebar PERSONAL section is collapsible and COLLAPSED BY DEFAULT
// (Chris 2026-09-14). Preference is per-browser; an unset key means collapsed,
// so first-run and cleared-storage both land on the compact sidebar. Storage can
// throw (private window / blocked site data) — treat any failure as the default.
//
// TASK-9245 — the mobile More sheet's PERSONAL mirror is collapsible too, also
// collapsed by default (Chris 2026-09-15). It uses its OWN key on purpose: a
// shared key would hand a phone the '0' a user already stored by expanding the
// desktop sidebar, and the sheet would open expanded — the exact complaint.
// Each surface also keeps its state in memory once toggled, so when storage
// throws the section still collapses again on the next tap (the old toggle
// re-read storage to pick the next state, so with storage blocked every tap
// computed "expand" and it could never be closed again).
const PERSONAL_ACTIONS_COLLAPSED_KEY = 'dd.personalActions.collapsed';
const PERSONAL_ACTIONS_MOBILE_COLLAPSED_KEY = 'dd.personalActionsMobile.collapsed';
const _PERSONAL_ACTIONS_SURFACES = {
  desktop: { key: PERSONAL_ACTIONS_COLLAPSED_KEY, section: 'personal-actions-section', toggle: 'personal-actions-toggle' },
  mobile: { key: PERSONAL_ACTIONS_MOBILE_COLLAPSED_KEY, section: 'personal-actions-section-mobile', toggle: 'personal-actions-toggle-mobile' },
};
const _personalActionsCollapsedMem = { desktop: null, mobile: null };

function _isPersonalActionsCollapsed(surface = 'desktop') {
  const s = _PERSONAL_ACTIONS_SURFACES[surface] ? surface : 'desktop';
  if (typeof _personalActionsCollapsedMem[s] === 'boolean') return _personalActionsCollapsedMem[s];
  try {
    return localStorage.getItem(_PERSONAL_ACTIONS_SURFACES[s].key) !== '0';
  } catch (e) {
    return true;
  }
}

function _paintPersonalActionsCollapsed(surface, collapsed) {
  const cfg = _PERSONAL_ACTIONS_SURFACES[surface];
  const section = document.getElementById(cfg.section);
  const toggle = document.getElementById(cfg.toggle);
  if (section) section.classList.toggle('collapsed', collapsed);
  if (toggle) toggle.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
}

function _applyPersonalActionsCollapsed() {
  for (const surface of Object.keys(_PERSONAL_ACTIONS_SURFACES)) {
    _paintPersonalActionsCollapsed(surface, _isPersonalActionsCollapsed(surface));
  }
}

function togglePersonalActionsSection(surface = 'desktop') {
  const s = _PERSONAL_ACTIONS_SURFACES[surface] ? surface : 'desktop';
  const next = !_isPersonalActionsCollapsed(s);
  _personalActionsCollapsedMem[s] = next;
  try {
    localStorage.setItem(_PERSONAL_ACTIONS_SURFACES[s].key, next ? '1' : '0');
  } catch (e) { /* ignore — the in-memory state above still governs this page */ }
  _paintPersonalActionsCollapsed(s, next);
}
window.togglePersonalActionsSection = togglePersonalActionsSection;

// Resolve a friendly name for the SPA-signed-in person from the roster (people
// subcollection cached on Dispatch / Roster state if loaded), fall back to
// display name / email prefix. One-shot, sync read; no Firestore call here.
function _resolvePersonalActionName(personId) {
  if (!personId) return '';
  try {
    const dispPeople = window.DispatchState?.people || [];
    const rosterPeople = window.RosterState?.people || [];
    const all = [...dispPeople, ...rosterPeople];
    const match = all.find(p => p?.id === personId);
    if (match?.name) return match.name;
  } catch (e) { /* ignore */ }
  return window.AppState?.user?.displayName
    || (window.AppState?.user?.email ? window.AppState.user.email.split('@')[0] : '')
    || '';
}

// Flip the Personal Clock In/Out tile label based on whether the user has an
// open clockEntry.
// fix-1999-followups — it used to read window.DispatchState._clockEntries, which
// is NOT dispatch.js's state (that module never publishes it): it is the one-shot
// seed togglePersonalClock writes once and never updates, so after a clock-out
// away from Dispatch the tile kept saying CLOCK OUT. Now:
//   - a caller holding LIVE entries passes them (dispatch.js's clockEntries
//     snapshot does) and the label follows them;
//   - with no argument (sidebar render, after a Personal Actions tap, after the
//     board's clock-in / clock-out write) it reads this person's own active
//     entries from Firestore — the same self-scoped query togglePersonalClock
//     uses (TASK-452). A failed read leaves the label as it is.
let _personalClockLabelSeq = 0;
function _refreshPersonalClockLabel(liveEntries) {
  const tile = document.querySelector('#personal-actions-body [data-personal-action="clock"]');
  const tileMobile = document.querySelector('#personal-actions-body-mobile [data-personal-action="clock"]');
  if (!tile && !tileMobile) return;
  const personId = window.AppState?.memberAccess?.personId;
  if (!personId) return;
  const seq = ++_personalClockLabelSeq; // a newer refresh always wins over a slower read
  if (Array.isArray(liveEntries)) {
    _paintPersonalClockLabel(tile, tileMobile, liveEntries.some(e => e && e.personId === personId && e.status === 'active'));
    return;
  }
  const tenantId = window.AppState?.tenant;
  if (!tenantId) return;
  getDocs(query(
    collection(db, 'tenants', tenantId, 'clockEntries'),
    where('personId', '==', personId),
    where('status', '==', 'active')
  )).then((snap) => {
    if (seq !== _personalClockLabelSeq) return;
    if (window.AppState?.tenant !== tenantId || window.AppState?.memberAccess?.personId !== personId) return;
    _paintPersonalClockLabel(
      document.querySelector('#personal-actions-body [data-personal-action="clock"]'),
      document.querySelector('#personal-actions-body-mobile [data-personal-action="clock"]'),
      !snap.empty
    );
  }).catch((e) => console.warn('[PersonalActions] clock label read failed:', e?.message));
}

// TASK-9220 — three states, same rule as the Clock view's own button (clock.js
// getClockTabHTML: `shouldBuildTickets() ? 'Build Ticket' : …`), because the
// tile now opens that view: clocked in on a ticket-building tenant → BUILD
// TICKET; clocked in otherwise → CLOCK OUT; not clocked in → CLOCK IN.
// TASK-9818 — the NAME is always "Time Clock" (the tile opens the Clock screen;
// the screen's own button says Clock In / Clock Out / Build Ticket). The ICON
// still shows the state so a glance tells you whether you're on the clock.
function _personalClockLabelState(active, buildsTickets) {
  const label = 'TIME CLOCK', labelMobile = 'Time Clock';
  if (active && buildsTickets) return { label, labelMobile, icon: 'receipt_long' };
  if (active) return { label, labelMobile, icon: 'logout' };
  return { label, labelMobile, icon: 'login' };
}

function _paintPersonalClockLabel(tile, tileMobile, active) {
  const { label, labelMobile, icon } = _personalClockLabelState(active, shouldBuildTickets(window.AppState?.memberAccess?.personId || null)); // TASK-9553
  if (tile) {
    const lbl = tile.querySelector('.nav-label');
    const ic = tile.querySelector('.material-icons');
    if (lbl) lbl.textContent = label;
    if (ic) ic.textContent = icon;
  }
  if (tileMobile) {
    const lbl = tileMobile.querySelector('.mobile-more-label');
    const ic = tileMobile.querySelector('.material-icons');
    if (lbl) lbl.textContent = labelMobile;
    if (ic) ic.textContent = icon;
  }
}
window._refreshPersonalClockLabel = _refreshPersonalClockLabel;

// Stub helper for the empty-state hint. Settings -> Team Access is the
// member/invite management UI; for now we navigate to settings root so admins
// can find it. (When TASK-319 Slice 5/6 ships a dedicated Team Access tab,
// swap to that anchor.)
window.openTeamAccessSettings = function () {
  // TASK-9753 — Settings now has a Team & Access section; land on it.
  if (typeof window.openSettingsModal === 'function') {
    window.openSettingsModal('team');
  } else {
    window.showToast?.('Open Settings to manage Team Access.', 'info');
  }
};

// ============================================
// TASK-348 — Personal Action handlers (window.PersonalActions.*)
// ============================================
// All handlers pull personId from memberAccess (NOT driverPersonId, which is
// PIN-only and not populated for SPA staff logins).
window.PersonalActions = {
  // TASK-9220 — ONE clock. This tile used to run its own job-less, customer-less
  // punch (JobBoardModule.clockInFromBoardForPerson(null, null, …)): no customer
  // picker, no ticket, no equipment, and a label that never said Build Ticket.
  // It now opens the Clock view — the same screen as the Clock tile — for the
  // signed-in person: clocked in → that view's Build Ticket / Close Out; not
  // clocked in → its clock-in form with the customer + job picker. clock.js
  // _applyPersonalClockHandoff does the selecting; this side only hands off.
  // Clock-in/clock-out decisions, the double-shift chooser and the self-only
  // board close-out delegation all live in clock.js now, not here.
  // Not the board's openStandaloneClockIn: that modal gates on driverModePin
  // and silently no-ops in the SPA (TASK-9220 COD).
  togglePersonalClock: function () {
    const personId = window.AppState?.memberAccess?.personId;
    if (!personId) { window.showToast?.('Link your roster profile in Settings first.', 'error'); return; }
    const tenantId = window.AppState?.tenant;
    if (!tenantId) { window.showToast?.('Session not loaded.', 'error'); return; }
    if (checkModuleAccess('clock', true) === false) {
      window.showToast?.('Ask your admin for Clock access to clock in here.', 'error');
      return;
    }
    if (typeof window.navigateToView !== 'function') {
      window.showToast?.('Clock not loaded. Try refreshing.', 'error');
      return;
    }
    window._clockPersonalHandoff = { tenantId, personId, at: Date.now() };
    window.navigateToView('clock');
  },

  switchPersonalTicket: async function () {
    const personId = window.AppState?.memberAccess?.personId;
    if (!personId) { window.showToast?.('Link your roster profile in Settings first.', 'error'); return; }
    const personName = window.AppState?._personalActionsName || '';
    if (!window.DispatchModule?.openSwitchJobForPerson) {
      window.showToast?.('Switch Ticket not loaded. Try refreshing.', 'error');
      return;
    }
    window.DispatchModule.openSwitchJobForPerson(personId, personName);
  },

  goPersonalPretrip: function () {
    if (typeof window.navigateToView === 'function') window.navigateToView('pretrip');
  },

  openPersonalExpense: async function () {
    const personId = window.AppState?.memberAccess?.personId;
    if (!personId) { window.showToast?.('Link your roster profile in Settings first.', 'error'); return; }
    const personName = window.AppState?._personalActionsName || '';
    if (!window.DispatchModule?.openDriverRecordExpenseForPerson) {
      // Fallback to driver-portal wrapper if the SPA-friendly one isn't loaded
      // (shouldn't happen post-deploy but safer to degrade than fail).
      // TASK-1440 — deliberately NOT given the cold-fetch hydration the primary
      // path got. This branch only runs when dispatch.js failed to load at all,
      // and DispatchState is defined BY that module — so everything below
      // resolves empty, the job picker stays hidden, and a restricted person
      // cannot be shown the fleet. Fail-closed by construction; duplicating the
      // hydration here would be 60 lines to serve a path that cannot be warm.
      if (!window.JobBoardModule?.openExpenseModal) {
        window.showToast?.('Expense module not loaded.', 'error');
        return;
      }
      window.JobBoardModule.openExpenseModal(null, null, null, {
        tenantId: window.AppState.tenant,
        clockEntries: window.DispatchState?._clockEntries || [],
        trucks: window.DispatchState?.trucks || [],
        jobs: window.DispatchState?.jobs || [],
        jobDays: window.DispatchState?.jobDays || [],
        // TASK-1338 — people is REQUIRED for expense job-visibility scoping:
        // showAllJobs is a property of the PERSON, not the login mode, so
        // without this a restricted member resolves to "unrestricted".
        people: window.DispatchState?.people || [],
        driverId: personId,
        driverName: personName,
      });
      return;
    }
    await window.DispatchModule.openDriverRecordExpenseForPerson(personId, personName);
  },

  openPersonalTimeOff: function () {
    const personId = window.AppState?.memberAccess?.personId;
    if (!personId) { window.showToast?.('Link your roster profile in Settings first.', 'error'); return; }
    const personName = window.AppState?._personalActionsName || '';
    if (!window.DispatchModule?.openDriverTimeOffForPerson) {
      window.showToast?.('Time Off not loaded. Try refreshing.', 'error');
      return;
    }
    window.DispatchModule.openDriverTimeOffForPerson(personId, personName);
  },

  openPersonalSafetyMeeting: function () {
    const personId = window.AppState?.memberAccess?.personId;
    if (!personId) { window.showToast?.('Link your roster profile in Settings first.', 'error'); return; }
    if (!window.JobBoardModule?.openSafetyMeetingModal) {
      window.showToast?.('Safety meeting handler not loaded.', 'error');
      return;
    }
    window.JobBoardModule.openSafetyMeetingModal();
  },
};

// Coming-soon module definitions
// Legacy stub — coming-soon modals now use the unified feature upgrade modal
function initComingSoonNav() {
  // No longer needed — CRM/HR route through showFeatureUpgradeModal via navigateToView
}

// Check if account has full access (founder, VIP, subscribed, or legacy pro)
function isProAccount() {
  // Founder accounts get full access forever
  if (isFounderAccount()) return true;

  // VIP accounts get full access
  if (isVipAccount()) return true;

  // Check subscription status. TASK-4769 — this read ROOT only, so a tenant
  // whose signup wrote the nested key alone (8 live today) was "not pro" by
  // status and only rescued by the legacy plan check below. ONE read now, and a
  // dunning (past_due/unpaid) subscriber keeps full access per the table.
  const tenantData = window.AppState?.tenantData || {};
  const _proStatus = resolveSubscriptionStatus(tenantData);
  if (_proStatus === 'trialing'
      || SUBSCRIPTION_ACCESS_STATUSES.subscribed.includes(_proStatus)
      || SUBSCRIPTION_ACCESS_STATUSES.dunning.includes(_proStatus)) {
    return true;
  }

  // Legacy plan check
  const plan = tenantData.plan || window.AppState?.tenantSettings?.plan;
  return plan === 'pro' || plan === 'fleet' || plan === 'base';
}

// Check if account has a specific add-on package (clock, billing)
// Founder/VIP get all add-ons free; others need to purchase (Coming Soon)
function hasAddOnPackage(packageType) {
  // Founder always gets everything
  if (isFounderAccount()) return true;

  // Trial accounts get full Pro+ access — let them experience everything
  const trialStatus = getTrialStatus();
  if (trialStatus.status === 'trialing') return true;

  // VIP accounts get everything
  if (isVipAccount()) return true;

  // Check actual addons on tenant data
  const tenantData = window.AppState?.tenantData || {};
  const addons = tenantData.addons || {};

  // Direct addon check
  if (packageType === 'clockbilling' && addons.clockbilling === true) return true;
  if (packageType === 'hr' && addons.hr === true) return true;
  if (packageType === 'crm' && addons.crm === true) return true;
  if (addons[packageType] === true) return true;

  // Fallback: check subscription.packages array (set by Stripe webhook / admin scripts)
  const packages = tenantData.packages || tenantData.subscription?.packages || [];
  if (packages.includes(packageType)) return true;

  // Plan-level fallback: pro plan includes clockbilling, pro_plus includes everything
  const plan = tenantData.plan || tenantData.subscription?.plan;
  if (plan === 'pro' || plan === 'pro_plus') {
    if (packageType === 'clockbilling') return true;
  }
  if (plan === 'pro_plus') {
    return true; // Pro+ includes all packages
  }

  return false;
}

// ============================================================================
// TASK-9553 — per-person clock mode. people/{id}.clockOutMode ('simple' |
// 'ticket' | 'ticketonly') overrides the company's settings.clockOutMode for
// that one person; unset = the company's mode, so nothing moves without an
// override. Server twin: functions/src/shared/clockMode.js (held to the same
// answers by tests/task9553-per-person-clock-mode.unit.cjs).
//
// Views read it through window.ClockMode (Pitfall #1 — never an import). The
// overrides come from the people snapshots every view already runs
// (notePeople) plus a one-read prime of the signed-in person (primeSelf), so a
// PIN driver's own mode is known before the board's people listener lands.
// effective() with NO argument means "the person using this screen" (PIN
// driver, else the signed-in member's linked person).
// ============================================================================
const _CLOCK_OUT_MODES = ['simple', 'ticket', 'ticketonly'];
const _clockModeOverrides = new Map(); // personId -> override | null
// The signed-in person's own mode is unconfirmed (self read timed out / failed /
// cached): {tid, pid, pending}. The rest of that logic is lazy (perf budget):
// utils/clockModeLazy.js, loaded at PIN login by primeSelf.
let _selfUnconfirmed = null, _cmLazy = null;
const _clockModeLazy = () => __ddChunkWrap(import('./utils/clockModeLazy.js?v=1')).then(m => (_cmLazy = m));
function _personClockOutMode(person) {
  const m = person && typeof person === 'object' ? person.clockOutMode : null;
  return _CLOCK_OUT_MODES.includes(m) ? m : null;
}
function _effectiveClockOutMode(tenantMode, person) {
  return _personClockOutMode(person) || tenantMode || 'simple';
}
window.ClockMode = {
  MODES: _CLOCK_OUT_MODES.slice(),
  LABELS: { simple: 'Simple Clock Out', ticket: 'Clock + Ticket', ticketonly: 'Ticket Only' },
  selfPersonId() {
    return window.AppState?.driverPersonId || window.AppState?.memberAccess?.personId || null;
  },
  tenant() {
    return window.AppState?.settings?.clockOutMode || 'simple';
  },
  // The person's own override, or null when they follow the company (or are unknown).
  override(personId) {
    if (!personId) return null;
    // Unknown own mode: never the no-ticket path. A ticket company already
    // builds; a Simple one gets 'ticket' (clock entry + ticket, a superset).
    if (this.unconfirmed(personId)) return /^ticket/.test(this.tenant()) ? null : 'ticket';
    return _clockModeOverrides.get(String(personId)) || null;
  },
  unconfirmed(pid) {
    const u = _selfUnconfirmed;
    return !!u && u.pid === String(pid) && u.tid === window.AppState?.tenant;
  },
  _u(v) { if (v !== undefined) _selfUnconfirmed = v; return _selfUnconfirmed; },
  markSelfUnconfirmed(pending) {
    const pid = this.selfPersonId();
    if (_cmLazy) _cmLazy.mark(this, pending);
    else if (pid) _selfUnconfirmed = { tid: window.AppState?.tenant, pid: String(pid), pending };
  },
  // true = confirmed, false = not (ticket path), null = session changed while
  // waiting. The whole wait is bounded HERE, incl. a slow chunk import.
  confirmSelf(ms = 5000) {
    const pid = this.selfPersonId(), tid = window.AppState?.tenant;
    if (!this.unconfirmed(pid)) return Promise.resolve(true);
    let t;
    return Promise.race([_clockModeLazy().then(m => m.confirmSelf(this, ms), () => false),
      new Promise(r => { t = setTimeout(r, ms + 500, false); })])
      .then(v => { clearTimeout(t); return window.AppState?.tenant === tid && this.selfPersonId() === pid ? v : null; });
  },
  effective(personId) {
    const pid = personId === undefined ? this.selfPersonId() : personId;
    return _effectiveClockOutMode(window.AppState?.settings?.clockOutMode, pid ? { clockOutMode: this.override(pid) } : null);
  },
  isTicket(personId) {
    const m = this.effective(personId);
    return m === 'ticket' || m === 'ticketonly';
  },
  isTicketOnly(personId) {
    return this.effective(personId) === 'ticketonly';
  },
  // server === true (a non-cached read) is what confirms an unconfirmed own mode.
  notePerson(personId, data, server) {
    if (!personId) return;
    const set = () => _clockModeOverrides.set(String(personId), _personClockOutMode(data));
    _cmLazy ? _cmLazy.note(this, personId, server, set) : set();
  },
  notePeople(list, server) {
    if (!Array.isArray(list)) return;
    for (const p of list) if (p && p.id) this.notePerson(p.id, p, server);
  },
  reset() { _clockModeOverrides.clear(); _selfUnconfirmed = null; _cmLazy?.reset(); },
  // One read of the signed-in person's doc (lazy). Never throws; a failed or
  // cached read leaves the own mode unconfirmed until a server read.
  primeSelf() {
    const tid = window.AppState?.tenant, pid = this.selfPersonId();
    return _clockModeLazy().then(m => m.primeSelf(this), () => {
      if (window.AppState?.tenant === tid && this.selfPersonId() === pid) this.markSelfUnconfirmed(null); // chunk failed = read failed
    });
  },
};

// Office 12:45 — primeSelf is one getDoc with no timeout; a slow read hung the
// driver board at "Loading account…". Wait at most ms, then continue on the
// company mode. A late resolve only calls notePerson (idempotent) — no re-init.
// On timeout the self mode is unconfirmed (ClockMode.override → ticket path).
function primeSelfWithin(ms = 3000) {
  const p = Promise.resolve().then(() => window.ClockMode?.primeSelf?.()).catch(() => {});
  let t;
  const timeout = new Promise(res => { t = setTimeout(() => {
    console.warn('[ClockMode] self prime >' + ms + 'ms — mode unconfirmed');
    window.ClockMode?.markSelfUnconfirmed?.(p);
    res('timeout');
  }, ms); });
  return Promise.race([p, timeout]).finally(() => clearTimeout(t));
}

// Check if tenant should build billing tickets from clock entries.
// TASK-9553 — pass the person being clocked to honor their own clock mode. No
// person, or a person with no override, = the company answer exactly as before.
// A Simple override never builds; a ticket override on a Simple company builds
// (that company's ticket switch was forced off only BECAUSE it is Simple), and
// on a ticket company it follows the company's switch.
function shouldBuildTickets(personId) {
  if (!hasAddOnPackage('clockbilling')) return false;
  const companyBuilds = window.AppState?.settings?.buildTicketsFromClock === true;
  const own = personId ? window.ClockMode.override(personId) : null;
  if (!own) return companyBuilds;
  if (own === 'simple') return false;
  const companyMode = window.ClockMode.tenant();
  return (companyMode === 'ticket' || companyMode === 'ticketonly') ? companyBuilds : true;
}

// Human labels for a module/view key. Shared by the real compliance upgrade
// overlay and TASK-1290's locked-module PREVIEW so the two never drift.
const MODULE_GATE_LABELS = {
  dispatch: 'Dispatch', roster: 'Roster', maintenance: 'Maintenance',
  pretrip: 'Inspections', dvir: 'Inspections',
  hr: 'HR', clock: 'Clock & Billing', billing: 'Billing',
  workforce: 'Workforce', crm: 'CRM', bi: 'Business Intelligence',
  compliance: 'Compliance', jobboard: 'Job Board', ap: 'Accounts Payable',
  // TASK-1117 — HR post-hire management gates (_hrGateComplianceOnly passes
  // `hr-<subView>`): one UX-approved label, "Unlock Employee Management".
  'hr-directory': 'Employee Management', 'hr-memos': 'Employee Management',
  'hr-timeoff': 'Employee Management', 'hr-reviews': 'Employee Management',
  'hr-disciplinary': 'Employee Management', 'hr-safety': 'Employee Management',
  'hr-onboarding': 'Employee Management',
};

// Show compliance-only upgrade overlay on locked modules (lets them see data underneath)
function showComplianceUpgradeOverlay(view) {
  // Remove any existing overlay
  const existing = document.getElementById('compliance-upgrade-overlay');
  if (existing) existing.remove();

  const moduleLabels = MODULE_GATE_LABELS;
  const label = moduleLabels[view] || view;
  const partner = ((window.AppState?.tenantData?.compliancePartner || 'your compliance manager').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;'));

  // Smart tease: build context-specific data hints from existing tenant data
  const td = window.AppState?.tenantData || {};
  const fmcsa = td.fmcsaData || {};
  const peopleCount = window.AppState?.people?.length || fmcsa.totalDrivers || 0;
  const truckCount = window.AppState?.trucks?.length || fmcsa.totalPowerUnits || 0;

  const smartTeases = {
    dispatch: peopleCount > 0 ? `<div style="display:flex;gap:12px;justify-content:center;margin-bottom:20px;flex-wrap:wrap;">
      <div style="background:#F0F9FF;border:1px solid #BAE6FD;border-radius:8px;padding:8px 16px;"><span style="font-weight:700;color:#0369A1;font-size:1.1rem;">${peopleCount}</span><div style="font-size:0.72rem;color:#0C4A6E;">Drivers Ready</div></div>
      <div style="background:#F0F9FF;border:1px solid #BAE6FD;border-radius:8px;padding:8px 16px;"><span style="font-weight:700;color:#0369A1;font-size:1.1rem;">${truckCount}</span><div style="font-size:0.72rem;color:#0C4A6E;">Vehicles</div></div>
    </div><p style="color:#0369A1;font-size:0.82rem;font-weight:600;margin-bottom:16px;">Your roster is ready — start dispatching jobs today.</p>` : '',
    roster: peopleCount > 0 ? `<div style="background:#F0F9FF;border:1px solid #BAE6FD;border-radius:8px;padding:10px 16px;margin-bottom:16px;text-align:center;">
      <span style="font-weight:700;color:#0369A1;">${peopleCount} people</span><span style="color:#0C4A6E;font-size:0.85rem;"> and </span><span style="font-weight:700;color:#0369A1;">${truckCount} vehicles</span><span style="color:#0C4A6E;font-size:0.85rem;"> already in your roster</span>
    </div>` : '',
    maintenance: truckCount > 0 ? `<div style="background:#FFF7ED;border:1px solid #FED7AA;border-radius:8px;padding:10px 16px;margin-bottom:16px;text-align:center;">
      <span style="font-weight:700;color:#C2410C;">${truckCount} vehicles</span><span style="color:#9A3412;font-size:0.85rem;"> waiting for maintenance tracking</span>
    </div>` : '',
    pretrip: truckCount > 0 ? `<div style="background:#FFF7ED;border:1px solid #FED7AA;border-radius:8px;padding:10px 16px;margin-bottom:16px;text-align:center;">
      <span style="color:#9A3412;font-size:0.85rem;">Start DVIR inspections for your </span><span style="font-weight:700;color:#C2410C;">${truckCount} vehicles</span>
    </div>` : '',
    billing: peopleCount > 0 ? `<div style="background:#ECFDF5;border:1px solid #A7F3D0;border-radius:8px;padding:10px 16px;margin-bottom:16px;text-align:center;">
      <span style="color:#065F46;font-size:0.85rem;">Clock, invoice, and bill for </span><span style="font-weight:700;color:#047857;">${peopleCount} crew members</span>
    </div>` : '',
    clock: peopleCount > 0 ? `<div style="background:#ECFDF5;border:1px solid #A7F3D0;border-radius:8px;padding:10px 16px;margin-bottom:16px;text-align:center;">
      <span style="color:#065F46;font-size:0.85rem;">Track hours for </span><span style="font-weight:700;color:#047857;">${peopleCount} crew members</span>
    </div>` : '',
    crm: `<div style="background:#F5F3FF;border:1px solid #DDD6FE;border-radius:8px;padding:10px 16px;margin-bottom:16px;text-align:center;">
      <span style="color:#5B21B6;font-size:0.85rem;">Customer portal, bidding, price books & pipeline management</span>
    </div>`,
    bi: peopleCount > 0 ? `<div style="background:#F0F9FF;border:1px solid #BAE6FD;border-radius:8px;padding:10px 16px;margin-bottom:16px;text-align:center;">
      <span style="color:#0C4A6E;font-size:0.85rem;">Revenue analytics, cost tracking & fleet utilization dashboards</span>
    </div>` : '',
  };
  const tease = smartTeases[view] || '';

  const overlay = document.createElement('div');
  overlay.id = 'compliance-upgrade-overlay';
  overlay.style.cssText = `
    position: fixed; top: 0; left: 0; right: 0; bottom: 0; z-index: 900;
    background: rgba(15, 23, 42, 0.35); backdrop-filter: blur(2px);
    display: flex; align-items: center; justify-content: center;
    pointer-events: all;
  `;
  overlay.innerHTML = `
    <div style="background: #fff; border-radius: 16px; padding: 40px; max-width: 480px; width: 90%;
      box-shadow: 0 20px 60px rgba(0,0,0,0.2); text-align: center;">
      <div style="width: 64px; height: 64px; border-radius: 50%; margin: 0 auto 20px;
        background: linear-gradient(135deg, #3B82F6, #06B6D4); display: flex; align-items: center; justify-content: center;">
        <span class="material-icons" style="font-size: 32px; color: #fff;">lock_open</span>
      </div>
      <h2 style="font-size: 1.5rem; font-weight: 700; color: #0F172A; margin-bottom: 8px;">
        Unlock ${label}
      </h2>
      ${tease}
      <p style="color: #475569; line-height: 1.6; margin-bottom: 8px;">
        Your compliance data is already here. Subscribe to DispatchDeck to unlock
        ${label} and all other fleet management tools.
      </p>
      <p style="color: #94A3B8; font-size: 0.85rem; margin-bottom: 24px;">
        Managed by ${partner}
      </p>
      <div style="display: flex; gap: 12px; justify-content: center; flex-wrap: wrap;">
        <button onclick="document.getElementById('compliance-upgrade-overlay').remove()"
          style="padding: 12px 24px; border-radius: 8px; border: 1px solid #CBD5E1; background: #fff;
          color: #475569; font-weight: 600; cursor: pointer; font-size: 0.9rem;">
          Keep Browsing
        </button>
        <button onclick="document.getElementById('compliance-upgrade-overlay').remove(); showFeatureUpgradeModal('base')"
          style="padding: 12px 24px; border-radius: 8px; border: none;
          background: linear-gradient(135deg, #3B82F6, #06B6D4); color: #fff;
          font-weight: 600; cursor: pointer; font-size: 0.9rem; box-shadow: 0 2px 8px rgba(59,130,246,0.3);">
          Unlock for $250/mo
        </button>
      </div>
    </div>
  `;

  // Click backdrop to dismiss
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) overlay.remove();
  });

  document.body.appendChild(overlay);
}
// Expose for cross-module callers (hr.js sub-view gate — see _hrGateComplianceOnly)
window.showComplianceUpgradeOverlay = showComplianceUpgradeOverlay;

// ===================================================================
// TASK-1290 — "locked module preview" (the dimmed WINDOW, not just the button)
// -------------------------------------------------------------------
// Chris: "when I grey out a button selection, it would be good to grey out the
// window the same way it will be 'unusable' for a non-paying compliance
// person's tenant… so I can show what that looks like while also keeping the
// grey-out capability."
//
// Bridges the two existing systems:
//   (A) hold-to-dim  — sidebarCustomize.js, ephemeral `.ghost` nav rows.
//   (B) the locked look — showComplianceUpgradeOverlay above, which today only
//       fires for a genuinely unsubscribed compliance-partner-managed tenant.
//
// Opening a DIMMED module now paints the content window in the (B) language —
// desaturated + blurred behind a scrim — so Chris can SHOW a compliance partner
// what an unsubscribed carrier sees, on any tenant, at any time.
//
// HARD INVARIANTS (do not regress):
//   1. NON-BLOCKING. "Keep Browsing" always reveals the real, fully working
//      module. This preview NEVER gates access — it is presentation only, and
//      it does not touch checkModuleAccess / isModuleUnlocked / the
//      pretrip+dvir access floor.
//   2. NEVER shows the real dunning copy. No "$250/mo", no
//      showFeatureUpgradeModal — a paying admin who dimmed a button to teach
//      must not be shown a subscribe prompt. It is explicitly demo-labelled.
//   3. NOT tied to isComplianceOnlyTenant(). Real unpaid tenants keep the real
//      overlay (and are excluded here so the two can never stack).
//   4. Ephemeral + reversible, exactly like the dim it rides on: cleared by
//      un-dimming, by resetDim() (device lock/unlock), by tenant switch, and by
//      a refresh. The sidebar / mobile nav stay above the scrim's rect so the
//      hold-to-dim un-dim path is always reachable.
//   5. NEVER paints over Inspections (LOCKED_PREVIEW_NEVER) — see the comment
//      on that list. Dimming the nav row still works; only the scrim is barred.
// ===================================================================
const LOCKED_PREVIEW_ID = 'locked-module-preview';
// SAFETY FLOOR (COD Domain & Regulatory ⚠️). Inspections is DOT-mandated
// (49 CFR 396.11/396.13) and TASK-1224 deliberately made it dimmable — a
// trainer muting it while teaching Dispatch is a stated, common case. But a
// yard tablet is shared: a driver who opens Inspections and reads
// "Inspections is locked … the window just doesn't work" could skip the DVIR
// instead of finding Keep Browsing. The nav row still dims exactly as before;
// the WINDOW simply never gets the locked scrim for these views. Chris's demo
// never needs Inspections previewed, so this costs the feature nothing.
const LOCKED_PREVIEW_NEVER = ['pretrip', 'dvir'];

// Views the user chose to browse anyway this session. Pruned against the live
// dim state on every sync, so un-dim → re-dim shows the preview again.
const _lockedPreviewDismissed = new Set();
let _lockedPreviewResizeBound = false;

// The preview only makes sense in the real signed-in app. Demo / driver-PIN /
// public-token sessions can't dim in the first place (gestureEnabled), and
// compliance-only tenants get the REAL overlay instead.
function _lockedPreviewEligible() {
  const st = window.AppState || {};
  if (!st.tenant) return false;
  if (st.isDemo || st.tenant === 'demo') return false;
  if (st.driverMode || st.driverModePin) return false;
  if (st.isPublicAccess || st.accessToken) return false;
  if (typeof isComplianceOnlyTenant === 'function' && isComplianceOnlyTenant()) return false;
  return true;
}

function removeLockedModulePreview() {
  document.getElementById(LOCKED_PREVIEW_ID)?.remove();
  document.body.classList.remove('locked-preview-active');
}
window.removeLockedModulePreview = removeLockedModulePreview;

// Park the scrim exactly over #main-content so the sidebar (desktop) and the
// bottom nav (mobile) stay live — that's the un-dim escape hatch. Measured
// rather than CSS-offset because the content box moves with the trial /
// verification banners and with #app-sidebar.hidden.
function _positionLockedPreview(el) {
  const mc = document.getElementById('main-content');
  if (!el) return;
  if (!mc) { el.style.inset = '0'; return; }
  const r = mc.getBoundingClientRect();
  el.style.top = Math.max(0, r.top) + 'px';
  el.style.left = Math.max(0, r.left) + 'px';
  el.style.width = Math.max(0, r.width) + 'px';
  el.style.height = Math.max(0, Math.min(r.height, window.innerHeight - Math.max(0, r.top))) + 'px';
}

function _onLockedPreviewResize() {
  _positionLockedPreview(document.getElementById(LOCKED_PREVIEW_ID));
}

function showLockedModulePreview(view) {
  removeLockedModulePreview();
  // `view` comes off the URL hash on some routes, so the label is escaped even
  // though every known key maps to a static string (COD Code Reviewer).
  const label = MODULE_GATE_LABELS[view] || capitalizeFirst(String(view || 'This module'));
  const safeLabel = escapeHtml(label);

  // Data-aware tease — the same "your data is already in here" beat the real
  // locked state uses, which is what makes the demo read as authentic.
  // BEST-EFFORT BY DESIGN: `AppState.people`/`.trucks` are never populated in
  // this codebase (see the HRState note at ~26189), so we also read the
  // jobboard bridge (fleetPeople/fleetTrucks) and the partner-provisioned FMCSA
  // snapshot. When none of them are loaded the tease renders NOTHING rather
  // than a bogus "0" — the card stands on its own without it.
  // COD Code Reviewer ⚠️: fmcsaData is tenant-doc data (imported, not code), so
  // totalDrivers/totalPowerUnits are coerced to a non-negative integer before
  // they ever reach innerHTML — a string in that field is not an XSS vector.
  const st = window.AppState || {};
  const fmcsa = st.tenantData?.fmcsaData || {};
  const _num = (v) => { const n = Math.floor(Number(v)); return Number.isFinite(n) && n > 0 ? n : 0; };
  const peopleCount = _num(st.people?.length || st.fleetPeople?.length || fmcsa.totalDrivers);
  const truckCount = _num(st.trucks?.length || st.fleetTrucks?.length || fmcsa.totalPowerUnits);
  const bits = [];
  if (peopleCount) bits.push(`<strong>${peopleCount}</strong> ${peopleCount === 1 ? 'person' : 'people'}`);
  if (truckCount) bits.push(`<strong>${truckCount}</strong> ${truckCount === 1 ? 'vehicle' : 'vehicles'}`);
  const tease = bits.length
    ? `<div class="lmp-tease">${bits.join(' and ')} already in this account — visible to the office, but the module stays shut.</div>`
    : '';

  const overlay = document.createElement('div');
  overlay.id = LOCKED_PREVIEW_ID;
  overlay.className = 'locked-preview-overlay';
  overlay.dataset.view = view;
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-label', `${label} locked-module preview`);
  overlay.innerHTML = `
    <div class="locked-preview-card">
      <span class="lmp-ribbon">
        <span class="material-icons" aria-hidden="true">visibility</span>
        Preview &mdash; your access is unchanged
      </span>
      <div class="lmp-icon"><span class="material-icons" aria-hidden="true">lock</span></div>
      <h2 class="lmp-title">${safeLabel} is locked</h2>
      <p class="lmp-body">
        This is what ${safeLabel} looks like to an account that isn&rsquo;t subscribed to
        DispatchDeck &mdash; the data is still in here, the window just doesn&rsquo;t work.
      </p>
      ${tease}
      <div class="lmp-actions">
        <button type="button" class="lmp-btn lmp-btn-primary" data-lmp="keep">Keep Browsing</button>
        <button type="button" class="lmp-btn lmp-btn-secondary" data-lmp="undim">Turn preview off</button>
      </div>
      <p class="lmp-foot">
        You dimmed ${safeLabel}. Press and hold it again in the menu &mdash; or use
        Turn preview off &mdash; to bring it back.
      </p>
    </div>
  `;

  overlay.querySelector('[data-lmp="keep"]')?.addEventListener('click', () => {
    // ACCESS FLOOR — always reveals the real, fully working module.
    _lockedPreviewDismissed.add(view);
    removeLockedModulePreview();
  });
  overlay.querySelector('[data-lmp="undim"]')?.addEventListener('click', () => {
    _lockedPreviewDismissed.delete(view);
    // unhide() fires onChange → syncLockedModulePreview() → scrim clears.
    const undimmed = window.SidebarCustomize?.unhide?.(view);
    if (!undimmed) removeLockedModulePreview();
  });
  // Clicking the dead area is inert on purpose (that's the point of the demo),
  // but Esc always gets out — never trap a keyboard user behind a preview.
  overlay.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') { _lockedPreviewDismissed.add(view); removeLockedModulePreview(); }
  });

  document.body.appendChild(overlay);
  document.body.classList.add('locked-preview-active');
  _positionLockedPreview(overlay);
  if (!_lockedPreviewResizeBound) {
    window.addEventListener('resize', _onLockedPreviewResize);
    _lockedPreviewResizeBound = true;
  }
  overlay.querySelector('[data-lmp="keep"]')?.focus();
}

// Single reconciler: "should the current view be showing the preview right now?"
// Called after every navigation AND from SidebarCustomize's onChange (so a
// dim/un-dim/resetDim on the view you're standing on takes effect immediately).
function syncLockedModulePreview(view) {
  try {
    const sc = window.SidebarCustomize;
    // Prune stale dismissals so un-dim → re-dim shows the preview again.
    if (sc?.isHidden) {
      _lockedPreviewDismissed.forEach(v => { if (!sc.isHidden(v)) _lockedPreviewDismissed.delete(v); });
    }
    const current = view || window.AppState?.currentView;
    const dimmed = !!(current && sc?.isHidden?.(current));
    if (LOCKED_PREVIEW_NEVER.includes(current)) { removeLockedModulePreview(); return; }
    if (!dimmed || !_lockedPreviewEligible() || _lockedPreviewDismissed.has(current)) {
      removeLockedModulePreview();
      return;
    }
    // Already showing for this exact view — leave it alone (don't re-animate).
    if (document.getElementById(LOCKED_PREVIEW_ID)?.dataset?.view === current) return;
    showLockedModulePreview(current);
  } catch (e) {
    // A preview must never break navigation.
    console.warn('[TASK-1290] locked preview sync failed (non-fatal):', e?.message);
    removeLockedModulePreview();
  }
}
window.syncLockedModulePreview = syncLockedModulePreview;

// Show Feature upgrade modal with package highlight
function showFeatureUpgradeModal(packageType, fromInlineButton = false) {
  window.AppState._featureModalInline = fromInlineButton;

  const modal = document.getElementById('feature-upgrade-modal');
  if (!modal) return;

  // Highlight the relevant package card (check data-package and data-package-alt)
  modal.querySelectorAll('.feature-package-card').forEach(card => {
    const matches = card.dataset.package === packageType ||
      (card.dataset.packageAlt || '').split(' ').includes(packageType);
    card.classList.toggle('package-highlighted', matches);
  });

  // TASK-9812 — price, toggle, fine print come from PLAN_PRICING.
  syncPlanIntervalUi();

  // Context-aware footer copy. TASK-4772: no "add-ons coming soon" — SMS packs
  // and vehicle blocks are already sold, and the card above names them.
  const note = document.getElementById('feature-upgrade-note');
  if (note) note.textContent = window.AppState?.isDemo ? 'Explore freely in demo.' : '';

  // Context-aware dismiss button text
  const dismissBtn = document.getElementById('feature-upgrade-dismiss');
  if (dismissBtn) {
    dismissBtn.textContent = window.AppState?.isDemo ? 'Continue Exploring' : 'Maybe Later';
  }

  modal.classList.remove('hidden');
}

// Close Feature upgrade modal
// Close upgrade modal on Escape key
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') {
    const modal = document.getElementById('feature-upgrade-modal');
    if (modal && !modal.classList.contains('hidden')) {
      closeFeatureUpgradeModal();
    }
  }
});

function closeFeatureUpgradeModal() {
  const modal = document.getElementById('feature-upgrade-modal');
  if (modal) modal.classList.add('hidden');

  // In demo mode, let them continue exploring the feature
  if (window.AppState?.isDemo) {
    window.AppState._featureModalInline = false;
    return;
  }

  // If triggered from nav (not inline), go back to dispatch
  if (!window.AppState._featureModalInline) {
    navigateToView('dispatch');
  }
  window.AppState._featureModalInline = false;
}

// Start a purchase — routes to REAL Stripe checkout via createCheckoutSession.
// TASK-1164 — this used to client-write plan:'pro_plus' + all addons directly onto
// the tenant doc (a free self-upgrade that never charged and granted no SMS credits).
// Now it opens the real Stripe checkout; the webhook grants access/credits via the
// server-owned fields (subscriptionStatus/hasBasePlan/hasSmsPack/addons via Admin SDK).
// Button intents map to the two real Stripe products: 'sms_pack' = one-time SMS pack;
// everything else ('base'/'pro'/undefined) = the single $250/mo plan (all modules).
// TASK-9816 — `opts.packs` (1–10) pre-fills the SMS pack count; the buyer can
// still change it on the Stripe page and the server re-validates it.
async function startCheckout(plan, opts = {}) {
  // Apple App Store guideline 3.1.1 — no Stripe purchases inside iOS app
  if (window.isIOSApp) {
    showToast('Manage your subscription at dispatchdeck.app', 'info');
    return;
  }
  const tenantId = window.AppState?.tenant;
  if (!tenantId || tenantId === 'demo') {
    showToast('Please create an account first', 'error');
    return;
  }

  // TASK-9812 — 'sms_pack' is the pack; 'base_annual' / 'base_monthly' are
  // explicit; anything else is the base plan at the interval the owner picked
  // on the toggle (monthly unless annual is offered and selected).
  const checkoutPlan = (plan === 'sms_pack') ? 'sms_pack'
    : (plan === 'base_annual') ? basePlanFor('year', _annualOfferedNow())
    : (plan === 'base_monthly') ? 'base'
    : selectedBasePlan();

  // Loading overlay (mirrors startSubscription).
  let overlay = document.getElementById('checkout-loading-overlay');
  if (!overlay) {
    overlay = document.createElement('div');
    overlay.id = 'checkout-loading-overlay';
    overlay.innerHTML = `
      <div style="display:flex;flex-direction:column;align-items:center;gap:16px;">
        <div class="btn-spinner" style="width:36px;height:36px;border-width:3px;"></div>
        <div style="font-size:1rem;font-weight:600;color:var(--text-primary);">Opening Stripe Checkout...</div>
        <div style="font-size:0.8125rem;color:var(--text-secondary);">You'll be redirected in a moment</div>
      </div>`;
    overlay.style.cssText = 'position:fixed;inset:0;z-index:9999;background:rgba(255,255,255,0.92);display:flex;align-items:center;justify-content:center;';
    document.body.appendChild(overlay);
  }
  overlay.style.display = 'flex';

  try {
    const createCheckout = httpsCallable(functions, 'createCheckoutSession');
    const payload = { tenantId, plan: checkoutPlan };
    if (checkoutPlan === 'sms_pack') {
      const n = Math.floor(Number(opts && opts.packs));
      payload.packs = (n >= 1 && n <= 10) ? n : 1;
    }
    const result = await createCheckout(payload);
    if (result.data?.url) {
      // TWA (Android app) blocks window.open — a location redirect is safest.
      window.location.href = result.data.url;
    } else {
      throw new Error('No checkout URL returned');
    }
  } catch (error) {
    console.error('[Checkout] Error:', error);
    const ov = document.getElementById('checkout-loading-overlay');
    if (ov) ov.style.display = 'none';
    if (checkoutPlan === 'sms_pack' || !_handleBaseCheckoutRefusal(error)) {
      showToast('Error starting checkout: ' + (error.message || 'Please try again'), 'error');
    }
  }
}

// TASK-9432 — createCheckoutSession refuses a base checkout with
// 'already-exists' when the tenant already has a live subscription (incl.
// past_due/unpaid). The server checks OWNER before that, so the caller is the
// owner: open the billing portal, where the card can actually be fixed.
// Returns true when it handled the error.
function _handleBaseCheckoutRefusal(error) {
  const code = String(error?.code || '');
  if (code === 'functions/already-exists' || code === 'already-exists') {
    showToast('You already have a subscription — opening billing so you can update it.', 'info');
    openBillingPortal();
    return true;
  }
  return false;
}

// Open Stripe billing portal for subscription management
async function openBillingPortal() {
  // TASK-9432 — Apple 3.1.1: the Stripe portal takes cards and pays invoices,
  // so it stays out of the iOS app exactly like startCheckout does.
  if (window.isIOSApp) {
    showToast('Manage your subscription at dispatchdeck.app', 'info');
    return;
  }
  const tenantId = window.AppState?.tenant;
  if (!tenantId || tenantId === 'demo') {
    showToast('No billing account found', 'error');
    return;
  }

  try {
    showToast('Opening billing portal...', 'info');
    const createPortal = httpsCallable(functions, 'createBillingPortalSession');
    const result = await createPortal({ tenantId });

    if (result.data?.url) {
      // TWA (Android app) blocks window.open — fall back to location redirect
      const opened = window.open(result.data.url, '_blank');
      if (!opened) {
        window.location.href = result.data.url;
      }
    } else {
      throw new Error('No portal URL returned');
    }
  } catch (error) {
    console.error('[BillingPortal] Error:', error);
    showToast('Failed to open billing portal: ' + (error.message || 'Unknown error'), 'error');
  }
}

// AI Pack upgrade modal — reusable across all modules. Rare (Smart Tools are in
// every plan), so the modal loads on first open: views/aiUpgradeModal.js (perf budget).
function showAiUpgradeModal(featureHighlight) {
  return _lazyImportWithRetry(() => __ddChunkWrap(import('./views/aiUpgradeModal.js?v=1')), 'aiUpgradeModal')
    .then((m) => m.showAiUpgradeModal(featureHighlight))
    .catch(() => showToast('Could not open Smart Tools info. Try again.', 'error'));
}

function closeAiUpgradeModal() {
  const modal = document.getElementById('ai-upgrade-modal');
  if (modal) modal.classList.add('hidden');
}

// Check if tenant has AI Pack — Smart Tools now included in all plans
function hasAiPack() {
  return true;
}

// Check if tenant has SMS access.
// TESTING PHASE (Apr 2026): open to everyone while we gather real user feedback.
// Flip this to the real gate (twilio.status active + credits + founder/VIP) when testing ends.
function hasSmsAccess() {
  return true;
}

// SMS Pack upgrade modal — reusable across all modules
function showSmsUpgradeModal(featureHighlight) {
  let modal = document.getElementById('sms-upgrade-modal');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'sms-upgrade-modal';
    modal.className = 'modal hidden';
    modal.innerHTML = `
      <div class="modal-backdrop" onclick="window.closeSmsUpgradeModal()"></div>
      <div class="modal-content" style="max-width:420px;">
        <div class="modal-header">
          <h3><span class="material-icons" style="color:var(--accent-teal);vertical-align:middle;margin-right:8px;">sms</span>SMS Pack</h3>
          <button class="btn-close" onclick="window.closeSmsUpgradeModal()">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="modal-body" style="padding:var(--spacing-lg);">
          <p style="text-align:center;margin-bottom:16px;font-size:1.05em;">Keep your crew in the loop with built-in text messaging.</p>
          <ul class="ai-upgrade-feature-list" id="sms-upgrade-feature-list">
            <li data-feature="mass-text">
              <span class="material-icons" style="color:var(--accent-teal);">campaign</span>
              <div>
                <strong>Notify Crew</strong>
                <span>Send the day's schedule to all drivers with one tap.</span>
              </div>
            </li>
            <li data-feature="individual-text">
              <span class="material-icons" style="color:var(--accent-teal);">chat</span>
              <div>
                <strong>Individual Text</strong>
                <span>Send a quick message to any crew member right from their job card.</span>
              </div>
            </li>
            <li data-feature="notifications">
              <span class="material-icons" style="color:var(--accent-teal);">notifications_active</span>
              <div>
                <strong>Automated Alerts</strong>
                <span>Inspection submissions, maintenance tasks, compliance expiry &mdash; sent automatically via SMS.</span>
              </div>
            </li>
            <li data-feature="pin-setup">
              <span class="material-icons" style="color:var(--accent-teal);">pin</span>
              <div>
                <strong>PIN Setup via Text</strong>
                <span>Send drivers their access PIN link straight to their phone.</span>
              </div>
            </li>
            <li data-feature="signature-request">
              <span class="material-icons" style="color:var(--accent-teal);">draw</span>
              <div>
                <strong>Signature Requests</strong>
                <span>Text customers a link to sign delivery tickets on their phone.</span>
              </div>
            </li>
          </ul>
          <div style="text-align:center;margin-top:20px;">
            <p style="color:var(--text-muted);font-size:0.9em;margin-bottom:4px;">One-time purchase &mdash; <strong>$25</strong></p>
            <p style="color:var(--text-muted);font-size:0.82em;margin-bottom:16px;">Includes your own dedicated phone number + 1,500 text messages.</p>
            <div style="display:flex;gap:12px;justify-content:center;">
              <button class="btn btn-secondary" onclick="window.closeSmsUpgradeModal()">Maybe Later</button>
              <button class="btn btn-primary" id="sms-upgrade-buy-btn" onclick="window.closeSmsUpgradeModal(); window.startCheckout('sms_pack');">
                <span class="material-icons">sms</span> Get SMS Pack
              </button>
            </div>
          </div>
        </div>
      </div>
    `;
    document.body.appendChild(modal);
  }

  // TASK-4665-S3 — only the owner can buy (the server refuses anyone else);
  // everyone else is told who can, instead of a button that can only error.
  // Re-evaluated on every open: the modal element is cached across viewers.
  const _buyBtn = modal.querySelector('#sms-upgrade-buy-btn');
  if (_buyBtn) {
    const _canBuy = _smsPackControlForViewer().show;
    _buyBtn.style.display = _canBuy ? '' : 'none';
    let _note = modal.querySelector('#sms-upgrade-owner-note');
    if (!_canBuy && !_note) {
      _note = document.createElement('p');
      _note.id = 'sms-upgrade-owner-note';
      _note.style.cssText = 'color:var(--text-secondary);font-size:0.85em;margin:12px 0 0;';
      _note.textContent = window.isIOSApp
        ? 'SMS packs are purchased at dispatchdeck.app.'
        : 'Only the account owner can add SMS credits. Ask them to buy a pack.';
      _buyBtn.parentNode.insertAdjacentElement('afterend', _note);
    }
    if (_note) _note.style.display = _canBuy ? 'none' : '';
  }

  // Highlight the relevant feature if specified
  modal.querySelectorAll('#sms-upgrade-feature-list li').forEach(li => {
    li.classList.toggle('ai-feature-highlighted', li.dataset.feature === featureHighlight);
  });

  modal.classList.remove('hidden');
}

function closeSmsUpgradeModal() {
  const modal = document.getElementById('sms-upgrade-modal');
  if (modal) modal.classList.add('hidden');
}

// Expose to window for onclick handlers
window.closeFeatureUpgradeModal = closeFeatureUpgradeModal;
window.showFeatureUpgradeModal = showFeatureUpgradeModal;
window.showAiUpgradeModal = showAiUpgradeModal;
window.closeAiUpgradeModal = closeAiUpgradeModal;
window.showSmsUpgradeModal = showSmsUpgradeModal;
window.closeSmsUpgradeModal = closeSmsUpgradeModal;
window.hasAiPack = hasAiPack;
window.hasSmsAccess = hasSmsAccess;

// =============================================================================
// SMS CREDIT METER COMPONENT
// =============================================================================

/**
 * TASK-4665-S3 — which call-to-action the ACTIVE (not exhausted) SMS meter
 * shows. 'topup' when the balance is LOW (the same ≤20%-remaining threshold
 * that colours the bar yellow/red) for every tenant incl. founder and comped
 * VIP; 'upgrade' for a healthy promo tenant (unchanged); null otherwise.
 * Only when this viewer may buy (canBuy = getSmsPackPurchaseControl().show:
 * owner, billable, not iOS) — the meter also renders in Notify Crew for
 * dispatchers, and the server refuses non-owners. Pure; the unit test runs it.
 */
const SMS_METER_LOW_PERCENT = 20;

/**
 * TASK-4665 — credits used BEYOND the balance. Safety texts (driver codes, PIN
 * setup, clock-out alerts) still send at zero and are still charged, so
 * creditUsed can exceed creditBalance. The meter shows that overdraft instead
 * of clamping to "0 credits left"; the next pack nets it out. Pure.
 */
function smsCreditOverdraft(twilio) {
  const tw = twilio || {};
  return Math.max(0, (Number(tw.creditUsed) || 0) - (Number(tw.creditBalance) || 0));
}
function smsMeterCtaKind({ isPromo, isProPlus, isVipComp, fillPercent, canBuy }) {
  if (!canBuy) return null;
  if (fillPercent <= SMS_METER_LOW_PERCENT) return 'topup';
  if (isPromo && !isProPlus && !isVipComp) return 'upgrade';
  return null;
}

/**
 * Render SMS credit meter into a container.
 * @param {string} containerId - DOM element ID to render into
 * @param {Object} options - { variant: 'compact'|'large' }
 */
function renderSmsCreditMeter(containerId, options = {}) {
  const container = document.getElementById(containerId);
  if (!container) return;

  // TASK-9473 — opt-in for the Account page's single SMS card: hideTitle drops the
  // "SMS Balance" label (the card has its own heading), hideCta drops every
  // in-meter buy button (the card has the one button). Defaults change nothing.
  const { variant = 'compact', hideTitle = false, hideCta = false } = options;
  const smsTitle = hideTitle ? '' : '<span class="material-icons">sms</span> SMS Balance';
  const tenantData = window.AppState?.tenantData || {};
  const twilio = tenantData.twilio || {};
  const isDemo = window.AppState?.isDemo;
  const isFounder = isFounderAccount();

  // No SMS at all and not founder/demo — show nothing
  if (!tenantData.hasSmsPack && !isFounder && !isDemo) {
    container.innerHTML = '';
    return;
  }

  // Founder (TITS) — truly unlimited (hard-skips the send gate), shows the badge.
  // TASK-1166 — VIPs are NOT hard-unlimited: they're metered against a large
  // COMPED balance, so they now fall through to the real balance meter below and
  // watch it deteriorate (transparency + soft-landing toward the eventual paid
  // transition Chris already told them about). Requires hasSmsPack on the VIP
  // tenant (the top-up script sets it) or the 9288 guard hides the meter.
  // Chris 2026-08-21 — the founder is METERED now, so this branch must not claim
  // "unlimited" any more. An unlimited badge over a balance that CAN refuse a send
  // is exactly the combination that produced today's confusing error: the meter
  // said unlimited while the pre-flight threw "no SMS credit". Fall through to the
  // real balance meter below, same as every other tenant, so what he reads is what
  // the send path will actually do.
  //
  // Kept as a named constant rather than deleting the branch: sends still route
  // through the TITS messaging service (his call — the crew keeps 701-773-3485).
  // TASK-4665 landed the metering: founder sends now debit twilio.creditUsed
  // like every tenant, so this badge must stay OFF unless that is reverted.
  const FOUNDER_SMS_UNLIMITED_BADGE = false;
  if (isFounder && FOUNDER_SMS_UNLIMITED_BADGE) {
    const sent = twilio.messagesSent || twilio.monthlySends || 0;
    container.innerHTML = `
      <div class="sms-credit-meter ${variant}">
        <div class="sms-meter-unlimited">
          <div class="sms-meter-label">
            ${smsTitle}
          </div>
          <span class="badge-unlimited">
            <span class="material-icons" style="font-size:14px;">all_inclusive</span> Unlimited
          </span>
        </div>
        <div class="sms-meter-unlimited" style="margin-top:6px;">
          <span class="sms-meter-stat"><strong>${sent.toLocaleString()}</strong> messages sent</span>
        </div>
      </div>
    `;
    return;
  }

  // Demo mode — show sample meter
  if (isDemo) {
    const demoBalance = 500;
    const demoUsed = 47;
    const demoRemaining = demoBalance - demoUsed;
    const demoPercent = Math.round((demoRemaining / demoBalance) * 100);
    container.innerHTML = `
      <div class="sms-credit-meter ${variant}">
        <div class="sms-meter-header">
          <div class="sms-meter-label">
            ${smsTitle}
            <span class="badge-promo">Free Credit</span>
          </div>
          <span class="sms-meter-amount">${demoRemaining} credits left</span>
        </div>
        <div class="sms-meter-bar-wrap">
          <div class="sms-meter-fill meter-green" style="width:${demoPercent}%;"></div>
        </div>
        <div class="sms-meter-footer">
          <span class="sms-meter-messages">${demoUsed} sent</span>
        </div>
      </div>
    `;
    return;
  }

  // Regular user with SMS credit
  const creditBalance = twilio.creditBalance || 0;
  const creditUsed = twilio.creditUsed || 0;
  const creditRemaining = Math.max(0, creditBalance - creditUsed);
  const messagesSent = twilio.messagesSent || 0;
  const isPromo = twilio.isPromo === true;
  const isProPlus = twilio.promoType === 'pro_plus';
  const promoExpiresAt = twilio.promoExpiresAt ? new Date(twilio.promoExpiresAt) : null;
  const promoExpired = promoExpiresAt && new Date() > promoExpiresAt;
  const isExhausted = twilio.status === 'exhausted' || creditRemaining <= 0 || promoExpired;
  const meterMax = creditBalance || 2500;
  const fillPercent = creditBalance > 0 ? Math.round((creditRemaining / meterMax) * 100) : 0;

  // Color thresholds based on percentage remaining of max
  let colorClass = 'meter-green';
  if (fillPercent <= 5) colorClass = 'meter-red';
  else if (fillPercent <= SMS_METER_LOW_PERCENT) colorClass = 'meter-yellow'; // same threshold as the top-up CTA

  // Exhausted state
  if (isExhausted) {
    // TASK-4665 — honest at zero: safety texts that went out at zero are charged,
    // so show "N credits over" rather than pretending the account sits at 0.
    const overdraft = smsCreditOverdraft(twilio);
    const amountLabel = overdraft > 0 ? `${overdraft.toLocaleString()} credits over` : '0 credits left';
    container.innerHTML = `
      <div class="sms-credit-meter ${variant} sms-meter-exhausted">
        <div class="sms-meter-header">
          <div class="sms-meter-label">
            ${smsTitle}
          </div>
          <span class="sms-meter-amount" style="color:var(--status-danger);" title="${overdraft > 0 ? 'Driver codes, PIN setup and clock-out alerts still send at zero and are charged; your next pack covers this first.' : ''}">${amountLabel}</span>
        </div>
        <div class="sms-meter-bar-wrap">
          <div class="sms-meter-fill meter-red" style="width:2%;"></div>
        </div>
        <div class="sms-meter-footer">
          <span class="sms-meter-messages" style="color:var(--status-danger);">${promoExpired ? 'Starter credit expired' : overdraft > 0 ? 'Crew texts paused. Driver codes and clock-out alerts still send; your next pack covers them first.' : messagesSent + ' messages sent'}</span>
          ${hideCta ? '' : (_smsPackControlForViewer().show
            ? `<button class="sms-meter-cta" onclick="window.startCheckout?.('sms_pack')">$25 Pack (1,500 SMS Messages)</button>`
            : (window.isIOSApp || isDemo ? '' : `<span class="sms-meter-messages">Ask the account owner to add SMS credits</span>`))}
        </div>
      </div>
    `;
    return;
  }

  // Active credit meter
  const isStarter = twilio.promoType === 'starter';
  const isVipComp = twilio.promoType === 'vip_comp'; // TASK-1166 — comped VIP carrier
  let promoBadge = '';
  if (isProPlus) {
    promoBadge = '<span class="badge-promo" style="background:linear-gradient(135deg,#3B82F6,#06B6D4);color:#fff;">Pro+ Perk</span>';
  } else if (isVipComp) {
    promoBadge = '<span class="badge-promo">Comped</span>';
  } else if (isStarter) {
    promoBadge = '<span class="badge-promo">Starter Credit</span>';
  } else if (isPromo) {
    promoBadge = '<span class="badge-promo">Bonus Credit</span>';
  }
  const expiryNote = isProPlus && promoExpiresAt
    ? `<span class="sms-meter-expiry">Expires ${promoExpiresAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>`
    : '';
  // Don't nag comped VIPs with an upgrade CTA while healthy — they SEE the real
  // balance deteriorate (transparency); the low/exhausted states surface the buy
  // button naturally when it actually matters.
  // TASK-4665-S3 — "the low states surface the buy button" was only true for
  // EXHAUSTED. A LOW balance now offers a top-up too, to the owner only.
  const ctaKind = smsMeterCtaKind({ isPromo, isProPlus, isVipComp, fillPercent, canBuy: _smsPackControlForViewer().show });
  const upgradeCta = hideCta ? '' : ctaKind === 'topup'
    ? `<button class="sms-meter-cta" onclick="window.startCheckout?.('sms_pack')">Top up — $25 Pack (1,500 SMS)</button>`
    : ctaKind === 'upgrade'
      ? `<button class="sms-meter-cta" onclick="window.showSmsUpgradeModal?.()">Upgrade to SMS Pack</button>`
      : '';

  container.innerHTML = `
    <div class="sms-credit-meter ${variant}">
      <div class="sms-meter-header">
        <div class="sms-meter-label">
          ${smsTitle}
          ${promoBadge}
        </div>
        <span class="sms-meter-amount">${creditRemaining.toLocaleString()} credits left</span>
      </div>
      <div class="sms-meter-bar-wrap">
        <div class="sms-meter-fill ${colorClass}" style="width:${Math.max(2, fillPercent)}%;"></div>
      </div>
      <div class="sms-meter-footer">
        <span class="sms-meter-messages">${messagesSent.toLocaleString()} sent ${expiryNote}</span>
        ${upgradeCta}
      </div>
    </div>
  `;
}

window.renderSmsCreditMeter = renderSmsCreditMeter;
// TASK-9473 — account.js reads real trial days; it can't import app.js (Pitfall #1).
window.getTrialStatus = getTrialStatus;

// TASK-1531 — THE client-side mute predicate. Mute lives in two places on a
// person doc: `notifications.muted` (nested, canonical) and `notifyMuted`
// (flat, legacy mirror — writers set both). Readers that checked only one
// flag were fail-open: a flat-only mute showed a MUTED badge in Roster and
// still got texted (2026-08-12 Notify-Crew incident class). Fail CLOSED:
// EITHER flag truthy = muted. Truthiness on purpose — `=== true` would fail
// open on a truthy non-boolean written by a legacy/import path.
// Server twin: `isNotificationMuted` in functions/src/services/notifications.js
// — client and server cannot share a module (Pitfall #1 / functions packaging),
// so the predicate exists exactly twice, each pointing at the other. Change
// BOTH or neither. Parity is asserted by tests/mute-slate.unit.cjs.
// Exposed on window (never a cross-`?v=` import) for roster.js + dispatch.js.
window.isNotificationMuted = function(person) {
  const p = person || {};
  return !!(p.notifyMuted || (p.notifications && p.notifications.muted));
};

// TASK-10027 muted-tenant UI hints (Chris 10/22) — what THIS COMPANY can send right now,
// so an office user can tell before and after acting what will and won't go out.
// Client twin of functions/src/services/notificationSuppression.js
// isTenantSuspended (now-only arm) + isTenantNoContact — change BOTH or neither;
// parity is pinned by tests/muted-tenant-ui-hints.unit.cjs. Reads the live tenant
// doc (AppState.tenantData, kept current by subscribeTenantSettings). Display only:
// the server decides every send.
//   'live'      — nothing paused.
//   'muted'     — notificationsSuspendedUntil is in the future: texts and crew
//                 emails are held; account emails (invites, verification) and
//                 driver setup/reset codes still go BY EMAIL, capped 20/day.
//   'noContact' — internal account: nothing goes to anyone, email or text.
//   textsRefused — TASK-10025: a HARD marker (listed id / internalNoContact) — the
//                 server refuses its texts for good. A restore-marked company inside
//                 its window is noContact too, but its reminder texts only WAIT
//                 (processReminders / opsReminders defer them), so textsRefused is false.
const _NO_CONTACT_TENANT_IDS = ['mmwwgj1lkr941cytz']; // server: NO_CONTACT_TENANT_IDS
function _sendModeMillis(v) {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v.getTime();
  if (typeof v === 'string') { const t = Date.parse(v); return isNaN(t) ? null : t; }
  if (typeof v === 'object') {
    if (typeof v.toMillis === 'function') { try { return v.toMillis(); } catch (_e) { return null; } }
    if (typeof v.seconds === 'number') return v.seconds * 1000 + Math.floor((v.nanoseconds || 0) / 1e6);
    if (typeof v._seconds === 'number') return v._seconds * 1000 + Math.floor((v._nanoseconds || 0) / 1e6);
  }
  return null;
}
window.getTenantSendMode = function(tenantData, tenantId, nowMs) {
  const t = (tenantData !== undefined ? tenantData : window.AppState?.tenantData) || {};
  const tid = tenantId !== undefined ? tenantId : window.AppState?.tenant;
  const now = Number.isFinite(nowMs) ? nowMs : Date.now();
  if (!tid || tid === 'demo') return { mode: 'live', untilMs: null, textsHeld: false, textsRefused: false };
  const until = _sendModeMillis(t.notificationsSuspendedUntil);
  const suspended = until !== null && now < until;
  // textsHeld: the server's isTenantSuspended is true, so no text goes out now.
  // TASK-10025 (re-applied from integ/2114 on2125): isTenantSuspended is true for
  // EVERY no-contact company, with or without the switch, so a hard-marker
  // company's texts are held too — and refused for good (textsRefused).
  if (_NO_CONTACT_TENANT_IDS.includes(tid) || t.internalNoContact === true) return { mode: 'noContact', untilMs: suspended ? until : null, textsHeld: true, textsRefused: true };
  if (!suspended) return { mode: 'live', untilMs: null, textsHeld: false, textsRefused: false };
  if (t.restoredAt || t.restoredFrom) return { mode: 'noContact', untilMs: until, textsHeld: true, textsRefused: false };
  return { mode: 'muted', untilMs: until, textsHeld: true, textsRefused: false };
};

// One notice, one look, every surface (driver codes, invites, reminders, crew
// texts, memos). Empty string on a live company. `context` picks the second
// sentence: what happens to THIS action.
const _SEND_NOTICE_COPY = {
  muted: {
    driverCode: 'Setup and reset codes go by email to the driver’s own address instead (up to 20 a day). No email on file? Use Show setup code and read it to them.',
    invite: 'Invites still go out by email (up to 20 a day). The text part is not sent.',
    reminder: 'A reminder text waits until texts are back on. The in-app reminder still shows.',
    crewText: 'Nothing sent from here reaches the crew right now. You can still copy the message and send it from your own phone.',
    memo: 'Memos are not delivered right now, by text or email.',
  },
  noContact: {
    driverCode: 'No code is emailed or texted. Use Show setup code and read it to the driver.',
    invite: 'An invite made here is not emailed or texted.',
    reminder: 'No reminder text goes out. The in-app reminder still shows.',
    // Restore-marked company inside its window: the server holds (defers) a
    // reminder text and sends it once sending is back on — never say it is dropped.
    reminderWaits: 'A reminder text waits until sending is back on for this company. The in-app reminder still shows.',
    crewText: 'Nothing sent from here reaches the crew. You can still copy the message and send it from your own phone.',
    memo: 'Memos are not delivered, by text or email.',
  },
};
function _sendModeUntilLabel(untilMs, nowMs) {
  if (!Number.isFinite(untilMs)) return '';
  const now = Number.isFinite(nowMs) ? nowMs : Date.now();
  if (untilMs - now > 366 * 86400000) return ''; // a far-future switch reads as "off", not a date
  try {
    const tz = window.AppState?.settings?.timezone || undefined;
    return ' until ' + new Date(untilMs).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: tz });
  } catch (_e) { return ''; }
}
window.tenantSendNoticeText = function(context, sendMode) {
  const s = sendMode || window.getTenantSendMode();
  if (!s || s.mode === 'live') return '';
  const copy = _SEND_NOTICE_COPY[s.mode] || {};
  const head = s.mode === 'noContact'
    ? 'This company is set to send nothing to anyone right now — no texts, no emails.'
    : `Texts are paused for this company${_sendModeUntilLabel(s.untilMs)}.`;
  // TASK-10025: only a HARD no-contact company has its reminder texts refused for
  // good; a restore-marked one inside its window has them wait (textsRefused false).
  const key = (s.mode === 'noContact' && context === 'reminder' && s.textsRefused === false) ? 'reminderWaits' : context;
  return `${head} ${copy[key] || ''}`.trim();
};
window.tenantSendNoticeHtml = function(context, sendMode) {
  const s = sendMode || window.getTenantSendMode();
  const text = window.tenantSendNoticeText(context, s);
  if (!text) return '';
  const nc = s.mode === 'noContact';
  const esc = (v) => String(v).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  return `<div class="tenant-send-notice" data-send-mode="${s.mode}" role="status" style="display:flex;gap:8px;align-items:flex-start;background:${nc ? '#FEF2F2' : '#FEF3C7'};border:1px solid ${nc ? '#FECACA' : '#FDE68A'};color:${nc ? '#991B1B' : '#78350F'};border-radius:8px;padding:8px 10px;margin:0 0 10px;font-size:0.85rem;line-height:1.4;text-align:left;">`
    + `<span class="material-icons" aria-hidden="true" style="font-size:18px;flex:0 0 auto;">${nc ? 'block' : 'notifications_paused'}</span>`
    + `<span style="flex:1;min-width:0;">${esc(text)}</span></div>`;
};
// The resend toast on a muted / no-contact company (Roster + Team Access share
// it). null on a live company — the caller's own wording stands.
window.inviteResendOutcome = function(resendData) {
  const mode = window.getTenantSendMode().mode;
  if (mode === 'live') return null;
  if (mode === 'noContact') return { type: 'error', text: 'Nothing was sent: this company is set to send nothing to anyone right now.' };
  if (resendData?.emailSent) return { type: 'success', text: 'Invite resent by email. No text was sent because texts are paused for this company.' };
  return { type: 'error', text: 'The invite email did not send. Texts are paused for this company and invite emails are limited to 20 a day. Check the address, or try again tomorrow.' };
};

// Fills every `[data-tenant-send-notice="<context>"]` slot under `root`
// (default: the whole page). Called on modal open and on every tenant-doc change.
window.paintTenantSendNotices = function(root) {
  const scope = root || document;
  if (!scope || typeof scope.querySelectorAll !== 'function') return;
  const s = window.getTenantSendMode();
  scope.querySelectorAll('[data-tenant-send-notice]').forEach((el) => {
    const html = window.tenantSendNoticeHtml(el.getAttribute('data-tenant-send-notice'), s);
    if (el.innerHTML !== html) el.innerHTML = html;
    el.style.display = html ? '' : 'none';
  });
};

window.isProAccount = isProAccount;
window.shouldBuildTickets = shouldBuildTickets;
window.isFounderAccount = isFounderAccount;
window.isVipAccount = isVipAccount;
window.hasAddOnPackage = hasAddOnPackage;

// ============================================
// TASK-9414 — ProjectsModule on demand, for AR/AP's "Add to project".
// ============================================
// The ONE write path for jobs.projectId is ProjectsModule.changeJobProject
// (TASK-9289), but window.ProjectsModule only exists once Dispatch has mounted
// (initProjects runs in the 'dispatch' case above). Billing can't import
// projects.js itself (Pitfall #1: a second instance with its own empty
// ProjectState). So app.js — which already owns the one projects.js instance —
// starts it on request and chains its teardown onto the CURRENT view's cleanup,
// exactly as the dispatch case composes it. Idempotent per view: a second call
// in the same view reuses the first. initProjects' listener is itself
// idempotent per tenant (projects.js loadProjects).
window.ensureProjectsModule = async function ensureProjectsModule() {
  const tenant = window.AppState?.tenant;
  if (!tenant) return null;
  const cur = window.AppState.viewCleanup;
  if (cur && cur.__projectsBootTenant === tenant && window.ProjectsModule) return window.ProjectsModule;
  const cleanupP = initProjects(tenant);
  // Keep the RESOLVED teardown so it runs SYNCHRONOUSLY on navigate. A deferred
  // (.then) teardown would fire AFTER the next view's initProjects — which, on
  // Billing → Dispatch, sees our listener still live, reuses it, and then has it
  // unsubscribed and ProjectState.projects emptied underneath it (independent
  // review, TASK-9414).
  let resolvedCleanup = null;
  Promise.resolve(cleanupP).then(fn => { resolvedCleanup = fn || (() => {}); }).catch(() => { resolvedCleanup = () => {}; });
  const composed = () => {
    try { cur?.(); } catch (_) { /* view already torn down */ }
    if (resolvedCleanup) { try { resolvedCleanup(); } catch (_) { /* already gone */ } return; }
    Promise.resolve(cleanupP)
      .then(fn => { try { fn?.(); } catch (_) { /* already gone */ } })
      .catch(() => { /* initProjects threw — nothing to unsubscribe */ });
  };
  composed.__projectsBootTenant = tenant;
  window.AppState.viewCleanup = composed;
  await cleanupP;
  return window.ProjectsModule || null;
};

// ============================================
// GLOBAL AUDIT LOG — all modules call this
// Writes to tenants/{id}/auditLog for universal tracking
// ============================================
// TASK-9766 — resolves true when the row was written, false when it was not
// (no tenant, or the write failed). Still never throws; callers that ignore the
// result behave exactly as before. maintenance.js auditTaskStatus reads it so a
// work-order status change whose audit row failed is not silent.
window.logAudit = async function(category, action, data = {}) {
  try {
    // AppState.tenant is the canonical tenant id field (not tenantId — that was a
    // shipped-broken typo that silently disabled the audit trail for ~3 weeks).
    const tenant = window.AppState?.tenant;
    if (!tenant) return false;
    // Telemetry-category writes get an `expiresAt` 90 days out so Firestore
    // TTL deletes them automatically (Architect's "unbounded growth" concern
    // from TASK-218). Business-event categories (ticket/roster/etc.) stay
    // indefinitely — those are compliance / audit-trail records, not signal.
    // TTL policy is enabled at the database level via gcloud against the
    // `expiresAt` field on this collection group.
    // TASK-1479 — STRIP undefined BEFORE the write. Firestore's addDoc THROWS on
    // an undefined field value (the SDK is not configured with
    // ignoreUndefinedProperties), and the catch below turns that throw into a
    // console.warn nobody reads. The result was a silently missing audit trail,
    // not a noisy one.
    //
    // This was not hypothetical: on the founder tenant, across 7,824 audit rows,
    // UPDATE_PERSON and UPDATE_TRUCK had **zero** — because both payloads carry
    // conditionally-undefined fields (`oldName: changed ? old : undefined`,
    // `terminated: data.terminated`) that are undefined on almost every edit,
    // while CREATE_PERSON/ASSET_DEACTIVATE — whose payloads coerce to booleans —
    // wrote fine. So person and vehicle EDITS were unauditable, including the
    // termination that started this investigation.
    //
    // Stripping (rather than making every call site defensive) fixes all ~135
    // actions at once and can't regress: a key that was never persistable is
    // simply absent, exactly as the caller intended by passing undefined.
    const writeData = {
      category,
      action,
      timestamp: Timestamp.now(),
      user: window.AppState?.user?.email || window.AppState?.driverPersonName || 'unknown',
      ..._stripUndefinedForFirestore(data || {}),
    };
    if (category === 'telemetry') {
      writeData.expiresAt = Timestamp.fromMillis(Date.now() + 90 * 24 * 60 * 60 * 1000);
    }
    // TASK-9422 — a managing compliance partner inside the carrier's app. The
    // rules' partner leg on auditLog CREATE requires exactly these two fields
    // (actorKind 'partner' + partnerUid == caller), pins `user` to the
    // partner's own email, and REFUSES self-asserted partnerFirm/actorName
    // (unverifiable client-side; the firm is on the server-written rows).
    // Written LAST so a caller-supplied `data` can't overwrite them.
    const _pctx = window.AppState?.partnerClientContext;
    if (_pctx && _pctx.tenantId === tenant) {
      writeData.actorKind = 'partner';
      writeData.partnerUid = window.AppState?.user?.uid || null;
      delete writeData.partnerFirm; delete writeData.partnerFirmUid;
      delete writeData.actorUid; delete writeData.actorName;
    }
    await addDoc(collection(db, 'tenants', tenant, 'auditLog'), writeData);
    return true;
  } catch (err) {
    // TASK-1479 — was console.warn with no action name, which is how a
    // 100%-failing audit action stayed invisible. Name the action so a failure
    // is greppable, and use error so it surfaces above the console noise floor.
    // Still non-throwing on purpose: an audit failure must never break the user's
    // save. The strip above removes the one cause that was firing in practice.
    console.error(`[Audit] FAILED to log ${category}/${action}:`, err && err.message);
    return false;
  }
};

// ============================================
// TASK-341 Company Holidays — shared evaluation helpers
// hr.js + clock.js both need to know "is this date a holiday for this person?"
// — but Pitfall #1 forbids cross-importing across modules with ?v= cache
// busters. This is the canonical home, exposed on window.AppHelpers so both
// readers reference the SAME tenant settings.companyHolidays array.
// Schema: tenants/{tid}.settings.companyHolidays = [
//   { id, date: 'YYYY-MM-DD', label, paid: bool, hoursMultiplier: number,
//     observedFor: 'all' | 'salary_only' | 'driver_only' }
// ]
// ============================================
window.AppHelpers = window.AppHelpers || {};

/**
 * Format a Date as YYYY-MM-DD in LOCAL time (avoids UTC off-by-one that
 * bit DispatchDeck twice this month). Use this any time you compare a
 * shift/PTO date against companyHolidays[].date.
 */
window.AppHelpers.formatLocalYmd = function(d) {
  if (!d) return '';
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** Return the current tenant's companyHolidays array (or []). */
window.AppHelpers.getCompanyHolidays = function() {
  const arr = window.AppState?.settings?.companyHolidays;
  return Array.isArray(arr) ? arr : [];
};

/**
 * `observedFor` filter shared by PTO + Clock readers.
 * Uses the canonical role lookup pattern: `p.crewType || p.role || p.roles?.[0]`.
 * Matches `'all'` always, `'driver_only'` when crewType is 'driver',
 * `'salary_only'` when person.hrEmploymentType === 'salary'.
 */
window.AppHelpers.holidayAppliesToPerson = function(holiday, person) {
  if (!holiday) return false;
  const scope = holiday.observedFor || 'all';
  if (scope === 'all') return true;
  if (!person) return false;
  if (scope === 'driver_only') {
    const crew = (person.crewType || person.role || person.roles?.[0] || '').toString().toLowerCase();
    return crew === 'driver';
  }
  if (scope === 'salary_only') {
    return person.hrEmploymentType === 'salary';
  }
  return false;
};

/**
 * Get the holiday row matching a given local-YMD date (string 'YYYY-MM-DD').
 * Returns the matched holiday or null. Pass an optional `person` to enforce
 * the `observedFor` filter; omit to get the raw row regardless of scope.
 */
window.AppHelpers.getHolidayForDate = function(ymd, person) {
  if (!ymd) return null;
  const holidays = window.AppHelpers.getCompanyHolidays();
  for (const h of holidays) {
    if (h?.date !== ymd) continue;
    if (person !== undefined && !window.AppHelpers.holidayAppliesToPerson(h, person)) continue;
    return h;
  }
  return null;
};

/**
 * True when the given local-YMD is a PAID holiday for the given person.
 * Used by PTO calc to skip burning PTO days that fall on paid holidays.
 */
window.AppHelpers.isPaidHolidayForPerson = function(ymd, person) {
  const h = window.AppHelpers.getHolidayForDate(ymd, person);
  return !!(h && h.paid);
};

/**
 * Hours-multiplier for a clock entry whose START DATE (local) is `ymd`.
 * v1 deliberately does NOT split shifts that span midnight — Skeptic 🟡
 * called the split a complexity trap; apply to the start date only.
 * Returns 1 (no premium) when no matching holiday or multiplier missing.
 */
window.AppHelpers.holidayMultiplierForDate = function(ymd, person) {
  const h = window.AppHelpers.getHolidayForDate(ymd, person);
  if (!h) return 1;
  const m = parseFloat(h.hoursMultiplier);
  return Number.isFinite(m) && m > 0 ? m : 1;
};

window.startCheckout = startCheckout;
window.openBillingPortal = openBillingPortal;
window.navigateToView = navigateToView;

function doNavigateToView(view) {
  // Remove compliance upgrade overlay when navigating
  const overlay = document.getElementById('compliance-upgrade-overlay');
  if (overlay) overlay.remove();

  // TASK-1290 — drop any locked-module preview before the new view renders so
  // the scrim never sits over the loading spinner. Re-evaluated at the bottom.
  removeLockedModulePreview();

  // TASK-9658 S1 — a record focus parked for ANOTHER view dies here: the user
  // went somewhere else, so it must not open later on an unrelated mount.
  if (window.AppState.pendingRecordFocus && window.AppState.pendingRecordFocus.view !== view) {
    window.AppState.pendingRecordFocus = null;
  }
  // TASK-9653 S1 — same rule for a parked open hint (AppState.pendingFocus).
  if (window.AppState.pendingFocus && window.AppState.pendingFocus.view !== view) {
    window.AppState.pendingFocus = null;
  }

  // Keep hash in sync so bottom nav href clicks trigger hashchange correctly
  const targetHash = `#/${view}`;
  if (window.location.hash !== targetHash) {
    window._skipHashChange = true;
    window.location.hash = targetHash;
  }

  // Workforce is a virtual view — load clock module then switch to workforce tab
  const isWorkforce = view === 'workforce';
  const loadView = isWorkforce ? 'clock' : view;

  // Cleanup previous view
  if (window.AppState.viewCleanup) {
    window.AppState.viewCleanup();
    window.AppState.viewCleanup = null;
  }

  // Update sidebar nav — highlight workforce or the actual view
  // AP module highlights the billing nav item since it's accessed through billing
  const navView = view === 'ap' ? 'billing' : view;
  DOM.navItems.forEach(item => {
    item.classList.toggle('active', item.dataset.view === navView);
  });

  // Update mobile nav
  DOM.mobileNavItems.forEach(item => {
    item.classList.toggle('active', item.dataset.view === navView);
  });

  // TASK-587 (supersedes TASK-377) — the driver bottom-nav + Actions sheet are
  // now persistent shell chrome on EVERY driver-PIN view (injected by
  // _syncDriverPinShellChrome after the view loads), instead of falling back
  // to the generic #mobile-nav (whose More sheet is gutted to Help + Sign Out
  // in PIN mode). Board views (dispatch/jobboard) still render their own
  // #driver-bottom-nav inside the view DOM — remove the shell copy BEFORE the
  // view renders so the id never double-renders.
  if (window.AppState?.driverModePin && (view === 'dispatch' || view === 'jobboard')) {
    document.getElementById('driver-pin-shell-chrome')?.remove();
  }

  // Load view content. Pass the original view name (e.g. 'workforce') alongside
  // the module-to-load (e.g. 'clock') so the stale-navigation guard inside
  // loadViewContent checks the right currentView — workforce sets
  // currentView='workforce' but loads the clock module, so the guard must
  // compare against 'workforce', not 'clock' (Code Reviewer COD blocker #1).
  loadViewContent(loadView, view);

  // TASK-587 — inject/refresh the persistent driver chrome for the view that
  // just rendered. Runs AFTER loadViewContent so the previous view's in-DOM
  // nav copy is already gone (all view inits are synchronous static imports —
  // same task, no intermediate paint). No-op for non-PIN sessions.
  if (window.AppState?.driverModePin) {
    _syncDriverPinShellChrome(view);
  }

  // After clock module loads, switch to workforce tab
  if (isWorkforce) {
    setTimeout(() => window.ClockModule?.switchTab?.('workforce'), 300);
  }

  // TASK-1290 — if this view is hold-to-dimmed, paint the "locked module"
  // preview over the (already rendered, still fully functional) content.
  // rAF so the scrim is measured against the settled #main-content box.
  requestAnimationFrame(() => syncLockedModulePreview(view));
}

// TASK-587 — persistent driver-PIN shell chrome (#driver-bottom-nav + Actions
// sheet) on non-board views. Before this, the rich driver nav existed ONLY
// inside the Job Board / dispatch driver-board view DOM, so navigating to
// Inspections destroyed it and TASK-377 fell back to the generic #mobile-nav,
// whose More sheet is CSS-gutted to Help + Sign Out in PIN mode (Chris
// screenshot 2026-06-12). Now every PIN view keeps the driver chrome:
//  - dispatch/jobboard: the view's own in-DOM nav (unchanged behavior).
//  - everything else (pretrip/maintenance): a body-level copy injected here,
//    with a "Job Board" home tab (the only way back once #mobile-nav hides)
//    and the Clock tab as a pure router back to the board (no live
//    clock-state source off the board — see getDriverBottomNavHTML opts
//    comment in dispatch.js).
// The existing CSS body.driver-mode-pin:has(#driver-bottom-nav) .mobile-nav
// {display:none} hides the generic nav wherever the driver nav exists; the
// classList belt below covers pre-:has() Safari (<iOS 15.4).
function _syncDriverPinShellChrome(view) {
  const mobileNav = document.getElementById('mobile-nav');
  const wrap = document.getElementById('driver-pin-shell-chrome');
  if (view === 'dispatch' || view === 'jobboard') {
    // Board views own their nav (also removed pre-load in doNavigateToView).
    if (wrap) wrap.remove();
    mobileNav?.classList.add('hidden');
    return;
  }
  const dm = window.DispatchModule;
  if (!dm?.getDriverBottomNavHTML || !dm?.getDriverActionsSheetHTML) {
    // Fallback = pre-TASK-587 (TASK-377) behavior: show the generic nav so the
    // driver is never stranded without ANY chrome (the v1.15.914 lesson).
    if (wrap) wrap.remove();
    mobileNav?.classList.remove('hidden');
    return;
  }
  // Guard by id — never create a second #driver-bottom-nav. A nav outside the
  // wrap can only be a board-view copy, which is impossible on non-board views
  // (loadViewContent just wiped #main-content), but check defensively.
  const existingNav = document.getElementById('driver-bottom-nav');
  if (existingNav && !(wrap && wrap.contains(existingNav))) {
    mobileNav?.classList.add('hidden');
    return;
  }
  const el = wrap || document.createElement('div');
  el.id = 'driver-pin-shell-chrome';
  // Rebuild each navigation — cheap, idempotent (sheet renders closed), and
  // keeps the active-tab highlight + feature gates current.
  el.innerHTML = dm.getDriverBottomNavHTML({ boardTab: true, clockRoutesToBoard: true, activeView: view })
    + dm.getDriverActionsSheetHTML();
  if (!el.parentNode) document.body.appendChild(el);
  mobileNav?.classList.add('hidden');
}

function loadViewContent(view, originalView) {
  // Slice L reverted 2026-05-28 — originalView arg kept for callers that
  // pass it explicitly (workforce → clock routing), but unused now because
  // all view inits are synchronous static imports. No more stale-nav guard
  // needed because there's no await between the user click and the init
  // call.
  originalView = originalView || view;
  // Show loading
  DOM.mainContent.innerHTML = `
    <div class="loading-screen">
      <div class="loading-spinner"></div>
      <p>Loading ${view}...</p>
    </div>
  `;

  // TASK-627: partner-provisioned carriers confirm pre-filled company info on
  // first login. View-AGNOSTIC — placed before the switch so it fires on
  // whatever view they land on (compliance-only carriers default to the
  // `compliance` view, not roster). Idempotent: gated on provisionedBy +
  // onboardingCompleted !== true + a once-per-session guard + no-other-modal,
  // so calling it on every navigation is safe. Inits are synchronous, so the
  // target view renders under the modal in the same tick.
  maybeShowPartnerProvisionConfirm();
  // TASK-9527 slice B -- a brand-new company's owner is asked the four Quick
  // setup questions once (same once-only + no-other-modal guards, own marker).
  maybeShowQuickSetupPrompt();

  switch (view) {
    case 'roster':
      window.AppState.viewCleanup = initRoster(DOM.mainContent, window.AppState.tenant);
      // Onboarding checklist for new accounts
      injectOnboardingChecklist();
      // Onboarding Hint 3: point at roster tabs on first roster visit
      if (window.AppState?.tenantData?.ownerProfileSetup !== false) {
        setTimeout(() => {
          if (window.AppState?._profileSetupActive) return;
          if (document.querySelector('.modal:not(.hidden)')) return;
          const rosterTabs = DOM.mainContent.querySelector('.roster-tabs');
          // TASK-9513 — "Build Your Fleet" only on an empty fleet (restored/demo tenants have one).
          if (rosterTabs) _tenantHasNoDocs('trucks').then(empty => { if (empty && rosterTabs.isConnected && !document.querySelector('.modal:not(.hidden)')) showOnboardingHint('onboard-roster-tabs', rosterTabs); });
        }, 800);
      }
      break;

    case 'pretrip':
      window.AppState.viewCleanup = initPretrip(DOM.mainContent, window.AppState.tenant);
      break;

    case 'maintenance':
      window.AppState.viewCleanup = initMaintenance(DOM.mainContent, window.AppState.tenant);
      break;

    case 'dispatch': {
      const _dispatchCleanup = initDispatch(DOM.mainContent, window.AppState.tenant);
      // initProjects is `async`, so it hands back a PROMISE of a cleanup fn, not a
      // cleanup fn — which is why its return was dropped on the floor here. That was
      // harmless while loadProjects was a one-shot getDocs. It is NOT harmless now
      // that projects.js holds a real onSnapshot: an un-torn-down listener would
      // outlive every navigate-away for the rest of the session. Compose both so one
      // viewCleanup() call tears down dispatch AND projects.
      const _projectsCleanup = initProjects(window.AppState.tenant);
      window.AppState.viewCleanup = () => {
        try { _dispatchCleanup?.(); } catch (_) { /* dispatch already torn down */ }
        Promise.resolve(_projectsCleanup)
          .then(fn => { try { fn?.(); } catch (_) { /* already gone */ } })
          .catch(() => { /* initProjects itself threw — no listener to unsubscribe */ });
      };
      // Onboarding Hint 2: point at Add Job button on first dispatch visit
      setTimeout(() => {
        if (window.AppState?._profileSetupActive) return;
        if (document.querySelector('.modal:not(.hidden)')) return;
        const addJobBtn = DOM.mainContent.querySelector('[onclick*="showAddJob"]');
        // TASK-9513 — "Create Your First Job" only when the tenant has no jobs at all.
        if (addJobBtn) _tenantHasNoDocs('jobs').then(empty => { if (empty && addJobBtn.isConnected && !document.querySelector('.modal:not(.hidden)')) showOnboardingHint('onboard-first-job', addJobBtn); });
      }, 800);
      break;
    }

    case 'jobboard':
      window.AppState.viewCleanup = initJobBoard(DOM.mainContent, window.AppState.tenant);
      break;

    case 'clock':
      window.AppState.viewCleanup = initClock(DOM.mainContent, window.AppState.tenant);
      break;

    case 'billing':
      window.AppState.viewCleanup = initBilling(DOM.mainContent, window.AppState.tenant);
      break;

    case 'timeoff':
      window.AppState.viewCleanup = initTimeOff(DOM.mainContent, window.AppState.tenant);
      break;

    case 'hr':
      window.AppState.viewCleanup = initHR(DOM.mainContent, window.AppState.tenant);
      break;

    case 'crm':
      window.AppState.viewCleanup = initCRM(DOM.mainContent, window.AppState.tenant);
      break;

    case 'bi':
      window.AppState.viewCleanup = initBI(DOM.mainContent, window.AppState.tenant);
      break;

    case 'compliance':
      window.AppState.viewCleanup = initCompliance(DOM.mainContent, window.AppState.tenant);
      break;

    case 'dot-advisor':
      window.AppState.viewCleanup = initDotAdvisor(DOM.mainContent, window.AppState.tenant);
      break;

    case 'ap':
      window.AppState.viewCleanup = initAP(DOM.mainContent, window.AppState.tenant);
      break;

    case 'receipts':
      // TASK-9778 — Receipts & Expenses: the receipt inbox + submitted-expense
      // review, and nothing else of AP (no bills, vendors, bank, checks).
      window.AppState.viewCleanup = initReceiptsView(DOM.mainContent, window.AppState.tenant);
      break;

    case 'account':
      initAccount(DOM.mainContent, window.AppState.tenant);
      break;

    case 'approvals': // TASK-10249 — lazy page; the server gates every approve
      _lazyImportWithRetry(() => __ddChunkWrap(import('./views/approvals.js?v=2')), 'approvals')
        .then((m) => { if (window.AppState.currentView === 'approvals') m.initApprovals(DOM.mainContent); })
        .catch(() => showToast('Could not open Approvals. Refresh the page to try again.', 'error'));
      break;

    case 'payroll':
      // TASK-850B — DD Payroll. Admin-only surface: the view renders an
      // access message for non-admins and every payroll callable enforces
      // isTenantAdmin server-side.
      initPayroll(DOM.mainContent, window.AppState.tenant);
      break;

    case 'mytoday':
    case 'my-today':
      // TASK-355 2026-05-22: My Today killed per unanimous COD verdict. Job
      // Board (jobboard.js, member-aware) is the canonical personId-scoped
      // daily view now. Redirect any legacy bookmarks or back-button hits.
      // Use navigateToView so the regular allow-list / hash sync runs.
      setTimeout(() => navigateToView('jobboard'), 0);
      return;

    default:
      // Show coming soon for unimplemented modules
      DOM.mainContent.innerHTML = `
        <div class="card">
          <div class="card-header">${capitalizeFirst(view)}</div>
          <div class="empty-state">
            <div class="empty-state-icon">🚧</div>
            <div class="empty-state-title">Coming Soon</div>
            <div class="empty-state-text">
              The ${view} module is being built. Check back soon!
            </div>
          </div>
        </div>
      `;
      break;
  }
}

// ============================================
// EVENT LISTENERS
// ============================================
function setupEventListeners() {
  // Login form
  DOM.loginForm.addEventListener('submit', handleLogin);

  // Signup form
  DOM.signupForm.addEventListener('submit', handleSignup);

  // TASK-9861 — roster-match panel: Driver Code, or a deliberate "own company".
  document.getElementById('signup-roster-goto-driver')?.addEventListener('click', () => {
    hideSignupRosterMatch();
    window.showDriverPinForm();
  });
  document.getElementById('signup-roster-own-company')?.addEventListener('click', () => {
    const email = (document.getElementById('signup-email')?.value || '').trim();
    setSignupRosterOverride(email);
    hideSignupRosterMatch();
    if (typeof DOM.signupForm.requestSubmit === 'function') DOM.signupForm.requestSubmit();
    else document.getElementById('btn-signup')?.click();
  });
  document.getElementById('signup-email')?.addEventListener('input', () => {
    window._signupRosterOverrideEmail = '';
    hideSignupRosterMatch();
  });
  document.getElementById('social-roster-own-company')?.addEventListener('click', () => {
    window._socialStartingOwnCompany = true;
    document.getElementById('social-roster-match')?.classList.add('hidden');
    document.getElementById('btn-social-company-submit')?.click();
  });

  // Demo button
  document.getElementById('btn-demo').addEventListener('click', handleDemoLogin);

  // Social company name modal submit
  const socialCompanyBtn = document.getElementById('btn-social-company-submit');
  if (socialCompanyBtn) {
    socialCompanyBtn.addEventListener('click', () => {
      const companyName = document.getElementById('social-company-name').value.trim();
      if (!companyName) {
        showToast('Please enter your company name', 'error');
        return;
      }
      if (window._pendingSocialUser) {
        completeSocialSignup(window._pendingSocialUser, companyName);
      }
    });
  }
  // Allow Enter key in company name input
  const socialCompanyInput = document.getElementById('social-company-name');
  if (socialCompanyInput) {
    socialCompanyInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        socialCompanyBtn.click();
      }
    });
  }

  // Link account modal
  const linkAccountBtn = document.getElementById('btn-link-account');
  if (linkAccountBtn) {
    linkAccountBtn.addEventListener('click', handleLinkAccount);
  }
  const linkAccountPwInput = document.getElementById('link-account-password');
  if (linkAccountPwInput) {
    linkAccountPwInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleLinkAccount();
      }
    });
  }

  // Logout button
  document.getElementById('btn-logout').addEventListener('click', handleLogout);

  // Help button (opens Help modal with FAQ, Ask AI, Import)
  document.getElementById('btn-help').addEventListener('click', () => {
    showHelpModal();
  });

  // Settings button
  const settingsBtn = document.getElementById('btn-settings');
  if (settingsBtn) {
    settingsBtn.addEventListener('click', openSettingsModal);
  }

  // Toggle login/signup
  if (window.isIOSApp) {
    // Hide Microsoft sign-in on iOS (no native plugin for it)
    const msBtn = document.querySelector('[onclick*="microsoft"]');
    if (msBtn) msBtn.style.display = 'none';
  }
  document.getElementById('show-signup').addEventListener('click', (e) => {
    e.preventDefault();
    DOM.loginForm.classList.add('hidden');
    DOM.signupForm.classList.add('hidden');
    document.getElementById('driver-pin-form')?.classList.add('hidden');
    document.getElementById('auth-tabs')?.classList.add('hidden');
    document.getElementById('auth-tab-hint')?.classList.add('hidden'); // TASK-4742
    document.getElementById('signup-step-1').classList.remove('hidden');
    hideAuthError();
    // GA4: Track signup intent
    if (typeof gtag === 'function') gtag('event', 'signup_started', { event_category: 'engagement' });
  });

  document.getElementById('show-login').addEventListener('click', (e) => {
    e.preventDefault();
    DOM.signupForm.classList.add('hidden');
    document.getElementById('signup-step-1').classList.add('hidden');
    switchAuthTab('signin');
  });

  // Show login from plans page
  const showLoginFromPlans = document.getElementById('show-login-from-plans');
  if (showLoginFromPlans) {
    showLoginFromPlans.addEventListener('click', (e) => {
      e.preventDefault();
      document.getElementById('signup-step-1').classList.add('hidden');
      switchAuthTab('signin');
    });
  }

  // Plan card selection
  document.querySelectorAll('.plan-card input[name="selected-plan"]').forEach(radio => {
    radio.addEventListener('change', updateSelectedPlan);
  });

  // Nav items
  DOM.navItems.forEach(item => {
    item.addEventListener('click', (e) => {
      e.preventDefault();
      window.location.hash = '/' + item.dataset.view;
    });
  });

  // Mobile nav items
  DOM.mobileNavItems.forEach(item => {
    item.addEventListener('click', (e) => {
      e.preventDefault();
      window.location.hash = '/' + item.dataset.view;
    });
  });

  // Mobile More button
  const mobileMoreBtn = document.getElementById('mobile-nav-more');
  if (mobileMoreBtn) {
    mobileMoreBtn.addEventListener('click', () => {
      console.log('[Mobile] More button clicked, showing menu');
      DOM.mobileMoreMenu.classList.remove('hidden');
    });
  } else {
    console.warn('[Mobile] More button not found!');
  }

  // Mobile More menu items
  document.querySelectorAll('.mobile-more-item[data-view]').forEach(item => {
    item.addEventListener('click', (e) => {
      e.preventDefault();
      DOM.mobileMoreMenu.classList.add('hidden');
      window.location.hash = '/' + item.dataset.view;
    });
  });

  // Mobile More menu backdrop close
  const mobileMoreBackdrop = document.querySelector('.mobile-more-backdrop');
  if (mobileMoreBackdrop) {
    mobileMoreBackdrop.addEventListener('click', () => {
      DOM.mobileMoreMenu.classList.add('hidden');
    });
  }

  // Mobile Settings button
  const mobileSettingsBtn = document.getElementById('mobile-settings-btn');
  if (mobileSettingsBtn) {
    mobileSettingsBtn.addEventListener('click', () => {
      console.log('[Settings] Mobile settings button clicked');
      DOM.mobileMoreMenu.classList.add('hidden');
      openSettingsModal();
    });
  } else {
    console.warn('[Settings] Mobile settings button not found!');
  }

  // Mobile Help button
  const mobileHelpBtn = document.getElementById('mobile-help-btn');
  if (mobileHelpBtn) {
    mobileHelpBtn.addEventListener('click', () => {
      DOM.mobileMoreMenu.classList.add('hidden');
      showHelpModal();
    });
  }

  // Mobile logout button
  const mobileLogoutBtn = document.getElementById('mobile-logout-btn');
  if (mobileLogoutBtn) {
    mobileLogoutBtn.addEventListener('click', () => {
      DOM.mobileMoreMenu.classList.add('hidden');
      handleLogout();
    });
  }

  // Fix scroll propagation - ensure wheel events scroll the page for fleet/roster views
  document.addEventListener('wheel', (e) => {
    // Check if we're in a fleet section, roster section, or on a card
    const scrollableArea = e.target.closest('.roster-card, .fleet-card, .fleet-grid, #fleet-all-section, .fleet-attention-section, .roster-list, #trailers-view, #vehicles-view');
    if (scrollableArea) {
      // Don't interfere if we're in a modal or explicit scrollable container
      const scrollableParent = scrollableArea.closest('.modal-body, .modal-form-scroll');
      if (scrollableParent) return;

      // Scroll the window manually
      window.scrollBy(0, e.deltaY);
    }
  }, { passive: true });
}

// ============================================
// UI HELPERS
// ============================================
function showLoading() {
  DOM.loadingScreen.classList.remove('hidden');
}

function hideLoading() {
  DOM.loadingScreen.classList.add('hidden');
  // TASK-411 boot watchdog: reaching a rendered state = healthy boot. Signal it
  // (idempotent; this is the universal splash→content transition across login /
  // sidebar / public-access / driver paths) and clear the recovery guard so a
  // future white-screen can self-heal again.
  window.__appBooted = true;
  _clearBootRecoveryGuards();
}

// TASK-1361 C — a healthy boot must clear BOTH recovery loop guards, or the next
// genuine white-screen skips the self-heal. The ?_recover URL param is the PRIMARY
// guard (index.html boot watchdog reads it before sessionStorage), so leaving it in
// the address bar / a bookmark / a PWA start_url disabled self-heal permanently.
// Strip ONLY _recover: other params (?access=TOKEN driver links) and the hash route
// (#/billing) must survive, so never rebuild from bare pathname.
function _clearBootRecoveryGuards() {
  try { sessionStorage.removeItem('dd_boot_recovery'); } catch (e) {}
  // showAuthModal() reloads public-token pages from the ORIGINAL_URL_PARAMS snapshot
  // (not the live URL), so the snapshot must lose _recover too or that reload would
  // re-stamp the stale guard. Other snapshot params (access, timeoff, …) stay.
  try { if (typeof ORIGINAL_URL_PARAMS !== 'undefined') ORIGINAL_URL_PARAMS.delete('_recover'); } catch (e) {}
  try {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('_recover')) return;
    params.delete('_recover');
    const qs = params.toString();
    window.history.replaceState(window.history.state, document.title,
      window.location.pathname + (qs ? '?' + qs : '') + window.location.hash);
  } catch (e) {}
}

function showSidebar() {
  window.__appBooted = true; // TASK-411 boot watchdog signal (belt-and-suspenders to hideLoading)
  _clearBootRecoveryGuards(); // TASK-1361 C
  // Start version checking after auth (not on login screen)
  if (!window._versionCheckStarted) {
    window._versionCheckStarted = true;
    startVersionChecking();
  }

  const isDriverMode = window.AppState?.driverMode || window.AppState?.driverModePin || window.AppState?.isPublicAccess;

  // In driver mode, hide the sidebar completely - they only get dispatch and pretrip via bottom nav
  if (isDriverMode) {
    if (DOM.sidebar) DOM.sidebar.classList.add('hidden');
  } else {
    if (DOM.sidebar) DOM.sidebar.classList.remove('hidden');
  }

  if (DOM.mobileNav) DOM.mobileNav.classList.remove('hidden');
  if (DOM.mainContent) DOM.mainContent.classList.remove('hidden');

  // In driver mode, only show allowed nav items in mobile nav
  if (isDriverMode) {
    const tokenModules = window.AppState?.tokenModules || [];
    const allowedViews = [...new Set(['dispatch', 'pretrip', ...tokenModules])];
    // Add maintenance if driver has allowMaintenance grant OR ≥1 assigned task (TASK-774 Gap B)
    if (window.driverCanSeeMaintenance?.() && !allowedViews.includes('maintenance')) {
      allowedViews.push('maintenance');
    }
    // Add jobboard if driver has clock access (so they can clock in from there)
    if (window.driverHasClockAccess?.() && !allowedViews.includes('jobboard')) {
      allowedViews.push('jobboard');
    }
    DOM.navItems.forEach(item => {
      if (!allowedViews.includes(item.dataset.view)) {
        item.style.display = 'none';
      } else {
        item.style.display = '';
      }
    });
    DOM.mobileNavItems.forEach(item => {
      if (!allowedViews.includes(item.dataset.view)) {
        item.style.display = 'none';
      } else {
        item.style.display = ''; // Show allowed items
      }
    });
    // Hide the "more" button in driver mode (unless token has extra modules or clock access adds jobboard)
    const mobileMoreBtn = document.getElementById('mobile-nav-more');
    if (mobileMoreBtn) mobileMoreBtn.style.display = allowedViews.length > 2 ? '' : 'none';
  } else {
    // Reset any inline display:none left over from a prior driver-mode session
    // (TWA/PWA may persist DOM state across resumes; without this, nav items
    // hidden by the driver-mode branch above stay hidden after sign-in as owner)
    DOM.navItems.forEach(item => { item.style.display = ''; });
    DOM.mobileNavItems.forEach(item => { item.style.display = ''; });
    const _mobileMoreBtn = document.getElementById('mobile-nav-more');
    if (_mobileMoreBtn) _mobileMoreBtn.style.display = '';
    // Update nav for PIN-based access
    updateNavForPinAccess();
  }
}

function hideSidebar() {
  if (DOM.sidebar) DOM.sidebar.classList.add('hidden');
  if (DOM.mobileNav) DOM.mobileNav.classList.add('hidden');
  if (DOM.mainContent) DOM.mainContent.classList.add('hidden');
}

function showAuthModal() {
  // GUARD: Never show login on public access pages (driver links, timeoff, etc.)
  if (window.AppState?.isPublicAccess || window.AppState?.driverMode) {
    console.warn('[Auth] showAuthModal blocked — public access page active');
    return;
  }
  // GUARD: If original URL had a public token, don't show login (something went wrong, but login won't help a driver)
  if (ORIGINAL_URL_PARAMS.get('access') || ORIGINAL_URL_PARAMS.get('timeoff') || ORIGINAL_URL_PARAMS.get('approve') || ORIGINAL_URL_PARAMS.get('order') || ORIGINAL_URL_PARAMS.get('sign') || ORIGINAL_URL_PARAMS.get('clock') || ORIGINAL_URL_PARAMS.get('docs')) {
    console.warn('[Auth] showAuthModal blocked — URL has public token, reloading with original params');
    window.location.replace(window.location.pathname + '?' + ORIGINAL_URL_PARAMS.toString() + ORIGINAL_HASH);
    return;
  }

  // Dismiss any banners from previous session
  dismissTrialBanner();
  dismissVerificationBanner();
  document.getElementById('update-banner')?.remove();

  // Hide the carrier-assistant FAB + collapse its panel on the login screen.
  // initCarrierAssistant() reveals it (display:flex) only for authed owner/admin, but
  // that inline style persists across a return-to-login (logout / token expiry / session
  // recheck) — every such path lands here, so this is the single point that guarantees
  // the assistant never lingers on the auth screen. Also drop the lock + sales FABs
  // (handleLogout clears them, but expiry/recheck paths may not).
  const _ddFab = document.getElementById('dd-assistant-fab');
  if (_ddFab) {
    _ddFab.style.display = 'none';
    _ddFab.classList.remove('is-open');
    // Restore the closed-state glyph/aria — if the user logged out while the panel was
    // OPEN, carrierAssistantToggle had swapped the brain glyph for the X + set aria "Close";
    // initCarrierAssistant doesn't reset those on re-show, so do it here (COD S? Code ⚠️).
    const _g = _ddFab.querySelector('.dd-assistant-fab-glyph');
    const _c = _ddFab.querySelector('.dd-assistant-fab-close');
    if (_g) _g.style.display = '';
    if (_c) _c.style.display = 'none';
    _ddFab.setAttribute('aria-label', 'Open Ask DispatchDeck');
    _ddFab.title = 'Ask DispatchDeck';
  }
  // TASK-9985 — no count, label or queued badge filter survives into the next session.
  if (typeof carrierAssistantState === 'object' && carrierAssistantState) carrierAssistantState.pendingFilingCount = 0;
  { const _b = document.getElementById('dd-assistant-badge'); if (_b) { _b.style.display = 'none'; _b.textContent = ''; _b.removeAttribute('title'); } }
  if (window.AppState) { window.AppState.pendingHrDirectoryFilter = null; window.AppState.pendingRosterFilter = null; }
  document.getElementById('dd-assistant-panel')?.classList.remove('is-open');
  if (typeof carrierAssistantState === 'object' && carrierAssistantState) carrierAssistantState.open = false;
  // Clear the prior tenant's AI conversation buffer — on a shared device the next user must
  // not inherit Admin A's chat history/context (COD Adversary: cross-tenant history leak).
  if (typeof carrierAssistantReset === 'function') { try { carrierAssistantReset(); } catch (_) {} }
  // TASK-9268 — and the AI history modal: wipe rows, close it, and invalidate
  // any in-flight owner request so a late response cannot land in the next
  // account's modal (COD 2026-10-02 round 2, Code Reviewer).
  if (typeof aiHistoryResetForAccountChange === 'function') { try { aiHistoryResetForAccountChange(); } catch (_) {} }
  document.body.classList.remove('has-assistant-fab');
  document.getElementById('lock-fab')?.remove();
  teardownLockSidebarLogo(); // TASK-763 — clear sidebar lock pill + reset logo art on tenant-switch (no stale "Locked")
  document.getElementById('sales-fab')?.remove();
  try { removeLockedModulePreview(); } catch (_) {} // TASK-1290 — never leak the preview onto the login screen

  // TASK-903 — tear down any lingering driver-PIN chrome + state. showAuthModal()
  // is the common return-to-login path; without this the driver bottom nav (and
  // body.driver-mode-pin styling) leaked onto the Driver Code Login screen on
  // expiry/recheck/abandoned-recovery paths that don't route through handleLogout /
  // exitDriverPinMode. Safe no-op when no PIN session is active. NOTE (TASK-905):
  // silent PIN recovery short-circuits + returns BEFORE reaching showAuthModal, so
  // this only fires on a genuine logout or an ABANDONED reconnect — never a
  // transient drop that recovers.
  _teardownDriverPinChrome();

  // Close ALL other modals first (except password-reset which should stay visible)
  document.querySelectorAll('.modal').forEach(m => {
    if (m.id !== 'auth-modal' && m.id !== 'password-reset-modal') {
      m.classList.add('hidden');
    }
  });
  document.body.classList.remove('modal-open');
  document.documentElement.classList.remove('modal-open');

  // Reset login button state (in case it was stuck from previous attempt)
  const loginBtn = document.getElementById('btn-login');
  if (loginBtn) {
    loginBtn.disabled = false;
    loginBtn.innerHTML = 'Sign In';
  }

  // Reset signup button state (in case it was stuck in "Creating account...")
  const signupBtn = document.getElementById('btn-signup');
  if (signupBtn) {
    signupBtn.disabled = false;
    signupBtn.classList.remove('btn-loading');
    signupBtn.innerHTML = 'Start 30-Day Trial';
  }

  // TASK-4775 — consume the ?signup=true marketing-CTA intent here, at the single
  // point that decides what the auth modal opens on. Read it BEFORE the reset below
  // and clear it unconditionally: one page load, one honoured intent. A second
  // showAuthModal() in the same tab (session expiry, logout, a failed PIN reconnect)
  // must fall back to Sign In, never re-open signup.
  const _signupIntent = window.AppState?.pendingSignupIntent === true
    // A driver-PIN session — live or reconnecting — outranks a marketing param.
    // The driver has no carrier account and must not be pushed to create one
    // (that is the TASK-4742 duplicate-company trap, from the other direction).
    && window.AppState?.driverModePin !== true
    && window.AppState?._pinReconnectFailed !== true;
  if (window.AppState) window.AppState.pendingSignupIntent = false;

  // Reset to Sign In tab on open (tabs give Driver Code equal prominence)
  document.getElementById('signup-form')?.classList.add('hidden');
  if (_signupIntent) {
    // Same element set as the #show-signup click handler, so the two entry points
    // into signup leave the modal in an identical state.
    document.getElementById('login-form')?.classList.add('hidden');
    document.getElementById('driver-pin-form')?.classList.add('hidden');
    document.getElementById('auth-tabs')?.classList.add('hidden');
    document.getElementById('auth-tab-hint')?.classList.add('hidden');
    document.getElementById('signup-step-1')?.classList.remove('hidden');
  } else {
    document.getElementById('signup-step-1')?.classList.add('hidden');
    if (typeof switchAuthTab === 'function') {
      switchAuthTab('signin');
    } else {
      document.getElementById('login-form')?.classList.remove('hidden');
    }
  }

  // One-time attention wiggle on Driver Code tab so confused drivers notice it.
  // Skipped when we opened on signup — the tab strip is hidden there, and the
  // driver escape at the Company Name field (TASK-4742) is the route out instead.
  if (!_signupIntent) setTimeout(() => {
    const driverTab = document.getElementById('auth-tab-driver');
    if (driverTab && !driverTab.classList.contains('active')) {
      driverTab.classList.add('wiggle');
      setTimeout(() => driverTab.classList.remove('wiggle'), 800);
    }
  }, 900);

  // Reset demo button state (in case it was stuck in "Loading demo...")
  // TASK-4847: class toggle, not innerHTML — see handleDemoLogin().
  const demoBtn = document.getElementById('btn-demo');
  if (demoBtn) {
    demoBtn.disabled = false;
    demoBtn.classList.remove('is-loading');
  }

  // Hide error messages
  hideAuthError();

  DOM.authModal.classList.remove('hidden');
}

// TASK-4775 — showAuthModal is the canonical return-to-login path (12 call sites:
// logout, session expiry, stale-anon signout, abandoned PIN reconnect). Exposed on
// window alongside switchAuthTab / showDriverPinForm / hideDriverPinForm so the
// one-shot signup-intent guarantee can be exercised against the REAL function
// rather than a re-implementation of it.
window.showAuthModal = showAuthModal;

function hideAuthModal() {
  DOM.authModal.classList.add('hidden');
}

// ============================================
// DRIVER PIN ACCESS
// ============================================
function switchAuthTab(tab) {
  document.getElementById('login-form')?.classList.add('hidden');
  document.getElementById('driver-pin-form')?.classList.add('hidden');
  document.getElementById('signup-step-1')?.classList.add('hidden');
  document.getElementById('signup-form')?.classList.add('hidden');
  // TASK-10096 — the set-PIN panel and its "PIN saved" note are app.js-built
  // siblings of the forms; every tab switch (and showAuthModal's normal reset,
  // which routes here) must clear them too, or they linger on the next screen.
  document.getElementById('driver-set-pin-form')?.classList.add('hidden');
  const _pinSavedNote = document.getElementById('driver-pin-saved-msg');
  if (_pinSavedNote) _pinSavedNote.style.display = 'none';
  document.getElementById('auth-tabs')?.classList.remove('hidden');
  // TASK-4742 — the "Driver? Your PIN goes under Driver Code." bridge line lives
  // as a SIBLING of #auth-tabs (keeping it inside would break the tab strip's
  // border-bottom), so its visibility has to be driven explicitly wherever the
  // tabs are shown or hidden.
  document.getElementById('auth-tab-hint')?.classList.remove('hidden');

  document.querySelectorAll('.auth-tab').forEach(t => {
    t.classList.remove('active');
    t.setAttribute('aria-selected', 'false');
  });

  if (tab === 'driver') {
    // Already on the Driver Code tab — the bridge line is just noise here.
    document.getElementById('auth-tab-hint')?.classList.add('hidden');
    document.getElementById('driver-pin-form')?.classList.remove('hidden');
    const btn = document.getElementById('auth-tab-driver');
    if (btn) { btn.classList.add('active'); btn.setAttribute('aria-selected', 'true'); }
    setTimeout(() => document.getElementById('driver-pin-input')?.focus(), 50);
  } else {
    document.getElementById('login-form')?.classList.remove('hidden');
    const btn = document.getElementById('auth-tab-signin');
    if (btn) { btn.classList.add('active'); btn.setAttribute('aria-selected', 'true'); }
  }
  hideAuthError();
}
window.switchAuthTab = switchAuthTab;

let _driverPinEnterBound = false;
function bindDriverPinEnterKey() {
  if (_driverPinEnterBound) return;
  const input = document.getElementById('driver-pin-input');
  if (!input) return;
  input.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter') return;
    if (e.isComposing || e.keyCode === 229) return;
    if (e.repeat) return;
    e.preventDefault();
    submitDriverPin();
  });
  _driverPinEnterBound = true;
}

function showDriverPinForm() {
  switchAuthTab('driver');
  bindDriverPinEnterKey();
}
window.showDriverPinForm = showDriverPinForm;

// Generation token + in-flight flag so users can bail out of a slow PIN check
// (e.g. typed wrong code, hit "Back to Sign In" while it was still loading).
let _driverPinReqId = 0;
let _driverPinInFlight = false;
let _driverPinOriginalBtnHTML = '';

function _cancelDriverPinRequest() {
  if (!_driverPinInFlight) return false;
  _driverPinReqId++; // invalidates the await chain in submitDriverPin
  _driverPinInFlight = false;
  window.AppState.driverPinLoginInProgress = false;
  const btn = document.getElementById('btn-submit-pin');
  if (btn) {
    btn.disabled = false;
    if (_driverPinOriginalBtnHTML) btn.innerHTML = _driverPinOriginalBtnHTML;
  }
  return true;
}

function hideDriverPinForm() {
  _cancelDriverPinRequest();
  document.getElementById('driver-pin-input').value = '';
  switchAuthTab('signin');
}
window.hideDriverPinForm = hideDriverPinForm;

/**
 * Establish the driver session + enter the portal. Shared by the normal PIN
 * login path AND the post-setDriverPin path (both receive the same session
 * shape: {token, personName, tenantId, personId, addons, isTrialing,
 * allowMaintenance}). `btn` is the button whose label we update through the
 * sign-in steps; `isStale()` aborts cleanly if a newer submit superseded us.
 */
// 2026-07-09 (Chris live) — PIN entry/recovery set driverModePin + tenant/person
// state OPTIMISTICALLY, but their failure exits (auth/link error, TASK-416
// rule-visibility timeout, recovery exhaustion) only removed the body CSS class
// (or nothing). Result: a slow/failed entry stranded a ZOMBIE half-state —
// driverModePin still true, so the dispatch driver board rendered its
// "Viewing as" header, while the CSS class (and with it the entire driver
// bottom nav, body:not(.driver-mode-pin) hides it) was gone: a driver board
// with NO menu. This rolls back ALL the optimistic state. The localStorage
// token is deliberately PRESERVED (TASK-416: reopening the app retries without
// re-typing the code). Field set mirrors _teardownDriverPinChrome's AppState
// residue list (minus the token + chrome teardown, which full exit owns).
function _rollbackDriverPinOptimisticState() {
  const as = window.AppState;
  as.driverModePin = false;
  as.driverPersonId = null;
  as.driverPersonName = null;
  as.driverAccessToken = null;
  as.driverAddons = {};
  as.driverIsTrialing = false;
  as.driverAllowMaintenance = false;
  as.driverHasAssignedMaintenance = false;
  as.tenant = null; // every caller ends signed-out/failed — tenant context is dead
  document.body.classList.remove('driver-mode-pin');
}

async function _enterDriverPortalWithSession(sessionData, { btn, isStale }) {
  const _isStale = typeof isStale === 'function' ? isStale : () => false;
  const _setBtn = (html) => { if (btn) btn.innerHTML = html; };

  const _rollbackPinEntryState = _rollbackDriverPinOptimisticState;

  // Set driver mode state (PIN-based)
  window.AppState.driverModePin = true;
  window.AppState.driverPersonId = sessionData.personId;
  window.AppState.driverPersonName = sessionData.personName;
  window.AppState.tenant = sessionData.tenantId;
  window.AppState.driverAccessToken = sessionData.token;
  // TASK-402 leak fix — the carrier ops assistant FAB is owner/admin-only
  // (carrierAssistantEligible excludes driver mode), but this path never
  // re-evaluated it, so a FAB shown in a prior owner session in this same
  // SPA instance leaked into the PIN-driver view and overlapped the bottom
  // nav (Actions tab). Re-run the gate now that driverModePin is set.
  if (typeof initCarrierAssistant === 'function') initCarrierAssistant();
  window.AppState.driverAddons = sessionData.addons || {};
  window.AppState.driverIsTrialing = !!sessionData.isTrialing;
  window.AppState.driverAllowMaintenance = !!sessionData.allowMaintenance;

  // Store in localStorage so PIN persists across app restarts
  localStorage.setItem('driverPinAccess', JSON.stringify({
    token: sessionData.token,
    personId: sessionData.personId,
    personName: sessionData.personName,
    tenantId: sessionData.tenantId,
    addons: sessionData.addons || {},
    allowMaintenance: !!sessionData.allowMaintenance
  }));

  _setBtn('<span class="loading-spinner-small"></span> Signing in...');

  // Establish Firestore auth for the driver session.
  // Set flag to prevent handleAuthStateChange from racing with this flow
  window.AppState.driverPinLoginInProgress = true;
  try {
    // TASK-1054 — fast path: the submit handler pre-established anon auth and
    // validateDriverPin inline-linked this token to that uid server-side
    // (sessionData.linked). Skip the second callable round trip entirely —
    // just refresh the cached ID token so the new claims are live.
    if (sessionData.linked === true && auth.currentUser?.isAnonymous) {
      window.AppState.user = auth.currentUser;
    } else {
    // TASK-429 — route through ensureDriverAnonAuth() instead of a RAW
    // signInAnonymously(). It awaits persisted-session restoration FIRST,
    // then reuses an existing ANONYMOUS user with a refreshed token (the
    // churn fix), and — critically — NEVER reuses a real signed-in account.
    const _pinUser = await ensureDriverAnonAuth();
    if (_isStale()) {
      try { await auth.signOut(); } catch (_) { /* noop */ }
      return false;
    }
    window.AppState.user = _pinUser || auth.currentUser;

    // Link the (reused or fresh) UID to the driver token for Firestore access
    const linkFn = httpsCallable(functions, 'linkDriverSession');
    const linkResult = await linkFn({ token: sessionData.token });
    if (_isStale()) {
      try { await auth.signOut(); } catch (_) { /* noop */ }
      return false;
    }
    if (!linkResult.data.ok) {
      console.error('[DriverPIN] Failed to link session:', linkResult.data.error);
      try { await auth.signOut(); } catch (_) {} // drop the idle anon user too
      _rollbackPinEntryState();
      showToast('Unable to authenticate. Please try again.', 'error');
      return false;
    }
    }
    // Force the client-cached ID token to refresh so the new custom claims
    // (tenantId + role:'driver') set by linkDriverSession are visible to
    // Firebase Storage on the very next upload.
    try {
      if (window.AppState?.user?.getIdToken) {
        await window.AppState.user.getIdToken(true);
      }
    } catch (refreshErr) {
      console.warn('[DriverPIN] Token refresh after link failed (non-fatal):', refreshErr?.message);
    }
    console.log('[DriverPIN] Session linked successfully');
  } catch (authErr) {
    console.error('[DriverPIN] Auth/link failed:', authErr);
    try { await auth.signOut(); } catch (_) {} // drop any idle anon user too
    _rollbackPinEntryState();
    showToast('Unable to authenticate. Please try again.', 'error');
    return false;
  }

  // Add class for CSS targeting
  document.body.classList.add('driver-mode-pin');

  _setBtn('<span class="loading-spinner-small"></span> Loading account...');

  // Load tenant settings BEFORE navigating (needed for clockOutMode, billing config, Time Off button).
  // TASK-416 — same rule-visibility gate as the restore path.
  const _vis = await _awaitDriverSessionVisible(sessionData.tenantId, { isStale: _isStale });
  if (_vis.stale) return false; // a newer submit owns the flow; don't touch state
  if (!_vis.snap) {
    console.warn('[DriverPIN] Session not rule-visible after retries — halting fresh login (session preserved)');
    try { await auth.signOut(); } catch (_) {}
    _rollbackPinEntryState(); // incl. body class — was the "Viewing as + no menu" zombie
    showToast('Connection too slow to load your account. Please tap your code again.', 'error');
    return false;
  }
  if (_vis.snap.exists()) {
    window.AppState.settings = _vis.snap.data().settings || {};
    window.AppState.tenantData = _vis.snap.data();
    console.log('[DriverPIN] Loaded tenant settings:', Object.keys(window.AppState.settings).length, 'keys');
    await primeSelfWithin(); // TASK-9553 — this driver's own clock mode
    if (_isStale()) { // cancelled (undo the sign-in) or a newer submit owns the flow
      // Roll back + drop the saved token NOW (before any signOut, or TASK-905
      // recovery revives it); the lazy rest signs out only if no retry started.
      if (!_driverPinInFlight) {
        _rollbackPinEntryState(); localStorage.removeItem('driverPinAccess');
        const gen = _driverPinReqId; // any later submit (even one that already finished) bumps it
        _clockModeLazy().then(m => m.cancelPinEntry(() => _driverPinInFlight || _driverPinReqId !== gen), () => {});
      }
      return false;
    }
  }

  // TASK-1202 UX — final stage of the progress copy. On slow yards the whole
  // PIN→board stretch read as a frozen spinner; staged copy (Checking code… →
  // Signing in… → Loading account… → this) tells the driver it's moving.
  _setBtn('<span class="loading-spinner-small"></span> Fetching today\'s loads…');

  // TASK-774 Gap B — surface assigned maintenance to grant-less drivers.
  await checkDriverHasAssignedMaintenance();

  showToast(`Welcome, ${sessionData.personName}!`, 'success');

  // Hide auth modal and show sidebar
  hideAuthModal();
  showSidebar();

  // Check for portal project assignments (async, non-blocking)
  loadCrewPortalProjects(sessionData.token).catch(e => console.warn('[DriverPIN] Portal projects:', e.message));

  // HOTFIX 2026-05-28 — Driver PIN lands on jobboard (canonical driver-scoped
  // daily view per TASK-355), NOT dispatch.
  // TASK-9658 S1 — unless the driver arrived on a record link (parked by the
  // pre-auth handleHashChange). navigateToView still applies the driver-PIN
  // gate; a module this driver can't see shows no-access and drops the focus.
  let _pinFocus = window.RecordLinks?.hasPendingFocus?.() ? window.AppState.pendingRecordFocus : null;
  if (_pinFocus && _pinFocus.view === 'maintenance' && !window.driverCanSeeMaintenance?.()) {
    // Don't park a driver on a no-access screen for a link that wasn't theirs.
    window.AppState.pendingRecordFocus = null;
    _pinFocus = null;
    showToast("That record isn't available on your driver login.", 'info');
  }
  navigateToView(_pinFocus ? _pinFocus.view : 'jobboard');
  return true;
}

// ─────────────────────────────────────────────────────────────────────────────
// FORCED PIN RESET — "Set your PIN" screen (TASK-PIN Slice 1)
// Shown when validateDriverPin returns mustSetPin (bootstrap / legacy match).
// The driver picks a custom PIN; legacy migrations also enter the SMS code we
// texted. On success setDriverPin mints the real session and we enter the portal.
// ─────────────────────────────────────────────────────────────────────────────
let _setPinState = { setupToken: null, needsPhoneVerify: false, inFlight: false };

function showSetDriverPinScreen({ setupToken, needsPhoneVerify, personName }) {
  _setPinState = { setupToken, needsPhoneVerify: !!needsPhoneVerify, inFlight: false };

  // Hide the PIN-entry form, show (or build) the set-PIN form in the same modal.
  const pinForm = document.getElementById('driver-pin-form');
  if (pinForm) pinForm.classList.add('hidden');

  let form = document.getElementById('driver-set-pin-form');
  if (!form) {
    form = document.createElement('div');
    form.id = 'driver-set-pin-form';
    form.className = 'auth-form';
    // Insert right after the driver-pin-form so it lives in the same auth modal.
    if (pinForm && pinForm.parentNode) {
      pinForm.parentNode.insertBefore(form, pinForm.nextSibling);
    } else {
      document.body.appendChild(form);
    }
  }
  form.classList.remove('hidden');

  const safeName = personName ? String(personName).replace(/[<>&"]/g, '') : '';
  const greeting = safeName ? `Hi ${safeName.split(' ')[0]}! ` : '';
  const smsField = needsPhoneVerify ? `
    <div class="form-group">
      <label style="color:#fff;font-size:0.85rem;display:block;margin-bottom:4px;">Verification code</label>
      <input type="text" id="set-pin-sms-code" inputmode="numeric" maxlength="6" autocomplete="one-time-code"
             placeholder="6-digit code"
             style="text-align:center;font-size:1.3rem;letter-spacing:0.3em;font-weight:600;">
      <p style="color:var(--text-muted);font-size:0.78rem;margin-top:6px;text-align:center;">We texted a code to your phone on file. Enter it to verify it's you.</p>
    </div>` : '';

  form.innerHTML = `
    <div style="text-align:center;margin-bottom:var(--spacing-lg);">
      <span class="material-icons" style="font-size:48px;color:var(--brand-cyan);">lock</span>
      <h3 style="margin:var(--spacing-sm) 0 0 0;color:#fff;">Set Your PIN</h3>
      <p style="color:var(--text-muted);font-size:0.9rem;margin-top:var(--spacing-xs);">${greeting}Choose a private PIN you'll use to log in from now on.</p>
    </div>
    ${smsField}
    <div class="form-group">
      <label style="color:#fff;font-size:0.85rem;display:block;margin-bottom:4px;">New PIN (4-8 letters or numbers)</label>
      <!-- TASK-4678 — the #1 driver-support call: they set a PIN here, the code
           they were texted dies, and days later nobody (not even the dispatcher)
           can look the new PIN up. Say so BEFORE they type, not after. -->
      <p style="color:#FBBF24;font-size:0.8rem;margin:0 0 8px;line-height:1.4;">Write this down. It replaces the code you were texted, and nobody — not even your dispatcher — can look it up later.</p>
      <input type="password" id="set-pin-new" maxlength="8" autocomplete="new-password"
             placeholder="••••"
             style="text-align:center;font-size:1.4rem;letter-spacing:0.3em;font-weight:600;text-transform:uppercase;">
    </div>
    <div class="form-group">
      <label style="color:#fff;font-size:0.85rem;display:block;margin-bottom:4px;">Confirm PIN</label>
      <input type="password" id="set-pin-confirm" maxlength="8" autocomplete="new-password"
             placeholder="••••"
             style="text-align:center;font-size:1.4rem;letter-spacing:0.3em;font-weight:600;text-transform:uppercase;">
      <div id="set-pin-error" style="display:none;color:#EF4444;font-size:0.875rem;margin-top:8px;text-align:center;font-weight:600;"></div>
    </div>
    <button type="button" class="btn btn-primary btn-block" id="btn-set-pin" onclick="submitSetDriverPin()">
      <span class="material-icons" style="font-size:18px;margin-right:6px;">check</span>
      Set PIN &amp; Continue
    </button>
    <button type="button" class="btn btn-secondary btn-block" onclick="cancelSetDriverPin()" style="margin-top:var(--spacing-sm);">
      Cancel
    </button>
  `;

  setTimeout(() => {
    const first = document.getElementById(needsPhoneVerify ? 'set-pin-sms-code' : 'set-pin-new');
    if (first) first.focus();
  }, 60);
}
window.showSetDriverPinScreen = showSetDriverPinScreen;

function cancelSetDriverPin() {
  _setPinState = { setupToken: null, needsPhoneVerify: false, inFlight: false };
  const form = document.getElementById('driver-set-pin-form');
  if (form) form.classList.add('hidden');
  const pinForm = document.getElementById('driver-pin-form');
  if (pinForm) pinForm.classList.remove('hidden');
  const input = document.getElementById('driver-pin-input');
  if (input) { input.value = ''; input.focus(); }
}
window.cancelSetDriverPin = cancelSetDriverPin;

/**
 * Forgot-PIN from the LOGGED-OUT PIN entry screen. Uses whatever code the driver
 * typed (their old/legacy code) + the known tenant to let the server resolve who
 * they are, then texts them a fresh one-time code and notifies their dispatcher.
 * The server always returns a generic OK (no enumeration), so we always show the
 * same reassuring message.
 */
// TASK-4678 — first tap just reveals the phone field. The old version demanded
// the driver type "your current driver code" to identify themselves, which the
// server then resolved with classifyDriverPinMatch — so the only code that
// worked was the PIN they were asking to recover. Every reset ended up going
// through the dispatcher instead.
function forgotDriverPin(ev) {
  if (ev && ev.preventDefault) ev.preventDefault();
  const row = document.getElementById('forgot-pin-phone-row');
  const msgEl = document.getElementById('forgot-pin-msg');
  if (msgEl) msgEl.style.display = 'none';
  if (row) {
    row.style.display = 'block';
    const phoneEl = document.getElementById('forgot-pin-phone');
    if (phoneEl) setTimeout(() => phoneEl.focus(), 60);
  }
}
window.forgotDriverPin = forgotDriverPin;

async function submitForgotDriverPin(ev) {
  if (ev && ev.preventDefault) ev.preventDefault();
  const msgEl = document.getElementById('forgot-pin-msg');
  const phoneEl = document.getElementById('forgot-pin-phone');
  const btn = document.getElementById('btn-forgot-pin-send');
  const phone = (phoneEl?.value || '').trim();
  const digits = phone.replace(/\D/g, '');

  if (digits.length < 10) {
    if (msgEl) {
      msgEl.style.color = '#EF4444';
      msgEl.textContent = 'Enter the 10-digit mobile number your dispatcher has on file.';
      msgEl.style.display = 'block';
    }
    if (phoneEl) phoneEl.focus();
    return;
  }

  if (btn) btn.disabled = true;
  if (msgEl) {
    msgEl.style.color = 'var(--text-muted)';
    msgEl.textContent = 'Sending…';
    msgEl.style.display = 'block';
  }

  try {
    const knownTenantId = window.AppState?.tenant && window.AppState.tenant !== 'demo'
      ? window.AppState.tenant
      : undefined;
    // Still pass whatever is in the code box — it lets the server's PIN fallback
    // resolve a driver whose number isn't the one on file. TASK-4727: match the
    // server's shape gate (/^[A-Z0-9]{3,8}$/) so junk in the code box no longer
    // buys a full cross-tenant fan-out it could never match.
    const typed = (document.getElementById('driver-pin-input')?.value || '').trim().toUpperCase();
    const fn = httpsCallable(functions, 'forgotDriverPin');
    await fn({
      phone,
      ...(/^[A-Z0-9]{3,8}$/.test(typed) ? { pin: typed } : {}),
      // TASK-4727 — this NARROWS NOTHING any more. Both lanes in the callable
      // now ignore it for matching; it survives server-side only as a diagnostic
      // field on the ambiguity breadcrumb. Do not build anything on it.
      ...(knownTenantId ? { tenantId: knownTenantId } : {}),
    });
  } catch (e) {
    console.warn('[ForgotPIN] error (showing generic message anyway):', e?.message);
  }
  if (btn) btn.disabled = false;

  // ── TASK-4727 — the message must stop LYING on a refusal. ───────────────────
  // The server returns the identical `{ ok: true, generic: true }` on every
  // outcome BY DESIGN (no enumeration — see _logForgotPinAmbiguousPhone for why
  // every alternative leaks), so the client genuinely cannot tell success from
  // refusal and must not pretend to. What it CAN stop doing is asserting, in
  // green, that a code "is on its way" when the request was refused: an
  // ambiguous phone, an ambiguous PIN, a rate-limit, a driver with no phone on
  // file, or a wrong number all render as a confirmed send today. That leaves
  // the driver standing at the gate waiting for a text that was never sent, with
  // nobody in the building told either — the TASK-428 shape exactly.
  //
  // So: state the CONDITION instead of the outcome, and give the driver a
  // timeboxed next step that is true in every branch. This leaks nothing — a
  // driver who receives no text already knows they received no text; they are
  // told nothing about WHY, which is the whole non-enumeration invariant.
  // Neutral, not success-green, for the same reason.
  //
  // TASK-831 stays honoured: channel-agnostic, never promises a text that
  // legally can't be sent. TASK-4540 stays honoured: don't invite a resend, each
  // new code kills the previous one.
  //
  // COD Code Reviewer caught the first draft saying "we couldn't match you" —
  // which DIAGNOSES, and would be flatly wrong for a driver whose code is merely
  // late behind a slow carrier. The second line is now purely an ACTION, which
  // is correct in every branch: refused (there is no text coming, go get one) and
  // slow-success (the dispatcher-issued code works too, and asking costs nothing).
  if (msgEl) {
    msgEl.style.color = 'var(--text-primary)';
    msgEl.innerHTML =
      // "may be on its way", never "will arrive" — COD round 2. Even the
      // conditional lead-in does not cover an ambiguous or rate-limited number,
      // where the phone IS on a roster and no code was sent. This sentence has
      // to be true in every branch or we are back where we started.
      "Request sent. If that number is on a roster and can receive texts, a one-time " +
      "code may be on its way — enter it in the box above to set a fresh PIN." +
      "<span style=\"display:block;margin-top:6px;color:#B45309;\">" +
      "Nothing after 5 minutes? Don’t keep waiting — ask your dispatcher to send you a " +
      "code from Roster › Driver Code. And don’t resend from here: each new code " +
      "cancels the last one.</span>";
    msgEl.style.display = 'block';
  }
}
window.submitForgotDriverPin = submitForgotDriverPin;

async function submitSetDriverPin() {
  if (_setPinState.inFlight) return;
  const btn = document.getElementById('btn-set-pin');
  const errEl = document.getElementById('set-pin-error');
  const showErr = (msg) => { if (errEl) { errEl.textContent = msg; errEl.style.display = 'block'; } };
  if (errEl) errEl.style.display = 'none';

  const newPin = (document.getElementById('set-pin-new')?.value || '').trim().toUpperCase();
  const confirmPin = (document.getElementById('set-pin-confirm')?.value || '').trim().toUpperCase();
  const smsCode = _setPinState.needsPhoneVerify
    ? (document.getElementById('set-pin-sms-code')?.value || '').trim()
    : undefined;

  if (!/^[A-Z0-9]{4,8}$/.test(newPin)) {
    showErr('PIN must be 4-8 letters or numbers.');
    return;
  }
  if (newPin !== confirmPin) {
    showErr("Those PINs don't match.");
    return;
  }
  if (_setPinState.needsPhoneVerify && (!smsCode || smsCode.length < 4)) {
    showErr('Enter the verification code we texted you.');
    return;
  }

  _setPinState.inFlight = true;
  const originalBtn = btn ? btn.innerHTML : '';
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="loading-spinner-small"></span> Setting your PIN…'; }

  try {
    const setFn = httpsCallable(functions, 'setDriverPin');
    const res = await setFn({ setupToken: _setPinState.setupToken, newPin, smsCode });
    const data = res.data || {};

    if (!data.ok) {
      // pin_taken keeps the setup token alive — let them pick another.
      if (data.error === 'pin_taken') {
        showErr(data.message || 'That PIN is already in use. Choose a different one.');
      } else {
        showErr(data.message || 'Could not set your PIN. Please try again.');
      }
      _setPinState.inFlight = false;
      if (btn) { btn.disabled = false; btn.innerHTML = originalBtn; }
      return;
    }

    // Success — setDriverPin already minted the real session (same
    // buildPinSuccessResponse a normal PIN login gets). Enter the portal on it.
    //
    // TASK-10096 (Chris 2026-10-26: "brings him to a dead login page… also
    // feedback for 'pin saved!'"). This used to hide the set-PIN form and then
    // sign in with BOTH forms hidden: the progress text went into a hidden
    // button, and every failure exit of _enterDriverPortalWithSession (link
    // failed, session not rule-visible, any throw) left an empty auth modal —
    // the throw's message landed in the hidden form's error line, and
    // driverPinLoginInProgress stayed true. Now: say "PIN saved!" where the
    // driver is looking, keep the progress visible, and if sign-in does not
    // finish, hand back a fresh Driver Code screen asking for the NEW PIN (the
    // texted code is already dead). The PIN is never shown, logged or toasted.
    await _finishDriverPinSaved(data);
  } catch (e) {
    console.error('[SetDriverPIN] error:', e);
    // Firebase callable HttpsError surfaces a user-readable message.
    const msg = e?.message && !/internal/i.test(e.message) ? e.message : 'Could not set your PIN. Please try again.';
    showErr(msg);
    _setPinState.inFlight = false;
    if (btn) { btn.disabled = false; btn.innerHTML = originalBtn; }
  }
}
window.submitSetDriverPin = submitSetDriverPin;

// TASK-10096 — the step after a successful setDriverPin. Shows "PIN saved!" in
// the set-PIN panel (inputs removed, so the typed PIN leaves the DOM), signs in
// on the minted session, and on ANY failure lands on a working Driver Code
// screen. Never receives or renders the PIN — `sessionData` is the server's
// session payload only.
async function _finishDriverPinSaved(sessionData) {
  const form = document.getElementById('driver-set-pin-form');
  let progress = null;
  if (form) {
    form.classList.remove('hidden');
    form.innerHTML = `
      <div id="set-pin-saved" role="status" aria-live="polite" style="text-align:center;padding:var(--spacing-lg) 0;">
        <span class="material-icons" style="font-size:56px;color:var(--action-success, #059669);">check_circle</span>
        <h3 style="margin:var(--spacing-sm) 0 0 0;color:#fff;">PIN saved!</h3>
        <p style="color:rgba(255,255,255,0.85);font-size:0.9rem;margin-top:var(--spacing-xs);">Use your new PIN every time you sign in.</p>
        <div id="set-pin-progress" style="color:#fff;font-size:0.9rem;margin-top:var(--spacing-md);font-weight:600;">
          <span class="loading-spinner-small"></span> Signing you in…
        </div>
      </div>`;
    progress = document.getElementById('set-pin-progress');
  }
  showToast('PIN saved!', 'success');

  // Hold the auth handler off for the whole entry (the normal login path does
  // the same in submitDriverPin) and ALWAYS release it — this path used to
  // leave it stuck true, which muted every later auth event in the tab.
  window.AppState.driverPinLoginInProgress = true;
  let entered = false;
  try {
    entered = (await _enterDriverPortalWithSession(sessionData, { btn: progress, isStale: () => false })) === true;
  } catch (enterErr) {
    // Error code/name only — never the payload or message (COD Secrets lane: a message could echo a token).
    console.error('[SetDriverPIN] Sign-in after PIN save failed:', enterErr?.code || enterErr?.name || 'error');
    try { _rollbackDriverPinOptimisticState(); } catch (_) { /* noop */ }
    try { await auth.signOut(); } catch (_) { /* noop */ }
  }
  window.AppState.driverPinLoginInProgress = false;
  _setPinState = { setupToken: null, needsPhoneVerify: false, inFlight: false };

  if (entered) {
    if (form) { form.classList.add('hidden'); form.innerHTML = ''; }
    return true;
  }
  _showSignInWithNewPin();
  return false;
}

// TASK-10096 — fallback landing: a fresh, working Driver Code screen with the
// "PIN saved" note on top and the code box empty + focused.
function _showSignInWithNewPin() {
  window.AppState.driverPinLoginInProgress = false;
  if (DOM.authModal) DOM.authModal.classList.remove('hidden');
  switchAuthTab('driver'); // hides the set-PIN panel, shows + focuses the code box
  const pinForm = document.getElementById('driver-pin-form');
  const input = document.getElementById('driver-pin-input');
  if (input) input.value = '';
  const errEl = document.getElementById('driver-pin-error');
  if (errEl) { errEl.textContent = ''; errEl.style.display = 'none'; }
  const fpRow = document.getElementById('forgot-pin-phone-row');
  if (fpRow) fpRow.style.display = 'none';
  const fpMsg = document.getElementById('forgot-pin-msg');
  if (fpMsg) fpMsg.style.display = 'none';
  const btn = document.getElementById('btn-submit-pin');
  if (btn) btn.disabled = false;

  let note = document.getElementById('driver-pin-saved-msg');
  if (!note && pinForm) {
    note = document.createElement('div');
    note.id = 'driver-pin-saved-msg';
    note.setAttribute('role', 'status');
    note.setAttribute('aria-live', 'polite');
    note.style.cssText = 'display:none;align-items:center;gap:8px;justify-content:center;background:rgba(16,185,129,0.15);border:1px solid var(--action-success, #059669);color:#fff;border-radius:8px;padding:10px 12px;margin-bottom:var(--spacing-md);font-size:0.9rem;font-weight:600;text-align:center;';
    pinForm.insertBefore(note, pinForm.firstChild);
  }
  if (note) {
    note.innerHTML = '<span class="material-icons" style="font-size:20px;color:var(--action-success, #059669);">check_circle</span><span>PIN saved! Now sign in with your new PIN.</span>';
    note.style.display = 'flex';
  }
  setTimeout(() => document.getElementById('driver-pin-input')?.focus(), 60);
}

async function submitDriverPin() {
  const btn = document.getElementById('btn-submit-pin');

  // Second click while a check is in flight = cancel
  if (_driverPinInFlight) {
    _cancelDriverPinRequest();
    return;
  }

  const pinInput = document.getElementById('driver-pin-input');
  const pin = pinInput.value.trim().toUpperCase();
  const pinErr = document.getElementById('driver-pin-error');
  if (pinErr) pinErr.style.display = 'none';

  // Validate PIN format: 3-8 characters (2nd-COD TASK-1202 catch — custom PINs
  // are server-validated 4-8; the old 6-char cap let a driver SET a 7-8 char
  // PIN they could then never type into this form).
  if (pin.length < 3 || pin.length > 8) {
    showToast('Enter your Driver Code', 'error');
    return;
  }

  const myReqId = ++_driverPinReqId;
  const isStale = () => myReqId !== _driverPinReqId;
  _driverPinInFlight = true;
  // TASK-1054 — hold the auth-handler guard from the very start: the fast path
  // pre-establishes anon auth BEFORE driverModePin is set, and without this the
  // handleAuthStateChange "stale anonymous session" branch (:~5277) would sign
  // the pre-auth user right back out and flash the login modal. The finally
  // block below (and the cancel path) reset it.
  window.AppState.driverPinLoginInProgress = true;

  const originalText = btn.innerHTML;
  _driverPinOriginalBtnHTML = originalText;
  // Keep the button enabled so a second tap cancels.
  btn.innerHTML = '<span class="loading-spinner-small"></span> Checking code… <span style="opacity:0.75;font-size:11px;font-weight:500;">(tap to cancel)</span>';

  try {
    // TASK-1054 — single-round-trip fast path: when nobody (or only an anon
    // user) is signed in, establish the anon session FIRST and ask
    // validateDriverPin to inline-link the fresh token to our uid (`link:true`)
    // — collapses validate→auth→link (2 callable RTTs) into auth→validate+link
    // (1 RTT). NEVER pre-auth over a REAL signed-in account (kiosk owner
    // entering a PIN): ensureDriverAnonAuth signs the real user out, so a
    // mistyped PIN would destroy their session — that path keeps the legacy
    // validate-first ordering. Server only honors link for anonymous callers.
    if (!auth.currentUser || auth.currentUser.isAnonymous) {
      try {
        const _preUser = await ensureDriverAnonAuth();
        if (_preUser) window.AppState.user = _preUser;
      } catch (preAuthErr) {
        console.warn('[DriverPIN] Pre-auth failed (falling back to two-step):', preAuthErr?.message);
      }
      if (isStale()) return;
    }
    const _canInlineLink = !!(auth.currentUser && auth.currentUser.isAnonymous);
    const validateFn = httpsCallable(functions, 'validateDriverPin');
    // If we already know the tenant (e.g. QR-code flow pre-seeded the tenant context),
    // pass it so the Cloud Function does a scoped search instead of a global one.
    // This avoids non-deterministic routing when two tenants share the same PIN.
    const knownTenantId = window.AppState?.tenant && window.AppState.tenant !== 'demo'
      ? window.AppState.tenant
      : undefined;
    const result = await validateFn({
      pin,
      ...(knownTenantId ? { tenantId: knownTenantId } : {}),
      ...(_canInlineLink ? { link: true } : {}),
    });
    if (isStale()) {
      // User cancelled while we were waiting on validateDriverPin. If the
      // server already inline-linked this anon uid (claims + driverSessions
      // doc written), don't leave a LINKED driver session signed in outside
      // driver mode — drop it (COD Code Reviewer, TASK-1054).
      if (result?.data?.linked === true) {
        try { await auth.signOut(); } catch (_) {}
        window.AppState.user = null;
      }
      return;
    }

    // ── Forced PIN reset (bootstrap / legacy computed PIN) ──
    // validateDriverPin returns mustSetPin WITHOUT a session token — the driver
    // must choose a custom PIN (and, for legacy, enter an SMS code) before any
    // session is minted. Hand off to the Set-PIN screen.
    if (result.data.ok && result.data.mustSetPin) {
      _driverPinInFlight = false;
      window.AppState.driverPinLoginInProgress = false;
      showSetDriverPinScreen({
        setupToken: result.data.setupToken,
        needsPhoneVerify: !!result.data.needsPhoneVerify,
        personName: result.data.personName || '',
      });
      return; // finally{} restores the entry button; the set-PIN screen takes over
    }

    if (result.data.ok) {
      await _enterDriverPortalWithSession(result.data, { btn, isStale });
    } else {
      const pinErr = document.getElementById('driver-pin-error');
      // codex #1 — a legacy driver with no phone on file can't self-verify; the
      // server declines a setup token and asks them to get a dispatcher code.
      const isAmbiguous = result.data.ambiguous === true;
      // TASK-9919 — a wait after too many wrong codes, or the check being
      // briefly unavailable, is NOT "no driver found": show the server's words.
      const isWaitOrDown = (typeof result.data.retryAfterSec === 'number' || result.data.limiterUnavailable === true)
        && typeof result.data.error === 'string' && result.data.error;
      const errMsg = isWaitOrDown ? result.data.error : result.data.mustContactDispatch
        ? (result.data.message || 'Contact your dispatcher to get a new login code.') // TASK-831: channel-agnostic fallback (server message is the real copy)
        : isAmbiguous
          ? 'This code matches more than one account. Contact your dispatcher to get a unique code assigned.'
          : 'No driver found with that code. Check your schedule text or ask your dispatcher.';
      if (pinErr) { pinErr.textContent = errMsg; pinErr.style.display = 'block'; }
      pinInput.value = '';
      pinInput.focus();
    }
  } catch (error) {
    if (isStale()) return; // cancelled — already reset, swallow whatever errored
    console.error('[DriverPIN] Validation error:', error);
    const pinErr = document.getElementById('driver-pin-error');
    if (pinErr) { pinErr.textContent = 'Unable to verify code. Please try again.'; pinErr.style.display = 'block'; }
  } finally {
    // If a cancel already restored the UI, don't clobber whatever state it's in now.
    if (!isStale()) {
      _driverPinInFlight = false;
      window.AppState.driverPinLoginInProgress = false;
      btn.disabled = false;
      btn.innerHTML = originalText;
    }
  }
}
window.submitDriverPin = submitDriverPin;

// ============================================
// CREW PORTAL PROJECTS (after driver PIN login)
// ============================================
async function loadCrewPortalProjects(driverToken) {
  try {
    const getProjects = httpsCallable(functions, 'getCrewPortalProjects');
    const result = await getProjects({ token: driverToken });
    if (result.data.ok && result.data.projects?.length > 0) {
      window.AppState.crewPortalProjects = result.data.projects;
      // Show Projects nav item for driver PIN mode
      addCrewProjectsNavItem();
    }
  } catch (err) {
    console.warn('[CrewPortal] Failed to load projects:', err.message);
  }
}

function addCrewProjectsNavItem() {
  // Add to mobile nav if in driver-mode-pin
  if (!window.AppState?.driverModePin) return;

  const mobileNav = document.getElementById('mobile-nav');
  if (!mobileNav) return;

  // Don't add if already exists
  if (document.querySelector('[data-view="crew-projects"]')) return;

  const navItem = document.createElement('button');
  navItem.className = 'mobile-nav-item';
  navItem.dataset.view = 'crew-projects';
  navItem.onclick = () => renderCrewProjectsView();
  navItem.innerHTML = `
    <span class="material-icons">assignment</span>
    <span>Projects</span>
  `;
  mobileNav.appendChild(navItem);

  // Also add to sidebar if visible
  const sidebarNav = document.querySelector('.sidebar-nav');
  if (sidebarNav) {
    const sideItem = document.createElement('a');
    sideItem.className = 'nav-item';
    sideItem.dataset.view = 'crew-projects';
    sideItem.onclick = (e) => { e.preventDefault(); renderCrewProjectsView(); };
    sideItem.href = '#';
    sideItem.innerHTML = `<span class="material-icons">assignment</span><span>Projects</span>`;
    sidebarNav.appendChild(sideItem);
  }
}

// TASK-4706 — the crew list now carries two record kinds: a CRM opportunity
// (no `kind`, keyed by customerId + opportunityId — unchanged since TASK-4707)
// and a dispatch-side Project (`kind: 'project'`, keyed by projectId). One key
// for both so the list/detail/step/note handlers never mix them up.
function crewProjectKey(p) {
  return p && p.kind === 'project' ? `p:${p.projectId || ''}` : `o:${(p && p.opportunityId) || ''}`;
}
const CREW_STAGE_COLORS = { initiating: '#94A3B8', qualifying: '#F59E0B', prospecting: '#3B82F6', negotiating: '#06B6D4', closing: '#10B981', on_hold: '#F97316', won: '#8B5CF6', lost: '#EF4444', project_active: '#3B82F6', project_on_hold: '#F97316', project_planning: '#94A3B8' };
const CREW_STAGE_LABELS = { initiating: 'Planning', qualifying: 'Evaluating', prospecting: 'In Progress', negotiating: 'Finalizing', closing: 'Wrapping Up', on_hold: 'On Hold', won: 'Complete', lost: 'Cancelled', project_active: 'In Progress', project_on_hold: 'On Hold', project_planning: 'Planning' };

function renderCrewProjectsView() {
  const escHTML = (s) => s ? String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;') : '';
  const projects = window.AppState?.crewPortalProjects || [];
  const mainContent = document.getElementById('main-content');
  if (!mainContent) return;

  // Update active nav state
  document.querySelectorAll('.nav-item, .mobile-nav-item').forEach(n => n.classList.remove('active'));
  document.querySelectorAll('[data-view="crew-projects"]').forEach(n => n.classList.add('active'));

  const stageColors = CREW_STAGE_COLORS;
  const stageLabels = CREW_STAGE_LABELS;

  let html = `
    <div style="padding:var(--spacing-lg);max-width:700px;margin:0 auto;">
      <div style="display:flex;align-items:center;gap:12px;margin-bottom:var(--spacing-lg);">
        <span class="material-icons" style="font-size:28px;color:var(--brand-blue);">assignment</span>
        <div>
          <h2 style="margin:0;font-size:1.25rem;">My Projects</h2>
          <div style="font-size:0.8125rem;color:var(--text-secondary);">${projects.length} assigned project${projects.length !== 1 ? 's' : ''}</div>
        </div>
      </div>
  `;

  if (projects.length === 0) {
    html += `<div style="text-align:center;color:var(--text-secondary);padding:var(--spacing-xl);">No projects assigned to you.</div>`;
  } else {
    projects.forEach(p => {
      const sc = stageColors[p.stage] || '#94A3B8';
      const sl = stageLabels[p.stage] || (p.stage ? String(p.stage).replace(/^project_/, '').replace(/_/g, ' ') : '');
      const progress = p.totalSteps > 0 ? Math.round((p.completedSteps / p.totalSteps) * 100) : 0;

      html += `
        <div class="portal-card" style="margin-bottom:12px;cursor:pointer;" onclick="window.openCrewProject('${window.escJsAttr(crewProjectKey(p))}')">
          <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:12px;">
            <div style="flex:1;">
              <div style="font-weight:600;font-size:1rem;">${escHTML(p.title)}</div>
              <div style="font-size:0.8125rem;color:var(--text-secondary);">${escHTML(p.customerName)}</div>
            </div>
            ${sl ? `<span style="font-size:0.75rem;font-weight:600;color:${sc};background:${sc}15;padding:2px 8px;border-radius:4px;">${escHTML(sl)}</span>` : ''}
          </div>
          ${p.totalSteps > 0 ? `
          <div style="margin-top:10px;">
            <div style="display:flex;justify-content:space-between;font-size:0.75rem;color:var(--text-secondary);margin-bottom:4px;">
              <span>Current: ${escHTML(p.currentStep)}</span>
              <span>${progress}%</span>
            </div>
            <div style="height:6px;background:var(--border-light);border-radius:3px;overflow:hidden;">
              <div style="height:100%;width:${progress}%;background:var(--brand-blue);border-radius:3px;transition:width 0.3s;"></div>
            </div>
          </div>` : ''}
        </div>
      `;
    });
  }

  html += '</div>';
  mainContent.innerHTML = html;
}

function openCrewProject(key) {
  const projects = window.AppState?.crewPortalProjects || [];
  // TASK-4706 — a key from crewProjectKey(); a bare id is an old opportunity link.
  const k = (typeof key === 'string' && /^[op]:/.test(key)) ? key : `o:${key}`;
  const project = projects.find(p => crewProjectKey(p) === k);
  if (!project) return;

  renderCrewProjectDetail(project);
}
window.openCrewProject = openCrewProject;

function renderCrewProjectDetail(project) {
  const escHTML = (s) => s ? String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;') : '';
  const mainContent = document.getElementById('main-content');
  if (!mainContent) return;

  const steps = project.trackerSteps || [];
  const currentStepIdx = steps.findIndex(s => !s.completed);
  const notes = project.notes || [];

  const isPmProject = project.kind === 'project'; // TASK-4706
  const pKey = crewProjectKey(project);
  const sc = CREW_STAGE_COLORS[project.stage] || '#94A3B8';
  const sl = CREW_STAGE_LABELS[project.stage] || (project.stage ? String(project.stage).replace(/^project_/, '').replace(/_/g, ' ') : '');

  let trackerHTML = '';
  if (steps.length > 0) {
    trackerHTML = `
      <div class="portal-section" style="margin-bottom:16px;">
        <h3 style="font-size:0.9375rem;font-weight:600;margin:0 0 12px 0;"><span class="material-icons" style="font-size:18px;vertical-align:middle;">timeline</span> Progress</h3>
        <div class="pizza-tracker">
          ${steps.map((step, i) => {
            const isCompleted = step.completed;
            const isCurrent = i === currentStepIdx;
            const stageClass = isCompleted ? 'completed' : (isCurrent ? 'current' : '');
            const conn = i < steps.length - 1 ? `<div class="tracker-connector ${isCompleted ? 'completed' : ''}"></div>` : '';
            return `
              <div class="tracker-stage ${stageClass}">
                <div class="tracker-dot-large"></div>
                <div class="tracker-label">${escHTML(step.name)}</div>
              </div>
              ${conn}`;
          }).join('')}
        </div>
        ${currentStepIdx >= 0 ? `
        <div style="text-align:center;margin-top:16px;">
          <button id="crew-complete-step-btn" class="btn btn-primary" onclick="window.crewCompleteStep('${window.escJsAttr(pKey)}', ${currentStepIdx})" style="padding:10px 24px;">
            <span class="material-icons" style="font-size:18px;vertical-align:middle;">check_circle</span> Complete: ${escHTML(steps[currentStepIdx].name)}
          </button>
        </div>` : `<div style="text-align:center;color:var(--text-secondary);margin-top:8px;font-size:0.875rem;">All steps complete!</div>`}
      </div>
    `;
  }

  // TASK-4706 — a Project's notes are not sent to the phone (its notes mix
  // internal office notes with customer ones); a crew note there goes to the
  // office only.
  let notesHTML = isPmProject ? `
    <div class="portal-section">
      <h3 style="font-size:0.9375rem;font-weight:600;margin:0 0 12px 0;"><span class="material-icons" style="font-size:18px;vertical-align:middle;">rate_review</span> Note to the office</h3>
      <div style="font-size:0.8125rem;color:var(--text-secondary);">The office sees this on the project. The customer does not.</div>
      <div style="margin-top:10px;">
        <textarea id="crew-note-input" maxlength="500" rows="2" placeholder="Add a note..." style="width:100%;padding:8px;border:1px solid var(--border-light);border-radius:var(--radius-sm);font-size:0.875rem;resize:vertical;box-sizing:border-box;font-family:inherit;"></textarea>
        <button class="btn btn-primary btn-sm" onclick="window.crewSubmitProjectNote('${window.escJsAttr(project.projectId || '')}')" style="margin-top:6px;padding:8px 16px;">
          <span class="material-icons" style="font-size:16px;">send</span> Send
        </button>
      </div>
    </div>
  ` : `
    <div class="portal-section">
      <h3 style="font-size:0.9375rem;font-weight:600;margin:0 0 12px 0;"><span class="material-icons" style="font-size:18px;vertical-align:middle;">rate_review</span> Notes</h3>
      <div id="crew-notes-list">
        ${notes.length === 0 ? '<div style="color:var(--text-secondary);font-size:0.8125rem;">No notes yet.</div>' : notes.map(n => {
          const dt = n.createdAt?.toDate ? n.createdAt.toDate() : (n.createdAt ? new Date(n.createdAt) : null);
          const dateStr = dt ? dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
          const typeColor = n.authorType === 'customer' ? '#10B981' : n.authorType === 'crew' ? '#3B82F6' : '#8B5CF6';
          const typeLabel = n.authorType === 'customer' ? 'Customer' : n.authorType === 'crew' ? 'Crew' : 'Admin';
          return `<div style="padding:8px;background:var(--bg-secondary);border-radius:var(--radius-sm);margin-bottom:6px;">
            <div style="display:flex;gap:6px;margin-bottom:2px;"><span style="font-size:0.7rem;font-weight:600;color:${typeColor};background:${typeColor}15;padding:1px 6px;border-radius:4px;">${typeLabel}</span><span style="font-size:0.75rem;color:var(--text-secondary);">${escHTML(n.author)} · ${dateStr}</span></div>
            <div style="font-size:0.8125rem;">${escHTML(n.text)}</div>
          </div>`;
        }).join('')}
      </div>
      <div style="margin-top:10px;">
        <textarea id="crew-note-input" maxlength="500" rows="2" placeholder="Add a note..." style="width:100%;padding:8px;border:1px solid var(--border-light);border-radius:var(--radius-sm);font-size:0.875rem;resize:vertical;box-sizing:border-box;font-family:inherit;"></textarea>
        <button class="btn btn-primary btn-sm" onclick="window.crewSubmitNote('${window.escJsAttr(project.customerId || '')}', '${window.escJsAttr(project.opportunityId || '')}')" style="margin-top:6px;padding:8px 16px;">
          <span class="material-icons" style="font-size:16px;">send</span> Send
        </button>
      </div>
    </div>
  `;

  mainContent.innerHTML = `
    <div style="padding:var(--spacing-lg);max-width:700px;margin:0 auto;">
      <button class="btn btn-secondary btn-sm" onclick="renderCrewProjectsView()" style="margin-bottom:12px;">
        <span class="material-icons" style="font-size:16px;">arrow_back</span> Back
      </button>
      <div class="portal-card portal-hero" style="margin-bottom:16px;">
        <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:12px;">
          <div>
            <h2 style="margin:0;font-size:1.125rem;">${escHTML(project.title)}</h2>
            <div style="font-size:0.8125rem;color:var(--text-secondary);">${escHTML(project.customerName)}</div>
          </div>
          ${sl ? `<span style="font-size:0.75rem;font-weight:600;color:${sc};background:${sc}15;padding:2px 8px;border-radius:4px;">${escHTML(sl)}</span>` : ''}
        </div>
      </div>
      ${trackerHTML}
      ${notesHTML}
    </div>
  `;
}
window.renderCrewProjectsView = renderCrewProjectsView;

window.crewCompleteStep = async (key, stepIndex) => {
  const btn = document.getElementById('crew-complete-step-btn');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Updating...'; }

  try {
    const driverToken = window.AppState?.driverAccessToken;
    const projects = window.AppState?.crewPortalProjects || [];
    // TASK-4706 — key from crewProjectKey(); a bare id is an old opportunity call.
    const k = (typeof key === 'string' && /^[op]:/.test(key)) ? key : `o:${key}`;
    const project = projects.find(p => crewProjectKey(p) === k);
    if (!project || !driverToken) throw new Error('Missing context');

    const completeFn = httpsCallable(functions, 'portalCompleteStep');
    // TASK-4707 — the project is named by (customerId, opportunityId); the server
    // takes the tenant off the driver session and checks assignment itself. The
    // customer's portal token is no longer sent to (or held by) the crew phone.
    // TASK-4706 — a dispatch-side Project is named by projectId alone; same
    // server rules (session tenant + person, the project's own grant list).
    await completeFn(project.kind === 'project' ? {
      token: driverToken,
      projectId: project.projectId,
      stepIndex
    } : {
      token: driverToken,
      customerId: project.customerId,
      opportunityId: project.opportunityId,
      stepIndex,
      personId: window.AppState?.driverPersonId,
      personName: window.AppState?.driverPersonName
    });

    showToast('Step completed!', 'success');

    // Update local state and re-render
    project.trackerSteps[stepIndex].completed = true;
    project.completedSteps = project.trackerSteps.filter(s => s.completed).length;
    const nextIdx = project.trackerSteps.findIndex(s => !s.completed);
    project.currentStep = nextIdx >= 0 ? project.trackerSteps[nextIdx].name : 'Complete';
    renderCrewProjectDetail(project);
  } catch (err) {
    console.error('[CrewPortal] Step complete error:', err);
    showToast(err.message || 'Failed to complete step', 'error');
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:18px;vertical-align:middle;">check_circle</span> Complete Step'; }
  }
};

window.crewSubmitNote = async (customerId, opportunityId) => {
  const input = document.getElementById('crew-note-input');
  const text = input?.value?.trim();
  if (!text) return;

  try {
    const submitNote = httpsCallable(functions, 'portalSubmitNote');
    // TASK-4719 — the crew stamp now costs a driver session. `authorName`/`authorType`
    // are still sent and are ACCEPTED-AND-IGNORED server-side (the mismatch is logged
    // as an exploitation signal); `driverToken` is what actually earns `authorType:
    // 'crew'` and supplies the name, off the session doc. Without it the callable
    // rejects the crew claim rather than posting a forgeable "Crew said X" onto a
    // customer-visible record.
    // TASK-4707 — no portal token: the crew path is driverToken + project ids.
    await submitNote({
      driverToken: window.AppState?.driverAccessToken,
      customerId,
      opportunityId,
      text,
      authorName: window.AppState?.driverPersonName || 'Crew',
      authorType: 'crew'
    });
    input.value = '';
    showToast('Note sent', 'success');
    // Reload projects to get updated notes
    const driverToken = window.AppState?.driverAccessToken;
    if (driverToken) {
      await loadCrewPortalProjects(driverToken);
      const project = (window.AppState?.crewPortalProjects || []).find(p => p.customerId === customerId && p.opportunityId === opportunityId);
      if (project) renderCrewProjectDetail(project);
    }
  } catch (err) {
    console.error('[CrewPortal] Note submit error:', err);
    // Surface the server's message rather than a generic one — TASK-4719 can now
    // reject with "your crew session has expired, reload and re-enter your PIN",
    // which is actionable, and "Failed to send note" is not. Matches what
    // crewCompleteStep already does. The typed note is NOT cleared on this path
    // (the clear happens only after a successful await), so nobody re-types on a
    // phone at the dock.
    showToast(err.message || 'Failed to send note', 'error');
  }
};

// TASK-4706 — crew note on a dispatch-side Project: driver session + projectId,
// no portal token. Write-only (the office sees it on the project's Notes tab).
// The typed text is kept on failure, cleared only after the server says ok.
window.crewSubmitProjectNote = async (projectId) => {
  const input = document.getElementById('crew-note-input');
  const text = input?.value?.trim();
  if (!text || !projectId) return;
  try {
    const submitNote = httpsCallable(functions, 'portalSubmitNote');
    await submitNote({ driverToken: window.AppState?.driverAccessToken, projectId, text });
    input.value = '';
    showToast('Note sent to the office', 'success');
  } catch (err) {
    console.error('[CrewPortal] Project note error:', err);
    showToast(err.message || 'Failed to send note', 'error');
  }
};

// TASK-416 — confirm the driver session is RULE-VISIBLE before attaching any
// Firestore subscriptions. A successful read of tenants/{tid} by an anonymous
// PIN user can pass ONLY through hasDriverSession() (firestore.rules:165 -> 101
// -> 90: requires driverSessions/{uid} to exist + tenantId match + valid expiry),
// so success proves the session doc has propagated AND satisfies the exact rule
// the subscriptions depend on. Replaces the old blind setTimeout(500) that, on a
// slow cold load, let subs attach before the doc was visible -> permission-denied
// flood -> driver kicked out. First attempt is immediate (keeps the login speedup).
// Returns { snap } on success, { stale:true } if the caller was superseded, or
// { timeout:true } if never visible within the cap (~8s, generous for weak cell).
// TASK-1202 — first 4 retries poll at 150ms (propagation usually settles well
// under a second, and each 400ms wait was pure dead air on the measured 12s
// PIN→board path), then back off to the original 400ms. tries=23 keeps the
// worst-case wait at ~8.2s (4×150 + 19×400), matching the old ~8s cap. Also
// logs tries-actually-used so prod consoles show where the wait really lands.
async function _awaitDriverSessionVisible(tenantId, { tries = 23, delayMs = 400, isStale } = {}) {
  const _t0 = Date.now();
  for (let i = 0; i < tries; i++) {
    if (isStale && isStale()) return { stale: true };
    try {
      const snap = await getDoc(doc(db, 'tenants', tenantId));
      if (i > 0) console.info(`[DriverPIN] Session rule-visible after ${i + 1} tries (${Date.now() - _t0}ms)`);
      return { snap }; // readable => hasDriverSession passes => session rule-visible
    } catch (e) {
      if (e?.code !== 'permission-denied') throw e; // a real error, not propagation lag
      await new Promise(r => setTimeout(r, i < 4 ? Math.min(150, delayMs) : delayMs));
    }
  }
  console.warn(`[DriverPIN] Session NOT rule-visible after ${tries} tries (${Date.now() - _t0}ms)`);
  return { timeout: true };
}

async function checkDriverPinSession() {
  // Check for existing driver PIN session
  const savedSession = localStorage.getItem('driverPinAccess');
  if (!savedSession) return false;

  try {
    const session = JSON.parse(savedSession);

    // Validate token is still valid
    const validateFn = httpsCallable(functions, 'validateDriverToken');
    const result = await validateFn({ token: session.token });

    if (result.data.ok) {
      // Restore driver mode state
      window.AppState.driverModePin = true;
      window.AppState.driverPersonId = result.data.personId;
      window.AppState.driverPersonName = result.data.personName;
      window.AppState.tenant = result.data.tenantId;
      window.AppState.driverAccessToken = session.token;
      // TASK-402 leak fix — hide the owner/admin-only carrier assistant FAB on
      // the driver-session restore path too (see submitDriverPin for context).
      if (typeof initCarrierAssistant === 'function') initCarrierAssistant();
      window.AppState.driverAddons = result.data.addons || session.addons || {};
      window.AppState.driverIsTrialing = !!result.data.isTrialing;
      window.AppState.driverAllowMaintenance = result.data.allowMaintenance !== undefined ? !!result.data.allowMaintenance : !!session.allowMaintenance;

      // TASK-429 — establish Firestore auth via ensureDriverAnonAuth() rather
      // than a RAW signInAnonymously(). On restore the persisted Firebase
      // session may still be settling; a raw anon sign-in here churned the
      // uid (overriding currentUser moments later) and triggered the
      // permission-denied flood on already-mounted listeners. The helper
      // awaits restoration and reuses an existing ANONYMOUS user (token-
      // refreshed), but NEVER reuses a real signed-in account — linkDriverSession
      // below force-overwrites the uid's claims with driver tenant claims, which
      // would corrupt a real account's session (COD Code Reviewer ❌).
      const _restoreUser = await ensureDriverAnonAuth();
      window.AppState.user = _restoreUser || auth.currentUser;

      // Link the (reused or fresh) UID to the driver token for Firestore access
      const linkFn = httpsCallable(functions, 'linkDriverSession');
      const linkResult = await linkFn({ token: session.token });
      if (!linkResult.data.ok) {
        console.error('[DriverPIN] Failed to link session:', linkResult.data.error);
        throw new Error('Failed to link session');
      }
      // Same token-refresh as the fresh-PIN path — guarantees the tenantId
      // custom claim is in the cached ID token before any Storage upload.
      try {
        if (window.AppState?.user?.getIdToken) {
          await window.AppState.user.getIdToken(true);
        }
      } catch (refreshErr) {
        console.warn('[DriverPIN] Token refresh after link failed (non-fatal):', refreshErr?.message);
      }
      console.log('[DriverPIN] Session linked successfully');

      // TASK-416 — gate on rule-visibility instead of a blind 500ms sleep.
      const _vis = await _awaitDriverSessionVisible(result.data.tenantId);
      if (!_vis.snap) {
        // Never became rule-visible within the cap. HALT — do NOT attach subs
        // (that reproduces the flood). signOut the idle anon user, but PRESERVE
        // the saved PIN session so reopening the app retries WITHOUT re-typing the
        // code (the token is valid; only Firestore propagation timed out).
        console.warn('[DriverPIN] Session not rule-visible after retries — halting restore (session preserved)');
        try { await auth.signOut(); } catch (_) {}
        showToast?.('Connection too slow to load your schedule. Please reopen the app to try again.', 'error');
        return false;
      }

      document.body.classList.add('driver-mode-pin');

      // Load tenant settings (needed for clockOutMode, billing config, Time Off balance)
      if (_vis.snap.exists()) {
        window.AppState.settings = _vis.snap.data().settings || {};
        window.AppState.tenantData = _vis.snap.data();
      }

      await primeSelfWithin(); // TASK-9553 — this driver's own clock mode

      // Load portal projects (async, non-blocking)
      loadCrewPortalProjects(session.token).catch(e => console.warn('[DriverPIN] Portal projects:', e.message));

      // TASK-774 Gap B — surface assigned maintenance to grant-less drivers.
      await checkDriverHasAssignedMaintenance();

      return true;
    }
  } catch (err) {
    console.log('[DriverPIN] Session invalid:', err.message);
  }

  // Clear invalid session
  localStorage.removeItem('driverPinAccess');
  return false;
}

// TASK-903 — shared driver-PIN chrome + AppState residue teardown. showAuthModal()
// is the common return-to-login path but historically did NOT remove the body-level
// #driver-pin-shell-chrome div or the body.driver-mode-pin class, so the driver
// bottom nav leaked onto the Driver Code Login screen. handleLogout /
// exitDriverPinMode / real-login-over-PIN each rolled their own (slightly
// divergent) teardown; this consolidates them so EVERY return-to-login path tears
// the PIN chrome + state down identically. Idempotent + safe to call when no PIN
// session is active (no-op). Does NOT touch auth user / tenant / fleet bridges —
// those differ per call site (e.g. real-login-over-PIN keeps the signed-in user),
// so each caller handles its own non-PIN state after calling this.
function _teardownDriverPinChrome() {
  try { document.getElementById('driver-pin-shell-chrome')?.remove(); } catch (_) {}
  try { document.body.classList.remove('driver-mode-pin'); } catch (_) {}
  const as = window.AppState || {};
  // TASK-9399 — forget the exiting driver's Job Board customer filter
  // (jobboard.js _jbCustomerFilterKey: `${prefix}${tenantId}_${personId}`), so
  // the next driver on a shared PIN tablet starts clean. Matched by prefix +
  // personId suffix because the tenant id may already be gone at this point.
  if (as.driverModePin && as.driverPersonId) {
    try {
      const suffix = `_${as.driverPersonId}`;
      const doomed = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith('dd_jobboard_calfilter_v1_') && k.endsWith(suffix)) doomed.push(k);
      }
      doomed.forEach(k => localStorage.removeItem(k));
    } catch (_) {}
    // …and the pre-9399 tenant-only sessionStorage copy, which the next
    // driver's first read would otherwise migrate into THEIR key.
    try {
      const legacy = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const k = sessionStorage.key(i);
        if (k && k.startsWith('jb_calfilter_')) legacy.push(k);
      }
      legacy.forEach(k => sessionStorage.removeItem(k));
    } catch (_) {}
  }
  as.driverModePin = false;
  as.driverPersonId = null;
  as.driverPersonName = null;
  as.driverAccessToken = null;
  as.driverAddons = {};
  as.driverAllowMaintenance = false;
  as.driverHasAssignedMaintenance = false; // TASK-774 Gap B
  try { localStorage.removeItem('driverPinAccess'); } catch (_) {}
  // TASK-974 S2 — tear down the driver Spanish layer with the rest of the PIN
  // chrome: restores the exact original English strings, disconnects the
  // observer, removes the toggle/tile. Guarantees a dispatcher exiting a PIN
  // preview gets a 100% English office app back. Language PREFERENCE persists
  // in localStorage for the driver's next session. Safe no-op when never loaded.
  try { window.DriverI18n?.deactivate?.(); } catch (_) {}
  // Defensively hide the static driver-shell nav stub. It ships hidden +
  // aria-hidden in index.html (future-slice scaffold), but belt-and-suspenders so
  // it can never paint or be exposed to AT on the login screen.
  const _stub = document.getElementById('driver-shell-nav');
  if (_stub) { _stub.classList.add('hidden'); _stub.setAttribute('aria-hidden', 'true'); }
}

// TASK-905 — SILENT driver-PIN recovery after an anon-session drop (token
// rotation, network blip, version-forced reload) fires onAuthStateChanged(null)
// mid-shift. The old code dumped the driver straight to the carrier login screen
// ("kicked out"). This re-establishes the driver session in place and remounts the
// current view instead.
//
// DELIBERATELY DISTINCT from checkDriverPinSession() (the cold-start restore):
//  - checkDriverPinSession removes the saved token on ANY catch. This must NOT —
//    a network/timeout failure PRESERVES the token (mirror the _awaitDriverSessionVisible
//    HALT-but-preserve contract) so the driver can reconnect without re-typing.
//    The token is cleared ONLY on a DEFINITIVE validateDriverToken not-ok.
//  - Uses its OWN bounded retry counter (never re-enters checkDriverPinSession).
//  - Holds window.AppState.driverPinLoginInProgress throughout so the nested
//    signOut()/signInAnonymously() churn inside ensureDriverAnonAuth() (each of
//    which fires onAuthStateChanged) can't re-enter handleAuthStateChange and burn
//    a retry — that handler short-circuits on this flag (see top of
//    handleAuthStateChange). The flag is cleared in finally{} (mitigation #2).
// Returns { recovered:true } on success, { recovered:false, definitiveInvalid:true }
// when the token was definitively invalid (and cleared), or
// { recovered:false, definitiveInvalid:false } when retries were exhausted on a
// transient failure (token PRESERVED). Never logs the token — only attempt + code.
const _PIN_RECOVERY_MAX_ATTEMPTS = 3;
const _PIN_RECOVERY_BACKOFF_MS = 600;
async function _recoverDriverPinSession(savedSession) {
  // Mitigation #1 — hold the in-progress guard so anon-auth churn below can't
  // re-enter handleAuthStateChange concurrently with this recovery.
  window.AppState.driverPinLoginInProgress = true;
  let recovered = false;
  try {
    for (let attempt = 1; attempt <= _PIN_RECOVERY_MAX_ATTEMPTS; attempt++) {
      try {
        console.log('[PINRecover] attempt', attempt, 'of', _PIN_RECOVERY_MAX_ATTEMPTS);

        // 1. Validate token FIRST — separates a DEFINITIVE invalid token (clear +
        //    abandon, no point retrying) from a transient network error (preserve).
        const validateFn = httpsCallable(functions, 'validateDriverToken');
        let validation;
        try {
          validation = await validateFn({ token: savedSession.token });
        } catch (netErr) {
          console.warn('[PINRecover] validate network error (token preserved):', netErr?.code || 'unknown');
          await new Promise(r => setTimeout(r, _PIN_RECOVERY_BACKOFF_MS));
          continue;
        }
        if (!validation?.data?.ok) {
          // Only DEFINITIVE invalid-token errors clear the saved session. The
          // backend (validateDriverToken) CATCHES transient/internal errors and
          // RETURNS {ok:false,error:"Unable to validate token"} rather than throwing
          // — so a transient server/Firestore hiccup must NOT be treated as
          // definitive, or it burns a valid driver's token (the exact "kicked out,
          // can't get back in" bug this task fixes). Only these three are real
          // invalid-token verdicts; anything else is transient → preserve + retry.
          // (COD Code Reviewer ⚠️.)
          const _err = validation?.data?.error || '';
          const _DEFINITIVE = ['No token provided', 'Token not found', 'Token expired'];
          if (_DEFINITIVE.includes(_err)) {
            console.warn('[PINRecover] token definitively invalid — clearing saved session');
            try { localStorage.removeItem('driverPinAccess'); } catch (_) {}
            return { recovered: false, definitiveInvalid: true };
          }
          console.warn('[PINRecover] transient validate failure (token preserved):', _err || 'unknown');
          await new Promise(r => setTimeout(r, _PIN_RECOVERY_BACKOFF_MS));
          continue;
        }

        // 2. Re-establish Firestore auth (reuses an existing anon user token-
        //    refreshed, or mints a fresh one; NEVER reuses a real account).
        const _user = await ensureDriverAnonAuth();
        window.AppState.user = _user || auth.currentUser;

        // 3. Re-link the (reused/fresh) uid to the driver token.
        const linkFn = httpsCallable(functions, 'linkDriverSession');
        const linkResult = await linkFn({ token: savedSession.token });
        if (!linkResult?.data?.ok) {
          console.warn('[PINRecover] link failed (token preserved, retrying):', linkResult?.data?.error || 'unknown');
          await new Promise(r => setTimeout(r, _PIN_RECOVERY_BACKOFF_MS));
          continue;
        }

        // Restore PIN state from the validated session (tenant/etc. may have been
        // nulled by a prior pass through the signed-out branch).
        window.AppState.driverModePin = true;
        window.AppState.tenant = validation.data.tenantId || savedSession.tenantId;
        window.AppState.driverPersonId = validation.data.personId || savedSession.personId;
        window.AppState.driverPersonName = validation.data.personName || savedSession.personName;
        window.AppState.driverAccessToken = savedSession.token;
        window.AppState.driverAddons = validation.data.addons || savedSession.addons || {};
        window.AppState.driverIsTrialing = !!validation.data.isTrialing;
        window.AppState.driverAllowMaintenance = validation.data.allowMaintenance !== undefined
          ? !!validation.data.allowMaintenance
          : !!savedSession.allowMaintenance;

        // Refresh the cached ID token so tenantId/role claims for this uid are live
        // before any subscription or Storage upload (same as the login paths).
        try { if (window.AppState.user?.getIdToken) await window.AppState.user.getIdToken(true); } catch (_) {}

        // 4. Gate on rule-visibility before remount (HALT-but-preserve: a timeout
        //    PRESERVES the token and retries/abandons; it never nukes it).
        const _vis = await _awaitDriverSessionVisible(window.AppState.tenant);
        if (!_vis.snap) {
          console.warn('[PINRecover] session not rule-visible (token preserved, retrying)');
          await new Promise(r => setTimeout(r, _PIN_RECOVERY_BACKOFF_MS));
          continue;
        }
        if (_vis.snap.exists()) {
          window.AppState.settings = _vis.snap.data().settings || {};
          window.AppState.tenantData = _vis.snap.data();
        }
        document.body.classList.add('driver-mode-pin');

        // TASK-774 Gap B — re-surface assigned maintenance to grant-less drivers.
        try { await checkDriverHasAssignedMaintenance(); } catch (_) {}

        // 5. Remount the view the driver was on (jobboard is the PIN default).
        navigateToView(window.AppState.currentView || 'jobboard');
        recovered = true;
        console.log('[PINRecover] recovered on attempt', attempt);
        return { recovered: true };
      } catch (loopErr) {
        // Unexpected error — PRESERVE the token, retry within the bound.
        console.warn('[PINRecover] attempt', attempt, 'threw (token preserved):', loopErr?.code || 'error');
        await new Promise(r => setTimeout(r, _PIN_RECOVERY_BACKOFF_MS));
      }
    }
    console.warn('[PINRecover] exhausted', _PIN_RECOVERY_MAX_ATTEMPTS, 'attempts — token preserved, falling through to PIN re-entry');
    // 2026-07-09 — same zombie-state fix as _enterDriverPortalWithSession: the
    // loop sets driverModePin=true per attempt; on exhaustion roll the optimistic
    // state back so the login screen isn't half-driver (token stays preserved).
    _rollbackDriverPinOptimisticState();
    return { recovered: false, definitiveInvalid: false };
  } finally {
    // Mirror the _pinRestoreSkipInitialAuthFire pattern: on success a straggler
    // same-uid anonymous fire could still be queued for the new anon user —
    // swallow exactly that one so the PIN-mount path doesn't run concurrently with
    // the remount above. Harmless no-op if none is pending; only ever masks a
    // same-uid anon fire (a real later event still flows through — see the handler).
    if (recovered) window.AppState._pinRestoreSkipInitialAuthFire = true;
    // Mitigation #2 — always release the guard, success or failure.
    window.AppState.driverPinLoginInProgress = false;
  }
}

async function exitDriverPinMode() {
  // Clean up Firestore subscriptions FIRST to prevent permission errors after signout
  if (window.AppState.viewCleanup) {
    window.AppState.viewCleanup();
    window.AppState.viewCleanup = null;
  }

  // TASK-903 — shared PIN chrome + AppState residue teardown (localStorage token,
  // driver* fields, body.driver-mode-pin, #driver-pin-shell-chrome, stub nav).
  _teardownDriverPinChrome();

  // exitDriverPinMode additionally drops the auth user + tenant context and clears
  // the fleet bridge (cross-tenant residue guard), then fully signs out below.
  window.AppState.user = null;
  window.AppState.tenant = null;
  // TASK-379 — clear fleet bridge (cross-tenant residue guard) on driver-PIN logout.
  window.AppState.fleetTrucks = null;
  window.AppState.fleetTrailers = null;
  window.AppState.fleetPeople = null;

  window.ClockMode?.reset?.(); // TASK-9553
  try { window.DispatchModule?.resetTenantData?.(); } catch (_) {} // P0 1019 — board roster/fleet never outlives the tenant
  window.AppState.signingOut = true; // Prevent handleAuthStateChange from running during signout

  // Sign out fully before showing auth modal
  try {
    await signOut(auth);
  } catch (e) {
    console.warn('[DriverPIN] signOut error:', e.message);
  }

  window.AppState.signingOut = false;
  hideSidebar();
  showAuthModal();
}
window.exitDriverPinMode = exitDriverPinMode;

// H3 — is the current session a READ-ONLY access-link session (view-only share)?
// True for every ?access= link (TASK-10097: legacy links too). A PIN
// session (driverModePin) is never read-only. Drives the UI:
// when true, hide/disable clock/ticket/expense actions and show a PIN-login hint.
function isReadOnlyLinkSession() {
  const as = window.AppState || {};
  return !!as.isPublicAccess && !as.driverModePin && as.accessTokenReadOnly === true;
}
window.isReadOnlyLinkSession = isReadOnlyLinkSession;

function driverHasClockAccess() {
  // H3 — a read-only link session can never clock in (view-only). Belt-and-
  // suspenders to the rule + the existing driverModePin gate below.
  if (isReadOnlyLinkSession()) return false;
  if (!window.AppState?.driverModePin) return false;
  // Founder/VIP always have access
  if (['trippin-trucking'].includes(window.AppState.tenant)) return true;
  if (typeof window.isVipAccount === 'function' && window.isVipAccount()) return true;
  // Trial accounts get full access (matches hasAddOnPackage behavior)
  if (window.AppState.driverIsTrialing) return true;
  // Check clockbilling add-on
  const addons = window.AppState.driverAddons || {};
  return addons.clockbilling === true;
}
window.driverHasClockAccess = driverHasClockAccess;

// TASK-9373 driver-app-layout BEGIN
// ============================================
// TASK-9373 — "Driver App" layout: per-tenant visibility + order for the PIN
// app's bottom nav, Actions sheet and job-details modal sections.
// ONE key (settings.driverApp), ONE reader (driverAppLayout), consumed by
// dispatch.js getDriverBottomNavHTML / _renderDriverActionTiles and
// jobboard.js showJobDetails. Presets live HERE, in code, not in data.
//
// Schema: { v:1, preset:'standard'|'simple'|'custom',
//           nav:{hidden:[],order:[]}, actions:{hidden:[],order:[]},
//           jobModal:{hidden:[]} }
// Ids are the existing DOM ids (driver-btmnav-*, tile-*) and the
// data-section values on the modal.
//
// NEVER_HIDE is enforced here (a hidden entry naming one is dropped) AND in
// firestore.rules t9373DriverAppOk (a write carrying one is denied), because a
// client-only check is bypassed by a console write. Why each one is there:
//   driver-btmnav-board    the only way back off-board once #mobile-nav hides
//   driver-btmnav-inspect  the only route to a DVIR — 49 CFR 396.11
//   driver-btmnav-clock    a hidden clock tab = no clock-out = a trapped shift
//   driver-btmnav-actions  hosts Help + Sign Out
//   tile-vehicles          the only DVIR-adjacent vehicle list (396.11)
//   tile-mydocs            the driver's own DQ file — theirs by right (391.51)
//   tile-help / tile-signout  Sign Out was lost once already (v1.15.914)
//   tile-timeoff / tile-maintenance  the ONLY PIN entry points to Time Off and
//                          Maintenance once .jobboard-actions + #mobile-nav
//                          hide — hiding them would strand a driver whose gate
//                          is ON (COD Edge Case, 2026-09-24). They hide by
//                          their GATE being off, never by this layout.
//   clock / jsa            modal sections that carry the money + safety flows
// Gates still apply AND-wise: a tile its feature gate hides cannot be forced
// back on by `order`; this layer can only hide MORE, never show more.
// Defaults (absent key, or preset 'standard') render byte-identical to the
// pre-task markup — tests/driver-app-layout.unit.cjs holds the snapshot.
const DRIVER_APP_LAYOUT = Object.freeze({
  v: 1,
  PRESETS: Object.freeze(['standard', 'simple', 'custom']),
  MAX_LIST: 40,
  nav: Object.freeze({
    ids: Object.freeze(['driver-btmnav-board', 'driver-btmnav-inspect', 'driver-btmnav-clock', 'driver-btmnav-exam', 'driver-btmnav-actions']),
    neverHide: Object.freeze(['driver-btmnav-board', 'driver-btmnav-inspect', 'driver-btmnav-clock', 'driver-btmnav-actions']),
    pinFirst: 'driver-btmnav-board',
    pinLast: null,
    hasOrder: true,
  }),
  actions: Object.freeze({
    ids: Object.freeze(['tile-timeoff', 'tile-vehicles', 'tile-maintenance', 'tile-team', 'tile-docs', 'tile-mydocs', 'tile-switchjob', 'tile-timesheet', 'tile-record-expense', 'tile-help', 'tile-signout']),
    neverHide: Object.freeze(['tile-timeoff', 'tile-vehicles', 'tile-maintenance', 'tile-mydocs', 'tile-help', 'tile-signout']),
    pinFirst: null,
    pinLast: 'tile-signout',
    hasOrder: true,
  }),
  jobModal: Object.freeze({
    // `inspections` = TASK-9372's section (tag its wrapper data-section="inspections").
    // `type` = TASK-9390's Job Type row (shown only for a non-default type).
    // `documents` = TASK-9740's Documents section (signed BOL / disposal tickets; wrapper data-section="documents").
    ids: Object.freeze(['customer', 'po', 'vendor', 'type', 'start', 'location', 'status', 'crew', 'trucks', 'trailers', 'notes', 'attachments', 'permits', '811', 'clock', 'jsa', 'tickets', 'inspections', 'documents']),
    neverHide: Object.freeze(['clock', 'jsa']),
    pinFirst: null,
    pinLast: null,
    hasOrder: false,
  }),
});
// Presets in code (COD Code Reviewer). Standard = today's defaults.
// ⚠️ The Simple recipe is a DRAFT for Chris's review (TASK-9373 block, item 4):
// hides Exam Report, Doc Page, Team, Record Expense, Switch Job, My Hours;
// hides modal PO, Vendor, Status, Trucks, Trailers, Attachments,
// Load Permits. Gated tiles hide via `hidden` only — the gate itself is not
// touched. Nothing in NEVER_HIDE appears here (the tripwire asserts it).
const DRIVER_APP_PRESETS = Object.freeze({
  standard: Object.freeze({
    nav: { hidden: [], order: [] },
    actions: { hidden: [], order: [] },
    jobModal: { hidden: [] },
  }),
  simple: Object.freeze({
    nav: { hidden: ['driver-btmnav-exam'], order: [] },
    actions: { hidden: ['tile-docs', 'tile-team', 'tile-record-expense', 'tile-switchjob', 'tile-timesheet'], order: [] },
    jobModal: { hidden: ['po', 'vendor', 'status', 'trucks', 'trailers', 'attachments', 'permits'] },
  }),
});
function _daStringList(v) {
  if (!Array.isArray(v)) return [];
  const out = [];
  for (const x of v) {
    if (typeof x !== 'string') continue;
    const s = x.trim();
    if (!s || s.length > 64 || out.includes(s)) continue;
    out.push(s);
    if (out.length >= DRIVER_APP_LAYOUT.MAX_LIST) break;
  }
  return out;
}
// Fill defaults, drop unknown ids, strip NEVER_HIDE, append any known id
// missing from `order` (an order captured on-board omits driver-btmnav-board),
// pin board first / Sign Out last. Pure — no window reads.
function normalizeDriverAppLayout(raw) {
  const src = (raw && typeof raw === 'object' && !Array.isArray(raw)) ? raw : {};
  const preset = DRIVER_APP_LAYOUT.PRESETS.includes(src.preset) ? src.preset : 'standard';
  const out = { v: 1, preset };
  for (const kind of ['nav', 'actions', 'jobModal']) {
    const spec = DRIVER_APP_LAYOUT[kind];
    const sub = (src[kind] && typeof src[kind] === 'object' && !Array.isArray(src[kind])) ? src[kind] : {};
    const hidden = _daStringList(sub.hidden).filter(id => spec.ids.includes(id) && !spec.neverHide.includes(id));
    const entry = { hidden };
    if (spec.hasOrder) {
      const pinned = [spec.pinFirst, spec.pinLast].filter(Boolean);
      const wanted = _daStringList(sub.order).filter(id => spec.ids.includes(id) && !pinned.includes(id));
      const rest = spec.ids.filter(id => !wanted.includes(id) && !pinned.includes(id));
      entry.order = (spec.pinFirst ? [spec.pinFirst] : []).concat(wanted, rest, spec.pinLast ? [spec.pinLast] : []);
    }
    out[kind] = entry;
  }
  return out;
}
function driverAppLayout() {
  return normalizeDriverAppLayout(window.AppState?.settings?.driverApp);
}
function driverAppIsHidden(kind, id, layout) {
  const l = layout || driverAppLayout();
  const spec = DRIVER_APP_LAYOUT[kind];
  if (!spec || !l || !l[kind]) return false;
  if (spec.neverHide.includes(id)) return false;
  return l[kind].hidden.includes(id);
}
// items = [{id, …}] already filtered by their feature gates. Drops hidden ids,
// stable-sorts known ids by `order`; ids this layer does not know (a tile
// added later) keep their relative position after the known ones. Default
// layout ⇒ identity (the same array back, same order).
function driverAppApplyOrder(kind, items, layout) {
  const l = layout || driverAppLayout();
  const spec = DRIVER_APP_LAYOUT[kind];
  if (!spec || !l || !l[kind]) return items;
  const order = l[kind].order || spec.ids;
  const kept = items.filter(it => it && !driverAppIsHidden(kind, it.id, l));
  return kept
    .map((it, i) => ({ it, i, o: order.indexOf(it.id) }))
    .sort((a, b) => {
      const ao = a.o === -1 ? order.length + a.i : a.o;
      const bo = b.o === -1 ? order.length + b.i : b.o;
      return ao - bo || a.i - b.i;
    })
    .map(x => x.it);
}
function driverAppPresetRecipe(name) {
  const key = Object.prototype.hasOwnProperty.call(DRIVER_APP_PRESETS, name) ? name : 'standard';
  const recipe = DRIVER_APP_PRESETS[key];
  return normalizeDriverAppLayout({
    v: 1, preset: key,
    nav: { hidden: recipe.nav.hidden.slice(), order: recipe.nav.order.slice() },
    actions: { hidden: recipe.actions.hidden.slice(), order: recipe.actions.order.slice() },
    jobModal: { hidden: recipe.jobModal.hidden.slice() },
  });
}
window.DRIVER_APP_LAYOUT = DRIVER_APP_LAYOUT;
window.DRIVER_APP_PRESETS = DRIVER_APP_PRESETS;
window.normalizeDriverAppLayout = normalizeDriverAppLayout;
window.driverAppLayout = driverAppLayout;
window.driverAppIsHidden = driverAppIsHidden;
window.driverAppApplyOrder = driverAppApplyOrder;
window.driverAppPresetRecipe = driverAppPresetRecipe;
// TASK-9373 driver-app-layout END

// TASK-774 Gap B — a driver assigned a maintenance task needs to SEE it even
// without the per-driver `allowMaintenance` grant (allowMaintenance is purely a
// frontend view/tile gate — firestore.rules:1022 already lets a driver session
// READ maintenance). This probes for ≥1 task assigned to the driver and sets
// `driverHasAssignedMaintenance`, which the router/tile gates OR alongside
// `driverAllowMaintenance`. SCOPED: the driver maintenance list already filters
// to tasks assigned to / created by them, so a grant-less assigned driver only
// ever sees their own tasks — never Report-Issue against other vehicles.
async function checkDriverHasAssignedMaintenance() {
  window.AppState.driverHasAssignedMaintenance = false;
  try {
    const tenant = window.AppState?.tenant;
    const personId = window.AppState?.driverPersonId;
    if (!tenant || !personId) return false;
    const snap = await getDocs(query(
      collection(db, 'tenants', tenant, 'maintenance'),
      where('assignedTo', '==', personId),
      limit(1)
    ));
    window.AppState.driverHasAssignedMaintenance = !snap.empty;
    return window.AppState.driverHasAssignedMaintenance;
  } catch (err) {
    console.warn('[DriverPIN] Assigned-maintenance check failed (non-fatal):', err?.message);
    return false;
  }
}
window.checkDriverHasAssignedMaintenance = checkDriverHasAssignedMaintenance;

// True when the PIN driver may reach the Maintenance surface — either the explicit
// per-driver grant OR they have ≥1 task assigned to them (TASK-774 Gap B).
function driverCanSeeMaintenance() {
  return !!(window.AppState?.driverAllowMaintenance || window.AppState?.driverHasAssignedMaintenance);
}
window.driverCanSeeMaintenance = driverCanSeeMaintenance;

function showAuthError(message) {
  DOM.authError.textContent = message;
  DOM.authError.classList.remove('hidden');
  // A fresh error must never sit under a stale "check your email" banner.
  document.getElementById('auth-success')?.classList.add('hidden');
  // Also show toast as backup (error div may not be visible)
  showToast(message, 'error');
}

// TASK-1349 — the mirror of showAuthError for the one message users need to
// SEE and keep seeing: "we sent the reset email." It used to be a 1800ms toast
// in the bottom-right corner while the user's eyes were on the centered modal,
// so the send read as "nothing happened." Persists until the next auth action.
function showAuthSuccess(message) {
  const el = document.getElementById('auth-success');
  if (el) {
    el.textContent = message;
    el.classList.remove('hidden');
  }
  DOM.authError?.classList.add('hidden');
  showToast(message, 'success', null, 6000);
}

function hideAuthError() {
  DOM.authError.classList.add('hidden');
  document.getElementById('auth-success')?.classList.add('hidden');
}

// TASK-4593 — every toast used to vanish in 1800ms, errors included, and only 3
// of the ~800 call sites in public/src pass an explicit duration. So the app's
// most important messages were its least readable ones. Sean tapped Grab GPS on
// the driver board, the failure toast came and went before he could read it, and
// the message he missed was the 230-character one that tells him exactly how to
// fix it ("Location is blocked. Turn on location for this site…"). Nobody reads
// 230 characters in 1.8 seconds — reading rate says that message needs ~12s.
//
// Errors and warnings now persist and carry a dismiss ✕. Success/info are
// unchanged at 1800ms: they are confirmations, and making those sticky would
// train everyone to swipe toasts away without reading, which costs us the
// errors again.
//
// 🔴 THEY ARE NOT STICKY, AND THAT WAS A DELIBERATE REVERSAL. My first cut made
// error/warning toasts persist until dismissed. COD Code Reviewer killed it, and
// the evidence is three lines above this file's own container CSS:
// `#toast-container` is `position:fixed; bottom; right; z-index:1000001` with no
// `pointer-events` rule, while the driver Build Ticket footer is
// `position:fixed;bottom:0;left:0;right:0` holding `#btn-clockout-submit`
// (jobboard.js:6533). A stack of undismissed toasts sits directly on top of the
// primary button — and the container's own comment records a driver reporting a
// Save button as "dead" when a toast painted over it. Trapping a driver on a
// clock-out screen is a worse bug than the one being fixed (TASK-428 shape).
//
// So: long enough to READ, then gone on their own, with a ✕ to clear early.
// 12s ≈ the 230-character GPS message at normal reading speed.
const _TOAST_DISMISSIBLE = { error: 12000, warning: 8000 };
// Cap concurrent dismissible toasts. Driver `'error'` includes routine
// validation nags (shift note, bad time, cost allocation, amount —
// jobboard.js:7195, :8869), so bursts are normal, not exceptional.
const _TOAST_DISMISSIBLE_MAX = 2;

// durationMs: null = the type's default; a number = auto-hide after that many
// ms; Infinity = STICKY (TASK-9363 r4) — stays until the user taps ✕. 0 is NOT
// sticky: it is a zero-length timer and the toast vanishes at once.
function showToast(message, type = 'info', action = null, durationMs = null) {
  const toast = document.createElement('div');
  toast.className = `toast ${type}`;
  const _sticky = durationMs === Infinity;
  const _readMs = (durationMs == null && !action) ? _TOAST_DISMISSIBLE[type] : null;
  const _dismissible = _readMs != null || _sticky;

  // Keep the text in its own node — appending buttons to a textContent-only
  // element is what made the existing action button sit inline with the copy.
  const _text = document.createElement('span');
  _text.className = 'toast-text';
  _text.textContent = message;
  toast.appendChild(_text);

  const _dismiss = () => {
    if (!toast.parentNode) return;
    toast.style.animation = 'slideIn 0.3s ease reverse';
    setTimeout(() => toast.remove(), 300);
  };

  if (action) {
    const btn = document.createElement('button');
    btn.className = 'toast-action';
    btn.textContent = action.label || 'Undo';
    btn.onclick = () => {
      action.callback?.();
      _dismiss();
    };
    toast.appendChild(btn);
  }

  if (_dismissible) {
    // Oldest-first eviction, so a burst leaves the NEWEST errors on screen —
    // the last one is usually the one that actually stopped the driver.
    // A STICKY toast is never evicted and never counts toward the cap: it is
    // there because the user must act on it.
    if (_sticky) {
      toast.dataset.sticky = '1';
    } else {
      const _existing = DOM.toastContainer.querySelectorAll('.toast[data-dismissible="1"]');
      for (let i = 0; i <= _existing.length - _TOAST_DISMISSIBLE_MAX; i++) _existing[i]?.remove();
      toast.dataset.dismissible = '1';
    }
    const x = document.createElement('button');
    x.className = 'toast-dismiss';
    x.type = 'button';
    x.setAttribute('aria-label', 'Dismiss');
    x.textContent = '✕';
    x.onclick = _dismiss;
    toast.appendChild(x);
  }

  DOM.toastContainer.appendChild(toast);

  if (_sticky) return; // no timer — only the ✕ (or an action) removes it
  const timeout = durationMs != null ? durationMs
    : (action ? 15000 : (_readMs != null ? _readMs : 1800));
  setTimeout(_dismiss, timeout);
}

function hideToasts() {
  if (DOM.toastContainer) {
    DOM.toastContainer.innerHTML = '';
  }
}

function showBannerToast(message, type = 'info') {
  const toast = document.createElement('div');
  toast.className = `toast-banner ${type}`;
  toast.textContent = message;
  document.body.appendChild(toast);
  setTimeout(() => {
    toast.style.animation = 'slideDown 0.3s ease reverse';
    setTimeout(() => toast.remove(), 300);
  }, 5000);
}

// Expose toast functions globally for modules
window.showToast = showToast;
window.showBannerToast = showBannerToast;
window.hideToasts = hideToasts;

/**
 * Validate all [required] fields in a form. Returns true if valid, false if not.
 * On failure: shows toast, scrolls to field, highlights with red border.
 * Skips fields that are hidden (display:none ancestor or zero dimensions).
 */
window.validateRequiredFields = function(formEl) {
  if (typeof formEl === 'string') formEl = document.getElementById(formEl);
  if (!formEl) return true;
  const fields = formEl.querySelectorAll('[required]');
  for (const el of fields) {
    // Skip hidden fields — if the field or any ancestor is display:none, don't validate
    if (el.offsetParent === null && el.type !== 'hidden') continue;
    if (el.type === 'hidden') continue;
    const val = (el.value || '').trim();
    if (!val) {
      const label = el.closest('.form-group')?.querySelector('label')?.textContent?.replace(/\s*\*\s*$/, '').trim()
        || el.placeholder || el.id || 'Field';
      showToast(`${label} is required.`, 'error');
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Do NOT call el.focus() — on Android it opens the keyboard,
      // covering the toast and making validation look like "nothing happened"
      el.style.border = '2px solid #dc2626';
      el.style.boxShadow = '0 0 0 3px rgba(220,38,38,0.15)';
      const clear = () => { el.style.border = ''; el.style.boxShadow = ''; el.removeEventListener('input', clear); el.removeEventListener('change', clear); };
      el.addEventListener('input', clear, { once: true });
      el.addEventListener('change', clear, { once: true });
      return false;
    }
    // type=email validation
    if (el.type === 'email' && val && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
      const label = el.closest('.form-group')?.querySelector('label')?.textContent?.replace(/\s*\*\s*$/, '').trim() || 'Email';
      showToast(`${label} must be a valid email address.`, 'error');
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.focus();
      el.style.border = '2px solid #dc2626';
      el.style.boxShadow = '0 0 0 3px rgba(220,38,38,0.15)';
      const clear = () => { el.style.border = ''; el.style.boxShadow = ''; el.removeEventListener('input', clear); el.removeEventListener('change', clear); };
      el.addEventListener('input', clear, { once: true });
      el.addEventListener('change', clear, { once: true });
      return false;
    }
  }
  return true;
};

// Global count badge helper — hides badge when count is 0
window.setCountBadge = function(el, count) {
  if (typeof el === 'string') el = document.getElementById(el);
  if (!el) return;
  const n = parseInt(count, 10) || 0;
  el.textContent = n;
  el.classList.toggle('count-zero', n === 0);
};

// ============================================
// EMAIL VERIFICATION REMINDER BANNER
// ============================================
function showVerificationReminderBanner() {
  // TASK-1658 — the carrier owner's email verification is not the partner's to
  // chase, and this bar is position:fixed top:0 like the partner banner.
  if (typeof isPartnerClientSession === 'function' && isPartnerClientSession()) return;
  // Don't show if already shown or dismissed
  if (document.getElementById('verification-reminder-banner')) return;

  const deadline = window.AppState.tenantData?.verificationDeadline?.toDate?.() ||
                   window.AppState.tenantData?.verificationDeadline;
  const hoursLeft = deadline ? Math.max(0, Math.ceil((deadline - new Date()) / (1000 * 60 * 60))) : 24;

  const banner = document.createElement('div');
  banner.id = 'verification-reminder-banner';
  banner.className = 'verification-banner';
  // TASK-682: shorter copy on narrow phones (the full sentence + 3 controls
  // overflowed a 320-375px row and clipped the dismiss X off-screen).
  const isNarrow = window.matchMedia && window.matchMedia('(max-width: 480px)').matches;
  const msg = isNarrow
    ? `Verify your email (<strong>${hoursLeft}h</strong> left). Check spam.`
    : `Please verify your email within <strong>${hoursLeft} hours</strong>. <strong>Check spam folder if not in inbox.</strong>`;
  banner.innerHTML = `
    <div class="verification-banner-content">
      <span class="material-icons">mail</span>
      <span>${msg}</span>
      <button class="btn btn-sm btn-primary" onclick="recheckEmailVerification()">I've verified</button>
      <button class="btn btn-sm btn-secondary" onclick="resendVerificationEmail()">Resend Email</button>
      <button class="btn-icon" onclick="dismissVerificationBanner()" aria-label="Dismiss for this session">
        <span class="material-icons">close</span>
      </button>
    </div>
  `;
  document.body.appendChild(banner);
  document.body.classList.add('has-verification-banner');
  // TASK-682: reserve exactly the banner's wrapped height so a 2-line banner
  // can't overlap the form (the old fixed 50px reserve was too small once it wrapped).
  _setVerificationBannerHeightVar(banner);
}

// Measure the rendered banner height and expose it as a CSS var so #main-content
// reserves the correct padding even when the banner wraps to 2+ lines (TASK-682).
function _setVerificationBannerHeightVar(banner) {
  try {
    const h = (banner || document.getElementById('verification-reminder-banner'))?.offsetHeight || 50;
    document.documentElement.style.setProperty('--verification-banner-height', h + 'px');
  } catch (_) { /* non-fatal */ }
}

function dismissVerificationBanner() {
  const banner = document.getElementById('verification-reminder-banner');
  if (banner) {
    banner.remove();
    document.body.classList.remove('has-verification-banner');
    document.documentElement.style.removeProperty('--verification-banner-height');
    // Show trial banner now that verification banner is gone
    if (window.AppState.trialStatus?.status === 'trialing' && window.AppState.trialStatus?.daysRemaining <= 3) {
      showTrialBanner(window.AppState.trialStatus.daysRemaining);
    }
  }
  // TASK-682: dismiss is SESSION-ONLY (compliance). It uncovers the UI for the
  // current session but nothing is persisted — the banner RE-APPEARS on next
  // login until actually verified (verificationDeadline hard-block stays the
  // backstop). We deliberately do NOT write any sticky "dismissed" flag.
}
window.dismissVerificationBanner = dismissVerificationBanner;

// Re-measure the banner height on resize/orientation change so the reserved
// padding stays correct as the copy re-wraps (TASK-682).
window.addEventListener('resize', () => {
  if (document.getElementById('verification-reminder-banner')) {
    _setVerificationBannerHeightVar();
  }
});

async function resendVerificationEmail() {
  try {
    const user = window.AppState.user;
    const tenantId = window.AppState.tenant;
    const email = user?.email;

    if (!user) {
      showToast('Please sign in first.', 'error');
      return;
    }

    showToast('Sending verification email...', 'info');

    // Use custom Cloud Function (SendGrid branded email)
    const resendVerify = httpsCallable(functions, 'resendVerificationEmail');
    await resendVerify({ tenantId });

    // Log for Overlord visibility
    await logEmailEvent({
      type: 'verification_resend',
      recipient: email,
      tenantId: tenantId,
      status: 'sent',
      provider: 'sendgrid'
    });

    showToast('Verification email sent! Check spam folder if not in inbox.', 'success');
  } catch (error) {
    console.error('[Auth] Error resending verification:', error);

    // Log failure
    await logEmailEvent({
      type: 'verification_resend',
      recipient: window.AppState.user?.email,
      tenantId: window.AppState.tenant,
      status: 'failed',
      provider: 'sendgrid',
      error: error.message
    });

    // Handle rate limiting (Cloud Function returns resource-exhausted)
    if (error.code === 'functions/resource-exhausted') {
      showToast('Please wait 1 minute before requesting another email.', 'error');
    } else {
      showToast('Error sending email. Please try again.', 'error');
    }
  }
}
window.resendVerificationEmail = resendVerificationEmail;

// ============================================
// TRIAL SYSTEM
// ============================================
// 30-day free trial for new accounts
// After trial expires, subscription required to continue

const TRIAL_DAYS = 30; // TASK-1108 — canonical trial length (Chris 2026-07-12); matches admin console + partner "Add Client" copy (both already 30)
// FOUNDER_TENANTS already declared above

/**
 * Check trial status for current tenant
 * Returns: { status, daysRemaining, trialEnd }
 * status: 'founder' | 'subscribed' | 'trialing' | 'expired'
 */
// TASK-4769 — THE ONE READ of subscription status on the client. Server twin:
// _effectiveSubscriptionStatus() in functions/src/index.js — keep them
// identical; the tripwire executes both over the same matrix.
//   * NESTED first, then root. The access gate's historical order. Every
//     server writer moves both keys together (_subscriptionStatusWrite), so on
//     any doc written since 2026-09-11 the two orders agree; on the asymmetric
//     legacy docs the only present key wins either way.
//   * Normalised: trimmed, lower-cased, 'canceled' (Stripe) -> 'cancelled'.
// Pure (explicit arg, no AppState) so the unit test can execute it.
function resolveSubscriptionStatus(tenantData) {
  const t = tenantData || {};
  const norm = (v) => (v == null ? '' : String(v).trim().toLowerCase());
  // A blank nested value is ABSENCE (the historical `nested || root` reads fell
  // through on '' too); only a non-blank nested value wins over root.
  const s = norm(t.subscription && t.subscription.status) || norm(t.subscriptionStatus);
  if (!s) return null;
  return s === 'canceled' ? 'cancelled' : s;
}
window.resolveSubscriptionStatus = resolveSubscriptionStatus;

function getTrialStatus() {
  const tenant = window.AppState?.tenant;
  const tenantData = window.AppState?.tenantData;

  // Demo mode - no trial
  if (window.AppState?.isDemo || tenant === 'demo') {
    return { status: 'demo', daysRemaining: null, trialEnd: null };
  }

  // Founder accounts - always active
  if (FOUNDER_TENANTS.includes(tenant)) {
    return { status: 'founder', daysRemaining: null, trialEnd: null };
  }

  // VIP accounts - always active (hardcoded list OR Firestore flag OR comped — TASK-531)
  if (VIP_TENANTS.includes(tenant) || tenantData?.vipAccess === true || tenantData?.comped === true) {
    return { status: 'founder', daysRemaining: null, trialEnd: null };
  }

  // Subscription status — ONE read (TASK-4769), ONE semantics table.
  const subscriptionStatus = resolveSubscriptionStatus(tenantData);
  // Status 'comped' is the partner-sandbox provisioning stamp (server writes
  // both keys, no comped:true flag). The server twin (_subscriptionAccessClass)
  // and getPlanBillingControls.isComped already exempt it; this gate fell to
  // the trial math and would have expired a sandbox at day 30 (COD Code
  // Reviewer, 2026-10-12).
  if (subscriptionStatus === 'comped') {
    return { status: 'founder', daysRemaining: null, trialEnd: null };
  }
  if (SUBSCRIPTION_ACCESS_STATUSES.subscribed.includes(subscriptionStatus)) {
    return { status: 'subscribed', dunning: false, daysRemaining: null, trialEnd: null };
  }
  // past_due / unpaid: still 'subscribed' for every consumer of this status
  // (nav, chrome, the boot gate) — access is KEPT by decision (Chris
  // 2026-10-01). `dunning: true` is additive so a payment heads-up can read it
  // without any consumer having to learn a new status string.
  if (SUBSCRIPTION_ACCESS_STATUSES.dunning.includes(subscriptionStatus)) {
    return { status: 'subscribed', dunning: true, daysRemaining: null, trialEnd: null };
  }

  // Check trial
  const trialEnd = tenantData?.subscription?.trialEnd?.toDate?.() ||
                   (tenantData?.subscription?.trialEnd ? new Date(tenantData.subscription.trialEnd) : null);

  // No trial end set - calculate from createdAt
  if (!trialEnd) {
    const createdAt = tenantData?.createdAt?.toDate?.() ||
                      (tenantData?.createdAt ? new Date(tenantData.createdAt) : null);
    if (createdAt) {
      const calculatedTrialEnd = new Date(createdAt.getTime() + TRIAL_DAYS * 24 * 60 * 60 * 1000);
      const now = new Date();
      const daysRemaining = Math.ceil((calculatedTrialEnd - now) / (24 * 60 * 60 * 1000));

      if (daysRemaining <= 0) {
        return { status: 'expired', daysRemaining: 0, trialEnd: calculatedTrialEnd };
      }
      return { status: 'trialing', daysRemaining, trialEnd: calculatedTrialEnd };
    }
    // No createdAt - assume trial (shouldn't happen)
    return { status: 'trialing', daysRemaining: TRIAL_DAYS, trialEnd: null };
  }

  // Has explicit trialEnd
  const now = new Date();
  const daysRemaining = Math.ceil((trialEnd - now) / (24 * 60 * 60 * 1000));

  if (daysRemaining <= 0) {
    return { status: 'expired', daysRemaining: 0, trialEnd };
  }

  return { status: 'trialing', daysRemaining, trialEnd };
}

/**
 * Show trial banner with days remaining
 */
function showTrialBanner(daysRemaining) {
  // TASK-1658 — a partner inside a client account is not the payer, and this bar
  // is position:fixed top:0 like the partner banner (they would overlap).
  if (typeof isPartnerClientSession === 'function' && isPartnerClientSession()) return;
  // Don't show if already shown or trial screen is visible
  if (document.getElementById('trial-reminder-banner')) return;
  // TASK-1659 — the partner-coverage bar owns this slot during its window.
  if (document.getElementById('partner-grace-banner')) return;
  if (!document.getElementById('trial-expired-screen')?.classList.contains('hidden')) return;

  // Don't show on the auth/signup screen
  const authModal = document.getElementById('auth-modal');
  if (authModal && !authModal.classList.contains('hidden')) return;

  const urgencyClass = daysRemaining <= 1 ? 'trial-urgent' : '';
  const dayText = daysRemaining === 1 ? 'day' : 'days';

  const banner = document.createElement('div');
  banner.id = 'trial-reminder-banner';
  banner.className = `trial-banner ${urgencyClass}`;
  banner.innerHTML = `
    <div class="trial-banner-content">
      <span class="material-icons">schedule</span>
      <span><strong>${daysRemaining} ${dayText}</strong> left in your free trial</span>
      <button id="btn-banner-subscribe" class="btn btn-sm btn-primary" onclick="startSubscription(this)">Subscribe Now</button>
      <button class="btn-icon" onclick="dismissTrialBanner(true)">
        <span class="material-icons">close</span>
      </button>
    </div>
  `;
  document.body.appendChild(banner);
  document.body.classList.add('has-trial-banner');
}

// Chris 2026-10-16 — DECLINED ANNUAL RENEWAL: 30-day grace, then the account
// locks (the server cancels the subscription; the ordinary cancelled path does
// the rest). Server twin of this math: annualGraceDaysLeft in
// functions/src/services/annualBilling.js — the unit test runs both.
// Returns >= 1 while the grace runs, 0 once the lock date passed but the daily
// check has not locked yet, null when no banner applies. Pure.
function annualGraceDaysLeft(grace, nowMs) {
  if (!grace || typeof grace !== 'object' || grace.lockedAtMs) return null;
  const lockAt = Number(grace.lockAtMs);
  if (!Number.isFinite(lockAt) || lockAt <= 0 || !Number.isFinite(nowMs)) return null;
  if (lockAt <= nowMs) return 0;
  return Math.max(1, Math.ceil((lockAt - nowMs) / (24 * 60 * 60 * 1000)));
}

// Banner state for THIS viewer, or null. Owner only (the person who can fix
// the card — same isOwner as the billing buttons); only while the tenant is in
// dunning (a paid or cancelled tenant never sees it). Pure.
function annualGraceBannerState(td, { tenantId, uid, nowMs } = {}) {
  const t = td || {};
  if (!SUBSCRIPTION_ACCESS_STATUSES.dunning.includes(resolveSubscriptionStatus(t))) return null;
  if (!getPlanBillingControls(t, { tenantId, uid }).isOwner) return null;
  const daysLeft = annualGraceDaysLeft(t.subscription && t.subscription.annualGrace, nowMs);
  return daysLeft === null ? null : { daysLeft };
}

function showAnnualGraceBanner(state) {
  if (!state || document.getElementById('annual-grace-banner') || document.getElementById('trial-reminder-banner')) return;
  if (typeof isPartnerClientSession === 'function' && isPartnerClientSession()) return;
  const d = Number(state.daysLeft) || 0;
  const when = d <= 0 ? 'today' : `in <strong>${d} ${d === 1 ? 'day' : 'days'}</strong>`;
  const banner = document.createElement('div');
  banner.id = 'annual-grace-banner';
  banner.className = `trial-banner ${d <= 7 ? 'trial-urgent' : ''}`;
  banner.setAttribute('role', 'alert');
  banner.innerHTML = `
    <div class="trial-banner-content">
      <span class="material-icons">credit_card_off</span>
      <span>Your annual renewal payment was declined. Your account locks ${when} unless the card is updated.</span>
      <button id="btn-annual-grace-update" class="btn btn-sm btn-primary" onclick="openBillingPortal()">Update card</button>
      <button class="btn-icon" aria-label="Hide for now" onclick="dismissAnnualGraceBanner(true)">
        <span class="material-icons">close</span>
      </button>
    </div>
  `;
  document.body.appendChild(banner);
  document.body.classList.add('has-trial-banner');
}

// Session-only: it comes back on the next load until the card is fixed.
function dismissAnnualGraceBanner(userClicked) {
  const banner = document.getElementById('annual-grace-banner');
  if (banner) {
    banner.remove();
    document.body.classList.remove('has-trial-banner');
  }
  // TASK-1649 s2b2 — a held "costs moved" notice shows only when the USER closes
  // this bar (its X passes true); any other caller is cleanup.
  if (userClicked === true) _showPendingBgCostBanner(); else _clearBgCostBanner();
}
window.dismissAnnualGraceBanner = dismissAnnualGraceBanner;

// TASK-1649 slice 2b-2 — IN-APP NOTICE: the compliance partner moved
// background-check costs onto this carrier (Chris 2026-10-19: a notice is
// enough). The server writes compliance.bgPaidByChange (server-only, pinned in
// firestore.rules); the email goes out at the same time. Shown to the people
// who can act on it — the owner and active Billing-module holders
// (hrCanManageComplianceBilling) — for 30 days, until dismissed on this device,
// and only while the stored payer is still the carrier because of that move.
// Pure.
const BG_COST_NOTICE_DAYS = 30;
let _pendingBgCostBanner = null;
function _showPendingBgCostBanner() {
  const s = _pendingBgCostBanner;
  _pendingBgCostBanner = null;
  // Still the same signed-in company the notice was computed for.
  if (!s || !window.AppState?.user || window.AppState?.tenant !== s.tenantId) return;
  // Re-derive from the CURRENT tenant doc: the payer may have moved back while
  // the notice was held (COD r3 Edge).
  showBgCostMovedBanner(bgCostMovedNoticeState(window.AppState.tenantData, {
    tenantId: window.AppState.tenant, nowMs: Date.now(), canManage: hrCanManageComplianceBilling(),
  }));
}
function _clearBgCostBanner() {
  _pendingBgCostBanner = null;
  const b = document.getElementById('bg-cost-notice-banner');
  if (b) { b.remove(); document.body.classList.remove('has-trial-banner'); }
}
function bgCostMovedNoticeState(td, { tenantId, nowMs, canManage } = {}) {
  if (!canManage) return null;
  const t = td || {};
  if (!String(t.complianceManagerUid || '').trim()) return null;
  const c = (t.compliance && typeof t.compliance === 'object') ? t.compliance : {};
  const ch = (c.bgPaidByChange && typeof c.bgPaidByChange === 'object') ? c.bgPaidByChange : null;
  if (!ch || c.bgPaidBy !== 'carrier' || ch.value !== 'carrier' || ch.fromEffective !== 'partner') return null;
  if (ch.by !== 'partner' && ch.by !== 'partner-subuser') return null;
  const atMs = Number(ch.atMs);
  if (!Number.isFinite(atMs) || !Number.isFinite(nowMs) || nowMs - atMs > BG_COST_NOTICE_DAYS * 86400000) return null;
  const partnerName = (typeof ch.partnerName === 'string' && ch.partnerName.trim()) ? ch.partnerName.trim().slice(0, 120) : 'Your compliance partner';
  return { partnerName, atMs, tenantId: tenantId || '', key: `ddBgCostNotice:${tenantId || ''}:${atMs}` };
}

function showBgCostMovedBanner(state) {
  if (!state || document.getElementById('bg-cost-notice-banner')) return;
  // Never on the sign-in screen (the trial bar has the same guard).
  const _authModal = document.getElementById('auth-modal');
  if (_authModal && !_authModal.classList.contains('hidden')) return;
  // One bar at a time (they share the fixed top slot). If the trial / annual-
  // grace bar is up, hold this notice and show it when that bar is closed
  // (COD r1 Independent: grace returns every login for ~30 days, the same
  // window as this notice, so "skip" would mean "never").
  if (document.getElementById('trial-reminder-banner') || document.getElementById('annual-grace-banner')) {
    _pendingBgCostBanner = state;
    return;
  }
  _pendingBgCostBanner = null;
  if (typeof isPartnerClientSession === 'function' && isPartnerClientSession()) return;
  try { if (localStorage.getItem(state.key) === '1') return; } catch (_) { /* storage blocked: show it */ }
  const when = new Date(state.atMs).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  const banner = document.createElement('div');
  banner.id = 'bg-cost-notice-banner';
  banner.className = 'trial-banner';
  banner.setAttribute('role', 'status');
  banner.dataset.noticeKey = state.key;
  banner.innerHTML = `
    <div class="trial-banner-content">
      <span class="material-icons">payments</span>
      <span>${escapeHtml(state.partnerName)} moved background-check costs to your company on ${escapeHtml(when)}. Checks ordered since then are charged to your card.</span>
      <button id="btn-bg-cost-notice-review" class="btn btn-sm btn-primary" onclick="reviewBgCostMovedNotice()">Review</button>
      <button class="btn-icon" aria-label="Dismiss" onclick="dismissBgCostMovedBanner()">
        <span class="material-icons">close</span>
      </button>
    </div>
  `;
  document.body.appendChild(banner);
  document.body.classList.add('has-trial-banner');
}

function dismissBgCostMovedBanner() {
  const banner = document.getElementById('bg-cost-notice-banner');
  if (!banner) return;
  try { if (banner.dataset.noticeKey) localStorage.setItem(banner.dataset.noticeKey, '1'); } catch (_) { /* per-device convenience only */ }
  banner.remove();
  document.body.classList.remove('has-trial-banner');
}
window.dismissBgCostMovedBanner = dismissBgCostMovedBanner;
window.reviewBgCostMovedNotice = function () {
  dismissBgCostMovedBanner();
  if (typeof window.openCarrierComplianceBillingModal === 'function') window.openCarrierComplianceBillingModal();
};

function dismissTrialBanner(userClicked) {
  const banner = document.getElementById('trial-reminder-banner');
  if (banner) {
    banner.remove();
    document.body.classList.remove('has-trial-banner');
  }
  // TASK-1649 s2b2 — only the bar's own X (true) releases a held "costs moved"
  // notice. Every other caller is sign-out / session cleanup (handleLogout, the
  // signed-out auth path, showAuthModal, the verify-link sign-out): drop the
  // held notice and any shown one, so it can never paint on the login screen
  // or carry over to the next login on a shared device (COD r2 Edge/Independent).
  if (userClicked === true) _showPendingBgCostBanner(); else _clearBgCostBanner();
}
window.dismissTrialBanner = dismissTrialBanner;

// CRM "tasks due today" banner — TASK-10218: lives in utils/crmTasksBanner.js, a lazy
// chunk (perf budget); it also defines window.dismissCrmTasksBanner / openCrmTasks.
// Sign-out never waits on it: teardownAccountScopedChrome below is synchronous.
function checkCrmTasksBanner() {
  const g = window.__ddCrmBannerGen || 0; // stamped BEFORE the import: a slow chunk can't paint into the next account
  __ddChunkWrap(import('./utils/crmTasksBanner.js?v=1')).then((m) => m.check(g)).catch((e) => console.warn('[CRM Banner]', e?.message || e));
}

// TASK-10218 — body-level chrome carrying the PREVIOUS account's content. Called from
// handleLogout, the signed-out auth branch and the uid-change branch of
// _runAuthStateChange. Synchronous and import-free on purpose: offline or with a
// missing chunk, sign-out still clears all of it. The next login re-creates what it needs.
// Left alone: #update-banner (app-global, new build available, not account content);
// #trial-reminder-banner / #partner-client-banner (dismissTrialBanner /
// removePartnerClientBanner already run at every caller).
// crm-tasks-banner (count), partner-grace-banner (billing), verification-reminder-banner
// (this account's email deadline; NOT via dismissVerificationBanner, which can re-show
// the trial bar), partner-sandbox-banner (tenant flag; applyPartnerSandboxUi recreates it).
function teardownAccountScopedChrome() {
  window.__ddCrmBannerGen = (window.__ddCrmBannerGen || 0) + 1; // drops an in-flight CRM check
  const b = document.body;
  if (document.getElementById('partner-grace-banner')) b.classList.remove('has-trial-banner');
  for (const id of ['crm-tasks-banner', 'partner-grace-banner', 'verification-reminder-banner', 'partner-sandbox-banner']) document.getElementById(id)?.remove();
  b.classList.remove('has-crm-banner', 'has-verification-banner');
  document.documentElement.style.removeProperty('--verification-banner-height');
  // Starters clear their own banner / orb / counts in stop(); start() re-arms on login.
  for (const k of ['Reminders', 'TeamNotes', 'ApprovalsBadge']) try { window[k]?.stop?.(); } catch (_) {}
}

// ============================================
// ONBOARDING CHECKLIST
// ============================================
// TASK-9513 — true only when the collection is PROVABLY empty; a read error means
// "don't show the hint" (a missing hint is harmless, a wrong one is the bug).
async function _tenantHasNoDocs(coll) {
  try {
    const t = window.AppState?.tenant;
    if (!t) return false;
    const snap = await getDocs(query(collection(db, `tenants/${t}/${coll}`), limit(1)));
    return snap.empty === true && !(snap.metadata && snap.metadata.fromCache); // an empty CACHE proves nothing
  } catch (_) { return false; }
}

async function injectOnboardingChecklist() {
  try {
    const t = window.AppState?.tenant;
    if (!t || t === 'demo') return;
    if (isFounderAccount() || isVipAccount()) return;
    if (window.AppState?.driverModePin || window.AppState?.driverMode) return;

    // Check account age (< 30 days)
    const createdAt = window.AppState.tenantData?.createdAt;
    if (createdAt) {
      const created = createdAt.toDate ? createdAt.toDate() : new Date(createdAt._seconds * 1000);
      const daysSince = (Date.now() - created.getTime()) / 86400000;
      if (daysSince > 30) return;
    }

    // Check if dismissed
    if (window.AppState.tenantData?.onboardingDismissed) return;

    // Check 4 collections in parallel
    const [peopleSnap, trucksSnap, jobsSnap, dvirSnap] = await Promise.all([
      getDocs(query(collection(db, `tenants/${t}/people`), limit(1))),
      getDocs(query(collection(db, `tenants/${t}/trucks`), limit(1))),
      getDocs(query(collection(db, `tenants/${t}/jobs`), limit(1))),
      getDocs(query(collection(db, `tenants/${t}/pretrips`), limit(1)))
    ]);

    const steps = [
      { done: !peopleSnap.empty, label: 'Add your first team member', icon: 'person_add', action: 'onboard-add-person' },
      { done: !trucksSnap.empty, label: 'Add a vehicle to your fleet', icon: 'local_shipping', action: 'onboard-add-vehicle' },
      { done: !jobsSnap.empty, label: 'Create your first job', icon: 'add_task', action: 'onboard-add-job' },
      { done: !dvirSnap.empty, label: 'Run an inspection', icon: 'fact_check', action: 'onboard-run-dvir' }
    ];

    // Register onboarding action handlers
    window._onboardAction = function(key) {
      if (key === 'onboard-add-person') { window.RosterModule?.showAddPerson(); }
      else if (key === 'onboard-add-vehicle') { document.querySelector('[data-tab="trucks"]')?.click(); setTimeout(() => window.RosterModule?.showAddTruck(), 300); }
      else if (key === 'onboard-add-job') { window.location.hash = '#/dispatch'; }
      else if (key === 'onboard-run-dvir') { window.location.hash = '#/pretrip'; }
    };

    const completed = steps.filter(s => s.done).length;
    if (completed === 4) return; // All done

    // Remove any existing checklist
    document.getElementById('onboarding-checklist')?.remove();

    const pct = Math.round((completed / 4) * 100);
    const card = document.createElement('div');
    card.id = 'onboarding-checklist';
    card.className = 'onboarding-checklist';
    card.innerHTML = `
      <div class="onboarding-header">
        <div>
          <h3 class="onboarding-title">Get started with DispatchDeck</h3>
          <p class="onboarding-subtitle">${completed}/4 steps complete — you're ${completed === 0 ? 'just getting started' : completed === 1 ? 'off to a great start' : completed === 2 ? 'halfway there' : 'almost done'}!</p>
        </div>
        <button class="btn-icon onboarding-dismiss" onclick="window.dismissOnboarding()" title="Dismiss">
          <span class="material-icons" style="color:var(--text-secondary);">close</span>
        </button>
      </div>
      <div class="onboarding-progress">
        <div class="onboarding-progress-fill" style="width:${pct}%"></div>
      </div>
      <div class="onboarding-steps">
        ${steps.map((s, i) => `
          <div class="onboarding-step ${s.done ? 'done' : ''}" data-step="${i}">
            <span class="material-icons onboarding-check">${s.done ? 'check_circle' : 'radio_button_unchecked'}</span>
            <span class="onboarding-step-label">${s.label}</span>
            ${!s.done ? `<button class="btn btn-sm btn-primary onboarding-go" onclick="window._onboardAction('${s.action}')">Go</button>` : '<span class="material-icons onboarding-done-icon">done</span>'}
          </div>
        `).join('')}
      </div>
      <div class="onboarding-footer">
        <button class="btn-link" onclick="window.dismissOnboarding()">I'll explore on my own</button>
      </div>
    `;

    // Prepend to main content
    const mainContent = document.getElementById('main-content');
    if (mainContent?.firstChild) {
      mainContent.insertBefore(card, mainContent.firstChild);
    }
  } catch (err) {
    console.warn('[Onboarding] Error loading checklist:', err);
  }
}

window.dismissOnboarding = async function() {
  document.getElementById('onboarding-checklist')?.remove();
  try {
    const t = window.AppState?.tenant;
    if (t) {
      await updateDoc(doc(db, 'tenants', t), { onboardingDismissed: true });
      if (window.AppState.tenantData) window.AppState.tenantData.onboardingDismissed = true;
    }
  } catch (err) {
    console.warn('[Onboarding] Error saving dismiss:', err);
  }
};

// ============================================
// TASK-9527 slice B -- QUICK SETUP AT FIRST LOGIN
// ============================================
// A brand-new company is asked the four Quick setup questions (otherwise only
// under Settings -> Features) ONCE, on the owner's first ordinary session. The
// prompt is views/features.js open({ mode: 'setup' }): every answer still goes
// through that page's confirm + the existing per-key writer (audited old->new),
// so nothing changes until an answer is confirmed, and "Done for now" / X just
// closes it. tenants/{tid}.quickSetupPromptedAt is stamped once the prompt is
// actually showing (not a setting; tenant-admin writable like
// onboardingDismissed), so it never shows twice -- the questions stay in
// Settings -> Features.
//
// Who: only the OWNER (tenants.ownerId == uid) -- setting up the company is the
// owner's call, and an invited member must not get a company-wide prompt.
// Cutoff: only companies CREATED ON/AFTER 2026-10-21 (CT) and under 30 days old.
// The answers are not stored (the switch IS the answer), so an existing tenant
// that never answered looks exactly like one that is happy with its switches --
// "never answered" cannot be told apart, so existing tenants are never asked.
// No createdAt = treated as old (never asked).
// TASK-9527-quick-setup-eligible BEGIN
const QUICK_SETUP_CUTOFF_MS = Date.UTC(2026, 9, 21, 5, 0, 0); // 2026-10-21 00:00 CDT
const QUICK_SETUP_MAX_AGE_MS = 30 * 86400000;
function _quickSetupTsMs(v) {
  if (!v) return NaN;
  if (typeof v.toMillis === 'function') return v.toMillis();
  if (typeof v.toDate === 'function') return v.toDate().getTime();
  if (v instanceof Date) return v.getTime();
  if (typeof v.seconds === 'number') return v.seconds * 1000;
  if (typeof v._seconds === 'number') return v._seconds * 1000;
  if (typeof v === 'number') return v;
  if (typeof v === 'string') return Date.parse(v);
  return NaN;
}
// Pure: st = AppState-like; env = { complianceOnly, partnerClient, nowMs,
// allowPending }. allowPending = "could become eligible this session": skips the
// two wait-for-the-other-first-login-step checks (used only to ARM the watcher;
// the prompt itself always needs the full check).
function quickSetupPromptEligible(st, env) {
  const s = st || {};
  const e = env || {};
  const td = s.tenantData || {};
  const t = s.tenant;
  if (!t || t === 'demo' || s.isDemo) return false;
  if (s.driverMode || s.driverModePin || s.isPublicAccess) return false;
  if (e.complianceOnly || e.partnerClient) return false;
  const uid = s.user && s.user.uid;
  if (!uid || !td.ownerId || uid !== td.ownerId) return false;
  if (td.quickSetupPromptedAt) return false;
  if (!e.allowPending) {
    if (td.ownerProfileSetup === false || s._profileSetupActive) return false; // profile setup first
    if (td.provisionedBy && td.onboardingCompleted !== true) return false;   // partner confirm first
  }
  const created = _quickSetupTsMs(td.createdAt);
  if (!Number.isFinite(created)) return false;
  const now = Number.isFinite(e.nowMs) ? e.nowMs : Date.now();
  if (created < QUICK_SETUP_CUTOFF_MS) return false;
  if (now - created > QUICK_SETUP_MAX_AGE_MS) return false;
  return true;
}

let _quickSetupPromptPending = false;
const QUICK_SETUP_POLL_MS = 2000;      // first ~5 minutes on a page
const QUICK_SETUP_POLL_SLOW_MS = 10000; // after that (e.g. a long profile form), until shown or ineligible
const QUICK_SETUP_FAST_TRIES = 150;
function _quickSetupEnv(extra) {
  return Object.assign({
    complianceOnly: (() => { try { return isComplianceOnlyTenant(); } catch (_) { return true; } })(),
    partnerClient: (() => { try { return isPartnerClientSession(); } catch (_) { return true; } })(),
    nowMs: Date.now(),
  }, extra || {});
}
// Anything on screen the prompt must not cover: any open modal (Settings ->
// Features included -- COD r2: never flip an owner's open Features page into
// the prompt), a trailguide onboarding hint, or the owner typing in a field.
// ownOpen = the post-open check, where the prompt's own modal is expected.
function _quickSetupBlocked(ownOpen) {
  const open = Array.from(document.querySelectorAll('.modal:not(.hidden)'))
    .filter((m) => !(ownOpen && m.id === 'features-modal'));
  if (open.length > 0 || document.querySelector('.onboarding-hint')) return true;
  const a = document.activeElement;
  const typing = !!(a && (/^(INPUT|TEXTAREA|SELECT)$/.test(a.tagName || '') || a.isContentEditable === true));
  if (!typing) return false;
  // Post-open (COD r3): typing inside the prompt itself is fine; anywhere else blocks.
  return !(ownOpen && typeof a.closest === 'function' && a.closest('#features-modal'));
}
// Called on every navigation (like maybeShowPartnerProvisionConfirm). Arms a
// watcher (one per session at a time): it opens only when the owner is fully
// eligible (profile setup + partner confirm finished -- those flip without a
// navigation) and nothing else is on screen; it keeps waiting otherwise and
// stops once stamped or no longer eligible. COD r1: eligibility + collisions are
// re-checked AFTER the lazy chunk loads; if anything changed it closes the
// prompt (only if the modal is still in setup mode) without the stamp.
function maybeShowQuickSetupPrompt() {
  try {
    if (_quickSetupPromptPending) return;
    if (!quickSetupPromptEligible(window.AppState, _quickSetupEnv({ allowPending: true }))) return;
    _quickSetupPromptPending = true;
    let tries = 0;
    const tick = async () => {
      let again = false;
      try {
        const st = window.AppState;
        const tenant = st && st.tenant;
        if (!quickSetupPromptEligible(st, _quickSetupEnv({ allowPending: true }))) return; // stamped / left / signed out
        if (!quickSetupPromptEligible(st, _quickSetupEnv()) || _quickSetupBlocked(false)) { again = true; return; }
        // onlyIfClosed (COD r3): if the owner's own Settings -> Features open
        // landed first (both wait on the same lazy chunk), open() returns false
        // and leaves their page alone -> wait. undefined = the chunk failed to
        // load (the stub already toasted) -> stop; the next navigation re-arms.
        const shown = await window.FeaturesPage?.open?.({ mode: 'setup', onlyIfClosed: true });
        if (shown === false) { again = true; return; }
        if (shown !== true) return;
        const fm = document.getElementById('features-modal');
        const ours = !!(fm && !fm.classList.contains('hidden') && fm.classList.contains('fp-mode-setup'));
        const now = window.AppState;
        if (!ours || now !== st || now.tenant !== tenant || !quickSetupPromptEligible(now, _quickSetupEnv()) || _quickSetupBlocked(true)) {
          if (ours) window.FeaturesPage?.close?.();
          again = true;
          return;
        }
        const stamp = Timestamp.now();
        if (st.tenantData) st.tenantData.quickSetupPromptedAt = stamp; // never re-show this session either
        await updateDoc(doc(db, 'tenants', tenant), { quickSetupPromptedAt: stamp, quickSetupPromptedBy: st.user.uid });
      } catch (err) {
        console.warn('[QuickSetup] prompt failed:', err?.message || err);
      } finally {
        if (again) setTimeout(tick, ++tries < QUICK_SETUP_FAST_TRIES ? QUICK_SETUP_POLL_MS : QUICK_SETUP_POLL_SLOW_MS);
        else _quickSetupPromptPending = false;
      }
    };
    setTimeout(tick, 1500);
  } catch (err) {
    _quickSetupPromptPending = false;
    console.warn('[QuickSetup] Error evaluating trigger:', err);
  }
}
// TASK-9527-quick-setup-eligible END

// ============================================
// PARTNER-PROVISIONED CARRIER — CONFIRM INFO (TASK-627)
// ============================================
// A compliance partner provisions a carrier via provisionComplianceTenant,
// pre-filling company-identity fields (name/dotNumber/phone/address/contact)
// and stamping provisionedBy. On the carrier's first authenticated session we
// surface that pre-filled info so they can review, edit, and confirm it.
// Gated on: provisionedBy set AND onboardingCompleted !== true AND not demo.
// Shown at most once per session (sessionStorage guard) and never while
// another modal is open (mirrors the onboarding-hint guards).
const PARTNER_CONFIRM_SESSION_KEY = '_partnerProvisionConfirmSeen';

function maybeShowPartnerProvisionConfirm() {
  try {
    const t = window.AppState?.tenant;
    const td = window.AppState?.tenantData;
    if (!t || t === 'demo') return;
    if (window.AppState?.driverModePin || window.AppState?.driverMode) return;
    if (!td?.provisionedBy) return;            // not partner-provisioned
    if (td?.onboardingCompleted === true) return; // already confirmed — never again
    // Once per session, and not while another modal is open.
    if (sessionStorage.getItem(PARTNER_CONFIRM_SESSION_KEY)) return;
    if (document.querySelector('.modal:not(.hidden)')) return;
    showPartnerProvisionConfirm();
  } catch (err) {
    console.warn('[PartnerConfirm] Error evaluating trigger:', err);
  }
}

function showPartnerProvisionConfirm() {
  const td = window.AppState?.tenantData || {};
  const partner = td.compliancePartner || 'your compliance partner';
  const companyName = td.companyName || td.name || '';
  const dotNumber = td.dotNumber || '';
  const address = td.address || '';
  const phone = td.phone || '';
  const contactName = td.ownerName || '';
  const contactEmail = td.ownerEmail || window.AppState?.user?.email || '';

  // Mark as seen for this session so dismiss/confirm both stop the re-pop.
  try { sessionStorage.setItem(PARTNER_CONFIRM_SESSION_KEY, '1'); } catch (e) {}

  let modal = document.getElementById('partner-confirm-modal');
  if (modal) modal.remove();
  modal = document.createElement('div');
  modal.id = 'partner-confirm-modal';
  modal.className = 'modal';
  modal.innerHTML = `
    <div class="modal-backdrop" onclick="window.dismissPartnerProvisionConfirm()"></div>
    <div class="modal-content" style="max-width:520px;">
      <div class="modal-header">
        <h3><span class="material-icons" style="color:var(--brand-cyan);vertical-align:middle;margin-right:8px;">verified_user</span>Confirm your company info</h3>
        <button class="btn-close" onclick="window.dismissPartnerProvisionConfirm()">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body" style="padding:var(--spacing-lg);">
        <p style="margin-bottom:16px;color:var(--text-secondary);">
          ${escapeHtml(partner)} set up your account with the info below. Please review it,
          make any corrections, and confirm.
        </p>
        <div class="form-group">
          <label>Company Name</label>
          <input type="text" id="partner-confirm-company" />
        </div>
        <div class="form-group">
          <label>USDOT #</label>
          <div id="partner-confirm-dot-display" style="padding:9px 12px;background:var(--bg-subtle,#f1f5f9);border:1px solid var(--border-light);border-radius:8px;color:var(--text-secondary);font-variant-numeric:tabular-nums;"></div>
          <div style="font-size:0.68rem;color:var(--text-secondary);margin-top:3px;">Set by ${escapeHtml(partner)} from FMCSA records — contact them if it's wrong (it anchors your compliance lookups).</div>
        </div>
        <div class="form-group">
          <label>Address</label>
          <input type="text" id="partner-confirm-address" placeholder="Street, City, State ZIP" />
        </div>
        <div class="form-group">
          <label>Phone</label>
          <input type="tel" id="partner-confirm-phone" />
        </div>
        <div class="form-group">
          <label>Contact Name</label>
          <input type="text" id="partner-confirm-contact-name" />
        </div>
        <div class="form-group">
          <label>Contact Email</label>
          <input type="email" id="partner-confirm-contact-email" />
        </div>
        <div style="display:flex;gap:12px;justify-content:flex-end;margin-top:20px;flex-wrap:wrap;">
          <button class="btn btn-secondary" onclick="window.dismissPartnerProvisionConfirm()">I'll do this later</button>
          <button class="btn btn-primary" id="partner-confirm-save" onclick="window.confirmPartnerProvisionInfo()">
            <span class="material-icons">check</span> Confirm &amp; Continue
          </button>
        </div>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
  // Set field values via DOM property (NOT value="${escapeHtml}") — escapeHtml
  // does not encode the double-quote, so a `"` in any tenant value (e.g. a
  // company name) would break out of the value attribute = attribute injection.
  // Property assignment is serialization-free and injection-proof. DOT is a
  // read-only display (textContent) — editing it would desync the FMCSA-stamped
  // fmcsaData against a different carrier (PSP/MVR/Clearinghouse on wrong subject).
  const _set = (id, v) => { const el = document.getElementById(id); if (el) el.value = v; };
  _set('partner-confirm-company', companyName);
  _set('partner-confirm-address', address);
  _set('partner-confirm-phone', phone);
  _set('partner-confirm-contact-name', contactName);
  _set('partner-confirm-contact-email', contactEmail);
  const dotEl = document.getElementById('partner-confirm-dot-display');
  if (dotEl) dotEl.textContent = dotNumber || '—';
}

// Dismiss = "I'll do this later". Session guard already set on open, so it won't
// re-pop this session, but onboardingCompleted is NOT set → it returns next login.
window.dismissPartnerProvisionConfirm = function() {
  document.getElementById('partner-confirm-modal')?.remove();
};

window.confirmPartnerProvisionInfo = async function() {
  const t = window.AppState?.tenant;
  if (!t || t === 'demo') { document.getElementById('partner-confirm-modal')?.remove(); return; }

  const companyName = document.getElementById('partner-confirm-company')?.value?.trim() || '';
  const address = document.getElementById('partner-confirm-address')?.value?.trim() || '';
  const phone = document.getElementById('partner-confirm-phone')?.value?.trim() || '';
  const contactName = document.getElementById('partner-confirm-contact-name')?.value?.trim() || '';
  const contactEmail = document.getElementById('partner-confirm-contact-email')?.value?.trim() || '';
  // dotNumber is intentionally NOT read/written here — it's a read-only display.
  // Editing it would desync the FMCSA-stamped fmcsaData (safety rating/crash/
  // insurance) against a different carrier identity. Partner changes it if wrong.

  if (!companyName) {
    showToast('Company name is required', 'error');
    return;
  }
  // Owner email feeds notifications + account recovery — block obvious typos
  // before sealing it (same lightweight check used on the application-notify field).
  if (contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail)) {
    showToast('Contact email looks invalid', 'error');
    document.getElementById('partner-confirm-contact-email')?.focus();
    return;
  }

  const saveBtn = document.getElementById('partner-confirm-save');
  const saveBtnOriginal = saveBtn ? saveBtn.innerHTML : '';
  if (saveBtn) {
    saveBtn.disabled = true;
    saveBtn.innerHTML = '<span class="btn-spinner"></span> Saving...';
  }

  // Write both name + companyName so every read path stays in sync (settings
  // save uses `name`; the provisioner stamps both). address is the canonical
  // single-string field the rest of the app reads. dotNumber omitted (read-only).
  const updates = {
    name: companyName,
    companyName: companyName,
    address: address || null,
    phone: phone || null,
    ownerName: contactName || null,
    ownerEmail: contactEmail || null,
    onboardingCompleted: true
  };

  try {
    await updateDoc(doc(db, 'tenants', t), updates);
    // Keep local state in sync so the modal never re-fires this session.
    if (window.AppState.tenantData) {
      Object.assign(window.AppState.tenantData, updates);
    }
    document.getElementById('partner-confirm-modal')?.remove();
    showToast('Company info confirmed', 'success');
  } catch (err) {
    console.warn('[PartnerConfirm] Error saving:', err);
    showToast('Could not save — please try again', 'error');
    if (saveBtn) { saveBtn.disabled = false; saveBtn.innerHTML = saveBtnOriginal; }
  }
};

// ============================================
// ONBOARDING SETUP WIZARD
// ============================================

/**
 * Check if the setup wizard should show on login.
 * Shows a popup with "Help me Finish Setup" / "Remind me Later" / "Never show again"
 * when the account is missing key setup data (people, vehicles, jobs, customers).
 */
async function checkSetupWizard() {
  // Setup wizard banner disabled — keeping onboarding hints instead
  return;
  try {
    const t = window.AppState?.tenant;
    if (!t || t === 'demo') return;
    if (isFounderAccount() || isVipAccount()) return;
    if (isComplianceOnlyTenant()) return;
    if (window.AppState?.driverModePin || window.AppState?.driverMode) return;

    const td = window.AppState.tenantData || {};

    // Never show again
    if (td.setupWizardDismissed) return;

    // Snoozed — check if snooze period passed
    if (td.setupWizardSnoozedUntil) {
      const snoozedUntil = td.setupWizardSnoozedUntil.toDate ? td.setupWizardSnoozedUntil.toDate() : new Date(td.setupWizardSnoozedUntil);
      if (snoozedUntil > new Date()) return;
    }

    // Check account age (< 60 days)
    const createdAt = td.createdAt;
    if (createdAt) {
      const created = createdAt.toDate ? createdAt.toDate() : new Date(createdAt._seconds * 1000);
      const daysSince = (Date.now() - created.getTime()) / 86400000;
      if (daysSince > 60) return;
    }

    // Check 4 collections in parallel (cheap limit(1) queries)
    const [peopleSnap, trucksSnap, jobsSnap, customersSnap] = await Promise.all([
      getDocs(query(collection(db, `tenants/${t}/people`), limit(1))),
      getDocs(query(collection(db, `tenants/${t}/trucks`), limit(1))),
      getDocs(query(collection(db, `tenants/${t}/jobs`), limit(1))),
      getDocs(query(collection(db, `tenants/${t}/customers`), limit(1)))
    ]);

    const hasPeople = !peopleSnap.empty;
    const hasTrucks = !trucksSnap.empty;
    const hasJobs = !jobsSnap.empty;
    const hasCustomers = !customersSnap.empty;

    // If all done, mark complete and bail
    const hasCrm = hasAddOnPackage('crm');
    const allDone = hasPeople && hasTrucks && hasJobs && (!hasCrm || hasCustomers);
    if (allDone) return;

    // Show the banner
    showSetupWizardBanner({ hasPeople, hasTrucks, hasJobs, hasCustomers, hasCrm });
  } catch (err) {
    console.warn('[SetupWizard] Error checking setup state:', err);
  }
}

function showSetupWizardBanner(setupState) {
  if (document.getElementById('setup-wizard-banner')) return;

  const completed = [setupState.hasPeople, setupState.hasTrucks, setupState.hasJobs,
    ...(setupState.hasCrm ? [setupState.hasCustomers] : [])].filter(Boolean).length;
  const total = setupState.hasCrm ? 4 : 3;

  const banner = document.createElement('div');
  banner.id = 'setup-wizard-banner';
  banner.className = 'setup-wizard-banner';
  banner.innerHTML = `
    <span class="material-icons">rocket_launch</span>
    <span><strong>${completed}/${total}</strong> setup steps complete — finish setting up your account</span>
    <button class="btn" onclick="window._launchSetupWizard()">Help me Finish Setup</button>
    <button class="btn" onclick="window._snoozeSetupWizard()" style="background:rgba(255,255,255,0.1);border-color:rgba(255,255,255,0.2);">Remind me Later</button>
    <label style="display:flex;align-items:center;gap:4px;font-size:11px;opacity:0.85;cursor:pointer;white-space:nowrap;">
      <input type="checkbox" id="setup-wizard-never-again" style="margin:0;"> Never show
    </label>
    <button class="btn-icon" onclick="window._snoozeSetupWizard()">
      <span class="material-icons">close</span>
    </button>
  `;
  document.body.appendChild(banner);
  document.body.classList.add('has-setup-banner');
}

window._launchSetupWizard = async function() {
  const banner = document.getElementById('setup-wizard-banner');
  if (banner) { banner.remove(); document.body.classList.remove('has-setup-banner'); }

  const t = window.AppState?.tenant;
  if (!t) return;

  // Re-check what's missing
  const td = window.AppState?.tenantData || {};
  const [peopleSnap, trucksSnap, jobsSnap, customersSnap] = await Promise.all([
    getDocs(query(collection(db, `tenants/${t}/people`), limit(1))),
    getDocs(query(collection(db, `tenants/${t}/trucks`), limit(1))),
    getDocs(query(collection(db, `tenants/${t}/jobs`), limit(1))),
    getDocs(query(collection(db, `tenants/${t}/customers`), limit(1)))
  ]);

  const hasCrm = hasAddOnPackage('crm');
  const hasCompanyProfile = !!(td.companyName || td.name);

  // Build all steps with done/pending status
  const allSteps = [
    { key: 'company', done: hasCompanyProfile },
    { key: 'person', done: !peopleSnap.empty },
    { key: 'vehicle', done: !trucksSnap.empty },
    { key: 'job', done: !jobsSnap.empty },
  ];
  if (hasCrm) allSteps.push({ key: 'customer', done: !customersSnap.empty });

  const pendingSteps = allSteps.filter(s => !s.done).map(s => s.key);
  if (pendingSteps.length === 0) {
    showToast('Setup already complete!', 'success');
    return;
  }

  showSetupWizardModal(allSteps, 0);
};

window._snoozeSetupWizard = async function() {
  const neverAgain = document.getElementById('setup-wizard-never-again')?.checked;
  const banner = document.getElementById('setup-wizard-banner');
  if (banner) { banner.remove(); document.body.classList.remove('has-setup-banner'); }

  const t = window.AppState?.tenant;
  if (!t) return;

  try {
    if (neverAgain) {
      await updateDoc(doc(db, 'tenants', t), { setupWizardDismissed: true });
      if (window.AppState.tenantData) window.AppState.tenantData.setupWizardDismissed = true;
    } else {
      // Snooze for 3 days
      const snoozeDate = new Date();
      snoozeDate.setDate(snoozeDate.getDate() + 3);
      await updateDoc(doc(db, 'tenants', t), { setupWizardSnoozedUntil: Timestamp.fromDate(snoozeDate) });
      if (window.AppState.tenantData) window.AppState.tenantData.setupWizardSnoozedUntil = Timestamp.fromDate(snoozeDate);
    }
  } catch (err) {
    console.warn('[SetupWizard] Error saving snooze/dismiss:', err);
  }
};

function showSetupWizardModal(allSteps, currentIndex) {
  document.getElementById('setup-wizard-modal')?.remove();

  // Auto-advance past completed steps
  while (currentIndex < allSteps.length && allSteps[currentIndex].done) {
    currentIndex++;
  }

  // All done
  if (currentIndex >= allSteps.length) {
    showSetupWizardComplete();
    return;
  }

  const stepObj = allSteps[currentIndex];
  const step = stepObj.key;
  const totalSteps = allSteps.length;
  const completedCount = allSteps.filter(s => s.done).length;
  const pct = Math.round((completedCount / totalSteps) * 100);

  const stepConfigs = {
    company: {
      icon: 'business_center',
      title: 'Company profile',
      subtitle: 'This shows on invoices, field tickets, and the driver app.',
      fields: `
        <div style="display:flex;flex-direction:column;gap:12px;">
          <div>
            <label style="font-weight:600;font-size:0.85rem;color:var(--text-primary);display:block;margin-bottom:4px;">Company Name *</label>
            <input type="text" id="wiz-company-name" class="form-control" placeholder="e.g. Smith Trucking LLC" style="padding:12px;font-size:1rem;">
          </div>
          <div>
            <label style="font-weight:600;font-size:0.85rem;color:var(--text-primary);display:block;margin-bottom:4px;">Phone <span style="color:var(--text-secondary);font-weight:400;">(optional)</span></label>
            <input type="tel" id="wiz-company-phone" class="form-control" placeholder="(555) 123-4567" style="padding:12px;font-size:1rem;">
          </div>
          <div>
            <label style="font-weight:600;font-size:0.85rem;color:var(--text-primary);display:block;margin-bottom:4px;">DOT Number <span style="color:var(--text-secondary);font-weight:400;">(optional)</span></label>
            <input type="text" id="wiz-company-dot" class="form-control" placeholder="e.g. 1234567" style="padding:12px;font-size:1rem;">
          </div>
        </div>
      `
    },
    person: {
      icon: 'person_add',
      title: 'Add your first team member',
      subtitle: 'Who works for you? Add a driver, operator, or crew member.',
      fields: `
        <div style="display:flex;flex-direction:column;gap:12px;">
          <div>
            <label style="font-weight:600;font-size:0.85rem;color:var(--text-primary);display:block;margin-bottom:4px;">Name *</label>
            <input type="text" id="wiz-person-name" class="form-control" placeholder="e.g. John Smith" style="padding:12px;font-size:1rem;">
          </div>
          <div>
            <label style="font-weight:600;font-size:0.85rem;color:var(--text-primary);display:block;margin-bottom:4px;">Role *</label>
            <select id="wiz-person-role" class="form-control" style="padding:12px;font-size:1rem;">
              <option value="">Select a role...</option>
              <option value="Driver">Driver</option>
              <option value="Operator">Operator</option>
              <option value="Laborer">Laborer</option>
              <option value="Mechanic">Mechanic</option>
              <option value="Supervisor">Supervisor</option>
              <option value="Dispatcher">Dispatcher</option>
              <option value="Office">Office</option>
            </select>
          </div>
          <div>
            <label style="font-weight:600;font-size:0.85rem;color:var(--text-primary);display:block;margin-bottom:4px;">Compliance Classification <span style="color:var(--text-secondary);font-weight:400;">(optional)</span></label>
            <select id="wiz-person-position-type" class="form-control" style="padding:12px;font-size:1rem;">
              <option value="">Not classified yet</option>
              <option value="cdl">CDL Driver</option>
              <option value="non_cdl_driver">Non-CDL Driver (box truck / hot-shot 10,001–26,000 lb — med card + driver file, no CDL)</option>
              <option value="drives_no_cmv">Drives, no CMV (pickup / car, 10,000 lb or less, no placarded hazmat, under 9 seats — no DOT driver file)</option>
              <option value="non_driver">Non-Driver (office/shop)</option>
            </select>
          </div>
          <div>
            <label style="font-weight:600;font-size:0.85rem;color:var(--text-primary);display:block;margin-bottom:4px;">Phone <span style="color:var(--text-secondary);font-weight:400;">(optional)</span></label>
            <input type="tel" id="wiz-person-phone" class="form-control" placeholder="(555) 123-4567" style="padding:12px;font-size:1rem;">
          </div>
        </div>
      `
    },
    vehicle: {
      icon: 'local_shipping',
      title: 'Add your first vehicle',
      subtitle: 'What does your fleet look like? Start with one truck or trailer.',
      fields: `
        <div style="display:flex;flex-direction:column;gap:12px;">
          <div>
            <label style="font-weight:600;font-size:0.85rem;color:var(--text-primary);display:block;margin-bottom:4px;">Unit Number *</label>
            <input type="text" id="wiz-vehicle-unit" class="form-control" placeholder="e.g. 101" style="padding:12px;font-size:1rem;">
          </div>
          <div>
            <label style="font-weight:600;font-size:0.85rem;color:var(--text-primary);display:block;margin-bottom:4px;">Type</label>
            <select id="wiz-vehicle-type" class="form-control" style="padding:12px;font-size:1rem;">
              <option value="Semi Truck">Semi Truck</option>
              <option value="Pickup Truck">Pickup Truck</option>
              <option value="Flatbed">Flatbed</option>
              <option value="Tanker">Tanker</option>
              <option value="Dump Truck">Dump Truck</option>
              <option value="Service Truck">Service Truck</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div style="display:flex;gap:12px;">
            <div style="flex:1;">
              <label style="font-weight:600;font-size:0.85rem;color:var(--text-primary);display:block;margin-bottom:4px;">Make <span style="color:var(--text-secondary);font-weight:400;">(optional)</span></label>
              <input type="text" id="wiz-vehicle-make" class="form-control" placeholder="e.g. Peterbilt" style="padding:12px;font-size:1rem;">
            </div>
            <div style="flex:1;">
              <label style="font-weight:600;font-size:0.85rem;color:var(--text-primary);display:block;margin-bottom:4px;">Year <span style="color:var(--text-secondary);font-weight:400;">(optional)</span></label>
              <input type="text" id="wiz-vehicle-year" class="form-control" placeholder="e.g. 2022" style="padding:12px;font-size:1rem;">
            </div>
          </div>
        </div>
      `
    },
    job: {
      icon: 'add_task',
      title: 'Create your first job',
      subtitle: 'Jobs are how you schedule and dispatch your crew.',
      fields: `
        <div style="display:flex;flex-direction:column;gap:12px;">
          <div>
            <label style="font-weight:600;font-size:0.85rem;color:var(--text-primary);display:block;margin-bottom:4px;">Job Name *</label>
            <input type="text" id="wiz-job-title" class="form-control" placeholder="e.g. Hauling - Site A" style="padding:12px;font-size:1rem;">
          </div>
          <div style="display:flex;gap:12px;">
            <div style="flex:1;">
              <label style="font-weight:600;font-size:0.85rem;color:var(--text-primary);display:block;margin-bottom:4px;">Start Date *</label>
              <input type="date" id="wiz-job-start" class="form-control" style="padding:12px;font-size:1rem;">
            </div>
            <div style="flex:1;">
              <label style="font-weight:600;font-size:0.85rem;color:var(--text-primary);display:block;margin-bottom:4px;">End Date *</label>
              <input type="date" id="wiz-job-end" class="form-control" style="padding:12px;font-size:1rem;">
            </div>
          </div>
        </div>
      `
    },
    customer: {
      icon: 'business',
      title: 'Add your first customer',
      subtitle: 'Who do you do work for? Add a customer to start building your pipeline.',
      fields: `
        <div style="display:flex;flex-direction:column;gap:12px;">
          <div>
            <label style="font-weight:600;font-size:0.85rem;color:var(--text-primary);display:block;margin-bottom:4px;">Company Name *</label>
            <input type="text" id="wiz-customer-name" class="form-control" placeholder="e.g. Acme Trucking" style="padding:12px;font-size:1rem;">
          </div>
          <div>
            <label style="font-weight:600;font-size:0.85rem;color:var(--text-primary);display:block;margin-bottom:4px;">Phone <span style="color:var(--text-secondary);font-weight:400;">(optional)</span></label>
            <input type="tel" id="wiz-customer-phone" class="form-control" placeholder="(555) 123-4567" style="padding:12px;font-size:1rem;">
          </div>
          <div>
            <label style="font-weight:600;font-size:0.85rem;color:var(--text-primary);display:block;margin-bottom:4px;">Email <span style="color:var(--text-secondary);font-weight:400;">(optional)</span></label>
            <input type="email" id="wiz-customer-email" class="form-control" placeholder="billing@acme.com" style="padding:12px;font-size:1rem;">
          </div>
        </div>
      `
    }
  };

  const config = stepConfigs[step];
  // Find next pending step index after current (to determine if this is the last pending)
  let nextPendingIdx = -1;
  for (let i = currentIndex + 1; i < allSteps.length; i++) {
    if (!allSteps[i].done) { nextPendingIdx = i; break; }
  }
  const isLast = nextPendingIdx === -1;

  // Build step dots showing completed vs current vs upcoming
  const stepDots = allSteps.map((s, i) => {
    if (s.done) return `<span class="material-icons" style="font-size:16px;color:#10B981;" title="${stepConfigs[s.key]?.title || s.key}">check_circle</span>`;
    if (i === currentIndex) return `<span style="width:10px;height:10px;border-radius:50%;background:var(--brand-blue);display:inline-block;" title="${stepConfigs[s.key]?.title || s.key}"></span>`;
    return `<span style="width:10px;height:10px;border-radius:50%;background:var(--border-light);display:inline-block;" title="${stepConfigs[s.key]?.title || s.key}"></span>`;
  }).join('');

  const modal = document.createElement('div');
  modal.id = 'setup-wizard-modal';
  modal.className = 'setup-wizard-overlay';
  modal.innerHTML = `
    <div class="setup-wizard-modal-card">
      <div class="setup-wizard-modal-header">
        <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:16px;">
          <div style="display:flex;align-items:center;gap:8px;">${stepDots}</div>
          <button class="btn-icon" onclick="window._closeSetupWizard()" title="Close" style="margin:-8px -8px 0 0;">
            <span class="material-icons" style="color:var(--text-secondary);">close</span>
          </button>
        </div>
        <div class="setup-wizard-progress">
          <div class="setup-wizard-progress-fill" style="width:${pct}%"></div>
        </div>
      </div>
      <div class="setup-wizard-modal-body">
        <div style="text-align:center;margin-bottom:20px;">
          <span class="material-icons" style="font-size:44px;color:var(--brand-blue);">${config.icon}</span>
          <h3 style="font-size:1.25rem;font-weight:700;color:var(--text-primary);margin:8px 0 4px;">${config.title}</h3>
          <p style="color:var(--text-secondary);font-size:0.9rem;margin:0;">${config.subtitle}</p>
        </div>
        ${config.fields}
      </div>
      <div class="setup-wizard-modal-footer">
        <button class="btn btn-secondary" onclick="window._wizardSkip()" style="padding:12px 20px;border-radius:8px;">
          Skip
        </button>
        <button class="btn btn-primary" id="wiz-next-btn" onclick="window._wizardNext()" style="padding:12px 28px;border-radius:8px;background:linear-gradient(135deg,var(--brand-blue),var(--accent-teal));border:none;font-weight:600;">
          ${isLast ? 'Finish' : 'Next'} <span class="material-icons" style="font-size:18px;vertical-align:middle;margin-left:4px;">${isLast ? 'check' : 'arrow_forward'}</span>
        </button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);

  // Set default dates for job step
  if (step === 'job') {
    // TASK-4739 (COD) — LOCAL calendar day. toISOString() is UTC, so after
    // ~7pm Central the default was already tomorrow.
    const localYmd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const today = localYmd(new Date());
    const nextWeek = localYmd(new Date(Date.now() + 7 * 86400000));
    const startInput = document.getElementById('wiz-job-start');
    const endInput = document.getElementById('wiz-job-end');
    if (startInput && !startInput.value) startInput.value = today;
    if (endInput && !endInput.value) endInput.value = nextWeek;
  }

  // Store state for navigation
  window._wizardState = { allSteps, currentIndex };

  // Focus first input
  setTimeout(() => {
    const firstInput = modal.querySelector('input:not([type="hidden"]), select');
    firstInput?.focus();
  }, 100);
}

window._wizardSkip = function() {
  const state = window._wizardState;
  if (!state) return;
  showSetupWizardModal(state.allSteps, state.currentIndex + 1);
};

// TASK-4739 — one Date per calendar day from startYmd..endYmd inclusive, each
// at 12:00 LOCAL. setDate(+1) keeps the wall-clock hour across a DST change, so
// every day stays at noon. Pure — tests/task4739-wizard-jobday-noon.unit.cjs
// lifts this body out of the source and runs it under several TZs.
function wizardJobDayDates(startYmd, endYmd) {
  const out = [];
  const endDt = new Date(endYmd + 'T12:00:00');
  for (let d = new Date(startYmd + 'T12:00:00'); d <= endDt; d.setDate(d.getDate() + 1)) {
    out.push(new Date(d));
  }
  return out;
}

window._wizardNext = async function() {
  const state = window._wizardState;
  if (!state) return;

  const step = state.allSteps[state.currentIndex].key;
  const t = window.AppState?.tenant;
  if (!t) return;

  const btn = document.getElementById('wiz-next-btn');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Saving...'; }

  try {
    let saved = false;

    if (step === 'company') {
      const name = document.getElementById('wiz-company-name')?.value?.trim();
      const phone = document.getElementById('wiz-company-phone')?.value?.trim() || '';
      const dotNumber = document.getElementById('wiz-company-dot')?.value?.trim() || '';
      if (!name) { showToast('Company name is required', 'error'); restoreWizBtn(btn, state); return; }

      const updates = { companyName: name, name: name };
      if (phone) updates.companyPhone = phone;
      if (dotNumber) updates.dotNumber = dotNumber;
      updates.updatedAt = Timestamp.now();
      await updateDoc(doc(db, 'tenants', t), updates);
      if (window.AppState.tenantData) {
        Object.assign(window.AppState.tenantData, updates);
      }
      saved = true;

    } else if (step === 'person') {
      const name = document.getElementById('wiz-person-name')?.value?.trim();
      const role = document.getElementById('wiz-person-role')?.value;
      const phone = document.getElementById('wiz-person-phone')?.value?.trim();
      if (!name) { showToast('Name is required', 'error'); restoreWizBtn(btn, state); return; }
      if (!role) { showToast('Please select a role', 'error'); restoreWizBtn(btn, state); return; }

      const cdlRoles = ['Driver', 'Operator'];
      const personRef = await addDoc(collection(db, `tenants/${t}/people`), {
        name, role, roles: [role], crewType: role,
        phone: phone || '',
        cdlLicensed: cdlRoles.includes(role),
        active: true, showInDispatch: true,
        notifications: { tags: [], preferredChannel: 'push', sms: false, muted: false },
        createdAt: Timestamp.now(), updatedAt: Timestamp.now()
      });
      // TASK-1122 — stamp the classification chosen in the wizard. positionType
      // is SERVER-OWNED (TASK-934 — firestore.rules would deny the addDoc above
      // if it carried the field), so it goes through the setDriverClassification
      // callable. Non-fatal: the person is created either way; on failure the
      // TASK-1087 classify_person prompt stays open as the safety net.
      const wizPositionType = document.getElementById('wiz-person-position-type')?.value || '';
      if (['cdl', 'non_cdl_driver', 'drives_no_cmv', 'non_driver'].includes(wizPositionType) && t !== 'demo') { // TASK-9696
        try {
          // TASK-9806 — "Drives, no CMV" asks the placarded-hazmat / passenger checks first.
          const _pick9806 = window.DqMatrix?.confirmClassificationPick
            ? await window.DqMatrix.confirmClassificationPick(wizPositionType, { name })
            : { positionType: wizPositionType, cmvChecks: null };
          if (_pick9806) {
            const setPt = httpsCallable(functions, 'setDriverClassification');
            await setPt({ tenantId: t, personId: personRef.id, positionType: _pick9806.positionType, cmvChecks: _pick9806.cmvChecks, reason: 'Set in the setup wizard' });
          }
        } catch (ptErr) {
          console.warn('[Wizard] classification stamp failed (non-blocking):', ptErr);
        }
      }
      saved = true;

    } else if (step === 'vehicle') {
      const unit = document.getElementById('wiz-vehicle-unit')?.value?.trim();
      const type = document.getElementById('wiz-vehicle-type')?.value || 'Semi Truck';
      const make = document.getElementById('wiz-vehicle-make')?.value?.trim() || '';
      const year = document.getElementById('wiz-vehicle-year')?.value?.trim() || '';
      if (!unit) { showToast('Unit number is required', 'error'); restoreWizBtn(btn, state); return; }

      await addDoc(collection(db, `tenants/${t}/trucks`), {
        unit, type, make, year,
        active: true, showInDispatch: true,
        createdAt: Timestamp.now(), updatedAt: Timestamp.now()
      });
      saved = true;

    } else if (step === 'job') {
      const title = document.getElementById('wiz-job-title')?.value?.trim();
      const startDate = document.getElementById('wiz-job-start')?.value;
      const endDate = document.getElementById('wiz-job-end')?.value;
      if (!title) { showToast('Job name is required', 'error'); restoreWizBtn(btn, state); return; }
      if (!startDate || !endDate) { showToast('Start and end dates are required', 'error'); restoreWizBtn(btn, state); return; }
      if (endDate < startDate) { showToast('End date must be after start date', 'error'); restoreWizBtn(btn, state); return; }

      // TASK-4739 — noon local, the same anchor saveJob writes. The old
      // T00:00:00 / T23:59:59 pair differed from every other job on the board.
      const start = Timestamp.fromDate(new Date(startDate + 'T12:00:00'));
      const end = Timestamp.fromDate(new Date(endDate + 'T12:00:00'));

      // TASK-4781 slice 2 — allocate the human job number BEFORE the write.
      // allocateJobNumberSafe never throws: on a failure it returns
      // { jobNumberPending: true } so the hole is QUERYABLE rather than invisible,
      // mirroring ticketNoPending. window.JobOps per Pitfall #1 — never a
      // cross-import with a ?v= buster.
      const _jn = await window.JobOps.allocateJobNumberSafe(t);
      const jobRef = await addDoc(collection(db, `tenants/${t}/jobs`), {
        ...(_jn.jobNumberPending ? { jobNumberPending: true } : { jobNumber: _jn.jobNumber }),
        title, type: 'job', status: 'active',
        startDate: start, endDate: end, allDay: true,
        // TASK-1333 — REQUIRED by the jobs create rule (must equal request.auth.uid).
        // This is the very FIRST job a brand-new tenant ever creates, so an
        // unstamped write here would have broken onboarding at step one.
        createdBy: auth.currentUser?.uid || window.AppState?.user?.uid || null,
        createdByName: window.AppState?.user?.displayName || window.AppState?.user?.email || 'Unknown',
        createdAt: Timestamp.now(), updatedAt: Timestamp.now()
      });

      // Create jobDay docs for each calendar day.
      // TASK-4739 — this used to write `date` as a 'YYYY-MM-DD' STRING (from a
      // local-midnight Date run through toISOString, so a day early east of
      // UTC). Readers turn a string into UTC midnight — one day EARLY on the
      // driver board in every US zone — and the Timestamp range queries never
      // see it at all. Write a noon-local Timestamp like every other writer, and
      // the drivers/helpers field names the board reads (not `crew`).
      const dayPromises = [];
      for (const d of wizardJobDayDates(startDate, endDate)) {
        dayPromises.push(addDoc(collection(db, `tenants/${t}/jobDays`), {
          jobId: jobRef.id, date: Timestamp.fromDate(d), title,
          drivers: [], helpers: [], trucks: [], trailers: [], assets: [],
          status: 'scheduled',
          createdAt: Timestamp.now(), updatedAt: Timestamp.now()
        }));
      }
      await Promise.all(dayPromises);
      saved = true;

    } else if (step === 'customer') {
      const name = document.getElementById('wiz-customer-name')?.value?.trim();
      const phone = document.getElementById('wiz-customer-phone')?.value?.trim() || '';
      const email = document.getElementById('wiz-customer-email')?.value?.trim() || '';
      if (!name) { showToast('Company name is required', 'error'); restoreWizBtn(btn, state); return; }

      await addDoc(collection(db, `tenants/${t}/customers`), {
        name, type: 'customer', status: 'Active',
        phone, phones: phone ? [{ label: 'Main', phone }] : [],
        email, emails: email ? [{ label: 'General', email }] : [],
        address: '', addresses: [],
        crmManaged: true, color: '',
        createdAt: Timestamp.now(), updatedAt: Timestamp.now()
      });
      saved = true;
    }

    if (saved) {
      // Mark step as done in state
      state.allSteps[state.currentIndex].done = true;
      showToast(`${step.charAt(0).toUpperCase() + step.slice(1)} added!`, 'success');
    }

    // Move to next step (auto-advances past completed)
    showSetupWizardModal(state.allSteps, state.currentIndex + 1);
  } catch (err) {
    console.error('[SetupWizard] Error saving:', err);
    showToast('Error saving. Please try again.', 'error');
    restoreWizBtn(btn, state);
  }
};

function restoreWizBtn(btn, state) {
  if (!btn) return;
  btn.disabled = false;
  // Check if there's another pending step after current
  let hasMore = false;
  for (let i = state.currentIndex + 1; i < state.allSteps.length; i++) {
    if (!state.allSteps[i].done) { hasMore = true; break; }
  }
  btn.innerHTML = `${hasMore ? 'Next' : 'Finish'} <span class="material-icons" style="font-size:18px;vertical-align:middle;margin-left:4px;">${hasMore ? 'arrow_forward' : 'check'}</span>`;
}

function showSetupWizardComplete() {
  document.getElementById('setup-wizard-modal')?.remove();

  const hasCrm = hasAddOnPackage('crm');
  const hasClock = hasAddOnPackage('clockbilling');
  const hasHr = hasAddOnPackage('hr');

  // Build "what's next" cards based on tier
  const nextSteps = [
    { icon: 'fact_check', title: 'Run an Inspection', desc: 'Start a DVIR for one of your vehicles', action: "window.location.hash='#/pretrip'" },
    { icon: 'build', title: 'Set up Maintenance', desc: 'Track PMs, work orders, and fleet health', action: "window.location.hash='#/maintenance'" },
    { icon: 'notifications_active', title: 'Configure Notifications', desc: 'Set up SMS and push alerts for your team', action: "window.location.hash='#/roster'" },
    { icon: 'upload_file', title: 'Import from Spreadsheet', desc: 'Bulk import people, vehicles, or jobs', action: "window.location.hash='#/roster'" },
  ];
  if (hasClock) {
    nextSteps.push({ icon: 'receipt_long', title: 'Set up Billing', desc: 'Rates, price books, and field tickets', action: "window.location.hash='#/billing'" });
  }
  if (hasCrm) {
    nextSteps.push({ icon: 'support_agent', title: 'Try Deal Desk', desc: 'Your AI sales assistant in CRM', action: "window.location.hash='#/crm'" });
  }
  if (hasHr) {
    nextSteps.push({ icon: 'badge', title: 'Set up HR', desc: 'Hiring pipeline, reviews, and compliance', action: "window.location.hash='#/hr'" });
  }

  const overlay = document.createElement('div');
  overlay.id = 'setup-wizard-complete';
  overlay.className = 'setup-wizard-overlay';
  overlay.innerHTML = `
    <div class="setup-wizard-modal-card" style="max-width:520px;">
      <div style="text-align:center;padding:32px 32px 16px;">
        <span class="material-icons" style="font-size:56px;color:#10B981;margin-bottom:12px;">check_circle</span>
        <h2 style="font-size:1.5rem;font-weight:700;color:var(--text-primary);margin:0 0 6px;">You're all set!</h2>
        <p style="color:var(--text-secondary);font-size:0.9rem;margin:0;">The basics are in place. Here are some things to explore next.</p>
      </div>
      <div style="padding:0 24px 8px;">
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;">
          ${nextSteps.map(s => `
            <button onclick="${s.action};document.getElementById('setup-wizard-complete')?.remove()" class="setup-wizard-next-card">
              <span class="material-icons" style="font-size:28px;color:var(--brand-blue);margin-bottom:6px;">${s.icon}</span>
              <span style="font-weight:600;font-size:0.85rem;color:var(--text-primary);line-height:1.3;">${s.title}</span>
              <span style="font-size:0.75rem;color:var(--text-secondary);line-height:1.3;margin-top:2px;">${s.desc}</span>
            </button>
          `).join('')}
        </div>
      </div>
      <div style="padding:16px 24px 24px;text-align:center;">
        <button class="btn btn-primary" onclick="document.getElementById('setup-wizard-complete')?.remove()" style="padding:12px 32px;font-size:0.95rem;font-weight:600;border-radius:10px;background:linear-gradient(135deg,var(--brand-blue),var(--accent-teal));border:none;">
          I'll explore on my own
        </button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  // Also dismiss the inline onboarding checklist
  document.getElementById('onboarding-checklist')?.remove();
  const t = window.AppState?.tenant;
  if (t) {
    updateDoc(doc(db, 'tenants', t), { onboardingDismissed: true }).catch(e => console.warn('[Onboarding] dismiss failed:', e.message));
    if (window.AppState.tenantData) window.AppState.tenantData.onboardingDismissed = true;
  }
}

window._closeSetupWizard = function() {
  document.getElementById('setup-wizard-modal')?.remove();
};

/**
 * Show trial expired screen (blocks app access)
 */
function showTrialExpiredScreen() {
  const screen = document.getElementById('trial-expired-screen');
  if (screen) {
    screen.classList.remove('hidden');
    screen.style.display = 'flex';
  }
  syncPlanIntervalUi(); // TASK-9812

  // Hide main content
  document.getElementById('main-content')?.classList.add('hidden');
  document.getElementById('bottom-nav')?.classList.add('hidden');
  document.getElementById('sidebar')?.classList.add('hidden');
}

function hideTrialExpiredScreen() {
  const screen = document.getElementById('trial-expired-screen');
  if (screen) {
    screen.classList.add('hidden');
    screen.style.display = 'none';
  }
}

/**
 * Start subscription - redirect to Stripe checkout
 */
async function startSubscription(clickedBtn) {
  // TASK-9432 — Apple 3.1.1, same guard as startCheckout.
  if (window.isIOSApp) {
    showToast('Manage your subscription at dispatchdeck.app', 'info');
    return;
  }
  // Find the button - either passed in, or by known IDs
  const btn = clickedBtn || document.getElementById('btn-start-subscription') || document.getElementById('btn-banner-subscribe');
  if (btn) {
    btn.disabled = true;
    btn.innerHTML = '<span class="btn-spinner"></span> Redirecting...';
  }

  try {
    const tenantId = window.AppState?.tenant;
    if (!tenantId) {
      showToast('Please sign in first.', 'error');
      return;
    }

    // Show loading overlay
    let overlay = document.getElementById('checkout-loading-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'checkout-loading-overlay';
      overlay.innerHTML = `
        <div style="display:flex;flex-direction:column;align-items:center;gap:16px;">
          <div class="btn-spinner" style="width:36px;height:36px;border-width:3px;"></div>
          <div style="font-size:1rem;font-weight:600;color:var(--text-primary);">Opening Stripe Checkout...</div>
          <div style="font-size:0.8125rem;color:var(--text-secondary);">You'll be redirected in a moment</div>
        </div>`;
      overlay.style.cssText = 'position:fixed;inset:0;z-index:9999;background:rgba(255,255,255,0.92);display:flex;align-items:center;justify-content:center;';
      document.body.appendChild(overlay);
    }
    overlay.style.display = 'flex';

    // Call the createCheckoutSession cloud function
    const createCheckout = httpsCallable(functions, 'createCheckoutSession');
    const result = await createCheckout({
      tenantId: tenantId,
      plan: selectedBasePlan(), // TASK-9812 — honours the Monthly/Annual toggle
      successUrl: window.location.origin + '/?checkout=success&plan=pro_plus',
      cancelUrl: window.location.origin + '/?checkout=cancelled'
    });

    if (result.data?.url) {
      window.location.href = result.data.url;
    } else {
      throw new Error('No checkout URL returned');
    }
  } catch (error) {
    console.error('[Subscription] Error creating checkout:', error);
    const overlay = document.getElementById('checkout-loading-overlay');
    if (overlay) overlay.style.display = 'none';
    // TASK-9432 — a past_due owner lands on the trial wall, whose only button
    // is this one; the server now refuses a second subscription. Send them to
    // the billing portal to fix the card instead of a dead "try again".
    if (_handleBaseCheckoutRefusal(error)) {
      // handled
    } else {
      showToast('Error starting checkout: ' + (error.message || 'Please try again.'), 'error');
    }

    if (btn) {
      btn.disabled = false;
      if (btn.id === 'btn-banner-subscribe') {
        btn.innerHTML = 'Subscribe Now';
      } else {
        btn.innerHTML = '<span class="material-icons" style="font-size:18px;margin-right:4px;">credit_card</span> Subscribe Now';
      }
    }
  }
}
window.startSubscription = startSubscription;

// ============================================
// ACCESS PIN SYSTEM
// ============================================
// New PIN system: No PIN = basic access (Clock, DVIR only)
// PIN unlocks additional modules based on settings.accessPins configuration
// Device remembers PIN for 30 days via localStorage

const PIN_EXPIRY_DAYS = 30;

// TASK-9858 — WHERE THE PINS LIVE. The digits used to sit in settings.accessPins
// on the tenant doc, which every member, every driver link and every PIN session
// reads. Now:
//   tenants/{tid}/private/accessPins  { pins: { <id>: '<digits>' } }  owner only
//   settings.accessPinMeta            { <id>: { label, modules, isPrimary } }
// The kiosk unlock sends the typed PIN to verifyAccessPin and stores the returned
// id (blob `v: 2`). A tenant not migrated yet still has the old map keyed by the
// digits; accessPinMap() falls back to it, so every gate below (zero-PIN
// bootstrap, isPrimary, a stored key's modules) reads one shape either way.
// The PIN screen migrates a tenant on its first save; scripts/migrate-access-pins.cjs
// does the rest. Views read it through window.accessPinMap (never import it).
const _isPinMap = (v) => !!v && typeof v === 'object' && !Array.isArray(v);
function accessPinMap(settings = window.AppState?.settings) {
  const s = settings || {};
  if (_isPinMap(s.accessPinMeta) && Object.keys(s.accessPinMeta).length) return s.accessPinMeta;
  return _isPinMap(s.accessPins) ? s.accessPins : {};
}
window.accessPinMap = accessPinMap;

// The PIN screen's own view (owner only): digits -> { ...config, _id }. Built from
// the tenant settings + the private doc (legacy: the old map, no _id). Kept in this
// module, never on window, and only while it matches the live meta.
const _pinDocRef = (tenantId) => doc(db, 'tenants', tenantId, 'private', 'accessPins');
function _pinsFullFrom(settings, priv) {
  const s = settings || {};
  const out = {};
  if (_isPinMap(s.accessPinMeta) && Object.keys(s.accessPinMeta).length) {
    const pins = _isPinMap(priv?.pins) ? priv.pins : {};
    for (const [id, cfg] of Object.entries(s.accessPinMeta)) {
      if (typeof pins[id] === 'string' && pins[id] && _isPinMap(cfg)) out[pins[id]] = { ...cfg, _id: id };
    }
  } else if (_isPinMap(s.accessPins)) {
    for (const [pin, cfg] of Object.entries(s.accessPins)) if (_isPinMap(cfg)) out[pin] = { ...cfg };
  }
  return out;
}
let _pinsFull = null;
let _pinsFullSig = null;
function accessPinsFull() {
  const s = window.AppState?.settings || {};
  if (accessPinMap(s) !== s.accessPinMeta) return _pinsFullFrom(s, null); // legacy / none: synchronous
  return (_pinsFull && _pinsFullSig === JSON.stringify(s.accessPinMeta)) ? _pinsFull : null;
}
async function loadAccessPinsFull() {
  const s = window.AppState?.settings || {};
  const sig = JSON.stringify(s.accessPinMeta || null);
  const p = await getDoc(_pinDocRef(window.AppState.tenant));
  _pinsFull = _pinsFullFrom(s, p.exists() ? p.data() : null);
  _pinsFullSig = sig;
  return _pinsFull;
}
function _newPinId() {
  return 'k' + Array.from(crypto.getRandomValues(new Uint8Array(12)), (b) => (b % 36).toString(36)).join('');
}
// Inside a transaction that has already read the tenant doc: write the WHOLE PIN
// set back — digits to the private doc, everything else to settings.accessPinMeta.
// Whole maps are safe here only because the transaction re-runs if either doc moved.
//
// 🔴 settings.accessPins is NOT deleted (verifier, 2026-10-14): an app tab still
// running the OLD bundle reads only that map, and an empty map is its zero-PIN
// bootstrap = every module open. So it is REPLACED by a decoy keyed by the PIN
// ids with NO modules: old code then sees "PINs configured", finds no stored
// code in it, and grants nothing (tests/task9858-old-bundle-fails-closed.unit.cjs).
// New code never reads it while accessPinMeta is non-empty.
function _pinDecoy(ids) {
  const d = {};
  for (const id of ids) d[id] = { modules: [], label: 'moved', isPrimary: false, moved: true };
  return d;
}
function _writePinsFull(tx, tenantRef, tenantExists, full) {
  const meta = {}, pins = {}, idByPin = {};
  for (const [pin, cfg] of Object.entries(full)) {
    const { _id, ...rest } = cfg;
    const id = _id || _newPinId();
    meta[id] = rest; pins[id] = pin; idByPin[pin] = id;
  }
  const decoy = _pinDecoy(Object.keys(meta));
  tx.set(_pinDocRef(tenantRef.id), { pins });
  // COD r2 — the old member-readable copy in settings/general goes in the same commit.
  tx.set(doc(db, 'tenants', tenantRef.id, 'settings', 'general'), { accessPins: deleteField() }, { merge: true });
  // accessPinRev: every PIN-set write moves it, so offline hash caches retire.
  const rev = Date.now();
  if (tenantExists) {
    tx.update(tenantRef, new FieldPath('settings', 'accessPinMeta'), meta, new FieldPath('settings', 'accessPins'), decoy,
      new FieldPath('settings', 'accessPinRev'), rev);
  } else {
    tx.set(tenantRef, { settings: { accessPinMeta: meta, accessPins: decoy, accessPinRev: rev } }, { merge: true });
  }
  return { meta, idByPin };
}
// After a PIN-screen write: this device's stored blob may still hold digits (a
// legacy tenant just migrated) or the code that was renamed. Re-key it to the id.
function _applyPinsWrite(w, blob, fromPin, toPin, fromId) {
  const s = window.AppState.settings || {};
  window.AppState.settings = { ...s, accessPinMeta: w.meta, accessPins: _pinDecoy(Object.keys(w.meta)) };
  if (window.AppState.tenantData) window.AppState.tenantData.settings = window.AppState.settings;
  const full = {};
  for (const [pin, id] of Object.entries(w.idByPin)) full[pin] = { ...w.meta[id], _id: id };
  _pinsFull = full; _pinsFullSig = JSON.stringify(w.meta);
  if (blob && blob.v !== 2 && blob.pin) {
    const id = w.idByPin[(fromPin && blob.pin === fromPin) ? toPin : blob.pin];
    try {
      const key = `dispatchdeck_pin_${window.AppState?.tenant}`;
      if (id) localStorage.setItem(key, JSON.stringify({ ...blob, pin: id, v: 2 }));
    } catch (_) { /* storage refused — the holder is re-challenged, which is safe */ }
  }
  // A rename gives the PIN a NEW id (every other device unlocked with the old
  // code is locked out); the holder who typed the new code here follows it.
  if (blob && blob.v === 2 && fromId && blob.pin === fromId && w.idByPin[toPin]) {
    try {
      localStorage.setItem(`dispatchdeck_pin_${window.AppState?.tenant}`, JSON.stringify({ ...blob, pin: w.idByPin[toPin] }));
    } catch (_) { /* re-challenged, which is safe */ }
  }
}
// Pure: the PIN set after a save. 🔴 A RENAME drops the id (verifier 2026-10-14):
// devices store the id, so keeping it would leave every tablet unlocked with the
// OLD digits still unlocked. New id = those devices fail their lookup and lock.
function _nextPinSet(accessPins, originalPin, pin, pinConfig) {
  const next = { ...accessPins };
  const prev = originalPin ? accessPins[originalPin] : null;
  if (originalPin) delete next[originalPin];
  next[pin] = { ...(prev || {}), ...pinConfig };
  if (originalPin && originalPin !== pin) delete next[pin]._id;
  return next;
}
// ---------------------------------------------------------------------------
// TASK-9858 (dispatcher) — OFFLINE UNLOCK. A yard tablet with no signal must
// still unlock at shift start. After an ONLINE verify the device keeps a slow
// salted hash (PBKDF2-SHA-256, per-tenant salt + iteration count from the
// server) of THAT PIN with its id — never the PIN. Offline, a typed PIN is
// checked against those hashes only, 5 wrong tries per 15 min on this device.
// The cache is per tenant, dropped on logout / another tenant, and cleared
// when settings.accessPinRev (bumped on every PIN-set write) moves.
// Honest limit: a 4–6 digit PIN under any hash can be brute-forced by whoever
// copies this device's storage; the cache only holds PINs this device has
// already unlocked with, and the rev bump retires it on the next PIN change.
const _pinHashKey = (t) => `dispatchdeck_pinhash_${t}`;
const _pinOfflineFailKey = (t) => `dispatchdeck_pinoffline_${t}`;
const _PIN_OFFLINE_FAILS = 5;
const _PIN_OFFLINE_WINDOW_MS = 15 * 60 * 1000;
async function _pinHash(pin, saltHex, iter) {
  const enc = new TextEncoder();
  const salt = new Uint8Array((saltHex.match(/../g) || []).map((h) => parseInt(h, 16)));
  const key = await crypto.subtle.importKey('raw', enc.encode(String(pin)), 'PBKDF2', false, ['deriveBits']);
  const bits = await crypto.subtle.deriveBits({ name: 'PBKDF2', hash: 'SHA-256', salt, iterations: iter }, key, 256);
  return Array.from(new Uint8Array(bits), (b) => b.toString(16).padStart(2, '0')).join('');
}
function _readPinCache(t) {
  try { return JSON.parse(localStorage.getItem(_pinHashKey(t)) || 'null'); } catch (_) { return null; }
}
const _curPinRev = () => Number(window.AppState?.settings?.accessPinRev) || 0;
async function _rememberPinOffline(t, key, pin, off) {
  if (!t || !key || !off || typeof off.salt !== 'string' || !(off.iter > 0)) return;
  const h = await _pinHash(pin, off.salt, off.iter);
  // COD r3 — re-check AFTER the slow hash: a logout, tenant switch or PIN-set
  // change while it ran must not resurrect a cache it just cleared.
  if (window.AppState?.tenant !== t || _curPinRev() !== off.ver) return;
  let c = _readPinCache(t);
  if (!c || c.salt !== off.salt || c.iter !== off.iter || c.ver !== off.ver) c = { salt: off.salt, iter: off.iter, ver: off.ver, entries: {} };
  c.entries[key] = h;
  try { localStorage.setItem(_pinHashKey(t), JSON.stringify(c)); } catch (_) { /* no cache: online only */ }
}
// -> { key, config } | null. Throws { _offlineWait: seconds } when over the local limit.
async function _verifyPinOffline(t, pin) {
  const now = Date.now();
  let f = null;
  try { f = JSON.parse(localStorage.getItem(_pinOfflineFailKey(t)) || 'null'); } catch (_) { f = null; }
  if (!f || now - f.start > _PIN_OFFLINE_WINDOW_MS) f = { start: now, n: 0 };
  if (f.n >= _PIN_OFFLINE_FAILS) throw { _offlineWait: Math.ceil((f.start + _PIN_OFFLINE_WINDOW_MS - now) / 1000) };
  // COD r3 — spend the try BEFORE hashing (a success gives it back), so two
  // overlapping attempts can not both read the same count.
  f.n++;
  try { localStorage.setItem(_pinOfflineFailKey(t), JSON.stringify(f)); } catch (_) {}
  const c = _readPinCache(t);
  const map = accessPinMap();
  // A cache made under another PIN set (e.g. a renamed PIN that kept its id) is dead.
  if (c && c.entries && typeof c.salt === 'string' && c.ver === _curPinRev()) {
    const h = await _pinHash(pin, c.salt, c.iter);
    for (const [key, eh] of Object.entries(c.entries)) {
      if (eh === h && Object.prototype.hasOwnProperty.call(map, key)) { try { localStorage.removeItem(_pinOfflineFailKey(t)); } catch (_) {} return { key, config: map[key] }; }
    }
  }
  return null;
}
// Logout / tenant switch: drop every other tenant's cache (or all of them).
function _clearPinCaches(keepTenant) {
  try {
    const doomed = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && (k.startsWith('dispatchdeck_pinhash_') || k.startsWith('dispatchdeck_pinoffline_'))
          && !(keepTenant && (k === _pinHashKey(keepTenant) || k === _pinOfflineFailKey(keepTenant)))) doomed.push(k);
    }
    doomed.forEach((k) => localStorage.removeItem(k));
  } catch (_) { /* storage unavailable */ }
}
window._clearPinCaches = _clearPinCaches;
const _isOfflineErr = (e) => (typeof navigator !== 'undefined' && navigator.onLine === false)
  || ['functions/unavailable', 'functions/deadline-exceeded', 'functions/internal'].includes(e?.code);
// Countdown on the PIN pad for a wait the server (or the offline limit) set.
let _pinWaitTimer = null;
function _showPinWait(errorEl, submitBtn, seconds, prefix) {
  if (_pinWaitTimer) clearInterval(_pinWaitTimer);
  let left = Math.max(1, Math.round(seconds));
  const paint = () => {
    const m = Math.floor(left / 60), s = String(left % 60).padStart(2, '0');
    errorEl.textContent = `${prefix} Try again in ${m}:${s}.`;
    errorEl.style.display = 'block';
    if (submitBtn) submitBtn.disabled = true;
  };
  paint();
  _pinWaitTimer = setInterval(() => {
    left--;
    if (left <= 0) {
      clearInterval(_pinWaitTimer); _pinWaitTimer = null;
      errorEl.textContent = ''; errorEl.style.display = 'none';
      if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = 'Unlock'; }
      return;
    }
    paint();
  }, 1000);
}

// A blob saved before the tenant migrated holds digits: trade them for the id once.
let _pinUpgradeTried = null;
function _upgradeStoredPin(tenantId, blob) {
  if (_pinUpgradeTried === blob.pin) return;
  _pinUpgradeTried = blob.pin;
  const key = `dispatchdeck_pin_${tenantId}`;
  httpsCallable(functions, 'verifyAccessPin')({ tenantId, pin: String(blob.pin) }).then(({ data }) => {
    const cur = JSON.parse(localStorage.getItem(key) || 'null');
    if (!cur || cur.pin !== blob.pin || cur.v === 2) return; // changed meanwhile
    if (data?.ok) localStorage.setItem(key, JSON.stringify({ ...cur, pin: data.key, v: 2 }));
    else localStorage.removeItem(key);
    if (typeof window.updateNavForPinAccess === 'function') window.updateNavForPinAccess();
  }).catch((e) => console.warn('[PIN] stored PIN upgrade failed:', e?.code || e?.message));
}

// Live-sync tenant settings (accessPins, etc.) so admin changes propagate
// to active sessions without requiring an app restart.
let _tenantSettingsUnsub = null;
function subscribeTenantSettings(tenantId) {
  if (!tenantId || tenantId === 'demo') return;
  if (_tenantSettingsUnsub) { try { _tenantSettingsUnsub(); } catch(_){} _tenantSettingsUnsub = null; }
  try {
    _tenantSettingsUnsub = onSnapshot(doc(db, 'tenants', tenantId), (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      // TASK-9369 (COD Edge Case #5) — settings.yards drives the Roster yard
      // filters AND the Assets Items|Branches toggle, but only Roster's own
      // add/delete paths called refreshYardFilters. A yard added in ANOTHER
      // session left this one with no toggle (and a Branches view missing the
      // new card) until reload. Compare the yards list, not the whole settings
      // blob — every other settings save must not rebuild the dropdowns.
      const _prevYardsJson = JSON.stringify((window.AppState.settings && window.AppState.settings.yards) || []);
      const _prevPinnedJson = JSON.stringify((window.AppState.settings && window.AppState.settings.pinnedAssetIds) || []); // TASK-10007
      window.AppState.settings = data.settings || {};
      window.AppState.tenantData = data;
      // TASK-9858 — offline PIN hashes: only this tenant's, and only while the
      // PIN set is the one they were made under.
      _clearPinCaches(tenantId);
      const _pc = _readPinCache(tenantId);
      if (_pc && _pc.ver !== (Number(window.AppState.settings.accessPinRev) || 0)) {
        try { localStorage.removeItem(_pinHashKey(tenantId)); } catch (_) {}
      }
      if (JSON.stringify(window.AppState.settings.yards || []) !== _prevYardsJson
          && document.getElementById('filter-assets-yard')
          && typeof window.RosterModule?.refreshYardFilters === 'function') {
        try { window.RosterModule.refreshYardFilters(); } catch (e) { console.warn('[Settings] yard filter refresh failed:', e?.message || e); }
      }
      // TASK-10007 — a star set in another session repaints the dispatch strip, the Add Job picker and the Roster stars.
      if (JSON.stringify(window.AppState.settings.pinnedAssetIds || []) !== _prevPinnedJson) {
        try { window.dispatchEvent(new CustomEvent('dd:pinned-assets-changed')); } catch (_) { /* no CustomEvent: next render picks it up */ }
      }
      // Muted-tenant hints follow a pause starting or ending without a reload.
      try {
        window.paintTenantSendNotices?.();
        // Surfaces that draw their own buttons (Driver Code, Driver PINs) redraw
        // when the mode flips (COD r1: a slot repaint alone left labels stale).
        const _m = window.getTenantSendMode?.() || {};
        const _key = `${_m.mode}|${_m.textsHeld}|${_m.textsRefused}`;
        if (window.__ddSendModeKey !== undefined && window.__ddSendModeKey !== _key) {
          window.dispatchEvent(new CustomEvent('dd:tenant-send-mode-changed', { detail: _m }));
        }
        window.__ddSendModeKey = _key;
        // COD r2: a pause that simply runs out writes nothing, so no snapshot
        // fires — re-check just after it ends (only when that is today).
        clearTimeout(window.__ddSendModeTimer);
        const _left = Number.isFinite(_m.untilMs) ? _m.untilMs - Date.now() : -1;
        if (_left > 0 && _left < 86400000) {
          window.__ddSendModeTimer = setTimeout(() => {
            try {
              window.paintTenantSendNotices?.();
              const _n = window.getTenantSendMode?.() || {};
              const _k = `${_n.mode}|${_n.textsHeld}|${_n.textsRefused}`;
              if (_k !== window.__ddSendModeKey) { window.__ddSendModeKey = _k; window.dispatchEvent(new CustomEvent('dd:tenant-send-mode-changed', { detail: _n })); }
            } catch (_) { /* display only */ }
          }, _left + 2000);
        }
      } catch (_) { /* display only */ }
      if (typeof window.updateNavForPinAccess === 'function') window.updateNavForPinAccess();
      // TASK-634 (COD Blast/Code ⚠️): live-sync the Smart Tools UI gate. initCarrierAssistant
      // (idempotent — initialized-guard) re-runs applySmartToolsVisibility (toggles
      // body.smart-tools-off → CSS shows/hides .ai-entry) AND re-evaluates the FAB's
      // inline display, so flipping the flag reflects WITHOUT a reload — incl. restoring
      // the FAB when turned back ON (its inline display:none would otherwise persist).
      if (typeof initCarrierAssistant === 'function') initCarrierAssistant();
    }, (err) => console.warn('[Settings] Live sync error:', err?.message || err));
  } catch (err) {
    console.warn('[Settings] Failed to subscribe to tenant settings:', err);
  }
}
function unsubscribeTenantSettings() {
  if (_tenantSettingsUnsub) { try { _tenantSettingsUnsub(); } catch(_){} _tenantSettingsUnsub = null; }
}
window.subscribeTenantSettings = subscribeTenantSettings;
window.unsubscribeTenantSettings = unsubscribeTenantSettings;

// TASK-366 — Live-sync members/{uid} doc so admin changes to a user's modules/
// role via updateMemberAccess propagate to that user's active session without
// requiring a full reload. Replaces the original one-shot getDoc at the
// auth-bootstrap path (~app.js:5142). The bootstrap still seeds AppState.
// memberAccess from its initial getDoc for fast first-paint; this subscription
// then takes over for subsequent updates.
let _memberAccessUnsub = null;
function subscribeMemberAccess(tenantId, uid) {
  if (!tenantId || !uid || tenantId === 'demo') return;
  // TASK-1658 — a partner has NO members/{uid} doc at the client carrier, so this
  // listener fires !snap.exists() and nulls AppState.memberAccess, deleting the
  // synthetic member-equivalent a tick after loadPartnerClientTenant set it (the
  // partner's nav empties and the next nav PIN-challenges). loadPartnerClientTenant
  // already declines to call this; the guard is here so no future caller can.
  if (typeof isPartnerClientSession === 'function' && isPartnerClientSession()) return;
  if (_memberAccessUnsub) { try { _memberAccessUnsub(); } catch(_){} _memberAccessUnsub = null; }
  try {
    _memberAccessUnsub = onSnapshot(
      doc(db, 'tenants', tenantId, 'members', uid),
      (snap) => {
        if (!snap.exists()) {
          // Member doc deleted (e.g. revokeUserAccess) — clear local cache.
          // The next nav will fail any module gate and either kick the user
          // to legacy PIN access (if owner) or bounce them out.
          window.AppState.memberAccess = null;
          if (typeof window.updateNavForPinAccess === 'function') window.updateNavForPinAccess();
          return;
        }
        const m = snap.data() || {};
        window.AppState.memberAccess = {
          uid,
          role: m.role || null,
          modules: Array.isArray(m.modules) ? m.modules : null,
          personId: m.personId || null,
          // TASK-1489 — must be mirrored here as well as in loadUserTenant, or a
          // live access edit (updateMemberAccess → this onSnapshot) would silently
          // drop the flag and re-expose the self-service surfaces. Explicit flag
          // only — see the loadUserTenant comment for why it isn't derived.
          personless: m.personless === true,
          status: m.status || 'active',
          // TASK-1098 — tenure signal for the changed-markers baseline.
          acceptedAtMs: (typeof m.acceptedAtMs === 'number') ? m.acceptedAtMs : null,
          // TASK-608 — keep the capability flag live in sync (advisory in UI;
          // firestore.rules is the enforcement layer). See loadUserTenant.
          viewUploadOnly: m.viewUploadOnly === true,
          readOnly: m.readOnly === true, // TASK-1403 final COD — mirror of the loadUserTenant projection
          settingsModules: Array.isArray(m.settingsModules) ? m.settingsModules.filter(x => typeof x === 'string') : [], // TASK-9986 S1 — see loadUserTenant
          manageLogins: m.manageLogins === true, // TASK-9389 S1 — see loadUserTenant
          displayName: m.displayName || null,
          // TASK-9369 — home branch, DEFAULT FILTER ONLY (never a boundary).
          // Second writer of this object: see the loadUserTenant comment.
          yardId: (typeof m.yardId === 'string' && m.yardId) ? m.yardId : null,
        };
        // Re-evaluate nav-visibility (module pills) so the sidebar reflects
        // the freshly-granted/revoked modules without a route nav.
        if (typeof window.updateNavForPinAccess === 'function') window.updateNavForPinAccess();
      },
      (err) => console.warn('[MemberAccess] Live sync error:', err?.message || err)
    );
  } catch (err) {
    console.warn('[MemberAccess] Failed to subscribe:', err);
  }
}
function unsubscribeMemberAccess() {
  if (_memberAccessUnsub) { try { _memberAccessUnsub(); } catch(_){} _memberAccessUnsub = null; }
}
window.subscribeMemberAccess = subscribeMemberAccess;
window.unsubscribeMemberAccess = unsubscribeMemberAccess;

// ============================================
// TASK-9135 — A PIN MUST ONLY UNLOCK WHAT IT GRANTS
// ============================================
// THE DEFECT this replaces: submitPinChallenge looked up `accessPins[pin]` and,
// if the PIN existed AT ALL, called the challenge callback with `true`. The
// router's callback turns that straight into doNavigateToView(view) with no
// re-check. It even printed `pinConfig.modules` in the success toast — the code
// knew exactly what that PIN granted and waved the user through regardless. On
// the one live tenant that actually runs a multi-PIN kiosk (8 PINs, one of which
// grants `maintenance` and NOTHING else) that gap meant the maintenance PIN
// opened billing, BI, workforce and AR/AP. Scoping a shared yard tablet is the
// whole purpose of this system; at the challenge boundary the scoping was
// cosmetic.
//
// 🔴 WHY NOT THE OBVIOUS FIX. "Unlock, then re-run checkModuleAccess(view) and
// require === true" is wrong, and the census run for this task says why:
// `payroll` sits in `lockedModules` (TASK-850B) but has NO checkbox in the
// Add/Edit PIN modal, so NO PIN can grant it and none in production does (0/46).
// A post-hoc checkModuleAccess re-check would turn payroll from "any PIN opens
// it" into "no PIN opens it" for all 39 PIN-configured tenants — a tightening
// that strands a real person at a real tablet (the TASK-428 shape). Enforce at
// the PIN boundary with an explicit predicate instead, so every carve-out is
// written down here rather than emerging from another function's tail.
//
// PIN-SHAPE CENSUS — read-only admin SDK, 2026-09-10, all 93 tenants. Shapes and
// counts only; no PIN value was read out, printed or stored anywhere.
//   • 39 tenants configure `accessPins` at all — 46 PINs total
//   • 38 of the 39 have exactly ONE PIN; ONE tenant has 8
//   • all 39 have EXACTLY ONE `isPrimary: true` PIN — never zero, never two
//   • ZERO PINs have an empty or absent `modules` array (0/46). The feared
//     "primary PIN with modules: []" master key does NOT exist in production,
//     and saveAccessPin has refused to create one ("Select at least one module")
//   • ZERO PINs grant no locked module
//   • module frequency: maintenance 46, roster 44, dispatch 43, clock/billing/
//     crm/bi 40, hr/workforce/compliance 39, hr-directory 19, 811 4,
//     timeoff 1 (legacy), pretrip 1, jobboard 1 — and payroll ZERO.

// Modules an admin can actually tick in the Add/Edit PIN modal. Mirrors the
// checkbox list in saveAccessPin(); tests/task9135-pin-scope.unit.cjs parses
// that function out of this file and fails if the two ever drift.
const PIN_GRANTABLE_MODULES = ['dispatch', 'maintenance', 'roster', 'hr', 'hr-directory', 'clock', 'billing', 'workforce', 'crm', 'bi', 'compliance', '811'];

// PIN-gated modules that NO checkbox can grant, so they ride on the PRIMARY PIN
// — the de-facto admin credential in this system: it force-includes `roster`,
// it is the one assertCanManageAccessPins demands before PIN management, and it
// is what the recovery email sends. Today that is `payroll` alone. The tripwire
// asserts this list is EXACTLY lockedModules minus PIN_GRANTABLE_MODULES, so a
// future locked module added without a checkbox fails the suite instead of
// silently becoming "any PIN opens it" or "no PIN opens it".
const PIN_PRIMARY_ONLY_MODULES = ['payroll'];

// Does this PIN config grant `module`? Mirrors the alias rules the rest of the
// access layer already applies, so the challenge boundary and the nav lock
// state can never disagree with each other:
//   • ap → billing        — VIEW_MODULE_ALIASES / moduleForView (TASK-9131)
//   • workforce ← timeoff — legacy PIN spelling; isModuleUnlocked's back-compat
//                           branch. One live PIN still stores `timeoff`.
//   • dispatch  ← 811     — checkModuleAccess's "811 rides dispatch" branch.
//                           Four live PINs carry 811 and one of them has 811
//                           and NOT dispatch, so dropping this strands it.
// A null/empty `module` means a SCOPE-LESS challenge (device-lock unlock,
// Settings, Help) — "prove you hold any valid PIN for this tenant". Those are
// deliberately unchanged: gating them on a module list would lock a kiosk user
// out of the very prompt that unlocks their own modules.
function pinGrantsModule(pinConfig, module) {
  if (!module) return true;
  const m = moduleForView(module);
  const mods = Array.isArray(pinConfig?.modules) ? pinConfig.modules : [];
  if (mods.includes(m)) return true;
  if (m === 'workforce' && mods.includes('timeoff')) return true;
  if (m === 'dispatch' && mods.includes('811')) return true;
  if (PIN_PRIMARY_ONLY_MODULES.includes(m) && pinConfig?.isPrimary === true) return true;
  return false;
}

// Get currently unlocked modules from localStorage
function getUnlockedModules() {
  // Demo mode - all modules unlocked
  if (window.AppState?.isDemo || window.AppState?.tenant === 'demo') {
    console.log('[PIN] Demo mode - all modules unlocked');
    return ['dispatch', 'roster', 'maintenance', 'clock', 'pretrip', 'billing', 'workforce', 'hr', 'crm', 'bi', 'compliance', 'hr-directory'];
  }

  // Public access (driver link) - limited modules
  if (window.AppState?.accessToken || window.AppState?.driverMode) {
    console.log('[PIN] Driver mode - limited modules');
    return ['dispatch', 'pretrip'];
  }

  const tenantId = window.AppState?.tenant;
  if (!tenantId) {
    console.log('[PIN] No tenant ID - no modules unlocked');
    return [];
  }

  try {
    const stored = localStorage.getItem(`dispatchdeck_pin_${tenantId}`);
    console.log('[PIN] Stored PIN data:', stored ? 'exists' : 'none');
    if (!stored) return [];

    const { pin, expiresAt, unlockedAt } = JSON.parse(stored);
    if (Date.now() > expiresAt) {
      // Expired - clear storage
      localStorage.removeItem(`dispatchdeck_pin_${tenantId}`);
      return [];
    }

    // Check if admin forced a PIN re-entry
    const pinResetAt = window.AppState?.settings?.pinResetAt;
    if (pinResetAt && unlockedAt && unlockedAt < pinResetAt) {
      localStorage.removeItem(`dispatchdeck_pin_${tenantId}`);
      return [];
    }

    // Look up PIN in settings
    // Only validate against accessPins if settings have loaded
    const storedData = JSON.parse(stored);
    if (!window.AppState?.settings) {
      // Settings not loaded yet - trust stored PIN temporarily
      return storedData.modules || [];
    }

    const accessPins = accessPinMap();
    const pinConfig = accessPins[pin];
    if (!pinConfig) {
      // TASK-9858 — a blob from before this tenant's PINs moved holds the DIGITS
      // (no v:2), which are no longer keys once settings.accessPinMeta exists.
      // Trade them for the id once (server-side) instead of logging the kiosk
      // out; until that answers, keep the modules it was unlocked with.
      if (storedData.v !== 2 && accessPins === window.AppState.settings.accessPinMeta) {
        _upgradeStoredPin(tenantId, storedData);
        return storedData.modules || [];
      }
      // Settings loaded but PIN not found - it was deleted by admin
      localStorage.removeItem(`dispatchdeck_pin_${tenantId}`);
      return [];
    }

    // TASK-9135 (COD Code Reviewer, on the fix diff) — keep this in step with
    // pinGrantsModule. `payroll` is PIN-gated but has no checkbox, so it rides
    // the PRIMARY PIN; if only the challenge knew that and this did not, the
    // primary-PIN holder would unlock payroll at the prompt and then be
    // re-prompted by every later gate (nav lock icon, nav-click enforcement,
    // the no-access CTA) because isModuleUnlocked still said no. Same rule in
    // both places, so the boundary and the display agree.
    const granted = pinConfig.modules || [];
    return pinConfig.isPrimary === true
      ? [...new Set([...granted, ...PIN_PRIMARY_ONLY_MODULES])]
      : granted;
  } catch (e) {
    console.error('[PIN] Error reading stored PIN:', e);
    return [];
  }
}

// Check if a module is accessible
function isModuleUnlocked(moduleName) {
  // Compliance-only tenants: compliance, hr, roster fully unlocked (pretrip/dvir shown as locked preview)
  if (isComplianceOnlyTenant()) {
    return moduleName === 'compliance' || moduleName === 'hr' || moduleName === 'roster';
  }

  // Always accessible modules (driver boards need pretrip/dvir access — not for
  // compliance-only, which already returned above; the TASK-1097 guard here is
  // defense-in-depth so a future edit to that branch can't re-open the leak).
  const alwaysAccessible = ['pretrip', 'dvir'];
  if (alwaysAccessible.includes(moduleName)
      && !(typeof isComplianceOnlyTenant === 'function' && isComplianceOnlyTenant())) return true;

  // Driver PIN mode - dispatch, jobboard and pretrip are accessible
  if (window.AppState?.driverModePin && ['dispatch', 'jobboard', 'pretrip'].includes(moduleName)) {
    return true;
  }

  // Demo mode - everything unlocked
  if (window.AppState?.isDemo || window.AppState?.tenant === 'demo') return true;

  // Founder accounts follow the same PIN rules as everyone else.
  // Only bypass if no PINs are configured at all (new account / owner setup).
  // TASK-748/719-A — VIP/comped accounts honor configured PINs like everyone
  // else (per-device lock); they no longer get a bypass here. (When no PINs
  // are configured, the unconditional check just below still grants access.)
  if (isFounderAccount()) {
    const accessPins = accessPinMap();
    if (Object.keys(accessPins).length === 0) return true;
    // PINs configured — fall through to standard PIN check
  }

  // No PINs configured = full access (new account / owner)
  const accessPins = accessPinMap();
  if (Object.keys(accessPins).length === 0) return true;

  // Check unlocked modules
  const unlocked = getUnlockedModules();
  if (unlocked.includes(moduleName)) return true;
  // Backwards compat: old PINs stored 'timeoff', sidebar uses 'workforce'
  if (moduleName === 'workforce' && unlocked.includes('timeoff')) return true;
  return false;
}

// The canonical module the OPEN challenge is gating, or null for a scope-less
// one. Module-private on purpose (same reasoning as toggleDeviceLock): this is
// the authorization scope of a live prompt and does not belong on window where
// a console one-liner or a third-party script can retarget it. Set on EVERY
// showPinChallenge call — including to null — so a scope from a previous
// challenge can never leak into the next one, and cleared when the modal closes.
let _pinChallengeModule = null;

// Show PIN challenge modal.
// `moduleName` is the human LABEL shown in the prompt ("AR / AP Billing",
// "Settings", "Help Access") — it is NOT an access decision and must never be
// used as one. `requiredModule` is the machine module the entered PIN must
// actually grant; omit it (or pass null) for a scope-less "any valid PIN"
// prompt. 🔴 If you add a call site that leads to a MODULE, pass requiredModule
// — an omitted third argument fails OPEN, and tests/task9135-pin-scope.unit.cjs
// enumerates the call sites in this file for exactly that reason.
function showPinChallenge(moduleName, callback, requiredModule = null) {
  // Trial expired — don't request a PIN, route to subscribe screen instead
  const trialStatus = window.AppState?.trialStatus || getTrialStatus();
  if (trialStatus?.status === 'expired') {
    showTrialExpiredScreen();
    if (callback) callback(false);
    return;
  }

  const modal = document.getElementById('pin-challenge-modal');
  const input = document.getElementById('pin-challenge-input');
  const error = document.getElementById('pin-challenge-error');
  const message = document.getElementById('pin-challenge-message');

  message.textContent = moduleName
    ? `Enter PIN to unlock ${capitalizeFirst(moduleName)}`
    : 'Enter your access PIN';
  input.value = '';
  error.textContent = '';
  error.style.display = 'none';
  // TASK-10077 — "Forgot your PIN?" for the account owner only.
  const forgot = document.getElementById('pin-challenge-forgot');
  if (forgot) forgot.innerHTML = pinRecoveryLinkHtml();

  // Store callback for when PIN is verified
  window._pinChallengeCallback = callback;
  // TASK-9135 — set unconditionally (null included) so a scope from a previous
  // challenge can never carry into this one.
  _pinChallengeModule = requiredModule ? moduleForView(requiredModule) : null;

  modal.classList.remove('hidden');
  setTimeout(() => input.focus(), 100);

  // Handle enter key
  input.onkeydown = (e) => {
    if (e.key === 'Enter') submitPinChallenge();
  };
}

function closePinChallenge() {
  const modal = document.getElementById('pin-challenge-modal');
  modal.classList.add('hidden');
  _pinChallengeModule = null; // TASK-9135 — never leave a scope armed

  if (window._pinChallengeCallback) {
    window._pinChallengeCallback(false);
    window._pinChallengeCallback = null;
  }
}

let _pinSubmitting = false;
async function submitPinChallenge() {
  // COD r3 — one attempt at a time (Enter still fires while the button is disabled).
  if (_pinSubmitting || _pinWaitTimer) return;
  _pinSubmitting = true;
  try { return await _submitPinChallenge(); } finally { _pinSubmitting = false; }
}
async function _submitPinChallenge() {
  const input = document.getElementById('pin-challenge-input');
  const error = document.getElementById('pin-challenge-error');
  const submitBtn = document.getElementById('pin-challenge-submit');
  const pin = input.value;

  if (pin.length < 4) {
    error.textContent = 'PIN must be at least 4 digits';
    error.style.display = 'block';
    return;
  }

  submitBtn.disabled = true;
  submitBtn.textContent = 'Verifying...';

  try {
    // TASK-9858 — the PIN is checked SERVER-side (verifyAccessPin). This browser
    // no longer holds the codes; it gets back only the matching PIN's id (what it
    // stores) and what that PIN unlocks.
    const accessPins = accessPinMap();
    let pinConfig = null;
    let pinKey = null;
    if (Object.keys(accessPins).length) {
      const _tid = window.AppState?.tenant;
      let v = null;
      try {
        v = (await httpsCallable(functions, 'verifyAccessPin')({ tenantId: _tid, pin })).data;
      } catch (e) {
        if (!_isOfflineErr(e)) throw e;
        // No server: the hashes this device saved after earlier online unlocks.
        let hit = null;
        try { hit = await _verifyPinOffline(_tid, pin); } catch (w) {
          if (w && w._offlineWait) { _showPinWait(error, submitBtn, w._offlineWait, 'No connection and too many wrong PINs.'); input.value = ''; return; }
          throw w;
        }
        if (!hit) {
          error.textContent = 'No connection. Only a PIN this device has used online before works offline.';
          error.style.display = 'block';
          input.value = ''; input.focus();
          submitBtn.disabled = false; submitBtn.textContent = 'Unlock';
          return;
        }
        v = { ok: true, key: hit.key, config: hit.config };
      }
      if (v?.ok) {
        pinKey = v.key; pinConfig = v.config;
        if (v.offline) _rememberPinOffline(_tid, v.key, pin, v.offline).catch(() => {});
      }
    }

    // TASK-9134 — three console.log lines used to live here and they SHIPPED.
    // Verified in the live prod bundle (dist/app.min.js?v=1.15.1919) before
    // removal, not inferred from source:
    //   console.log("[PIN Debug] Entered PIN:", o)            <- the PIN, cleartext
    //   console.log("[PIN Debug] Available PINs:", Object.keys(i))  <- EVERY PIN
    //   console.log("[PIN Debug] Settings object:", ...settings)
    // The middle one is the bad one: it enumerates every access PIN configured
    // for the tenant, to anyone who opens devtools on a shared yard tablet, and
    // to any extension or error-reporting tool that scrapes console.
    // 🔴 Do not reintroduce these, not even temporarily. If this path ever needs
    // debugging again, log the OUTCOME (matched / no match / which module set),
    // never the credential and never the key list. Same rule as never printing a
    // driver PIN into a log or a report.
    // COD Architect, on the diff that removed them: do NOT leave a dead boolean
    // switch here either — "dead switches inevitably get toggled in live
    // incident debugging", and the thing on the other side of this one is a
    // credential. So there is deliberately nothing to flip. If this path needs
    // debugging, add a log that names the OUTCOME and delete it before commit.

    // Check if settings are loaded and PINs exist
    if (Object.keys(accessPins).length === 0) {
      error.textContent = 'No PINs configured. Check Settings → Access PINs.';
      error.style.display = 'block';
      submitBtn.disabled = false;
      submitBtn.textContent = 'Unlock';
      return;
    }

    if (!pinConfig) {
      error.textContent = 'Invalid PIN.'; // TASK-9858 — never how many PINs exist
      error.style.display = 'block';
      input.value = '';
      input.focus();
      submitBtn.disabled = false;
      submitBtn.textContent = 'Unlock';
      return;
    }

    // TASK-9135 — the PIN exists. That is NOT the same as "this PIN opens the
    // screen the user was challenged for". Enforce the scope before anything is
    // stored, before the audit row, and before the callback.
    //
    // TASK-719 owner re-elevation is the one legitimate bypass: an OWNER who
    // enters the PRIMARY PIN has the device lock cleared a few lines below, at
    // which point checkModuleAccess's owner branch returns true for every
    // module — so refusing here would strand the owner on a screen they are
    // about to be fully entitled to. Owner AND primary, both: a non-primary PIN
    // leaves the lock engaged and the owner scoped to that PIN's subset, exactly
    // as the TASK-719 block below specifies, and a non-owner holding the primary
    // PIN gets only what that PIN grants (plus PIN_PRIMARY_ONLY_MODULES).
    const _isOwnerReElevation = window.AppState?.memberAccess?.role === 'owner'
      && pinConfig.isPrimary === true;

    if (!_isOwnerReElevation && !pinGrantsModule(pinConfig, _pinChallengeModule)) {
      // OUTCOME only. Never the PIN, never the key list, never the module set of
      // the PIN that was entered — see the TASK-9134 block above.
      console.warn(`[PIN] Refused — entered PIN does not grant ${_pinChallengeModule}`);
      error.textContent = `That PIN doesn't unlock ${_prettyModuleLabel(_pinChallengeModule)}. Enter a PIN with access to it, or ask your administrator.`;
      error.style.display = 'block';
      input.value = '';
      input.focus();
      submitBtn.disabled = false;
      submitBtn.textContent = 'Unlock';
      // Modal stays OPEN and the callback stays armed, so the user can try the
      // right PIN; cancelling still runs callback(false) → showNoAccessMessage.
      // Nothing is written: the device's existing PIN scope is left untouched
      // rather than being silently swapped for a weaker one.
      return;
    }

    // Valid PIN - store in localStorage (including modules for offline/race condition handling)
    const tenantId = window.AppState?.tenant;
    const expiresAt = Date.now() + (PIN_EXPIRY_DAYS * 24 * 60 * 60 * 1000);
    localStorage.setItem(`dispatchdeck_pin_${tenantId}`, JSON.stringify({
      pin: pinKey,
      ...(pinKey !== pin ? { v: 2 } : {}), // TASK-9858 — an id, not the digits
      modules: pinConfig.modules || [],
      unlockedAt: Date.now(),
      expiresAt
    }));

    // Audit log: record who unlocked what and when (non-blocking)
    if (tenantId && tenantId !== 'demo') {
      const maskedPin = pin.length > 2 ? pin.slice(0, -2).replace(/./g, '*') + pin.slice(-2) : pin;
      addDoc(collection(db, 'tenants', tenantId, 'pinAuditLog'), {
        pin: maskedPin,
        label: pinConfig.label || '',
        isPrimary: !!pinConfig.isPrimary,
        modules: pinConfig.modules || [],
        unlockedAt: Timestamp.now(),
        userAgent: (navigator.userAgent || '').slice(0, 300),
        uid: auth.currentUser?.uid || null
      }).catch(err => console.warn('[PIN] Audit log write failed:', err?.message || err));
    }

    // TASK-719 — owner re-elevation. If the signed-in user is an owner and they
    // entered the PRIMARY PIN, clear the explicit device-lock flag so the owner
    // is fully re-elevated to full access (checkModuleAccess owner branch
    // returns true again). A NON-primary PIN leaves the lock engaged — the owner
    // stays scoped to that PIN's module subset (the stored PIN + lock flag
    // coexist; checkModuleAccess falls through to isModuleUnlocked which honors
    // the stored PIN). Then refresh the FAB icon.
    if (window.AppState?.memberAccess?.role === 'owner' && pinConfig.isPrimary === true) {
      setOwnerDeviceLock(false);
    }

    // Update nav to show unlocked modules
    updateNavForPinAccess();
    updateLockFab();

    document.getElementById('pin-challenge-modal').classList.add('hidden');
    const moduleLabels = { '811': 'Locates (811)', billing: 'AR / AP', workforce: 'Workforce', bi: 'BI' };
    showToast(`Modules unlocked: ${pinConfig.modules.map(m => moduleLabels[m] || capitalizeFirst(m)).join(', ')}`, 'success');

    _pinChallengeModule = null; // TASK-9135 — challenge satisfied, disarm the scope

    if (window._pinChallengeCallback) {
      window._pinChallengeCallback(true);
      window._pinChallengeCallback = null;
    }
  } catch (err) {
    console.error('[PIN] Verification error:', err?.code || err);
    // TASK-9858 — wrong-PIN limit from verifyAccessPin: count down its wait.
    if (err?.code === 'functions/resource-exhausted' && err?.details?.retryAfterSec > 0) {
      _showPinWait(error, submitBtn, err.details.retryAfterSec, 'Too many wrong PINs.');
      return;
    }
    error.textContent = err?.code === 'functions/resource-exhausted' ? err.message : 'Error verifying PIN';
    error.style.display = 'block';
  }

  submitBtn.disabled = false;
  submitBtn.textContent = 'Unlock';
}

// TASK-10077 follow-up — "Forgot your PIN?" only for the account owner
// (uid === tenants.ownerId, the same test sendPinRecoveryEmail makes; never
// driver / PIN / public / demo — aiHistoryViewerIsOwner is that rule). Anyone
// else is told to ask the owner up front. The server stays the real gate.
function pinRecoveryLinkHtml(isOwner = aiHistoryViewerIsOwner()) {
  return isOwner
    ? '<a href="#" onclick="event.preventDefault();requestPrimaryPinRecovery();" style="color:var(--accent-cyan);">Forgot your PIN?</a>'
    : 'Forgot the PIN? Ask your account owner.';
}

// Request Primary PIN recovery via email
async function requestPrimaryPinRecovery() {
  // Close PIN challenge modal if open
  closePinChallenge();

  // TASK-10077 — a non-owner never reaches the confirm (the link is hidden for
  // them; this covers a stale screen). Same copy the server's 403 gets.
  if (!aiHistoryViewerIsOwner()) {
    showToast('Only the account owner can recover the Primary PIN. Ask them to use "Forgot your PIN?" from their login.', 'error');
    return;
  }

  const confirmed = await showConfirm(
    'Recover Primary PIN?',
    'We\'ll email your Primary PIN to the account owner\'s login email. Only the account owner can recover it.',
    { confirmText: 'Send Email' }
  );

  if (!confirmed) return;

  try {
    showToast('Sending recovery email...', 'info');

    // Get auth token
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      showToast('You must be logged in to recover your PIN', 'error');
      return;
    }

    // Call Cloud Function to send recovery email
    const response = await fetch('https://us-central1-dispatch-deck-be88c.cloudfunctions.net/sendPinRecoveryEmail', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        data: { tenantId: window.AppState?.tenant }
      })
    });

    const result = await response.json().catch(() => ({}));

    if (result.result?.success) {
      showToast('Recovery email sent! Check your inbox.', 'success');
    } else if (response.status === 403) {
      // TASK-10077 — refusal (not the owner / no such account): one fixed message.
      showToast('Only the account owner can recover the Primary PIN. Ask them to use "Forgot your PIN?" from their login.', 'error');
    } else {
      throw new Error(result.result?.error || 'Failed to send recovery email');
    }
  } catch (error) {
    console.error('[PIN Recovery] Error:', error);
    showToast(error.message || 'Failed to send recovery email', 'error');
  }
}
window.requestPrimaryPinRecovery = requestPrimaryPinRecovery;

// Clear stored PIN (lock modules)
function clearPinAccess() {
  const tenantId = window.AppState?.tenant;
  if (tenantId) {
    localStorage.removeItem(`dispatchdeck_pin_${tenantId}`);
  }
  updateNavForPinAccess();
  showToast('Modules locked', 'info');
  // Navigate to a module that's always accessible
  navigateToView('pretrip');
}

// TASK-719 — explicit per-device owner-lock flag.
// SEPARATE from the PIN-unlock key (`dispatchdeck_pin_<tenant>`). This flag
// means "the owner DELIBERATELY locked this device into PIN-scope." Absent =
// the default (NOT owner-locked) → the owner branch in checkModuleAccess keeps
// its full-access bypass, preserving the 2026-05-26 anti-lockout hotfix. A
// fresh owner login has NO flag → full access, byte-for-byte unchanged. This is
// a SOFT/convenience gate (accident-prevention on a shared yard tablet), NOT a
// security boundary: the session stays authed as owner, Firestore rules are
// unchanged, and devtools could clear the flag.
function isOwnerDeviceLocked() {
  const t = window.AppState?.tenant;
  return !!(t && localStorage.getItem(`dispatchdeck_owner_locked_${t}`));
}
function setOwnerDeviceLock(locked) {
  const t = window.AppState?.tenant;
  if (!t) return;
  if (locked) localStorage.setItem(`dispatchdeck_owner_locked_${t}`, '1');
  else localStorage.removeItem(`dispatchdeck_owner_locked_${t}`);
}

// Update navigation to show locked/unlocked state.
// TASK-745 — wrapper: after the lock/upgrade state is (re)computed by the inner
// function (whatever return path it takes), ALWAYS re-apply the user-hidden
// "hold-to-dim" ghost state + (re)wire the hold gesture + the Customize
// affordance. Doing this in the wrapper guarantees the hide-render can't be
// clobbered no matter which early-return _updateNavLockState hits (Blast).
function updateNavForPinAccess() {
  _updateNavLockState();
  try {
    if (window.SidebarCustomize) {
      window.SidebarCustomize.ensureAffordance();
      window.SidebarCustomize.wireAll();
      window.SidebarCustomize.applyHideState();
    }
  } catch (_) { /* never let customize break nav */ }
}

function _updateNavLockState() {
  const lockedModules = ['dispatch', 'roster', 'maintenance', 'hr', 'crm', 'clock', 'billing', 'workforce', 'bi', 'compliance', 'payroll']; // TASK-850B
  let hasLockedModules = false;

  // Demo mode or driver mode - no lock/unlock needed
  if (window.AppState?.isDemo || window.AppState?.tenant === 'demo' || window.AppState?.driverMode) {
    // Ensure no modules show as locked
    DOM.navItems.forEach(item => {
      item.classList.remove('locked');
      const lockIcon = item.querySelector('.lock-icon');
      if (lockIcon) lockIcon.remove();
    });
    DOM.mobileNavItems.forEach(item => item.classList.remove('locked'));
    return;
  }

  // TASK-319 multi-user member — memberAccess.modules is absolute. Members
  // never see PIN locks; non-granted modules are hidden by hideUpgradeBadges.
  // Granted modules should show clean (no .locked, no .lock-icon child).
  const memberAccess = window.AppState?.memberAccess;
  if (memberAccess && memberAccess.status === 'active'
      && memberAccess.role !== 'owner'
      && Array.isArray(memberAccess.modules)
      && memberAccess.modules.length > 0) {
    DOM.navItems.forEach(item => {
      item.classList.remove('locked');
      const lockIcon = item.querySelector('.lock-icon');
      if (lockIcon) lockIcon.remove();
      item.querySelectorAll('.nav-upgrade-badge').forEach(b => { b.style.display = 'none'; });
    });
    DOM.mobileNavItems?.forEach(item => {
      item.classList.remove('locked');
      const lockIcon = item.querySelector('.lock-icon');
      if (lockIcon) lockIcon.remove();
      item.querySelectorAll('.nav-upgrade-badge').forEach(b => { b.style.display = 'none'; });
    });
    return;
  }

  // TASK-622: drive per-item lock DISPLAY off checkModuleAccess — the same
  // function nav-click enforcement uses — so display can't drift from access.
  // (Previously used isModuleUnlocked, which has no owner / legacy-admin /
  // dispatch-via-811 bypass, so owners saw cosmetic locks on modules they
  // could click straight into.) Pass quiet=true: this runs ~20×/update and
  // would otherwise spam the console; the nav-click path keeps its logs.
  // Map: === true → UNLOCKED; 'needs-pin' / false → LOCKED.
  // 'loading' (member doc still resolving on cold load) → DEFER: bail without
  // touching lock state so locks don't paint-then-clear. navigateToView
  // re-gates once the member read resolves and re-calls this.

  // Desktop nav
  for (const item of DOM.navItems) {
    const view = item.dataset.view;
    if (!view) continue;

    if (lockedModules.includes(view)) {
      const access = checkModuleAccess(view, true);
      if (access === 'loading') return; // defer — don't flash locks during cold load
      const isUnlocked = access === true;
      item.classList.toggle('locked', !isUnlocked);

      if (!isUnlocked) hasLockedModules = true;

      // Add or remove lock icon
      let lockIcon = item.querySelector('.lock-icon');
      if (!isUnlocked) {
        if (!lockIcon) {
          lockIcon = document.createElement('span');
          lockIcon.className = 'material-icons lock-icon';
          lockIcon.textContent = 'lock';
          item.appendChild(lockIcon);
        }
      } else if (lockIcon) {
        lockIcon.remove();
      }
    }
  }

  // Mobile nav
  for (const item of DOM.mobileNavItems) {
    const view = item.dataset.view;
    if (!view) continue;

    if (lockedModules.includes(view)) {
      const access = checkModuleAccess(view, true);
      if (access === 'loading') return; // defer — don't flash locks during cold load
      const isUnlocked = access === true;
      item.classList.toggle('locked', !isUnlocked);
      if (!isUnlocked) hasLockedModules = true;
    }
  }

  // Update the floating lock FAB state
  updateLockFab();
}

// ============================================
// LOCK/UNLOCK FAB
// ============================================

// Create floating padlock FAB for non-demo, non-founder accounts
function initLockFab() {
  // Remove existing if present (idempotent)
  const existing = document.getElementById('lock-fab');
  if (existing) existing.remove();

  // Skip for demo, driver mode, public access
  if (window.AppState?.isDemo || window.AppState?.driverMode || window.AppState?.isPublicAccess) return;
  // TASK-748/719-A — VIP/comped owners now get the lock FAB too. The per-device
  // lock honors access-PINs exactly like a non-VIP tenant (default unlocked =
  // no behavior change). The owner/legacy-admin member scoping below still
  // applies, and demo/driver/public still skip above.

  // TASK-319 multi-user members — PIN system is legacy. Members have explicit
  // members/{uid}.modules; lock FAB has no role for them. Skip rendering.
  // Owners + legacy admins (no modules field) fall through to the standard
  // FAB rendering since they still use the PIN UI for tenant-shared kiosks.
  const memberAccess = window.AppState?.memberAccess;
  if (memberAccess && memberAccess.status === 'active'
      && memberAccess.role !== 'owner'
      && Array.isArray(memberAccess.modules)
      && memberAccess.modules.length > 0) {
    return;
  }

  const fab = document.createElement('button');
  fab.id = 'lock-fab';
  fab.className = 'lock-fab';
  fab.innerHTML = '<span class="material-icons">lock</span>';
  fab.title = 'Unlock modules';

  fab.addEventListener('click', toggleDeviceLock);

  document.body.appendChild(fab);
  updateLockFab();
}

// TASK-1107 — the device lock is only ENFORCEABLE when at least one access PIN
// exists. checkModuleAccess / isModuleUnlocked deliberately short-circuit to
// full access when settings.accessPins is empty (anti-lockout guarantee — do
// NOT change those short-circuits), so with zero PINs a "LOCKED" state is a
// hollow assertion: the badge says LOCKED while every module stays wide open
// (show-account, mrsc 2026-07-10). Returns true ONLY when settings are loaded
// AND zero PINs are configured; while settings are still loading we return
// false (keep the existing lock semantics — never suppress or repaint a state
// we can't verify yet).
function deviceLockUnenforceable() {
  const settings = window.AppState?.settings;
  if (!settings) return false;
  return Object.keys(accessPinMap(settings)).length === 0;
}

// TASK-763 — single shared lock/unlock toggle. The FAB (mobile), the sidebar
// logo, and the status pill (desktop) all call THIS one function so the lock
// semantics stay byte-for-byte identical to the TASK-719 FAB logic. Kept
// MODULE-PRIVATE on purpose (COD Adversary) — do NOT add window.toggleDeviceLock;
// the lock is a soft/convenience gate, not a security boundary, and we don't
// want to amplify the global-export surface.
// TASK-1224 — clear the ephemeral hold-to-dim state. Called ONLY from the
// device lock/unlock funnel below (lock, and each unlock-challenge success), so
// a training dim survives everything EXCEPT a deliberate lock/unlock handoff.
// 🔴 BINDING (COD Code-Reviewer): do NOT move this into submitPinChallenge —
// that challenge also services Settings / module-gate PIN prompts, and an
// unrelated PIN entry must not wipe an active dim.
function _resetSidebarDim() {
  try { window.SidebarCustomize?.resetDim(); } catch (_) { /* never break the lock */ }
}

function toggleDeviceLock() {
  // TASK-1107 — zero access-PINs: locking is a total no-op at the access layer
  // (the anti-lockout short-circuits grant every module), so don't assert a
  // LOCKED state we can't enforce. Tell the owner how to make the lock real.
  if (deviceLockUnenforceable()) {
    showToast('Set an access PIN in Settings first to use device lock', 'info');
    return;
  }
  const tenantId = window.AppState?.tenant;
  const isOwner = window.AppState?.memberAccess?.role === 'owner';
  if (isOwner) {
    // TASK-719 — owner lock is driven by the explicit owner-locked flag, NOT
    // the presence of a kiosk PIN. A fresh owner has no flag (full access);
    // tapping lock SETS the flag (+ clears any stored PIN so modules re-gate),
    // and the owner drops to Inspections-only until they enter a PIN. Tapping
    // unlock runs the PIN challenge; on a valid Primary PIN, submitPinChallenge
    // clears the flag and fully re-elevates (a non-primary PIN leaves the lock
    // engaged but scopes the owner to that PIN's module subset).
    if (!isOwnerDeviceLocked()) {
      // Currently unlocked — lock this device into PIN scope.
      setOwnerDeviceLock(true);
      _resetSidebarDim(); // TASK-1224 — dim is per-session; a lock is a handoff
      clearPinAccess(); // drops stored PIN + re-renders nav + navigates to pretrip
      updateLockFab();
      // TASK-763 — a plain click locks very easily and locking is consequential
      // (owner drops to Inspections-only + needs a PIN to come back). Soften the
      // foot-gun: make an accidental click obvious + reversible (UNLOCK is already
      // PIN-gated so it's recoverable, not dangerous — no confirm dialog per Chris).
      // TASK-1201 — copy updated: the LOGO now navigates; the lock lives on the PILL.
      showToast('Locked — click the lock pill or enter your PIN to unlock', 'info');
    } else {
      // Currently locked — challenge for a PIN to unlock.
      showPinChallenge(null, (ok) => {
        if (ok) { _resetSidebarDim(); updateNavForPinAccess(); } // TASK-1224
        updateLockFab();
      });
    }
    return;
  }
  // Non-owner kiosk user (no member doc / legacy PIN-only): unchanged —
  // presence of a stored PIN means unlocked.
  const hasStoredPin = tenantId && localStorage.getItem(`dispatchdeck_pin_${tenantId}`);
  if (hasStoredPin) {
    // Currently unlocked — lock immediately
    _resetSidebarDim(); // TASK-1224
    clearPinAccess();
  } else {
    // Currently locked — show PIN challenge
    showPinChallenge(null, (ok) => {
      if (ok) { _resetSidebarDim(); updateNavForPinAccess(); } // TASK-1224
    });
  }
}

// TASK-763 → TASK-1201 SPLIT — the sidebar brand used to be the click-to-lock
// toggle. Now:
//   • the LOGO (brand) NAVIGATES — deep-link to the Compliance module's
//     Company tab — for any signed-in office user; access is evaluated AT
//     CLICK TIME via checkModuleAccess('compliance', quiet) and the click is a
//     silent no-op without it (a locked-down or module-less user sees nothing).
//     Driver-PIN / public-access sessions keep an inert logo.
//   • the status PILL keeps the DEVICE LOCK — same eligibility as before
//     (owner/legacy-admin PIN-kiosk path only; demo/driver/public and TASK-319
//     members-with-modules never get a pill), same toggleDeviceLock semantics,
//     desktop-only at click time (mobile keeps the FAB).
// Idempotent: re-running rewires cleanly (called on login AND re-init at sign-in).
function _brandNavigateToCompanyProfile() {
  // Guards re-checked at click time — state may have changed since wiring.
  if (window.AppState?.driverMode || window.AppState?.isPublicAccess) return;
  // No compliance access (module not granted, PIN-locked out, compliance
  // hidden) → no-op. quiet=true: no toast/log spam from a logo click.
  // === true, NOT truthy (2nd-COD Code Reviewer): checkModuleAccess returns
  // 'needs-pin' for a device-locked owner — truthy would fire a PIN challenge
  // off a logo click instead of the silent no-op this is specced to be.
  if (typeof checkModuleAccess !== 'function' || checkModuleAccess('compliance', true) !== true) return;
  window._complianceDeepLinkTab = 'profile';
  if (window.AppState?.currentView === 'compliance') {
    // Already on Compliance — just flip to the Company tab.
    window._complianceDeepLinkTab = null;
    window.ComplianceModule?.setTab?.('profile');
    return;
  }
  navigateToView('compliance');
}

function initLockSidebarLogo() {
  const brand = document.querySelector('#app-sidebar .sidebar-brand');
  if (!brand) return;

  // Reset any prior wiring (idempotent re-init / state reset) so we never
  // double-attach a handler or leave a stale pill across login/tenant-switch.
  brand.querySelector('.sidebar-lock-pill')?.remove();
  brand.classList.remove('lock-clickable', 'brand-nav');
  brand.onclick = null;
  brand.onkeydown = null;
  brand.removeAttribute('role');
  brand.removeAttribute('tabindex');
  brand.removeAttribute('aria-pressed');
  brand.removeAttribute('aria-label');

  // ── LOGO = NAVIGATE (TASK-1201). Wire for every signed-in office session
  // (incl. TASK-319 members and demo); the click-time access check no-ops it
  // for users without the compliance module. Any viewport width — navigating
  // from the mobile drawer is harmless (unlike locking, which stays desktop).
  if (!window.AppState?.driverMode && !window.AppState?.isPublicAccess) {
    brand.classList.add('brand-nav');
    brand.setAttribute('role', 'button');
    brand.setAttribute('tabindex', '0');
    brand.setAttribute('aria-label', 'Open company compliance profile');
    // Assign (not addEventListener) so re-init can't double-attach.
    brand.onclick = () => { _brandNavigateToCompanyProfile(); };
    brand.onkeydown = (e) => {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        _brandNavigateToCompanyProfile();
      }
    };
  }

  // ── PILL = LOCK. Skip for demo, driver mode, public access (match initLockFab).
  if (window.AppState?.isDemo || window.AppState?.driverMode || window.AppState?.isPublicAccess) {
    updateLockFab();
    return;
  }
  // TASK-319 multi-user members — explicit modules, PIN/lock has no role. Skip
  // (owners + legacy admins with no modules field fall through).
  const memberAccess = window.AppState?.memberAccess;
  if (memberAccess && memberAccess.status === 'active'
      && memberAccess.role !== 'owner'
      && Array.isArray(memberAccess.modules)
      && memberAccess.modules.length > 0) {
    updateLockFab();
    return;
  }

  // lock-clickable marks "this brand carries a live lock state" — updateLockFab
  // gates its locked-art/pill mutations on it (a staff member's logo must never
  // swap to the locked art off non-owner PIN state — COD Code Reviewer, TASK-763).
  brand.classList.add('lock-clickable');

  // The PILL is the lock control (desktop-only at click time — on mobile the
  // sidebar is behind the hamburger and the FAB owns locking; CSS hides the
  // pill there). stopPropagation so a pill click never ALSO navigates.
  const isDesktopWidth = () => window.matchMedia('(min-width: 769px)').matches;
  const pill = document.createElement('span');
  pill.className = 'sidebar-lock-pill';
  pill.setAttribute('role', 'button');
  pill.setAttribute('tabindex', '0');
  pill.innerHTML = '<span class="material-icons">lock_open</span><span class="sidebar-lock-pill-label">Unlocked</span>';
  pill.onclick = (e) => {
    e.stopPropagation();
    if (isDesktopWidth()) toggleDeviceLock();
  };
  pill.onkeydown = (e) => {
    if (!isDesktopWidth()) return;
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      e.stopPropagation();
      toggleDeviceLock();
    }
  };
  brand.appendChild(pill);

  updateLockFab();
}

// Update FAB icon to reflect current lock state
function updateLockFab() {
  const tenantId = window.AppState?.tenant;
  const isOwner = window.AppState?.memberAccess?.role === 'owner';

  // TASK-719 — for an OWNER, the lock state reflects the explicit owner-locked
  // flag (not the kiosk PIN). A default owner is UNLOCKED → shows the open
  // padlock, NOT the misleading red closed padlock the old hasStoredPin logic
  // produced. Non-owner kiosk users keep the existing hasStoredPin semantics.
  // TASK-1107 — zero access-PINs: the lock is unenforceable (see
  // deviceLockUnenforceable), so render always-UNLOCKED rather than a hollow
  // "LOCKED" badge that asserts a protection the access layer doesn't apply.
  const unlocked = deviceLockUnenforceable()
    ? true
    : (isOwner
      ? !isOwnerDeviceLocked()
      : !!(tenantId && localStorage.getItem(`dispatchdeck_pin_${tenantId}`)));

  // FAB (mobile). Null-check — the FAB may be absent (skipped tenants).
  const fab = document.getElementById('lock-fab');
  if (fab) {
    const icon = fab.querySelector('.material-icons');
    if (icon) icon.textContent = unlocked ? 'lock_open' : 'lock';
    fab.title = unlocked ? 'Lock modules' : 'Unlock modules';
    fab.classList.toggle('unlocked', unlocked);
  }

  // TASK-763 — sidebar logo + status pill (desktop). All null-checked so an
  // absent brand/pill/logo is a no-op. ONLY mutate when the brand is actually
  // wired as the lock control (.lock-clickable) — initLockSidebarLogo SKIPS
  // wiring for demo/driver/public + TASK-319 members-with-modules, and those
  // users have no lock at all; without this guard updateLockFab would swap a
  // staff member's logo to the locked art off non-owner PIN state (COD Code
  // Reviewer). The standalone unlocked-default logo lives in index.html, so a
  // skipped/un-wired brand correctly stays on /logo-sm.png untouched.
  const brand = document.querySelector('#app-sidebar .sidebar-brand');
  if (brand && brand.classList.contains('lock-clickable')) {
    brand.classList.toggle('locked', !unlocked);
    const logo = brand.querySelector('.sidebar-logo');
    if (logo) setSidebarLogoSrc(logo, unlocked ? '/logo-sm.png' : '/logo-sm-locked.png'); // TASK-1322 — self-heal a transient miss with the CORRECT lock-state art
    const pill = brand.querySelector('.sidebar-lock-pill');
    if (pill) {
      // TASK-1201 — the PILL is the lock toggle now (the brand navigates), so
      // the toggle-state ARIA lives on the pill, not the brand.
      pill.classList.toggle('on', unlocked);
      pill.classList.toggle('off', !unlocked);
      pill.setAttribute('aria-pressed', String(!unlocked));
      pill.setAttribute('aria-label', unlocked ? 'Device unlocked — click to lock' : 'Device locked — click to unlock');
      const pillIcon = pill.querySelector('.material-icons');
      if (pillIcon) pillIcon.textContent = unlocked ? 'lock_open' : 'lock';
      const pillLabel = pill.querySelector('.sidebar-lock-pill-label');
      if (pillLabel) pillLabel.textContent = unlocked ? 'Unlocked' : 'Locked';
    }
  }
}

// TASK-763 — tear down the sidebar lock pill + reset the brand to its default
// (unlocked) art/affordance. Called wherever the lock FAB is torn down
// (sign-out, tenant-switch) so a stale "Locked" pill/logo can't survive into
// the next session or the login screen.
function teardownLockSidebarLogo() {
  const brand = document.querySelector('#app-sidebar .sidebar-brand');
  if (!brand) return;
  brand.querySelector('.sidebar-lock-pill')?.remove();
  brand.classList.remove('lock-clickable', 'locked', 'brand-nav'); // brand-nav: TASK-1201 logo-navigate affordance
  brand.onclick = null;
  brand.onkeydown = null;
  brand.removeAttribute('role');
  brand.removeAttribute('tabindex');
  brand.removeAttribute('aria-pressed');
  brand.removeAttribute('aria-label');
  const logo = brand.querySelector('.sidebar-logo');
  if (logo) setSidebarLogoSrc(logo, '/logo-sm.png');
}

// TASK-1322 — set the sidebar logo to its INTENDED art and make it self-heal a
// transient fetch miss. A `firebase deploy` swaps every asset's ETag, so a bare
// <img> that misses once paints NOTHING forever until the user refreshes (Chris
// saw an empty brand block for ~1h after a deploy landed on an open tab). This
// retries THE SAME intended path once with a cache-buster (never the wrong lock-
// state art — the old inline-onerror proposal would've painted the unlocked logo
// on a locked device), then hides on a second miss (one-shot, no loop).
// dataset.retried is reset on every intentional src change so a later lock-state
// swap gets its own retry. The only two callers are updateLockFab + teardownLockSidebarLogo.
function setSidebarLogoSrc(logo, path) {
  if (!logo) return;
  logo.dataset.retried = '';
  logo.onerror = () => {
    if (logo.dataset.retried === '1') { logo.style.visibility = 'hidden'; return; }
    logo.dataset.retried = '1';
    logo.src = path + '?r=' + Date.now();
  };
  logo.style.visibility = '';
  logo.src = path;
}

// Expose PIN functions globally
window.showPinChallenge = showPinChallenge;
window.closePinChallenge = closePinChallenge;
window.submitPinChallenge = submitPinChallenge;
window.clearPinAccess = clearPinAccess;
window.isModuleUnlocked = isModuleUnlocked;
window.getUnlockedModules = getUnlockedModules;
window.updateNavForPinAccess = updateNavForPinAccess;
window.initLockFab = initLockFab;
// TASK-719 — owner device-lock state (read by other modules if ever needed).
window.isOwnerDeviceLocked = isOwnerDeviceLocked;
window.setOwnerDeviceLock = setOwnerDeviceLock;

// ============================================
// PASSWORD VISIBILITY TOGGLE
// ============================================
function togglePasswordVisibility(inputId, btn) {
  const input = document.getElementById(inputId);
  const icon = btn.querySelector('.material-icons');
  if (input.type === 'password') {
    input.type = 'text';
    icon.textContent = 'visibility';
  } else {
    input.type = 'password';
    icon.textContent = 'visibility_off';
  }
}
window.togglePasswordVisibility = togglePasswordVisibility;

// ============================================
// HELP CENTER (FAQ, Ask AI, Import Data)
// ============================================
let helpModalState = {
  activeTab: 'faq',
  importState: null
};

function showHelpModal(initialTab = 'faq') {
  // Close more menu if open
  const moreMenu = document.getElementById('mobile-more-menu');
  if (moreMenu) moreMenu.classList.add('hidden');

  const isCompliance = isComplianceOnlyTenant();
  const partner = ((window.AppState?.tenantData?.compliancePartner || 'your compliance advisor').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;'));

  // Always recreate modal to ensure fresh HTML (fixes caching issues)
  let modal = document.getElementById('modal-help');
  if (modal) {
    modal.remove();
    modal = null;
  }
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'modal-help';
    modal.className = 'modal hidden';
    modal.innerHTML = `
      <div class="modal-content modal-large help-modal">
        <div class="modal-header">
          <h3><span class="material-icons" style="vertical-align:middle;margin-right:8px;">${isCompliance ? 'verified' : 'help'}</span>${isCompliance ? 'Compliance Help Center' : 'Help Center'}</h3>
          <button class="btn-icon" onclick="document.getElementById('modal-help').classList.add('hidden')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="help-tabs">
          <button class="help-tab active" data-tab="faq" onclick="window.switchHelpTab('faq')">
            <span class="material-icons">quiz</span>
            <span>FAQ</span>
          </button>
          <button class="help-tab help-tab-shimmy" data-tab="ask-ai" onclick="window.switchHelpTab('ask-ai')">
            <span class="material-icons icon-dd-ai" aria-hidden="true"></span>
            <span>Get Help</span>
          </button>
          <button class="help-tab" data-tab="contact" onclick="window.switchHelpTab('contact')">
            <span class="material-icons">mail</span>
            <span>Contact</span>
          </button>
          ${isCompliance ? '' : `<button class="help-tab" data-tab="import" onclick="window.switchHelpTab('import')">
            <span class="material-icons">upload_file</span>
            <span>Import Data</span>
          </button>
          <button class="help-tab" onclick="window.isTWA() ? (window.location.href='/marketing/academy.html') : window.open('/marketing/academy.html','_blank')">
            <span class="material-icons">school</span>
            <span>Academy</span>
          </button>`}
        </div>
        <div class="modal-body help-body">
          <!-- FAQ Tab -->
          <div class="help-panel active" id="help-panel-faq">
            <div class="faq-search">
              <span class="material-icons">search</span>
              <input type="search" id="faq-search-input" placeholder="Search FAQs..." autocomplete="off" oninput="window.filterFaqs(this.value)">
            </div>
            <div class="faq-list" id="faq-list"></div>
          </div>

          <!-- Ask AI Tab -->
          <div class="help-panel" id="help-panel-ask-ai">
            <div class="ai-chat-window" id="help-ai-chat-window">
              <div class="ai-message ai-intro" id="help-ai-intro-message">
                <div class="ai-avatar">
                  <span class="material-icons icon-dd-ai" aria-hidden="true"></span>
                </div>
                <div class="ai-bubble">
                  <span id="help-ai-typing-text"></span><span class="help-ai-cursor">|</span>
                </div>
              </div>
            </div>
            <div class="ai-faq-suggestions" id="help-ai-faq-suggestions"></div>
            <div class="ai-input-area">
              <textarea id="help-ai-input" data-maxlen="50000" rows="2" placeholder="Type your question here..." oninput="window.updateHelpAIFaqSuggestions()" onkeydown="if(event.key==='Enter'&&!event.shiftKey){event.preventDefault();window.askHelpAI();}"></textarea>
              <button class="btn btn-primary ai-send-btn" onclick="window.askHelpAI()" id="help-ai-submit">
                <span class="material-icons">send</span>
              </button>
            </div>
            <div class="ai-transcript-notice" role="note" style="font-size:0.72rem;line-height:1.4;color:var(--text-secondary);padding:4px 2px 0;">${escapeHtml(aiTranscriptNoticeText(aiHistoryViewerIsOwner()))}</div>
          </div>

          <!-- Import Data Tab -->
          <div class="help-panel" id="help-panel-import">
            <div class="import-container" id="import-container">
              <!-- Upload step -->
              <div id="import-step-upload">
                <div style="text-align:center;padding:var(--spacing-lg) var(--spacing-md);">
                  <span class="material-icons" style="font-size:56px;color:var(--brand-blue);margin-bottom:var(--spacing-sm);">upload_file</span>
                  <h4 style="margin-bottom:var(--spacing-xs);">Send Us Your Fleet Data</h4>
                  <p style="color:var(--text-secondary);font-size:0.9rem;max-width:360px;margin:0 auto var(--spacing-lg);">
                    Send us one spreadsheet — your people, vehicles or customers — and we'll load it for you. Typically 3–5 business days. Need a whole migration done fast? That's what Concierge onboarding is for.
                  </p>
                </div>
                <div class="import-dropzone" id="import-dropzone" ondragover="window.handleImportDragOver(event)" ondragleave="window.handleImportDragLeave(event)" ondrop="window.handleImportDrop(event)">
                  <span class="material-icons">cloud_upload</span>
                  <p>Drag & drop your file here</p>
                  <p class="text-muted">or</p>
                  <button class="btn btn-secondary" onclick="document.getElementById('import-file-input').click()">Browse Files</button>
                  <input type="file" id="import-file-input" accept=".csv,.xlsx,.xls,.pdf,.png,.jpg,.jpeg,.webp,.heic" multiple hidden onchange="window.handleImportFileSelect(event)">
                </div>
                <p class="import-hint">Excel, CSV, PDF, or images. Up to 10MB per file.</p>
                <div id="import-file-list" style="margin-top:var(--spacing-sm);"></div>
                <div class="form-group" style="margin-top:var(--spacing-md);">
                  <label for="import-notes">Notes (optional)</label>
                  <textarea id="import-notes" class="form-control" rows="3" placeholder="Anything we should know? e.g. 'Tabs 1-2 are trucks, tab 3 is trailers, ignore the rest'"></textarea>
                </div>
                <button class="btn btn-primary" id="import-submit-btn" onclick="window.submitImportRequest()" disabled style="width:100%;margin-top:var(--spacing-sm);">
                  <span class="material-icons">send</span> Send to DispatchDeck Team
                </button>
              </div>

              <!-- Success step -->
              <div id="import-step-complete" class="hidden">
                <div class="import-complete">
                  <span class="material-icons import-success-icon">check_circle</span>
                  <h3>Files Received!</h3>
                  <p id="import-complete-message">We'll load your data and let you know when it's ready — typically 3–5 business days.</p>
                  <button class="btn btn-primary" onclick="window.closeHelpModal()">Done</button>
                </div>
              </div>
            </div>
          </div>

          <!-- Academy Tab -->
          <div class="help-panel" id="help-panel-academy">
            <div class="academy-grid" id="academy-grid"></div>
            <div style="text-align:center;padding:var(--spacing-md) 0 var(--spacing-lg);">
              <a href="/marketing/academy.html" target="${academyTarget()}" style="color:var(--brand-cyan);font-weight:500;text-decoration:none;font-size:0.9rem;">
                <span class="material-icons" style="vertical-align:middle;font-size:16px;margin-right:4px;">open_in_new</span>
                Open Full Academy
              </a>
            </div>
          </div>

          <!-- Contact Support Tab -->
          <div class="help-panel" id="help-panel-contact">
            <div class="contact-support-container">
              <div class="contact-header">
                <span class="material-icons" style="font-size:48px;color:var(--brand-blue);">support_agent</span>
                <h3>Contact Support</h3>
                <p>Have a question, feedback, or need help? Send us a message and we'll get back to you.</p>
              </div>
              <div id="phone-support-callout" class="phone-support-callout hidden">
                <div class="phone-support-inner">
                  <span class="material-icons">phone_in_talk</span>
                  <div>
                    <strong>Prefer to talk?</strong>
                    <a href="tel:+18304064458" class="phone-number">+1 (830) 406-4458</a>
                    <span class="phone-hint">24/7 phone support</span>
                  </div>
                </div>
              </div>
              <form id="contact-support-form" novalidate onsubmit="window.submitSupportMessage(event)">
                <div class="form-group">
                  <label for="contact-subject">Subject</label>
                  <select id="contact-subject" class="form-control" required>
                    <option value="">Select a topic...</option>
                    <option value="question">General Question</option>
                    <option value="bug">Bug Report</option>
                    <option value="feature">Feature Request</option>
                    <option value="billing">Billing Question</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div class="form-group">
                  <label for="contact-message">Message</label>
                  <textarea id="contact-message" class="form-control" rows="5" placeholder="Describe your question or issue..." required></textarea>
                </div>
                <div class="form-group">
                  <label for="contact-email">Reply Email (optional)</label>
                  <input type="email" id="contact-email" class="form-control" placeholder="Leave blank to use your account email">
                </div>
                <button type="submit" class="btn btn-primary" id="contact-submit-btn" style="width:100%;">
                  <span class="material-icons">send</span> Send Message
                </button>
              </form>
              <div id="contact-success-message" class="hidden" style="text-align:center;padding:var(--spacing-xl);">
                <span class="material-icons" style="font-size:64px;color:var(--action-success);">check_circle</span>
                <h3 style="margin:var(--spacing-md) 0;">Message Sent!</h3>
                <p style="color:var(--text-secondary);">We'll review your message and get back to you soon.</p>
                <button class="btn btn-secondary" onclick="window.resetContactForm()" style="margin-top:var(--spacing-md);">Send Another</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
    document.body.appendChild(modal);
  }

  // Reset import state when opening modal
  importUploadState.files = [];
  const importFileList = document.getElementById('import-file-list');
  if (importFileList) importFileList.innerHTML = '';
  const importSubmitBtn = document.getElementById('import-submit-btn');
  if (importSubmitBtn) { importSubmitBtn.disabled = true; importSubmitBtn.innerHTML = '<span class="material-icons">send</span> Send to DispatchDeck Team'; }
  const importUploadStep = document.getElementById('import-step-upload');
  if (importUploadStep) importUploadStep.classList.remove('hidden');
  const importCompleteStep = document.getElementById('import-step-complete');
  if (importCompleteStep) importCompleteStep.classList.add('hidden');
  const importNotes = document.getElementById('import-notes');
  if (importNotes) importNotes.value = '';

  // Show FAQ tab content
  populateFaqList();

  // Show modal and switch to initial tab
  modal.classList.remove('hidden');
  switchHelpTab(initialTab);

  // If opening Ask AI tab, start intro animation
  if (initialTab === 'ask-ai') {
    startHelpAIIntroAnimation();
  }
}

function switchHelpTab(tabName) {
  helpModalState.activeTab = tabName;

  // Update tab buttons
  document.querySelectorAll('.help-tab').forEach(tab => {
    tab.classList.toggle('active', tab.dataset.tab === tabName);
  });

  // Update panels
  document.querySelectorAll('.help-panel').forEach(panel => {
    panel.classList.toggle('active', panel.id === `help-panel-${tabName}`);
  });

  // Start AI intro if switching to Ask AI tab
  if (tabName === 'ask-ai') {
    // Check if user has roster access (Primary PIN required for AI)
    const hasAiAccess = window.AppState?.isDemo || isModuleUnlocked('roster');
    const aiPanel = document.getElementById('help-panel-ask-ai');

    if (!hasAiAccess && aiPanel) {
      // Show locked message
      aiPanel.innerHTML = `
        <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;height:100%;text-align:center;padding:var(--spacing-xl);">
          <span class="material-icons" style="font-size:64px;color:var(--text-muted);margin-bottom:var(--spacing-md);">lock</span>
          <h3 style="margin-bottom:var(--spacing-sm);color:var(--text-primary);">Primary PIN Required</h3>
          <p style="color:var(--text-secondary);margin-bottom:var(--spacing-lg);max-width:300px;">
            The help assistant is available for admins with the Primary PIN. This helps keep your account information secure.
          </p>
          <button class="btn btn-primary" onclick="showPinChallenge('Help Access', (ok) => { if(ok) switchHelpTab('ask-ai'); })">
            <span class="material-icons">lock_open</span>
            Enter Primary PIN
          </button>
          <p style="color:var(--text-secondary);font-size:0.85rem;margin-top:var(--spacing-md);">
            ${pinRecoveryLinkHtml()}
          </p>
        </div>
      `;
      return;
    }

    // Access granted — if the panel was previously replaced with the lock screen,
    // rebuild the chat UI so it works without requiring the user to close/reopen the modal.
    if (aiPanel && !document.getElementById('help-ai-input')) {
      aiPanel.innerHTML = `
        <div class="ai-chat-window" id="help-ai-chat-window">
          <div class="ai-message ai-intro" id="help-ai-intro-message">
            <div class="ai-avatar">
              <span class="material-icons icon-dd-ai" aria-hidden="true"></span>
            </div>
            <div class="ai-bubble">
              <span id="help-ai-typing-text"></span><span class="help-ai-cursor">|</span>
            </div>
          </div>
        </div>
        <div class="ai-faq-suggestions" id="help-ai-faq-suggestions"></div>
        <div class="ai-input-area">
          <textarea id="help-ai-input" data-maxlen="50000" rows="2" placeholder="Type your question here..." oninput="window.updateHelpAIFaqSuggestions()" onkeydown="if(event.key==='Enter'&&!event.shiftKey){event.preventDefault();window.askHelpAI();}"></textarea>
          <button class="btn btn-primary ai-send-btn" onclick="window.askHelpAI()" id="help-ai-submit">
            <span class="material-icons">send</span>
          </button>
        </div>
        <div class="ai-transcript-notice" role="note" style="font-size:0.72rem;line-height:1.4;color:var(--text-secondary);padding:4px 2px 0;">${escapeHtml(aiTranscriptNoticeText(aiHistoryViewerIsOwner()))}</div>
      `;
    }

    const typingEl = document.getElementById('help-ai-typing-text');
    if (typingEl && !typingEl.textContent) {
      startHelpAIIntroAnimation();
    }
  }

  // Show phone support callout for paying customers on Contact tab
  if (tabName === 'contact') {
    const phoneCallout = document.getElementById('phone-support-callout');
    if (phoneCallout) {
      // Show only for paying customers (not demo, not trial)
      const showPhone = isProAccount() && !window.AppState?.isDemo;
      phoneCallout.classList.toggle('hidden', !showPhone);
    }
  }

  // Lazy-populate Academy grid on first visit
  if (tabName === 'academy') {
    populateAcademyGrid();
  }
}

function populateAcademyGrid() {
  const grid = document.getElementById('academy-grid');
  if (!grid || grid.children.length > 0) return; // already populated

  const episodes = [
    { id: 'welcome',    title: 'Getting Started',        icon: 'rocket_launch', duration: '3 min' },
    { id: 'dispatch',   title: 'Dispatch & Scheduling',  icon: 'calendar_month', duration: '3 min' },
    { id: 'driver-pin', title: 'Driver Code & Job Board', icon: 'pin',            duration: '2 min' },
    { id: 'dvir',       title: 'Inspections',            icon: 'checklist',      duration: '3 min' },
    { id: 'roster',     title: 'Managing Your Roster',   icon: 'groups',         duration: '3 min' },
    { id: 'masstext',   title: 'Texting Your Crew',      icon: 'sms',            duration: '2 min' },
    { id: 'maintenance', title: 'Maintenance & PMs',     icon: 'build',          duration: '3 min' },
    { id: 'clock',       title: 'Clock & Billing',       icon: 'schedule',       duration: '3 min' },
    { id: 'crm',         title: 'CRM & Price Books',     icon: 'handshake',      duration: '3 min' },
    { id: 'hr',          title: 'HR & Compliance',       icon: 'badge',          duration: '3 min' },
    { id: 'bi',          title: 'BI Dashboard',          icon: 'insights',       duration: '2 min' },
    { id: 'timeoff',     title: 'Time Off Requests',     icon: 'event_busy',     duration: '2 min' },
    { id: 'billing',     title: 'AR Billing & Invoices', icon: 'request_quote',  duration: '3 min' },
    { id: 'tickets-po',  title: 'Tickets & POs',         icon: 'receipt',        duration: '2 min' },
    { id: 'ap',          title: 'Accounts Payable',      icon: 'receipt_long',   duration: '3 min' },
    { id: 'compliance',  title: 'Compliance Tracker',    icon: 'verified_user',  duration: '3 min' },
    { id: 'fieldtickets',title: 'Field Tickets & Piece Rate', icon: 'draw',     duration: '3 min' },
    { id: 'portal',      title: 'Customer Portal & Orders', icon: 'storefront', duration: '3 min' },
  ];

  grid.innerHTML = episodes.map(ep => ep.coming ? `
    <div class="academy-card academy-card-coming" style="opacity:0.6;cursor:default;">
      <div class="academy-card-icon" style="background:var(--bg-secondary);">
        <span class="material-icons">${ep.icon}</span>
      </div>
      <div class="academy-card-info">
        <span class="academy-card-title">${ep.title}</span>
        <span class="academy-card-duration" style="color:var(--accent-teal);font-weight:600;">Coming Soon</span>
      </div>
    </div>
  ` : `
    <a href="/marketing/academy.html?episode=${ep.id}" target="${academyTarget()}" class="academy-card">
      <div class="academy-card-icon">
        <span class="material-icons">${ep.icon}</span>
      </div>
      <div class="academy-card-info">
        <span class="academy-card-title">${ep.title}</span>
        <span class="academy-card-duration"><span class="material-icons" style="font-size:14px;vertical-align:middle;margin-right:2px;">schedule</span>${ep.duration}</span>
      </div>
    </a>
  `).join('');
}

function videoLink(episode, label) {
  return ` <a href="/marketing/academy.html?episode=${episode}" target="${academyTarget()}" class="faq-video-link"><span class="material-icons">play_circle</span> ${label}</a>`;
}

function populateFaqList() {
  if (!FAQ_DATA.length) { ensureFaqData().then(() => populateFaqList()).catch(() => {}); return; } // rel2089 lazy FAQ
  const container = document.getElementById('faq-list');
  if (!container) return;

  // Compliance-only tenants get a tailored FAQ
  if (isComplianceOnlyTenant()) {
    const partner = ((window.AppState?.tenantData?.compliancePartner || 'your compliance advisor').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;'));
    const complianceFaqs = [
      { q: "What am I looking at?", a: `This is your compliance portal, managed by <strong>${partner}</strong>. Your advisor handles DOT compliance, driver qualification files, vehicle inspections, and safety documentation on your behalf. The Compliance tab is your home base — it shows the health of your fleet and crew at a glance.` },
      { q: "What are the greyed-out modules?", a: "The modules you see with lock icons (Dispatch, Maintenance, Inspections, Billing, BI) are additional fleet management tools available with a DispatchDeck subscription. You can click any of them to preview what they look like with your actual data — they're fully functional previews, not screenshots. Compliance, HR, and Roster are open to you so you and your advisor can keep your driver and vehicle records up to date." },
      { q: "What does each module do?", a: "<strong>Compliance</strong> — Driver qualification files, expiring docs, MVR/Clearinghouse tracking. (Open to you)<br><strong>HR</strong> — Employee files, hiring pipeline, document signing, safety meetings. (Open to you)<br><strong>Roster</strong> — Manage your people, vehicles, trailers, and equipment inventory. (Open to you so you can keep your fleet roster current)<br><strong>Dispatch</strong> — Schedule jobs, assign drivers and equipment, notify crew via SMS.<br><strong>Maintenance</strong> — Track work orders, PM schedules, and fleet health.<br><strong>Inspections</strong> — Digital DVIRs, JSAs, and equipment inspections with DOT-compliant reports.<br><strong>Billing</strong> — Field tickets, invoicing, QuickBooks integration, online payments.<br><strong>BI</strong> — Business intelligence dashboard with P&L, profitability, and financial reports." },
      { q: "How do I upgrade to the full platform?", a: "DispatchDeck is <strong>$250/mo</strong> flat — every module: Dispatch, Roster, Maintenance, Inspections, Clock & Billing, CRM, HR, BI Dashboard, Smart Tools, and more. Unlimited users and up to 50 vehicles (+$25/mo per additional 50). Contact us through the Contact tab or ask your compliance advisor to get started." },
      { q: "How does the Compliance tab work?", a: "The Compliance tab shows health cards for every person and vehicle with color-coded status: <span style='color:#dc2626;font-weight:600;'>Red</span> (critical — expired or safety issue), <span style='color:#d97706;font-weight:600;'>Yellow</span> (warning — expiring within 30 days), <span style='color:#16a34a;font-weight:600;'>Green</span> (all compliant). Click any card for full details including cert expiry dates, open defects, and maintenance holds." },
      { q: "How do I upload compliance documents?", a: `Your compliance advisor at ${partner} handles document uploads and management. If you have documents to submit (CDLs, medical cards, insurance certs, etc.), contact your advisor directly or use the Contact tab to send them to us.` },
      { q: "Who manages my account?", a: `Your account is managed by <strong>${partner}</strong>. They handle your compliance documentation, driver qualification files, and safety records. For questions about your compliance status, contact your advisor directly. For questions about the DispatchDeck platform, use the Get Help tab or Contact tab.` },
      { q: "What is a DOT Audit Report?", a: "The DOT Audit Report in the Compliance tab generates a full DVIR-to-work-order audit trail for your fleet. Choose Fleet Summary (all vehicles, defect-only) or Per-Vehicle Audit (every inspection for one unit). It traces every DVIR defect to its maintenance resolution — exactly what a DOT auditor needs for 49 CFR 396.11 compliance." },
      { q: "What do the compliance colors mean?", a: "<span style='color:#dc2626;font-weight:600;'>Red (Critical)</span>: CDL/Med Card expired, open safety defects, or active safety-priority maintenance.<br><span style='color:#d97706;font-weight:600;'>Yellow (Warning)</span>: Cert expiring within 30 days, missed DVIRs, or recent defects.<br><span style='color:#16a34a;font-weight:600;'>Green (Good)</span>: Everything valid with more than 30 days to expiry." },
      { q: "Can I see my actual fleet data in the locked modules?", a: "Yes! Click any greyed-out module to see a live preview with your real data. You can browse around and see exactly how DispatchDeck would work for your operation. The only difference is you'll see a subscribe prompt — dismiss it with 'Keep Browsing' to keep exploring." },
      { q: "How much does DispatchDeck cost per truck?", a: "There is no per-truck fee. DispatchDeck is $250/mo flat with unlimited users and up to 50 vehicles (trucks and trailers both count); past 50, the account owner adds another 50 for $25/mo under Account → Vehicles. For a 20-truck fleet, that works out to $12.50/truck/month — a fraction of what competitors charge per truck." },
    ];

    container.innerHTML = complianceFaqs.map((faq, i) => `
      <div class="faq-item${i === 0 ? ' expanded' : ''}" onclick="this.classList.toggle('expanded')">
        <div class="faq-question">
          <span>${faq.q}</span>
          <span class="material-icons faq-chevron">expand_more</span>
        </div>
        <div class="faq-answer">${faq.a}</div>
      </div>
    `).join('');
    return;
  }

  // TASK-500: derive the rendered FAQ list from the single canonical FAQ_DATA source
  // (answer-bearing entries, in canonical order). Was a hand-mirrored array — see FAQ_DATA below.
  // TASK-9994 (Chris 10/21: everything 811 disappears when a company has 811 off).
  const faqs = FAQ_LIST.map(_faqForTenant).filter(Boolean);

  container.innerHTML = faqs.map((faq, i) => `
    <div class="faq-item" onclick="this.classList.toggle('expanded')">
      <div class="faq-question">
        <span>${faq.q}</span>
        <span class="material-icons faq-chevron">expand_more</span>
      </div>
      <div class="faq-answer">${faq.a}</div>
    </div>
  `).join('');
}

// TASK-9994 — a Locates (811) / potential-job FAQ entry only shows when the
// company has 811 turned on (settings.enable811 === true; the demo always does).
const FAQ_811_RE = /\b811\b|\blocates?\b|one-call|potential (?:job|project)/i;
function _faqVisibleForTenant(faq) {
  if (!faq) return false;
  if (window.AppState?.settings?.enable811 === true || window.AppState?.isDemo === true) return true;
  // COD r1: the answer counts too (an entry about something else that names 811 in passing is reworded by _faqForTenant).
  return !FAQ_811_RE.test(String(faq.q || '')) && !FAQ_811_RE.test(String(faq.a || ''));
}
// The FAQ list: hide an 811 entry; drop the 811 clause from the two general
// answers that name it in passing (anything else still naming 811 is hidden).
function _faqForTenant(faq) {
  if (!faq) return null;
  if (window.AppState?.settings?.enable811 === true || window.AppState?.isDemo === true) return faq;
  if (FAQ_811_RE.test(String(faq.q || ''))) return null;
  const a = String(faq.a || '')
    .replace('811 locate permits (uploaded to jobs, linked to tickets) and heavy load/oversize permits', 'heavy load/oversize permits')
    .replace(', no equipment and no 811 tickets', ' and no equipment');
  if (FAQ_811_RE.test(a)) return null;
  return a === faq.a ? faq : { ...faq, a };
}
window._faqVisibleForTenant = _faqVisibleForTenant;

function closeHelpModal() {
  const modal = document.getElementById('modal-help');
  if (modal) modal.classList.add('hidden');
}

window.showHelpModal = showHelpModal;
window.switchHelpTab = switchHelpTab;
window.closeHelpModal = closeHelpModal;

function filterFaqs(query) {
  const faqItems = document.querySelectorAll('.faq-item');
  const searchTerm = query.toLowerCase().trim();

  faqItems.forEach(item => {
    const question = item.querySelector('.faq-question span')?.textContent?.toLowerCase() || '';
    const answer = item.querySelector('.faq-answer')?.textContent?.toLowerCase() || '';

    if (!searchTerm || question.includes(searchTerm) || answer.includes(searchTerm)) {
      item.style.display = '';
    } else {
      item.style.display = 'none';
    }
  });
}
window.filterFaqs = filterFaqs;

// ============================================
// HELP CENTER - CONTACT SUPPORT TAB
// ============================================
const HELP_REQUEST_SAFE_CODES = new Set(['functions/invalid-argument', 'functions/resource-exhausted', 'functions/failed-precondition']);

async function submitSupportMessage(e) {
  e.preventDefault();
  if (!window.validateRequiredFields(e.target)) return;

  const subjectSelect = document.getElementById('contact-subject');
  const messageInput = document.getElementById('contact-message');
  const emailInput = document.getElementById('contact-email');
  const submitBtn = document.getElementById('contact-submit-btn');

  const subject = subjectSelect.value;
  const message = messageInput.value.trim();

  if (!subject || !message) {
    showToast('Please fill in all required fields', 'error');
    return;
  }

  // Show loading state
  submitBtn.disabled = true;
  submitBtn.innerHTML = '<span class="material-icons rotating">sync</span> Sending...';

  try {
    const tenant = window.AppState?.tenant;
    if (!tenant) throw new Error('no tenant');

    // TASK-9928 — the server writes _helpRequests (the browser never could:
    // no firestore.rules stanza). Tenant, account email and tenant name are
    // bound server-side; only the reply email the user typed is sent.
    await httpsCallable(functions, 'submitHelpCenterRequest')({
      tenantId: tenant,
      kind: 'contact',
      type: subject,
      message: message,
      replyEmail: emailInput.value.trim(),
      // Include browser/device info for debugging
      userAgent: navigator.userAgent,
      pageUrl: window.location.href
    });

    // Show success message
    document.getElementById('contact-support-form').classList.add('hidden');
    document.getElementById('contact-success-message').classList.remove('hidden');

    showToast('Message sent! We\'ll review it soon.', 'success');

  } catch (error) {
    console.error('[Contact] Error submitting support message:', error?.code || error);
    // TASK-9928 — these three carry a plain-English server message; anything else does not.
    const why = HELP_REQUEST_SAFE_CODES.has(error?.code) ? error.message : 'Please try again.';
    showToast(`Message not sent. ${why}`, 'error');
  } finally {
    submitBtn.disabled = false;
    submitBtn.innerHTML = '<span class="material-icons">send</span> Send Message';
  }
}

function resetContactForm() {
  const form = document.getElementById('contact-support-form');
  const successMsg = document.getElementById('contact-success-message');

  if (form) {
    form.reset();
    form.classList.remove('hidden');
  }
  if (successMsg) {
    successMsg.classList.add('hidden');
  }
}

window.submitSupportMessage = submitSupportMessage;
window.resetContactForm = resetContactForm;

// ============================================
// HELP CENTER - ASK AI TAB
// ============================================
function startHelpAIIntroAnimation() {
  const typingEl = document.getElementById('help-ai-typing-text');
  const cursorEl = document.querySelector('.help-ai-cursor');
  if (!typingEl) return;

  // Hide cursor, show typing dots
  if (cursorEl) cursorEl.style.display = 'none';
  typingEl.innerHTML = `<span class="thinking-dots"><span>.</span><span>.</span><span>.</span></span>`;

  // After a brief delay, show the full message instantly
  setTimeout(() => {
    typingEl.innerHTML = `<strong>Howdy, partner!</strong> I'm your DispatchDeck trail guide. Ask me anything about scheduling jobs, running inspections, or keeping your fleet in shape. What can I help you with?`;

    const chatWindow = document.getElementById('help-ai-chat-window');
    if (chatWindow) chatWindow.scrollTop = chatWindow.scrollHeight;

    showHelpAIInitialSuggestions();
  }, 800);
}

function showHelpAIInitialSuggestions() {
  const container = document.getElementById('help-ai-faq-suggestions');
  if (!container) return;

  const popularQuestions = [
    "How do I add a new job?",
    "How do I assign a driver?",
    "How do inspections work?",
    "What features are coming?"
  ];

  container.innerHTML = `
    <div class="faq-label">Quick questions:</div>
    <div class="faq-chips">
      ${popularQuestions.map(q => `
        <button class="faq-chip" onclick="window.selectHelpAIQuestion('${q.replace(/'/g, "\\'")}')">
          ${q}
        </button>
      `).join('')}
    </div>
  `;
}

function updateHelpAIFaqSuggestions() {
  if (!FAQ_DATA.length) { ensureFaqData().then(() => updateHelpAIFaqSuggestions()).catch(() => {}); return; } // rel2089 lazy FAQ
  const input = document.getElementById('help-ai-input');
  const container = document.getElementById('help-ai-faq-suggestions');
  if (!input || !container) return;

  const query = input.value.toLowerCase().trim();

  if (query.length < 2) {
    showHelpAIInitialSuggestions();
    return;
  }

  const matches = AI_HELP_FAQ.filter(faq => { if (!_faqVisibleForTenant(faq)) return false; // TASK-9994
    return faq.keywords.some(kw => query.includes(kw)) ||
           faq.q.toLowerCase().includes(query);
  }).slice(0, 4);

  if (matches.length === 0) {
    container.innerHTML = '';
    return;
  }

  container.innerHTML = `
    <div class="faq-label">Related questions:</div>
    <div class="faq-chips">
      ${matches.map(faq => `
        <button class="faq-chip" onclick="window.selectHelpAIQuestion('${faq.q.replace(/'/g, "\\'")}')">
          ${faq.q}
        </button>
      `).join('')}
    </div>
  `;
}

function selectHelpAIQuestion(question) {
  const input = document.getElementById('help-ai-input');
  if (input) {
    input.value = question;
    input.focus();
  }
  const container = document.getElementById('help-ai-faq-suggestions');
  if (container) container.innerHTML = '';
}

// TASK-9960 — "Didn't help? Talk to a person": one quiet button under each AI
// answer. Rendered ONLY when the server returned that turn's id (askAI returns
// `turnId` only when this caller's own transcript row was written — never in
// demo); no id, no button. One tap → flagAiTurnUnhelpful({turnId}) sets the
// server flag the morning digest counts; it stores no text. The reply points at
// the support contacts the Help modal's Contact tab already shows (same phone
// rule as switchHelpTab: paying, non-demo accounts only).
const HELP_AI_TURN_ID_RE = /^\d{14}_[0-9a-f]{12}$/;
function helpAIUnhelpfulButtonHTML(turnId) {
  if (typeof turnId !== 'string' || !HELP_AI_TURN_ID_RE.test(turnId)) return '';
  return `<div class="help-ai-unhelpful" style="min-width:0;">
    <button type="button" class="btn btn-secondary btn-sm" data-turn-id="${escapeHtmlBasic(turnId)}" onclick="window.flagHelpAITurnUnhelpful(this)"
      style="min-height:48px;padding:var(--spacing-sm) var(--spacing-md);border-radius:var(--radius-full);font-size:0.85rem;color:var(--text-primary);background:transparent;border:1px solid var(--border-color);display:inline-flex;align-items:center;gap:var(--spacing-sm);">
      <span class="material-icons" aria-hidden="true" style="font-size:18px;">support_agent</span>${escapeHtmlBasic("Didn't help? Talk to a person")}
    </button>
  </div>`;
}

async function flagHelpAITurnUnhelpful(btn) {
  const wrap = btn && btn.closest ? btn.closest('.help-ai-unhelpful') : null;
  const turnId = btn && btn.getAttribute ? btn.getAttribute('data-turn-id') : '';
  if (!wrap || !HELP_AI_TURN_ID_RE.test(turnId || '') || btn.disabled) return;
  btn.disabled = true;
  const label = btn.innerHTML;
  btn.textContent = 'Sending…';
  try {
    await httpsCallable(functions, 'flagAiTurnUnhelpful')({ turnId });
  } catch (err) {
    console.warn('[Help AI] flag failed:', err && err.code);
    btn.disabled = false;
    btn.innerHTML = label;
    showToast("Couldn't send that. Try again, or use the Contact tab.", 'error');
    return;
  }
  const showPhone = isProAccount() && !window.AppState?.isDemo;
  wrap.innerHTML = `<div role="status" style="font-size:0.85rem;line-height:1.5;color:var(--text-secondary);">
    ${escapeHtmlBasic("Thanks — we'll look at this. To reach us now:")}
    <div style="display:flex;flex-direction:column;align-items:flex-start;gap:var(--spacing-md);margin-top:var(--spacing-sm);">
      <button type="button" class="btn btn-secondary btn-sm" onclick="window.switchHelpTab('contact')"
        style="min-height:48px;border-radius:var(--radius-full);display:inline-flex;align-items:center;gap:var(--spacing-sm);">
        <span class="material-icons" aria-hidden="true" style="font-size:18px;">mail</span>Contact support
      </button>
      <a href="mailto:support@dispatchdeck.app" style="color:var(--text-primary);">support@dispatchdeck.app</a>
      ${showPhone ? '<a href="tel:+18304064458" style="color:var(--text-primary);">+1 (830) 406-4458</a>' : ''}
    </div>
  </div>`;
}
window.flagHelpAITurnUnhelpful = flagHelpAITurnUnhelpful;

// TASK-9956 S2 — web search in Help lives in the LAZY views/webResearchHelp.js
// (perf budget); these are its only eager hooks.
let _wrHelpLoad = null;
function _wrHelp() {
  if (!_wrHelpLoad) _wrHelpLoad = _lazyImportWithRetry(() => __ddChunkWrap(import('./views/webResearchHelp.js?v=99560001')), 'webResearchHelp')
    .then((m) => { m._bind({ httpsCallable, functions }); return m; })
    .catch((e) => { _wrHelpLoad = null; throw e; });
  return _wrHelpLoad;
}
function helpWebResearchMount(host, question) {
  const s = window.AppState?.tenantData?.settings || {};
  if (!host || s.webResearchEnabled !== true || s.smartToolsEnabled === false || window.AppState?.isDemo) return;
  _wrHelp().then((m) => m.mount(host, question, window.AppState)).catch(() => {});
}

async function askHelpAI() {
  const input = document.getElementById('help-ai-input');
  const chatWindow = document.getElementById('help-ai-chat-window');
  const submitBtn = document.getElementById('help-ai-submit');
  const faqContainer = document.getElementById('help-ai-faq-suggestions');

  const question = input?.value?.trim();
  if (!question) {
    showToast('Please enter a question', 'error');
    return;
  }
  if (question.length > ASK_AI_MAX_QUESTION_CHARS) {   // TASK-9301
    showToast(assistantTooLongMessage(question.length, ASK_AI_MAX_QUESTION_CHARS), 'error', null, 12000);
    return;
  }

  if (faqContainer) faqContainer.innerHTML = '';

  // Add user message
  const userMsg = document.createElement('div');
  userMsg.className = 'user-message';
  userMsg.innerHTML = `
    <div class="user-bubble">${escapeHtmlBasic(question)}</div>
    <div class="user-avatar">
      <span class="material-icons">person</span>
    </div>
  `;
  chatWindow.appendChild(userMsg);

  // Add AI thinking
  const aiThinking = document.createElement('div');
  aiThinking.className = 'ai-message ai-thinking';
  aiThinking.innerHTML = `
    <div class="ai-avatar">
      <span class="material-icons icon-dd-ai" aria-hidden="true"></span>
    </div>
    <div class="ai-bubble">
      <span class="thinking-dots"><span>.</span><span>.</span><span>.</span></span>
      <span style="color:var(--text-muted);margin-left:8px;">Thinkin' on that...</span>
    </div>
  `;
  chatWindow.appendChild(aiThinking);
  chatWindow.scrollTop = chatWindow.scrollHeight;

  input.value = '';
  submitBtn.disabled = true;

  try {
    // Get auth token for the request
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch('https://us-central1-dispatch-deck-be88c.cloudfunctions.net/askAI', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        data: {
          question,
          module: window.AppState.currentView || null,
          tenantId: window.AppState.tenant || null
        }
      })
    });

    const result = await response.json();

    aiThinking.remove();

    if (result.result?.ok && result.result?.answer) {
      const aiResponse = document.createElement('div');
      aiResponse.className = 'ai-message';
      // TASK-9960 — the bubble and its "Didn't help?" button stack in one column
      // (the button renders only when the server returned this turn's id).
      aiResponse.innerHTML = `
        <div class="ai-avatar">
          <span class="material-icons icon-dd-ai" aria-hidden="true"></span>
        </div>
        <div class="help-ai-answer-col" style="display:flex;flex-direction:column;align-items:flex-start;gap:var(--spacing-sm);min-width:0;">
          <div class="ai-bubble">${escapeHtmlBasic(result.result.answer).replace(/\n/g, '<br>')}</div>
          ${helpAIUnhelpfulButtonHTML(result.result.turnId)}
        </div>
      `;
      chatWindow.appendChild(aiResponse);
      helpWebResearchMount(aiResponse.querySelector?.('.help-ai-answer-col'), question); // TASK-9956 S2
    } else {
      throw new Error(result.error || 'No response received');
    }
  } catch (error) {
    console.error('[Help AI] Error:', error);
    aiThinking.remove();

    const displayMsg = "Well, shoot. Hit a snag there. Try again in a bit, partner.";
    const aiError = document.createElement('div');
    aiError.className = 'ai-message ai-error';
    aiError.innerHTML = `
      <div class="ai-avatar">
        <span class="material-icons icon-dd-ai" aria-hidden="true"></span>
      </div>
      <div class="ai-bubble" style="border-color:var(--status-danger);">${displayMsg}</div>
    `;
    chatWindow.appendChild(aiError);
  } finally {
    submitBtn.disabled = false;
    chatWindow.scrollTop = chatWindow.scrollHeight;
  }
}

window.updateHelpAIFaqSuggestions = updateHelpAIFaqSuggestions;
window.selectHelpAIQuestion = selectHelpAIQuestion;
window.askHelpAI = askHelpAI;

// ============================================
// HELP CENTER - IMPORT DATA TAB (Concierge Upload)
// ============================================
const importUploadState = { files: [] };

function handleImportDragOver(e) {
  e.preventDefault();
  e.stopPropagation();
  document.getElementById('import-dropzone')?.classList.add('drag-over');
}

function handleImportDragLeave(e) {
  e.preventDefault();
  e.stopPropagation();
  document.getElementById('import-dropzone')?.classList.remove('drag-over');
}

function handleImportDrop(e) {
  e.preventDefault();
  e.stopPropagation();
  document.getElementById('import-dropzone')?.classList.remove('drag-over');
  if (e.dataTransfer.files.length > 0) {
    addImportFiles(e.dataTransfer.files);
  }
}

function handleImportFileSelect(e) {
  if (e.target.files.length > 0) {
    addImportFiles(e.target.files);
  }
  e.target.value = '';
}

function addImportFiles(fileList) {
  for (const file of fileList) {
    if (file.size > 10 * 1024 * 1024) {
      showToast(`${file.name} is too large (max 10MB)`, 'error');
      continue;
    }
    if (importUploadState.files.length >= 10) {
      showToast('Maximum 10 files allowed', 'error');
      break;
    }
    importUploadState.files.push(file);
  }
  renderImportFileList();
}

function removeImportFile(idx) {
  importUploadState.files.splice(idx, 1);
  renderImportFileList();
}

function renderImportFileList() {
  const container = document.getElementById('import-file-list');
  const submitBtn = document.getElementById('import-submit-btn');
  if (!container) return;

  if (importUploadState.files.length === 0) {
    container.innerHTML = '';
    if (submitBtn) submitBtn.disabled = true;
    return;
  }

  if (submitBtn) submitBtn.disabled = false;

  const iconMap = {
    'csv': 'table_chart', 'xlsx': 'table_chart', 'xls': 'table_chart',
    'pdf': 'picture_as_pdf',
    'png': 'image', 'jpg': 'image', 'jpeg': 'image', 'webp': 'image', 'heic': 'image'
  };

  container.innerHTML = importUploadState.files.map((f, i) => {
    const ext = f.name.split('.').pop().toLowerCase();
    const icon = iconMap[ext] || 'description';
    const size = f.size < 1024 ? `${f.size} B` : f.size < 1048576 ? `${(f.size / 1024).toFixed(1)} KB` : `${(f.size / 1048576).toFixed(1)} MB`;
    return `<div style="display:flex;align-items:center;gap:8px;padding:6px 8px;background:var(--bg-secondary);border-radius:8px;margin-bottom:4px;">
      <span class="material-icons" style="font-size:20px;color:var(--brand-blue);">${icon}</span>
      <span style="flex:1;font-size:0.85rem;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escapeHtmlBasic(f.name)}</span>
      <span style="font-size:0.75rem;color:var(--text-muted);white-space:nowrap;">${size}</span>
      <button type="button" class="btn-icon" onclick="window.removeImportFile(${i})" title="Remove" style="color:#ef4444;padding:2px;">
        <span class="material-icons" style="font-size:18px;">close</span>
      </button>
    </div>`;
  }).join('');
}

async function submitImportRequest() {
  if (importUploadState.files.length === 0) return;

  const submitBtn = document.getElementById('import-submit-btn');
  submitBtn.disabled = true;
  submitBtn.innerHTML = '<span class="material-icons rotating">sync</span> Uploading...';

  try {
    const tenant = window.AppState?.tenant;

    // Gate on trial-expired or missing tenant — surface a clear subscribe prompt
    // instead of letting the storage 403 bubble up as a Firebase error toast.
    const trialStatus = window.AppState?.trialStatus || getTrialStatus();
    if (trialStatus?.status === 'expired' || !tenant) {
      submitBtn.disabled = false;
      submitBtn.innerHTML = '<span class="material-icons">send</span> Send to DispatchDeck Team';
      showToast('Subscription required to import data. Please subscribe to continue.', 'error');
      showTrialExpiredScreen();
      return;
    }

    const uploadedFiles = [];

    // Upload each file to Firebase Storage
    for (const file of importUploadState.files) {
      const fileName = `${Date.now()}_${file.name}`;
      const storageRef = ref(storage, `tenants/${tenant}/onboarding-uploads/${fileName}`);
      await uploadBytes(storageRef, file);
      // TASK-9928 — only the path goes to the server (no bearer download URL stored).
      uploadedFiles.push({ name: file.name, size: file.size, path: storageRef.fullPath });
    }

    // TASK-9928 — record the request server-side. The browser's direct
    // _helpRequests write was always refused by firestore.rules, so uploads
    // landed with nothing pointing at them and nobody was told.
    const notes = document.getElementById('import-notes')?.value?.trim() || '';
    await httpsCallable(functions, 'submitHelpCenterRequest')({
      tenantId: tenant,
      kind: 'data_import',
      notes,
      files: uploadedFiles,
      userAgent: navigator.userAgent,
      pageUrl: window.location.href
    });

    // Show success
    document.getElementById('import-step-upload').classList.add('hidden');
    document.getElementById('import-step-complete').classList.remove('hidden');

    // Reset state
    importUploadState.files = [];
    showToast('Files sent! We\'ll let you know when your data is loaded.', 'success');

  } catch (error) {
    console.error('[Import] Upload error:', error?.code || error);
    const fnErr = String(error?.code || '').startsWith('functions/');
    const why = !fnErr || HELP_REQUEST_SAFE_CODES.has(error.code) ? error.message : 'Please try again.';
    showToast(`Upload failed: ${why}`, 'error');
    submitBtn.disabled = false;
    submitBtn.innerHTML = '<span class="material-icons">send</span> Send to DispatchDeck Team';
  }
}

window.handleImportDragOver = handleImportDragOver;
window.handleImportDragLeave = handleImportDragLeave;
window.handleImportDrop = handleImportDrop;
window.handleImportFileSelect = handleImportFileSelect;
window.removeImportFile = removeImportFile;
window.submitImportRequest = submitImportRequest;

// === Canonical FAQ dataset (TASK-500) — single source of truth ===
// TASK-9644 — the data MOVED to functions/src/shared/helpFaq.js so the in-app Help bot
// (askAI) reads the SAME FAQ the Help Center shows. This file imports the GENERATED browser
// copy (scripts/build-shared.cjs). Edit FAQ entries in helpFaq.js, never here.
// Each entry is { q, a?, keywords?, ai?, category }. BOTH help surfaces derive from it:
//   - the rendered Get-Help FAQ list (populateFaqList) = entries WITH an `a`, in array order.
//   - the Ask-AI suggestion index (AI_HELP_FAQ) = entries WITH `keywords`, ordered by `ai`.
// rel2089: filled on first use by ensureFaqData() (dynamic import, own chunk), so the
// ~88 KB gzip FAQ is off the cold-load path. Every consumer awaits it and re-renders.
let FAQ_DATA = [];
let FAQ_LIST = [];
let AI_HELP_FAQ = [];
let _faqLoad = null;
function ensureFaqData() {
  if (!_faqLoad) {
    // ?v=9898003 (integ/2090) — TASK-9898 fleet FAQ + TASK-9433 + TASK-9888 Reset link + TASK-9567 Piece Rate FAQ + 2088.6 fuel-surcharge FAQ.
    // ?v=9917202 (integ/2098) — TASK-9724 supply cost switch FAQ (merged over 2097's 9917201).
    // ?v=9917205 (integ/2100) — TASK-9725 S2 PPE bill-to-customer FAQ merged over 9652 S2's 9917204.
    // ?v=9917952 (fix/notes-golden-orb-1019) — Notes FAQ describes the yellow orb again. ?v=9917951 (integ/2103) — TASK-9941 F S2 tablet-PIN FAQ (9917941, its own) merged over 2102 9917747. ?v=9917747 (integ/2102) — TASK-9584 HR-tier FAQ (9917741, its own) + TASK-9726 follow-up Used on jobs / tickets FAQ (9917746, its own) merged. ?v=9917741 (TASK-9584 HR-tier FAQ: no 'all features included'). Was ?v=9917727 (integ/2101) — TASK-9726 Supplies-by-job FAQ + TASK-9730 tracked-supply-on-a-job FAQ merged (9917726 was 9726's own) · ?v=9917726 TASK-9726 Supplies-by-job FAQ · ?v=9917403 (integ/2100) — TASK-9917 S4 personal-expense FAQ + 9725 S2 PPE billing FAQ + 9652 S2 portal FAQ merged (9917401/9917205/9917204 were each a lane's own).
    // ?v=10118001 (integ/2118: + TASK-10030 customer merge FAQ from integ/2116 10116001) · ?v=10117002 (integ/2117) — TASK-10034 NLE FAQ + TASK-10050 List toggles FAQ + TASK-10037 restore FAQ + TASK-10040/10040b missed clock-out FAQ (duplicate article removed), merged over main's 10114001 / 10040b's 10114101.
    // ?v=10118501 (TASK-9501) — Stops instead of Waypoints in the yard/saved-location FAQs. ?v=10124696 (TASK-4696). ?v=98910001 (TASK-9891). ?v=10269389 (TASK-9389/9986 who can manage logins / change Billing settings; also carries TASK-9891 S2 link-length FAQ, branch 98910003). ?v=100930001 (TASK-10093 driver Academy videos + forgot-PIN FAQ; integ/2122 carries all three). ?v=109882128 (integ/2128, above TASK-9694 S6's 102896946, TASK-9880 S2's 109880201 and TASK-9797's 102979797) — TASK-9880 S2 rented-equipment billing FAQ + TASK-10180 Locates list FAQ + TASK-9797 named slots FAQ + TASK-10188 job/ticket notes, signatures, when a ticket is made + TASK-10181 811 nudge FAQ. ?v=109882129 (integ/2128 addendum) — TASK-9672 Price Book follow-ups FAQ (item codes) merged over 109882128.
    _faqLoad = __ddChunkWrap(import('./shared/help-faq.js?v=1105010299')).then(({ buildFaqData }) => { // TASK-10299 Personalized Schedule → Notify Crew defaults to personalized texts (1105010299, above every READY value) · TASK-10295 Destinations wording (111610295, above 10288 111510288) · TASK-10288 move/cancel refusals FAQ 482/483 (111510288, above 10261 111410261) · TASK-10261 partner PEI how-to FAQ (111410261, above 2166 audit 111141104) · AI knowledge audit 11/4 (111141104, above 10249's 111130249) · TASK-10249 Approvals page paths (111130249, above main's 111129955) · TASK-10208 inspection export + PDFs FAQ (111120208) over main's 111119957 · 111119957: Simple Clock FAQ setting label · integ/2131 111119956: TASK-9653 S2 send_me_link FAQ + TASK-10123 QuickBooks setup FAQ path + TASK-9264 crew Materials Used switch FAQ (its own 111119264) + TASK-9952 FAQ refund for a closed pay period (111119952) + TASK-9631 Pin + Saved maps FAQ (111119632) over main's 111110206 = TASK-10206 WO shop invoice total + card fee FAQ / TASK-10205 WO Smart Scan PDF FAQ · integ/2129: TASK-10168 + TASK-10172-A FAQ
      FAQ_DATA = buildFaqData(STARTER_LIMITS, videoLink); // videoLink = the function declaration above (hoisted)
      // Derived: FAQ list source — answer-bearing entries in canonical (render) order.
      FAQ_LIST = FAQ_DATA.filter(e => e.a !== undefined);
      // Derived: Ask-AI suggestion index — keyword-bearing entries, sorted into the original Ask-AI match order.
      AI_HELP_FAQ = FAQ_DATA.filter(e => e.keywords !== undefined).slice().sort((a, b) => a.ai - b.ai);
      return FAQ_DATA;
    }).catch((err) => { _faqLoad = null; console.warn('[Help] FAQ failed to load:', err); throw err; });
  }
  return _faqLoad;
}
// Warm it once the app is idle so Help / Ask-AI open with the FAQ already there.
try {
  const _warmFaq = () => { ensureFaqData().catch(() => {}); };
  if (typeof window !== 'undefined' && 'requestIdleCallback' in window) window.requestIdleCallback(_warmFaq, { timeout: 15000 });
  else setTimeout(_warmFaq, 8000);
} catch (_) { /* never block boot on the FAQ */ }

// Simple HTML escape for chat messages
// Auto-compress images via canvas (for logo uploads)
function compressImage(file, maxBytes, maxDim) {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let w = img.width, h = img.height;
      if (w > maxDim || h > maxDim) {
        const scale = maxDim / Math.max(w, h);
        w = Math.round(w * scale);
        h = Math.round(h * scale);
      }
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      // Try decreasing quality until under maxBytes
      let quality = 0.85;
      const tryCompress = () => {
        canvas.toBlob((blob) => {
          if (!blob) { resolve(null); return; }
          if (blob.size <= maxBytes || quality <= 0.3) {
            resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' }));
          } else {
            quality -= 0.1;
            tryCompress();
          }
        }, 'image/jpeg', quality);
      };
      tryCompress();
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  });
}

window.compressImage = compressImage;

function escapeHtmlBasic(text) {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

// HTML escape utility
function escapeHtml(text) {
  if (!text) return '';
  const div = document.createElement('div');
  div.textContent = String(text);
  return div.innerHTML;
}

// Format phone number for display (e.g., +14051234567 -> (405) 123-4567)
function formatPhoneDisplay(phone) {
  if (!phone) return '';
  const digits = String(phone).replace(/\D/g, '');
  if (digits.length === 11 && digits[0] === '1') {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return phone; // Return as-is if can't format
}

// ============================================
// CONFIRM MODAL
// ============================================

// TASK-1342 (COD Code Reviewer ⚠️ + Edge Case Hunter) — the confirm modal is a
// SINGLETON. If a second showConfirm() opens while a first is still pending, the
// second overwrites the shared content/classes but the FIRST call's click
// listeners are still attached, so one click resolves BOTH promises `true` — the
// stale dialog silently "confirms" something the user never saw. Pre-existing,
// but the per-call class bookkeeping below makes it worse (classes from the
// superseded call would linger). Fix: a superseded confirm is force-resolved
// `false` (the safe/negative answer — never `true`) and its listeners torn down
// before the new one renders.
let _confirmInFlightCleanup = null;

function showConfirm(title, message, options = {}) {
  return new Promise((resolve) => {
    const modal = document.getElementById('confirm-modal');
    const titleEl = document.getElementById('confirm-modal-title');
    const messageEl = document.getElementById('confirm-modal-message');
    const confirmBtn = document.getElementById('confirm-modal-confirm');
    const cancelBtn = document.getElementById('confirm-modal-cancel');

    // Supersede any still-pending confirm (see note above) before touching shared DOM.
    if (_confirmInFlightCleanup) {
      const supersede = _confirmInFlightCleanup;
      _confirmInFlightCleanup = null;
      try { supersede(false); } catch (_) { /* never let a stale dialog block a new one */ }
    }

    // Set content
    titleEl.textContent = title;
    messageEl.textContent = message;
    confirmBtn.textContent = options.confirmText || 'Confirm';
    cancelBtn.textContent = options.cancelText || 'Cancel';

    // Optional icon override (e.g. success "check_circle" for a confirmation pop-out)
    const iconEl = modal.querySelector('.confirm-modal-icon');
    if (iconEl) {
      iconEl.textContent = options.icon || 'help_outline';
      iconEl.style.color = options.iconColor || '';
    }

    // hideCancel → single-button acknowledgement (success/info pop-out, not a choice)
    cancelBtn.style.display = options.hideCancel ? 'none' : '';

    // TASK-1342 (COD Code Reviewer ⚠️, round 3) — clear the PREVIOUS caller's custom
    // classes FIRST, before any built-in variant toggle below. If this ran after the
    // toggles and a caller had passed an internal token as `className`, the removal
    // would strip a class the current call had just legitimately set.
    const prevCustom = modal.dataset.confirmCustomClass;
    if (prevCustom) {
      prevCustom.split(' ').filter(Boolean).forEach((c) => modal.classList.remove(c));
      delete modal.dataset.confirmCustomClass;
    }

    // Set danger variant
    if (options.danger) {
      modal.classList.add('confirm-danger');
    } else {
      modal.classList.remove('confirm-danger');
    }

    // TASK-1342 — body copy that wraps to 2+ lines is ragged on both edges when
    // centered and hard to scan (the "Billed hours look impossible" guardrail ran
    // ~6 centered lines). Long messages left-align; short one-liners stay centered
    // so the house pattern (CLAUDE.md pitfall #6) is unchanged for the ~50 short
    // confirms. ~100 chars ≈ 2 lines at the dialog's roomy width.
    const _longMessage = String(message == null ? '' : message).length > 100;
    modal.classList.toggle('confirm-long-message', _longMessage);

    // TASK-1342 (COD Code Reviewer ⚠️, round 2) — the 360px→420px widening was
    // originally unconditional, which is visible blast radius on all ~59 dialogs
    // for no benefit to the ~50 that hold "Cancel / Confirm". So the extra width is
    // OPTED INTO by content: a long message, or a label that can't fit one line in
    // a 140px button (~10 chars at 17px/600). Default "Cancel"/"Confirm" (6/7 chars,
    // short message) stays at exactly 360px — pixel-identical to before this task.
    const _longLabel = Math.max(confirmBtn.textContent.length, cancelBtn.textContent.length) > 10;
    modal.classList.toggle('confirm-roomy', _longMessage || _longLabel);

    // TASK-1342 — `safeDefault` is for GUARDRAIL confirms, where a validator
    // interrupted the user rather than the user deliberately asking to destroy
    // something. It makes the CANCEL button the solid primary and the confirm a
    // quiet red outline. Opt-in on purpose: `danger: true` on its own keeps the
    // standard solid-red confirm that every delete flow in the app relies on.
    modal.classList.toggle('confirm-safe-default', !!options.safeDefault);

    // TASK-1342 — per-caller styling seam. The confirm modal is a SINGLETON (markup
    // lives in index.html) shared by ~59 call sites, so before this there was no way
    // to tune one dialog without restyling all of them. The previous caller's classes
    // were already cleared above, so nothing leaks into an unrelated confirm.
    // `className` is for CALLER-AUTHORED LITERALS only — never derive it from tenant
    // data (a job/person name). It only ever reaches classList.add (no innerHTML/eval),
    // but user-derived tokens would still be a needless input path into shared chrome.
    // Reserved prefixes are rejected so a caller can't hand-roll (or clobber) an
    // internal variant — `danger`/`safeDefault` are the supported knobs for those.
    const nextCustom = String(options.className || '')
      .split(/\s+/)
      .filter((c) => c && !/^(hidden|modal|confirm-)/.test(c));
    if (nextCustom.length) {
      nextCustom.forEach((c) => modal.classList.add(c));
      modal.dataset.confirmCustomClass = nextCustom.join(' ');
    }

    // Show modal
    modal.classList.remove('hidden');

    // Cleanup function
    let settled = false;
    const cleanup = (result) => {
      if (settled) return; // idempotent — a superseded dialog must not double-resolve
      settled = true;
      if (_confirmInFlightCleanup === cleanup) _confirmInFlightCleanup = null;
      modal.classList.add('hidden');
      confirmBtn.removeEventListener('click', handleConfirm);
      cancelBtn.removeEventListener('click', handleCancel);
      modal.removeEventListener('click', handleBackdrop);
      resolve(result);
    };
    _confirmInFlightCleanup = cleanup;

    const handleConfirm = () => cleanup(true);
    const handleCancel = () => cleanup(false);
    const handleBackdrop = (e) => {
      if (e.target === modal) cleanup(false);
    };

    confirmBtn.addEventListener('click', handleConfirm);
    cancelBtn.addEventListener('click', handleCancel);
    modal.addEventListener('click', handleBackdrop);
  });
}

// Expose globally
window.showConfirm = showConfirm;
// TASK-10225 — OK-only error dialog (roster termination/FCRA errors called it but it was never defined).
window.showAlert = (title, message) => document.getElementById('confirm-modal')
  ? showConfirm(title, message, { icon: 'error', iconColor: '#DC2626', hideCancel: true, confirmText: 'OK' }).then(() => {})
  : Promise.resolve(alert(`${title}\n\n${message}`));
window.formatPhoneDisplay = formatPhoneDisplay; // TASK-10225: account/roster/dispatch call it via window

// TASK-935 — styled single-field text prompt: a Promise-based replacement for the
// native window.prompt() (which looks awful + breaks the app chrome). Resolves the
// entered string on OK (trimmed), or null on Cancel/backdrop/Escape — SAME contract
// as window.prompt so callers keep their existing null-check. Self-contained DOM.
function showInputModal(title, label, defaultValue = '', options = {}) {
  return new Promise((resolve) => {
    document.getElementById('_input-modal')?.remove();
    const ov = document.createElement('div');
    ov.id = '_input-modal';
    ov.setAttribute('style', 'position:fixed;inset:0;z-index:10050;background:rgba(15,23,42,0.55);display:flex;align-items:center;justify-content:center;padding:20px;');
    const multiline = options.multiline === true;
    const inputType = options.type === 'number' ? 'number' : 'text';
    const fieldHtml = multiline
      ? `<textarea id="_input-modal-field" rows="3" style="width:100%;box-sizing:border-box;border:1px solid var(--border-medium,#cbd5e1);border-radius:8px;padding:10px;font-size:15px;font-family:inherit;"></textarea>`
      : `<input id="_input-modal-field" type="${inputType}" style="width:100%;box-sizing:border-box;border:1px solid var(--border-medium,#cbd5e1);border-radius:8px;padding:11px 12px;font-size:15px;">`;
    ov.innerHTML = `
      <div style="background:#fff;max-width:420px;width:100%;border-radius:14px;padding:22px;box-shadow:0 20px 60px rgba(0,0,0,0.35);">
        <div style="font-size:1.05rem;font-weight:700;color:var(--text-primary,#0F172A);margin-bottom:${label ? '10' : '14'}px;">${escapeHtml(title || '')}</div>
        ${label ? `<label style="display:block;font-size:0.8rem;color:var(--text-secondary,#475569);margin-bottom:5px;">${escapeHtml(label)}</label>` : ''}
        ${fieldHtml}
        <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:18px;">
          <button type="button" id="_input-modal-cancel" style="padding:10px 16px;border:1px solid var(--border-medium,#cbd5e1);border-radius:8px;background:#fff;font-weight:600;cursor:pointer;">Cancel</button>
          <button type="button" id="_input-modal-ok" style="padding:10px 18px;border:none;border-radius:8px;background:var(--brand-blue,#2563EB);color:#fff;font-weight:700;cursor:pointer;">${escapeHtml(options.okText || 'OK')}</button>
        </div>
      </div>`;
    document.body.appendChild(ov);
    const field = ov.querySelector('#_input-modal-field');
    field.value = defaultValue != null ? String(defaultValue) : '';
    setTimeout(() => { field.focus(); try { field.select(); } catch (_) {} }, 30);
    const done = (val) => { document.removeEventListener('keydown', onKey); ov.remove(); resolve(val); };
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); done(null); }
      else if (e.key === 'Enter' && !multiline) { e.preventDefault(); done(field.value.trim()); }
    };
    ov.querySelector('#_input-modal-ok').addEventListener('click', () => done(field.value.trim()));
    ov.querySelector('#_input-modal-cancel').addEventListener('click', () => done(null));
    ov.addEventListener('click', (e) => { if (e.target === ov) done(null); });
    document.addEventListener('keydown', onKey);
  });
}
window.showInputModal = showInputModal;

// ============================================
// UTILITIES
// ============================================
function generateId() {
  return Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
}

// Global button debounce guard — prevents rapid double/triple taps
(function initButtonGuard() {
  const DEBOUNCE_MS = 800;
  let lastClickTime = 0;
  let lastClickTarget = null;

  document.addEventListener('click', (e) => {
    // Skip debounce for form controls — native browser interactions shouldn't be blocked
    const tag = e.target.tagName;
    const type = e.target.type;
    if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA' || tag === 'LABEL') return;

    const btn = e.target.closest('button, .btn');
    if (!btn) return;

    const now = Date.now();
    if (btn === lastClickTarget && (now - lastClickTime) < DEBOUNCE_MS) {
      e.stopImmediatePropagation();
      e.preventDefault();
      return;
    }
    lastClickTime = now;
    lastClickTarget = btn;
  }, true); // capture phase
})();

function capitalizeFirst(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

// ============================================
// UPGRADE GATE
// ============================================
// ============================================
// SETTINGS MODAL
// ============================================
async function openSettingsModal(sectionArg) {
  // TASK-9753 S2 — optional section to land on ('team', 'billing', ...). The
  // header gear binds this straight to addEventListener('click', ...), so the
  // first argument is often a click Event: only a string counts.
  const _requestedSection = typeof sectionArg === 'string' ? sectionArg : null;
  // Block settings for compliance-only tenants — managed by their compliance advisor
  if (isComplianceOnlyTenant()) {
    const partner = ((window.AppState?.tenantData?.compliancePartner || 'your compliance advisor').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;'));
    showToast(`Account settings are managed by ${partner}`, 'info');
    return;
  }
  // Block settings for driver PIN mode
  if (window.AppState?.driverModePin) {
    showToast('Settings are only available with a full account login', 'error');
    return;
  }
  // TASK-9389 (admin backfill lane) — a staff login (explicit modules, not the
  // owner) opens Settings only with an owner-given switch, and sees only what it
  // covers. null = owner / legacy admin / PIN session: the whole modal, as before.
  const _memberScope = memberSettingsScope();
  if (_memberScope && !_memberScope.any) {
    showToast('Settings are managed by the account owner.', 'info');
    return;
  }
  // Require PIN to access Settings (if PINs are configured). Not for a staff
  // login: PINs are a shared-device gate and never apply to a member with
  // explicit modules (TASK-319) — their access is the login itself.
  // TASK-748/719-A — VIP/comped accounts honor the Settings PIN gate like
  // everyone else (per-device lock).
  // TASK-791 — founder (TITS) now honors it too: removed the founder bypass so
  // every account behaves the same (no-TITS-special). Safe because the
  // first-login required-PIN gate (app.js:~5412) has no founder exception, so
  // founder accounts always have accessPins; this gate only fires when
  // accessPins.length > 0, so a zero-PIN tenant still falls straight through.
  const accessPins = accessPinMap();
  if (!_memberScope && Object.keys(accessPins).length > 0) {
    const tenantId = window.AppState?.tenant;
    const storedPin = tenantId ? localStorage.getItem(`dispatchdeck_pin_${tenantId}`) : null;
    if (!storedPin) {
      showPinChallenge('Settings', (ok) => {
        if (ok) openSettingsModal(_requestedSection || undefined);
      });
      return;
    }
  }
  try {
    console.log('[Settings] openSettingsModal called');
    _settingsSession = {};
    const modal = document.getElementById('settings-modal');
    if (!modal) {
      console.log('[Settings] Modal element not found!');
      return;
    }

    // Don't open settings if not logged in
    if (!window.AppState?.user || !window.AppState?.tenant) {
      console.log('[Settings] Cannot open - user not logged in', { user: !!window.AppState?.user, tenant: window.AppState?.tenant });
      return;
    }

  // Close ALL other modals first - be aggressive
  document.querySelectorAll('.modal').forEach(m => {
    if (m.id !== 'settings-modal') {
      m.classList.add('hidden');
    }
  });
  // TASK-9753 — the Add team member chooser is built on demand; drop a stale one.
  try { _closeAddTeamMember(); } catch (_) {}

  // Also close dispatch module modals if open
  if (window.DispatchModule?.closeModal) {
    window.DispatchModule.closeModal('job');
  }

  // Remove modal-open first, the settings modal MutationObserver will re-add
  document.body.classList.remove('modal-open');
  document.documentElement.classList.remove('modal-open');

  // TASK-9569 — this login's "Show reminder bells" row (window.Reminders owns it).
  try { window.Reminders?.paintSettingsRow?.(); } catch (e) { console.warn('[Reminders] settings row failed:', e?.message || e); }
  // TASK-9724 S2 — the owner's Track supply cost switch (roster.js owns it; hidden for everyone else).
  try { window.RosterModule?.paintSupplyCostSetting?.(); } catch (e) { console.warn('[Roster] supply cost setting failed:', e?.message || e); }
  // TASK-9375 S5 — "Part of <org> — Leave" card (loads views/organization.js only when tenantData.orgId is set).
  window.OrganizationView?.paintBranchCard?.();
  // TASK-9891 — Settings → Security (memo link length; owner-only, own Save button).
  try { paintSecuritySettings(); } catch (e) { console.warn('[Settings] security section failed:', e?.message || e); }

  // 🔴 TASK-1577 — paint the two access-management panels HERE, at open, not only
  // from the settings-load block far below. That block sits inside a try/catch
  // around an async tenant-doc fetch: if the fetch is slow or throws, it never
  // reaches renderAccessPinsList/renderTeamAccessList, and the sections keep their
  // static index.html placeholder — which leaves the "Add Access PIN" button
  // VISIBLE for a holder who may not manage PINs. The write paths still refuse, so
  // this was never an escalation, but a button that only ever errors is a lie, and
  // a security panel must not depend on an unrelated fetch succeeding.
  // Caught by tests/task1577-pin-management-gate.spec.js, which found the panel
  // empty for a limited PIN even though both predicates gated correctly.
  // Safe to call twice: both are idempotent full re-renders, and both read
  // AppState.settings, which the live settings subscription keeps current
  // independently of this fetch.
  try {
    renderAccessPinsList();
    // Start Team unfiltered every time. The modal is hidden, not destroyed, so
    // a leftover filter -- or a browser autofill in the search box -- would
    // otherwise carry over and make the list look like it lost people.
    _resetTeamFilterInputs();
    renderTeamAccessList();
  } catch (e) {
    console.warn('[Settings] access panel pre-render failed:', e?.message || e);
  }
  // TASK-9683 slice 2 — the owner's Claude approval rules (company + per
  // person). Owner-only box; the callable re-checks. Never blocks the modal.
  try { window.ClaudeApprovalRules?.mountSettings?.(); } catch (e) { console.warn('[Settings] approval rules failed:', e?.message || e); }

  // Load current tenant settings
  try {
    const tenantDoc = await getDoc(doc(db, 'tenants', window.AppState.tenant));
    if (tenantDoc.exists()) {
      const data = tenantDoc.data();
      const settings = data.settings || {};

      // Populate form
      document.getElementById('settings-company-name').value = data.name || '';
      const dotEl = document.getElementById('settings-dot-number');
      if (dotEl) dotEl.value = data.dotNumber || '';
      // TASK-261 Slice 1A — MC# (FMCSA docket number)
      const mcEl = document.getElementById('settings-mc-number');
      if (mcEl) mcEl.value = data.mcNumber || '';
      // Decentralized 2026-05-19 (v1.15.693) — Liability Insurance fields
      // (tenant.insurance.*) and State MVR Credentials (UT/PA per-carrier,
      // tenant.compliance.stateMvrCreds.*) moved to Compliance Settings
      // sub-modal in views/compliance.js (openComplianceSettingsModal /
      // saveComplianceSettings). State-key schema unchanged. These elements
      // no longer live in the main Settings modal.

      document.getElementById('settings-timezone').value = settings.timezone || 'America/Chicago';
      document.getElementById('settings-distance-unit').value = settings.distanceUnit || 'miles';

      // DVIR retention settings
      const dvirAutoPurge = settings.dvirAutoPurge !== false; // Default true
      document.getElementById('settings-dvir-autopurge').checked = dvirAutoPurge;
      document.getElementById('settings-dvir-retention').value = settings.dvirRetentionMonths || 6;

      // JSA retention settings (default OFF — incident investigations may need older records)
      const jsaAutoPurgeEl = document.getElementById('settings-jsa-autopurge');
      if (jsaAutoPurgeEl) jsaAutoPurgeEl.checked = settings.jsaAutoPurge === true;
      const jsaRetEl = document.getElementById('settings-jsa-retention');
      if (jsaRetEl) jsaRetEl.value = settings.jsaRetentionMonths || 24;

      // Photo retention settings
      const photoAutoPurge = settings.photoAutoPurge !== false; // Default true
      const photoAutoPurgeEl = document.getElementById('settings-photo-autopurge');
      const photoRetentionEl = document.getElementById('settings-photo-retention');
      if (photoAutoPurgeEl) photoAutoPurgeEl.checked = photoAutoPurge;
      if (photoRetentionEl) photoRetentionEl.value = settings.photoRetentionDays || 90;

      // Receipt retention settings
      const receiptAutoPurge = settings.receiptAutoPurge !== false; // Default true
      const receiptAutoPurgeEl = document.getElementById('settings-receipt-autopurge');
      const receiptRetentionEl = document.getElementById('settings-receipt-retention');
      if (receiptAutoPurgeEl) receiptAutoPurgeEl.checked = receiptAutoPurge;
      if (receiptRetentionEl) receiptRetentionEl.value = settings.receiptRetentionMonths || 24;

      // Decentralized 2026-05-19 (v1.15.691) — Dispatch-domain toggles
      // (enable811, enableLoadCalculator, enableRoutePlanner,
      // personalizedSchedule, hideComplianceWarnings) moved to
      // openDispatchSettingsModal in dispatch.js. Schema unchanged — these
      // elements no longer live in the main Settings modal.

      // Decentralized 2026-05-06 — Inspection-domain (DVIR/JSA/citations) and
      // HR-domain (track OSHA + custodian, require-clear BG, applicationFlow,
      // applicationQuestions count) load handlers moved to their respective
      // sub-modals' open functions in this file (search "openInspectionSettingsModal"
      // and "openHrRecruitingSettingsModal"). These elements no longer live
      // in the main Settings modal.

      // applicationNotifyEmail + applicationContactPhone decentralized
      // 2026-05-08 → loaded by openHrRecruitingSettingsModal instead.

      // Decentralized 2026-05-06 (Phase D) — Clock & Billing toggles
      // (clockInAskCustomer, captureGpsOnClock) moved to Billing Settings.
      // See openBillingSettings/saveBillingSettings in views/billing.js.

      // Online Payments section — moved to AR/AP Billing Settings
      {
        const opSection = document.getElementById('settings-online-payments-section');
        const opHr = document.getElementById('settings-online-payments-hr');
        if (opSection) opSection.style.display = 'none';
        if (opHr) opHr.style.display = 'none';
      }

      // TASK-9740 — Bills of lading & disposal tickets. Default OFF. Only an
      // owner/admin can flip it (rules: no module allow-list carries the key),
      // so the box is disabled for everyone else rather than failing the Save.
      {
        const jdEl = document.getElementById('settings-job-documents-enabled');
        const jdHint = document.getElementById('settings-job-documents-hint');
        if (jdEl) {
          jdEl.checked = settings.jobDocumentsEnabled === true;
          const ma = window.AppState?.memberAccess;
          const jdAdmin = window.AppState?.isDemo === true || ma == null
            || (!(ma.status && ['revoked', 'inactive', 'pending'].includes(ma.status)) && (ma.role === 'owner' || ma.role === 'admin'));
          jdEl.disabled = !jdAdmin;
          if (jdHint) jdHint.textContent = 'Lets drivers fill out and sign a bill of lading or disposal ticket on a job from their phone. Off by default.' + (jdAdmin ? '' : ' Only an owner or admin can change this.');
        }
      }

      // Smart Tools toggles (default ON, included in all plans)
      const smartToolsGroup = document.getElementById('settings-smart-tools-group');
      const smartToolsEnabledEl = document.getElementById('settings-smart-tools-enabled');
      const smartAssistantEl = document.getElementById('settings-enable-smart-assistant');
      if (smartToolsGroup && smartAssistantEl) {
        smartToolsGroup.style.display = '';
        // Master AI off-switch (TASK-631) — when false, ALL AI/model calls are blocked. Default ON (absent → on).
        const smartToolsOn = settings.smartToolsEnabled !== false;
        if (smartToolsEnabledEl) smartToolsEnabledEl.checked = smartToolsOn;
        smartAssistantEl.checked = settings.enableSmartAssistant !== false; // default ON
        // Sub-toggle is moot when all AI is off — grey it out + reflect master state live
        const applySmartToolsState = () => {
          const on = smartToolsEnabledEl ? smartToolsEnabledEl.checked : true;
          const sub = document.getElementById('settings-smart-assistant-subgroup');
          if (sub) sub.style.opacity = on ? '1' : '0.45';
          smartAssistantEl.disabled = !on;
        };
        if (smartToolsEnabledEl) smartToolsEnabledEl.onchange = applySmartToolsState;
        applySmartToolsState();
        // TASK-9956 S2 — the "Web search in Help" box is set up by the lazy chunk (ships disabled).
        _wrHelp().then((m) => m.initSettings({ settings, memberAccess: window.AppState?.memberAccess, smartToolsEl: smartToolsEnabledEl })).catch(() => {});
      }

      // Samsara API token -- TASK-1621: the raw token no longer lives in
      // `settings` (moved to the backend-only secrets/samsara sibling via
      // saveSamsaraToken), so this can only show a "connected" placeholder,
      // not a last-6-chars hint. `samsaraApiToken` fallback covers a tenant
      // that connected before this change and hasn't been migrated yet
      // (scripts/task1621-migrate-samsara-token.cjs --apply not run) -- drop
      // that fallback once the migration has run everywhere.
      const samsaraTokenEl = document.getElementById('settings-samsara-token');
      if (samsaraTokenEl) {
        const samsaraConnected = settings.samsaraConnected === true || !!settings.samsaraApiToken;
        if (samsaraConnected) {
          samsaraTokenEl.value = '';
          samsaraTokenEl.placeholder = '•••••••••••••••• (connected)';
        } else {
          samsaraTokenEl.value = '';
          samsaraTokenEl.placeholder = 'samsara_api_xxxxxxxxxxxxxxxx';
        }
      }

      // TASK-1569 — SMS Command Approver (credential surface). Admin-only in the
      // UI (mirrors firestore.rules isTenantAdmin on the tenant-doc update, which
      // already governs this nested settings write — see saveSmsApproverPhone).
      {
        const smsSec = document.getElementById('settings-sms-backoffice-section');
        if (smsSec) {
          if (typeof hrEffectiveAdmin === 'function' && hrEffectiveAdmin()) {
            smsSec.style.display = '';
            // resolveApprover (functions/src/services/smsInbox.js) reads
            // settings.smsInbox.approverPhone first, then the legacy top-level
            // smsInbox.approverPhone, then falls back to tenant.ownerPhone.
            const explicit = settings?.smsInbox?.approverPhone || data?.smsInbox?.approverPhone || '';
            const ownerPhone = data?.ownerPhone || '';
            const inputEl = document.getElementById('settings-sms-approver-phone');
            if (inputEl) inputEl.value = explicit || '';
            // TASK-4586 — seed the enable toggle. isEnabled() on the server is
            // `cfg.enabled === true`, so an absent key means OFF; render that
            // rather than a default-on checkbox that lies about the state.
            const enabledEl = document.getElementById('settings-sms-inbox-enabled');
            if (enabledEl) enabledEl.checked = settings?.smsInbox?.enabled === true;
            // TASK-9769 S2 — customer texts → requests. Server reads
            // `enabled === true`, so absent = OFF.
            const ctiEl = document.getElementById('settings-customer-text-intake-enabled');
            if (ctiEl) ctiEl.checked = settings?.customerTextIntake?.enabled === true;
            if (typeof renderCustomerTextIntakeStatus === 'function') renderCustomerTextIntakeStatus();
            if (typeof renderSmsApproverEffective === 'function') {
              renderSmsApproverEffective(explicit, ownerPhone);
            }
            // TASK-9256 — pilot numbers, seeded from this fresh tenant read so an
            // absent/empty list renders as "Everyone", never as "no one".
            const pilotInput = document.getElementById('settings-sms-pilot-phone');
            if (pilotInput) pilotInput.value = '';
            if (typeof renderSmsPilotList === 'function') {
              renderSmsPilotList(settings?.smsInbox || {});
            }
          } else {
            smsSec.style.display = 'none';
          }
        }
      }

      // Accounting basis moved to Billing Settings modal

      // Company Logo section (available to founder + clockbilling subscribers)
      const logoGroup = document.getElementById('settings-logo-group');
      if (logoGroup) {
        if (hasAddOnPackage('clockbilling')) {
          logoGroup.style.display = '';
          const preview = document.getElementById('settings-logo-preview');
          const removeBtn = document.getElementById('btn-remove-logo');
          const fileInput = document.getElementById('settings-logo-file');
          if (fileInput) fileInput.value = '';
          if (data.companyLogoUrl) {
            if (preview) { preview.src = data.companyLogoUrl; preview.style.display = ''; }
            if (removeBtn) removeBtn.style.display = '';
          } else {
            if (preview) { preview.src = ''; preview.style.display = 'none'; }
            if (removeBtn) removeBtn.style.display = 'none';
          }
        } else {
          logoGroup.style.display = 'none';
        }
      }

      // Update billing section display
      let plan = data.plan || 'free';
      const subscriptionStatus = data.subscriptionStatus || 'none';

      const planDisplayEl = document.getElementById('current-plan-display');
      const planDescEl = document.getElementById('plan-description');
      const manageBillingBtn = document.getElementById('btn-manage-billing');
      const upgradePlanBtn = document.getElementById('btn-upgrade-plan');

      const planNames = {
        founder: 'Founder Account',
        free: 'Free Trial',
        trial: 'Free Trial',
        base: 'DispatchDeck',
        active: 'DispatchDeck',
        pro: 'DispatchDeck',
        proPlus: 'DispatchDeck'
      };

      // TASK-9812 — "$2,500/yr" when the webhook stamped an annual checkout
      // (subscription.billingInterval is a display hint; Stripe is the record).
      const _paidLabel = (data.subscription?.billingInterval === 'year' ? PLAN_PRICING.annualLabel : PLAN_PRICING.monthlyLabel) + ' • All modules';
      const planDescs = {
        founder: 'Full access • Forever',
        free: 'Demo mode',
        trial: '30-day trial active',
        base: _paidLabel,
        active: _paidLabel,
        pro: _paidLabel,
        proPlus: _paidLabel
      };

      // Check if founder account
      const isFounder = FOUNDER_TENANTS.includes(window.AppState?.tenant);
      if (isFounder) {
        plan = 'founder';
      }

      if (planDisplayEl) {
        planDisplayEl.textContent = planNames[plan] || 'Free Plan';
        if (subscriptionStatus === 'active') {
          planDisplayEl.innerHTML += ' <span class="ss-text" style="font-size:0.75rem;">✓ Active</span>';
        } else if (subscriptionStatus === 'trialing') {
          planDisplayEl.innerHTML += ' <span style="color:var(--brand-cyan);font-size:0.75rem;">Trial</span>';
        }
      }
      if (planDescEl) planDescEl.textContent = planDescs[plan] || '';

      // Show/hide buttons based on subscription
      // Hide Stripe billing entirely on iOS native app (Apple App Store guideline 3.1.1)
      const billingSection = document.getElementById('billing-section');
      if (window.isIOSApp && billingSection) {
        billingSection.innerHTML = `
          <div style="font-weight:600;color:var(--text-primary);">${planNames[plan] || 'Free Plan'}</div>
          <p style="font-size:0.8rem;color:var(--text-muted);margin-top:4px;">Manage your subscription at <strong>dispatchdeck.app</strong></p>
        `;
      } else {
        // TASK-9432 — #btn-manage-billing is an empty legacy stub (no label, no
        // onclick); the real Manage Billing lives in the account view, reached
        // via "Manage Account & Billing". Never show the stub.
        if (manageBillingBtn) manageBillingBtn.style.display = 'none';
        if (upgradePlanBtn) {
          // TASK-9432 — owner only, never comped (founder/VIP/comped), never
          // already subscribed. Server enforces the same in createCheckoutSession.
          const _billingCtl = getPlanBillingControls(data, {
            tenantId: window.AppState?.tenant, uid: window.AppState?.user?.uid,
            isIOS: !!window.isIOSApp, isDemo: !!window.AppState?.isDemo,
          });
          upgradePlanBtn.style.display = (_billingCtl.showUpgrade && plan !== 'fleet' && plan !== 'enterprise') ? 'inline-flex' : 'none';
        }
      }

      // Compliance Billing row (TASK-926 — moved from the HR action grid).
      // TASK-9431 — owner (tenants.ownerId) OR the `billing` module: exactly the
      // people the server lets change the card. Fail closed if the gate helper
      // is somehow missing.
      const complianceBillingRow = document.getElementById('settings-compliance-billing-row');
      if (complianceBillingRow) {
        const canManageComplianceBilling = (typeof window.hrCanManageComplianceBilling === 'function')
          ? window.hrCanManageComplianceBilling() : false;
        complianceBillingRow.style.display = canManageComplianceBilling ? '' : 'none';
        if (canManageComplianceBilling) _refreshCarrierBillingSubtitle();
      }

      // Update SMS Pack section
      const smsPromoEl = document.getElementById('sms-pack-promo');
      const smsActiveEl = document.getElementById('sms-pack-active');
      const smsPhoneEl = document.getElementById('sms-phone-number');
      const smsSectionEl = document.getElementById('sms-pack-section');

      // TASK-4665 — shown for everyone, the founder included. It used to be hidden
      // for the founder ("they already have it"), back when the founder texted
      // free; it is metered now, so it needs the same meter + buy path.
      if (smsSectionEl) {
        smsSectionEl.style.display = 'block';
      }

      if (data.hasSmsPack || data.twilio?.status === 'active') {
        // Has SMS (pack or promo) - show active state with meter
        if (smsPromoEl) smsPromoEl.style.display = 'none';
        if (smsActiveEl) smsActiveEl.style.display = 'block';
        if (smsPhoneEl) {
          smsPhoneEl.textContent = data.twilio?.phoneNumber
            ? formatPhoneDisplay(data.twilio.phoneNumber)
            : 'Shared number';
        }
        // Render meter in settings
        renderSmsCreditMeter('sms-meter-settings', { variant: 'compact' });
        // Top-up and auto-recharge live on the Account page; point there from here.
        const smsMeterEl = document.getElementById('sms-meter-settings');
        if (smsMeterEl && !smsMeterEl.querySelector('.sms-settings-manage-link') && _smsPackControlForViewer().show) {
          smsMeterEl.insertAdjacentHTML('beforeend', `<a class="sms-settings-manage-link" href="#/account" onclick="closeSettingsModal()" title="Buy more credits or turn on auto-recharge">Add credits or turn on auto-recharge &rarr;</a>`);
        }
      } else {
        // No SMS Pack - show promo
        if (smsPromoEl) smsPromoEl.style.display = 'block';
        if (smsActiveEl) smsActiveEl.style.display = 'none';
      }

      // Update local settings state
      window.AppState.settings = settings;

      // Render access PINs list
      renderAccessPinsList();

      // Render Team Access list (TASK-319 slice 6) — async, non-blocking
      renderTeamAccessList();

      // Show/hide retention dropdowns based on autopurge checkboxes
      updateRetentionVisibility();
    }
  } catch (error) {
    console.error('[Settings] Error loading:', error);
    showToast('Error loading settings', 'error');
    _settingsSession = null; // abandoned open: drop its deferred approval-rules paint/toast
    return; // Don't show modal if settings failed to load
  }

  // Double-check we're still logged in (race condition protection)
  if (!window.AppState?.user || !window.AppState?.tenant) {
    console.log('[Settings] Aborted - user logged out during load');
    _settingsSession = null; // abandoned open: drop its deferred approval-rules paint/toast
    return;
  }

  // Explicitly hide auth modal
  const authModal = document.getElementById('auth-modal');
  if (authModal) authModal.classList.add('hidden');

  modal.classList.remove('hidden');
  // Reset again AFTER unhide: browser autofill can land on unhide, defeating the early reset (TASK-9225).
  // Repaint from the cache ONLY if autofill actually filled a filter box (no second fetch).
  try {
    const _tf = ['team-filter-search','team-filter-module','team-filter-role'].some(id => (document.getElementById(id)||{}).value);
    _resetTeamFilterInputs();
    if (_tf) _renderTeamAccessFiltered();
  } catch (_) {}

  // TASK-9389 (admin backfill lane) — a staff login sees only its switches'
  // parts (Team & Access / Features); undone for the next full-modal open.
  try { _applyMemberSettingsScope(_memberScope); } catch (e) { console.warn('[Settings] member scope failed:', e?.message || e); }
  // TASK-9753 — land on the requested section (deep link / caller), else the
  // first one; on a phone with no section asked for, show the section list.
  try { _applySettingsSectionOnOpen(_requestedSection); } catch (e) { console.warn('[Settings] section nav failed:', e?.message || e); }
  // TASK-9753 S1 — Add team member: same gate as Roster's Invite User.
  try { syncAddTeamMemberButton(); } catch (_) {}

  // Set up autopurge checkbox listeners
  const dvirAutoPurgeCheckbox = document.getElementById('settings-dvir-autopurge');
  const photoAutoPurgeCheckbox = document.getElementById('settings-photo-autopurge');
  const receiptAutoPurgeCheckbox = document.getElementById('settings-receipt-autopurge');
  if (dvirAutoPurgeCheckbox) dvirAutoPurgeCheckbox.addEventListener('change', updateRetentionVisibility);
  if (photoAutoPurgeCheckbox) photoAutoPurgeCheckbox.addEventListener('change', updateRetentionVisibility);
  if (receiptAutoPurgeCheckbox) receiptAutoPurgeCheckbox.addEventListener('change', updateRetentionVisibility);

  } catch (outerError) {
    console.error('[Settings] Unexpected error in openSettingsModal:', outerError);
    // Log error to Firestore for debugging
    if (window.logError) {
      window.logError(outerError, { context: 'openSettingsModal', tenant: window.AppState?.tenant });
    }
    showToast('Error opening settings: ' + (outerError?.message || 'Unknown'), 'error');
  }
}


function closeSettingsModal() {
  const modal = document.getElementById('settings-modal');
  if (modal) modal.classList.add('hidden');
  _settingsSession = null;
  // Leave the Team filter blank behind us -- belt and braces with the reset in
  // openSettingsModal, which sits inside a try/catch that can bail early.
  try { _resetTeamFilterInputs(); } catch (_) {}
  try { _closeAddTeamMember(); } catch (_) {}
}
// TASK-9891 — Settings → Security (memo link length). The code is a lazy chunk
// (views/securitySettings.js) so it stays off the cold load; these two only load it.
let _securitySettingsLoad = null;
function _loadSecuritySettings() {
  if (!_securitySettingsLoad) _securitySettingsLoad = __ddChunkWrap(import('./views/securitySettings.js?v=102560001')).catch((e) => { _securitySettingsLoad = null; throw e; });
  return _securitySettingsLoad;
}
function paintSecuritySettings() {
  return _loadSecuritySettings().then(() => window.SecuritySettings?.paint?.()).catch((e) => console.warn('[Settings] security section failed:', e?.message || e));
}
function saveMemoLinkLength() {
  return _loadSecuritySettings().then(() => window.SecuritySettings?.save?.()).catch(() => showToast('Could not open Security settings. Try again.', 'error'));
}
// TASK-9891 Slice 2 — the other link lengths (own Save button, setLinkLengths callable).
function saveLinkLengths() {
  return _loadSecuritySettings().then(() => window.SecuritySettings?.saveLinks?.()).catch(() => showToast('Could not open Security settings. Try again.', 'error'));
}
// The lazy chunk clears its own select from the unsaved-work tracker after a save.
window._clearUnsavedField = (el) => { _dirtyFields.delete(el); };
// ============================================
// TASK-9753 S2 — SETTINGS SECTIONS
// ============================================
// Settings used to be one 4,000px column. It is now eight sections with a nav
// (desktop: left rail; phone: a section list, then one section with "All
// settings" to go back). Every section stays in the DOM — switching only sets
// `hidden` — so an unsaved edit survives a switch and the ONE Save Settings
// button still writes every section (the rail says so on screen).
// Deep link: #/settings/<section> (handleHashChange → _routeSettingsDeepLink).
const SETTINGS_SECTIONS = ['company', 'team', 'billing', 'integrations', 'assistant', 'security', 'data', 'danger'];
const SETTINGS_SECTION_ALIASES = {
  'team-access': 'team', access: 'team', pins: 'team', 'access-pins': 'team', logins: 'team',
  subscription: 'billing', plan: 'billing',
  'smart-tools': 'assistant', sms: 'assistant', ai: 'assistant',
  retention: 'data', storage: 'data',
  'danger-zone': 'danger',
  links: 'security', 'link-expiry': 'security', 'memo-links': 'security', // TASK-9891
};

// TASK-9389 (admin backfill lane) — the scope of the CURRENT open for a staff
// login (memberSettingsScope), or null for the full modal. Set on every open.
let _activeMemberSettingsScope = null;
const _MEMBER_SCOPE_HID = 'data-member-scope-hid';
function _applyMemberSettingsScope(scope) {
  const modal = document.getElementById('settings-modal');
  _activeMemberSettingsScope = scope || null;
  if (!modal) return;
  // Undo the previous scoped open: the modal is reused across logins in a tab.
  modal.querySelectorAll(`[${_MEMBER_SCOPE_HID}]`).forEach((el) => {
    el.style.display = el.getAttribute(_MEMBER_SCOPE_HID) === '-' ? '' : el.getAttribute(_MEMBER_SCOPE_HID);
    el.removeAttribute(_MEMBER_SCOPE_HID);
  });
  document.getElementById('settings-member-scope-note')?.remove();
  if (!scope) return;
  // Inline display, not `hidden`: .settings-nav-item / .btn set display in CSS,
  // which beats the [hidden] UA rule.
  const hide = (el) => {
    if (!el || el.hasAttribute(_MEMBER_SCOPE_HID)) return;
    el.setAttribute(_MEMBER_SCOPE_HID, el.style.display || '-');
    el.style.display = 'none';
  };
  modal.querySelectorAll('.settings-nav-item').forEach((btn) => {
    const nav = btn.dataset.settingsNav;
    if (nav === 'team' && scope.team) return;
    if (btn.id === 'settings-nav-features' && scope.features) return;
    hide(btn);
  });
  hide(modal.querySelector('.settings-nav-note')); // "Save Settings saves every section"
  hide(modal.querySelector('.modal-footer .btn-primary')); // the one Save Settings: owner sections only
  const panels = document.getElementById('settings-panels');
  if (panels) {
    const note = document.createElement('div');
    note.id = 'settings-member-scope-note';
    note.setAttribute('role', 'note');
    note.style.cssText = 'display:flex;flex-direction:column;gap:8px;padding:10px 12px;margin:0 0 var(--spacing-md,12px) 0;background:var(--bg-secondary,#F3F4F6);border:1px solid var(--border-light,#E5E7EB);border-radius:8px;font-size:0.85rem;color:var(--text-primary);';
    const p = document.createElement('div');
    const parts = [];
    if (scope.team) parts.push('add people and change their logins (Team & Access)');
    if (scope.features) parts.push('change some company switches (Features)');
    p.textContent = `The account owner lets you ${parts.join(' and ')}. The rest of Settings stays with the owner.`;
    note.appendChild(p);
    if (scope.features) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'btn btn-secondary btn-sm';
      b.style.alignSelf = 'flex-start';
      b.innerHTML = '<span class="material-icons" aria-hidden="true" style="font-size:16px;">toggle_on</span> Open Features';
      b.addEventListener('click', () => { window.FeaturesPage && window.FeaturesPage.open(); });
      note.appendChild(b);
    }
    const back = panels.querySelector('.settings-back');
    if (back && back.nextSibling) panels.insertBefore(note, back.nextSibling); else panels.prepend(note);
  }
}

function _normalizeSettingsSection(name) {
  const k = String(name == null ? '' : name).trim().toLowerCase();
  if (!k) return null;
  if (SETTINGS_SECTIONS.includes(k)) return k;
  return Object.prototype.hasOwnProperty.call(SETTINGS_SECTION_ALIASES, k) ? SETTINGS_SECTION_ALIASES[k] : null;
}

function _settingsIsPhone() {
  try { return !!(window.matchMedia && window.matchMedia('(max-width: 768px)').matches); } catch (_) { return false; }
}

function showSettingsSection(name, opts = {}) {
  let key = _normalizeSettingsSection(name) || 'company';
  // TASK-9389 (admin backfill lane) — a scoped staff login reaches only Team &
  // Access (with "Can manage logins"); any other section (deep link, Features
  // link, a stale caller) lands on Team, or on no section when they have only a
  // settings switch (the note on top points to Features).
  const _scope = _activeMemberSettingsScope;
  if (_scope && !(_scope.team && key === 'team')) key = _scope.team ? 'team' : '';
  const body = document.querySelector('#settings-modal .settings-body');
  if (!body) return;
  document.querySelectorAll('#settings-modal .settings-section').forEach(sec => {
    sec.hidden = sec.dataset.settingsSection !== key;
  });
  document.querySelectorAll('#settings-modal .settings-nav-item').forEach(btn => {
    if (btn.dataset.settingsNav === key) btn.setAttribute('aria-current', 'true');
    else btn.removeAttribute('aria-current');
  });
  body.dataset.settingsView = 'section';
  body.dataset.activeSection = key;
  const panels = document.getElementById('settings-panels');
  if (panels) panels.scrollTop = 0;
  body.scrollTop = 0;
  if (opts.focus !== false) {
    // Land keyboard / screen-reader users in the section they picked.
    const title = document.getElementById(`settings-section-title-${key}`);
    if (title) {
      if (!title.hasAttribute('tabindex')) title.setAttribute('tabindex', '-1');
      try { title.focus({ preventScroll: true }); } catch (_) {}
    }
  }
}

// Phone only (the CSS ignores data-settings-view above 768px): back to the list.
function showSettingsSectionList() {
  const body = document.querySelector('#settings-modal .settings-body');
  if (!body) return;
  body.dataset.settingsView = 'list';
  body.scrollTop = 0;
  const active = body.dataset.activeSection || 'company';
  const btn = document.querySelector(`#settings-modal .settings-nav-item[data-settings-nav="${active}"]`);
  try { btn?.focus({ preventScroll: true }); } catch (_) {}
}

function _applySettingsSectionOnOpen(requested) {
  const key = _normalizeSettingsSection(requested);
  showSettingsSection(key || 'company', { focus: !!key });
  if (!key && _settingsIsPhone()) showSettingsSectionList();
}

// #/settings and #/settings/<section>. Settings is a modal, not a view, so the
// page underneath stays (or, on a cold load, becomes the default view) and the
// URL goes back to that page — replaceState fires no hashchange — so closing
// Settings and clicking the same link again opens it again. Hashes with a
// ?query (Stripe / QuickBooks OAuth returns) never reach here; they keep their
// existing handling.
function _routeSettingsDeepLink(section, defaultView) {
  const AS = window.AppState || {};
  const cur = AS.currentView;
  const under = (cur && cur !== 'settings') ? cur : defaultView;
  AS.currentView = under;
  try { history.replaceState(history.state, '', `#/${under}`); } catch (_) {}
  const warm = !!(AS.user && AS.tenant);
  window._pendingSettingsDeepLink = { section: section || '', warm };
  _drainSettingsDeepLink(0);
}

function _drainSettingsDeepLink(tries) {
  const p = window._pendingSettingsDeepLink;
  if (!p) return;
  const AS = window.AppState || {};
  const signedIn = !!(AS.user && AS.tenant) && AS.memberAccessPending !== true;
  // A cold load waits for sign-in AND for every boot dialog (auth, required
  // PIN, first-login prompts) to clear: openSettingsModal closes all other
  // modals, and a link must never sweep a boot dialog away.
  const clear = p.warm || !document.querySelector('.modal:not(.hidden)');
  if (signedIn && clear) {
    window._pendingSettingsDeepLink = null;
    openSettingsModal(p.section || undefined);
    return;
  }
  if (tries >= 100) { // ~30s
    window._pendingSettingsDeepLink = null;
    console.warn('[Settings] deep link dropped: not signed in, or a dialog stayed open');
    return;
  }
  setTimeout(() => _drainSettingsDeepLink(tries + 1), 300);
}

// ============================================
// TASK-9753 S1 — "ADD TEAM MEMBER" (Settings → Team & Access)
// ============================================
// One button, one question: someone on your roster (pick them → the EXISTING
// Roster "Set up login" modal for that person) or someone who is not (→ the
// EXISTING personless "Invite a user" modal). No new invite path: both end in
// roster.js submitStaffLoginInvite → sendUserInvite, which re-checks authority
// server-side (requireTenantAdmin). The gate is Roster's Invite User gate, read
// through window.RosterModule so there is ONE definition of it.
function _canAddTeamMember() {
  const tenant = window.AppState?.tenant;
  if (!tenant || tenant === 'demo') return false;
  if (window.AppState?.memberAccessPending === true) return false;
  return window.RosterModule?.canInviteUsers?.() === true;
}

function syncAddTeamMemberButton(_retries = 8) {
  const btn = document.getElementById('btn-settings-add-team-member');
  if (!btn) return;
  // Member doc still loading → memberAccess is null, which the gate reads as
  // "legacy owner". Stay hidden and re-check rather than flash it at staff.
  if (window.AppState?.memberAccessPending === true) {
    btn.style.display = 'none';
    if (_retries > 0) setTimeout(() => syncAddTeamMemberButton(_retries - 1), 250);
    return;
  }
  btn.style.display = _canAddTeamMember() ? '' : 'none';
}

let _atmPeople = null; // roster people for the picker (this open only)
let _atmLoadSeq = 0;    // bumps on every open/close/Back so a late roster read can't paint a newer picker (COD)
// TASK-10094 — { byPerson: Map(personId → 'login'|'pending'), complete } read
// fresh with the roster on every picker open (lazy views/addTeamMemberPicker.js),
// so the tags are right when the chooser opens from Roster and Settings never did.
let _atmLogins = null;
let _atmMod = null; // that lazy chunk, once loaded

function _closeAddTeamMember() {
  document.getElementById('add-team-member-modal')?.remove();
  _atmPeople = null;
  _atmLoadSeq++;
}

function openAddTeamMember() {
  if (!_canAddTeamMember()) {
    showToast('Only an owner or admin can add team members.', 'error');
    return;
  }
  _closeAddTeamMember();
  const modal = document.createElement('div');
  modal.id = 'add-team-member-modal';
  modal.className = 'modal';
  modal.style.zIndex = '1001'; // above Settings (.modal 1000), below confirm (9999)
  modal.innerHTML = `
    <div class="modal-content modal-form-narrow" role="dialog" aria-modal="true" aria-labelledby="atm-title" style="max-width:520px;">
      <div class="modal-header">
        <h2 id="atm-title"><span class="material-icons" aria-hidden="true">person_add</span> Add team member</h2>
        <button type="button" class="modal-close" aria-label="Close" onclick="window._atmClose()">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body" id="atm-body" style="max-height:70vh;max-height:70dvh;overflow-y:auto;"></div>
    </div>`;
  modal.addEventListener('click', (e) => { if (e.target === modal) _closeAddTeamMember(); });
  document.body.appendChild(modal);
  _atmRenderChoice();
}

function _atmRenderChoice() {
  const body = document.getElementById('atm-body');
  if (!body) return;
  _atmPeople = null;
  _atmLoadSeq++;
  body.innerHTML = `
    <p style="margin:0 0 var(--spacing-lg);color:var(--text-secondary);">Who are you adding? Either way they get their own login and see only the modules you pick.</p>
    <button type="button" class="atm-choice" id="atm-choice-roster" onclick="window._atmShowRoster()">
      <span class="material-icons" aria-hidden="true">badge</span>
      <span><strong>Someone on your roster</strong><small>An employee already in Roster. You pick them next.</small></span>
    </button>
    <button type="button" class="atm-choice" id="atm-choice-outside" onclick="window._atmOutside()">
      <span class="material-icons" aria-hidden="true">person_add_alt</span>
      <span><strong>Someone not on your roster</strong><small>A login tied to no employee: a bookkeeper, compliance manager, agent or shared office login. Not on the roster, schedule or payroll, and no Clock or Job Board.</small></span>
    </button>`;
  try { document.getElementById('atm-choice-roster')?.focus(); } catch (_) {}
}

async function _atmShowRoster() {
  const body = document.getElementById('atm-body');
  if (!body) return;
  body.innerHTML = `
    <button type="button" class="btn btn-secondary" onclick="window._atmBack()" style="min-height:44px;margin-bottom:var(--spacing-md);">
      <span class="material-icons" aria-hidden="true">arrow_back</span> Back
    </button>
    <label for="atm-search" style="display:block;font-weight:600;margin-bottom:6px;color:var(--text-primary);">Who on your roster?</label>
    <input type="search" id="atm-search" class="form-control" placeholder="Search by name or email" autocomplete="off" data-lpignore="true" data-1p-ignore style="font-size:16px;margin-bottom:var(--spacing-md);" oninput="window._atmRenderPeople()">
    <div id="atm-list" style="display:flex;flex-direction:column;gap:6px;">
      <div style="padding:var(--spacing-md);text-align:center;color:var(--text-secondary);">Loading your roster…</div>
    </div>
    <div style="margin-top:var(--spacing-lg);padding-top:var(--spacing-md);border-top:1px solid var(--border-light);">
      <p style="font-size:0.85rem;color:var(--text-secondary);margin:0 0 var(--spacing-sm);">Not listed? Add them to Roster first, then come back here.</p>
      <button type="button" class="btn btn-secondary" onclick="window._atmAddPerson()" style="min-height:44px;">
        <span class="material-icons" aria-hidden="true">person_add</span> Add a new person to Roster
      </button>
    </div>`;
  try { document.getElementById('atm-search')?.focus(); } catch (_) {}
  const tenant = window.AppState?.tenant;
  const seq = ++_atmLoadSeq;
  // TASK-10094 — the list + who-has-a-login read live in a lazy chunk. The
  // login read runs beside the roster read and never rejects (complete:false).
  const mod = __ddChunkWrap(import('./views/addTeamMemberPicker.js?v=100940002'));
  const logins = mod.then(m => m.loadTeamLoginState(tenant)).catch(() => null);
  let people, m;
  try {
    const [snap, mm] = await Promise.all([getDocs(collection(db, 'tenants', tenant, 'people')), mod]);
    m = mm;
    // Same filter as Roster's CRM-team picker: active people only.
    people = snap.docs.map(d => ({ id: d.id, ...(d.data() || {}) }))
      .filter(p => p.active !== false && p.terminated !== true)
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  } catch (err) {
    console.warn('[AddTeamMember] roster load failed:', err?.message || err);
    if (seq !== _atmLoadSeq) return;
    const list = document.getElementById('atm-list');
    if (list) list.innerHTML = `<div style="padding:var(--spacing-md);background:#FEF2F2;color:#991B1B;border:1px solid #FECACA;border-radius:var(--radius-md,8px);">Couldn't load your roster. Close this and try again.</div>`;
    return;
  }
  // Closed, went Back, reopened or switched tenant meanwhile: drop this read.
  const state = await logins;
  if (seq !== _atmLoadSeq || window.AppState?.tenant !== tenant || !document.getElementById('atm-list')) return;
  _atmLogins = state;
  _atmMod = m;
  _atmPeople = people;
  _atmRenderPeople();
}

// TASK-10094 — the rows (with the "Has a login / Invite sent" tags) are built
// by the lazy chunk's atmPeopleHtml from the fresh login read, never from
// Settings' Team-list cache.
function _atmRenderPeople() {
  const list = document.getElementById('atm-list');
  if (!list || !Array.isArray(_atmPeople) || !_atmMod) return;
  list.innerHTML = _atmMod.atmPeopleHtml(_atmPeople, (document.getElementById('atm-search')?.value || '').trim().toLowerCase(), _atmLogins, escapeHtml);
}

// Leaving Settings for Roster drops unsaved Settings edits (same as Cancel), so
// ask first when there are any.
async function _atmLeaveSettingsOk() {
  const settingsModal = document.getElementById('settings-modal');
  if (!settingsModal || settingsModal.classList.contains('hidden')) return true;
  let dirty = false;
  try { dirty = hasUnsavedWork(settingsModal); } catch (_) { dirty = false; }
  if (!dirty) return true;
  try {
    return await showConfirm('Leave Settings?', 'You have Settings changes that are not saved. Adding someone from your roster opens Roster, and those changes will be lost.', {
      confirmText: 'Leave without saving', cancelText: 'Stay in Settings', danger: true, safeDefault: true,
    });
  } catch (_) { return false; }
}

// Roster owns the person card and the Set up login modal, and both need the
// roster list loaded, so hop to Roster (what CRM's "Invite to CRM Team" does),
// open the person, then the EXISTING Set up login modal.
async function _atmPickPerson(personId) {
  if (!personId) return;
  if (!(await _atmLeaveSettingsOk())) return;
  _closeAddTeamMember();
  closeSettingsModal();
  if (window.AppState?.currentView !== 'roster') navigateToView('roster');
  try { window.RosterModule?.ensureModalsMounted?.(); } catch (_) {}
  const pid = document.getElementById('person-id');
  if (pid) pid.value = ''; // a stale value must not pass the check below
  let waited = 0;
  const tryOpen = () => {
    try { window.RosterModule?.editPerson?.(personId); } catch (_) {}
    if (document.getElementById('person-id')?.value === personId) {
      setTimeout(() => window.RosterModule?.showSetupStaffLoginModal?.(), 50);
      return;
    }
    waited += 200;
    if (waited >= 8000) {
      showToast('Could not open that person. Open Roster, click them, and use Set up login.', 'error');
      return;
    }
    setTimeout(tryOpen, 200);
  };
  setTimeout(tryOpen, 150);
}

async function _atmAddPerson() {
  if (!(await _atmLeaveSettingsOk())) return;
  _closeAddTeamMember();
  closeSettingsModal();
  if (window.AppState?.currentView !== 'roster') navigateToView('roster');
  setTimeout(() => {
    try { window.RosterModule?.showAddPerson?.(); } catch (_) {}
    showToast('Save the new person, then use Set up login on their card.', 'info');
  }, 400);
}

// Outside user: the personless invite modal opens over Settings (Roster mounts
// it on <body> after the Settings modal, so it stacks above). When it closes,
// refresh the Team list so the new pending invite shows without a reopen —
// Roster's own refresh keys on currentView === 'settings', which a modal never is.
function _atmOutside() {
  _closeAddTeamMember();
  if (typeof window.RosterModule?.showInviteUserModal !== 'function') {
    showToast('Could not open the invite form. Refresh and try again.', 'error');
    return;
  }
  window.RosterModule.showInviteUserModal();
  const inviteModal = document.getElementById('modal-staff-login-invite');
  if (!inviteModal || inviteModal.classList.contains('hidden')) return;
  const obs = new MutationObserver(() => {
    if (!inviteModal.classList.contains('hidden')) return;
    obs.disconnect();
    const settingsModal = document.getElementById('settings-modal');
    if (settingsModal && !settingsModal.classList.contains('hidden')) {
      try { renderTeamAccessList(); } catch (_) {}
    }
  });
  obs.observe(inviteModal, { attributes: true, attributeFilter: ['class'] });
}

window.showSettingsSection = showSettingsSection;
window.showSettingsSectionList = showSettingsSectionList;
window.saveMemoLinkLength = saveMemoLinkLength; // TASK-9891
window.saveLinkLengths = saveLinkLengths; // TASK-9891 Slice 2
window.openAddTeamMember = openAddTeamMember;
window.syncAddTeamMemberButton = syncAddTeamMemberButton;
window._atmClose = _closeAddTeamMember;
window._atmBack = _atmRenderChoice;
window._atmShowRoster = _atmShowRoster;
window._atmRenderPeople = _atmRenderPeople;
window._atmPickPerson = _atmPickPerson;
window._atmAddPerson = _atmAddPerson;
window._atmOutside = _atmOutside;

function updateRetentionVisibility() {
  // DVIR retention visibility
  const dvirAutoPurge = document.getElementById('settings-dvir-autopurge')?.checked;
  const dvirRetentionGroup = document.getElementById('dvir-retention-group');
  if (dvirRetentionGroup) {
    dvirRetentionGroup.style.display = dvirAutoPurge ? 'block' : 'none';
  }

  // Photo retention visibility
  const photoAutoPurge = document.getElementById('settings-photo-autopurge')?.checked;
  const photoRetentionGroup = document.getElementById('photo-retention-group');
  if (photoRetentionGroup) {
    photoRetentionGroup.style.display = photoAutoPurge ? 'block' : 'none';
  }

  // Receipt retention visibility
  const receiptAutoPurge = document.getElementById('settings-receipt-autopurge')?.checked;
  const receiptRetentionGroup = document.getElementById('receipt-retention-group');
  if (receiptRetentionGroup) {
    receiptRetentionGroup.style.display = receiptAutoPurge ? 'block' : 'none';
  }
}

async function saveSettings() {
  if (!window.AppState.tenant) {
    showToast('Not logged in', 'error');
    return;
  }
  // TASK-9389 (admin backfill lane) — Save Settings writes the owner's sections
  // (company, plan, retention, PINs...). A staff login never has it: its button
  // is hidden and this refuses too (rules refuse the write regardless).
  if (memberSettingsScope()) {
    showToast('Settings are managed by the account owner.', 'info');
    return;
  }

  const saveBtn = document.querySelector('#settings-modal .modal-footer .btn-primary');
  const saveBtnOriginal = saveBtn ? saveBtn.innerHTML : 'Save Settings';
  if (saveBtn) {
    saveBtn.disabled = true;
    saveBtn.innerHTML = '<span class="btn-spinner"></span> Saving...';
  }

  const companyName = document.getElementById('settings-company-name').value.trim();
  const dotNumber = document.getElementById('settings-dot-number')?.value?.trim() || '';
  // TASK-261 Slice 1A — MC# + Insurance object (top-level on tenant doc, matching dotNumber precedent)
  const mcNumber = document.getElementById('settings-mc-number')?.value?.trim() || '';
  // Decentralized 2026-05-19 (v1.15.693) — Insurance fields + State MVR creds
  // (UT/PA) moved to Compliance Settings sub-modal (views/compliance.js
  // saveComplianceSettings). Schema unchanged. Do NOT read them here, or
  // the existingInsurance / existingCompliance.stateMvrCreds spread below
  // would overwrite the canonical write path with empty strings every time
  // someone saves Primary Settings (same pattern as the HR / Dispatch
  // decentralizations above).
  const timezone = document.getElementById('settings-timezone').value;
  const distanceUnit = document.getElementById('settings-distance-unit').value || 'miles';
  const dvirAutoPurge = document.getElementById('settings-dvir-autopurge').checked;
  const dvirRetentionMonths = parseInt(document.getElementById('settings-dvir-retention').value, 10);
  const jsaAutoPurge = document.getElementById('settings-jsa-autopurge')?.checked ?? false;
  const jsaRetentionMonths = parseInt(document.getElementById('settings-jsa-retention')?.value || '24', 10);
  const photoAutoPurge = document.getElementById('settings-photo-autopurge')?.checked ?? true;
  const photoRetentionDays = parseInt(document.getElementById('settings-photo-retention')?.value || '90', 10);
  const receiptAutoPurge = document.getElementById('settings-receipt-autopurge')?.checked ?? true;
  const receiptRetentionMonths = parseInt(document.getElementById('settings-receipt-retention')?.value || '24', 10);
  // Decentralized 2026-05-19 (v1.15.691) — Dispatch sub-modal (dispatch.js
  // openDispatchSettingsModal) owns enable811, enableLoadCalculator,
  // enableRoutePlanner, personalizedSchedule, hideComplianceWarnings. Do NOT
  // read them here, or they join `ownedSettings` below and this Save overwrites
  // the canonical write path with stale defaults (false) every time someone
  // saves Primary Settings.
  //
  // Decentralized 2026-05-06 — these settings are now owned by their module
  // sub-modals. saveSettings preserves their existing values by OMITTING them
  // from the write (TASK-9255 — `merge:true` leaves every absent key untouched;
  // it was never the `...existingSettings` spread that protected them):
  //   - Maintenance & Inspections (Inspection Settings sub-modal):
  //     dvirAccountability, jsaAccountability, enableCitations
  //   - HR & Recruiting (HR Settings sub-modal):
  //     trackOshaRecords, recordsCustodianPersonId, requireClearBgCheckBeforeRoster,
  //     applicationFlow
  //   - Billing Settings (existing modal in views/billing.js):
  //     allowDriverTimeEdit, clockInAskCustomer, captureGpsOnClock
  const enableSmartAssistant = document.getElementById('settings-enable-smart-assistant')?.checked ?? true;
  // TASK-631 — master AI off-switch. Default ON (absent → on). When false, the Gemini choke-point blocks ALL AI/model calls.
  const smartToolsEnabled = document.getElementById('settings-smart-tools-enabled')?.checked ?? true;
  // TASK-9740 — read only when the box is enabled (owner/admin); null = untouched.
  const _jdEl = document.getElementById('settings-job-documents-enabled');
  const jobDocumentsEnabled = (_jdEl && !_jdEl.disabled) ? _jdEl.checked === true : null;
  // TASK-9956 S2 — read only when the box is enabled (owner/admin, Smart Tools on); null = untouched.
  const _wrEl = document.getElementById('settings-web-research-enabled');
  const webResearchEnabled = (_wrEl && !_wrEl.disabled) ? _wrEl.checked === true : null;
  // applicationNotifyEmail + applicationContactPhone decentralized 2026-05-08
  // → saveHrRecruitingSettings owns these now. Do NOT read here, or the
  // existingSettings spread below will overwrite the canonical write path
  // with empty strings every time someone saves Primary Settings (Codex
  // catch from cod-reviews/2026-05-08T18-21).
  if (!companyName) {
    showToast('Company name is required', 'error');
    if (saveBtn) { saveBtn.disabled = false; saveBtn.innerHTML = saveBtnOriginal; }
    return;
  }
  // TASK-261 Slice 1A — MC# numeric guard (mirrors verifyDotNumber regex; empty allowed)
  if (mcNumber && !/^\d{1,10}$/.test(mcNumber)) {
    showToast('MC Number must be 1-10 digits (numbers only)', 'error');
    if (saveBtn) { saveBtn.disabled = false; saveBtn.innerHTML = saveBtnOriginal; }
    return;
  }
  // STATE_MVR_RE validation moved to views/compliance.js saveComplianceSettings
  // (v1.15.693). Same regex, same toast messages — just lives next to the inputs.

  // TASK-261 Slice 1A — preflight logo size/type guard BEFORE setDoc so a bad file doesn't leave a partial save
  {
    const logoFileEarly = document.getElementById('settings-logo-file')?.files?.[0];
    if (logoFileEarly) {
      if (logoFileEarly.size > 5 * 1024 * 1024) {
        showToast('Logo file is over 5MB. Pick a smaller image.', 'error');
        if (saveBtn) { saveBtn.disabled = false; saveBtn.innerHTML = saveBtnOriginal; }
        return;
      }
      if (logoFileEarly.type && !logoFileEarly.type.startsWith('image/')) {
        showToast('Logo must be an image file (PNG, JPG, etc).', 'error');
        if (saveBtn) { saveBtn.disabled = false; saveBtn.innerHTML = saveBtnOriginal; }
        return;
      }
    }
  }

  try {
    const tenantRef = doc(db, 'tenants', window.AppState.tenant);
    const tenantDoc = await getDoc(tenantRef);
    const existingData = tenantDoc.exists() ? tenantDoc.data() : {};
    const existingSettings = existingData.settings || {};

    // 🔴 TASK-9255 — ownedSettings carries ONLY the keys THIS modal owns. It
    // deliberately does NOT spread existingSettings, and the setDoc below
    // deliberately does NOT spread existingData.
    //
    // WHY (the bug this replaced): the old shape was
    //   newSettings = { ...existingSettings, <12 owned keys> }
    //   setDoc(tenantRef, { ...existingData, …, settings: newSettings }, { merge: true })
    // which round-tripped the ENTIRE tenant document and the ENTIRE settings
    // map through the snapshot read ~15 lines above. Measured against live
    // data (83 tenants, 2026-09-22): 139 distinct top-level tenant keys and 98
    // distinct `settings.*` keys exist in the wild, of which this modal owns
    // 4 and 12 respectively. Everything else was written back from a snapshot.
    //
    // Two distinct harms, both live:
    //  1. LOST WRITES. `merge: true` deep-merges maps but REPLACES arrays
    //     wholesale, and re-writes every scalar leaf present in the payload.
    //     Any writer that touches the tenant doc between the getDoc above and
    //     this setDoc is reverted. The consent case is the sharp one:
    //     `optOutOfAi` (functions/src/services/smsInbox.js) writes
    //     `settings.smsInbox.optedOutOfAi` + `.allowlist` from an inbound
    //     "NO AI"/"HUMAN" text; a racing Settings save put the assistant back
    //     on someone who had just asked for a human, silently. Same exposure
    //     on every other array: customTypes.{truck,trailer,asset,crew}Types,
    //     yards, safetyTrainings, applicationQuestions, hiringPacket, hrLinks,
    //     invoiceSectionOrder, clockOutFormSections, customTicketSections,
    //     driverExpenses.categories — plus every scalar leaf (accessPins,
    //     pmIntervals, jobAutoClose.*, samsaraTokenValid, …).
    //  2. SPURIOUS PERMISSION-DENIED. firestore.rules gates this doc's update
    //     on a `diff(resource.data).affectedKeys()` denylist containing plan,
    //     addons, subscription, subscriptionStatus, twilio, vipAccess, isVip,
    //     comped, complianceOnly, hasSmsPack, stripe*. `...existingData`
    //     re-wrote all of them; a server-side change to any one inside the
    //     window makes it "affected" and the rule REFUSES the whole save. The
    //     hottest is `twilio.creditUsed`, incremented on EVERY outbound SMS
    //     (functions/src/index.js:16212). Writing only owned keys means
    //     affectedKeys() can never contain a denylisted name.
    //
    // 🔴 Do NOT "fix" a future clobber by re-reading just before the write —
    // that is the same TOCTOU window, only narrower. The invariant is: this
    // function writes what this modal owns and NOTHING else.
    //
    // `merge: true` already preserves every omitted key — including the
    // decentralized ones (dvirAccountability, jsaAccountability,
    // enableCitations, trackOshaRecords, recordsCustodianPersonId,
    // requireClearBgCheckBeforeRoster, applicationFlow, applicationQuestions,
    // enable811, enableLoadCalculator, enableRoutePlanner, personalizedSchedule,
    // hideComplianceWarnings, applicationNotifyEmail, applicationContactPhone,
    // accessPins, accountingBasis, buildTicketsFromClock, …) — so the spread
    // was never what protected them. Omission is.
    //
    // setDoc(…, { merge: true }) rather than updateDoc(dotted paths): identical
    // per-leaf field mask for these all-scalar keys, but it still CREATES the
    // doc when tenantDoc.exists() is false, which updateDoc would reject with
    // NOT_FOUND. Same reason the exists() branch above is tolerated.
    const ownedSettings = {
      timezone,
      distanceUnit,
      dvirAutoPurge,
      dvirRetentionMonths,
      jsaAutoPurge,
      jsaRetentionMonths,
      photoAutoPurge,
      photoRetentionDays,
      receiptAutoPurge,
      receiptRetentionMonths,
      // enable811, enableLoadCalculator, enableRoutePlanner,
      // personalizedSchedule, hideComplianceWarnings — owned by Dispatch
      // sub-modal (dispatch.js openDispatchSettingsModal) as of v1.15.691.
      // Preserved by OMISSION under merge:true (TASK-9255).
      enableSmartAssistant,
      smartToolsEnabled,
      // applicationNotifyEmail + applicationContactPhone owned by HR
      // & Recruiting Settings (saveHrRecruitingSettings) — preserved by
      // OMISSION under merge:true (TASK-9255).
      // accessPins is managed separately via PIN modal
      // accountingBasis moved to Billing Settings modal
      // buildTicketsFromClock, showTicketsInDriverView, showPricesOnDriverTickets moved to Billing module settings
    };

    // TASK-9740 — jobDocumentsEnabled is written ONLY when it changed (owner/
    // admin box), so a non-admin's Save never carries a key the rules refuse
    // to them. Added as a named key, never a spread (TASK-9255 G4).
    if (jobDocumentsEnabled !== null && jobDocumentsEnabled !== (existingSettings.jobDocumentsEnabled === true)) {
      ownedSettings.jobDocumentsEnabled = jobDocumentsEnabled;
    }
    // TASK-9956 S2 — same rule: written only when the owner/admin box changed.
    if (webResearchEnabled !== null && webResearchEnabled !== (existingSettings.webResearchEnabled === true)) {
      ownedSettings.webResearchEnabled = webResearchEnabled;
    }

    // Samsara token: only update if user typed a new value (not the masked
    // placeholder). TASK-1621 — moved OFF this doc's `settings` map entirely
    // (was broadcast to every driver-PIN session via app.js's PIN-mode
    // tenant-doc read). Written via the saveSamsaraToken callable AFTER the
    // main setDoc below succeeds, straight into the backend-only
    // secrets/samsara sibling — never through newSettings/this setDoc.
    const samsaraTokenInput = document.getElementById('settings-samsara-token')?.value?.trim();

    // Insurance + State MVR Credentials writes moved to views/compliance.js
    // saveComplianceSettings (v1.15.693). That handler:
    //   - reads getDoc(tenantRef) for forward-compat `existingInsurance` spread
    //   - writes `tenant.insurance` (top-level) via updateDoc
    //   - routes UT/PA through the `saveStateMvrCredAsTenant` callable
    //     (TASK-287 Phase B — server-side audit log + admin-only subcollection
    //     dual-write).
    // Main Settings save no longer touches tenant.insurance or tenant.compliance —
    // those keys are preserved as-is by Firestore merge:true semantics.
    //
    // 🔴 TASK-9255 — four top-level keys, twelve settings leaves, nothing else.
    // Never re-introduce `...existingData` here (see the ownedSettings comment).
    await setDoc(tenantRef, {
      name: companyName,
      dotNumber: dotNumber || null,
      mcNumber: mcNumber || null,
      settings: ownedSettings
    }, { merge: true });

    // 🔴 TASK-9255 — anything the LOCAL mirror must reflect that this function
    // did NOT write to `settings` itself gets staged here and applied at the
    // bottom. Deliberately a patch and not a pre-built map: the mirror is
    // assembled AFTER the awaits below, never before them (see there for why).
    const localSettingsPatch = {};
    const localSettingsDeletes = [];

    // TASK-1621 — Samsara token save, routed through a callable so the raw
    // value goes straight to the backend (secrets/samsara) and never through
    // this doc's broadcast `settings` map. Failure here does not roll back
    // the rest of Settings (matches the prior behavior where a bad Samsara
    // save didn't block anything else).
    let samsaraSaveFailed = false;
    if (samsaraTokenInput) {
      try {
        await httpsCallable(functions, 'saveSamsaraToken')({ tenantId: window.AppState.tenant, token: samsaraTokenInput });
        // The callable itself wrote settings.samsaraConnected / deleted
        // settings.samsaraTokenValid server-side (functions index.js ~:38686).
        // Stage the same shape for the local mirror only.
        localSettingsPatch.samsaraConnected = true;
        localSettingsDeletes.push('samsaraTokenValid');
        window.logAudit?.('settings', 'SAVE_SETTINGS', { changed: { samsaraApiToken: { from: '(unchanged)', to: '(updated)' } } });
      } catch (samsaraErr) {
        console.error('[Settings] Error saving Samsara token:', samsaraErr);
        samsaraSaveFailed = true;
      }
    }

    // Handle logo upload (founder + clockbilling subscribers)
    let logoUploadFailed = false;
    if (hasAddOnPackage('clockbilling')) {
      let logoFile = document.getElementById('settings-logo-file')?.files?.[0];
      if (logoFile) {
        try {
          // Auto-compress if over 500KB
          if (logoFile.size > 500 * 1024) {
            console.log('[Settings] Logo too large (' + Math.round(logoFile.size / 1024) + 'KB), compressing...');
            const compressed = await compressImage(logoFile, 500 * 1024, 800);
            if (compressed) {
              logoFile = compressed;
              console.log('[Settings] Compressed to ' + Math.round(compressed.size / 1024) + 'KB');
            }
          }
          const logoRef = ref(storage, `tenants/${window.AppState.tenant}/branding/company-logo`);
          await uploadBytes(logoRef, logoFile, { contentType: logoFile.type || 'image/jpeg' });
          const logoUrl = await getDownloadURL(logoRef);
          await setDoc(tenantRef, { companyLogoUrl: logoUrl }, { merge: true });
          if (window.AppState.tenantData) window.AppState.tenantData.companyLogoUrl = logoUrl;
          console.log('[Settings] Logo uploaded:', logoUrl);
        } catch (logoErr) {
          console.error('[Settings] Logo upload error:', logoErr);
          showToast('Error uploading logo: ' + (logoErr.message || 'Unknown error'), 'error');
          logoUploadFailed = true;
        }
      }
    }

    // enable811 reload-guard moved to dispatch.js saveDispatchSettings as of
    // v1.15.691 (the toggle no longer lives in this modal).

    // Update local state.
    //
    // 🔴 TASK-9255 — LOCAL MIRROR ONLY, and built HERE, not earlier. It is never
    // written to Firestore; it exists so AppState.settings /
    // tenantData.settings stay a COMPLETE picture after the save.
    //
    // Two rules, each paid for by a different bug:
    //
    //  MERGE, NEVER REPLACE. Assigning the write payload straight onto AppState
    //  (what the old code did, back when the payload WAS the whole map) would
    //  now leave AppState.settings holding 12 keys and nothing else until the
    //  tenant-doc onSnapshot (subscribeTenantSettings, ~:15495) caught up — and
    //  `canManageAccessPins()` / `hasPrimaryPinAccess()` read
    //  `AppState.settings.accessPins` SYNCHRONOUSLY. An empty accessPins map
    //  takes the zero-PIN anti-lockout bootstrap and returns TRUE, i.e. the
    //  PIN-management surface would unlock for a limited-PIN holder for the
    //  width of one round trip.
    //
    //  BUILD IT LAST. (COD Code Reviewer + Edge Case Hunter, independently, on
    //  this diff — and true of the pre-TASK-9255 code too.) The Samsara
    //  callable and the logo compress+upload above are awaits that can run for
    //  SECONDS. `subscribeTenantSettings` keeps firing through them, so a map
    //  snapshotted before the awaits is stale by the time it is assigned — and
    //  the sharpest case is a PIN revocation landing mid-upload, which a
    //  pre-built mirror would put straight back. Read AppState at the moment of
    //  assignment, not at the top of the function.
    //
    // Base = the LIVE listener view, with the save-time read only as a FALLBACK
    // for the case where the listener has not populated anything yet (demo, a
    // PIN session, a listener error). Layering the snapshot UNDERNEATH the live
    // view instead would resurrect a key that was DELETED during the awaits —
    // COD round 2, Code Reviewer. Then what we just wrote, then anything a
    // callable above wrote server-side on our behalf.
    const liveSettings = window.AppState.settings;
    const mirrorBase = (liveSettings && Object.keys(liveSettings).length) ? liveSettings : existingSettings;
    const newSettings = {
      ...mirrorBase,
      ...ownedSettings,
      ...localSettingsPatch
    };
    localSettingsDeletes.forEach((k) => { delete newSettings[k]; });

    window.AppState.settings = newSettings;
    // TASK-634: keep tenantData.settings (the source applySmartToolsVisibility reads)
    // in sync, then re-apply AI entry-point visibility live so toggling Smart Tools
    // off/on in Settings hides/shows the AI buttons immediately without a reload.
    if (window.AppState.tenantData) window.AppState.tenantData.settings = newSettings;
    if (typeof applySmartToolsVisibility === 'function') applySmartToolsVisibility();
    // TASK-591 — log from→to on CHANGED keys only (was: key names only, which
    // couldn't answer "what changed"). Old values come from the PRE-save
    // Firestore read (existingData/existingSettings above), normalized with
    // the same defaults the modal populate + this save use, so an open→save
    // with no edits logs NOTHING. Samsara token is redacted (secret).
    try {
      const auditChanged = {};
      const auditDiff = (key, from, to) => { if (from !== to) auditChanged[key] = { from, to }; };
      auditDiff('name', existingData.name || '', companyName);
      auditDiff('dotNumber', existingData.dotNumber || null, dotNumber || null);
      auditDiff('mcNumber', existingData.mcNumber || null, mcNumber || null);
      auditDiff('timezone', existingSettings.timezone || 'America/Chicago', timezone);
      auditDiff('distanceUnit', existingSettings.distanceUnit || 'miles', distanceUnit);
      auditDiff('dvirAutoPurge', existingSettings.dvirAutoPurge !== false, dvirAutoPurge);
      auditDiff('dvirRetentionMonths', existingSettings.dvirRetentionMonths || 6, dvirRetentionMonths);
      auditDiff('jsaAutoPurge', existingSettings.jsaAutoPurge === true, jsaAutoPurge);
      auditDiff('jsaRetentionMonths', existingSettings.jsaRetentionMonths || 24, jsaRetentionMonths);
      auditDiff('photoAutoPurge', existingSettings.photoAutoPurge !== false, photoAutoPurge);
      auditDiff('photoRetentionDays', existingSettings.photoRetentionDays || 90, photoRetentionDays);
      auditDiff('receiptAutoPurge', existingSettings.receiptAutoPurge !== false, receiptAutoPurge);
      auditDiff('receiptRetentionMonths', existingSettings.receiptRetentionMonths || 24, receiptRetentionMonths);
      auditDiff('enableSmartAssistant', existingSettings.enableSmartAssistant !== false, enableSmartAssistant);
      auditDiff('smartToolsEnabled', existingSettings.smartToolsEnabled !== false, smartToolsEnabled);
      if (jobDocumentsEnabled !== null) auditDiff('jobDocumentsEnabled', existingSettings.jobDocumentsEnabled === true, jobDocumentsEnabled);
      if (webResearchEnabled !== null) auditDiff('webResearchEnabled', existingSettings.webResearchEnabled === true, webResearchEnabled);
      // Samsara token audit entry is logged separately, right after the
      // saveSamsaraToken callable call above (TASK-1621 — the raw value is
      // no longer in existingSettings/newSettings to diff here).
      if (Object.keys(auditChanged).length) {
        window.logAudit?.('settings', 'SAVE_SETTINGS', { changed: auditChanged });
      }
    } catch (auditErr) {
      console.warn('[Settings] Audit log failed:', auditErr);
    }

    // TASK-10256 — a changed link length saves through its own owner-only
    // callable (securitySettings.js saveChanged); never close over a lost one.
    const ss = window.SecuritySettings;
    if (!(ss?.saveChanged ? await ss.saveChanged() : !hasUnsavedWork(document.getElementById('settings-section-security')))) {
      showToast('Settings saved, but a link length did not save. See Security.', 'error');
      showSettingsSection('security');
      return;
    }

    if (logoUploadFailed && samsaraSaveFailed) {
      showToast('Settings saved but logo upload and Samsara token save failed', 'error');
      closeSettingsModal();
    } else if (logoUploadFailed) {
      showToast('Settings saved but logo upload failed', 'error');
      closeSettingsModal();
    } else if (samsaraSaveFailed) {
      showToast('Settings saved but Samsara token save failed', 'error');
      closeSettingsModal();
    } else {
      showToast('Settings saved', 'success');
      closeSettingsModal();
    }
  } catch (error) {
    console.error('[Settings] Error saving:', error);
    showToast('Error saving settings', 'error');
  } finally {
    if (saveBtn) {
      saveBtn.disabled = false;
      saveBtn.innerHTML = saveBtnOriginal;
    }
  }
}

// ============================================
// ACCESS PIN MANAGEMENT (Settings)
// ============================================

// True if the current session has owner-level access (founder, or unlocked via the Primary PIN).
// TASK-748/719-A — VIP/comped accounts honor the Primary-PIN gate like everyone
// else (per-device lock): a locked VIP device must enter the Primary PIN to
// reach Primary-PIN-only surfaces (access log). Founder still bypasses.
function hasPrimaryPinAccess() {
  if (isFounderAccount()) return true;
  const tenantId = window.AppState?.tenant;
  if (!tenantId) return false;
  try {
    const stored = JSON.parse(localStorage.getItem(`dispatchdeck_pin_${tenantId}`) || 'null');
    if (!stored?.pin) return false;
    const accessPins = accessPinMap();
    return !!accessPins[stored.pin]?.isPrimary;
  } catch { return false; }
}
window.hasPrimaryPinAccess = hasPrimaryPinAccess;

// TASK-1577 — the authority check for the Access-PIN MANAGEMENT surface.
// Before this, every PIN-management entry point (list/add/save/edit/delete) was
// unguarded: openSettingsModal only checks that SOME pin blob is in localStorage,
// so a dispatch-only PIN reached Settings and could tick every module onto its own
// PIN, mint a fresh all-access PIN, or overwrite the Primary PIN's code and lock
// the owner out (deleteAccessPin blocked deleting the primary, never rewriting it).
//
// The rules layer CANNOT fix this: PIN holders are not distinct identities, they
// share the tenant's single Firebase Auth login, so the server sees the owner no
// matter which PIN was typed. A client-side gate is the correct fix here, not a
// workaround — there is no per-PIN identity to scope a Firestore rule to.
//
// Deliberately gates ONLY the PIN surface, not the whole Settings modal: dispatchers
// and mechanics legitimately use notification prefs, fleet defaults and integration
// toggles daily, and locking them out of all of Settings is the TASK-428 trap again.
//
// ANTI-LOCKOUT: a tenant with zero PINs falls through, otherwise the very first
// Primary PIN could never be created (openSettingsModal's own gate at ~:16498 has
// the same carve-out, as does hasFullModuleAccess at ~:14266). Recovery for a
// holder who unlocked with a limited PIN is the lock pill (clearPinAccess →
// re-challenge), and requestPrimaryPinRecovery (~:14030) if the code itself is lost.
//
// 🔴 DELIBERATELY does NOT reuse hasPrimaryPinAccess()'s founder bypass.
// isFounderAccount() (:7866) tests the TENANT (FOUNDER_TENANTS), not the person —
// so inheriting it would make this whole fix INERT on trippin-trucking, which is
// exactly the tenant running shared yard tablets with limited PINs, and the account
// Chris was dogfooding when he found the bug. Same reasoning TASK-791 used when it
// removed the founder bypass from openSettingsModal: no TITS-special.
// hasPrimaryPinAccess() keeps its bypass so no other caller changes behavior, but
// its two access-log call sites were since MOVED onto this predicate (see
// updateAuditLogButtonVisibility below): the E2E showed that gate was inert on the
// founder tenant for exactly the same reason, so leaving it would have shipped a
// known-broken gate next to a fixed one.
function canManageAccessPins() {
  const accessPins = accessPinMap();
  if (Object.keys(accessPins).length === 0) return true;

  const tenantId = window.AppState?.tenant;
  if (!tenantId) return false;
  try {
    const stored = JSON.parse(localStorage.getItem(`dispatchdeck_pin_${tenantId}`) || 'null');
    if (!stored?.pin) return false;
    return !!accessPins[stored.pin]?.isPrimary;
  } catch { return false; }
}
window.canManageAccessPins = canManageAccessPins;

// 🔴 TASK-1577 (COD round 3) — the WRITE-side gate. canManageAccessPins() above
// reads window.AppState.settings, which is a mutable global: `AppState.settings
// .accessPins = {}` in devtools makes it return true via the zero-PIN bootstrap,
// and saveAccessPin's own fresh read then merges a new all-modules PIN into the
// REAL map. Same trick on deleteAccessPin walks past the isPrimary-delete guard.
// So every path that WRITES re-derives authority from the SERVER's copy instead.
//
// Division of labour: canManageAccessPins() (sync, client state) decides what to
// RENDER — it must stay sync because the render paths can't await, and a wrong
// answer there only paints a panel. assertCanManageAccessPins() (async, server
// state) decides what may WRITE. Never use the sync one to guard a mutation.
//
// ⚠️ HONEST LIMIT, unchanged and Chris's call to make (see TASK-1577's 🟠 note):
// this raises the bar, it does not make PINs a security boundary. All PIN holders
// share the tenant's one Firebase Auth login, so a determined console user can
// still call updateDoc() directly and Firestore will authorize it — there is no
// per-PIN identity for a rule to scope to. Real isolation = distinct staff logins
// (members/{uid}.modules, TASK-319). What this closes is the CLICK path: the
// escalation Chris hit by tapping buttons with a dispatch-only PIN.
async function assertCanManageAccessPins() {
  const tenantId = window.AppState?.tenant;
  if (!tenantId) return false;
  try {
    const snap = await getDoc(doc(db, 'tenants', tenantId));
    const serverPins = accessPinMap((snap.exists() && snap.data()?.settings) || {});
    // Bootstrap, decided by the server rather than by AppState: a tenant with no
    // PINs on the tenant doc must still be able to create its first Primary PIN.
    if (Object.keys(serverPins).length === 0) return true;

    const stored = JSON.parse(localStorage.getItem(`dispatchdeck_pin_${tenantId}`) || 'null');
    if (!stored?.pin) return false;
    return !!serverPins[stored.pin]?.isPrimary;
  } catch (err) {
    // Fail CLOSED. A read failure here must not hand out PIN-management rights;
    // the caller shows the standard refusal and the holder retries.
    console.warn('[AccessPins] authority re-check failed, refusing:', err?.message);
    return false;
  }
}
window.assertCanManageAccessPins = assertCanManageAccessPins;

// Shared refusal for the write paths below, so the message stays identical
// whichever entry point a non-primary holder reaches.
function _refuseAccessPinManagement() {
  showToast('Access PINs can only be changed by the Primary PIN holder.', 'error');
}

// TASK-1577 (COD 🔴, folded in) — Team Access is the SAME escalation, one section
// down in the same modal. revokeUserAccess/resendUserInvite are server-gated on
// requireTenantAdmin, which passes for ANY PIN holder because they all share the
// tenant's one Firebase Auth login — so a dispatch-only PIN could revoke the
// owner's admins and other staff logins. Adversary, AuthZ Sentinel, Architect and
// Code Reviewer all landed on this independently. Same predicate, same recovery.
function _refuseTeamAccessManagement() {
  // TASK-9389 — a staff login is refused by the owner's switch, not a PIN.
  showToast(memberSettingsScope() ? 'Only the account owner, or someone the owner lets manage logins, can change logins.' : 'Staff logins can only be changed by the Primary PIN holder.', 'error');
}

// Hide the "View Access Log" button unless the viewer has Primary PIN access.
//
// TASK-1577 — switched from hasPrimaryPinAccess() to canManageAccessPins().
// The two agree everywhere EXCEPT the founder tenant: hasPrimaryPinAccess() starts
// with isFounderAccount(), which tests the TENANT (FOUNDER_TENANTS), not the person,
// so on trippin-trucking it returned true for ANY PIN holder and this gate was
// INERT on the one tenant running shared yard tablets. The access log records who
// unlocked which modules and when — a mechanic PIN should not read it.
//
// Caught by tests/task1577-pin-management-gate.spec.js: the lock panel, the hidden
// Add button and the zero PIN rows all asserted correctly for a limited PIN, and
// this button was still visible. Same defect class as the founder bypass this task
// already declined to inherit for PIN management — it was simply already present in
// the older TASK-748/719-A gate, and only showed up once something exercised it.
//
// hasPrimaryPinAccess() itself is deliberately left alone (still exported) so this
// change cannot alter any other caller's behavior.
function updateAuditLogButtonVisibility() {
  const btn = document.querySelector('[onclick="showPinAuditLog()"]');
  if (btn) btn.style.display = canManageAccessPins() ? '' : 'none';
}
window.updateAuditLogButtonVisibility = updateAuditLogButtonVisibility;

// Show the PIN audit log modal
async function showPinAuditLog() {
  // TASK-1577 — same swap as updateAuditLogButtonVisibility above: the button is
  // reachable from the console even when hidden, so the guard has to move with it.
  if (!canManageAccessPins()) {
    showToast('Access log is only available to the Primary PIN holder.', 'error');
    return;
  }
  const modal = document.getElementById('pin-audit-log-modal');
  const list = document.getElementById('pin-audit-log-list');
  if (!modal || !list) return;
  list.innerHTML = `<div style="text-align:center;padding:var(--spacing-lg);color:var(--text-muted);">Loading...</div>`;
  modal.classList.remove('hidden');

  const tenantId = window.AppState?.tenant;
  if (!tenantId || tenantId === 'demo') {
    list.innerHTML = `<div style="text-align:center;padding:var(--spacing-lg);color:var(--text-muted);">No log available in demo mode.</div>`;
    return;
  }

  try {
    const q = query(collection(db, 'tenants', tenantId, 'pinAuditLog'), orderBy('unlockedAt', 'desc'), limit(100));
    const snap = await getDocs(q);
    if (snap.empty) {
      list.innerHTML = `
        <div style="text-align:center;padding:var(--spacing-lg);color:var(--text-muted);">
          No PIN unlocks recorded yet. The log starts filling as people enter PINs to access modules.
        </div>`;
      return;
    }
    const moduleLabels = { '811': 'Locates (811)', billing: 'AR / AP', workforce: 'Workforce', bi: 'BI' };
    list.innerHTML = snap.docs.map(d => {
      const e = d.data();
      const when = e.unlockedAt?.toDate ? e.unlockedAt.toDate() : new Date(e.unlockedAt);
      const whenStr = when ? when.toLocaleString() : '';
      const mods = (e.modules || []).map(m => moduleLabels[m] || capitalizeFirst(m)).join(', ') || 'None';
      const ua = e.userAgent || '';
      const device = /iPhone|iPad/i.test(ua) ? 'iOS' : /Android/i.test(ua) ? 'Android' : /Mac/i.test(ua) ? 'Mac' : /Windows/i.test(ua) ? 'Windows' : 'Web';
      const labelLine = e.label ? `<strong>${escapeHtml(e.label)}</strong> · ` : '';
      return `
        <div style="padding:var(--spacing-sm) var(--spacing-md);background:var(--surface-secondary);border-radius:var(--border-radius);">
          <div style="display:flex;justify-content:space-between;gap:var(--spacing-sm);flex-wrap:wrap;">
            <div style="font-family:monospace;font-weight:500;">${labelLine}${escapeHtml(e.pin || '')}</div>
            <div style="font-size:0.8rem;color:var(--text-secondary);">${escapeHtml(whenStr)}</div>
          </div>
          <div style="font-size:0.8rem;color:var(--text-secondary);margin-top:2px;">Unlocked: ${escapeHtml(mods)} · ${device}</div>
        </div>
      `;
    }).join('');
  } catch (err) {
    console.error('[PIN Audit] Load failed:', err);
    list.innerHTML = `<div style="text-align:center;padding:var(--spacing-lg);color:var(--danger);">Failed to load log: ${escapeHtml(err?.message || 'Unknown error')}</div>`;
  }
}
function closePinAuditLog() {
  document.getElementById('pin-audit-log-modal')?.classList.add('hidden');
}
window.showPinAuditLog = showPinAuditLog;
window.closePinAuditLog = closePinAuditLog;

// TASK-9400 — delegated click handler for the Edit / Delete buttons in the PIN
// list. The key comes from dataset.pin (the browser has already decoded the
// attribute), so it reaches editAccessPin/deleteAccessPin byte-identical and is
// never compiled as JavaScript. Both callees re-check permission themselves.
function _onAccessPinsListClick(e) {
  const btn = e?.target?.closest?.('button[data-pin-action]');
  if (!btn || (e.currentTarget && !e.currentTarget.contains(btn))) return;
  const pin = btn.dataset.pin;
  if (typeof pin !== 'string' || !pin) return;
  const action = btn.dataset.pinAction;
  if (action === 'edit') editAccessPin(pin);
  else if (action === 'delete') deleteAccessPin(pin);
}

// Render the list of access PINs in settings modal
function renderAccessPinsList() {
  const container = document.getElementById('access-pins-list');
  if (!container) return;

  updateAuditLogButtonVisibility();

  // TASK-1577 — non-primary holders see the section but no codes and no controls.
  // Showing WHY beats a section that silently vanishes: a dispatcher who can't find
  // PIN settings files a support ticket, one who reads this line does not.
  const addBtn = document.getElementById('add-access-pin-btn');
  // TASK-9858 — the codes are owner-only now; a non-owner login gets no PIN
  // section at all rather than a panel that can only fail.
  const _sec = document.getElementById('access-pins-section');
  const _ownerId = window.AppState?.tenantData?.ownerId;
  const _isOwner = !_ownerId || _ownerId === auth?.currentUser?.uid || window.AppState?.isDemo;
  if (_sec) _sec.style.display = _isOwner ? '' : 'none';
  if (!_isOwner) return;
  if (!canManageAccessPins()) {
    if (addBtn) addBtn.style.display = 'none';
    container.innerHTML = `
      <div style="padding:var(--spacing-md);background:var(--surface-secondary);border-radius:var(--border-radius);color:var(--text-secondary);display:flex;align-items:center;gap:var(--spacing-sm);">
        <span class="material-icons" style="font-size:20px;color:var(--text-muted);">lock</span>
        <span>Access PINs are managed by the Primary PIN holder.</span>
      </div>
    `;
    return;
  }
  if (addBtn) addBtn.style.display = '';

  // TASK-9400 — ONE delegated listener per container element. The flag lives on
  // the element, so a re-mounted Settings modal gets its own binding.
  if (!container._accessPinsDelegated) {
    container.addEventListener('click', _onAccessPinsListClick);
    container._accessPinsDelegated = true;
  }

  // TASK-9858 — the codes come from the owner-only private doc, loaded on demand.
  const accessPins = accessPinsFull();
  if (!accessPins) {
    container.innerHTML = '<div style="padding:var(--spacing-md);color:var(--text-secondary);">Loading PINs…</div>';
    loadAccessPinsFull().then(() => { if (container.isConnected !== false) renderAccessPinsList(); }).catch((err) => {
      console.warn('[AccessPins] load failed:', err?.code || err?.message);
      container.textContent = 'Could not load the access PINs. Only the account owner can manage them.';
    });
    return;
  }
  const pins = Object.keys(accessPins);

  if (pins.length === 0) {
    container.innerHTML = `
      <div style="padding:var(--spacing-md);background:var(--surface-secondary);border-radius:var(--border-radius);text-align:center;color:var(--text-muted);">
        No access PINs configured yet. Create your Primary PIN to get started.
      </div>
    `;
    return;
  }

  // Sort so primary PIN is first
  const sortedPins = pins.sort((a, b) => {
    const aIsPrimary = accessPins[a].isPrimary;
    const bIsPrimary = accessPins[b].isPrimary;
    if (aIsPrimary && !bIsPrimary) return -1;
    if (!aIsPrimary && bIsPrimary) return 1;
    return 0;
  });

  container.innerHTML = sortedPins.map(pin => {
    const config = accessPins[pin];
    const modules = config.modules || [];
    const label = config.label || 'Unlocks';
    const isPrimary = config.isPrimary;
    const maskedPin = escapeHtml(pin.slice(0, -2).replace(/./g, '*') + pin.slice(-2));
    // TASK-9400 — the key rides in a data attribute (escAttr is quote-safe), never
    // inside onclick="fn('...')": no HTML escaper makes a JS string inside an
    // attribute safe. _onAccessPinsListClick reads dataset.pin instead.
    const pinAttr = window.escAttr(pin);

    return `
      <div class="access-pin-row" style="display:flex;align-items:center;gap:var(--spacing-sm);padding:var(--spacing-sm) var(--spacing-md);background:var(--surface-secondary);border-radius:var(--border-radius);${isPrimary ? 'border:1px solid var(--accent-cyan);' : ''}">
        <span class="material-icons" style="color:${isPrimary ? 'var(--accent-cyan)' : 'var(--text-muted)'};font-size:20px;">${isPrimary ? 'verified_user' : 'pin'}</span>
        <div style="flex:1;min-width:0;">
          <div style="font-weight:500;font-family:monospace;">
            ${maskedPin}
            ${isPrimary ? '<span style="font-size:0.7rem;background:var(--accent-cyan);color:#000;padding:2px 6px;border-radius:4px;margin-left:8px;font-family:inherit;">PRIMARY</span>' : ''}
          </div>
          <div style="font-size:0.8rem;color:var(--text-secondary);">${escapeHtml(label)}: ${escapeHtml(modules.map(m => m === '811' ? 'Locates (811)' : capitalizeFirst(String(m))).join(', ') || 'None')}${isPrimary ? ' + Smart Tools' : ''}</div>
        </div>
        <button type="button" class="btn btn-xs btn-secondary" data-pin-action="edit" data-pin="${pinAttr}" title="Edit">
          <span class="material-icons" style="font-size:16px;">edit</span>
        </button>
        ${!isPrimary ? `
          <button type="button" class="btn btn-xs btn-danger-outline" data-pin-action="delete" data-pin="${pinAttr}" title="Delete">
            <span class="material-icons" style="font-size:16px;">delete</span>
          </button>
        ` : ''}
      </div>
    `;
  }).join('');
}

// ============================================
// TEAM ACCESS LIST (TASK-319 slice 6)
// Mirror of Access PINs section for per-user staff logins.
// Reads tenants/{tid}/members — source of truth for accepted logins.
// Pending invites live in _userInviteTokens (server-only); deferred to follow-up.
// v1 scope: list + revoke + cap chip. No resend, no edit-modules.
// ============================================
const TEAM_ACCESS_SOFT_CAP = 50;

// Pretty label for module slugs (mirrors STAFF_LOGIN_ROLE_PRESETS in roster.js)
function _prettyModuleLabel(slug) {
  if (!slug) return '';
  if (slug === '811-permits' || slug === '811') return 'Locates (811)';
  if (slug === 'hr-directory') return 'HR Directory';
  if (slug === 'bi') return 'BI';
  if (slug === 'hr') return 'HR';
  if (slug === 'crm') return 'CRM';
  if (slug === 'ap') return 'AP';
  if (slug === 'jobboard') return 'Job Board';
  if (slug === 'receipts') return 'Receipts & Expenses'; // TASK-9778
  return slug.charAt(0).toUpperCase() + slug.slice(1);
}

// TASK-375 slice B — module-scoped cache of the last-loaded Team data so the
// client-side filter can re-render without re-fetching. Only the current
// tenant's members/invites are ever stored here (tenant-scoped fetch below).
let _teamAccessData = { members: [], pendingInvites: [], activeCount: 0 };

// TASK-375 slice B — "full access" = owner, or a legacy admin with no explicit
// modules array (matches the auth model: owner / legacy-admin = full access).
// An admin WITH an explicit modules[] is shown by their actual grants (honest,
// and avoids the brittle ">= N modules" heuristic flagged by COD Domain).
function _teamMemberIsFullAccess(m) {
  const role = m?.role || 'staff';
  if (role === 'owner') return true;
  const mods = Array.isArray(m?.modules) ? m.modules : [];
  return role === 'admin' && mods.length === 0;
}

// TASK-10086-R — client twin of the members LIST rule (firestore.rules
// mbrLister, TASK-10086-M): admin/owner, a login with no modules list (legacy
// full access), or a roster/hr/notes/projects/dispatch holder. RENDER gate only;
// canManageAccessPins() is not one (zero PINs = true for everyone). No members
// doc (memberAccess null) is left to the query, whose denial shows the same line.
function canListTeamMembers(a = window.AppState) {
  const m = a?.memberAccess;
  return !m || !!a.isDemo || m.role === 'owner' || m.role === 'admin' || !Array.isArray(m.modules)
    || m.modules.some(x => ['roster', 'hr', 'notes', 'projects', 'dispatch'].includes(x));
}
// The Team card's lock line: the PIN gate (TASK-1577), or with `admin` the list rule.
function _teamLock(el, admin) {
  el.innerHTML = `<div style="padding:var(--spacing-md);background:var(--surface-secondary);border-radius:var(--border-radius);color:var(--text-secondary);display:flex;align-items:center;gap:var(--spacing-sm);"><span class="material-icons" style="font-size:20px;color:var(--text-muted);">lock</span><span>${admin ? 'Ask an admin to manage staff logins.' : 'Staff logins are managed by the Primary PIN holder.'}</span></div>`;
}

async function renderTeamAccessList() {
  const container = document.getElementById('team-access-list');
  if (!container) return;

  // TASK-1577 — same gate as the Access-PIN section above. Returning before the
  // fetch also keeps staff emails off a shared yard tablet unlocked with a
  // limited PIN, which the old unconditional render leaked.
  if (!_teamAccessRenderOk()) return _teamLock(container); // TASK-9389 — the owner's switch for a staff login, else the PIN gate
  if (!canListTeamMembers()) return _teamLock(container, 1);

  const tenant = window.AppState?.tenant;
  if (!tenant) {
    container.innerHTML = `<div style="padding:var(--spacing-md);background:var(--surface-secondary);border-radius:var(--border-radius);text-align:center;color:var(--text-muted);">Sign in to manage team access.</div>`;
    return;
  }

  // Loading placeholder
  container.innerHTML = `<div style="padding:var(--spacing-md);background:var(--surface-secondary);border-radius:var(--border-radius);text-align:center;color:var(--text-muted);"><span class="loading-spinner-small" style="margin-right:8px;"></span>Loading staff logins...</div>`;

  let members = [];
  let pendingInvites = [];
  try {
    // TASK-349 — fetch active members AND pending invites in parallel.
    // `_userInviteTokens` is rules-locked server-only, so pending invites
    // come via the `listPendingInvites` admin callable. Failures on the
    // pending-invite branch are non-fatal — the member list still renders.
    const membersSnapPromise = getDocs(collection(db, 'tenants', tenant, 'members'));
    const pendingPromise = httpsCallable(functions, 'listPendingInvites')({ tenantId: tenant })
      .catch(err => {
        console.warn('[TeamAccess] listPendingInvites failed (non-fatal):', err?.message);
        return null;
      });

    const [snap, pendingRes] = await Promise.all([membersSnapPromise, pendingPromise]);
    members = snap.docs
      .map(d => ({ uid: d.id, ...(d.data() || {}) }))
      // Filter phantom legacy member docs (TITS had 637, S&F 4, demo 7 as of 2026-05-19).
      // Real members have email OR displayName OR explicit non-empty modules.
      // Always keep `role === 'owner'` so the owner row never gets hidden by missing fields.
      .filter(m => m.role === 'owner' || m.email || m.displayName || m.name || (Array.isArray(m.modules) && m.modules.length > 0));
    pendingInvites = Array.isArray(pendingRes?.data?.invites) ? pendingRes.data.invites : [];
  } catch (err) {
    console.error('[TeamAccess] Failed to load members:', err);
    // TASK-10086-R — a non-lister the gate let through (no members doc, partner).
    if (err?.code === 'permission-denied') return _teamLock(container, 1);
    container.innerHTML = `<div style="padding:var(--spacing-md);background:#fee2e2;color:#991b1b;border-radius:var(--border-radius);font-size:0.85rem;">Couldn't load staff logins — refresh and try again.</div>`;
    return;
  }

  // Sort: owner first, then admins, then by displayName.
  const ROLE_RANK = { owner: 0, admin: 1, dispatcher: 2, mechanic: 3, staff: 4 };
  members.sort((a, b) => {
    const ra = ROLE_RANK[a.role] ?? 99;
    const rb = ROLE_RANK[b.role] ?? 99;
    if (ra !== rb) return ra - rb;
    return (a.displayName || a.email || '').localeCompare(b.displayName || b.email || '');
  });

  // Active count for cap chip
  const activeCount = members.filter(m => (m.status || 'active') === 'active').length;

  // TASK-375 slice B — cache the loaded data + (re)build the filter dropdowns,
  // then delegate the actual DOM render to the filter-aware path. The client
  // filter never re-fetches — it re-renders from this tenant-scoped cache.
  _teamAccessData = { members, pendingInvites, activeCount };
  _populateTeamFilterOptions(members, pendingInvites);
  _renderTeamAccessFiltered();
}

// TASK-375 slice B — populate the module + role filter dropdowns from the
// modules/roles actually present in the loaded set (no drift vs a hardcoded
// list). Preserves the current selection across re-renders.
function _populateTeamFilterOptions(members, pendingInvites) {
  const moduleSel = document.getElementById('team-filter-module');
  const roleSel = document.getElementById('team-filter-role');
  const moduleSet = new Set();
  const roleSet = new Set();
  [...members, ...pendingInvites].forEach(m => {
    (Array.isArray(m.modules) ? m.modules : []).forEach(mod => moduleSet.add(mod));
    if (m.role) roleSet.add(m.role);
  });
  if (moduleSel) {
    const prev = moduleSel.value;
    const opts = ['<option value="">Any module</option>']
      .concat([...moduleSet].sort().map(mod =>
        `<option value="${escapeHtml(mod)}">${escapeHtml(_prettyModuleLabel(mod))} access</option>`));
    moduleSel.innerHTML = opts.join('');
    if ([...moduleSet].includes(prev)) moduleSel.value = prev;
  }
  if (roleSel) {
    const prev = roleSel.value;
    const ROLE_ORDER = ['owner', 'admin', 'dispatcher', 'mechanic', 'staff'];
    const sortedRoles = [...roleSet].sort((a, b) => {
      const ia = ROLE_ORDER.indexOf(a); const ib = ROLE_ORDER.indexOf(b);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    });
    const opts = ['<option value="">Any role</option>']
      .concat(sortedRoles.map(r =>
        `<option value="${escapeHtml(r)}">${escapeHtml(r.charAt(0).toUpperCase() + r.slice(1))}</option>`));
    roleSel.innerHTML = opts.join('');
    if ([...roleSet].includes(prev)) roleSel.value = prev;
  }
}

// TASK-375 slice B — read the filter controls and re-render the list from the
// cached tenant data. Called on every filter change and after each load.
function applyTeamFilter() { _renderTeamAccessFiltered(); }

// Blank the three filter controls WITHOUT repainting. The Settings modal is
// only ever hidden, never torn down, so whatever sat in these inputs survives
// close -> reopen for the whole page session -- and the browser can seed the
// search box on its own (Chrome autofilled the saved account email into it,
// which silently filtered Team to one row and read as "my team vanished").
// Called on Settings open AND close so the panel always starts unfiltered.
function _resetTeamFilterInputs() {
  const s = document.getElementById('team-filter-search');
  const mo = document.getElementById('team-filter-module');
  const ro = document.getElementById('team-filter-role');
  if (s) s.value = '';
  if (mo) mo.value = '';
  if (ro) ro.value = '';
  const clearBtn = document.getElementById('team-filter-clear');
  if (clearBtn) clearBtn.style.display = 'none';
}
function clearTeamFilter() {
  _resetTeamFilterInputs();
  _renderTeamAccessFiltered();
}

// TASK-375 slice B — the filter-aware render. Renders cap chip + matching
// member rows (full module grants, CRM level inline) + matching pending invites.
function _renderTeamAccessFiltered() {
  const container = document.getElementById('team-access-list');
  if (!container) return;

  // TASK-1577 — this renders from the _teamAccessData CACHE, so it can repaint
  // rows (and their revoke/edit buttons) without going through
  // renderTeamAccessList's gate. Reachable in one page load: unlock with the
  // Primary PIN → view Team → lock pill → re-enter a limited PIN → type in the
  // filter box. The write paths already refuse, but the rows would still paint.
  if (!_teamAccessRenderOk()) return _teamLock(container); // TASK-9389 — the owner's switch for a staff login, else the PIN gate
  if (!canListTeamMembers()) return _teamLock(container, 1);

  const { members, pendingInvites, activeCount } = _teamAccessData;

  // Read filters
  const searchEl = document.getElementById('team-filter-search');
  const moduleEl = document.getElementById('team-filter-module');
  const roleEl = document.getElementById('team-filter-role');
  const search = (searchEl?.value || '').trim().toLowerCase();
  const moduleFilter = moduleEl?.value || '';
  const roleFilter = roleEl?.value || '';
  const filtering = !!(search || moduleFilter || roleFilter);

  const clearBtn = document.getElementById('team-filter-clear');
  if (clearBtn) clearBtn.style.display = filtering ? '' : 'none';

  // A member "has" a module if it's in modules[], OR they're owner / a
  // legacy-admin with no explicit modules (full access). The module filter
  // asks "who can see X?" so full-access members must match every module.
  const memberHasModule = (m, mod) => {
    if (!mod) return true;
    if (_teamMemberIsFullAccess(m)) return true;
    return (Array.isArray(m.modules) ? m.modules : []).includes(mod);
  };
  // Pending invites are never full-access — they carry an explicit modules[].
  const inviteHasModule = (inv, mod) => {
    if (!mod) return true;
    return (Array.isArray(inv.modules) ? inv.modules : []).includes(mod);
  };
  const matchesSearch = (name, email) => {
    if (!search) return true;
    return (`${name || ''} ${email || ''}`).toLowerCase().includes(search);
  };

  const filteredMembers = members.filter(m =>
    matchesSearch(m.displayName || m.name, m.email)
    && memberHasModule(m, moduleFilter)
    && (!roleFilter || (m.role || 'staff') === roleFilter)
  );
  const filteredInvites = pendingInvites.filter(inv =>
    matchesSearch(inv.personName, inv.email)
    && inviteHasModule(inv, moduleFilter)
    && (!roleFilter || (inv.role || 'staff') === roleFilter)
  );

  const capColor = activeCount >= TEAM_ACCESS_SOFT_CAP ? '#991b1b' : activeCount >= 45 ? '#92400e' : 'var(--text-muted)';
  const capBg = activeCount >= TEAM_ACCESS_SOFT_CAP ? '#fee2e2' : activeCount >= 45 ? '#fef3c7' : 'transparent';
  const capChip = `
    <div style="display:flex;align-items:center;justify-content:space-between;padding:6px 10px;margin-bottom:var(--spacing-sm);background:${capBg};border-radius:6px;font-size:0.8rem;color:${capColor};">
      <span><strong>${activeCount}</strong> of ${TEAM_ACCESS_SOFT_CAP} active staff logins</span>
    </div>
  `;

  // A hidden filter is the worst kind -- the list just looks short. When
  // anything is filtering, say so above the rows in full contrast, name how
  // many of how many are showing, and put the escape hatch right there rather
  // than only in the filter bar, where the old borderless "Clear" was easy to
  // miss entirely.
  const shownCount = filteredMembers.length + filteredInvites.length;
  const totalCount = members.length + pendingInvites.length;
  const filterBanner = filtering ? `
    <div style="display:flex;align-items:center;justify-content:space-between;gap:var(--spacing-sm);flex-wrap:wrap;padding:8px 10px;margin-bottom:var(--spacing-sm);background:#eff6ff;border:1px solid var(--brand-blue,#3B82F6);border-radius:6px;font-size:0.82rem;color:#1e40af;">
      <span style="display:flex;align-items:center;gap:6px;">
        <span class="material-icons" style="font-size:16px;">filter_alt</span>
        <span>Filtered &mdash; showing <strong>${shownCount}</strong> of <strong>${totalCount}</strong></span>
      </span>
      <button type="button" class="btn btn-xs btn-secondary" onclick="window.clearTeamFilter && window.clearTeamFilter()">
        <span class="material-icons">close</span> Show all
      </button>
    </div>
  ` : '';

  // TASK-349 — Pending Invites subsection. Built once, appended below members.
  // Each row shows email + personName + sent age + Resend/Revoke buttons.
  function _formatTeamAccessRelative(ms) {
    if (!ms) return '';
    const diff = Date.now() - ms;
    const min = Math.floor(diff / 60000);
    if (min < 1) return 'just now';
    if (min < 60) return `${min} min ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr} hr ago`;
    const days = Math.floor(hr / 24);
    if (days === 1) return 'yesterday';
    if (days < 7) return `${days} days ago`;
    return new Date(ms).toLocaleDateString();
  }
  function _formatTeamAccessExpiry(ms) {
    if (!ms) return '';
    const diff = ms - Date.now();
    if (diff <= 0) return 'expired';
    const days = Math.ceil(diff / 86400000);
    if (days === 1) return 'tomorrow';
    if (days < 7) return `in ${days} days`;
    return new Date(ms).toLocaleDateString();
  }
  // TASK-375 slice B — CRM level chip (rep / supervisor) for CRM members/invites.
  // Reads the admin-authored `crmRole` field Slice A added (members/{uid}.crmRole
  // / token.crmRole). Only shown when the grant actually includes `crm`.
  function _crmLevelChip(entity) {
    const mods = Array.isArray(entity.modules) ? entity.modules : [];
    const hasCrm = _teamMemberIsFullAccess(entity) || mods.includes('crm');
    if (!hasCrm) return '';
    const lvl = entity.crmRole === 'supervisor' ? 'Supervisor' : entity.crmRole === 'rep' ? 'Rep' : null;
    const label = lvl ? `CRM ${lvl}` : 'CRM';
    return `<span style="font-size:0.7rem;background:#e0f2fe;color:#075985;padding:2px 6px;border-radius:4px;margin-left:6px;font-weight:600;" title="CRM team level">${escapeHtml(label)}</span>`;
  }

  const pendingRowsHtml = filteredInvites.map(inv => {
    const safeEmail = escapeHtml(inv.email || '');
    const safePersonName = escapeHtml(inv.personName || '(no name)');
    // TASK-9400 — both land inside onclick="fn('...')". app.js's escapeHtml escapes
    // & < > only (no quotes), so a " in the email broke out of the attribute and a
    // \' walked past the hand-rolled replace. escJsAttr is the escaper for exactly
    // this position. Kept as onclick (not data-*) because _setTeamActionBusy finds
    // these buttons by onclick substring on the raw token/uid.
    const safeToken = window.escJsAttr(inv.token || '');
    const safeEmailForJs = window.escJsAttr(inv.email || 'this email');
    const sentRel = _formatTeamAccessRelative(inv.createdAtMs);
    const expiresRel = _formatTeamAccessExpiry(inv.expiresAtMs);
    const resendSuffix = inv.resendCount > 0 ? ` &middot; resent ${inv.resendCount}×` : '';
    // Full module grants for the pending invite (no truncation).
    const invMods = Array.isArray(inv.modules) ? inv.modules : [];
    const invModulesSummary = invMods.length
      ? invMods.map(_prettyModuleLabel).map(escapeHtml).join(', ')
      : '<span style="color:#a16207;">No modules</span>';
    return `
      <div class="team-access-row" style="display:flex;align-items:flex-start;gap:var(--spacing-sm);padding:var(--spacing-sm) var(--spacing-md);background:#fef3c7;border:1px solid #f59e0b;border-radius:var(--border-radius);">
        <span class="material-icons" style="color:#92400e;font-size:20px;flex-shrink:0;line-height:1.35;">mail_outline</span>
        <div style="flex:1;min-width:0;">
          <div style="font-weight:500;color:#92400e;display:flex;align-items:center;flex-wrap:wrap;gap:6px;">
            <span>${safePersonName}</span>
            <span style="font-size:0.7rem;background:#fde68a;color:#78350f;padding:2px 6px;border-radius:4px;font-weight:600;">PENDING</span>${inv.personless === true ? '<span style="font-size:0.7rem;background:#fde68a;color:#78350f;border:1px solid #92400e;padding:1px 6px;border-radius:9999px;font-weight:600;white-space:nowrap;" title="Outside user — no employee record, will never appear on the roster">NO EMPLOYEE RECORD</span>' : ''}${_crmLevelChip(inv)}
          </div>
          <div style="font-size:0.75rem;color:#92400e;font-family:monospace;overflow:hidden;text-overflow:ellipsis;">${safeEmail}</div>
          <div style="font-size:0.78rem;color:#92400e;margin-top:2px;">${invModulesSummary}</div>
          <div style="font-size:0.78rem;color:#92400e;margin-top:2px;">Sent ${sentRel} &middot; expires ${expiresRel}${resendSuffix}</div>
        </div>
        <div class="team-access-actions">
          <button type="button" class="btn btn-xs btn-secondary btn-icon-only" onclick="resendStaffInviteFromTeamAccess('${safeToken}')" title="Resend invite link" aria-label="Resend invite link">
            <span class="material-icons">send</span>
          </button>
          <button type="button" class="btn btn-xs btn-danger-outline btn-icon-only" onclick="revokeStaffInviteFromTeamAccess('${safeToken}', '${safeEmailForJs}')" title="Revoke pending invite" aria-label="Revoke pending invite">
            <span class="material-icons">block</span>
          </button>
        </div>
      </div>
    `;
  }).join('');

  const pendingSection = filteredInvites.length > 0
    ? `
      <div style="margin-top:var(--spacing-md);">
        <div style="font-size:0.75rem;text-transform:uppercase;letter-spacing:0.5px;color:var(--text-secondary);margin-bottom:var(--spacing-xs);font-weight:600;">Pending Invites</div>
        <div style="display:flex;flex-direction:column;gap:var(--spacing-xs);">${pendingRowsHtml}</div>
      </div>
    `
    : '';

  // Empty / no-match states. Distinguish "no team at all" from "filter matched nothing".
  if (filteredMembers.length === 0 && filteredInvites.length === 0) {
    const emptyBody = filtering
      ? `No team members match this filter.<br>
         <button type="button" class="btn btn-sm btn-secondary" style="margin-top:var(--spacing-sm);" onclick="window.clearTeamFilter && window.clearTeamFilter()">
           <span class="material-icons" style="font-size:16px;">close</span> Clear filters
         </button>`
      : 'No staff logins yet. Use <strong>Add team member</strong> above to give someone their own login.';
    container.innerHTML = capChip + filterBanner + `
      <div style="padding:var(--spacing-md);background:var(--surface-secondary);border-radius:var(--border-radius);text-align:center;color:var(--text-muted);">
        ${emptyBody}
      </div>
    `;
    return;
  }

  const rowsHtml = filteredMembers.map(m => {
    const isOwner = m.role === 'owner';
    const modules = Array.isArray(m.modules) ? m.modules : [];
    const role = m.role || 'staff';

    // Module summary — owner / legacy-admin (no explicit modules) = full
    // access; otherwise list EVERY granted module (TASK-375 slice B: no
    // truncation — the whole point of the Team surface is "who can see what").
    let modulesSummary;
    if (isOwner) {
      modulesSummary = 'Owner — full access';
    } else if (_teamMemberIsFullAccess(m)) {
      modulesSummary = 'Admin — all modules';
    } else if (modules.length === 0) {
      modulesSummary = '<span style="color:var(--text-muted);">No modules granted</span>';
    } else {
      modulesSummary = modules.map(_prettyModuleLabel).map(escapeHtml).join(', ');
    }

    const displayName = escapeHtml(m.displayName || m.email || '(no name)');
    const email = m.email ? escapeHtml(m.email) : '';
    // TASK-9400 — JS string inside an onclick attribute: escJsAttr, not escapeHtml.
    const safeUid = window.escJsAttr(m.uid);
    const safeNameForJs = window.escJsAttr(m.displayName || m.email || 'this user');

    const roleBadge = isOwner
      ? '<span style="font-size:0.7rem;background:var(--accent-cyan);color:#000;padding:2px 6px;border-radius:4px;margin-left:6px;font-weight:600;">OWNER</span>'
      : role === 'admin'
        ? '<span style="font-size:0.7rem;background:#dbeafe;color:#1e40af;padding:2px 6px;border-radius:4px;margin-left:6px;font-weight:600;">ADMIN</span>'
        : `<span style="font-size:0.7rem;background:var(--surface-tertiary,#e5e7eb);color:var(--text-secondary);padding:2px 6px;border-radius:4px;margin-left:6px;font-weight:600;text-transform:capitalize;">${escapeHtml(role)}</span>`;

    const revokeBtn = isOwner
      ? ''
      : `<button type="button" class="btn btn-xs btn-danger-outline btn-icon-only" onclick="revokeStaffAccess('${safeUid}', '${safeNameForJs}')" title="Revoke access" aria-label="Revoke access">
           <span class="material-icons">person_remove</span>
         </button>`;

    // TASK-1489 — Edit access from HERE, not only from the Roster person card.
    // Module editing used to live exclusively inside _renderStaffLoginSection in
    // the Roster person modal. A personless member has no person card, so without
    // this button the ONLY thing an admin could do to an outside compliance
    // manager was revoke them — no way to add or remove a module. editStaffLoginAccess
    // is already uid-keyed and falls back to members/{uid}.displayName when no
    // person card is open, so it works unchanged from this surface; it just needs
    // the Roster modals mounted first (ensureModalsMounted is idempotent).
    // Deliberately passes ONLY the uid. editStaffLoginAccess's `email` param is a
    // display-only fallback and it already re-reads members/{uid}.email itself, so
    // interpolating the address into an onclick attribute would add an injection
    // sink for zero benefit — escapeHtml does NOT escape quotes (it is not
    // attribute-safe), which is exactly how that class of bug gets in.
    const editBtn = isOwner
      ? ''
      : `<button type="button" class="btn btn-xs btn-secondary btn-icon-only" onclick="editTeamMemberAccess('${safeUid}')" title="Change which modules this user can access" aria-label="Change module access">
           <span class="material-icons">tune</span>
         </button>`;

    // TASK-1489 — make the personless class visible. An admin scanning this list
    // needs to tell "outside user, deliberately not on the roster" apart from
    // "employee whose login was never linked" — they are managed differently.
    const personlessChip = (!isOwner && m.personless === true)
      ? '<span style="font-size:0.7rem;background:#eff6ff;color:#1e40af;border:1px solid var(--brand-blue,#3B82F6);padding:1px 6px;border-radius:9999px;margin-left:2px;font-weight:600;white-space:nowrap;" title="Outside user — no employee record, never appears on the roster">NO EMPLOYEE RECORD</span>'
      : '';

    return `
      <div class="team-access-row" style="display:flex;align-items:flex-start;gap:var(--spacing-sm);padding:var(--spacing-sm) var(--spacing-md);background:var(--surface-secondary);border-radius:var(--border-radius);${isOwner ? 'border:1px solid var(--accent-cyan);' : ''}">
        <span class="material-icons" style="color:${isOwner ? 'var(--accent-cyan)' : 'var(--text-muted)'};font-size:20px;flex-shrink:0;line-height:1.35;">${isOwner ? 'verified_user' : (m.personless === true ? 'badge' : 'person')}</span>
        <div style="flex:1;min-width:0;">
          <div style="font-weight:500;display:flex;align-items:center;flex-wrap:wrap;gap:4px;">
            <span>${displayName}</span>${roleBadge}${personlessChip}${_crmLevelChip(m)}
          </div>
          ${email ? `<div style="font-size:0.75rem;color:var(--text-muted);font-family:monospace;overflow:hidden;text-overflow:ellipsis;">${email}</div>` : ''}
          <div style="font-size:0.8rem;color:var(--text-secondary);margin-top:2px;">${modulesSummary}</div>
        </div>
        <div class="team-access-actions">${editBtn}${revokeBtn}</div>
      </div>
    `;
  }).join('');

  container.innerHTML = capChip + filterBanner + rowsHtml + pendingSection;
}

// Double-click guard for resend/revoke from Team Access — token-keyed so two
// different invites can't share an in-flight lock (TASK-349 COD Edge Case 1).
const _teamAccessInviteInFlight = new Set();

// TASK-349 — Resend a pending invite from Settings → Team Access.
async function resendStaffInviteFromTeamAccess(token) {
  if (!await _teamAccessWriteOk()) { _refuseTeamAccessManagement(); return; }
  const tenant = window.AppState?.tenant;
  if (!tenant || !token) return;
  if (_teamAccessInviteInFlight.has(`resend:${token}`)) return;
  _teamAccessInviteInFlight.add(`resend:${token}`);
  _setTeamActionBusy(`resendStaffInviteFromTeamAccess('${token}'`, true); // TASK-4531
  try {
    const fn = httpsCallable(functions, 'resendUserInvite');
    // TASK-4840 — server always attempts email on resend; report what actually
    // delivered, and never claim a channel whose send returned {ok:false}.
    const { data: resendData } = await fn({ tenantId: tenant, token });
    // A text WITHOUT the email is the dangerous half-success (the texted link
    // often won't open on an iPhone) — warn, never green (COD, integrated diff).
    const _out = window.inviteResendOutcome(resendData); // TASK-10027 muted-tenant UI hints
    if (_out) {
      showToast(_out.text, _out.type);
    } else if (resendData?.emailSent) {
      showToast(`Invite resent by email${resendData?.smsSent ? ' + text' : ''}`, 'success');
    } else if (resendData?.smsSent) {
      showToast("Resent by text only — the email did not send, and a texted login link often won't open on an iPhone. Check the email address.", 'warning');
    } else {
      showToast('Resend failed — nothing was delivered. Check the email address.', 'error');
    }
    renderTeamAccessList();
  } catch (err) {
    console.error('[TeamAccess] resendUserInvite error:', err);
    const msg = err?.message || 'Resend failed — try again.';
    showToast(msg.replace(/^internal:\s*/i, '').slice(0, 140), 'error');
  } finally {
    _teamAccessInviteInFlight.delete(`resend:${token}`);
    _setTeamActionBusy(`resendStaffInviteFromTeamAccess('${token}'`, false);
  }
}

// TASK-349 — Revoke a pending invite from Settings → Team Access.
async function revokeStaffInviteFromTeamAccess(token, email) {
  if (!await _teamAccessWriteOk()) { _refuseTeamAccessManagement(); return; }
  const tenant = window.AppState?.tenant;
  if (!tenant || !token) return;
  if (_teamAccessInviteInFlight.has(`revoke:${token}`)) return;
  const confirmed = await showConfirm(
    'Revoke this pending invite?',
    `The invite link sent to ${email || 'this email'} will stop working. You can send a fresh invite afterward.`,
    { confirmText: 'Revoke invite', danger: true }
  );
  if (!confirmed) return;
  _teamAccessInviteInFlight.add(`revoke:${token}`);
  _setTeamActionBusy(`revokeStaffInviteFromTeamAccess('${token}'`, true); // TASK-4531
  try {
    const fn = httpsCallable(functions, 'revokeUserAccess');
    await fn({ tenantId: tenant, token });
    showToast('Invite revoked', 'success');
    renderTeamAccessList();
  } catch (err) {
    console.error('[TeamAccess] revokeUserAccess (token) error:', err);
    const msg = err?.message || 'Revoke failed — try again.';
    showToast(msg.replace(/^internal:\s*/i, '').slice(0, 140), 'error');
  } finally {
    _teamAccessInviteInFlight.delete(`revoke:${token}`);
    _setTeamActionBusy(`revokeStaffInviteFromTeamAccess('${token}'`, false);
  }
}

// TASK-4531 — in-flight feedback twin of roster.js's _setRowActionBusy
// (Pitfall #1: no cross-imports under ?v= busters — keep in sync by hand).
function _setTeamActionBusy(match, busy) {
  document.querySelectorAll(`button[onclick*="${match}"]`).forEach(b => {
    b.disabled = busy;
    if (busy) {
      b.dataset.prevHtml = b.innerHTML;
      b.innerHTML = '<span class="material-icons spin" style="font-size:14px;vertical-align:middle;">autorenew</span>';
    } else if (b.dataset.prevHtml) {
      b.innerHTML = b.dataset.prevHtml;
      delete b.dataset.prevHtml;
    }
  });
}

// Revoke a staff login (delete members/{uid} via callable). Cannot revoke owner.
async function revokeStaffAccess(uid, name) {
  if (!await _teamAccessWriteOk()) { _refuseTeamAccessManagement(); return; }
  const tenant = window.AppState?.tenant;
  if (!tenant) {
    showToast('Tenant not loaded — refresh and try again.', 'error');
    return;
  }
  if (!uid) return;

  const confirmed = await showConfirm(
    'Revoke staff login?',
    `${name || 'This user'} will lose access to this tenant immediately. Their DispatchDeck account (other tenants) is unaffected. Continue?`,
    { confirmText: 'Revoke access', danger: true }
  );
  if (!confirmed) return;

  // TASK-4531 — this was the ONE revoke path with no double-click guard at all:
  // two confirms in flight = two callables. Same in-flight set as invite revoke.
  if (_teamAccessInviteInFlight.has(`revoke-member:${uid}`)) return;
  _teamAccessInviteInFlight.add(`revoke-member:${uid}`);
  _setTeamActionBusy(`revokeStaffAccess('${uid}'`, true);
  try {
    const fn = httpsCallable(functions, 'revokeUserAccess');
    await fn({ tenantId: tenant, uid });
    showToast('Access revoked', 'success');
    renderTeamAccessList();
  } catch (err) {
    console.error('[TeamAccess] revokeUserAccess error:', err);
    const msg = err?.message || 'Revoke failed — try again.';
    showToast(msg.replace(/^internal:\s*/i, '').slice(0, 140), 'error');
  } finally {
    _teamAccessInviteInFlight.delete(`revoke-member:${uid}`);
    _setTeamActionBusy(`revokeStaffAccess('${uid}'`, false);
  }
}

// TASK-1489 — Edit a team member's modules from Settings → Team Access.
// Thin bridge to the EXISTING Roster Edit-Access modal (single source of truth
// for the module grid + role derivation + updateMemberAccess call) rather than a
// parallel mini-form. Needed because a PERSONLESS member has no Roster person
// card, which was previously the only place this modal could be opened from —
// leaving revoke as the sole action available on an outside user.
async function editTeamMemberAccess(uid, email) {
  if (!await _teamAccessWriteOk()) { _refuseTeamAccessManagement(); return; }
  if (!uid) return;
  const RM = window.RosterModule;
  if (!RM || typeof RM.editStaffLoginAccess !== 'function') {
    showToast('Access editor not ready — open the Roster once, then try again.', 'error');
    return;
  }
  // The Edit-Access modal lives in the Roster modal bundle, which is lazily
  // mounted. Idempotent, and safe to call while Settings is the active view.
  try { RM.ensureModalsMounted?.(); } catch (_) {}
  // editStaffLoginAccess reads #person-id for a friendly header name. When
  // Settings is the active view no person card is open, but a STALE value can
  // linger from an earlier Roster visit and would label this modal with the wrong
  // person. Clear it so the header falls back to members/{uid}.displayName.
  const personIdField = document.getElementById('person-id');
  if (personIdField) personIdField.value = '';
  await RM.editStaffLoginAccess(uid, email || '');
}

window.renderTeamAccessList = renderTeamAccessList;
window.revokeStaffAccess = revokeStaffAccess;
window.editTeamMemberAccess = editTeamMemberAccess;
// TASK-349 — pending-invite actions exposed for onclick handlers
window.resendStaffInviteFromTeamAccess = resendStaffInviteFromTeamAccess;
window.revokeStaffInviteFromTeamAccess = revokeStaffInviteFromTeamAccess;
// TASK-375 slice B — Team filter handlers exposed for the filter-bar controls
window.applyTeamFilter = applyTeamFilter;
window.clearTeamFilter = clearTeamFilter;
window._resetTeamFilterInputs = _resetTeamFilterInputs;

// Show add/edit PIN modal
function showAddPinModal(existingPin = null) {
  // TASK-1577 — gate the modal itself, not just the button that opens it:
  // the button is reachable from the console and from editAccessPin().
  if (!canManageAccessPins()) { _refuseAccessPinManagement(); return; }

  const modal = document.getElementById('add-pin-modal');
  if (!modal) return;

  const titleEl = document.getElementById('add-pin-title');
  const pinInput = document.getElementById('add-pin-input');
  const labelInput = document.getElementById('add-pin-label');
  const originalInput = document.getElementById('edit-pin-original');
  const rosterCheckbox = document.getElementById('pin-module-roster');
  const rosterLabel = rosterCheckbox?.parentElement;

  // Reset form
  pinInput.value = '';
  labelInput.value = '';
  originalInput.value = '';
  document.getElementById('pin-module-dispatch').checked = false;
  document.getElementById('pin-module-maintenance').checked = false;
  rosterCheckbox.checked = false;
  rosterCheckbox.disabled = false;
  if (rosterLabel) rosterLabel.style.opacity = '1';
  document.getElementById('pin-module-hr').checked = false;
  document.getElementById('pin-module-hr-directory').checked = false;
  document.getElementById('pin-module-clock').checked = false;
  document.getElementById('pin-module-billing').checked = false;
  document.getElementById('pin-module-workforce').checked = false;
  document.getElementById('pin-module-crm').checked = false;
  document.getElementById('pin-module-bi').checked = false;
  document.getElementById('pin-module-compliance').checked = false;
  document.getElementById('pin-module-811').checked = false;

  const accessPins = accessPinsFull() || {}; // TASK-9858 — digits -> config (owner view)
  const existingPinCount = Object.keys(accessPinMap()).length;
  const isFirstPin = existingPinCount === 0;

  if (existingPin) {
    // Editing existing PIN
    const config = accessPins[existingPin] || {};
    const isPrimary = config.isPrimary;

    titleEl.textContent = isPrimary ? 'Edit Primary PIN' : 'Edit Access PIN';
    pinInput.value = existingPin;
    labelInput.value = config.label || '';
    originalInput.value = existingPin;

    const modules = config.modules || [];
    document.getElementById('pin-module-dispatch').checked = modules.includes('dispatch');
    document.getElementById('pin-module-maintenance').checked = modules.includes('maintenance');
    rosterCheckbox.checked = modules.includes('roster');
    document.getElementById('pin-module-hr').checked = modules.includes('hr');
    document.getElementById('pin-module-hr-directory').checked = modules.includes('hr-directory');
    document.getElementById('pin-module-clock').checked = modules.includes('clock');
    document.getElementById('pin-module-billing').checked = modules.includes('billing');
    document.getElementById('pin-module-workforce').checked = modules.includes('workforce') || modules.includes('timeoff');
    document.getElementById('pin-module-crm').checked = modules.includes('crm');
    document.getElementById('pin-module-bi').checked = modules.includes('bi');
    document.getElementById('pin-module-compliance').checked = modules.includes('compliance');
    document.getElementById('pin-module-811').checked = modules.includes('811');

    // Primary PIN: roster is always checked and disabled
    if (isPrimary) {
      rosterCheckbox.checked = true;
      rosterCheckbox.disabled = true;
      if (rosterLabel) rosterLabel.style.opacity = '0.6';
    }
  } else {
    // Adding new PIN
    if (isFirstPin) {
      titleEl.textContent = 'Create Primary PIN';
      // First PIN must have roster - check and disable
      rosterCheckbox.checked = true;
      rosterCheckbox.disabled = true;
      if (rosterLabel) rosterLabel.style.opacity = '0.6';
    } else {
      titleEl.textContent = 'Add Access PIN';
    }
  }

  // Only show 811 checkbox if feature is enabled
  const pin811Label = document.getElementById('pin-module-811-label');
  if (pin811Label) {
    pin811Label.style.display = window.AppState?.settings?.enable811 ? '' : 'none';
  }

  // Only show addon checkboxes for accounts that have them
  const pinHrLabel = document.getElementById('pin-module-hr-label');
  const pinHrDirLabel = document.getElementById('pin-module-hr-directory-label');
  const hrVisible = hasAddOnPackage('hr');
  if (pinHrLabel) {
    pinHrLabel.style.display = hrVisible ? '' : 'none';
  }
  if (pinHrDirLabel) {
    // Show sub-module only when HR is visible and checked
    const hrChecked = document.getElementById('pin-module-hr')?.checked;
    pinHrDirLabel.style.display = (hrVisible && hrChecked) ? '' : 'none';
  }
  // Toggle hr-directory visibility when HR checkbox changes
  const hrCheckbox = document.getElementById('pin-module-hr');
  if (hrCheckbox && pinHrDirLabel) {
    hrCheckbox.onchange = () => {
      pinHrDirLabel.style.display = hrCheckbox.checked ? '' : 'none';
      if (!hrCheckbox.checked) {
        document.getElementById('pin-module-hr-directory').checked = false;
      }
    };
  }
  const pinCrmLabel = document.getElementById('pin-module-crm-label');
  if (pinCrmLabel) {
    pinCrmLabel.style.display = hasAddOnPackage('crm') ? '' : 'none';
  }
  const pinBiLabel = document.getElementById('pin-module-bi-label');
  if (pinBiLabel) {
    pinBiLabel.style.display = hasAddOnPackage('hr') ? '' : 'none';
  }
  const pinComplianceLabel = document.getElementById('pin-module-compliance-label');
  if (pinComplianceLabel) {
    pinComplianceLabel.style.display = hasAddOnPackage('hr') ? '' : 'none';
  }

  modal.classList.remove('hidden');
  setTimeout(() => pinInput.focus(), 100);
}

function closeAddPinModal() {
  const modal = document.getElementById('add-pin-modal');
  if (modal) modal.classList.add('hidden');
}

async function saveAccessPin() {
  // TASK-1577 — the load-bearing check. Server-truth, not AppState: a modal opened
  // before a PIN downgrade, a direct saveAccessPin() call, and a tainted
  // AppState.settings.accessPins all fail here.
  if (!await assertCanManageAccessPins()) { _refuseAccessPinManagement(); return; }

  const pinInput = document.getElementById('add-pin-input');
  const labelInput = document.getElementById('add-pin-label');
  const originalInput = document.getElementById('edit-pin-original');

  const pin = pinInput.value.trim();
  const label = labelInput.value.trim();
  const originalPin = originalInput.value;

  // Validate PIN
  if (!/^\d{4,6}$/.test(pin)) {
    showToast('PIN must be 4-6 digits', 'error');
    pinInput.focus();
    return;
  }

  // Gather selected modules
  const modules = [];
  if (document.getElementById('pin-module-dispatch').checked) modules.push('dispatch');
  if (document.getElementById('pin-module-maintenance').checked) modules.push('maintenance');
  if (document.getElementById('pin-module-roster').checked) modules.push('roster');
  if (document.getElementById('pin-module-hr').checked) modules.push('hr');
  if (document.getElementById('pin-module-hr-directory').checked) modules.push('hr-directory');
  if (document.getElementById('pin-module-clock').checked) modules.push('clock');
  if (document.getElementById('pin-module-billing').checked) modules.push('billing');
  if (document.getElementById('pin-module-workforce').checked) modules.push('workforce');
  if (document.getElementById('pin-module-crm').checked) modules.push('crm');
  if (document.getElementById('pin-module-bi').checked) modules.push('bi');
  if (document.getElementById('pin-module-compliance').checked) modules.push('compliance');
  if (document.getElementById('pin-module-811').checked) modules.push('811');

  if (modules.length === 0) {
    showToast('Select at least one module to unlock', 'error');
    return;
  }

  // 🔴 TASK-9279 — a RENAME is "write the new code AND revoke the old one", and
  // EVERY guard below has to be decided against the SAME snapshot the write lands
  // on. Two separate defects used to live here:
  //   1. `delete accessPins[originalPin]` ran on the LOCAL copy and the trimmed map
  //      was written back with merge:true, which cannot remove a key — so the old
  //      code kept authenticating. Every flag computed after that local delete also
  //      read a map the old code had already been removed from, so renaming the
  //      Primary PIN DEMOTED it and, on a 2-PIN tenant, a limited PIN got PROMOTED.
  //   2. (COD round 2, raised independently by the Code Reviewer, the Independent
  //      Code Reviewer and the Edge Case Hunter) the guards were read-then-write
  //      against a plain getDoc. A delete committing between the read and the write
  //      PUT THE REVOKED CODE BACK, because the ADD / EDIT-IN-PLACE branch writes
  //      its PIN key unconditionally. The same lane found the worse shape: admin A
  //      renames the Primary to 7044 while admin B adds a limited PIN 7044 — B's
  //      duplicate check passes against a stale read, last writer wins, and the
  //      tenant can end up with ZERO primaries, which locks PIN management for
  //      everyone. Both are only reachable now that deletes actually happen.
  // A transaction is the only thing that closes (2): it re-runs the read AND the
  // guards if the document moved under them, so a concurrent revocation is seen
  // rather than skipped. It also removes the double-primary race outright.
  //
  // ⚠️ A transaction REQUIRES connectivity — it cannot be queued into
  // persistentLocalCache the way a plain write can. That is deliberate on this
  // surface: a PIN change that reports success out of an offline queue and lands
  // minutes later is the same lie this whole task exists to kill. Offline, the save
  // fails loudly and nothing changes.
  const isRenaming = !!(originalPin && originalPin !== pin);

  // 🔴 CAPTURE the device's stored PIN blob BEFORE the write (COD round 1, Code
  // Reviewer), because re-reading it afterwards races the write's own effects.
  // persistentLocalCache applies the mutation LOCALLY before the server acks it, so
  // subscribeTenantSettings (~:15517) can fire with the new map and run
  // updateNavForPinAccess -> getUnlockedModules, which REMOVES
  // `dispatchdeck_pin_<tid>` the moment the old code stops resolving — all before
  // the write resolves. A post-write read would find null, skip the re-point, and
  // lock the admin out of the panel they are standing in. Reading first makes the
  // race irrelevant, and pins the storage KEY to the tenant that was current at
  // decision time rather than to a mutable global read later.
  // TASK-9858 — captured whether or not this is a rename: the first save on a
  // tenant that still has the old map migrates it, and every PIN gets an id.
  const _selfHeldKey = `dispatchdeck_pin_${window.AppState?.tenant}`;
  let _selfHeldBlob = null;
  try {
    _selfHeldBlob = JSON.parse(localStorage.getItem(_selfHeldKey) || 'null');
  } catch (_) { /* an unparseable blob is re-challenged, which is safe */ }

  // Refusals raised from INSIDE the transaction, tagged rather than toasted in
  // place: a transaction body can be re-run and must never paint UI.
  const _refuse = (code) => Object.assign(new Error('pin-refusal:' + code), { _pinRefusal: code });

  try {
    const tenantRef = doc(db, 'tenants', window.AppState.tenant);
    let isPrimary = false;
    let finalModules = modules;
    let pinConfig = null;
    let existingSettings = {};
    let _written = null;
    let _renamedFromId = null;

    await runTransaction(db, async (tx) => {
      // TASK-9858 — both homes in the SAME transaction: the digits (owner-only
      // private doc) and the configs (settings.accessPinMeta, or the legacy map).
      const [snap, priv] = await Promise.all([tx.get(tenantRef), tx.get(_pinDocRef(tenantRef.id))]);
      existingSettings = (snap.exists() ? snap.data().settings : null) || {};
      const accessPins = _pinsFullFrom(existingSettings, priv.exists() ? priv.data() : null);

      // Duplicate PIN (unless we're editing the same PIN). Inside the transaction
      // this is a real check: a code another admin minted a moment ago is SEEN, so
      // two saves can never both claim it.
      if (accessPins[pin] && pin !== originalPin) throw _refuse('duplicate');

      // A rename is an UPDATE of an existing map, never a create. If the tenant doc
      // is gone, refuse with the real reason rather than letting NOT_FOUND land as a
      // generic "Error saving PIN". Nothing is written, so the old code keeps
      // working and the holder is not stranded.
      if (isRenaming && !snap.exists()) throw _refuse('nodoc');

      // The PIN this modal is editing is GONE from the map: another admin revoked it
      // while the modal was open. Saving would RESURRECT the credential they just
      // revoked, under a new code — the merge-resurrection this task exists to kill,
      // in human form. Refusing strands nobody: there is nothing left to edit.
      if (originalPin && !accessPins[originalPin]) throw _refuse('gone');

      // Determine if this is the primary PIN. First PIN created is always primary,
      // or if editing the existing primary. Both read the UNTRIMMED map, and "first
      // PIN" means the tenant has NONE, or the only PIN on file is the one being
      // edited — the bare `count === 1 && originalPin` it replaces was true whenever
      // ANY single PIN remained, whether or not it was this one.
      const existingPinCount = Object.keys(accessPins).length;
      const isEditingPrimary = !!(originalPin && accessPins[originalPin]?.isPrimary);
      const isFirstPin = existingPinCount === 0
        || (existingPinCount === 1 && !!originalPin && !!accessPins[originalPin]);
      isPrimary = !!(isFirstPin || isEditingPrimary);

      // Primary PIN always includes roster (admin access)
      finalModules = isPrimary && !modules.includes('roster')
        ? [...modules, 'roster']
        : modules;
      pinConfig = { modules: finalModules, label, isPrimary };

      // 🔴 TASK-9255 — no `...existingSettings` in the payload. This writer had the
      // same whole-map round-trip as saveSettings did: merge:true REPLACES arrays
      // and rewrites every scalar leaf it is handed, so adding a PIN also wrote back
      // `settings.smsInbox.optedOutOfAi` / `.allowlist` (the SMS assistant's consent
      // record, written Admin-SDK-side by optOutOfAi on an inbound "NO AI" text),
      // `customTypes.*`, `yards`, `safetyTrainings` and the rest. Omitting them is
      // what preserves them — verified against live Firestore, not assumed.
      //
      // 🔴 TASK-9279 — the write, in two shapes, on purpose.
      // RENAME: ONE update carrying two field paths — set the new key, deleteField()
      // the old one. No ordering leaves both codes live, or neither.
      // ADD / EDIT-IN-PLACE: set + merge, deliberately, because `update` throws
      // NOT_FOUND on a missing tenant doc where set + merge CREATES it (the TASK-428
      // trap), and the first-PIN bootstrap on a doc-less tenant is the one flow this
      // must not break.
      // Both name ONLY the key this modal owns rather than writing the whole map
      // back: merge:true already preserves every other PIN, and a whole-map write
      // from a save-time snapshot would resurrect a PIN another admin deleted.
      // TASK-9858 — the whole set goes back through _writePinsFull (digits to the
      // private doc, configs to accessPinMeta). A rename gets a NEW id, so every
      // device unlocked with the old code locks (_nextPinSet).
      _renamedFromId = originalPin ? (accessPins[originalPin]?._id || null) : null;
      const next = _nextPinSet(accessPins, originalPin, pin, pinConfig);
      _written = _writePinsFull(tx, tenantRef, snap.exists(), next);
    });

    // TASK-9279 — re-point THIS device's stored PIN when the holder renamed the
    // very code they are unlocked with, from the blob captured BEFORE the write.
    // Without it, renaming your own Primary PIN revokes your session mid-modal:
    // getUnlockedModules() stops finding the stored code, canManageAccessPins()
    // flips false, and the Access PINs panel you are standing in swaps itself for
    // the "managed by the Primary PIN holder" lock — the TASK-428 shape, and a NEW
    // consequence of making revocation real.
    // This grants nothing: assertCanManageAccessPins() already proved this session
    // holds the Primary PIN against server truth, it is the same device and the same
    // module set, and the holder just typed the new code themselves. Scoped hard to
    // `stored.pin === originalPin` so renaming any OTHER PIN never touches it.
    // TASK-9858 — re-key by id (the id survives a rename; digits do not).
    _applyPinsWrite(_written, _selfHeldBlob, originalPin, pin, _renamedFromId);

    // Update local state — merge over the LIVE listener view, don't replace it
    // with the save-time snapshot (same TASK-9255 reasoning, local side).
    //
    // 🔴 TASK-9279 (COD round 1) — and apply only the KEYS THIS SAVE OWNS to the
    // live view, rather than assigning the whole map read at the top of this
    // function. Assigning the whole map would locally RESURRECT a PIN another admin
    // deleted while this modal was open — the same resurrection the server write
    // now refuses to do, re-created in AppState. The listener corrects it on the
    // next snapshot, but "wrong until a snapshot arrives" is not good enough on the
    // map every access gate reads.
    // TASK-9858 — done by _applyPinsWrite above: it sets the meta the transaction
    // COMMITTED (not a save-time snapshot), so nothing deleted elsewhere returns.

    // Re-render list
    renderAccessPinsList();

    // Update nav (in case user's stored PIN now has different modules)
    updateNavForPinAccess();

    closeAddPinModal();
    showToast(originalPin ? 'PIN updated' : 'PIN added', 'success');

    // After creating the FIRST PIN (primary), prompt user to set up their profile
    if (isPrimary && !originalPin) {
      setTimeout(() => {
        const goSetup = confirm('PIN created! Would you like to add yourself to the Roster now? This sets up your name, phone, and notification preferences.');
        if (goSetup) {
          closeSettingsModal();
          // Store PIN in localStorage so they have access after navigating
          const tenantId = window.AppState?.tenant;
          if (tenantId) {
            localStorage.setItem(`dispatchdeck_pin_${tenantId}`, JSON.stringify({
              pin: _written.idByPin[pin], v: 2, // TASK-9858 — the id, never the digits
              modules: finalModules,
              unlockedAt: Date.now(),
              expiresAt: Date.now() + (30 * 24 * 60 * 60 * 1000)
            }));
          }
          navigateToView('roster');
          // Wait for roster to load, then open add person modal
          setTimeout(() => {
            if (window.RosterModule?.showAddPerson) {
              window.RosterModule.showAddPerson();
              // Onboarding Hint 1: point at notification tags in the add person modal
              setTimeout(() => {
                const notifyTags = document.getElementById('person-notify-tags');
                if (notifyTags) showOnboardingHint('onboard-profile', notifyTags);
              }, 1000);
            }
          }, 500);
        }
      }, 300);
    }
  } catch (error) {
    // 🔴 TASK-9279 — refusals raised inside the transaction land here. Each names
    // the REAL reason; none of them writes anything, so in every case the existing
    // PINs keep working exactly as they did and nobody is stranded.
    if (error && error._pinRefusal) {
      if (error._pinRefusal === 'duplicate') {
        showToast('This PIN already exists', 'error');
        pinInput.focus();
      } else if (error._pinRefusal === 'nodoc') {
        showToast('Account settings could not be loaded. Refresh and try again.', 'error');
      } else if (error._pinRefusal === 'gone') {
        showToast('That PIN was removed by another admin. Reopen Access PINs for the current list.', 'error');
        closeAddPinModal();
        renderAccessPinsList();
      }
      return;
    }
    console.error('[PIN] Error saving:', error);
    // A transaction needs the network; offline it fails instead of queueing. Say
    // that, rather than leaving the holder to guess whether the PIN changed.
    if (error?.code === 'unavailable') {
      showToast('Could not reach the server — the PIN was NOT changed. Check your connection and try again.', 'error');
      return;
    }
    showToast('Error saving PIN', 'error');
  }
}

function editAccessPin(pin) {
  if (!canManageAccessPins()) { _refuseAccessPinManagement(); return; }
  showAddPinModal(pin);
}

async function deleteAccessPin(pin) {
  // TASK-1577 — before the confirm, so a non-primary holder never sees a dialog
  // for an action that cannot succeed. Server-truth: the isPrimary guard below
  // reads AppState, so without this a tainted global could delete the Primary PIN
  // and re-open zero-PIN bootstrap for the whole tenant.
  if (!await assertCanManageAccessPins()) { _refuseAccessPinManagement(); return; }

  // Prevent deleting primary PIN
  const accessPins = accessPinsFull() || {}; // TASK-9858 — keyed by digits (owner view)
  if (accessPins[pin]?.isPrimary) {
    showToast('Cannot delete the Primary PIN. Edit it instead.', 'error');
    return;
  }

  const confirmed = await showConfirm(
    'Delete PIN?',
    `Are you sure you want to delete PIN ending in ...${pin.slice(-2)}? Anyone using this PIN will lose access.`,
    { danger: true, confirmText: 'Delete' }
  );

  if (!confirmed) return;

  // Refusals raised from INSIDE the transaction, tagged rather than toasted in
  // place: a transaction body can be re-run and must never paint UI.
  const _refuse = (code) => Object.assign(new Error('pin-refusal:' + code), { _pinRefusal: code });

  let _selfHeldBlob = null;
  try { _selfHeldBlob = JSON.parse(localStorage.getItem(`dispatchdeck_pin_${window.AppState?.tenant}`) || 'null'); } catch (_) { /* re-challenged */ }
  try {
    const tenantRef = doc(db, 'tenants', window.AppState.tenant);
    let existingSettings = {};
    let _written = null;

    // 🔴 TASK-9279 (COD round 3, Code Reviewer — CRITICAL) — the guards and the
    // delete must share ONE snapshot, for the same reason saveAccessPin does.
    // `deleteField()` being idempotent is NOT enough, because the KEY can be
    // REUSED: tenant holds {P (primary), L}. Two devices both open the confirm for
    // L and both pass the count guard. Device 1's delete commits, leaving {P}. An
    // admin then renames P -> L, leaving {L (primary)}. Device 2's delayed delete
    // finally fires and removes L — and the map is now EMPTY, which is the
    // zero-PIN bootstrap: canManageAccessPins / isModuleUnlocked /
    // hasFullModuleAccess all return full access to every device on the tenant.
    // Re-deriving "still exists / still not primary / still more than one" against
    // the snapshot the delete lands on is the only thing that closes it.
    await runTransaction(db, async (tx) => {
      const [snap, priv] = await Promise.all([tx.get(tenantRef), tx.get(_pinDocRef(tenantRef.id))]);
      existingSettings = (snap.exists() ? snap.data().settings : null) || {};
      const accessPins = _pinsFullFrom(existingSettings, priv.exists() ? priv.data() : null); // TASK-9858

      if (!snap.exists()) throw _refuse('nodoc');

      // Already revoked by someone else. Treat as success — the credential is gone,
      // which is what the user asked for — but write nothing.
      if (!accessPins[pin]) return;

      // Re-assert against the SERVER map, not the AppState copy checked before the
      // confirm dialog. Deleting the Primary PIN would drop the tenant back to
      // zero-PIN bootstrap, which hands PIN management to everyone.
      if (accessPins[pin].isPrimary) throw _refuse('primary');

      // The ANTI-LOCKOUT guard this fix makes necessary. Before this task the
      // delete was a no-op, so no click path could ever empty the map. Now that it
      // is real, emptying it would silently strip PIN protection from the whole
      // tenant — the opposite of what the button says. The isPrimary refusal above
      // covers every tenant in production (censused 2026-09-22, read-only: 39
      // tenants configure PINs, 46 PINs, and exactly ZERO have a lone non-primary
      // PIN), so this guard blocks no flow that exists today; it is here so a
      // future data shape — or the key-reuse race above — cannot open the
      // bootstrap through a button. Editing the PIN, or adding another first, both
      // still work.
      if (Object.keys(accessPins).length <= 1) throw _refuse('last');

      // 🔴 THE FIX. This used to write a locally-trimmed copy of the map with
      // merge:true, which does not delete anything: merge merges maps key-by-key
      // and PRESERVES every key absent from the payload. The PIN stayed on the
      // server and kept authenticating; the list only looked updated because the
      // local state below was rewritten, and the user was told "PIN deleted".
      // Re-measured against real Firestore 2026-09-22 before writing this. A
      // field-path deleteField() is the only form that removes a map key (see the
      // import note at the top of this file for why FieldPath, not a dotted
      // string).
      //
      // ⚠️ A transaction needs connectivity and cannot be queued into
      // persistentLocalCache. Deliberate on a revocation path: "PIN deleted" out of
      // an offline queue, landing minutes later, is the same lie this task exists
      // to kill. Offline it fails loudly and nothing changes.
      // TASK-9858 — the set minus this PIN, back through _writePinsFull (whole
      // maps, inside this transaction): its digits leave the private doc and its
      // id leaves accessPinMeta in the same commit.
      const next = { ...accessPins };
      delete next[pin];
      _written = _writePinsFull(tx, tenantRef, true, next);
    });

    // Update local state — merge over the LIVE listener view, don't replace it
    // with the save-time snapshot (same TASK-9255 reasoning, local side).
    //
    // 🔴 TASK-9279 (COD round 1) — remove ONLY the revoked key from the live view.
    // Assigning the whole map read at the top of this function would locally
    // resurrect a PIN another admin deleted in the meantime, which is the exact
    // defect this task closes on the server, re-created in AppState. Applied only
    // AFTER the server write resolved — a throw lands in the catch below with the
    // list still showing the truth.
    // TASK-9858 — the committed meta (never empty: the transaction refuses the
    // last PIN), so the mirror can not go to zero-PIN bootstrap either.
    if (_written) _applyPinsWrite(_written, _selfHeldBlob, null, null);
    // 🔴 TASK-9279 (COD round 4, Independent Code Reviewer) — NEVER let the LOCAL
    // mirror go empty. The server transaction refuses to empty the map, but this
    // line runs after the commit RPC returns, and the onSnapshot listener arrives
    // on a different channel. If another admin renamed the Primary ONTO the code
    // we just deleted, AppState can already hold `{ <pin>: primary }` — and
    // removing it here would leave `{}` locally, which IS the zero-PIN bootstrap:
    // isModuleUnlocked / canManageAccessPins / hasFullModuleAccess all read
    // AppState and would open every module on this kiosk until the next snapshot.
    // Dropping the key only when something else remains is enough; the listener is
    // authoritative and corrects the view either way.

    // Re-render list
    renderAccessPinsList();

    // Update nav
    updateNavForPinAccess();

    showToast('PIN deleted', 'success');
  } catch (error) {
    // 🔴 TASK-9279 — refusals raised inside the transaction. Each names the REAL
    // reason and none of them writes anything, so the PIN stays listed and stays
    // live rather than being falsely reported as revoked. The one thing this path
    // must never do is claim success without the write.
    if (error && error._pinRefusal) {
      if (error._pinRefusal === 'primary') {
        showToast('Cannot delete the Primary PIN. Edit it instead.', 'error');
      } else if (error._pinRefusal === 'last') {
        showToast('This is the only PIN on the account. Edit it, or add another PIN first.', 'error');
      } else {
        showToast('Account settings could not be loaded. Refresh and try again.', 'error');
      }
      renderAccessPinsList();
      return;
    }
    console.error('[PIN] Error deleting:', error);
    // A transaction needs the network; offline it fails instead of queueing. Say
    // so, rather than leaving the holder believing a credential was revoked.
    if (error?.code === 'unavailable') {
      showToast('Could not reach the server — the PIN was NOT deleted. Check your connection and try again.', 'error');
      return;
    }
    showToast('Error deleting PIN', 'error');
  }
}

// Expose PIN management functions globally
window.showAddPinModal = showAddPinModal;
window.closeAddPinModal = closeAddPinModal;
window.saveAccessPin = saveAccessPin;
window.editAccessPin = editAccessPin;
window.deleteAccessPin = deleteAccessPin;

// ============================================
// REQUIRED PIN SETUP (First-time login)
// ============================================
function showRequiredPinSetup() {
  const modal = document.getElementById('required-pin-modal');
  const input = document.getElementById('required-pin-input');
  if (!modal) return;

  // Clear input
  if (input) input.value = '';

  modal.classList.remove('hidden');
  setTimeout(() => input?.focus(), 100);

  // Handle enter key
  if (input) {
    input.onkeydown = (e) => {
      if (e.key === 'Enter') saveRequiredPin();
    };
  }
}

async function saveRequiredPin() {
  const input = document.getElementById('required-pin-input');
  const pin = input?.value?.trim() || '';

  // Validate PIN
  if (!/^\d{4,6}$/.test(pin)) {
    showToast('PIN must be 4-6 digits', 'error');
    input?.focus();
    return;
  }

  try {
    const tenant = window.AppState?.tenant;
    if (!tenant) {
      showToast('Account error. Please refresh.', 'error');
      return;
    }

    // 🔴 TASK-1577 (COD round 2) — this was the LAST unguarded accessPins writer,
    // and the worst of them: it REPLACES the entire map with one new all-modules
    // Primary PIN and stores that PIN locally. window.saveRequiredPin is global
    // (:18359) with an inline button in index.html, so an ungated call is the whole
    // privilege escalation in a single step. canManageAccessPins() returns true when
    // zero PINs exist, so the first-login bootstrap this was built for is untouched.
    if (!await assertCanManageAccessPins()) { _refuseAccessPinManagement(); return; }

    // Re-read from the server before clobbering. AppState.settings is client state,
    // and this write is destructive — it drops every existing PIN. If PINs appeared
    // since this modal opened (another admin, another device, or a stale modal left
    // open through a PIN change), refuse instead of wiping them. This also closes the
    // gap where a manipulated AppState.settings fakes the zero-PIN bootstrap.
    const tenantSnap = await getDoc(doc(db, 'tenants', tenant));
    const existingPins = accessPinMap((tenantSnap.exists() && tenantSnap.data()?.settings) || {});
    if (Object.keys(existingPins).length > 0) {
      showToast('This account already has PINs. Manage them in Settings → Access PINs.', 'error');
      document.getElementById('required-pin-modal')?.classList.add('hidden');
      return;
    }

    // Save PIN to Firestore — primary PIN gets ALL modules so owner has full access
    const pinConfig = {
      modules: ['dispatch', 'roster', 'maintenance', 'hr', 'crm', 'clock', 'billing', 'workforce', 'bi', 'compliance'],
      label: 'Admin',
      isPrimary: true
    };

    // TASK-9858 — digits to the owner-only private doc, the rest to
    // settings.accessPinMeta. (The copy this used to write to settings/general,
    // which every member can read, is no longer written.)
    const _tref = doc(db, 'tenants', tenant);
    let _written = null;
    const _exists = Object.assign(new Error('pin-refusal:exists'), { _pinRefusal: 'exists' });
    try {
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(_tref);
        // COD r2 — re-check inside the transaction: two first-PIN saves racing must
        // not overwrite each other.
        if (Object.keys(accessPinMap((snap.exists() && snap.data()?.settings) || {})).length) throw _exists;
        _written = _writePinsFull(tx, _tref, snap.exists(), { [pin]: pinConfig });
      });
    } catch (e) {
      if (e !== _exists) throw e;
      showToast('This account already has PINs. Manage them in Settings → Access PINs.', 'error');
      document.getElementById('required-pin-modal')?.classList.add('hidden');
      return;
    }
    _applyPinsWrite(_written, null, null, null);

    // Store PIN in localStorage (so user doesn't have to enter it immediately)
    localStorage.setItem(`dispatchdeck_pin_${tenant}`, JSON.stringify({
      pin: _written.idByPin[pin], v: 2,
      modules: pinConfig.modules,
      unlockedAt: Date.now(),
      expiresAt: Date.now() + (PIN_EXPIRY_DAYS * 24 * 60 * 60 * 1000)
    }));

    // Close modal
    document.getElementById('required-pin-modal').classList.add('hidden');

    showToast('Primary PIN created! You have full access.', 'success');

    // Now continue with app initialization
    showSidebar();
    hideUpgradeBadges();

    // If owner profile setup is needed, load roster first then run profile setup on top of it
    const needsProfileSetup = window.AppState.tenantData?.ownerProfileSetup === false;
    if (needsProfileSetup) {
      window.AppState._profileSetupActive = true;
      updateNavForPinAccess();
      // Navigate to roster so the view actually renders (checkOwnerProfileSetup
      // assumes the view is already loaded; otherwise the user sees a blank screen)
      navigateToView('roster');
      checkOwnerProfileSetup().catch(err => {
        console.error('[Onboarding] checkOwnerProfileSetup failed:', err);
        // Recover — let the user use the app even if profile setup hiccupped
        window.AppState._profileSetupActive = false;
      });
      return;
    }

    // TASK-319 member-gate (7th instance of this miss today — see Worker FF
    // consolidation note). currentView defaults to 'roster' for non-driver
    // tenants; if Test Chris (or any member) doesn't have that module granted,
    // we'd land on no-access on every login. Redirect to member-default.
    let _memberLoginView = window.AppState.currentView;
    const _memberAccess = window.AppState?.memberAccess;
    if (_memberAccess && _memberAccess.status === 'active'
        && _memberAccess.role !== 'owner'
        && Array.isArray(_memberAccess.modules)
        && _memberAccess.modules.length > 0
        && !_memberAccess.modules.includes(_memberLoginView)
        && _memberLoginView !== 'pretrip' && _memberLoginView !== 'dvir' && _memberLoginView !== 'account' && _memberLoginView !== 'approvals') {
      // Fresh-login landing — dispatch-first member default. This is the path
      // Roger @ Split Rock actually hit (a fresh sign-in lands here, NOT
      // handleHashChange), so the dispatch-first rule MUST live here too.
      _memberLoginView = computeMemberDefaultView(_memberAccess);
    }
    navigateToView(_memberLoginView);
    updateNavForPinAccess();

    // Initialize Trailguide and Sparkles
    setTimeout(() => {
      initTrailguide();
      initSalesButton();
      initDemoTrialCtas();
      initLockFab();
      initLockSidebarLogo(); // TASK-763 — sidebar logo = desktop lock/unlock + status pill
      initSparkles();
      initChangedMarkers(); // TASK-1098 "what changed" nav markers
      initOnboardingHints((hintsSeen) => {
        // Persist onboarding hints to Firestore
        updateDoc(doc(db, 'tenants', tenant), { onboardingHintsSeen: hintsSeen })
          .catch(err => console.warn('[Onboarding] Firestore persist error:', err));
      });
    }, 500);

  } catch (error) {
    console.error('[PIN] Error saving required PIN:', error);
    showToast('Error creating PIN. Please try again.', 'error');
  }
}

window.showRequiredPinSetup = showRequiredPinSetup;
window.saveRequiredPin = saveRequiredPin;
window.signOutFromPinSetup = async function() {
  try {
    document.getElementById('required-pin-modal')?.classList.add('hidden');
    await signOut(auth);
    window.location.reload();
  } catch (err) {
    console.error('[Auth] Sign out error:', err);
    window.location.reload();
  }
};

// ============================================
// FIRST-TIME OWNER PROFILE SETUP
// ============================================
async function checkOwnerProfileSetup() {
  const tenant = window.AppState?.tenant;
  const tenantData = window.AppState?.tenantData;

  // Skip if: demo, driver mode, already setup, or missing data
  if (!tenant || tenant === 'demo' || window.AppState?.isDemo ||
      window.AppState?.driverMode || window.AppState?.accessToken ||
      tenantData?.ownerProfileSetup !== false) return;

  // Suppress onboarding hints during profile setup flow
  window.AppState._profileSetupActive = true;

  // Find owner's person record by email (from Firestore directly)
  const userEmail = window.AppState.user?.email;
  if (!userEmail) {
    console.warn('[Onboarding] No user email — skipping profile setup');
    return;
  }

  const peopleSnap = await getDocs(collection(db, 'tenants', tenant, 'people'));
  const ownerPerson = peopleSnap.docs.find(d => d.data().email === userEmail);
  if (!ownerPerson) {
    console.warn('[Onboarding] No owner person record found — skipping profile setup');
    return;
  }

  // Mark as setup so we don't re-trigger this on next nav
  // (do this AFTER finding the owner — if it fails, user can retry on next login)
  try {
    await updateDoc(doc(db, 'tenants', tenant), { ownerProfileSetup: true });
    window.AppState.tenantData.ownerProfileSetup = true;
  } catch (e) {
    console.warn('[Onboarding] Could not mark ownerProfileSetup:', e.message);
  }

  // Force-load roster (saveRequiredPin already navigated, but if we got here from
  // another path the view may not actually be initialized yet)
  if (!window.RosterModule?.editPerson) {
    navigateToView('roster');
  }

  // Wait for roster module AND its people data to load (editPerson needs RosterState.people)
  const waitForRosterData = () => new Promise(resolve => {
    const check = () => {
      if (window.RosterModule?.editPerson && window.RosterModule?.getPeopleCount?.() > 0) {
        resolve(true);
      } else {
        setTimeout(check, 200);
      }
    };
    check();
    setTimeout(() => resolve(false), 8000); // Timeout after 8s
  });

  const rosterReady = await waitForRosterData();
  if (!rosterReady) return;

  // Dismiss any open onboarding tooltip (e.g. "Build Your Fleet") before opening modal
  const existingTooltip = document.getElementById('trailguide-tooltip');
  if (existingTooltip) existingTooltip.remove();

  // Open the edit person modal
  window.RosterModule.editPerson(ownerPerson.id);
  window.AppState._profileSetupActive = false;
  showToast('Complete your profile — add phone & notification preferences', 'info');

  // Sequential hints: phone → roles → notification tags
  setTimeout(() => {
    const phoneEl = document.getElementById('person-phone');
    if (phoneEl) {
      phoneEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      showOnboardingHint('onboard-phone', phoneEl, () => {
        setTimeout(() => {
          const rolesEl = document.getElementById('person-role-select-group') || document.getElementById('person-roles');
          if (rolesEl) {
            rolesEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
            showOnboardingHint('onboard-roles', rolesEl, () => {
              setTimeout(() => {
                const tagsEl = document.getElementById('person-notify-tags');
                if (tagsEl) {
                  tagsEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  showOnboardingHint('onboard-profile', tagsEl);
                }
              }, 400);
            });
          }
        }, 400);
      });
    }
  }, 800);
}

// ============================================================================
// TASK-637 — HR-scoped fail-closed access predicates.
//
// Product principle (Chris 2026-06-17): module access = FULL access within the
// module. If a member holds the `hr` module they DO everything in HR — no
// role/admin-vs-staff carve-outs INSIDE the module. The bug we're closing:
// an `hr`-module STAFF member saw admin-only HR features in the UI (hiring
// packet template, compliance billing, etc.) and tapped into raw "insufficient
// permissions" errors because the backend gated those on tenant-admin.
//
// These helpers are the client-side mirror of the server gates so the UI fails
// CLOSED (hide/disable the feature) instead of letting a member hit a raw error.
//
// `hrEffectiveAdmin()` mirrors firestore.rules isTenantAdmin: legacy single-
// login owner (no member doc), member role owner/admin. (Server isTenantAdmin
// is role-only — modules array does NOT downgrade an admin.)
// ============================================================================
function hrEffectiveAdmin() {
  const st = window.AppState || {};
  if (st.isDemo || st.tenant === 'demo') return true;
  const ma = st.memberAccess;
  if (ma == null) return true; // legacy single-login owner / no member doc = full access
  if (ma.status && ['revoked', 'inactive', 'pending'].includes(ma.status)) return false;
  return ma.role === 'owner' || ma.role === 'admin';
}

// True when the current user may do FULL HR work (all admin-gated HR features
// except the SSN/secure-tax carve-out): effective admin OR holds the `hr`
// module. Mirrors server assertHrModuleOrAdmin / firestore.rules
// `isTenantAdmin(tid) || hasModule(tid, 'hr')`.
function hrCanManageModule() {
  if (hrEffectiveAdmin()) return true;
  const ma = window.AppState?.memberAccess;
  // TASK-637 (COD ⚠️): this is the MANAGE/EDIT gate. A TASK-608 "view & upload
  // only" hr member may VIEW HR but must not see edit affordances (template save,
  // settings toggles) — the server callable now rejects those writes, so hide
  // them here too to avoid a raw permission error. owner/admin handled above.
  if (ma && ma.viewUploadOnly === true) return false;
  return !!(ma && Array.isArray(ma.modules) && ma.modules.includes('hr'));
}

// SSN / secure-tax (peopleTax) gate.
// TASK-815 (Chris 2026-06-25, reverses the TASK-637/607 admin-only carve-out):
// the `hr-directory` module is now the REAL grant for SSN/W-4/I-9. A member who
// holds hr-directory SEES + edits SSN without an admin role (matching the
// firestore.rules peopleTax gate: isTenantAdmin || hasModule('hr-directory')).
// owner / admin / founder / legacy single-login stay true via hrEffectiveAdmin.
// A bare `hr`-module member (NO hr-directory) must still NOT see SSN — the
// safe default — because app.js:7724 no longer auto-rides hr-directory onto hr.
function hrCanViewSecureTax() {
  if (hrEffectiveAdmin()) return true;
  const ma = window.AppState?.memberAccess;
  if (ma && ['revoked', 'inactive', 'pending'].includes(ma.status)) return false;
  return !!(ma && Array.isArray(ma.modules) && ma.modules.includes('hr-directory'));
}

// Carrier compliance billing (TASK-637 exception b): NOT an HR feature.
// TASK-9431 — MODULE access, never a role (Chris 2026-09-24: "hardline that
// rule"). Exact client mirror of the server gate COMPLIANCE_BILLING_MANAGE_MODULES
// (_claimBoundTenantAuthorize in setupCarrierComplianceBillingSession /
// removeCarrierComplianceCard / manageCarrierComplianceCard): the tenant OWNER
// via tenants/{tid}.ownerId, or an active member holding `billing`. Before 9431
// this used hrEffectiveAdmin (role owner/admin, and "no member doc = full
// access"), which showed the Settings row to people the server then refused
// (a billing-only member could not even load it; a legacy owner could not add a
// card). Driver/public sessions and demo are refused, as the server refuses them.
// Statuses are failed closed a little wider than the server (revoked/inactive/
// pending as well as suspended/removed) — the UI may hide more, never offer more.
function hrCanManageComplianceBilling() {
  const st = window.AppState || {};
  if (st.driverMode || st.isPublicAccess || st.driverModePin) return false;
  if (st.isDemo || st.tenant === 'demo') return false;
  const uid = st.user?.uid || null;
  const ownerId = st.tenantData?.ownerId || null;
  if (uid && ownerId && uid === ownerId) return true;
  const ma = st.memberAccess;
  if (!ma) return false;
  if (['revoked', 'inactive', 'pending', 'suspended', 'removed'].includes(ma.status)) return false;
  if (ma.readOnly === true) return false;
  return Array.isArray(ma.modules) && ma.modules.includes('billing');
}

// TASK-9491 — may this login CHANGE money (invoice, mark paid, write off, edit
// price book / AP / pay)? Exact client mirror of firestore.rules canEditMoney
// and the server _moneyEditorDecision: demo, the OWNER via tenants/{tid}.ownerId,
// or an active member whose modules LIST holds `billing` and who is not
// viewUploadOnly / readOnly. members.role is never read — an admin ROLE set to
// view-only is view-only for money. Driver / public sessions are refused. The UI
// may hide more than the server, never offer more.
function canEditMoney() {
  const st = window.AppState || {};
  if (st.isDemo || st.tenant === 'demo') return true;
  if (st.driverMode || st.isPublicAccess || st.driverModePin) return false;
  const uid = st.user?.uid || null;
  const ownerId = st.tenantData?.ownerId || null;
  if (uid && ownerId && uid === ownerId) return true;
  const ma = st.memberAccess;
  if (!ma) return false;
  if (['revoked', 'inactive', 'pending', 'suspended', 'removed'].includes(ma.status)) return false;
  if (ma.readOnly === true || ma.viewUploadOnly === true) return false;
  return Array.isArray(ma.modules) && ma.modules.includes('billing');
}
window.canEditMoney = canEditMoney;
// The one sentence every refused money control shows.
window.MONEY_VIEW_ONLY_MSG = 'View only — ask the owner to make this change.';

// TASK-9986 S1 — may this login change MODULE's SETTINGS? Client mirror of the
// server _settingsEditorDecision and the rules t9986G: demo, the OWNER via
// tenants/{tid}.ownerId, or an active member (not readOnly / viewUploadOnly)
// holding `module` in BOTH modules and settingsModules (the owner's per-person
// switch, set at invite / Edit access). members.role is never read. Driver /
// public / PIN sessions are refused. Modules outside SETTINGS_MODULES are not
// grant-gated yet: they fall back to plain module access (S2 moves them).
const SETTINGS_MODULES = ['billing']; // twin of functions SETTINGS_MODULES
function canChangeModuleSettings(module) {
  const st = window.AppState || {};
  if (st.isDemo || st.tenant === 'demo') return true;
  if (st.driverMode || st.isPublicAccess || st.driverModePin) return false;
  const uid = st.user?.uid || null;
  const ownerId = st.tenantData?.ownerId || null;
  if (uid && ownerId && uid === ownerId) return true;
  const ma = st.memberAccess;
  if (!ma) return false;
  if (['revoked', 'inactive', 'pending', 'suspended', 'removed'].includes(ma.status)) return false;
  if (ma.readOnly === true || ma.viewUploadOnly === true) return false;
  if (!(Array.isArray(ma.modules) && ma.modules.includes(module))) return false;
  if (!SETTINGS_MODULES.includes(module)) return true;
  return Array.isArray(ma.settingsModules) && ma.settingsModules.includes(module);
}
window.canChangeModuleSettings = canChangeModuleSettings;

// TASK-9389 (admin backfill lane) — the Settings entry for a STAFF login (a
// TASK-319 member with explicit modules — the same test as hideUpgradeBadges'
// member branch). Settings used to be hidden from every one of them; now a
// member the owner gave a switch sees it, and inside it only what the switch
// covers: manageLogins → Team & Access (Access PINs stay owner-only, TASK-9858);
// a module that owns a Features switch (canChangeModuleSettings: billing with
// the owner's settings switch; dispatch / hr / compliance by module, TASK-9527
// item 5) → Features, where only that module's switches flip. Everything else stays hidden, including the one Save Settings
// button. UX only — the callables (_memberManagerDecision, requireSettingsEditor)
// and firestore.rules decide. Returns null when the viewer is NOT a staff
// login (owner, legacy admin, PIN session, demo): they keep the whole modal.
function memberSettingsScope() {
  const st = window.AppState || {};
  const ma = st.memberAccess;
  if (!ma || ma.status !== 'active' || ma.role === 'owner' || !Array.isArray(ma.modules) || ma.modules.length === 0) return null;
  const none = { team: false, features: false, any: false };
  if (st.driverMode || st.isPublicAccess || st.driverModePin) return none;
  if (typeof window.isPartnerClientSession === 'function' && window.isPartnerClientSession()) return none;
  const limited = ma.readOnly === true || ma.viewUploadOnly === true;
  const team = !limited && ma.manageLogins === true;
  // TASK-9527 (5) — Features opens for anyone who may FLIP at least one switch
  // on it: the modules owning a views/features.js save:'gate' switch (a unit
  // test pins this list to that table). Billing still needs the owner's
  // settings switch (canChangeModuleSettings); dispatch / hr / compliance
  // holders get in on the module alone. The page greys every other module's
  // switch, and firestore.rules t9337ModuleWrite refuses it regardless.
  const FEATURES_GATE_MODULES = ['billing', 'dispatch', 'hr', 'compliance'];
  const features = !limited && FEATURES_GATE_MODULES.some((m) => canChangeModuleSettings(m));
  return { team, features, any: team || features };
}
window.memberSettingsScope = memberSettingsScope;

// Team & Access gates. PINs are a shared-device gate and never apply to a staff
// login (TASK-319), so for one the owner's "Can manage logins" switch is the
// render/click gate (the callables enforce it and the holder's caps). Every
// other session keeps the TASK-1577 Primary-PIN gate unchanged.
function _teamAccessRenderOk() {
  const sc = memberSettingsScope();
  return sc ? sc.team === true : canManageAccessPins();
}
async function _teamAccessWriteOk() {
  const sc = memberSettingsScope();
  return sc ? sc.team === true : assertCanManageAccessPins();
}
window.SETTINGS_MODULES = SETTINGS_MODULES.slice();
// The one line a read-only settings panel shows.
window.SETTINGS_READ_ONLY_MSG = 'You can see these settings but not change them. Ask the account owner if something here needs to change.';

// TASK-679 Slice A (Chris greenlit 2026-06-18): HR module holders manage disciplinary.
// Reverses the TASK-357 carve-out intentionally.
function hrCanManageDisciplinary() {
  return hrCanManageModule();
}
window.hrEffectiveAdmin = hrEffectiveAdmin;
window.hrCanManageModule = hrCanManageModule;
// TASK-9373 — read-only module gate for views that point at another module's
// page (Dispatch Settings → AR/AP Driver App). Callers must compare === true:
// the function can also return 'loading' / a PIN-challenge value.
window.checkModuleAccess = checkModuleAccess;
window.hrCanViewSecureTax = hrCanViewSecureTax;
window.hrCanManageComplianceBilling = hrCanManageComplianceBilling;
window.hrCanManageDisciplinary = hrCanManageDisciplinary;

// Check if current user can access a module
// Returns: true (allowed), false (not allowed), 'needs-pin' (needs PIN to unlock)
// `quiet` (TASK-622): when true, suppress the diagnostic console.logs. The nav
// display loop calls this ~20×/update; logging there would spam the console.
// The nav-click enforcement path leaves quiet=false so its logs are preserved.
function checkModuleAccess(module, quiet = false) {
  // TASK-9131 — canonicalize a VIEW name to the MODULE that owns it before any
  // gate runs (today: ap → billing; see VIEW_MODULE_ALIASES for why). This must
  // be the FIRST statement: every branch below — driver-PIN, token, member
  // modules, and the kiosk PIN path — keys off `module`, and 'ap' matched none
  // of them, so it fell through to the "allowed by default" tail with no PIN.
  module = moduleForView(module);

  // Always accessible modules — EXCEPT compliance-only tenants (TASK-1097):
  // pretrip/dvir are force-granted so driver boards work, but a compliance-only
  // carrier has no driver board; let them fall to the compliance-only branch
  // below (preview-overlay gating) like every other paid module. Fail OPEN to
  // the always-grant when the compliance signal is unavailable (e.g. tenantData
  // not loaded in a driver/PIN session) so a paying tenant's drivers are never
  // denied pre-trip access.
  const alwaysAccessible = ['pretrip', 'dvir'];
  if (alwaysAccessible.includes(module)
      && !(typeof isComplianceOnlyTenant === 'function' && isComplianceOnlyTenant())) {
    if (!quiet) console.log(`[Access] ${module} is always accessible`);
    return true;
  }

  // Compliance-only tenants: allow all navigation (overlay handles gating)
  //
  // 🔴 TASK-1658 (COD Adversary + Blast-Radius, both 🔴/❌, verified in source):
  // this early-return grants EVERY module and fires BEFORE the member branch
  // below, so on a complianceOnly carrier it silently overrode the compliance
  // partner's module scope and handed them billing / crm / clock / workforce /
  // bi / dispatch — the exact set derivePartnerModuleScope() excludes as "money
  // and operations the partner has no role in". Not a corner case: a
  // partner-provisioned carrier auto-downgrades to complianceOnly when its trial
  // lapses, so this is the STEADY state for the carriers Crown is adding.
  //
  // The preview-mode grant is an UPSELL affordance aimed at the CARRIER ("look
  // what you would get") — it is meaningless to a third-party partner who cannot
  // buy the upgrade, so skipping it costs the carrier nothing. A partner falls
  // through to the member-modules branch and is bounded by their scope.
  if (isComplianceOnlyTenant() && !isPartnerClientSession()) {
    if (!quiet) console.log(`[Access] Compliance-only tenant - ${module} allowed (preview mode)`);
    return true;
  }

  // Demo mode - everything allowed
  if (window.AppState?.isDemo || window.AppState?.tenant === 'demo') {
    if (!quiet) console.log(`[Access] Demo mode - ${module} allowed`);
    return true;
  }

  // Driver PIN mode — restrict to dispatch + pretrip + maintenance (if allowed) + jobboard (if clock access)
  if (window.AppState?.driverModePin) {
    const driverAllowed = ['dispatch', 'pretrip'];
    if (window.driverCanSeeMaintenance?.()) driverAllowed.push('maintenance'); // TASK-774 Gap B
    if (window.driverHasClockAccess?.()) driverAllowed.push('jobboard');
    const allowed = driverAllowed.includes(module);
    if (!quiet) console.log(`[Access] Driver PIN mode - ${module} ${allowed ? 'allowed' : 'blocked'}`);
    return allowed;
  }

  // Public access (driver link / token-based)
  if (window.AppState?.accessToken || window.AppState?.driverMode) {
    // PIN-gated tokens: fall through to PIN system for gated modules
    if (window.AppState?.pinGated) {
      const tokenModules = window.AppState?.tokenModules || [];
      if (tokenModules.includes(module)) {
        if (!quiet) console.log(`[Access] PIN-gated token - ${module} requires PIN check`);
        // Fall through to PIN check below
      } else {
        if (!quiet) console.log(`[Access] PIN-gated token - ${module} not in token modules`);
        return false;
      }
    } else {
      const baseModules = ['dispatch', 'pretrip'];
      const tokenModules = window.AppState?.tokenModules || [];
      const allowedInPublicMode = [...new Set([...baseModules, ...tokenModules])];
      const allowed = allowedInPublicMode.includes(module);
      if (!quiet) console.log(`[Access] Public/driver mode - ${module} ${allowed ? 'allowed' : 'blocked'}`);
      return allowed;
    }
  }

  // TASK-319 — Multi-user member access. members/{uid}.modules is ABSOLUTE
  // for signed-in members. PIN system below only applies to users without
  // a member doc (the kiosk / shared-tablet path). Owners and legacy admins
  // (member docs that pre-date the modules field) see everything.
  //
  // 2026-05-26 hotfix — legacy member docs (esp. founder/owner docs created
  // before the `status` field existed) have `status: undefined`. The
  // original `status === 'active'` guard was bumping those owners to the
  // PIN fallback below, silently hiding ALL modules from the actual tenant
  // owner. New rule: any non-revoked/non-inactive status counts. Active +
  // missing both qualify; only explicit 'revoked' / 'inactive' / 'pending'
  // get filtered out.
  // TASK-595 — a signed-in user's member doc is still loading (slow cold-load
  // race: tenant set, member read not yet resolved). Do NOT fall through to the
  // PIN system below, which would spuriously PIN-challenge an entitled member.
  // Signal navigateToView to defer + re-gate once the read resolves. Scoped to
  // the loadUserTenant path (the only setter), so PIN/kiosk users never see it.
  if (window.AppState?.memberAccessPending) {
    if (!quiet) console.log(`[Access] memberAccess still loading — defer ${module}`);
    return 'loading';
  }

  const memberAccess = window.AppState?.memberAccess;
  const memberDocBlocked = memberAccess && ['revoked', 'inactive', 'pending'].includes(memberAccess.status);
  if (memberAccess && !memberDocBlocked) {
    if (memberAccess.role === 'owner') {
      // TASK-719 — conditional owner bypass. By DEFAULT (no explicit
      // device-lock flag) the owner keeps full access exactly as before — this
      // preserves the 2026-05-26 anti-lockout hotfix (a fresh owner login has
      // no flag → falls into this `return true`). ONLY when the owner has
      // DELIBERATELY locked this device (lock FAB set `dispatchdeck_owner_locked_<tenant>`)
      // do we skip the bypass and fall through to the PIN system below, so the
      // owner's nav modules honor access-PINs like a kiosk user.
      if (!isOwnerDeviceLocked()) {
        if (!quiet) console.log(`[Access] Member owner — ${module} allowed (full access)`);
        return true;
      }
      // else: owner deliberately locked this device — fall through to the PIN
      // path below (isModuleUnlocked / no-PINs → true / 'needs-pin').
      if (!quiet) console.log(`[Access] Member owner — device locked, deferring ${module} to PIN system`);
    } else if (memberAccess.role === 'admin' && (!Array.isArray(memberAccess.modules) || memberAccess.modules.length === 0)) {
      // Legacy admin without modules field — full access (pre-TASK-319).
      // TASK-719 — same conditional bypass as owner: full access by default,
      // PIN-scoped only when this device is explicitly owner-locked.
      if (!isOwnerDeviceLocked()) {
        if (!quiet) console.log(`[Access] Legacy admin (no modules field) — ${module} allowed`);
        return true;
      }
      if (!quiet) console.log(`[Access] Legacy admin — device locked, deferring ${module} to PIN system`);
    }
    // TASK-719 — a device-locked owner / legacy-admin must fall THROUGH to the
    // PIN system below, not get caught by a stray `modules` array on their own
    // doc. Owner/legacy-admin already returned above when unlocked; if we reach
    // here as one of those roles, the device is locked → skip the member-modules
    // branch so control reaches the PIN path (isModuleUnlocked / no-PINs).
    const isOwnerOrLegacyAdmin = memberAccess.role === 'owner'
      || (memberAccess.role === 'admin' && (!Array.isArray(memberAccess.modules) || memberAccess.modules.length === 0));
    if (!isOwnerOrLegacyAdmin && Array.isArray(memberAccess.modules)) {
      // Person-scoped surfaces always allowed for members with personId linked.
      // 'account' settings always allowed.
      // TASK-355 2026-05-22: My Today killed; jobboard is the personId-scoped
      // daily view now and is gated through the regular modules array.
      const implicitForMembers = new Set(['account']); // 'approvals' arrives here as 'account' (VIEW_MODULE_ALIASES)
      if (implicitForMembers.has(module)) {
        if (!quiet) console.log(`[Access] Member implicit — ${module} allowed`);
        return true;
      }
      // TASK-9779 — '811-permits' opens the Dispatch VIEW only (see isMember811Only).
      // TASK-9778 — the Receipts & Expenses view: the receipts module, or billing
      // (which already holds every receipt surface).
      const allowed = memberAccess.modules.includes(module) || (module === 'dispatch' && isMember811Only(memberAccess))
        || (module === 'receipts' && memberAccess.modules.includes('billing'));
      if (!quiet) console.log(`[Access] Member modules — ${module} ${allowed ? 'allowed' : 'blocked'} (role=${memberAccess.role})`);
      return allowed;
    }
  }

  // TASK-9778 — 'receipts' is a signed-in members grant (no PIN checkbox). On
  // the kiosk / shared-login PIN path below it rides the Billing PIN, so it can
  // never fall to the "allowed by default" tail (the TASK-9131 'ap' class).
  if (module === 'receipts') module = 'billing';

  // If settings haven't loaded yet, require PIN as a safe default
  if (!window.AppState?.settings) {
    if (!quiet) console.log(`[Access] Settings not loaded yet - requiring PIN for ${module}`);
    return 'needs-pin';
  }

  // If no PINs are configured, grant full access (new account / owner)
  const accessPins = accessPinMap();
  const hasPinsConfigured = Object.keys(accessPins).length > 0;
  if (!hasPinsConfigured) {
    if (!quiet) console.log(`[Access] No PINs configured - ${module} allowed (owner/new account)`);
    return true;
  }

  // Check if module is unlocked via PIN
  // TASK-850B: 'payroll' PIN-gated on kiosk/shared-login tenants (crown-jewels
  // surface — bank/tax setup). Server-side isTenantAdmin is the real boundary.
  const lockedModules = ['dispatch', 'roster', 'maintenance', 'hr', 'crm', 'clock', 'billing', 'workforce', 'bi', 'compliance', 'payroll'];
  if (lockedModules.includes(module)) {
    let unlocked = isModuleUnlocked(module);
    // Allow dispatch access if user has 811 permits PIN
    if (!unlocked && module === 'dispatch') {
      unlocked = isModuleUnlocked('811');
    }
    if (!quiet) console.log(`[Access] ${module} is locked module, unlocked=${unlocked}`);
    if (unlocked) {
      return true;
    }
    return 'needs-pin';
  }

  if (!quiet) console.log(`[Access] ${module} allowed by default`);
  return true;
}

// TASK-9658 S1b — when the no-access screen is answering a RECORD LINK (an
// alert's "In DispatchDeck: ..." link), say so first, so the person knows the
// link worked and what is missing. Fixed copy keyed by view — never the id.
const _RECORD_LINK_KIND = { maintenance: 'a Maintenance record', pretrip: 'an inspection', dispatch: 'a Dispatch record' };
function _deniedRecordLinkLead(module) {
  const f = window.AppState?.pendingRecordFocus;
  if (!f || f.view !== module || !Object.prototype.hasOwnProperty.call(_RECORD_LINK_KIND, module)) return '';
  return `That link opens ${_RECORD_LINK_KIND[module]}. `;
}

function showNoAccessMessage(module) {
  const _linkLead = _deniedRecordLinkLead(module); // TASK-9658 S1b — read BEFORE the clear below
  // TASK-9658 S1 — no access to the module = no access to its record. Drop a
  // parked record focus so a later grant/PIN unlock can't open it unasked.
  if (window.AppState) window.AppState.pendingRecordFocus = null;
  if (window.AppState) window.AppState.pendingFocus = null; // TASK-9653 S1 — same for an open hint
  // TASK-355 2026-05-22: My Today killed; PRETTY no longer maps mytoday/my-today.
  const PRETTY = { hr: 'HR', bi: 'BI', crm: 'CRM', ap: 'AR / AP', jobboard: 'Job Board' };
  const moduleName = PRETTY[module] || capitalizeFirst(module);
  const isDriverPin = window.AppState?.driverModePin;

  // TASK-319 multi-user member — PIN prompts don't apply. Show a friendly
  // no-access message + a button to a view they DO have access to.
  // TASK-1528 (COD Code Reviewer) — match checkModuleAccess's memberDocBlocked
  // rule (any non-revoked/inactive/pending status counts, not only 'active'),
  // so the same member shape that suppresses the PIN challenge upstream also
  // gets the member-aware message here — never the generic "PIN Required"
  // page with an Enter PIN button they can't answer.
  const memberAccess = window.AppState?.memberAccess;
  const isMember = memberAccess
    && !['revoked', 'inactive', 'pending'].includes(memberAccess.status)
    && memberAccess.role !== 'owner'
    && Array.isArray(memberAccess.modules)
    && memberAccess.modules.length > 0;
  if (isMember) {
    // Send them to a view they DO have — dispatch-first member default
    // (see computeMemberDefaultView).
    const fallback = computeMemberDefaultView(memberAccess);
    const fallbackPretty = PRETTY[fallback] || capitalizeFirst(fallback);
    DOM.mainContent.innerHTML = `
      <div class="card" style="max-width:500px;margin:var(--spacing-xl) auto;text-align:center;">
        <div class="empty-state">
          <div class="empty-state-icon"><span class="material-icons" style="font-size:64px;color:var(--text-muted);">lock</span></div>
          <div class="empty-state-title">No access</div>
          <div class="empty-state-text">
            ${_linkLead}Your account doesn't have access to the ${moduleName} module. Ask an admin to enable it from Roster &rarr; Edit your person &rarr; Set up login.
          </div>
          <button class="btn btn-primary" onclick="navigateToView('${fallback}')" style="margin-top:var(--spacing-md);">
            <span class="material-icons">arrow_back</span> Back to ${fallbackPretty}
          </button>
        </div>
      </div>
    `;
    return;
  }

  DOM.mainContent.innerHTML = `
    <div class="card" style="max-width:500px;margin:var(--spacing-xl) auto;text-align:center;">
      <div class="empty-state">
        <div class="empty-state-icon"><span class="material-icons" style="font-size:64px;color:var(--text-muted);">${isDriverPin ? 'block' : 'lock'}</span></div>
        <div class="empty-state-title">${isDriverPin ? 'Not Available' : 'PIN Required'}</div>
        <div class="empty-state-text">
          ${_linkLead}${isDriverPin
            ? 'This feature requires a full account login. Use the Dispatch and Inspections tabs.'
            : `Enter an access PIN to unlock the ${moduleName} module.`}
        </div>
        ${isDriverPin ? `
        <button class="btn btn-primary" onclick="navigateToView('dispatch')" style="margin-top:var(--spacing-md);">
          <span class="material-icons">arrow_back</span> Back to Dispatch
        </button>` : `
        <button class="btn btn-primary" onclick="showPinChallenge('${module}', (ok) => { if(ok) navigateToView('${module}'); }, '${moduleForView(module)}')" style="margin-top:var(--spacing-md);">
          <span class="material-icons">lock_open</span> Enter PIN
        </button>`}
      </div>
    </div>
  `;
}

// FMCSA DOT Number Verification
window.verifyDotNumber = async function() {
  const dotInput = document.getElementById('settings-dot-number');
  const resultDiv = document.getElementById('dot-verify-result');
  const btn = document.getElementById('btn-verify-dot');
  if (!dotInput || !resultDiv) return;

  const dotNumber = dotInput.value.trim();
  if (!dotNumber || !/^\d{1,10}$/.test(dotNumber)) {
    showToast('Enter a valid DOT number (digits only)', 'error');
    return;
  }

  btn.disabled = true;
  btn.innerHTML = '<span class="btn-spinner"></span>';
  resultDiv.style.display = 'none';

  try {
    const lookup = httpsCallable(functions, 'fmcsaCarrierLookup');
    const { data } = await lookup({ dotNumber });

    const statusColor = data.allowedToOperate === 'Y' ? '#059669' : '#DC2626';
    const statusText = data.allowedToOperate === 'Y' ? 'Authorized' : 'NOT Authorized';
    const safetyColor = data.safetyRating === 'Satisfactory' ? '#059669' : data.safetyRating ? '#F59E0B' : 'var(--text-muted)';

    resultDiv.innerHTML = `
      <div style="background:var(--bg-secondary);border-radius:var(--radius-md);padding:12px;font-size:0.82rem;">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">
          <strong style="font-size:0.95rem;">${data.legalName || 'Unknown'}</strong>
          <span style="background:${statusColor};color:#fff;padding:2px 8px;border-radius:12px;font-size:0.72rem;font-weight:600;">${statusText}</span>
        </div>
        ${data.dbaName ? `<div style="color:var(--text-secondary);margin-bottom:4px;">DBA: ${data.dbaName}</div>` : ''}
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;color:var(--text-secondary);">
          <div><strong>DOT:</strong> ${data.dotNumber}</div>
          <div><strong>MC:</strong> ${data.mcNumber || 'N/A'}</div>
          <div><strong>Drivers:</strong> ${data.totalDrivers}</div>
          <div><strong>Power Units:</strong> ${data.totalPowerUnits}</div>
          <div><strong>Safety Rating:</strong> <span style="color:${safetyColor};font-weight:600;">${data.safetyRating || 'Not Rated'}</span></div>
          <div><strong>Entity:</strong> ${data.entityType || 'N/A'}</div>
          <div><strong>Insurance (BIPD):</strong> ${data.bipdInsuranceOnFile || 'N/A'}</div>
          <div><strong>Insurance (Cargo):</strong> ${data.cargoInsuranceOnFile || 'N/A'}</div>
        </div>
        ${data.crashTotal > 0 ? `
        <div style="margin-top:8px;padding:8px;background:#FEF3C7;border-radius:var(--radius-sm);font-size:0.78rem;">
          <strong>Crash History (24mo):</strong> ${data.crashTotal} total — ${data.fatalCrash} fatal, ${data.injCrash} injury, ${data.towCrash} tow-away
        </div>` : ''}
        <div style="margin-top:8px;font-size:0.72rem;color:var(--text-muted);">
          ${data.phyCity ? `${data.phyStreet}, ${data.phyCity}, ${data.phyState} ${data.phyZipcode}` : ''} ${data.phone ? `| ${data.phone}` : ''}
        </div>
      </div>
    `;
    resultDiv.style.display = 'block';
  } catch (err) {
    console.error('[verifyDotNumber]', err);
    const msg = err?.message || 'Lookup failed';
    resultDiv.innerHTML = `<div style="color:#DC2626;font-size:0.82rem;"><span class="material-icons" style="font-size:14px;vertical-align:text-bottom;">error</span> ${msg.includes('not-found') ? 'Carrier not found — check the DOT number' : msg}</div>`;
    resultDiv.style.display = 'block';
  } finally {
    btn.disabled = false;
    btn.innerHTML = '<span class="material-icons" style="font-size:16px;">verified</span> Verify';
  }
};

// Logo upload helpers
window.previewLogoUpload = function(input) {
  const file = input.files?.[0];
  const preview = document.getElementById('settings-logo-preview');
  if (!file || !preview) return;
  // TASK-1383 — guarded read; preview is best-effort (upload path unaffected).
  window.FileReadUtil.readAsDataURL(file).then((dataUrl) => {
    preview.src = dataUrl;
    preview.style.display = '';
  }).catch(() => { /* preview only — silent */ });
};

window.removeLogo = async function() {
  if (!confirm('Remove company logo?')) return;
  try {
    const tenant = window.AppState?.tenant;
    if (!tenant) return;
    const logoRef = ref(storage, `tenants/${tenant}/branding/company-logo`);
    try { await deleteObject(logoRef); } catch (e) { /* may not exist */ }
    const tenantRef = doc(db, 'tenants', tenant);
    await setDoc(tenantRef, { companyLogoUrl: null }, { merge: true });
    if (window.AppState.tenantData) window.AppState.tenantData.companyLogoUrl = null;
    const preview = document.getElementById('settings-logo-preview');
    const removeBtn = document.getElementById('btn-remove-logo');
    if (preview) { preview.src = ''; preview.style.display = 'none'; }
    if (removeBtn) removeBtn.style.display = 'none';
    showToast('Logo removed', 'success');
  } catch (err) {
    console.error('[Settings] Error removing logo:', err);
    showToast('Error removing logo', 'error');
  }
};

// Test Samsara API token from Settings. TASK-1621 — this used to fetch
// https://api.samsara.com/fleet/vehicles DIRECTLY FROM THE BROWSER with the
// raw token in the Authorization header (the client held the plaintext
// credential by design). Now it calls the testSamsaraConnection callable:
// the browser sends only a NOT-YET-SAVED typed value (or nothing, to test
// the already-saved secret), and the server makes the real Samsara call.
// The token never needs to leave the backend for this button to work.
async function testSamsaraToken() {
  const tokenInput = document.getElementById('settings-samsara-token');
  const resultDiv = document.getElementById('samsara-test-result');
  const btn = document.getElementById('btn-test-samsara');
  if (!resultDiv || !btn) return;

  const typedToken = tokenInput?.value?.trim() || undefined;

  btn.disabled = true;
  btn.innerHTML = '<span class="btn-spinner"></span>';
  resultDiv.style.display = 'block';
  resultDiv.innerHTML = '<span style="color:var(--text-muted);">Testing connection...</span>';

  try {
    const result = await httpsCallable(functions, 'testSamsaraConnection')({
      tenantId: window.AppState.tenant,
      token: typedToken,
    });
    const { ok, count, message } = result.data || {};
    if (ok) {
      resultDiv.innerHTML = `<span style="color:#10B981;">Connected! Found ${count} vehicle${count !== 1 ? 's' : ''} in your Samsara fleet.</span>`;
    } else {
      resultDiv.innerHTML = `<span style="color:#DC2626;">${message || 'Connection failed.'}</span>`;
    }
  } catch (err) {
    console.error('[Settings] testSamsaraConnection error:', err);
    resultDiv.innerHTML = `<span style="color:#DC2626;">Connection failed: ${err.message}</span>`;
  }

  btn.disabled = false;
  btn.innerHTML = '<span class="material-icons" style="font-size:16px;">wifi_tethering</span> Test';
}

// Expose settings functions globally
window.openSettingsModal = openSettingsModal;
window.closeSettingsModal = closeSettingsModal;
window.saveSettings = saveSettings;
window.testSamsaraToken = testSamsaraToken;

// ============================================
// TASK-219 Slice 3 — APPLICATION QUESTIONS MANAGER
// ============================================
// Carrier-defined custom questions on apply.html. Up to 25 active per
// tenant. text + yesno types (multichoice deferred). Snapshot model on
// candidate doc preserves prompt + type per UX Lead 2026-05-06.
//
// TASK-1467 — 10 → 25 (all tenants). This constant is the ONLY gate: both the
// add guard and the restore/unarchive guard read it, getApplyTenantInfo has no
// count limit of its own, and the tenant-doc rule is a denylist that does not
// mention applicationQuestions. Note ARCHIVED questions live in the same array
// and do not count toward this cap — that is why a firestore.rules `size()`
// check was explicitly rejected (it would break long-lived tenants over time);
// the server-side total ceiling lives in TASK-1468's APPQ_MAX_SERVED instead.
// Coupled knob: active questions are also fed into the AI pre-screening system
// prompt (_buildScreeningSystemPrompt), so raising this grows that prompt too.
const APPQ_CAP_ACTIVE = 25;
let _appqEditingId = null;

// TASK-1467 Slice B — optional position-type scoping.
// Absent / empty / all-three ⇒ ask everyone. That is deliberately the same
// behavior every pre-existing question already has, so this is backward
// compatible with every tenant without a migration.
const APPQ_POSITION_TYPES = [
  { key: 'cdl', label: 'CDL Drivers' },
  { key: 'non-cdl-driver', label: 'Non-CDL Drivers' },
  { key: 'drives-no-cmv', label: 'Drives, no CMV' }, // TASK-9696
  { key: 'non-driver', label: 'Non-Drivers' },
];

/**
 * Read the three scope checkboxes for a given id prefix.
 * Returns undefined when the question should be asked of everyone (all checked)
 * — we store NOTHING in that case rather than an all-three array, so the common
 * case stays identical to existing data and can't drift out of sync if a fourth
 * position type is ever added.
 * Returns null when the user unchecked everything, which is a mistake (a
 * question nobody can ever see), so callers reject it.
 */
function _appqReadPositionTypes(prefix) {
  const picked = APPQ_POSITION_TYPES
    .filter(pt => document.getElementById(`${prefix}-${pt.key}`)?.checked)
    .map(pt => pt.key);
  if (picked.length === 0) return null;
  if (picked.length === APPQ_POSITION_TYPES.length) return undefined;
  return picked;
}

/**
 * Does this question apply to the given position type?
 * Absent/empty positionTypes ⇒ yes (asked of everyone). This is the SAME
 * predicate apply.html uses to decide whether to render — keep them in step.
 */
function _appqScopedFor(q, key) {
  const raw = Array.isArray(q && q.positionTypes) ? q.positionTypes : null;
  if (!raw || raw.length === 0) return true;
  // Same sanitization as the server predicate — an unrecognized value must not
  // mean "everyone" in one place and "nobody" in another.
  const pts = raw.filter(t => APPQ_POSITION_TYPES.some(pt => pt.key === t));
  if (pts.length === 0) return true;
  return pts.includes(key);
}

/** Human-readable scope for the settings list. '' when it applies to everyone. */
function _appqScopeLabel(q) {
  const pts = Array.isArray(q && q.positionTypes) ? q.positionTypes : null;
  if (!pts || pts.length === 0 || pts.length >= APPQ_POSITION_TYPES.length) return '';
  return pts
    .map(k => (APPQ_POSITION_TYPES.find(pt => pt.key === k) || {}).label || k)
    .join(', ');
}

function _appqGetCurrent() {
  const qs = window.AppState?.settings?.applicationQuestions;
  return Array.isArray(qs) ? qs.slice() : [];
}

function _appqEsc(s) {
  return String(s ?? '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
}

function _appqRenderList() {
  const list = document.getElementById('application-questions-list');
  if (!list) return;
  const all = _appqGetCurrent();
  const active = all.filter(q => q && q.archived !== true);
  const archived = all.filter(q => q && q.archived === true);
  // TASK-1467 — show the cap, not just the count. At 10 the ceiling was easy to
  // hold in your head; at 25 it isn't, and the only other place it appears is
  // the error toast you get AFTER being refused. Updated BEFORE the empty-state
  // early return below, which otherwise leaves a stale "(3 active)" badge after
  // the last question is removed.
  const countEl = document.getElementById('application-questions-count');
  if (countEl) {
    countEl.textContent = `(${active.length} of ${APPQ_CAP_ACTIVE} active)`;
    countEl.style.color = active.length >= APPQ_CAP_ACTIVE ? 'var(--danger, #DC2626)' : '';
  }
  if (active.length === 0 && archived.length === 0) {
    list.innerHTML = `<div style="font-size:0.85rem;color:var(--text-secondary);text-align:center;padding:20px;background:var(--bg-page);border-radius:8px;">No questions yet. Add your first below.</div>`;
    return;
  }
  const renderEditRow = (q) => `
    <div style="padding:12px;border:1px solid var(--brand-blue);border-radius:8px;background:#F0F7FF;">
      <div class="form-group" style="margin-bottom:8px;">
        <label style="font-size:0.75rem;color:var(--text-secondary);">Prompt</label>
        <input type="text" id="appq-edit-prompt-${_appqEsc(q.id)}" class="form-control" maxlength="200" value="${_appqEsc(q.prompt)}">
      </div>
      <div class="form-group" style="margin-bottom:8px;">
        <label style="font-size:0.75rem;color:var(--text-secondary);">Type</label>
        <select id="appq-edit-type-${_appqEsc(q.id)}" class="form-control">
          <option value="text"${q.type === 'yesno' ? '' : ' selected'}>Text answer (open-ended)</option>
          <option value="yesno"${q.type === 'yesno' ? ' selected' : ''}>Yes / No</option>
        </select>
      </div>
      <div class="form-group" style="margin-bottom:10px;">
        <label style="display:flex;align-items:center;gap:8px;font-weight:400;font-size:0.85rem;">
          <input type="checkbox" id="appq-edit-required-${_appqEsc(q.id)}"${q.required ? ' checked' : ''}>
          Required (applicant must answer)
        </label>
      </div>
      <div class="form-group" style="margin-bottom:10px;">
        <label style="font-size:0.75rem;color:var(--text-secondary);">Ask this of</label>
        <div style="display:flex;flex-direction:column;gap:6px;margin-top:4px;">
          ${APPQ_POSITION_TYPES.map(pt => `
          <label style="display:flex;align-items:center;gap:8px;font-weight:400;font-size:0.85rem;">
            <input type="checkbox" id="appq-edit-pos-${_appqEsc(q.id)}-${pt.key}"${_appqScopedFor(q, pt.key) ? ' checked' : ''}>
            ${pt.label}
          </label>`).join('')}
        </div>
      </div>
      <div style="display:flex;gap:8px;">
        <button type="button" class="btn btn-primary btn-sm" onclick="window.saveEditedApplicationQuestion('${_appqEsc(q.id)}')" style="padding:4px 12px;font-size:0.8rem;">
          <span class="material-icons" style="font-size:14px;vertical-align:middle;">save</span> Save
        </button>
        <button type="button" class="btn btn-secondary btn-sm" onclick="window.cancelEditApplicationQuestion()" style="padding:4px 12px;font-size:0.8rem;">Cancel</button>
      </div>
    </div>`;
  const renderRow = (q) => {
    if (q.id === _appqEditingId && !q.archived) return renderEditRow(q);
    return `
    <div style="display:flex;justify-content:space-between;align-items:center;gap:10px;padding:10px 12px;border:1px solid var(--border-light);border-radius:8px;background:${q.archived ? '#FAFAFA' : '#fff'};">
      <div style="flex:1;min-width:0;">
        <div style="font-size:0.9rem;color:var(--text-primary);font-weight:600;${q.archived ? 'text-decoration:line-through;color:var(--text-muted);' : ''}">${_appqEsc(q.prompt)}</div>
        <div style="font-size:0.72rem;color:var(--text-secondary);margin-top:2px;">
          ${q.type === 'yesno' ? 'Yes / No' : 'Text'} · ${q.required ? '<strong style="color:var(--brand-blue);">Required</strong>' : 'Optional'}${q.archived ? ' · <em>archived</em>' : ''}${_appqScopeLabel(q) ? ` · <span style="color:var(--brand-blue);">${_appqEsc(_appqScopeLabel(q))} only</span>` : ''}
        </div>
      </div>
      <div style="display:flex;gap:6px;flex-shrink:0;">
        ${q.archived
          ? `<button type="button" class="btn btn-sm btn-secondary" onclick="window.unarchiveApplicationQuestion('${_appqEsc(q.id)}')" style="padding:4px 10px;font-size:0.75rem;">Restore</button>`
          : `<button type="button" class="btn btn-sm btn-secondary" onclick="window.editApplicationQuestion('${_appqEsc(q.id)}')" style="padding:4px 10px;font-size:0.75rem;" title="Edit prompt, type, or required">Edit</button>
             <button type="button" class="btn btn-sm btn-secondary" onclick="window.archiveApplicationQuestion('${_appqEsc(q.id)}')" style="padding:4px 10px;font-size:0.75rem;">Archive</button>`}
      </div>
    </div>`;
  };
  const activeHtml = active.length > 0
    ? `<div style="display:flex;flex-direction:column;gap:var(--spacing-md);">${active.map(renderRow).join('')}</div>`
    : '';
  const archivedHtml = archived.length > 0
    ? `<div style="font-size:0.78rem;color:var(--text-secondary);margin:var(--spacing-lg) 0 var(--spacing-md);font-weight:600;">Archived</div><div style="display:flex;flex-direction:column;gap:var(--spacing-md);">${archived.map(renderRow).join('')}</div>`
    : '';
  list.innerHTML = activeHtml + archivedHtml;
  // (count badge is updated at the top — it must also run on the empty-state path)
}

async function _appqPersist(updatedList) {
  if (!window.AppState?.tenant) {
    showToast('Not logged in', 'error');
    return false;
  }
  // TASK-9449 — a managing compliance partner inside this carrier's app cannot
  // write the tenant doc (owner/admin rule, deliberately). It saves through
  // executePartnerAction('tenant.setApplicationQuestions'), which writes ONLY
  // settings.applicationQuestions, refuses any other key, and audits old -> new
  // in the partner's name. The server's cleaned list becomes local state.
  const _pctx = window.AppState?.partnerClientContext;
  if (isPartnerClientSession() && _pctx && _pctx.tenantId === window.AppState.tenant) {
    try {
      const res = await httpsCallable(functions, 'executePartnerAction')({
        tenantId: _pctx.tenantId,
        action: 'tenant.setApplicationQuestions',
        payload: { questions: updatedList },
      });
      const saved = Array.isArray(res?.data?.questions) ? res.data.questions : updatedList;
      if (!window.AppState.settings) window.AppState.settings = {};
      window.AppState.settings.applicationQuestions = saved;
      return true;
    } catch (e) {
      console.error('[appq] partner persist failed', e);
      showToast('Save failed: ' + String(e?.message || 'unknown').replace(/^internal:\s*/, ''), 'error');
      return false;
    }
  }
  try {
    const tenantRef = doc(db, 'tenants', window.AppState.tenant);
    await setDoc(tenantRef, { settings: { applicationQuestions: updatedList } }, { merge: true });
    if (!window.AppState.settings) window.AppState.settings = {};
    window.AppState.settings.applicationQuestions = updatedList;
    return true;
  } catch (e) {
    console.error('[appq] persist failed', e);
    showToast('Save failed: ' + (e?.message || 'unknown'), 'error');
    return false;
  }
}

// TASK-995 — only an owner/admin (or founder / legacy single-login) may flip the
// AI pre-screening toggle. Matches the client gate used elsewhere for admin-only
// surfaces; the tenant-doc write rule is the real enforcement.
function _appqCanManageScreening() {
  try { if (typeof isFounderAccount === 'function' && isFounderAccount()) return true; } catch (_) {}
  const ma = window.AppState?.memberAccess;
  const role = ma?.role;
  return !ma || role === 'owner' || role === 'admin';
}

function _appqSyncScreeningToggle() {
  // TASK-1024 — AI pre-screening is paid-tier Smart Tools. Hide the toggle
  // entirely for a complianceOnly (free-tier / trial-expired) carrier; the
  // server (conductScreeningInterview + getApplyTenantInfo) also treats it as OFF.
  const wrap = document.getElementById('ai-screening-toggle-wrap');
  // TASK-9449 — a managing partner reaches this modal for the QUESTIONS only; the
  // AI pre-screening switch is a paid Smart Tools setting and stays the carrier's.
  if ((typeof window.isComplianceOnlyTenant === 'function' && window.isComplianceOnlyTenant()) || isPartnerClientSession()) {
    if (wrap) wrap.style.display = 'none';
    return;
  }
  if (wrap) wrap.style.display = '';
  const cb = document.getElementById('ai-screening-enabled');
  if (!cb) return;
  cb.checked = window.AppState?.settings?.aiScreening?.enabled === true;
  const canManage = _appqCanManageScreening();
  cb.disabled = !canManage;
  const note = document.getElementById('ai-screening-admin-note');
  if (note) note.style.display = canManage ? 'none' : 'block';
}

window.onToggleAiScreening = async function() {
  const cb = document.getElementById('ai-screening-enabled');
  if (!cb) return;
  if (!_appqCanManageScreening()) {
    cb.checked = window.AppState?.settings?.aiScreening?.enabled === true; // revert
    showToast('Only an owner or admin can change this.', 'error');
    return;
  }
  const desired = !!cb.checked;
  cb.disabled = true;
  const ok = await _settingsPersist({ aiScreening: { enabled: desired } });
  cb.disabled = false;
  if (ok) {
    showToast(desired ? 'AI pre-screening enabled' : 'AI pre-screening disabled', 'success');
  } else {
    cb.checked = !desired; // revert on failure
  }
};

window.openApplicationQuestionsModal = function() {
  const m = document.getElementById('application-questions-modal');
  if (!m) return;
  _appqEditingId = null;
  _appqRenderList();
  _appqSyncScreeningToggle();
  // Reset add form
  const promptEl = document.getElementById('appq-new-prompt');
  if (promptEl) promptEl.value = '';
  const typeEl = document.getElementById('appq-new-type');
  if (typeEl) typeEl.value = 'text';
  const reqEl = document.getElementById('appq-new-required');
  if (reqEl) reqEl.checked = false;
  m.classList.remove('hidden');
};

window.closeApplicationQuestionsModal = function() {
  const m = document.getElementById('application-questions-modal');
  if (m) m.classList.add('hidden');
};

window.addApplicationQuestion = async function() {
  const promptEl = document.getElementById('appq-new-prompt');
  const typeEl = document.getElementById('appq-new-type');
  const reqEl = document.getElementById('appq-new-required');
  const prompt = (promptEl?.value || '').trim();
  const type = typeEl?.value || 'text';
  const required = !!reqEl?.checked;
  if (!prompt) { showToast('Prompt is required', 'error'); return; }
  if (prompt.length > 200) { showToast('Prompt is too long (max 200 chars)', 'error'); return; }
  if (type !== 'text' && type !== 'yesno') { showToast('Invalid type', 'error'); return; }
  // TASK-1467 Slice B
  const positionTypes = _appqReadPositionTypes('appq-new-pos');
  if (positionTypes === null) {
    showToast('Pick at least one applicant type — otherwise nobody would ever see this question.', 'error');
    return;
  }
  const current = _appqGetCurrent();
  const activeCount = current.filter(q => q.archived !== true).length;
  if (activeCount >= APPQ_CAP_ACTIVE) {
    showToast(`Cap reached (${APPQ_CAP_ACTIVE} active questions). Archive one before adding more.`, 'error');
    return;
  }
  // Stable id from timestamp + random suffix; never reused
  const id = 'q_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);
  const newQ = {
    id, prompt, type, required, archived: false,
    createdAt: new Date().toISOString(),
    order: current.length, // simple insertion-order int
    // TASK-1467 Slice B — omitted entirely when it applies to everyone
    ...(positionTypes ? { positionTypes } : {}),
  };
  const updated = [...current, newQ];
  const ok = await _appqPersist(updated);
  if (ok) {
    showToast('Question added', 'success');
    if (promptEl) promptEl.value = '';
    if (reqEl) reqEl.checked = false;
    _appqRenderList();
  }
};

window.editApplicationQuestion = function(qid) {
  _appqEditingId = qid;
  _appqRenderList();
  // Focus the prompt input for fast inline typing
  const input = document.getElementById(`appq-edit-prompt-${qid}`);
  if (input) { input.focus(); input.select(); }
};

window.cancelEditApplicationQuestion = function() {
  _appqEditingId = null;
  _appqRenderList();
};

window.saveEditedApplicationQuestion = async function(qid) {
  const promptEl = document.getElementById(`appq-edit-prompt-${qid}`);
  const typeEl = document.getElementById(`appq-edit-type-${qid}`);
  const reqEl = document.getElementById(`appq-edit-required-${qid}`);
  const prompt = (promptEl?.value || '').trim();
  const type = typeEl?.value || 'text';
  const required = !!reqEl?.checked;
  if (!prompt) { showToast('Prompt is required', 'error'); return; }
  if (prompt.length > 200) { showToast('Prompt is too long (max 200 chars)', 'error'); return; }
  if (type !== 'text' && type !== 'yesno') { showToast('Invalid type', 'error'); return; }
  // TASK-1467 Slice B — must be read and written here too. The spread below
  // preserves unknown fields, so omitting this wouldn't LOSE the scoping, but
  // it would make it uneditable (delete-and-re-add to change who sees it).
  const positionTypes = _appqReadPositionTypes(`appq-edit-pos-${qid}`);
  if (positionTypes === null) {
    showToast('Pick at least one applicant type — otherwise nobody would ever see this question.', 'error');
    return;
  }
  const updated = _appqGetCurrent().map(q => {
    if (q.id !== qid) return q;
    const next = { ...q, prompt, type, required, updatedAt: new Date().toISOString() };
    // undefined ⇒ applies to everyone ⇒ remove the field rather than storing
    // an all-three array, matching what the add path writes.
    if (positionTypes) next.positionTypes = positionTypes;
    else delete next.positionTypes;
    return next;
  });
  const ok = await _appqPersist(updated);
  if (ok) {
    showToast('Question updated', 'success');
    _appqEditingId = null;
    _appqRenderList();
  }
};

window.archiveApplicationQuestion = async function(qid) {
  const updated = _appqGetCurrent().map(q => q.id === qid ? { ...q, archived: true, archivedAt: new Date().toISOString() } : q);
  const ok = await _appqPersist(updated);
  if (ok) { showToast('Question archived', 'success'); _appqRenderList(); }
};

window.unarchiveApplicationQuestion = async function(qid) {
  const current = _appqGetCurrent();
  const activeCount = current.filter(q => q.archived !== true).length;
  if (activeCount >= APPQ_CAP_ACTIVE) {
    showToast(`Can't restore — already at cap (${APPQ_CAP_ACTIVE} active). Archive one first.`, 'error');
    return;
  }
  const updated = current.map(q => q.id === qid ? { ...q, archived: false, archivedAt: null } : q);
  const ok = await _appqPersist(updated);
  if (ok) { showToast('Question restored', 'success'); _appqRenderList(); }
};

// ============================================
// TASK-4725 — JOB OPENINGS MANAGER (carrier-authored)
// ============================================
// `tenants/{id}.settings.jobOpenings` — the jobs a carrier is hiring for. The
// apply page asks "Which position are you applying for?" from this list and the
// server writes the chosen title onto the candidate as `position`.
// Shape, id rules and sanitizing live in public/src/jobOpenings.js
// (window.JobOpenings — loaded by hr.js, which app.js imports statically).
//
// Rules this manager enforces:
//   • The application type (bucket) is REQUIRED at creation, has no default, and
//     is FIXED after — edit changes title/location only. The server lets a resolved
//     opening's bucket override the submitted one, so a bucket edited under an
//     in-flight application would demand fields that applicant never saw.
//   • Archive, never delete. Ids are never reused (makeId checks archived ids too).
//   • 25 open at a time (archived don't count).
// Writes go through a TRANSACTION that re-reads the stored list, so two admins
// editing at once can't silently drop each other's openings (the whole-array
// setDoc pattern loses the other writer's entry — TASK-1085's DQ-flag wipe was
// that class).
// Access: the tenant-doc update rule (owner/admin) is the enforcement; a denied
// write surfaces as the "Save failed" toast. No new client-side role gate.
let _joEditingId = null;
let _joLastAddedId = null; // TASK-9448 — id minted inside the add transaction, for its audit row

function _joApi() { return window.JobOpenings || null; }

function _joGetCurrent() {
  const list = window.AppState?.settings?.jobOpenings;
  return Array.isArray(list) ? list.slice() : [];
}

function _joTypeLabel(key) {
  const meta = _joApi()?.typeMeta(key);
  return meta ? meta.label : 'Unknown type';
}

// TASK-9448 — "Requires DOT medical card". Per-type default at
// settings.medCardRequiredByPosition (CDL yes, non-CDL no, non-driver never) and a
// per-opening `requiresMedCard`. The rule itself lives in window.JobOpenings
// (mirrors functions/src/services/medCardRequirement.js, which is what the server
// enforces at submit). Every change is audited with its old and new value.
const _JO_MED_391_41 = 'Under 49 CFR 391.41, anyone who drives a commercial motor vehicle (over 10,000 lb, interstate) needs a DOT medical certificate, with or without a CDL. Only turn this off if the job never puts the driver in a vehicle that counts.';
function _joIsDriverType(pt) { return pt === 'cdl' || pt === 'non-cdl-driver'; }
function _joBucketOf(o) { return (o && typeof o === 'object') ? o.positionType : null; }
function _joMedDefaults() {
  const JO = _joApi();
  const raw = window.AppState?.settings?.medCardRequiredByPosition;
  return JO ? JO.medCardDefaults(raw) : { cdl: true, 'non-cdl-driver': false };
}
function _joMedRequired(o) {
  const JO = _joApi();
  if (!o || !JO) return false;
  return JO.requiresMedCard(o.positionType, o, window.AppState?.settings?.medCardRequiredByPosition);
}
// Turning the med card OFF for a CDL job is never a silent click.
async function _joConfirmCdlMedOff(what) {
  return showConfirm(
    'Stop asking CDL drivers for a medical card?',
    `${what} ${_JO_MED_391_41}`,
    { confirmText: 'Turn it off', cancelText: 'Keep it on', icon: 'medical_services' },
  );
}
function _joRenderMedDefaults() {
  const d = _joMedDefaults();
  const a = document.getElementById('jo-med-default-cdl'); if (a) a.checked = d.cdl === true;
  const b = document.getElementById('jo-med-default-non-cdl-driver'); if (b) b.checked = d['non-cdl-driver'] === true;
}

window._joSetMedDefault = async function(positionType, el) {
  if (!_joIsDriverType(positionType) || !el) return;
  const to = !!el.checked;
  const from = _joMedDefaults()[positionType];
  if (to === from) return;
  if (positionType === 'cdl' && to === false) {
    const ok = await _joConfirmCdlMedOff('CDL Driver jobs without their own setting will stop asking for a DOT medical card.');
    if (!ok) { el.checked = from; return; }
  }
  if (!window.AppState?.tenant) { el.checked = from; showToast('Not logged in', 'error'); return; }
  try {
    const tenantRef = doc(db, 'tenants', window.AppState.tenant);
    const next = await runTransaction(db, async (tx) => {
      const snap = await tx.get(tenantRef);
      const JO = _joApi();
      const stored = JO ? JO.medCardDefaults(snap.exists() ? snap.data()?.settings?.medCardRequiredByPosition : null) : {};
      const updated = { ...stored, [positionType]: to };
      tx.update(tenantRef, { 'settings.medCardRequiredByPosition': updated });
      return updated;
    });
    if (!window.AppState.settings) window.AppState.settings = {};
    window.AppState.settings.medCardRequiredByPosition = next;
    window.logAudit?.('hr', 'MED_CARD_DEFAULT_CHANGED', {
      applicationType: positionType, changed: { requiresMedCard: { from, to } },
    });
    showToast(`${_joTypeLabel(positionType)} jobs ${to ? 'now require' : 'no longer require'} a DOT medical card by default`, 'success');
    _joRenderList();
  } catch (e) {
    el.checked = from;
    console.error('[jobOpenings] med card default save failed', e);
    showToast('Save failed: ' + (e?.message || 'unknown'), 'error');
  }
};

/**
 * Read-modify-write settings.jobOpenings in one transaction. `mutate(list)`
 * returns the next list, or throws an Error whose message is shown to the user.
 */
async function _joMutate(mutate) {
  if (!window.AppState?.tenant) { showToast('Not logged in', 'error'); return false; }
  try {
    const tenantRef = doc(db, 'tenants', window.AppState.tenant);
    const next = await runTransaction(db, async (tx) => {
      const snap = await tx.get(tenantRef);
      const stored = snap.exists() ? snap.data()?.settings?.jobOpenings : null;
      const list = Array.isArray(stored) ? stored.slice() : [];
      const updated = mutate(list);
      tx.update(tenantRef, { 'settings.jobOpenings': updated });
      return updated;
    });
    if (!window.AppState.settings) window.AppState.settings = {};
    window.AppState.settings.jobOpenings = next;
    _hrRenderJobOpeningChips();
    return true;
  } catch (e) {
    if (e && e._joUserError) { showToast(e.message, 'error'); return false; }
    console.error('[jobOpenings] persist failed', e);
    showToast('Save failed: ' + (e?.message || 'unknown'), 'error');
    return false;
  }
}

function _joUserError(msg) { const e = new Error(msg); e._joUserError = true; return e; }

function _joRenderList() {
  const list = document.getElementById('job-openings-list');
  if (!list) return;
  const all = _joGetCurrent().filter(o => o && typeof o === 'object');
  const open = all.filter(o => o.archived !== true);
  const closed = all.filter(o => o.archived === true);
  const countEl = document.getElementById('hr-job-openings-count');
  const cap = _joApi()?.CAP_ACTIVE || 25;
  if (countEl) countEl.textContent = `(${open.length} open)`;
  if (all.length === 0) {
    list.innerHTML = `<div style="font-size:0.85rem;color:var(--text-secondary);text-align:center;padding:20px;background:var(--bg-page);border-radius:8px;">No job openings yet. Without any, your application form works exactly as it does today. Add your first below.</div>`;
    return;
  }
  const e = _appqEsc;
  const idAttr = (id) => e(String(id || ''));
  const renderEditRow = (o) => `
    <div style="padding:12px;border:1px solid var(--brand-blue);border-radius:8px;background:#F0F7FF;">
      <div class="form-group" style="margin-bottom:8px;">
        <label style="font-size:0.75rem;color:var(--text-secondary);">Job title</label>
        <input type="text" id="jo-edit-title-${idAttr(o.id)}" class="form-control" maxlength="120" value="${e(o.title)}">
      </div>
      <div class="form-group" style="margin-bottom:8px;">
        <label style="font-size:0.75rem;color:var(--text-secondary);">Location (optional)</label>
        <input type="text" id="jo-edit-location-${idAttr(o.id)}" class="form-control" maxlength="120" value="${e(o.location || '')}">
      </div>
      ${_joIsDriverType(o.positionType) ? `<label style="display:flex;align-items:center;gap:8px;font-size:0.8rem;color:var(--text-primary);margin-bottom:8px;cursor:pointer;">
        <input type="checkbox" id="jo-edit-medcard-${idAttr(o.id)}"${_joMedRequired(o) ? ' checked' : ''}> Requires DOT medical card
      </label>` : ''}
      <div style="font-size:0.75rem;color:var(--text-secondary);margin-bottom:10px;">Application: <strong style="color:var(--text-primary);">${e(_joTypeLabel(o.positionType))}</strong>. To change it, close this opening and add a new one.</div>
      <div style="display:flex;gap:8px;">
        <button type="button" class="btn btn-primary btn-sm" data-jo-act="save" data-jo-id="${idAttr(o.id)}" style="padding:4px 12px;font-size:0.8rem;">
          <span class="material-icons" style="font-size:14px;vertical-align:middle;">save</span> Save
        </button>
        <button type="button" class="btn btn-secondary btn-sm" data-jo-act="cancel" style="padding:4px 12px;font-size:0.8rem;">Cancel</button>
      </div>
    </div>`;
  const renderRow = (o) => {
    if (o.id === _joEditingId && o.archived !== true) return renderEditRow(o);
    const closedRow = o.archived === true;
    return `
    <div style="display:flex;justify-content:space-between;align-items:center;gap:10px;padding:10px 12px;border:1px solid var(--border-light);border-radius:8px;background:${closedRow ? '#FAFAFA' : '#fff'};">
      <div style="flex:1;min-width:0;">
        <div style="font-size:0.9rem;color:${closedRow ? 'var(--text-secondary)' : 'var(--text-primary)'};font-weight:600;overflow-wrap:anywhere;${closedRow ? 'text-decoration:line-through;' : ''}">${e(o.title || '(untitled)')}</div>
        <div style="font-size:0.72rem;color:var(--text-secondary);margin-top:2px;">
          <span style="color:var(--brand-blue);font-weight:600;">${e(_joTypeLabel(o.positionType))}</span>${o.location ? ` · ${e(o.location)}` : ''}${_joIsDriverType(o.positionType) ? ` · Med card: ${_joMedRequired(o) ? 'required' : 'not required'}` : ''}${closedRow ? ' · <em>closed</em>' : ''}
        </div>
      </div>
      <div style="display:flex;gap:6px;flex-shrink:0;">
        ${closedRow
          ? `<button type="button" class="btn btn-sm btn-secondary" data-jo-act="reopen" data-jo-id="${idAttr(o.id)}" style="padding:4px 10px;font-size:0.75rem;">Reopen</button>`
          : `<button type="button" class="btn btn-sm btn-secondary" data-jo-act="edit" data-jo-id="${idAttr(o.id)}" style="padding:4px 10px;font-size:0.75rem;">Edit</button>
             <button type="button" class="btn btn-sm btn-secondary" data-jo-act="close" data-jo-id="${idAttr(o.id)}" style="padding:4px 10px;font-size:0.75rem;">Close</button>`}
      </div>
    </div>`;
  };
  const openHtml = open.length
    ? `<div style="display:flex;flex-direction:column;gap:var(--spacing-md);">${open.map(renderRow).join('')}</div>`
    : `<div style="font-size:0.85rem;color:var(--text-secondary);text-align:center;padding:14px;background:var(--bg-page);border-radius:8px;">No open jobs. Reopen one below or add a new one.</div>`;
  const closedHtml = closed.length
    ? `<div style="font-size:0.78rem;color:var(--text-secondary);margin:var(--spacing-lg) 0 var(--spacing-md);font-weight:600;">Closed</div><div style="display:flex;flex-direction:column;gap:var(--spacing-md);">${closed.map(renderRow).join('')}</div>`
    : '';
  const capNote = open.length >= cap
    ? `<div style="font-size:0.78rem;color:var(--action-danger);margin-top:8px;">You have ${cap} open jobs, the most allowed. Close one before adding another.</div>`
    : '';
  list.innerHTML = openHtml + capNote + closedHtml;
}

// Delegated clicks — ids are carrier-typed text turned into slugs, so they never
// ride inside an inline onclick JS string (escapeHtml is not attribute-JS safe).
function _joBindListClicks() {
  const list = document.getElementById('job-openings-list');
  if (!list || list._joBound) return;
  list._joBound = true;
  list.addEventListener('click', (ev) => {
    const btn = ev.target.closest('[data-jo-act]');
    if (!btn) return;
    const act = btn.getAttribute('data-jo-act');
    const id = btn.getAttribute('data-jo-id') || '';
    if (act === 'edit') { _joEditingId = id; _joRenderList(); document.getElementById(`jo-edit-title-${id}`)?.focus(); }
    else if (act === 'cancel') { _joEditingId = null; _joRenderList(); }
    else if (act === 'save') window.saveEditedJobOpening(id);
    else if (act === 'close') window.archiveJobOpening(id);
    else if (act === 'reopen') window.unarchiveJobOpening(id);
  });
}

// What each application type actually collects — shown at authoring time so a
// carrier sees the consequence of the bucket BEFORE applicants hit it (COD Domain ⚠️:
// a mislabelled bucket silently skips FMCSA disclosures).
const _JO_TYPE_HELP = {
  'cdl': 'Full DOT/FMCSA driver application: license, medical card, driving history, drug & alcohol history, Clearinghouse consent.',
  'non-cdl-driver': 'Driver application without the CDL-only parts: license, driving history and MVR, but no Clearinghouse or drug & alcohol history. Use only for jobs that do not need a CDL.',
  'drives-no-cmv': 'Employment application plus the driver\'s license (any class). No medical card, no driving-history or DOT questions. Use for jobs that drive a pickup or car of 10,000 lb or less (trailer included), never haul placarded hazmat and never drive a van or bus built for 9 or more people, so never a commercial motor vehicle.', // TASK-9696 / TASK-9806
  'non-driver': 'Short employment application: personal info, employment history, background check. No license or driving questions. Use only for jobs where the person will not drive.',
};

window._joSyncTypeHelp = function() {
  const sel = document.getElementById('jo-new-type');
  const help = document.getElementById('jo-new-type-help');
  if (!sel || !help) return;
  help.textContent = _JO_TYPE_HELP[sel.value] || 'Pick the application an applicant for this job fills out. It decides which DOT sections they are asked for.';
  // TASK-9448 — the per-job med card box: driver applications only, pre-set from
  // the carrier's default for the chosen type.
  const wrap = document.getElementById('jo-new-medcard-wrap');
  const box = document.getElementById('jo-new-medcard');
  if (wrap) wrap.style.display = _joIsDriverType(sel.value) ? '' : 'none';
  if (box && _joIsDriverType(sel.value)) box.checked = _joMedDefaults()[sel.value] === true;
};

window.openJobOpeningsModal = function() {
  const m = document.getElementById('job-openings-modal');
  if (!m) return;
  // Opened on top of HR & Recruiting Settings or the Copy Apply Link modal —
  // re-append so it always stacks above whichever opened it.
  document.body.appendChild(m);
  _joEditingId = null;
  _joBindListClicks();
  _joRenderMedDefaults(); // TASK-9448
  _joRenderList();
  const t = document.getElementById('jo-new-title'); if (t) t.value = '';
  const ty = document.getElementById('jo-new-type'); if (ty) ty.value = '';
  const l = document.getElementById('jo-new-location'); if (l) l.value = '';
  window._joSyncTypeHelp();
  m.classList.remove('hidden');
};

window.closeJobOpeningsModal = function() {
  const m = document.getElementById('job-openings-modal');
  if (m) m.classList.add('hidden');
};

window.addJobOpening = async function() {
  const JO = _joApi();
  if (!JO) { showToast('Job openings failed to load. Refresh and try again.', 'error'); return; }
  const titleEl = document.getElementById('jo-new-title');
  const typeEl = document.getElementById('jo-new-type');
  const locEl = document.getElementById('jo-new-location');
  const title = String(titleEl?.value || '').replace(/\s+/g, ' ').trim();
  const positionType = typeEl?.value || '';
  const location = String(locEl?.value || '').replace(/\s+/g, ' ').trim();
  if (!title) { showToast('Job title is required', 'error'); titleEl?.focus(); return; }
  if (title.length > JO.TITLE_MAX) { showToast(`Job title is too long (max ${JO.TITLE_MAX} characters)`, 'error'); return; }
  if (!JO.typeMeta(positionType)) { showToast('Choose which application this job uses', 'error'); typeEl?.focus(); return; }
  if (location.length > JO.LOCATION_MAX) { showToast(`Location is too long (max ${JO.LOCATION_MAX} characters)`, 'error'); return; }
  const enabled = JO.enabledTypes(window.AppState?.tenantData?.enabledApplicantPositionTypes);
  const typeLabel = _joTypeLabel(positionType);
  // TASK-9448 — driver jobs carry their own yes/no; non-driver jobs never ask.
  const requiresMedCard = _joIsDriverType(positionType)
    ? !!document.getElementById('jo-new-medcard')?.checked
    : null;
  if (positionType === 'cdl' && requiresMedCard === false) {
    const medOk = await _joConfirmCdlMedOff(`Applicants for "${title}" will not be asked for a DOT medical card.`);
    if (!medOk) return;
  }
  const ok = await showConfirm(
    `Add "${title}"?`,
    `Applicants for this job will fill out the ${typeLabel} application. ${_JO_TYPE_HELP[positionType]} This can't be changed later.`
      + (enabled.includes(positionType) ? '' : ` Note: ${typeLabel} applications are switched off under "Application types candidates can use", so this job won't show on your form until you turn that type back on.`),
    { confirmText: 'Add opening', cancelText: 'Go back', icon: 'work_outline' },
  );
  if (!ok) return;
  const saved = await _joMutate((list) => {
    const openCount = list.filter(o => o && o.archived !== true).length;
    if (openCount >= JO.CAP_ACTIVE) throw _joUserError(`You already have ${JO.CAP_ACTIVE} open jobs. Close one before adding another.`);
    const dup = list.find(o => o && o.archived !== true && String(o.title || '').trim().toLowerCase() === title.toLowerCase());
    if (dup) throw _joUserError(`"${title}" is already an open job.`);
    const id = JO.makeId(title, list.map(o => o && o.id).filter(Boolean));
    _joLastAddedId = id;
    return [...list, {
      id, title, positionType, archived: false, createdAt: new Date().toISOString(),
      ...(location ? { location } : {}),
      ...(requiresMedCard === null ? {} : { requiresMedCard }),
    }];
  });
  if (saved) {
    if (requiresMedCard !== null) {
      window.logAudit?.('hr', 'JOB_OPENING_MED_CARD_SET', {
        openingId: _joLastAddedId, applicationType: positionType, changed: { requiresMedCard: { from: null, to: requiresMedCard } },
      });
    }
    showToast('Job opening added', 'success');
    if (titleEl) titleEl.value = '';
    if (locEl) locEl.value = '';
    if (typeEl) typeEl.value = '';
    window._joSyncTypeHelp();
    _joRenderList();
  }
};

window.saveEditedJobOpening = async function(id) {
  const JO = _joApi();
  if (!JO) return;
  const title = String(document.getElementById(`jo-edit-title-${id}`)?.value || '').replace(/\s+/g, ' ').trim();
  const location = String(document.getElementById(`jo-edit-location-${id}`)?.value || '').replace(/\s+/g, ' ').trim();
  if (!title) { showToast('Job title is required', 'error'); return; }
  if (title.length > JO.TITLE_MAX || location.length > JO.LOCATION_MAX) { showToast('Too long (max 120 characters)', 'error'); return; }
  // TASK-9448 — the per-job med card answer (driver jobs only). Only written when it
  // actually changed, so an untouched opening keeps following the default.
  const before = _joGetCurrent().find(o => o && o.id === id) || null;
  const medBox = document.getElementById(`jo-edit-medcard-${id}`);
  // (the bucket is read through _joBucketOf: this function must never name the
  // opening's type in an assignment — tests/task4725 pins that it is not editable)
  const medBucket = _joBucketOf(before);
  const medFrom = _joIsDriverType(medBucket) ? _joMedRequired(before) : null;
  const medTo = (medBox && medFrom !== null) ? !!medBox.checked : null;
  const medChanged = medTo !== null && medTo !== medFrom;
  if (medChanged && medBucket === 'cdl' && medTo === false) {
    const medOk = await _joConfirmCdlMedOff(`Applicants for "${title}" will stop being asked for a DOT medical card.`);
    if (!medOk) return;
  }
  const saved = await _joMutate((list) => {
    if (!list.some(o => o && o.id === id)) throw _joUserError('That opening no longer exists. Close this window and reopen it.');
    if (list.some(o => o && o.id !== id && o.archived !== true && String(o.title || '').trim().toLowerCase() === title.toLowerCase())) {
      throw _joUserError(`"${title}" is already an open job.`);
    }
    return list.map(o => {
      if (!o || o.id !== id) return o;
      // positionType deliberately NOT editable (see the header of this section).
      const next = { ...o, title, updatedAt: new Date().toISOString() };
      if (location) next.location = location; else delete next.location;
      if (medChanged) next.requiresMedCard = medTo; // TASK-9448
      return next;
    });
  });
  if (saved && medChanged) {
    window.logAudit?.('hr', 'JOB_OPENING_MED_CARD_SET', {
      openingId: id, applicationType: medBucket, changed: { requiresMedCard: { from: medFrom, to: medTo } },
    });
  }
  if (saved) { showToast('Job opening updated', 'success'); _joEditingId = null; _joRenderList(); }
};

window.archiveJobOpening = async function(id) {
  const saved = await _joMutate((list) => list.map(o => (o && o.id === id) ? { ...o, archived: true, archivedAt: new Date().toISOString() } : o));
  if (saved) { showToast('Job opening closed', 'success'); _joRenderList(); }
};

window.unarchiveJobOpening = async function(id) {
  const JO = _joApi();
  const cap = JO?.CAP_ACTIVE || 25;
  const saved = await _joMutate((list) => {
    if (list.filter(o => o && o.archived !== true).length >= cap) throw _joUserError(`You already have ${cap} open jobs. Close one first.`);
    // COD r3 Edge Case Hunter — never two OPEN jobs with the same title (they could
    // carry different application types and the applicant couldn't tell them apart).
    const me = list.find(o => o && o.id === id);
    const t = String(me?.title || '').trim().toLowerCase();
    if (t && list.some(o => o && o.id !== id && o.archived !== true && String(o.title || '').trim().toLowerCase() === t)) {
      throw _joUserError(`"${me.title}" is already an open job. Close or rename that one first.`);
    }
    return list.map(o => (o && o.id === id) ? { ...o, archived: false, archivedAt: null } : o);
  });
  if (saved) { showToast('Job opening reopened', 'success'); _joRenderList(); }
};

// HR & Recruiting Settings card: up to 3 open-job chips + "+N more".
function _hrRenderJobOpeningChips() {
  const wrap = document.getElementById('hr-job-openings-chips');
  const countEl = document.getElementById('hr-job-openings-count');
  const JO = _joApi();
  const open = JO ? JO.active(_joGetCurrent()) : [];
  if (countEl) countEl.textContent = `(${open.length} open)`;
  if (!wrap) return;
  if (open.length === 0) {
    wrap.innerHTML = '<div class="hr-question-chips-empty">No job openings — applicants aren\'t asked which job they want.</div>';
    return;
  }
  const chips = open.slice(0, 3).map(o => `<span class="hr-question-chip">${_appqEsc(o.title)}</span>`);
  if (open.length > 3) chips.push(`<span class="hr-question-chip hr-question-chip-more">+${open.length - 3} more</span>`);
  wrap.innerHTML = chips.join('');
}

// ============================================
// TASK-221 Slice 1 — Customizable Hiring Packet
// Per-tenant config at tenants/{id}/settings.hiringPacket. Falls back to
// hardcoded ONBOARDING_DOC_SLOTS in hr.js when settings field is absent.
// COD-reviewed 2026-05-07; refinements: itemId key (matches hr.js pattern),
// auto-derived slug (hidden from user), nested-map storage for custom items
// (customPacketDocs.<itemId>), starter library inline, 44px touch targets.
// ============================================
const HP_CAP_ACTIVE = 20;
const HP_LABEL_MAX = 64;
const HP_ID_MAX = 32;
const HP_ID_RE = /^[a-z0-9_]{1,32}$/;

// Built-in items mirror hr.js:541 ONBOARDING_DOC_SLOTS — same itemId,
// fieldUrl, fieldStoragePath, fieldUploadedAt names so existing tenants
// see zero data migration. Tenants can ARCHIVE built-ins (e.g., they
// don't use Direct Deposit) but cannot DELETE — preserves any uploaded
// docs that reference these field names.
const HP_BUILTIN_ITEMS = [
  { itemId: 'online_application', label: 'Online Application',  builtin: true, fieldUrl: 'onlineApplicationDocumentUrl', fieldStoragePath: 'onlineApplicationStoragePath', fieldUploadedAt: 'onlineApplicationUploadedAt' },
  { itemId: 'i9',                 label: 'I-9 Form',             builtin: true, fieldUrl: 'i9DocumentUrl',                fieldStoragePath: 'i9StoragePath',                fieldUploadedAt: 'i9UploadedAt' },
  { itemId: 'w4',                 label: 'W-4 Form',             builtin: true, fieldUrl: 'w4DocumentUrl',                fieldStoragePath: 'w4StoragePath',                fieldUploadedAt: 'w4UploadedAt' },
  { itemId: 'direct_deposit',     label: 'Direct Deposit Form',  builtin: true, fieldUrl: 'directDepositDocumentUrl',     fieldStoragePath: 'directDepositStoragePath',     fieldUploadedAt: 'directDepositUploadedAt' },
  { itemId: 'safety_training',    label: 'Safety Training Cert', builtin: true, fieldUrl: 'safetyTrainingDocumentUrl',    fieldStoragePath: 'safetyTrainingStoragePath',    fieldUploadedAt: 'safetyTrainingUploadedAt' },
];

// Starter library — items the carrier can ENABLE via "Add from library"
// dropdown (not enabled by default). Common requests we don't ship as
// built-ins because not every carrier needs them.
const HP_STARTER_LIBRARY = [
  { suggestedId: 'blue_cross',         label: 'Blue Cross / Health Benefits Enrollment' },
  { suggestedId: 'employee_handbook',  label: 'Employee Handbook Acknowledgement' },
  { suggestedId: 'drug_test_consent',  label: 'Drug Test Consent Form' },
  { suggestedId: 'workers_comp',       label: "Workers' Comp Acknowledgement" },
  { suggestedId: 'confidentiality',    label: 'Confidentiality Agreement' },
];

function _hpEsc(s) {
  return String(s ?? '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
}

// Auto-derive slug from label. Lowercase, non-alnum → underscore, trim,
// dedupe leading/trailing underscores, cap at 32 chars.
function _hpSlugify(label) {
  return String(label || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 32);
}

// Returns the current hiring packet item array (active + archived). Seeds
// from HP_BUILTIN_ITEMS when settings field is absent — first-read migration.
function _hpGetCurrent() {
  const stored = window.AppState?.settings?.hiringPacket;
  if (Array.isArray(stored) && stored.length) return stored.slice();
  return HP_BUILTIN_ITEMS.map(b => ({ ...b }));
}

// Exposed on window so hr.js's uploadHiringPacketTemplate /
// removeHiringPacketTemplate (Slice 2) can ping a re-render after
// updating the settings array.
window._hpRenderList = _hpRenderList;
// TASK-637 — expose the debounced/callable-routed persist so hr.js template
// upload/remove handlers reuse the SAME write path (no direct tenant-doc write).
window._hpPersist = (list) => _hpPersist(list);

// TASK-637 — write whitelisted HR settings flags (safety sign-in, PPE driver,
// driver Doc Page, OSHA tracking, hrLinks) through the requireHrAccess-gated
// updateHiringPacketTemplate callable, so an hr-module STAFF member can flip
// them without a raw tenant-doc permission error. `partial` = { key: value }.
// Mirrors AppState.settings on success. Returns true/false.
window._persistHrSettings = async function(partial) {
  const tenant = window.AppState?.tenant;
  if (!tenant || !partial || typeof partial !== 'object') return false;
  try {
    const call = httpsCallable(functions, 'updateHiringPacketTemplate');
    await call({ tenantId: tenant, hrSettings: partial });
    if (!window.AppState.settings) window.AppState.settings = {};
    Object.assign(window.AppState.settings, partial);
    return true;
  } catch (e) {
    console.error('[hr-settings] persist failed', e);
    return false;
  }
};
function _hpRenderList() {
  const list = document.getElementById('hiring-packet-list');
  if (!list) return;
  const all = _hpGetCurrent();
  const active = all.filter(i => i && i.archived !== true);
  const archived = all.filter(i => i && i.archived === true);
  if (active.length === 0 && archived.length === 0) {
    list.innerHTML = `<div style="font-size:0.85rem;color:var(--text-muted);text-align:center;padding:20px;background:var(--bg-page);border-radius:8px;">No items yet. Add your first below.</div>`;
    return;
  }
  const renderRow = (item, idx, total) => {
    const isFirst = idx === 0;
    const isLast = idx === total - 1;
    // Slice 2 — template state. Carrier-uploaded blank PDF lives at
    // item.templateUrl. Render either an "Add template" or
    // "📄 filename · Replace · Remove" line below the label.
    const hasTpl = !!item.templateUrl;
    const tplFile = item.templateFileName || 'template.pdf';
    const templateRow = item.archived ? '' : `
      <div style="font-size:0.72rem;color:var(--text-muted);margin-top:6px;display:flex;align-items:center;gap:6px;flex-wrap:wrap;">
        ${hasTpl ? `
          <span class="material-icons" style="font-size:14px;color:var(--brand-blue);">picture_as_pdf</span>
          <span style="color:var(--text-secondary);">${_hpEsc(tplFile)}</span>
          <button type="button" class="btn-text" onclick="window.HRModule&&window.HRModule.uploadHiringPacketTemplate('${_hpEsc(item.itemId)}')" style="background:none;border:none;color:var(--brand-blue);cursor:pointer;font-size:0.72rem;padding:2px 4px;">Replace</button>
          <span style="color:var(--text-muted);">·</span>
          <button type="button" class="btn-text" onclick="window.HRModule&&window.HRModule.removeHiringPacketTemplate('${_hpEsc(item.itemId)}')" style="background:none;border:none;color:#DC2626;cursor:pointer;font-size:0.72rem;padding:2px 4px;">Remove</button>
        ` : `
          <button type="button" class="btn-text" onclick="window.HRModule&&window.HRModule.uploadHiringPacketTemplate('${_hpEsc(item.itemId)}')" style="background:none;border:1px dashed var(--border-light);color:var(--text-secondary);cursor:pointer;font-size:0.72rem;padding:3px 8px;border-radius:4px;">+ Add blank template (PDF)</button>
        `}
      </div>`;
    return `
    <div style="display:flex;flex-direction:column;gap:0;padding:10px 12px;border:1px solid var(--border-light);border-radius:8px;background:${item.archived ? '#FAFAFA' : '#fff'};">
      <div style="display:flex;justify-content:space-between;align-items:center;gap:8px;">
        <div style="flex:1;min-width:0;">
          <div style="font-size:0.9rem;color:var(--text-primary);font-weight:600;${item.archived ? 'text-decoration:line-through;color:var(--text-muted);' : ''}">${_hpEsc(item.label)}</div>
          <div style="font-size:0.72rem;color:var(--text-secondary);margin-top:2px;">
            ${item.builtin ? 'Built-in' : 'Custom'}${item.archived ? ' · <em>archived</em>' : ''}
          </div>
        </div>
        ${item.archived ? `
          <button type="button" class="btn btn-sm btn-secondary" onclick="window.restoreHiringPacketItem('${_hpEsc(item.itemId)}')" style="padding:8px 12px;font-size:0.75rem;min-height:44px;min-width:44px;">Restore</button>
        ` : `
          <button type="button" class="btn-icon" onclick="window.moveHiringPacketItem('${_hpEsc(item.itemId)}', -1)" ${isFirst ? 'disabled' : ''} title="Move up" style="padding:8px;min-height:44px;min-width:44px;${isFirst ? 'opacity:0.3;' : ''}">
            <span class="material-icons" style="font-size:18px;">arrow_upward</span>
          </button>
          <button type="button" class="btn-icon" onclick="window.moveHiringPacketItem('${_hpEsc(item.itemId)}', 1)" ${isLast ? 'disabled' : ''} title="Move down" style="padding:8px;min-height:44px;min-width:44px;${isLast ? 'opacity:0.3;' : ''}">
            <span class="material-icons" style="font-size:18px;">arrow_downward</span>
          </button>
          <button type="button" class="btn-icon" onclick="window.editHiringPacketItem('${_hpEsc(item.itemId)}')" title="Rename" style="padding:8px;min-height:44px;min-width:44px;">
            <span class="material-icons" style="font-size:18px;">edit</span>
          </button>
          <button type="button" class="btn-icon" onclick="window.archiveHiringPacketItem('${_hpEsc(item.itemId)}')" title="Archive" style="padding:8px;min-height:44px;min-width:44px;">
            <span class="material-icons" style="font-size:18px;color:var(--text-muted);">archive</span>
          </button>
        `}
      </div>
      ${templateRow}
    </div>`;
  };
  const activeRows = active.map((it, i) => renderRow(it, i, active.length)).join('');
  const archivedRows = archived.length > 0
    ? `<div style="font-size:0.78rem;color:var(--text-muted);margin-top:8px;font-weight:600;">Archived</div>${archived.map((it, i) => renderRow(it, i, archived.length)).join('')}`
    : '';
  list.innerHTML = activeRows + archivedRows;
  // Update count badge in HR sub-modal
  const countEl = document.getElementById('hr-hiring-packet-count');
  if (countEl) countEl.textContent = `(${active.length} active)`;
}

// TASK-637 — the hiring-packet TEMPLATE save writes the TENANT DOC
// (settings.hiringPacket). The tenants/{tid} update rule is intentionally
// isTenantAdmin-only (TASK-530 billing-bypass guard: opening it to hasModule('hr')
// would let an hr-staff member self-write vipAccess / subscriptionStatus / etc.).
// So an hr-module STAFF member can't write the tenant doc directly — the old
// setDoc here threw a raw "insufficient permissions" for them.
//
// Route the save through the `updateHiringPacketTemplate` callable, which is
// gated server-side on assertHrModuleOrAdmin and writes ONLY settings.hiringPacket
// via Admin SDK (so it can't touch billing flags). Idempotent + double-tap-safe:
// the callable serializes on a per-tenant transaction. Owner/admin go through the
// same path (one code path, no role branch).
let _hpPersistInFlight = null;
async function _hpPersist(updatedList) {
  if (!window.AppState?.tenant) {
    showToast('Not logged in', 'error');
    return false;
  }
  // Debounce/idempotency guard against a double-tap race — coalesce concurrent
  // saves onto the latest list (the callable is the authoritative serializer,
  // but this avoids two competing round-trips clobbering each other client-side).
  if (_hpPersistInFlight) {
    try { await _hpPersistInFlight; } catch (_) { /* fall through to fresh save */ }
  }
  const run = (async () => {
    try {
      const call = httpsCallable(functions, 'updateHiringPacketTemplate');
      await call({ tenantId: window.AppState.tenant, items: updatedList });
      if (!window.AppState.settings) window.AppState.settings = {};
      window.AppState.settings.hiringPacket = updatedList;
      return true;
    } catch (e) {
      console.error('[hp] persist failed', e);
      const msg = e?.message || (e?.code === 'permission-denied'
        ? 'You do not have HR access to edit the hiring packet'
        : 'unknown');
      showToast('Save failed: ' + msg, 'error');
      return false;
    }
  })();
  _hpPersistInFlight = run;
  try { return await run; } finally { if (_hpPersistInFlight === run) _hpPersistInFlight = null; }
}

window.openHiringPacketModal = function() {
  const m = document.getElementById('hiring-packet-modal');
  if (!m) return;
  _hpRenderList();
  // Reset add form
  const labelEl = document.getElementById('hp-new-label');
  if (labelEl) labelEl.value = '';
  // Populate starter-library dropdown
  const lib = document.getElementById('hp-library-select');
  if (lib) {
    const current = _hpGetCurrent();
    const usedIds = new Set(current.map(i => i.itemId));
    const opts = HP_STARTER_LIBRARY
      .filter(s => !usedIds.has(s.suggestedId))
      .map(s => `<option value="${_hpEsc(s.suggestedId)}|${_hpEsc(s.label)}">${_hpEsc(s.label)}</option>`)
      .join('');
    lib.innerHTML = `<option value="">— Add from library (optional) —</option>${opts}`;
  }
  m.classList.remove('hidden');
};

window.closeHiringPacketModal = function() {
  const m = document.getElementById('hiring-packet-modal');
  if (m) m.classList.add('hidden');
};

// Add from library — when the dropdown changes, immediately add that item
// (no double-confirm; user can archive after if wrong).
window.addHiringPacketFromLibrary = async function() {
  const sel = document.getElementById('hp-library-select');
  if (!sel || !sel.value) return;
  const [suggestedId, suggestedLabel] = sel.value.split('|');
  await _hpAddItem(suggestedLabel, suggestedId);
  sel.value = '';
};

window.addHiringPacketItem = async function() {
  const labelEl = document.getElementById('hp-new-label');
  const label = (labelEl?.value || '').trim();
  if (!label) { showToast('Label is required', 'error'); return; }
  if (label.length > HP_LABEL_MAX) { showToast(`Label too long (max ${HP_LABEL_MAX} chars)`, 'error'); return; }
  await _hpAddItem(label, _hpSlugify(label));
};

async function _hpAddItem(label, suggestedId) {
  const current = _hpGetCurrent();
  const activeCount = current.filter(i => i.archived !== true).length;
  if (activeCount >= HP_CAP_ACTIVE) {
    showToast(`Cap reached (${HP_CAP_ACTIVE} active items). Archive one before adding more.`, 'error');
    return;
  }
  // Generate unique itemId (slug + numeric suffix if collision)
  let itemId = (suggestedId || '').toLowerCase();
  if (!itemId || !HP_ID_RE.test(itemId)) itemId = 'item';
  if (itemId.length > HP_ID_MAX) itemId = itemId.slice(0, HP_ID_MAX);
  const usedIds = new Set(current.map(i => i.itemId));
  let finalId = itemId;
  let suffix = 2;
  while (usedIds.has(finalId)) {
    finalId = (itemId + '_' + suffix).slice(0, HP_ID_MAX);
    suffix++;
    if (suffix > 999) { showToast('Could not generate unique ID', 'error'); return; }
  }
  if (!HP_ID_RE.test(finalId)) { showToast('Invalid item ID generated. Try a different label.', 'error'); return; }

  const newItem = {
    itemId: finalId,
    label: String(label).slice(0, HP_LABEL_MAX),
    builtin: false,
    archived: false,
    createdAt: new Date().toISOString(),
  };
  const updated = [...current, newItem];
  const ok = await _hpPersist(updated);
  if (ok) {
    showToast('Item added', 'success');
    const labelEl = document.getElementById('hp-new-label');
    if (labelEl) labelEl.value = '';
    _hpRenderList();
    // Refresh library options (used IDs changed)
    window.openHiringPacketModal && (() => {
      const lib = document.getElementById('hp-library-select');
      if (!lib) return;
      const usedIds = new Set(updated.map(i => i.itemId));
      const opts = HP_STARTER_LIBRARY
        .filter(s => !usedIds.has(s.suggestedId))
        .map(s => `<option value="${_hpEsc(s.suggestedId)}|${_hpEsc(s.label)}">${_hpEsc(s.label)}</option>`)
        .join('');
      lib.innerHTML = `<option value="">— Add from library (optional) —</option>${opts}`;
    })();
  }
}

window.archiveHiringPacketItem = async function(itemId) {
  const updated = _hpGetCurrent().map(i => i.itemId === itemId
    ? { ...i, archived: true, archivedAt: new Date().toISOString() }
    : i);
  const ok = await _hpPersist(updated);
  if (ok) { showToast('Item archived', 'success'); _hpRenderList(); }
};

window.restoreHiringPacketItem = async function(itemId) {
  const current = _hpGetCurrent();
  const activeCount = current.filter(i => i.archived !== true).length;
  if (activeCount >= HP_CAP_ACTIVE) {
    showToast(`Can't restore — already at cap (${HP_CAP_ACTIVE} active). Archive one first.`, 'error');
    return;
  }
  const updated = current.map(i => i.itemId === itemId ? { ...i, archived: false, archivedAt: null } : i);
  const ok = await _hpPersist(updated);
  if (ok) { showToast('Item restored', 'success'); _hpRenderList(); }
};

window.editHiringPacketItem = async function(itemId) {
  const current = _hpGetCurrent();
  const item = current.find(i => i.itemId === itemId);
  if (!item) return;
  const next = await window.showInputModal('Rename item', '', item.label);
  if (next === null) return; // cancelled
  const trimmed = String(next || '').trim();
  if (!trimmed) { showToast('Label cannot be empty', 'error'); return; }
  if (trimmed.length > HP_LABEL_MAX) { showToast(`Label too long (max ${HP_LABEL_MAX} chars)`, 'error'); return; }
  const updated = current.map(i => i.itemId === itemId ? { ...i, label: trimmed } : i);
  const ok = await _hpPersist(updated);
  if (ok) { showToast('Item renamed', 'success'); _hpRenderList(); }
};

window.moveHiringPacketItem = async function(itemId, delta) {
  // Reorder within ACTIVE items only — archived items stay below.
  const current = _hpGetCurrent();
  const active = current.filter(i => i.archived !== true);
  const archived = current.filter(i => i.archived === true);
  const idx = active.findIndex(i => i.itemId === itemId);
  if (idx < 0) return;
  const newIdx = idx + delta;
  if (newIdx < 0 || newIdx >= active.length) return;
  const swapped = active.slice();
  [swapped[idx], swapped[newIdx]] = [swapped[newIdx], swapped[idx]];
  const updated = [...swapped, ...archived];
  const ok = await _hpPersist(updated);
  if (ok) { _hpRenderList(); }
};

// ============================================
// TASK-513 — SAFETY ROSTER: REQUIRED-TRAINING LIST CONFIG (Slice 1)
// ============================================
// Per-tenant required-training library, stored at settings.safetyTrainings.
// Clones the hiring-packet config pattern above (add/archive/restore/reorder/
// rename) but each item ALSO carries `recurrenceMonths` (0 = no expiry). This
// drives the "required-vs-has" Safety Roster matrix: each person's
// isnetTrainings[] entry links back to a config item via requiredTrainingId.
//
// DISTINCT from the hiring-packet "Safety Training Cert" UPLOAD slot
// (HP_BUILTIN_ITEMS.safety_training) — that is a single new-hire document
// upload; THIS is the recurring required-training matrix. Different concern,
// different settings key (settings.safetyTrainings vs settings.hiringPacket).
//
// 🔴 DOMAIN HARD RULE (49 CFR §382.603): Reasonable Suspicion training is
// ONE-TIME, NO EXPIRY. Its recurrenceMonths is hard-defaulted to 0 and the
// editor WARNS + refuses to set a recurrence on it — a bogus recurrence would
// flag every supervisor "expired" = a self-inflicted DOT audit finding.
const ST_CAP_ACTIVE = 30;
const ST_LABEL_MAX = 64;
const ST_ID_MAX = 32;
const ST_ID_RE = /^[a-z0-9_]{1,32}$/;
const ST_RECUR_MAX = 120; // 10 years — sane upper bound on a recurrence interval

// itemIds whose recurrence is regulatorily fixed at 0 (no expiry) and must
// NOT be made recurring by an admin. Reasonable Suspicion = 49 CFR §382.603.
const ST_NO_RECURRENCE_LOCKED = new Set(['reasonable_suspicion']);

// Starter library — seeded as the default required-trainings list when a tenant
// has none configured yet. recurrenceMonths reflects common industry cadence;
// Reasonable Suspicion is LOCKED at 0 (one-time, §382.603).
// TASK-1085 — dotRelevant: include this training's certificates in the §391.51
// DQ file packet. Chris-confirmed mapping (Crown/Kolson 2026-07-09): ONLY
// Reasonable Suspicion (§382.603) is DOT-relevant; H2S / confined space / fit
// test / OSHA / industry certs are oil&gas-site items that do NOT belong in the
// standard DQ file. Editable per item in the Required-Trainings editor below.
const ST_BUILTIN_ITEMS = [
  { itemId: 'osha_10',              label: 'OSHA 10',                 builtin: true, recurrenceMonths: 0,  dotRelevant: false },
  { itemId: 'osha_30',              label: 'OSHA 30',                 builtin: true, recurrenceMonths: 0,  dotRelevant: false },
  { itemId: 'h2s',                  label: 'H2S',                     builtin: true, recurrenceMonths: 12, dotRelevant: false },
  { itemId: 'confined_space',       label: 'Confined Space',          builtin: true, recurrenceMonths: 12, dotRelevant: false },
  { itemId: 'fit_test',             label: 'Fit Test',                builtin: true, recurrenceMonths: 12, dotRelevant: false },
  { itemId: 'first_aid_cpr',        label: 'First Aid / CPR',         builtin: true, recurrenceMonths: 24, dotRelevant: false },
  { itemId: 'defensive_driving',    label: 'Defensive Driving',       builtin: true, recurrenceMonths: 36, dotRelevant: false },
  { itemId: 'reasonable_suspicion', label: 'Reasonable Suspicion',    builtin: true, recurrenceMonths: 0,  dotRelevant: true },
];

// Effective DQ-file flag for a config item: explicit boolean wins; items persisted
// BEFORE the flag existed fall back to the builtin default (keeps a pre-existing
// quick-added Reasonable Suspicion IN the packet); everything else false. Mirrors
// the server resolver (_isTrainingDotRelevant, functions/src/index.js).
const ST_DOT_RELEVANT_DEFAULTS = { reasonable_suspicion: true };
function _stIsDotRelevant(item) {
  if (!item) return false;
  if (typeof item.dotRelevant === 'boolean') return item.dotRelevant;
  return ST_DOT_RELEVANT_DEFAULTS[item.itemId] === true;
}

function _stEsc(s) {
  return String(s ?? '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
}

function _stSlugify(label) {
  return String(label || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 32);
}

// Returns the current required-trainings array (active + archived). EMPTY = EMPTY
// (Chris 2026-06-25): an unconfigured carrier has NO required trainings — we do
// NOT auto-seed ST_BUILTIN_ITEMS here. Auto-seeding made this resolver disagree
// with every raw-reading surface (the partner-portal matrix, the DOT-portal
// editor, and the server projection all read the raw settings field), so the
// SAME carrier showed 8 phantom "required" trainings here (→ false "Missing"
// everywhere) while those surfaces showed none. The 8 builtins are now a one-tap
// QUICK-ADD starter menu (see _stRenderQuickAdd / window.quickAddSafetyTraining),
// not an auto-applied required set. Empty here ⇒ empty in the editor, the People
// summary, and the matrix — all surfaces finally agree.
function _stGetCurrent() {
  const stored = window.AppState?.settings?.safetyTrainings;
  return Array.isArray(stored) ? stored.slice() : [];
}
// Exposed so hr.js (Safety Roster matrix) can read the canonical list.
window._stGetCurrent = _stGetCurrent;
// TASK-9911 M2 — the Skills & certifications screen (roster.js) reads the
// partner lock through the same mirror the editor uses.
window._stCanEdit = () => _stCanEdit();
window.ST_NO_RECURRENCE_LOCKED = ST_NO_RECURRENCE_LOCKED;

// TASK-667 — UI mirror of the server resolver _resolveCanCarrierEditTrainings.
// A partner-managed carrier (tenantData.complianceManagerUid present) may edit
// the required-training list ONLY when the partner opted in via
// compliance.allowClientEditTrainings === true. Unmanaged carriers always own
// their list. When false the modal opens READ-ONLY (no add row / per-row edit
// buttons). The server callable re-checks regardless — this is UI only.
function _stCanEdit() {
  const td = window.AppState?.tenantData || {};
  const hasManager = !!(td.complianceManagerUid && String(td.complianceManagerUid).trim());
  if (!hasManager) return true;
  return td.compliance?.allowClientEditTrainings === true;
}

window._stRenderList = _stRenderList;
function _stRenderList() {
  const list = document.getElementById('safety-trainings-list');
  if (!list) return;
  const all = _stGetCurrent();
  const active = all.filter(i => i && i.archived !== true);
  const archived = all.filter(i => i && i.archived === true);
  if (active.length === 0 && archived.length === 0) {
    list.innerHTML = `<div style="font-size:0.85rem;color:var(--text-secondary);text-align:center;padding:20px;background:var(--bg-page);border-radius:8px;">No required trainings yet. Add the first below.</div>`;
    return;
  }
  const recurLabel = (m) => {
    const n = Number(m) || 0;
    if (n <= 0) return 'No expiry';
    if (n % 12 === 0) return `Every ${n / 12} yr`;
    return `Every ${n} mo`;
  };
  // TASK-667 — partner-managed read-only gate. TASK-9911 M2 — the HOST of the
  // editor (the Skills & certifications screen) may also mark it read-only for
  // a login without HR/Compliance access: data-readonly="1" on the list.
  const canEdit = _stCanEdit() && list.dataset.readonly !== '1';
  const renderRow = (item, idx, total) => {
    const isFirst = idx === 0;
    const isLast = idx === total - 1;
    const locked = ST_NO_RECURRENCE_LOCKED.has(item.itemId);
    return `
    <div style="display:flex;flex-direction:column;gap:0;padding:10px 12px;border:1px solid var(--border-light);border-radius:8px;background:${item.archived ? '#FAFAFA' : '#fff'};">
      <div style="display:flex;justify-content:space-between;align-items:center;gap:8px;">
        <div style="flex:1;min-width:0;">
          <div style="font-size:0.9rem;color:var(--text-primary);font-weight:600;${item.archived ? 'text-decoration:line-through;color:var(--text-muted);' : ''}">${_stEsc(item.label)}</div>
          <div style="font-size:0.72rem;color:var(--text-secondary);margin-top:2px;display:flex;align-items:center;gap:6px;flex-wrap:wrap;">
            <span>${item.builtin ? 'Built-in' : 'Custom'}${item.archived ? ' · <em>archived</em>' : ''}</span>
            ${item.archived ? '' : `<span style="color:var(--text-muted);">·</span>
              <span style="display:inline-flex;align-items:center;gap:3px;">
                <span class="material-icons" style="font-size:13px;color:var(--brand-blue);">event_repeat</span>${recurLabel(item.recurrenceMonths)}${locked ? ' <span style="color:#92400E;" title="49 CFR §382.603 — one-time, no expiry">(§382.603 fixed)</span>' : ''}
              </span>
              <span style="color:var(--text-muted);">·</span>
              <label title="DOT-relevant — include this training's certificates in the driver's §391.51 DQ file packet. Leave off for non-DOT site certs (H2S, confined space, fit test)." style="display:inline-flex;align-items:center;gap:4px;cursor:${canEdit ? 'pointer' : 'default'};margin:0;font-weight:400;">
                <input type="checkbox" ${_stIsDotRelevant(item) ? 'checked' : ''} ${canEdit ? `onchange="window.toggleSafetyTrainingDotRelevant('${_stEsc(item.itemId)}')"` : 'disabled'} style="margin:0;width:14px;height:14px;accent-color:var(--brand-blue);">
                Include in DQ file
              </label>
              <span style="color:var(--text-muted);">·</span>
              <span title="TASK-1353 — which position tiers this training is required for. Unchecked tiers show N/A instead of critical. All checked = everyone (the default)." style="display:inline-flex;align-items:center;gap:6px;flex-wrap:wrap;">
                <span>Applies to:</span>
                ${[['cdl', 'CDL'], ['non_cdl_driver', 'Non-CDL'], ['drives_no_cmv', 'No CMV'], ['non_driver', 'Non-Driver']].map(([tier, tLabel]) => {
                  const scope = (Array.isArray(item.appliesTo) ? item.appliesTo : []).filter(t => ['cdl', 'non_cdl_driver', 'drives_no_cmv', 'non_driver'].includes(t)); // TASK-9696
                  const checked = scope.length === 0 || scope.includes(tier);
                  return `<label style="display:inline-flex;align-items:center;gap:3px;cursor:${canEdit ? 'pointer' : 'default'};margin:0;font-weight:400;">
                    <input type="checkbox" ${checked ? 'checked' : ''} ${canEdit ? `onchange="window.toggleSafetyTrainingTier('${_stEsc(item.itemId)}', '${tier}')"` : 'disabled'} style="margin:0;width:14px;height:14px;accent-color:var(--brand-blue);">${tLabel}
                  </label>`;
                }).join('')}
              </span>`}
          </div>
        </div>
        ${!canEdit ? '' : (item.archived ? `
          <button type="button" class="btn btn-sm btn-secondary" onclick="window.restoreSafetyTraining('${_stEsc(item.itemId)}')" style="padding:8px 12px;font-size:0.75rem;min-height:44px;min-width:44px;">Restore</button>
        ` : `
          <button type="button" class="btn-icon" onclick="window.moveSafetyTraining('${_stEsc(item.itemId)}', -1)" ${isFirst ? 'disabled' : ''} title="Move up" style="padding:8px;min-height:44px;min-width:44px;${isFirst ? 'opacity:0.3;' : ''}">
            <span class="material-icons" style="font-size:18px;">arrow_upward</span>
          </button>
          <button type="button" class="btn-icon" onclick="window.moveSafetyTraining('${_stEsc(item.itemId)}', 1)" ${isLast ? 'disabled' : ''} title="Move down" style="padding:8px;min-height:44px;min-width:44px;${isLast ? 'opacity:0.3;' : ''}">
            <span class="material-icons" style="font-size:18px;">arrow_downward</span>
          </button>
          <button type="button" class="btn-icon" onclick="window.editSafetyTraining('${_stEsc(item.itemId)}')" title="Edit" style="padding:8px;min-height:44px;min-width:44px;">
            <span class="material-icons" style="font-size:18px;">edit</span>
          </button>
          <button type="button" class="btn-icon" onclick="window.archiveSafetyTraining('${_stEsc(item.itemId)}')" title="Archive" style="padding:8px;min-height:44px;min-width:44px;">
            <span class="material-icons" style="font-size:18px;color:var(--text-muted);">archive</span>
          </button>
        `)}
      </div>
    </div>`;
  };
  const activeRows = active.map((it, i) => renderRow(it, i, active.length)).join('');
  const archivedRows = archived.length > 0
    ? `<div style="font-size:0.78rem;color:var(--text-muted);margin-top:8px;font-weight:600;">Archived</div>${archived.map((it, i) => renderRow(it, i, archived.length)).join('')}`
    : '';
  list.innerHTML = activeRows + archivedRows;
  const countEl = document.getElementById('safety-trainings-count');
  if (countEl) countEl.textContent = `(${active.length} active)`;
  _stRenderQuickAdd(all);
}

// Quick-add starter menu — renders the ST_BUILTIN_ITEMS not already on the list
// as one-tap chips, so the 8 industry-standard trainings (OSHA 10/30, H2S, …)
// stay a single click away now that they're no longer auto-seeded (empty=empty,
// Chris 2026-06-25). Populates #safety-trainings-quickadd when present + editable.
function _stRenderQuickAdd(all) {
  const host = document.getElementById('safety-trainings-quickadd');
  if (!host) return;
  if (!_stCanEdit() || document.getElementById('safety-trainings-list')?.dataset.readonly === '1') { host.innerHTML = ''; return; }
  const present = new Set((all || []).map(i => i && i.itemId));
  const remaining = ST_BUILTIN_ITEMS.filter(b => !present.has(b.itemId));
  if (remaining.length === 0) { host.innerHTML = ''; return; }
  const chips = remaining.map(b => {
    // Surface the §382.603 citation on Reasonable Suspicion so removing the old
    // auto-seed doesn't bury the fact that supervisor RS training is FMCSA-required
    // (COD Domain catch 2026-06-25).
    const reg = b.itemId === 'reasonable_suspicion'
      ? ` <span style="font-size:0.66rem;color:#92400E;">FMCSA §382.603</span>` : '';
    return `
    <button type="button" class="btn btn-sm btn-secondary" onclick="window.quickAddSafetyTraining('${_stEsc(b.itemId)}')" style="font-size:0.78rem;padding:6px 12px;min-height:36px;">
      <span class="material-icons" style="font-size:15px;vertical-align:-3px;">add</span> ${_stEsc(b.label)}${reg}
    </button>`;
  }).join('');
  host.innerHTML = `
    <div style="font-size:0.78rem;color:var(--text-secondary);font-weight:600;margin-bottom:6px;">Quick add common trainings</div>
    <div style="display:flex;flex-wrap:wrap;gap:8px;">${chips}</div>`;
}

// Adds a built-in starter training by itemId, preserving its canonical id /
// label / recurrence / builtin flag (so the §382.603 lock + "Built-in" badge
// keep working). Distinct from _stAddItem (custom, builtin:false).
let _stQuickAddBusy = false; // guard a rapid double-tap from appending a dup before the first write lands
window.quickAddSafetyTraining = async function(itemId) {
  if (_stQuickAddBusy) return;
  const builtin = ST_BUILTIN_ITEMS.find(b => b.itemId === itemId);
  if (!builtin) return;
  const current = _stGetCurrent();
  if (current.some(i => i.itemId === itemId)) { showToast('Already on the list', 'info'); return; }
  if (current.filter(i => i.archived !== true).length >= ST_CAP_ACTIVE) {
    showToast(`Cap reached (${ST_CAP_ACTIVE} active). Archive one before adding more.`, 'error');
    return;
  }
  _stQuickAddBusy = true;
  try {
    const updated = [...current, { ...builtin, archived: false }];
    await _stOptimisticPersist(updated, () => showToast(`${builtin.label} added`, 'success'));
  } finally {
    _stQuickAddBusy = false;
  }
};

// TASK-667 — Route the save through the `updateSafetyTrainings` callable instead
// of a direct client setDoc. The old setDoc on tenants/{tid} required
// isTenantAdmin (TASK-530 billing-bypass rule), so a non-admin hr/compliance
// STAFF member silently got permission-denied. The callable runs with the Admin
// SDK (writes ONLY settings.safetyTrainings) and applies the carrier-vs-partner
// auth gate + the partner-managed allowClientEditTrainings toggle + item
// validation (itemId charset / recurrence / caps) server-side. Server is
// authoritative; we keep the optimistic local update on success.
async function _stPersist(updatedList) {
  if (!window.AppState?.tenant) {
    showToast('Not logged in', 'error');
    return false;
  }
  try {
    const call = httpsCallable(functions, 'updateSafetyTrainings');
    const res = await call({ tenantId: window.AppState.tenant, trainings: updatedList });
    if (!window.AppState.settings) window.AppState.settings = {};
    // Prefer the server-normalized list when returned (caps/validation applied).
    const saved = Array.isArray(res?.data?.trainings) ? res.data.trainings : updatedList;
    window.AppState.settings.safetyTrainings = saved;
    return true;
  } catch (e) {
    console.error('[st] persist failed', e);
    const msg = e?.message || (e?.code === 'permission-denied'
      ? 'You do not have access to edit required trainings'
      : 'unknown');
    showToast('Save failed: ' + msg, 'error');
    return false;
  }
}

// TASK-899 — optimistic apply for the Required Safety Training list. The handlers
// used to await the _stPersist network round-trip BEFORE re-rendering, so every
// add/archive/restore/edit/reorder felt laggy. This snapshots the current list,
// paints the new one IMMEDIATELY, then persists in the background. On success the
// server-normalized list (already stored on AppState by _stPersist) is re-rendered;
// on failure we roll AppState back to the snapshot, re-render, and rely on the
// error toast _stPersist already surfaced. `onSuccess` runs only after a confirmed
// save (success toast / input reset). Returns the persist result.
//
// CONCURRENCY (COD Code Reviewer): rapid clicks (reorder arrows, archive/restore)
// must not race — an older failed save must never roll back a newer SUCCESSFUL
// change. We always paint optimistically for instant feedback, run exactly ONE
// _stPersist at a time, and COALESCE — while a save is in flight, the latest
// desired list is flushed when it settles. On failure we roll back to the last
// SERVER-CONFIRMED list (or the pre-sequence snapshot if the first save failed).
let _stPersistInFlight = false;
let _stPersistPending = null; // { list, onSuccess } — latest desired while a save runs
async function _stOptimisticPersist(updated, onSuccess) {
  if (!window.AppState.settings) window.AppState.settings = {};
  const prev = _stGetCurrent(); // pre-mutation snapshot (a fresh slice)
  window.AppState.settings.safetyTrainings = updated;
  _stRenderList(); // optimistic paint

  if (_stPersistInFlight) {
    // Coalesce — newest desired state wins; flushed when the current save ends.
    // Optimistic paint above already gave instant feedback. onSuccess (toast /
    // input reset) is DEFERRED to the confirmed flush so it never fires for a
    // change that later rolls back (COD Code Reviewer ⚠️).
    _stPersistPending = { list: updated, onSuccess };
    return true;
  }

  _stPersistInFlight = true;
  let rollback = prev; // last known-good before this sequence
  try {
    let target = updated;
    let cb = onSuccess;
    while (target) {
      const ok = await _stPersist(target);
      if (!ok) {
        window.AppState.settings.safetyTrainings = rollback; // restore last good
        _stPersistPending = null;
        _stRenderList();
        // _stPersist already showed the failure toast.
        return false;
      }
      rollback = _stGetCurrent(); // server-normalized list = new known-good
      if (typeof cb === 'function') { try { cb(); } catch (_) {} }
      if (_stPersistPending) {
        // A newer change arrived mid-save — persist it next (already painted). Its
        // onSuccess fires only after THIS item's own confirmed save below.
        target = _stPersistPending.list;
        cb = _stPersistPending.onSuccess;
        _stPersistPending = null;
        window.AppState.settings.safetyTrainings = target;
      } else {
        target = null;
        _stRenderList(); // reflect the final server-normalized list
      }
    }
    return true;
  } finally {
    _stPersistInFlight = false;
  }
}

// TASK-9911 M2 — the editor's markup now lives ONLY in the "Skills &
// certifications" screen (roster.js _certSectionHTML); HR → Safety shows a door
// to it. The history below is kept for the ids/handlers, which are unchanged.
// TASK-787 — The Required Safety Trainings editor moved from a standalone modal
// to an INLINE section on the HR → Safety page (hr.js getSafetySubViewHTML).
// hr.js now renders the #safety-trainings-list + add row directly and calls
// window._stRenderList() after injecting the view. The former
// open/closeSafetyTrainingsModal launchers + #safety-trainings-modal markup
// were removed — the add-row read-only gate (TASK-667) is now baked into the
// hr.js markup (it only emits the add row when _canCarrierEditTrainings()), so
// _stCanEdit() inside _stRenderList still suppresses per-row edit buttons for
// partner-managed read-only carriers. All add/edit/move/archive logic below is
// unchanged and keyed off the same DOM ids.

window.addSafetyTraining = async function() {
  const labelEl = document.getElementById('st-new-label');
  const label = (labelEl?.value || '').trim();
  if (!label) { showToast('Training name is required', 'error'); return; }
  if (label.length > ST_LABEL_MAX) { showToast(`Name too long (max ${ST_LABEL_MAX} chars)`, 'error'); return; }
  const recurEl = document.getElementById('st-new-recurrence');
  let recurrenceMonths = Math.max(0, Math.min(ST_RECUR_MAX, parseInt(recurEl?.value, 10) || 0));
  await _stAddItem(label, _stSlugify(label), recurrenceMonths);
};

async function _stAddItem(label, suggestedId, recurrenceMonths) {
  const current = _stGetCurrent();
  const activeCount = current.filter(i => i.archived !== true).length;
  if (activeCount >= ST_CAP_ACTIVE) {
    showToast(`Cap reached (${ST_CAP_ACTIVE} active). Archive one before adding more.`, 'error');
    return;
  }
  let itemId = (suggestedId || '').toLowerCase();
  if (!itemId || !ST_ID_RE.test(itemId)) itemId = 'training';
  if (itemId.length > ST_ID_MAX) itemId = itemId.slice(0, ST_ID_MAX);
  const usedIds = new Set(current.map(i => i.itemId));
  let finalId = itemId;
  let suffix = 2;
  while (usedIds.has(finalId)) {
    finalId = (itemId + '_' + suffix).slice(0, ST_ID_MAX);
    suffix++;
    if (suffix > 999) { showToast('Could not generate unique ID', 'error'); return; }
  }
  if (!ST_ID_RE.test(finalId)) { showToast('Invalid ID generated. Try a different name.', 'error'); return; }

  // §382.603 guard — a locked-id custom add can never carry a recurrence.
  if (ST_NO_RECURRENCE_LOCKED.has(finalId)) recurrenceMonths = 0;

  const newItem = {
    itemId: finalId,
    label: String(label).slice(0, ST_LABEL_MAX),
    builtin: false,
    archived: false,
    recurrenceMonths: Math.max(0, Math.min(ST_RECUR_MAX, Number(recurrenceMonths) || 0)),
    createdAt: new Date().toISOString(),
  };
  const updated = [...current, newItem];
  await _stOptimisticPersist(updated, () => {
    showToast('Training added', 'success');
    const labelEl = document.getElementById('st-new-label');
    if (labelEl) labelEl.value = '';
    const recurEl = document.getElementById('st-new-recurrence');
    if (recurEl) recurEl.value = '0';
  });
}

// TASK-1085 — flip a training's §391.51 DQ-file inclusion (writes an EXPLICIT
// boolean, so the builtin-default fallback no longer applies to this item).
window.toggleSafetyTrainingDotRelevant = async function(itemId) {
  const current = _stGetCurrent();
  const item = current.find(i => i.itemId === itemId);
  if (!item) return;
  const next = !_stIsDotRelevant(item);
  const updated = current.map(i => i.itemId === itemId ? { ...i, dotRelevant: next } : i);
  await _stOptimisticPersist(updated, () => showToast(next
    ? `${item.label} will be included in DQ file packets`
    : `${item.label} excluded from DQ file packets`, 'success'));
};

// TASK-1353 — flip one position tier in a training's appliesTo scope (carrier
// mirror of the partner editor's toggleTrainingTier; same enum + semantics:
// absent/empty appliesTo = all tiers, last tier can't be unchecked — archive
// the training to retire it). Server sanitizer is the real enum gate.
window.toggleSafetyTrainingTier = async function(itemId, tier) {
  const ALL_TIERS = ['cdl', 'non_cdl_driver', 'drives_no_cmv', 'non_driver']; // TASK-9696
  const rerender = () => { if (typeof _stRenderList === 'function') _stRenderList(); };
  if (!ALL_TIERS.includes(tier)) { rerender(); return; }
  const current = _stGetCurrent();
  const item = current.find(i => i.itemId === itemId);
  if (!item) { rerender(); return; }
  const cur = (Array.isArray(item.appliesTo) && item.appliesTo.some(t => ALL_TIERS.includes(t)))
    ? item.appliesTo.filter(t => ALL_TIERS.includes(t))
    : ALL_TIERS.slice(); // absent/empty = all tiers
  const has = cur.includes(tier);
  // §382.603-class trainings can never exclude CDL drivers (server enforces too).
  if (has && tier === 'cdl' && itemId === 'reasonable_suspicion') {
    rerender();
    showToast('Reasonable Suspicion is DOT-mandated for CDL drivers — CDL can\'t be unchecked.', 'error');
    return;
  }
  const nextSet = has ? cur.filter(t => t !== tier) : [...cur, tier];
  if (nextSet.length === 0) {
    rerender();
    showToast("At least one role must apply — archive the training to retire it entirely.", 'error');
    return;
  }
  const next = ALL_TIERS.filter(t => nextSet.includes(t)); // canonical order
  const updated = current.map(i => i.itemId === itemId ? { ...i, appliesTo: next } : i);
  await _stOptimisticPersist(updated, () => showToast(
    next.length === ALL_TIERS.length
      ? `${item.label} now applies to all roles`
      : `${item.label} scoped to: ${next.map(t => ({ cdl: 'CDL', non_cdl_driver: 'Non-CDL', drives_no_cmv: 'No CMV', non_driver: 'Non-Driver' })[t]).join(', ')}`, 'success'));
};

window.archiveSafetyTraining = async function(itemId) {
  const updated = _stGetCurrent().map(i => i.itemId === itemId
    ? { ...i, archived: true, archivedAt: new Date().toISOString() }
    : i);
  await _stOptimisticPersist(updated, () => showToast('Training archived', 'success'));
};

window.restoreSafetyTraining = async function(itemId) {
  const current = _stGetCurrent();
  const activeCount = current.filter(i => i.archived !== true).length;
  if (activeCount >= ST_CAP_ACTIVE) {
    showToast(`Can't restore — already at cap (${ST_CAP_ACTIVE} active). Archive one first.`, 'error');
    return;
  }
  const updated = current.map(i => i.itemId === itemId ? { ...i, archived: false, archivedAt: null } : i);
  await _stOptimisticPersist(updated, () => showToast('Training restored', 'success'));
};

// Edit = rename + change recurrence. 🔴 §382.603: locked items cannot take a
// recurrence; warn and force 0.
window.editSafetyTraining = async function(itemId) {
  const current = _stGetCurrent();
  const item = current.find(i => i.itemId === itemId);
  if (!item) return;
  const locked = ST_NO_RECURRENCE_LOCKED.has(itemId);

  const nextLabel = await window.showInputModal('Training name', '', item.label);
  if (nextLabel === null) return; // cancelled
  const trimmed = String(nextLabel || '').trim();
  if (!trimmed) { showToast('Name cannot be empty', 'error'); return; }
  if (trimmed.length > ST_LABEL_MAX) { showToast(`Name too long (max ${ST_LABEL_MAX} chars)`, 'error'); return; }

  let recurrenceMonths = Number(item.recurrenceMonths) || 0;
  if (locked) {
    // Do not even prompt — regulatorily fixed at 0.
    if (recurrenceMonths !== 0) recurrenceMonths = 0;
    showToast('Reasonable Suspicion (§382.603) is one-time — recurrence stays off.', 'info');
  } else {
    const nextRecur = await window.showInputModal('Recurrence in months', '0 = no expiry', String(recurrenceMonths), { type: 'number' });
    if (nextRecur !== null) {
      recurrenceMonths = Math.max(0, Math.min(ST_RECUR_MAX, parseInt(nextRecur, 10) || 0));
    }
  }
  const updated = current.map(i => i.itemId === itemId ? { ...i, label: trimmed, recurrenceMonths } : i);
  await _stOptimisticPersist(updated, () => showToast('Training updated', 'success'));
};

window.moveSafetyTraining = async function(itemId, delta) {
  const current = _stGetCurrent();
  const active = current.filter(i => i.archived !== true);
  const archived = current.filter(i => i.archived === true);
  const idx = active.findIndex(i => i.itemId === itemId);
  if (idx < 0) return;
  const newIdx = idx + delta;
  if (newIdx < 0 || newIdx >= active.length) return;
  const swapped = active.slice();
  [swapped[idx], swapped[newIdx]] = [swapped[newIdx], swapped[idx]];
  const updated = [...swapped, ...archived];
  await _stOptimisticPersist(updated);
};

// ============================================
// TASK-262 — CUSTOM DRIVER REMINDERS (Slice 1)
// ============================================
// John's old DQF (2026-05-14 screenshot review) lets carriers define
// arbitrary recurring reminder fields per driver — e.g. "eRailSafe",
// "TWIC", "BNSF RWP renewal", site-specific badges, HAZMAT endorsement,
// custom training certs. DD's fixed compliance reminder set (Med Card,
// CDL, BG, MVR, Annual Review) doesn't cover oil-and-gas / rail / port
// fleets.
//
// Schema:
//   tenant.compliance.customReminders[] — array of
//     { itemId, label, archived?, daysBeforeNotify?: [30,15,0], createdAt }
//   person.customReminderExpirations.{itemId} — ISO date string
//
// Slice 1: tenant-level CRUD + roster person-edit date inputs + countdown
// badge on roster cards. Notification fanout (cron + tag) → Slice 2.
const CR_CAP_ACTIVE = 30;
const CR_LABEL_MAX = 48;
const CR_ID_MAX = 32;
const CR_ID_RE = /^[a-z0-9_]{1,32}$/;

// Starter library — common asks from John's old system + rail/oilfield
// fleets. Suggestions only; carrier explicitly opts in via dropdown.
const CR_STARTER_LIBRARY = [
  { suggestedId: 'erailsafe',      label: 'eRailSafe Background' },
  { suggestedId: 'twic',           label: 'TWIC Card' },
  { suggestedId: 'bnsf_rwp',       label: 'BNSF Roadway Worker Protection' },
  { suggestedId: 'hazmat_endorse', label: 'HAZMAT Endorsement' },
  { suggestedId: 'tanker_endorse', label: 'Tanker Endorsement' },
  { suggestedId: 'site_badge',     label: 'Site Access Badge' },
  { suggestedId: 'h2s_cert',       label: 'H2S Awareness Cert' },
  { suggestedId: 'pec_safeland',   label: 'PEC SafeLandUSA' },
  { suggestedId: 'isn_orientation',label: 'ISN Orientation' },
  { suggestedId: 'fit_test',       label: 'Respirator Fit Test' },
];

function _crEsc(s) {
  return String(s ?? '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
}

function _crSlugify(label) {
  return String(label || '').trim().toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_').replace(/^_+|_+$/g, '').slice(0, CR_ID_MAX);
}

// Returns the current customReminders array. Tenant-level field lives at
// `tenant.compliance.customReminders` (NOT under settings). Falls back to
// empty array when unset — carriers explicitly opt in by adding their first.
function _crGetCurrent() {
  const td = window.AppState?.tenantData || {};
  const stored = td.compliance?.customReminders;
  return Array.isArray(stored) ? stored.slice() : [];
}

// Expose so roster.js / compliance.js can re-render after edits.
window._crGetCurrent = _crGetCurrent;

function _crRenderList() {
  const list = document.getElementById('custom-reminders-list');
  if (!list) return;
  const all = _crGetCurrent();
  const active = all.filter(i => i && i.archived !== true);
  const archived = all.filter(i => i && i.archived === true);
  if (active.length === 0 && archived.length === 0) {
    list.innerHTML = `<div style="font-size:0.85rem;color:var(--text-muted);text-align:center;padding:20px;background:var(--bg-page);border-radius:8px;">No custom reminders yet. Add one below or pick from the starter library.</div>`;
    const cnt = document.getElementById('hr-custom-reminders-count');
    if (cnt) cnt.textContent = '(0 active)';
    return;
  }
  const renderRow = (item, idx, total) => {
    const isFirst = idx === 0;
    const isLast = idx === total - 1;
    return `
    <div style="display:flex;justify-content:space-between;align-items:center;gap:8px;padding:10px 12px;border:1px solid var(--border-light);border-radius:8px;background:${item.archived ? '#FAFAFA' : '#fff'};">
      <div style="flex:1;min-width:0;">
        <div style="font-size:0.9rem;color:var(--text-primary);font-weight:600;${item.archived ? 'text-decoration:line-through;color:var(--text-muted);' : ''}">${_crEsc(item.label)}</div>
        <div style="font-size:0.72rem;color:var(--text-secondary);margin-top:2px;">
          id: <code style="font-size:0.7rem;background:var(--bg-page);padding:1px 4px;border-radius:3px;">${_crEsc(item.itemId)}</code>${item.archived ? ' · <em>archived</em>' : ''}
        </div>
      </div>
      ${item.archived ? `
        <button type="button" class="btn btn-sm btn-secondary" onclick="window.restoreCustomReminder('${_crEsc(item.itemId)}')" style="padding:8px 12px;font-size:0.75rem;min-height:44px;min-width:44px;">Restore</button>
      ` : `
        <button type="button" class="btn-icon" onclick="window.moveCustomReminder('${_crEsc(item.itemId)}', -1)" ${isFirst ? 'disabled' : ''} title="Move up" style="padding:8px;min-height:44px;min-width:44px;${isFirst ? 'opacity:0.3;' : ''}">
          <span class="material-icons" style="font-size:18px;">arrow_upward</span>
        </button>
        <button type="button" class="btn-icon" onclick="window.moveCustomReminder('${_crEsc(item.itemId)}', 1)" ${isLast ? 'disabled' : ''} title="Move down" style="padding:8px;min-height:44px;min-width:44px;${isLast ? 'opacity:0.3;' : ''}">
          <span class="material-icons" style="font-size:18px;">arrow_downward</span>
        </button>
        <button type="button" class="btn-icon" onclick="window.editCustomReminder('${_crEsc(item.itemId)}')" title="Rename" style="padding:8px;min-height:44px;min-width:44px;">
          <span class="material-icons" style="font-size:18px;">edit</span>
        </button>
        <button type="button" class="btn-icon" onclick="window.archiveCustomReminder('${_crEsc(item.itemId)}')" title="Archive" style="padding:8px;min-height:44px;min-width:44px;">
          <span class="material-icons" style="font-size:18px;color:var(--text-muted);">archive</span>
        </button>
      `}
    </div>`;
  };
  const activeRows = active.map((it, i) => renderRow(it, i, active.length)).join('');
  const archivedRows = archived.length > 0
    ? `<div style="font-size:0.78rem;color:var(--text-muted);margin-top:8px;font-weight:600;">Archived</div>${archived.map((it, i) => renderRow(it, i, archived.length)).join('')}`
    : '';
  list.innerHTML = activeRows + archivedRows;
  const cnt = document.getElementById('hr-custom-reminders-count');
  if (cnt) cnt.textContent = `(${active.length} active)`;
}

async function _crPersist(updatedList) {
  if (!window.AppState?.tenant) {
    showToast('Not logged in', 'error');
    return false;
  }
  try {
    const tenantRef = doc(db, 'tenants', window.AppState.tenant);
    // Dotted path so we don't clobber other tenant.compliance.* fields
    // (notifiers[], partnerUid, billingMode, etc).
    await updateDoc(tenantRef, { 'compliance.customReminders': updatedList });
    if (!window.AppState.tenantData) window.AppState.tenantData = {};
    if (!window.AppState.tenantData.compliance) window.AppState.tenantData.compliance = {};
    window.AppState.tenantData.compliance.customReminders = updatedList;
    return true;
  } catch (e) {
    console.error('[customReminders] persist failed', e);
    showToast('Save failed: ' + (e?.message || 'unknown'), 'error');
    return false;
  }
}

window.openCustomRemindersModal = function() {
  const m = document.getElementById('custom-reminders-modal');
  if (!m) return;
  _crRenderList();
  const labelEl = document.getElementById('cr-new-label');
  if (labelEl) labelEl.value = '';
  const lib = document.getElementById('cr-library-select');
  if (lib) {
    const current = _crGetCurrent();
    const usedIds = new Set(current.map(i => i.itemId));
    const opts = CR_STARTER_LIBRARY
      .filter(s => !usedIds.has(s.suggestedId))
      .map(s => `<option value="${_crEsc(s.suggestedId)}|${_crEsc(s.label)}">${_crEsc(s.label)}</option>`)
      .join('');
    lib.innerHTML = `<option value="">— Add from library (optional) —</option>${opts}`;
  }
  m.classList.remove('hidden');
};

window.closeCustomRemindersModal = function() {
  const m = document.getElementById('custom-reminders-modal');
  if (m) m.classList.add('hidden');
};

window.addCustomReminderFromLibrary = async function() {
  const sel = document.getElementById('cr-library-select');
  if (!sel || !sel.value) return;
  const [suggestedId, suggestedLabel] = sel.value.split('|');
  await _crAddItem(suggestedLabel, suggestedId);
  sel.value = '';
};

window.addCustomReminderItem = async function() {
  const labelEl = document.getElementById('cr-new-label');
  const label = (labelEl?.value || '').trim();
  if (!label) { showToast('Label is required', 'error'); return; }
  if (label.length > CR_LABEL_MAX) { showToast(`Label too long (max ${CR_LABEL_MAX} chars)`, 'error'); return; }
  await _crAddItem(label, _crSlugify(label));
};

async function _crAddItem(label, suggestedId) {
  const current = _crGetCurrent();
  const activeCount = current.filter(i => i.archived !== true).length;
  if (activeCount >= CR_CAP_ACTIVE) {
    showToast(`Cap reached (${CR_CAP_ACTIVE} active reminders). Archive one before adding more.`, 'error');
    return;
  }
  let itemId = (suggestedId || '').toLowerCase();
  if (!itemId || !CR_ID_RE.test(itemId)) itemId = 'reminder';
  if (itemId.length > CR_ID_MAX) itemId = itemId.slice(0, CR_ID_MAX);
  const usedIds = new Set(current.map(i => i.itemId));
  let finalId = itemId;
  let suffix = 2;
  while (usedIds.has(finalId)) {
    finalId = (itemId + '_' + suffix).slice(0, CR_ID_MAX);
    suffix++;
    if (suffix > 999) { showToast('Could not generate unique ID', 'error'); return; }
  }
  if (!CR_ID_RE.test(finalId)) { showToast('Invalid reminder ID generated. Try a different label.', 'error'); return; }

  const newItem = {
    itemId: finalId,
    label: String(label).slice(0, CR_LABEL_MAX),
    archived: false,
    daysBeforeNotify: [30, 15, 0], // Slice 2 will surface this as configurable
    createdAt: new Date().toISOString(),
  };
  const updated = [...current, newItem];
  const ok = await _crPersist(updated);
  if (ok) {
    showToast('Reminder added', 'success');
    const labelEl = document.getElementById('cr-new-label');
    if (labelEl) labelEl.value = '';
    _crRenderList();
    // Refresh library dropdown — used IDs changed
    const lib = document.getElementById('cr-library-select');
    if (lib) {
      const usedNow = new Set(updated.map(i => i.itemId));
      const opts = CR_STARTER_LIBRARY
        .filter(s => !usedNow.has(s.suggestedId))
        .map(s => `<option value="${_crEsc(s.suggestedId)}|${_crEsc(s.label)}">${_crEsc(s.label)}</option>`)
        .join('');
      lib.innerHTML = `<option value="">— Add from library (optional) —</option>${opts}`;
    }
  }
}

window.archiveCustomReminder = async function(itemId) {
  const updated = _crGetCurrent().map(i => i.itemId === itemId
    ? { ...i, archived: true, archivedAt: new Date().toISOString() }
    : i);
  const ok = await _crPersist(updated);
  if (ok) { showToast('Reminder archived', 'success'); _crRenderList(); }
};

window.restoreCustomReminder = async function(itemId) {
  const current = _crGetCurrent();
  const activeCount = current.filter(i => i.archived !== true).length;
  if (activeCount >= CR_CAP_ACTIVE) {
    showToast(`Can't restore — already at cap (${CR_CAP_ACTIVE} active). Archive one first.`, 'error');
    return;
  }
  const updated = current.map(i => i.itemId === itemId ? { ...i, archived: false, archivedAt: null } : i);
  const ok = await _crPersist(updated);
  if (ok) { showToast('Reminder restored', 'success'); _crRenderList(); }
};

window.editCustomReminder = async function(itemId) {
  const current = _crGetCurrent();
  const item = current.find(i => i.itemId === itemId);
  if (!item) return;
  const next = await window.showInputModal('Rename reminder', '', item.label);
  if (next === null) return;
  const trimmed = String(next || '').trim();
  if (!trimmed) { showToast('Label cannot be empty', 'error'); return; }
  if (trimmed.length > CR_LABEL_MAX) { showToast(`Label too long (max ${CR_LABEL_MAX} chars)`, 'error'); return; }
  const updated = current.map(i => i.itemId === itemId ? { ...i, label: trimmed } : i);
  const ok = await _crPersist(updated);
  if (ok) { showToast('Reminder renamed', 'success'); _crRenderList(); }
};

window.moveCustomReminder = async function(itemId, delta) {
  const current = _crGetCurrent();
  const active = current.filter(i => i.archived !== true);
  const archived = current.filter(i => i.archived === true);
  const idx = active.findIndex(i => i.itemId === itemId);
  if (idx < 0) return;
  const newIdx = idx + delta;
  if (newIdx < 0 || newIdx >= active.length) return;
  const swapped = active.slice();
  [swapped[idx], swapped[newIdx]] = [swapped[newIdx], swapped[idx]];
  const updated = [...swapped, ...archived];
  const ok = await _crPersist(updated);
  if (ok) { _crRenderList(); }
};

// ============================================
// TASK-225 — DECENTRALIZED MODULE SETTINGS SUB-MODALS
// ============================================
// Per Chris 2026-05-06 + COD: Settings was a junk drawer. Module-specific
// toggles now live in dedicated sub-modals each with self-contained Save.
// All persist to tenant.settings.* (no schema migration). Main Settings
// keeps deep-link buttons that open these sub-modals.

async function _settingsPersist(patch) {
  if (!window.AppState?.tenant) {
    showToast('Not logged in', 'error');
    return false;
  }
  try {
    const tenantRef = doc(db, 'tenants', window.AppState.tenant);
    const out = {};
    Object.keys(patch).forEach(k => { out['settings.' + k] = patch[k]; });
    await updateDoc(tenantRef, out);
    if (!window.AppState.settings) window.AppState.settings = {};
    Object.assign(window.AppState.settings, patch);
    return true;
  } catch (e) {
    console.error('[settings] persist failed', e);
    showToast('Save failed: ' + (e?.message || 'unknown'), 'error');
    return false;
  }
}
// Expose canonical settings-persist helper for sub-modals in other modules
// (e.g. dispatch.js saveDispatchSettings — Code Reviewer COD 2026-05-19).
window._settingsPersist = _settingsPersist;

// ── TASK-1569 — SMS Command Approver (credential surface) ────────────────────
// The approver phone is the write credential for the crew-text back-office: a
// reply from it can approve the AI assistant's job-write drafts (phone-as-
// identity, TASK-1518). So this is admin-gated (hrEffectiveAdmin in the UI +
// firestore.rules isTenantAdmin on the tenant-doc update — the write below goes
// through the same tenant-doc update path every settings save uses, and that
// rule's ONLY non-admin branch (compliance manager) is affectedKeys().hasOnly()
// of partner-display fields, so it can never carry a settings.* write), the
// value is normalized to E.164 (mirrors notifications.normalizePhoneNumber so
// resolveApprover's re-normalized compare matches), and the change is
// audit-logged MASKED (last-4 only — never a full phone in the audit trail).
//
// Rotation: no in-flight-pending cleanup is needed here. resolveApprover is
// re-evaluated at COMMIT time (smsInbox.js:1661): after a rotation the old
// number's reply no longer equals the current approver, so any writing draft it
// was already sent is REFUSED and discarded; and listPending keys pending docs
// by the approver stored at draft time, so the NEW number can't match a stale
// pending either — the orphaned draft simply expires via its TTL (TASK-1545).

// Mirror of functions/src/services/notifications.js normalizePhoneNumber so the
// stored value is the exact E.164 shape resolveApprover re-derives before its
// equality check. Returns '' for an empty input (a deliberate CLEAR → owner
// fallback) or null for an un-normalizable non-empty input (rejected in the UI).
function _normalizeApproverPhoneE164(phone) {
  const raw = (phone == null ? '' : String(phone)).trim();
  if (!raw) return '';                 // empty = clear the override
  if (raw.startsWith('#')) return null;
  const digits = raw.replace(/[^0-9]/g, '');
  if (/^0+$/.test(digits)) return null;
  if (/^(\d)\1{9,}$/.test(digits)) return null;
  if (digits.length === 10 && digits.startsWith('000')) return null;
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits[0] === '1') return `+${digits}`;
  if (digits.length > 10) return `+${digits}`;
  return null;
}

// Mask a phone to its last 4 for the audit trail / effective-approver display.
// Never surface a full phone number in a log or a masked line.
function _maskPhoneLast4(phone) {
  const digits = (phone == null ? '' : String(phone)).replace(/[^0-9]/g, '');
  if (!digits) return '(none)';
  return '***' + digits.slice(-4);
}

// Render the effective-approver status line so the fallback is never invisible:
// an explicit value, or "defaulting to owner phone ***1234", or a warning when
// neither is set.
function renderSmsApproverEffective(explicit, ownerPhone) {
  const el = document.getElementById('sms-approver-effective');
  if (!el) return;
  const explicitNorm = _normalizeApproverPhoneE164(explicit);
  const ownerNorm = _normalizeApproverPhoneE164(ownerPhone);
  if (explicitNorm) {
    el.innerHTML = `<span class="material-icons" style="font-size:15px;vertical-align:middle;color:var(--accent-teal,#06B6D4);">verified_user</span> Approvals go to <strong>${_maskPhoneLast4(explicitNorm)}</strong> (explicitly set).`;
  } else if (ownerNorm) {
    el.innerHTML = `<span class="material-icons" style="font-size:15px;vertical-align:middle;color:var(--text-secondary);">info</span> No approver set &mdash; defaulting to the owner phone <strong>${_maskPhoneLast4(ownerNorm)}</strong>.`;
  } else {
    el.innerHTML = `<span class="material-icons" style="font-size:15px;vertical-align:middle;color:#DC2626;">warning</span> No approver set and no owner phone on file &mdash; the crew-text assistant has no one to send approvals to.`;
  }
}
window.renderSmsApproverEffective = renderSmsApproverEffective;

async function saveSmsApproverPhone() {
  if (!window.AppState?.tenant) { showToast('Not logged in', 'error'); return; }
  // Defense-in-depth: the firestore.rules isTenantAdmin gate is the real
  // boundary, but don't even attempt the write (and the confusing permission
  // error it would raise) for a non-admin.
  if (typeof hrEffectiveAdmin === 'function' && !hrEffectiveAdmin()) {
    showToast('Only an owner or admin can change the SMS approver', 'error');
    return;
  }
  const inputEl = document.getElementById('settings-sms-approver-phone');
  const btn = document.getElementById('btn-save-sms-approver');
  const rawInput = inputEl ? inputEl.value : '';
  const normalized = _normalizeApproverPhoneE164(rawInput);
  if (normalized === null) {
    showToast('Enter a valid phone number (10 digits) or leave blank', 'error');
    return;
  }
  const btnOriginal = btn ? btn.innerHTML : '';
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Saving...'; }
  try {
    // Read the CURRENT value fresh so the audit from→to and the no-op check are
    // against Firestore truth, not a possibly-stale AppState copy.
    const tenantRef = doc(db, 'tenants', window.AppState.tenant);
    const snap = await getDoc(tenantRef);
    const tData = snap.exists() ? snap.data() : {};
    const oldExplicit = tData?.settings?.smsInbox?.approverPhone || tData?.smsInbox?.approverPhone || '';
    const ownerPhone = tData?.ownerPhone || '';
    const oldNorm = _normalizeApproverPhoneE164(oldExplicit) || '';
    const newVal = normalized; // '' clears the override
    if (oldNorm === newVal) {
      if (btn) { btn.disabled = false; btn.innerHTML = btnOriginal; }
      showToast('No change to the approver', 'info');
      renderSmsApproverEffective(newVal, ownerPhone);
      return;
    }
    // Persist through the canonical settings write path (dotted nested key →
    // 'settings.smsInbox.approverPhone'); '' explicitly stores an empty override
    // so resolveApprover falls back to the owner phone.
    const ok = await _settingsPersist({ 'smsInbox.approverPhone': newVal });
    if (!ok) {
      if (btn) { btn.disabled = false; btn.innerHTML = btnOriginal; }
      return; // _settingsPersist already toasted the failure
    }
    // resolveApprover prefers settings.smsInbox.approverPhone but then falls back
    // to a LEGACY top-level tenant.smsInbox.approverPhone before ownerPhone
    // (smsInbox.js:1156). If that legacy field exists, clearing our value to ''
    // would silently resurrect it instead of falling through to the owner phone —
    // so neutralize it in the same save. Only fires when the legacy key is
    // actually present (near-nobody), and admin-authorized like the write above.
    if (tData?.smsInbox && Object.prototype.hasOwnProperty.call(tData.smsInbox, 'approverPhone')) {
      try {
        await updateDoc(tenantRef, { 'smsInbox.approverPhone': newVal });
        if (window.AppState.tenantData?.smsInbox) window.AppState.tenantData.smsInbox.approverPhone = newVal;
      } catch (legacyErr) {
        console.warn('[SMS approver] legacy top-level clear failed', legacyErr?.message);
      }
    }
    // Keep the nested in-memory copy correct (the flat-dotted-key quirk of
    // _settingsPersist's Object.assign would otherwise leave AppState.settings
    // with a literal 'smsInbox.approverPhone' key, not the nested value).
    if (!window.AppState.settings) window.AppState.settings = {};
    if (!window.AppState.settings.smsInbox || typeof window.AppState.settings.smsInbox !== 'object') {
      window.AppState.settings.smsInbox = {};
    }
    window.AppState.settings.smsInbox.approverPhone = newVal;
    delete window.AppState.settings['smsInbox.approverPhone'];
    if (window.AppState.tenantData) window.AppState.tenantData.settings = window.AppState.settings;
    // Audit MASKED — last-4 only, never a full phone.
    window.logAudit?.('settings', 'SMS_APPROVER_CHANGED', {
      from: _maskPhoneLast4(oldNorm),
      to: newVal ? _maskPhoneLast4(newVal) : '(cleared → owner phone)',
    });
    renderSmsApproverEffective(newVal, ownerPhone);
    if (inputEl) inputEl.value = newVal;
    if (btn) { btn.disabled = false; btn.innerHTML = btnOriginal; }
    showToast(newVal ? 'SMS approver updated' : 'SMS approver cleared — defaulting to owner phone', 'success');
  } catch (e) {
    console.error('[SMS approver] save failed', e);
    if (btn) { btn.disabled = false; btn.innerHTML = btnOriginal; }
    showToast('Save failed: ' + (e?.message || 'unknown'), 'error');
  }
}
window.saveSmsApproverPhone = saveSmsApproverPhone;

// TASK-4586 — the missing writer for `settings.smsInbox.enabled`. Server-side
// isEnabled() gates the ENTIRE inbound lane on this one boolean, and nothing in
// public/, functions/src or scripts/ ever wrote it: onboarding a tenant to the
// crew-text lane meant hand-editing Firestore, which is why 1 of 79 tenants had
// the setting at all. This is the TASK-1506 multi-tenant blocker, not the approver.
//
// Two semantics decided here, both matching what the server already does rather
// than inventing a third behaviour:
//  • Scope is INBOUND ONLY. isEnabled() gates routing (smsInbox.js:1531) and
//    processing (:2055); outbound (Notify Crew, reminders) is a different system
//    and is untouched.
//  • In-flight drafts are NOT swept. The precedent at smsInbox.js:1678-1691 is to
//    refuse at commit time, and turning the lane off already does exactly that:
//    an approver's "Y" re-enters through the routing gate, reads enabled=false,
//    and commits nothing. Sweeping would be a second, divergent mechanism.
// The one thing the server cannot do is warn the person flipping the switch, so
// that happens here: the confirm names the open drafts that will never commit.
async function saveSmsInboxEnabled(nextChecked) {
  const el = document.getElementById('settings-sms-inbox-enabled');
  const revert = () => { if (el) el.checked = !nextChecked; };
  if (!window.AppState?.tenant) { showToast('Not logged in', 'error'); revert(); return; }
  if (typeof hrEffectiveAdmin === 'function' && !hrEffectiveAdmin()) {
    showToast('Only an owner or admin can change this', 'error');
    revert();
    return;
  }
  const tenantId = window.AppState.tenant;
  const turningOff = !nextChecked;

  if (turningOff) {
    // Count the drafts that are still live (same 2h TTL the server filters on)
    // so the warning is a number, not a vague caution. Best-effort: a failed
    // count must not block the admin from turning it off.
    let liveDrafts = null;
    try {
      const cutoff = Date.now() - (2 * 60 * 60 * 1000);
      const snap = await getDocs(collection(db, 'tenants', tenantId, 'smsInboxPending'));
      liveDrafts = snap.docs.filter(d => Number(d.data()?.createdAtMs || 0) > cutoff).length;
    } catch (e) {
      console.warn('[smsInbox] pending count failed', e?.message);
    }
    const draftLine = liveDrafts === null
      ? 'Any drafts already waiting on the approver will never commit.'
      : liveDrafts === 0
        ? 'No drafts are currently waiting on the approver.'
        : `${liveDrafts} draft${liveDrafts === 1 ? '' : 's'} waiting on the approver will never commit, even if they reply Y.`;
    const ok = await (window.showConfirm?.(
      'Stop answering crew texts?',
      `Inbound texts to this number will not be read or answered — the crew gets silence, not a bounce. ${draftLine} Texts you send out are unaffected.`,
      { confirmText: 'Turn it off', cancelText: 'Keep it on' }
    ) ?? Promise.resolve(true));
    if (!ok) { revert(); return; }
  }

  if (el) el.disabled = true;
  try {
    const ok = await _settingsPersist({ 'smsInbox.enabled': nextChecked === true });
    if (!ok) { revert(); if (el) el.disabled = false; return; }
    // _settingsPersist's Object.assign leaves a literal dotted key in memory.
    if (!window.AppState.settings) window.AppState.settings = {};
    if (!window.AppState.settings.smsInbox || typeof window.AppState.settings.smsInbox !== 'object') {
      window.AppState.settings.smsInbox = {};
    }
    window.AppState.settings.smsInbox.enabled = nextChecked === true;
    delete window.AppState.settings['smsInbox.enabled'];
    if (window.AppState.tenantData) window.AppState.tenantData.settings = window.AppState.settings;
    window.logAudit?.('settings', 'SMS_INBOX_ENABLED_CHANGED', {
      to: nextChecked ? 'enabled' : 'disabled',
    });
    showToast(nextChecked
      ? 'Crew texts will now be read and answered'
      : 'Inbound crew texts are no longer read', 'success');
  } catch (e) {
    console.error('[smsInbox] enable save failed', e);
    revert();
    showToast('Save failed: ' + (e?.message || 'unknown'), 'error');
  } finally {
    if (el) el.disabled = false;
  }
}
window.saveSmsInboxEnabled = saveSmsInboxEnabled;

// ── TASK-9769 S2 — customer texts → requests (settings.customerTextIntake.enabled)
// Slice 1 shipped the intake (customerTextIntake.js) with no writer for this
// switch, so turning it on meant the admin SDK. Only `enabled` is written here:
// `confirmByText` (the one outbound text) waits on the A2P decision and is
// never touched from this screen. Owner/admin only (hrEffectiveAdmin here +
// firestore.rules on the tenant-doc update). The status line is the server's
// answer to "can a customer text reach us at all?" — a shared pool number
// binds nobody, so "on" with no number of your own would otherwise fail quiet.
const CTI_ROUTING_TEXT = {
  own_number: (l4) => `Texts to your DispatchDeck number${l4 ? ` ending ${l4}` : ''} reach you.`,
  shared_number: (l4) => `Your DispatchDeck number${l4 ? ` ending ${l4}` : ''} is shared with other companies, so customer texts to it can't be sorted to you. Nothing will arrive until you have a number of your own. Contact support to get one.`,
  no_number: () => 'Your company does not have its own DispatchDeck texting number yet, so customer texts can\'t reach you. Contact support to get one.',
  demo: () => 'The demo does not receive texts.',
};
function _ctiStatusLine(st) {
  if (!st || typeof st !== 'object') return '';
  const fn = CTI_ROUTING_TEXT[st.routing];
  const where = fn ? fn(st.numberLast4 ? String(st.numberLast4).replace(/[^0-9]/g, '').slice(-4) : '') : '';
  if (!st.enabled) return where ? `Off. ${where}` : 'Off.';
  // TASK-9769 S2b: promise the text only when the server's A2P gate allows it
  // too; confirmByText on its own never sends (customerTextIntake.confirmTextApproved).
  const reply = (st.confirmByText && st.confirmTextApproved)
    ? 'After you approve a request, the customer gets one text saying it is on your schedule.'
    : 'The customer gets no text back. Call them to confirm.';
  return `On. ${where} ${st.routing === 'own_number' ? reply : ''}`.trim();
}
let _ctiLastStatus = null;
async function renderCustomerTextIntakeStatus() {
  const box = document.getElementById('customer-text-intake-status');
  if (!box) return;
  box.style.display = '';
  box.textContent = 'Checking your texting number…';
  try {
    const res = await httpsCallable(functions, 'getCustomerTextIntakeStatus')({});
    _ctiLastStatus = res?.data || null;
    const line = _ctiStatusLine(res?.data);
    if (line) box.textContent = line; else box.style.display = 'none';
  } catch (e) {
    _ctiLastStatus = null;
    console.warn('[customerTextIntake] status failed', e?.message);
    box.textContent = e?.code === 'functions/permission-denied'
      ? 'Only the owner or someone with Dispatch access can check whether customer texts reach you.'
      : 'Could not check your texting number right now.';
  }
}
window.renderCustomerTextIntakeStatus = renderCustomerTextIntakeStatus;

async function saveCustomerTextIntakeEnabled(nextChecked) {
  const el = document.getElementById('settings-customer-text-intake-enabled');
  const revert = () => { if (el) el.checked = !nextChecked; };
  if (!window.AppState?.tenant) { showToast('Not logged in', 'error'); revert(); return; }
  if (typeof hrEffectiveAdmin === 'function' && !hrEffectiveAdmin()) {
    showToast('Only an owner or admin can change this', 'error');
    revert();
    return;
  }
  const was = window.AppState.settings?.customerTextIntake?.enabled === true;
  // COD S2 r1 (UX): don't switch on something that can't work. A shared or
  // missing number binds nobody, so say so instead of saving an inert "on".
  const r = _ctiLastStatus?.routing;
  if (nextChecked && (r === 'shared_number' || r === 'no_number' || r === 'demo')) {
    showToast(r === 'demo' ? 'The demo does not receive texts' : 'Customer texts need a DispatchDeck number of your own. Contact support to get one.', 'error');
    revert();
    return;
  }
  if (!nextChecked) {
    const ok = await (window.showConfirm?.(
      'Stop turning customer texts into requests?',
      'New texts from customers will not become requests. Requests already on the Dispatch Board stay there. Customers are not told.',
      { confirmText: 'Turn it off', cancelText: 'Keep it on' }
    ) ?? Promise.resolve(true));
    if (!ok) { revert(); return; }
  }
  if (el) el.disabled = true;
  try {
    const ok = await _settingsPersist({ 'customerTextIntake.enabled': nextChecked === true });
    if (!ok) { revert(); return; }
    // _settingsPersist's Object.assign leaves a literal dotted key in memory.
    if (!window.AppState.settings) window.AppState.settings = {};
    const cur = window.AppState.settings.customerTextIntake;
    window.AppState.settings.customerTextIntake = { ...(cur && typeof cur === 'object' ? cur : {}), enabled: nextChecked === true };
    delete window.AppState.settings['customerTextIntake.enabled'];
    if (window.AppState.tenantData) window.AppState.tenantData.settings = window.AppState.settings;
    window.logAudit?.('settings', 'CUSTOMER_TEXT_INTAKE_CHANGED', {
      from: was ? 'enabled' : 'disabled',
      to: nextChecked ? 'enabled' : 'disabled',
    });
    showToast(nextChecked
      ? 'Customer texts will now become requests for you to approve'
      : 'Customer texts no longer become requests', 'success');
    renderCustomerTextIntakeStatus();
  } catch (e) {
    console.error('[customerTextIntake] enable save failed', e);
    revert();
    showToast('Save failed: ' + (e?.message || 'unknown'), 'error');
  } finally {
    if (el) el.disabled = false;
  }
}
window.saveCustomerTextIntakeEnabled = saveCustomerTextIntakeEnabled;

// ── TASK-9256 — SMS assistant pilot numbers (settings.smsInbox.allowlist) ────
// The list had no writer anywhere: scoping the crew-text assistant to a few
// numbers meant hand-editing Firestore. Three things this editor must never do:
//
//  🔴 1. NEVER WRITE A COMPUTED ARRAY. optOutOfAi (functions/src/services/
//     smsInbox.js) writes this same list Admin-SDK side when a crew member texts
//     HUMAN / NO AI. A read-modify-write of the whole array from this page would
//     silently undo that (or be undone by it). Every write here is ONE
//     arrayUnion / arrayRemove on the dotted path `settings.smsInbox.allowlist`
//     — and nothing else in `settings` is touched, so optedOutOfAi and every
//     other server-written key are out of reach by construction (TASK-9255).
//     The ops run inside runTransaction so the guards below are checked against
//     the SAME doc version the write lands on; the transaction replaces
//     _settingsPersist here only because that helper cannot read-then-guard.
//
//  🔴 2. TASK-9832 (Chris 2026-10-13): AN EMPTY LIST MEANS NOBODY. It used to
//     mean EVERYONE (so removing the last number was refused and adding the
//     first one asked first). isAllowlisted now returns false on an absent or
//     empty array, which makes emptying the list the SAFE direction: the
//     assistant goes quiet, texts still land in the inbox. What a listed number
//     may DO is decided by that person's login modules on the server
//     (smsSenderAccess), not by being on this list.
//
//  🔴 3. NEVER RE-ADD SOMEONE WHO OPTED OUT. A number in optedOutOfAi is refused
//     — they asked for a human and there is no opt-back-in from the office side.
//     (On the server an opted-out number on the list is inert anyway, because
//     hasOptedOutOfAi is checked first; the refusal is so the admin isn't shown
//     an enrollment that does nothing, and so the office never overrides them.)
//
// voiceAllowlist is deliberately NOT touched (voice lane is not deployed and
// fails CLOSED on absent; creating it would enroll people in a lane with no UI).
// Admin-gated like the approver: hrEffectiveAdmin() in the UI, firestore.rules
// isTenantAdmin on the tenant-doc update. Audit rows are MASKED to last-4.

let _smsPilotEntries = [];

function _smsPilotNorm(n) {
  return _normalizeApproverPhoneE164(n) || '';
}

// Best-effort name for a number. AppState.people is never populated in this
// codebase; fleetPeople (jobboard bridge) usually is. No match → number only.
function _smsPilotNameFor(e164) {
  if (!e164) return '';
  const st = window.AppState || {};
  for (const pool of [st.fleetPeople, st.people]) {
    if (!Array.isArray(pool)) continue;
    const p = pool.find(x => x && x.phone && _smsPilotNorm(x.phone) === e164);
    if (p?.name) return String(p.name);
  }
  return '';
}

function _smsPilotLabel(e164, raw) {
  const name = _smsPilotNameFor(e164);
  const num = e164 ? formatPhoneDisplay(e164) : String(raw || '');
  return name ? `${name} (${num})` : num;
}

function renderSmsPilotList(cfg) {
  const statusEl = document.getElementById('sms-pilot-effective');
  const listEl = document.getElementById('sms-pilot-list');
  const allow = Array.isArray(cfg?.allowlist) ? cfg.allowlist : [];
  const outs = new Set((Array.isArray(cfg?.optedOutOfAi) ? cfg.optedOutOfAi : [])
    .map(_smsPilotNorm).filter(Boolean));

  // Group stored elements by what the server will compare (normalized E.164),
  // so a hand-edited "(701) 555-1234" and "+17015551234" show as one person.
  const byKey = new Map();
  allow.forEach(raw => {
    const e164 = _smsPilotNorm(raw);
    const key = e164 || `raw:${raw}`;
    if (!byKey.has(key)) byKey.set(key, { e164, raw: String(raw ?? '') });
  });
  _smsPilotEntries = [...byKey.values()];
  const answered = _smsPilotEntries.filter(x => x.e164 && !outs.has(x.e164)).length;

  if (statusEl) {
    if (!allow.length) {
      statusEl.innerHTML = `<span class="material-icons" style="font-size:15px;vertical-align:middle;color:var(--text-secondary);">block</span> <strong>Nobody yet.</strong> The assistant replies only to numbers on this list. Texts from everyone else land in the inbox for a person to read.`;
    } else if (answered === 0) {
      statusEl.innerHTML = `<span class="material-icons" style="font-size:15px;vertical-align:middle;color:var(--text-secondary);">block</span> <strong>Pilot crew only &mdash; but nobody on it gets a reply.</strong> Everyone listed has opted out, so the assistant answers no one. Texts still land in the inbox.`;
    } else {
      statusEl.innerHTML = `<span class="material-icons" style="font-size:15px;vertical-align:middle;color:var(--accent-teal,#06B6D4);">verified_user</span> <strong>Pilot crew only.</strong> The assistant replies to ${answered} number${answered === 1 ? '' : 's'} below. Texts from everyone else land in the inbox for a person to read.`;
    }
  }

  if (listEl) {
    const rows = _smsPilotEntries.map((x, i) => {
      const opted = x.e164 && outs.has(x.e164);
      const bad = !x.e164;
      const note = opted
        ? `<span style="display:block;font-size:0.72rem;color:#B45309;">Opted out &mdash; texted to reach a person. The assistant will not reply.</span>`
        : bad
          ? `<span style="display:block;font-size:0.72rem;color:#DC2626;">Not a valid phone number &mdash; never matches a text.</span>`
          : '';
      return `<li style="display:flex;align-items:center;gap:8px;padding:6px 0;border-bottom:1px solid var(--border-light);">
        <span class="material-icons" style="font-size:18px;color:${opted ? '#B45309' : 'var(--text-secondary)'};">${opted ? 'do_not_disturb_on' : 'person'}</span>
        <span style="flex:1;min-width:0;color:var(--text-primary);font-size:0.85rem;">${escapeHtml(_smsPilotLabel(x.e164, x.raw))}${note}</span>
        <button type="button" class="btn btn-secondary" data-sms-pilot-remove="${i}" title="Remove from the pilot" style="min-height:44px;padding:4px 12px;font-size:0.78rem;white-space:nowrap;">
          <span class="material-icons" style="font-size:15px;">remove_circle_outline</span> Remove
        </button>
      </li>`;
    });
    // Opted-out people NOT on the list, read-only — an admin wondering why a
    // number won't engage deserves the answer on screen.
    const listed = new Set(_smsPilotEntries.map(x => x.e164).filter(Boolean));
    const offList = [...outs].filter(e => !listed.has(e));
    if (offList.length) {
      rows.push(`<li style="padding:8px 0 0;font-size:0.75rem;color:var(--text-secondary);">
        Opted out of the assistant: ${offList.map(e => escapeHtml(_smsPilotLabel(e, e))).join(', ')}
      </li>`);
    }
    listEl.innerHTML = rows.join('');
    listEl.onclick = (ev) => {
      const btn = ev.target?.closest?.('[data-sms-pilot-remove]');
      if (!btn || btn.disabled) return;
      removeSmsPilotNumber(Number(btn.getAttribute('data-sms-pilot-remove')));
    };
  }
}
window.renderSmsPilotList = renderSmsPilotList;

// Mirror a committed allowlist into AppState. Only `allowlist` changes; the
// nested object is copied so nothing else under smsInbox is rewritten.
function _smsPilotMirror(allowAfter, cfg) {
  const st = window.AppState;
  if (!st) return;
  if (!st.settings) st.settings = {};
  const cur = (st.settings.smsInbox && typeof st.settings.smsInbox === 'object') ? st.settings.smsInbox : {};
  st.settings.smsInbox = { ...cur, allowlist: allowAfter.slice() };
  if (Array.isArray(cfg?.optedOutOfAi)) st.settings.smsInbox.optedOutOfAi = cfg.optedOutOfAi.slice();
  if (st.tenantData) st.tenantData.settings = st.settings;
}

function _smsPilotSmsInbox(snap) {
  const cfg = snap && snap.exists() ? (snap.data()?.settings?.smsInbox || {}) : {};
  return {
    cfg,
    allow: Array.isArray(cfg.allowlist) ? cfg.allowlist : [],
    outs: new Set((Array.isArray(cfg.optedOutOfAi) ? cfg.optedOutOfAi : []).map(_smsPilotNorm).filter(Boolean)),
  };
}

async function addSmsPilotNumber() {
  if (!window.AppState?.tenant) { showToast('Not logged in', 'error'); return; }
  if (typeof hrEffectiveAdmin === 'function' && !hrEffectiveAdmin()) {
    showToast('Only an owner or admin can change who the assistant answers', 'error');
    return;
  }
  const inputEl = document.getElementById('settings-sms-pilot-phone');
  const btn = document.getElementById('btn-add-sms-pilot');
  const e164 = _smsPilotNorm(inputEl ? inputEl.value : '');
  if (!e164) { showToast('Enter a valid 10-digit phone number', 'error'); return; }
  const tenantRef = doc(db, 'tenants', window.AppState.tenant);
  const btnOriginal = btn ? btn.innerHTML : '';
  const restore = () => { if (btn) { btn.disabled = false; btn.innerHTML = btnOriginal; } };
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Adding...'; }
  try {
    // Pre-read ONLY to decide whether to ask; the transaction re-checks all of it.
    const pre = _smsPilotSmsInbox(await getDoc(tenantRef));
    if (pre.outs.has(e164)) {
      restore();
      showToast(`${_smsPilotLabel(e164, e164)} texted to get off the assistant. They can't be added back from here.`, 'error');
      renderSmsPilotList(pre.cfg);
      return;
    }
    const res = await runTransaction(db, async (tx) => {
      const snap = await tx.get(tenantRef);
      if (!snap.exists()) return { status: 'missing' };
      const cur = _smsPilotSmsInbox(snap);
      if (cur.outs.has(e164)) return { status: 'optedOut', cfg: cur.cfg };
      if (cur.allow.some(n => _smsPilotNorm(n) === e164)) return { status: 'dup', cfg: cur.cfg };
      tx.update(tenantRef, { 'settings.smsInbox.allowlist': arrayUnion(e164) });
      return { status: 'added', cfg: cur.cfg, before: cur.allow.slice(), after: [...cur.allow, e164] };
    });
    restore();
    if (res.status === 'missing') { showToast('Company record not found', 'error'); return; }
    if (res.status === 'optedOut') {
      showToast(`${_smsPilotLabel(e164, e164)} just texted to get off the assistant. Not added.`, 'error');
      renderSmsPilotList(res.cfg);
      return;
    }
    if (res.status === 'dup') { showToast('That number is already on the list', 'info'); renderSmsPilotList(res.cfg); return; }
    _smsPilotMirror(res.after, res.cfg);
    renderSmsPilotList({ ...res.cfg, allowlist: res.after });
    if (inputEl) inputEl.value = '';
    window.logAudit?.('settings', 'SMS_PILOT_NUMBER_ADDED', {
      number: _maskPhoneLast4(e164),
      from: res.before.length ? res.before.map(_maskPhoneLast4) : '(empty — nobody)',
      to: res.after.map(_maskPhoneLast4),
    });
    showToast('Added to the pilot', 'success');
  } catch (e) {
    console.error('[smsPilot] add failed', e);
    restore();
    showToast('Save failed: ' + (e?.message || 'unknown'), 'error');
  }
}
window.addSmsPilotNumber = addSmsPilotNumber;

async function removeSmsPilotNumber(idx) {
  if (!window.AppState?.tenant) { showToast('Not logged in', 'error'); return; }
  if (typeof hrEffectiveAdmin === 'function' && !hrEffectiveAdmin()) {
    showToast('Only an owner or admin can change who the assistant answers', 'error');
    return;
  }
  const entry = _smsPilotEntries[idx];
  if (!entry) return;
  const label = _smsPilotLabel(entry.e164, entry.raw);
  const ok = await (window.showConfirm?.(
    'Remove from the pilot?',
    `The assistant will stop replying to ${label}. Their texts still land in the inbox for a person to read.`,
    { confirmText: 'Remove', cancelText: 'Keep' }
  ) ?? Promise.resolve(false));
  if (!ok) return;
  const tenantRef = doc(db, 'tenants', window.AppState.tenant);
  // Match the way the server compares (normalized), so every stored shape of
  // this number goes; an un-normalizable entry is removed by exact value.
  const isHit = (n) => entry.e164 ? _smsPilotNorm(n) === entry.e164 : String(n ?? '') === entry.raw;
  try {
    const res = await runTransaction(db, async (tx) => {
      const snap = await tx.get(tenantRef);
      if (!snap.exists()) return { status: 'missing' };
      const cur = _smsPilotSmsInbox(snap);
      const hits = cur.allow.filter(isHit);
      if (!hits.length) return { status: 'gone', cfg: cur.cfg };
      tx.update(tenantRef, { 'settings.smsInbox.allowlist': arrayRemove(...hits) });
      return { status: 'removed', cfg: cur.cfg, before: cur.allow.slice(), after: cur.allow.filter(n => !hits.includes(n)) };
    });
    if (res.status === 'missing') { showToast('Company record not found', 'error'); return; }
    if (res.status === 'gone') { showToast('That number was already off the list', 'info'); renderSmsPilotList(res.cfg); return; }
    _smsPilotMirror(res.after, res.cfg);
    renderSmsPilotList({ ...res.cfg, allowlist: res.after });
    window.logAudit?.('settings', 'SMS_PILOT_NUMBER_REMOVED', {
      number: _maskPhoneLast4(entry.e164 || entry.raw),
      from: res.before.map(_maskPhoneLast4),
      to: res.after.map(_maskPhoneLast4),
    });
    showToast('Removed from the pilot', 'success');
  } catch (e) {
    console.error('[smsPilot] remove failed', e);
    showToast('Save failed: ' + (e?.message || 'unknown'), 'error');
  }
}
window.removeSmsPilotNumber = removeSmsPilotNumber;

// ── Inspection & Compliance sub-modal (Maintenance domain) ──
// IOGP Life-Saving Rules — must mirror XTO_LSRA_CARDS in jsa.js
const _LSRA_RULES_FOR_SETTINGS = [
  { key: 'confined_space_entry', title: 'Confined Space Entry' },
  { key: 'bypassing_safety_controls', title: 'Bypassing Safety Controls' },
  { key: 'energy_isolation', title: 'Energy Isolation' },
  { key: 'excavation', title: 'Excavation' },
  { key: 'safe_mechanical_lifting', title: 'Safe Mechanical Lifting' },
  { key: 'hot_work', title: 'Hot Work' },
  { key: 'working_around_mobile_equipment', title: 'Working Around Mobile Equipment' },
  { key: 'working_at_height', title: 'Working at Height' },
  { key: 'breaking_containment_ope', title: 'Breaking Containment / OPE' },
  { key: 'driving', title: 'Driving' },
  { key: 'work_authorization', title: 'Work Authorization' },
  { key: 'line_of_fire', title: 'Line of Fire' },
];
function _renderLsraRulesCheckboxes() {
  const container = document.getElementById('ins-lsra-rules-checkboxes');
  if (!container) return;
  const rulesMap = window.AppState?.settings?.lsraRules || {};
  container.innerHTML = _LSRA_RULES_FOR_SETTINGS.map(r => {
    // Default: missing key OR true = enabled. Only explicit false hides it.
    const enabled = rulesMap[r.key] !== false;
    return `<label class="checkbox-label" style="display:flex;align-items:center;gap:6px;cursor:pointer;font-size:0.85rem;">
      <input type="checkbox" data-lsra-rule-key="${r.key}" ${enabled ? 'checked' : ''}>
      <span>${r.title}</span>
    </label>`;
  }).join('');
}
window.toggleLsraRulesExpander = function() {
  const btn = document.getElementById('ins-lsra-expander-btn');
  const list = document.getElementById('ins-lsra-rules-list');
  const chev = document.getElementById('ins-lsra-chevron');
  if (!btn || !list) return;
  const willExpand = btn.getAttribute('aria-expanded') !== 'true';
  btn.setAttribute('aria-expanded', willExpand ? 'true' : 'false');
  list.classList.toggle('hidden', !willExpand);
  if (chev) chev.style.transform = willExpand ? 'rotate(90deg)' : 'rotate(0deg)';
};
window.setAllLsraRules = function(enabled) {
  const checks = document.querySelectorAll('#ins-lsra-rules-checkboxes input[data-lsra-rule-key]');
  checks.forEach(c => { c.checked = !!enabled; });
};

window.openInspectionSettingsModal = function() {
  const m = document.getElementById('inspection-settings-modal');
  if (!m) return;
  const s = window.AppState?.settings || {};
  const dvirEl = document.getElementById('ins-dvir-accountability');
  const jsaEl = document.getElementById('ins-jsa-accountability');
  const citEl = document.getElementById('ins-enable-citations');
  if (dvirEl) dvirEl.checked = s.dvirAccountability === true;
  if (jsaEl) jsaEl.checked = s.jsaAccountability === true;
  if (citEl) citEl.checked = s.enableCitations !== false;
  _renderLsraRulesCheckboxes();
  // Reset expander to collapsed on open
  const btn = document.getElementById('ins-lsra-expander-btn');
  const list = document.getElementById('ins-lsra-rules-list');
  const chev = document.getElementById('ins-lsra-chevron');
  if (btn) btn.setAttribute('aria-expanded', 'false');
  if (list) list.classList.add('hidden');
  if (chev) chev.style.transform = 'rotate(0deg)';
  m.classList.remove('hidden');
};
window.closeInspectionSettingsModal = function() {
  const m = document.getElementById('inspection-settings-modal');
  if (m) m.classList.add('hidden');
};
window.saveInspectionSettings = async function() {
  const dvir = document.getElementById('ins-dvir-accountability')?.checked ?? false;
  const jsa = document.getElementById('ins-jsa-accountability')?.checked ?? false;
  const cit = document.getElementById('ins-enable-citations')?.checked ?? true;
  const lsraRules = {};
  document.querySelectorAll('#ins-lsra-rules-checkboxes input[data-lsra-rule-key]').forEach(input => {
    lsraRules[input.dataset.lsraRuleKey] = !!input.checked;
  });
  const ok = await _settingsPersist({
    dvirAccountability: dvir,
    jsaAccountability: jsa,
    enableCitations: cit,
    lsraRules,
  });
  if (ok) {
    showToast('Inspection settings saved', 'success');
    window.closeInspectionSettingsModal();
  }
};

// ── HR & Recruiting sub-modal ──
// v1.15.384 — expander helper for compliance-detail collapsibles inside the
// HR settings modal. Toggles aria-expanded on the button and the .hidden
// class on the controlled <div>. Public so inline onclick handlers can use it.
window._toggleHrExpander = function(btn) {
  if (!btn) return;
  const expanded = btn.getAttribute('aria-expanded') === 'true';
  btn.setAttribute('aria-expanded', expanded ? 'false' : 'true');
  const target = document.getElementById(btn.getAttribute('aria-controls'));
  if (target) target.classList.toggle('hidden', expanded);
};

// v1.15.384 — mark the modal footer as dirty so the "Unsaved changes"
// indicator appears. One-way (set-only) until next open.
function _hrMarkDirty() {
  const f = document.getElementById('hr-modal-footer');
  if (f) f.classList.add('is-dirty');
}

// v1.15.384 — render up to 3 question titles as preview chips, with an
// "+N more" chip when there are extras. Empty state shows a friendly note.
function _hrRenderQuestionChips(questions) {
  const wrap = document.getElementById('hr-application-questions-chips');
  if (!wrap) return;
  const active = (questions || []).filter(q => q && q.archived !== true);
  if (active.length === 0) {
    wrap.innerHTML = '<div class="hr-question-chips-empty">No custom questions yet — defaults only.</div>';
    return;
  }
  const esc = window._appqEsc || (s => String(s || '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c])));
  const chips = active.slice(0, 3).map(q => {
    // TASK-1467 — `prompt` is the schema field (see the object literal in the
    // add handler). label/question/text have never existed on these objects, so
    // this chain always fell through and EVERY chip rendered the literal string
    // "Question". Aliases kept as a tail in case an older hand-seeded doc used
    // one. Precedent: candidate detail already reads a.prompt.
    const label = q.prompt || q.label || q.question || q.text || 'Question';
    return `<span class="hr-question-chip">${esc(label)}</span>`;
  });
  if (active.length > 3) {
    chips.push(`<span class="hr-question-chip hr-question-chip-more">+${active.length - 3} more</span>`);
  }
  wrap.innerHTML = chips.join('');
}

window.openHrRecruitingSettingsModal = function() {
  const m = document.getElementById('hr-recruiting-settings-modal');
  if (!m) return;
  const s = window.AppState?.settings || {};
  const oshaEl = document.getElementById('hr-track-osha-records');
  const oshaGroupEl = document.getElementById('hr-records-custodian-group');
  const oshaSelEl = document.getElementById('hr-records-custodian');
  const reqClearEl = document.getElementById('hr-require-clear-bgcheck');
  const flowEl = document.getElementById('hr-application-flow');
  const oshaOn = s.trackOshaRecords === true;
  if (oshaEl) oshaEl.checked = oshaOn;
  if (oshaGroupEl) oshaGroupEl.style.display = oshaOn ? 'block' : 'none';
  if (oshaSelEl) {
    const peopleList = (window.AppState?.people || []).slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    oshaSelEl.innerHTML = '<option value="">— Select a person —</option>' +
      peopleList.map(p => `<option value="${p.id}">${escapeHtml ? escapeHtml(p.name || p.id) : (p.name || p.id)}</option>`).join('');
    oshaSelEl.value = s.recordsCustodianPersonId || '';
  }
  if (oshaEl && oshaGroupEl) {
    // Toggle visibility on change. Use a fresh listener each open via { once: true }.
    const onToggle = () => { oshaGroupEl.style.display = oshaEl.checked ? 'block' : 'none'; };
    oshaEl.addEventListener('change', onToggle, { once: true });
  }
  if (reqClearEl) reqClearEl.checked = s.requireClearBgCheckBeforeRoster === true;
  // TASK-1093 — "Disable manual background-check status entry" master switch
  // (Crown/Kolson 2026-07-09). Stored at settings.compliance.disableManualBgStatus.
  // Card injected dynamically after the Require-Clear card (static modal markup
  // lives in index.html, which this change doesn't touch). Suppresses the
  // friction-gated "Set status manually" paths in HR + the partner portal and is
  // enforced server-side in _applyReportStatusOverride. Default OFF.
  {
    let dmbEl = document.getElementById('hr-disable-manual-bg');
    if (!dmbEl) {
      const anchorCard = reqClearEl?.closest('.hr-setting-card');
      if (anchorCard) {
        const card = document.createElement('div');
        card.className = 'hr-setting-card';
        card.innerHTML = `
          <div class="hr-setting-card-header">
            <input type="checkbox" id="hr-disable-manual-bg">
            <span class="hr-setting-card-icon"><span class="material-icons">lock</span></span>
            <div style="flex:1;min-width:0;">
              <label for="hr-disable-manual-bg" class="hr-setting-card-title">Disable manual background-check status entry (require a real report or upload)</label>
              <p class="hr-setting-card-summary">Removes the "Set status manually" escape hatch on MVR / PSP background reports — for your team and your compliance partner. Statuses can then only come from a real Checkr run or an uploaded copy of the actual report. Enforced server-side, not just hidden.</p>
            </div>
          </div>`;
        anchorCard.insertAdjacentElement('afterend', card);
        dmbEl = document.getElementById('hr-disable-manual-bg');
      }
    }
    if (dmbEl) dmbEl.checked = s.compliance?.disableManualBgStatus === true;
  }
  // TASK-650 — stale-application cleanup settings (settings.applicantExpiry.*).
  // Defaults: expire 30d (TASK-9699; was 45), purge OFF / 730d. Blank expire-days input shows the
  // placeholder (30) so an unset tenant reads as the default.
  {
    const ae = s.applicantExpiry || {};
    const expireEl = document.getElementById('hr-applicant-expire-days');
    if (expireEl) expireEl.value = (ae.expireDays != null) ? String(ae.expireDays) : '';
    const purgeEnEl = document.getElementById('hr-applicant-purge-enabled');
    if (purgeEnEl) purgeEnEl.checked = ae.purgeEnabled === true;
    const purgeDaysEl = document.getElementById('hr-applicant-purge-days');
    if (purgeDaysEl) purgeDaysEl.value = (ae.purgeDays != null) ? String(ae.purgeDays) : '';
    // TASK-9692 — applicant reminder email, DEFAULT OFF (only an explicit true is on).
    const nudgeEl = document.getElementById('hr-applicant-nudge-enabled');
    if (nudgeEl) nudgeEl.checked = ae.nudgeEnabled === true;
  }
  // TASK-261 (v1.15.515) — Accept Applications kill switch loads from
  // top-level `tenant.hiring.acceptingApplications` (NOT settings.*).
  // Default `true` — undefined or absent reads as accepting.
  const acceptingEl = document.getElementById('hr-accepting-applications');
  if (acceptingEl) {
    const td = window.AppState?.tenantData || {};
    acceptingEl.checked = td.hiring?.acceptingApplications !== false;
  }
  // TASK-467 — Enabled Application Types. Loads from top-level
  // `tenant.enabledApplicantPositionTypes`. Empty/absent = ALL THREE enabled
  // (no restriction — legacy default), so all three checkboxes check.
  {
    const td = window.AppState?.tenantData || {};
    const valid = ['cdl', 'non-cdl-driver', 'drives-no-cmv', 'non-driver']; // TASK-9696
    const raw = Array.isArray(td.enabledApplicantPositionTypes)
      ? td.enabledApplicantPositionTypes.filter(t => valid.includes(t))
      : [];
    const effective = raw.length > 0 ? raw : valid;
    valid.forEach(t => {
      const cb = document.getElementById(`hr-enabled-app-type-${t}`);
      if (cb) cb.checked = effective.includes(t);
    });
    const errEl = document.getElementById('hr-enabled-app-types-error');
    if (errEl) { errEl.style.display = 'none'; errEl.textContent = ''; }
  }
  // TASK-261 Slice 1B (2026-05-28) — Apply page custom description loads from
  // top-level `tenant.hiring.applyHeroDescription`. Plain text — render side
  // on apply.html uses CSS white-space:pre-wrap + textContent so newlines
  // display naturally with zero HTML interpretation. URLs auto-linked.
  const heroEl = document.getElementById('hr-apply-hero-html');
  if (heroEl) {
    const td = window.AppState?.tenantData || {};
    heroEl.value = (td.hiring?.applyHeroDescription || '').toString();
  }
  // v1.15.384 — flow now driven by radio buttons; hidden <select> stays in
  // DOM so the existing save flow (and any older callers) can read .value.
  const flowVal = s.applicationFlow === 'two_stage' ? 'two_stage' : 'one_stage';
  if (flowEl) flowEl.value = flowVal;
  document.querySelectorAll('input[name="hr-application-flow"]').forEach(r => {
    r.checked = (r.value === flowVal);
    // Wire the radio→select mirror once per modal open.
    r.onchange = (e) => {
      if (flowEl) flowEl.value = e.target.value;
      _hrMarkDirty();
    };
  });
  // Reset dirty state on each open, then wire change listeners on inputs.
  const footer = document.getElementById('hr-modal-footer');
  if (footer) footer.classList.remove('is-dirty');
  ['hr-track-osha-records', 'hr-records-custodian', 'hr-require-clear-bgcheck',
   'hr-disable-manual-bg',
   'hr-application-notify-email', 'hr-application-contact-phone',
   'hr-notify-bg-ordered', 'hr-notify-bg-completed',
   'hr-accepting-applications', 'hr-apply-hero-html',
   'hr-applicant-expire-days', 'hr-applicant-purge-enabled', 'hr-applicant-purge-days',
   'hr-enabled-app-type-cdl', 'hr-enabled-app-type-non-cdl-driver', 'hr-enabled-app-type-drives-no-cmv', 'hr-enabled-app-type-non-driver'].forEach(id => {
    const el = document.getElementById(id);
    if (el) el.onchange = _hrMarkDirty;
    if (el && (el.type === 'email' || el.type === 'tel' || el.type === 'text' || el.type === 'number' || el.tagName === 'TEXTAREA')) el.oninput = _hrMarkDirty;
  });
  // v1.15.366+ — moved from Primary Settings (apps notify email + contact phone).
  const notifyEmailEl = document.getElementById('hr-application-notify-email');
  if (notifyEmailEl) notifyEmailEl.value = s.applicationNotifyEmail || '';
  const contactPhoneEl = document.getElementById('hr-application-contact-phone');
  if (contactPhoneEl) contactPhoneEl.value = s.applicationContactPhone || '';
  // v1.15.381+ — bg-check notification toggles. Completion default ON
  // (preserves opt-out semantics — flag stored as false when carrier opts
  // out). Ordered default OFF (opt-in).
  const orderedEl = document.getElementById('hr-notify-bg-ordered');
  if (orderedEl) orderedEl.checked = s.notifyOnBgCheckOrdered === true;
  const completedEl = document.getElementById('hr-notify-bg-completed');
  if (completedEl) completedEl.checked = s.notifyOnBgCheckCompletion !== false;
  // 2026-05-12 — MVR early-completion notification (default ON, opt-out semantics)
  const mvrEl = document.getElementById('hr-notify-mvr-complete');
  if (mvrEl) mvrEl.checked = s.notifyOnMvrComplete !== false;
  // BG Decision UI Phase 1+2 (2026-05-12) — tenant.compliance.notifiers[]
  // is a top-level tenant doc field (not under settings). Load from
  // tenantData so the textarea pre-fills with whatever's stored.
  const notifiersEl = document.getElementById('hr-compliance-notifiers');
  if (notifiersEl) {
    const td = window.AppState?.tenantData || {};
    const arr = Array.isArray(td.compliance?.notifiers) ? td.compliance.notifiers : [];
    notifiersEl.value = arr.join('\n');
  }
  const notifierErrEl = document.getElementById('hr-compliance-notifiers-error');
  if (notifierErrEl) { notifierErrEl.style.display = 'none'; notifierErrEl.textContent = ''; }

  // TASK-DA-v1 slice 4 — load DER (Designated Employer Representative) from
  // settings.dABuilder.der. Per §382.601(b)(1) each carrier must identify
  // the person drivers contact with policy questions. Surfaced on the
  // incumbent receipt page + audit packet manifest.
  const derSettings = (s.dABuilder?.der) || {};
  const derNameEl = document.getElementById('hr-der-name');
  const derPhoneEl = document.getElementById('hr-der-phone');
  const derEmailEl = document.getElementById('hr-der-email');
  const derAlternateEl = document.getElementById('hr-der-alternate');
  if (derNameEl) derNameEl.value = derSettings.name || '';
  if (derPhoneEl) derPhoneEl.value = derSettings.phone || '';
  if (derEmailEl) derEmailEl.value = derSettings.email || '';
  if (derAlternateEl) derAlternateEl.value = derSettings.alternate || '';
  // Wire dirty state on DER inputs
  ['hr-der-name', 'hr-der-phone', 'hr-der-email', 'hr-der-alternate'].forEach(id => {
    const el = document.getElementById(id);
    if (el) el.oninput = _hrMarkDirty;
  });
  // Update questions count + chip preview (v1.15.384 — chips added)
  const qs = Array.isArray(s.applicationQuestions) ? s.applicationQuestions : [];
  _hrRenderQuestionChips(qs);
  _hrRenderJobOpeningChips(); // TASK-4725
  const qsCountEl = document.getElementById('hr-application-questions-count');
  if (qsCountEl) {
    const active = qs.filter(q => q && q.archived !== true).length;
    qsCountEl.textContent = `(${active} active)`;
  }
  // Update hiring packet count (TASK-221 Slice 1) — falls back to 5 (built-in
  // default count) when settings.hiringPacket is unset.
  const hpCountEl = document.getElementById('hr-hiring-packet-count');
  if (hpCountEl) {
    const hp = Array.isArray(s.hiringPacket) && s.hiringPacket.length ? s.hiringPacket : null;
    const active = hp ? hp.filter(i => i && i.archived !== true).length : 5;
    hpCountEl.textContent = `(${active} active)`;
  }
  m.classList.remove('hidden');
};
window.closeHrRecruitingSettingsModal = function() {
  const m = document.getElementById('hr-recruiting-settings-modal');
  if (m) m.classList.add('hidden');
};
window.saveHrRecruitingSettings = async function() {
  const trackOsha = document.getElementById('hr-track-osha-records')?.checked ?? false;
  const custodian = document.getElementById('hr-records-custodian')?.value?.trim() || null;
  const reqClear = document.getElementById('hr-require-clear-bgcheck')?.checked ?? false;
  // TASK-1093 — manual BG-status master switch (settings.compliance.disableManualBgStatus)
  const disableManualBg = document.getElementById('hr-disable-manual-bg')?.checked ?? false;
  const flowRaw = document.getElementById('hr-application-flow')?.value;
  const flow = flowRaw === 'two_stage' ? 'two_stage' : 'one_stage';
  // OSHA 1910.1020(g)(1)(i) — Records Custodian must be designated when
  // tracking is on (preserved from old saveSettings validation).
  if (trackOsha && !custodian) {
    showToast('Pick a Records Custodian — required by OSHA 1910.1020 when records access tracking is on', 'error');
    document.getElementById('hr-records-custodian')?.focus();
    return;
  }
  // v1.15.366+ — application notify email + contact phone moved from
  // Primary Settings to here. Same canonical write path
  // (settings.applicationNotifyEmail / .applicationContactPhone) so the
  // backend bug fix (functions/src/index.js:18984) reads correctly.
  const notifyEmailRaw = document.getElementById('hr-application-notify-email')?.value?.trim() || '';
  const notifyEmail = notifyEmailRaw || null;
  const contactPhoneRaw = document.getElementById('hr-application-contact-phone')?.value?.trim() || '';
  const contactPhone = contactPhoneRaw || null;
  // Light email validation — allow blank (= use ownerEmail) but block obvious typos.
  if (notifyEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(notifyEmail)) {
    showToast('Application Notification Email looks invalid', 'error');
    document.getElementById('hr-application-notify-email')?.focus();
    return;
  }
  // v1.15.381+ — bg-check notify toggles
  const notifyOrdered = document.getElementById('hr-notify-bg-ordered')?.checked ?? false;
  const notifyCompleted = document.getElementById('hr-notify-bg-completed')?.checked ?? true;
  const notifyMvrComplete = document.getElementById('hr-notify-mvr-complete')?.checked ?? true;

  // BG Decision UI Phase 1+2 (2026-05-12) — parse + validate notifier list.
  // Top-level tenant.compliance.notifiers[] (NOT under settings.*).
  const notifiersRaw = document.getElementById('hr-compliance-notifiers')?.value || '';
  const notifierErrEl = document.getElementById('hr-compliance-notifiers-error');
  if (notifierErrEl) { notifierErrEl.style.display = 'none'; notifierErrEl.textContent = ''; }
  const lines = notifiersRaw.split(/[\r\n,]+/).map(l => l.trim()).filter(Boolean);
  const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const seen = new Set();
  const notifiers = [];
  for (const l of lines) {
    const lower = l.toLowerCase();
    if (!emailRe.test(lower)) {
      if (notifierErrEl) {
        notifierErrEl.textContent = `"${l}" doesn't look like a valid email address.`;
        notifierErrEl.style.display = 'block';
      }
      document.getElementById('hr-compliance-notifiers')?.focus();
      return;
    }
    if (seen.has(lower)) continue;
    seen.add(lower);
    notifiers.push(lower);
    if (notifiers.length > 20) {
      if (notifierErrEl) {
        notifierErrEl.textContent = 'Max 20 recipient addresses. Trim the list.';
        notifierErrEl.style.display = 'block';
      }
      return;
    }
  }

  // TASK-DA-v1 slice 4 — collect DER fields (§382.601(b)(1)). Validate email
  // shape if provided. Stored under settings.dABuilder.der.
  const derName = (document.getElementById('hr-der-name')?.value || '').trim().slice(0, 120);
  const derPhone = (document.getElementById('hr-der-phone')?.value || '').trim().slice(0, 40);
  const derEmail = (document.getElementById('hr-der-email')?.value || '').trim().slice(0, 200);
  const derAlternate = (document.getElementById('hr-der-alternate')?.value || '').trim().slice(0, 200);
  if (derEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(derEmail)) {
    showToast('DER Email looks invalid', 'error');
    document.getElementById('hr-der-email')?.focus();
    return;
  }

  // TASK-650 — stale-application cleanup settings. Parsed + clamped here, then
  // written as DOTTED settings.applicantExpiry.* keys so we don't clobber any
  // sibling fields (e.g. warnDaysBefore/warnEnabled) the scheduled job reads.
  // Auto-purge defaults OFF (consumer-report retention floor unconfirmed).
  const expireRaw = (document.getElementById('hr-applicant-expire-days')?.value || '').trim();
  // TASK-9699 — default 30 (was 45; Crown 10/7). Keep in step with
  // DEFAULT_EXPIRE_DAYS in functions/src/scheduled/expireStaleApplicants.js.
  let expireDays = expireRaw === '' ? 30 : parseInt(expireRaw, 10);
  if (!Number.isFinite(expireDays) || expireDays < 0) expireDays = 30;
  if (expireDays > 3650) expireDays = 3650;
  const purgeEnabled = document.getElementById('hr-applicant-purge-enabled')?.checked === true;
  const purgeRaw = (document.getElementById('hr-applicant-purge-days')?.value || '').trim();
  let purgeDays = purgeRaw === '' ? 730 : parseInt(purgeRaw, 10);
  if (!Number.isFinite(purgeDays) || purgeDays < 1) purgeDays = 730;
  if (purgeDays > 7300) purgeDays = 7300;
  const nudgeEnabled = document.getElementById('hr-applicant-nudge-enabled')?.checked === true; // TASK-9692

  const ok = await _settingsPersist({
    trackOshaRecords: trackOsha,
    recordsCustodianPersonId: custodian,
    requireClearBgCheckBeforeRoster: reqClear,
    // TASK-1093 — dotted key so sibling settings.compliance.* fields survive.
    'compliance.disableManualBgStatus': disableManualBg,
    applicationFlow: flow,
    'applicantExpiry.expireDays': expireDays,
    'applicantExpiry.purgeEnabled': purgeEnabled,
    'applicantExpiry.purgeDays': purgeDays,
    'applicantExpiry.nudgeEnabled': nudgeEnabled, // TASK-9692
    applicationNotifyEmail: notifyEmail,
    applicationContactPhone: contactPhone,
    notifyOnBgCheckOrdered: notifyOrdered,
    notifyOnBgCheckCompletion: notifyCompleted,  // existing opt-out flag
    notifyOnMvrComplete: notifyMvrComplete,      // 2026-05-12 opt-out (default ON)
    // TASK-DA-v1 slice 4 — DER persisted under settings.dABuilder.der
    'dABuilder.der': {
      name: derName || null,
      phone: derPhone || null,
      email: derEmail || null,
      alternate: derAlternate || null,
    },
  });
  // TASK-650 — _settingsPersist Object.assigns the DOTTED keys flat onto the
  // local settings cache, so mirror them into the nested applicantExpiry object
  // too (otherwise an immediate modal re-open would read the stale nested copy).
  if (ok) {
    if (!window.AppState.settings) window.AppState.settings = {};
    const ae = window.AppState.settings.applicantExpiry = window.AppState.settings.applicantExpiry || {};
    ae.expireDays = expireDays;
    ae.purgeEnabled = purgeEnabled;
    ae.purgeDays = purgeDays;
    ae.nudgeEnabled = nudgeEnabled; // TASK-9692
    // TASK-1093 — mirror the dotted compliance.disableManualBgStatus key into the
    // nested local cache so hr.js (_manualBgStatusDisabled) and a modal re-open
    // read the fresh value without a reload.
    const comp = window.AppState.settings.compliance = window.AppState.settings.compliance || {};
    comp.disableManualBgStatus = disableManualBg;
  }
  // Top-level tenant doc write for compliance.notifiers[] (separate from
  // settings.* fields handled by _settingsPersist).
  if (ok && window.AppState?.tenant) {
    try {
      const tenantRef = doc(db, 'tenants', window.AppState.tenant);
      await updateDoc(tenantRef, { 'compliance.notifiers': notifiers });
      if (!window.AppState.tenantData) window.AppState.tenantData = {};
      if (!window.AppState.tenantData.compliance) window.AppState.tenantData.compliance = {};
      window.AppState.tenantData.compliance.notifiers = notifiers;
    } catch (e) {
      console.error('[hr-settings] notifier persist failed', e);
      showToast('Settings saved, but notifier list failed: ' + (e?.message || 'unknown'), 'error');
    }
  }

  // TASK-261 Slice 1B (2026-05-28) — Apply page custom description (plain text).
  // Top-level `tenant.hiring.applyHeroDescription` write. 4000-char hard cap
  // (matches HTML maxlength + server-side clamp in getApplyTenantInfo). Stored
  // as plain text; apply.html renders via textContent + CSS pre-wrap with URL
  // auto-linking. Zero HTML interpretation, zero XSS surface.
  if (ok && window.AppState?.tenant) {
    try {
      const heroEl = document.getElementById('hr-apply-hero-html');
      if (heroEl) {
        const rawVal = (heroEl.value || '').toString().slice(0, 4000);
        const td = window.AppState.tenantData || {};
        const oldVal = (td.hiring?.applyHeroDescription || '').toString();
        if (rawVal !== oldVal) {
          const tenantRef = doc(db, 'tenants', window.AppState.tenant);
          await updateDoc(tenantRef, { 'hiring.applyHeroDescription': rawVal || null });
          if (!window.AppState.tenantData) window.AppState.tenantData = {};
          if (!window.AppState.tenantData.hiring) window.AppState.tenantData.hiring = {};
          window.AppState.tenantData.hiring.applyHeroDescription = rawVal;
          try {
            // TASK-1495 — routed through the shared writer. window.logAudit stamps
            // category/action/timestamp/user and strips undefined (TASK-1479) before the
            // write, so a conditionally-undefined field can no longer throw the whole row
            // away. `ts` and `module` stay in the payload so existing consumers of those
            // keys are unaffected; do NOT pass `timestamp` — the payload spread runs after
            // logAudit's stamp and would override it.
            await window.logAudit?.('hr', 'APPLY_HERO_DESCRIPTION_UPDATED', {
              ts: Timestamp.now(),
              module: 'hr',
              entityType: 'tenant',
              entityId: window.AppState.tenant,
              before: { length: oldVal.length },
              after: { length: rawVal.length },
              source: 'carrier_hr_settings',
              uid: window.AppState?.user?.email || window.AppState?.user?.uid || null,
            });
          } catch (auditErr) {
            console.warn('[hr-settings] applyHeroDescription audit log skipped:', auditErr?.message);
          }
        }
      }
    } catch (e) {
      console.error('[hr-settings] applyHeroDescription persist failed', e);
      showToast('Settings saved, but Apply Description failed: ' + (e?.message || 'unknown'), 'error');
    }
  }

  // TASK-261 (v1.15.515) — Accept Applications kill switch. Top-level
  // `tenant.hiring.acceptingApplications` write (NOT under settings.*).
  // Audit-log on change so tech support can answer "who closed apps?".
  if (ok && window.AppState?.tenant) {
    try {
      const acceptingEl = document.getElementById('hr-accepting-applications');
      if (acceptingEl) {
        const newVal = acceptingEl.checked === true;
        const td = window.AppState.tenantData || {};
        const oldVal = td.hiring?.acceptingApplications !== false; // default true
        if (newVal !== oldVal) {
          const tenantRef = doc(db, 'tenants', window.AppState.tenant);
          await updateDoc(tenantRef, { 'hiring.acceptingApplications': newVal });
          if (!window.AppState.tenantData) window.AppState.tenantData = {};
          if (!window.AppState.tenantData.hiring) window.AppState.tenantData.hiring = {};
          window.AppState.tenantData.hiring.acceptingApplications = newVal;
          // Mirror the partner-handler audit shape for cross-surface parity.
          try {
            // TASK-1495 — routed through the shared writer. window.logAudit stamps
            // category/action/timestamp/user and strips undefined (TASK-1479) before the
            // write, so a conditionally-undefined field can no longer throw the whole row
            // away. `ts` and `module` stay in the payload so existing consumers of those
            // keys are unaffected; do NOT pass `timestamp` — the payload spread runs after
            // logAudit's stamp and would override it.
            await window.logAudit?.('hr', 'HIRING_ACCEPTING_APPLICATIONS_TOGGLED', {
              ts: Timestamp.now(),
              module: 'hr',
              entityType: 'tenant',
              entityId: window.AppState.tenant,
              before: { acceptingApplications: oldVal },
              after: { acceptingApplications: newVal },
              source: 'carrier_hr_settings',
              uid: window.AppState?.user?.email || window.AppState?.user?.uid || null,
            });
          } catch (auditErr) {
            console.warn('[hr-settings] acceptingApplications audit log skipped:', auditErr?.message);
          }
        }
      }
    } catch (e) {
      console.error('[hr-settings] acceptingApplications persist failed', e);
      showToast('Settings saved, but Accept Applications toggle failed: ' + (e?.message || 'unknown'), 'error');
    }
  }

  // TASK-467 — Enabled Application Types. Top-level
  // `tenant.enabledApplicantPositionTypes` write. The public apply page only
  // offers the enabled type(s); saveDriverApplication re-validates the
  // submitted type against this set server-side (the real enforcement).
  // At least one type must stay enabled — unchecking all is what the Accept
  // Applications kill switch is for. Storing all three === no restriction.
  if (ok && window.AppState?.tenant) {
    try {
      const valid = ['cdl', 'non-cdl-driver', 'drives-no-cmv', 'non-driver']; // TASK-9696
      const selected = valid.filter(t => document.getElementById(`hr-enabled-app-type-${t}`)?.checked);
      const errEl = document.getElementById('hr-enabled-app-types-error');
      if (selected.length === 0) {
        if (errEl) {
          errEl.textContent = 'Enable at least one application type. To stop all applications, turn off "Accept new applications" above.';
          errEl.style.display = 'block';
        }
        showToast('Enable at least one application type', 'error');
        return; // abort close — don't persist an empty set
      }
      const td = window.AppState.tenantData || {};
      const oldArr = Array.isArray(td.enabledApplicantPositionTypes)
        ? td.enabledApplicantPositionTypes.filter(t => valid.includes(t))
        : [];
      // Normalize both to canonical order for a stable change-compare.
      const newCanon = valid.filter(t => selected.includes(t));
      const oldCanon = valid.filter(t => oldArr.includes(t));
      if (newCanon.join(',') !== oldCanon.join(',')) {
        const tenantRef = doc(db, 'tenants', window.AppState.tenant);
        await updateDoc(tenantRef, { enabledApplicantPositionTypes: newCanon });
        if (!window.AppState.tenantData) window.AppState.tenantData = {};
        window.AppState.tenantData.enabledApplicantPositionTypes = newCanon;
        try {
          // TASK-1495 — routed through the shared writer. window.logAudit stamps
          // category/action/timestamp/user and strips undefined (TASK-1479) before the
          // write, so a conditionally-undefined field can no longer throw the whole row
          // away. `ts` and `module` stay in the payload so existing consumers of those
          // keys are unaffected; do NOT pass `timestamp` — the payload spread runs after
          // logAudit's stamp and would override it.
          await window.logAudit?.('hr', 'ENABLED_APPLICATION_TYPES_UPDATED', {
            ts: Timestamp.now(),
            module: 'hr',
            entityType: 'tenant',
            entityId: window.AppState.tenant,
            before: { types: oldCanon },
            after: { types: newCanon },
            source: 'carrier_hr_settings',
            uid: window.AppState?.user?.email || window.AppState?.user?.uid || null,
          });
        } catch (auditErr) {
          console.warn('[hr-settings] enabledApplicantPositionTypes audit log skipped:', auditErr?.message);
        }
      }
    } catch (e) {
      console.error('[hr-settings] enabledApplicantPositionTypes persist failed', e);
      showToast('Settings saved, but Application Types failed: ' + (e?.message || 'unknown'), 'error');
    }
  }

  if (ok) {
    showToast('HR & Recruiting settings saved', 'success');
    window.closeHrRecruitingSettingsModal();
  }
};

// ============================================
// SIGNUP FLOW
// ============================================
const PRICING = {
  base: PLAN_PRICING.monthlyDollars,        // TASK-9812 — checkout plan ids
  base_annual: PLAN_PRICING.annualDollars,
  dispatch: 250,      // $250/mo — single plan
  pro: 250,           // $250/mo — single plan (legacy compat)
  proPlus: 250,       // $250/mo — single plan (legacy compat)
  vehicleBlock: 25,   // +$25/mo per additional 50 vehicles
  smsBlock: 25        // $25 per SMS credit block
};

function goToSignupStep1() {
  document.getElementById('signup-form').classList.add('hidden');
  document.getElementById('auth-tabs')?.classList.add('hidden');
  document.getElementById('signup-step-1').classList.remove('hidden');
}

function goToSignupStep2() {
  // Get add-on selections
  const hasSms = document.getElementById('addon-sms')?.checked || false;

  // Update hidden inputs — Smart Tools always included
  document.getElementById('signup-addon-ai').value = 'true';
  document.getElementById('signup-addon-sms').value = hasSms ? 'true' : 'false';

  // Calculate total
  let monthlyTotal = PRICING.dispatch;

  // Build display
  const priceDisplay = document.getElementById('selected-plan-price');
  const addonsDisplay = document.getElementById('selected-addons-display');

  if (priceDisplay) {
    let priceText = `$${monthlyTotal}/mo`;
    if (hasSms) priceText += ` + $${PRICING.smsBlock}`;
    priceDisplay.textContent = priceText;
  }

  if (addonsDisplay) {
    addonsDisplay.textContent = hasSms ? '+ SMS Credits' : '';
  }

  // Show step 2
  document.getElementById('signup-step-1').classList.add('hidden');
  document.getElementById('signup-form').classList.remove('hidden');
}

function toggleAddon(type) {
  const checkbox = document.getElementById(`addon-${type}`);
  if (checkbox) {
    checkbox.checked = !checkbox.checked;
    updatePlanTotal();
  }
}

function updatePlanTotal() {
  // Get add-on selections — Smart Tools always included
  const hasSms = document.getElementById('addon-sms')?.checked || false;

  // Calculate total
  let monthlyTotal = PRICING.dispatch;

  // Update total display
  const totalEl = document.getElementById('plan-total');
  const extraEl = document.getElementById('plan-total-extra');
  if (totalEl) {
    totalEl.textContent = `$${monthlyTotal}/mo`;
  }
  if (extraEl) {
    extraEl.textContent = hasSms ? ` + $${PRICING.smsBlock} today` : '';
  }

  // Update visual styling on addon cards
  const smsCard = document.getElementById('addon-sms-card');
  const smsCheck = document.getElementById('addon-sms-check');

  if (smsCard && smsCheck) {
    smsCard.style.borderColor = hasSms ? 'var(--brand-cyan)' : 'var(--border-medium)';
    smsCheck.style.color = hasSms ? 'var(--brand-cyan)' : 'var(--border-medium)';
  }
}

window.toggleAddon = toggleAddon;

// Expose signup functions globally
window.goToSignupStep1 = goToSignupStep1;
window.goToSignupStep2 = goToSignupStep2;
window.updatePlanTotal = updatePlanTotal;

// ============================================
// SALES AGENT (Demo Mode Only)
// ============================================
const SALES_INTRO = `Howdy! I'm Dusty, your DispatchDeck guide.

What kind of fleet you runnin' and what's your biggest headache? I'll show you how we can help.`;

let salesChatHistory = [];

function showSalesAgent() {
  if (!window.AppState?.isDemo) return;

  // Create or show the sales modal
  let modal = document.getElementById('modal-sales-agent');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'modal-sales-agent';
    modal.className = 'modal hidden';
    modal.innerHTML = `
      <div class="modal-content sales-agent-modal">
        <div class="modal-header sales-header">
          <h3><span class="material-icons" style="vertical-align:middle;margin-right:8px;">support_agent</span>Talk to Sales</h3>
          <button class="btn-icon" onclick="closeSalesAgent()">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="modal-body sales-chat-body">
          <div class="sales-chat-window" id="sales-chat-window">
            <div class="ai-message sales-intro">
              <div class="ai-avatar sales-avatar">
                <span class="material-icons">face</span>
              </div>
              <div class="ai-bubble sales-bubble"></div>
            </div>
          </div>
          <div class="sales-input-area">
            <textarea id="sales-input" rows="3" placeholder="Tell me about your operation..." onkeydown="if(event.key==='Enter'&&!event.shiftKey){event.preventDefault();sendSalesMessage();}"></textarea>
            <button class="btn btn-primary sales-send-btn" onclick="sendSalesMessage()" id="sales-submit">
              <span class="material-icons">send</span>
            </button>
          </div>
        </div>
        <div class="sales-lead-section">
          <button class="sales-lead-toggle" onclick="toggleSalesLeadForm()">
            <span class="material-icons" id="sales-lead-chevron">expand_more</span>
            <span>Or have a representative contact you</span>
          </button>
          <div id="sales-lead-form-wrap" class="sales-lead-form-wrap" style="display:none;">
            <form id="sales-modal-lead-form" class="sales-modal-lead-form" novalidate onsubmit="return submitSalesModalLead(event)">
              <input type="text" name="website" style="display:none" tabindex="-1" autocomplete="off">
              <div class="sales-lead-row">
                <div class="sales-lead-field">
                  <label>Your name *</label>
                  <input type="text" id="sml-name" required placeholder="John Smith" autocomplete="name" data-maxlen="100">
                </div>
                <div class="sales-lead-field">
                  <label>Phone number *</label>
                  <input type="tel" id="sml-phone" required placeholder="(555) 123-4567" autocomplete="tel">
                </div>
              </div>
              <div class="sales-lead-row">
                <div class="sales-lead-field">
                  <label>Email</label>
                  <input type="email" id="sml-email" placeholder="john@company.com" autocomplete="email">
                </div>
                <div class="sales-lead-field">
                  <label>Company name</label>
                  <input type="text" id="sml-company" placeholder="ABC Trucking" autocomplete="organization">
                </div>
              </div>
              <div class="sales-lead-row">
                <div class="sales-lead-field">
                  <label>How many vehicles?</label>
                  <select id="sml-vehicles">
                    <option value="">Select...</option>
                    <option value="1-5">1–5</option>
                    <option value="6-15">6–15</option>
                    <option value="16-30">16–30</option>
                    <option value="30+">30+</option>
                  </select>
                </div>
                <div class="sales-lead-field">
                  <label>Best time to reach you *</label>
                  <select id="sml-time" required>
                    <option value="">Select...</option>
                    <option value="Early Morning (7-9 AM CT)">Early Morning (7–9 AM CT)</option>
                    <option value="Morning (9-11 AM CT)">Morning (9–11 AM CT)</option>
                    <option value="Midday (11 AM-1 PM CT)">Midday (11 AM–1 PM CT)</option>
                    <option value="Afternoon (1-3 PM CT)">Afternoon (1–3 PM CT)</option>
                    <option value="Late Afternoon (3-5 PM CT)">Late Afternoon (3–5 PM CT)</option>
                  </select>
                </div>
              </div>
              <button type="submit" class="btn btn-primary sales-lead-submit" id="sml-submit-btn">
                <span class="material-icons" style="font-size:18px;vertical-align:middle;margin-right:6px;">phone_callback</span>
                Request a Callback
              </button>
              <p class="sales-lead-reassurance">We'll contact you within one business day.</p>
              <p style="text-align:center;margin-top:10px;"><a href="https://calendar.app.google/HsckDButgQUweG4d7" target="_blank" rel="noopener" style="color:var(--brand-blue);font-size:0.85rem;text-decoration:underline;" onclick="if(typeof gtag==='function')gtag('event','landing_cta_click',{cta_type:'schedule',cta_location:'sales-bot'})">Or schedule a call on our calendar</a></p>
            </form>
            <div id="sml-success" class="sales-lead-success" style="display:none;">
              <span class="material-icons" style="font-size:40px;color:#10B981;">check_circle</span>
              <h4>We got your info!</h4>
              <p>One of our team members will reach out at your preferred time.</p>
              <p style="margin-top:8px;"><a href="https://calendar.app.google/HsckDButgQUweG4d7" target="_blank" rel="noopener" style="color:var(--brand-blue);font-weight:600;text-decoration:underline;">Or schedule a specific time on our calendar</a></p>
            </div>
          </div>
        </div>
      </div>
    `;
    document.body.appendChild(modal);
  }

  modal.classList.remove('hidden');
  salesChatHistory = [];

  // Type out the intro message
  const introBubble = modal.querySelector('.sales-intro .sales-bubble');
  if (introBubble) {
    introBubble.innerHTML = '';
    typeOutMessage(introBubble, SALES_INTRO, 15);
  }

  // Focus input after intro
  setTimeout(() => {
    document.getElementById('sales-input')?.focus();
  }, SALES_INTRO.length * 15 + 500);
}

function typeOutMessage(element, text, speed = 20) {
  let i = 0;
  element.innerHTML = '';
  const cursor = document.createElement('span');
  cursor.className = 'sales-cursor';
  cursor.textContent = '|';
  element.appendChild(cursor);

  function type() {
    if (!cursor.parentNode) return;
    if (i < text.length) {
      const char = text.charAt(i);
      if (char === '\n') {
        element.insertBefore(document.createElement('br'), cursor);
      } else {
        element.insertBefore(document.createTextNode(char), cursor);
      }
      i++;
      setTimeout(type, speed);
    } else {
      cursor.remove();
    }
  }
  type();
}

function closeSalesAgent() {
  document.getElementById('modal-sales-agent')?.classList.add('hidden');
}

function toggleSalesLeadForm() {
  const wrap = document.getElementById('sales-lead-form-wrap');
  const chevron = document.getElementById('sales-lead-chevron');
  if (!wrap) return;
  const isHidden = wrap.style.display === 'none';
  wrap.style.display = isHidden ? 'block' : 'none';
  if (chevron) chevron.textContent = isHidden ? 'expand_less' : 'expand_more';
  if (isHidden) {
    wrap.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    if (typeof gtag === 'function') {
      gtag('event', 'sales_lead_form_opened', { source: 'sales-bot-modal' });
    }
  }
}

async function submitSalesModalLead(e) {
  e.preventDefault();
  if (!window.validateRequiredFields(e.target)) return;
  const btn = document.getElementById('sml-submit-btn');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Sending...'; }

  const data = {
    name: document.getElementById('sml-name')?.value?.trim(),
    phone: document.getElementById('sml-phone')?.value?.trim(),
    email: document.getElementById('sml-email')?.value?.trim(),
    company: document.getElementById('sml-company')?.value?.trim(),
    vehicleCount: document.getElementById('sml-vehicles')?.value,
    preferredTime: document.getElementById('sml-time')?.value,
    website: document.querySelector('#sales-modal-lead-form [name="website"]')?.value,
    source: 'demo-sales-modal',
    utm: getUtmData()
  };

  try {
    const resp = await fetch('https://us-central1-dispatch-deck-be88c.cloudfunctions.net/submitLeadCapture', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!resp.ok) throw new Error('Network error');
    document.getElementById('sales-modal-lead-form').style.display = 'none';
    document.getElementById('sml-success').style.display = 'block';
    if (typeof gtag === 'function') {
      gtag('event', 'lead_capture', { source: 'demo-sales-modal', company: data.company || 'N/A' });
    }
    showToast('Callback request sent!', 'success');
  } catch (err) {
    console.error('[Lead] Sales modal submit failed:', err);
    if (btn) { btn.disabled = false; btn.innerHTML = 'Request Callback'; }
    showToast('Something went wrong — please try again', 'error');
  }
  return false;
}

// ============================================
// LOGIN PAGE LEAD CAPTURE
// ============================================
function toggleLoginLeadForm() {
  const wrap = document.getElementById('login-lead-form-wrap');
  const chevron = document.getElementById('login-lead-chevron');
  if (!wrap) return;
  const isHidden = wrap.style.display === 'none';
  wrap.style.display = isHidden ? 'block' : 'none';
  if (chevron) chevron.textContent = isHidden ? 'expand_less' : 'expand_more';
  if (isHidden) {
    wrap.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    if (typeof gtag === 'function') {
      gtag('event', 'sales_lead_form_opened', { source: 'login-page' });
    }
  }
}

async function submitLoginLead(e) {
  e.preventDefault();
  const btn = document.getElementById('ll-submit-btn');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Sending...'; }

  const data = {
    name: document.getElementById('ll-name')?.value?.trim(),
    phone: document.getElementById('ll-phone')?.value?.trim(),
    email: document.getElementById('ll-email')?.value?.trim(),
    company: document.getElementById('ll-company')?.value?.trim(),
    vehicleCount: document.getElementById('ll-vehicles')?.value,
    preferredTime: document.getElementById('ll-time')?.value,
    website: document.querySelector('#login-lead-form [name="website"]')?.value,
    source: 'login-page',
    utm: getUtmData()
  };

  try {
    const resp = await fetch('https://us-central1-dispatch-deck-be88c.cloudfunctions.net/submitLeadCapture', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!resp.ok) throw new Error('Network error');
    document.getElementById('login-lead-form').style.display = 'none';
    document.getElementById('ll-success').style.display = 'block';
    if (typeof gtag === 'function') {
      gtag('event', 'lead_capture', { source: 'login-page', company: data.company || 'N/A' });
    }
    showToast('Callback request sent!', 'success');
  } catch (err) {
    console.error('[Lead] Login page submit failed:', err);
    if (btn) { btn.disabled = false; btn.innerHTML = 'Request Callback'; }
    showToast('Something went wrong — please try again', 'error');
  }
  return false;
}

window.toggleLoginLeadForm = toggleLoginLeadForm;
window.submitLoginLead = submitLoginLead;

// ============================================
// DEMO ENGAGEMENT POPUP (3-click trigger)
// ============================================
let demoClickCount = 0;

function trackDemoEngagement() {
  if (!window.AppState?.isDemo) return;
  if (sessionStorage.getItem('dd_demo_engagement_shown')) return;
  demoClickCount++;
  if (demoClickCount >= 3) {
    sessionStorage.setItem('dd_demo_engagement_shown', '1');
    setTimeout(() => showDemoEngagementPopup(), 500);
  }
}

// Demo-only popup markup (engagement popup + trial signup modal) loads on first
// use from views/demoPopups.js (perf budget: not in the cold load).
const _demoPopups = () => _lazyImportWithRetry(() => __ddChunkWrap(import('./views/demoPopups.js?v=2')), 'demoPopups');
function showDemoEngagementPopup() {
  _demoPopups().then(m => m.showDemoEngagementPopup(demoClickCount, getUtmData), (e) => console.warn('[Demo] engagement popup failed to load:', e?.message));
}

function closeDemoEngagement() {
  const modal = document.getElementById('modal-demo-engagement');
  if (modal) modal.remove();
}

async function sendSalesMessage() {
  const input = document.getElementById('sales-input');
  const chatWindow = document.getElementById('sales-chat-window');
  const submitBtn = document.getElementById('sales-submit');

  const message = input?.value?.trim();
  if (!message) return;

  // Check if we need email first (using same gate as AI help)
  const trailguideState = JSON.parse(localStorage.getItem('dispatchdeck_trailguide') || '{}');
  if (!trailguideState.aiEmailCollected) {
    showSalesEmailGate(() => {
      sendSalesMessage();
    });
    return;
  }

  // Add user message to chat
  const userMsg = document.createElement('div');
  userMsg.className = 'user-message';
  userMsg.innerHTML = `
    <div class="user-bubble">${escapeHtmlBasic(message)}</div>
    <div class="user-avatar">
      <span class="material-icons">person</span>
    </div>
  `;
  chatWindow.appendChild(userMsg);

  // Add to history
  salesChatHistory.push({ role: 'user', content: message });

  // Add thinking indicator
  const aiThinking = document.createElement('div');
  aiThinking.className = 'ai-message ai-thinking';
  aiThinking.innerHTML = `
    <div class="ai-avatar sales-avatar">
      <span class="material-icons">face</span>
    </div>
    <div class="ai-bubble sales-bubble">
      <span class="thinking-dots"><span>.</span><span>.</span><span>.</span></span>
      <span style="color:var(--text-muted);margin-left:8px;">Let me think on that...</span>
    </div>
  `;
  chatWindow.appendChild(aiThinking);
  chatWindow.scrollTop = chatWindow.scrollHeight;

  input.value = '';
  submitBtn.disabled = true;

  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) throw new Error('Not authenticated');

    const response = await fetch('https://us-central1-dispatch-deck-be88c.cloudfunctions.net/salesAgent', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        data: {
          message,
          history: salesChatHistory,
          email: trailguideState.demoEmail || null
        }
      })
    });

    const result = await response.json();
    aiThinking.remove();

    if (result.result?.ok && result.result?.response) {
      const aiResponse = document.createElement('div');
      aiResponse.className = 'ai-message';
      aiResponse.innerHTML = `
        <div class="ai-avatar sales-avatar">
          <span class="material-icons">face</span>
        </div>
        <div class="ai-bubble sales-bubble">${escapeHtmlBasic(result.result.response).replace(/\n/g, '<br>')}</div>
      `;
      chatWindow.appendChild(aiResponse);
      salesChatHistory.push({ role: 'assistant', content: result.result.response });

      // Check if we should show contact form
      if (result.result.showContactForm) {
        showSalesContactForm(chatWindow);
      }
    } else {
      throw new Error(result.error || 'No response');
    }
  } catch (error) {
    console.error('[Sales Agent] Error:', error);
    aiThinking.remove();

    const aiError = document.createElement('div');
    aiError.className = 'ai-message';
    aiError.innerHTML = `
      <div class="ai-avatar sales-avatar">
        <span class="material-icons">face</span>
      </div>
      <div class="ai-bubble sales-bubble" style="border-color:var(--status-warning);">Well shoot, hit a snag there partner. Mind tryin' again?</div>
    `;
    chatWindow.appendChild(aiError);
  } finally {
    submitBtn.disabled = false;
    chatWindow.scrollTop = chatWindow.scrollHeight;
  }
}

function showSalesEmailGate(callback) {
  const modal = document.createElement('div');
  modal.id = 'sales-email-gate';
  modal.className = 'trailguide-modal';
  modal.innerHTML = `
    <div class="trailguide-backdrop"></div>
    <div class="trailguide-modal-content" style="max-width:400px;">
      <h2 class="trailguide-title">🤠 Hold Up There, Partner</h2>
      <div class="trailguide-body">
        <p>Before we get to talkin' shop, I'll need your email address. This helps us keep the tumbleweeds and bots outta here, and lets my human colleagues follow up if you want a real conversation.</p>
        <input type="email" id="sales-email-input" class="form-control" placeholder="your@email.com" style="margin-top:16px;">
        <p id="sales-email-error" style="color:var(--status-danger);font-size:0.85em;margin-top:4px;display:none;">Please enter a valid email address.</p>
      </div>
      <div class="trailguide-actions" style="margin-top:16px;">
        <button class="btn btn-secondary" id="sales-email-cancel">Nevermind</button>
        <button class="btn btn-primary" id="sales-email-submit">Let's Talk</button>
      </div>
    </div>
  `;

  document.body.appendChild(modal);

  const emailInput = modal.querySelector('#sales-email-input');
  const errorEl = modal.querySelector('#sales-email-error');

  modal.querySelector('#sales-email-cancel').addEventListener('click', () => modal.remove());
  modal.querySelector('.trailguide-backdrop').addEventListener('click', () => modal.remove());

  modal.querySelector('#sales-email-submit').addEventListener('click', () => {
    const email = emailInput.value.trim();
    if (!email || !email.includes('@') || !email.includes('.')) {
      errorEl.style.display = 'block';
      emailInput.focus();
      return;
    }

    // Save to trailguide state (same as AI help)
    const state = JSON.parse(localStorage.getItem('dispatchdeck_trailguide') || '{}');
    state.aiEmailCollected = true;
    state.demoEmail = email;
    localStorage.setItem('dispatchdeck_trailguide', JSON.stringify(state));

    modal.remove();
    callback?.();
  });

  setTimeout(() => emailInput.focus(), 100);
}

function showSalesContactForm(chatWindow) {
  const formDiv = document.createElement('div');
  formDiv.className = 'sales-contact-form';
  formDiv.innerHTML = `
    <div class="contact-form-card">
      <h4>📞 Ready for a Real Conversation?</h4>
      <p>Leave your details and one of our humans will reach out faster than a jackrabbit on a hot griddle.</p>
      <input type="text" id="sales-contact-name" class="form-control" placeholder="Your name" style="margin-bottom:8px;">
      <input type="tel" id="sales-contact-phone" class="form-control" placeholder="Phone number (optional)" style="margin-bottom:8px;">
      <textarea id="sales-contact-notes" class="form-control" rows="2" placeholder="Best time to call? Any other notes?" style="margin-bottom:12px;"></textarea>
      <div style="display:flex;gap:8px;">
        <button class="btn btn-secondary" onclick="this.closest('.sales-contact-form').remove()">Maybe Later</button>
        <button class="btn btn-primary" onclick="submitSalesLead()">Request Callback</button>
      </div>
    </div>
  `;
  chatWindow.appendChild(formDiv);
  chatWindow.scrollTop = chatWindow.scrollHeight;
}

async function submitSalesLead() {
  const name = document.getElementById('sales-contact-name')?.value?.trim();
  const phone = document.getElementById('sales-contact-phone')?.value?.trim();
  const notes = document.getElementById('sales-contact-notes')?.value?.trim();
  const trailguideState = JSON.parse(localStorage.getItem('dispatchdeck_trailguide') || '{}');
  const email = trailguideState.demoEmail;

  if (!name) {
    showToast('Please enter your name', 'error');
    return;
  }

  try {
    const token = await auth.currentUser?.getIdToken();
    await fetch('https://us-central1-dispatch-deck-be88c.cloudfunctions.net/submitSalesLead', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        data: {
          name,
          email,
          phone,
          notes,
          chatHistory: salesChatHistory
        }
      })
    });

    // Remove form and show success
    document.querySelector('.sales-contact-form')?.remove();
    const chatWindow = document.getElementById('sales-chat-window');
    const successMsg = document.createElement('div');
    successMsg.className = 'ai-message';
    successMsg.innerHTML = `
      <div class="ai-avatar sales-avatar">
        <span class="material-icons">face</span>
      </div>
      <div class="ai-bubble sales-bubble" style="border-color:var(--status-success);">
        🎉 <strong>Got it, ${escapeHtmlBasic(name)}!</strong><br><br>
        One of our folks will be reachin' out soon. In the meantime, keep on explorin' the demo — every click teaches you somethin' new!
      </div>
    `;
    chatWindow.appendChild(successMsg);
    chatWindow.scrollTop = chatWindow.scrollHeight;

    showToast('Callback request sent!', 'success');
  } catch (error) {
    console.error('[Sales Lead] Error:', error);
    showToast('Error submitting request. Try again.', 'error');
  }
}

// Create floating sales button for demo mode
function initSalesButton() {
  // Remove sales button if not in demo mode
  if (!window.AppState?.isDemo) {
    const existingFab = document.getElementById('sales-fab');
    if (existingFab) existingFab.remove();
    return;
  }

  // Check if button already exists
  if (document.getElementById('sales-fab')) return;

  const fab = document.createElement('button');
  fab.id = 'sales-fab';
  fab.className = 'sales-fab';
  fab.innerHTML = `
    <span class="material-icons">support_agent</span>
    <span class="sales-fab-label">Talk to Sales</span>
  `;
  fab.onclick = showSalesAgent;
  document.body.appendChild(fab);
}

window.showSalesAgent = showSalesAgent;
window.closeSalesAgent = closeSalesAgent;
window.toggleSalesLeadForm = toggleSalesLeadForm;
window.submitSalesModalLead = submitSalesModalLead;
window.closeDemoEngagement = closeDemoEngagement;
window.sendSalesMessage = sendSalesMessage;
window.submitSalesLead = submitSalesLead;

// ============================================
// DEMO → TRIAL CONVERSION CTAs
// ============================================

function showDemoSignupModal(source) {
  _demoPopups().then(m => m.showDemoSignupModal(source), (e) => console.warn('[Demo] signup modal failed to load:', e?.message));
}

function closeDemoSignupModal() {
  const modal = document.getElementById('modal-demo-signup');
  if (modal) modal.remove();
}

async function handleDemoSignup(e) {
  e.preventDefault();
  if (!window.validateRequiredFields(e.target)) return;
  const btn = document.getElementById('ds-submit-btn');
  const errDiv = document.getElementById('ds-error');
  if (errDiv) errDiv.style.display = 'none';

  const company = document.getElementById('ds-company')?.value?.trim();
  const firstName = document.getElementById('ds-firstname')?.value?.trim();
  const lastName = document.getElementById('ds-lastname')?.value?.trim();
  const email = document.getElementById('ds-email')?.value?.trim();
  const password = document.getElementById('ds-password')?.value;

  if (!company || !firstName || !lastName || !email || !password) {
    if (errDiv) { errDiv.textContent = 'All fields are required.'; errDiv.style.display = 'block'; }
    return false;
  }
  if (password.length < 6) {
    if (errDiv) { errDiv.textContent = 'Password must be at least 6 characters.'; errDiv.style.display = 'block'; }
    return false;
  }

  try {
    btn.disabled = true;
    btn.innerHTML = '<span class="btn-spinner"></span> Creating account...';

    // TASK-9861 — same roster check as the main signup form, BEFORE the demo
    // session is signed out (a match leaves the demo running untouched).
    const rosterMatchOverride = hasSignupRosterOverride(email);
    if (!rosterMatchOverride) {
      const rc = await checkSignupRoster({ email });
      if (rc.match) {
        btn.disabled = false;
        btn.innerHTML = '<span class="material-icons" style="font-size:18px;vertical-align:middle;margin-right:6px;">rocket_launch</span> Start 30-Day Trial';
        if (errDiv) {
          errDiv.textContent = '';
          const p = document.createElement('p');
          p.style.cssText = 'margin:0 0 8px;color:#155E75;font-weight:600;';
          p.textContent = rc.message;
          const own = document.createElement('button');
          own.type = 'button';
          own.textContent = "I'm starting my own trucking company";
          own.style.cssText = 'min-height:44px;background:none;border:none;color:var(--brand-blue);font-weight:600;text-decoration:underline;cursor:pointer;';
          own.addEventListener('click', () => {
            setSignupRosterOverride(email);
            errDiv.style.display = 'none';
            const form = document.getElementById('ds-submit-btn')?.closest('form');
            if (form && typeof form.requestSubmit === 'function') form.requestSubmit();
            else document.getElementById('ds-submit-btn')?.click();
          });
          errDiv.appendChild(p);
          errDiv.appendChild(own);
          errDiv.style.display = 'block';
        }
        return false;
      }
    }

    // Sign out anonymous demo session first
    await signOut(auth);

    // Create the real account
    const result = await createTrialAccount({ company, firstName, lastName, email, password, entry: 'demo', rosterMatchOverride });

    // GA4: Track signup from demo (skip test accounts)
    if (typeof gtag === 'function' && !isTestEmail(email)) {
      gtag('event', 'sign_up', { method: 'email', source: 'demo', has_ai_pack: false, has_sms: false });
    }
    if (typeof fbq !== 'undefined' && !isTestEmail(email)) fbq('track', 'CompleteRegistration');
    if (typeof window.lintrk !== 'undefined' && !isTestEmail(email)) window.lintrk('track', { conversion_id: 25160532 });

    // Close modal and clean up demo UI
    closeDemoSignupModal();
    removeDemoTrialCtas();

    showToast('Welcome! Your 30-day free trial has started.', 'success');
    if (typeof fbq !== 'undefined' && !isTestEmail(email)) fbq('track', 'StartTrial', {value: 0, currency: 'USD'});
    if (typeof window.lintrk !== 'undefined' && !isTestEmail(email)) window.lintrk('track', { conversion_id: 25160532 });

    // Clear signup flag and trigger auth state
    window.AppState.signupInProgress = false;
    window.AppState.isDemo = false;
    await handleAuthStateChange(result.user);

  } catch (error) {
    console.error('[DemoSignup] Error:', error);
    window.AppState.signupInProgress = false;
    btn.disabled = false;
    btn.innerHTML = '<span class="material-icons" style="font-size:18px;vertical-align:middle;margin-right:6px;">rocket_launch</span> Start 30-Day Trial';
    if (errDiv) {
      errDiv.textContent = getAuthErrorMessage(error.code);
      errDiv.style.display = 'block';
    }
    // Re-sign in anonymously if signup failed so demo keeps working
    try { await signInAnonymously(auth); } catch (reAuthErr) { console.error('[DemoSignup] Re-auth failed:', reAuthErr); }
  }
  return false;
}

function initDemoTrialCtas() {
  if (!window.AppState?.isDemo) return;

  // 1. Show sidebar trial CTA
  const sidebarCta = document.getElementById('sidebar-trial-cta');
  if (sidebarCta) sidebarCta.classList.remove('hidden');

  // Hide settings in demo (not relevant)
  const btnSettings = document.getElementById('btn-settings');
  if (btnSettings) btnSettings.style.display = 'none';
  setAccountNavVisible(false);

  // 2. Trial FAB (above sales FAB)
  if (!document.getElementById('trial-fab')) {
    const trialFab = document.createElement('button');
    trialFab.id = 'trial-fab';
    trialFab.className = 'trial-fab';
    trialFab.innerHTML = `
      <span class="material-icons">rocket_launch</span>
      <span class="trial-fab-label">Start Trial</span>
    `;
    trialFab.onclick = () => {
      if (typeof gtag === 'function') gtag('event', 'demo_trial_cta_click', { cta_location: 'fab' });
      showDemoSignupModal('fab');
    };
    document.body.appendChild(trialFab);
  }

  // 3. Timed mobile banner (60s delay, dismissable)
  if (!sessionStorage.getItem('dd_demo_banner_dismissed')) {
    setTimeout(() => {
      if (!window.AppState?.isDemo) return;
      if (sessionStorage.getItem('dd_demo_banner_dismissed')) return;
      const banner = document.getElementById('demo-trial-banner');
      if (banner) banner.classList.remove('hidden');
    }, 60000);
  }
}

function dismissDemoTrialBanner() {
  sessionStorage.setItem('dd_demo_banner_dismissed', '1');
  const banner = document.getElementById('demo-trial-banner');
  if (banner) banner.classList.add('hidden');
}

function removeDemoTrialCtas() {
  // Clean up all demo trial elements when user signs up
  const sidebarCta = document.getElementById('sidebar-trial-cta');
  if (sidebarCta) sidebarCta.classList.add('hidden');
  const trialFab = document.getElementById('trial-fab');
  if (trialFab) trialFab.remove();
  const banner = document.getElementById('demo-trial-banner');
  if (banner) banner.classList.add('hidden');
  const salesFab = document.getElementById('sales-fab');
  if (salesFab) salesFab.remove();
}

window.showDemoSignupModal = showDemoSignupModal;
window.closeDemoSignupModal = closeDemoSignupModal;
window.handleDemoSignup = handleDemoSignup;
window.dismissDemoTrialBanner = dismissDemoTrialBanner;

// ============================================
// EMAIL VERIFICATION
// ============================================

async function checkEmailVerification() {
  // Skip for demo
  if (window.AppState?.isDemo) return true;

  const tenant = window.AppState?.tenant;
  if (!tenant) return true;

  // Check if email is verified
  if (tenant.emailVerified === false) {
    showEmailVerificationPending();
    return false;
  }

  return true;
}

function showEmailVerificationPending() {
  const screen = document.getElementById('email-verification-pending');
  if (screen) {
    screen.classList.remove('hidden');
    screen.style.display = 'flex'; // Override inline display:none
    // Update email display
    const emailSpan = screen.querySelector('.verification-email');
    if (emailSpan && window.AppState?.user?.email) {
      emailSpan.textContent = window.AppState.user.email;
    }
  }

  // Hide main content and sidebar
  document.getElementById('main-content')?.classList.add('hidden');
  document.getElementById('bottom-nav')?.classList.add('hidden');
  document.getElementById('sidebar')?.classList.add('hidden');
}

function hideEmailVerificationPending() {
  const screen = document.getElementById('email-verification-pending');
  if (screen) {
    screen.classList.add('hidden');
    screen.style.display = 'none';
  }
}

// resendVerificationEmail defined earlier in EMAIL VERIFICATION REMINDER BANNER section
window.checkEmailVerification = checkEmailVerification;

async function handleEmailVerificationToken(token) {
  showLoading();
  try {
    // Sign out any existing session first to prevent wrong account loading
    if (auth.currentUser) {
      await signOut(auth);
      // Clean up banners from previous session
      dismissTrialBanner();
      dismissVerificationBanner();
    }

    // Call the verification endpoint. TASK-685 (S3): pass &format=json so the
    // (now redirect-by-default) function returns the JSON contract instead of a
    // 302 for this in-flight SPA fetch() path. New email links hit the function
    // directly and get the server-side verify + redirect without this code path.
    const response = await fetch(`https://us-central1-dispatch-deck-be88c.cloudfunctions.net/verifyEmailToken?token=${encodeURIComponent(token)}&format=json`);
    const result = await response.json();

    if (result.success) {
      showToast('Email verified successfully! You can now log in.', 'success');
      // Remove token from URL
      const url = new URL(window.location);
      url.searchParams.delete('verify');
      window.history.replaceState({}, document.title, url.toString());
      // Show auth modal for fresh login
      showAuthModal();
    } else {
      showToast(result.error || 'Verification failed. Please try again.', 'error');
    }
  } catch (error) {
    console.error('[Email Verification] Error:', error);
    showToast('Error verifying email. Please try again.', 'error');
  }
  hideLoading();
}

// ============================================
// PASSWORD RESET
// ============================================

// Store the current reset token for form submission
let currentResetToken = null;

/**
 * Show password reset modal and validate token
 */
async function showPasswordResetModal(token) {
  currentResetToken = token;
  const modal = document.getElementById('password-reset-modal');
  if (!modal) return;

  // Show modal with validating state
  modal.classList.remove('hidden');
  document.getElementById('reset-validating')?.classList.remove('hidden');
  document.getElementById('reset-invalid')?.classList.add('hidden');
  document.getElementById('reset-form')?.classList.add('hidden');
  document.getElementById('reset-success')?.classList.add('hidden');

  try {
    // Validate token
    const response = await fetch(`https://us-central1-dispatch-deck-be88c.cloudfunctions.net/verifyPasswordResetToken?token=${encodeURIComponent(token)}`);
    const result = await response.json();

    document.getElementById('reset-validating')?.classList.add('hidden');

    if (result.valid) {
      // Show form
      document.getElementById('reset-form')?.classList.remove('hidden');
      document.getElementById('reset-email-display').textContent = result.email;
      document.getElementById('reset-new-password').value = '';
      document.getElementById('reset-confirm-password').value = '';
      document.getElementById('reset-form-error')?.classList.add('hidden');
      // Focus on password field
      setTimeout(() => document.getElementById('reset-new-password')?.focus(), 100);
    } else {
      // Show invalid state
      document.getElementById('reset-invalid')?.classList.remove('hidden');
      document.getElementById('reset-error-message').textContent = result.error || 'This password reset link is no longer valid.';
    }
  } catch (error) {
    console.error('[Password Reset] Token validation error:', error);
    document.getElementById('reset-validating')?.classList.add('hidden');
    document.getElementById('reset-invalid')?.classList.remove('hidden');
    document.getElementById('reset-error-message').textContent = 'Unable to validate reset link. Please try again.';
  }
}

/**
 * Close password reset modal and clean up URL
 */
function closePasswordResetModal() {
  const modal = document.getElementById('password-reset-modal');
  if (modal) {
    modal.classList.add('hidden');
  }
  currentResetToken = null;

  // Remove reset param from URL
  const url = new URL(window.location);
  url.searchParams.delete('reset');
  window.history.replaceState({}, document.title, url.toString());
}

/**
 * Submit new password
 */
async function submitPasswordReset() {
  const newPassword = document.getElementById('reset-new-password')?.value || '';
  const confirmPassword = document.getElementById('reset-confirm-password')?.value || '';
  const errorDiv = document.getElementById('reset-form-error');
  const errorText = document.getElementById('reset-form-error-text');
  const submitBtn = document.getElementById('btn-submit-reset');

  // Clear previous error
  errorDiv?.classList.add('hidden');

  // Validate
  if (!newPassword) {
    errorDiv?.classList.remove('hidden');
    errorText.textContent = 'Please enter a new password.';
    return;
  }

  if (newPassword.length < 8) {
    errorDiv?.classList.remove('hidden');
    errorText.textContent = 'Password must be at least 8 characters.';
    return;
  }

  if (newPassword !== confirmPassword) {
    errorDiv?.classList.remove('hidden');
    errorText.textContent = 'Passwords do not match.';
    return;
  }

  // Submit
  submitBtn.disabled = true;
  submitBtn.innerHTML = '<span class="btn-spinner"></span> Updating...';

  try {
    const response = await fetch('https://us-central1-dispatch-deck-be88c.cloudfunctions.net/completePasswordReset', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: currentResetToken, newPassword }),
    });

    const result = await response.json();

    if (result.ok) {
      // Show success state
      document.getElementById('reset-form')?.classList.add('hidden');
      document.getElementById('reset-success')?.classList.remove('hidden');
    } else {
      errorDiv?.classList.remove('hidden');
      errorText.textContent = result.error || 'Unable to reset password. Please try again.';
    }
  } catch (error) {
    console.error('[Password Reset] Submit error:', error);
    errorDiv?.classList.remove('hidden');
    errorText.textContent = 'Network error. Please check your connection and try again.';
  }

  submitBtn.disabled = false;
  submitBtn.innerHTML = '<span class="material-icons">lock</span> Set New Password';
}

// Expose to window for onclick handlers
window.showPasswordResetModal = showPasswordResetModal;
window.closePasswordResetModal = closePasswordResetModal;
window.submitPasswordReset = submitPasswordReset;

// ============================================
// SUPPORT REQUEST
// ============================================

/**
 * Show support request modal
 * Can be called from login page or Help Center
 */
function showSupportRequestModal() {
  const modal = document.getElementById('support-request-modal');
  if (!modal) return;

  // Trial-expired and email-verification-pending screens are z-index 9999
  // overlays; the default .modal z-index is 1000, which would hide this
  // modal behind them. Force a higher z-index when those overlays are visible.
  const expiredVisible = !document.getElementById('trial-expired-screen')?.classList.contains('hidden');
  const verifyVisible = !document.getElementById('email-verification-pending')?.classList.contains('hidden');
  modal.style.zIndex = (expiredVisible || verifyVisible) ? '10000' : '';

  // Reset form
  document.getElementById('support-form')?.classList.remove('hidden');
  document.getElementById('support-success')?.classList.add('hidden');
  document.getElementById('support-form-error')?.classList.add('hidden');

  // Clear fields
  document.getElementById('support-name').value = '';
  document.getElementById('support-email').value = '';
  document.getElementById('support-phone').value = '';
  document.getElementById('support-message').value = '';

  // Pre-fill if user is logged in
  if (window.AppState?.user?.email) {
    document.getElementById('support-email').value = window.AppState.user.email;
  }
  if (window.AppState?.user?.displayName) {
    document.getElementById('support-name').value = window.AppState.user.displayName;
  }

  modal.classList.remove('hidden');
}

/**
 * Close support request modal
 */
function closeSupportRequestModal() {
  const modal = document.getElementById('support-request-modal');
  if (modal) {
    modal.classList.add('hidden');
  }
}

/**
 * Submit support request
 */
async function submitSupportRequest() {
  const name = document.getElementById('support-name')?.value?.trim() || '';
  const email = document.getElementById('support-email')?.value?.trim() || '';
  const phone = document.getElementById('support-phone')?.value?.trim() || '';
  const message = document.getElementById('support-message')?.value?.trim() || '';

  const errorDiv = document.getElementById('support-form-error');
  const errorText = document.getElementById('support-form-error-text');
  const submitBtn = document.getElementById('btn-submit-support');

  // Clear previous error
  errorDiv?.classList.add('hidden');

  // Validate
  if (!name) {
    errorDiv?.classList.remove('hidden');
    errorText.textContent = 'Please enter your name.';
    return;
  }

  if (!email || !email.includes('@')) {
    errorDiv?.classList.remove('hidden');
    errorText.textContent = 'Please enter a valid email address.';
    return;
  }

  if (!message) {
    errorDiv?.classList.remove('hidden');
    errorText.textContent = 'Please describe your question or issue.';
    return;
  }

  // Submit
  submitBtn.disabled = true;
  submitBtn.innerHTML = '<span class="btn-spinner"></span> Sending...';

  try {
    const requestData = {
      name,
      email,
      phone: phone || null,
      message,
      tenantId: window.AppState?.tenant || null,
      source: 'form'
    };

    const response = await fetch('https://us-central1-dispatch-deck-be88c.cloudfunctions.net/submitSupportRequest', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestData),
    });

    const result = await response.json();

    if (result.ok) {
      // Show success state
      document.getElementById('support-form')?.classList.add('hidden');
      document.getElementById('support-success')?.classList.remove('hidden');
    } else {
      errorDiv?.classList.remove('hidden');
      errorText.textContent = result.error || 'Unable to submit request. Please try again.';
    }
  } catch (error) {
    console.error('[Support Request] Submit error:', error);
    errorDiv?.classList.remove('hidden');
    errorText.textContent = 'Network error. Please check your connection and try again.';
  }

  submitBtn.disabled = false;
  submitBtn.innerHTML = '<span class="material-icons">send</span> Send Message';
}

// Expose to window for onclick handlers
window.showSupportRequestModal = showSupportRequestModal;
window.closeSupportRequestModal = closeSupportRequestModal;
window.submitSupportRequest = submitSupportRequest;

// ============================================
// CANCEL SUBSCRIPTION
// ============================================

function showCancelSubscriptionModal() {
  const modal = document.getElementById('cancel-subscription-modal');
  if (modal) {
    modal.classList.remove('hidden');
    // Reset to step 1
    document.getElementById('cancel-step-1')?.classList.remove('hidden');
    document.getElementById('cancel-step-2')?.classList.add('hidden');
    document.getElementById('cancel-step-3')?.classList.add('hidden');
    // Clear selections
    document.querySelectorAll('.cancel-reason-option').forEach(opt => opt.classList.remove('selected'));
    document.getElementById('cancel-feedback')?.value && (document.getElementById('cancel-feedback').value = '');
    document.querySelectorAll('.cancel-rating-star').forEach(star => star.classList.remove('selected'));
    // TASK-9229 — the retention highlight now actually applies (it never did
    // before), so it also has to be CLEARED on reopen or a previous session's
    // choice stays lit while the state below says 'now'.
    document.querySelectorAll('.retention-option').forEach(opt => opt.classList.remove('selected'));
    // TASK-9229 — the per-outcome billing line is injected into step 3 at
    // runtime; drop any line left over from a previous cancel attempt.
    document.getElementById('cancel-billing-outcome')?.remove();
    window.cancelSubscriptionData = { reasons: [], feedback: '', rating: 0, retentionOption: 'now' };
  }
}

function closeCancelSubscriptionModal() {
  const modal = document.getElementById('cancel-subscription-modal');
  if (modal) {
    modal.classList.add('hidden');
  }
}

function toggleCancelReason(reason) {
  window.cancelSubscriptionData = window.cancelSubscriptionData || { reasons: [], feedback: '', rating: 0, retentionOption: 'now' };
  const idx = window.cancelSubscriptionData.reasons.indexOf(reason);
  if (idx > -1) {
    window.cancelSubscriptionData.reasons.splice(idx, 1);
  } else {
    window.cancelSubscriptionData.reasons.push(reason);
  }
  // Update UI
  document.querySelectorAll('.cancel-reason-option').forEach(opt => {
    if (opt.dataset.reason === reason) {
      opt.classList.toggle('selected');
    }
  });
}

function setCancelRating(rating) {
  window.cancelSubscriptionData = window.cancelSubscriptionData || { reasons: [], feedback: '', rating: 0, retentionOption: 'now' };
  window.cancelSubscriptionData.rating = rating;
  // Update stars UI
  document.querySelectorAll('.cancel-rating-star').forEach((star, idx) => {
    if (idx < rating) {
      star.classList.add('selected');
    } else {
      star.classList.remove('selected');
    }
  });
}

function goToCancelStep2() {
  window.cancelSubscriptionData = window.cancelSubscriptionData || { reasons: [], feedback: '', rating: 0, retentionOption: 'now' };
  window.cancelSubscriptionData.feedback = document.getElementById('cancel-feedback')?.value || '';

  document.getElementById('cancel-step-1')?.classList.add('hidden');
  document.getElementById('cancel-step-2')?.classList.remove('hidden');
}

function goToCancelStep1() {
  document.getElementById('cancel-step-2')?.classList.add('hidden');
  document.getElementById('cancel-step-1')?.classList.remove('hidden');
}

// TASK-9229 — the retention choice is an ENUM, not a day count.
// index.html has always called this with the STRINGS 'now' / 'keep90', and the
// cancelSubscription callable has always read `retentionOption` and tested it
// with `retentionOption === "keep90" ? 90 : 30`. The parameter was named `days`
// and stashed under `retentionDays`, so the string travelled under a key the
// server does not read — the 90-day choice was silently discarded and everyone
// got 30. The selection highlight was dead for the same reason in reverse:
// `parseInt(opt.dataset.days)` read a `data-days` attribute that does not exist
// on either option, so NaN === 'keep90' was false every time and the card the
// customer clicked never lit up.
function selectRetentionOption(option) {
  window.cancelSubscriptionData = window.cancelSubscriptionData || { reasons: [], feedback: '', rating: 0, retentionOption: 'now' };
  window.cancelSubscriptionData.retentionOption = option;

  // Update selection UI. Matched on the element's OWN id (`retention-option-now`
  // / `retention-option-keep90`), which is what the markup actually carries.
  document.querySelectorAll('.retention-option').forEach(opt => {
    opt.classList.toggle('selected', opt.id === `retention-option-${option}`);
  });
}

async function downloadAccountData() {
  const btn = document.querySelector('.btn-download-data');
  if (btn) {
    btn.disabled = true;
    btn.innerHTML = '<span class="material-icons spin">refresh</span> Preparing...';
  }

  try {
    // 🔴 TASK-9229 — SAME DEFECT, ADJACENT BUTTON. `downloadTenantData` also
    // does `const { tenantId } = data;` then throws invalid-argument without it,
    // and this called it with no argument at all. It is the escape hatch the
    // cancellation email points customers at ("Download your data from
    // Settings") before their data is deleted, so a dead button here costs
    // somebody their records. One line, same fix, same contract.
    const tenantId = window.AppState?.tenant;
    if (!tenantId) throw new Error('No tenant in AppState');
    const downloadFn = httpsCallable(functions,'downloadTenantData');
    const result = await downloadFn({ tenantId });

    // Create and download JSON file
    const blob = new Blob([JSON.stringify(result.data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dispatchdeck-backup-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    showToast('Data downloaded successfully!', 'success');
  } catch (error) {
    console.error('[Download Data] Error:', error);
    showToast('Error downloading data. Try again.', 'error');
  } finally {
    if (btn) {
      btn.disabled = false;
      btn.innerHTML = '<span class="material-icons">download</span> Download My Data';
    }
  }
}

// =============================================================================
// TASK-9229 — WHAT THE OWNER IS TOLD ABOUT THEIR CARD, PER OUTCOME.
// =============================================================================
// TASK-9218 rebuilt the server so a self-serve cancel actually reaches Stripe,
// and made the un-confirmable cases LOUD: it returns `stripeCancelUnverified`
// alongside a named `stripeCancelOutcome`. The client ignored both and showed a
// flat "Subscription cancelled." on every path — which is the exact
// silent-success-while-Stripe-keeps-billing failure 9218 exists to end,
// reappearing one layer up in the UI.
//
// 🔴 THE AUTHORITY IS THE BOOLEAN, NOT THIS MAP. `stripeCancelUnverified` is
// computed server-side; the map below only supplies nicer copy for the outcomes
// we know by name. A future outcome string this map has never heard of falls
// through to the generic line for its side of the boolean, so adding an outcome
// server-side can never silently produce a false "billing stopped".
const _CANCEL_OUTCOME_COPY = {
  // Verified — Stripe confirmed. cancel_at_period_end is deliberate policy
  // (server-side), so the customer keeps the board through the period they
  // already paid for. Say that, rather than implying billing stopped today.
  'cancelled': "Your billing is cancelled — you won't be charged again after your current billing period.",
  'no-live-subscription': 'There was no active subscription left to cancel, so nothing further will be charged.',
  'no-stripe-billing': 'There was no card billing set up on this account, so nothing further will be charged.',
  // Unverified — we could NOT confirm with Stripe. Never say "cancelled" here.
  'stripe-unreachable': "⚠️ We couldn't reach our card processor, so we can't confirm your billing was stopped. Please check your next statement — if you see another DispatchDeck charge, email support@dispatchdeck.app and we'll refund it and stop the billing right away.",
  'partial-failure': "⚠️ We couldn't confirm every charge was stopped with our card processor. Please check your next statement — if you see another DispatchDeck charge, email support@dispatchdeck.app and we'll refund it and stop the billing right away.",
  'unattributable-subscription-skipped': "⚠️ We found a subscription we couldn't safely confirm belongs to this account, so we left it alone. Please check your next statement — if you see another DispatchDeck charge, email support@dispatchdeck.app and we'll refund it and stop the billing right away."
};
const _CANCEL_OUTCOME_FALLBACK_UNVERIFIED = "⚠️ We couldn't confirm your billing is cancelled with our card processor. Please check your next statement — if you see another DispatchDeck charge, email support@dispatchdeck.app and we'll refund it and stop the billing right away.";

// Renders the billing line into the confirmation step. Injected rather than
// added to index.html on purpose — the modal markup is another lane's surface
// and this is the only thing in it that depends on the server's answer.
function _renderCancelBillingOutcome(unverified, message) {
  const step3 = document.getElementById('cancel-step-3');
  if (!step3) return;
  document.getElementById('cancel-billing-outcome')?.remove();
  const p = document.createElement('p');
  p.id = 'cancel-billing-outcome';
  // textContent, not innerHTML — nothing here is markup and nothing here should
  // ever be able to become markup.
  p.textContent = message;
  // Light background: var(--text-primary), never --text-muted (Pitfall #3).
  p.style.cssText = unverified
    ? 'margin:0 0 var(--spacing-lg) 0;padding:var(--spacing-md);border-radius:var(--radius-md);text-align:left;font-size:0.9rem;line-height:1.45;color:var(--text-primary);background:var(--bg-elevated);border-left:4px solid var(--status-warning);'
    : 'margin:0 0 var(--spacing-lg) 0;font-size:0.9rem;line-height:1.45;color:var(--text-secondary);';
  const doneBtn = step3.querySelector('.btn-primary');
  if (doneBtn) step3.insertBefore(p, doneBtn); else step3.appendChild(p);
}

async function confirmCancelSubscription() {
  const btn = document.querySelector('#cancel-step-2 .btn-danger');

  // 🔴 TASK-9229 — the callable REQUIRES tenantId and throws
  // invalid-argument("tenantId required") without it, BEFORE it ever reaches
  // Stripe. The client never sent one, so every self-serve cancel died at the
  // first line of the function and the whole TASK-9218 Stripe fix was
  // unreachable from this button. Bound from AppState (the same source every
  // other callable in this file uses) and checked here so a missing tenant is a
  // clear message instead of a generic "Error cancelling subscription".
  const tenantId = window.AppState?.tenant;
  if (!tenantId) {
    console.error('[Cancel Subscription] No tenant in AppState — refusing to call cancelSubscription');
    showToast('Could not identify your account. Refresh and try again, or email support@dispatchdeck.app.', 'error');
    return;
  }

  if (btn) {
    btn.disabled = true;
    btn.innerHTML = '<span class="material-icons spin">refresh</span> Cancelling...';
  }

  try {
    const cancelFn = httpsCallable(functions,'cancelSubscription');
    // 🔴 THESE KEYS ARE THE SERVER'S CONTRACT, verified against the destructure
    // at the top of `exports.cancelSubscription` in functions/src/index.js:
    //     const { tenantId, reasons, feedback, rating, retentionOption } = data;
    // tests/task9229-cancel-subscription-payload.unit.cjs re-derives that list
    // from the real source and fails if either side drifts. Do not rename a key
    // here without changing it there — a mismatch fails SILENTLY (an ignored
    // field) or fatally (a missing tenantId), never loudly.
    const result = await cancelFn({
      tenantId,
      reasons: window.cancelSubscriptionData?.reasons || [],
      feedback: window.cancelSubscriptionData?.feedback || '',
      rating: window.cancelSubscriptionData?.rating || 0,
      retentionOption: window.cancelSubscriptionData?.retentionOption || 'now'
    });

    const data = result?.data || {};

    // Show confirmation step
    document.getElementById('cancel-step-2')?.classList.add('hidden');
    document.getElementById('cancel-step-3')?.classList.remove('hidden');

    // Update deletion date.
    // Two fixes here: the id is `cancel-deletion-date` (the old code looked up
    // `deletion-date`, which does not exist in index.html, so the date always
    // stayed '---'); and the demo tenant returns { ok, demo } with NO
    // scheduledDeletionAt, which used to render "Invalid Date".
    const dateEl = document.getElementById('cancel-deletion-date');
    if (dateEl) {
      const deletionDate = data.scheduledDeletionAt ? new Date(data.scheduledDeletionAt) : null;
      dateEl.textContent = (deletionDate && !isNaN(deletionDate.getTime()))
        ? deletionDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
        : 'Not scheduled';
    }
    // TASK-9812 — a paid-through cancel (annual, or mid-month) only turns
    // renewal off: say until when, and show NO "closed" / deletion countdown.
    const paidEnd = data.paidThroughAt ? new Date(data.paidThroughAt) : null;
    const paidThrough = !!paidEnd && !isNaN(paidEnd.getTime());
    document.getElementById('cancel-deletion-block')?.classList.toggle('hidden', paidThrough);
    const paidEl = document.getElementById('cancel-paid-through');
    if (paidEl) {
      paidEl.classList.toggle('hidden', !paidThrough);
      paidEl.textContent = paidThrough
        ? `Your plan stays active until ${paidEnd.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}, then it won't renew.`
        : '';
    }
    const titleEl = document.getElementById('cancel-step3-title');
    if (titleEl) titleEl.textContent = paidThrough ? 'Auto-renew is off' : 'Subscription Cancelled';

    // 🔴 The truthful part. `stripeCancelUnverified` is the server's own verdict.
    // If the field is ABSENT (an older functions deploy than this bundle), we do
    // not know either way — so we say nothing about the card rather than assert
    // a cancellation we cannot stand behind.
    const knowsBilling = typeof data.stripeCancelUnverified === 'boolean';
    const unverified = data.stripeCancelUnverified === true;
    if (data.demo) {
      _renderCancelBillingOutcome(false, 'This is the demo account — no real billing was changed.');
      showToast('Demo cancellation — no billing was changed.', 'info');
    } else if (!knowsBilling) {
      _renderCancelBillingOutcome(false, 'Your DispatchDeck account is closed. Check your next statement to confirm billing has stopped, and email support@dispatchdeck.app if anything looks wrong.');
      showToast('Account cancelled.', 'info');
    } else {
      const message = _CANCEL_OUTCOME_COPY[data.stripeCancelOutcome]
        || (unverified ? _CANCEL_OUTCOME_FALLBACK_UNVERIFIED : 'Your billing is cancelled.');
      _renderCancelBillingOutcome(unverified, message);
      if (unverified) {
        console.error('[Cancel Subscription] 🔴 Stripe cancel NOT confirmed:', data.stripeCancelOutcome);
        showToast("Account cancelled — but we couldn't confirm your billing stopped. Check your next statement.", 'warning');
      } else {
        showToast('Subscription cancelled.', 'info');
      }
    }
  } catch (error) {
    console.error('[Cancel Subscription] Error:', error);
    showToast('Error cancelling subscription. Try again.', 'error');
    if (btn) {
      btn.disabled = false;
      btn.innerHTML = '<span class="material-icons">cancel</span> Cancel Subscription';
    }
  }
}

window.showCancelSubscriptionModal = showCancelSubscriptionModal;
window.closeCancelSubscriptionModal = closeCancelSubscriptionModal;
window.toggleCancelReason = toggleCancelReason;
window.setCancelRating = setCancelRating;
window.goToCancelStep1 = goToCancelStep1;
window.goToCancelStep2 = goToCancelStep2;
window.selectRetentionOption = selectRetentionOption;
window.downloadAccountData = downloadAccountData;
window.confirmCancelSubscription = confirmCancelSubscription;

// ============================================
// FORGOT EMAIL
// ============================================

function showForgotEmailModal() {
  const modal = document.getElementById('forgot-email-modal');
  if (modal) {
    modal.classList.remove('hidden');
    // Clear previous messages
    const chatWindow = document.getElementById('forgot-email-chat');
    if (chatWindow) {
      chatWindow.innerHTML = '';
      // Add initial AI message
      addForgotEmailMessage('ai', `Howdy! I'm here to help you recover access to your account. 🤠

Let's get started. What's the <strong>name of your company</strong> that's registered with DispatchDeck?`);
    }
    window.forgotEmailData = { step: 'company', companyName: '', ownerName: '', phone: '', signupDate: '' };
  }
}

function closeForgotEmailModal() {
  const modal = document.getElementById('forgot-email-modal');
  if (modal) {
    modal.classList.add('hidden');
  }
}

function addForgotEmailMessage(type, content) {
  const chatWindow = document.getElementById('forgot-email-chat');
  if (!chatWindow) return;

  const msg = document.createElement('div');
  msg.className = type === 'ai' ? 'ai-message' : 'user-message';

  if (type === 'ai') {
    msg.innerHTML = `
      <div class="ai-avatar">
        <span class="material-icons icon-dd-ai" aria-hidden="true"></span>
      </div>
      <div class="ai-bubble">${content}</div>
    `;
  } else {
    msg.innerHTML = `<div class="user-bubble">${escapeHtmlBasic(content)}</div>`;
  }

  chatWindow.appendChild(msg);
  chatWindow.scrollTop = chatWindow.scrollHeight;
}

async function sendForgotEmailMessage() {
  const input = document.getElementById('forgot-email-input');
  if (!input || !input.value.trim()) return;

  const message = input.value.trim();
  input.value = '';

  addForgotEmailMessage('user', message);

  const data = window.forgotEmailData || { step: 'company' };

  // Progress through steps
  switch (data.step) {
    case 'company':
      data.companyName = message;
      data.step = 'owner';
      setTimeout(() => {
        addForgotEmailMessage('ai', `Got it — <strong>${escapeHtmlBasic(message)}</strong>.

Now, what's the <strong>full name of the account owner</strong>?`);
      }, 500);
      break;

    case 'owner':
      data.ownerName = message;
      data.step = 'phone';
      setTimeout(() => {
        addForgotEmailMessage('ai', `Thanks, ${escapeHtmlBasic(message.split(' ')[0])}!

What <strong>phone number</strong> is associated with the account? (We'll use this to contact you for verification.)`);
      }, 500);
      break;

    case 'phone':
      data.phone = message;
      data.step = 'date';
      setTimeout(() => {
        addForgotEmailMessage('ai', `Perfect. Last question — approximately <strong>when did you sign up</strong>? (Month and year is fine, like "October 2025")`);
      }, 500);
      break;

    case 'date':
      data.signupDate = message;
      data.step = 'submitting';
      setTimeout(() => {
        addForgotEmailMessage('ai', `Great! Let me submit this recovery request...`);
        submitForgotEmailRequest();
      }, 500);
      break;

    case 'complete':
      addForgotEmailMessage('ai', `Your request has already been submitted. Our team will contact you at the phone number you provided. Thanks for your patience!`);
      break;
  }

  window.forgotEmailData = data;
}

async function submitForgotEmailRequest() {
  const data = window.forgotEmailData;

  try {
    const submitFn = httpsCallable(functions,'submitEmailRecoveryRequest');
    await submitFn({
      companyName: data.companyName,
      ownerName: data.ownerName,
      phone: data.phone,
      signupApproxDate: data.signupDate
    });

    data.step = 'complete';
    window.forgotEmailData = data;

    setTimeout(() => {
      addForgotEmailMessage('ai', `✅ <strong>Request submitted!</strong>

Here's what happens next:
1. Our verification team will review your request
2. We'll contact you at <strong>${escapeHtmlBasic(data.phone)}</strong> to verify your identity
3. You'll need to confirm some billing details (last 4 digits of card, recent charge amount)
4. Once verified, we'll help you update your email

This usually takes 1-2 business days. Thanks for your patience! 🤠`);
    }, 1000);

  } catch (error) {
    console.error('[Forgot Email] Error:', error);
    data.step = 'date'; // Allow retry
    window.forgotEmailData = data;
    addForgotEmailMessage('ai', `Oops, something went wrong submitting your request. Please try again or contact us directly at support@dispatchdeck.app`);
  }
}

window.showForgotEmailModal = showForgotEmailModal;
window.closeForgotEmailModal = closeForgotEmailModal;
window.sendForgotEmailMessage = sendForgotEmailMessage;

// ============================================
// SPARKLES TRAINING SYSTEM
// ============================================
// Lightweight training system that shows sparkle indicators on important UI elements
// to help users discover features. Sparkles disappear after first click.
// State is stored in Firestore (per-tenant) for cross-device persistence.

// In-memory cache of clicked sparkles (loaded from Firestore on init)
let sparklesClickedCache = [];
let sparklesLoaded = false;
let sparklesLoadingPromise = null; // Prevent concurrent loads

// Define which elements should have sparkles
// Easy to add more by just adding to this array
const SPARKLE_ELEMENTS = [
  { id: 'mobile-nav-more', name: 'more-button' },
  { id: 'btn-help', name: 'help-sidebar' },
  { id: 'mobile-help-btn', name: 'help-mobile' },
  { id: 'btn-ai-create', name: 'ai-create' },
  { selector: '.btn-text-crew', name: 'individual-text', dynamic: true },
  { selector: '[onclick*="showAddJob"]', name: 'add-job', dynamic: true, small: true },
  // Mass text date range buttons
  { id: 'mass-text-today', name: 'mass-text-today', dynamic: true },
  { id: 'mass-text-tomorrow', name: 'mass-text-tomorrow', dynamic: true },
  { id: 'mass-text-3days', name: 'mass-text-3days', dynamic: true },
  { id: 'mass-text-week', name: 'mass-text-week', dynamic: true }
];

// Get list of already clicked sparkles (from cache)
function getClickedSparkles() {
  return sparklesClickedCache;
}

// Mark a sparkle as clicked (updates cache and Firestore)
async function markSparkleClicked(name) {
  if (sparklesClickedCache.includes(name)) return;

  sparklesClickedCache.push(name);

  // Save to Firestore if logged in
  const tenantId = window.AppState?.tenant;
  if (tenantId && tenantId !== 'demo') {
    try {
      await setDoc(doc(db, 'tenants', tenantId), {
        sparklesClicked: sparklesClickedCache
      }, { merge: true });
    } catch (error) {
      console.error('[Sparkles] Error saving to Firestore:', error);
    }
  }
}

// Check if a sparkle has been clicked
function isSparkleClicked(name) {
  return sparklesClickedCache.includes(name);
}

// Load sparkles state from Firestore (with deduplication to prevent race conditions)
async function loadSparklesFromFirestore() {
  // If already loaded, return immediately
  if (sparklesLoaded) return;

  // If already loading, wait for the existing promise
  if (sparklesLoadingPromise) {
    return sparklesLoadingPromise;
  }

  const tenantId = window.AppState?.tenant;
  if (!tenantId || tenantId === 'demo') {
    sparklesLoaded = true;
    return;
  }

  // Create a promise that other callers can await
  sparklesLoadingPromise = (async () => {
    try {
      const tenantDoc = await getDoc(doc(db, 'tenants', tenantId));
      if (tenantDoc.exists()) {
        sparklesClickedCache = tenantDoc.data().sparklesClicked || [];
      }
      sparklesLoaded = true;
    } catch (error) {
      console.error('[Sparkles] Error loading from Firestore:', error);
      sparklesLoaded = true;
    } finally {
      sparklesLoadingPromise = null;
    }
  })();

  return sparklesLoadingPromise;
}

// Add sparkle indicator to an element
function addSparkle(element, sparkleName, small = false) {
  if (!element || element.querySelector('.sparkle-indicator')) return;

  element.classList.add('has-sparkle');
  const indicator = document.createElement('span');
  indicator.className = 'sparkle-indicator' + (small ? ' sparkle-small' : '');
  indicator.setAttribute('data-sparkle-name', sparkleName);

  // Add floating particles — fewer for small sparkles
  const particleCount = small ? 0 : 4;
  for (let i = 0; i < particleCount; i++) {
    const particle = document.createElement('span');
    particle.className = 'sparkle-particle';
    indicator.appendChild(particle);
  }

  element.appendChild(indicator);

  // Add click handler to remove sparkle on first click
  const removeSparkle = () => {
    markSparkleClicked(sparkleName);
    indicator.remove();
    element.classList.remove('has-sparkle');
    element.removeEventListener('click', removeSparkle);
  };
  element.addEventListener('click', removeSparkle, { once: true });
}

// Remove sparkle from element
function removeSparkle(element) {
  const indicator = element?.querySelector('.sparkle-indicator');
  if (indicator) {
    indicator.remove();
    element.classList.remove('has-sparkle');
  }
}

// Initialize sparkles for all defined elements
async function initSparkles() {
  // Skip in driver mode or for public access
  if (window.AppState?.isDriverMode || window.AppState?.accessToken) return;

  // Load sparkles state from Firestore first
  if (!sparklesLoaded) {
    await loadSparklesFromFirestore();
  }

  SPARKLE_ELEMENTS.forEach(config => {
    // Skip if already clicked
    if (isSparkleClicked(config.name)) return;

    // For dynamic elements (rendered later), we'll handle separately
    if (config.dynamic) return;

    const element = config.id
      ? document.getElementById(config.id)
      : document.querySelector(config.selector);

    if (element) {
      addSparkle(element, config.name, config.small);
    }
  });
}

// Apply sparkles to dynamically rendered elements (like job card buttons)
async function applyDynamicSparkles() {
  // Skip in driver mode or for public access
  if (window.AppState?.isDriverMode || window.AppState?.accessToken) return;

  // Wait for sparkles to load from Firestore first
  if (!sparklesLoaded) {
    await loadSparklesFromFirestore();
  }

  SPARKLE_ELEMENTS.filter(c => c.dynamic).forEach(config => {
    if (isSparkleClicked(config.name)) return;

    const elements = document.querySelectorAll(config.selector);
    elements.forEach(el => {
      if (!el.querySelector('.sparkle-indicator')) {
        addSparkle(el, config.name, config.small);
      }
    });
  });
}

// Re-apply sparkle to a specific element (e.g., after modal opens)
function refreshSparkle(elementId, sparkleName) {
  if (isSparkleClicked(sparkleName)) return;

  const element = document.getElementById(elementId);
  if (element && !element.querySelector('.sparkle-indicator')) {
    addSparkle(element, sparkleName);
  }
}

// Clear all sparkle states (for testing/reset)
async function resetSparkles() {
  // Clear cache
  sparklesClickedCache = [];
  sparklesLoaded = true;

  // Clear Firestore
  const tenantId = window.AppState?.tenant;
  if (tenantId && tenantId !== 'demo') {
    try {
      await setDoc(doc(db, 'tenants', tenantId), {
        sparklesClicked: []
      }, { merge: true });
    } catch (error) {
      console.error('[Sparkles] Error clearing Firestore:', error);
    }
  }

  // Remove all existing sparkle indicators
  document.querySelectorAll('.sparkle-indicator').forEach(el => el.remove());
  document.querySelectorAll('.has-sparkle').forEach(el => el.classList.remove('has-sparkle'));
  // Re-init
  initSparkles();
}

// Expose for use in other modules
window.initSparkles = initSparkles;
window.applyDynamicSparkles = applyDynamicSparkles;
window.refreshSparkle = refreshSparkle;
window.resetSparkles = resetSparkles;
window.markSparkleClicked = markSparkleClicked;

// ============================================
// "WHAT CHANGED" UI MARKERS (TASK-1098)
// ============================================
// Helps long-time users when a release MOVES or RENAMES a surface they
// navigate to by muscle memory (e.g. Payroll moving under Workforce). A small
// amber dot appears on the target nav item; clicking the item shows the blurb
// and dismisses the marker for that user.
//
// REGISTRY CONVENTION (the registry IS the builder gate — manually curated):
// - Add an entry when a release moves/renames a surface long-time users
//   navigate to. Do NOT add entries for new features (sparkles/trailguide
//   cover discovery) — only for "the thing you know moved."
// - Remove stale (expired) entries opportunistically on later releases.
// - Fields: id (stable, never reuse), sinceVersion (release that shipped the
//   change, informational), shippedAt (ISO date the change went live — drives
//   baseline + expiry since a version date isn't derivable client-side),
//   navKey (data-view of the sidebar / mobile more-sheet nav item), blurb
//   (one plain sentence, shown via toast), expiresDays (age cap from
//   shippedAt — after this the entry never shows and needs no persistence).
//
// Seen-state (per USER, not tenant — deliberate contrast with sparkles'
// tenant-wide storage): members merge changedSeen/changedBaselineAt into the
// EXISTING tenants/{tid}/members/{uid}/prefs/nav doc (prefId 'nav' is the only
// rules-permitted prefId, and the merge keeps any other prefs fields intact).
// NOTE — this is now the doc's ONLY writer: SidebarCustomize stopped storing
// navHidden in TASK-1224 (the dim went memory-only). Owner/legacy sessions
// without a member doc use localStorage. Driver-PIN/kiosk sessions are
// excluded entirely (shared devices; office members are the audience).
const CHANGED_UI_MARKERS = [
  {
    id: 'payroll-workforce-tab',
    sinceVersion: '1.15.1401',
    shippedAt: '2026-07-09',
    navKey: 'workforce',
    blurb: 'Payroll now lives under Workforce.',
    expiresDays: 14
  }
];

const CHANGED_MARKERS_MAX = 3; // never more than 3 dots at once (oldest first)

// { mode:'member'|'local', tenantId, uid?, changedSeen:{[id]:epochMs}, changedBaselineAt:epochMs|null }
let changedMarkersState = null;

function _changedMarkersLsKey(tenantId) {
  return `dispatchdeck_changed_seen_${tenantId}`;
}

// A real (non-owner, active, module-scoped) member reads/writes prefs/nav;
// everyone else (owner / legacy admin, no member doc) uses localStorage.
// (This was the same member test SidebarCustomize used before TASK-1224 made
// its dim ephemeral; the changed-markers still need the member/local split.)
function _changedMarkersMemberCtx() {
  const ma = window.AppState?.memberAccess;
  const isMember = !!(ma && ma.uid && Array.isArray(ma.modules)
    && ma.modules.length > 0 && ma.role !== 'owner'
    && !['revoked', 'inactive', 'pending'].includes(ma.status));
  return isMember ? { uid: ma.uid } : null;
}

async function _loadChangedMarkersState(tenantId) {
  const memberCtx = _changedMarkersMemberCtx();
  if (memberCtx) {
    try {
      const snap = await getDoc(doc(db, 'tenants', tenantId, 'members', memberCtx.uid, 'prefs', 'nav'));
      const data = (snap && snap.exists()) ? (snap.data() || {}) : {};
      return {
        mode: 'member', tenantId, uid: memberCtx.uid,
        changedSeen: (data.changedSeen && typeof data.changedSeen === 'object') ? data.changedSeen : {},
        changedBaselineAt: (typeof data.changedBaselineAt === 'number') ? data.changedBaselineAt : null
      };
    } catch (e) {
      // Read failed — fail closed (no markers) rather than falling back to a
      // second storage that could double-baseline this user.
      console.warn('[ChangedMarkers] prefs/nav read failed (non-fatal):', e && e.message);
      return null;
    }
  }
  // Owner / legacy session — tenant-keyed localStorage.
  try {
    const raw = localStorage.getItem(_changedMarkersLsKey(tenantId));
    const obj = raw ? JSON.parse(raw) : {};
    return {
      mode: 'local', tenantId,
      changedSeen: (obj && obj.changedSeen && typeof obj.changedSeen === 'object') ? obj.changedSeen : {},
      changedBaselineAt: (obj && typeof obj.changedBaselineAt === 'number') ? obj.changedBaselineAt : null
    };
  } catch (_) {
    return { mode: 'local', tenantId, changedSeen: {}, changedBaselineAt: null };
  }
}

// Persist a partial patch. Member path: setDoc(..., { merge: true }) on
// prefs/nav — merge means any other prefs fields are never clobbered, and
// changedSeen deep-merges per key.
function _persistChangedMarkersState(patch) {
  const st = changedMarkersState;
  if (!st || !st.tenantId) return;
  if (st.mode === 'member') {
    setDoc(doc(db, 'tenants', st.tenantId, 'members', st.uid, 'prefs', 'nav'), patch, { merge: true })
      .catch(err => console.warn('[ChangedMarkers] prefs write failed (non-fatal):', err && err.message));
  } else {
    try {
      localStorage.setItem(_changedMarkersLsKey(st.tenantId), JSON.stringify({
        changedBaselineAt: st.changedBaselineAt,
        changedSeen: st.changedSeen
      }));
    } catch (_) { /* quota / private mode — non-fatal */ }
  }
}

// Visible = shipped AFTER this user's baseline (never spam users who joined
// after the change) AND not already seen AND not expired. Oldest first, cap 3.
function _computeVisibleChangedMarkers() {
  const st = changedMarkersState;
  if (!st || typeof st.changedBaselineAt !== 'number') return [];
  const now = Date.now();
  return CHANGED_UI_MARKERS
    .map(e => ({ ...e, _shippedMs: Date.parse(e.shippedAt) }))
    .filter(e => Number.isFinite(e._shippedMs))
    .filter(e => e._shippedMs >= st.changedBaselineAt)
    .filter(e => !st.changedSeen[e.id])
    .filter(e => (now - e._shippedMs) < (e.expiresDays || 14) * 86400000)
    .sort((a, b) => a._shippedMs - b._shippedMs)
    .slice(0, CHANGED_MARKERS_MAX);
}

function _markChangedSeen(entryId) {
  const st = changedMarkersState;
  if (!st || st.changedSeen[entryId]) return;
  st.changedSeen[entryId] = Date.now();
  _persistChangedMarkersState({ changedSeen: { [entryId]: st.changedSeen[entryId] } });
}

function _dismissChangedMarker(entry) {
  // Both the sidebar item and the mobile more-sheet item carry a once-listener;
  // only the FIRST dismissal toasts (the other surface's residual listener
  // just no-ops after the entry is already seen).
  const alreadySeen = !!(changedMarkersState && changedMarkersState.changedSeen[entry.id]);
  _markChangedSeen(entry.id);
  document.querySelectorAll(`.changed-marker[data-changed-id="${CSS.escape(entry.id)}"]`).forEach(dot => {
    const host = dot.parentElement;
    dot.remove();
    if (host) host.classList.remove('has-changed-marker');
  });
  // Blurb via toast — showToast assigns message with textContent (safe), and
  // the registry is code-authored anyway. Longer duration so it can be read.
  if (!alreadySeen && typeof showToast === 'function') showToast(entry.blurb, 'info', null, 6000);
}

function _renderChangedMarkers(entries) {
  entries.forEach(entry => {
    // Target both the desktop sidebar item and the mobile more-sheet item
    // (both are static index.html markup keyed by data-view, same as sparkles).
    const targets = document.querySelectorAll(
      `.nav-item[data-view="${CSS.escape(entry.navKey)}"], .mobile-more-item[data-view="${CSS.escape(entry.navKey)}"]`
    );
    targets.forEach(el => {
      if (el.querySelector('.changed-marker')) return;
      el.classList.add('has-changed-marker');
      const dot = document.createElement('span');
      dot.className = 'changed-marker';
      dot.setAttribute('data-changed-id', entry.id);
      dot.title = entry.blurb; // desktop hover affordance; click is the real path
      el.appendChild(dot);
      // Clicking the nav item shows the blurb and marks the entry seen.
      // { once } so navigation clicks after dismissal aren't intercepted.
      el.addEventListener('click', () => _dismissChangedMarker(entry), { once: true });
    });
  });
}

// Init after login/nav render — called alongside initSparkles with the same
// defensive guards. Safe to call more than once (re-render is idempotent).
async function initChangedMarkers() {
  try {
    // Driver-PIN / public-access-token / kiosk sessions: excluded entirely.
    if (window.AppState?.isDriverMode || window.AppState?.accessToken) return;
    const tenantId = window.AppState?.tenant;
    if (!tenantId || tenantId === 'demo') return;
    if (!CHANGED_UI_MARKERS.length) return;

    changedMarkersState = await _loadChangedMarkersState(tenantId);
    if (!changedMarkersState) return;

    // First sighting of this user (no baseline, no seen state): stamp the
    // baseline "now" and show NOTHING — markers are only for users who were
    // around BEFORE a change shipped. Entries with shippedAt < baseline never
    // show for this user; entries shipping later will.
    if (changedMarkersState.changedBaselineAt == null) {
      // (integration tweak) Seed the first baseline from REAL tenure signals so
      // pre-existing users DO see markers for changes shipped during their
      // tenure — members: their invite-acceptance time; owner/legacy local
      // mode: tenant creation. Fallback "now" (legacy member docs without
      // acceptedAtMs) keeps the never-spam-new-users guarantee.
      let _base = null;
      if (changedMarkersState.mode === 'member') {
        const _am = window.AppState?.memberAccess?.acceptedAtMs;
        _base = (typeof _am === 'number' && _am > 0) ? _am : null;
      } else {
        const _tc = window.AppState?.tenantData?.createdAt;
        _base = _tc?.toMillis ? _tc.toMillis()
          : (_tc?._seconds ? _tc._seconds * 1000
            : (typeof _tc === 'number' ? _tc : null));
      }
      changedMarkersState.changedBaselineAt = _base || Date.now();
      _persistChangedMarkersState({ changedBaselineAt: changedMarkersState.changedBaselineAt });
      // With a real tenure baseline, markers can be immediately visible.
    }

    _renderChangedMarkers(_computeVisibleChangedMarkers());
  } catch (e) {
    console.warn('[ChangedMarkers] init failed (non-fatal):', e && e.message);
  }
}

window.initChangedMarkers = initChangedMarkers;


// ============================================
// DRIVER-PIN TIMESHEET HELPERS (TASK-363, 2026-05-26)
// Formerly part of the unauthenticated clock-in page (nuked in TASK-363). The
// pub* helpers below survive because they're reachable from dispatch.js
// initDriverTimesheet via window._timesheetCtx — the driver PIN view's
// "Edit Ticket" cascade. Page-only entry points were removed.
// ============================================
async function pubLoadTimesheet(personId) {
  const pc = window._timesheetCtx;
  const container = document.getElementById('pub-timesheet');
  const summaryBar = document.getElementById('pub-ts-summary');
  const daysContainer = document.getElementById('pub-ts-days');
  if (!container || !daysContainer || !pc) return;

  try {
    const now = new Date();
    const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 14); cutoff.setHours(0,0,0,0);
    const todayStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;

    const snap = await getDocs(query(
      collection(db, 'tenants', pc.tenantId, 'clockEntries'),
      where('personId', '==', personId),
      where('status', '==', 'completed'),
      orderBy('clockIn', 'desc'),
      limit(50)
    ));

    const entries = snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(e => {
        const cin = e.clockIn?.toDate?.() || new Date(e.clockIn);
        return cin >= cutoff;
      });

    if (entries.length === 0) {
      container.style.display = 'none';
      return;
    }

    // Group by date (YYYY-MM-DD)
    const dayMap = {};
    entries.forEach(e => {
      const cin = e.clockIn?.toDate?.() || new Date(e.clockIn);
      const key = `${cin.getFullYear()}-${String(cin.getMonth()+1).padStart(2,'0')}-${String(cin.getDate()).padStart(2,'0')}`;
      if (!dayMap[key]) dayMap[key] = [];
      dayMap[key].push(e);
    });
    const sortedDays = Object.keys(dayMap).sort((a, b) => b.localeCompare(a));

    // Calculate weekly totals (Mon-Sun boundaries)
    const getMonday = (d) => { const dt = new Date(d); const day = dt.getDay(); const diff = dt.getDate() - day + (day === 0 ? -6 : 1); dt.setDate(diff); dt.setHours(0,0,0,0); return dt; };
    const thisMonday = getMonday(now);
    const lastMonday = new Date(thisMonday); lastMonday.setDate(lastMonday.getDate() - 7);
    let thisWeekHrs = 0, lastWeekHrs = 0;
    entries.forEach(e => {
      const cin = e.clockIn?.toDate?.() || new Date(e.clockIn);
      const hrs = e.totalHours || 0;
      if (cin >= thisMonday) thisWeekHrs += hrs;
      else if (cin >= lastMonday) lastWeekHrs += hrs;
    });

    // Summary bar
    if (summaryBar) {
      const otThis = thisWeekHrs > 40 ? ` <span style="color:#dc2626;font-weight:700;font-size:0.7rem;">OT ${(thisWeekHrs - 40).toFixed(1)}h</span>` : '';
      const otLast = lastWeekHrs > 40 ? ` <span style="color:#dc2626;font-weight:700;font-size:0.7rem;">OT ${(lastWeekHrs - 40).toFixed(1)}h</span>` : '';
      summaryBar.innerHTML = `<div style="display:flex;justify-content:space-around;text-align:center;">
        <div><div style="font-size:0.65rem;text-transform:uppercase;letter-spacing:0.5px;color:var(--text-secondary);font-weight:600;">This Week</div><div style="font-size:1.3rem;font-weight:800;color:var(--text-primary);">${thisWeekHrs.toFixed(1)}<span style="font-size:0.75rem;font-weight:600;color:var(--text-secondary);"> hrs</span></div>${otThis}</div>
        <div style="width:1px;background:var(--border-light);"></div>
        <div><div style="font-size:0.65rem;text-transform:uppercase;letter-spacing:0.5px;color:var(--text-secondary);font-weight:600;">Last Week</div><div style="font-size:1.3rem;font-weight:800;color:var(--text-primary);">${lastWeekHrs.toFixed(1)}<span style="font-size:0.75rem;font-weight:600;color:var(--text-secondary);"> hrs</span></div>${otLast}</div>
      </div>`;
      summaryBar.style.display = 'block';
    }

    // TASK-602 — current OPEN pay-period boundary. Driver edit/delete is allowed
    // ONLY within the current open period; closed/paid periods are read-only even
    // with allowDriverTimeEdit ON (Chris 2026-06-12, FLSA). Boundary comes from
    // the EXISTING getPayPeriodDates(0) primitive (clock.js) off already-loaded
    // settings.payPeriodType/payPeriodAnchor — ZERO new Firestore reads (pure
    // client date math), computed ONCE here (not per row). Called via
    // window.ClockModule.* (Pitfall #1 — never cross-import clock.js with a ?v=).
    // Defensive fallback: if the primitive is unavailable, openPeriodStart stays
    // null → the per-row check below treats every row as in-period (no regression
    // of the affordance) while the firestore.rules 31d floor still backstops the
    // closed-period write/delete server-side.
    let _openPeriodStart = null;
    try {
      const _pp = window.ClockModule?.getPayPeriodDates?.(0);
      if (_pp?.start instanceof Date && !isNaN(_pp.start)) _openPeriodStart = _pp.start;
    } catch (_ppErr) { /* fall through — rules still enforce the 31d floor */ }

    // TASK-10039 — Edit Ticket is the person's grant (Roster, Billing holder),
    // not allowDriverTimeEdit; read once per render from the driver session.
    const _tkEdit = await pubTicketEditGranted();

    // Render day accordion rows
    daysContainer.innerHTML = sortedDays.map(dateKey => {
      const dayEntries = dayMap[dateKey];
      const dayTotal = dayEntries.reduce((s, e) => s + (e.totalHours || 0), 0);
      const d = new Date(dateKey + 'T12:00:00');
      const isToday = dateKey === todayStr;
      const dayLabel = isToday ? 'Today' : d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
      const expanded = isToday;

      // TASK-602 — default reconcile: OFF-by-default everywhere (safer money
      // setting). Was `!== false` (unset = ON) here while delete/cascades used
      // `!== true`/`=== true` (unset = OFF) — normalized so an unset/undefined
      // allowDriverTimeEdit means NO driver edit affordance, matching the rule
      // intent and the other call sites.
      const _editAllowed = window.AppState?.settings?.allowDriverTimeEdit === true;
      const shiftsHtml = dayEntries.map(entry => {
        const cin = entry.clockIn?.toDate?.() || (entry.clockIn ? new Date(entry.clockIn) : null);
        const cout = entry.clockOut?.toDate?.() || (entry.clockOut ? new Date(entry.clockOut) : null);
        const hrs = entry.totalHours?.toFixed(2) || '0.00';
        // TASK-9189 — time-only in/out made every overnight shift read backwards
        // here too (this is the driver-facing hours list). Shared formatter in
        // sharedFormat.js carries the +1d marker; window.Fmt is guaranteed since
        // app.js imports sharedFormat.js first as a side-effect module.
        const _shiftSpan = window.Fmt.shiftSpan(cin, cout);
        const cinStr = cin && !isNaN(cin) ? _shiftSpan.inText : '—';
        const coutStr = cout && !isNaN(cout) ? _shiftSpan.outHtml : 'Active';
        // TASK-602 — is THIS entry inside the current open pay period? Pure
        // client compare against the once-computed _openPeriodStart (zero reads).
        // If we couldn't derive the boundary (_openPeriodStart null) we don't
        // strip the affordance — the rules 31d floor still backstops the write.
        const _inOpenPeriod = !_openPeriodStart || (cin instanceof Date && !isNaN(cin) && cin >= _openPeriodStart);
        // Driver may edit/delete only when the toggle is on AND the entry is in
        // the open period. Closed-period rows show a lock hint instead.
        const _rowEditable = _editAllowed && _inOpenPeriod;
        // Distinguish the two locked states for the hint copy.
        const _lockTitle = !_editAllowed
          ? 'Times locked — contact your dispatcher to make changes'
          : 'This pay period is closed — contact your dispatcher to change past hours';
        return `
          <div style="padding:12px 20px;border-bottom:1px solid var(--border-light);">
            <div id="pub-shift-${entry.id}" style="display:flex;justify-content:space-between;align-items:center;">
              <div style="flex:1;min-width:0;">
                <div style="display:flex;align-items:baseline;gap:6px;margin-bottom:3px;">
                  <span style="font-weight:800;font-size:0.95rem;color:var(--text-primary);">${hrs} hrs</span>
                </div>
                <div style="display:flex;align-items:center;gap:4px;font-size:0.8rem;color:var(--text-secondary);font-weight:500;">
                  <span class="material-icons" style="font-size:13px;">schedule</span>
                  ${cinStr} – ${coutStr}
                </div>
                ${entry.customerName ? `<div style="font-size:0.75rem;color:var(--text-secondary);margin-top:2px;font-weight:500;"><span class="material-icons" style="font-size:12px;vertical-align:-1px;margin-right:2px;">business</span>${escapeHtml(entry.customerName)}${entry.jobTitle ? ' / ' + escapeHtml(entry.jobTitle) : ''}</div>` : ''}
              </div>
              ${_rowEditable ? `
              <button onclick="window.pubDeleteShift('${entry.id}','${entry.personId}')" style="background:none;border:none;cursor:pointer;padding:8px;color:var(--text-secondary);border-radius:8px;transition:background 0.15s;-webkit-tap-highlight-color:transparent;" title="Delete entry">
                <span class="material-icons" style="font-size:18px;">delete_outline</span>
              </button>
              ` : `
              <span class="material-icons" style="font-size:16px;color:var(--text-muted,#94A3B8);padding:8px;" title="${_lockTitle}">lock</span>
              `}
            </div>
            ${_rowEditable || (_tkEdit && _inOpenPeriod && entry.ticketId) ? `
            <div style="display:flex;gap:8px;margin-top:8px;flex-wrap:wrap;">
              ${_rowEditable ? `<button onclick="window.pubEditShift('${entry.id}')" style="background:linear-gradient(135deg,#F0FDF4,#ECFDF5);border:1.5px solid #BBF7D0;border-radius:8px;padding:6px 12px;cursor:pointer;font-size:0.75rem;font-weight:600;color:#16A34A;display:inline-flex;align-items:center;gap:5px;transition:background 0.15s;-webkit-tap-highlight-color:transparent;">
                <span class="material-icons" style="font-size:14px;">edit</span> Edit Timesheet
              </button>` : ''}
              ${_tkEdit && _inOpenPeriod && entry.ticketId ? `
              <button onclick="window.pubReEditTicket('${entry.ticketId}','${entry.id}')" style="background:linear-gradient(135deg,#EFF6FF,#F0F9FF);border:1.5px solid #BFDBFE;border-radius:8px;padding:6px 12px;cursor:pointer;font-size:0.75rem;font-weight:600;color:var(--brand-blue);display:inline-flex;align-items:center;gap:5px;transition:background 0.15s;-webkit-tap-highlight-color:transparent;">
                <span class="material-icons" style="font-size:14px;">receipt_long</span> Edit Ticket
              </button>
              ` : ''}
            </div>
            ` : ''}
            ${!_inOpenPeriod && _editAllowed ? `
            <div style="margin-top:6px;font-size:0.7rem;color:var(--text-muted,#94A3B8);display:flex;align-items:center;gap:4px;">
              <span class="material-icons" style="font-size:13px;">lock</span> Pay period closed
            </div>
            ` : ''}
          </div>
        `;
      }).join('');

      const entryCount = dayEntries.length;
      const hintText = expanded ? '' : `<span data-ts-hint style="font-size:0.7rem;color:var(--brand-blue);font-weight:600;">${entryCount} shift${entryCount !== 1 ? 's' : ''} — tap to view</span>`;

      return `
        <div style="border-bottom:1px solid var(--border-light);">
          <button onclick="window.pubToggleTimesheetDay('${dateKey}')" style="width:100%;display:flex;align-items:center;justify-content:space-between;padding:12px 20px;min-height:48px;background:${expanded ? '#f0f9ff' : '#fafbfc'};border:none;cursor:pointer;-webkit-tap-highlight-color:transparent;transition:background 0.15s;">
            <div style="display:flex;flex-direction:column;align-items:flex-start;gap:3px;">
              <div style="display:flex;align-items:center;gap:8px;">
                <span style="font-weight:700;font-size:0.85rem;color:var(--text-primary);">${dayLabel}</span>
                ${isToday ? '<span style="background:var(--brand-blue);color:#fff;font-size:0.6rem;font-weight:700;padding:2px 7px;border-radius:10px;text-transform:uppercase;letter-spacing:0.5px;">Today</span>' : ''}
              </div>
              ${hintText}
            </div>
            <div style="display:flex;align-items:center;gap:6px;">
              <span style="font-weight:700;font-size:0.85rem;color:var(--text-primary);">${dayTotal.toFixed(1)} hrs</span>
              <span class="material-icons" id="pub-ts-chev-${dateKey}" style="font-size:22px;color:var(--brand-blue);">${expanded ? 'expand_less' : 'expand_more'}</span>
            </div>
          </button>
          <div id="pub-ts-day-${dateKey}" style="display:${expanded ? 'block' : 'none'};">
            ${shiftsHtml}
          </div>
        </div>
      `;
    }).join('');

    container.style.display = 'block';
  } catch (err) {
    console.error('[PublicClock] Error loading timesheet:', err);
    container.style.display = 'none';
  }
}

window.pubLoadTimesheet = pubLoadTimesheet;

window.pubToggleTimesheetDay = function(dateKey) {
  const content = document.getElementById(`pub-ts-day-${dateKey}`);
  const chev = document.getElementById(`pub-ts-chev-${dateKey}`);
  if (!content) return;
  const show = content.style.display === 'none';
  content.style.display = show ? 'block' : 'none';
  if (chev) {
    chev.textContent = show ? 'expand_less' : 'expand_more';
    // Update button background + hide/show hint
    const btn = chev.closest('button');
    if (btn) {
      btn.style.background = show ? '#f0f9ff' : '#fafbfc';
      const hint = btn.querySelector('[data-ts-hint]');
      if (hint) hint.style.display = show ? 'none' : '';
    }
  }
};

window.pubDeleteShift = async function(entryId, personId) {
  const pc = window._timesheetCtx;
  if (!pc) return;
  // Driver-scope gate: deletion is an edit. Honor allowDriverTimeEdit setting.
  if (window.AppState?.settings?.allowDriverTimeEdit !== true) {
    showToast('Times are locked — contact your dispatcher to make changes', 'info');
    return;
  }
  try {
    // Load entry first to check what will be deleted
    const entrySnap = await getDoc(doc(db, 'tenants', pc.tenantId, 'clockEntries', entryId));
    const e = entrySnap.exists() ? entrySnap.data() : null;
    if (!e) { showToast('Entry not found', 'error'); return; }

    // TASK-602 — a deleted shift is lost payroll; closed periods are read-only
    // for drivers (FLSA). UI guard for a graceful toast; the firestore.rules 31d
    // floor on the clockEntries delete self-edit branch is the real backstop.
    if (!pubEntryInOpenPeriod(e)) {
      showToast(PUB_PERIOD_CLOSED_MSG, 'info');
      return;
    }

    // Block deletion if the ticket is invoiced or ready (past draft stage).
    // TASK-566 — also blocks when the biller flipped the per-ticket
    // driverEditLocked 🔒 (rules deny the cascade's ticket writes anyway;
    // this keeps the failure graceful).
    if (e.ticketId) {
      const ticketSnap = await getDoc(doc(db, 'tenants', pc.tenantId, 'tickets', e.ticketId));
      if (ticketSnap.exists()) {
        const tData = ticketSnap.data();
        // TASK-10039 — this delete takes the driver's line off the ticket (or the
        // whole ticket): a ticket change after clock-out needs the grant.
        if (!(await pubTicketEditGranted())) {
          showToast('This shift has a ticket — ask the office to delete it.', 'error');
          return;
        }
        if (pubTicketIsDriverLocked(tData) || tData.status === 'paid') {
          showToast(pubTicketIsSent(tData) ? PUB_TICKET_SENT_MSG
            : tData.driverEditLocked === true
            ? PUB_TICKET_LOCKED_MSG
            : `Can't delete — this entry is on a ${tData.invoiced ? 'invoiced' : (tData.status || 'locked')} ticket. Contact your dispatcher.`, 'error');
          return;
        }
      }
    }

    // Build confirm message based on whether ticket will be deleted
    let willDeleteTicket = false;
    let confirmMsg = 'This will permanently delete this shift entry.';
    if (e.ticketId) {
      const linesSnap = await getDocs(collection(db, 'tenants', pc.tenantId, 'tickets', e.ticketId, 'ticketLines'));
      const otherEmployees = linesSnap.docs.filter(d => d.data().type === 'EMPLOYEE' && d.id !== e.ticketLineId);
      willDeleteTicket = otherEmployees.length === 0;
      if (willDeleteTicket) {
        confirmMsg = 'This will permanently delete this shift entry AND the associated billing ticket (including all equipment, downtime, standby, and line items).';
      } else {
        confirmMsg = 'This will permanently delete this shift entry. The billing ticket will be kept for the other employees on it.';
      }
    }

    const confirmed = await showConfirm('Delete Shift?', confirmMsg, {
      confirmText: 'Delete', cancelText: 'Cancel', danger: true
    });
    if (!confirmed) return;

    // Audit log: snapshot the entry before deleting
    // Uses timestamp/user to match the audit viewer's orderBy query
    try {
      await addDoc(collection(db, 'tenants', pc.tenantId, 'clockAuditLog'), {
        action: 'DELETE',
        source: 'driver-timesheet',
        clockEntryId: entryId,
        personId: e.personId || personId,
        personName: e.personName || null,
        entrySnapshot: e,
        user: pc.personName || pc.personId || 'driver-pin',
        timestamp: Timestamp.now()
      });
    } catch (auditErr) {
      console.warn('[PublicClock] Audit log failed:', auditErr);
    }
    // CLOCK_ENTRY_DELETE — driver-initiated path. Mirrors the admin path in
    // clock.js so payroll disputes can be queried from a single auditLog stream
    // (Tier A spec). Red flags identify hoursLost / ticket entanglement.
    try {
      const ci = e.clockIn?.toDate?.() || (e.clockIn ? new Date(e.clockIn) : null);
      const co = e.clockOut?.toDate?.() || (e.clockOut ? new Date(e.clockOut) : null);
      const hoursLost = (ci && co) ? Math.max(0, (co - ci) / 3600000) : 0;
      const createdAt = e.createdAt?.toDate?.() || (e.createdAt ? new Date(e.createdAt) : null);
      const entryAgeDays = createdAt ? Math.floor((Date.now() - createdAt.getTime()) / 86400000) : null;
      window.logAudit?.('clock', 'CLOCK_ENTRY_DELETE', {
        source: 'driver',
        entryId,
        personId: e.personId || personId,
        personName: e.personName || null,
        jobId: e.jobId || null,
        ticketId: e.ticketId || null,
        willDeleteTicket,
        redFlags: {
          deletedBy: pc.personName || pc.personId || 'driver-pin',
          hadTicket: !!e.ticketId,
          hadCoTime: !!co,
          hoursLost: Number(hoursLost.toFixed(2)),
          entryAgeDays
        }
      });
    } catch (auditErr) { console.warn('[PublicClock] CLOCK_ENTRY_DELETE audit failed:', auditErr); }

    // Now delete
    if (e.ticketId) {
      // TASK-4699 — this delete is SWALLOWED (pre-existing, deliberately: a shift
      // delete must not be blocked by a stuck line). So it is the one place in
      // this task where a customer-visible change can silently NOT happen, and
      // the stamp below has to know. Tracked rather than assumed — COD round 2's
      // Code Reviewer ⚠️ caught this hunk carrying exactly the bug the PIN test
      // had just measured in pubSaveEditShift: a legacy EMPLOYEE line with no
      // clockEntryId fails driverOwnsTicketLine, so the LINE delete is denied
      // while the ticket-HEADER write is still allowed. Stamping regardless would
      // flag a ticket whose crew row never left it.
      let _lineRemoved = false;
      if (e.ticketLineId) {
        try {
          await deleteDoc(doc(db, 'tenants', pc.tenantId, 'tickets', e.ticketId, 'ticketLines', e.ticketLineId));
          _lineRemoved = true;
        } catch (tlErr) { console.warn('[PublicClock] Could not delete ticket line:', tlErr); }
      }
      if (willDeleteTicket) {
        const remainingLines = await getDocs(collection(db, 'tenants', pc.tenantId, 'tickets', e.ticketId, 'ticketLines'));
        for (const lineDoc of remainingLines.docs) {
          await deleteDoc(doc(db, 'tenants', pc.tenantId, 'tickets', e.ticketId, 'ticketLines', lineDoc.id));
        }
        await deleteDoc(doc(db, 'tenants', pc.tenantId, 'tickets', e.ticketId));
      } else if (_lineRemoved) {
        // TASK-9646 S2 — no grandTotal here any more: the server re-derives the
        // header from the lines on the line delete above (onTicketLineWrite).
        // This bare-sum recompute was also wrong (no DOWNTIME subtraction, no
        // tax). With the money gone the write only carries the dirty stamp, so
        // it is skipped entirely when the line delete did not land.
        await updateDoc(doc(db, 'tenants', pc.tenantId, 'tickets', e.ticketId), {
          // TASK-4699 — this branch KEEPS the ticket and deletes this driver's
          // EMPLOYEE line off it. The emailed copy prints every EMPLOYEE line by
          // name/role with its hours (functions/src/index.js:29941), so a whole
          // crew row vanishing is as visible to the customer as a rate change.
          // The sibling branch above DELETES the ticket outright — there is no
          // doc left to stamp there, deliberately.
          //
          // GATED ON THE LINE ACTUALLY HAVING GONE. `_lineRemoved` is false when
          // the delete was rules-denied and swallowed above, and also when the
          // entry carried no ticketLineId at all — in both cases nothing the
          // customer sees changed, and stamping would flag a ticket nobody's
          // customer copy diverged from (the `else if` above now skips it).
          ticketEmailDirtyAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        });
      }
    }
    await deleteDoc(doc(db, 'tenants', pc.tenantId, 'clockEntries', entryId));

    // TASK-566 — mirror the ticket-side effect of this shift delete into
    // jobAuditLog (tickets audit there; the clockAuditLog rows above cover
    // the clock-entry side).
    if (e.ticketId) {
      pubLogDriverTicketAudit('DRIVER_SHIFT_DELETE_TICKET_CASCADE', e.ticketId, {
        clockEntryId: entryId,
        ticketDeleted: willDeleteTicket,
        before: {
          ticketLineId: e.ticketLineId || null,
          clockIn: pubAuditIso(e.clockIn),
          clockOut: pubAuditIso(e.clockOut),
          totalHours: e.totalHours ?? null
        },
        after: null
      });
    }

    showToast('Entry deleted', 'success');
    pubLoadTimesheet(personId);
  } catch (err) {
    console.error('[PublicClock] Delete error:', err);
    showToast(pubIsLockDenied(err) ? PUB_TICKET_LOCKED_MSG : 'Error deleting entry', 'error');
  }
};

window.pubEditShift = function(entryId) {
  const pc = window._timesheetCtx;
  const row = document.getElementById(`pub-shift-${entryId}`);
  if (!row || !pc) return;

  // Find the entry from the rendered data — re-query Firestore
  getDoc(doc(db, 'tenants', pc.tenantId, 'clockEntries', entryId)).then(async snap => {
    if (!snap.exists()) { showToast('Entry not found', 'error'); return; }
    const entry = snap.data();

    const cin = entry.clockIn?.toDate?.() || new Date(entry.clockIn);
    const cout = entry.clockOut?.toDate?.() || new Date(entry.clockOut);

    // clock-dst-all — prefill in the COMPANY zone (window.TenantLocalTime), keyed
    // per box so an unchanged repeated-hour time re-saves as the stored instant.
    const fmtDT = (d, key) => window.TenantLocalTime.formatLocal(d, { key });

    // TASK-9217 — the entry id goes into a JS STRING inside an HTML attribute.
    // No html escaper fixes that context; escJsAttr is the one that does. The
    // pre-existing handlers below are unchanged (separate concern, not this
    // task's), but nothing NEW on this row goes in unescaped.
    const jsEntryId = window.escJsAttr ? window.escJsAttr(entryId) : String(entryId);
    // Only a COMPLETED pre-image is bounded by the TASK-9191 window clause —
    // see pubClockWindowIsBounded for the three shapes that must NOT warn.
    const windowBounded = pubClockWindowIsBounded(entry);

    row.innerHTML = `
      <div style="width:100%;display:flex;flex-direction:column;gap:8px;">
        <div style="display:flex;gap:8px;align-items:center;">
          <label style="font-size:0.75rem;font-weight:600;color:var(--text-secondary);min-width:28px;">In:</label>
          <input type="datetime-local" id="pub-edit-cin-${entryId}" value="${fmtDT(cin, `pub-edit-cin-${entryId}`)}" oninput="window.pubEditShiftWindowHint('${jsEntryId}',${windowBounded})" style="flex:1;padding:8px;border:1px solid var(--border-color);border-radius:var(--radius-sm);font-size:0.85rem;">
        </div>
        <div style="display:flex;gap:8px;align-items:center;">
          <label style="font-size:0.75rem;font-weight:600;color:var(--text-secondary);min-width:28px;">Out:</label>
          <input type="datetime-local" id="pub-edit-cout-${entryId}" value="${fmtDT(cout, `pub-edit-cout-${entryId}`)}" oninput="window.pubEditShiftWindowHint('${jsEntryId}',${windowBounded})" style="flex:1;padding:8px;border:1px solid var(--border-color);border-radius:var(--radius-sm);font-size:0.85rem;">
        </div>
        <!-- TASK-9217 — predicts the TASK-9191 denial BEFORE the driver presses
             Save. Amber-on-amber: this row sits on a light card, so the text is
             a dark amber (#92400E), never var(--text-muted). -->
        <div id="pub-edit-warn-${entryId}" style="display:none;padding:8px 10px;border-left:3px solid #D97706;background:#FEF3C7;border-radius:var(--radius-sm);font-size:0.78rem;line-height:1.35;color:#92400E;"></div>
        <div style="display:flex;gap:8px;justify-content:flex-end;">
          <button onclick="window.pubCancelEditShift('${entryId}','${entry.personId}')" style="padding:6px 14px;font-size:0.8rem;font-weight:600;border:1px solid var(--border-color);border-radius:var(--radius-sm);background:#fff;color:var(--text-primary);cursor:pointer;">Cancel</button>
          <button onclick="window.pubSaveEditShift('${entryId}','${entry.personId}')" style="padding:6px 14px;font-size:0.8rem;font-weight:600;border:none;border-radius:var(--radius-sm);background:var(--brand-blue);color:#fff;cursor:pointer;">Save</button>
        </div>
      </div>
    `;
    // Paint once on open: an entry that is ALREADY over 24h (9 live TITS rows)
    // must say so before the driver touches anything, not only after he edits.
    window.pubEditShiftWindowHint(entryId, windowBounded);
  });
};

window.pubCancelEditShift = function(entryId, personId) {
  pubLoadTimesheet(personId);
};

// TASK-1651/1653 — Chris: "we don't bill people on tickets, just equipment."
// EMPLOYEE ticket lines are payroll shadows: they carry HOURS for review, never
// a customer rate or amount. Every canonical writer (jobboard, dispatch,
// clock.js, employeeLineBackfill) already writes rate:0 / lineAmount:0 — these
// named constants pin the public-clock writers to the same rule, so a future
// "just multiply by the rate" edit reads as the violation it is. (The phantom
// PUB_RATES global these replace was never defined anywhere — every reference
// threw a ReferenceError.) EQUIPMENT lines are created UNPRICED (rate 0) and
// the server (onTicketLineWrite → equipmentLineEnrichment) prices them from
// the customer's price book — the TASK-1165 model; a client-invented rate
// would also fail the firestore.rules S1b driver-arm rate-0 create gate.
// TASK-9646 S5 — `rate` is no longer sent on the EMPLOYEE line update at all
// (firestore.rules tlMoneyPinned: a driver arm never changes a rate); the
// constant that pinned it to 0 is gone with it. lineAmount stays pinned to 0.
const PUB_EMPLOYEE_LINE_AMOUNT = 0;
const PUB_EQUIPMENT_RATE_SERVER_PRICED = 0;

window.pubSaveEditShift = async function(entryId, personId) {
  const pc = window._timesheetCtx;
  const cinVal = document.getElementById(`pub-edit-cin-${entryId}`)?.value;
  const coutVal = document.getElementById(`pub-edit-cout-${entryId}`)?.value;

  if (!cinVal || !coutVal) { showToast('Both times required', 'error'); return; }

  // clock-dst-all — typed times are COMPANY time: resolve both (asks which one
  // in the repeated fall-back hour, refuses a nonexistent time or an unreadable
  // zone) BEFORE anything is read or written.
  const _tlt = window.TenantLocalTime;
  const _cinKey = `pub-edit-cin-${entryId}`, _coutKey = `pub-edit-cout-${entryId}`;
  const _res = await _tlt.resolveAll([
    { str: cinVal, key: _cinKey, label: 'Clock In', optional: false },
    { str: coutVal, key: _coutKey, label: 'Clock Out', optional: false },
  ], { describe: (it, ms) => {
    const other = it.key === _cinKey ? _tlt.peekMs(coutVal, { key: _coutKey }) : _tlt.peekMs(cinVal, { key: _cinKey });
    const h = it.key === _cinKey ? (other - ms) / 3600000 : (ms - other) / 3600000;
    return Number.isFinite(h) && h > 0 ? ` — ${_tlt.hm(h)} shift` : '';
  } });
  if (!_res.ok) { showToast(_res.message, 'error'); return; }
  const newClockIn = new Date(_res.ms[0]);
  const newClockOut = new Date(_res.ms[1]);
  if (newClockOut <= newClockIn) { showToast('Clock Out must be after Clock In', 'error'); return; }

  const totalHours = Math.round(((newClockOut - newClockIn) / 3600000) * 100) / 100;

  // TASK-9217 — hoisted out of the try so the catch can classify a denial off
  // the PRE-IMAGE (is this entry even bounded by the 24h clause?) instead of
  // off the error, which carries nothing to classify. See pubIsLockDenied.
  let preEntry = null;

  try {
    // TASK-566 — read the entry FIRST: (a) before-values for the audit row,
    // (b) lock pre-check on its ticket BEFORE touching the clock entry, so a
    // biller-locked/invoiced ticket can't end up with hours updated on the
    // clock entry but the EMPLOYEE line write rules-denied (partial state).
    const preSnap = await getDoc(doc(db, 'tenants', pc.tenantId, 'clockEntries', entryId));
    const e = preSnap.exists() ? preSnap.data() : null;
    if (!e) { showToast('Entry not found', 'error'); return; }
    preEntry = e;
    // TASK-602 — closed pay periods are read-only for drivers (FLSA). UI-side
    // guard for a graceful toast; firestore.rules 31d floor is the real backstop.
    if (!pubEntryInOpenPeriod(e)) {
      showToast(PUB_PERIOD_CLOSED_MSG, 'error');
      pubLoadTimesheet(personId);
      return;
    }
    // TASK-9217 — the TASK-9191 window bound, PREDICTED. This save writes all
    // four window keys unconditionally, so on a COMPLETED pre-image the rules
    // will refuse it outright whenever the post-image window exceeds 24h. Ahead
    // of the read-and-write below rather than after it: it costs nothing, it is
    // deterministic (not a race like the lock check), and the refusal it names
    // is the one the driver cannot otherwise identify.
    //
    // 🔴 NOT A TRAP, AND THE EDITOR STAYS OPEN. Deliberately no
    // pubLoadTimesheet() here — every other bail on this path reloads the list,
    // which would throw away the dates the driver just typed and leave him
    // staring at the row he was trying to fix. He has two ways forward and both
    // stay reachable: shrink the window back under 24h (allowed by the rules,
    // verified on the emulator, and the actual fix for a forgotten clock-out),
    // or take the office route the message names.
    if (pubClockWindowIsBounded(e)) {
      const windowRefusal = pubClockWindowRefusal(newClockIn, newClockOut);
      if (windowRefusal) {
        showToast(windowRefusal, 'error');
        window.pubEditShiftWindowHint?.(entryId, true);
        return;
      }
    }
    if (e.ticketId) {
      const { editable, ticket: _lockT } = await pubCheckTicketEditable(e.ticketId);
      if (!editable) {
        showToast(pubTicketLockedMsg(_lockT), 'error');
        pubLoadTimesheet(personId);
        return;
      }
    }

    // TASK-9196 — `hours` IS WRITTEN HERE, and it is not redundancy for its own
    // sake. Every OTHER writer in the tree already keeps the pair in sync —
    // clock.js :15006 / :16175 / :18311 / :19648 / :20462 ("keep both fields in
    // sync — see fix log 2026-05-07"), jobboard.js :10555 / :10653,
    // ticketGateway.js :3502 — and these two correction paths (here and the
    // re-edit cascade below) were the ONLY two that wrote totalHours alone. So
    // `hours` froze at its pre-correction value the instant anyone fixed a
    // shift: 23-25 live TITS entries disagree, and in every one of them it is
    // `hours` that is wrong (worst: 70.56 against a 4.70h window).
    //
    // 🔴 THE SIBLING IS NOT DEAD — DO NOT "SIMPLIFY" IT AWAY, and do not delete
    // the `totalHours || hours` fallback in the readers either. 13 live
    // completed entries (auto-clock) carry NO numeric totalHours at all, so on
    // those rows `hours` is the only number there is. The split, measured:
    //   • totalHours ONLY, no fallback — clock.js's payroll period build and
    //     `_payEngineEntries` (:5381) → PayRates.computePeriodPay, and the
    //     QuickBooks payroll export (functions/src/index.js :30776, which emits
    //     "0" when totalHours is absent). THE MONEY SURFACES ALREADY IGNORE
    //     `hours`, which is why this defect never mis-paid a check.
    //   • `totalHours || hours` fallback — bi.js (~20 sites: accrued wages, job
    //     costing, crew/customer rollups), hr.js :12436 (PTO accrual YTD),
    //     ticketGateway.js :2828/:2889/:2921/:2979 and employeeLineBackfill.js
    //     :261 (EMPLOYEE ticket-line hours; rate is pinned 0, so display not
    //     money). Those are the surfaces the stale sibling was corrupting.
    // Removing the fallback would zero the 13 auto-clock rows on exactly those
    // surfaces — strictly worse than the drift it would close. So: write both,
    // keep the fallback, and let a correction self-heal its own row.
    //
    // 🔴 AND THIS PASSES THE TASK-9191 BOUND — the fear that it would not was
    // checked, not assumed. The self-arm bounds `hours` to the POST-image
    // window within 0.02h *only when `hours` is in affectedKeys()*. We write the
    // FRESHLY COMPUTED value (identical to totalHours, itself
    // round((out-in)/3600000, 2)), so it is inside the bound by construction; if
    // it happens to already equal that value it isn't in affectedKeys() and the
    // clause is skipped. Writing the STALE value through would have been denied
    // — we never do. Writing both also UNBLOCKS a case the old shape would now
    // deny: vector 3 requires the merged post-image `hours` to match a ZERO
    // window when totalHours is 0, and the pre-image's stale `hours` rides into
    // that post-image whether or not we write it.
    await updateDoc(doc(db, 'tenants', pc.tenantId, 'clockEntries', entryId), {
      clockIn: Timestamp.fromDate(newClockIn),
      clockOut: Timestamp.fromDate(newClockOut),
      totalHours,
      hours: totalHours,
      updatedAt: Timestamp.now()
    });

    // Also update the EMPLOYEE ticketLine if it exists.
    // TASK-1653 — e.rate is the clock entry's PAYROLL rate (gateway:manualEntry
    // stamps it); it must never price a customer-facing ticket line. Pin the
    // EMPLOYEE line's money to 0 (see the constants above pubSaveEditShift).
    if (e.ticketId && e.ticketLineId) {
      // TASK-4699 — the driver is about to move the HOURS printed against his
      // name on the emailed ticket (functions/src/index.js:29941 renders every
      // EMPLOYEE line as `${hours}h`). The money is pinned to 0 on this line
      // type, so grandTotal does NOT move and nothing else on this path would
      // ever tell the biller the customer's copy went out of date.
      //
      // 🔴 THE ONE STAMP IN THIS TASK THAT IS ITS OWN WRITE — everywhere else it
      // rides an update the caller already makes, but this function touches only
      // the clock entry and the line, never the ticket header.
      //
      // IT RUNS **AFTER** THE LINE WRITE AND IS **NOT** SWALLOWED. That exact
      // ordering is the answer to two findings, and both were measured, not
      // reasoned:
      //
      // 1. My first cut put it after but inside its own try/catch, so a stamp
      //    failure could never surface as "Error saving" on a save whose payroll
      //    hours had already landed (the TASK-428 stranding shape). Three COD
      //    lanes — Code Reviewer ⚠️, Skeptic, AuthZ — independently named the
      //    hole in that: a swallow leaves a customer-visible hours edit SILENTLY
      //    unflagged, which is the failure class this whole feature exists to
      //    remove. So the swallow is gone.
      //
      // 2. My fix for (1) moved the stamp BEFORE the line write, on the argument
      //    that the two share a denial condition (TASK-4640 aligned the tkUpdOk
      //    driver arm and ticketOpenForDriverEdit on the same six locks). 🔴 THE
      //    PIN TEST DISPROVED THAT — tests/task4699-driver-stale-pin.spec.js ran
      //    it and the header stamp LANDED while the line write came back
      //    "Missing or insufficient permissions", leaving the flag set on a
      //    ticket whose content never moved. The gates are not symmetric: the
      //    LINE gate adds driverOwnsTicketLine, which requires the line to carry
      //    a clockEntryId resolving to this driver's own entry. A legacy
      //    EMPLOYEE line without one (the state TASK-1027's backfill exists for)
      //    is exactly that shape, so the false positive is reachable in real
      //    data, not just in a race.
      //
      // Stamping last, unswallowed, gets both: a denied line write throws before
      // anything is stamped (no noise), and a stamp that fails is reported
      // instead of hidden (no silence). It adds no new stranding class either —
      // the line write on the line above is already unswallowed with the same
      // user-visible outcome, the PAYROLL write (clockEntries) has already landed
      // and is outside this failure domain, and a retry rewrites identical values.
      await updateDoc(
        doc(db, 'tenants', pc.tenantId, 'tickets', e.ticketId, 'ticketLines', e.ticketLineId),
        { fromTime: Timestamp.fromDate(newClockIn), toTime: Timestamp.fromDate(newClockOut), hours: totalHours, lineAmount: PUB_EMPLOYEE_LINE_AMOUNT, updatedAt: Timestamp.now() }
      );
      await updateDoc(doc(db, 'tenants', pc.tenantId, 'tickets', e.ticketId), {
        ticketEmailDirtyAt: Timestamp.now(), updatedAt: Timestamp.now()
      });
    }

    // TASK-566 — audit: this path edits HOURS (clockEntries = payroll source)
    pubLogDriverTicketAudit('DRIVER_SHIFT_TIME_EDIT', e.ticketId || null, {
      clockEntryId: entryId,
      touchesPayrollHours: true,
      // TASK-9196 — `hours` is on BOTH sides. It is a wage field this write now
      // moves (it did not before), and the standing rule on a money-governing
      // field is old AND new value in the audit row, not just the new one. On a
      // pre-corrected entry the before/after delta here is the whole story of
      // the 23-25 divergent rows, recorded the moment one of them is touched.
      before: { clockIn: pubAuditIso(e.clockIn), clockOut: pubAuditIso(e.clockOut), totalHours: e.totalHours ?? null, hours: e.hours ?? null },
      after: { clockIn: newClockIn.toISOString(), clockOut: newClockOut.toISOString(), totalHours, hours: totalHours }
    });

    showToast('Times updated', 'success');
    pubLoadTimesheet(personId);
  } catch (err) {
    console.error('[PublicClock] Error saving edit:', err);
    // TASK-9217 — three-way, and the ORDER is the whole point.
    //
    // Before this, a permission-denied here became PUB_TICKET_LOCKED_MSG — "this
    // ticket was locked by the office" — for EVERY denial, the 24h one included.
    // (The firestore.rules TASK-9191 comment says this path shows "Error saving";
    // it does not, and has not since TASK-566. Corrected there is not mine to
    // make — the rules file belongs to another lane — but the record is here.)
    // That is worse than generic: it names a lock that does not exist and sends
    // the driver to argue with his biller about it.
    //
    // 🔴 KEYED OFF THE WINDOW WE COMPUTED, NOT OFF THE ERROR. The emulator probe
    // showed the 24h denial and an unrelated denial are byte-identical, so the
    // only honest discriminator is: was this write one the 24h clause would
    // refuse? That needs BOTH halves — a bounded (COMPLETED) pre-image AND an
    // over-24h post-image. If either is false we say nothing about 24 hours,
    // because blaming this rule for an unrelated failure sends the driver to
    // dispatch for a bug and is strictly worse than the generic toast.
    //
    // Reachable only past the pre-check above — devtools, a stale render, or the
    // entry going 'completed' between the read and the write — but a denial that
    // can happen at all has to land somewhere a human can read.
    const _windowDenial = pubIsLockDenied(err) && pubClockWindowIsBounded(preEntry)
      ? pubClockWindowRefusal(newClockIn, newClockOut)
      : null;
    showToast(_windowDenial || (pubIsLockDenied(err) ? PUB_TICKET_LOCKED_MSG : 'Error saving'), 'error');
  }
};

// ============================================
// PUBLIC CLOCK: TICKET VIEW / EDIT / DELETE
// ============================================

// TASK-566 — single lock predicate for the driver timesheet. A past ticket is
// read-only for the driver when it has moved past draft (status), is invoiced
// (hard lock, always), or the biller flipped the per-ticket 🔒 driverEditLocked
// in the billing editor (the biller keeps editing; only the driver is locked).
function pubTicketIsDriverLocked(ticketData) {
  return ['ready', 'invoiced', 'sent', 'archived'].includes(ticketData?.status)
    // TASK-4600 — the THIRD lock site, and the one I missed on my own sweep (COD
    // Code Reviewer found it). billing.js and jobboard.js were widened to the
    // display predicate; this public-clock/timesheet path was still on the raw
    // boolean, so line edits/deletes and photo deletion stayed open on a ticket
    // carrying an invoiceNumber with the flag not yet true. Every lock that means
    // "this ticket is financially closed" must read the SAME predicate, or the
    // narrowest one is the real one.
    || ticketData?.invoiced === true
    || !!ticketData?.invoiceNumber
    || !!ticketData?.invoicedAt
    || ticketData?.driverEditLocked === true;
}

// TASK-566 — fresh-read lock pre-check before any driver write to a past
// ticket. Catches the biller flipping the lock (or invoicing) WHILE the driver
// is mid-edit, so the driver gets a clear toast instead of a raw rules denial.
async function pubCheckTicketEditable(ticketId) {
  const pc = window._timesheetCtx;
  if (!pc || !ticketId) return { editable: true, ticket: null };
  try {
    const snap = await getDoc(doc(db, 'tenants', pc.tenantId, 'tickets', ticketId));
    if (!snap.exists()) return { editable: true, ticket: null };
    const t = snap.data();
    return { editable: !pubTicketIsDriverLocked(t), ticket: t };
  } catch (e) {
    // Read failed — let the write attempt proceed; rules are the backstop.
    return { editable: true, ticket: null };
  }
}

// TASK-10039 — may this driver change a ticket after clock-out? Only with the
// per-person grant (Roster → "Can edit their own tickets after clock-out",
// Billing holder or owner), mirrored by the server onto driverSessions/{uid}
// .ticketEdit — the same field firestore.rules reads. Absent / unreadable /
// no session = NO. The clock-out ticket build never comes through here.
async function pubTicketEditGranted() {
  const uid = auth.currentUser?.uid;
  if (!uid) return false;
  try {
    const s = await getDoc(doc(db, 'driverSessions', uid));
    return s.exists() && s.data()?.ticketEdit === true;
  } catch (_e) { return false; }
}
const PUB_TICKET_GRANT_MSG = 'Ask the office to change this ticket.';
async function pubRequireTicketGrant() {
  if (await pubTicketEditGranted()) return true;
  showToast(PUB_TICKET_GRANT_MSG, 'error');
  return false;
}

const PUB_TICKET_LOCKED_MSG = 'This ticket was locked by the office — changes not saved';
// TASK-10026 — a ticket that went to the customer is closed to the driver. The
// server stamps the driver lock with this actor on every successful send
// (functions/src/services/ticketSendLog.js sentLockPatch), so the lock itself
// is already in pubTicketIsDriverLocked; this only picks the words.
const PUB_SENT_LOCK_ACTOR = 'ticket-sent';
const PUB_TICKET_SENT_MSG = 'This ticket was already sent — ask the office to change it';
function pubTicketIsSent(t) {
  return (t?.driverEditLocked === true && t?.driverEditLockedBy === PUB_SENT_LOCK_ACTOR) || t?.status === 'sent';
}
function pubTicketLockedMsg(t) {
  return pubTicketIsSent(t) ? PUB_TICKET_SENT_MSG : PUB_TICKET_LOCKED_MSG;
}
const PUB_PERIOD_CLOSED_MSG = 'This pay period is closed — contact your dispatcher to change past hours';

// TASK-602 — is this clockEntry inside the current OPEN pay period? Defense-in-
// depth for pubSaveEditShift / pubDeleteShift so a driver who slips past the UI
// gate (stale render, devtools) gets a clear toast instead of a raw rules
// denial. The AUTHORITATIVE enforcement is the firestore.rules 31d window floor
// on the clockEntries update/delete self-edit branches — this is UX only.
// Boundary from getPayPeriodDates(0) (clock.js) off already-loaded settings —
// ZERO Firestore reads. If the boundary can't be derived, returns true (don't
// block on a missing primitive; rules still backstop). `entry` is the entry's
// raw data (clockIn = Firestore Timestamp or ms).
function pubEntryInOpenPeriod(entry) {
  try {
    const pp = window.ClockModule?.getPayPeriodDates?.(0);
    if (!(pp?.start instanceof Date) || isNaN(pp.start)) return true;
    const cin = entry?.clockIn?.toDate?.() || (entry?.clockIn ? new Date(entry.clockIn) : null);
    if (!(cin instanceof Date) || isNaN(cin)) return true;
    return cin >= pp.start;
  } catch (_e) { return true; }
}

// TASK-566 — was a write rejected by firestore.rules (e.g. lock flipped in the
// race window between pre-check and write)? Surface the lock toast, not a
// generic "Error saving".
//
// 🔴 TASK-9217 — THIS PREDICATE IS NOT A LOCK DETECTOR, it is a
// permission-denied detector, and since TASK-9191 that difference is
// load-bearing. Measured against the rules emulator (probe, 2026-09-14): a
// correction that leaves the window over 24h and an unrelated denial (a
// non-member editing somebody else's entry) come back BYTE-IDENTICAL —
//   code:    "permission-denied"
//   message: "7 PERMISSION_DENIED: \nUnable to evaluate the expression as the
//             maximum of 1000 expressions to evaluate has been reached.
//             for 'update' @ L5425, false for 'update' @ L5425"
// There is NOTHING in the error to key off. The rules do not say which clause
// refused, and on a ruleset this size they do not even report the clause — the
// evaluator gives up at 1000 expressions and reports the arm. So every caller
// that wants to name a specific refusal has to derive it from what it was
// ABOUT to write, never from what came back. See pubClockWindowRefusal.
function pubIsLockDenied(err) {
  return err?.code === 'permission-denied' || /insufficient permission|permission.denied/i.test(err?.message || '');
}

// TASK-9217 — the 24h clock-window bound, mirrored client-side.
//
// WHY THIS EXISTS AT ALL. TASK-9191 FOLLOW-UP #3 added clockWindowOk to
// firestore.rules: on a COMPLETED pre-image, if any of clockIn / clockOut /
// totalHours / hours moves, the POST-IMAGE window must be <= 24h on the driver
// SELF arm. The clause is right and stays. What it left behind is a human
// problem: the driver's correction is refused and the only thing he is told is
// a generic toast for a rule WE chose. TASK-428 is the precedent — a tightening
// whose denial landed somewhere nobody looked stranded real drivers 14+ hours.
//
// This is the same refusal the SERVER path already words properly
// (functions/src/services/ticketGateway.js checkSelfClockWindow, which clock.js
// surfaces verbatim). The string below is that string, deliberately — one
// refusal, one wording, whichever path the driver happens to be on.
const PUB_CLOCK_WINDOW_MAX_MS = 24 * 3600000;

// 🔴 >24h IS A SUPPORTED STATE, NOT AN ERROR. clock.js :19987 lets a human
// confirm one through ("are you sure the dates are correct?"), 9 of ~2,174 live
// TITS completed entries legitimately exceed 24h (forgotten clock-outs), and
// the admin / dispatcher / workforce / billing rules arms are unbounded ON
// PURPOSE. So this is never a verdict on the ENTRY — only on whether THIS
// actor's write of it will be refused. Callers must gate on
// pubClockWindowIsBounded before showing anything.
//
// Returns a user-facing refusal string, or null to allow. Fails OPEN on a
// missing/unparseable timestamp, matching the rules helper (nothing to bound).
function pubClockWindowRefusal(inDate, outDate) {
  const inMs = inDate instanceof Date ? inDate.getTime() : NaN;
  const outMs = outDate instanceof Date ? outDate.getTime() : NaN;
  if (!Number.isFinite(inMs) || !Number.isFinite(outMs)) return null;
  const windowMs = outMs - inMs;
  if (windowMs <= PUB_CLOCK_WINDOW_MAX_MS) return null;
  return 'That would make this shift ' + (windowMs / 3600000).toFixed(1) + ' hours long. ' +
    'A shift over 24 hours has to be corrected by a dispatcher or the office — ' +
    'ask them to fix the dates on it.';
}

// 🔴 THE GATE THAT KEEPS THIS FROM BECOMING ITS OWN TASK-428. The rules clause
// fires ONLY on a pre-image whose status is literally 'completed'. Anything
// else is ALLOWED and must never be warned about, let alone blocked. All three
// were run against the emulator rather than read off the rules text:
//   • status ABSENT (legacy entries), 30h post-window ......... ALLOWED
//   • status 'active' → completed (the INITIAL clock-out, the
//     TASK-9190 device-skew transition, jobboard :10253) ...... ALLOWED
//   • status 'completed', window EXACTLY 24.00h ............... ALLOWED (<=)
//   • status 'completed', window 24.02h ....................... DENIED
// Blocking any of the first three would invent a trap the server does not have
// — which is the exact failure this task exists to remove, pointed the other
// way. Match the rules' condition exactly; do not "harden" it.
function pubClockWindowIsBounded(entry) {
  return entry?.status === 'completed';
}

// TASK-9217 — live hint under the timesheet shift editor. A WARNING, never a
// disabled button: the driver must always be able to see the number move as he
// fixes the dates, and shrinking the window back under 24h is both allowed by
// the rules (verified) and the actual fix for a forgotten clock-out.
window.pubEditShiftWindowHint = function(entryId, bounded) {
  const el = document.getElementById(`pub-edit-warn-${entryId}`);
  if (!el) return;
  const cinVal = document.getElementById(`pub-edit-cin-${entryId}`)?.value;
  const coutVal = document.getElementById(`pub-edit-cout-${entryId}`)?.value;
  const msg = (bounded && cinVal && coutVal)
    ? pubClockWindowRefusal(new Date(window.TenantLocalTime.peekMs(cinVal, { key: `pub-edit-cin-${entryId}` })), new Date(window.TenantLocalTime.peekMs(coutVal, { key: `pub-edit-cout-${entryId}` })))
    : null;
  // textContent, not innerHTML — the string is ours, but this row renders on the
  // public driver page and nothing here should ever be an HTML sink.
  el.textContent = msg || '';
  el.style.display = msg ? 'block' : 'none';
};

// TASK-566 — audit EVERY driver edit to a past ticket (who/what/when +
// before→after) into tenants/{tid}/jobAuditLog (jobs/tickets audit THERE, not
// the general auditLog — CLAUDE.md). Fire-and-forget: an audit failure never
// blocks the driver's save. jobAuditLog create is canReadTenant (driver
// sessions allowed); update/delete are denied, so rows are immutable.
function pubAuditIso(v) {
  const d = v?.toDate?.() || (v instanceof Date ? v : (v ? new Date(v) : null));
  return (d && !isNaN(d)) ? d.toISOString() : null;
}
async function pubLogDriverTicketAudit(action, ticketId, details = {}) {
  try {
    const pc = window._timesheetCtx;
    if (!pc?.tenantId) return;
    const personId = window.AppState?.driverPersonId || pc.authenticatedPersonId || null;
    const person = (pc.people || []).find(p => p.id === personId);
    // Strip undefined values (Firestore rejects them)
    const clean = {};
    for (const [k, v] of Object.entries(details)) {
      if (v !== undefined) clean[k] = v;
    }
    await addDoc(collection(db, 'tenants', pc.tenantId, 'jobAuditLog'), {
      action,
      source: 'driver-timesheet-edit',
      timestamp: Timestamp.now(),
      user: window.AppState?.user?.email || 'driver-pin',
      personId: personId || null,
      personName: person?.name || null,
      ticketId: ticketId || null,
      ...clean
    });
  } catch (e) {
    console.warn('[PublicClock] Driver ticket audit log failed:', e);
  }
}

window.pubToggleTicketView = async function(ticketId, entryId) {
  const container = document.getElementById(`pub-ticket-view-${entryId}`);
  if (!container) return;
  if (container.style.display === 'none' && !(await pubRequireTicketGrant())) return; // TASK-10039
  if (container.style.display !== 'none') {
    container.style.display = 'none';
    return;
  }
  container.style.display = 'block';
  container.innerHTML = '<div style="text-align:center;padding:10px;"><span class="btn-spinner"></span> Loading ticket...</div>';

  const pc = window._timesheetCtx;
  if (!pc) return;

  try {
    // Check ticket status for read-only lock (TASK-566: includes the biller's
    // per-ticket driverEditLocked 🔒 — rules enforce it server-side too)
    const ticketSnap = await getDoc(doc(db, 'tenants', pc.tenantId, 'tickets', ticketId));
    const ticketData = ticketSnap.exists() ? ticketSnap.data() : {};
    const isLocked = pubTicketIsDriverLocked(ticketData);

    const linesSnap = await getDocs(collection(db, 'tenants', pc.tenantId, 'tickets', ticketId, 'ticketLines'));
    // TASK-9440 — the fuel surcharge is office billing, never shown to the crew.
    const lines = linesSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(l => l.type !== 'FUEL_SURCHARGE');
    // TASK-9646 S5 — amounts come from the server and only when the company
    // shows prices to drivers (this view used to print lineAmount for everyone).
    const _prices = await pubFetchTicketPrices(ticketId);

    const typeLabels = { EMPLOYEE: 'Employee', EQUIPMENT: 'Equipment', DOWNTIME: 'Downtime', STANDBY: 'Standby', POTHOLE: 'Pothole/Line', PERMIT: 'Permit' };
    const typeIcons = { EMPLOYEE: 'person', EQUIPMENT: 'precision_manufacturing', DOWNTIME: 'pause_circle', STANDBY: 'hourglass_empty', POTHOLE: 'location_on', PERMIT: 'description' };

    let html = '<div style="margin-top:8px;border:1px solid var(--border-color);border-radius:var(--radius-md);overflow:hidden;">';
    html += '<div style="padding:10px 12px;background:#f8fafc;border-bottom:1px solid var(--border-light);display:flex;justify-content:space-between;align-items:center;">';
    html += `<span style="font-weight:700;font-size:0.8rem;color:var(--text-primary);">Ticket Line Items${isLocked ? ` <span style="font-size:0.65rem;font-weight:600;color:#166534;background:#DCFCE7;padding:1px 6px;border-radius:4px;margin-left:6px;">${pubTicketIsSent(ticketData) ? 'Sent' : 'Locked'}</span>` : ''}</span>`;
    html += isLocked ? '' : `<div style="display:flex;align-items:center;gap:6px;">
      <button onclick="window.pubReEditTicket('${ticketId}','${entryId}')" style="background:none;border:none;cursor:pointer;padding:4px;color:#3b82f6;font-size:0.7rem;font-weight:600;display:flex;align-items:center;gap:2px;" title="Re-edit full ticket form">
        <span class="material-icons" style="font-size:14px;">edit_note</span> Edit Ticket
      </button>
      <button onclick="window.pubDeleteTicket('${ticketId}','${entryId}')" style="background:none;border:none;cursor:pointer;padding:4px;color:#dc2626;font-size:0.7rem;font-weight:600;display:flex;align-items:center;gap:2px;" title="Delete entire ticket">
        <span class="material-icons" style="font-size:14px;">delete_forever</span> Delete Ticket
      </button>
    </div>`;
    html += '</div>';

    if (_prices && _prices.failed && lines.length > 0) {
      // integ/2100 COD r1 — said out loud, no money: the amounts are on the office copy.
      html += '<div style="padding:8px 12px;font-size:0.75rem;color:var(--text-secondary);background:#fffbeb;">Prices couldn\'t be checked just now — reopen the ticket to try again.</div>';
    }
    if (lines.length === 0) {
      html += '<div style="padding:14px;text-align:center;font-size:0.8rem;color:var(--text-secondary);font-style:italic;">No line items on this ticket.</div>';
    } else {
      lines.sort((a, b) => {
        const order = ['EMPLOYEE', 'EQUIPMENT', 'DOWNTIME', 'STANDBY', 'POTHOLE', 'PERMIT'];
        return (order.indexOf(a.type) || 99) - (order.indexOf(b.type) || 99);
      });

      lines.forEach(line => {
        const type = line.type || 'OTHER';
        const label = typeLabels[type] || type;
        const icon = typeIcons[type] || 'receipt';
        const hrs = line.hours ? `${line.hours}h` : '';
        // TASK-9646 S2 (COD UX) — a line whose amount the server is still
        // computing reads "…", never a scary $0.00 right after Save.
        // fix/1017-small-followups — and SAYS why: a bare "…" read as a broken
        // line to drivers. Plain words, no money.
        // TASK-9646 S5: the price comes from driverTicketPrices (rate authority), never the line doc.
        const _p = _prices && _prices.enabled && _prices.lines ? _prices.lines[line.id] : null;
        const amt = !(_prices && _prices.enabled) ? '' : ((_p && _p.pending)
          ? '<span title="Price being calculated" aria-label="Price being calculated">… <span style="font-style:italic;">Price being calculated</span></span>'
          : (_p ? `${Number(_p.lineAmount).toFixed(2)}` : ''));
        const name = line.personName || line.assetName || line.description || '';
        // TASK-9197 — the shared cell formatter (sharedFormat.js) dates BOTH
        // ends when the window crosses a local calendar day, so an overnight
        // ticket line stops reading backwards. Same day-offset implementation
        // as the office ticket/invoice surfaces and TASK-9189's clock surfaces;
        // reached through `window.*`, never a cross-module `?v=` import.
        const _tc = window.Fmt.shiftCells(line.fromTime, line.toTime);
        const timeStr = _tc.ok ? `${_tc.fromHtml} - ${_tc.toHtml}` : '';
        const notes = line.notes ? `<div style="font-size:0.7rem;color:var(--text-secondary);margin-top:2px;font-style:italic;">${escapeHtml(line.notes)}</div>` : '';

        html += `
          <div id="pub-tl-${line.id}" style="padding:8px 12px;border-bottom:1px solid var(--border-light);display:flex;justify-content:space-between;align-items:flex-start;">
            <div style="flex:1;min-width:0;">
              <div style="display:flex;align-items:center;gap:4px;font-size:0.75rem;font-weight:600;color:var(--text-primary);">
                <span class="material-icons" style="font-size:14px;color:var(--text-secondary);">${icon}</span>
                ${escapeHtml(label)}${name ? ': ' + escapeHtml(name) : ''}
              </div>
              <div style="font-size:0.75rem;color:var(--text-secondary);margin-top:1px;">
                ${timeStr}${hrs ? ' &middot; ' + hrs : ''}${amt ? ' &middot; ' + amt : ''}
              </div>
              ${notes}
            </div>
            ${isLocked ? '' : `<div style="display:flex;gap:2px;flex-shrink:0;">
              ${(type === 'EMPLOYEE' || type === 'EQUIPMENT' || type === 'DOWNTIME' || type === 'STANDBY') ? `
                <button onclick="window.pubEditTicketLine('${ticketId}','${line.id}','${entryId}')" style="background:none;border:none;cursor:pointer;padding:4px;color:var(--text-secondary);" title="Edit">
                  <span class="material-icons" style="font-size:16px;">edit</span>
                </button>
              ` : ''}
              <button onclick="window.pubDeleteTicketLine('${ticketId}','${line.id}','${entryId}')" style="background:none;border:none;cursor:pointer;padding:4px;color:var(--text-secondary);" title="Delete">
                <span class="material-icons" style="font-size:16px;">delete_outline</span>
              </button>
            </div>`}
          </div>
        `;
      });
    }
    html += '</div>';
    container.innerHTML = html;
  } catch (err) {
    console.error('[PublicClock] Error loading ticket:', err);
    container.innerHTML = '<div style="padding:10px;font-size:0.8rem;color:#dc2626;">Error loading ticket</div>';
  }
};

window.pubEditTicketLine = async function(ticketId, lineId, entryId) {
  const pc = window._timesheetCtx;
  if (!pc) return;
  if (!(await pubRequireTicketGrant())) return; // TASK-10039
  const row = document.getElementById(`pub-tl-${lineId}`);
  if (!row) return;

  try {
    const lineSnap = await getDoc(doc(db, 'tenants', pc.tenantId, 'tickets', ticketId, 'ticketLines', lineId));
    if (!lineSnap.exists()) { showToast('Line not found', 'error'); return; }
    const line = lineSnap.data();

    const fromTime = line.fromTime?.toDate?.() || (line.fromTime ? new Date(line.fromTime) : new Date());
    const toTime = line.toTime?.toDate?.() || (line.toTime ? new Date(line.toTime) : new Date());
    // clock-dst-all — COMPANY zone, keyed per box (see pubSaveTicketLine).
    const fmtDT = (d, key) => window.TenantLocalTime.formatLocal(d, { key });

    // TASK-9646 S5 — no Rate field: a driver session never authors a rate
    // (firestore.rules tlMoneyPinned refuses it); the server owns the price.
    const notes = line.notes || '';

    row.innerHTML = `
      <div style="width:100%;display:flex;flex-direction:column;gap:6px;padding:4px 0;">
        <div style="font-size:0.75rem;font-weight:700;color:var(--text-primary);">${escapeHtml(line.type || 'Line')}${line.personName ? ': ' + escapeHtml(line.personName) : ''}${line.assetName ? ': ' + escapeHtml(line.assetName) : ''}</div>
        <div style="display:flex;gap:6px;align-items:center;">
          <label style="font-size:0.7rem;font-weight:600;color:var(--text-secondary);min-width:28px;">From:</label>
          <input type="datetime-local" id="pub-tl-from-${lineId}" value="${fmtDT(fromTime, `pub-tl-from-${lineId}`)}" style="flex:1;padding:6px;border:1px solid var(--border-color);border-radius:var(--radius-sm);font-size:0.8rem;">
        </div>
        <div style="display:flex;gap:6px;align-items:center;">
          <label style="font-size:0.7rem;font-weight:600;color:var(--text-secondary);min-width:28px;">To:</label>
          <input type="datetime-local" id="pub-tl-to-${lineId}" value="${fmtDT(toTime, `pub-tl-to-${lineId}`)}" style="flex:1;padding:6px;border:1px solid var(--border-color);border-radius:var(--radius-sm);font-size:0.8rem;">
        </div>
        <div style="display:flex;gap:6px;align-items:center;">
          <label style="font-size:0.7rem;font-weight:600;color:var(--text-secondary);min-width:28px;">Notes:</label>
          <input type="text" id="pub-tl-notes-${lineId}" value="${window.escAttr(notes)}" placeholder="Optional notes" style="flex:1;padding:6px;border:1px solid var(--border-color);border-radius:var(--radius-sm);font-size:0.8rem;">
        </div>
        <div style="display:flex;gap:6px;justify-content:flex-end;margin-top:2px;">
          <button onclick="window.pubToggleTicketView('${ticketId}','${entryId}')" style="padding:5px 12px;font-size:0.75rem;font-weight:600;border:1px solid var(--border-color);border-radius:var(--radius-sm);background:#fff;color:var(--text-primary);cursor:pointer;">Cancel</button>
          <button onclick="window.pubSaveTicketLine('${ticketId}','${lineId}','${entryId}')" style="padding:5px 12px;font-size:0.75rem;font-weight:600;border:none;border-radius:var(--radius-sm);background:var(--brand-blue);color:#fff;cursor:pointer;">Save</button>
        </div>
      </div>
    `;
  } catch (err) {
    console.error('[PublicClock] Error loading ticket line:', err);
    showToast('Error loading line', 'error');
  }
};

window.pubSaveTicketLine = async function(ticketId, lineId, entryId) {
  const pc = window._timesheetCtx;
  if (!pc) return;
  if (!(await pubRequireTicketGrant())) return; // TASK-10039

  const fromVal = document.getElementById(`pub-tl-from-${lineId}`)?.value;
  const toVal = document.getElementById(`pub-tl-to-${lineId}`)?.value;
  const notesVal = document.getElementById(`pub-tl-notes-${lineId}`)?.value?.trim() || '';

  if (!fromVal || !toVal) { showToast('Both times required', 'error'); return; }

  // clock-dst-all — COMPANY zone; repeated-hour time asked, gap / unreadable zone refused.
  const _tlt = window.TenantLocalTime;
  const _fk = `pub-tl-from-${lineId}`, _tk = `pub-tl-to-${lineId}`;
  const _res = await _tlt.resolveAll([
    { str: fromVal, key: _fk, label: 'From', optional: false },
    { str: toVal, key: _tk, label: 'To', optional: false },
  ], { describe: (it, ms) => {
    const other = it.key === _fk ? _tlt.peekMs(toVal, { key: _tk }) : _tlt.peekMs(fromVal, { key: _fk });
    const h = it.key === _fk ? (other - ms) / 3600000 : (ms - other) / 3600000;
    return Number.isFinite(h) && h > 0 ? ` — ${_tlt.hm(h)}` : '';
  } });
  if (!_res.ok) { showToast(_res.message, 'error'); return; }
  const newFrom = new Date(_res.ms[0]);
  const newTo = new Date(_res.ms[1]);
  if (newTo <= newFrom) { showToast('End time must be after start', 'error'); return; }

  const hours = Math.round(((newTo - newFrom) / 3600000) * 100) / 100;
  // TASK-9646 S2 — lineAmount is NOT computed here any more. The line is
  // stamped `amountPending` and the server (onTicketLineWrite →
  // ticketTotalsReconcile) re-derives it from the STORED hours × STORED rate,
  // then re-totals the header. A later slice takes rates off what a driver
  // session can read; a client-side hours × rate would then write $0.

  try {
    // TASK-566 — lock pre-check (biller may have locked/invoiced mid-edit)
    const { editable, ticket: _lockT } = await pubCheckTicketEditable(ticketId);
    if (!editable) {
      showToast(pubTicketLockedMsg(_lockT), 'error');
      const container = document.getElementById(`pub-ticket-view-${entryId}`);
      if (container) container.style.display = 'none';
      window.pubToggleTicketView(ticketId, entryId);
      return;
    }

    // TASK-566 — before-snapshot for the audit row
    const beforeSnap = await getDoc(doc(db, 'tenants', pc.tenantId, 'tickets', ticketId, 'ticketLines', lineId));
    const beforeLine = beforeSnap.exists() ? beforeSnap.data() : {};

    // TASK-9646 S5 — `rate` never rides this write. The S2 "only if edited"
    // echo is gone with the Rate field: firestore.rules tlMoneyPinned refuses a
    // rate change from a driver session, and the server owns the price.
    await updateDoc(doc(db, 'tenants', pc.tenantId, 'tickets', ticketId, 'ticketLines', lineId), {
      fromTime: Timestamp.fromDate(newFrom),
      toTime: Timestamp.fromDate(newTo),
      hours,
      amountPending: true,
      notes: notesVal || null,
      updatedAt: Timestamp.now()
    });

    // TASK-566 — audit who/what/when + before→after
    pubLogDriverTicketAudit('DRIVER_TICKET_LINE_EDIT', ticketId, {
      lineId,
      lineType: beforeLine.type || null,
      clockEntryId: beforeLine.clockEntryId || null,
      before: {
        fromTime: pubAuditIso(beforeLine.fromTime), toTime: pubAuditIso(beforeLine.toTime),
        // TASK-9646 S4a — no rate / lineAmount: a driver session's audit row
        // never copies money (the server prices; Billing audits the money).
        hours: beforeLine.hours ?? null, notes: beforeLine.notes || null
      },
      after: {
        fromTime: newFrom.toISOString(), toTime: newTo.toISOString(),
        hours, notes: notesVal || null
      }
    });

    // TASK-9646 S2 — the header total is the server's (re-derived on the line
    // write above); this write now carries only the dirty stamp.
    await updateDoc(doc(db, 'tenants', pc.tenantId, 'tickets', ticketId), {
      // TASK-4699 — THE headline case for this surface, and the one the board
      // pointed at: the driver just rewrote a line's from/to window, HOURS, RATE
      // and lineAmount. Every one of those is printed on the copy the customer
      // was emailed. Stamped on the ticket HEADER because that is where the
      // delivery state lives.
      ticketEmailDirtyAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });

    showToast('Line item updated', 'success');
    // Re-render the ticket view
    const container = document.getElementById(`pub-ticket-view-${entryId}`);
    if (container) container.style.display = 'none';
    window.pubToggleTicketView(ticketId, entryId);
  } catch (err) {
    console.error('[PublicClock] Error saving ticket line:', err);
    showToast(pubIsLockDenied(err) ? PUB_TICKET_LOCKED_MSG : 'Error saving', 'error');
  }
};

window.pubDeleteTicketLine = async function(ticketId, lineId, entryId) {
  if (!(await pubRequireTicketGrant())) return; // TASK-10039
  if (!confirm('Delete this line item?')) return;
  const pc = window._timesheetCtx;
  if (!pc) return;

  try {
    // TASK-566 — lock pre-check (biller may have locked/invoiced mid-edit)
    const { editable, ticket: _lockT } = await pubCheckTicketEditable(ticketId);
    if (!editable) {
      showToast(pubTicketLockedMsg(_lockT), 'error');
      const container = document.getElementById(`pub-ticket-view-${entryId}`);
      if (container) container.style.display = 'none';
      window.pubToggleTicketView(ticketId, entryId);
      return;
    }

    // TASK-566 — before-snapshot for the audit row
    const beforeSnap = await getDoc(doc(db, 'tenants', pc.tenantId, 'tickets', ticketId, 'ticketLines', lineId));
    const beforeLine = beforeSnap.exists() ? beforeSnap.data() : {};

    await deleteDoc(doc(db, 'tenants', pc.tenantId, 'tickets', ticketId, 'ticketLines', lineId));

    // TASK-566 — audit the deletion (before = the removed line)
    pubLogDriverTicketAudit('DRIVER_TICKET_LINE_DELETE', ticketId, {
      lineId,
      lineType: beforeLine.type || null,
      clockEntryId: beforeLine.clockEntryId || null,
      before: {
        fromTime: pubAuditIso(beforeLine.fromTime), toTime: pubAuditIso(beforeLine.toTime),
        hours: beforeLine.hours ?? null, notes: beforeLine.notes || null, // TASK-9646 S4a — no money
        assetName: beforeLine.assetName || null, personName: beforeLine.personName || null
      },
      after: null
    });

    // TASK-9646 S2 — the header total is the server's (re-derived on the line
    // delete above); this write now carries only the dirty stamp.
    await updateDoc(doc(db, 'tenants', pc.tenantId, 'tickets', ticketId), {
      // TASK-4699 — a billed line the customer can see on his copy just stopped
      // existing, and the total moved with it.
      ticketEmailDirtyAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });

    showToast('Line item deleted', 'success');
    // Re-render
    const container = document.getElementById(`pub-ticket-view-${entryId}`);
    if (container) container.style.display = 'none';
    window.pubToggleTicketView(ticketId, entryId);
  } catch (err) {
    console.error('[PublicClock] Error deleting ticket line:', err);
    showToast(pubIsLockDenied(err) ? PUB_TICKET_LOCKED_MSG : 'Error deleting', 'error');
  }
};

// TASK-4699 — no `ticketEmailDirtyAt` anywhere in here, and not by oversight:
// this DELETES the ticket doc. There is nothing left to stamp, and nothing left
// for the biller's Tickets list to read the stamp off. A ticket that was emailed
// and then deleted is a real hole in the customer's paper trail, but it is a
// different problem from "the copy he holds no longer matches" and the send-log
// subcollection is where it would have to be answered.
window.pubDeleteTicket = async function(ticketId, entryId) {
  if (!(await pubRequireTicketGrant())) return; // TASK-10039
  if (!confirm('Delete this entire ticket and all its line items? This cannot be undone.')) return;
  const pc = window._timesheetCtx;
  if (!pc) return;

  try {
    // TASK-566 — lock pre-check + ticket snapshot for the audit row. A driver
    // may not delete an invoiced or biller-locked ticket (rules deny it too).
    const { editable, ticket: tSnap } = await pubCheckTicketEditable(ticketId);
    if (!editable) {
      showToast(pubTicketLockedMsg(tSnap), 'error');
      const personIdRefresh = pc.authenticatedPersonId || window.AppState?.driverPersonId;
      if (personIdRefresh) pubLoadTimesheet(personIdRefresh);
      return;
    }

    // Delete all ticket lines first
    const linesSnap = await getDocs(collection(db, 'tenants', pc.tenantId, 'tickets', ticketId, 'ticketLines'));
    for (const lineDoc of linesSnap.docs) {
      await deleteDoc(doc(db, 'tenants', pc.tenantId, 'tickets', ticketId, 'ticketLines', lineDoc.id));
    }
    // Delete the ticket itself
    await deleteDoc(doc(db, 'tenants', pc.tenantId, 'tickets', ticketId));

    // TASK-566 — audit the whole-ticket deletion
    pubLogDriverTicketAudit('DRIVER_TICKET_DELETE', ticketId, {
      clockEntryId: entryId || null,
      before: {
        ticketNo: tSnap?.ticketNo ?? null,
        customerName: tSnap?.customerName || null,
        jobId: tSnap?.jobId || null,
        lineCount: linesSnap.docs.length // TASK-9646 S4a — no grandTotal from a driver session
      },
      after: null
    });

    // Clear ticketId from the clock entry so it doesn't reference a deleted ticket.
    // TASK-452 — scope to the driver's OWN entries. This runs in the driver-PIN
    // timesheet-edit cascade; the driver only deletes their own ticket, so the
    // referencing clock entry is their own. The tightened clockEntries READ rule
    // denies a PIN driver a tenant-wide query, so add `where personId == self`
    // (= driverPersonId) to keep the cleanup working under the scoped read.
    const _selfPid = window.AppState?.driverPersonId || pc.authenticatedPersonId || null;
    // TASK-452 — fail loud rather than silently no-op. Without a self personId
    // the scoped query can't run and the un-scoped fallback would be rule-denied
    // anyway (PIN driver = own-entry reads only). Warn so the un-cleared
    // ticketId surfaces in logs instead of disappearing quietly.
    let entrySnap = { docs: [] };
    if (_selfPid) {
      entrySnap = await getDocs(query(
        collection(db, 'tenants', pc.tenantId, 'clockEntries'),
        where('personId', '==', _selfPid),
        where('ticketId', '==', ticketId)
      ));
    } else {
      console.warn('[PublicClock] pubDeleteTicket: no driverPersonId — skipping clock-entry ticketId cleanup for ticket', ticketId);
    }
    for (const entryDoc of entrySnap.docs) {
      await updateDoc(doc(db, 'tenants', pc.tenantId, 'clockEntries', entryDoc.id), {
        ticketId: null,
        ticketLineId: null,
        updatedAt: Timestamp.now()
      });
    }

    showToast('Ticket deleted', 'success');
    // Reload shifts to reflect the ticket being gone
    const personId = pc.authenticatedPersonId;
    if (personId) pubLoadTimesheet(personId);
  } catch (err) {
    console.error('[PublicClock] Error deleting ticket:', err);
    showToast(pubIsLockDenied(err) ? PUB_TICKET_LOCKED_MSG : 'Error deleting ticket', 'error');
  }
};

window.pubToggleSwitchTicketReview = async function(ticketId) {
  const container = document.getElementById('pub-switch-ticket-review');
  if (!container) return;

  if (container.style.display !== 'none') {
    container.style.display = 'none';
    return;
  }

  container.style.display = 'block';

  if (!ticketId) {
    container.innerHTML = '<div style="padding:8px;font-size:0.8rem;color:#64748b;font-style:italic;">No ticket linked (Shop time).</div>';
    return;
  }

  container.innerHTML = '<div style="text-align:center;padding:10px;"><span class="btn-spinner"></span> Loading ticket...</div>';
  const pc = window._timesheetCtx;
  if (!pc) return;

  try {
    const linesSnap = await getDocs(collection(db, 'tenants', pc.tenantId, 'tickets', ticketId, 'ticketLines'));
    // TASK-9440 — the fuel surcharge is office billing, never shown to the crew.
    const lines = linesSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(l => l.type !== 'FUEL_SURCHARGE');

    if (lines.length === 0) {
      container.innerHTML = '<div style="padding:8px;font-size:0.8rem;color:#64748b;font-style:italic;">No line items on this ticket yet.</div>';
      return;
    }

    const typeLabels = { EMPLOYEE: 'Employee', EQUIPMENT: 'Equipment', DOWNTIME: 'Downtime', STANDBY: 'Standby', POTHOLE: 'Pothole/Line', PERMIT: 'Permit' };
    const typeIcons = { EMPLOYEE: 'person', EQUIPMENT: 'precision_manufacturing', DOWNTIME: 'pause_circle', STANDBY: 'hourglass_empty', POTHOLE: 'location_on', PERMIT: 'description' };

    const grouped = {};
    lines.forEach(l => {
      const type = l.type || 'OTHER';
      if (!grouped[type]) grouped[type] = [];
      grouped[type].push(l);
    });

    // TASK-9646 S4a — amounts only from the server (pubFetchTicketPrices), never
    // off the line docs: hours only when prices are off / offline.
    const _pr = await pubFetchTicketPrices(ticketId);
    const _prices = _pr && _pr.enabled === true ? (_pr.lines || {}) : null;

    let html = '<div style="border:1px solid #e2e8f0;border-radius:8px;overflow:hidden;font-size:0.8rem;">';
    html += '<div style="padding:8px 10px;background:#f8fafc;border-bottom:1px solid #e2e8f0;font-weight:700;font-size:0.75rem;color:var(--text-primary);">Ticket Line Items</div>';
    if (_pr && _pr.failed && !_pr.denied && window.AppState?.settings?.showPricesOnDriverTickets === true) {
      // integ/2100 COD r1 rule (2nd COD on S4a): a failed call is NOT "prices off".
      html += '<div style="padding:8px 10px;font-size:0.75rem;color:var(--text-secondary);background:#fffbeb;">Prices could not be checked just now — reopen the ticket to try again.</div>';
    }

    for (const [type, items] of Object.entries(grouped)) {
      const label = typeLabels[type] || type;
      const icon = typeIcons[type] || 'receipt';
      items.forEach(item => {
        const hrs = item.hours ? `${item.hours}h` : '';
        const _p = _prices && _prices[item.id];
        const amt = (_p && !_p.pending && Number(_p.lineAmount) > 0) ? `$${Number(_p.lineAmount).toFixed(2)}` : '';
        const name = item.personName || item.assetName || item.description || '';
        // TASK-9197 — the shared cell formatter (sharedFormat.js) dates BOTH
        // ends when the window crosses a local calendar day, so an overnight
        // ticket line stops reading backwards. Same day-offset implementation
        // as the office ticket/invoice surfaces and TASK-9189's clock surfaces;
        // reached through `window.*`, never a cross-module `?v=` import.
        const _tc = window.Fmt.shiftCells(item.fromTime, item.toTime);
        const timeStr = _tc.ok ? `${_tc.fromHtml} - ${_tc.toHtml}` : '';

        html += `<div style="padding:6px 10px;border-bottom:1px solid #f1f5f9;display:flex;align-items:center;gap:6px;">
          <span class="material-icons" style="font-size:14px;color:#64748b;">${icon}</span>
          <div style="flex:1;min-width:0;">
            <div style="font-weight:600;color:var(--text-primary);">${escapeHtml(label)}${name ? ': ' + escapeHtml(name) : ''}</div>
            <div style="font-size:0.75rem;color:var(--text-secondary);">${timeStr}${hrs ? ' &middot; ' + hrs : ''}${amt ? ' &middot; ' + amt : ''}</div>
          </div>
        </div>`;
      });
    }

    html += '</div>';
    container.innerHTML = html;
  } catch (err) {
    console.error('[PublicClock] Error loading ticket for switch review:', err);
    container.innerHTML = '<div style="padding:8px;font-size:0.8rem;color:#dc2626;">Error loading ticket</div>';
  }
};

// TASK-9646 S5 — the driver timesheet's ONLY source of ticket money. The server
// (`driverTicketPrices`) reads the Billing "Show prices on driver tickets"
// switch, binds the tenant from the session and checks the caller is on the
// ticket; this session never reads rate / lineAmount off the line docs for
// display. Null = show no amounts (setting off / not on the ticket / offline).
async function pubFetchTicketPrices(ticketId) {
  try {
    const res = await httpsCallable(functions, 'driverTicketPrices')({ ticketId });
    const d = res && res.data;
    return d && d.enabled === true ? d : null;
  } catch (err) {
    // integ/2100 COD r1 — a failed call is NOT "prices off": the view says so
    // instead of printing a blank amount column (drivers read blank as "gone").
    console.warn('[Timesheet] driverTicketPrices unavailable:', err?.message || err);
    // TASK-9646 S4a (COD r2): a refusal (not on the ticket) is not an outage — callers may stay quiet on it.
    return { enabled: false, failed: true, denied: /permission-denied/.test(String(err?.code || '')) };
  }
}

async function pubCreateTicketLine(ticketId, lineData) {
  const tenant = window._timesheetCtx.tenantId;
  lineData.createdAt = Timestamp.now();
  const lineRef = await addDoc(
    collection(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines'),
    lineData
  );
  return lineRef.id;
}

// clock-dst-all — every wall string on these driver screens is COMPANY time
// (window.TenantLocalTime), never the phone's zone. STRICT: an unresolved
// repeated-hour time / gap / unreadable zone gives 0 here — every save path
// runs pubResolveTimeRows() first, which asks or refuses.
function pubCalcHoursBetween(startStr, endStr) {
  const T = window.TenantLocalTime;
  const start = T.toMs(startStr);
  const end = T.toMs(endStr);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return 0;
  return Math.max(0, (end - start) / 3600000);
}

// Preview label for a row: "8.50h", or "7.75 or 8.75h" in the repeated hour
// (the person picks on save), or "—" when the time can't be read.
function pubHoursLabel(startStr, endStr) {
  const hb = window.TenantLocalTime.hoursBetween(startStr, endStr);
  if (hb.ok) return Math.max(0, hb.hours).toFixed(2) + 'h';
  if (hb.ambiguous && hb.options.length) return hb.options.map((o) => o.hours.toFixed(2)).join(' or ') + 'h';
  return '—';
}

function pubToLocalDatetimeStr(d) {
  return window.TenantLocalTime.formatLocal(d instanceof Date ? d : new Date(d));
}

// COD 1.15.2009 — a ticket ROW's time is read through the ROW (its own answer:
// stamped from a stored instant, or its own pick) — never another row's.
function pubRowHours(row) {
  const T = window.TenantLocalTime;
  const a = T.rowMs(row, 'startTime'); const b = T.rowMs(row, 'endTime');
  if (!Number.isFinite(a) || !Number.isFinite(b)) return 0;
  return Math.max(0, (b - a) / 3600000);
}
function pubRowHoursLabel(row) {
  const hb = window.TenantLocalTime.hoursBetween(row && row.startTime, row && row.endTime, { row });
  if (hb.ok) return Math.max(0, hb.hours).toFixed(2) + 'h';
  if (hb.ambiguous && hb.options.length) return hb.options.map((o) => o.hours.toFixed(2)).join(' or ') + 'h';
  return '—';
}
// A row filled from stored instants keeps exactly those instants on re-save.
function pubRowFromInstants(row, fromTime, toTime) {
  const T = window.TenantLocalTime;
  if (fromTime) T.stampRow(row, 'startTime', fromTime);
  if (toTime) T.stampRow(row, 'endTime', toTime);
  return row;
}

// Resolve every start/end string in these rows BEFORE a save writes anything:
// asks which time in the repeated fall-back hour (with that row's hours), and
// refuses a nonexistent time or an unreadable company zone. Returns
// { ok:true } or { ok:false, message }.
async function pubResolveTimeRows(groups) {
  const T = window.TenantLocalTime;
  const items = [];
  (groups || []).forEach(({ label, rows }) => (rows || []).forEach((r, i) => {
    if (!r) return;
    const name = `${label} ${i + 1}`;
    if (r.startTime) items.push({ str: r.startTime, row: r, field: 'startTime', label: `${name} start`, _row: r, _end: false });
    if (r.endTime) items.push({ str: r.endTime, row: r, field: 'endTime', label: `${name} end`, _row: r, _end: true });
  }));
  if (!items.length) return { ok: true };
  return T.resolveAll(items, { describe: (it, ms) => {
    const other = T.rowMs(it._row, it._end ? 'startTime' : 'endTime');
    const h = it._end ? (ms - other) / 3600000 : (other - ms) / 3600000;
    return Number.isFinite(h) && h >= 0 ? ` — ${T.hm(h)}` : '';
  } });
}

function pubBuildClockOutForm(entry, person, clockIn, now, hours, elapsed, mode) {
  mode = mode || 'clockout';
  // TASK-9189 — the public PIN clock-out screen. `now` being the current time
  // does NOT make it the same calendar DAY as the clock-in: a driver clocking
  // out at 2 AM after an 11 PM start read "In 11:00 PM ... Out 2:00 AM" beside
  // the duration, which is the same self-contradiction as the payroll panel. The
  // times are also editable here when allowDriverTimeEdit is on, so the end is
  // not pinned to now anyway.
  const clockInStr = clockIn.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const clockOutStr = window.Fmt.shiftSpan(clockIn, now).outHtml;
  const firstName = (person?.name || entry.personName || '').split(/\s+/)[0];

  // Format for datetime-local inputs — COMPANY zone (clock-dst-all), keyed per
  // box so an unchanged repeated-hour time re-saves as the stored instant.
  const fmtDT = (d, key) => window.TenantLocalTime.formatLocal(d, { key });
  const cinDT = fmtDT(clockIn, 'pub-clockout-edit-cin');
  const coutDT = fmtDT(now, 'pub-clockout-edit-cout');

  // Public PIN/link flow is always driver scope — gate time-edit on tenant setting
  const canEditClockTimes = window.AppState?.settings?.allowDriverTimeEdit === true;
  const timeEditOnClick = canEditClockTimes ? `onclick="window.pubShowTimeEdit()"` : '';
  const timeEditCursor = canEditClockTimes ? 'cursor:pointer;' : '';
  const timeEditUnderline = canEditClockTimes ? 'border-bottom:1px dashed' : '';

  return `
    <div style="max-width:480px;margin:0 auto;padding:0;font-family:var(--font-body);min-height:100vh;min-height:100dvh;background:#f1f5f9;">

      <!-- Hero Header -->
      <div style="background:linear-gradient(135deg, #1e293b 0%, #0f172a 100%);padding:24px 20px 28px;border-radius:0 0 24px 24px;margin-bottom:20px;position:relative;">
        <!-- Close button — always edit-mode now (TASK-363 removed clock-out flow) -->
        <button onclick="window.pubCancelEditTicket()" style="position:absolute;top:12px;right:12px;background:rgba(255,255,255,0.1);border:none;border-radius:50%;width:36px;height:36px;display:flex;align-items:center;justify-content:center;cursor:pointer;z-index:10;">
          <span class="material-icons" style="font-size:20px;color:#94a3b8;">close</span>
        </button>
        <!-- Time bar: In → duration → Out (tappable to edit) -->
        <div id="pub-clockout-times-display" style="display:flex;align-items:center;justify-content:center;gap:12px;margin-bottom:16px;">
          <div style="text-align:center;${timeEditCursor}" ${timeEditOnClick}>
            <div style="font-size:0.6rem;text-transform:uppercase;letter-spacing:1px;color:#475569;">In</div>
            <div style="font-size:1.1rem;font-weight:700;color:#34d399;${timeEditUnderline} rgba(52,211,153,0.4);">${clockInStr}</div>
          </div>
          <div style="flex:0 0 auto;display:flex;flex-direction:column;align-items:center;gap:2px;">
            <div style="width:40px;height:1px;background:#334155;"></div>
            <div id="pub-clockout-hours" style="font-size:1.5rem;font-weight:800;color:#fff;line-height:1;">${hours}</div>
            <div style="font-size:0.65rem;color:#64748b;font-weight:500;">hours</div>
            <div style="width:40px;height:1px;background:#334155;"></div>
          </div>
          <div style="text-align:center;${timeEditCursor}" ${timeEditOnClick}>
            <div style="font-size:0.6rem;text-transform:uppercase;letter-spacing:1px;color:#475569;">Out</div>
            <div style="font-size:1.1rem;font-weight:700;color:#f87171;${timeEditUnderline} rgba(248,113,113,0.4);">${clockOutStr}</div>
          </div>
          ${canEditClockTimes ? `<span class="material-icons" onclick="window.pubShowTimeEdit()" style="font-size:16px;color:#475569;cursor:pointer;margin-left:4px;">edit</span>` : ''}
        </div>
        <!-- Inline time editor (hidden by default) -->
        <div id="pub-clockout-times-edit" style="display:none;margin-bottom:16px;background:rgba(59,130,246,0.08);border:1px solid rgba(59,130,246,0.3);border-radius:12px;padding:14px 12px 12px;">
          <div style="font-size:0.7rem;font-weight:600;color:#93c5fd;text-align:center;margin-bottom:10px;text-transform:uppercase;letter-spacing:0.5px;">Adjust Times</div>
          <div style="display:flex;gap:8px;align-items:center;margin-bottom:8px;">
            <label style="font-size:0.7rem;font-weight:600;color:#34d399;min-width:28px;">In:</label>
            <input type="datetime-local" id="pub-clockout-edit-cin" value="${cinDT}" style="flex:1;padding:8px 10px;border:1px solid #334155;border-radius:8px;font-size:0.85rem;background:#1e293b;color:#e2e8f0;color-scheme:dark;">
          </div>
          <div style="display:flex;gap:8px;align-items:center;margin-bottom:12px;">
            <label style="font-size:0.7rem;font-weight:600;color:#f87171;min-width:28px;">Out:</label>
            <input type="datetime-local" id="pub-clockout-edit-cout" value="${coutDT}" style="flex:1;padding:8px 10px;border:1px solid #334155;border-radius:8px;font-size:0.85rem;background:#1e293b;color:#e2e8f0;color-scheme:dark;">
          </div>
          <button onclick="window.pubApplyTimeEdit()" style="width:100%;padding:10px 16px;font-size:0.9rem;font-weight:700;border:none;border-radius:10px;background:linear-gradient(135deg, #3b82f6, #2563eb);color:#fff;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:6px;box-shadow:0 2px 8px rgba(59,130,246,0.3);">
            <span class="material-icons" style="font-size:18px;">check</span> Apply Time Change
          </button>
          <button onclick="window.pubCancelTimeEdit()" style="width:100%;margin-top:6px;padding:6px 16px;font-size:0.8rem;font-weight:600;border:none;border-radius:8px;background:transparent;color:#64748b;cursor:pointer;">Cancel</button>
        </div>

        <!-- Info pills -->
        <div style="display:flex;gap:8px;">
          <div style="flex:1;background:rgba(255,255,255,0.07);border-radius:12px;padding:10px 12px;text-align:center;">
            <div style="font-size:0.6rem;text-transform:uppercase;letter-spacing:0.5px;color:#475569;margin-bottom:3px;">Employee</div>
            <div style="font-size:0.85rem;font-weight:600;color:#e2e8f0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escapeHtml(firstName)}</div>
          </div>
          <div style="flex:1;background:rgba(255,255,255,0.07);border-radius:12px;padding:10px 12px;text-align:center;">
            <div style="font-size:0.6rem;text-transform:uppercase;letter-spacing:0.5px;color:#475569;margin-bottom:3px;">Customer</div>
            <div style="font-size:0.85rem;font-weight:600;color:#e2e8f0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escapeHtml(entry.customerName || 'Shop')}</div>
          </div>
          ${entry.jobTitle ? `<div style="flex:1;background:rgba(255,255,255,0.07);border-radius:12px;padding:10px 12px;text-align:center;">
            <div style="font-size:0.6rem;text-transform:uppercase;letter-spacing:0.5px;color:#475569;margin-bottom:3px;">Job</div>
            <div style="font-size:0.85rem;font-weight:600;color:#e2e8f0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escapeHtml(entry.jobTitle)}</div>
          </div>` : ''}
        </div>
      </div>

      <!-- Form Sections -->
      <div style="padding:0 16px 130px;">
        ${pubBuildNotesSection()}
        ${pubBuildEquipmentSection()}
        ${pubBuildDowntimeSection()}
        ${pubBuildStandbySection()}
        ${pubBuildFieldActivitySection()}
        ${pubBuildPotholeSection()}
        ${pubBuildPhotosSection()}
      </div>

      <!-- Sticky Bottom Actions — edit-mode only post TASK-363 -->
      <div style="position:fixed;bottom:0;left:0;right:0;background:#fff;border-top:1px solid #e2e8f0;padding:12px 16px;padding-bottom:max(12px, env(safe-area-inset-bottom));z-index:100;box-shadow:0 -4px 20px rgba(0,0,0,0.08);">
        <div style="max-width:480px;margin:0 auto;display:flex;gap:10px;">
          <button id="pub-btn-cancel-clock-out" style="flex:0 0 auto;padding:13px 20px;font-size:0.9rem;font-weight:600;border:1px solid #e2e8f0;border-radius:12px;background:#fff;color:#334155;cursor:pointer;" onclick="window.pubCancelEditTicket()">
            Cancel
          </button>
          <button id="pub-btn-save-edit-ticket" style="flex:1;padding:13px;font-size:1rem;font-weight:700;border:none;border-radius:12px;background:linear-gradient(135deg, #3b82f6, #2563eb);color:#fff;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:8px;box-shadow:0 4px 14px rgba(59,130,246,0.25);" onclick="window.pubSaveEditTicket()">
            <span class="material-icons" style="font-size:20px;">save</span> Save Changes
          </button>
        </div>
      </div>
    </div>
  `;
}

function pubBuildCollapsible(id, icon, title, content, startOpen, badge) {
  const badgeHtml = badge ? `<span style="background:#3b82f6;color:#fff;font-size:0.7rem;font-weight:700;padding:2px 8px;border-radius:10px;min-width:18px;text-align:center;line-height:1.4;">${badge}</span>` : '';
  return `
    <div style="background:#fff;border-radius:14px;margin-bottom:10px;overflow:hidden;box-shadow:0 1px 3px rgba(0,0,0,0.05),0 1px 2px rgba(0,0,0,0.03);">
      <div onclick="window.pubToggleSection('${id}')" style="display:flex;align-items:center;gap:10px;padding:14px 16px;cursor:pointer;user-select:none;">
        <div style="width:32px;height:32px;border-radius:8px;background:#f1f5f9;display:flex;align-items:center;justify-content:center;flex-shrink:0;">
          <span class="material-icons" style="font-size:18px;color:#3b82f6;">${icon}</span>
        </div>
        <span style="flex:1;font-weight:600;font-size:0.9rem;color:#0f172a;">${title}</span>
        ${badgeHtml}
        <span class="material-icons" id="${id}-chevron" style="font-size:20px;color:#94a3b8;transition:transform 0.2s;">${startOpen ? 'expand_less' : 'expand_more'}</span>
      </div>
      <div id="${id}-body" style="padding:0 16px 16px;${startOpen ? '' : 'display:none;'}">
        ${content}
      </div>
    </div>
  `;
}

window.pubToggleSection = function(id) {
  const body = document.getElementById(id + '-body');
  const chevron = document.getElementById(id + '-chevron');
  if (!body) return;
  const isHidden = body.style.display === 'none';
  body.style.display = isHidden ? 'block' : 'none';
  if (chevron) chevron.textContent = isHidden ? 'expand_less' : 'expand_more';
};

function pubBuildEquipmentSection() {
  const pc = window._timesheetCtx;
  const count = pc.clockOutEquipment.length;
  const eqHtml = count === 0
    ? '<div style="font-size:0.85rem;color:#64748b;padding:8px 0;">Auto-filled from dispatch when assigned</div>'
    : pubRenderEquipmentList();

  const content = `
    <div id="pub-equipment-list">${eqHtml}</div>
    <button type="button" style="margin-top:10px;padding:10px 14px;font-size:0.85rem;font-weight:600;border:2px dashed #cbd5e1;border-radius:10px;background:transparent;color:#3b82f6;cursor:pointer;display:flex;align-items:center;gap:6px;width:100%;justify-content:center;" onclick="window.pubShowEquipmentPicker()">
      <span class="material-icons" style="font-size:18px;">add_circle_outline</span> Add Equipment
    </button>
    <div id="pub-equipment-picker" style="display:none;margin-top:8px;"></div>
  `;

  return pubBuildCollapsible('pub-sec-equipment', 'local_shipping', 'Equipment', content, count > 0, count > 0 ? count : null);
}

function pubRenderEquipmentList() {
  const pc = window._timesheetCtx;
  return pc.clockOutEquipment.map((eq, idx) => {
    const hrs = pubRowHoursLabel(eq);
    return `
      <div style="padding:12px;margin-bottom:8px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:10px;">
        <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:10px;">
          <div style="display:flex;align-items:center;gap:8px;">
            <div style="width:28px;height:28px;border-radius:6px;background:#dbeafe;display:flex;align-items:center;justify-content:center;">
              <span class="material-icons" style="font-size:16px;color:#3b82f6;">${eq.assetType === 'truck' ? 'local_shipping' : eq.assetType === 'trailer' ? 'rv_hookup' : 'build'}</span>
            </div>
            <div>
              <div style="font-size:0.85rem;font-weight:600;color:#0f172a;">${escapeHtml(eq.assetName)}</div>
              ${eq.fleetNumber ? `<div style="font-size:0.75rem;color:#64748b;">#${escapeHtml(eq.fleetNumber)}</div>` : ''}
            </div>
          </div>
          <div style="display:flex;align-items:center;gap:8px;">
            <span style="font-weight:700;font-size:0.9rem;color:#3b82f6;">${hrs}</span>
            <button type="button" onclick="window.pubRemoveEquipment(${idx})" style="background:none;border:none;cursor:pointer;padding:2px;display:flex;">
              <span class="material-icons" style="font-size:18px;color:#94a3b8;">close</span>
            </button>
          </div>
        </div>
        <div style="display:flex;gap:8px;">
          <div style="flex:1;">
            <label style="font-size:0.7rem;font-weight:500;color:#64748b;display:block;margin-bottom:3px;">Start</label>
            <input type="datetime-local" value="${eq.startTime}" onchange="window.pubUpdateEquipTime(${idx},'start',this.value)" style="width:100%;font-size:0.8rem;padding:8px;border:1px solid #e2e8f0;border-radius:8px;background:#fff;color:#0f172a;">
          </div>
          <div style="flex:1;">
            <label style="font-size:0.7rem;font-weight:500;color:#64748b;display:block;margin-bottom:3px;">End</label>
            <input type="datetime-local" value="${eq.endTime}" onchange="window.pubUpdateEquipTime(${idx},'end',this.value)" style="width:100%;font-size:0.8rem;padding:8px;border:1px solid #e2e8f0;border-radius:8px;background:#fff;color:#0f172a;">
          </div>
        </div>
        <div style="margin-top:8px;">
          <textarea id="pub-eq-notes-${idx}" rows="1" placeholder="Notes for this equipment..." oninput="window.pubUpdateEquipNotes(${idx},this.value)"
            style="width:100%;padding:8px 10px;font-size:0.8rem;border:1px solid #e2e8f0;border-radius:8px;background:#fff;color:#0f172a;resize:vertical;">${escapeHtml(eq.notes || '')}</textarea>
        </div>
      </div>
    `;
  }).join('');
}

window.pubUpdateEquipTime = function(idx, field, value) {
  const pc = window._timesheetCtx;
  if (field === 'start') pc.clockOutEquipment[idx].startTime = value;
  if (field === 'end') pc.clockOutEquipment[idx].endTime = value;
  pc.clockOutEquipment[idx]._dirty = true;
  document.getElementById('pub-equipment-list').innerHTML = pubRenderEquipmentList();
};

window.pubUpdateEquipNotes = function(idx, value) {
  window._timesheetCtx.clockOutEquipment[idx].notes = value;
};

window.pubRemoveEquipment = function(idx) {
  window._timesheetCtx.clockOutEquipment.splice(idx, 1);
  document.getElementById('pub-equipment-list').innerHTML = pubRenderEquipmentList();
};

window.pubShowEquipmentPicker = function() {
  const pc = window._timesheetCtx;
  const usedIds = new Set(pc.clockOutEquipment.map(e => e.assetId));
  const trucks = pc.trucks.filter(t => !usedIds.has(t.id));
  const trailers = pc.trailers.filter(t => !usedIds.has(t.id));
  const assets = pc.assets.filter(a => !usedIds.has(a.id));

  if (trucks.length === 0 && trailers.length === 0 && assets.length === 0) {
    showToast('All equipment already added', 'info');
    return;
  }

  let options = '<option value="">Select equipment...</option>';
  if (trucks.length > 0) {
    options += '<optgroup label="Trucks">';
    trucks.forEach(t => {
      const display = window.getUnitDisplay ? window.getUnitDisplay(t) : (t.nickname ? `${t.nickname} — #${t.unit}` : (t.unit || t.name || ''));
      options += `<option value="truck:${t.id}">${escapeHtml(display)}</option>`;
    });
    options += '</optgroup>';
  }
  if (trailers.length > 0) {
    options += '<optgroup label="Trailers">';
    trailers.forEach(t => {
      const display = window.getUnitDisplay ? window.getUnitDisplay(t) : (t.nickname ? `${t.nickname} — #${t.unit}` : (t.unit || t.name || ''));
      options += `<option value="trailer:${t.id}">${escapeHtml(display)}</option>`;
    });
    options += '</optgroup>';
  }
  if (assets.length > 0) {
    options += '<optgroup label="Assets">';
    assets.forEach(a => options += `<option value="asset:${a.id}">${escapeHtml(a.name)}${a.partNumber ? ' #' + escapeHtml(a.partNumber) : ''}</option>`);
    options += '</optgroup>';
  }

  const pickerEl = document.getElementById('pub-equipment-picker');
  pickerEl.innerHTML = `<select class="form-control" onchange="window.pubSelectEquipment(this.value)" style="width:100%;padding:10px;font-size:0.9rem;">${options}</select>`;
  pickerEl.style.display = 'block';
};

window.pubSelectEquipment = function(value) {
  if (!value) return;
  const [type, id] = value.split(':');
  const pc = window._timesheetCtx;
  const entry = pc.clockOutEntry;
  const clockIn = entry.clockIn?.toDate?.() || new Date(entry.clockIn);

  // TASK-1651 — this handler used to open with `rate = PUB_RATES.asset`, a
  // global that was never defined anywhere: the first statement threw a
  // ReferenceError inside the <select onchange>, so picking a truck silently
  // did nothing. Equipment is added UNPRICED; the server enrichment prices it
  // from the customer's price book after save (never invent a client rate).
  const rate = PUB_EQUIPMENT_RATE_SERVER_PRICED;
  let item = null, assetName = 'Equipment', fleetNumber = '';
  if (type === 'truck') {
    item = pc.trucks.find(t => t.id === id);
    assetName = ((item && window.getUnitDisplay) ? window.getUnitDisplay(item) : '') || item?.name || item?.unit || 'Truck';
    fleetNumber = item?.unit || '';
  } else if (type === 'trailer') {
    item = pc.trailers.find(t => t.id === id);
    assetName = ((item && window.getUnitDisplay) ? window.getUnitDisplay(item) : '') || item?.name || item?.unit || 'Trailer';
    fleetNumber = item?.unit || '';
  } else if (type === 'asset') {
    item = pc.assets.find(a => a.id === id);
    assetName = item?.name || 'Asset';
    fleetNumber = item?.partNumber || '';
  }
  if (!item) return;

  pc.clockOutEquipment.push({ assetId: item.id, assetName, assetType: type, fleetNumber, rate, startTime: pubToLocalDatetimeStr(clockIn), endTime: pubToLocalDatetimeStr(new Date()), notes: '' });
  document.getElementById('pub-equipment-picker').style.display = 'none';
  document.getElementById('pub-equipment-list').innerHTML = pubRenderEquipmentList();
};

function pubBuildDowntimeSection() {
  const content = `
    <div id="pub-downtime-list"><div style="font-size:0.85rem;color:#64748b;padding:8px 0;">No downtime entries</div></div>
    <button type="button" style="margin-top:10px;padding:10px 14px;font-size:0.85rem;font-weight:600;border:2px dashed #cbd5e1;border-radius:10px;background:transparent;color:#3b82f6;cursor:pointer;display:flex;align-items:center;gap:6px;width:100%;justify-content:center;" onclick="window.pubAddDowntimeRow()">
      <span class="material-icons" style="font-size:18px;">add_circle_outline</span> Add Downtime
    </button>
  `;
  return pubBuildCollapsible('pub-sec-downtime', 'pause_circle', 'Downtime', content, false);
}

window.pubAddDowntimeRow = function() {
  const pc = window._timesheetCtx;
  const entry = pc.clockOutEntry;
  const clockIn = entry.clockIn?.toDate?.() || new Date(entry.clockIn);
  pc.clockOutDowntime.push({ startTime: pubToLocalDatetimeStr(clockIn), endTime: pubToLocalDatetimeStr(new Date()), notes: '' });
  pubRefreshTimeEntryList('downtime');
};

window.pubRemoveDowntime = function(idx) {
  window._timesheetCtx.clockOutDowntime.splice(idx, 1);
  pubRefreshTimeEntryList('downtime');
};

window.pubUpdateTimeEntry = function(type, idx, field, value) {
  const list = type === 'downtime' ? window._timesheetCtx.clockOutDowntime : window._timesheetCtx.clockOutStandby;
  list[idx][field] = value;
  pubRefreshTimeEntryList(type);
};

function pubRefreshTimeEntryList(type) {
  const list = type === 'downtime' ? window._timesheetCtx.clockOutDowntime : window._timesheetCtx.clockOutStandby;
  const el = document.getElementById(`pub-${type}-list`);
  if (!el) return;

  if (list.length === 0) {
    el.innerHTML = `<div style="font-size:0.85rem;color:#64748b;padding:8px 0;">No ${type} entries</div>`;
    return;
  }

  el.innerHTML = list.map((item, idx) => {
    const hrs = pubRowHoursLabel(item);
    const removeFn = type === 'downtime' ? 'pubRemoveDowntime' : 'pubRemoveStandby';
    return `
      <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:10px;padding:12px;margin-bottom:8px;">
        <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:10px;">
          <span style="font-weight:700;font-size:0.9rem;color:#3b82f6;">${hrs}</span>
          <button type="button" onclick="window.${removeFn}(${idx})" style="background:none;border:none;cursor:pointer;padding:2px;display:flex;">
            <span class="material-icons" style="font-size:18px;color:#94a3b8;">close</span>
          </button>
        </div>
        <div style="display:flex;gap:8px;margin-bottom:8px;">
          <div style="flex:1;">
            <label style="font-size:0.7rem;font-weight:500;color:#64748b;display:block;margin-bottom:3px;">Start</label>
            <input type="datetime-local" value="${item.startTime}" onchange="window.pubUpdateTimeEntry('${type}',${idx},'startTime',this.value)" style="width:100%;font-size:0.8rem;padding:8px;border:1px solid #e2e8f0;border-radius:8px;background:#fff;color:#0f172a;">
          </div>
          <div style="flex:1;">
            <label style="font-size:0.7rem;font-weight:500;color:#64748b;display:block;margin-bottom:3px;">End</label>
            <input type="datetime-local" value="${item.endTime}" onchange="window.pubUpdateTimeEntry('${type}',${idx},'endTime',this.value)" style="width:100%;font-size:0.8rem;padding:8px;border:1px solid #e2e8f0;border-radius:8px;background:#fff;color:#0f172a;">
          </div>
        </div>
        <input type="text" value="${window.escAttr(item.notes)}" placeholder="Notes..." onchange="window.pubUpdateTimeEntry('${type}',${idx},'notes',this.value)" style="width:100%;padding:8px;font-size:0.85rem;border:1px solid #e2e8f0;border-radius:8px;background:#fff;color:#0f172a;">
      </div>
    `;
  }).join('');
}

function pubBuildStandbySection() {
  const content = `
    <div id="pub-standby-list"><div style="font-size:0.85rem;color:#64748b;padding:8px 0;">No standby entries</div></div>
    <button type="button" style="margin-top:10px;padding:10px 14px;font-size:0.85rem;font-weight:600;border:2px dashed #cbd5e1;border-radius:10px;background:transparent;color:#3b82f6;cursor:pointer;display:flex;align-items:center;gap:6px;width:100%;justify-content:center;" onclick="window.pubAddStandbyRow()">
      <span class="material-icons" style="font-size:18px;">add_circle_outline</span> Add Standby
    </button>
  `;
  return pubBuildCollapsible('pub-sec-standby', 'hourglass_empty', 'Standby', content, false);
}

window.pubAddStandbyRow = function() {
  const pc = window._timesheetCtx;
  const entry = pc.clockOutEntry;
  const clockIn = entry.clockIn?.toDate?.() || new Date(entry.clockIn);
  pc.clockOutStandby.push({ startTime: pubToLocalDatetimeStr(clockIn), endTime: pubToLocalDatetimeStr(new Date()), notes: '' });
  pubRefreshTimeEntryList('standby');
};

window.pubRemoveStandby = function(idx) {
  window._timesheetCtx.clockOutStandby.splice(idx, 1);
  pubRefreshTimeEntryList('standby');
};

function pubBuildFieldActivitySection() {
  const content = `
    <div style="display:flex;gap:10px;">
      <div style="flex:1;">
        <label style="font-size:0.75rem;font-weight:500;color:#64748b;display:block;margin-bottom:4px;">Dumps</label>
        <input type="number" id="pub-field-dumps" min="0" value="0" style="width:100%;padding:10px;font-size:1.1rem;font-weight:600;text-align:center;border:1px solid #e2e8f0;border-radius:10px;background:#f8fafc;color:#0f172a;">
      </div>
      <div style="flex:1;">
        <label style="font-size:0.75rem;font-weight:500;color:#64748b;display:block;margin-bottom:4px;">Water Refills</label>
        <input type="number" id="pub-field-water-refills" min="0" value="0" style="width:100%;padding:10px;font-size:1.1rem;font-weight:600;text-align:center;border:1px solid #e2e8f0;border-radius:10px;background:#f8fafc;color:#0f172a;">
      </div>
    </div>
  `;
  return pubBuildCollapsible('pub-sec-field', 'construction', 'Field Activity', content, false);
}

function pubBuildPotholeSection() {
  const content = `
    <div id="pub-pothole-list"><div style="font-size:0.85rem;color:#64748b;padding:8px 0;">No line entries</div></div>
    <button type="button" style="margin-top:10px;padding:10px 14px;font-size:0.85rem;font-weight:600;border:2px dashed #cbd5e1;border-radius:10px;background:transparent;color:#3b82f6;cursor:pointer;display:flex;align-items:center;gap:6px;width:100%;justify-content:center;" onclick="window.pubAddPothole()">
      <span class="material-icons" style="font-size:18px;">add_circle_outline</span> Add Entry
    </button>
  `;
  return pubBuildCollapsible('pub-sec-pothole', 'pin_drop', 'Line Info', content, false);
}

window.pubAddPothole = function() {
  window._timesheetCtx.clockOutPotholes.push({
    type: '', tolFeet: 0, tolInches: 0, pipeSize: '', gpsLat: null, gpsLng: null, notes: '', photos: []
  });
  pubRefreshPotholeList();
};

window.pubRemovePothole = function(idx) {
  window._timesheetCtx.clockOutPotholes.splice(idx, 1);
  pubRefreshPotholeList();
};

window.pubUpdatePotholeField = function(idx, field, value) {
  window._timesheetCtx.clockOutPotholes[idx][field] = value;
};

window.pubGrabGPS = function(idx) {
  if (!navigator.geolocation) { showToast('GPS not supported', 'error'); return; }
  navigator.geolocation.getCurrentPosition(
    (pos) => {
      window._timesheetCtx.clockOutPotholes[idx].gpsLat = pos.coords.latitude;
      window._timesheetCtx.clockOutPotholes[idx].gpsLng = pos.coords.longitude;
      pubRefreshPotholeList();
      showToast('GPS captured', 'success');
    },
    (err) => showToast('GPS unavailable: ' + err.message, 'error'),
    { enableHighAccuracy: true, timeout: 10000 }
  );
};

// TASK-9402 — pick-time photo prep (HEIC → JPEG) for the public timesheet.
// pubSaveEditTicket settles this BEFORE it starts its delete-and-recreate
// cascade, so a Save pressed mid-conversion can't run without the photo.
const _pubPhotoPrep = createPhotoPrepTracker();
// Integration COD r1 — see pubLoadTicketIntoForm / pubSaveEditTicket.
let _pubTicketLoadGen = 0;

/**
 * Prepare one picked photo for upload. Resolves to { file, contentType } or
 * null when it can't be converted — the photo is SKIPPED with the converter's
 * message; the rest of the ticket still saves.
 */
async function pubPreparePickedPhoto(file) {
  try {
    const r = await prepareUploadFile(file);
    return { file: r.file, contentType: r.contentType };
  } catch (err) {
    showToast(`Photo skipped — ${photoErrorMessage(err)}`, 'error');
    return null;
  }
}

window.pubHandlePotholePhoto = function(potholeIdx, files) {
  if (!files || files.length === 0) return;
  const ph = window._timesheetCtx.clockOutPotholes[potholeIdx];
  if (!ph) return;
  if (!ph.photos) ph.photos = [];
  // Convert first; the line object is captured so a late photo can only land
  // on the line it was picked for.
  const picked = Array.from(files);
  _pubPhotoPrep.track((async () => {
    for (const raw of picked) {
      const prep = await pubPreparePickedPhoto(raw);
      if (prep) await pubAddPotholePhoto(ph, prep.file, prep.contentType);
    }
  })());
};

async function pubAddPotholePhoto(ph, file, contentType) {
  let preview = null;
  if ((file.type || '').startsWith('image/')) {
    // TASK-1383 — guarded read; a failed preview must not silently DROP the
    // photo (the old resolve-only reader did): fall back to preview:null so
    // the file still uploads (mirrors clock.js handlePotholePhotoSelect).
    try { preview = await window.FileReadUtil.readAsDataURL(file); } catch (_) { preview = null; }
  }
  ph.photos.push({ file, contentType, preview, name: file.name });
  pubRefreshPotholeList();
}

window.pubRemovePotholePhoto = function(potholeIdx, photoIdx) {
  const ph = window._timesheetCtx.clockOutPotholes[potholeIdx];
  if (!ph || !ph.photos) return;
  ph.photos.splice(photoIdx, 1);
  pubRefreshPotholeList();
};

async function pubUploadPotholePhotos(ticketId, ticketLineId, photos) {
  if (!photos || photos.length === 0) return;
  const tenant = window._timesheetCtx.tenantId;
  const uploaded = [];

  for (const photo of photos) {
    try {
      const timestamp = Date.now();
      const safeName = photo.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const path = `tickets/${tenant}/${ticketId}/potholes/${ticketLineId}/${timestamp}_${safeName}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, photo.file, photo.contentType ? { contentType: photo.contentType } : undefined);
      const url = await getDownloadURL(storageRef);
      uploaded.push({ url, name: photo.name, uploadedAt: new Date().toISOString() });
    } catch (err) {
      console.error('[PublicClock] Pothole photo upload error:', err);
    }
  }

  if (uploaded.length > 0) {
    await updateDoc(
      doc(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines', ticketLineId),
      { photos: uploaded, updatedAt: Timestamp.now() }
    );
  }
}

function pubRefreshPotholeList() {
  const el = document.getElementById('pub-pothole-list');
  if (!el) return;
  const potholes = window._timesheetCtx.clockOutPotholes;

  if (potholes.length === 0) {
    el.innerHTML = '<div style="font-size:0.85rem;color:#64748b;padding:8px 0;">No line entries</div>';
    return;
  }

  el.innerHTML = potholes.map((ph, idx) => {
    const hasGPS = ph.gpsLat !== null && ph.gpsLng !== null;
    const photos = ph.photos || [];
    return `
      <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:10px;padding:12px;margin-bottom:8px;">
        <div style="display:flex;gap:8px;align-items:center;margin-bottom:10px;">
          <div style="flex:1;">
            <label style="font-size:0.7rem;font-weight:500;color:#64748b;display:block;margin-bottom:3px;">Type of Line</label>
            <input type="text" value="${window.escAttr(ph.type)}" placeholder="e.g. poly, metal"
                   style="width:100%;padding:8px;font-size:0.85rem;border:1px solid #e2e8f0;border-radius:8px;background:#fff;color:#0f172a;"
                   onchange="window.pubUpdatePotholeField(${idx},'type',this.value)">
          </div>
          <button type="button" onclick="window.pubRemovePothole(${idx})" style="background:none;border:none;cursor:pointer;padding:2px;display:flex;align-self:flex-end;">
            <span class="material-icons" style="font-size:18px;color:#94a3b8;">close</span>
          </button>
        </div>
        <div style="display:flex;gap:8px;align-items:flex-end;margin-bottom:10px;">
          <div style="flex:1;">
            <label style="font-size:0.7rem;font-weight:500;color:#64748b;display:block;margin-bottom:3px;">TOL (ft)</label>
            <input type="number" value="${ph.tolFeet}" min="0" style="width:100%;padding:8px;font-size:0.9rem;border:1px solid #e2e8f0;border-radius:8px;background:#fff;color:#0f172a;text-align:center;"
                   onchange="window.pubUpdatePotholeField(${idx},'tolFeet',parseInt(this.value)||0)">
          </div>
          <div style="flex:1;">
            <label style="font-size:0.7rem;font-weight:500;color:#64748b;display:block;margin-bottom:3px;">TOL (in)</label>
            <select onchange="window.pubUpdatePotholeField(${idx},'tolInches',parseFloat(this.value)||0)" style="width:100%;padding:8px;font-size:0.9rem;border:1px solid #e2e8f0;border-radius:8px;background:#fff;color:#0f172a;">
              <option value="0" ${ph.tolInches === 0 ? 'selected' : ''}>0</option>
              <option value="3" ${ph.tolInches === 3 ? 'selected' : ''}>3</option>
              <option value="6" ${ph.tolInches === 6 ? 'selected' : ''}>6</option>
              <option value="9" ${ph.tolInches === 9 ? 'selected' : ''}>9</option>
            </select>
          </div>
          <div style="flex:1;">
            <label style="font-size:0.7rem;font-weight:500;color:#64748b;display:block;margin-bottom:3px;">Pipe (in)</label>
            <input type="text" value="${window.escAttr(ph.pipeSize)}" placeholder="4" style="width:100%;padding:8px;font-size:0.9rem;border:1px solid #e2e8f0;border-radius:8px;background:#fff;color:#0f172a;text-align:center;"
                   onchange="window.pubUpdatePotholeField(${idx},'pipeSize',this.value)">
          </div>
          <button type="button" onclick="window.pubGrabGPS(${idx})" style="padding:8px 12px;font-size:0.8rem;font-weight:600;border:1px solid ${hasGPS ? '#86efac' : '#e2e8f0'};border-radius:8px;background:${hasGPS ? '#dcfce7' : '#fff'};color:${hasGPS ? '#16a34a' : '#3b82f6'};cursor:pointer;display:flex;align-items:center;gap:4px;white-space:nowrap;">
            <span class="material-icons" style="font-size:16px;">${hasGPS ? 'check_circle' : 'my_location'}</span>
            ${hasGPS ? 'OK' : 'GPS'}
          </button>
        </div>
        <input type="text" value="${window.escAttr(ph.notes)}" placeholder="Notes..." onchange="window.pubUpdatePotholeField(${idx},'notes',this.value)" style="width:100%;padding:8px;font-size:0.85rem;border:1px solid #e2e8f0;border-radius:8px;background:#fff;color:#0f172a;margin-bottom:8px;">
        <div style="display:flex;align-items:center;gap:6px;flex-wrap:wrap;">
          ${photos.map((p, pi) => `
            <div style="position:relative;width:56px;height:56px;border-radius:8px;overflow:hidden;border:1px solid #e2e8f0;flex-shrink:0;">
              ${(p.preview || p.url) ? `<img src="${p.preview || p.url}" style="width:100%;height:100%;object-fit:cover;">` : `<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:#f1f5f9;"><span class="material-icons" style="font-size:20px;color:#94a3b8;">description</span></div>`}
              <button type="button" onclick="window.pubRemovePotholePhoto(${idx},${pi})"
                      style="position:absolute;top:1px;right:1px;background:rgba(0,0,0,0.6);color:#fff;border:none;border-radius:50%;width:18px;height:18px;display:flex;align-items:center;justify-content:center;cursor:pointer;padding:0;">
                <span class="material-icons" style="font-size:12px;">close</span>
              </button>
            </div>
          `).join('')}
          <label style="width:56px;height:56px;border:2px dashed #cbd5e1;border-radius:8px;display:flex;flex-direction:column;align-items:center;justify-content:center;cursor:pointer;flex-shrink:0;">
            <span class="material-icons" style="font-size:20px;color:#94a3b8;">add_a_photo</span>
            <span style="font-size:0.55rem;color:#94a3b8;">Photo</span>
            <input type="file" hidden accept="image/*" multiple
                   onchange="window.pubHandlePotholePhoto(${idx}, this.files); this.value='';">
          </label>
        </div>
      </div>
    `;
  }).join('');
}

function pubBuildNotesSection() {
  const content = `
    <div style="margin-bottom:12px;">
      <label style="font-size:0.75rem;font-weight:500;color:#64748b;display:block;margin-bottom:4px;">Shift Notes <span style="font-size:0.7rem;font-weight:400;">(internal)</span></label>
      <textarea id="pub-shift-notes" rows="3" placeholder="${window.AppState?.settings?.requireShiftNotes === true ? 'REQUIRED — What did you do today?' : 'What did you do today?'}" style="width:100%;padding:10px;font-size:0.9rem;border:1px solid #e2e8f0;border-radius:10px;resize:vertical;background:#f8fafc;color:#0f172a;"></textarea>
    </div>
    <div>
      <label style="font-size:0.75rem;font-weight:500;color:#64748b;display:block;margin-bottom:4px;">Ticket Notes <span style="font-size:0.7rem;font-weight:400;">(appears on invoice)</span></label>
      <textarea id="pub-ticket-notes" rows="3" placeholder="Notes for customer..." style="width:100%;padding:10px;font-size:0.9rem;border:1px solid #e2e8f0;border-radius:10px;resize:vertical;background:#f8fafc;color:#0f172a;"></textarea>
    </div>
  `;
  return pubBuildCollapsible('pub-sec-notes', 'notes', 'Notes', content, true);
}

function pubBuildPhotosSection() {
  const content = `
    <div id="pub-photo-thumbnails" style="display:flex;flex-wrap:wrap;gap:8px;margin-bottom:8px;"></div>
    <button type="button" style="padding:10px 14px;font-size:0.85rem;font-weight:600;border:2px dashed #cbd5e1;border-radius:10px;background:transparent;color:#3b82f6;cursor:pointer;display:flex;align-items:center;gap:6px;width:100%;justify-content:center;" onclick="document.getElementById('pub-photo-input').click()">
      <span class="material-icons" style="font-size:18px;">add_photo_alternate</span> Add Photos
    </button>
    <input type="file" id="pub-photo-input" hidden multiple accept="image/*,.pdf" onchange="window.pubHandlePhotoSelect(this.files)">
  `;
  return pubBuildCollapsible('pub-sec-photos', 'photo_camera', 'Photos', content, false);
}

// ============================================
// SHARED TICKET PHOTO UTILITIES — TASK-1214 / TASK-1220
// ============================================
// window.PhotoLightbox — full-size photo viewer, promoted from pretrip.js
// showFullPhoto (reuses the existing .photo-lightbox CSS). Tap anywhere or
// the ✕ to close. Views call window.PhotoLightbox.show(url) — NEVER a
// cross-import (Pitfall #1: ?v= busters fork module instances).
window.PhotoLightbox = {
  show(url) {
    if (!url) return;
    const existing = document.getElementById('photo-lightbox');
    if (existing) existing.remove();
    const lightbox = document.createElement('div');
    lightbox.id = 'photo-lightbox';
    lightbox.className = 'photo-lightbox';
    lightbox.onclick = () => lightbox.remove();
    // DOM construction (not innerHTML) so data: URIs / arbitrary URLs are set
    // as properties, never parsed as markup.
    const img = document.createElement('img');
    img.src = url;
    img.alt = 'Full photo';
    const closeBtn = document.createElement('button');
    closeBtn.className = 'photo-lightbox-close';
    closeBtn.setAttribute('aria-label', 'Close photo');
    closeBtn.innerHTML = '<span class="material-icons">close</span>';
    lightbox.appendChild(img);
    lightbox.appendChild(closeBtn);
    document.body.appendChild(lightbox);
  }
};

// window.TicketPhotos.removeUploaded — THE single removal path for a photo
// already persisted on tenants/{tid}/tickets/{ticketId}.photos (TASK-1214 —
// uploaded ticket photos were permanent: every surface spliced local state
// only, so the doc kept the photo and it reappeared on reopen + rendered on
// the customer-facing ticket PDF). All four surfaces (clock kiosk, job-board
// ticket builder, public token portal, billing View Ticket) call this via
// window.* (Pitfall #1).
//
// Behavior (COD 2026-07-16T20-03 binding mitigations):
//  - confirm ("Delete this photo?") before any write
//  - fresh-reads the ticket; refuses when invoiced/archived/locked (server
//    rules already deny — this is defense-in-depth with a human message),
//    and when driverEditLocked if respectDriverLock (driver surfaces; the
//    biller is deliberately NOT blocked by the driver lock — TASK-566)
//  - arrayRemove(EXACT stored element) — matched by url against the LIVE doc
//    (urls are unique: the storage path embeds a ms timestamp), so restore-
//    path field drift (dropped uploadedAt etc.) can never silently no-op it
//  - recomputes photoCount when the doc carries one (jobboard/public portal
//    increment it on add; clock.js never did — recompute self-heals drift;
//    never introduces the field on docs that lack it)
//  - audit receipt to tenants/{tid}/jobAuditLog (tickets audit there, NOT
//    the general auditLog): action TICKET_PHOTO_DELETE + remover identity +
//    photo name/url + ticketId. Fire-and-forget, matching
//    pubLogDriverTicketAudit / toggleDriverEditLock.
//  - storage blob deliberately left orphaned (recoverable; no delete)
// Returns { ok, alreadyGone?, reason? } — callers splice local UI state on ok.
window.TicketPhotos = {
  async removeUploaded({ tenantId, ticketId, photo, source, personId = null, personName = null, respectDriverLock = false }) {
    if (!tenantId || !ticketId || !photo?.url) {
      console.warn('[TicketPhotos] removeUploaded missing args', { tenantId, ticketId, hasUrl: !!photo?.url });
      return { ok: false, reason: 'bad-args' };
    }
    const confirmed = typeof window.showConfirm === 'function'
      ? await window.showConfirm('Delete Photo', 'Delete this photo?', { confirmText: 'Delete', cancelText: 'Cancel' })
      : window.confirm('Delete this photo?');
    if (!confirmed) return { ok: false, reason: 'cancelled' };
    try {
      const ticketRef = doc(db, 'tenants', tenantId, 'tickets', ticketId);
      const snap = await getDoc(ticketRef);
      if (!snap.exists()) return { ok: true, alreadyGone: true };
      const data = snap.data();
      // TASK-4600 — same widening as pubTicketIsDriverLocked; a photo on a
      // billed ticket is as much part of the closed record as a line is.
      const locked = data.invoiced === true || !!data.invoiceNumber || !!data.invoicedAt
        || data.status === 'archived' || data.status === 'locked';
      if (locked || (respectDriverLock && data.driverEditLocked === true)) {
        window.showToast?.("This ticket is locked — photos can't be deleted", 'error');
        return { ok: false, reason: 'locked' };
      }
      const current = Array.isArray(data.photos) ? data.photos : [];
      // Legacy tolerance: some old entries are raw url strings, not objects.
      const urlOf = (p) => (typeof p === 'string' ? p : (p?.url || p?.downloadUrl || null));
      // Exact-object match FIRST (2nd-COD Code Reviewer): if the caller's
      // _originalPhoto is present verbatim in the live doc, remove only that
      // element — a duplicated URL in legacy/imported data must not bulk-delete
      // every row sharing it. URL matching stays as the drift fallback.
      const exact = current.find(p => typeof p === 'object' && p !== null
        && JSON.stringify(p) === JSON.stringify(photo));
      const matches = exact ? [exact] : current.filter(p => urlOf(p) === photo.url);
      if (matches.length === 0) return { ok: true, alreadyGone: true };
      const updates = {
        photos: arrayRemove(...matches),
        // TASK-4699 — the exact MIRROR of the photo-upload stamps that landed on
        // jobboard.js / clock.js. A photo attached to a ticket is on the copy the
        // customer received; a photo REMOVED from it is the same fact running
        // backwards, and it was unstamped on every surface — office included,
        // which is a hole in TASK-4662 itself, not just in the driver lane.
        // Stamped in the shared helper because all four callers (this file's
        // pubRemovePhoto, clock.js, jobboard.js, billing.js) want the same
        // answer: something the customer can see is gone. Reached only past the
        // `matches.length === 0` early-return, so a delete that removed nothing
        // stamps nothing, and past the lock check, so it never fires on a ticket
        // whose rules arm carries the hasOnly() lifecycle allowlist.
        ticketEmailDirtyAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };
      if (typeof data.photoCount === 'number') {
        updates.photoCount = Math.max(0, current.length - matches.length);
      }
      await updateDoc(ticketRef, updates);
      try {
        await addDoc(collection(db, 'tenants', tenantId, 'jobAuditLog'), {
          action: 'TICKET_PHOTO_DELETE',
          source: source || 'unknown',
          timestamp: Timestamp.now(),
          user: window.AppState?.user?.email || window.AppState?.user?.uid || 'driver-pin',
          personId: personId || null,
          personName: personName || null,
          ticketId,
          ticketNo: data.ticketNo ?? null,
          customerName: data.customerName || null,
          photoName: photo.name || (typeof matches[0] === 'object' ? matches[0]?.name : null) || null,
          photoUrl: photo.url,
          photoUploadedAt: (typeof matches[0] === 'object' ? matches[0]?.uploadedAt : null) || null
        });
      } catch (ae) {
        // Evidence-integrity posture (2nd-COD): the deletion succeeded but the
        // audit receipt didn't — surface it loudly instead of console-only.
        console.warn('[TicketPhotos] Photo-delete audit log failed:', ae);
        window.logError?.(ae, { module: 'ticket-photos', operation: 'TICKET_PHOTO_DELETE_AUDIT', ticketId, source });
        window.showToast?.('Photo deleted, but the audit record failed to write', 'error');
        return { ok: true, auditFailed: true };
      }
      window.showToast?.('Photo deleted', 'success');
      return { ok: true };
    } catch (err) {
      console.error('[TicketPhotos] Photo delete failed:', err);
      window.logError?.(err, { module: 'ticket-photos', operation: 'TICKET_PHOTO_DELETE', ticketId, source });
      const denied = err?.code === 'permission-denied' || /permission/i.test(err?.message || '');
      window.showToast?.(denied ? "This ticket is locked — photos can't be deleted" : 'Error deleting photo — try again', 'error');
      return { ok: false, reason: denied ? 'denied' : 'error' };
    }
  }
};

window.pubHandlePhotoSelect = function(files) {
  const pc = window._timesheetCtx;
  if (!pc || !files || files.length === 0) return;
  // The list is captured at pick time: the editor resets clockOutPhotos to a
  // NEW array on open/close, so a conversion finishing late can't land on a
  // different ticket.
  const list = pc.clockOutPhotos;
  const picked = Array.from(files);
  const input = document.getElementById('pub-photo-input');
  if (input) input.value = '';
  _pubPhotoPrep.track((async () => {
    for (const raw of picked) {
      const prep = await pubPreparePickedPhoto(raw);
      if (!prep) continue;
      list.push({ file: prep.file, contentType: prep.contentType, name: prep.file.name || raw.name });
      if (pc.clockOutPhotos === list) pubRefreshPhotoThumbnails();
    }
  })());
};

window.pubRemovePhoto = async function(idx) {
  const pc = window._timesheetCtx;
  const photo = pc?.clockOutPhotos?.[idx];
  if (!photo) return;
  if (photo._existing && photo.url) {
    // TASK-1214 — persist the removal IMMEDIATELY through the shared helper
    // (confirm + arrayRemove + photoCount + jobAuditLog receipt). The old
    // splice-only path relied on the Save-time full-array overwrite below —
    // abandoning the form silently resurrected the photo.
    const personId = window.AppState?.driverPersonId || pc.authenticatedPersonId || null;
    const person = (pc.people || []).find(p => p.id === personId);
    const res = await window.TicketPhotos?.removeUploaded({
      tenantId: pc.tenantId,
      ticketId: pc.editTicketId || pc.clockOutEntry?.ticketId || null,
      photo: photo._originalPhoto || { url: photo.url, name: photo.name },
      source: 'driver-public-portal',
      personId,
      personName: person?.name || null,
      respectDriverLock: true
    });
    if (!res?.ok) return;
    const i = pc.clockOutPhotos.indexOf(photo);
    if (i !== -1) pc.clockOutPhotos.splice(i, 1);
  } else {
    pc.clockOutPhotos.splice(idx, 1);
  }
  pubRefreshPhotoThumbnails();
};

// TASK-1220 — tap a thumbnail → full-size lightbox (PDFs keep a new tab).
window.pubShowPhoto = function(idx) {
  const photo = window._timesheetCtx?.clockOutPhotos?.[idx];
  if (!photo) return;
  const isPdf = photo.name?.toLowerCase().endsWith('.pdf');
  const url = photo._existing ? photo.url : (photo.file ? URL.createObjectURL(photo.file) : '');
  if (!url) return;
  if (isPdf) { if (photo._existing) window.open(url, '_blank'); return; }
  window.PhotoLightbox?.show(url);
};

function pubRefreshPhotoThumbnails() {
  const el = document.getElementById('pub-photo-thumbnails');
  if (!el) return;
  const photos = window._timesheetCtx.clockOutPhotos;

  if (photos.length === 0) { el.innerHTML = ''; return; }

  el.innerHTML = photos.map((photo, idx) => {
    const isPdf = photo.name.toLowerCase().endsWith('.pdf');
    const imgSrc = photo._existing ? photo.url : (photo.file ? URL.createObjectURL(photo.file) : '');
    const viewable = !isPdf ? !!imgSrc : !!(photo._existing && photo.url);
    return `
      <div ${viewable ? `onclick="window.pubShowPhoto(${idx})"` : ''} style="position:relative;width:68px;height:68px;border-radius:10px;overflow:hidden;border:1px solid #e2e8f0;background:#f8fafc;${viewable ? 'cursor:pointer;' : ''}">
        ${isPdf
          ? `<div style="display:flex;align-items:center;justify-content:center;width:100%;height:100%;"><span class="material-icons" style="font-size:28px;color:#64748b;">description</span></div>`
          : `<img src="${window.escAttr(imgSrc)}" style="width:100%;height:100%;object-fit:cover;" alt="${window.escAttr(photo.name)}">`
        }
        <button type="button" aria-label="Delete photo" onclick="event.stopPropagation();window.pubRemovePhoto(${idx})" style="position:absolute;top:0;right:0;width:44px;height:44px;background:transparent;border:none;cursor:pointer;padding:0;display:flex;align-items:flex-start;justify-content:flex-end;">
          <span style="margin:3px 3px 0 0;background:rgba(0,0,0,0.55);border-radius:50%;width:22px;height:22px;display:flex;align-items:center;justify-content:center;"><span class="material-icons" style="font-size:14px;color:#fff;">close</span></span>
        </button>
      </div>
    `;
  }).join('');
}
// ============================================
// CLOCK-OUT TIME EDIT (inline on hero header)
// ============================================
window.pubShowTimeEdit = function() {
  // Public PIN/link flow is always driver scope — gate on tenant setting
  if (window.AppState?.settings?.allowDriverTimeEdit !== true) return;
  const display = document.getElementById('pub-clockout-times-display');
  const editor = document.getElementById('pub-clockout-times-edit');
  if (display) display.style.display = 'none';
  if (editor) editor.style.display = 'block';
};

window.pubCancelTimeEdit = function() {
  const display = document.getElementById('pub-clockout-times-display');
  const editor = document.getElementById('pub-clockout-times-edit');
  if (display) display.style.display = 'flex';
  if (editor) editor.style.display = 'none';
};

window.pubApplyTimeEdit = async function() {
  // Defense-in-depth gate
  if (window.AppState?.settings?.allowDriverTimeEdit !== true) return;
  const cinVal = document.getElementById('pub-clockout-edit-cin')?.value;
  const coutVal = document.getElementById('pub-clockout-edit-cout')?.value;
  if (!cinVal || !coutVal) { showToast('Both times required', 'error'); return; }

  // clock-dst-all — COMPANY zone; repeated-hour time asked (with hours),
  // nonexistent time / unreadable zone refused. Nothing applied on refusal.
  const _tlt = window.TenantLocalTime;
  const _ck = 'pub-clockout-edit-cin', _ok = 'pub-clockout-edit-cout';
  const _res = await _tlt.resolveAll([
    { str: cinVal, key: _ck, label: 'Clock In', optional: false },
    { str: coutVal, key: _ok, label: 'Clock Out', optional: false },
  ], { describe: (it, ms) => {
    const other = it.key === _ck ? _tlt.peekMs(coutVal, { key: _ok }) : _tlt.peekMs(cinVal, { key: _ck });
    const h = it.key === _ck ? (other - ms) / 3600000 : (ms - other) / 3600000;
    return Number.isFinite(h) && h > 0 ? ` — ${_tlt.hm(h)} shift` : '';
  } });
  if (!_res.ok) { showToast(_res.message, 'error'); return; }
  const newIn = new Date(_res.ms[0]);
  const newOut = new Date(_res.ms[1]);
  const now = new Date();
  const pc = window._timesheetCtx;
  const entry = pc.clockOutEntry;
  const origClockIn = entry?.clockIn?.toDate?.() || new Date(entry?.clockIn);
  const newHours = parseFloat(((newOut - newIn) / 3600000).toFixed(2));

  // --- Time validation with modal dialogs ---

  // 1. Negative hours (hard block)
  if (newOut <= newIn) {
    await showConfirm?.('Invalid Times', 'Clock-out is before clock-in. Please fix the times.', {
      confirmText: 'OK', cancelText: 'Cancel'
    });
    return;
  }

  // 2. Clock-out in the future (> 1 min ahead)
  if ((newOut - now) > 60000) {
    const ok = await showConfirm?.('Future Clock-Out', 'Clock-out time is in the future. Are you sure?', {
      confirmText: 'Yes, keep it', cancelText: 'Cancel'
    });
    if (!ok) return;
  }

  // 3. Clock-in date changed to a different day
  if (origClockIn && _tlt.dateKey(origClockIn) !== _tlt.dateKey(newIn)) {
    const ok = await showConfirm?.('Date Changed', 'Clock-in date was changed to a different day. Are you sure?', {
      confirmText: 'Yes, keep it', cancelText: 'Cancel'
    });
    if (!ok) return;
  }

  // 4. Shift > 24 hours
  //
  // TASK-9217 — TWO CASES NOW, because since TASK-9191 they have two different
  // outcomes and telling a driver "are you sure?" about a save that is going to
  // be refused is not a question, it is a dead end.
  //
  // 🔴 THE `active` BRANCH IS UNCHANGED ON PURPOSE. This same function gates the
  // INITIAL clock-out, whose pre-image is 'active' — and the rules clause skips
  // an active pre-image entirely (verified on the emulator: active → completed
  // with a 30h window is ALLOWED). A genuine 26-hour forgotten clock-out has to
  // stay closable, so that path keeps its confirm and keeps its wording. Making
  // it a block here would be a fresh TASK-428, pointed the other way.
  if (newHours > 24) {
    if (pubClockWindowIsBounded(entry)) {
      // Re-edit of an ALREADY-COMPLETED entry: firestore.rules WILL refuse the
      // clock write in step 9 of the cascade. Say so, and default to the way
      // out — cancelText is the primary action here, not the escape.
      const ok = await showConfirm?.(
        'Over 24 Hours',
        pubClockWindowRefusal(newIn, newOut) +
        ' Go back and fix the dates to keep editing this ticket yourself.',
        { confirmText: 'Keep it anyway', cancelText: 'Go back and fix the dates' }
      );
      // Still a confirm, not a block: the office arms (admin / dispatcher /
      // workforce / billing) are unbounded, and this public editor is reachable
      // by an office user on a driver link. Blocking would lock the one actor
      // the rules deliberately let through.
      if (!ok) return;
    } else {
      const ok = await showConfirm?.('Long Shift', 'Shift is over 24 hours. Are you sure?', {
        confirmText: 'Yes, keep it', cancelText: 'Cancel'
      });
      if (!ok) return;
    }
  }

  // Store edited times on timesheet context for the confirm step
  pc._editedClockIn = newIn;
  pc._editedClockOut = newOut;

  // Update display
  const display = document.getElementById('pub-clockout-times-display');
  const editor = document.getElementById('pub-clockout-times-edit');
  const hoursEl = document.getElementById('pub-clockout-hours');
  if (display) {
    const _z = _tlt.zone() || undefined;
    const inStr = newIn.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: _z });
    const outStr = newOut.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: _z });
    display.innerHTML = `
      <div style="text-align:center;cursor:pointer;" onclick="window.pubShowTimeEdit()">
        <div style="font-size:0.6rem;text-transform:uppercase;letter-spacing:1px;color:#475569;">In</div>
        <div style="font-size:1.1rem;font-weight:700;color:#34d399;border-bottom:1px dashed rgba(52,211,153,0.4);">${inStr}</div>
      </div>
      <div style="flex:0 0 auto;display:flex;flex-direction:column;align-items:center;gap:2px;">
        <div style="width:40px;height:1px;background:#334155;"></div>
        <div id="pub-clockout-hours" style="font-size:1.5rem;font-weight:800;color:#fff;line-height:1;">${newHours.toFixed(2)}</div>
        <div style="font-size:0.65rem;color:#64748b;font-weight:500;">hours</div>
        <div style="width:40px;height:1px;background:#334155;"></div>
      </div>
      <div style="text-align:center;cursor:pointer;" onclick="window.pubShowTimeEdit()">
        <div style="font-size:0.6rem;text-transform:uppercase;letter-spacing:1px;color:#475569;">Out</div>
        <div style="font-size:1.1rem;font-weight:700;color:#f87171;border-bottom:1px dashed rgba(248,113,113,0.4);">${outStr}</div>
      </div>
      <span class="material-icons" onclick="window.pubShowTimeEdit()" style="font-size:16px;color:#34d399;margin-left:4px;">check_circle</span>
    `;
    display.style.display = 'flex';
  }
  if (editor) editor.style.display = 'none';
  showToast('Times adjusted', 'success');
};

// ============================================
// AUTO-APPLY PENDING TIME EDITS
// If the time editor is still open when user hits Save/Clock Out,
// auto-apply the edited values so they don't get silently lost.
// Returns false if validation fails (caller should abort).
// ============================================
window.pubAutoApplyPendingTimeEdit = async function() {
  const editor = document.getElementById('pub-clockout-times-edit');
  if (!editor || editor.style.display === 'none') return true; // editor not open, nothing to do
  // Editor is open — apply the values automatically
  const cinVal = document.getElementById('pub-clockout-edit-cin')?.value;
  const coutVal = document.getElementById('pub-clockout-edit-cout')?.value;
  if (!cinVal || !coutVal) { showToast('Both clock-in and clock-out times are required', 'error'); return false; }
  // Delegate to the existing Apply logic (handles validation, confirm dialogs, display update)
  await window.pubApplyTimeEdit();
  // Check if Apply succeeded (editor should be hidden now)
  return editor.style.display === 'none';
};
async function pubUploadClockOutPhotos(ticketId) {
  const pc = window._timesheetCtx;
  // Only upload NEW photos (skip _existing ones already in Firestore)
  const newPhotos = pc.clockOutPhotos.filter(p => !p._existing && p.file);
  if (newPhotos.length === 0) return;

  const photos = [];
  const uploadedStaged = [];
  let failCount = 0;
  for (const photo of newPhotos) {
    try {
      const timestamp = Date.now();
      const safeName = photo.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const path = `tickets/${pc.tenantId}/${ticketId}/${timestamp}_${safeName}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, photo.file, photo.contentType ? { contentType: photo.contentType } : undefined);
      const url = await getDownloadURL(storageRef);
      const photoObj = { url, name: photo.name, uploadedAt: new Date().toISOString() };
      photos.push(photoObj);
      uploadedStaged.push({ photo, photoObj, url });
    } catch (err) {
      failCount++;
      console.error('[PublicClock] Photo upload error:', err);
    }
  }

  if (photos.length > 0) {
    await updateDoc(doc(db, 'tenants', pc.tenantId, 'tickets', ticketId), {
      photos: arrayUnion(...photos),
      photoCount: increment(photos.length),
      // TASK-4699 — ticket photos ride along on the customer's copy; same call
      // as clock.js uploadClockOutPhotos. Inside the `photos.length > 0` guard,
      // so a pass that uploaded nothing stamps nothing. Stamped here rather than
      // left to the caller (pubSaveEditTicket, which also stamps) so this writer
      // is self-sufficient — a second caller added later inherits the stamp
      // instead of silently not having one.
      ticketEmailDirtyAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    // TASK-1214 — mark just-uploaded photos as persisted so their ✕ routes
    // through TicketPhotos.removeUploaded (they're in the ticket doc now; a
    // local splice would resurrect them). _originalPhoto = the EXACT stored
    // element (COD binding) — never mutate it.
    for (const { photo, photoObj, url } of uploadedStaged) {
      photo._existing = true;
      photo.url = url;
      photo.uploadedAt = photoObj.uploadedAt;
      photo._originalPhoto = photoObj;
    }
  }

  if (failCount > 0) {
    showToast(`${failCount} photo(s) failed to upload — try again`, 'error');
  }

  // Return whether ALL uploads succeeded (controls _appliedPhotos flag)
  return failCount === 0;
}

async function pubCheckTicketReady(ticketId) {
  // Ticket stays draft until biller explicitly marks it ready
  // (Previously auto-promoted to 'ready' when last person clocked out)
}

// TASK-9646 S2 — pubRecalcTicketTotal removed: the ticket header total is
// computed on the SERVER on every ticketLine write (onTicketLineWrite →
// ticketTotalsReconcile). Its bare lineAmount sum never subtracted DOWNTIME or
// added tax, so it was also writing a wrong number.

// ============================================
// PUBLIC CLOCK — RE-EDIT TICKET
// ============================================

async function pubLoadTicketIntoForm(ticketId, entryId) {
  const pc = window._timesheetCtx;
  const tenant = pc.tenantId;
  // TASK-9402 integration COD r1 — bumped BEFORE the first await: the photo and
  // line arrays and editTicketId are only swapped after the Firestore reads, so a
  // Save waiting on a photo conversion can't see this load any other way.
  _pubTicketLoadGen++;

  // Fetch ticket doc
  const ticketSnap = await getDoc(doc(db, 'tenants', tenant, 'tickets', ticketId));
  if (!ticketSnap.exists()) throw new Error('Ticket not found');
  const ticketData = ticketSnap.data();

  // Check lock status (TASK-566: includes the biller's per-ticket
  // driverEditLocked 🔒 — rules enforce it server-side too)
  const isLocked = pubTicketIsDriverLocked(ticketData);
  if (isLocked) {
    showToast(pubTicketIsSent(ticketData) ? PUB_TICKET_SENT_MSG : 'Ticket is locked — cannot edit', 'error');
    return false;
  }

  // Fetch all ticketLines
  const linesSnap = await getDocs(collection(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines'));
  const lines = linesSnap.docs.map(d => ({ id: d.id, ...d.data() }));

  // TASK-566 — compact BEFORE snapshot for the audit row pubSaveEditTicket
  // writes to jobAuditLog on save (before→after of the driver's edit).
  pc._auditBefore = {
    notes: ticketData.notes || '',
    dumps: ticketData.dumps || 0,
    waterRefills: ticketData.waterRefills || 0,
    // TASK-9646 S4a — no grandTotal / rate / lineAmount in a driver audit row.
    lines: lines.filter(l => l.type !== 'EMPLOYEE').map(l => ({
      type: l.type || null,
      assetName: l.assetName || null,
      fromTime: pubAuditIso(l.fromTime),
      toTime: pubAuditIso(l.toTime),
      hours: l.hours ?? null,
      notes: l.notes || null
    }))
  };

  // Reset form arrays
  pc.clockOutEquipment = [];
  pc.clockOutDowntime = [];
  pc.clockOutStandby = [];
  pc.clockOutPotholes = [];
  pc.clockOutPhotos = [];

  // Map EQUIPMENT lines
  for (const line of lines.filter(l => l.type === 'EQUIPMENT')) {
    const fromTime = line.fromTime?.toDate?.() || (line.fromTime ? new Date(line.fromTime) : new Date());
    const toTime = line.toTime?.toDate?.() || (line.toTime ? new Date(line.toTime) : new Date());
    pc.clockOutEquipment.push(pubRowFromInstants({
      assetId: line.assetId || '',
      assetName: line.assetName || 'Equipment',
      assetType: line.assetType || 'asset',
      fleetNumber: line.fleetNumber || '',
      rate: PUB_EQUIPMENT_RATE_SERVER_PRICED, // TASK-9646 S4a — never hold the stored rate (save recreates at 0 + ratePending)
      startTime: pubToLocalDatetimeStr(fromTime),
      endTime: pubToLocalDatetimeStr(toTime),
      notes: line.notes || ''
    }, fromTime, toTime));
  }

  // Map DOWNTIME lines
  for (const line of lines.filter(l => l.type === 'DOWNTIME')) {
    const fromTime = line.fromTime?.toDate?.() || (line.fromTime ? new Date(line.fromTime) : new Date());
    const toTime = line.toTime?.toDate?.() || (line.toTime ? new Date(line.toTime) : new Date());
    pc.clockOutDowntime.push(pubRowFromInstants({
      startTime: pubToLocalDatetimeStr(fromTime),
      endTime: pubToLocalDatetimeStr(toTime),
      notes: line.notes || ''
    }, fromTime, toTime));
  }

  // Map STANDBY lines
  for (const line of lines.filter(l => l.type === 'STANDBY')) {
    const fromTime = line.fromTime?.toDate?.() || (line.fromTime ? new Date(line.fromTime) : new Date());
    const toTime = line.toTime?.toDate?.() || (line.toTime ? new Date(line.toTime) : new Date());
    pc.clockOutStandby.push(pubRowFromInstants({
      startTime: pubToLocalDatetimeStr(fromTime),
      endTime: pubToLocalDatetimeStr(toTime),
      notes: line.notes || ''
    }, fromTime, toTime));
  }

  // Map POTHOLE lines (parse description back to fields)
  for (const line of lines.filter(l => l.type === 'POTHOLE')) {
    const desc = line.description || '';
    let type = '', tolFeet = 0, tolInches = 0, pipeSize = '';
    const typeMatch = desc.match(/Type:\s*([^,]+)/);
    if (typeMatch) type = typeMatch[1].trim();
    const tolMatch = desc.match(/TOL:\s*(\d+)'(\d+)"/);
    if (tolMatch) { tolFeet = parseInt(tolMatch[1]) || 0; tolInches = parseInt(tolMatch[2]) || 0; }
    const pipeMatch = desc.match(/Pipe:\s*(\d+(?:\.\d+)?)"/);
    if (pipeMatch) pipeSize = pipeMatch[1];

    // Load existing photos with _existing flag
    const existingPhotos = (line.photos || []).map(p => ({
      _existing: true, url: p.url, name: p.name || 'photo', preview: p.url
    }));

    pc.clockOutPotholes.push({
      type, tolFeet, tolInches, pipeSize,
      gpsLat: line.gpsLat || null,
      gpsLng: line.gpsLng || null,
      notes: line.notes || '',
      photos: existingPhotos
    });
  }

  // Store existing ticket-level photos
  if (ticketData.photos && ticketData.photos.length > 0) {
    for (const p of ticketData.photos) {
      // TASK-1214 (COD binding) — carry uploadedAt (the old restore dropped it,
      // so the Save-time overwrite rewrote every photo with a fabricated
      // timestamp) and keep the EXACT stored object for arrayRemove.
      pc.clockOutPhotos.push({ _existing: true, url: p.url, name: p.name || 'photo', uploadedAt: p.uploadedAt || null, _originalPhoto: p });
    }
  }

  // Store ticket-level data for pre-fill
  pc._editTicketNotes = ticketData.notes || '';
  pc._editShiftNotes = ''; // Will be loaded from clock entry
  pc._editDumps = ticketData.dumps || 0;
  pc._editWaterRefills = ticketData.waterRefills || 0;

  // Set edit mode state
  pc.editMode = true;
  pc.editTicketId = ticketId;
  pc.editEntryId = entryId;

  // Set all dedup flags to true (we manage saves via delete-and-recreate)
  pc._appliedEquipment = true;
  pc._appliedDowntime = true;
  pc._appliedStandby = true;
  pc._appliedFieldActivity = true;
  pc._appliedPotholes = true;
  pc._appliedNotes = true;
  pc._appliedPhotos = true;

  return true;
}

window.pubReEditTicket = async function(ticketId, entryId) {
  const pc = window._timesheetCtx;
  if (!pc) return;
  if (!(await pubRequireTicketGrant())) return; // TASK-10039

  const mainContent = document.getElementById('main-content');
  if (!mainContent) return;

  // Show loading
  mainContent.innerHTML = `
    <div style="max-width:480px;margin:0 auto;padding:60px 20px;text-align:center;">
      <span class="btn-spinner" style="width:36px;height:36px;border-width:3px;"></span>
      <p style="margin-top:16px;font-size:0.9rem;color:#64748b;">Loading ticket for editing...</p>
    </div>`;

  try {
    // Timeout to prevent forever loading on iOS
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Ticket load timed out')), 15000)
    );
    const loaded = await Promise.race([
      pubLoadTicketIntoForm(ticketId, entryId),
      timeoutPromise
    ]);
    if (!loaded) {
      // Lock check failed — return to dispatch (driver-PIN context only post TASK-363)
      window.navigateToView?.('dispatch');
      return;
    }

    // Fetch clock entry for header info
    const entrySnap = await getDoc(doc(db, 'tenants', pc.tenantId, 'clockEntries', entryId));
    if (!entrySnap.exists()) {
      showToast('Clock entry not found', 'error');
      window.navigateToView?.('dispatch');
      return;
    }
    const entry = { id: entrySnap.id, ...entrySnap.data() };

    // Store entry as clockOutEntry so form helpers work
    pc.clockOutEntry = entry;
    pc.clockOutEntryId = entry.id;

    // Load shift notes from clock entry
    pc._editShiftNotes = entry.clockOutNotes || '';

    // Ensure people array exists (may not be loaded yet in PIN driver mode)
    if (!pc.people || pc.people.length === 0) {
      try {
        const peopleSnap = await getDocs(query(collection(db, 'tenants', pc.tenantId, 'people'), where('active', '==', true)));
        pc.people = peopleSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      } catch (e) {
        console.warn('[PublicClock] Could not load people for re-edit:', e);
        pc.people = [];
      }
    }
    const person = (pc.people || []).find(p => p.id === entry.personId);
    const clockIn = entry.clockIn?.toDate?.() || new Date(entry.clockIn);
    const clockOut = entry.clockOut?.toDate?.() || new Date(entry.clockOut || Date.now());
    const elapsedMs = clockOut - clockIn;
    const hrs = Math.floor(elapsedMs / 3600000);
    const mins = Math.floor((elapsedMs % 3600000) / 60000);
    const elapsed = hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
    const hours = (elapsedMs / 3600000).toFixed(2);

    // Reset edited times
    pc._editedClockIn = null;
    pc._editedClockOut = null;

    // Hide mobile nav so it doesn't cover the sticky save bar
    const mobileNav = document.querySelector('.mobile-nav');
    if (mobileNav) mobileNav.classList.add('hidden');

    // Render the form in edit mode
    mainContent.innerHTML = pubBuildClockOutForm(entry, person, clockIn, clockOut, hours, elapsed, 'edit');

    // Pre-fill DOM elements after form is rendered
    const shiftNotesEl = document.getElementById('pub-shift-notes');
    if (shiftNotesEl) shiftNotesEl.value = pc._editShiftNotes;
    const ticketNotesEl = document.getElementById('pub-ticket-notes');
    if (ticketNotesEl) ticketNotesEl.value = pc._editTicketNotes;
    const dumpsEl = document.getElementById('pub-field-dumps');
    if (dumpsEl) dumpsEl.value = pc._editDumps;
    const waterRefillsEl = document.getElementById('pub-field-water-refills');
    if (waterRefillsEl) waterRefillsEl.value = pc._editWaterRefills;

    // Refresh dynamic lists
    if (pc.clockOutEquipment.length > 0) {
      const eqList = document.getElementById('pub-equipment-list');
      if (eqList) eqList.innerHTML = pubRenderEquipmentList();
    }
    pubRefreshTimeEntryList('downtime');
    pubRefreshTimeEntryList('standby');
    pubRefreshPotholeList();
    pubRefreshPhotoThumbnails();

    // Auto-expand sections that have data
    if (pc._editShiftNotes || pc._editTicketNotes) window.pubToggleSection?.('pub-sec-notes');
    if (pc.clockOutEquipment.length > 0) { /* already open via badge in builder */ }
    if (pc.clockOutDowntime.length > 0) window.pubToggleSection?.('pub-sec-downtime');
    if (pc.clockOutStandby.length > 0) window.pubToggleSection?.('pub-sec-standby');
    if (pc._editDumps > 0 || pc._editWaterRefills > 0) window.pubToggleSection?.('pub-sec-field');
    if (pc.clockOutPotholes.length > 0) window.pubToggleSection?.('pub-sec-pothole');
    if (pc.clockOutPhotos.length > 0) window.pubToggleSection?.('pub-sec-photos');

  } catch (err) {
    console.error('[PublicClock] Re-edit ticket error:', err);
    showToast('Error loading ticket for editing', 'error');
    window.navigateToView?.('dispatch');
  }
};

window.pubSaveEditTicket = async function() {
  if (!(await pubRequireTicketGrant())) return; // TASK-10039 — before any write, incl. the time auto-apply
  // TASK-9402 — wait out any photo still converting BEFORE the cascade starts
  // (a second Save during the wait is refused, not queued).
  // COD r4 — capture WHICH ticket editor this Save is for before the wait
  // (Cancel stays live; a re-edit of ticket B replaces editTicketId and the
  // photo array). If either changed, this Save is dropped — never run A's
  // delete-and-recreate cascade on B.
  const _pcBefore = window._timesheetCtx;
  const _ticketBefore = _pcBefore?.editTicketId;
  const _photosBefore = _pcBefore?.clockOutPhotos;
  const _loadGenBefore = _pubTicketLoadGen;
  const _editorChanged = () => window._timesheetCtx !== _pcBefore || _pcBefore?.editTicketId !== _ticketBefore
    || _pcBefore?.clockOutPhotos !== _photosBefore || _pubTicketLoadGen !== _loadGenBefore;
  if (!await settlePhotoPrepBeforeSave(_pubPhotoPrep, 'pub-btn-save-edit-ticket')) return;
  if (_editorChanged()) {
    console.warn('[Timesheet] ticket editor changed while photos converted — that Save was dropped');
    return;
  }
  // Auto-apply any pending time edits before saving
  if (!await window.pubAutoApplyPendingTimeEdit()) return;
  // Integration COD r1 — that was another await; re-check before the cascade.
  if (_editorChanged()) {
    console.warn('[Timesheet] ticket editor changed before Save started — that Save was dropped');
    return;
  }

  const pc = window._timesheetCtx;
  if (!pc?.editMode || !pc.editTicketId) {
    showToast('No ticket in edit mode', 'error');
    return;
  }

  const btn = document.getElementById('pub-btn-save-edit-ticket');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Saving...'; }

  try {
    const tenant = pc.tenantId;
    const ticketId = pc.editTicketId;
    const entryId = pc.editEntryId;
    const entry = pc.clockOutEntry;

    // TASK-566 — fresh lock pre-check: the biller may have flipped the
    // per-ticket 🔒 (or invoiced) WHILE the driver had this editor open.
    // Bail gracefully before the delete-and-recreate cascade starts; rules
    // would deny the writes anyway, but this avoids a half-run cascade.
    const { editable: _stillEditable, ticket: _lockT } = await pubCheckTicketEditable(ticketId);
    if (!_stillEditable) {
      showToast(pubTicketLockedMsg(_lockT), 'error');
      if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:20px;">save</span> Save Changes'; }
      pc.editMode = false;
      pc.editTicketId = null;
      pc.editEntryId = null;
      const mobileNavLock = document.querySelector('.mobile-nav');
      if (mobileNavLock) mobileNavLock.classList.remove('hidden');
      window.navigateToView?.('dispatch');
      return;
    }

    // clock-dst-all — every equipment / downtime / standby time is COMPANY time.
    // Resolve them all BEFORE the delete-and-recreate cascade: a repeated-hour
    // time is asked (never guessed), a nonexistent time or an unreadable zone
    // refuses with nothing written. The strict TenantLocalTime.toDate() calls
    // below then return exactly these instants.
    const _tlRes = await pubResolveTimeRows([
      { label: 'Equipment', rows: pc.clockOutEquipment },
      { label: 'Downtime', rows: pc.clockOutDowntime },
      { label: 'Standby', rows: pc.clockOutStandby },
    ]);
    if (!_tlRes.ok) {
      showToast(_tlRes.message, 'error');
      if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:20px;">save</span> Save Changes'; }
      return;
    }
    const _tlDate = (s) => window.TenantLocalTime.toDate(s);
    const _tlRowDate = (row, field) => window.TenantLocalTime.rowDate(row, field);

    // Validate time entries
    const finalClockIn = pc._editedClockIn || entry.clockIn?.toDate?.() || new Date(entry.clockIn);
    const finalClockOut = pc._editedClockOut || entry.clockOut?.toDate?.() || new Date(entry.clockOut || Date.now());

    // COD 1.15.2009 — keep the ORIGINAL row objects (a row's own time answer
    // lives on the object; a {...copy} would carry none).
    const allTimeEntries = [
      ...pc.clockOutDowntime.map(e => ({ row: e, label: 'Downtime' })),
      ...pc.clockOutStandby.map(e => ({ row: e, label: 'Standby' })),
      ...pc.clockOutEquipment.map(e => ({ row: e, label: 'Equipment', assetName: e.assetName }))
    ];
    for (const te of allTimeEntries) {
      const teStart = _tlRowDate(te.row, 'startTime');
      const teEnd = _tlRowDate(te.row, 'endTime');
      if (teEnd < teStart) {
        const entryName = te.assetName ? `${te.label} "${te.assetName}"` : te.label;
        showToast(`${entryName} end time must be after start time`, 'error');
        if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:20px;">save</span> Save Changes'; }
        return;
      }
    }

    // TASK-9217 — THE 24h WINDOW BOUND, CHECKED HERE AND NOT AT STEP 9.
    //
    // Step 9 writes clockIn / clockOut / totalHours / hours to the clock entry.
    // On a COMPLETED pre-image with a post-image window over 24h, firestore.rules
    // refuses that write — and by then steps 1-8 have ALREADY deleted and
    // re-created every non-EMPLOYEE ticket line, stamped ticketEmailDirtyAt, and
    // rewritten the photo array. The driver's save dies HALF APPLIED: the ticket
    // is rebuilt, the payroll hours are not, and there is no audit row (the audit
    // is step 11). That is not new — a totalHours-only write had the identical
    // failure mode before TASK-9196, and the same shape is on record from
    // TASK-1651's PUB_RATES ReferenceError — but a refusal WE CHOSE is not the
    // place to keep relying on it. Bailing here means nothing has been touched.
    //
    // Same seat as the lock pre-check above, same reasoning, and gated the same
    // three ways it must be: only when the times were actually edited (otherwise
    // no window key moves and the rules clause never fires), only on a COMPLETED
    // pre-image (an 'active' one is the initial clock-out and is allowed), and
    // only over 24h. The editor stays open with every other change intact — undo
    // the time edit and the rest of the save goes through.
    if ((pc._editedClockIn || pc._editedClockOut) && pubClockWindowIsBounded(entry)) {
      const _windowRefusal = pubClockWindowRefusal(finalClockIn, finalClockOut);
      if (_windowRefusal) {
        showToast(_windowRefusal + ' Nothing was saved — undo the time change to save the rest.', 'error');
        if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:20px;">save</span> Save Changes'; }
        return;
      }
    }

    // 1. Delete all non-EMPLOYEE ticketLines
    // TASK-9440 — never the FUEL_SURCHARGE line: it is server-owned (no
    // clockEntryId, so the driver delete rule refuses it, and Promise.all would
    // reject AFTER the other deletes landed, before the re-create step). The
    // server re-derives it from the re-created lines on its next pass.
    const linesSnap = await getDocs(collection(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines'));
    const deletePromises = [];
    for (const lineDoc of linesSnap.docs) {
      if (lineDoc.data().type !== 'EMPLOYEE' && lineDoc.data().type !== 'FUEL_SURCHARGE') {
        deletePromises.push(deleteDoc(doc(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines', lineDoc.id)));
      }
    }
    await Promise.all(deletePromises);

    // TASK-428 slice 2: this driver-PIN "Edit Ticket" cascade DELETES + RE-CREATES
    // every non-EMPLOYEE line. The firestore.rules driver-write gate
    // (driverOwnsTicketLine) requires each created line to carry a clockEntryId
    // that resolves to the driver's own clock entry on this ticket's job. Stamp
    // pc.editEntryId (the entry this ticket was built from) onto every recreated
    // line so these writes pass. Without it, driver edits of EQUIPMENT/DOWNTIME/
    // STANDBY/POTHOLE lines would be DENIED.
    const _editCeId = pc.editEntryId || null;

    // 2. Re-create EQUIPMENT lines
    for (const eq of pc.clockOutEquipment) {
      const eqHours = pubRowHours(eq);
      await pubCreateTicketLine(ticketId, {
        type: 'EQUIPMENT',
        assetId: eq.assetId,
        assetName: eq.assetName,
        assetType: eq.assetType,
        fleetNumber: eq.fleetNumber,
        fromTime: Timestamp.fromDate(_tlRowDate(eq, 'startTime')),
        toTime: Timestamp.fromDate(_tlRowDate(eq, 'endTime')),
        hours: parseFloat(eqHours.toFixed(2)),
        // TASK-1165 (2nd-COD Blast ❌) — recreate at rate:0 and let the server
        // enrichment price it. Passing the STORED rate through violated the S1b
        // driver-arm create gate (rate must be 0) → the whole driver Edit-Ticket
        // save would have been permission-denied. Server re-prices from the price
        // book in the same trigger cycle (the documented biller-override tradeoff).
        rate: 0,
        lineAmount: 0,
        ratePending: true, // TASK-9646 S5 — the server prices it from the price book
        clockEntryId: _editCeId,
        notes: eq.notes?.trim() || null
      });
    }

    // 3. Re-create DOWNTIME lines
    for (const dt of pc.clockOutDowntime) {
      const dtHours = pubRowHours(dt);
      await pubCreateTicketLine(ticketId, {
        type: 'DOWNTIME',
        fromTime: Timestamp.fromDate(_tlRowDate(dt, 'startTime')),
        toTime: Timestamp.fromDate(_tlRowDate(dt, 'endTime')),
        hours: parseFloat(dtHours.toFixed(2)),
        rate: 0,
        lineAmount: 0,
        clockEntryId: _editCeId,
        notes: dt.notes || null
      });
    }

    // 4. Re-create STANDBY lines
    for (const sb of pc.clockOutStandby) {
      const sbHours = pubRowHours(sb);
      await pubCreateTicketLine(ticketId, {
        type: 'STANDBY',
        fromTime: Timestamp.fromDate(_tlRowDate(sb, 'startTime')),
        toTime: Timestamp.fromDate(_tlRowDate(sb, 'endTime')),
        hours: parseFloat(sbHours.toFixed(2)),
        rate: 0,
        lineAmount: 0,
        clockEntryId: _editCeId,
        notes: sb.notes || null
      });
    }

    // 5. Re-create POTHOLE lines (upload only NEW photos, re-attach existing)
    for (const ph of pc.clockOutPotholes) {
      const tolStr = (ph.tolFeet > 0 || ph.tolInches > 0) ? `${ph.tolFeet}'${ph.tolInches}"` : '';
      const desc = [
        ph.type ? `Type: ${ph.type}` : '',
        tolStr ? `TOL: ${tolStr}` : '',
        ph.pipeSize ? `Pipe: ${ph.pipeSize}"` : ''
      ].filter(Boolean).join(', ');
      const lineId = await pubCreateTicketLine(ticketId, {
        type: 'POTHOLE',
        description: desc,
        gpsLat: ph.gpsLat || null,
        gpsLng: ph.gpsLng || null,
        clockEntryId: _editCeId,
        notes: ph.notes || null,
        lineAmount: 0
      });

      if (lineId && (ph.photos || []).length > 0) {
        const existingPhotos = ph.photos.filter(p => p._existing);
        const newPhotos = ph.photos.filter(p => !p._existing);

        // Upload new photos
        if (newPhotos.length > 0) {
          await pubUploadPotholePhotos(ticketId, lineId, newPhotos);
        }

        // Re-attach existing photos
        if (existingPhotos.length > 0) {
          const existingData = existingPhotos.map(p => ({ url: p.url, name: p.name, uploadedAt: p.uploadedAt || new Date().toISOString() }));
          // Merge with any newly uploaded
          const lineSnap = await getDoc(doc(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines', lineId));
          const currentPhotos = lineSnap.exists() ? (lineSnap.data().photos || []) : [];
          await updateDoc(doc(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines', lineId), {
            photos: [...currentPhotos, ...existingData],
            updatedAt: Timestamp.now()
          });
        }
      }
    }

    // 6. Ticket notes — TASK-10036 (c): NOT a straight overwrite any more. The
    // notes are shared by the whole crew; this editor used to SET `notes` from
    // the box (leaving `workPerformed` stale), so a driver re-editing a past
    // shift could silently erase a coworker's line. They now go through THE
    // one merge writer (clock.js saveInvoiceNotes, via window.ClockModule —
    // Pitfall #1) right after the header write below: only the typed delta
    // lands, both copies stay in step, a coworker line is removed only after a
    // confirm naming it, and the server audits the change.
    const ticketNotes = document.getElementById('pub-ticket-notes')?.value?.trim() || '';
    const ticketUpdates = {
      // TASK-4699 — the driver's whole "Edit Ticket" cascade lands here. Steps
      // 1–5 above DELETED and re-created every non-EMPLOYEE line (equipment,
      // downtime, standby, pothole) and step 7 rewrites the field-activity
      // counts; this is the one UNCONDITIONAL ticket-header write in the
      // function, so it is the reliable place to stamp for the entire save —
      // the same reason jbSaveTicket stamps on its notes write.
      //
      // Stamped on the Save PRESS without diffing the form, matching
      // jbSaveTicket and the office's saveReviewTicket. A driver who opens this
      // editor and presses Save has re-created the ticket's lines whether or not
      // he typed anything, and under-flagging is the failure that costs money.
      ticketEmailDirtyAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };

    // 7. SET field activity (absolute values, not increment)
    const dumps = parseInt(document.getElementById('pub-field-dumps')?.value) || 0;
    const waterRefills = parseInt(document.getElementById('pub-field-water-refills')?.value) || 0;
    ticketUpdates.dumps = dumps;
    ticketUpdates.waterRefills = waterRefills;

    await updateDoc(doc(db, 'tenants', tenant, 'tickets', ticketId), ticketUpdates);

    // TASK-10036 (c) — the merge write (see step 6). Fails LOUD: no fallback to
    // the old overwrite if the clock module is somehow missing.
    let _savedTicketNotes = ticketNotes;
    {
      const _saveNotes = window.ClockModule?.saveInvoiceNotes;
      if (typeof _saveNotes !== 'function') {
        throw new Error('Ticket notes could not be saved (clock module not loaded). Reload and try again.');
      }
      const _m = await _saveNotes({
        tenantId: tenant,
        ticketId,
        currentText: ticketNotes,
        baseline: String(pc._editTicketNotes || ''),
        syncElId: 'pub-ticket-notes',
        stateless: true,
        surface: 'driver_timesheet_edit'
      });
      if (_m && typeof _m.text === 'string') _savedTicketNotes = _m.text;
    }

    // 8. Upload only NEW shift-level photos (pubUploadClockOutPhotos already filters _existing)
    const hasNewPhotos = pc.clockOutPhotos.some(p => !p._existing && p.file);
    if (hasNewPhotos) {
      await pubUploadClockOutPhotos(ticketId);
    }

    // Remove deleted existing photos: keep only the _existing ones still in the array
    const keptExisting = pc.clockOutPhotos.filter(p => p._existing).map(p => ({ url: p.url, name: p.name, uploadedAt: p.uploadedAt || new Date().toISOString() }));
    // Re-read ticket to get newly uploaded photos
    const ticketRefresh = await getDoc(doc(db, 'tenants', tenant, 'tickets', ticketId));
    const allPhotos = ticketRefresh.exists() ? (ticketRefresh.data().photos || []) : [];
    // Newly uploaded photos (just added) + kept existing
    const newlyUploaded = allPhotos.filter(p => !keptExisting.some(k => k.url === p.url));
    await updateDoc(doc(db, 'tenants', tenant, 'tickets', ticketId), {
      photos: [...keptExisting, ...newlyUploaded]
    });

    // 9. Persist clock time adjustments if user changed them
    // TASK-9196 — hoisted out of the block below so the audit row at the end of
    // this function can record the same number that was written, instead of
    // re-deriving it from operands that are no longer in scope there.
    const _correctedHours = parseFloat(((finalClockOut - finalClockIn) / 3600000).toFixed(2));
    if (pc._editedClockIn || pc._editedClockOut) {
      const totalHours = _correctedHours;
      const clockUpdate = { updatedAt: Timestamp.now() };
      if (pc._editedClockIn) clockUpdate.clockIn = Timestamp.fromDate(finalClockIn);
      if (pc._editedClockOut) clockUpdate.clockOut = Timestamp.fromDate(finalClockOut);
      clockUpdate.totalHours = totalHours;
      // TASK-9196 — the second of the two paths that used to write totalHours
      // ALONE and leave `hours` frozen at its pre-correction value. Full
      // reasoning (which surfaces read which key, why the fallback stays, and
      // why this satisfies the TASK-9191 window bound) is at pubSaveEditShift
      // above; this site is the same write with the same invariant.
      //
      // ⚠️ This one is step 9 of a cascade whose steps 1-8 have ALREADY deleted
      // and re-created every non-EMPLOYEE ticket line. A rules denial here is a
      // half-applied save — which is precisely why `hours` must carry the fresh
      // value and never the stale one: a stale `hours` is the only shape of this
      // write the 9191 bound would reject.
      clockUpdate.hours = totalHours;
      await updateDoc(doc(db, 'tenants', tenant, 'clockEntries', entryId), clockUpdate);

      // Update EMPLOYEE ticketLine times.
      // TASK-1651/1653 — this used to be `entry.rate || PUB_RATES.nonCdl`:
      // entry.rate is 0 on every canonical line, so the undefined PUB_RATES
      // global ALWAYS threw here — after steps 1–8 had already deleted and
      // recreated the ticket's lines (half-applied save, stale grandTotal, no
      // audit row). EMPLOYEE lines never carry customer money; pin to 0.
      if (entry.ticketLineId) {
        await updateDoc(
          doc(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines', entry.ticketLineId),
          { fromTime: Timestamp.fromDate(finalClockIn), toTime: Timestamp.fromDate(finalClockOut), hours: totalHours, lineAmount: PUB_EMPLOYEE_LINE_AMOUNT, updatedAt: Timestamp.now() }
        );
      }
    }

    // Update shift notes on clock entry
    const shiftNotes = document.getElementById('pub-shift-notes')?.value?.trim() || '';
    await updateDoc(doc(db, 'tenants', tenant, 'clockEntries', entryId), {
      clockOutNotes: shiftNotes || null,
      updatedAt: Timestamp.now()
    });

    // 10. Ticket total — TASK-9646 S2: computed on the SERVER from the lines
    // just written (onTicketLineWrite); no client recalc.

    // TASK-566 — audit the full re-edit cascade (who/what/when, before→after).
    // before = snapshot taken in pubLoadTicketIntoForm; after = what was saved.
    try {
      const _afterLines = [
        ...pc.clockOutEquipment.map(eq => ({ type: 'EQUIPMENT', assetName: eq.assetName || null, fromTime: pubAuditIso(eq.startTime), toTime: pubAuditIso(eq.endTime), notes: eq.notes || null })), // TASK-9646 S4a — no rate
        ...pc.clockOutDowntime.map(dt => ({ type: 'DOWNTIME', fromTime: pubAuditIso(dt.startTime), toTime: pubAuditIso(dt.endTime), notes: dt.notes || null })),
        ...pc.clockOutStandby.map(sb => ({ type: 'STANDBY', fromTime: pubAuditIso(sb.startTime), toTime: pubAuditIso(sb.endTime), notes: sb.notes || null })),
        ...pc.clockOutPotholes.map(ph => ({ type: 'POTHOLE', notes: ph.notes || null }))
      ];
      const _hoursTouched = !!(pc._editedClockIn || pc._editedClockOut);
      pubLogDriverTicketAudit('DRIVER_TICKET_REEDIT_SAVE', ticketId, {
        clockEntryId: entryId,
        touchesPayrollHours: _hoursTouched,
        before: _hoursTouched
          ? {
            ...(pc._auditBefore || {}),
            clockIn: pubAuditIso(entry.clockIn),
            clockOut: pubAuditIso(entry.clockOut),
            totalHours: entry.totalHours ?? null,
            hours: entry.hours ?? null
          }
          : (pc._auditBefore || null),
        after: {
          notes: _savedTicketNotes, // TASK-10036 — what the merge actually stored
          dumps,
          waterRefills,
          lines: _afterLines,
          // TASK-9196 — the hours this cascade wrote, recorded. `_auditBefore`
          // (built in pubLoadTicketIntoForm) carries no clock fields, so the
          // before-values are pulled straight off the pre-image `entry` here
          // rather than leaving a wage change logged one-sided.
          ...(_hoursTouched ? {
            clockIn: finalClockIn?.toISOString?.() || null,
            clockOut: finalClockOut?.toISOString?.() || null,
            totalHours: _correctedHours,
            hours: _correctedHours
          } : {})
        }
      });
      pc._auditBefore = null;
    } catch (auditErr) { console.warn('[PublicClock] Re-edit audit failed:', auditErr); }

    // Clear edit state and return to timesheet
    pc.editMode = false;
    pc.editTicketId = null;
    pc.editEntryId = null;
    pc.clockOutEntry = null;
    pc.clockOutEquipment = [];
    pc.clockOutDowntime = [];
    pc.clockOutStandby = [];
    pc.clockOutPotholes = [];
    pc.clockOutPhotos = [];
    pc._editedClockIn = null;
    pc._editedClockOut = null;

    showToast('Ticket updated successfully', 'success');

    // Restore mobile nav
    const mobileNav = document.querySelector('.mobile-nav');
    if (mobileNav) mobileNav.classList.remove('hidden');

    // Post TASK-363: only reachable from driver-PIN context. Return to dispatch.
    window.navigateToView?.('dispatch');

  } catch (err) {
    console.error('[PublicClock] Save edit ticket error:', err);
    // TASK-9217 — same three-way as pubSaveEditShift, and same discriminator:
    // the window we were about to write, never the error (the emulator probe
    // shows the 24h denial is byte-identical to every other permission-denied).
    //
    // ⚠️ THIS TOAST IS ATTACHED TO A HALF-APPLIED SAVE and says so. Reaching it
    // for the 24h reason means the pre-check above was bypassed (devtools, or
    // the entry flipping to 'completed' mid-edit), so steps 1-8 have already run:
    // the ticket's non-EMPLOYEE lines were deleted and re-created, the header was
    // stamped ticketEmailDirtyAt, photos were rewritten — and the clock entry's
    // hours were NOT moved, and no audit row was written. The ticket is
    // internally consistent (the header total is server-derived from the lines,
    // TASK-9646 S2); the payroll hours are simply unchanged. Re-opening the
    // editor and saving again with the times left alone reconciles it.
    const _windowDenialC = pubIsLockDenied(err)
      && (pc._editedClockIn || pc._editedClockOut)
      && pubClockWindowIsBounded(pc.clockOutEntry)
      ? pubClockWindowRefusal(
        pc._editedClockIn || pc.clockOutEntry?.clockIn?.toDate?.() || new Date(pc.clockOutEntry?.clockIn),
        pc._editedClockOut || pc.clockOutEntry?.clockOut?.toDate?.() || new Date(pc.clockOutEntry?.clockOut)
      )
      : null;
    showToast(
      _windowDenialC
        ? _windowDenialC + ' Your ticket changes saved but the new times did not — tell dispatch so they can finish it.'
        : (pubIsLockDenied(err) ? PUB_TICKET_LOCKED_MSG : 'Error saving ticket changes'),
      'error'
    );
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:20px;">save</span> Save Changes'; }
  }
};

window.pubCancelEditTicket = function() {
  const pc = window._timesheetCtx;
  pc.editMode = false;
  pc.editTicketId = null;
  pc.editEntryId = null;
  pc.clockOutEntry = null;
  pc.clockOutEquipment = [];
  pc.clockOutDowntime = [];
  pc.clockOutStandby = [];
  pc.clockOutPotholes = [];
  pc.clockOutPhotos = [];
  pc._editedClockIn = null;
  pc._editedClockOut = null;

  // Restore mobile nav
  const mobileNav = document.querySelector('.mobile-nav');
  if (mobileNav) mobileNav.classList.remove('hidden');

  // Post TASK-363: only reachable from driver-PIN context. Return to dispatch.
  window.navigateToView?.('dispatch');
};

// ============================================
// PUBLIC TICKET BUILDER (Forever URL)
// ============================================
async function loadPublicTicketBuilder(token) {
  try {
    window.AppState.isPublicAccess = true;

    try {
      await ensurePublicAuth();
    } catch (authErr) {
      console.error('[PublicTicket] Auth error:', authErr);
      window.AppState.isPublicAccess = false;
      showAccessError('Unable to load ticket builder. Please try again.');
      return false;
    }

    const tokenDoc = await getDoc(doc(db, 'accessTokens', token));
    if (!tokenDoc.exists()) {
      showAccessError('This ticket builder link is invalid or has been revoked.');
      return false;
    }

    const tokenData = tokenDoc.data();
    if (tokenData.type !== 'ticket_builder') {
      showAccessError('This link is not valid for ticket submission.');
      return false;
    }

    const tenantId = tokenData.tenantId;
    window.AppState.tenant = tenantId;

    // Hide chrome
    const header = document.getElementById('app-header');
    const sidebar = document.getElementById('app-sidebar');
    const mobileNav = document.getElementById('mobile-nav');
    if (header) header.style.display = 'none';
    if (sidebar) sidebar.style.display = 'none';
    if (mobileNav) mobileNav.style.display = 'none';
    const mainContent = document.getElementById('main-content');
    mainContent.style.marginLeft = '0';
    mainContent.style.padding = '0';
    mainContent.innerHTML = `<div style="display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:60vh;gap:16px;">
      <div style="width:48px;height:48px;border:4px solid #e2e8f0;border-top-color:var(--brand-blue);border-radius:50%;animation:spin 0.8s linear infinite;"></div>
      <span style="color:var(--text-secondary);font-size:0.9rem;font-weight:500;">Loading ticket builder...</span>
    </div>`;

    // Grant membership for anonymous session
    try {
      const grantMembership = httpsCallable(functions, 'grantAccessTokenMembership');
      await grantMembership({ token });
    } catch (memberErr) {
      console.error('[PublicTicket] Membership grant error:', memberErr);
      window.AppState.isPublicAccess = false;
      showAccessError('Unable to load ticket builder. Please try again.');
      return false;
    }

    // Load tenant data for company name + customers + jobs + people
    // TASK-9856 -- header, pickers and people all come from callables
    // (id + display name only); this page reads no tenant / customer / job doc.
    const [tenantSnap, optsRes, peopleSnap] = await Promise.all([
      _linkTenantDoc(token),
      httpsCallable(functions, 'getTicketBuilderOptions')({ token }),
      // TASK-9856 -- id + name from the server, never whole people docs.
      httpsCallable(functions, 'getLinkPagePeople')({ token })
    ]);

    const tenantData = tenantSnap.exists() ? tenantSnap.data() : {};
    const companyName = tenantData.name || tenantData.companyName || 'Company';
    // P0 2026-10-06 — customers only (QBO vendors share the collection).
    // Already billable + non-internal + unfinished, server-side.
    const customers = (optsRes && optsRes.data && optsRes.data.customers) || [];
    const jobs = (optsRes && optsRes.data && optsRes.data.jobs) || [];
    const people = (peopleSnap && peopleSnap.data && peopleSnap.data.people) || [];

    window.AppState.tenantData = tenantData;
    window.AppState.settings = tenantData.settings || {};

    hideLoading();

    // Render a stripped-down ticket builder
    const escHtml = (s) => s ? String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;') : '';
    const today = new Date().toISOString().slice(0, 10);
    const now = new Date();

    mainContent.innerHTML = `
      <div style="max-width:600px;margin:0 auto;padding:20px;">
        <div style="text-align:center;margin-bottom:24px;">
          <h2 style="margin:0 0 4px;color:var(--text-primary);">${escHtml(companyName)}</h2>
          <p style="margin:0;color:var(--text-secondary);font-size:0.9rem;">Field Ticket Submission</p>
        </div>

        <div style="display:flex;flex-direction:column;gap:16px;">
          <div class="form-group">
            <label for="pub-tb-employee">Employee *</label>
            <select id="pub-tb-employee" class="form-control">
              <option value="">Select employee...</option>
              ${people.map(p => `<option value="${p.id}" data-name="${escHtml(p.name)}">${escHtml(p.name)}</option>`).join('')}
            </select>
          </div>

          <div class="form-group">
            <label for="pub-tb-customer">Customer</label>
            <select id="pub-tb-customer" class="form-control">
              <option value="">Select customer...</option>
              ${/* TASK-9548 — a field ticket is a billing document: decision 2026-10-10, Shop/Office (internal time, never billed) are not offered */ window.PickerGroups.customerOptionsHtml(customers, { nameOf: c => c.name || '', optionAttrs: c => ` data-name="${escHtml(c.name)}"`, excludeInternal: true })}
            </select>
          </div>

          <div class="form-group">
            <label for="pub-tb-job">Job</label>
            <select id="pub-tb-job" class="form-control">
              <option value="">Select job...</option>
              ${jobs.map(j => `<option value="${j.id}" data-name="${escHtml(j.title || j.name || '')}">${escHtml(j.title || j.name || j.id)}</option>`).join('')}
            </select>
          </div>

          <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
            <div class="form-group">
              <label for="pub-tb-date">Work Date *</label>
              <input type="date" id="pub-tb-date" class="form-control" value="${today}">
            </div>
            <div class="form-group">
              <label for="pub-tb-location">Location</label>
              <input type="text" id="pub-tb-location" class="form-control" placeholder="Job site location">
            </div>
          </div>

          <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
            <div class="form-group">
              <label for="pub-tb-clock-in">Clock In</label>
              <input type="time" id="pub-tb-clock-in" class="form-control">
            </div>
            <div class="form-group">
              <label for="pub-tb-clock-out">Clock Out</label>
              <input type="time" id="pub-tb-clock-out" class="form-control">
            </div>
          </div>

          <div class="form-group">
            <label for="pub-tb-po">PO Number</label>
            <input type="text" id="pub-tb-po" class="form-control" placeholder="PO #">
          </div>

          <div class="form-group">
            <label for="pub-tb-shift-notes">Shift Notes *</label>
            <textarea id="pub-tb-shift-notes" class="form-control" rows="3" placeholder="What did you do today?"></textarea>
          </div>

          <div class="form-group">
            <label for="pub-tb-ticket-notes">Ticket Notes</label>
            <textarea id="pub-tb-ticket-notes" class="form-control" rows="2" placeholder="Notes for customer..."></textarea>
          </div>

          <button id="pub-tb-submit" class="btn btn-primary" style="width:100%;padding:12px;" onclick="window._submitPublicTicket()">
            <span class="material-icons" style="font-size:18px;margin-right:4px;">send</span> Submit Ticket
          </button>
        </div>
      </div>
    `;

    // Submit handler
    window._submitPublicTicket = async function() {
      const empSelect = document.getElementById('pub-tb-employee');
      const custSelect = document.getElementById('pub-tb-customer');
      const jobSelect = document.getElementById('pub-tb-job');
      const employeeId = empSelect?.value;
      const employeeName = empSelect?.selectedOptions[0]?.dataset?.name || '';
      const customerId = custSelect?.value || '';
      const customerName = custSelect?.selectedOptions[0]?.dataset?.name || '';
      const jobId = jobSelect?.value || '';
      const jobName = jobSelect?.selectedOptions[0]?.dataset?.name || '';
      const workDate = document.getElementById('pub-tb-date')?.value;
      const location = document.getElementById('pub-tb-location')?.value?.trim() || '';
      const clockIn = document.getElementById('pub-tb-clock-in')?.value || '';
      const clockOut = document.getElementById('pub-tb-clock-out')?.value || '';
      const poNumber = document.getElementById('pub-tb-po')?.value?.trim() || '';
      const shiftNotes = document.getElementById('pub-tb-shift-notes')?.value?.trim() || '';
      const ticketNotes = document.getElementById('pub-tb-ticket-notes')?.value?.trim() || '';

      if (!employeeId) { showToast('Select an employee', 'error'); return; }
      if (!workDate) { showToast('Work date is required', 'error'); return; }
      if (!shiftNotes) { showToast('Shift notes are required', 'error'); return; }

      const btn = document.getElementById('pub-tb-submit');
      btn.disabled = true;
      btn.innerHTML = '<span class="btn-spinner"></span> Submitting...';

      try {
        // TASK-1339 — this public builder was another create path that never
        // assigned a ticket number, so its tickets are invisible to every
        // ticketNo-ordered list. NEVER-TRAP: a public/driver-token session may
        // not be able to read the collection to allocate, and a field worker
        // must still be able to submit — on failure the ticket is created
        // unnumbered but FLAGGED, not silently numberless.
        //
        // TASK-4826 — the DEGRADATION was right; what the human saw was not.
        // This path recorded the failure as a bare `console.warn` and then
        // painted the green check, so the submitter — the one person still
        // standing there who could do something about it — was the only one not
        // told. Every sibling already lands properly (clock.js clock-out logs +
        // toasts + flags; billing.js's call sites fail closed with a toast);
        // this was the outlier. Both failure shapes now leave the same trace
        // clock.js leaves: console.error + window.logError, tagged
        // TICKET_NUMBER_ALLOC so the two paths group in debugLogs/errorLogs.
        //
        // ⚠️ THIS WRITES TWO debugLogs ROWS PER FAILURE, DELIBERATELY. The
        // console.error interceptor (app.js ~L530) matches the `[Module]` prefix
        // and calls logError itself, so the pair yields one generic
        // `{type:'module-error'}` row plus the explicit `TICKET_NUMBER_ALLOC` one
        // that is actually queryable. clock.js's identical pair does the same —
        // measured, not assumed (tests/public-ticket-builder-number-pending.spec.js
        // asserts the exact count). Dropping the console.error to silence the
        // duplicate would diverge from the sibling and cost the console trace;
        // the noise is a property of the interceptor, not of this call site.
        let _pubTicketNo = null;
        let _pubNoPending = false;
        try {
          _pubTicketNo = await window.BillingModule?.getNextTicketNumber?.(tenantId) ?? null;
          if (_pubTicketNo == null) {
            // The QUIETER half, and the reason this is not just a catch-block
            // change: a null return — or a BillingModule that never evaluated —
            // reaches the identical unnumbered write while emitting NOTHING at
            // all, not even the warn below. Same flag, same trace, same screen.
            _pubNoPending = true;
            console.error('[PublicTicket] Ticket number allocation returned no number — creating ticket unnumbered');
            window.logError?.(new Error('Ticket number allocation returned no number'),
              { module: 'publicTicket', operation: 'TICKET_NUMBER_ALLOC', tenant: tenantId });
          }
        } catch (numErr) {
          _pubNoPending = true;
          console.error('[PublicTicket] Ticket number allocation failed — creating ticket unnumbered:', numErr?.message || numErr);
          window.logError?.(numErr, { module: 'publicTicket', operation: 'TICKET_NUMBER_ALLOC', tenant: tenantId });
        }
        await addDoc(collection(db, 'tenants', tenantId, 'tickets'), {
          ...(_pubTicketNo != null ? { ticketNo: _pubTicketNo } : {}),
          ...(_pubNoPending ? { ticketNoPending: true } : {}),
          personId: employeeId,
          personName: employeeName,
          customerId, customerName,
          jobId, jobName,
          workDate, location,
          clockIn: clockIn ? workDate + 'T' + clockIn : '',
          clockOut: clockOut ? workDate + 'T' + clockOut : '',
          poNumber,
          shiftNotes, ticketNotes,
          status: 'pending',
          source: 'public_ticket_builder',
          date: Timestamp.fromDate(new Date(workDate + 'T00:00:00')),
          createdAt: Timestamp.now(),
          lines: []
        });

        // TASK-4826 — the checkmark is only honest when there is a number.
        //
        // WHY THIS IS NOT "Submission failed". The addDoc above already ran: the
        // ticket IS saved, in full, with every field that was typed. And it is
        // recoverable — the dispatcher's ticket list is orderBy('date','desc')
        // (billing.js loadBillingTickets), NOT ticketNo-ordered, so an unnumbered
        // ticket still renders there, carrying the amber "no #" badge.
        // 🔴 That badge keys off a MISSING `ticketNo`, NOT off `ticketNoPending`
        // (renderTicketCard, billing.js ~L7676 — `ticket.ticketNo ? '#…' : 'no #'`).
        // The flag is the queryable marker; the absent number is what renders.
        // "Failed" would be the same lie pointing the other way, and would earn a
        // duplicate ticket from someone who submitted again to be safe — hence
        // the explicit "don't submit it again".
        //
        // WHY "will need to assign one" and NOT "will assign one": nothing
        // schedules or requests it. NOTHING backfills `ticketNoPending` anywhere
        // in the repo (verified 2026-09-08 — no cron, no function, no script; the
        // flag's only consumer is the render-side badge), and nothing notifies
        // dispatch either. Recovery depends on a human noticing the badge, so the
        // copy states the requirement rather than promising an action that has no
        // implementation behind it. A "needs a number" queue is TASK-1354(c).
        //
        // AUDIENCE: this forever link is minted by copyTicketBuilderLink and the
        // form asks the submitter to pick themselves out of the tenant's OWN
        // active people ("What did you do today?") — it is the company's field
        // worker, not a customer. So the copy names dispatch and asks for no
        // action, rather than routing anyone to support.
        mainContent.innerHTML = _pubNoPending
          ? `
          <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:60vh;text-align:center;padding:20px;">
            <span class="material-icons" style="font-size:64px;color:var(--status-warning);margin-bottom:16px;">schedule</span>
            <h2 style="margin:0 0 8px;color:var(--text-primary);">Ticket Saved — Number Pending</h2>
            <p style="margin:0 0 24px;color:var(--text-secondary);max-width:400px;">Your ticket was saved with everything you entered. It didn't get a ticket number — dispatch will need to assign one. Don't submit it again; that would create a duplicate.</p>
            <button class="btn btn-primary" onclick="location.reload()">
              <span class="material-icons">add</span> Submit Another
            </button>
          </div>
        `
          : `
          <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:60vh;text-align:center;padding:20px;">
            <span class="material-icons" style="font-size:64px;color:var(--status-success);margin-bottom:16px;">check_circle</span>
            <h2 style="margin:0 0 8px;color:var(--text-primary);">Ticket Submitted</h2>
            <p style="margin:0 0 24px;color:var(--text-secondary);max-width:400px;">Your field ticket has been submitted successfully.</p>
            <button class="btn btn-primary" onclick="location.reload()">
              <span class="material-icons">add</span> Submit Another
            </button>
          </div>
        `;
      } catch (err) {
        console.error('[PublicTicket] Submit error:', err);
        showToast('Error submitting ticket', 'error');
        btn.disabled = false;
        btn.innerHTML = '<span class="material-icons" style="font-size:18px;margin-right:4px;">send</span> Submit Ticket';
      }
    };

    return true;
  } catch (err) {
    console.error('[PublicTicket] Error loading:', err);
    window.AppState.isPublicAccess = false;
    showAccessError('Unable to load ticket builder. Please try again.');
    return false;
  }
}

// ============================================
// OSHA 1910.1020 RECORDS ACCESS LOG — modal
// Surfaced from Settings → Compliance when trackOshaRecords is on.
// Lists requests, supports new request + mark-fulfilled + mark-denied
// + soft-void. Read/write rules: tenant admins + compliance manager.
// ============================================
window.showOshaRecordsLog = async function() {
  const tenant = window.AppState?.tenant;
  if (!tenant) {
    showToast?.('Not logged in', 'error');
    return;
  }
  let modal = document.getElementById('modal-osha-records-log');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'modal-osha-records-log';
    modal.className = 'modal';
    document.body.appendChild(modal);
  }
  modal.innerHTML = `
    <div class="modal-content modal-fullscreen">
      <div class="modal-header">
        <h3>
          <span class="material-icons" style="vertical-align:middle;">folder_shared</span>
          OSHA Records Access Log
        </h3>
        <button class="btn-icon" onclick="document.getElementById('modal-osha-records-log').classList.add('hidden')">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div id="osha-log-body" style="max-height:75vh;overflow-y:auto;padding:var(--spacing-lg);">
        <div style="text-align:center;color:var(--text-secondary);padding:24px;">
          <span class="btn-spinner" style="margin-right:8px;"></span> Loading...
        </div>
      </div>
    </div>
  `;
  modal.classList.remove('hidden');
  await renderOshaLog();
};

async function renderOshaLog() {
  const tenant = window.AppState?.tenant;
  const body = document.getElementById('osha-log-body');
  if (!body || !tenant) return;
  let entries = [];
  try {
    entries = await oshaListAccessLog(tenant);
  } catch (err) {
    body.innerHTML = `<div style="padding:24px;color:var(--status-danger);">Error loading log: ${escapeHtml(err.message || 'Unknown')}</div>`;
    return;
  }

  const recordTypeLabel = { medical: 'Medical', exposure: 'Exposure', analysis: 'Analysis' };
  const sourceLabel = { employee: 'Employee', designated_rep: 'Designated Rep', osha: 'OSHA' };
  const statusBadge = (s, color) => `<span style="font-size:0.7rem;background:${color};color:#fff;padding:2px 8px;border-radius:4px;text-transform:uppercase;">${s}</span>`;

  const rows = entries.map(e => {
    const sla = oshaGetSlaStatus(e);
    const requestedAt = e.requestedAt?.toDate?.() || (e.requestedAt ? new Date(e.requestedAt) : null);
    const fulfilledAt = e.fulfilledAt?.toDate?.() || (e.fulfilledAt ? new Date(e.fulfilledAt) : null);
    const dateStr = requestedAt ? requestedAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
    const slaBadge = e.status === 'pending'
      ? `<span style="font-size:0.7rem;background:${sla.color};color:#fff;padding:2px 8px;border-radius:4px;">${sla.workingDays}/15 working days</span>`
      : '';
    const statBadge = e.status === 'fulfilled' ? statusBadge('Fulfilled', '#10b981')
                    : e.status === 'denied'    ? statusBadge('Denied', '#dc2626')
                    : e.status === 'voided'    ? statusBadge('Void', '#6b7280')
                    : statusBadge('Pending', sla.color);
    return `
      <tr style="${e.status === 'voided' ? 'opacity:0.55;' : ''}">
        <td style="white-space:nowrap;">${escapeHtml(dateStr)}</td>
        <td>${escapeHtml(e.requesterName || '—')}<br><span style="font-size:0.75rem;color:var(--text-secondary);">${escapeHtml(sourceLabel[e.requestSource] || e.requestSource || '')}</span></td>
        <td>${escapeHtml(e.requestedForName || e.requesterName || '—')}</td>
        <td>${escapeHtml(recordTypeLabel[e.recordType] || e.recordType || '—')}</td>
        <td>${statBadge}<br>${slaBadge}</td>
        <td style="font-size:0.8rem;color:var(--text-secondary);max-width:200px;">${escapeHtml(e.notes || '')}</td>
        <td style="white-space:nowrap;text-align:right;">
          ${e.status === 'pending' ? `
            <button class="btn-icon" title="Mark fulfilled" onclick="window.oshaPromptFulfill('${escapeHtml(e.id)}')">
              <span class="material-icons" style="font-size:18px;color:#10b981;">check_circle</span>
            </button>
            <button class="btn-icon" title="Mark denied" onclick="window.oshaPromptDeny('${escapeHtml(e.id)}')">
              <span class="material-icons" style="font-size:18px;color:#dc2626;">block</span>
            </button>
          ` : ''}
          ${e.status !== 'voided' ? `<button class="btn-icon" title="Void entry" onclick="window.oshaPromptVoid('${escapeHtml(e.id)}')">
            <span class="material-icons" style="font-size:18px;color:var(--text-muted);">delete_outline</span>
          </button>` : ''}
        </td>
      </tr>
    `;
  }).join('');

  body.innerHTML = `
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;flex-wrap:wrap;gap:8px;">
      <div style="font-size:0.85rem;color:var(--text-secondary);">
        ${entries.length} entr${entries.length === 1 ? 'y' : 'ies'} · OSHA 29 CFR 1910.1020 requires fulfillment in 15 working days where practicable.
      </div>
      <button class="btn btn-primary btn-sm" onclick="window.oshaShowNewRequestForm()">
        <span class="material-icons" style="font-size:18px;vertical-align:middle;">add</span> New Request
      </button>
    </div>
    ${entries.length === 0 ? `
      <div style="padding:32px;text-align:center;color:var(--text-secondary);background:var(--bg-secondary);border-radius:8px;">
        No access requests logged yet. Tap "New Request" when an employee or OSHA representative asks for medical or exposure records.
      </div>
    ` : `
      <div style="overflow-x:auto;">
        <table class="billing-lines-table" style="width:100%;">
          <thead>
            <tr><th>Date</th><th>Requester</th><th>For</th><th>Type</th><th>Status / SLA</th><th>Notes</th><th></th></tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
    `}
  `;
}

window.oshaShowNewRequestForm = function() {
  const body = document.getElementById('osha-log-body');
  if (!body) return;
  const peopleList = (window.AppState?.people || []).slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  const peopleOptions = peopleList.map(p => `<option value="${escapeHtml(p.id)}">${escapeHtml(p.name || p.id)}</option>`).join('');
  body.innerHTML = `
    <div style="max-width:560px;margin:0 auto;">
      <h4 style="margin-bottom:12px;">Log a New Records Access Request</h4>
      <p style="font-size:0.85rem;color:var(--text-secondary);margin-bottom:16px;">
        Use this when an employee, their designated representative, or OSHA requests access to medical or exposure records.
      </p>
      <form id="form-osha-new-request" onsubmit="window.oshaSaveNewRequest(event)">
        <div class="form-group" style="margin-bottom:12px;">
          <label>Request Source *</label>
          <select id="osha-req-source" class="form-control" required onchange="document.getElementById('osha-rep-row').style.display = this.value === 'designated_rep' ? '' : 'none';">
            <option value="employee">Employee (themselves)</option>
            <option value="designated_rep">Designated Representative</option>
            <option value="osha">OSHA Inspector</option>
          </select>
        </div>
        <div class="form-group" style="margin-bottom:12px;">
          <label>Requester Name *</label>
          <input type="text" id="osha-req-name" class="form-control" required placeholder="Person making the request">
        </div>
        <div class="form-group" style="margin-bottom:12px;">
          <label>Requester Email</label>
          <input type="email" id="osha-req-email" class="form-control" placeholder="optional">
        </div>
        <div class="form-group" id="osha-rep-row" style="display:none;margin-bottom:12px;">
          <label>
            <input type="checkbox" id="osha-rep-auth" style="margin-right:6px;">
            Written authorization from employee on file
          </label>
        </div>
        <div class="form-group" style="margin-bottom:12px;">
          <label>Records Subject (whose records)</label>
          <select id="osha-req-subject" class="form-control">
            <option value="">— Select person —</option>
            ${peopleOptions}
          </select>
        </div>
        <div class="form-group" style="margin-bottom:12px;">
          <label>Record Type *</label>
          <select id="osha-req-type" class="form-control" required>
            <option value="medical">Medical Record</option>
            <option value="exposure">Exposure Record</option>
            <option value="analysis">Analysis (with personal identifiers)</option>
          </select>
        </div>
        <div class="form-group" style="margin-bottom:12px;">
          <label>Requested Date *</label>
          <input type="date" id="osha-req-date" class="form-control" required value="${new Date().toISOString().slice(0,10)}">
        </div>
        <div class="form-group" style="margin-bottom:16px;">
          <label>Notes</label>
          <textarea id="osha-req-notes" class="form-control" rows="2" placeholder="What was requested, why, anything specific to track"></textarea>
        </div>
        <div style="display:flex;gap:8px;justify-content:flex-end;">
          <button type="button" class="btn btn-secondary" onclick="renderOshaLog()">Cancel</button>
          <button type="submit" class="btn btn-primary">Log Request</button>
        </div>
      </form>
    </div>
  `;
};

window.oshaSaveNewRequest = async function(event) {
  event.preventDefault();
  const tenant = window.AppState?.tenant;
  if (!tenant) return;
  const subjectId = document.getElementById('osha-req-subject').value || null;
  const subjectName = subjectId ? ((window.AppState?.people || []).find(p => p.id === subjectId)?.name || '') : null;
  try {
    await oshaCreateAccessRequest(tenant, {
      requestSource: document.getElementById('osha-req-source').value,
      requesterName: document.getElementById('osha-req-name').value.trim(),
      requesterEmail: document.getElementById('osha-req-email').value.trim() || null,
      designatedRepAuthOnFile: document.getElementById('osha-rep-auth')?.checked === true,
      requestedForPersonId: subjectId,
      requestedForName: subjectName,
      recordType: document.getElementById('osha-req-type').value,
      requestedAt: document.getElementById('osha-req-date').value,
      notes: document.getElementById('osha-req-notes').value.trim()
    });
    showToast?.('Request logged', 'success');
    await renderOshaLog();
  } catch (err) {
    console.error('[OSHA] save failed:', err);
    showToast?.('Error logging request: ' + (err.message || 'Unknown'), 'error');
  }
};

// Action forms render inline in the modal body (replaces window.prompt/confirm
// which is brittle on iOS Safari per UX Lead's COD review). Each form has a
// Cancel button that re-renders the log.
window.oshaPromptFulfill = function(entryId) {
  const body = document.getElementById('osha-log-body');
  if (!body) return;
  body.innerHTML = `
    <div style="max-width:520px;margin:0 auto;">
      <h4 style="margin-bottom:12px;">Mark Fulfilled</h4>
      <p style="font-size:0.85rem;color:var(--text-secondary);margin-bottom:16px;">
        OSHA 1910.1020(e)(1) requires reasonable time, place, and manner of access. Log how you delivered.
      </p>
      <form onsubmit="window.oshaSubmitFulfill(event, '${escapeHtml(entryId)}')">
        <div class="form-group" style="margin-bottom:12px;">
          <label>Delivery Method *</label>
          <select id="osha-fulfill-method" class="form-control" required>
            <option value="copy_email">Copy delivered by email</option>
            <option value="copy_print">Printed copy delivered</option>
            <option value="inspection_in_person">In-person inspection of records</option>
            <option value="mailed">Copy mailed</option>
          </select>
        </div>
        <div class="form-group" style="margin-bottom:12px;">
          <label style="display:flex;align-items:center;gap:8px;cursor:pointer;">
            <input type="checkbox" id="osha-fulfill-scrubbed">
            <span>Personal identifiers were removed from any analyses in this delivery <span style="color:var(--text-muted);font-size:0.8rem;">(per 1910.1020(e)(2)(iii)(C))</span></span>
          </label>
        </div>
        <div class="form-group" style="margin-bottom:16px;">
          <label>Notes (optional)</label>
          <textarea id="osha-fulfill-notes" class="form-control" rows="2" placeholder="What was provided, any caveats"></textarea>
        </div>
        <div style="display:flex;gap:8px;justify-content:flex-end;">
          <button type="button" class="btn btn-secondary" onclick="renderOshaLog()">Cancel</button>
          <button type="submit" class="btn btn-primary">
            <span class="material-icons" style="font-size:18px;vertical-align:middle;">check_circle</span> Mark Fulfilled
          </button>
        </div>
      </form>
    </div>
  `;
};

window.oshaSubmitFulfill = async function(event, entryId) {
  event.preventDefault();
  const tenant = window.AppState?.tenant;
  if (!tenant) return;
  try {
    await oshaMarkFulfilled(tenant, entryId, {
      deliveryMethod: document.getElementById('osha-fulfill-method').value,
      identifiersScrubbed: document.getElementById('osha-fulfill-scrubbed').checked,
      notes: document.getElementById('osha-fulfill-notes').value.trim() || undefined
    });
    showToast?.('Marked fulfilled', 'success');
    await renderOshaLog();
  } catch (err) {
    console.error('[OSHA] fulfill failed:', err);
    showToast?.('Error: ' + (err.message || 'Unknown'), 'error');
  }
};

window.oshaPromptDeny = function(entryId) {
  const body = document.getElementById('osha-log-body');
  if (!body) return;
  body.innerHTML = `
    <div style="max-width:520px;margin:0 auto;">
      <h4 style="margin-bottom:12px;color:var(--status-danger);">Deny Records Access Request</h4>
      <p style="font-size:0.85rem;color:var(--text-secondary);margin-bottom:16px;">
        Denials are rare and must be justified under 1910.1020. Document your reason — OSHA may review it on inspection.
      </p>
      <form onsubmit="window.oshaSubmitDeny(event, '${escapeHtml(entryId)}')">
        <div class="form-group" style="margin-bottom:16px;">
          <label>Reason for denial *</label>
          <textarea id="osha-deny-reason" class="form-control" rows="3" required placeholder="e.g. Designated representative authorization not provided"></textarea>
        </div>
        <div style="display:flex;gap:8px;justify-content:flex-end;">
          <button type="button" class="btn btn-secondary" onclick="renderOshaLog()">Cancel</button>
          <button type="submit" class="btn btn-primary" style="background:var(--status-danger);border-color:var(--status-danger);">
            <span class="material-icons" style="font-size:18px;vertical-align:middle;">block</span> Mark Denied
          </button>
        </div>
      </form>
    </div>
  `;
};

window.oshaSubmitDeny = async function(event, entryId) {
  event.preventDefault();
  const tenant = window.AppState?.tenant;
  if (!tenant) return;
  const reason = document.getElementById('osha-deny-reason').value.trim();
  if (!reason) return;
  try {
    await oshaMarkDenied(tenant, entryId, reason);
    showToast?.('Marked denied', 'success');
    await renderOshaLog();
  } catch (err) {
    console.error('[OSHA] deny failed:', err);
    showToast?.('Error: ' + (err.message || 'Unknown'), 'error');
  }
};

window.oshaPromptVoid = function(entryId) {
  const body = document.getElementById('osha-log-body');
  if (!body) return;
  body.innerHTML = `
    <div style="max-width:520px;margin:0 auto;">
      <h4 style="margin-bottom:12px;">Void Entry</h4>
      <p style="font-size:0.85rem;color:var(--text-secondary);margin-bottom:16px;">
        Voiding marks the entry as void but preserves it in the audit trail. Use for duplicates, mistakes, or test entries.
      </p>
      <form onsubmit="window.oshaSubmitVoid(event, '${escapeHtml(entryId)}')">
        <div class="form-group" style="margin-bottom:16px;">
          <label>Reason for voiding (optional)</label>
          <textarea id="osha-void-reason" class="form-control" rows="2" placeholder="e.g. Logged in error / duplicate of #..."></textarea>
        </div>
        <div style="display:flex;gap:8px;justify-content:flex-end;">
          <button type="button" class="btn btn-secondary" onclick="renderOshaLog()">Cancel</button>
          <button type="submit" class="btn btn-primary">
            <span class="material-icons" style="font-size:18px;vertical-align:middle;">delete_outline</span> Void
          </button>
        </div>
      </form>
    </div>
  `;
};

window.oshaSubmitVoid = async function(event, entryId) {
  event.preventDefault();
  const tenant = window.AppState?.tenant;
  if (!tenant) return;
  try {
    await oshaVoidEntry(tenant, entryId, document.getElementById('osha-void-reason').value.trim() || null);
    showToast?.('Entry voided', 'success');
    await renderOshaLog();
  } catch (err) {
    console.error('[OSHA] void failed:', err);
    showToast?.('Error: ' + (err.message || 'Unknown'), 'error');
  }
};

// Expose renderOshaLog globally for cancel-form button
window.renderOshaLog = renderOshaLog;

// ============================================
// OSHA 1910.1020(g) — Annual Right-of-Access Notification
// Surfaces a list of people who have never been notified, or whose last
// notification is overdue (>12 mo) / due soon (>11 mo). Admin can bulk-stamp
// acknowledgments after a safety meeting, training session, etc.
// ============================================
window.openOshaNotificationModal = function() {
  const tenant = window.AppState?.tenant;
  if (!tenant) { showToast?.('Not logged in', 'error'); return; }
  const helpers = window.OshaRecords;
  if (!helpers?.listPeopleNeedingNotification) {
    showToast?.('OSHA helpers not loaded', 'error');
    return;
  }
  // Pull from HRState via HRModule. AppState.people is never populated;
  // HRState.people is the authoritative onSnapshot source. (Caught by Code
  // Reviewer in COD review 2026-05-11T17-59.)
  const people = window.HRModule?.getOshaPeopleForNotification?.() || [];
  const due = helpers.listPeopleNeedingNotification(people);

  let modal = document.getElementById('modal-osha-notification');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'modal-osha-notification';
    modal.className = 'modal';
    document.body.appendChild(modal);
  }

  const todayIso = new Date().toISOString().slice(0, 10);
  const ageLabel = (d) => {
    if (d.status === 'never') return 'Never notified';
    if (d.ageDays == null) return '—';
    const months = Math.round(d.ageDays / 30);
    return `${months} mo ago`;
  };
  const statusPill = (s) => {
    if (s === 'overdue') return '<span style="background:#dc2626;color:#fff;font-size:0.7rem;padding:2px 8px;border-radius:4px;text-transform:uppercase;">Overdue</span>';
    if (s === 'never') return '<span style="background:#f59e0b;color:#fff;font-size:0.7rem;padding:2px 8px;border-radius:4px;text-transform:uppercase;">Never</span>';
    if (s === 'due_soon') return '<span style="background:#facc15;color:#1f2937;font-size:0.7rem;padding:2px 8px;border-radius:4px;text-transform:uppercase;">Due soon</span>';
    return '';
  };

  const dueRows = due.map(d => `
    <tr>
      <td style="padding:8px;border-bottom:1px solid var(--border-light);">
        <input type="checkbox" class="osha-notif-cb" data-person-id="${escapeHtml(d.person.id)}">
      </td>
      <td style="padding:8px;border-bottom:1px solid var(--border-light);">${escapeHtml(d.person.name || d.person.id)}</td>
      <td style="padding:8px;border-bottom:1px solid var(--border-light);">${statusPill(d.status)}</td>
      <td style="padding:8px;border-bottom:1px solid var(--border-light);font-size:0.85rem;color:var(--text-secondary);">${escapeHtml(ageLabel(d))}</td>
    </tr>
  `).join('');

  const emptyMsg = due.length === 0 ? `
    <div style="text-align:center;padding:32px 16px;color:var(--text-secondary);">
      <span class="material-icons" style="font-size:48px;color:#10b981;">check_circle</span>
      <div style="font-weight:600;margin-top:8px;color:var(--text-primary);">All active employees current</div>
      <div style="font-size:0.85rem;margin-top:4px;">Annual records-access notification is logged for everyone. Next reminders surface 11 months from each person's last acknowledgment.</div>
    </div>
  ` : '';

  modal.innerHTML = `
    <div class="modal-content" style="max-width:640px;">
      <div class="modal-header" style="background:linear-gradient(135deg,#f59e0b,#d97706);color:#fff;">
        <h3 style="color:#fff;">
          <span class="material-icons" style="vertical-align:middle;">campaign</span>
          Annual Records-Access Notification
        </h3>
        <button class="btn-icon" onclick="document.getElementById('modal-osha-notification').classList.add('hidden')">
          <span class="material-icons" style="color:#fff;">close</span>
        </button>
      </div>
      <div class="modal-body" style="padding:var(--spacing-lg);">
        <div style="background:#fffbeb;border:1px solid #fde68a;border-radius:8px;padding:12px 14px;margin-bottom:16px;color:#78350f;font-size:0.85rem;line-height:1.5;">
          <strong>OSHA 1910.1020(g)(1)(ii–iv)</strong> requires that each employee be informed — at initial assignment and at least annually — of the existence/location/availability of their medical and exposure records, the records custodian's identity, and their right of access. Document each round of notifications below. Use this with a safety meeting, all-hands, or one-on-one onboarding.
        </div>
        ${emptyMsg}
        ${due.length > 0 ? `
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;flex-wrap:wrap;gap:8px;">
          <div style="font-size:0.85rem;color:var(--text-secondary);">${due.length} ${due.length === 1 ? 'person needs' : 'people need'} notification</div>
          <div>
            <button type="button" class="btn btn-sm btn-secondary" onclick="window._oshaNotifSelectAll(true)">Select all</button>
            <button type="button" class="btn btn-sm btn-secondary" onclick="window._oshaNotifSelectAll(false)">Clear</button>
          </div>
        </div>
        <div style="max-height:50vh;overflow-y:auto;border:1px solid var(--border-light);border-radius:8px;">
          <table style="width:100%;border-collapse:collapse;">
            <thead style="background:var(--bg-secondary);position:sticky;top:0;">
              <tr>
                <th style="padding:8px;text-align:left;font-size:0.75rem;text-transform:uppercase;color:var(--text-secondary);width:40px;">✓</th>
                <th style="padding:8px;text-align:left;font-size:0.75rem;text-transform:uppercase;color:var(--text-secondary);">Person</th>
                <th style="padding:8px;text-align:left;font-size:0.75rem;text-transform:uppercase;color:var(--text-secondary);">Status</th>
                <th style="padding:8px;text-align:left;font-size:0.75rem;text-transform:uppercase;color:var(--text-secondary);">Last notified</th>
              </tr>
            </thead>
            <tbody id="osha-notif-tbody">${dueRows}</tbody>
          </table>
        </div>
        <div class="form-group" style="margin-top:16px;">
          <label for="osha-notif-date">Notification date</label>
          <input type="date" id="osha-notif-date" class="form-control" value="${todayIso}" max="${todayIso}">
        </div>
        <div class="form-group">
          <label for="osha-notif-note">Note (optional, e.g. "covered in 2026-05-11 toolbox talk")</label>
          <input type="text" id="osha-notif-note" class="form-control" maxlength="200" placeholder="">
        </div>
        <div id="osha-notif-result" style="margin-top:12px;"></div>
        <div class="modal-footer" style="margin-top:16px;display:flex;justify-content:flex-end;gap:8px;">
          <button type="button" class="btn btn-secondary" onclick="document.getElementById('modal-osha-notification').classList.add('hidden')">Cancel</button>
          <button type="button" id="osha-notif-stamp" class="btn btn-primary" onclick="window.submitOshaNotifications()">Stamp selected as notified</button>
        </div>
        ` : `
        <div style="display:flex;justify-content:flex-end;margin-top:16px;">
          <button type="button" class="btn btn-primary" onclick="document.getElementById('modal-osha-notification').classList.add('hidden')">Close</button>
        </div>
        `}
      </div>
    </div>
  `;
  modal.classList.remove('hidden');
};

window._oshaNotifSelectAll = function(checked) {
  document.querySelectorAll('.osha-notif-cb').forEach(cb => { cb.checked = checked; });
};

window.submitOshaNotifications = async function() {
  const tenant = window.AppState?.tenant;
  if (!tenant) { showToast?.('Not logged in', 'error'); return; }
  const helpers = window.OshaRecords;
  const btn = document.getElementById('osha-notif-stamp');
  const resultEl = document.getElementById('osha-notif-result');
  if (!btn || !helpers?.stampRecordsAccessNotification) return;

  const cbs = Array.from(document.querySelectorAll('.osha-notif-cb')).filter(c => c.checked);
  if (cbs.length === 0) {
    if (resultEl) resultEl.innerHTML = `<div style="color:var(--status-danger);font-size:0.85rem;">Select at least one person to stamp.</div>`;
    return;
  }
  const notifiedAt = document.getElementById('osha-notif-date')?.value || new Date().toISOString().slice(0, 10);
  const note = document.getElementById('osha-notif-note')?.value?.trim() || null;
  const notifiedBy = window.AppState?.user?.email || window.AppState?.user?.uid || null;

  if (btn.disabled) return;
  btn.disabled = true;
  const orig = btn.textContent;
  btn.innerHTML = '<span class="btn-spinner" style="margin-right:8px;"></span> Stamping…';

  const personIds = cbs.map(c => c.dataset.personId).filter(Boolean);
  let success = 0;
  const errors = [];
  for (const pid of personIds) {
    try {
      await helpers.stampRecordsAccessNotification(tenant, pid, { notifiedAt, notifiedBy, note });
      success++;
    } catch (err) {
      console.error('[OSHA notif] stamp failed for', pid, err);
      errors.push(pid);
    }
  }

  if (errors.length === 0) {
    showToast?.(`Notified ${success} ${success === 1 ? 'person' : 'people'}`, 'success');
    document.getElementById('modal-osha-notification').classList.add('hidden');
    // Refresh the HR card subtitle so the "N due" count reflects the new state.
    // onSnapshot will also fire and trigger the same refresh, but this gives
    // immediate feedback while the snapshot round-trips.
    try { window.HRModule?.updateOshaNotificationBadge?.(); } catch (_) {}
  } else {
    if (resultEl) {
      resultEl.innerHTML = `<div style="color:var(--status-danger);font-size:0.85rem;">Stamped ${success}/${personIds.length}. ${errors.length} failed — see console.</div>`;
    }
    btn.disabled = false;
    btn.innerHTML = orig || 'Stamp selected as notified';
  }
};

// ============================================
// OSHA 1910.1020(h) — Business Cessation Manifest
// Generates a CSV index of all medical + exposure records across the tenant
// when the business ceases operations or sells. Server-side function does
// the heavy lifting (locked storage path + audit log); this is just the UI.
// ============================================
window.openOshaCessationModal = function() {
  const tenant = window.AppState?.tenant;
  if (!tenant) { showToast?.('Not logged in', 'error'); return; }
  let modal = document.getElementById('modal-osha-cessation');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'modal-osha-cessation';
    modal.className = 'modal';
    document.body.appendChild(modal);
  }
  const todayIso = new Date().toISOString().slice(0, 10);
  modal.innerHTML = `
    <div class="modal-content" style="max-width:560px;">
      <div class="modal-header" style="background:linear-gradient(135deg,#b91c1c,#7f1d1d);color:#fff;">
        <h3 style="color:#fff;">
          <span class="material-icons" style="vertical-align:middle;">warning</span>
          OSHA 1910.1020(h) — Business Cessation Export
        </h3>
        <button class="btn-icon" onclick="document.getElementById('modal-osha-cessation').classList.add('hidden')">
          <span class="material-icons" style="color:#fff;">close</span>
        </button>
      </div>
      <div class="modal-body" style="padding:var(--spacing-lg);">
        <div style="background:#fef2f2;border:1px solid #fecaca;border-radius:8px;padding:12px 14px;margin-bottom:16px;color:#7f1d1d;font-size:0.9rem;line-height:1.5;">
          <strong>Run only when ceasing business operations or selling.</strong>
          This generates a <strong>spreadsheet index</strong> (CSV) of every medical and exposure record in your tenant — file names, categories, upload dates, storage paths. You or the successor employer still need to manually retrieve the actual files from Storage. 29 CFR 1910.1020(h) also requires that you notify NIOSH and each affected employee at least 3 months before cessation — that is <strong>your manual responsibility</strong>; DispatchDeck does not auto-notify.
        </div>
        <form id="osha-cessation-form" onsubmit="window.submitOshaCessation(event)">
          <div class="form-group">
            <label for="osha-cessation-date">Cessation date <span style="color:var(--status-danger);">*</span></label>
            <input type="date" id="osha-cessation-date" class="form-control" value="${todayIso}" required>
          </div>
          <div class="form-group">
            <label for="osha-successor-name">Successor employer (optional)</label>
            <input type="text" id="osha-successor-name" class="form-control" maxlength="200" placeholder="e.g. Acme Trucking LLC">
          </div>
          <div class="form-group">
            <label for="osha-successor-email">Successor contact email (optional)</label>
            <input type="email" id="osha-successor-email" class="form-control" placeholder="records@successor.example">
          </div>
          <div class="form-group" style="display:flex;align-items:flex-start;gap:8px;margin-top:12px;">
            <input type="checkbox" id="osha-cessation-confirm" required style="margin-top:3px;">
            <label for="osha-cessation-confirm" style="font-size:0.85rem;line-height:1.4;cursor:pointer;">
              I confirm we are ceasing operations or selling and need to discharge OSHA 1910.1020(h) records-transfer obligations. I understand DispatchDeck will not notify employees or NIOSH on my behalf.
            </label>
          </div>
          <div id="osha-cessation-result" style="margin-top:16px;"></div>
          <div class="modal-footer" style="margin-top:16px;display:flex;justify-content:flex-end;gap:8px;">
            <button type="button" class="btn btn-secondary" onclick="document.getElementById('modal-osha-cessation').classList.add('hidden')">Cancel</button>
            <button type="submit" id="osha-cessation-submit" class="btn btn-primary" style="background:#b91c1c;border-color:#b91c1c;">
              Generate Manifest
            </button>
          </div>
        </form>
      </div>
    </div>
  `;
  modal.classList.remove('hidden');
};

window.submitOshaCessation = async function(event) {
  event.preventDefault();
  const tenant = window.AppState?.tenant;
  if (!tenant) { showToast?.('Not logged in', 'error'); return; }
  const btn = document.getElementById('osha-cessation-submit');
  const resultEl = document.getElementById('osha-cessation-result');
  if (!btn || !resultEl) return;
  // Debounce — disable button immediately so double-click cannot fire twice.
  if (btn.disabled) return;
  btn.disabled = true;
  const origLabel = btn.textContent;
  btn.innerHTML = '<span class="btn-spinner" style="margin-right:8px;"></span> Generating…';
  resultEl.innerHTML = '';

  const cessationDate = document.getElementById('osha-cessation-date')?.value;
  const successorName = document.getElementById('osha-successor-name')?.value?.trim() || '';
  const successorEmail = document.getElementById('osha-successor-email')?.value?.trim() || '';
  const confirmCessation = document.getElementById('osha-cessation-confirm')?.checked === true;

  try {
    const call = httpsCallable(functions, 'generateOshaCessationManifest');
    const res = await call({
      tenantId: tenant,
      cessationDate,
      successorName: successorName || undefined,
      successorEmail: successorEmail || undefined,
      confirmCessation,
    });
    const data = res?.data || {};
    if (!data.ok || !data.signedUrl) {
      throw new Error('Function returned no signed URL');
    }
    const sum = data.summary || {};
    const byCat = sum.byCategory || {};
    resultEl.innerHTML = `
      <div style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:8px;padding:12px 14px;color:#14532d;">
        <div style="font-weight:600;margin-bottom:6px;">
          <span class="material-icons" style="vertical-align:middle;font-size:18px;">check_circle</span>
          Manifest generated
        </div>
        <div style="font-size:0.85rem;line-height:1.5;">
          ${sum.peopleCount || 0} people scanned · ${sum.docCount || 0} protected records
          ${byCat.medical ? ` (${byCat.medical} medical` : ''}${byCat.exposure ? `${byCat.medical ? ', ' : ' ('}${byCat.exposure} exposure` : ''}${(byCat.medical || byCat.exposure) ? ')' : ''}
        </div>
        <a href="${data.signedUrl}" target="_blank" rel="noopener" class="btn btn-primary btn-sm" style="margin-top:10px;display:inline-flex;align-items:center;gap:6px;background:#16a34a;border-color:#16a34a;">
          <span class="material-icons" style="font-size:16px;">download</span>
          Download CSV manifest
        </a>
        <div style="font-size:0.75rem;color:#16a34a;margin-top:8px;">
          Link valid for 7 days · permanent record in audit log at <code>tenants/${tenant}/auditLog</code>
        </div>
      </div>
    `;
    btn.style.display = 'none';
  } catch (err) {
    console.error('[OSHA cessation] failed:', err);
    resultEl.innerHTML = `
      <div style="background:#fef2f2;border:1px solid #fecaca;border-radius:8px;padding:12px 14px;color:#7f1d1d;font-size:0.85rem;">
        <strong>Error:</strong> ${escapeHtml(err.message || 'Unknown error')}
      </div>
    `;
    btn.disabled = false;
    btn.innerHTML = origLabel || 'Generate Manifest';
  }
};

// ============================================
// OSHA 1910.1020(e)(2)(iii)(C) — Anonymized Analysis Export (TASK-211 #6)
// Releases an anonymized CSV of medical/exposure document inventory to a
// designated representative (union rep, attorney, OSHA inspector, NIOSH)
// with PII removed per the reg. Backend at functions/src/services/
// oshaAnalysisExport.js does server-side pseudonymization + bucketing.
// ============================================
window.openOshaAnonymizedAnalysisModal = function() {
  const tenant = window.AppState?.tenant;
  if (!tenant) { showToast?.('Not logged in', 'error'); return; }
  let modal = document.getElementById('modal-osha-anonymized');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'modal-osha-anonymized';
    modal.className = 'modal';
    document.body.appendChild(modal);
  }
  modal.innerHTML = `
    <div class="modal-content" style="max-width:600px;">
      <div class="modal-header" style="background:linear-gradient(135deg,#6366f1,#4338ca);color:#fff;">
        <h3 style="color:#fff;">
          <span class="material-icons" style="vertical-align:middle;">analytics</span>
          Anonymized Analysis Export
        </h3>
        <button class="btn-icon" onclick="document.getElementById('modal-osha-anonymized').classList.add('hidden')">
          <span class="material-icons" style="color:#fff;">close</span>
        </button>
      </div>
      <div class="modal-body" style="padding:var(--spacing-lg);">
        <div style="background:#eef2ff;border:1px solid #c7d2fe;border-radius:8px;padding:12px 14px;margin-bottom:16px;color:#3730a3;font-size:0.9rem;line-height:1.5;">
          <strong>29 CFR 1910.1020(e)(2)(iii)(C)</strong> — when a designated representative requests access to <em>analyses</em> derived from medical or exposure records, you may delete personal identifiers before providing access. This generates a CSV with <strong>no names, no SSNs, no dates of birth, no addresses, no upload timestamps</strong> — only stable pseudo-IDs and bucketed metadata. An entry is automatically opened in your Records Access Log as <em>pending</em>; mark it <em>fulfilled</em> after you deliver the file.
        </div>
        <form id="osha-anonymized-form" onsubmit="window.submitOshaAnonymizedAnalysis(event)">
          <div class="form-group">
            <label for="osha-anon-requester-name">Requester name <span style="color:var(--status-danger);">*</span></label>
            <input type="text" id="osha-anon-requester-name" class="form-control" required maxlength="200" placeholder="e.g. Jane Smith" style="font-size:16px;">
          </div>
          <div class="form-group">
            <label for="osha-anon-requester-org">Requester organization <span style="color:var(--status-danger);">*</span></label>
            <input type="text" id="osha-anon-requester-org" class="form-control" required maxlength="200" placeholder="e.g. Teamsters Local 100" style="font-size:16px;">
          </div>
          <div class="form-group">
            <label for="osha-anon-basis">Basis for request <span style="color:var(--status-danger);">*</span></label>
            <select id="osha-anon-basis" class="form-control" required onchange="window._oshaAnonBasisChange(this.value)" style="font-size:16px;">
              <option value="" disabled selected>Select…</option>
              <option value="employee_request">Employee request (rep acting for one or more employees)</option>
              <option value="osha_inspector">OSHA inspector</option>
              <option value="niosh">NIOSH</option>
              <option value="union_representative">Union representative</option>
              <option value="attorney">Attorney</option>
              <option value="other">Other (explain)</option>
            </select>
          </div>
          <div class="form-group" id="osha-anon-basis-detail-group" style="display:none;">
            <label for="osha-anon-basis-detail">Explanation <span style="color:var(--status-danger);">*</span></label>
            <input type="text" id="osha-anon-basis-detail" class="form-control" maxlength="500" placeholder="Briefly describe the basis…" style="font-size:16px;">
          </div>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
            <div class="form-group">
              <label for="osha-anon-period-start">Period start (optional)</label>
              <input type="date" id="osha-anon-period-start" class="form-control" style="font-size:16px;">
            </div>
            <div class="form-group">
              <label for="osha-anon-period-end">Period end (optional)</label>
              <input type="date" id="osha-anon-period-end" class="form-control" style="font-size:16px;">
            </div>
          </div>
          <div class="form-group" style="display:flex;align-items:flex-start;gap:8px;margin-top:12px;">
            <input type="checkbox" id="osha-anon-confirm" required style="margin-top:3px;">
            <label for="osha-anon-confirm" style="font-size:0.85rem;line-height:1.4;cursor:pointer;">
              I confirm this request was received from a designated representative and personal identifiers are being removed per 29 CFR 1910.1020(e)(2)(iii)(C).
            </label>
          </div>
          <div id="osha-anon-result" style="margin-top:16px;"></div>
          <div class="modal-footer" style="margin-top:16px;display:flex;justify-content:flex-end;gap:8px;">
            <button type="button" class="btn btn-secondary" onclick="document.getElementById('modal-osha-anonymized').classList.add('hidden')">Cancel</button>
            <button type="submit" id="osha-anon-submit" class="btn btn-primary" style="background:#4338ca;border-color:#4338ca;">
              Generate Anonymized Export
            </button>
          </div>
        </form>
      </div>
    </div>
  `;
  modal.classList.remove('hidden');
};

window._oshaAnonBasisChange = function(value) {
  const grp = document.getElementById('osha-anon-basis-detail-group');
  const inp = document.getElementById('osha-anon-basis-detail');
  if (!grp || !inp) return;
  if (value === 'other') {
    grp.style.display = '';
    inp.required = true;
  } else {
    grp.style.display = 'none';
    inp.required = false;
    inp.value = '';
  }
};

window.submitOshaAnonymizedAnalysis = async function(event) {
  event.preventDefault();
  const tenant = window.AppState?.tenant;
  if (!tenant) { showToast?.('Not logged in', 'error'); return; }
  const btn = document.getElementById('osha-anon-submit');
  const resultEl = document.getElementById('osha-anon-result');
  if (!btn || !resultEl) return;
  if (btn.disabled) return;
  btn.disabled = true;
  const origLabel = btn.textContent;
  btn.innerHTML = '<span class="btn-spinner" style="margin-right:8px;"></span> Generating…';
  resultEl.innerHTML = '';

  const requesterName = document.getElementById('osha-anon-requester-name')?.value?.trim() || '';
  const requesterOrg = document.getElementById('osha-anon-requester-org')?.value?.trim() || '';
  const basis = document.getElementById('osha-anon-basis')?.value || '';
  const basisDetail = document.getElementById('osha-anon-basis-detail')?.value?.trim() || '';
  const periodStart = document.getElementById('osha-anon-period-start')?.value || '';
  const periodEnd = document.getElementById('osha-anon-period-end')?.value || '';
  const confirmAnonymized = document.getElementById('osha-anon-confirm')?.checked === true;

  try {
    const call = httpsCallable(functions, 'generateOshaAnonymizedAnalysis');
    const res = await call({
      tenantId: tenant,
      requesterName,
      requesterOrg,
      basis,
      basisDetail: basisDetail || undefined,
      periodStart: periodStart || undefined,
      periodEnd: periodEnd || undefined,
      confirmAnonymized,
    });
    const data = res?.data || {};
    if (!data.ok || !data.signedUrl) {
      throw new Error('Function returned no signed URL');
    }
    // Auto-open SLA loop in the records-access log (status: pending — admin
    // marks fulfilled after actually delivering the file to the rep).
    try {
      if (window.OshaRecords?.logAnonymizedAnalysisRequest) {
        await window.OshaRecords.logAnonymizedAnalysisRequest(tenant, {
          requesterName,
          requesterOrg,
          basis,
          storagePath: data.storagePath,
          notes: `Basis: ${basis}${basisDetail ? ' — ' + basisDetail : ''}${periodStart ? ' · Period ' + periodStart + '→' + (periodEnd || '?') : ''}`,
        });
      }
    } catch (logErr) {
      console.warn('[OSHA anonymized] log entry failed (export succeeded):', logErr);
    }

    resultEl.innerHTML = `
      <div style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:8px;padding:12px 14px;color:#14532d;">
        <div style="font-weight:600;margin-bottom:6px;">
          <span class="material-icons" style="vertical-align:middle;font-size:18px;">check_circle</span>
          Anonymized export ready
        </div>
        <div style="font-size:0.85rem;line-height:1.5;">
          ${data.rowCount || 0} rows · ${data.uniquePersons || 0} unique pseudo-IDs · ${(data.summary?.byCategory?.medical || 0)} medical, ${(data.summary?.byCategory?.exposure || 0)} exposure
        </div>
        <a href="${data.signedUrl}" target="_blank" rel="noopener" class="btn btn-primary btn-sm" style="margin-top:10px;display:inline-flex;align-items:center;gap:6px;background:#16a34a;border-color:#16a34a;">
          <span class="material-icons" style="font-size:16px;">download</span>
          Download anonymized CSV
        </a>
        <div style="font-size:0.75rem;color:#16a34a;margin-top:8px;">
          Link valid for 7 days · pending entry added to Records Access Log · audit trail at <code>tenants/${tenant}/auditLog</code>
        </div>
      </div>
    `;
    btn.style.display = 'none';
  } catch (err) {
    console.error('[OSHA anonymized] failed:', err);
    resultEl.innerHTML = `
      <div style="background:#fef2f2;border:1px solid #fecaca;border-radius:8px;padding:12px 14px;color:#7f1d1d;font-size:0.85rem;">
        <strong>Error:</strong> ${escapeHtml(err.message || 'Unknown error')}
      </div>
    `;
    btn.disabled = false;
    btn.innerHTML = origLabel || 'Generate Anonymized Export';
  }
};

// ============================================
// CARRIER COMPLIANCE BILLING (Commit 3/3, 2026-05-11)
// Carrier-side card-on-file flow. Companion to the partner-side Billing
// view in compliance-manager.html. Tenant admin attaches a payment method
// via Stripe Checkout so the carrier can pay for their own Checkr/MVR
// pulls when partner-pays mode isn't active for them. Pre-flight gate
// in _runBackgroundCheckCore stays OFF until coverage is high enough.
// ============================================

const _carrierBillingState = { tenantId: null, data: null, loading: false };

// TASK-1361 B — an error path must never assert card state. Key off the real error
// code so a 403 and a Stripe/network outage read differently (COD AuthZ mitigation).
// Callable errors arrive as 'functions/permission-denied' on the web SDK.
function _carrierBillingErrorText(err) {
  const code = String((err && err.code) || '').replace(/^functions\//, '');
  if (code === 'permission-denied') return 'Billing access required';
  return "Couldn't load billing status";
}

// TASK-9265 s3 — "Background checks this period" card for the Compliance Billing
// modal. `lim` = getCarrierComplianceBillingState.screeningLimit, `pend` =
// .pendingCharges (null unless the caller manages the card). Every value is a
// server number, coerced before it touches markup.
function _carrierScreeningLimitHtml(lim, pend) {
  if (!lim || !Number.isFinite(Number(lim.cap))) return '';
  const cap = Math.max(0, Math.floor(Number(lim.cap)));
  const used = Math.max(0, Math.floor(Number(lim.used) || 0));
  const blocked = Math.max(0, Math.floor(Number(lim.blockedThisWindow) || 0));
  const blockedApplicants = Math.min(blocked, Math.max(0, Math.floor(Number(lim.blockedApplicantsThisWindow) || 0)));
  const mail = '<a href="mailto:support@dispatchdeck.app?subject=Raise%20our%20background-check%20limit">support@dispatchdeck.app</a>';
  const days = Math.floor(Number(lim.windowDays) || 30);
  let resets = '';
  if (lim.resetsAt) {
    const d = new Date(lim.resetsAt);
    if (!isNaN(d.getTime())) resets = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  const atCap = used >= cap;
  const near = !atCap && cap > 0 && used >= Math.ceil(cap * 0.8);
  const tone = atCap ? { bg: '#FEF2F2', bd: '#FECACA', fg: '#7F1D1D', icon: 'block', ic: '#DC2626' }
    : near ? { bg: '#FFFBEB', bd: '#FDE68A', fg: '#78350F', icon: 'warning', ic: '#D97706' }
      : { bg: '#F8FAFC', bd: 'var(--border-light)', fg: 'var(--text-primary)', icon: 'fact_check', ic: '#3B82F6' };
  const line = cap === 0
    ? 'Background checks are paused on this account.'
    : `${used} of ${cap} used${resets ? ` · resets ${escapeHtml(resets)}` : ` per ${days} days`}`;
  const help = atCap
    ? `New checks are refused until ${resets ? escapeHtml(resets) : 'the period resets'}. Email ${mail} to raise the limit — accounts in good standing are raised same-day.`
    : `A limit on background checks and MVRs per ${days} days. Email ${mail} if you need more.`;
  const blockedLine = blocked > 0
    ? `<div style="font-size:0.8rem;color:#7F1D1D;margin-top:6px;">${blocked} background check${blocked === 1 ? ' was' : 's were'} turned away this period because the limit was reached${blockedApplicants > 0 ? ` (${blockedApplicants} ${blockedApplicants === 1 ? 'was a job applicant' : 'were job applicants'} who applied online and were not told why)` : ''}.</div>`
    : '';
  const money = (c) => (Math.max(0, Number(c) || 0) / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  let pendLine = '';
  if (pend && ((Number(pend.awaitingCount) || 0) + (Number(pend.unpaidCount) || 0)) > 0) {
    const parts = [];
    const ac = Math.floor(Number(pend.awaitingCount) || 0);
    const uc = Math.floor(Number(pend.unpaidCount) || 0);
    if (ac > 0) parts.push(`${ac} check${ac === 1 ? '' : 's'} (${money(pend.awaitingCents)}) waiting to be billed`);
    if (uc > 0) parts.push(`${uc} check${uc === 1 ? '' : 's'} (${money(pend.unpaidCents)}) unpaid`);
    pendLine = `<div style="font-size:0.8rem;color:var(--text-secondary);margin-top:6px;">Not yet paid: ${parts.join(' · ')}.</div>`;
  }
  return `
      <div style="background:${tone.bg};border:1px solid ${tone.bd};border-radius:8px;padding:12px 14px;margin-top:16px;">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:4px;">
          <span class="material-icons" style="font-size:18px;color:${tone.ic};">${tone.icon}</span>
          <span style="font-weight:600;font-size:0.92rem;color:${tone.fg};">Background checks this period</span>
        </div>
        <div style="font-size:0.9rem;color:${tone.fg};">${line}</div>
        <div style="font-size:0.78rem;color:var(--text-secondary);margin-top:4px;line-height:1.5;">${help}</div>
        ${blockedLine}${pendLine}
      </div>`;
}

async function _refreshCarrierBillingSubtitle() {
  const sub = document.getElementById('carrier-compliance-billing-subtitle');
  if (!sub) return;
  const tenant = window.AppState?.tenant;
  if (!tenant) return;
  // TASK-637 — carrier compliance billing is NOT an HR feature (exception b).
  // TASK-9431 — only the people who can CHANGE the card (owner / `billing`
  // module, hrCanManageComplianceBilling) see the Settings row, so only they
  // refresh its subtitle.
  if (typeof window.hrCanManageComplianceBilling === 'function' && !window.hrCanManageComplianceBilling()) {
    sub.textContent = 'Billing access required';
    return;
  }
  try {
    const call = httpsCallable(functions, 'getCarrierComplianceBillingState');
    const res = await call({ tenantId: tenant });
    const s = res.data || {};
    _carrierBillingState.tenantId = tenant;
    _carrierBillingState.data = s;
    if (s.partnerPaysActive) {
      sub.textContent = 'Your compliance partner pays — no card needed';
    } else if (s.hasPaymentMethod) {
      sub.textContent = `Card on file · ${(s.paymentMethodBrand || 'card').toString().toUpperCase()} •••• ${s.paymentMethodLast4 || '••••'}`;
    } else if (s.cardIssue === 'multiple_cards_no_default') {
      // TASK-9321 — cards exist but none is chosen; the server will not guess
      // which to charge, so checks are blocked. Say so instead of "no card".
      // TASK-9431 — the fix is in-app now (Choose card), not a customer default.
      sub.textContent = 'Several cards on file, none chosen — open to pick the card for Checkr / MVR pulls';
    } else {
      sub.textContent = 'No card on file — add one to run Checkr / MVR pulls';
    }
  } catch (err) {
    console.warn('[carrierBilling] subtitle refresh failed', err);
    sub.textContent = _carrierBillingErrorText(err);
  }
}

window.openCarrierComplianceBillingModal = async function() {
  const tenant = window.AppState?.tenant;
  if (!tenant) { showToast?.('Not logged in', 'error'); return; }

  let modal = document.getElementById('modal-carrier-compliance-billing');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'modal-carrier-compliance-billing';
    modal.className = 'modal';
    document.body.appendChild(modal);
  }

  // TASK-1382 — post-redirect banner from Stripe Checkout, VERIFIED before we
  // celebrate. The old version rendered green success straight off the URL
  // param, so a failed/canceled session (or a hand-typed param) showed
  // "Payment method attached" with no card on file — the same defect class
  // TASK-1371 fixed on the partner portal. Now: capture + strip the param up
  // front, render the banner only AFTER getCarrierComplianceBillingState
  // returns (its non-positive-cache live reconcile forces the Stripe
  // self-heal), and let the catch path say "couldn't confirm" instead of
  // green. Cancel needs no verification — it claims nothing happened.
  // Read from ORIGINAL_URL_PARAMS (auth-access Gotcha 8 — live search may
  // already be stripped by the time the modal opens), consume-once by deleting
  // from the snapshot, and strip ONLY `billing` from the address bar so other
  // params + hash survive (the old code nuked them all via pathname).
  const returnFlag = ORIGINAL_URL_PARAMS.get('billing')
    || new URLSearchParams(window.location.search).get('billing');
  // TASK-9431 — `cs` is the Checkout session id Stripe appends to the success
  // URL. The return leg records the card that session attached as THE compliance
  // card (manageCarrierComplianceCard confirm_setup), so a Replace never leaves
  // two cards with none chosen even when the webhook is slow. Server re-verifies
  // the session belongs to this tenant + customer; the id here is only a hint.
  const returnSessionId = returnFlag === 'compliance_success'
    ? (ORIGINAL_URL_PARAMS.get('cs') || new URLSearchParams(window.location.search).get('cs') || '')
    : '';
  if (returnFlag === 'compliance_success' || returnFlag === 'compliance_cancel') {
    ORIGINAL_URL_PARAMS.delete('billing');
    ORIGINAL_URL_PARAMS.delete('cs');
    try {
      const u = new URL(window.location.href);
      u.searchParams.delete('billing');
      u.searchParams.delete('cs');
      window.history.replaceState({}, document.title, u.pathname + u.search + u.hash);
    } catch (_) {}
  }
  let banner = '';
  if (returnFlag === 'compliance_cancel') {
    banner = `<div style="background:#FFFBEB;border:1px solid #FDE68A;border-radius:8px;padding:12px 14px;margin-bottom:16px;color:#78350F;font-size:0.85rem;">
      Setup canceled — no payment method attached.
    </div>`;
  }

  modal.innerHTML = `
    <div class="modal-content" style="max-width:560px;">
      <div class="modal-header" style="background:linear-gradient(135deg,#3B82F6,#06B6D4);color:#fff;">
        <h3 style="color:#fff;">
          <span class="material-icons" style="vertical-align:middle;">credit_card</span>
          Compliance Billing
        </h3>
        <button class="btn-icon" onclick="document.getElementById('modal-carrier-compliance-billing').classList.add('hidden')">
          <span class="material-icons" style="color:#fff;">close</span>
        </button>
      </div>
      <div class="modal-body" id="carrier-billing-body" style="padding:var(--spacing-lg);">
        ${banner}
        <div class="empty-state" style="padding:24px;"><span class="btn-spinner" style="margin-right:8px;"></span> Loading…</div>
      </div>
    </div>
  `;
  modal.classList.remove('hidden');

  // TASK-9431 — record the card the finished Checkout attached BEFORE reading
  // state, so the verified banner below names it. Best effort: the webhook
  // records the same session, and the picker is the fallback either way.
  let confirmRes = null;
  if (/^cs_[A-Za-z0-9_]{1,250}$/.test(returnSessionId)) {
    try {
      confirmRes = (await httpsCallable(functions, 'manageCarrierComplianceCard')({ tenantId: tenant, action: 'confirm_setup', sessionId: returnSessionId })).data || null;
    } catch (err) {
      console.warn('[openCarrierComplianceBillingModal] confirm_setup failed — the webhook / Choose card cover it', err);
    }
  }

  try {
    const call = httpsCallable(functions, 'getCarrierComplianceBillingState');
    const res = await call({ tenantId: tenant });
    const s = res.data || {};
    _carrierBillingState.tenantId = tenant;
    _carrierBillingState.data = s;

    // TASK-9431 — the server says whether THIS caller may change the card
    // (owner / `billing` module). An hr / compliance member who reaches this
    // modal from a background-check prompt can read it but gets no buttons the
    // server would refuse — they are told who can fix it instead. An older
    // functions build omits the field: fall back to the client mirror.
    const canManage = typeof s.canManage === 'boolean'
      ? s.canManage
      : (typeof window.hrCanManageComplianceBilling === 'function' && window.hrCanManageComplianceBilling());
    const whoCanFix = `<div style="font-size:0.78rem;color:var(--text-secondary);margin-top:8px;">Only the account owner or someone with the Billing module can change this card.</div>`;

    const partnerActive = s.partnerPaysActive;
    const hasPartner = !!s.partnerUid;
    // TASK-1361a — card DETAILS follow the Billing module. A non-manager (an
    // hr / compliance member sent here from a background-check prompt) gets only
    // `hasCardOnFile` from the server — no brand, last4 or receipt list — so it
    // is told whether a usable card exists and who can change it, nothing more.
    // An older functions build has no hasCardOnFile: fall back to hasPaymentMethod.
    const hasPm = s.hasCardOnFile ?? !!s.hasPaymentMethod;
    const hasSub = s.hasActiveSubscription;
    const brand = (s.paymentMethodBrand || '').toString().toUpperCase();
    const last4 = s.paymentMethodLast4 || '';

    // TASK-1382 — success banner earned by the verified state, not the URL.
    if (returnFlag === 'compliance_success') {
      banner = hasPm ? `<div style="background:#F0FDF4;border:1px solid #BBF7D0;border-radius:8px;padding:12px 14px;margin-bottom:16px;color:#14532D;font-size:0.85rem;">
        <span class="material-icons" style="vertical-align:middle;color:#16A34A;">check_circle</span>
        ${(confirmRes && confirmRes.reason === 'newer_choice_exists') ? 'Card saved. A different card was chosen more recently, so background checks still bill to' : 'Payment method attached'}${(brand || last4) ? ` (${escapeHtml(brand || 'Card')} ending in ${escapeHtml(last4 || '••••')})` : ''}. You're set up to run Checkr / MVR pulls.
      </div>` : `<div style="background:#FFFBEB;border:1px solid #FDE68A;border-radius:8px;padding:12px 14px;margin-bottom:16px;color:#78350F;font-size:0.85rem;">
        <span class="material-icons" style="vertical-align:middle;color:#D97706;">hourglass_top</span>
        Stripe reported setup complete, but we don't see a card on file yet — give it a moment and reopen this window. If it persists, try adding the card again.
      </div>`;
    }

    // v1.15.609 — partner-aware copy. Three buckets:
    //   1. partnerActive: green banner, hide PM card entirely (partner is
    //      paying; a "no card on file" warning here is a confusing
    //      conflicting signal that we previously rendered).
    //   2. hasPartner && !partnerActive: blue "talk to your partner" banner
    //      above an existing PM card section (kept as a soft fallback so
    //      the carrier isn't dead-ended if the partner is unreachable).
    //   3. no partner: unchanged "Add payment method" CTA + wholesale-buffer
    //      disclosure footer.
    const partnerBanner = partnerActive ? `
      <div style="background:#DCFCE7;border:1px solid #BBF7D0;border-radius:8px;padding:12px 14px;margin-bottom:16px;color:#14532D;font-size:0.85rem;">
        <span class="material-icons" style="vertical-align:middle;color:#16A34A;">verified_user</span>
        Your compliance partner is currently paying for Checkr / MVR pulls on your behalf. No card needed.
      </div>` : (hasPartner ? `
      <div style="background:#EFF6FF;border:1px solid #BFDBFE;border-radius:8px;padding:12px 14px;margin-bottom:16px;color:#1E3A8A;font-size:0.85rem;">
        <span class="material-icons" style="vertical-align:middle;color:#3B82F6;">handshake</span>
        Your compliance partner manages your background checks. Contact them about how Checkr / MVR pulls are billed for your account.
      </div>` : '');

    // TASK-1649 — "Who pays for background checks", separate from who runs them.
    // The carrier may only ever choose ITSELF (setCarrierBgPaidBy, owner / billing
    // module, card required); choosing the partner is the partner's call. An
    // older functions build omits bgPaidByEffective → no row.
    let payerRow = '';
    if (s.hasCompliancePartner && (s.bgPaidByEffective === 'partner' || s.bgPaidByEffective === 'carrier')) {
      const partnerPays = s.bgPaidByEffective === 'partner';
      // TASK-1649 slice 2b-2 — when the partner moved the cost to us, say who,
      // since when, and how to change it (the same facts as the email).
      const ch = (s.bgPaidByChange && typeof s.bgPaidByChange === 'object') ? s.bgPaidByChange : null;
      const movedByPartner = !partnerPays && ch && ch.value === 'carrier' && ch.fromEffective === 'partner' && (ch.by === 'partner' || ch.by === 'partner-subuser') && Number.isFinite(ch.atMs);
      const chPartner = movedByPartner ? escapeHtml(String(ch.partnerName || 'Your compliance partner')) : '';
      const chWhen = movedByPartner ? escapeHtml(new Date(ch.atMs).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })) : '';
      const why = partnerPays
        ? 'Your compliance partner pays for your background checks and MVRs.'
        : movedByPartner
          ? `${chPartner} moved background-check costs to you on ${chWhen}. Checks and MVRs ordered since then are charged to your card. Only ${chPartner} can choose to pay for them again.`
          : 'You pay for your own background checks and MVRs.';
      // Offered whenever the carrier has not pinned itself: a partner-paid
      // carrier can take the cost on; a carrier paying only because it RUNS
      // the checks can pin that, so a who-runs flip never moves its money
      // (COD Code Reviewer).
      let action = '';
      if (s.bgPaidBy !== 'carrier' && canManage) {
        const label = partnerPays ? 'Pay for our own checks ($)' : 'Keep paying for our own checks';
        action = hasPm
          ? `<button type="button" class="btn btn-sm btn-secondary" onclick="window.setCarrierPaysOwnChecks(this)">${label}</button>`
          : `<div style="font-size:0.78rem;color:var(--text-secondary);margin-top:6px;">To pay for your own checks, add a card first.</div><button type="button" class="btn btn-sm btn-secondary" style="margin-top:6px;" onclick="window.startCarrierComplianceCheckout(this)">Add payment method</button>`;
      }
      payerRow = `
      <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap;background:#F8FAFC;border:1px solid var(--border-light);border-radius:8px;padding:12px 14px;margin-bottom:16px;">
        <span class="material-icons" style="color:#3B82F6;">payments</span>
        <div style="flex:1;min-width:200px;">
          <div style="font-weight:600;font-size:0.92rem;color:var(--text-primary);">Who pays for checks</div>
          <div style="font-size:0.78rem;color:var(--text-secondary);">${why}</div>
        </div>
        ${action ? `<div>${action}</div>` : ''}
      </div>`;
    }

    // v1.15.595 — In partner-pays mode, drop the "wholesale + processing
    // buffer" parenthetical. It leaks the consultant's cost basis to the
    // carrier (the consultant pays DD and rebills the carrier with their
    // own markup; exposing DD wholesale lets the carrier reverse-engineer
    // the consultant's margin). v1.15.609 — same suppression when carrier
    // has a partner in carrier_pays mode (partner coordinates pricing).
    const subBanner = hasSub ? `
      <div style="background:#EFF6FF;border:1px solid #BFDBFE;border-radius:8px;padding:12px 14px;margin-bottom:16px;color:#1E3A8A;font-size:0.85rem;">
        <span class="material-icons" style="vertical-align:middle;color:#3B82F6;">receipt_long</span>
        You're on the $250/mo DispatchDeck plan.${(partnerActive || hasPartner) ? '' : ' Each Checkr / MVR pull rolls onto your next monthly invoice (wholesale + small processing buffer).'}
      </div>` : '';

    let pmCard = '';
    // TASK-9321 — the third state. Cards ARE attached, none is the customer's
    // default, and the charge path refuses to guess (this Stripe customer also
    // pays the $250/mo subscription, so the wrong guess bills the wrong card).
    // "Add payment method" would be the wrong instruction — a third card makes it
    // worse. TASK-9431 — the fix is picking the card IN-APP (Choose card →
    // manageCarrierComplianceCard). It used to send the payer to Stripe's billing
    // portal to set a customer default, which a founder / VIP never sees and a
    // non-owner admin is refused, and which would also have moved the $250/mo
    // subscription onto that card (TASK-4565).
    const ambiguousCards = !hasPm && s.cardIssue === 'multiple_cards_no_default';
    if (!partnerActive && ambiguousCards) {
      const n = Number.isFinite(s.attachedCardCount) && s.attachedCardCount > 1
        ? (s.attachedCardCount >= 3 ? '3 or more' : String(s.attachedCardCount)) : 'Several';
      pmCard = `
      <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap;background:#FFFBEB;border:1px solid #FDE68A;border-radius:8px;padding:12px 14px;">
        <span class="material-icons" style="color:#D97706;">credit_card</span>
        <div style="flex:1;min-width:200px;">
          <div style="font-weight:600;font-size:0.92rem;color:#78350F;">${s.attachedCardCount === 1 ? 'The card you chose for background checks was removed' : `${escapeHtml(n)} cards on file — none chosen for background checks`}</div>
          <div style="font-size:0.78rem;color:#92400E;">We won't guess which card to charge, so Checkr / MVR pulls are paused until you pick one.</div>
          ${canManage ? '' : whoCanFix}
        </div>
        ${canManage ? `<button type="button" class="btn btn-sm btn-primary" onclick="window.openCarrierComplianceCardPicker(this)">Choose card</button>` : ''}
      </div>`;
    } else if (!partnerActive) {
      pmCard = hasPm ? `
      <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap;background:#F8FAFC;border:1px solid var(--border-light);border-radius:8px;padding:12px 14px;">
        <span class="material-icons" style="color:#16A34A;">check_circle</span>
        <div style="flex:1;min-width:200px;">
          <div style="font-weight:600;font-size:0.92rem;">${canManage ? `${escapeHtml(brand || 'Card')} ending in ${escapeHtml(last4 || '••••')}` : 'Card on file'}</div>
          <div style="font-size:0.78rem;color:var(--text-secondary);">${hasSub ? 'Your background-check card. While you are on the monthly plan, pulls are added to your monthly DispatchDeck invoice and paid with it.' : 'The card Checkr / MVR pulls bill to.'}</div>
          ${canManage ? '' : whoCanFix}
        </div>
        ${canManage ? `
        <button type="button" class="btn btn-sm btn-secondary" onclick="window.startCarrierComplianceCheckout(this)">Replace card</button>
        <button type="button" class="btn btn-sm btn-secondary" onclick="window.openCarrierComplianceCardPicker(this)">Choose card</button>
        <button type="button" class="btn btn-sm btn-secondary" style="color:var(--danger);" onclick="window.removeCarrierComplianceCard(this)">Remove</button>` : ''}
      </div>` : `
      <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap;background:#FFFBEB;border:1px solid #FDE68A;border-radius:8px;padding:12px 14px;">
        <span class="material-icons" style="color:#D97706;">credit_card_off</span>
        <div style="flex:1;min-width:200px;">
          <div style="font-weight:600;font-size:0.92rem;color:#78350F;">No payment method on file</div>
          <div style="font-size:0.78rem;color:#92400E;">${hasPartner ? 'If your compliance partner is unable to cover Checkr / MVR pulls, you can attach a card here as a fallback.' : 'Attach a card via Stripe to run background checks billed to your business.'}</div>
          ${canManage ? '' : whoCanFix}
        </div>
        ${canManage ? `<button type="button" class="btn btn-sm ${hasPartner ? 'btn-secondary' : 'btn-primary'}" onclick="window.startCarrierComplianceCheckout(this)">Add payment method</button>` : ''}
      </div>`;
    }

    // TASK-9265 s3 — the screening ceiling the server enforces, and what has not
    // been collected yet. Read-only; numbers come from the same resolver the
    // order gate uses. An older functions build omits both → no card.
    const limitCard = _carrierScreeningLimitHtml(s.screeningLimit, s.pendingCharges);

    // TASK-966 — extra receipt-copy recipients (tenant.complianceReceiptRecipients).
    // Explicit opt-in list: DD emails a SendGrid copy of each pay-per-pull
    // compliance receipt (amount + package + date ONLY — no driver details) to
    // these addresses. Stripe's own receipt still goes to the primary as-is.
    // Rendered in every modal state incl. partner-pays (the TITS/AP case is
    // exactly a partner-pays tenant wanting AP + owner copies).
    const receiptCcList = Array.isArray(s.complianceReceiptRecipients)
      ? s.complianceReceiptRecipients.filter(e => typeof e === 'string')
      : [];
    // TASK-1361a — billing configuration: shown to card managers only (the
    // server no longer sends the list to anyone else).
    const receiptCopiesCard = !canManage ? '' : `
      <div style="background:#F8FAFC;border:1px solid var(--border-light);border-radius:8px;padding:12px 14px;margin-top:16px;">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px;">
          <span class="material-icons" style="font-size:18px;color:#3B82F6;">forward_to_inbox</span>
          <span style="font-weight:600;font-size:0.92rem;color:var(--text-primary);">Receipt copies</span>
        </div>
        <div style="font-size:0.78rem;color:var(--text-secondary);margin-bottom:10px;line-height:1.5;">
          Email a copy of each Checkr / MVR charge receipt to additional people — your AP inbox, bookkeeper, or compliance partner. Copies show the amount, package, and date only (no driver details).
        </div>
        <div style="display:flex;gap:8px;flex-wrap:wrap;align-items:center;">
          <input id="compliance-receipt-recipients-input" type="text" class="form-control" placeholder="ap@yourcompany.com, books@yourcompany.com" value="${window.escAttr(receiptCcList.join(', '))}" style="flex:1;min-width:200px;"${canManage ? '' : ' disabled'}>
          ${canManage ? `<button type="button" class="btn btn-sm btn-secondary" onclick="window.saveComplianceReceiptRecipients(this)">Save</button>` : ''}
        </div>
        <div style="font-size:0.72rem;color:var(--text-secondary);margin-top:6px;">Comma-separated · up to 10 addresses · leave empty to turn off</div>
      </div>`;

    // TASK-1605 (Chris 2026-09-29) — the account's default screening package
    // (`compliancePackageDefault`): what the online application's Checkr Embed
    // orders and what "Run MVR" keys off. An account with none STOPS and says
    // "pick a package" with a link (hr.js hrScreeningPackageStopHtml) — this card
    // is where that link lands. The server (getCarrierComplianceBillingState)
    // decides who may set it: when the partner RUNS checks the default is theirs,
    // picked in their portal (TASK-1217, Chris: "don't give carrier option"), and
    // the carrier is told who to ask. Picking a package here ORDERS NOTHING and
    // does not change who PAYS (TASK-1649 _resolveBgPaidBy is untouched).
    // integ/2129 perf (appColdLoadGzip, never raised): the card is built by the LAZY
    // views/screeningPackageCard.js cardHtml; this host is swapped for it once the
    // chunk is in (only the host THIS render made).
    const pkgHost = 't1605h' + (++_pkgCardSeq);
    const screeningPackageCard = `<div id="${pkgHost}"></div>`;
    _loadPkgCard().then((m) => { const h = document.getElementById(pkgHost); if (h) h.outerHTML = m.cardHtml(s, { canManage, whoCanFix, escapeHtml }); })
      .catch(() => {}); // the host stays empty; reopening the modal retries the chunk

    document.getElementById('carrier-billing-body').innerHTML = banner + partnerBanner + payerRow + subBanner + pmCard + limitCard + screeningPackageCard + receiptCopiesCard + `
      <div style="font-size:0.75rem;color:var(--text-muted);margin-top:16px;line-height:1.5;">
        ${partnerActive
          ? `Cards are stored by Stripe — DispatchDeck never sees card numbers. Your compliance partner is currently paying for Checkr / MVR pulls on your behalf.`
          : hasPartner
            ? `Cards are stored by Stripe — DispatchDeck never sees card numbers. Your compliance partner coordinates background-check billing for your account; reach out to them with any questions about Checkr / MVR costs.`
            : `Cards are stored by Stripe — DispatchDeck never sees card numbers. Checkr / MVR pulls are billed at wholesale + a small processing buffer (covers Stripe fees + vendor management). One-off charges appear on a separate invoice if you don't have an active subscription.`}
      </div>
    `;
  } catch (err) {
    console.error('[openCarrierComplianceBillingModal]', err);
    // TASK-1382 — a success return we couldn't verify is "unconfirmed", never green.
    if (returnFlag === 'compliance_success') {
      banner = `<div style="background:#FFFBEB;border:1px solid #FDE68A;border-radius:8px;padding:12px 14px;margin-bottom:16px;color:#78350F;font-size:0.85rem;">
        We couldn't confirm your payment method just now — reopen this window in a moment to check.
      </div>`;
    }
    document.getElementById('carrier-billing-body').innerHTML = banner + `<div style="background:#FEF2F2;border:1px solid #FECACA;border-radius:8px;padding:12px 14px;color:#7F1D1D;font-size:0.85rem;">${escapeHtml(_carrierBillingErrorText(err))}</div>`;
  }
};

window.startCarrierComplianceCheckout = async function(btn) {
  const tenant = window.AppState?.tenant;
  if (!tenant) { showToast?.('Not logged in', 'error'); return; }
  if (!btn || btn.disabled) return;
  const orig = btn.innerHTML;
  btn.disabled = true;
  btn.innerHTML = '<span class="btn-spinner" style="margin-right:6px;"></span> Opening Stripe…';
  try {
    const call = httpsCallable(functions, 'setupCarrierComplianceBillingSession');
    const res = await call({ tenantId: tenant, origin: window.location.origin });
    const url = res.data?.url;
    if (!url) throw new Error('No checkout URL returned');
    window.location.href = url;
  } catch (err) {
    console.error('[startCarrierComplianceCheckout]', err);
    showToast?.('Could not open Stripe setup: ' + (err.message || 'Unknown'), 'error');
    btn.disabled = false;
    btn.innerHTML = orig;
  }
};

// Remove the compliance card. The product had no remove path at all until
// 2026-08-20 — only "Replace card" — so a carrier who wanted their card OFF
// had no way to do it, and doing it in Stripe left our cached display fields
// advertising a card that was gone.
//
// The confirm spells out the consequence because it is NOT obvious: with no
// card and no partner paying, _assertComplianceBillingAvailable refuses every
// Checkr / MVR order, so this quietly disables background checks for the whole
// tenant. Better to say that up front than to have someone discover it when a
// hire is waiting.
window.removeCarrierComplianceCard = async function(btn) {
  const tenant = window.AppState?.tenant;
  if (!tenant) { showToast?.('Not logged in', 'error'); return; }
  if (!btn || btn.disabled) return;

  const s = _carrierBillingState.data || {};
  const label = (s.paymentMethodBrand || 'card').toString().toUpperCase()
    + ' ending in ' + (s.paymentMethodLast4 || '••••');
  const warning = `${label} will be detached from Stripe. Checkr / MVR pulls will be blocked until a new card is added`
    + (s.partnerPaysActive ? '.' : ' — nobody on this account will be able to run a background check.');
  const ok = typeof window.showConfirm === 'function'
    ? await window.showConfirm('Remove this card?', warning, {
        confirmText: 'Remove card',
        cancelText: 'Keep card',
        icon: 'credit_card_off',
        iconColor: 'var(--status-danger)',
      })
    : window.confirm(warning);
  if (!ok) return;

  const orig = btn.innerHTML;
  btn.disabled = true;
  btn.innerHTML = '<span class="btn-spinner" style="margin-right:6px;"></span> Removing…';
  try {
    const call = httpsCallable(functions, 'removeCarrierComplianceCard');
    await call({ tenantId: tenant });
    showToast?.('Card removed', 'success');
    // Re-open against fresh server state rather than patching the DOM — the
    // modal has several banner permutations and hand-editing them drifts.
    _carrierBillingState.data = null;
    await window.openCarrierComplianceBillingModal();
    _refreshCarrierBillingSubtitle();
  } catch (err) {
    console.error('[removeCarrierComplianceCard]', err);
    showToast?.(err.message || 'Could not remove card', 'error');
    btn.disabled = false;
    btn.innerHTML = orig;
  }
};

// TASK-1649 — the carrier chooses to pay for its own background checks
// (setCarrierBgPaidBy: owner / billing module, tenant bound from the auth claim,
// card required). There is no "partner pays" button here: only the partner can
// choose to pay.
window.setCarrierPaysOwnChecks = async function(btn) {
  const tenant = window.AppState?.tenant;
  if (!tenant) { showToast?.('Not logged in', 'error'); return; }
  if (!btn || btn.disabled) return;
  const s = _carrierBillingState.data || {};
  const label = (s.paymentMethodBrand || 'card').toString().toUpperCase() + ' ending in ' + (s.paymentMethodLast4 || '••••');
  const msg = `Background checks and MVRs ordered from now on will be charged to your ${label}, no matter who orders them. Checks already ordered are billed to whoever was paying when they were ordered. Only your compliance partner can take the cost back.`;
  const ok = typeof window.showConfirm === 'function'
    ? await window.showConfirm('Pay for your own checks?', msg, {
        confirmText: 'We’ll pay',
        cancelText: 'Cancel',
        icon: 'payments',
      })
    : window.confirm(msg);
  if (!ok) return;
  const orig = btn.innerHTML;
  btn.disabled = true;
  btn.innerHTML = '<span class="btn-spinner" style="margin-right:6px;"></span> Saving…';
  try {
    await httpsCallable(functions, 'setCarrierBgPaidBy')({ tenantId: tenant, value: 'carrier' });
    showToast?.('You now pay for your own background checks', 'success');
    _carrierBillingState.data = null;
    await window.openCarrierComplianceBillingModal();
    _refreshCarrierBillingSubtitle();
  } catch (err) {
    console.error('[setCarrierPaysOwnChecks]', err);
    showToast?.(err.message || 'Could not save', 'error');
    btn.disabled = false;
    btn.innerHTML = orig;
  }
};

// TASK-9431 — "Choose card": pick which saved card Checkr / MVR pulls bill to.
// The in-app way out of "several cards, none chosen" (TITS on 2026-09-28: two
// cards from two Replace clicks, no default, every order refused). Lists every
// card on the Stripe customer via manageCarrierComplianceCard `list`, marks the
// one background checks use today and any that pay the DispatchDeck
// subscription, and records the pick with `choose`. The server writes ONLY a
// server-only DispatchDeck record (no Stripe write) — never the customer default, so
// the subscription's card is untouched (TASK-4565), and nothing is detached.
window.openCarrierComplianceCardPicker = async function(btn) {
  const tenant = window.AppState?.tenant;
  if (!tenant) { showToast?.('Not logged in', 'error'); return; }
  if (btn && btn.disabled) return;
  const body = document.getElementById('carrier-billing-body');
  if (!body) return;
  const orig = btn ? btn.innerHTML : null;
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner" style="margin-right:6px;"></span> Loading…'; }
  let listed;
  try {
    const res = await httpsCallable(functions, 'manageCarrierComplianceCard')({ tenantId: tenant, action: 'list' });
    listed = res.data || {};
  } catch (err) {
    console.error('[openCarrierComplianceCardPicker]', err);
    showToast?.('Could not load your cards: ' + (err.message || 'Unknown'), 'error');
    if (btn) { btn.disabled = false; btn.innerHTML = orig; }
    return;
  }
  const cards = Array.isArray(listed.cards) ? listed.cards : [];
  // COD r2 — an ACTIVE subscriber's pulls ride the monthly invoice and are paid
  // with the subscription's card (recordComplianceUsage), so never tell them the
  // picked card is what gets charged.
  const onMonthlyPlan = !!(_carrierBillingState.data && _carrierBillingState.data.hasActiveSubscription);
  const inUseLabel = onMonthlyPlan ? 'Background-check card' : 'Background checks bill here';
  const rows = cards.map((c) => {
    const label = `${escapeHtml((c.brand || 'card').toString().toUpperCase())} ending in ${escapeHtml(c.last4 || '••••')}`;
    const exp = (c.expMonth && c.expYear) ? ` · expires ${escapeHtml(String(c.expMonth).padStart(2, '0'))}/${escapeHtml(String(c.expYear).slice(-2))}` : '';
    const badges = [
      c.inUse ? '<span style="font-size:0.7rem;font-weight:600;color:#14532D;background:#DCFCE7;border-radius:999px;padding:2px 8px;">' + inUseLabel + '</span>' : '',
      c.paysSubscription ? '<span style="font-size:0.7rem;font-weight:600;color:#1E3A8A;background:#DBEAFE;border-radius:999px;padding:2px 8px;">Pays your DispatchDeck subscription</span>' : '',
    ].filter(Boolean).join(' ');
    const idAttr = window.escJsAttr ? window.escJsAttr(c.id) : String(c.id).replace(/[^A-Za-z0-9_]/g, '');
    return `
      <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap;background:#F8FAFC;border:1px solid var(--border-light);border-radius:8px;padding:10px 12px;margin-bottom:8px;">
        <span class="material-icons" style="color:var(--text-secondary);">credit_card</span>
        <div style="flex:1;min-width:180px;">
          <div style="font-weight:600;font-size:0.9rem;color:var(--text-primary);">${label}<span style="font-weight:400;color:var(--text-secondary);">${exp}</span></div>
          ${badges ? `<div style="margin-top:4px;display:flex;gap:6px;flex-wrap:wrap;">${badges}</div>` : ''}
        </div>
        ${c.inUse
          ? '<span style="font-size:0.8rem;color:var(--text-secondary);">In use</span>'
          : `<button type="button" class="btn btn-sm btn-primary" onclick="window.chooseCarrierComplianceCard(this, '${idAttr}')">Use this card</button>`}
      </div>`;
  }).join('');
  body.innerHTML = `
    <div style="font-weight:600;font-size:0.95rem;color:var(--text-primary);margin-bottom:6px;">Which card should background checks bill to?</div>
    <div style="font-size:0.8rem;color:var(--text-secondary);margin-bottom:12px;line-height:1.5;">
      This only changes the card Checkr / MVR pulls are charged to. It does not remove any card, and your DispatchDeck subscription keeps billing the card it uses now.${(_carrierBillingState.data && _carrierBillingState.data.hasActiveSubscription) ? ' While you are on the monthly plan, pulls are added to your monthly invoice and paid with it.' : ''}
    </div>
    ${cards.length ? rows : '<div style="font-size:0.85rem;color:var(--text-secondary);margin-bottom:12px;">No cards on file yet — add one with Add payment method.</div>'}
    ${listed.truncated ? '<div style="font-size:0.75rem;color:var(--text-secondary);margin-bottom:8px;">Showing the first 20 cards.</div>' : ''}
    <div style="display:flex;justify-content:flex-end;margin-top:8px;">
      <button type="button" class="btn btn-sm btn-secondary" onclick="window.openCarrierComplianceBillingModal()">Back</button>
    </div>`;
};

window.chooseCarrierComplianceCard = async function(btn, paymentMethodId) {
  const tenant = window.AppState?.tenant;
  if (!tenant) { showToast?.('Not logged in', 'error'); return; }
  if (!btn || btn.disabled) return;
  const orig = btn.innerHTML;
  btn.disabled = true;
  btn.innerHTML = '<span class="btn-spinner" style="margin-right:6px;"></span> Saving…';
  try {
    const res = await httpsCallable(functions, 'manageCarrierComplianceCard')({ tenantId: tenant, action: 'choose', paymentMethodId });
    const d = res.data || {};
    const onPlan = !!(_carrierBillingState.data && _carrierBillingState.data.hasActiveSubscription);
    showToast?.(d.hasPaymentMethod
      ? `${onPlan ? 'Background-check card set to' : 'Background checks will bill'} ${(d.paymentMethodBrand || 'card').toString().toUpperCase()} ending in ${d.paymentMethodLast4 || '••••'}`
      : 'Saved', 'success');
    _carrierBillingState.data = null;
    await window.openCarrierComplianceBillingModal();
    _refreshCarrierBillingSubtitle();
  } catch (err) {
    console.error('[chooseCarrierComplianceCard]', err);
    showToast?.(err.message || 'Could not choose that card', 'error');
    btn.disabled = false;
    btn.innerHTML = orig;
  }
};

// TASK-1605 — save the account's default screening package through
// manageCarrierComplianceCard (set_package_default): same owner / billing-module
// gate as the card, server-side slug whitelist + partner lock + partner
// allow-list, history doc + old→new audit row (_applyCompliancePackageDefault).
// The HR / Compliance stops read AppState.tenantData.compliancePackageDefault, so
// mirror the server's answer there and drop the stops it just satisfied — no
// reload. Nothing is ordered and nobody is charged by this call.
let _pkgCardSeq = 0, _pkgCardLoad = null;
function _loadPkgCard() {
  if (!_pkgCardLoad) _pkgCardLoad = __ddChunkWrap(import('./views/screeningPackageCard.js?v=1')).catch((e) => { _pkgCardLoad = null; throw e; });
  return _pkgCardLoad;
}
window.saveCompliancePackageDefault = (btn) => _loadPkgCard()
  .then((m) => m.save(btn, { call: (p) => httpsCallable(functions, 'manageCarrierComplianceCard')(p), state: _carrierBillingState }))
  .catch(() => showToast?.('Could not save. Try again.', 'error'));

// TASK-966 — save the extra receipt-copy recipient list.
// TASK-9431 — through manageCarrierComplianceCard (set_receipt_recipients), not
// a direct tenant-doc write: the rules gate on that write is isTenantAdmin (a
// ROLE), so a `billing`-module member shown this modal got a raw permission
// error. The callable applies the same owner / billing-module gate as every
// other Compliance Billing action and audits the change. Client-side
// validation is UX only; the server re-validates on save and again at send
// time (_resolveComplianceReceiptCcRecipients in functions/src/index.js).
window.saveComplianceReceiptRecipients = async function(btn) {
  const tenant = window.AppState?.tenant;
  if (!tenant) { showToast?.('Not logged in', 'error'); return; }
  const input = document.getElementById('compliance-receipt-recipients-input');
  if (!input || (btn && btn.disabled)) return;
  const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const parts = input.value.split(/[,;\s]+/).map(e => e.trim().toLowerCase()).filter(Boolean);
  const bad = parts.find(e => !emailRe.test(e));
  if (bad) { showToast?.(`Not a valid email: ${bad}`, 'error'); return; }
  const emails = [...new Set(parts)];
  if (emails.length > 10) { showToast?.('Up to 10 receipt-copy addresses', 'error'); return; }
  if (btn) btn.disabled = true;
  try {
    await httpsCallable(functions, 'manageCarrierComplianceCard')({ tenantId: tenant, action: 'set_receipt_recipients', emails });
    if (_carrierBillingState.data) _carrierBillingState.data.complianceReceiptRecipients = emails;
    input.value = emails.join(', ');
    showToast?.(emails.length
      ? `Receipt copies will go to ${emails.length} address${emails.length > 1 ? 'es' : ''}`
      : 'Receipt copies turned off', 'success');
  } catch (err) {
    console.error('[saveComplianceReceiptRecipients]', err);
    showToast?.('Could not save: ' + (err.message || 'Unknown'), 'error');
  } finally {
    if (btn) btn.disabled = false;
  }
};

// Refresh subtitle on view changes (HR view in particular)
if (typeof document !== 'undefined') {
  document.addEventListener('DOMContentLoaded', () => {
    // Initial refresh attempt after a short delay so HRState/tenant has settled
    setTimeout(() => { _refreshCarrierBillingSubtitle(); }, 1500);
  });
}

// ============================================
// PWA INSTALL BANNER
// ============================================

// Capture Android/Chrome install prompt
let deferredInstallPrompt = null;
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  deferredInstallPrompt = e;
});

function initInstallBanner() {
  // Don't show if already running as PWA
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
  if (isStandalone) return;

  // Don't show inside native app (Capacitor iOS/Android)
  if (window.isNativeApp) return;

  // Don't show if dismissed in last 14 days
  const dismissed = localStorage.getItem('dd_install_dismissed');
  if (dismissed && (Date.now() - parseInt(dismissed)) < 14 * 86400000) return;

  // Don't show in demo mode
  if (window.AppState?.isDemo) return;

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const isAndroid = /Android/.test(navigator.userAgent);
  const isChrome = /Chrome/.test(navigator.userAgent) && !/Edge|Edg/.test(navigator.userAgent);

  // Only show on mobile (iOS Safari or Android Chrome)
  if (!isIOS && !(isAndroid && isChrome) && !deferredInstallPrompt) return;

  const banner = document.createElement('div');
  banner.id = 'pwa-install-banner';
  banner.style.cssText = 'position:fixed;bottom:0;left:0;right:0;z-index:10000;background:#0F172A;color:#fff;padding:14px 16px;display:flex;align-items:center;gap:12px;box-shadow:0 -4px 20px rgba(0,0,0,0.3);animation:slideUpBanner 0.3s ease-out;';

  const icon = document.createElement('img');
  icon.src = '/icons/icon-192x192.png';
  icon.style.cssText = 'width:44px;height:44px;border-radius:10px;flex-shrink:0;';

  const text = document.createElement('div');
  text.style.cssText = 'flex:1;min-width:0;';
  text.innerHTML = `<div style="font-weight:600;font-size:0.9rem;">Install DispatchDeck</div>`;

  if (isIOS) {
    text.innerHTML += `<div style="font-size:0.78rem;color:#94A3B8;margin-top:2px;">Tap <span style="display:inline-flex;align-items:center;vertical-align:middle;background:#3B82F6;border-radius:4px;padding:1px 4px;margin:0 2px;"><svg width="14" height="14" viewBox="0 0 24 24" fill="#fff"><path d="M16 5l-1.42 1.42-1.59-1.59V16h-2V4.83l-1.59 1.59L8 5l4-4 4 4zm4 5v11c0 1.1-.9 2-2 2H6c-1.11 0-2-.9-2-2V10c0-1.11.89-2 2-2h3v2H6v11h12V10h-3V8h3c1.1 0 2 .89 2 2z"/></svg></span> then <strong>"Add to Home Screen"</strong></div>`;
  } else {
    text.innerHTML += `<div style="font-size:0.78rem;color:#94A3B8;margin-top:2px;">Get the app on your home screen</div>`;
  }

  const actions = document.createElement('div');
  actions.style.cssText = 'display:flex;gap:8px;flex-shrink:0;';

  if (!isIOS && deferredInstallPrompt) {
    const installBtn = document.createElement('button');
    installBtn.textContent = 'Install';
    installBtn.style.cssText = 'background:#3B82F6;color:#fff;border:none;border-radius:6px;padding:8px 16px;font-size:0.85rem;font-weight:600;cursor:pointer;';
    installBtn.onclick = async () => {
      deferredInstallPrompt.prompt();
      const result = await deferredInstallPrompt.userChoice;
      if (result.outcome === 'accepted') {
        if (typeof gtag === 'function') gtag('event', 'pwa_install', { platform: 'android' });
      }
      deferredInstallPrompt = null;
      banner.remove();
      _teardownBannerPlacement();
    };
    actions.appendChild(installBtn);
  }

  const dismissBtn = document.createElement('button');
  dismissBtn.innerHTML = '<span class="material-icons" style="font-size:20px;">close</span>';
  dismissBtn.style.cssText = 'background:none;border:none;color:#94A3B8;cursor:pointer;padding:4px;display:flex;';
  dismissBtn.onclick = () => {
    localStorage.setItem('dd_install_dismissed', Date.now().toString());
    banner.remove();
    _teardownBannerPlacement();
  };
  actions.appendChild(dismissBtn);

  banner.appendChild(icon);
  banner.appendChild(text);
  banner.appendChild(actions);

  // Add animation keyframes if not already present
  // 2026-10-13 (mobile lane) — the banner is fixed bottom at z-index 10000, so it
  // sat on top of everything bottom-anchored on a phone until dismissed: measured
  // at 390px it covered ALL FIVE .mobile-nav tabs, the bottom row of the More
  // sheet, and the bottom of every .modal (the Notes panel's reply @ picker,
  // TASK-9619). Rules, injected here so they ship in the bundle with the banner
  // (no separate styles.css buster to forget):
  //   - hidden while any .modal is open (body.modal-open, set by
  //     initModalScrollLock — same pattern as the assistant FAB, TASK-1283), while
  //     the More sheet is open, and in driver PIN mode (backstop; init is already
  //     gated off for PIN drivers). It comes back when they close; dismiss/install
  //     are untouched.
  //   - lifted above the bottom nav instead of over it (_placeInstallBanner).
  if (!document.getElementById('pwa-banner-styles')) {
    const style = document.createElement('style');
    style.id = 'pwa-banner-styles';
    style.textContent = '@keyframes slideUpBanner{from{transform:translateY(100%)}to{transform:translateY(0)}}'
      + 'body.modal-open #pwa-install-banner,body.driver-mode-pin #pwa-install-banner{display:none !important}'
      + 'body:has(#mobile-more-menu:not(.hidden)) #pwa-install-banner{display:none !important}'
      // The floating buttons (Smart Tools FAB, lock FAB) sit just above the nav, i.e.
      // exactly where the lifted banner goes — raise them by the banner's height
      // while it is up (margin shifts a fixed box without touching its bottom rule).
      + 'body:has(#pwa-install-banner) .dd-assistant-fab,body:has(#pwa-install-banner) .lock-fab{margin-bottom:var(--pwa-banner-h,0px)}';
    document.head.appendChild(style);
  }

  document.body.appendChild(banner);

  // Sit directly above the mobile bottom nav when it is showing (its height
  // varies with the safe-area inset, so measure rather than hard-code), and
  // publish the banner's height for the FAB lift. Re-measured on resize AND
  // whenever the banner or nav changes size — that includes display:none <->
  // shown (modal / More sheet open+close), so the FAB lift is never stuck at a
  // height measured while the banner was hidden (COD 2026-10-13).
  let _bannerRO = null;
  const _teardownBannerPlacement = () => {
    window.removeEventListener('resize', _placeInstallBanner);
    if (_bannerRO) { _bannerRO.disconnect(); _bannerRO = null; }
    document.documentElement.style.removeProperty('--pwa-banner-h');
  };
  function _placeInstallBanner() {
    if (!banner.isConnected) { _teardownBannerPlacement(); return; } // removed elsewhere (jobboard.js clock-out)
    const nav = document.querySelector('.mobile-nav');
    const r = nav && getComputedStyle(nav).display !== 'none' ? nav.getBoundingClientRect() : null;
    banner.style.bottom = (r && r.height > 0) ? Math.max(0, Math.round(window.innerHeight - r.top)) + 'px' : '0px';
    document.documentElement.style.setProperty('--pwa-banner-h', banner.offsetHeight + 'px');
  }
  _placeInstallBanner();
  window.addEventListener('resize', _placeInstallBanner);
  if (typeof ResizeObserver === 'function') {
    _bannerRO = new ResizeObserver(() => _placeInstallBanner());
    _bannerRO.observe(banner);
    const _nav = document.querySelector('.mobile-nav');
    if (_nav) _bannerRO.observe(_nav);
  }

  // GA4 track impression
  if (typeof gtag === 'function') gtag('event', 'pwa_install_banner_shown', { platform: isIOS ? 'ios' : 'android' });
}

// ============================================
// START APP
// ============================================
init();

function __ddChunkWrap(p){var h=globalThis.__ddChunk;return typeof h==="function"?h(p):p}
